import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export type Language = 'en' | 'sv' | 'ar' | 'ku' | 'ckb' | 'fa' | 'de' | 'fr' | 'es' | 'it' | 'pt' | 'tr';

const SUPPORTED_LANGUAGES: Language[] = ['en', 'sv', 'ar', 'ku', 'ckb', 'fa', 'de', 'fr', 'es', 'it', 'pt', 'tr'];
const STORAGE_KEY = 'aqarnet-language';

function detectBrowserLanguage(): Language {
  const browserLanguages = navigator.languages || [navigator.language];
  
  for (const browserLang of browserLanguages) {
    const primaryLang = browserLang.split('-')[0].toLowerCase();
    
    if (SUPPORTED_LANGUAGES.includes(primaryLang as Language)) {
      return primaryLang as Language;
    }
    
    // Handle Kurdish variants
    if (browserLang.toLowerCase().includes('ku') || browserLang.toLowerCase().includes('kur')) {
      if (browserLang.toLowerCase().includes('ckb') || browserLang.toLowerCase().includes('central')) {
        return 'ckb';
      }
      return 'ku';
    }
  }
  
  return 'en'; // Default fallback
}

function getInitialLanguage(): Language {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && SUPPORTED_LANGUAGES.includes(stored as Language)) {
      return stored as Language;
    }
  } catch {
    // localStorage not available
  }
  return detectBrowserLanguage();
}

export interface LanguageContextType {
  language: Language;
  setLanguage: (language: Language) => void;
  t: (key: string) => string;
  isRTL: boolean;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

// Translation object
const translations = {
  en: {
    // Header
    'nav.search': 'Search Properties',
    'nav.search_agents': 'Search Agents',
    'nav.sell': 'Sell Property',
    'nav.news': 'News',
    'nav.build': 'Build House',
    'nav.agencies': 'Agencies',
    'nav.buy': 'Buy',
    'nav.valuation': 'Valuation',
    'nav.more': 'More',
    'nav.saved': 'Saved',
    'nav.admin': 'Admin',
    'nav.agency': 'Agency',
    'nav.agency_dashboard': 'Agency Dashboard',
    'nav.location_manager': 'Location Manager',
    'nav.map_configuration': 'Map Configuration',
    'nav.hello': 'Hello',
    'nav.menu': 'Menu',
    'nav.manage_news': 'Manage News',
    
    // Search Section
    'search.for_sale': 'For Sale',
    'search.for_rent': 'For Rent',
    'search.sold_prices': 'Sold Prices',
    'search.search_agents': 'Search Agents',
    'search.area_label': 'Area',
    'search.area_placeholder': 'Type area or address',
    'search.area_examples': 'Example: Baghdad, Basra, Erbil, Najaf, Karbala',
    'search.expand_area': 'Expand area with',
    'search.property_type': 'Property Type',
    'search.show_filters': 'Show search filters',
    'search.find_properties': 'Find properties for sale',
    'search.min_rooms': 'Min rooms',
    'search.min_area': 'Min area',
    'search.max_price': 'Max price',
    'search.all': 'All',
    'search.no_max': 'No max',
    'search.new_construction': 'New construction',
    'search.show_new_construction': 'Show new construction',
    'search.only_new_construction': 'Only new construction',
    'search.exclude_new_construction': 'Exclude new construction',
    'search.keywords': 'Keywords',
    'search.keywords_placeholder': 'Pool, fireplace, etc.',
    'search.more_filters_note': 'More search filters are available in the search results.',
    'search.recent_searches': 'Recent searches',
    'search.hide_history': 'Hide history',
    'search.clear_history': 'Clear history',
    'search.recent_erbil': 'Properties, Erbil',
    
    // Property Types
    'property.all_types': 'All types',
    'property.villas': 'Villas',
    'property.townhouses': 'Townhouses',
    'property.apartments': 'Apartments',
    'property.condominiums': 'Condominiums',
    'property.business': 'Business properties',
    'property.land': 'Land',
    'property.farms': 'Farms/Forests',
    'property.other': 'Other',
    
    // Cities/Governorates
    'city.baghdad': 'Baghdad',
    'city.basra': 'Basra',
    'city.erbil': 'Erbil',
    'city.sulaymaniyah': 'Sulaymaniyah',
    'city.najaf': 'Najaf',
    'city.karbala': 'Karbala',
    'city.mosul': 'Mosul',
    'city.kirkuk': 'Kirkuk',
    'city.anbar': 'Anbar',
    'city.dhi_qar': 'Dhi Qar',
    'city.maysan': 'Maysan',
    'city.wasit': 'Wasit',
    'city.babil': 'Babil',
    'city.qadisiyyah': 'Qadisiyyah',
    'city.muthanna': 'Muthanna',
    'city.diyala': 'Diyala',
    'city.saladin': 'Saladin',
    'city.nineveh': 'Nineveh',
    'city.dohuk': 'Dohuk',
    
    // Auth extended
    'auth.first_name': 'First Name',
    'auth.last_name': 'Last Name',
    'auth.first_name_placeholder': 'Enter your first name',
    'auth.last_name_placeholder': 'Enter your last name',
    'auth.display_name': 'Display Name',
    'auth.display_name_placeholder': 'Choose a display name',
    'auth.display_name_hint': 'This is the only name others will see',
    'auth.date_of_birth': 'Date of Birth',
    'auth.fill_all_fields': 'Please fill in all required fields',
    'auth.must_be_18': 'You must be at least 18 years old to register',
    'auth.must_be_18_hint': 'You must be at least 18 years old',
    
    // Identity Verification
    'verification.verify_identity': 'Verify Identity',
    'verification.verified': 'Verified',
    'verification.title': 'Identity Verification',
    'verification.description': 'Upload a government-issued document to verify your identity and get a verified badge.',
    'verification.document_type': 'Document Type',
    'verification.doc_id_card': 'ID Card',
    'verification.doc_passport': 'Passport',
    'verification.doc_drivers_license': "Driver's License",
    'verification.upload_document': 'Upload Document',
    'verification.click_to_upload': 'Click to upload or drag and drop',
    'verification.file_requirements': 'JPG, PNG, WEBP or PDF (max 10MB)',
    'verification.submit': 'Submit for Verification',
    'verification.uploading': 'Uploading...',
    'verification.submitted': 'Verification Submitted',
    'verification.submitted_desc': 'Your verification request has been submitted. We will review it shortly.',
    'verification.pending_message': 'Your verification is being reviewed. This usually takes 1-2 business days.',
    'verification.rejected_message': 'Your previous verification was rejected. Please submit a new document.',
    'verification.status_approved': 'Approved',
    'verification.status_pending': 'Pending',
    'verification.status_rejected': 'Rejected',
    'verification.invalid_file_type': 'Invalid file type',
    'verification.allowed_types': 'Please upload a JPG, PNG, WEBP, or PDF file',
    'verification.file_too_large': 'File too large',
    'verification.max_size': 'Maximum file size is 10MB',
    'verification.error': 'Verification Error',
    'verification.admin_title': 'Identity Verifications',
    'verification.admin_description': 'Review and approve identity verification requests',
    'verification.no_requests': 'No verification requests',
    'verification.view': 'Review',
    'verification.review_title': 'Review Verification',
    'verification.review_notes': 'Review Notes',
    'verification.review_notes_placeholder': 'Add notes about your decision (optional)',
    'verification.approve': 'Approve',
    'verification.reject': 'Reject',
    'verification.approved': 'Verification Approved',
    'verification.rejected': 'Verification Rejected',
    'verification.review_complete': 'The verification has been processed.',

    // Common
    'common.price': 'Price',
    'common.rooms': 'Rooms',
    'common.size': 'Size',
    'common.governorate': 'Governorate',
    'common.select_governorate': 'Select governorate',
    'common.cancel': 'Cancel',
    'common.save': 'Save',
    'common.saving': 'Saving...',
    'common.send': 'Send',
    'common.sending': 'Sending...',
    'common.submit': 'Submit',
    'common.close': 'Close',
    'common.error': 'Error',
    'common.success': 'Success',
    'common.loading': 'Loading...',
    'common.name': 'Name',
    'common.email': 'Email',
    'common.phone': 'Phone',
    'common.message': 'Message',
    'common.required': 'Required',
    'common.optional': 'Optional',
    'common.delete': 'Delete',
    'common.free': 'Free',
    
    // Leaderboard
    'leaderboard': 'Leaderboard',
    'leaderboardDescription': 'Discover top-performing agencies and brokers ranked by views and sales',
    'topAgencies': 'Top Agencies',
    'topBrokers': 'Top Brokers',
    'topAgenciesDescription': 'Agencies ranked by property views and sales performance',
    'topBrokersDescription': 'Brokers ranked by property views and sales performance',
    'allRegions': 'All Regions',
    'noAgenciesFound': 'No agencies found',
    'noBrokersFound': 'No brokers found',
    'brokers': 'brokers',
    'sold': 'sold',
    'active': 'active',
    'views': 'views',
    // Dialogs - Contact Agent
    'dialog.contact_agent': 'Contact Agent',
    'dialog.contact_agent_btn': 'Contact Agent',
    'dialog.contact_message_placeholder': 'Hello! I am interested in the property at {address} and would like to know more. Please contact me when you have time.',
    'dialog.contact_sending': 'Sending...',
    'dialog.contact_send': 'Send Message',
    'dialog.contact_note': 'Your inquiry will be sent directly to the responsible agent who will contact you.',
    
    // Dialogs - Schedule Viewing
    'dialog.schedule_viewing': 'Schedule Viewing',
    'dialog.schedule_viewing_btn': 'Schedule Viewing',
    'dialog.preferred_time': 'Preferred viewing time',
    'dialog.preferred_time_note': 'Select date and time for the viewing. The agent will confirm availability.',
    'dialog.special_requests': 'Special requests',
    'dialog.special_requests_placeholder': 'Any special requests or questions...',
    'dialog.schedule_booking': 'Booking viewing...',
    'dialog.schedule_book': 'Book Viewing',
    'dialog.schedule_note': 'Your request will be sent to the agent who will confirm the time via email.',
    
    // Dialogs - Save Search
    'dialog.save_search': 'Save Search',
    'dialog.save_search_btn': 'Save Search',
    'dialog.search_name': 'Search name',
    'dialog.search_name_placeholder': 'e.g., Apartment in Baghdad',
    'dialog.city_municipality': 'City/Municipality',
    'dialog.min_rooms': 'Min rooms',
    'dialog.min_size': 'Min size (m²)',
    'dialog.property_type': 'Property type',
    'dialog.email_alerts': 'Email notifications when new properties match',
    'dialog.saving_search': 'Saving...',
    
    // Dialogs - Mark as Sold
    'dialog.mark_sold': 'Mark as Sold',
    'dialog.mark_sold_description': 'Enter the final sale price for this property. This will move the listing to sold properties.',
    'dialog.final_sale_price': 'Final Sale Price (USD)',
    'dialog.listed_price': 'Listed price',
    'dialog.property_details': 'Property Details',
    'dialog.enter_valid_price': 'Please enter a valid sold price',
    'dialog.enter_price_placeholder': 'Enter sold price',
    'dialog.marked_sold_success': 'Property marked as sold successfully',
    'dialog.mark_sold_error': 'Failed to mark property as sold',
    
    // Dialogs - Add Property
    'dialog.add_property': 'Add New Property',
    'dialog.hide_preview': 'Hide preview',
    'dialog.show_preview': 'Show preview',
    'dialog.selected_images': 'Selected images',
    'dialog.uploaded_images': 'Uploaded images',
    'dialog.click_primary': 'Click on an image to set it as primary',
    'dialog.invalid_format': 'Invalid file format',
    'dialog.file_too_large': 'File too large',
    'dialog.upload_failed': 'Upload failed',
    'dialog.images_required': 'Images required',
    'dialog.please_upload': 'Please upload at least one image for the property',
    'dialog.form_validation_failed': 'Form validation failed',
    
    // Hero/News Section
    'hero.find_dream_home': 'Find Your Dream Home in Iraq',
    'hero.explore_description': 'Explore thousands of homes for sale and final prices from all over Iraq. Get valuations, read property news and contact agents.',
    'hero.badge': 'Iraq\'s Premier Property Platform',
    
    // Home Features
    'home.feature.properties': 'Thousands of Properties',
    'home.feature.properties_desc': 'Browse the largest selection of homes, apartments, and land across all Iraqi governorates.',
    'home.feature.market': 'Local Expertise',
    'home.feature.market_desc': 'Deep knowledge of every governorate and neighborhood in Iraq.',
    'home.feature.trusted': 'Verified Agents',
    'home.feature.trusted_desc': 'Connect with trusted, verified real estate professionals.',
    'home.why_choose': 'Why Choose Aqarnet',
    'home.why_choose_desc': 'Everything you need to find your perfect property',
    
    // CTA Section
    'cta.ready': 'Ready to find your home?',
    'cta.desc': 'Browse thousands of properties across Iraq',
    'cta.explore': 'Explore Properties',
    
    // Property Listings
    'listings.latest_for_sale': 'Latest for sale',
    'listings.properties_found': 'properties found',
    'listings.all_news': 'All news',
    'listings.add_property': 'Add property',
    'listings.edit': 'Edit',
    'listings.edit_property': 'Edit Property',
    'listings.monthly_fee': 'Monthly fee',
    'listings.open_floor_plan': 'Open floor plan',
    'listings.large_balcony': 'Large balcony',
    'listings.family_friendly': 'Family friendly',
    'listings.photos': 'photos',
    'listings.per_month': 'month',
    'listings.delete_confirm': 'Are you sure you want to delete this property?',
    
    // Forms
    'form.price': 'Price',
    'form.priceUSD': 'Price (USD)',
    'form.priceIQD': 'Price (IQD)',
    'form.priceConversionNote': 'Enter price in either currency - the other will auto-calculate (Rate: 1 USD ≈ 1,310 IQD)',
    'form.price_usd': 'Price (USD)',
    'form.price_placeholder': 'Enter price in US dollars',
    'form.price_description': 'Enter the price in US dollars. It will be displayed in both USD and Iraqi dinar.',
    'form.address': 'Address', 
    'form.city': 'City',
    'form.rooms': 'Rooms',
    'form.bathrooms': 'Bathrooms',
    'form.size': 'Size (m²)',
    'form.building_size': 'Building size (m²)',
    'form.building_size_placeholder': 'Building area in m²',
    'form.building_size_description': 'Building area (boarea)',
    'form.plot_size': 'Plot size (m²)',
    'form.plot_size_placeholder': 'Plot area in m²',
    'form.plot_size_description': 'Total plot area (tomtarea)',
    'form.type': 'Type',
    'form.propertyType': 'Property Type',
    'form.monthly_fee': 'Monthly fee (SEK)',
    'form.monthly_fee_usd': 'Monthly fee (USD)',
    'form.monthly_fee_placeholder': 'Enter monthly fee in USD (optional)',
    'form.monthly_fee_description': 'Optional monthly fee in US dollars',
    'form.description': 'Description',
    'form.description_placeholder': 'Describe the property...',
    'form.payment_plan': 'Payment Plan',
    'form.payment_plan_placeholder': 'e.g., 30% down payment, 70% on delivery, or monthly installments...',
    'form.payment_plan_hint': 'Add payment installments to show buyers your payment options',
    'payment_plan.total': 'Total',
    'payment_plan.amount': 'Amount',
    'payment_plan.description': 'Description',
    'payment_plan.date': 'Date',
    'payment_plan.percentage': 'Percentage',
    'payment_plan.add_installment': 'Add Installment',
    'payment_plan.hint': 'Leave empty if not offering a payment plan',
    'payment_plan.down_payment': 'Down Payment',
    'payment_plan.first_payment': 'First Payment',
    'payment_plan.second_payment': 'Second Payment',
    'payment_plan.third_payment': 'Third Payment',
    'payment_plan.fourth_payment': 'Fourth Payment',
    'payment_plan.fifth_payment': 'Fifth Payment',
    'payment_plan.sixth_payment': 'Sixth Payment',
    'payment_plan.seventh_payment': 'Seventh Payment',
    'payment_plan.final_payment': 'Final Payment',
    'payment_plan.payment_description': 'Payment description',
    'payment_plan.enable': 'Include Payment Plan',
    'form.floor_plans': 'Floor Plans',
    'form.upload_floor_plans': 'Upload floor plan images',
    'form.features': 'Features (comma separated)',
    'form.features_placeholder': 'e.g., Pool, Garden, Garage, Modern kitchen',
    'form.features_description': 'Separate multiple features with commas',
    'form.amenities': 'Amenities',
    'form.amenities_hint': 'Select all amenities that apply to this property',
    'amenities.balcony': 'Balcony',
    'amenities.electricity_generator': 'Electricity Generator',
    'amenities.central_ac': 'Central Air Conditioner',
    'amenities.double_glazed_windows': 'Double Glazed Windows',
    'amenities.satellite_tv': 'Satellite/Cable TV',
    'amenities.cctv': 'CCTV Security',
    'amenities.security_staff': 'Security Staff',
    'amenities.sewerage_system': 'Sewerage System',
    'amenities.furnished_kitchen': 'Furnished Kitchen',
    'amenities.24hr_electricity': '24hr Electricity',
    'amenities.market': 'Nearby Market',
    'amenities.elevator': 'Elevator',
    'amenities.school': 'Nearby School',
    'amenities.mosque': 'Nearby Mosque',
    'amenities.central_gas': 'Central Gas',
    'amenities.maintenance': 'Maintenance Services',
    'amenities.cleaning': 'Cleaning Services',
    'amenities.fire_fighting': 'Fire Fighting Equipment',
    'amenities.car_washing': 'Car Washing Area',
    'amenities.smart_home': 'Smart Home System',
    'amenities.earthquake_resistant': 'Earthquake Resistant',
    'amenities.central_fire_system': 'Central Fire Fighting System',
    'amenities.hot_water_system': 'Central Hot Water System',
    'amenities.central_vacuum': 'Central Vacuum System',
    'amenities.fountain': 'Fountain',
    'amenities.24hr_water': '24hr Water Supply',
    'amenities.garbage_chute': 'Garbage Chute',
    'amenities.kitchen_counter': 'Kitchen Counter',
    'amenities.street_lights': 'Street Lights',
    'amenities.playground': 'Children Playground',
    'amenities.gym': 'Gym',
    'amenities.garden': 'Garden',
    'amenities.parking': 'Parking',
    'amenities.wifi': 'WiFi',
    'amenities.security_door': 'Security Door',
    'amenities.split_ac': 'Split AC',
    'amenities.bathtub': 'Bathtub',
    'amenities.refrigerator': 'Refrigerator',
    'amenities.washing_machine': 'Washing Machine',
    'amenities.swimming_pool': 'Swimming Pool',
    'amenities.solar_panels': 'Solar Panels',
    'amenities.water_tank': 'Water Storage Tank',
    'amenities.water_well': 'Water Well',
    'amenities.septic_tank': 'Septic Tank',
    'amenities.central_heating': 'Central Heating',
    'amenities.desert_cooler': 'Desert Cooler',
    'amenities.intercom': 'Intercom System',
    'amenities.external_camera': 'External Security Camera',
    'amenities.guard_room': 'Guard Room',
    'amenities.rooftop_terrace': 'Rooftop Terrace',
    'amenities.basement': 'Basement',
    'amenities.storage_room': 'Storage Room',
    'amenities.internal_stairs': 'Internal Stairs',
    'amenities.external_stairs': 'External Stairs',
    'amenities.corner_unit': 'Corner Unit',
    'amenities.two_streets': 'Two Street Access',
    'amenities.double_entrance': 'Double Entrance',
    'amenities.marble_flooring': 'Marble Flooring',
    'amenities.ceramic_tiles': 'Ceramic Tiles',
    'amenities.parquet_flooring': 'Parquet Flooring',
    'amenities.gypsum_decorations': 'Gypsum Decorations',
    'amenities.false_ceiling': 'False Ceiling',
    'amenities.steel_doors': 'Steel Doors',
    'amenities.aluminum_windows': 'Aluminum Windows',
    'amenities.pvc_windows': 'PVC Windows',
    'amenities.guest_room': 'Guest Room (Diwan)',
    'amenities.servant_quarters': 'Servant Quarters',
    'amenities.covered_parking': 'Covered Parking',
    'amenities.concrete_fence': 'Concrete Fence',
    'amenities.iron_fence': 'Iron Fence',
    'amenities.street_pavement': 'Street Pavement',
    'amenities.fully_furnished': 'Fully Furnished',
    'amenities.hospital': 'Nearby Hospital',
    'amenities.category.utilities': 'Power & Utilities',
    'amenities.category.climate': 'Climate Control',
    'amenities.category.security': 'Security',
    'amenities.category.building': 'Building Features',
    'amenities.category.interior': 'Interior & Flooring',
    'amenities.category.rooms': 'Rooms',
    'amenities.category.outdoor': 'Outdoor & Parking',
    'amenities.category.appliances': 'Appliances',
    'amenities.category.services': 'Services',
    'amenities.category.nearby': 'Nearby',
    'common.selected': 'selected',
    'form.property_images': 'Property Images',
    'form.current_images': 'Current images',
    'form.image': 'Image',
    'form.primary': 'Primary',
    'form.click_image_primary': 'Click on an image to set as primary. Use arrows to change order.',
    'form.click_to_upload_new': 'Click to upload new images',
    'form.new_images_upload': 'New images to upload',
    'form.new_image': 'New image',
    'form.upload': 'Upload',
    'form.new_images': 'new images',
    'form.location': 'Location',
    'form.required': 'Required',
    'form.optional': 'Optional',
    'form.cancel': 'Cancel',
    'form.save_changes': 'Save changes',
    'form.uploading': 'Uploading...',
    'form.upload_images': 'Upload images',
    'form.click_to_upload': 'Click to upload images',
    'form.file_formats': 'JPEG, PNG, WebP, GIF (max 10MB per image)',
    'form.select_type': 'Select type',
    'form.location_on_map': 'Property location on map',
    'form.location_hint': 'Click on the map to set the exact location of the property',
    'form.hide_preview': 'Hide preview',
    'form.show_preview': 'Show preview',
    'form.selected_images': 'Selected images',
    'form.click_set_primary': 'Click on an image to set it as primary',
    'form.uploaded_images': 'Uploaded images',
    'form.images': 'images',
    'form.uploading_images': 'Uploading images...',
    'form.city_placeholder': 'Search for a city...',
    'form.new_construction': 'New construction (Future home)',
    'form.preview': 'Preview',
    'form.preview_description': 'This is how your listing will appear on the site',
    'form.fullscreen': 'Fullscreen',
    'form.exit_fullscreen': 'Exit Fullscreen',
    'form.click_preview_fullscreen': 'Click preview to view fullscreen',
    'form.preview_contact_disabled': 'Contact options are disabled in preview mode',
    'form.invalid_format': 'Invalid file format',
    'form.invalid_format_desc': '{filename} has invalid format. Allowed: JPEG, PNG, WebP, GIF',
    'form.file_too_large': 'File too large',
    'form.file_too_large_desc': '{filename} is larger than 10MB',
    'form.upload_failed': 'Upload failed',
    'form.upload_failed_desc': 'Could not upload images',
    'form.images_required': 'Images required',
    'form.images_required_desc': 'Please upload at least one image for the property',
    'form.sold_price_usd': 'Sold Price (USD)',
    'form.sold_price_placeholder': 'Enter actual sold price',
    'form.sold_price_description': 'The actual sale price (may differ from listing price)',
    'form.video_url': 'Video Tour URL',
    'form.video': 'Video',
    'form.add_video': 'Add another video',
    'form.video_addon': '+$3 addon',
    'form.video_included': 'Included free',
    'form.video_url_hint': 'Add a YouTube or Vimeo video tour. Video addon can be purchased after listing is created.',
    'form.video_url_hint_free': 'Add a YouTube or Vimeo video tour - included with your subscription!',
    'form.video_url_hint_addon': 'Add a YouTube or Vimeo video tour. $3 will be added to your listing cost.',
    'form.include_video_tour': 'Include video tour',
    'form.show_map': 'Show Map',
    'form.no_location': 'No location selected',
    
    // News Section
    'news.property_news': 'Property News',
    'news.all_news': 'All news',
    'news.click_tops': 'Click tops',
    'news.guides': 'Guides',
    'news.news': 'News',
    'news.article_1_title': '"The Lundell House" attracts thousands – week\'s top click again',
    'news.article_2_title': 'Find dream homes together – create and share collections on Aqarnet',
    'news.article_3_title': 'Mini castle for sale: "Associated with many famous families"',
    
    // Loading and empty states
    'loading.properties': 'Loading properties...',
    'loading.saved_searches': 'Loading saved searches...',
    'empty.no_properties': 'No properties available right now',
    'empty.check_back': 'Check back later to see new listings.',
    'saved_searches.title': 'Saved searches',
    'saved_searches.empty': 'No saved searches yet',
    'saved_searches.description': 'Save a search to get email notifications when matching properties are added',
    'saved_searches.created': 'Created',
    'saved_searches.alerts_on': 'Notifications on',
    'saved_searches.alerts_off': 'Notifications off',
    'saved_searches.email_enabled': 'Email notifications enabled',
    'saved_searches.email_disabled': 'Email notifications disabled',
    'saved_searches.settings_updated': 'Setting has been updated',
    'saved_searches.removed': 'Search removed',
    'saved_searches.removed_desc': 'The saved search has been removed',
    'saved_searches.error': 'Error',
    'saved_searches.update_error': 'Could not update setting',
    'saved_searches.delete_error': 'Could not delete search',
    'saved_searches.all_properties': 'All properties',
    
    // Rental properties
    'rental.for_sale': 'For Sale',
    'rental.for_rent': 'For Rent',
    'rental.listing_type': 'Listing Type',
    'rental.rental_details': 'Rental Details',
    'rental.rental_period': 'Rental Period',
    'rental.rental_period_hint': 'How often rent is paid',
    'rental.daily': 'Daily',
    'rental.weekly': 'Weekly',
    'rental.monthly': 'Monthly',
    'rental.yearly': 'Yearly',
    'rental.per_daily': '/day',
    'rental.per_weekly': '/week',
    'rental.per_monthly': '/month',
    'rental.per_yearly': '/year',
    'rental.deposit_amount': 'Deposit Amount (USD)',
    'rental.minimum_lease': 'Minimum Lease (months)',
    'rental.months': 'months',
    'rental.available_from': 'Available From',
    'rental.furnished': 'Furnished',
    'rental.utilities_included': 'Utilities Included',
    'rental.pets_allowed': 'Pets Allowed',
    
    // Filters
    'filters.listing_type': 'Listing Type',
    'filters.all': 'All',
    'filters.sale': 'For Sale',
    'filters.rent': 'For Rent',
    
    // Property card
    'property.sold': 'SOLD',
    'property.photos': 'photos',
    'property.rooms_short': 'rooms',
    'property.plot': 'plot',
    'property.per_month': '/month',
    'property.delete_title': 'Delete property?',
    'property.delete_confirm': 'Are you sure you want to delete {address}? This action cannot be undone.',
    'property.deleted': 'Property deleted',
    'property.deleted_desc': 'The property has been removed',
    'property.delete_error': 'Could not delete property',
    
    // Navigation
    'nav.favorites': 'Favorites',
    'nav.profile': 'Profile',
    'nav.login': 'Login',
    'nav.logout': 'Logout',
    'nav.recent_searches': 'Recent searches',
    'nav.no_recent_searches': 'No recent searches',
    'nav.new_search': 'New search',
    'nav.all_properties': 'All properties',
    'nav.new_production': 'New production',
    
    // Footer
    'footer.contact': 'Contact',
    'footer.customer_service': 'Customer service & contact',
    'footer.about': 'About Aqarnet',
    'footer.press': 'Press',
    'footer.investors': 'For investors',
    'footer.advertising': 'Advertising and marketing',
    'footer.jobs': 'Jobs with us',
    'footer.statistics': 'Property statistics',
    'footer.apps': 'Apps',
    'footer.iphone_ipad': 'iPhone and iPad',
    'footer.android': 'Android',
    'footer.follow': 'Follow Aqarnet',
    'footer.newsletter': 'Aqarnet newsletter',
    'footer.whatsapp': 'WhatsApp',
    'footer.facebook': 'Facebook',
    'footer.instagram': 'Instagram',
    'footer.linkedin': 'LinkedIn',
    'footer.pinterest': 'Pinterest',
    'footer.tiktok': 'TikTok',
    'footer.threads': 'Threads',
    'footer.podcast': 'Aqarnet podcast - The Aqarnet Way',
    'footer.all_properties': 'All properties',
    'footer.privacy': 'Privacy',
    'footer.cookies': 'Cookies',
    'footer.terms': 'Terms & Legal Information',
    'footer.cookie_settings': 'Cookie settings',
    
    // Agents
    'agents.title': 'Find an agent',
    'agents.subtitle': 'Thinking of selling? We help you find agents who are experts in your area!',
    'agents.area_search': 'Area Search',
    'agents.name_search': 'Name Search',
    'agents.search_placeholder': 'Enter area or address',
    'agents.show': 'Show',
    'agents.sort_by': 'Sort by',
    'agents.agents_count': 'Agents',
    'agents.companies_label': 'Brokerage firms',
    'agents.recent_sales': 'Latest sale date',
    'agents.most_sales': 'Most sales',
    'agents.alphabetical': 'Alphabetical order',
    'agents.showing_results': 'Showing',
    'agents.agents_label': 'agents',
    'agents.loading': 'Loading agents...',
    'agents.no_results': 'No agents found.',
    'agents.phone': 'Phone:',
    'agents.status': 'Status:',
    'agents.active_agent': 'Active agent',
    'agents.anonymous_agent': 'Anonymous agent',
    'agents.agent_label': 'Agent',
    'agents.show_more': 'Show more agents',

    // Properties for Sale Page
    'propertiesForSale.title': 'Properties for Sale in Sweden',
    'propertiesForSale.statistics.municipalities': 'municipalities',
    'propertiesForSale.statistics.forSale': 'for sale',
    'propertiesForSale.statistics.sold': 'sold this week',
    'propertiesForSale.map.fullscreen': 'Map / Full screen',
    'propertiesForSale.map.area': 'Area',
    'propertiesForSale.filters.searchWithin': 'Search within',
    'propertiesForSale.filters.km': 'km',
    'propertiesForSale.filters.propertyType': 'Property type',
    'propertiesForSale.filters.allTypes': 'All types',
    'propertiesForSale.filters.villa': 'Villa',
    'propertiesForSale.filters.apartment': 'Apartment',
    'propertiesForSale.filters.townhouse': 'Townhouse',
    'propertiesForSale.filters.plot': 'Plot',
    'propertiesForSale.filters.commercial': 'Commercial',
    'propertiesForSale.filters.other': 'Other',
    'propertiesForSale.filters.rooms': 'Rooms',
    'propertiesForSale.filters.bathrooms': 'Bathrooms',
    'propertiesForSale.filters.min': 'Min',
    'propertiesForSale.filters.max': 'Max',
    'propertiesForSale.filters.size': 'Size',
    'propertiesForSale.filters.price': 'Price',
    'propertiesForSale.filters.features': 'Features',
    'propertiesForSale.filters.balcony': 'Balcony/Uteplats/Terrace',
    'propertiesForSale.filters.pets': 'Pets',
    'propertiesForSale.filters.newBuild': 'New build',
    'propertiesForSale.filters.showAll': 'Show all',
    'propertiesForSale.filters.buildYear': 'Build year',
    'propertiesForSale.filters.searchResults': 'Search results',
    'propertiesForSale.filters.searchPlaceholder': 'Search areas, cities etc...',
    'propertiesForSale.filters.searchingFor': 'Searching for:',
    'propertiesForSale.filters.any': 'Any',
    'propertiesForSale.filters.keywords': 'Keywords',
    'propertiesForSale.filters.showResults': 'Show results',
    'propertiesForSale.filters.clearFilters': 'Clear',
    'propertiesForSale.filters.realEstateSituation': 'Real Estate Situation',
    'propertiesForSale.filters.situation.tapu': 'Tapu',
    'propertiesForSale.filters.situation.landContract': 'Land Contract',
    'propertiesForSale.filters.situation.noDocuments': 'You Have No Documents',
    'propertiesForSale.filters.situation.investment': 'Investment',
    'propertiesForSale.filters.situation.musataha': 'Musataha',
    'propertiesForSale.filters.situation.card': 'Card',
    'propertiesForSale.filters.situation.agriculturalTapu': 'Agricultural Tapu',
    'propertiesForSale.sort.newest': 'Newest first',
    'propertiesForSale.sort.price_low': 'Price: Low to High',
    'propertiesForSale.sort.price_high': 'Price: High to Low',
    'propertiesForSale.sort.size': 'Size',
    'propertiesForSale.noResults': 'No properties found',
    'propertiesForSale.tryAdjustingFilters': 'Try adjusting your filters or search in a different area',

    // Property Types
    'propertyTypes.allTypes': 'All types',
    'propertyTypes.villa': 'Villas',
    'propertyTypes.apartment': 'Apartments',
    'propertyTypes.townhouse': 'Townhouses',
    'propertyTypes.business': 'Business properties',
    'propertyTypes.plot': 'Plots',
    'propertyTypes.farm': 'Farms/Forests',
    'propertyTypes.other': 'Other',

    // Cookie Policy
    'cookie.title': 'Cookie Policy',
    'cookie.last_updated': 'Last updated:',
    'cookie.section1.title': 'Section 1 - General Provisions Regarding Implementation of Tracking Technologies',
    'cookie.section1.p1': 'Ploqs AB, hereinafter referred to as "the Service Provider", together with partners and subcontractors engaged by the Service Provider, implements and utilizes various forms of digital tracking mechanisms, including but not limited to cookies, web beacons, pixel tags and other similar technological solutions (collectively referred to as "Tracking Technologies") on the Service Provider\'s digital platforms, websites and mobile applications (collectively referred to as "the Service Provider\'s Digital Channels"). This policy for handling Tracking Technologies specifies the purposes for which and the methods by which the Service Provider applies said technologies.',
    'cookie.section1.p2': 'Tracking Technologies consist primarily of smaller text-based data files which are deposited on the user\'s terminal equipment or web browser program for the purpose of aggregating information regarding the user\'s interaction with and navigation on digital platforms and mobile applications.',
    'cookie.section1.h1': 'Processing of Personal Data and Data Protection Considerations',
    'cookie.section1.p3': 'Information collected through the application of Tracking Technologies constitutes in most cases data which, directly or indirectly and in combination with other available data, can potentially be used to identify individuals, including but not limited to IP addresses, unique cookie identifiers, advertising identifiers (IDFA), device identifiers and various metadata that enable differentiation of specific terminal devices. Notwithstanding that such information does not explicitly reveal the data subject\'s identity, this type of data is regularly classified as personal data in accordance with applicable data protection legislation.',
    'cookie.section1.h2': 'User Choices',
    'cookie.section1.p4': 'The user has the right to revise previously given consents at any time by navigating to the consent management options provided through the Service Provider\'s digital interface.',
    'cookie.section1.h3': 'Persistence and Storage Periods of Tracking Technologies',
    'cookie.section1.p5': 'Tracking Technologies exhibit varying persistence depending on the intended purpose and functionality of each technology. Session-based Tracking Technologies are initiated upon the user\'s connection to the digital platform and are terminated at the latest upon closing of the user\'s browser instance. Persistent Tracking Technologies are retained in the user\'s browser for a predetermined time period exceeding individual sessions.',
    'cookie.section2.title': 'Section 2 - Mandatory Tracking Technologies for Service Delivery',
    'cookie.section2.p1': 'Mandatory Tracking Technologies refer to such technological implementations that are required for the Service Provider to provide basic and essential functionality to the user, for example for the purpose of maintaining information regarding the user\'s previously given consents regarding Tracking Technologies, to enable differentiation between distinct user sessions through anonymized identifiers, to facilitate cartographic functionality, to enable playback of audiovisual material in property advertisements uploaded by real estate agents, to preserve the user\'s saved search preferences, to maintain the security of the services, prevent improper use of the services and to ensure continuous and optimal service performance.',
    'cookie.section2.legal': 'Legal basis for processing:',
    'cookie.section2.legal_value': 'Legitimate interest in accordance with Article 6.1(f) GDPR',
    'cookie.section2.storage': 'Storage period:',
    'cookie.section2.storage_value': 'Up to twenty-four (24) months for persistent Tracking Technologies; session-based Tracking Technologies are terminated immediately upon closing of browser instance.',
    'cookie.section3.title': 'Section 3 - Tracking Technologies for Analytical Purposes',
    'cookie.section3.p1': 'The Service Provider attaches significant importance to the ability to quantify visit frequency, register interaction data on the digital platform and in mobile applications, and otherwise analyze how the Service Provider\'s digital services are used by users. Provided that the user has given the required consent, the Service Provider implements Tracking Technologies, excluding direct personal data, for the purpose of analyzing and further developing the Service Provider\'s digital services and to continuously offer the user an optimized user experience, maintain service performance and to produce target group analyses and statistical compilations regarding platform usage.',
    'cookie.section3.legal_value': 'Consent in accordance with Article 6.1(a) GDPR',
    'cookie.section3.storage_value': 'Up to twenty-six (26) months',
    'cookie.section4.title': 'Section 4 - Tracking Technologies for Personalization Purposes',
    'cookie.section4.p1': 'Tracking Technologies implemented for personalization purposes aim to adapt the user\'s experience on the Service Provider\'s digital platforms by providing content with enhanced personal relevance for the individual user. This adaptation is based, for example, on the user\'s search behavior and interaction patterns. In the event that the user holds a registered user account with the Service Provider, this behavioral data is combined with information that the user voluntarily provides within the framework of the service, for example information regarding the user\'s housing conditions, whereby content adaptations are made based on the user\'s aggregated profile.',
    'cookie.section5.title': 'Section 5 - Tracking Technologies for Marketing Purposes',
    'cookie.section5.p1': 'The Service Provider implements Tracking Technologies for marketing purposes, provided that the user has given the required consent, for the purpose of enabling exposure of relevant advertising content and property advertisements with enhanced relevance for the user based on a constructed advertising profile.',
    'cookie.section5.p2': 'Through the implementation of Tracking Technologies, the Service Provider also ensures that advertising exposures are limited to a predefined number of occasions per user, and that advertising effectiveness is measured and analyzed. The Service Provider\'s advertising content may also be exposed on external digital platforms.',
    'cookie.section5.p3': 'It should be noted that in the event the user chooses to deactivate Tracking Technologies for marketing purposes, advertising content will still be exposed to the user on the Service Provider\'s digital platforms, however without personalization.',
    'cookie.section5.storage_value': 'Up to twenty-four (24) months',
    'cookie.section6.title': 'Section 6 - International Data Transfers to Third Countries',
    'cookie.section6.p1': 'In the Service Provider\'s consent management tool, information is provided regarding which Tracking Technologies and partners process the user\'s personal data within, or transfer the user\'s personal data to, jurisdictions outside the European Union and the European Economic Area (so-called "third countries"). The information provided is based on the assumption that the user visiting the Service Provider\'s digital platforms is geographically located within EU/EEA territory and thereby primarily connects to server infrastructure located within EU/EEA jurisdiction.',
    'cookie.section7.title': 'Section 7 - Instructions for Removal of Tracking Technologies',
    'cookie.section7.p1': 'The user has the option to remove Tracking Technologies from their terminal equipment or mobile device and configure settings for local data storage via the browser\'s settings interface. The user can choose to deactivate Tracking Technologies or receive notifications each time a new Tracking Technology is initiated on the user\'s terminal equipment.',
    'cookie.section7.p2': 'For detailed instructions regarding handling and removal of Tracking Technologies, reference is made to the help option in the user\'s browser or the hyperlinks below for each browser application:',
    
    // Auth
    'auth.loading': 'Loading...',
    'auth.login': 'Log in',
    'auth.create_account': 'Create account',
    'auth.login_subtitle': 'Log in by choosing one of the options below.',
    'auth.signup_subtitle': 'Create a new account to get started.',
    'auth.continue_apple': 'Continue with Apple',
    'auth.continue_google': 'Continue with Google',
    'auth.or': 'or',
    'auth.email': 'Email',
    'auth.email_placeholder': 'Enter email',
    'auth.password': 'Password',
    'auth.password_placeholder': 'Enter password',
    'auth.account_type': 'Account type',
    'auth.user_role': 'User - I want to buy/rent property',
    'auth.agent_role': 'Agent - I want to sell/rent out properties',
    'auth.continue': 'Continue',
    'auth.no_account': "Don't have an account? Create account",
    'auth.have_account': 'Already have an account? Log in',
    'auth.login_failed': 'Login failed',
    'auth.invalid_credentials': 'Invalid email or password',
    'auth.welcome_back': 'Welcome back!',
    'auth.logged_in': 'You have been logged in.',
    'auth.signup_failed': 'Registration failed',
    'auth.account_created': 'Account created!',
    'auth.check_email': 'Check your email to confirm your account.',
    'auth.error': 'An error occurred',
    'auth.try_again': 'Please try again later.',
    'auth.broker_interest_title': 'Are you a broker?',
    'auth.broker_interest_description': 'If you are interested in listing properties on Aqarnet, send us a declaration of interest and we will get back to you.',
    'auth.broker_contact_us': 'Contact us',
    'auth.broker_email_subject': 'Broker Registration Interest',
    'auth.broker_email_body': 'Hello,\n\nI am interested in registering as a broker on Aqarnet.\n\nName:\nAgency Name:\nPhone:\nCity:\n\nThank you!',
    
    // Favorites Page
    'favorites.title': 'My favorites',
    'favorites.saved_properties': 'saved properties',
    'favorites.login_required': 'Log in to see your favorites',
    'favorites.login_description': 'Save properties you like to easily find them later.',
    'favorites.loading': 'Loading your favorites...',
    'favorites.no_favorites': 'No favorites yet',
    'favorites.start_saving': 'Start saving properties by clicking the heart on listings you like.',
    'favorites.search_properties': 'Search properties',
    
    // Profile Page
    'profile.title': 'My profile',
    'profile.subtitle': 'Manage your personal information and settings',
    'profile.loading': 'Loading profile...',
    'profile.fetch_error': 'Error fetching profile',
    'profile.fetch_error_description': 'Could not fetch profile information.',
    'profile.unnamed_user': 'Unnamed user',
    'profile.personal_info': 'Personal information',
    'profile.update_info': 'Update your personal information',
    'profile.email': 'Email address',
    'profile.email_readonly': 'Email address cannot be changed',
    'profile.display_name': 'Display name',
    'profile.display_name_placeholder': 'Your name',
    'profile.phone': 'Phone number',
    'profile.phone_placeholder': '070-123 45 67',
    'profile.save_changes': 'Save changes',
    'profile.saving': 'Saving...',
    'profile.reset': 'Reset',
    'profile.updated': 'Profile updated',
    'profile.updated_description': 'Your profile has been saved.',
    'profile.update_error': 'Update error',
    'profile.update_error_description': 'Could not save changes.',
    'profile.active_listings': 'Active listings',
    'profile.active_listings_description': 'Your published property listings for sale',
    'profile.view_all': 'View all',
    'profile.loading_listings': 'Loading listings...',
    'profile.no_active_listings': 'You have no active listings yet',
    'profile.more_active_listings': 'And {count} more active listings',
    'profile.sold_properties': 'Sold properties',
    'profile.sold_properties_description': 'Your sold properties',
    'profile.no_sold_properties': 'You have no sold properties yet',
    'profile.more_sold_properties': 'And {count} more sold properties',
    'profile.listing_fetch_error': 'Error fetching listings',
    'profile.listing_fetch_error_description': 'Could not fetch your listings.',
    'profile.account_settings': 'Account settings',
    'profile.account_settings_description': 'Manage your account and security',
    'profile.change_password': 'Change password',
    'profile.change_password_description': 'Update your password for more secure login',
    'profile.change': 'Change',
    'profile.two_factor': 'Two-factor authentication',
    'profile.two_factor_description': 'Add extra security for your account',
    'profile.enable': 'Enable',
    'profile.delete_account': 'Delete account',
    'profile.delete_account_description': 'Permanent deletion of your account and all data',
    'profile.delete': 'Delete',
    'profile.logout': 'Log out',
    
    // Profile Email Notifications
    'profile.email_notifications': 'Email Notifications',
    'profile.email_notifications_description': 'Choose which email notifications you want to receive',
    'profile.notif_property_alerts': 'Property Alerts',
    'profile.notif_property_alerts_desc': 'Get notified about new properties matching your saved searches',
    'profile.notif_saved_search_alerts': 'Saved Search Updates',
    'profile.notif_saved_search_alerts_desc': 'Receive updates when new properties match your criteria',
    'profile.notif_newsletter': 'Newsletter',
    'profile.notif_newsletter_desc': 'Receive our weekly newsletter with market insights',
    'profile.notif_viewing_confirmations': 'Viewing Confirmations',
    'profile.notif_viewing_confirmations_desc': 'Get confirmation emails for scheduled viewings',
    'profile.save_preferences': 'Save Preferences',
    'profile.notifications_saved': 'Notification preferences saved',
    'profile.notifications_save_error': 'Failed to save notification preferences',
    
    // Not Found Page
    'notfound.title': '404',
    'notfound.message': 'Oops! Page not found',
    'notfound.back_home': 'Return to Home',
    
    // Property Detail Page
    'property.back_to_listings': 'Back to listings',
    'property.not_found': 'Property not found',
    'property.link_copied': 'Link copied',
    'property.link_copied_description': 'Property link copied to clipboard',
    'property.description': 'Description',
    'property.features': 'Features',
    'property.details': 'Property Details',
    'property.rooms_label': 'Rooms',
    'property.size_label': 'Size',
    'property.plot_size_label': 'Plot Size',
    'property.monthly_fee_label': 'Monthly Fee',
    'property.contact_agent': 'Contact Agent',
    'property.send_message': 'Send Message',
    'property.schedule_viewing': 'Schedule Viewing',
    'property.section.details': 'Details',
    'property.section.amenities': 'Amenities',
    'property.section.overview': 'Overview',
    'property.section.payment_plan': 'Payment Plan',
    'property.section.floor_plans': 'Floor Plans',
    'property.section.view_map': 'View Map',
    'property.copy_link': 'Copy Link',
    'property.share': 'Share',
    'property.share_text': 'Check out this property',
    'property.posted_on': 'Posted On',
    'property.type': 'Type',
    'property.status': 'Status',
    'property.available': 'Available',
    'property.no_amenities': 'No amenities listed for this property',
    'property.no_description': 'No description available',
    'property.location': 'Location',
    'property.payment_plan_info': 'Contact the agent for payment plan details',
    'property.contact_for_payment_plan': 'Contact for Payment Plan',
    'property.no_floor_plans': 'No floor plans available',
    'property.request_floor_plans': 'Request Floor Plans',
    'property.no_map_location': 'Location not available on map',
    
    // News Page
    'news.subtitle': 'Stay updated with the latest property news, market insights, and guides',
    'news.no_articles': 'No news articles yet',
    'news.check_back': 'Check back soon for the latest property news and market insights.',
    'news.search_placeholder': 'Search articles...',
    'news.showing_filtered': 'Showing {shown} of {total} articles on this page',
    'news.clear_filters': 'Clear filters',
    'news.no_matching': 'No matching articles',
    'news.adjust_filters': 'Try adjusting your search or filters to find what you\'re looking for.',
    'news.publisher': 'Publisher',
    'news.page_info': 'Page {current} of {total} • {count} total articles',
    'news.category.all': 'All',
    'news.category.market_news': 'Market News',
    'news.category.property_guides': 'Property Guides',
    'news.category.investment_tips': 'Investment Tips',
    'news.category.industry_updates': 'Industry Updates',
    'news.category.announcements': 'Announcements',
    'news.category.expert_advice': 'Expert Advice',
    
    // Filters/Sorting
    'filters.any': 'Any',
    'filters.newest_first': 'Newest first',
    'filters.price_low_high': 'Price: Low to High',
    'filters.price_high_low': 'Price: High to Low',
    'filters.size': 'Size',
    'filters.searching_for': 'Searching for',
    'filters.search_areas': 'Search areas, cities etc...',
    
    // Toast Messages
    'toast.error': 'Error',
    'toast.success': 'Success',
    'toast.auth_required': 'Authentication Required',
    'toast.sign_in_required': 'Please sign in to continue',
    'toast.sign_in_favorites': 'Please sign in to add favorites',
    'toast.sign_in_properties': 'Please sign in to add properties',
    'toast.properties_load_error': 'Failed to load properties',
    'toast.property_added': 'Property added successfully',
    'toast.property_add_error': 'Failed to add property',
    'toast.property_updated': 'Property updated successfully',
    'toast.property_update_error': 'Failed to update property',
    'toast.property_deleted': 'Property deleted successfully',
    'toast.property_delete_error': 'Failed to delete property',
    'toast.property_marked_sold': 'Property marked as sold successfully',
    'toast.property_sold_error': 'Failed to mark property as sold',
    'toast.invalid_price': 'Please enter a valid sold price',
    'toast.favorites_load_error': 'Failed to load favorites',
    'toast.added_to_favorites': 'Added to favorites',
    'toast.add_favorites_error': 'Failed to add to favorites',
    'toast.removed_from_favorites': 'Removed from favorites',
    'toast.remove_favorites_error': 'Failed to remove from favorites',
    'toast.search_saved': 'Search saved',
    'toast.search_saved_desc': 'has been saved with email alerts enabled',
    'toast.search_save_error': 'Could not save the search',
    'toast.valuation_complete': 'Valuation complete',
    'toast.valuation_estimated': 'Estimated value',
    'toast.valuation_error': 'Could not get valuation',
    'toast.message_sent': 'Message sent',
    'toast.agent_contact_desc': 'The agent will contact you shortly',
    'toast.message_error': 'Could not send the message',
    'toast.viewing_booked': 'Viewing booked',
    'toast.viewing_confirmed': 'Your viewing has been booked and confirmation sent via email',
    'toast.viewing_error': 'Could not book the viewing',
    'toast.image_analysis_complete': 'Image analysis complete',
    'toast.image_analysis_desc': 'AI analysis of the image has been completed',
    'toast.image_analysis_error': 'Could not analyze the image',
    'toast.logged_out': 'Logged out',
    'toast.welcome_back': 'Welcome back anytime!',
    
    // Recent searches
    'search.properties_label': 'Properties',
    'search.from': 'from',
    'search.up_to': 'up to',
    
    // News Management
    'news_mgmt.title': 'News Management',
    'news_mgmt.subtitle': 'Create and manage news articles',
    'news_mgmt.create_article': 'Create Article',
    'news_mgmt.edit_article': 'Edit Article',
    'news_mgmt.create_new': 'Create New Article',
    'news_mgmt.all_articles': 'All Articles',
    'news_mgmt.no_articles': 'No articles yet. Create your first one!',
    'news_mgmt.delete_article': 'Delete Article',
    'news_mgmt.delete_confirm': 'Are you sure you want to delete this article? This action cannot be undone.',
    'news_mgmt.title_label': 'Title',
    'news_mgmt.title_placeholder': 'Enter article title',
    'news_mgmt.category_label': 'Category',
    'news_mgmt.category_placeholder': 'e.g., Market News, Guides, Tips',
    'news_mgmt.image_url': 'Image URL',
    'news_mgmt.content': 'Content',
    'news_mgmt.content_placeholder': 'Enter article content',
    'news_mgmt.published': 'Published',
    'news_mgmt.draft': 'Draft',
    'news_mgmt.update_article': 'Update Article',
    'news_mgmt.required_fields': 'Title and category are required',
    'news_mgmt.article_updated': 'News article updated successfully',
    'news_mgmt.article_created': 'News article created successfully',
    'news_mgmt.article_save_error': 'Failed to save news article',
    'news_mgmt.article_deleted': 'News article deleted successfully',
    'news_mgmt.article_delete_error': 'Failed to delete news article',
    'news_mgmt.access_denied': 'Access Denied',
    'news_mgmt.access_denied_desc': "You don't have permission to access this page. Only publishers and admins can manage news.",
    
    // Badges & Labels
    'badge.new': 'New',
    'badge.sold': 'Sold',
    'badge.featured': 'Featured',
    'badge.reduced': 'Price Reduced',
    'badge.new_construction': 'New Construction',
    
    // Confirmation Messages
    'confirm.delete': 'Delete',
    'confirm.cancel': 'Cancel',
    'confirm.yes': 'Yes',
    'confirm.no': 'No',
    'confirm.are_you_sure': 'Are you sure?',
    'confirm.action_irreversible': 'This action cannot be undone.',
    
    // Tooltips
    'tooltip.favorite': 'Add to favorites',
    'tooltip.share': 'Share property',
    'tooltip.edit': 'Edit property',
    'tooltip.delete': 'Delete property',
    'tooltip.contact': 'Contact agent',
    'tooltip.schedule': 'Schedule viewing',
    'tooltip.map_view': 'View on map',
    'tooltip.list_view': 'List view',
    'tooltip.grid_view': 'Grid view',
    
    // Admin Pages
    'admin.loading': 'Loading...',
    'admin.access_denied': 'Access denied. Admin privileges required.',
    
    // Admin Notification Settings
    'admin.notifications.title': 'Notification Settings',
    'admin.notifications.description': 'Manage notification settings for all user types. Enable, disable, or hide notifications as needed.',
    'admin.notifications.fetch_error': 'Failed to fetch notification settings',
    'admin.notifications.update_success': 'Notification setting updated',
    'admin.notifications.update_error': 'Failed to update notification setting',
    'admin.notifications.role_description': 'Manage notifications for {role} users',
    'admin.notifications.hidden': 'Hidden',
    'admin.notifications.disabled': 'Disabled',
    'admin.notifications.active': 'Active',
    'admin.notifications.hide': 'Hide',
    'admin.notifications.roles.user': 'Users',
    'admin.notifications.roles.maklare': 'Brokers',
    'admin.notifications.roles.agency': 'Agencies',
    'admin.notifications.roles.admin': 'Admins',
    'admin.notifications.roles.publisher': 'Publishers',
    'admin.notifications.types.new_viewing_request': 'New Viewing Request',
    'admin.notifications.types.new_client_message': 'New Client Message',
    'admin.notifications.types.property_alerts': 'Property Alerts',
    'admin.notifications.types.weekly_summary': 'Weekly Summary',
    'admin.notifications.types.broker_property_added': 'Broker Added Property',
    'admin.notifications.types.broker_viewing_request': 'Broker Viewing Request',
    'admin.notifications.types.broker_client_message': 'Broker Client Message',
    'admin.notifications.types.broker_joined': 'Broker Joined',
    'admin.notifications.types.weekly_agency_summary': 'Weekly Agency Summary',
    'admin.notifications.types.saved_search_alerts': 'Saved Search Alerts',
    'admin.notifications.types.favorite_property_updates': 'Favorite Property Updates',
    'admin.notifications.types.newsletter': 'Newsletter',
    'admin.notifications.types.system_alerts': 'System Alerts',
    'admin.notifications.types.new_user_signup': 'New User Signup',
    'admin.notifications.types.article_published': 'Article Published',
    
    // Admin Settings
    'admin.settings': 'Admin Settings',
    'admin.messaging_settings': 'Messaging Settings',
    'admin.messaging_settings_description': 'Control messaging and call features for all users',
    'admin.messaging': 'Messaging',
    'admin.messaging_description': 'Allow users to send messages',
    'admin.voice_calls': 'Voice Calls',
    'admin.voice_calls_description': 'Allow users to make voice calls',
    'admin.video_calls': 'Video Calls',
    'admin.video_calls_description': 'Allow users to make video calls',
    'admin.screen_sharing': 'Screen Sharing',
    'admin.screen_sharing_description': 'Allow users to share their screen during calls',
    
    // Location Manager
    'location_mgr.title': 'Location Manager',
    'location_mgr.current_locations': 'Current Locations',
    'location_mgr.locations_in_db': '{count} locations in database',
    'location_mgr.refresh': 'Refresh',
    'location_mgr.populate_title': 'Populate Locations',
    'location_mgr.populate_desc': 'Fetch all Iraqi cities and areas from Google Places API and save them to the database. This will replace all existing locations.',
    'location_mgr.populating': 'Populating...',
    'location_mgr.populate_btn': 'Populate from Google Places',
    'location_mgr.success': 'Success',
    'location_mgr.success_msg': 'Locations populated successfully',
    'location_mgr.error': 'Error',
    'location_mgr.error_msg': 'Failed to populate locations. Check console for details.',
    
    // Map Configuration Manager
    'map_config.title': 'Map Configuration Manager',
    'map_config.subtitle': 'Manage your permanent map configurations to minimize Google Maps API usage',
    'map_config.loading': 'Loading map configurations...',
    'map_config.create': 'Create Configuration',
    'map_config.create_title': 'Create Map Configuration',
    'map_config.create_desc': 'Create a new permanent map configuration that will be used instead of calling the Google Maps API repeatedly.',
    'map_config.edit_title': 'Edit Map Configuration',
    'map_config.edit_desc': 'Update your permanent map configuration.',
    'map_config.name': 'Configuration Name',
    'map_config.name_placeholder': 'e.g., Iraq Property Map',
    'map_config.description': 'Description (Optional)',
    'map_config.description_placeholder': 'Brief description of this configuration',
    'map_config.map_data': 'Map Configuration (JSON)',
    'map_config.map_data_placeholder': 'Map configuration in JSON format',
    'map_config.cancel': 'Cancel',
    'map_config.update': 'Update Configuration',
    'map_config.no_configs': 'No Map Configurations',
    'map_config.no_configs_desc': 'Create your first map configuration to start managing your maps efficiently.',
    'map_config.create_first': 'Create First Configuration',
    'map_config.active': 'Active',
    'map_config.activate': 'Activate',
    'map_config.edit': 'Edit',
    'map_config.delete': 'Delete',
    'map_config.delete_confirm': 'Are you sure?',
    'map_config.delete_desc': 'This will permanently delete the map configuration "{name}". This action cannot be undone.',
    'map_config.created': 'Created',
    'map_config.last_updated': 'Last Updated',
    'map_config.map_center': 'Map Center',
    'map_config.zoom_level': 'Zoom Level',
    'map_config.cities': 'Cities',
    'map_config.cities_configured': '{count} cities configured',
    'map_config.invalid_json': 'Invalid JSON format in map data',
    
    // About Page
    'about.title': 'About Aqarnet',
    'about.subtitle': 'Your trusted partner in the Iraqi real estate market',
    'about.description': 'Aqarnet is Iraq\'s leading real estate platform, connecting buyers, sellers, and agents across the country. We provide comprehensive property listings, market insights, and professional tools to make your real estate journey seamless.',
    'about.mission_title': 'Our Mission',
    'about.mission_text': 'To revolutionize the Iraqi real estate market by providing transparent, accessible, and reliable property information to all.',
    'about.vision_title': 'Our Vision',
    'about.vision_text': 'To become the most trusted real estate platform in the Middle East, empowering everyone to find their perfect home.',
    'about.services_title': 'Our Services',
    'about.services_text': 'Property listings, market analysis, agent connections, property valuations, and comprehensive real estate news.',
    'about.team_title': 'Our Team',
    'about.team_text': 'A dedicated team of real estate professionals, technology experts, and customer service specialists.',
    'about.company_info': 'Company Information',
    'about.based_in': 'Based in Sweden, serving Iraq',
    
    // Contact Page
    'contact.title': 'Contact Us',
    'contact.subtitle': 'We\'re here to help. Reach out to us with any questions or concerns.',
    'contact.email_label': 'Email',
    'contact.phone_label': 'Phone',
    'contact.phone_info': 'Coming soon',
    'contact.location_label': 'Location',
    'contact.location_info': 'Sweden',
    'contact.form_title': 'Send us a message',
    'contact.form_description': 'Fill out the form below and we\'ll get back to you as soon as possible.',
    'contact.name_placeholder': 'Your name',
    'contact.email_placeholder': 'your@email.com',
    'contact.subject': 'Subject',
    'contact.subject_placeholder': 'What is this about?',
    'contact.message_placeholder': 'Your message...',
    'contact.success': 'Message sent! We\'ll get back to you soon.',
    
    // Press Page
    'press.title': 'Press & Media',
    'press.subtitle': 'Latest news and press releases from Aqarnet',
    'press.contact_title': 'Press Contact',
    'press.contact_description': 'For press inquiries, please contact us via email.',
    'press.add_release': 'Add Press Release',
    'press.edit_release': 'Edit Press Release',
    'press.new_release': 'New Press Release',
    'press.release_title': 'Title',
    'press.content': 'Content',
    'press.publish': 'Publish',
    'press.no_releases': 'No press releases yet.',
    'press.delete_confirm_title': 'Delete Press Release?',
    'press.delete_confirm_description': 'This action cannot be undone.',
    
    // Investors Page
    'investors.title': 'For Investors',
    'investors.subtitle': 'Join us in transforming the Iraqi real estate market',
    'investors.growth_title': 'Growth Potential',
    'investors.growth_text': 'Iraq\'s real estate market is experiencing rapid growth with significant investment opportunities.',
    'investors.market_title': 'Market Leader',
    'investors.market_text': 'Aqarnet is positioned as the leading digital platform in the Iraqi real estate sector.',
    'investors.vision_title': 'Regional Expansion',
    'investors.vision_text': 'Our vision extends beyond Iraq to become the leading platform in the Middle East region.',
    'investors.opportunity_title': 'Investment Opportunity',
    'investors.opportunity_text': 'Aqarnet offers a unique opportunity to invest in the digital transformation of one of the world\'s largest emerging real estate markets.',
    'investors.opportunity_text_2': 'With a growing user base and expanding services, we are well-positioned for sustainable growth and market leadership.',
    'investors.contact_title': 'Get in Touch',
    'investors.contact_description': 'Interested in investment opportunities? Contact us to learn more.',
    'investors.company': 'Company / Organization',
    'investors.company_placeholder': 'Your company name',
    'investors.message_placeholder': 'Tell us about your investment interests...',
    'investors.success': 'Thank you for your interest! We\'ll be in touch soon.',
    
    // Advertising Page
    'advertising.title': 'Advertising & Marketing',
    'advertising.subtitle': 'Reach thousands of potential buyers and renters in Iraq',
    'advertising.targeted_title': 'Targeted Reach',
    'advertising.targeted_text': 'Reach users actively looking for properties in specific areas and price ranges.',
    'advertising.audience_title': 'Quality Audience',
    'advertising.audience_text': 'Connect with serious buyers and renters who are ready to make decisions.',
    'advertising.analytics_title': 'Detailed Analytics',
    'advertising.analytics_text': 'Track your campaign performance with comprehensive analytics and insights.',
    'advertising.formats_title': 'Multiple Formats',
    'advertising.formats_text': 'Banner ads, featured listings, sponsored content, and more advertising options.',
    'advertising.contact_title': 'Start Advertising',
    'advertising.contact_description': 'Contact us to discuss advertising opportunities.',
    'advertising.company': 'Company / Organization',
    'advertising.company_placeholder': 'Your company name',
    'advertising.budget': 'Budget Range',
    'advertising.budget_placeholder': 'e.g., $500 - $1000/month',
    'advertising.message_placeholder': 'Tell us about your advertising goals...',
    'advertising.success': 'Thank you! We\'ll contact you about advertising opportunities.',
    
    // Jobs Page
    'jobs.title': 'Careers at Aqarnet',
    'jobs.subtitle': 'Join our team and help shape the future of real estate in Iraq',
    'jobs.add_job': 'Add Job',
    'jobs.edit_job': 'Edit Job',
    'jobs.new_job': 'New Job Posting',
    'jobs.job_title': 'Job Title',
    'jobs.department': 'Department',
    'jobs.location': 'Location',
    'jobs.employment_type': 'Employment Type',
    'jobs.full_time': 'Full-time',
    'jobs.part_time': 'Part-time',
    'jobs.contract': 'Contract',
    'jobs.internship': 'Internship',
    'jobs.description': 'Description',
    'jobs.requirements': 'Requirements',
    'jobs.benefits': 'Benefits',
    'jobs.publish': 'Publish',
    'jobs.no_jobs': 'No open positions at the moment.',
    'jobs.check_back': 'Check back later for new opportunities.',
    'jobs.apply_now': 'Apply Now',
    'jobs.apply_for': 'Apply for',
    'jobs.apply_description': 'Send your application via email.',
    'jobs.application_sent': 'Application sent! Good luck!',
    'jobs.delete_confirm_title': 'Delete Job Posting?',
    'jobs.delete_confirm_description': 'This action cannot be undone.',
    
    // Property Statistics Page
    'statistics.title': 'Property Statistics',
    'statistics.subtitle': 'Market insights and trends based on sold properties',
    'statistics.6_months': 'Last 6 months',
    'statistics.12_months': 'Last 12 months',
    'statistics.24_months': 'Last 24 months',
    'statistics.36_months': 'Last 36 months',
    'statistics.all_cities': 'All cities',
    'statistics.total_sold': 'Total Sold',
    'statistics.avg_price': 'Average Price',
    'statistics.avg_price_sqm': 'Avg. Price/m²',
    'statistics.price_change': 'Price Change',
    'statistics.price_trends': 'Price Trends',
    'statistics.price_trends_desc': 'Average property prices over time',
    'statistics.sales_volume': 'Sales Volume',
    'statistics.sales_volume_desc': 'Number of properties sold per month',
    'statistics.properties_sold': 'Properties sold',
    'statistics.price_by_city': 'Price per m² by City',
    'statistics.price_by_city_desc': 'Comparison of average price per square meter across cities',
    'statistics.price_per_sqm': 'Price per m²',
    'statistics.type_distribution': 'Property Type Distribution',
    'statistics.type_distribution_desc': 'Distribution of sold properties by type',
    'statistics.avg_days_market': 'Avg. Days on Market',
    'statistics.days': 'days',
    'statistics.price_ranges': 'Price Ranges Distribution',
    'statistics.price_ranges_desc': 'Number of properties sold in each price range',
    'statistics.properties': 'Properties',
    'statistics.year_over_year': 'Year-over-Year Comparison',
    'statistics.year_over_year_desc': 'Compare average prices and sales volume across years',
    'statistics.all_types': 'All property types',
    'statistics.all_rooms': 'All rooms',
    'statistics.rooms': 'rooms',
    'statistics.export': 'Export',
    'statistics.export_csv': 'Download CSV',
    'statistics.export_json': 'Download JSON',
    'statistics.share': 'Share',
    'statistics.link_copied': 'Link copied',
    'statistics.link_copied_desc': 'Statistics report link has been copied to clipboard',
    'statistics.custom_date': 'Custom date',
    'statistics.use_preset': 'Use preset',
    'statistics.start_date': 'Start date',
    'statistics.end_date': 'End date',
    'statistics.city_comparison': 'City Comparison',
    'statistics.city_comparison_desc': 'Compare real estate statistics between two cities side by side',
    'statistics.select_city_1': 'Select first city',
    'statistics.select_city_2': 'Select second city',
    'statistics.vs': 'vs',
    'statistics.select_cities_to_compare': 'Select two cities above to compare their statistics',
    'statistics.avg_size': 'Avg. Size',
    
    // Cookie Settings
    'cookie_settings.title': 'Cookie Settings',
    'cookie_settings.description': 'We use cookies to enhance your browsing experience. You can customize your cookie preferences below.',
    'cookie_settings.necessary': 'Necessary Cookies',
    'cookie_settings.necessary_desc': 'These cookies are essential for the website to function properly. They cannot be disabled.',
    'cookie_settings.functional': 'Functional Cookies',
    'cookie_settings.functional_desc': 'These cookies enable personalized features like language preferences and saved settings.',
    'cookie_settings.analytics': 'Analytics Cookies',
    'cookie_settings.analytics_desc': 'These cookies help us understand how visitors interact with our website to improve user experience.',
    'cookie_settings.marketing': 'Marketing Cookies',
    'cookie_settings.marketing_desc': 'These cookies are used to show you relevant advertisements and track the effectiveness of our campaigns.',
    'cookie_settings.accept_all': 'Accept All',
    'cookie_settings.reject_all': 'Reject All',
    'cookie_settings.save_preferences': 'Save Preferences',
    'cookie_settings.saved': 'Cookie preferences saved',
    
    // Cookie Banner
    'cookie_banner.title': 'We use cookies',
    'cookie_banner.description': 'We use cookies to enhance your browsing experience, serve personalized content, and analyze our traffic. By clicking "Accept All", you consent to our use of cookies.',
    'cookie_banner.accept_all': 'Accept All',
    'cookie_banner.reject_all': 'Reject All',
    'cookie_banner.customize': 'Customize',
    
    // Terms of Service
    'terms.title': 'Terms of Service',
    'terms.last_updated': 'Last updated:',
    'terms.section1.title': '1. Acceptance of Terms',
    'terms.section1.p1': 'By accessing and using Aqarnet, you accept and agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our services.',
    'terms.section1.p2': 'We reserve the right to modify these terms at any time. Continued use of the service after changes constitutes acceptance of the modified terms.',
    'terms.section2.title': '2. Description of Service',
    'terms.section2.p1': 'Aqarnet provides a digital platform for real estate listings, property searches, and connecting buyers with sellers and agents in Iraq.',
    'terms.section2.p2': 'Our services include property listings, search functionality, saved searches, favorites, and communication tools between users and agents.',
    'terms.section3.title': '3. User Responsibilities',
    'terms.section3.p1': 'As a user of Aqarnet, you agree to:',
    'terms.section3.li1': 'Provide accurate and truthful information in your listings and profile',
    'terms.section3.li2': 'Not use the platform for fraudulent or illegal activities',
    'terms.section3.li3': 'Respect the intellectual property rights of others',
    'terms.section3.li4': 'Not interfere with the proper functioning of the service',
    'terms.section4.title': '4. Property Listings',
    'terms.section4.p1': 'Users who list properties are responsible for the accuracy of their listings. Aqarnet does not verify the accuracy of property information.',
    'terms.section4.p2': 'We reserve the right to remove any listing that violates our terms or is reported as fraudulent.',
    'terms.section5.title': '5. Intellectual Property',
    'terms.section5.p1': 'All content on Aqarnet, including text, graphics, logos, and software, is the property of Aqarnet or its content suppliers.',
    'terms.section5.p2': 'You may not reproduce, distribute, or create derivative works from our content without express written permission.',
    'terms.section6.title': '6. Limitation of Liability',
    'terms.section6.p1': 'Aqarnet is provided "as is" without warranties of any kind. We are not liable for any damages arising from your use of our services or any transactions conducted through our platform.',
    'terms.section7.title': '7. Governing Law',
    'terms.section7.p1': 'These terms are governed by the laws of Sweden. Any disputes shall be resolved in the courts of Sweden.',
    'terms.section7.p2': 'If any provision of these terms is found to be unenforceable, the remaining provisions will continue in effect.',
    'terms.section8.title': '8. Contact Information',
    'terms.section8.p1': 'For questions about these Terms of Service, please contact us:',
    'terms.section8.email': 'Email',
    
    // Privacy Policy
    'privacy.title': 'Privacy Policy',
    'privacy.last_updated': 'Last updated:',
    'privacy.section1.title': '1. Introduction',
    'privacy.section1.p1': 'At Aqarnet, we are committed to protecting your privacy and personal data. This policy explains how we collect, use, and safeguard your information.',
    'privacy.section1.p2': 'By using our services, you consent to the data practices described in this policy.',
    'privacy.section2.title': '2. Information We Collect',
    'privacy.section2.p1': 'We collect the following types of information:',
    'privacy.section2.li1': 'Account information: name, email address, phone number',
    'privacy.section2.li2': 'Property listing data: addresses, descriptions, photos, prices',
    'privacy.section2.li3': 'Usage data: search history, saved properties, browsing behavior',
    'privacy.section2.li4': 'Device information: IP address, browser type, operating system',
    'privacy.section3.title': '3. How We Use Your Information',
    'privacy.section3.p1': 'We use your information to:',
    'privacy.section3.li1': 'Provide and improve our services',
    'privacy.section3.li2': 'Personalize your experience and recommendations',
    'privacy.section3.li3': 'Send notifications about saved searches and new listings',
    'privacy.section3.li4': 'Communicate with you about your account and inquiries',
    'privacy.section4.title': '4. Data Sharing',
    'privacy.section4.p1': 'We do not sell your personal data. We may share information with property agents when you make inquiries, and with service providers who assist our operations.',
    'privacy.section4.p2': 'We may disclose information when required by law or to protect our rights and safety.',
    'privacy.section5.title': '5. Your Rights',
    'privacy.section5.p1': 'You have the following rights regarding your data:',
    'privacy.section5.li1': 'Access: Request a copy of your personal data',
    'privacy.section5.li2': 'Correction: Update or correct inaccurate information',
    'privacy.section5.li3': 'Deletion: Request deletion of your account and data',
    'privacy.section5.li4': 'Portability: Receive your data in a portable format',
    'privacy.section5.li5': 'Objection: Opt out of certain data processing activities',
    'privacy.section6.title': '6. Data Security',
    'privacy.section6.p1': 'We implement appropriate technical and organizational measures to protect your data against unauthorized access, alteration, or destruction.',
    'privacy.section6.p2': 'However, no method of transmission over the internet is 100% secure, and we cannot guarantee absolute security.',
    'privacy.section7.title': '7. Changes to This Policy',
    'privacy.section7.p1': 'We may update this privacy policy from time to time. We will notify you of significant changes by posting a notice on our website or sending you an email.',
    'privacy.section8.title': '8. Contact Us',
    'privacy.section8.p1': 'If you have questions about this Privacy Policy or your personal data, please contact us:',
    'privacy.section8.email': 'Email',
    
    // FAQ Page
    'faq.title': 'Frequently Asked Questions',
    'faq.subtitle': 'Find answers to common questions about using Aqarnet',
    'faq.account.title': 'Account & Registration',
    'faq.account.q1': 'How do I create an account?',
    'faq.account.q1_answer': 'Click the "Login" button in the header, then select "Create Account". You can sign up with your email, Google, or Apple account.',
    'faq.account.q2': 'How do I reset my password?',
    'faq.account.q2_answer': 'On the login page, click "Forgot Password" and enter your email address. You will receive a link to reset your password.',
    'faq.account.q3': 'Can I change my email address?',
    'faq.account.q3_answer': 'Currently, you cannot change your email address directly. Please contact our support team for assistance.',
    'faq.listings.title': 'Property Listings',
    'faq.listings.q1': 'How do I list a property for sale?',
    'faq.listings.q1_answer': 'You must be registered as an agent to list properties. After logging in, click "Add Property" and fill in the required details including photos, description, and price.',
    'faq.listings.q2': 'How long does a listing stay active?',
    'faq.listings.q2_answer': 'Listings remain active until marked as sold or removed by the owner. There is no automatic expiration.',
    'faq.listings.q3': 'Can I edit my listing after posting?',
    'faq.listings.q3_answer': 'Yes, you can edit your listing at any time. Go to your profile, find the listing, and click "Edit" to make changes.',
    'faq.search.title': 'Searching Properties',
    'faq.search.q1': 'How do I save a search?',
    'faq.search.q1_answer': 'After performing a search, click "Save Search" button. You can enable email notifications to be alerted when new matching properties are listed.',
    'faq.search.q2': 'How do I add a property to favorites?',
    'faq.search.q2_answer': 'Click the heart icon on any property card to add it to your favorites. You must be logged in to save favorites.',
    'faq.search.q3': 'Can I search by map location?',
    'faq.search.q3_answer': 'Yes, use the map view on the properties page to browse listings by location. Click on markers to view property details.',
    'faq.contact.title': 'Contacting Agents',
    'faq.contact.q1': 'How do I contact a property agent?',
    'faq.contact.q1_answer': 'Click "Contact Agent" on any property listing to send a message directly to the responsible agent.',
    'faq.contact.q2': 'Can I schedule a viewing?',
    'faq.contact.q2_answer': 'Yes, click "Schedule Viewing" on a property page to request a viewing time. The agent will confirm availability.',
    'faq.security.title': 'Security & Privacy',
    'faq.security.q1': 'Is my personal information secure?',
    'faq.security.q1_answer': 'Yes, we use industry-standard encryption and security measures to protect your data. See our Privacy Policy for details.',
    'faq.security.q2': 'How can I delete my account?',
    'faq.security.q2_answer': 'Go to your profile settings and select "Delete Account". This action is permanent and cannot be undone.',
    'faq.still_questions': 'Still have questions?',
    'faq.contact_us': 'Contact our support team and we will be happy to help.',
    
    // Newsletter
    'newsletter.title': 'Newsletter Signup',
    'newsletter.description': 'Subscribe to get the latest property news and updates.',
    'newsletter.placeholder': 'Enter your email',
    'newsletter.subscribe': 'Subscribe',
    'newsletter.subscribing': 'Subscribing...',
    'newsletter.success': 'Successfully subscribed to newsletter!',
    'newsletter.subscribed': 'Thank you for subscribing!',
    'newsletter.invalid_email': 'Please enter a valid email address',
    'newsletter.error': 'Failed to subscribe',
    'newsletter.unsubscribe_note': 'You can unsubscribe at any time.',
    'newsletter.unsubscribe_title': 'Unsubscribe from Newsletter',
    'newsletter.unsubscribe_description': 'Enter your email to unsubscribe from our newsletter.',
    'newsletter.unsubscribe_button': 'Unsubscribe',
    'newsletter.unsubscribe_success': 'Successfully unsubscribed',
    'newsletter.unsubscribe_error': 'Failed to unsubscribe',
    'newsletter.unsubscribe_message': 'You will no longer receive our newsletter.',
    'newsletter.invalid_link': 'Invalid Unsubscribe Link',
    'newsletter.invalid_link_message': 'This unsubscribe link is invalid or has expired. Please use the link from your newsletter email.',
    'newsletter.unsubscribe_reason_label': 'Reason for unsubscribing (optional)',
    'newsletter.select_reason': 'Select a reason...',
    'newsletter.reason_too_many_emails': 'Too many emails',
    'newsletter.reason_not_relevant': 'Content not relevant to me',
    'newsletter.reason_never_subscribed': 'I never subscribed',
    'newsletter.reason_found_another': 'Found another service',
    'newsletter.reason_other': 'Other',
    'common.try_again': 'Try Again',
    'newsletter.admin.title': 'Newsletter Subscribers',
    'newsletter.admin.description': 'Manage your newsletter subscribers',
    'newsletter.admin.total': 'Total Subscribers',
    'newsletter.admin.active': 'Active',
    'newsletter.admin.unsubscribed': 'Unsubscribed',
    'newsletter.admin.export_csv': 'Export CSV',
    'newsletter.admin.refresh': 'Refresh',
    'newsletter.admin.subscribers_list': 'Subscribers List',
    'newsletter.admin.subscribers_description': 'All newsletter subscribers and their status',
    'newsletter.admin.no_subscribers': 'No subscribers yet',
    'newsletter.admin.subscribed_at': 'Subscribed At',
    'newsletter.admin.status': 'Status',
    'newsletter.admin.unsubscribed_at': 'Unsubscribed At',
    'newsletter.admin.actions': 'Actions',
    'newsletter.admin.deleted': 'Subscriber deleted',
    'newsletter.admin.delete_error': 'Failed to delete subscriber',
    'newsletter.admin.no_data': 'No data to export',
    'newsletter.admin.exported': 'Exported successfully',
    'newsletter.admin.delete_confirm_title': 'Delete Subscriber?',
    'newsletter.admin.delete_confirm_description': 'This will permanently delete this subscriber. This action cannot be undone.',
    
    // Footer FAQ
    'footer.faq': 'FAQ',
    
    // Broker Dashboard
    'broker.dashboard_title': 'Broker Dashboard',
    'broker.dashboard_subtitle': 'Manage your properties and client interactions',
    'broker.my_properties': 'My Properties',
    'broker.my_properties_desc': 'View and manage your property listings',
    'broker.viewings': 'Property Viewings',
    'broker.viewings_desc': 'Manage scheduled property viewings',
    'broker.contacts': 'Client Messages',
    'broker.contacts_desc': 'View and respond to client inquiries',
    'broker.analytics': 'Property Analytics',
    'broker.analytics_desc': 'Track views and engagement on your listings',
    'broker.subscription': 'Subscription',
    'broker.subscription_desc': 'Manage your plan and listing limits',
    'broker.access_denied': 'This page is only accessible to registered brokers.',
    'broker.profile_title': 'Broker Profile',
    'broker.profile_description': 'Manage your public profile and contact information',
    'broker.back_to_dashboard': 'Back to Dashboard',
    'broker.display_name': 'Display Name',
    'broker.display_name_placeholder': 'Your name as shown to clients',
    'broker.contact_email': 'Contact Email',
    'broker.email_placeholder': 'your@email.com',
    'broker.email_notification_note': 'This email will be used for notifications about viewings and client messages.',
    'broker.phone_number': 'Phone Number',
    'broker.phone_placeholder': '+964 xxx xxx xxxx',
    'broker.change_photo': 'Change Photo',
    'broker.save_profile': 'Save Profile',
    'broker.profile_saved': 'Profile saved successfully',
    'broker.profile_save_error': 'Failed to save profile',
    'broker.profile_load_error': 'Failed to load profile',
    'broker.avatar_uploaded': 'Photo uploaded successfully',
    'broker.avatar_upload_error': 'Failed to upload photo',
    'broker.invalid_image_type': 'Please select an image file',
    'broker.image_too_large': 'Image must be less than 2MB',
    'broker.notification_preferences': 'Notification Preferences',
    'broker.notification_preferences_description': 'Choose which email notifications you want to receive',
    'broker.notif_new_viewing_requests': 'New Viewing Requests',
    'broker.notif_new_viewing_requests_desc': 'Get notified when clients request property viewings',
    'broker.notif_new_client_messages': 'New Client Messages',
    'broker.notif_new_client_messages_desc': 'Get notified when clients send you messages',
    'broker.notif_property_alerts': 'Property Alerts',
    'broker.notif_property_alerts_desc': 'Receive market updates and matching property alerts',
    'broker.notif_weekly_summary': 'Weekly Summary',
    'broker.notif_weekly_summary_desc': 'Receive a weekly digest of your activity',
    'broker.listing_usage': 'Listing Usage',
    'broker.monthly_listing_limits': 'Monthly listing limits',
    'broker.listings_used': 'Listings used',
    'broker.listings_remaining': 'listings remaining',
    'broker.period_resets': 'Period resets',
    'broker.export_csv': 'Export CSV',
    'broker.total_views': 'Total Views',
    'broker.scheduled_viewings': 'Scheduled Viewings',
    'broker.client_messages': 'Client Messages',
    'broker.filters': 'Filters',
    'broker.all_time': 'All Time',
    'broker.active_listings': 'Active Listings',
    'broker.sold_listings': 'Sold Listings',
    'broker.active_listings_desc': 'Your currently published properties',
    'broker.no_active_listings': 'No active listings yet. Add your first property to get started.',
    'broker.all_types': 'All Types',
    'broker.all_cities': 'All Cities',
    'broker.property_type': 'Property Type',
    'broker.city': 'City',
    'broker.date_range': 'Date Range',
    'broker.last_week': 'Last Week',
    'broker.last_month': 'Last Month',
    'broker.last_quarter': 'Last 3 Months',
    'broker.sold_listings_desc': 'Properties you have sold',
    'broker.no_sold_listings': 'No sold properties yet',
    'broker.clear_filters': 'Clear Filters',
    'broker.selected': 'selected',
    'broker.compare': 'Compare',
    'broker.mark_as_sold': 'Mark as Sold',
    'broker.delete_selected': 'Delete Selected',
    'broker.select_all': 'Select All',
    'broker.rejected_listings': 'Rejected Listings',
    'broker.rejected_listings_desc': 'These listings were rejected by moderators. You can submit an appeal to request reconsideration.',
    'broker.no_rejected_listings': 'No rejected listings',
    'broker.confirm_delete': 'Confirm Delete',
    'broker_public.not_found': 'Broker Not Found',
    'broker_public.not_found_description': 'The broker profile you are looking for does not exist.',
    'broker_public.back_to_search': 'Back to Agent Search',
    'broker_public.contact_broker': 'Contact Broker',
    'broker_public.agency': 'Agency',
    'broker_public.visit_website': 'Visit Website',
    'broker_public.active_listings': 'Active Listings',
    'broker_public.sold_properties': 'Sold Properties',
    'broker_public.total_sales': 'Total Sales',
    'broker_public.avg_days_market': 'Avg Days on Market',
    'broker_public.no_active_listings': 'No active listings at the moment',
    'broker_public.no_sold_properties': 'No sold properties yet',
    'broker_public.view_all_properties': 'View All Properties',
    'broker_public.send_message': 'Send Message',
    'broker_public.view_agency': 'View Agency',
    'broker_public.works_at': 'Works at',
    'broker_public.listings': 'Listings',
    'broker_public.sold': 'Sold',
    'broker_public.reviews': 'Reviews',
    'broker_public.rating': 'Rating',
    'broker_public.featured_broker': 'Featured Broker',
    'broker_public.top_performer': 'Top Performer',
    'agents.verified_agent': 'Verified Agent',
    
    // Agency Detail
    'agency_detail.brokers': 'Brokers',
    'agency_detail.listings': 'Listings',
    'agency_detail.sold': 'Sold',
    'agency_detail.reviews': 'Reviews',
    'agency_detail.rating': 'Rating',
    'agency_detail.active_listings': 'Active Listings',
    'agency_detail.no_active_listings': 'No active listings at the moment',
    'agency_detail.no_sold_properties': 'No sold properties yet',
    'agency_detail.view_profile': 'View Profile',
    'agency_detail.message': 'Message',
    'agency_detail.featured_agency': 'Featured Agency',
    'agency_detail.premium_agency': 'Premium Agency',
    'agency_detail.verified_agency': 'Verified Agency',
    
    // Reviews
    'reviews.write_review': 'Write a Review',
    'reviews.write_agency_review': 'Review this Agency',
    'reviews.write_broker_review': 'Review this Broker',
    'reviews.select_rating': 'Please select a rating',
    'reviews.submitted': 'Review submitted successfully',
    'reviews.submit_error': 'Failed to submit review',
    'reviews.comment_placeholder': 'Share your experience... (optional)',
    'reviews.submitting': 'Submitting...',
    'reviews.submit': 'Submit Review',
    'reviews.all_reviews': 'All Reviews',
    'reviews.no_reviews': 'No reviews yet. Be the first to leave a review!',
    'reviews.login_to_review': 'Log in to leave a review',
    'reviews.cannot_review_self': 'You cannot review yourself',
    'common.login_required': 'Please log in to access this page.',
    
    // Agency Dashboard
    'agency.my_agency': 'My Agency',
    'agency.dashboard_title': 'Agency Dashboard',
    'agency.dashboard_subtitle': 'Manage your brokers and agency settings',
    'agency.brokers_tab': 'Brokers',
    'agency.invites_tab': 'Invites',
    'agency.settings_tab': 'Settings',
    'agency.brokers_title': 'Agency Brokers',
    'agency.brokers_description': 'Manage brokers in your agency',
    'agency.create_broker': 'Create Broker',
    'agency.create_broker_title': 'Create New Broker Account',
    'agency.create_broker_description': 'Create a new broker account directly linked to your agency',
    'agency.broker_name': 'Full Name',
    'agency.broker_name_placeholder': 'Enter broker name',
    'agency.broker_email': 'Email Address',
    'agency.broker_email_placeholder': 'broker@example.com',
    'agency.broker_password': 'Password',
    'agency.broker_password_placeholder': 'Create a password',
    'agency.create_broker_btn': 'Create Broker Account',
    'agency.broker_created': 'Broker account created successfully',
    'agency.broker_create_error': 'Failed to create broker account',
    'agency.no_brokers': 'No brokers yet',
    'agency.no_brokers_description': 'Create broker accounts or send invites to add brokers to your agency',
    'agency.broker_column': 'Broker',
    'agency.email_column': 'Email',
    'agency.phone_column': 'Phone',
    'agency.actions_column': 'Actions',
    'agency.unnamed_broker': 'Unnamed Broker',
    'agency.broker_removed': 'Broker removed from agency',
    'agency.broker_remove_error': 'Failed to remove broker',
    'agency.invites_title': 'Pending Invites',
    'agency.invites_description': 'Manage invitations sent to potential brokers',
    'agency.send_invite': 'Send Invite',
    'agency.send_invite_title': 'Invite a Broker',
    'agency.send_invite_description': 'Send an invitation to join your agency',
    'agency.invite_email': 'Email Address',
    'agency.invite_email_placeholder': 'broker@example.com',
    'agency.send_invite_btn': 'Send Invitation',
    'agency.invite_sent': 'Invitation sent successfully',
    'agency.invite_error': 'Failed to send invitation',
    'agency.link_copied': 'Invite link copied to clipboard',
    'agency.no_invites': 'No pending invites',
    'agency.status_column': 'Status',
    'agency.sent_column': 'Sent',
    'agency.expires_column': 'Expires',
    'agency.status_pending': 'Pending',
    'agency.status_accepted': 'Accepted',
    'agency.status_expired': 'Expired',
    'agency.settings_title': 'Agency Settings',
    'agency.settings_description': 'Update your agency information',
    'agency.agency_name': 'Agency Name',
    'agency.agency_name_placeholder': 'Your agency name',
    'agency.agency_description': 'Description',
    'agency.agency_description_placeholder': 'Brief description of your agency',
    'agency.agency_phone': 'Phone',
    'agency.agency_phone_placeholder': '+964 xxx xxx xxxx',
    'agency.agency_email': 'Email',
    'agency.agency_email_placeholder': 'contact@agency.com',
    'agency.agency_website': 'Website',
    'agency.settings_saved': 'Agency settings saved',
    'agency.settings_save_error': 'Failed to save settings',
    'agency.load_error': 'Failed to load agency data',
    'agency.default_name': 'My Agency',
    'agency.notifications_tab': 'Notifications',
    'agency.notifications_title': 'Email Notifications',
    'agency.notifications_description': 'Control which notifications you receive about broker activity',
    'agency.notifications_saved': 'Notification preferences saved',
    'agency.notifications_save_error': 'Failed to save preferences',
    'agency.notif_new_broker_joins': 'New Broker Joins',
    'agency.notif_new_broker_joins_desc': 'Get notified when a broker accepts your invitation',
    'agency.notif_broker_property_added': 'Broker Adds Property',
    'agency.notif_broker_property_added_desc': 'Get notified when brokers add new property listings',
    'agency.notif_broker_viewing_requests': 'Viewing Requests',
    'agency.notif_broker_viewing_requests_desc': 'Get notified about viewing requests for broker properties',
    'agency.notif_broker_client_messages': 'Client Messages',
    'agency.notif_broker_client_messages_desc': 'Get notified when clients contact your brokers',
    'agency.notif_weekly_summary': 'Weekly Summary',
    'agency.notif_weekly_summary_desc': 'Receive a weekly summary of agency activity',
    
    // Agency Analytics
    'agency.analytics_tab': 'Analytics',
    'agency.no_brokers_analytics': 'Add brokers to see performance analytics',
    'agency.total_properties': 'Total Properties',
    'agency.total_views': 'Total Views',
    'agency.total_viewings': 'Viewing Requests',
    'agency.total_sold': 'Properties Sold',
    'agency.total_sales_value': 'Total Sales Value',
    'agency.broker_performance_chart': 'Broker Performance',
    'agency.broker_performance_chart_desc': 'Compare broker metrics at a glance',
    'agency.individual_broker_stats': 'Individual Broker Statistics',
    'agency.individual_broker_stats_desc': 'Detailed metrics for each broker',
    'agency.properties': 'Properties',
    'agency.views': 'Views',
    'agency.viewings': 'Viewings',
    'agency.contacts': 'Contacts',
    'agency.active': 'Active',
    'agency.sold': 'Sold',
    'agency.sales_value': 'Sales Value',
    'agency.upgrade_plan': 'Upgrade Plan',
    'agency.view_plans': 'View Plans',
    'agency.billing_tab': 'Billing',
    'agency.name_label': 'Agency Name',
    'agency.email_label': 'Email',
    'agency.phone_label': 'Phone',
    'agency.website_label': 'Website',
    'agency.address_label': 'Address',
    'agency.city_label': 'City',
    'agency.description_label': 'Description',
    'agency.description_placeholder': 'Tell clients about your agency...',
    'agency.save_settings': 'Save Settings',
    'agency.broker_usage': 'Broker Usage',
    'agency.invite_broker': 'Invite Broker',
    'agency.brokers': 'brokers',
    'agency.broker_limit_reached': 'Broker limit reached',
    'agency.upgrade_for_more': 'Upgrade your plan to add more brokers',
    'agency.active_listings': 'Active Listings',
    'agency.broker_slots_remaining': 'Slots remaining',
    'agency.manage_subscription': 'Manage Subscription',
    'agency.upgrade_your_plan': 'Upgrade Your Plan',
    'agency.choose_plan': 'Choose the plan that best fits your agency',
    'agency.pro_plan': 'Pro',
    'agency.enterprise_plan': 'Enterprise',
    'agency.for_growing_agencies': 'For growing agencies',
    'agency.for_large_agencies': 'For large agencies',
    'agency.per_month': '/month',
    'agency.up_to_brokers': 'Up to {count} brokers',
    'agency.unlimited_brokers': 'Unlimited brokers',
    'agency.advanced_analytics': 'Advanced analytics',
    'agency.full_analytics': 'Full analytics suite',
    'agency.priority_support': 'Priority support',
    'agency.dedicated_support': 'Dedicated support',
    'agency.upgrade_to_pro': 'Upgrade to Pro',
    'agency.upgrade_to_enterprise': 'Upgrade to Enterprise',
    'agency.popular': 'Popular',
    'agency.current_plan': 'Current Plan',
    'agency.plan_features': 'Plan Features',
    'agency.billing_history': 'Billing History',
    'agency.no_billing_history': 'No billing history yet',
    'agency.subscription_billing': 'Subscription & Billing',
    'agency.manage_agency_subscription': 'Manage your agency subscription',
    'agency.plan': 'Plan',
    'agency.basic_plan': 'Basic',
    'agency.broker_limit': 'Broker Limit',
    'agency.current_usage': 'Current Usage',
    'agency.status': 'Status',
    'agency.active_status': 'Active',
    'agency.unlimited': 'Unlimited',
    'agency.feature': 'Feature',
    'agency.price': 'Price',
    'agency.free': 'Free',
    'agency.analytics': 'Analytics',
    'agency.basic_analytics': 'Basic',
    'agency.advanced': 'Advanced',
    'agency.full_suite': 'Full Suite',
    'agency.support': 'Support',
    'agency.email_support': 'Email',
    'agency.priority': 'Priority',
    'agency.dedicated': 'Dedicated',
    
    // Agency Directory
    'agency_directory.title': 'Real Estate Agencies',
    'agency_directory.subtitle': 'Browse and find trusted real estate agencies',
    'agency_directory.search_placeholder': 'Search agencies...',
    'agency_directory.location_placeholder': 'Filter by location...',
    'agency_directory.agencies_found': 'agencies found',
    'agency_directory.no_agencies': 'No agencies found',
    'agency_directory.no_agencies_description': 'Try adjusting your search criteria',
    'agency_directory.brokers': 'brokers',
    'agency_directory.view_agency': 'View Agency',
    'agency_directory.not_found': 'Agency not found',
    'agency_directory.back_to_directory': 'Back to Directory',
    'agency_directory.visit_website': 'Visit Website',
    'agency_directory.our_brokers': 'Our Brokers',
    'agency_directory.no_brokers_yet': 'No brokers in this agency yet',
    'agency_directory.agency_listings': 'Property Listings',
    
    // Agency Invite Accept
    'agency_invite.title': 'Join Agency',
    'agency_invite.description': 'You have been invited to join {agencyName}',
    'agency_invite.joining': 'You are joining',
    'agency_invite.logged_in_as': 'Logged in as',
    'agency_invite.accept_btn': 'Accept Invitation',
    'agency_invite.accepted_success': 'Successfully joined the agency!',
    'agency_invite.accept_error': 'Failed to accept invitation',
    'agency_invite.create_account': 'Create an Account',
    'agency_invite.create_account_description': 'Create an account to accept this invitation',
    'agency_invite.your_name': 'Your Name',
    'agency_invite.name_placeholder': 'Enter your full name',
    'agency_invite.email_placeholder': 'your@email.com',
    'agency_invite.password': 'Password',
    'agency_invite.password_placeholder': 'Create a password',
    'agency_invite.confirm_password': 'Confirm Password',
    'agency_invite.confirm_password_placeholder': 'Confirm your password',
    'agency_invite.register_accept_btn': 'Create Account & Join',
    'agency_invite.registered_success': 'Account created and joined agency!',
    'agency_invite.register_error': 'Failed to create account',
    'agency_invite.passwords_mismatch': 'Passwords do not match',
    'agency_invite.password_too_short': 'Password must be at least 6 characters',
    'agency_invite.already_have_account': 'Already have an account?',
    'agency_invite.login_here': 'Log in here',
    'agency_invite.invalid_link': 'Invalid invitation link',
    'agency_invite.not_found': 'Invitation not found',
    'agency_invite.expired': 'This invitation has expired',
    'agency_invite.already_accepted': 'This invitation has already been accepted',
    'agency_invite.fetch_error': 'Failed to load invitation',
    'agency_invite.error_title': 'Invalid Invitation',
    'agency_invite.back_home': 'Back to Home',
    
    // User Listings
    'userListings.title': 'My Listings',
    'userListings.subtitle': 'Manage your property listings and create new ones',
    'userListings.pricingTitle': 'Listing Pricing',
    'userListings.pricingDesc': 'Create your property listing with our simple pricing',
    'userListings.listingPrice': '$10 per listing',
    'userListings.listingDuration': 'Valid for 1 month',
    'userListings.videoPrice': '+$3 for video',
    'userListings.videoDesc': 'Add a video tour to your listing',
    'userListings.includeVideo': 'Include video tour',
    'userListings.createListing': 'Create Listing',
    'userListings.myListingsTitle': 'Your Properties',
    'userListings.noListings': 'No listings yet',
    'userListings.noListingsDesc': 'Create your first property listing to get started',
    'userListings.createFirst': 'Create Your First Listing',
    'userListings.hasVideo': 'Video',
    'userListings.renew': 'Renew',
    'userListings.renewSuccess': 'Listing renewed!',
    'userListings.renewSuccessDesc': 'Your listing has been renewed for another month',
    'userListings.paymentSuccess': 'Payment successful!',
    'userListings.paymentSuccessDesc': 'Your listing is now live',
    'userListings.paymentCancelled': 'Payment cancelled',
    'userListings.paymentCancelledDesc': 'Your payment was cancelled',
    'userListings.checkoutError': 'Failed to create checkout',
    'userListings.renewError': 'Failed to renew listing',
    'userListings.createDraft': 'Create Listing',
    'userListings.previewListing': 'Preview Your Listing',
    'userListings.draft': 'Draft',
    'userListings.drafts': 'Drafts',
    'userListings.draftSaved': 'Draft saved',
    'userListings.draftSavedDesc': 'Your listing has been saved as a draft',
    'userListings.draftError': 'Failed to save draft',
    'userListings.draftDeleted': 'Draft deleted',
    'userListings.deleteError': 'Failed to delete',
    'userListings.saveDraft': 'Save Draft',
    'userListings.saveAndPreview': 'Save & Preview',
    'userListings.publishOptions': 'Publish Options',
    'userListings.addVideoTour': 'Add Video Tour',
    'userListings.videoIncluded': 'Video Tour Included',
    'userListings.totalPrice': 'Total',
    'userListings.backToEdit': 'Back to Edit',
    'userListings.publishNow': 'Publish Now',
    'userListings.publish': 'Publish',
    'userListings.publishSuccess': 'Listing published!',
    'userListings.publishSuccessDesc': 'Your listing is now pending review',
    'userListings.firstListingFree': 'First Listing is Free!',
    'userListings.firstListingFreeDesc': 'Your first property listing is completely free. Submit now and reach buyers across Iraq!',
    'userListings.pending': 'Pending Review',
    'userListings.pendingInfo': 'These listings are being reviewed by our moderation team. You will be notified once they are approved.',
    'userListings.pendingReview': 'Pending Review',
    'userListings.awaitingApproval': 'Awaiting moderator approval',
    'userListings.rejected': 'Rejected Listings',
    'userListings.rejectedInfo': 'These listings were not approved. Please edit and fix the issues before resubmitting for review.',
    'userListings.rejectedBadge': 'Rejected',
    'userListings.live': 'Live',
    'userListings.editDraft': 'Edit',
    'userListings.editAndResubmit': 'Edit & Resubmit',
    'userListings.pendingCannotEdit': 'Cannot edit while pending review',
    'userListings.resubmitForReview': 'Resubmit for Review',
    'userListings.resubmitInfo': 'Your changes have been saved. Submit your listing again for review at no additional cost.',
    'userListings.submitForReview': 'Submit for Review',
    'userListings.validation.priceRequired': 'Price is required',
    'userListings.validation.cityRequired': 'City is required',
    'userListings.validation.roomsRequired': 'Rooms is required',
    'userListings.validation.sizeRequired': 'Size is required',
    'userListings.validation.plotSizeRequired': 'Plot size is required',
    'userListings.validation.imagesRequired': 'At least one image is required',
    'userListings.choosePlan': 'Choose Your Listing Plan',
    'userListings.choosePlanDesc': 'Select how many images to display on your listing card',
    'userListings.upgradePlan': 'Upgrade Your Listing Plan',
    'userListings.upgradePlanDesc': 'Upgrade to show more images. You only pay the difference.',
    'userListings.selectImages': 'Select Images to Display',
    'userListings.selectImagesDesc': 'Choose images to show on your listing card',
    'userListings.selectPrimaryImage': 'Select Primary Image',
    'userListings.selectPrimaryImageDesc': 'Choose the main image to display first on your listing',
    'userListings.addVideo': 'Add Video Tour',
    'userListings.pleaseSelectImages': 'Please select images to continue',
    'userListings.pleaseSelectPrimaryImage': 'Please select a primary image to continue',
    'userListings.proceedToPayment': 'Proceed to Payment',
    'userListings.tierBasic': 'Basic',
    'userListings.tierStandard': 'Standard',
    'userListings.tierPremium': 'Premium',
    'userListings.tierBasicDesc': '1 image on card',
    'userListings.tierStandardDesc': '3 images carousel',
    'userListings.tierPremiumDesc': 'All images + badge',
    'userListings.tierDuration': '30 days active',
    'userListings.tierBest': 'Best',
    'userListings.tierBasicFull': 'Basic Tier (1 image)',
    'userListings.tierStandardFull': 'Standard Tier (3 images)',
    'userListings.tierPremiumFull': 'Premium Tier (All images)',
    'userListings.changeTier': 'Change Tier',
    'userListings.selectImageTier': 'Select Image Tier',
    'userListings.selectTierFirst': 'Select a tier before publishing',
    'userListings.upgrade': 'Upgrade',
    'common.total': 'Total',
    
    // Settings Page
    'settings.title': 'Settings',
    'settings.subtitle': 'Manage your account settings and preferences',
    'settings.profile': 'Profile',
    'settings.notifications': 'Notifications',
    'settings.account': 'Account',
    'settings.profile_info': 'Profile Information',
    'settings.profile_desc': 'Update your personal information',
    'settings.change_photo': 'Change Photo',
    'settings.display_name': 'Display Name',
    'settings.display_name_placeholder': 'Your name',
    'settings.email': 'Email',
    'settings.email_placeholder': 'your@email.com',
    'settings.phone': 'Phone',
    'settings.phone_placeholder': '+1 234 567 890',
    'settings.save': 'Save Changes',
    'settings.saved': 'Settings saved successfully',
    'settings.load_error': 'Failed to load settings',
    'settings.save_error': 'Failed to save settings',
    'settings.invalid_image': 'Please select an image file',
    'settings.image_too_large': 'Image must be less than 2MB',
    'settings.avatar_uploaded': 'Photo uploaded',
    'settings.avatar_error': 'Failed to upload photo',
    'settings.notification_preferences': 'Notification Preferences',
    'settings.notification_desc': 'Choose what notifications you want to receive',
    'settings.notif_property_alerts': 'Property Alerts',
    'settings.notif_property_alerts_desc': 'Get notified about new properties',
    'settings.notif_saved_search': 'Saved Search Alerts',
    'settings.notif_saved_search_desc': 'Get notified when new properties match your saved searches',
    'settings.notif_newsletter': 'Newsletter',
    'settings.notif_newsletter_desc': 'Receive our newsletter with market updates',
    'settings.notif_viewing': 'Viewing Confirmations',
    'settings.notif_viewing_desc': 'Get confirmations for scheduled viewings',
    'settings.account_settings': 'Account Settings',
    'settings.account_desc': 'Manage your account security and preferences',
    'settings.change_password': 'Change Password',
    'settings.change_password_desc': 'Update your password',
    'settings.change': 'Change',
    'settings.two_factor': 'Two-Factor Authentication',
    'settings.two_factor_desc': 'Add extra security to your account',
    'settings.enable': 'Enable',
    'settings.delete_account': 'Delete Account',
    'settings.delete_account_desc': 'Permanently delete your account',
    'settings.delete': 'Delete',
    'settings.logout': 'Sign Out',
    
    // Common
    'common.back': 'Back',
    
    // Contact Form Modal
    'contact_form.title': 'Contact',
    'contact_form.description': 'Send a message to get in touch.',
    'contact_form.name': 'Name',
    'contact_form.name_placeholder': 'Your name',
    'contact_form.email': 'Email',
    'contact_form.email_placeholder': 'your@email.com',
    'contact_form.phone': 'Phone',
    'contact_form.phone_placeholder': 'Your phone number',
    'contact_form.message': 'Message',
    'contact_form.message_placeholder': 'How can we help you?',
    'contact_form.send': 'Send Message',
    'contact_form.sending': 'Sending...',
    'contact_form.success_title': 'Message Sent!',
    'contact_form.success_message': 'Your message has been sent successfully. You will receive a confirmation email shortly.',
    'contact_form.error_title': 'Error',
    'contact_form.error_message': 'Failed to send message. Please try again.',
    
    // Messaging
    'messaging.title': 'Messages',
    'messaging.description': 'Your private conversations',
    'messaging.new_message': 'New message',
    'messaging.no_messages': 'No messages yet',
    'messaging.no_conversations': 'No conversations yet',
    'messaging.send': 'Send',
    'messaging.send_message': 'Send Message',
    'messaging.message_to': 'Send a message to',
    'messaging.subject': 'Subject',
    'messaging.subject_placeholder': 'Subject (optional)',
    'messaging.message': 'Message',
    'messaging.message_placeholder': 'Write your message...',
    'messaging.login_required': 'You need to be logged in to send messages.',
    'messaging.message_sent': 'Message sent successfully',
    'messaging.contact_agent_description': 'Contact the property agent directly',
    'messaging.type_message': 'Type a message...',
    'messaging.inbox': 'Inbox',
    'messaging.sent': 'Sent',
    'messaging.search': 'Search conversations...',
    'messaging.select_conversation': 'Select a conversation',
    'messaging.select_description': 'Choose a conversation from the list to start messaging',
    'messaging.encrypted': 'End-to-end encrypted',
    'messaging.decrypt': 'Decrypt message',
    'messaging.reply': 'Reply',
    'messaging.replying_to': 'Replying to',
    'messaging.replying_to_yourself': 'yourself',
    'messaging.forward': 'Forward',
    'messaging.delete': 'Delete',
    'messaging.delete_confirm': 'Delete Message',
    'messaging.delete_description': 'This message will be deleted for everyone. This action cannot be undone.',
    'messaging.delete_for_everyone': 'Delete for everyone',
    'messaging.pin': 'Pin',
    'messaging.unpin': 'Unpin',
    'messaging.pinned_messages': 'Pinned Messages',
    'messaging.no_pinned': 'No pinned messages',
    'messaging.delivered': 'Delivered',
    'messaging.read': 'Read',
    'messaging.seen_at': 'Seen at',
    'messaging.voice_message': 'Voice message',
    'messaging.search_messages': 'Search in messages...',
    'messaging.contacts': 'Contacts',
    'messaging.results_found': 'results found',
    'messaging.recording': 'Recording...',
    'messaging.typing': 'typing...',
    'messaging.online': 'online',
    'messaging.tap_for_info': 'tap here for contact info',
    'messaging.photos': 'Photos',
    'messaging.camera': 'Camera',
    'messaging.location': 'Location',
    'messaging.contact': 'Contact',
    'messaging.document': 'Document',
    'messaging.poll': 'Poll',
    'messaging.event': 'Event',
    'messaging.location_coming_soon': 'Location sharing coming soon',
    'messaging.contact_coming_soon': 'Contact sharing coming soon',
    'messaging.poll_coming_soon': 'Poll creation coming soon',
    'messaging.event_coming_soon': 'Event creation coming soon',
    'messaging.schedule_event': 'Schedule Event',
    'messaging.event_title': 'Event Title',
    'messaging.event_title_placeholder': 'e.g., Property Viewing',
    'messaging.event_date': 'Date',
    'messaging.event_time': 'Time',
    'messaging.event_location': 'Location',
    'messaging.event_location_placeholder': 'Enter location or address',
    'messaging.event_description': 'Description',
    'messaging.event_description_placeholder': 'Add any additional details...',
    'messaging.send_event': 'Send Event',
    'messaging.event_created': 'Event created',
    'messaging.create_poll': 'Create Poll',
    'messaging.poll_question': 'Question',
    'messaging.poll_question_placeholder': 'What would you like to ask?',
    'messaging.poll_options': 'Options',
    'messaging.option': 'Option',
    'messaging.add_option': 'Add Option',
    'messaging.allow_multiple': 'Allow multiple answers',
    'messaging.send_poll': 'Send Poll',
    'messaging.poll_created': 'Poll created',
    'messaging.edit': 'Edit',
    'messaging.edit_message': 'Edit Message',
    'messaging.edit_description': 'Make changes to your message',
    'messaging.edit_time_remaining': 'You have',
    'messaging.minutes_to_edit': 'minutes to edit this message',
    'messaging.message_edited': 'Message edited',
    'messaging.edit_failed': 'Failed to edit message',
    'messaging.from_date': 'From',
    'messaging.to_date': 'To',
    'messaging.clear_filters': 'Clear filters',
    'messaging.archive': 'Archive',
    'messaging.unarchive': 'Restore',
    'messaging.show_archived': 'Archived',
    'messaging.show_active': 'Show Active',
    'messaging.conversation_archived': 'Conversation archived',
    'messaging.conversation_unarchived': 'Conversation restored',
    'messaging.archive_failed': 'Failed to archive conversation',
    'messaging.unarchive_failed': 'Failed to restore conversation',
    'messaging.conversation_pinned': 'Conversation pinned',
    'messaging.conversation_unpinned': 'Conversation unpinned',
    'messaging.pin_chat': 'Pin chat',
    'messaging.unpin_chat': 'Unpin chat',
    'messaging.pin_failed': 'Failed to pin conversation',
    'messaging.unpin_failed': 'Failed to unpin conversation',
    'messaging.schedule_message': 'Schedule Message',
    'messaging.schedule_description': 'Choose when to send this message.',
    'messaging.select_date': 'Select Date',
    'messaging.select_time': 'Select Time',
    'messaging.pick_date': 'Pick a date',
    'messaging.will_be_sent': 'Message will be sent',
    'messaging.at': 'at',
    'messaging.schedule': 'Schedule',
    'messaging.message_scheduled': 'Message scheduled',
    'messaging.schedule_failed': 'Failed to schedule message',
    'messaging.manage_labels': 'Manage Labels',
    'messaging.labels_description': 'Add labels to organize your conversation with',
    'messaging.no_labels': 'No labels yet. Create one to get started.',
    'messaging.new_label': 'New Label',
    'messaging.label_name': 'Label Name',
    'messaging.enter_label_name': 'Enter label name',
    'messaging.label_color': 'Color',
    'messaging.label_icon': 'Icon',
    'messaging.label_created': 'Label created',
    'messaging.label_create_failed': 'Failed to create label',
    'messaging.label_deleted': 'Label deleted',
    'messaging.label_delete_failed': 'Failed to delete label',
    'messaging.export_chat': 'Export Chat',
    'messaging.export_description': 'Export your conversation history',
    'messaging.export_text': 'Text File (.txt)',
    'messaging.export_text_desc': 'Simple text format, easy to read and share',
    'messaging.export_pdf': 'PDF Document',
    'messaging.export_pdf_desc': 'Formatted document, ideal for printing',
    'messaging.export_messages_count': 'messages will be exported',
    'messaging.exporting': 'Exporting...',
    'messaging.export': 'Export',
    'messaging.export_success': 'Chat exported successfully',
    'messaging.export_failed': 'Failed to export chat',
    'messaging.is_typing': 'is typing...',
    
    // Status
    'status.online': 'Online',
    'status.offline': 'Offline',
    'status.last_seen': 'Last seen',
    
    // Quick Reply Templates
    'quick_reply.title': 'Quick Replies',
    'quick_reply.greeting': 'Hello! Thank you for reaching out.',
    'quick_reply.interested': 'I am interested in this property. Can you provide more details?',
    'quick_reply.schedule_viewing': 'I would like to schedule a viewing. What times are available?',
    'quick_reply.ask_price': 'Is the price negotiable?',
    'quick_reply.ask_availability': 'Is this property still available?',
    'quick_reply.thank_you': 'Thank you for the information. I will get back to you soon.',
    'quick_reply.will_contact': 'I will contact you shortly with more details.',
    'quick_reply.not_interested': 'Thank you, but I am no longer interested in this property.',
    
    // Notifications
    'notifications.enable': 'Enable notifications',
    'notifications.on': 'Notifications on',
    'notifications.preferences': 'Notification Preferences',
    'notifications.sound': 'Notification Sound',
    'notifications.sound_desc': 'Play a sound when you receive a message',
    'notifications.sound_default': 'Default',
    'notifications.sound_chime': 'Chime',
    'notifications.sound_bell': 'Bell',
    'notifications.sound_pop': 'Pop',
    'notifications.test': 'Test',
    'notifications.vibration': 'Vibration',
    'notifications.vibration_desc': 'Vibrate when you receive a message (mobile only)',
    
    // Messaging extras
    'messaging.you': 'You',
    'messaging.encrypted_message': 'Encrypted message',
    'messaging.unknown_user': 'Unknown User',
    'messaging.no_groups': 'No groups yet',
    'messaging.create_group': 'Create Group',
    'messaging.group_chat': 'Group chat',
    'messaging.direct_messages': 'Direct',
    'messaging.groups': 'Groups',
    'messaging.group_name': 'Group Name',
    'messaging.group_description': 'Description',
    'messaging.group_settings': 'Group Settings',
    'messaging.add_members': 'Add Members',
    'messaging.remove_member': 'Remove Member',
    'messaging.leave_group': 'Leave Group',
    'messaging.admin_only': 'Admin Only',
    'messaging.group_created': 'Group created successfully',
    'messaging.group_updated': 'Group updated successfully',
    'messaging.member_added': 'Member added successfully',
    'messaging.member_removed': 'Member removed',
    'messaging.left_group': 'You left the group',
    'messaging.e2e_notice': 'Messages and calls are end-to-end encrypted. Only people in this chat can read, listen to, or share them.',
    'messaging.today': 'Today',
    'messaging.yesterday': 'Yesterday',
    'messaging.search_groups': 'Search groups...',
    
    // Attachment viewer
    'attachment.all_media': 'All media',
    'attachment.go_to_message': 'Go to message',
    'attachment.download': 'Download',
    'attachment.view_fullscreen': 'View fullscreen',
    'attachment.save_to_gallery': 'Save to gallery',
    'attachment.share': 'Share',
    'attachment.double_tap_zoom': 'Double-tap to zoom',
    'attachment.zoom_in': 'Zoom in',
    'attachment.zoom_out': 'Zoom out',
    'attachment.reset_zoom': 'Reset zoom',
    'attachment.ctrl_scroll_zoom': 'Ctrl + Scroll to zoom',
    'attachment.fit_width': 'Fit to width',
    'attachment.fit_height': 'Fit to height',
    'attachment.click_to_navigate': 'Click to navigate',
    'attachment.brightness': 'Brightness',
    'attachment.contrast': 'Contrast',
    'attachment.saturation': 'Saturation',
    'attachment.filters': 'Filters',
    'attachment.save_preset': 'Save preset',
    'attachment.view_presets': 'View presets',
    'attachment.preset_saved': 'View preset saved',
    'attachment.preset_applied': 'Preset applied',
    'attachment.all_media_gallery': 'All Media',
    'attachment.images': 'Images',
    'attachment.videos': 'Videos',
    'attachment.audio': 'Audio',
    'attachment.documents': 'Files',
    'attachment.no_images': 'No images',
    'attachment.no_videos': 'No videos',
    'attachment.no_audio': 'No audio files',
    'attachment.no_documents': 'No documents',
    'attachment.swipe_fit_modes': 'Swipe to change fit mode',
    
    // Voice/Video Calling
    'calling.voice_call': 'Voice Call',
    'calling.video_call': 'Video Call',
    'calling.audio_call': 'Audio Call',
    'calling.incoming': 'Incoming',
    'calling.calling': 'Calling',
    'calling.incoming_call': 'Incoming Call',
    'calling.incoming_video_call': 'Incoming Video Call',
    'calling.outgoing_call': 'Calling...',
    'calling.connecting': 'Connecting...',
    'calling.connected': 'Connected',
    'calling.call_ended': 'Call Ended',
    'calling.accept': 'Accept',
    'calling.decline': 'Decline',
    'calling.end_call': 'End Call',
    'calling.mute': 'Mute',
    'calling.unmute': 'Unmute',
    'calling.camera_on': 'Camera On',
    'calling.camera_off': 'Camera Off',
    'calling.call_rejected': 'Call was declined',
    'calling.call_failed': 'Call failed to connect',
    'calling.no_answer': 'No answer',
    'calling.share_screen': 'Share Screen',
    'calling.stop_sharing': 'Stop Sharing',
    'calling.sharing': 'Sharing',
    'calling.missed': 'Missed',
    'calling.answered': 'Answered',
    'calling.declined': 'Declined',
    'calling.ended': 'Ended',
    'calling.no_history': 'No call history yet',
    'calling.missed_calls': 'missed calls',
    
    // Blocking
    'blocking.block_user': 'Block User',
    'blocking.block_confirm': 'Are you sure you want to block {name}? They will not be able to send you messages.',
    'blocking.reason_optional': 'Reason (optional)',
    'blocking.reason_placeholder': 'Why are you blocking this user?',
    'blocking.block': 'Block',
    'blocking.user_blocked': 'User blocked successfully',
    'blocking.already_blocked': 'User is already blocked',
    'blocking.error': 'Failed to block user',
    'blocking.blocked_users': 'Blocked Users',
    'blocking.blocked_users_description': 'Manage your blocked users list',
    'blocking.no_blocked_users': 'You haven\'t blocked anyone',
    'blocking.blocked_on': 'Blocked on {date}',
    'blocking.unblock': 'Unblock',
    'blocking.user_unblocked': 'User unblocked',
    'blocking.unblock_error': 'Failed to unblock user',
    'blocking.fetch_error': 'Failed to load blocked users',
    'blocking.user_is_blocked': 'You have blocked this user',
    'blocking.you_are_blocked': 'You cannot send messages to this user',
    
    // Reporting
    'reporting.report_user': 'Report User',
    'reporting.report_description': 'Report {name} for violating our community guidelines',
    'reporting.select_reason': 'Please select a reason',
    'reporting.reason': 'Reason for report',
    'reporting.additional_details': 'Additional details',
    'reporting.details_placeholder': 'Provide any additional context...',
    'reporting.submit_report': 'Submit Report',
    'reporting.report_submitted': 'Report submitted. We will review it shortly.',
    'reporting.error': 'Failed to submit report',
    'reporting.reasons.spam': 'Spam or unwanted messages',
    'reporting.reasons.harassment': 'Harassment or bullying',
    'reporting.reasons.inappropriate_content': 'Inappropriate content',
    'reporting.reasons.fraud': 'Fraud or scam',
    'reporting.reasons.impersonation': 'Impersonation',
    'reporting.reasons.other': 'Other',
    
    'common.create': 'Create',
    'common.done': 'Done',
    
    // Agency Pricing
    'agency_pricing.badge': 'Agency Plans',
    'agency_pricing.title': 'Grow Your Agency with the Right Plan',
    'agency_pricing.subtitle': 'Choose the perfect plan for your real estate agency. Scale your team, manage more properties, and deliver exceptional service to your clients.',
    'agency_pricing.most_popular': 'Most Popular',
    'agency_pricing.per_month': '/month',
    'agency_pricing.processing': 'Processing...',
    'agency_pricing.comparison_title': 'Detailed Feature Comparison',
    'agency_pricing.feature': 'Feature',
    'agency_pricing.faq_title': 'Frequently Asked Questions',
    'agency_pricing.cta_title': 'Ready to Grow Your Agency?',
    'agency_pricing.cta_subtitle': 'Join thousands of successful real estate agencies using our platform to manage their teams and properties efficiently.',
    'agency_pricing.cta_button': 'Get Started with Pro',
    'agency_pricing.sign_in_required': 'Please sign in to upgrade your plan',
    'agency_pricing.tier.starter': 'Starter',
    'agency_pricing.tier.starter_desc': 'Perfect for small agencies getting started',
    'agency_pricing.tier.starter_cta': 'Get Started',
    'agency_pricing.tier.pro': 'Pro',
    'agency_pricing.tier.pro_desc': 'For growing agencies with bigger teams',
    'agency_pricing.tier.pro_cta': 'Upgrade to Pro',
    'agency_pricing.tier.enterprise': 'Enterprise',
    'agency_pricing.tier.enterprise_desc': 'For large agencies with unlimited needs',
    'agency_pricing.tier.enterprise_cta': 'Go Enterprise',
    'agency_pricing.feature.brokers_3': 'Up to 3 brokers',
    'agency_pricing.feature.brokers_10': 'Up to 10 brokers',
    'agency_pricing.feature.brokers_unlimited': 'Unlimited brokers',
    'agency_pricing.feature.listings_basic': 'Basic property listings',
    'agency_pricing.feature.listings_advanced': 'Advanced property listings',
    'agency_pricing.feature.listings_premium': 'Premium property listings',
    'agency_pricing.feature.messaging': 'Client messaging',
    'agency_pricing.feature.scheduling': 'Viewing scheduling',
    'agency_pricing.feature.analytics': 'Analytics dashboard',
    'agency_pricing.feature.analytics_advanced': 'Advanced analytics',
    'agency_pricing.feature.support': 'Priority support',
    'agency_pricing.feature.support_247': '24/7 Priority support',
    'agency_pricing.feature.branding': 'Custom branding',
    'agency_pricing.feature.api': 'API access',
    'agency_pricing.comparison.brokers': 'Number of Brokers',
    'agency_pricing.comparison.listings': 'Property Listings',
    'agency_pricing.comparison.basic': 'Basic',
    'agency_pricing.comparison.advanced': 'Advanced',
    'agency_pricing.comparison.premium': 'Premium',
    'agency_pricing.faq.change_plan_q': 'Can I change my plan later?',
    'agency_pricing.faq.change_plan_a': 'Yes! You can upgrade or downgrade your plan at any time. Changes take effect immediately, and we\'ll prorate your billing accordingly.',
    'agency_pricing.faq.exceed_limit_q': 'What happens if I exceed my broker limit?',
    'agency_pricing.faq.exceed_limit_a': 'You\'ll need to upgrade to a higher plan to add more brokers. Your existing brokers will continue to work normally.',
    'agency_pricing.faq.trial_q': 'Is there a free trial for paid plans?',
    'agency_pricing.faq.trial_a': 'We offer a 14-day money-back guarantee on all paid plans. If you\'re not satisfied, contact us for a full refund.',
    'agency_pricing.faq.cancel_q': 'How do I cancel my subscription?',
    'agency_pricing.faq.cancel_a': 'You can manage your subscription from your Agency Dashboard. Go to the Billing tab and click "Manage Subscription" to cancel or modify your plan.',
    'agency_pricing.view_plans': 'View Plans & Pricing',
    
    // Broker Pricing
    'broker_pricing.badge': 'Broker Plans',
    'broker_pricing.title': 'Upgrade Your Broker Account',
    'broker_pricing.subtitle': 'Choose the perfect plan for your real estate business. Get more listings, better analytics, and priority support.',
    'broker_pricing.most_popular': 'Most Popular',
    'broker_pricing.current_plan': 'Current Plan',
    'broker_pricing.your_current_plan': 'Your Current Plan',
    'broker_pricing.free': 'Free',
    'broker_pricing.month': 'month',
    'broker_pricing.per_month': '/month',
    'broker_pricing.processing': 'Processing...',
    'broker_pricing.loading': 'Loading...',
    'broker_pricing.manage_subscription': 'Manage Subscription',
    'broker_pricing.cancel_anytime': 'Downgrade or cancel anytime • Auto-renews monthly',
    'broker_pricing.manage_note': 'Downgrade or cancel anytime • Auto-renews monthly',
    'broker_pricing.downgrade': 'Downgrade',
    'broker_pricing.upgrade_to_pro': 'Upgrade to Pro',
    'broker_pricing.go_enterprise': 'Go Business',
    'broker_pricing.get_started': 'Get Started',
    'broker_pricing.get_started_pro': 'Get Started with Pro',
    'broker_pricing.comparison_title': 'Detailed Feature Comparison',
    'broker_pricing.feature': 'Feature',
    'broker_pricing.faq_title': 'Frequently Asked Questions',
    'broker_pricing.cta_title': 'Ready to Grow Your Business?',
    'broker_pricing.cta_subtitle': 'Join thousands of successful brokers using our platform to list more properties and close more deals.',
    'broker_pricing.cta_button': 'Get Started with Pro',
    'broker_pricing.sign_in_required': 'Please sign in to upgrade your plan',
    'broker_pricing.already_on_plan': "You're already on this plan",
    'broker_pricing.checkout_error': 'Failed to start checkout',
    'broker_pricing.portal_error': 'Failed to open subscription management',
    'broker_pricing.tier.normal': 'Starter',
    'broker_pricing.tier.normal_desc': 'Essential tools to start listing properties',
    'broker_pricing.tier.normal_cta': 'Current Plan',
    'broker_pricing.tier.pro': 'Professional',
    'broker_pricing.tier.pro_desc': 'Double your listings with powerful analytics',
    'broker_pricing.tier.pro_cta': 'Upgrade to Pro',
    'broker_pricing.tier.enterprise': 'Business',
    'broker_pricing.tier.enterprise_desc': 'Maximum visibility with premium features',
    'broker_pricing.tier.enterprise_cta': 'Go Business',
    'broker_pricing.feature.listings_5': '5 active listings',
    'broker_pricing.feature.listings_10': '10 active listings',
    'broker_pricing.feature.listings_15': '15 active listings',
    'broker_pricing.feature.basic_features': 'Standard property display',
    'broker_pricing.feature.advanced_features': 'Enhanced property showcase',
    'broker_pricing.feature.premium_features': 'Premium property showcase',
    'broker_pricing.feature.client_messaging': 'Direct client messaging',
    'broker_pricing.feature.viewing_scheduling': 'Viewing scheduler',
    'broker_pricing.feature.video_tours_paid': 'Video tours ($3 each)',
    'broker_pricing.feature.video_tours_free': 'Unlimited video tours',
    'broker_pricing.feature.analytics': 'Performance analytics',
    'broker_pricing.feature.advanced_analytics': 'Advanced insights & reports',
    'broker_pricing.feature.priority_support': 'Priority email support',
    'broker_pricing.feature.support_24_7': '24/7 dedicated support',
    'broker_pricing.feature.featured_listings': 'Homepage featured spots',
    'broker_pricing.feature.extra_listings': 'Buy extra listings',
    'broker_pricing.feature.extra_10': '+$10 per additional listing',
    'broker_pricing.feature.messaging': 'Client messaging',
    'broker_pricing.feature.scheduling': 'Viewing scheduling',
    'broker_pricing.feature.video_paid': 'Video tours ($3 per listing)',
    'broker_pricing.feature.video_free': 'Free video tours included',
    'broker_pricing.feature.analytics_advanced': 'Advanced analytics',
    'broker_pricing.feature.support': 'Priority support',
    'broker_pricing.feature.featured': 'Featured listings',
    'broker_pricing.feature.extra_price': '+$10 per extra listing',
    'broker_pricing.table.listings_per_month': 'Listings per month',
    'broker_pricing.table.video_tours': 'Video Tours',
    'broker_pricing.table.extra_listings': 'Extra Listings',
    'broker_pricing.table.listing_duration': 'Listing Duration',
    'broker_pricing.table.client_messaging': 'Client Messaging',
    'broker_pricing.table.analytics_dashboard': 'Analytics Dashboard',
    'broker_pricing.table.priority_support': 'Priority Support',
    'broker_pricing.table.featured_listings': 'Featured Listings',
    'broker_pricing.table.15_extras': '15 + extras',
    'broker_pricing.table.3_per_listing': '$3 per listing',
    'broker_pricing.table.10_each': '$10 each',
    'broker_pricing.table.30_days': '30 days',
    'broker_pricing.comparison.listings': 'Listings per month',
    'broker_pricing.comparison.video': 'Video Tours',
    'broker_pricing.comparison.extra': 'Extra Listings',
    'broker_pricing.comparison.duration': 'Listing Duration',
    'broker_pricing.comparison.featured': 'Featured Listings',
    'broker_pricing.comparison.30_days': '30 days',
    'broker_pricing.comparison.per_listing': '$3 per listing',
    'broker_pricing.comparison.free': 'Free',
    'broker_pricing.comparison.each': '$10 each',
    'broker_pricing.comparison.15_plus': '15 + extras',
    'broker_pricing.faq.autorenew_q': 'Does my subscription auto-renew?',
    'broker_pricing.faq.autorenew_a': "Yes! Your subscription automatically renews each month so you never lose access to your listings. You'll be charged on the same day each month. You can cancel anytime from the Manage Subscription portal.",
    'broker_pricing.faq.upgrade_q': 'Can I upgrade or downgrade anytime?',
    'broker_pricing.faq.upgrade_a': 'Absolutely! Upgrades take effect immediately with prorated billing. Downgrades apply at the end of your current billing period—you keep all features until then. Click "Manage Subscription" to make changes.',
    'broker_pricing.faq.expire_q': 'What happens when my listings expire?',
    'broker_pricing.faq.expire_a': "Listings expire after 30 days. You'll receive an email notification 3 days before expiration. You can renew them using your monthly free slots or pay $10 per extra listing (Enterprise only).",
    'broker_pricing.faq.extra_q': 'What are "extra listings"?',
    'broker_pricing.faq.extra_a': 'Enterprise plan members can purchase additional listings at $10 each beyond their 15 monthly free listings. This is great for high-volume months.',
    'broker_pricing.faq.cancel_q': 'How do I cancel my subscription?',
    'broker_pricing.faq.cancel_a': 'Click "Manage Subscription" to access your billing portal where you can cancel, downgrade, or update your payment method. Cancellations take effect at the end of your billing period.',
    
    // Filter additions for rental
    'filter.listing_type': 'Listing Type',
    'filter.all_listings': 'All Listings',
    'filter.for_sale': 'For Sale',
    'filter.for_rent': 'For Rent',
    'form.listing_type': 'Listing Type',
    'form.monthly_rent': 'Monthly Rent',
    'form.rental_details': 'Rental Details',
    'form.rental_period': 'Rental Period',
    'form.deposit_amount': 'Deposit Amount',
    'form.minimum_lease': 'Minimum Lease',
    'form.available_from': 'Available From',
    'form.months': 'months',
    'rental.deposit': 'Security Deposit',
  },
  sv: {
    // Header
    'nav.search': 'Sök bostad',
    'nav.search_agents': 'Sök mäklare',
    'nav.sell': 'Sälja bostad',
    'nav.news': 'Nyheter',
    'nav.build': 'Bygga hus',
    'nav.agencies': 'Byråer',
    'nav.buy': 'Köpa',
    'nav.valuation': 'Värdera',
    'nav.more': 'Mer',
    'nav.saved': 'Sparade',
    'nav.admin': 'Admin',
    'nav.agency': 'Byrå',
    'nav.agency_dashboard': 'Byråpanel',
    'nav.location_manager': 'Platshanterare',
    'nav.map_configuration': 'Kartkonfiguration',
    'nav.hello': 'Hej',
    'nav.menu': 'Meny',
    'nav.manage_news': 'Hantera nyheter',
    
    // Search Section
    'search.for_sale': 'Till salu',
    'search.for_rent': 'Att hyra',
    'search.sold_prices': 'Slutpriser',
    'search.search_agents': 'Sök mäklare',
    'search.area_label': 'Område',
    'search.area_placeholder': 'Skriv område eller adress',
    'search.area_examples': 'Exempel: Bagdad, Basra, Erbil, Najaf, Karbala',
    'search.expand_area': 'Utöka område med',
    'search.property_type': 'Bostadstyp',
    'search.show_filters': 'Visa sökfilter',
    'search.find_properties': 'Hitta bostäder till salu',
    'search.min_rooms': 'Minst antal rum',
    'search.min_area': 'Minsta boarea',
    'search.max_price': 'Högst pris',
    'search.all': 'Alla',
    'search.no_max': 'Inget',
    'search.new_construction': 'Nyproduktion',
    'search.show_new_construction': 'Visa nyproduktion',
    'search.only_new_construction': 'Visa endast nyproduktion',
    'search.exclude_new_construction': 'Visa inte nyproduktion',
    'search.keywords': 'Nyckelord',
    'search.keywords_placeholder': 'Pool, kakelugn, etc.',
    'search.more_filters_note': 'Fler sökfilter finns vid sökresultatet.',
    'search.recent_searches': 'Senaste sökningarna',
    'search.hide_history': 'Dölj historik',
    'search.clear_history': 'Rensa historik',
    'search.recent_erbil': 'Bostäder, Erbil',
    
    // Property Types
    'property.all_types': 'Alla typer',
    'property.villas': 'Villor',
    'property.townhouses': 'Par/Kedje/Radhus',
    'property.apartments': 'Lägenheter',
    'property.condominiums': 'Bostadsrätter',
    'property.business': 'Affärsfastigheter',
    'property.land': 'Tomter',
    'property.farms': 'Gårdar/Skogar',
    'property.other': 'Övriga',
    
    // Cities/Governorates
    'city.baghdad': 'Bagdad',
    'city.basra': 'Basra',
    'city.erbil': 'Erbil',
    'city.sulaymaniyah': 'Sulaymaniyah',
    'city.najaf': 'Najaf',
    'city.karbala': 'Karbala',
    'city.mosul': 'Mosul',
    'city.kirkuk': 'Kirkuk',
    'city.anbar': 'Anbar',
    'city.dhi_qar': 'Dhi Qar',
    'city.maysan': 'Maysan',
    'city.wasit': 'Wasit',
    'city.babil': 'Babil',
    'city.qadisiyyah': 'Qadisiyyah',
    'city.muthanna': 'Muthanna',
    'city.diyala': 'Diyala',
    'city.saladin': 'Saladin',
    'city.nineveh': 'Nineveh',
    'city.dohuk': 'Dohuk',
    
    // Auth extended
    'auth.first_name': 'Förnamn',
    'auth.last_name': 'Efternamn',
    'auth.first_name_placeholder': 'Ange ditt förnamn',
    'auth.last_name_placeholder': 'Ange ditt efternamn',
    'auth.display_name': 'Visningsnamn',
    'auth.display_name_placeholder': 'Välj ett visningsnamn',
    'auth.display_name_hint': 'Detta är det enda namn som andra kommer att se',
    'auth.date_of_birth': 'Födelsedatum',
    'auth.fill_all_fields': 'Vänligen fyll i alla obligatoriska fält',
    'auth.must_be_18': 'Du måste vara minst 18 år för att registrera dig',
    'auth.must_be_18_hint': 'Du måste vara minst 18 år',
    
    // Identity Verification
    'verification.verify_identity': 'Verifiera identitet',
    'verification.verified': 'Verifierad',
    'verification.title': 'Identitetsverifiering',
    'verification.description': 'Ladda upp ett statligt utfärdat dokument för att verifiera din identitet och få ett verifierat märke.',
    'verification.document_type': 'Dokumenttyp',
    'verification.doc_id_card': 'ID-kort',
    'verification.doc_passport': 'Pass',
    'verification.doc_drivers_license': 'Körkort',
    'verification.upload_document': 'Ladda upp dokument',
    'verification.click_to_upload': 'Klicka för att ladda upp eller dra och släpp',
    'verification.file_requirements': 'JPG, PNG, WEBP eller PDF (max 10MB)',
    'verification.submit': 'Skicka för verifiering',
    'verification.uploading': 'Laddar upp...',
    'verification.submitted': 'Verifiering inskickad',
    'verification.submitted_desc': 'Din verifieringsbegäran har skickats in. Vi granskar den inom kort.',
    'verification.pending_message': 'Din verifiering granskas. Detta tar vanligtvis 1-2 arbetsdagar.',
    'verification.rejected_message': 'Din tidigare verifiering avvisades. Vänligen skicka in ett nytt dokument.',
    'verification.status_approved': 'Godkänd',
    'verification.status_pending': 'Väntar',
    'verification.status_rejected': 'Avvisad',
    'verification.invalid_file_type': 'Ogiltig filtyp',
    'verification.allowed_types': 'Vänligen ladda upp en JPG, PNG, WEBP eller PDF-fil',
    'verification.file_too_large': 'Filen är för stor',
    'verification.max_size': 'Maximal filstorlek är 10MB',
    'verification.error': 'Verifieringsfel',
    'verification.admin_title': 'Identitetsverifieringar',
    'verification.admin_description': 'Granska och godkänn identitetsverifieringsförfrågningar',
    'verification.no_requests': 'Inga verifieringsförfrågningar',
    'verification.view': 'Granska',
    'verification.review_title': 'Granska verifiering',
    'verification.review_notes': 'Granskningsanteckningar',
    'verification.review_notes_placeholder': 'Lägg till anteckningar om ditt beslut (valfritt)',
    'verification.approve': 'Godkänn',
    'verification.reject': 'Avvisa',
    'verification.approved': 'Verifiering godkänd',
    'verification.rejected': 'Verifiering avvisad',
    'verification.review_complete': 'Verifieringen har behandlats.',

    // Common
    'common.price': 'Pris',
    'common.rooms': 'Rum',
    'common.size': 'Storlek',
    'common.governorate': 'Kommun',
    'common.select_governorate': 'Välj kommun',
    'common.cancel': 'Avbryt',
    'common.save': 'Spara',
    'common.saving': 'Sparar...',
    'common.send': 'Skicka',
    'common.sending': 'Skickar...',
    'common.submit': 'Skicka',
    'common.close': 'Stäng',
    'common.error': 'Fel',
    'common.success': 'Klart',
    'common.loading': 'Laddar...',
    'common.name': 'Namn',
    'common.email': 'E-post',
    'common.phone': 'Telefon',
    'common.message': 'Meddelande',
    'common.required': 'Obligatoriskt',
    'common.optional': 'Valfritt',
    'common.delete': 'Radera',
    'common.free': 'Gratis',
    
    // Leaderboard
    'leaderboard': 'Topplistor',
    'leaderboardDescription': 'Upptäck topppresterande byråer och mäklare rankade efter visningar och försäljningar',
    'topAgencies': 'Toppbyråer',
    'topBrokers': 'Toppmäklare',
    'topAgenciesDescription': 'Byråer rankade efter fastighetsvisningar och försäljningsresultat',
    'topBrokersDescription': 'Mäklare rankade efter fastighetsvisningar och försäljningsresultat',
    'allRegions': 'Alla regioner',
    'noAgenciesFound': 'Inga byråer hittades',
    'noBrokersFound': 'Inga mäklare hittades',
    'brokers': 'mäklare',
    'sold': 'såld',
    'active': 'aktiv',
    'views': 'visningar',
    
    // Dialogs - Contact Agent
    'dialog.contact_agent': 'Kontakta mäklare',
    'dialog.contact_agent_btn': 'Kontakta mäklare',
    'dialog.contact_message_placeholder': 'Hej! Jag är intresserad av bostaden på {address} och skulle gärna veta mer. Hör gärna av er när ni har tid.',
    'dialog.contact_sending': 'Skickar...',
    'dialog.contact_send': 'Skicka meddelande',
    'dialog.contact_note': 'Din förfrågan skickas direkt till ansvarig mäklare som kommer att kontakta dig.',
    
    // Dialogs - Schedule Viewing
    'dialog.schedule_viewing': 'Boka visning',
    'dialog.schedule_viewing_btn': 'Boka visning',
    'dialog.preferred_time': 'Önskad visningstid',
    'dialog.preferred_time_note': 'Välj datum och tid för visningen. Mäklaren kommer att bekräfta tillgängligheten.',
    'dialog.special_requests': 'Särskilda önskemål',
    'dialog.special_requests_placeholder': 'Eventuella särskilda önskemål eller frågor...',
    'dialog.schedule_booking': 'Bokar visning...',
    'dialog.schedule_book': 'Boka visning',
    'dialog.schedule_note': 'Din förfrågan skickas till mäklaren som bekräftar tiden via e-post.',
    
    // Dialogs - Save Search
    'dialog.save_search': 'Spara sökning',
    'dialog.save_search_btn': 'Spara sökning',
    'dialog.search_name': 'Namn på sökning',
    'dialog.search_name_placeholder': 't.ex. Lägenhet i Stockholm',
    'dialog.city_municipality': 'Stad/Kommun',
    'dialog.min_rooms': 'Min antal rum',
    'dialog.min_size': 'Min yta (m²)',
    'dialog.property_type': 'Typ av bostad',
    'dialog.email_alerts': 'E-postaviseringar när nya bostäder matchar',
    'dialog.saving_search': 'Sparar...',
    
    // Dialogs - Mark as Sold
    'dialog.mark_sold': 'Markera som såld',
    'dialog.mark_sold_description': 'Ange det slutliga försäljningspriset för denna fastighet. Detta kommer att flytta annonsen till sålda fastigheter.',
    'dialog.final_sale_price': 'Slutligt försäljningspris (USD)',
    'dialog.listed_price': 'Listpris',
    'dialog.property_details': 'Fastighetsdetaljer',
    'dialog.enter_valid_price': 'Ange ett giltigt försäljningspris',
    'dialog.enter_price_placeholder': 'Ange försäljningspris',
    'dialog.marked_sold_success': 'Fastigheten har markerats som såld',
    'dialog.mark_sold_error': 'Det gick inte att markera fastigheten som såld',
    
    // Dialogs - Add Property
    'dialog.add_property': 'Lägg till ny bostad',
    'dialog.hide_preview': 'Dölj förhandsvisning',
    'dialog.show_preview': 'Visa förhandsvisning',
    'dialog.selected_images': 'Valda bilder',
    'dialog.uploaded_images': 'Uppladdade bilder',
    'dialog.click_primary': 'Klicka på en bild för att ange som primär',
    'dialog.invalid_format': 'Ogiltigt filformat',
    'dialog.file_too_large': 'Filen är för stor',
    'dialog.upload_failed': 'Uppladdning misslyckades',
    'dialog.images_required': 'Bilder krävs',
    'dialog.please_upload': 'Vänligen ladda upp minst en bild för fastigheten',
    'dialog.form_validation_failed': 'Formulärvalidering misslyckades',
    // Hero/News Section
    'hero.find_dream_home': 'Hitta ditt drömhem i Irak',
    'hero.explore_description': 'Utforska tusentals hem till salu och slutpriser från hela Irak. Få värdering, läs bostadsnyheter och kontakta mäklare.',
    'hero.badge': 'Iraks ledande bostadsplattform',
    
    // Home Features
    'home.feature.properties': 'Tusentals fastigheter',
    'home.feature.properties_desc': 'Bläddra bland det största utbudet av hem, lägenheter och mark över hela Irak.',
    'home.feature.market': 'Lokal expertis',
    'home.feature.market_desc': 'Djup kunskap om varje region och kvarter i Irak.',
    'home.feature.trusted': 'Verifierade mäklare',
    'home.feature.trusted_desc': 'Kontakta pålitliga, verifierade fastighetsproffs.',
    'home.why_choose': 'Varför välja Aqarnet',
    'home.why_choose_desc': 'Allt du behöver för att hitta din perfekta bostad',
    
    // CTA Section
    'cta.ready': 'Redo att hitta ditt hem?',
    'cta.desc': 'Bläddra bland tusentals fastigheter i Irak',
    'cta.explore': 'Utforska fastigheter',
    // Property Listings
    'listings.latest_for_sale': 'Senaste till salu',
    'listings.properties_found': 'bostäder hittades',
    'listings.all_news': 'Alla nyheter',
    'listings.add_property': 'Lägg till bostad',
    'listings.edit': 'Redigera',
    'listings.edit_property': 'Redigera bostad',
    'listings.monthly_fee': 'Avgift',
    'listings.open_floor_plan': 'Öppen planlösning',
    'listings.large_balcony': 'Stor balkong',
    'listings.family_friendly': 'Familjevänligt',
    'listings.photos': 'foton',
    'listings.per_month': 'mån',
    'listings.delete_confirm': 'Är du säker på att du vill ta bort denna bostad?',
    
    // Forms
    'form.price': 'Pris',
    'form.priceUSD': 'Pris (USD)',
    'form.priceIQD': 'Pris (IQD)',
    'form.priceConversionNote': 'Ange pris i endera valutan - den andra beräknas automatiskt (Kurs: 1 USD ≈ 1 310 IQD)',
    'form.price_usd': 'Pris (USD)',
    'form.price_placeholder': 'Ange pris i amerikanska dollar',
    'form.price_description': 'Ange priset i amerikanska dollar. Det visas både i USD och irakiska dinarer.',
    'form.address': 'Adress',
    'form.city': 'Stad', 
    'form.rooms': 'Rum',
    'form.bathrooms': 'Badrum',
    'form.size': 'Yta (m²)',
    'form.building_size': 'Byggnadsyta (m²)',
    'form.building_size_placeholder': 'Byggnadsyta i m²',
    'form.building_size_description': 'Byggnadsyta (boarea)',
    'form.plot_size': 'Tomtyta (m²)',
    'form.plot_size_placeholder': 'Tomtyta i m²',
    'form.plot_size_description': 'Total tomtyta (tomtarea)',
    'form.type': 'Typ',
    'form.propertyType': 'Fastighetstyp',
    'form.monthly_fee': 'Månadsavgift (SEK)',
    'form.monthly_fee_usd': 'Månadsavgift (USD)',
    'form.monthly_fee_placeholder': 'Ange månadsavgift i USD (valfritt)',
    'form.monthly_fee_description': 'Valfri månadsavgift i amerikanska dollar',
    'form.description': 'Beskrivning',
    'form.description_placeholder': 'Beskriv fastigheten...',
    'form.payment_plan': 'Betalningsplan',
    'form.payment_plan_placeholder': 't.ex. 30% handpenning, 70% vid leverans, eller månadsbetalningar...',
    'form.payment_plan_hint': 'Lägg till betalningsomgångar för att visa köpare dina betalningsalternativ',
    'payment_plan.total': 'Totalt',
    'payment_plan.amount': 'Belopp',
    'payment_plan.description': 'Beskrivning',
    'payment_plan.date': 'Datum',
    'payment_plan.percentage': 'Procent',
    'payment_plan.add_installment': 'Lägg till avbetalning',
    'payment_plan.hint': 'Lämna tomt om ingen betalningsplan erbjuds',
    'payment_plan.down_payment': 'Handpenning',
    'payment_plan.first_payment': 'Första betalning',
    'payment_plan.second_payment': 'Andra betalning',
    'payment_plan.third_payment': 'Tredje betalning',
    'payment_plan.fourth_payment': 'Fjärde betalning',
    'payment_plan.fifth_payment': 'Femte betalning',
    'payment_plan.sixth_payment': 'Sjätte betalning',
    'payment_plan.seventh_payment': 'Sjunde betalning',
    'payment_plan.final_payment': 'Slutbetalning',
    'payment_plan.payment_description': 'Betalningsbeskrivning',
    'payment_plan.enable': 'Inkludera betalningsplan',
    'form.floor_plans': 'Planritningar',
    'form.upload_floor_plans': 'Ladda upp planritningsbilder',
    'form.features': 'Egenskaper (komma-separerade)',
    'form.features_placeholder': 't.ex., Pool, Trädgård, Garage, Modernt kök',
    'form.features_description': 'Separera flera egenskaper med kommatecken',
    'form.amenities': 'Bekvämligheter',
    'form.amenities_hint': 'Välj alla bekvämligheter som gäller för denna fastighet',
    'amenities.balcony': 'Balkong',
    'amenities.electricity_generator': 'Elgenerator',
    'amenities.central_ac': 'Centralt luftkonditionering',
    'amenities.double_glazed_windows': 'Dubbelglasfönster',
    'amenities.satellite_tv': 'Satellit/Kabel-TV',
    'amenities.cctv': 'CCTV-säkerhet',
    'amenities.security_staff': 'Säkerhetspersonal',
    'amenities.sewerage_system': 'Avloppssystem',
    'amenities.furnished_kitchen': 'Möblerat kök',
    'amenities.24hr_electricity': '24-timmars el',
    'amenities.market': 'Närliggande marknad',
    'amenities.elevator': 'Hiss',
    'amenities.school': 'Närliggande skola',
    'amenities.mosque': 'Närliggande moské',
    'amenities.central_gas': 'Centralgas',
    'amenities.maintenance': 'Underhållstjänster',
    'amenities.cleaning': 'Städtjänster',
    'amenities.fire_fighting': 'Brandbekämpningsutrustning',
    'amenities.car_washing': 'Biltvättsområde',
    'amenities.smart_home': 'Smart hem-system',
    'amenities.earthquake_resistant': 'Jordbävningssäker',
    'amenities.central_fire_system': 'Centralt brandsläckningssystem',
    'amenities.hot_water_system': 'Centralt varmvattensystem',
    'amenities.central_vacuum': 'Centraldammsugare',
    'amenities.fountain': 'Fontän',
    'amenities.24hr_water': '24-timmars vattenförsörjning',
    'amenities.garbage_chute': 'Sopnedkast',
    'amenities.kitchen_counter': 'Köksbänk',
    'amenities.street_lights': 'Gatubelysning',
    'amenities.playground': 'Barnlekplats',
    'amenities.gym': 'Gym',
    'amenities.garden': 'Trädgård',
    'amenities.parking': 'Parkering',
    'amenities.wifi': 'WiFi',
    'amenities.security_door': 'Säkerhetsdörr',
    'amenities.split_ac': 'Split AC',
    'amenities.bathtub': 'Badkar',
    'amenities.refrigerator': 'Kylskåp',
    'amenities.washing_machine': 'Tvättmaskin',
    'amenities.swimming_pool': 'Pool',
    'amenities.solar_panels': 'Solpaneler',
    'amenities.water_tank': 'Vattentank',
    'amenities.water_well': 'Vattenbrunn',
    'amenities.septic_tank': 'Septiktank',
    'amenities.central_heating': 'Centralvärme',
    'amenities.desert_cooler': 'Ökenkylare',
    'amenities.intercom': 'Porttelefon',
    'amenities.external_camera': 'Extern säkerhetskamera',
    'amenities.guard_room': 'Vaktrum',
    'amenities.rooftop_terrace': 'Takterrass',
    'amenities.basement': 'Källare',
    'amenities.storage_room': 'Förråd',
    'amenities.internal_stairs': 'Inre trappa',
    'amenities.external_stairs': 'Yttre trappa',
    'amenities.corner_unit': 'Hörnlägenhet',
    'amenities.two_streets': 'Två gator',
    'amenities.double_entrance': 'Dubbel entré',
    'amenities.marble_flooring': 'Marmorgolv',
    'amenities.ceramic_tiles': 'Keramiska plattor',
    'amenities.parquet_flooring': 'Parkettgolv',
    'amenities.gypsum_decorations': 'Gipsdekorationer',
    'amenities.false_ceiling': 'Undertak',
    'amenities.steel_doors': 'Ståldörrar',
    'amenities.aluminum_windows': 'Aluminiumfönster',
    'amenities.pvc_windows': 'PVC-fönster',
    'amenities.guest_room': 'Gästrum',
    'amenities.servant_quarters': 'Tjänstekvarter',
    'amenities.covered_parking': 'Täckt parkering',
    'amenities.concrete_fence': 'Betongstaket',
    'amenities.iron_fence': 'Järnstaket',
    'amenities.street_pavement': 'Gatläggning',
    'amenities.fully_furnished': 'Fullt möblerad',
    'amenities.hospital': 'Närliggande sjukhus',
    'amenities.category.utilities': 'El & Vatten',
    'amenities.category.climate': 'Klimatkontroll',
    'amenities.category.security': 'Säkerhet',
    'amenities.category.building': 'Byggnadsegenskaper',
    'amenities.category.interior': 'Interiör & Golv',
    'amenities.category.rooms': 'Rum',
    'amenities.category.outdoor': 'Utomhus & Parkering',
    'amenities.category.appliances': 'Vitvaror',
    'amenities.category.services': 'Tjänster',
    'amenities.category.nearby': 'I närheten',
    'common.selected': 'valda',
    'form.property_images': 'Fastighetsbilder',
    'form.current_images': 'Nuvarande bilder',
    'form.image': 'Bild',
    'form.primary': 'Primär',
    'form.click_image_primary': 'Klicka på en bild för att sätta som primär. Använd pilarna för att ändra ordning.',
    'form.click_to_upload_new': 'Klicka för att ladda upp nya bilder',
    'form.new_images_upload': 'Nya bilder att ladda upp',
    'form.new_image': 'Ny bild',
    'form.upload': 'Ladda upp',
    'form.new_images': 'nya bilder',
    'form.location': 'Plats',
    'form.required': 'Krävs',
    'form.optional': 'Valfritt',
    'form.cancel': 'Avbryt',
    'form.save_changes': 'Spara ändringar',
    'form.uploading': 'Laddar upp...',
    'form.upload_images': 'Ladda upp bilder',
    'form.click_to_upload': 'Klicka för att ladda upp bilder',
    'form.file_formats': 'JPEG, PNG, WebP, GIF (max 10MB per bild)',
    'form.select_type': 'Välj typ',
    'form.location_on_map': 'Fastighetsposition på karta',
    'form.location_hint': 'Klicka på kartan för att sätta exakt position för fastigheten',
    'form.hide_preview': 'Dölj förhandsvisning',
    'form.show_preview': 'Visa förhandsvisning',
    'form.selected_images': 'Valda bilder',
    'form.click_set_primary': 'Klicka på en bild för att sätta som primär',
    'form.uploaded_images': 'Uppladdade bilder',
    'form.images': 'bilder',
    'form.uploading_images': 'Laddar upp bilder...',
    'form.city_placeholder': 'Sök efter en stad...',
    'form.new_construction': 'Nyproduktion (Framtida bostad)',
    'form.preview': 'Förhandsvisning',
    'form.preview_description': 'Så här kommer din annons att se ut på sidan',
    'form.fullscreen': 'Helskärm',
    'form.exit_fullscreen': 'Avsluta helskärm',
    'form.click_preview_fullscreen': 'Klicka på förhandsgranskning för helskärm',
    'form.preview_contact_disabled': 'Kontaktalternativ är inaktiverade i förhandsgranskningsläge',
    'form.invalid_format': 'Ogiltigt filformat',
    'form.invalid_format_desc': '{filename} har ogiltigt format. Tillåtna: JPEG, PNG, WebP, GIF',
    'form.file_too_large': 'Fil för stor',
    'form.file_too_large_desc': '{filename} är större än 10MB',
    'form.upload_failed': 'Uppladdning misslyckades',
    'form.upload_failed_desc': 'Kunde inte ladda upp bilder',
    'form.images_required': 'Bilder krävs',
    'form.images_required_desc': 'Vänligen ladda upp minst en bild för fastigheten',
    'form.sold_price_usd': 'Sålt pris (USD)',
    'form.sold_price_placeholder': 'Ange faktiskt sålt pris',
    'form.sold_price_description': 'Det faktiska försäljningspriset (kan skilja sig från utropspriset)',
    'form.video_url': 'Video-tur URL',
    'form.video': 'Video',
    'form.add_video': 'Lägg till en video till',
    'form.video_addon': '+$3 tillägg',
    'form.video_included': 'Ingår gratis',
    'form.video_url_hint': 'Lägg till en YouTube eller Vimeo video-tur. Videotillägg kan köpas efter att annonsen skapats.',
    'form.video_url_hint_free': 'Lägg till en YouTube eller Vimeo video-tur - ingår i ditt abonnemang!',
    'form.video_url_hint_addon': 'Lägg till en YouTube eller Vimeo video-tur. $3 läggs till på din annonskostnad.',
    'form.include_video_tour': 'Inkludera video-tur',
    'form.show_map': 'Visa karta',
    'form.no_location': 'Ingen plats vald',
    
    // News Section
    'news.property_news': 'Bostadsnyheter',
    'news.all_news': 'Alla nyheter',
    'news.click_tops': 'Klicktoppen',
    'news.guides': 'Guider',
    'news.news': 'Nyheter',
    'news.article_1_title': '"Lundellhuset" lockar tusentals – veckans klicktopp igen',
    'news.article_2_title': 'Leta drömhem tillsammans – skapa och dela samlingar på Aqarnet',
    'news.article_3_title': 'Minislott till salu: "Förknippad med många kända släkter"',
    
    // Loading and empty states
    'loading.properties': 'Laddar fastigheter...',
    'empty.no_properties': 'Inga fastigheter tillgängliga just nu',
    'empty.check_back': 'Kom tillbaka senare för att se nya objekt.',
    'saved_searches.title': 'Sparade sökningar',
    'saved_searches.empty': 'Inga sparade sökningar ännu',
    'saved_searches.description': 'Spara en sökning för att få e-postaviseringar när matchande bostäder läggs ut',
    'saved_searches.created': 'Skapad',
    'saved_searches.alerts_on': 'Aviseringar på',
    'saved_searches.alerts_off': 'Aviseringar av',
    'saved_searches.email_enabled': 'E-postaviseringar aktiverade',
    'saved_searches.email_disabled': 'E-postaviseringar avaktiverade',
    'saved_searches.settings_updated': 'Inställningen har uppdaterats',
    'saved_searches.removed': 'Sökning borttagen',
    'saved_searches.removed_desc': 'Den sparade sökningen har tagits bort',
    'saved_searches.error': 'Fel',
    'saved_searches.update_error': 'Kunde inte uppdatera inställningen',
    'saved_searches.delete_error': 'Kunde inte ta bort sökningen',
    'saved_searches.all_properties': 'Alla bostäder',
    // Rental properties
    'rental.for_sale': 'Till salu',
    'rental.for_rent': 'Uthyres',
    'rental.listing_type': 'Annonstyp',
    'rental.rental_details': 'Hyresdetaljer',
    'rental.rental_period': 'Hyresperiod',
    'rental.rental_period_hint': 'Hur ofta hyran betalas',
    'rental.daily': 'Dagligen',
    'rental.weekly': 'Veckovis',
    'rental.monthly': 'Månadsvis',
    'rental.yearly': 'Årligen',
    'rental.per_daily': '/dag',
    'rental.per_weekly': '/vecka',
    'rental.per_monthly': '/månad',
    'rental.per_yearly': '/år',
    'rental.deposit_amount': 'Depositionsbelopp (USD)',
    'rental.minimum_lease': 'Minsta hyrestid (månader)',
    'rental.months': 'månader',
    'rental.available_from': 'Tillgänglig från',
    'rental.furnished': 'Möblerad',
    'rental.utilities_included': 'El/vatten ingår',
    'rental.pets_allowed': 'Husdjur tillåtna',
    'filters.listing_type': 'Annonstyp',
    'filters.all': 'Alla',
    'filters.sale': 'Till salu',
    'filters.rent': 'Uthyres',
    
    // Property card
    'property.sold': 'SÅLD',
    'property.photos': 'bilder',
    'property.rooms_short': 'rum',
    'property.plot': 'tomt',
    'property.per_month': '/mån',
    'property.delete_title': 'Radera fastighet?',
    'property.delete_confirm': 'Är du säker på att du vill radera {address}? Denna åtgärd kan inte ångras.',
    'property.deleted': 'Fastighet raderad',
    'property.deleted_desc': 'Fastigheten har tagits bort',
    'property.delete_error': 'Kunde inte radera fastigheten',
    
    // Loading
    'loading.saved_searches': 'Laddar sparade sökningar...',
    
    // Navigation
    'nav.favorites': 'Favoriter',
    'nav.profile': 'Profil',
    'nav.login': 'Logga in',
    'nav.logout': 'Logga ut',
    'nav.recent_searches': 'Senaste sökningar',
    'nav.no_recent_searches': 'Inga senaste sökningar',
    'nav.new_search': 'Ny sökning',
    'nav.all_properties': 'Alla bostäder',
    'nav.new_production': 'Nyproduktion',
    
    // Footer
    'footer.contact': 'Kontakt',
    'footer.customer_service': 'Kundservice & kontakt',
    'footer.about': 'Om Aqarnet',
    'footer.press': 'Press',
    'footer.investors': 'För investerare',
    'footer.advertising': 'Bannerannonsering och reklam',
    'footer.jobs': 'Jobba hos oss',
    'footer.statistics': 'Bostadsstatistik',
    'footer.apps': 'Appar',
    'footer.iphone_ipad': 'iPhone och iPad',
    'footer.android': 'Android',
    'footer.follow': 'Följ Aqarnet',
    'footer.newsletter': 'Aqarnets nyhetsbrev',
    'footer.whatsapp': 'WhatsApp',
    'footer.facebook': 'Facebook',
    'footer.instagram': 'Instagram',
    'footer.linkedin': 'LinkedIn',
    'footer.pinterest': 'Pinterest',
    'footer.tiktok': 'TikTok',
    'footer.threads': 'Threads',
    'footer.podcast': 'Aqarnets podcast - Vägen Hem',
    'footer.all_properties': 'Alla bostäder',
    'footer.privacy': 'Integritet',
    'footer.cookies': 'Cookies',
    'footer.terms': 'Villkor & Legal Information',
    'footer.cookie_settings': 'Cookie-inställningar',
    
    // Agents
    'agents.title': 'Hitta en mäklare',
    'agents.subtitle': 'Går du i säljtankar? Vi hjälper dig att hitta mäklare som är experter inom ditt område!',
    'agents.area_search': 'Områdessök',
    'agents.name_search': 'Namnsök',
    'agents.search_placeholder': 'Skriv område eller adress',
    'agents.show': 'Visa',
    'agents.sort_by': 'Sorteras på',
    'agents.agents_count': 'Mäklare',
    'agents.companies_label': 'Mäklarfirmor',
    'agents.recent_sales': 'Senast såljdatum',
    'agents.most_sales': 'Flest försäljningar',
    'agents.alphabetical': 'Alfabetisk ordning',
    'agents.showing_results': 'Visar',
    'agents.agents_label': 'mäklare',
    'agents.loading': 'Laddar mäklare...',
    'agents.no_results': 'Inga mäklare hittades.',
    'agents.phone': 'Telefon:',
    'agents.status': 'Status:',
    'agents.active_agent': 'Aktiv mäklare',
    'agents.anonymous_agent': 'Namnlös mäklare',
    'agents.agent_label': 'Mäklare',
    'agents.show_more': 'Visa fler mäklare',

    // Properties for Sale Page
    'propertiesForSale.title': 'Bostäder till salu - Sverige',
    'propertiesForSale.statistics.municipalities': 'kommuner',
    'propertiesForSale.statistics.forSale': 'till salu',
    'propertiesForSale.statistics.sold': 'såld denna vecka',
    'propertiesForSale.map.fullscreen': 'Karta / Helskärm',
    'propertiesForSale.map.area': 'Område',
    'propertiesForSale.filters.searchWithin': 'Sök inom',
    'propertiesForSale.filters.km': 'km',
    'propertiesForSale.filters.propertyType': 'Bostadstyp',
    'propertiesForSale.filters.allTypes': 'Alla typer',
    'propertiesForSale.filters.villa': 'Villa',
    'propertiesForSale.filters.apartment': 'Lägenhet',
    'propertiesForSale.filters.townhouse': 'Radhus',
    'propertiesForSale.filters.plot': 'Tomt',
    'propertiesForSale.filters.commercial': 'Kommersiell',
    'propertiesForSale.filters.other': 'Övriga',
    'propertiesForSale.filters.rooms': 'Rum',
    'propertiesForSale.filters.bathrooms': 'Badrum',
    'propertiesForSale.filters.min': 'Min',
    'propertiesForSale.filters.max': 'Max',
    'propertiesForSale.filters.size': 'Yta',
    'propertiesForSale.filters.price': 'Pris',
    'propertiesForSale.filters.features': 'Egenskaper',
    'propertiesForSale.filters.balcony': 'Balkong/Uteplats/Terrass',
    'propertiesForSale.filters.pets': 'Husdjur',
    'propertiesForSale.filters.newBuild': 'Nybyggt',
    'propertiesForSale.filters.showAll': 'Visa alla',
    'propertiesForSale.filters.buildYear': 'Byggår',
    'propertiesForSale.filters.searchResults': 'Sökresultat',
    'propertiesForSale.filters.searchPlaceholder': 'Sök områden, städer etc...',
    'propertiesForSale.filters.searchingFor': 'Söker efter:',
    'propertiesForSale.filters.any': 'Alla',
    'propertiesForSale.filters.keywords': 'Nyckelord',
    'propertiesForSale.filters.showResults': 'Visa resultat',
    'propertiesForSale.filters.clearFilters': 'Rensa',
    'propertiesForSale.filters.realEstateSituation': 'Fastighetssituation',
    'propertiesForSale.filters.situation.tapu': 'Tapu',
    'propertiesForSale.filters.situation.landContract': 'Markkontrakt',
    'propertiesForSale.filters.situation.noDocuments': 'Inga dokument',
    'propertiesForSale.filters.situation.investment': 'Investering',
    'propertiesForSale.filters.situation.musataha': 'Musataha',
    'propertiesForSale.filters.situation.card': 'Kort',
    'propertiesForSale.filters.situation.agriculturalTapu': 'Jordbrukstapu',
    'propertiesForSale.sort.newest': 'Nyast först',
    'propertiesForSale.sort.price_low': 'Pris: Lågt till högt',
    'propertiesForSale.sort.price_high': 'Pris: Högt till lågt',
    'propertiesForSale.sort.size': 'Storlek',
    'propertiesForSale.noResults': 'Inga fastigheter hittades',
    'propertiesForSale.tryAdjustingFilters': 'Prova att justera dina filter eller sök i ett annat område',

    // Property Types
    'propertyTypes.allTypes': 'Alla typer',
    'propertyTypes.villa': 'Villor',
    'propertyTypes.apartment': 'Lägenheter',
    'propertyTypes.townhouse': 'Par/Kedje/Radhus',
    'propertyTypes.business': 'Affärsfastigheter',
    'propertyTypes.plot': 'Tomter',
    'propertyTypes.farm': 'Gårdar/Skogar',
    'propertyTypes.other': 'Övriga',

    // Cookie Policy
    'cookie.title': 'Cookie-policy',
    'cookie.last_updated': 'Senast uppdaterad:',
    'cookie.section1.title': 'Avsnitt 1 - Allmänna bestämmelser avseende implementering av spårningsteknologier',
    'cookie.section1.p1': 'Ploqs AB, nedan benämnt "Tjänsteleverantören", jämte av Tjänsteleverantören anlitade samarbetspartners och underleverantörer, implementerar och nyttjar diverse former av digitala spårningsmekanismer, innefattande men ej begränsat till cookies, webbfyrar, pixeltaggar samt övriga likartade teknologiska lösningar (sammantaget benämnda "Spårningsteknologier") på Tjänsteleverantörens digitala plattformar, webbplatser samt mobila applikationer (gemensamt refererade till som "Tjänsteleverantörens Digitala Kanaler"). Föreliggande policy för hantering av Spårningsteknologier specificerar de ändamål för vilka samt de metoder genom vilka Tjänsteleverantören tillämpar nämnda teknologier.',
    'cookie.section1.p2': 'Spårningsteknologier utgörs primärt av mindre textbaserade datafiler vilka deponeras på användarens terminalutrustning alternativt webbläsarprogram i syfte att aggregera information beträffande användarens interaktion med och navigation på digitala plattformar och mobila applikationer.',
    'cookie.section1.h1': 'Behandling av personuppgifter och dataskyddsrättsliga överväganden',
    'cookie.section1.p3': 'Information som inhämtas genom tillämpning av Spårningsteknologier utgörs i flertalet fall av uppgifter vilka, direkt eller indirekt och i kombination med övrig tillgänglig data, potentiellt kan nyttjas för identifiering av enskilda individer, innefattande men ej begränsat till IP-adresser, unika cookie-identifikatorer, annonsidentifikatorer (IDFA), enhetsidentifikatorer samt diverse metadata som möjliggör särskilning av specifika terminalenheter.',
    'cookie.section1.h2': 'Användarens valmöjligheter',
    'cookie.section1.p4': 'Användaren äger rätt att vid valfri tidpunkt revidera tidigare avgivna samtycken genom att navigera till de samtyckeshanteringsalternativ som tillhandahålls via Tjänsteleverantörens digitala gränssnitt.',
    'cookie.section1.h3': 'Spårningsteknologiernas persistens och lagringsperioder',
    'cookie.section1.p5': 'Spårningsteknologier uppvisar varierande persistens beroende på respektive teknologis avsedda ändamål och funktionalitet. Sessionsbaserade Spårningsteknologier initieras vid användarens anslutning till den digitala plattformen och termineras senast vid stängning av användarens webbläsarinstans.',
    'cookie.section2.title': 'Avsnitt 2 - Obligatoriska Spårningsteknologier för tjänsteutförande',
    'cookie.section2.p1': 'Obligatoriska Spårningsteknologier avser sådana teknologiska implementeringar vilka erfordras för att Tjänsteleverantören skall kunna tillhandahålla grundläggande och essentiell funktionalitet till användaren.',
    'cookie.section2.legal': 'Rättslig grund för behandling:',
    'cookie.section2.legal_value': 'Berättigat intresse i enlighet med artikel 6.1(f) GDPR',
    'cookie.section2.storage': 'Lagringsperiod:',
    'cookie.section2.storage_value': 'Upp till tjugofyra (24) månader för persistenta Spårningsteknologier; sessionsbaserade Spårningsteknologier termineras omedelbart vid avslutning av webbläsarinstans.',
    'cookie.section3.title': 'Avsnitt 3 - Spårningsteknologier för analytiska ändamål',
    'cookie.section3.p1': 'Tjänsteleverantören tillmäter betydande vikt vid förmågan att kvantifiera besöksfrekvens, registrera interaktionsdata på den digitala plattformen samt i mobila applikationer, och i övrigt analysera hur Tjänsteleverantörens digitala tjänster nyttjas av användare.',
    'cookie.section3.legal_value': 'Samtycke i enlighet med artikel 6.1(a) GDPR',
    'cookie.section3.storage_value': 'Upp till tjugosex (26) månader',
    'cookie.section4.title': 'Avsnitt 4 - Spårningsteknologier för personaliseringsändamål',
    'cookie.section4.p1': 'Spårningsteknologier implementerade för personaliseringsändamål har till syfte att anpassa användarens upplevelse på Tjänsteleverantörens digitala plattformar genom att tillhandahålla innehåll med förhöjd personlig relevans för den enskilde användaren.',
    'cookie.section5.title': 'Avsnitt 5 - Spårningsteknologier för marknadsföringsändamål',
    'cookie.section5.p1': 'Tjänsteleverantören implementerar Spårningsteknologier för marknadsföringsändamål, under förutsättning att användaren har avgivit erforderligt samtycke, i syfte att möjliggöra exponering av relevant reklaminnehåll.',
    'cookie.section5.p2': 'Genom implementering av Spårningsteknologier säkerställer Tjänsteleverantören därtill att reklamexponeringar begränsas till ett fördefinierat antal tillfällen per användare.',
    'cookie.section5.p3': 'Det bör noteras att i det fall användaren väljer att deaktivera Spårningsteknologier för marknadsföringsändamål, kommer reklaminnehåll fortfarande att exponeras för användaren på Tjänsteleverantörens digitala plattformar, dock utan personanpassning.',
    'cookie.section5.storage_value': 'Upp till tjugofyra (24) månader',
    'cookie.section6.title': 'Avsnitt 6 - Internationella dataöverföringar till tredje land',
    'cookie.section6.p1': 'I Tjänsteleverantörens samtyckeshanteringsverktyg tillhandahålls information avseende vilka Spårningsteknologier och samarbetspartners som genomför behandling av användarens personuppgifter inom, alternativt överför användarens personuppgifter till, jurisdiktioner utanför Europeiska Unionen respektive Europeiska Ekonomiska Samarbetsområdet.',
    'cookie.section7.title': 'Avsnitt 7 - Instruktioner för borttagning av Spårningsteknologier',
    'cookie.section7.p1': 'Användaren har möjlighet att avlägsna Spårningsteknologier från sin terminalutrustning eller mobila enhet samt konfigurera inställningar för lokal datalagring via webbläsarens inställningsgränssnitt.',
    'cookie.section7.p2': 'För detaljerade instruktioner avseende hantering och borttagning av Spårningsteknologier, hänvisas till hjälpalternativet i användarens webbläsare alternativt nedanstående hyperlänkar för respektive webbläsarapplikation:',
    
    // Auth
    'auth.loading': 'Laddar...',
    'auth.login': 'Logga in',
    'auth.create_account': 'Skapa konto',
    'auth.login_subtitle': 'Logga in genom att välja något av alternativen nedan.',
    'auth.signup_subtitle': 'Skapa ett nytt konto för att komma igång.',
    'auth.continue_apple': 'Fortsätt med Apple',
    'auth.continue_google': 'Fortsätt med Google',
    'auth.or': 'eller',
    'auth.email': 'E-post',
    'auth.email_placeholder': 'Ange e-post',
    'auth.password': 'Lösenord',
    'auth.password_placeholder': 'Ange lösenord',
    'auth.account_type': 'Kontotyp',
    'auth.user_role': 'Användare - Jag vill köpa/hyra bostad',
    'auth.agent_role': 'Mäklare - Jag vill sälja/hyra ut bostäder',
    'auth.continue': 'Fortsätt',
    'auth.no_account': 'Har du inget konto? Skapa konto',
    'auth.have_account': 'Har du redan ett konto? Logga in',
    'auth.login_failed': 'Inloggning misslyckades',
    'auth.invalid_credentials': 'Felaktig e-post eller lösenord',
    'auth.welcome_back': 'Välkommen tillbaka!',
    'auth.logged_in': 'Du har loggats in.',
    'auth.signup_failed': 'Registrering misslyckades',
    'auth.account_created': 'Konto skapat!',
    'auth.check_email': 'Kontrollera din e-post för att bekräfta ditt konto.',
    'auth.error': 'Ett fel uppstod',
    'auth.try_again': 'Försök igen senare.',
    'auth.broker_interest_title': 'Är du mäklare?',
    'auth.broker_interest_description': 'Om du är intresserad av att annonsera fastigheter på Aqarnet, skicka en intresseanmälan så återkommer vi.',
    'auth.broker_contact_us': 'Kontakta oss',
    'auth.broker_email_subject': 'Intresseanmälan mäklarregistrering',
    'auth.broker_email_body': 'Hej,\n\nJag är intresserad av att registrera mig som mäklare på Aqarnet.\n\nNamn:\nByrånamn:\nTelefon:\nStad:\n\nTack!',
    
    // Favorites Page
    'favorites.title': 'Mina favoriter',
    'favorites.saved_properties': 'sparade bostäder',
    'favorites.login_required': 'Logga in för att se dina favoriter',
    'favorites.login_description': 'Spara bostäder du gillar för att enkelt hitta dem senare.',
    'favorites.loading': 'Laddar dina favoriter...',
    'favorites.no_favorites': 'Inga favoriter än',
    'favorites.start_saving': 'Börja spara bostäder genom att klicka på hjärtat på de annonser du gillar.',
    'favorites.search_properties': 'Sök bostäder',
    
    // Profile Page
    'profile.title': 'Min profil',
    'profile.subtitle': 'Hantera dina personliga uppgifter och inställningar',
    'profile.loading': 'Laddar profil...',
    'profile.fetch_error': 'Fel vid hämtning av profil',
    'profile.fetch_error_description': 'Kunde inte hämta profilinformation.',
    'profile.unnamed_user': 'Namnlös användare',
    'profile.personal_info': 'Personuppgifter',
    'profile.update_info': 'Uppdatera din personliga information',
    'profile.email': 'E-postadress',
    'profile.email_readonly': 'E-postadressen kan inte ändras',
    'profile.display_name': 'Visningsnamn',
    'profile.display_name_placeholder': 'Ditt namn',
    'profile.phone': 'Telefonnummer',
    'profile.phone_placeholder': '070-123 45 67',
    'profile.save_changes': 'Spara ändringar',
    'profile.saving': 'Sparar...',
    'profile.reset': 'Återställ',
    'profile.updated': 'Profil uppdaterad',
    'profile.updated_description': 'Din profil har sparats.',
    'profile.update_error': 'Fel vid uppdatering',
    'profile.update_error_description': 'Kunde inte spara ändringar.',
    'profile.active_listings': 'Aktiva annonser',
    'profile.active_listings_description': 'Dina publicerade fastighetsannonser till salu',
    'profile.view_all': 'Visa alla',
    'profile.loading_listings': 'Laddar annonser...',
    'profile.no_active_listings': 'Du har inga aktiva annonser ännu',
    'profile.more_active_listings': 'Och {count} fler aktiva annonser',
    'profile.sold_properties': 'Sålda fastigheter',
    'profile.sold_properties_description': 'Dina sålda fastigheter',
    'profile.no_sold_properties': 'Du har inga sålda fastigheter ännu',
    'profile.more_sold_properties': 'Och {count} fler sålda fastigheter',
    'profile.listing_fetch_error': 'Fel vid hämtning av annonser',
    'profile.listing_fetch_error_description': 'Kunde inte hämta dina annonser.',
    'profile.account_settings': 'Kontoinställningar',
    'profile.account_settings_description': 'Hantera ditt konto och säkerhet',
    'profile.change_password': 'Ändra lösenord',
    'profile.change_password_description': 'Uppdatera ditt lösenord för säkrare inloggning',
    'profile.change': 'Ändra',
    'profile.two_factor': 'Tvåfaktorsautentisering',
    'profile.two_factor_description': 'Lägg till extra säkerhet för ditt konto',
    'profile.enable': 'Aktivera',
    'profile.delete_account': 'Radera konto',
    'profile.delete_account_description': 'Permanent radering av ditt konto och all data',
    'profile.delete': 'Radera',
    'profile.logout': 'Logga ut',
    
    // Profile Email Notifications
    'profile.email_notifications': 'E-postaviseringar',
    'profile.email_notifications_description': 'Välj vilka e-postmeddelanden du vill få',
    'profile.notif_property_alerts': 'Bostadsaviseringar',
    'profile.notif_property_alerts_desc': 'Bli meddelad om nya bostäder som matchar dina sparade sökningar',
    'profile.notif_saved_search_alerts': 'Uppdateringar för sparade sökningar',
    'profile.notif_saved_search_alerts_desc': 'Få uppdateringar när nya bostäder matchar dina kriterier',
    'profile.notif_newsletter': 'Nyhetsbrev',
    'profile.notif_newsletter_desc': 'Få vårt veckobrev med marknadsinsikter',
    'profile.notif_viewing_confirmations': 'Visningsbekräftelser',
    'profile.notif_viewing_confirmations_desc': 'Få bekräftelsemejl för bokade visningar',
    'profile.save_preferences': 'Spara inställningar',
    'profile.notifications_saved': 'Aviseringsinställningar sparade',
    'profile.notifications_save_error': 'Kunde inte spara aviseringsinställningar',
    
    // Not Found Page
    'notfound.title': '404',
    'notfound.message': 'Hoppsan! Sidan hittades inte',
    'notfound.back_home': 'Tillbaka till startsidan',
    
    // Property Detail Page
    'property.back_to_listings': 'Tillbaka till annonser',
    'property.not_found': 'Fastighet hittades inte',
    'property.link_copied': 'Länk kopierad',
    'property.link_copied_description': 'Fastighetslänk kopierad till urklipp',
    'property.description': 'Beskrivning',
    'property.features': 'Egenskaper',
    'property.details': 'Fastighetsdetaljer',
    'property.rooms_label': 'Rum',
    'property.size_label': 'Storlek',
    'property.plot_size_label': 'Tomtstorlek',
    'property.monthly_fee_label': 'Månadsavgift',
    'property.contact_agent': 'Kontakta mäklare',
    'property.send_message': 'Skicka meddelande',
    'property.schedule_viewing': 'Boka visning',
    'property.section.details': 'Detaljer',
    'property.section.amenities': 'Bekvämligheter',
    'property.section.overview': 'Översikt',
    'property.section.payment_plan': 'Betalningsplan',
    'property.section.floor_plans': 'Planritningar',
    'property.section.view_map': 'Visa karta',
    'property.copy_link': 'Kopiera länk',
    'property.share': 'Dela',
    'property.share_text': 'Kolla in denna fastighet',
    'property.posted_on': 'Publicerad',
    'property.type': 'Typ',
    'property.status': 'Status',
    'property.available': 'Tillgänglig',
    'property.no_amenities': 'Inga bekvämligheter listade för denna fastighet',
    'property.no_description': 'Ingen beskrivning tillgänglig',
    'property.location': 'Plats',
    'property.payment_plan_info': 'Kontakta mäklaren för betalningsplan',
    'property.contact_for_payment_plan': 'Kontakt för betalningsplan',
    'property.no_floor_plans': 'Inga planritningar tillgängliga',
    'property.request_floor_plans': 'Begär planritningar',
    'property.no_map_location': 'Plats ej tillgänglig på karta',
    
    // News Page
    'news.subtitle': 'Håll dig uppdaterad med de senaste bostadsnyheterna, marknadsinsikter och guider',
    'news.no_articles': 'Inga nyhetsartiklar ännu',
    'news.check_back': 'Kom tillbaka snart för de senaste bostadsnyheterna och marknadsinsikterna.',
    'news.search_placeholder': 'Sök artiklar...',
    'news.showing_filtered': 'Visar {shown} av {total} artiklar på denna sida',
    'news.clear_filters': 'Rensa filter',
    'news.no_matching': 'Inga matchande artiklar',
    'news.adjust_filters': 'Försök justera din sökning eller filter för att hitta det du letar efter.',
    'news.publisher': 'Utgivare',
    'news.page_info': 'Sida {current} av {total} • {count} artiklar totalt',
    'news.category.all': 'Alla',
    'news.category.market_news': 'Marknadsnyheter',
    'news.category.property_guides': 'Bostadsguider',
    'news.category.investment_tips': 'Investeringstips',
    'news.category.industry_updates': 'Branschuppdateringar',
    'news.category.announcements': 'Meddelanden',
    'news.category.expert_advice': 'Expertråd',
    
    // Filters/Sorting
    'filters.any': 'Alla',
    'filters.newest_first': 'Senaste först',
    'filters.price_low_high': 'Pris: Lägst till högst',
    'filters.price_high_low': 'Pris: Högst till lägst',
    'filters.size': 'Storlek',
    'filters.searching_for': 'Söker efter',
    'filters.search_areas': 'Sök områden, städer etc...',
    
    // Toast Messages
    'toast.error': 'Fel',
    'toast.success': 'Lyckades',
    'toast.auth_required': 'Autentisering krävs',
    'toast.sign_in_required': 'Vänligen logga in för att fortsätta',
    'toast.sign_in_favorites': 'Vänligen logga in för att lägga till favoriter',
    'toast.sign_in_properties': 'Vänligen logga in för att lägga till bostäder',
    'toast.properties_load_error': 'Kunde inte ladda bostäder',
    'toast.property_added': 'Bostad tillagd',
    'toast.property_add_error': 'Kunde inte lägga till bostad',
    'toast.property_updated': 'Bostad uppdaterad',
    'toast.property_update_error': 'Kunde inte uppdatera bostad',
    'toast.property_deleted': 'Bostad raderad',
    'toast.property_delete_error': 'Kunde inte radera bostad',
    'toast.property_marked_sold': 'Bostad markerad som såld',
    'toast.property_sold_error': 'Kunde inte markera bostad som såld',
    'toast.invalid_price': 'Ange ett giltigt försäljningspris',
    'toast.favorites_load_error': 'Kunde inte ladda favoriter',
    'toast.added_to_favorites': 'Tillagd i favoriter',
    'toast.add_favorites_error': 'Kunde inte lägga till i favoriter',
    'toast.removed_from_favorites': 'Borttagen från favoriter',
    'toast.remove_favorites_error': 'Kunde inte ta bort från favoriter',
    'toast.search_saved': 'Sökning sparad',
    'toast.search_saved_desc': 'har sparats med e-postaviseringar aktiverade',
    'toast.search_save_error': 'Kunde inte spara sökningen',
    'toast.valuation_complete': 'Värdering klar',
    'toast.valuation_estimated': 'Uppskattat värde',
    'toast.valuation_error': 'Kunde inte hämta värdering',
    'toast.message_sent': 'Meddelande skickat',
    'toast.agent_contact_desc': 'Mäklaren kommer att kontakta dig inom kort',
    'toast.message_error': 'Kunde inte skicka meddelandet',
    'toast.viewing_booked': 'Visning bokad',
    'toast.viewing_confirmed': 'Din visning har bokats och bekräftelse skickats via e-post',
    'toast.viewing_error': 'Kunde inte boka visningen',
    'toast.image_analysis_complete': 'Bildanalys klar',
    'toast.image_analysis_desc': 'AI-analys av bilden har slutförts',
    'toast.image_analysis_error': 'Kunde inte analysera bilden',
    'toast.logged_out': 'Utloggad',
    'toast.welcome_back': 'Välkommen tillbaka när som helst!',
    
    // Recent searches
    'search.properties_label': 'Bostäder',
    'search.from': 'från',
    'search.up_to': 'upp till',
    
    // News Management
    'news_mgmt.title': 'Nyhetshantering',
    'news_mgmt.subtitle': 'Skapa och hantera nyhetsartiklar',
    'news_mgmt.create_article': 'Skapa artikel',
    'news_mgmt.edit_article': 'Redigera artikel',
    'news_mgmt.create_new': 'Skapa ny artikel',
    'news_mgmt.all_articles': 'Alla artiklar',
    'news_mgmt.no_articles': 'Inga artiklar ännu. Skapa din första!',
    'news_mgmt.delete_article': 'Radera artikel',
    'news_mgmt.delete_confirm': 'Är du säker på att du vill radera denna artikel? Denna åtgärd kan inte ångras.',
    'news_mgmt.title_label': 'Titel',
    'news_mgmt.title_placeholder': 'Ange artikelns titel',
    'news_mgmt.category_label': 'Kategori',
    'news_mgmt.category_placeholder': 'T.ex. Marknadsnyheter, Guider, Tips',
    'news_mgmt.image_url': 'Bild-URL',
    'news_mgmt.content': 'Innehåll',
    'news_mgmt.content_placeholder': 'Ange artikelns innehåll',
    'news_mgmt.published': 'Publicerad',
    'news_mgmt.draft': 'Utkast',
    'news_mgmt.update_article': 'Uppdatera artikel',
    'news_mgmt.required_fields': 'Titel och kategori krävs',
    'news_mgmt.article_updated': 'Nyhetsartikel uppdaterad',
    'news_mgmt.article_created': 'Nyhetsartikel skapad',
    'news_mgmt.article_save_error': 'Kunde inte spara nyhetsartikel',
    'news_mgmt.article_deleted': 'Nyhetsartikel raderad',
    'news_mgmt.article_delete_error': 'Kunde inte radera nyhetsartikel',
    'news_mgmt.access_denied': 'Åtkomst nekad',
    'news_mgmt.access_denied_desc': 'Du har inte behörighet att komma åt denna sida. Endast utgivare och administratörer kan hantera nyheter.',
    
    // Badges & Labels
    'badge.new': 'Ny',
    'badge.sold': 'Såld',
    'badge.featured': 'Utvald',
    'badge.reduced': 'Prissänkt',
    'badge.new_construction': 'Nyproduktion',
    
    // Confirmation Messages
    'confirm.delete': 'Radera',
    'confirm.cancel': 'Avbryt',
    'confirm.yes': 'Ja',
    'confirm.no': 'Nej',
    'confirm.are_you_sure': 'Är du säker?',
    'confirm.action_irreversible': 'Denna åtgärd kan inte ångras.',
    
    // Tooltips
    'tooltip.favorite': 'Lägg till i favoriter',
    'tooltip.share': 'Dela bostad',
    'tooltip.edit': 'Redigera bostad',
    'tooltip.delete': 'Radera bostad',
    'tooltip.contact': 'Kontakta mäklare',
    'tooltip.schedule': 'Boka visning',
    'tooltip.map_view': 'Visa på karta',
    'tooltip.list_view': 'Listvy',
    'tooltip.grid_view': 'Rutnätsvy',
    
    // Admin Pages
    'admin.loading': 'Laddar...',
    'admin.access_denied': 'Åtkomst nekad. Administratörsbehörighet krävs.',
    
    // Admin Notification Settings
    'admin.notifications.title': 'Aviseringsinställningar',
    'admin.notifications.description': 'Hantera aviseringsinställningar för alla användartyper. Aktivera, inaktivera eller dölj aviseringar efter behov.',
    'admin.notifications.fetch_error': 'Kunde inte hämta aviseringsinställningar',
    'admin.notifications.update_success': 'Aviseringsinställning uppdaterad',
    'admin.notifications.update_error': 'Kunde inte uppdatera aviseringsinställning',
    'admin.notifications.role_description': 'Hantera aviseringar för {role}',
    'admin.notifications.hidden': 'Dold',
    'admin.notifications.disabled': 'Inaktiverad',
    'admin.notifications.active': 'Aktiv',
    'admin.notifications.hide': 'Dölj',
    'admin.notifications.roles.user': 'Användare',
    'admin.notifications.roles.maklare': 'Mäklare',
    'admin.notifications.roles.agency': 'Byråer',
    'admin.notifications.roles.admin': 'Administratörer',
    'admin.notifications.roles.publisher': 'Publicerare',
    'admin.notifications.types.new_viewing_request': 'Ny visningsförfrågan',
    'admin.notifications.types.new_client_message': 'Nytt kundmeddelande',
    'admin.notifications.types.property_alerts': 'Fastighetsnotiser',
    'admin.notifications.types.weekly_summary': 'Veckans sammanfattning',
    'admin.notifications.types.broker_property_added': 'Mäklare lade till fastighet',
    'admin.notifications.types.broker_viewing_request': 'Mäklares visningsförfrågan',
    'admin.notifications.types.broker_client_message': 'Mäklares kundmeddelande',
    'admin.notifications.types.broker_joined': 'Mäklare gick med',
    'admin.notifications.types.weekly_agency_summary': 'Veckans byråsammanfattning',
    'admin.notifications.types.saved_search_alerts': 'Sparade söknotiser',
    'admin.notifications.types.favorite_property_updates': 'Favoritfastighetsuppdateringar',
    'admin.notifications.types.newsletter': 'Nyhetsbrev',
    'admin.notifications.types.system_alerts': 'Systemvarningar',
    'admin.notifications.types.new_user_signup': 'Ny användarregistrering',
    'admin.notifications.types.article_published': 'Artikel publicerad',

    // Admin Settings
    'admin.settings': 'Administratörsinställningar',
    'admin.messaging_settings': 'Meddelandeinställningar',
    'admin.messaging_settings_description': 'Styr meddelanden och samtalsfunktioner för alla användare',
    'admin.messaging': 'Meddelanden',
    'admin.messaging_description': 'Tillåt användare att skicka meddelanden',
    'admin.voice_calls': 'Röstsamtal',
    'admin.voice_calls_description': 'Tillåt användare att ringa röstsamtal',
    'admin.video_calls': 'Videosamtal',
    'admin.video_calls_description': 'Tillåt användare att ringa videosamtal',
    'admin.screen_sharing': 'Skärmdelning',
    'admin.screen_sharing_description': 'Tillåt användare att dela sin skärm under samtal',

    'location_mgr.title': 'Platshanterare',
    'location_mgr.current_locations': 'Nuvarande platser',
    'location_mgr.locations_in_db': '{count} platser i databasen',
    'location_mgr.refresh': 'Uppdatera',
    'location_mgr.populate_title': 'Fyll i platser',
    'location_mgr.populate_desc': 'Hämta alla irakiska städer och områden från Google Places API och spara dem i databasen. Detta ersätter alla befintliga platser.',
    'location_mgr.populating': 'Fyller i...',
    'location_mgr.populate_btn': 'Fyll i från Google Places',
    'location_mgr.success': 'Lyckades',
    'location_mgr.success_msg': 'Platser fyllda i',
    'location_mgr.error': 'Fel',
    'location_mgr.error_msg': 'Kunde inte fylla i platser. Kontrollera konsolen för detaljer.',
    
    // Map Configuration Manager
    'map_config.title': 'Kartkonfigurationshanterare',
    'map_config.subtitle': 'Hantera dina permanenta kartkonfigurationer för att minimera användningen av Google Maps API',
    'map_config.loading': 'Laddar kartkonfigurationer...',
    'map_config.create': 'Skapa konfiguration',
    'map_config.create_title': 'Skapa kartkonfiguration',
    'map_config.create_desc': 'Skapa en ny permanent kartkonfiguration som kommer att användas istället för att anropa Google Maps API upprepade gånger.',
    'map_config.edit_title': 'Redigera kartkonfiguration',
    'map_config.edit_desc': 'Uppdatera din permanenta kartkonfiguration.',
    'map_config.name': 'Konfigurationsnamn',
    'map_config.name_placeholder': 't.ex. Irak fastighetskarta',
    'map_config.description': 'Beskrivning (valfritt)',
    'map_config.description_placeholder': 'Kort beskrivning av denna konfiguration',
    'map_config.map_data': 'Kartkonfiguration (JSON)',
    'map_config.map_data_placeholder': 'Kartkonfiguration i JSON-format',
    'map_config.cancel': 'Avbryt',
    'map_config.update': 'Uppdatera konfiguration',
    'map_config.no_configs': 'Inga kartkonfigurationer',
    'map_config.no_configs_desc': 'Skapa din första kartkonfiguration för att börja hantera dina kartor effektivt.',
    'map_config.create_first': 'Skapa första konfigurationen',
    'map_config.active': 'Aktiv',
    'map_config.activate': 'Aktivera',
    'map_config.edit': 'Redigera',
    'map_config.delete': 'Ta bort',
    'map_config.delete_confirm': 'Är du säker?',
    'map_config.delete_desc': 'Detta tar permanent bort kartkonfigurationen "{name}". Denna åtgärd kan inte ångras.',
    'map_config.created': 'Skapad',
    'map_config.last_updated': 'Senast uppdaterad',
    'map_config.map_center': 'Kartcentrum',
    'map_config.zoom_level': 'Zoomnivå',
    'map_config.cities': 'Städer',
    'map_config.cities_configured': '{count} städer konfigurerade',
    'map_config.invalid_json': 'Ogiltigt JSON-format i kartdata',
    
    // About Page
    'about.title': 'Om Aqarnet',
    'about.subtitle': 'Din pålitliga partner på den irakiska fastighetsmarknaden',
    'about.description': 'Aqarnet är Iraks ledande fastighetsplattform som kopplar samman köpare, säljare och mäklare över hela landet. Vi erbjuder omfattande fastighetslistor, marknadsinsikter och professionella verktyg för att göra din fastighetsresa smidig.',
    'about.mission_title': 'Vårt uppdrag',
    'about.mission_text': 'Att revolutionera den irakiska fastighetsmarknaden genom att erbjuda transparent, tillgänglig och pålitlig fastighetsinformation till alla.',
    'about.vision_title': 'Vår vision',
    'about.vision_text': 'Att bli den mest pålitliga fastighetsplattformen i Mellanöstern och ge alla möjlighet att hitta sitt drömhem.',
    'about.services_title': 'Våra tjänster',
    'about.services_text': 'Fastighetslistor, marknadsanalys, mäklarkontakter, fastighetsvärderingar och omfattande fastighetsnyheter.',
    'about.team_title': 'Vårt team',
    'about.team_text': 'Ett engagerat team av fastighetsproffs, teknikexperter och kundtjänstspecialister.',
    'about.company_info': 'Företagsinformation',
    'about.based_in': 'Baserat i Sverige, verksamt i Irak',
    
    // Contact Page
    'contact.title': 'Kontakta oss',
    'contact.subtitle': 'Vi finns här för att hjälpa. Kontakta oss med frågor eller funderingar.',
    'contact.email_label': 'E-post',
    'contact.phone_label': 'Telefon',
    'contact.phone_info': 'Kommer snart',
    'contact.location_label': 'Plats',
    'contact.location_info': 'Sverige',
    'contact.form_title': 'Skicka ett meddelande',
    'contact.form_description': 'Fyll i formuläret nedan så återkommer vi så snart som möjligt.',
    'contact.name_placeholder': 'Ditt namn',
    'contact.email_placeholder': 'din@email.com',
    'contact.subject': 'Ämne',
    'contact.subject_placeholder': 'Vad gäller det?',
    'contact.message_placeholder': 'Ditt meddelande...',
    'contact.success': 'Meddelande skickat! Vi återkommer snart.',
    
    // Press Page
    'press.title': 'Press & Media',
    'press.subtitle': 'Senaste nyheter och pressmeddelanden från Aqarnet',
    'press.contact_title': 'Presskontakt',
    'press.contact_description': 'För pressfrågor, kontakta oss via e-post.',
    'press.add_release': 'Lägg till pressmeddelande',
    'press.edit_release': 'Redigera pressmeddelande',
    'press.new_release': 'Nytt pressmeddelande',
    'press.release_title': 'Titel',
    'press.content': 'Innehåll',
    'press.publish': 'Publicera',
    'press.no_releases': 'Inga pressmeddelanden ännu.',
    'press.delete_confirm_title': 'Radera pressmeddelande?',
    'press.delete_confirm_description': 'Denna åtgärd kan inte ångras.',
    
    // Investors Page
    'investors.title': 'För investerare',
    'investors.subtitle': 'Följ med oss i att omvandla den irakiska fastighetsmarknaden',
    'investors.growth_title': 'Tillväxtpotential',
    'investors.growth_text': 'Iraks fastighetsmarknad upplever snabb tillväxt med betydande investeringsmöjligheter.',
    'investors.market_title': 'Marknadsledare',
    'investors.market_text': 'Aqarnet är positionerat som den ledande digitala plattformen inom den irakiska fastighetssektorn.',
    'investors.vision_title': 'Regional expansion',
    'investors.vision_text': 'Vår vision sträcker sig bortom Irak för att bli den ledande plattformen i Mellanösternregionen.',
    'investors.opportunity_title': 'Investeringsmöjlighet',
    'investors.opportunity_text': 'Aqarnet erbjuder en unik möjlighet att investera i den digitala omvandlingen av en av världens största framväxande fastighetsmarknader.',
    'investors.opportunity_text_2': 'Med en växande användarbas och utökade tjänster är vi väl positionerade för hållbar tillväxt och marknadsledarskap.',
    'investors.contact_title': 'Kontakta oss',
    'investors.contact_description': 'Intresserad av investeringsmöjligheter? Kontakta oss för att lära dig mer.',
    'investors.company': 'Företag / Organisation',
    'investors.company_placeholder': 'Ditt företagsnamn',
    'investors.message_placeholder': 'Berätta om dina investeringsintressen...',
    'investors.success': 'Tack för ditt intresse! Vi hör av oss snart.',
    
    // Advertising Page
    'advertising.title': 'Annonsering & Marknadsföring',
    'advertising.subtitle': 'Nå tusentals potentiella köpare och hyresgäster i Irak',
    'advertising.targeted_title': 'Riktad räckvidd',
    'advertising.targeted_text': 'Nå användare som aktivt letar efter fastigheter i specifika områden och prisklasser.',
    'advertising.audience_title': 'Kvalitetspublik',
    'advertising.audience_text': 'Anslut till seriösa köpare och hyresgäster som är redo att fatta beslut.',
    'advertising.analytics_title': 'Detaljerad analys',
    'advertising.analytics_text': 'Följ din kampanjprestanda med omfattande analyser och insikter.',
    'advertising.formats_title': 'Flera format',
    'advertising.formats_text': 'Bannerannonser, utvalda objekt, sponsrat innehåll och fler annonseringsalternativ.',
    'advertising.contact_title': 'Börja annonsera',
    'advertising.contact_description': 'Kontakta oss för att diskutera annonseringsmöjligheter.',
    'advertising.company': 'Företag / Organisation',
    'advertising.company_placeholder': 'Ditt företagsnamn',
    'advertising.budget': 'Budgetintervall',
    'advertising.budget_placeholder': 't.ex. 5000 - 10000 kr/månad',
    'advertising.message_placeholder': 'Berätta om dina annonseringsmål...',
    'advertising.success': 'Tack! Vi kontaktar dig om annonseringsmöjligheter.',
    
    // Jobs Page
    'jobs.title': 'Karriär på Aqarnet',
    'jobs.subtitle': 'Gå med i vårt team och hjälp forma framtiden för fastigheter i Irak',
    'jobs.add_job': 'Lägg till jobb',
    'jobs.edit_job': 'Redigera jobb',
    'jobs.new_job': 'Ny jobbannons',
    'jobs.job_title': 'Jobbtitel',
    'jobs.department': 'Avdelning',
    'jobs.location': 'Plats',
    'jobs.employment_type': 'Anställningstyp',
    'jobs.full_time': 'Heltid',
    'jobs.part_time': 'Deltid',
    'jobs.contract': 'Kontrakt',
    'jobs.internship': 'Praktik',
    'jobs.description': 'Beskrivning',
    'jobs.requirements': 'Krav',
    'jobs.benefits': 'Förmåner',
    'jobs.publish': 'Publicera',
    'jobs.no_jobs': 'Inga lediga tjänster just nu.',
    'jobs.check_back': 'Kolla tillbaka senare för nya möjligheter.',
    'jobs.apply_now': 'Ansök nu',
    'jobs.apply_for': 'Ansök till',
    'jobs.apply_description': 'Skicka din ansökan via e-post.',
    'jobs.application_sent': 'Ansökan skickad! Lycka till!',
    'jobs.delete_confirm_title': 'Radera jobbannons?',
    'jobs.delete_confirm_description': 'Denna åtgärd kan inte ångras.',
    
    // Property Statistics Page
    'statistics.title': 'Bostadsstatistik',
    'statistics.subtitle': 'Marknadsinsikter och trender baserat på sålda fastigheter',
    'statistics.6_months': 'Senaste 6 månaderna',
    'statistics.12_months': 'Senaste 12 månaderna',
    'statistics.24_months': 'Senaste 24 månaderna',
    'statistics.36_months': 'Senaste 36 månaderna',
    'statistics.all_cities': 'Alla städer',
    'statistics.total_sold': 'Totalt sålt',
    'statistics.avg_price': 'Genomsnittspris',
    'statistics.avg_price_sqm': 'Snitt pris/m²',
    'statistics.price_change': 'Prisförändring',
    'statistics.price_trends': 'Pristrender',
    'statistics.price_trends_desc': 'Genomsnittliga fastighetspriser över tid',
    'statistics.sales_volume': 'Försäljningsvolym',
    'statistics.sales_volume_desc': 'Antal sålda fastigheter per månad',
    'statistics.properties_sold': 'Sålda fastigheter',
    'statistics.price_by_city': 'Pris per m² per stad',
    'statistics.price_by_city_desc': 'Jämförelse av genomsnittligt pris per kvadratmeter mellan städer',
    'statistics.price_per_sqm': 'Pris per m²',
    'statistics.type_distribution': 'Fastighetstypsfördelning',
    'statistics.type_distribution_desc': 'Fördelning av sålda fastigheter efter typ',
    'statistics.avg_days_market': 'Snitt dagar på marknaden',
    'statistics.days': 'dagar',
    'statistics.price_ranges': 'Prisintervall fördelning',
    'statistics.price_ranges_desc': 'Antal sålda fastigheter i varje prisintervall',
    'statistics.properties': 'Fastigheter',
    'statistics.year_over_year': 'Årsjämförelse',
    'statistics.year_over_year_desc': 'Jämför genomsnittspriser och försäljningsvolym mellan år',
    'statistics.all_types': 'Alla bostadstyper',
    'statistics.all_rooms': 'Alla rum',
    'statistics.rooms': 'rum',
    'statistics.export': 'Exportera',
    'statistics.export_csv': 'Ladda ner CSV',
    'statistics.export_json': 'Ladda ner JSON',
    'statistics.share': 'Dela',
    'statistics.link_copied': 'Länk kopierad',
    'statistics.link_copied_desc': 'Länken till statistikrapporten har kopierats till urklipp',
    'statistics.custom_date': 'Anpassat datum',
    'statistics.use_preset': 'Använd förinställd',
    'statistics.start_date': 'Startdatum',
    'statistics.end_date': 'Slutdatum',
    'statistics.city_comparison': 'Stadsjämförelse',
    'statistics.city_comparison_desc': 'Jämför fastighetsstatistik mellan två städer sida vid sida',
    'statistics.select_city_1': 'Välj första stad',
    'statistics.select_city_2': 'Välj andra stad',
    'statistics.vs': 'vs',
    'statistics.select_cities_to_compare': 'Välj två städer ovan för att jämföra deras statistik',
    'statistics.avg_size': 'Snittarea',
    
    // Cookie Settings
    'cookie_settings.title': 'Cookie-inställningar',
    'cookie_settings.description': 'Vi använder cookies för att förbättra din surfupplevelse. Du kan anpassa dina cookie-preferenser nedan.',
    'cookie_settings.necessary': 'Nödvändiga cookies',
    'cookie_settings.necessary_desc': 'Dessa cookies är nödvändiga för att webbplatsen ska fungera korrekt. De kan inte inaktiveras.',
    'cookie_settings.functional': 'Funktionella cookies',
    'cookie_settings.functional_desc': 'Dessa cookies möjliggör personliga funktioner som språkpreferenser och sparade inställningar.',
    'cookie_settings.analytics': 'Analytiska cookies',
    'cookie_settings.analytics_desc': 'Dessa cookies hjälper oss förstå hur besökare interagerar med vår webbplats för att förbättra användarupplevelsen.',
    'cookie_settings.marketing': 'Marknadsförings-cookies',
    'cookie_settings.marketing_desc': 'Dessa cookies används för att visa relevanta annonser och spåra effektiviteten av våra kampanjer.',
    'cookie_settings.accept_all': 'Acceptera alla',
    'cookie_settings.reject_all': 'Avvisa alla',
    'cookie_settings.save_preferences': 'Spara inställningar',
    'cookie_settings.saved': 'Cookie-inställningar sparade',
    
    // Cookie Banner
    'cookie_banner.title': 'Vi använder cookies',
    'cookie_banner.description': 'Vi använder cookies för att förbättra din surfupplevelse, visa personligt innehåll och analysera vår trafik. Genom att klicka på "Acceptera alla" samtycker du till vår användning av cookies.',
    'cookie_banner.accept_all': 'Acceptera alla',
    'cookie_banner.reject_all': 'Avvisa alla',
    'cookie_banner.customize': 'Anpassa',
    
    // Terms of Service
    'terms.title': 'Användarvillkor',
    'terms.last_updated': 'Senast uppdaterad:',
    'terms.section1.title': '1. Godkännande av villkor',
    'terms.section1.p1': 'Genom att använda Aqarnet accepterar du att vara bunden av dessa användarvillkor. Om du inte godkänner dessa villkor, vänligen använd inte våra tjänster.',
    'terms.section1.p2': 'Vi förbehåller oss rätten att ändra dessa villkor när som helst. Fortsatt användning efter ändringar utgör ett godkännande av de ändrade villkoren.',
    'terms.section2.title': '2. Beskrivning av tjänsten',
    'terms.section2.p1': 'Aqarnet tillhandahåller en digital plattform för fastighetsannonser, bostadssökningar och kontakt mellan köpare, säljare och mäklare i Irak.',
    'terms.section2.p2': 'Våra tjänster inkluderar fastighetslistor, sökfunktioner, sparade sökningar, favoriter och kommunikationsverktyg mellan användare och mäklare.',
    'terms.section3.title': '3. Användaransvar',
    'terms.section3.p1': 'Som användare av Aqarnet samtycker du till att:',
    'terms.section3.li1': 'Tillhandahålla korrekt och sanningsenlig information i dina annonser och profil',
    'terms.section3.li2': 'Inte använda plattformen för bedrägliga eller olagliga aktiviteter',
    'terms.section3.li3': 'Respektera andras immateriella rättigheter',
    'terms.section3.li4': 'Inte störa tjänstens funktion',
    'terms.section4.title': '4. Fastighetsannonser',
    'terms.section4.p1': 'Användare som listar fastigheter ansvarar för att deras annonser är korrekta. Aqarnet verifierar inte fastighetsinformationens riktighet.',
    'terms.section4.p2': 'Vi förbehåller oss rätten att ta bort annonser som bryter mot våra villkor eller rapporteras som bedrägliga.',
    'terms.section5.title': '5. Immateriella rättigheter',
    'terms.section5.p1': 'Allt innehåll på Aqarnet, inklusive text, grafik, logotyper och programvara, tillhör Aqarnet eller dess innehållsleverantörer.',
    'terms.section5.p2': 'Du får inte reproducera, distribuera eller skapa härledda verk från vårt innehåll utan uttryckligt skriftligt tillstånd.',
    'terms.section6.title': '6. Ansvarsbegränsning',
    'terms.section6.p1': 'Aqarnet tillhandahålls "i befintligt skick" utan garantier av något slag. Vi är inte ansvariga för skador som uppstår från din användning av våra tjänster.',
    'terms.section7.title': '7. Tillämplig lag',
    'terms.section7.p1': 'Dessa villkor regleras av svensk lag. Eventuella tvister ska avgöras i svenska domstolar.',
    'terms.section7.p2': 'Om någon bestämmelse i dessa villkor anses vara ogiltig, förblir övriga bestämmelser i kraft.',
    'terms.section8.title': '8. Kontaktinformation',
    'terms.section8.p1': 'För frågor om dessa användarvillkor, vänligen kontakta oss:',
    'terms.section8.email': 'E-post',
    
    // Privacy Policy
    'privacy.title': 'Integritetspolicy',
    'privacy.last_updated': 'Senast uppdaterad:',
    'privacy.section1.title': '1. Introduktion',
    'privacy.section1.p1': 'På Aqarnet är vi engagerade i att skydda din integritet och personuppgifter. Denna policy förklarar hur vi samlar in, använder och skyddar din information.',
    'privacy.section1.p2': 'Genom att använda våra tjänster samtycker du till de datametoder som beskrivs i denna policy.',
    'privacy.section2.title': '2. Information vi samlar in',
    'privacy.section2.p1': 'Vi samlar in följande typer av information:',
    'privacy.section2.li1': 'Kontoinformation: namn, e-postadress, telefonnummer',
    'privacy.section2.li2': 'Fastighetsdata: adresser, beskrivningar, foton, priser',
    'privacy.section2.li3': 'Användningsdata: sökhistorik, sparade fastigheter, surfbeteende',
    'privacy.section2.li4': 'Enhetsinformation: IP-adress, webbläsartyp, operativsystem',
    'privacy.section3.title': '3. Hur vi använder din information',
    'privacy.section3.p1': 'Vi använder din information för att:',
    'privacy.section3.li1': 'Tillhandahålla och förbättra våra tjänster',
    'privacy.section3.li2': 'Personanpassa din upplevelse och rekommendationer',
    'privacy.section3.li3': 'Skicka notiser om sparade sökningar och nya objekt',
    'privacy.section3.li4': 'Kommunicera med dig om ditt konto och förfrågningar',
    'privacy.section4.title': '4. Datadelning',
    'privacy.section4.p1': 'Vi säljer inte dina personuppgifter. Vi kan dela information med fastighetsmäklare när du gör förfrågningar, och med tjänsteleverantörer som assisterar vår verksamhet.',
    'privacy.section4.p2': 'Vi kan lämna ut information när det krävs enligt lag eller för att skydda våra rättigheter och säkerhet.',
    'privacy.section5.title': '5. Dina rättigheter',
    'privacy.section5.p1': 'Du har följande rättigheter gällande dina uppgifter:',
    'privacy.section5.li1': 'Tillgång: Begär en kopia av dina personuppgifter',
    'privacy.section5.li2': 'Rättelse: Uppdatera eller korrigera felaktig information',
    'privacy.section5.li3': 'Radering: Begär radering av ditt konto och uppgifter',
    'privacy.section5.li4': 'Portabilitet: Ta emot dina uppgifter i ett portabelt format',
    'privacy.section5.li5': 'Invändning: Avböj vissa databehandlingsaktiviteter',
    'privacy.section6.title': '6. Datasäkerhet',
    'privacy.section6.p1': 'Vi implementerar lämpliga tekniska och organisatoriska åtgärder för att skydda dina uppgifter mot obehörig åtkomst, ändring eller förstörelse.',
    'privacy.section6.p2': 'Dock är ingen överföringsmetod via internet 100% säker, och vi kan inte garantera absolut säkerhet.',
    'privacy.section7.title': '7. Ändringar i denna policy',
    'privacy.section7.p1': 'Vi kan uppdatera denna integritetspolicy då och då. Vi kommer att meddela dig om väsentliga ändringar genom att publicera ett meddelande på vår webbplats eller skicka ett e-postmeddelande.',
    'privacy.section8.title': '8. Kontakta oss',
    'privacy.section8.p1': 'Om du har frågor om denna integritetspolicy eller dina personuppgifter, vänligen kontakta oss:',
    'privacy.section8.email': 'E-post',
    
    // FAQ Page
    'faq.title': 'Vanliga frågor',
    'faq.subtitle': 'Hitta svar på vanliga frågor om att använda Aqarnet',
    'faq.account.title': 'Konto & Registrering',
    'faq.account.q1': 'Hur skapar jag ett konto?',
    'faq.account.q1_answer': 'Klicka på "Logga in" i sidhuvudet och välj sedan "Skapa konto". Du kan registrera dig med din e-post, Google eller Apple-konto.',
    'faq.account.q2': 'Hur återställer jag mitt lösenord?',
    'faq.account.q2_answer': 'På inloggningssidan, klicka på "Glömt lösenord" och ange din e-postadress. Du får en länk för att återställa ditt lösenord.',
    'faq.account.q3': 'Kan jag ändra min e-postadress?',
    'faq.account.q3_answer': 'För närvarande kan du inte ändra din e-postadress direkt. Kontakta vår support för hjälp.',
    'faq.listings.title': 'Fastighetsannonser',
    'faq.listings.q1': 'Hur lägger jag upp en fastighet till salu?',
    'faq.listings.q1_answer': 'Du måste vara registrerad som mäklare för att lägga upp fastigheter. Efter inloggning, klicka på "Lägg till fastighet" och fyll i uppgifterna.',
    'faq.listings.q2': 'Hur länge är en annons aktiv?',
    'faq.listings.q2_answer': 'Annonser förblir aktiva tills de markeras som sålda eller tas bort av ägaren. Det finns ingen automatisk utgång.',
    'faq.listings.q3': 'Kan jag redigera min annons efter publicering?',
    'faq.listings.q3_answer': 'Ja, du kan redigera din annons när som helst. Gå till din profil, hitta annonsen och klicka på "Redigera".',
    'faq.search.title': 'Söka fastigheter',
    'faq.search.q1': 'Hur sparar jag en sökning?',
    'faq.search.q1_answer': 'Efter en sökning, klicka på "Spara sökning". Du kan aktivera e-postnotiser för nya matchande fastigheter.',
    'faq.search.q2': 'Hur lägger jag till en fastighet i favoriter?',
    'faq.search.q2_answer': 'Klicka på hjärtikonen på valfri fastighetskort. Du måste vara inloggad för att spara favoriter.',
    'faq.search.q3': 'Kan jag söka på kartan?',
    'faq.search.q3_answer': 'Ja, använd kartläget på fastighetssidan för att bläddra efter plats. Klicka på markörer för detaljer.',
    'faq.contact.title': 'Kontakta mäklare',
    'faq.contact.q1': 'Hur kontaktar jag en mäklare?',
    'faq.contact.q1_answer': 'Klicka på "Kontakta mäklare" på valfri annons för att skicka ett meddelande direkt till ansvarig mäklare.',
    'faq.contact.q2': 'Kan jag boka en visning?',
    'faq.contact.q2_answer': 'Ja, klicka på "Boka visning" på fastighetssidan för att begära en visningstid. Mäklaren bekräftar tillgängligheten.',
    'faq.security.title': 'Säkerhet & Integritet',
    'faq.security.q1': 'Är min personliga information säker?',
    'faq.security.q1_answer': 'Ja, vi använder branschstandard kryptering och säkerhetsåtgärder för att skydda dina uppgifter.',
    'faq.security.q2': 'Hur kan jag radera mitt konto?',
    'faq.security.q2_answer': 'Gå till dina profilinställningar och välj "Radera konto". Denna åtgärd är permanent.',
    'faq.still_questions': 'Har du fortfarande frågor?',
    'faq.contact_us': 'Kontakta vårt supportteam så hjälper vi dig gärna.',
    'newsletter.title': 'Nyhetsbrev',
    'newsletter.description': 'Prenumerera för att få de senaste fastighetsnyheterna.',
    'newsletter.placeholder': 'Ange din e-post',
    'newsletter.subscribe': 'Prenumerera',
    'newsletter.subscribing': 'Prenumererar...',
    'newsletter.success': 'Prenumerationen lyckades!',
    'newsletter.subscribed': 'Tack för din prenumeration!',
    'newsletter.invalid_email': 'Ange en giltig e-postadress',
    'newsletter.error': 'Prenumerationen misslyckades',
    'newsletter.unsubscribe_note': 'Du kan avregistrera dig när som helst.',
    'newsletter.unsubscribe_title': 'Avregistrera från nyhetsbrev',
    'newsletter.unsubscribe_description': 'Ange din e-post för att avregistrera dig.',
    'newsletter.unsubscribe_button': 'Avregistrera',
    'newsletter.unsubscribe_success': 'Avregistrerad',
    'newsletter.unsubscribe_error': 'Avregistreringen misslyckades',
    'newsletter.unsubscribe_message': 'Du kommer inte längre att få vårt nyhetsbrev.',
    'newsletter.invalid_link': 'Ogiltig avregistreringslänk',
    'newsletter.invalid_link_message': 'Denna avregistreringslänk är ogiltig eller har gått ut. Använd länken från ditt nyhetsbrev.',
    'newsletter.unsubscribe_reason_label': 'Anledning till avregistrering (valfritt)',
    'newsletter.select_reason': 'Välj en anledning...',
    'newsletter.reason_too_many_emails': 'För många e-postmeddelanden',
    'newsletter.reason_not_relevant': 'Innehållet är inte relevant för mig',
    'newsletter.reason_never_subscribed': 'Jag prenumererade aldrig',
    'newsletter.reason_found_another': 'Hittade en annan tjänst',
    'newsletter.reason_other': 'Annat',
    'common.try_again': 'Försök igen',
    'newsletter.admin.title': 'Nyhetsbrevsprenumeranter',
    'newsletter.admin.description': 'Hantera dina nyhetsbrevsprenumeranter',
    'newsletter.admin.total': 'Totalt antal prenumeranter',
    'newsletter.admin.active': 'Aktiv',
    'newsletter.admin.unsubscribed': 'Avregistrerad',
    'newsletter.admin.export_csv': 'Exportera CSV',
    'newsletter.admin.refresh': 'Uppdatera',
    'newsletter.admin.subscribers_list': 'Prenumerantlista',
    'newsletter.admin.subscribers_description': 'Alla prenumeranter och deras status',
    'newsletter.admin.no_subscribers': 'Inga prenumeranter ännu',
    'newsletter.admin.subscribed_at': 'Prenumererade',
    'newsletter.admin.status': 'Status',
    'newsletter.admin.unsubscribed_at': 'Avregistrerad',
    'newsletter.admin.actions': 'Åtgärder',
    'newsletter.admin.deleted': 'Prenumerant raderad',
    'newsletter.admin.delete_error': 'Kunde inte radera prenumerant',
    'newsletter.admin.no_data': 'Ingen data att exportera',
    'newsletter.admin.exported': 'Exporterad',
    'newsletter.admin.delete_confirm_title': 'Radera prenumerant?',
    'newsletter.admin.delete_confirm_description': 'Detta raderar prenumeranten permanent.',
    'footer.faq': 'Vanliga frågor',
    
    // Broker Dashboard
    'broker.dashboard_title': 'Mäklarpanel',
    'broker.dashboard_subtitle': 'Hantera dina fastigheter och kundkontakter',
    'broker.my_properties': 'Mina fastigheter',
    'broker.my_properties_desc': 'Visa och hantera dina fastighetsannonser',
    'broker.viewings': 'Visningar',
    'broker.viewings_desc': 'Hantera bokade fastighetsvisningar',
    'broker.contacts': 'Kundmeddelanden',
    'broker.contacts_desc': 'Visa och svara på kundförfrågningar',
    'broker.analytics': 'Fastighetsanalys',
    'broker.analytics_desc': 'Följ visningar och engagemang på dina annonser',
    'broker.subscription': 'Prenumeration',
    'broker.subscription_desc': 'Hantera din plan och annonsgränser',
    'broker.access_denied': 'Denna sida är endast tillgänglig för registrerade mäklare.',
    'broker.profile_title': 'Mäklarprofil',
    'broker.profile_description': 'Hantera din offentliga profil och kontaktinformation',
    'broker.back_to_dashboard': 'Tillbaka till instrumentpanelen',
    'broker.display_name': 'Visningsnamn',
    'broker.display_name_placeholder': 'Ditt namn som visas för kunder',
    'broker.contact_email': 'Kontakt-e-post',
    'broker.email_placeholder': 'din@email.com',
    'broker.email_notification_note': 'Denna e-post används för aviseringar om visningar och kundmeddelanden.',
    'broker.phone_number': 'Telefonnummer',
    'broker.phone_placeholder': '+46 xxx xxx xxx',
    'broker.change_photo': 'Byt foto',
    'broker.save_profile': 'Spara profil',
    'broker.profile_saved': 'Profilen sparades',
    'broker.profile_save_error': 'Kunde inte spara profilen',
    'broker.profile_load_error': 'Kunde inte ladda profilen',
    'broker.avatar_uploaded': 'Foto uppladdades',
    'broker.avatar_upload_error': 'Kunde inte ladda upp foto',
    'broker.invalid_image_type': 'Välj en bildfil',
    'broker.image_too_large': 'Bilden måste vara mindre än 2MB',
    'broker.notification_preferences': 'Aviseringsinställningar',
    'broker.notification_preferences_description': 'Välj vilka e-postaviseringar du vill ta emot',
    'broker.notif_new_viewing_requests': 'Nya visningsförfrågningar',
    'broker.notif_new_viewing_requests_desc': 'Få avisering när kunder begär visningar',
    'broker.notif_new_client_messages': 'Nya kundmeddelanden',
    'broker.notif_new_client_messages_desc': 'Få avisering när kunder skickar meddelanden',
    'broker.notif_property_alerts': 'Fastighetsnotiser',
    'broker.notif_property_alerts_desc': 'Få marknadsuppdateringar och matchande fastighetsnotiser',
    'broker.notif_weekly_summary': 'Veckosammanfattning',
    'broker.notif_weekly_summary_desc': 'Få en veckovis sammanfattning av din aktivitet',
    'broker.listing_usage': 'Annonsanvändning',
    'broker.monthly_listing_limits': 'Månatliga annonsgränser',
    'broker.listings_used': 'Använda annonser',
    'broker.listings_remaining': 'annonser kvar',
    'broker.period_resets': 'Perioden återställs',
    'broker.export_csv': 'Exportera CSV',
    'broker.total_views': 'Totala visningar',
    'broker.scheduled_viewings': 'Bokade visningar',
    'broker.client_messages': 'Kundmeddelanden',
    'broker.filters': 'Filter',
    'broker.all_time': 'Hela tiden',
    'broker.active_listings': 'Aktiva annonser',
    'broker.sold_listings': 'Sålda annonser',
    'broker.active_listings_desc': 'Dina publicerade fastigheter',
    'broker.no_active_listings': 'Inga aktiva annonser än. Lägg till din första fastighet.',
    'broker.all_types': 'Alla typer',
    'broker.all_cities': 'Alla städer',
    'broker.property_type': 'Fastighetstyp',
    'broker.city': 'Stad',
    'broker.date_range': 'Datumintervall',
    'broker.last_week': 'Senaste veckan',
    'broker.last_month': 'Senaste månaden',
    'broker.last_quarter': 'Senaste 3 månader',
    'broker.sold_listings_desc': 'Fastigheter du har sålt',
    'broker.no_sold_listings': 'Inga sålda fastigheter ännu',
    'broker.clear_filters': 'Rensa filter',
    'broker.selected': 'valda',
    'broker.compare': 'Jämför',
    'broker.mark_as_sold': 'Markera som såld',
    'broker.delete_selected': 'Ta bort valda',
    'broker.select_all': 'Välj alla',
    'broker.rejected_listings': 'Avvisade annonser',
    'broker.rejected_listings_desc': 'Dessa annonser avvisades av moderatorer. Du kan skicka en överklagan.',
    'broker.no_rejected_listings': 'Inga avvisade annonser',
    'broker.confirm_delete': 'Bekräfta borttagning',
    'broker_public.not_found': 'Mäklare hittades inte',
    'broker_public.not_found_description': 'Mäklarprofilen du söker finns inte.',
    'broker_public.back_to_search': 'Tillbaka till mäklarsök',
    'broker_public.contact_broker': 'Kontakta mäklare',
    'broker_public.agency': 'Byrå',
    'broker_public.visit_website': 'Besök webbplats',
    'broker_public.active_listings': 'Aktiva annonser',
    'broker_public.sold_properties': 'Sålda fastigheter',
    'broker_public.total_sales': 'Total försäljning',
    'broker_public.avg_days_market': 'Snitt dagar på marknaden',
    'broker_public.no_active_listings': 'Inga aktiva annonser för tillfället',
    'broker_public.no_sold_properties': 'Inga sålda fastigheter ännu',
    'broker_public.view_all_properties': 'Visa alla fastigheter',
    'broker_public.send_message': 'Skicka meddelande',
    'broker_public.view_agency': 'Visa byrå',
    'broker_public.works_at': 'Arbetar på',
    'broker_public.listings': 'Annonser',
    'broker_public.sold': 'Sålt',
    'broker_public.reviews': 'Omdömen',
    'broker_public.rating': 'Betyg',
    'broker_public.featured_broker': 'Utvald mäklare',
    'broker_public.top_performer': 'Topppresterande',
    'agents.verified_agent': 'Verifierad mäklare',
    
    // Agency Detail
    'agency_detail.brokers': 'Mäklare',
    'agency_detail.listings': 'Annonser',
    'agency_detail.sold': 'Sålt',
    'agency_detail.reviews': 'Omdömen',
    'agency_detail.rating': 'Betyg',
    'agency_detail.active_listings': 'Aktiva annonser',
    'agency_detail.no_active_listings': 'Inga aktiva annonser för tillfället',
    'agency_detail.no_sold_properties': 'Inga sålda fastigheter ännu',
    'agency_detail.view_profile': 'Visa profil',
    'agency_detail.message': 'Meddelande',
    'agency_detail.featured_agency': 'Utvald byrå',
    'agency_detail.premium_agency': 'Premiumbyrå',
    'agency_detail.verified_agency': 'Verifierad byrå',
    
    // Reviews
    'reviews.write_review': 'Skriv ett omdöme',
    'reviews.write_agency_review': 'Betygsätt denna byrå',
    'reviews.write_broker_review': 'Betygsätt denna mäklare',
    'reviews.select_rating': 'Välj ett betyg',
    'reviews.submitted': 'Omdöme skickat',
    'reviews.submit_error': 'Kunde inte skicka omdöme',
    'reviews.comment_placeholder': 'Dela din upplevelse... (valfritt)',
    'reviews.submitting': 'Skickar...',
    'reviews.submit': 'Skicka omdöme',
    'reviews.all_reviews': 'Alla omdömen',
    'reviews.no_reviews': 'Inga omdömen ännu. Bli först med att lämna ett!',
    'reviews.login_to_review': 'Logga in för att lämna ett omdöme',
    'reviews.cannot_review_self': 'Du kan inte betygsätta dig själv',
    'common.login_required': 'Vänligen logga in för att komma åt denna sida.',
    
    // Agency Dashboard
    'agency.my_agency': 'Min byrå',
    'agency.dashboard_title': 'Byråpanel',
    'agency.dashboard_subtitle': 'Hantera dina mäklare och byråinställningar',
    'agency.brokers_tab': 'Mäklare',
    'agency.invites_tab': 'Inbjudningar',
    'agency.settings_tab': 'Inställningar',
    'agency.brokers_title': 'Byrås mäklare',
    'agency.brokers_description': 'Hantera mäklare i din byrå',
    'agency.create_broker': 'Skapa mäklare',
    'agency.create_broker_title': 'Skapa nytt mäklarkonto',
    'agency.create_broker_description': 'Skapa ett nytt mäklarkonto direkt kopplat till din byrå',
    'agency.broker_name': 'Fullständigt namn',
    'agency.broker_name_placeholder': 'Ange mäklarens namn',
    'agency.broker_email': 'E-postadress',
    'agency.broker_email_placeholder': 'maklare@exempel.se',
    'agency.broker_password': 'Lösenord',
    'agency.broker_password_placeholder': 'Skapa ett lösenord',
    'agency.create_broker_btn': 'Skapa mäklarkonto',
    'agency.broker_created': 'Mäklarkonto skapat',
    'agency.broker_create_error': 'Kunde inte skapa mäklarkonto',
    'agency.no_brokers': 'Inga mäklare ännu',
    'agency.no_brokers_description': 'Skapa mäklarkonton eller skicka inbjudningar för att lägga till mäklare',
    'agency.broker_column': 'Mäklare',
    'agency.email_column': 'E-post',
    'agency.phone_column': 'Telefon',
    'agency.actions_column': 'Åtgärder',
    'agency.unnamed_broker': 'Namnlös mäklare',
    'agency.broker_removed': 'Mäklare borttagen från byrån',
    'agency.broker_remove_error': 'Kunde inte ta bort mäklare',
    'agency.invites_title': 'Väntande inbjudningar',
    'agency.invites_description': 'Hantera inbjudningar till potentiella mäklare',
    'agency.send_invite': 'Skicka inbjudan',
    'agency.send_invite_title': 'Bjud in en mäklare',
    'agency.send_invite_description': 'Skicka en inbjudan att gå med i din byrå',
    'agency.invite_email': 'E-postadress',
    'agency.invite_email_placeholder': 'maklare@exempel.se',
    'agency.send_invite_btn': 'Skicka inbjudan',
    'agency.invite_sent': 'Inbjudan skickad',
    'agency.invite_error': 'Kunde inte skicka inbjudan',
    'agency.link_copied': 'Inbjudningslänk kopierad',
    'agency.no_invites': 'Inga väntande inbjudningar',
    'agency.status_column': 'Status',
    'agency.sent_column': 'Skickad',
    'agency.expires_column': 'Går ut',
    'agency.status_pending': 'Väntande',
    'agency.status_accepted': 'Accepterad',
    'agency.status_expired': 'Utgången',
    'agency.settings_title': 'Byråinställningar',
    'agency.settings_description': 'Uppdatera din byråinformation',
    'agency.agency_name': 'Byrånamn',
    'agency.agency_name_placeholder': 'Ditt byrånamn',
    'agency.agency_description': 'Beskrivning',
    'agency.agency_description_placeholder': 'Kort beskrivning av din byrå',
    'agency.agency_phone': 'Telefon',
    'agency.agency_phone_placeholder': '+46 xxx xxx xxx',
    'agency.agency_email': 'E-post',
    'agency.agency_email_placeholder': 'kontakt@byra.se',
    'agency.agency_website': 'Webbplats',
    'agency.settings_saved': 'Byråinställningar sparade',
    'agency.settings_save_error': 'Kunde inte spara inställningar',
    'agency.load_error': 'Kunde inte ladda byrådata',
    'agency.default_name': 'Min byrå',
    'agency.notifications_tab': 'Aviseringar',
    'agency.notifications_title': 'E-postaviseringar',
    'agency.notifications_description': 'Välj vilka aviseringar du får om mäklaraktivitet',
    'agency.notifications_saved': 'Aviseringsinställningar sparade',
    'agency.notifications_save_error': 'Kunde inte spara inställningar',
    'agency.notif_new_broker_joins': 'Ny mäklare ansluter',
    'agency.notif_new_broker_joins_desc': 'Få avisering när en mäklare accepterar din inbjudan',
    'agency.notif_broker_property_added': 'Mäklare lägger till fastighet',
    'agency.notif_broker_property_added_desc': 'Få avisering när mäklare lägger till nya fastighetsannonser',
    'agency.notif_broker_viewing_requests': 'Visningsförfrågningar',
    'agency.notif_broker_viewing_requests_desc': 'Få avisering om visningsförfrågningar för mäklarfastigheter',
    'agency.notif_broker_client_messages': 'Kundmeddelanden',
    'agency.notif_broker_client_messages_desc': 'Få avisering när kunder kontaktar dina mäklare',
    'agency.notif_weekly_summary': 'Veckosammanfattning',
    'agency.notif_weekly_summary_desc': 'Få en veckovis sammanfattning av byråaktivitet',
    'agency.analytics_tab': 'Analys',
    'agency.no_brokers_analytics': 'Lägg till mäklare för att se prestandaanalys',
    'agency.total_properties': 'Totalt fastigheter',
    'agency.total_views': 'Totala visningar',
    'agency.total_viewings': 'Visningsförfrågningar',
    'agency.total_sold': 'Sålda fastigheter',
    'agency.total_sales_value': 'Totalt försäljningsvärde',
    'agency.broker_performance_chart': 'Mäklarprestanda',
    'agency.broker_performance_chart_desc': 'Jämför mäklarstatistik i en överblick',
    'agency.individual_broker_stats': 'Individuell mäklarstatistik',
    'agency.individual_broker_stats_desc': 'Detaljerade mätvärden för varje mäklare',
    'agency.properties': 'Fastigheter',
    'agency.views': 'Visningar',
    'agency.viewings': 'Visningar',
    'agency.contacts': 'Kontakter',
    'agency.active': 'Aktiva',
    'agency.sold': 'Sålda',
    'agency.sales_value': 'Försäljningsvärde',
    'agency.upgrade_plan': 'Uppgradera plan',
    'agency.view_plans': 'Visa planer',
    'agency.billing_tab': 'Fakturering',
    'agency.name_label': 'Byrånamn',
    'agency.email_label': 'E-post',
    'agency.phone_label': 'Telefon',
    'agency.website_label': 'Webbplats',
    'agency.address_label': 'Adress',
    'agency.city_label': 'Stad',
    'agency.description_label': 'Beskrivning',
    'agency.description_placeholder': 'Berätta för kunder om din byrå...',
    'agency.save_settings': 'Spara inställningar',
    'agency.broker_usage': 'Mäklaranvändning',
    'agency.invite_broker': 'Bjud in mäklare',
    'agency.brokers': 'mäklare',
    'agency.broker_limit_reached': 'Mäklargräns nådd',
    'agency.upgrade_for_more': 'Uppgradera din plan för att lägga till fler mäklare',
    'agency.active_listings': 'Aktiva annonser',
    'agency.broker_slots_remaining': 'Platser kvar',
    'agency.manage_subscription': 'Hantera prenumeration',
    'agency.upgrade_your_plan': 'Uppgradera din plan',
    'agency.choose_plan': 'Välj planen som passar din byrå bäst',
    'agency.pro_plan': 'Pro',
    'agency.enterprise_plan': 'Enterprise',
    'agency.for_growing_agencies': 'För växande byråer',
    'agency.for_large_agencies': 'För stora byråer',
    'agency.per_month': '/månad',
    'agency.up_to_brokers': 'Upp till {count} mäklare',
    'agency.unlimited_brokers': 'Obegränsat antal mäklare',
    'agency.advanced_analytics': 'Avancerad analys',
    'agency.full_analytics': 'Komplett analyspaket',
    'agency.priority_support': 'Prioriterad support',
    'agency.dedicated_support': 'Dedikerad support',
    'agency.upgrade_to_pro': 'Uppgradera till Pro',
    'agency.upgrade_to_enterprise': 'Uppgradera till Enterprise',
    'agency.popular': 'Populär',
    'agency.current_plan': 'Nuvarande plan',
    'agency.plan_features': 'Planfunktioner',
    'agency.billing_history': 'Faktureringshistorik',
    'agency.no_billing_history': 'Ingen faktureringshistorik ännu',
    'agency.subscription_billing': 'Prenumeration & Fakturering',
    'agency.manage_agency_subscription': 'Hantera din byrås prenumeration',
    'agency.plan': 'Plan',
    'agency.basic_plan': 'Grundläggande',
    'agency.broker_limit': 'Mäklargräns',
    'agency.current_usage': 'Nuvarande användning',
    'agency.status': 'Status',
    'agency.active_status': 'Aktiv',
    'agency.unlimited': 'Obegränsad',
    'agency.feature': 'Funktion',
    'agency.price': 'Pris',
    'agency.free': 'Gratis',
    'agency.analytics': 'Analys',
    'agency.basic_analytics': 'Grundläggande',
    'agency.advanced': 'Avancerad',
    'agency.full_suite': 'Komplett svit',
    'agency.support': 'Support',
    'agency.email_support': 'E-post',
    'agency.priority': 'Prioriterad',
    'agency.dedicated': 'Dedikerad',
    
    // Agency Invite Accept
    'agency_invite.title': 'Gå med i byrå',
    'agency_invite.description': 'Du har blivit inbjuden att gå med i {agencyName}',
    'agency_invite.joining': 'Du går med i',
    'agency_invite.logged_in_as': 'Inloggad som',
    'agency_invite.accept_btn': 'Acceptera inbjudan',
    'agency_invite.accepted_success': 'Gått med i byrån!',
    'agency_invite.accept_error': 'Kunde inte acceptera inbjudan',
    'agency_invite.create_account': 'Skapa ett konto',
    'agency_invite.create_account_description': 'Skapa ett konto för att acceptera inbjudan',
    'agency_invite.your_name': 'Ditt namn',
    'agency_invite.name_placeholder': 'Ange ditt fullständiga namn',
    'agency_invite.email_placeholder': 'din@email.se',
    'agency_invite.password': 'Lösenord',
    'agency_invite.password_placeholder': 'Skapa ett lösenord',
    'agency_invite.confirm_password': 'Bekräfta lösenord',
    'agency_invite.confirm_password_placeholder': 'Bekräfta ditt lösenord',
    'agency_invite.register_accept_btn': 'Skapa konto och gå med',
    'agency_invite.registered_success': 'Konto skapat och gått med i byrån!',
    'agency_invite.register_error': 'Kunde inte skapa konto',
    'agency_invite.passwords_mismatch': 'Lösenorden matchar inte',
    'agency_invite.password_too_short': 'Lösenordet måste vara minst 6 tecken',
    'agency_invite.already_have_account': 'Har du redan ett konto?',
    'agency_invite.login_here': 'Logga in här',
    'agency_invite.invalid_link': 'Ogiltig inbjudningslänk',
    'agency_invite.not_found': 'Inbjudan hittades inte',
    'agency_invite.expired': 'Denna inbjudan har gått ut',
    'agency_invite.already_accepted': 'Denna inbjudan har redan accepterats',
    'agency_invite.fetch_error': 'Kunde inte ladda inbjudan',
    'agency_invite.error_title': 'Ogiltig inbjudan',
    'agency_invite.back_home': 'Tillbaka till startsidan',
    
    // User Listings
    'userListings.title': 'Mina annonser',
    'userListings.subtitle': 'Hantera dina bostadsannonser och skapa nya',
    'userListings.pricingTitle': 'Annonspriser',
    'userListings.pricingDesc': 'Skapa din bostadsannons med vår enkla prissättning',
    'userListings.listingPrice': '$10 per annons',
    'userListings.listingDuration': 'Giltig i 1 månad',
    'userListings.videoPrice': '+$3 för video',
    'userListings.videoDesc': 'Lägg till en videovisning till din annons',
    'userListings.includeVideo': 'Inkludera videovisning',
    'userListings.createListing': 'Skapa annons',
    'userListings.myListingsTitle': 'Dina bostäder',
    'userListings.noListings': 'Inga annonser än',
    'userListings.noListingsDesc': 'Skapa din första bostadsannons för att komma igång',
    'userListings.createFirst': 'Skapa din första annons',
    'userListings.hasVideo': 'Video',
    'userListings.renew': 'Förnya',
    'userListings.renewSuccess': 'Annons förnyad!',
    'userListings.renewSuccessDesc': 'Din annons har förnyats i ytterligare en månad',
    'userListings.paymentSuccess': 'Betalning lyckades!',
    'userListings.paymentSuccessDesc': 'Din annons är nu live',
    'userListings.paymentCancelled': 'Betalning avbruten',
    'userListings.paymentCancelledDesc': 'Din betalning avbröts',
    'userListings.checkoutError': 'Kunde inte skapa betalning',
    'userListings.renewError': 'Kunde inte förnya annons',
    'userListings.createDraft': 'Skapa annons',
    'userListings.previewListing': 'Förhandsgranska din annons',
    'userListings.draft': 'Utkast',
    'userListings.drafts': 'Utkast',
    'userListings.draftSaved': 'Utkast sparat',
    'userListings.draftSavedDesc': 'Din annons har sparats som utkast',
    'userListings.draftError': 'Kunde inte spara utkast',
    'userListings.draftDeleted': 'Utkast raderat',
    'userListings.deleteError': 'Kunde inte radera',
    'userListings.saveDraft': 'Spara utkast',
    'userListings.saveAndPreview': 'Spara & förhandsgranska',
    'userListings.publishOptions': 'Publiceringsalternativ',
    'userListings.addVideoTour': 'Lägg till videorundtur',
    'userListings.videoIncluded': 'Videorundtur ingår',
    'userListings.totalPrice': 'Totalt',
    'userListings.backToEdit': 'Tillbaka till redigering',
    'userListings.publishNow': 'Publicera nu',
    'userListings.publish': 'Publicera',
    'userListings.publishSuccess': 'Annons publicerad!',
    'userListings.publishSuccessDesc': 'Din annons väntar nu på granskning',
    'userListings.firstListingFree': 'Första annonsen är gratis!',
    'userListings.firstListingFreeDesc': 'Din första bostadsannons är helt gratis. Publicera nu och nå köpare i hela Irak!',
    'userListings.pending': 'Väntar på granskning',
    'userListings.pendingInfo': 'Dessa annonser granskas av vårt modereringsteam. Du meddelas när de godkänns.',
    'userListings.pendingReview': 'Väntar på granskning',
    'userListings.awaitingApproval': 'Väntar på moderatorgodkännande',
    'userListings.rejected': 'Avvisade annonser',
    'userListings.rejectedInfo': 'Dessa annonser godkändes inte. Redigera och åtgärda problemen innan du skickar in igen.',
    'userListings.rejectedBadge': 'Avvisad',
    'userListings.live': 'Aktiv',
    'userListings.editDraft': 'Redigera',
    'userListings.editAndResubmit': 'Redigera & skicka in igen',
    'userListings.pendingCannotEdit': 'Kan inte redigera under granskning',
    'userListings.resubmitForReview': 'Skicka in för granskning igen',
    'userListings.resubmitInfo': 'Dina ändringar har sparats. Skicka in din annons igen för granskning utan extra kostnad.',
    'userListings.submitForReview': 'Skicka för granskning',
    'userListings.validation.priceRequired': 'Pris krävs',
    'userListings.validation.cityRequired': 'Stad krävs',
    'userListings.validation.roomsRequired': 'Antal rum krävs',
    'userListings.validation.sizeRequired': 'Storlek krävs',
    'userListings.validation.plotSizeRequired': 'Tomtstorlek krävs',
    'userListings.validation.imagesRequired': 'Minst en bild krävs',
    'userListings.choosePlan': 'Välj din annonsplan',
    'userListings.choosePlanDesc': 'Välj hur många bilder som ska visas på ditt annonkort',
    'userListings.upgradePlan': 'Uppgradera din annonsplan',
    'userListings.upgradePlanDesc': 'Uppgradera för att visa fler bilder. Du betalar bara mellanskillnaden.',
    'userListings.selectImages': 'Välj bilder att visa',
    'userListings.selectImagesDesc': 'Välj bilder att visa på ditt annonkort',
    'userListings.selectPrimaryImage': 'Välj huvudbild',
    'userListings.selectPrimaryImageDesc': 'Välj huvudbilden som visas först på din annons',
    'userListings.addVideo': 'Lägg till videovisning',
    'userListings.pleaseSelectImages': 'Välj bilder för att fortsätta',
    'userListings.pleaseSelectPrimaryImage': 'Välj en huvudbild för att fortsätta',
    'userListings.proceedToPayment': 'Fortsätt till betalning',
    'userListings.tierBasic': 'Bas',
    'userListings.tierStandard': 'Standard',
    'userListings.tierPremium': 'Premium',
    'userListings.tierBasicDesc': '1 bild på kortet',
    'userListings.tierStandardDesc': '3 bilder karusell',
    'userListings.tierPremiumDesc': 'Alla bilder + märke',
    'userListings.tierDuration': '30 dagar aktiv',
    'userListings.tierBest': 'Bäst',
    'userListings.tierBasicFull': 'Bas (1 bild)',
    'userListings.tierStandardFull': 'Standard (3 bilder)',
    'userListings.tierPremiumFull': 'Premium (Alla bilder)',
    'userListings.changeTier': 'Byt nivå',
    'userListings.selectImageTier': 'Välj bildnivå',
    'userListings.selectTierFirst': 'Välj en nivå innan publicering',
    'userListings.upgrade': 'Uppgradera',
    'common.total': 'Totalt',
    
    // Settings Page
    'settings.title': 'Inställningar',
    'settings.subtitle': 'Hantera dina kontoinställningar och preferenser',
    'settings.profile': 'Profil',
    'settings.notifications': 'Aviseringar',
    'settings.account': 'Konto',
    'settings.profile_info': 'Profilinformation',
    'settings.profile_desc': 'Uppdatera din personliga information',
    'settings.change_photo': 'Byt foto',
    'settings.display_name': 'Visningsnamn',
    'settings.display_name_placeholder': 'Ditt namn',
    'settings.email': 'E-post',
    'settings.email_placeholder': 'din@email.se',
    'settings.phone': 'Telefon',
    'settings.phone_placeholder': '+46 70 123 45 67',
    'settings.save': 'Spara ändringar',
    'settings.saved': 'Inställningar sparade',
    'settings.load_error': 'Kunde inte ladda inställningar',
    'settings.save_error': 'Kunde inte spara inställningar',
    'settings.invalid_image': 'Välj en bildfil',
    'settings.image_too_large': 'Bilden måste vara mindre än 2MB',
    'settings.avatar_uploaded': 'Foto uppladdat',
    'settings.avatar_error': 'Kunde inte ladda upp foto',
    'settings.notification_preferences': 'Aviseringspreferenser',
    'settings.notification_desc': 'Välj vilka aviseringar du vill få',
    'settings.notif_property_alerts': 'Fastighetsvarningar',
    'settings.notif_property_alerts_desc': 'Få meddelanden om nya fastigheter',
    'settings.notif_saved_search': 'Sparade sökningar',
    'settings.notif_saved_search_desc': 'Få meddelanden när nya fastigheter matchar dina sparade sökningar',
    'settings.notif_newsletter': 'Nyhetsbrev',
    'settings.notif_newsletter_desc': 'Ta emot vårt nyhetsbrev med marknadsuppdateringar',
    'settings.notif_viewing': 'Visningsbekräftelser',
    'settings.notif_viewing_desc': 'Få bekräftelser för schemalagda visningar',
    'settings.account_settings': 'Kontoinställningar',
    'settings.account_desc': 'Hantera din kontosäkerhet och preferenser',
    'settings.change_password': 'Ändra lösenord',
    'settings.change_password_desc': 'Uppdatera ditt lösenord',
    'settings.change': 'Ändra',
    'settings.two_factor': 'Tvåfaktorsautentisering',
    'settings.two_factor_desc': 'Lägg till extra säkerhet för ditt konto',
    'settings.enable': 'Aktivera',
    'settings.delete_account': 'Radera konto',
    'settings.delete_account_desc': 'Ta bort ditt konto permanent',
    'settings.delete': 'Radera',
    'settings.logout': 'Logga ut',
    
    // Common
    'common.back': 'Tillbaka',
    
    // Contact Form Modal
    'contact_form.title': 'Kontakt',
    'contact_form.description': 'Skicka ett meddelande för att komma i kontakt.',
    'contact_form.name': 'Namn',
    'contact_form.name_placeholder': 'Ditt namn',
    'contact_form.email': 'E-post',
    'contact_form.email_placeholder': 'din@email.se',
    'contact_form.phone': 'Telefon',
    'contact_form.phone_placeholder': 'Ditt telefonnummer',
    'contact_form.message': 'Meddelande',
    'contact_form.message_placeholder': 'Hur kan vi hjälpa dig?',
    'contact_form.send': 'Skicka meddelande',
    'contact_form.sending': 'Skickar...',
    'contact_form.success_title': 'Meddelande skickat!',
    'contact_form.success_message': 'Ditt meddelande har skickats. Du får en bekräftelse via e-post.',
    'contact_form.error_title': 'Fel',
    'contact_form.error_message': 'Kunde inte skicka meddelande. Försök igen.',
    
    // Messaging
    'messaging.title': 'Meddelanden',
    'messaging.description': 'Dina privata konversationer',
    'messaging.new_message': 'Nytt meddelande',
    'messaging.no_messages': 'Inga meddelanden ännu',
    'messaging.no_conversations': 'Inga konversationer ännu',
    'messaging.send': 'Skicka',
    'messaging.send_message': 'Skicka meddelande',
    'messaging.message_to': 'Skicka meddelande till',
    'messaging.subject': 'Ämne',
    'messaging.subject_placeholder': 'Ämne (valfritt)',
    'messaging.message': 'Meddelande',
    'messaging.message_placeholder': 'Skriv ditt meddelande...',
    'messaging.login_required': 'Du måste vara inloggad för att skicka meddelanden.',
    'messaging.message_sent': 'Meddelandet har skickats',
    'messaging.contact_agent_description': 'Kontakta mäklaren direkt',
    'messaging.type_message': 'Skriv ett meddelande...',
    'messaging.inbox': 'Inkorg',
    'messaging.sent': 'Skickade',
    'messaging.search': 'Sök konversationer...',
    'messaging.select_conversation': 'Välj en konversation',
    'messaging.select_description': 'Välj en konversation från listan för att börja chatta',
    'messaging.encrypted': 'End-to-end-krypterad',
    'messaging.decrypt': 'Dekryptera meddelande',
    'messaging.reply': 'Svara',
    'messaging.replying_to': 'Svarar på',
    'messaging.replying_to_yourself': 'dig själv',
    'messaging.forward': 'Vidarebefordra',
    'messaging.delete': 'Radera',
    'messaging.delete_confirm': 'Radera meddelande',
    'messaging.delete_description': 'Detta meddelande kommer att raderas för alla. Denna åtgärd kan inte ångras.',
    'messaging.delete_for_everyone': 'Radera för alla',
    'messaging.pin': 'Fäst',
    'messaging.unpin': 'Ta bort fästning',
    'messaging.pinned_messages': 'Fästa meddelanden',
    'messaging.no_pinned': 'Inga fästa meddelanden',
    'messaging.delivered': 'Levererad',
    'messaging.read': 'Läst',
    'messaging.seen_at': 'Sedd kl',
    'messaging.voice_message': 'Röstmeddelande',
    'messaging.search_messages': 'Sök i meddelanden...',
    'messaging.contacts': 'Kontakter',
    'messaging.results_found': 'resultat hittade',
    'messaging.recording': 'Spelar in...',
    'messaging.typing': 'skriver...',
    'messaging.online': 'online',
    'messaging.tap_for_info': 'tryck här för kontaktinfo',
    'messaging.photos': 'Bilder',
    'messaging.camera': 'Kamera',
    'messaging.location': 'Plats',
    'messaging.contact': 'Kontakt',
    'messaging.document': 'Dokument',
    'messaging.poll': 'Omröstning',
    'messaging.event': 'Händelse',
    'messaging.location_coming_soon': 'Platsdelning kommer snart',
    'messaging.contact_coming_soon': 'Kontaktdelning kommer snart',
    'messaging.poll_coming_soon': 'Omröstning kommer snart',
    'messaging.event_coming_soon': 'Händelseskapande kommer snart',
    'messaging.schedule_event': 'Schemalägg händelse',
    'messaging.event_title': 'Händelsetitel',
    'messaging.event_title_placeholder': 't.ex., Visning av fastighet',
    'messaging.event_date': 'Datum',
    'messaging.event_time': 'Tid',
    'messaging.event_location': 'Plats',
    'messaging.event_location_placeholder': 'Ange plats eller adress',
    'messaging.event_description': 'Beskrivning',
    'messaging.event_description_placeholder': 'Lägg till ytterligare detaljer...',
    'messaging.send_event': 'Skicka händelse',
    'messaging.event_created': 'Händelse skapad',
    'messaging.create_poll': 'Skapa omröstning',
    'messaging.poll_question': 'Fråga',
    'messaging.poll_question_placeholder': 'Vad vill du fråga?',
    'messaging.poll_options': 'Alternativ',
    'messaging.option': 'Alternativ',
    'messaging.add_option': 'Lägg till alternativ',
    'messaging.allow_multiple': 'Tillåt flera svar',
    'messaging.send_poll': 'Skicka omröstning',
    'messaging.poll_created': 'Omröstning skapad',
    'messaging.edit': 'Redigera',
    'messaging.edit_message': 'Redigera meddelande',
    'messaging.edit_description': 'Gör ändringar i ditt meddelande',
    'messaging.edit_time_remaining': 'Du har',
    'messaging.minutes_to_edit': 'minuter att redigera detta meddelande',
    'messaging.message_edited': 'Meddelande redigerat',
    'messaging.edit_failed': 'Kunde inte redigera meddelande',
    'messaging.from_date': 'Från',
    'messaging.to_date': 'Till',
    'messaging.clear_filters': 'Rensa filter',
    'messaging.export_chat': 'Exportera chatt',
    'messaging.export_description': 'Exportera din konversationshistorik',
    'messaging.export_text': 'Textfil (.txt)',
    'messaging.export_text_desc': 'Enkelt textformat, lätt att läsa och dela',
    'messaging.export_pdf': 'PDF-dokument',
    'messaging.export_pdf_desc': 'Formaterat dokument, perfekt för utskrift',
    'messaging.export_messages_count': 'meddelanden kommer exporteras',
    'messaging.exporting': 'Exporterar...',
    'messaging.export': 'Exportera',
    'messaging.export_success': 'Chatten exporterades',
    'messaging.export_failed': 'Kunde inte exportera chatten',
    'messaging.is_typing': 'skriver...',
    
    // Quick Reply Templates
    'quick_reply.title': 'Snabbsvar',
    'quick_reply.greeting': 'Hej! Tack för att du hör av dig.',
    'quick_reply.interested': 'Jag är intresserad av denna bostad. Kan du ge mer information?',
    'quick_reply.schedule_viewing': 'Jag vill boka en visning. Vilka tider finns tillgängliga?',
    'quick_reply.ask_price': 'Är priset förhandlingsbart?',
    'quick_reply.ask_availability': 'Är denna bostad fortfarande tillgänglig?',
    'quick_reply.thank_you': 'Tack för informationen. Jag återkommer snart.',
    'quick_reply.will_contact': 'Jag kontaktar dig snart med mer information.',
    'quick_reply.not_interested': 'Tack, men jag är inte längre intresserad av denna bostad.',
    
    // Notifications
    'notifications.enable': 'Aktivera notiser',
    'notifications.on': 'Notiser på',
    'notifications.preferences': 'Notisinställningar',
    'notifications.sound': 'Notisljud',
    'notifications.sound_desc': 'Spela ett ljud när du får ett meddelande',
    'notifications.sound_default': 'Standard',
    'notifications.sound_chime': 'Pling',
    'notifications.sound_bell': 'Klocka',
    'notifications.sound_pop': 'Pop',
    'notifications.test': 'Testa',
    'notifications.vibration': 'Vibration',
    'notifications.vibration_desc': 'Vibrera när du får ett meddelande (endast mobil)',
    'messaging.you': 'Du',
    'messaging.encrypted_message': 'Krypterat meddelande',
    'messaging.unknown_user': 'Okänd användare',
    'messaging.no_groups': 'Inga grupper ännu',
    'messaging.create_group': 'Skapa grupp',
    'messaging.group_chat': 'Gruppchatt',
    'messaging.direct_messages': 'Direkt',
    'messaging.groups': 'Grupper',
    'messaging.group_name': 'Gruppnamn',
    'messaging.group_description': 'Beskrivning',
    'messaging.e2e_notice': 'Meddelanden och samtal är end-to-end-krypterade. Endast personer i denna chatt kan läsa, lyssna på eller dela dem.',
    'messaging.today': 'Idag',
    'messaging.yesterday': 'Igår',
    'messaging.search_groups': 'Sök grupper...',
    
    // Attachment viewer
    'attachment.all_media': 'Alla medier',
    'attachment.go_to_message': 'Gå till meddelande',
    'attachment.download': 'Ladda ner',
    'attachment.view_fullscreen': 'Visa helskärm',
    'attachment.save_to_gallery': 'Spara till galleri',
    'attachment.share': 'Dela',
    'attachment.double_tap_zoom': 'Dubbeltryck för att zooma',
    'attachment.zoom_in': 'Zooma in',
    'attachment.zoom_out': 'Zooma ut',
    'attachment.reset_zoom': 'Återställ zoom',
    'attachment.ctrl_scroll_zoom': 'Ctrl + Scrolla för att zooma',
    'attachment.fit_width': 'Anpassa till bredd',
    'attachment.fit_height': 'Anpassa till höjd',
    'attachment.click_to_navigate': 'Klicka för att navigera',
    
    // Status
    'status.online': 'Online',
    'status.offline': 'Offline',
    'status.last_seen': 'Senast sedd',
    
    // Blocking
    'blocking.block_user': 'Blockera användare',
    'blocking.block_confirm': 'Är du säker på att du vill blockera {name}? De kommer inte kunna skicka meddelanden till dig.',
    'blocking.reason_optional': 'Anledning (valfritt)',
    'blocking.reason_placeholder': 'Varför blockerar du denna användare?',
    'blocking.block': 'Blockera',
    'blocking.user_blocked': 'Användaren har blockerats',
    'blocking.already_blocked': 'Användaren är redan blockerad',
    'blocking.error': 'Kunde inte blockera användaren',
    'blocking.blocked_users': 'Blockerade användare',
    'blocking.blocked_users_description': 'Hantera din lista över blockerade användare',
    'blocking.no_blocked_users': 'Du har inte blockerat någon',
    'blocking.blocked_on': 'Blockerad {date}',
    'blocking.unblock': 'Avblockera',
    'blocking.user_unblocked': 'Användaren har avblockerats',
    'blocking.unblock_error': 'Kunde inte avblockera användaren',
    'blocking.fetch_error': 'Kunde inte hämta blockerade användare',
    'blocking.you_blocked_user': 'Du har blockerat denna användare. Avblockera för att skicka meddelanden.',
    'blocking.user_blocked_you': 'Denna användare har blockerat dig. Du kan inte skicka meddelanden.',
    
    // Reporting
    'reporting.report_user': 'Anmäl användare',
    'reporting.report_description': 'Anmäl {name} för brott mot våra riktlinjer',
    'reporting.select_reason': 'Välj en anledning',
    'reporting.reason': 'Anledning till anmälan',
    'reporting.additional_details': 'Ytterligare detaljer',
    'reporting.details_placeholder': 'Ange ytterligare sammanhang...',
    'reporting.submit_report': 'Skicka anmälan',
    'reporting.report_submitted': 'Anmälan skickad. Vi kommer granska den snart.',
    'reporting.error': 'Kunde inte skicka anmälan',
    'reporting.reasons.spam': 'Spam eller oönskade meddelanden',
    'reporting.reasons.harassment': 'Trakasserier eller mobbning',
    'reporting.reasons.inappropriate_content': 'Olämpligt innehåll',
    'reporting.reasons.fraud': 'Bedrägeri eller bluff',
    'reporting.reasons.impersonation': 'Identitetsstöld',
    'reporting.reasons.other': 'Annat',
    
    // Admin Reports
    'admin.reports': 'Rapporter',
    'admin.reports_panel': 'Användarrapporter',
    'admin.reports_description': 'Granska och lös användarrapporter',
    'admin.fetch_reports_error': 'Kunde inte hämta rapporter',
    'admin.pending': 'Väntande',
    'admin.resolved': 'Löst',
    'admin.all': 'Alla',
    'admin.no_reports': 'Inga rapporter hittades',
    'admin.back_to_list': 'Tillbaka till listan',
    'admin.reporter': 'Anmälare',
    'admin.reported_user': 'Anmäld användare',
    'admin.reason': 'Anledning',
    'admin.reported_on': 'Anmäld den',
    'admin.reported_by': 'Anmäld av',
    'admin.resolution_notes': 'Lägg till anteckningar...',
    'admin.dismiss': 'Avfärda',
    'admin.warn': 'Varna användare',
    'admin.block': 'Blockera användare',
    'admin.report_resolved': 'Rapporten har lösts',
    'admin.resolve_error': 'Kunde inte lösa rapporten',
    'admin.resolution': 'Lösning',
    
    // Agency Pricing
    'agency_pricing.badge': 'Byråplaner',
    'agency_pricing.title': 'Väx din byrå med rätt plan',
    'agency_pricing.subtitle': 'Välj den perfekta planen för din fastighetsbyrå. Skala ditt team, hantera fler fastigheter och leverera exceptionell service till dina kunder.',
    'agency_pricing.most_popular': 'Mest populär',
    'agency_pricing.per_month': '/månad',
    'agency_pricing.processing': 'Bearbetar...',
    'agency_pricing.comparison_title': 'Detaljerad funktionsjämförelse',
    'agency_pricing.feature': 'Funktion',
    'agency_pricing.faq_title': 'Vanliga frågor',
    'agency_pricing.cta_title': 'Redo att växa din byrå?',
    'agency_pricing.cta_subtitle': 'Anslut dig till tusentals framgångsrika fastighetsbyråer som använder vår plattform för att hantera sina team och fastigheter effektivt.',
    'agency_pricing.cta_button': 'Kom igång med Pro',
    'agency_pricing.sign_in_required': 'Logga in för att uppgradera din plan',
    'agency_pricing.tier.starter': 'Start',
    'agency_pricing.tier.starter_desc': 'Perfekt för små byråer som precis börjar',
    'agency_pricing.tier.starter_cta': 'Kom igång',
    'agency_pricing.tier.pro': 'Pro',
    'agency_pricing.tier.pro_desc': 'För växande byråer med större team',
    'agency_pricing.tier.pro_cta': 'Uppgradera till Pro',
    'agency_pricing.tier.enterprise': 'Enterprise',
    'agency_pricing.tier.enterprise_desc': 'För stora byråer med obegränsade behov',
    'agency_pricing.tier.enterprise_cta': 'Gå Enterprise',
    'agency_pricing.feature.brokers_3': 'Upp till 3 mäklare',
    'agency_pricing.feature.brokers_10': 'Upp till 10 mäklare',
    'agency_pricing.feature.brokers_unlimited': 'Obegränsat antal mäklare',
    'agency_pricing.feature.listings_basic': 'Grundläggande fastighetsannonser',
    'agency_pricing.feature.listings_advanced': 'Avancerade fastighetsannonser',
    'agency_pricing.feature.listings_premium': 'Premium fastighetsannonser',
    'agency_pricing.feature.messaging': 'Kundmeddelanden',
    'agency_pricing.feature.scheduling': 'Visningsschemaläggning',
    'agency_pricing.feature.analytics': 'Analyspanel',
    'agency_pricing.feature.analytics_advanced': 'Avancerad analys',
    'agency_pricing.feature.support': 'Prioriterad support',
    'agency_pricing.feature.support_247': 'Dygnet runt prioriterad support',
    'agency_pricing.feature.branding': 'Anpassat varumärke',
    'agency_pricing.feature.api': 'API-åtkomst',
    'agency_pricing.comparison.brokers': 'Antal mäklare',
    'agency_pricing.comparison.listings': 'Fastighetsannonser',
    'agency_pricing.comparison.basic': 'Grundläggande',
    'agency_pricing.comparison.advanced': 'Avancerad',
    'agency_pricing.comparison.premium': 'Premium',
    'agency_pricing.faq.change_plan_q': 'Kan jag ändra min plan senare?',
    'agency_pricing.faq.change_plan_a': 'Ja! Du kan uppgradera eller nedgradera din plan när som helst. Ändringar träder i kraft omedelbart och vi justerar din fakturering därefter.',
    'agency_pricing.faq.exceed_limit_q': 'Vad händer om jag överskrider min mäklargräns?',
    'agency_pricing.faq.exceed_limit_a': 'Du måste uppgradera till en högre plan för att lägga till fler mäklare. Dina befintliga mäklare fortsätter att fungera normalt.',
    'agency_pricing.faq.trial_q': 'Finns det en gratis provperiod för betalda planer?',
    'agency_pricing.faq.trial_a': 'Vi erbjuder en 14-dagars pengarna-tillbaka-garanti på alla betalda planer. Om du inte är nöjd, kontakta oss för full återbetalning.',
    'agency_pricing.faq.cancel_q': 'Hur avbryter jag mitt abonnemang?',
    'agency_pricing.faq.cancel_a': 'Du kan hantera ditt abonnemang från din byrås instrumentpanel. Gå till fliken Fakturering och klicka på "Hantera abonnemang" för att avbryta eller ändra din plan.',
    'agency_pricing.view_plans': 'Visa planer och priser',
    
    // Broker Pricing
    'broker_pricing.badge': 'Mäklarplaner',
    'broker_pricing.title': 'Uppgradera ditt mäklarkonto',
    'broker_pricing.subtitle': 'Välj den perfekta planen för din fastighetsverksamhet. Få fler annonser, bättre analys och prioriterad support.',
    'broker_pricing.most_popular': 'Mest populär',
    'broker_pricing.current_plan': 'Din nuvarande plan',
    'broker_pricing.per_month': '/månad',
    'broker_pricing.processing': 'Bearbetar...',
    'broker_pricing.loading': 'Laddar...',
    'broker_pricing.manage_subscription': 'Hantera prenumeration',
    'broker_pricing.manage_note': 'Nedgradera eller avbryt när som helst • Förnyas automatiskt varje månad',
    'broker_pricing.downgrade': 'Nedgradera',
    'broker_pricing.comparison_title': 'Detaljerad funktionsjämförelse',
    'broker_pricing.feature': 'Funktion',
    'broker_pricing.faq_title': 'Vanliga frågor',
    'broker_pricing.cta_title': 'Redo att växa din verksamhet?',
    'broker_pricing.cta_subtitle': 'Gå med tusentals framgångsrika mäklare som använder vår plattform för att lista fler fastigheter och avsluta fler affärer.',
    'broker_pricing.cta_button': 'Kom igång med Pro',
    'broker_pricing.sign_in_required': 'Vänligen logga in för att uppgradera din plan',
    'broker_pricing.already_on_plan': 'Du är redan på denna plan',
    'broker_pricing.checkout_error': 'Kunde inte starta utcheckning',
    'broker_pricing.portal_error': 'Kunde inte öppna prenumerationshantering',
    'broker_pricing.tier.normal': 'Normal',
    'broker_pricing.tier.normal_desc': 'Perfekt för enskilda mäklare som kommer igång',
    'broker_pricing.tier.normal_cta': 'Nuvarande plan',
    'broker_pricing.tier.pro': 'Pro',
    'broker_pricing.tier.pro_desc': 'För växande mäklare med fler annonser',
    'broker_pricing.tier.pro_cta': 'Uppgradera till Pro',
    'broker_pricing.tier.enterprise': 'Enterprise',
    'broker_pricing.tier.enterprise_desc': 'För högvolymmäklare med obegränsade behov',
    'broker_pricing.tier.enterprise_cta': 'Gå Enterprise',
    'broker_pricing.feature.listings_5': '5 annonser per månad',
    'broker_pricing.feature.listings_10': '10 annonser per månad',
    'broker_pricing.feature.listings_15': '15 annonser per månad',
    'broker_pricing.feature.basic_features': 'Grundläggande fastighetsfunktioner',
    'broker_pricing.feature.advanced_features': 'Avancerade fastighetsfunktioner',
    'broker_pricing.feature.premium_features': 'Premium fastighetsfunktioner',
    'broker_pricing.feature.messaging': 'Kundmeddelanden',
    'broker_pricing.feature.scheduling': 'Visningsbokning',
    'broker_pricing.feature.video_paid': 'Videoturer ($3 per annons)',
    'broker_pricing.feature.video_free': 'Gratis videoturer inkluderade',
    'broker_pricing.feature.analytics': 'Analyspanel',
    'broker_pricing.feature.analytics_advanced': 'Avancerad analys',
    'broker_pricing.feature.support': 'Prioriterad support',
    'broker_pricing.feature.support_247': 'Prioriterad support dygnet runt',
    'broker_pricing.feature.featured': 'Utvalda annonser',
    'broker_pricing.feature.extra_listings': 'Alternativ för extra annonser',
    'broker_pricing.feature.extra_price': '+$10 per extra annons',
    'broker_pricing.comparison.listings': 'Annonser per månad',
    'broker_pricing.comparison.video': 'Videoturer',
    'broker_pricing.comparison.extra': 'Extra annonser',
    'broker_pricing.comparison.duration': 'Annonstid',
    'broker_pricing.comparison.featured': 'Utvalda annonser',
    'broker_pricing.comparison.30_days': '30 dagar',
    'broker_pricing.comparison.per_listing': '$3 per annons',
    'broker_pricing.comparison.free': 'Gratis',
    'broker_pricing.comparison.each': '$10 styck',
    'broker_pricing.comparison.15_plus': '15 + extra',
    'broker_pricing.faq.autorenew_q': 'Förnyas min prenumeration automatiskt?',
    'broker_pricing.faq.autorenew_a': 'Ja! Din prenumeration förnyas automatiskt varje månad så att du aldrig förlorar tillgång till dina annonser. Du debiteras samma dag varje månad. Du kan avbryta när som helst från Hantera prenumeration-portalen.',
    'broker_pricing.faq.upgrade_q': 'Kan jag uppgradera eller nedgradera när som helst?',
    'broker_pricing.faq.upgrade_a': 'Absolut! Uppgraderingar träder i kraft omedelbart med proportionell fakturering. Nedgraderingar gäller i slutet av din nuvarande faktureringsperiod – du behåller alla funktioner tills dess. Klicka på "Hantera prenumeration" för att göra ändringar.',
    'broker_pricing.faq.expire_q': 'Vad händer när mina annonser går ut?',
    'broker_pricing.faq.expire_a': 'Annonser går ut efter 30 dagar. Du får ett e-postmeddelande 3 dagar före utgång. Du kan förnya dem med dina månatliga gratis platser eller betala $10 per extra annons (endast Enterprise).',
    'broker_pricing.faq.extra_q': 'Vad är "extra annonser"?',
    'broker_pricing.faq.extra_a': 'Enterprise-planmedlemmar kan köpa ytterligare annonser för $10 styck utöver sina 15 månatliga gratisannonser. Detta är perfekt för högvolymmånader.',
    'broker_pricing.faq.cancel_q': 'Hur avbryter jag min prenumeration?',
    'broker_pricing.faq.cancel_a': 'Klicka på "Hantera prenumeration" för att komma åt din faktureringsportal där du kan avbryta, nedgradera eller uppdatera din betalningsmetod. Avbokningar träder i kraft i slutet av din faktureringsperiod.',
    'messaging.conversation_pinned': 'Konversation fäst',
    'messaging.conversation_unpinned': 'Konversation avfäst',
    'messaging.pin_chat': 'Fäst chatt',
    'messaging.unpin_chat': 'Lossa chatt',
    'messaging.pin_failed': 'Kunde inte fästa konversation',
    'messaging.unpin_failed': 'Kunde inte avfästa konversation',
    'messaging.archive': 'Arkivera',
    'messaging.unarchive': 'Återställ',
    'messaging.show_archived': 'Arkiverade',
    'messaging.show_active': 'Visa aktiva',
    'messaging.conversation_archived': 'Konversation arkiverad',
    'messaging.conversation_unarchived': 'Konversation återställd',
    'messaging.archive_failed': 'Kunde inte arkivera konversation',
    'messaging.unarchive_failed': 'Kunde inte återställa konversation',
    
    // Filter additions for rental (sv)
    'filter.listing_type': 'Listningstyp',
    'filter.all_listings': 'Alla listor',
    'filter.for_sale': 'Till salu',
    'filter.for_rent': 'Att hyra',
    'form.listing_type': 'Listningstyp',
    'form.monthly_rent': 'Månadshyra',
    'form.rental_details': 'Hyresdetaljer',
    'rental.deposit': 'Deposition',
  },
  ar: {
    // Header
    'nav.search': 'البحث عن عقارات',
    'nav.search_agents': 'البحث عن وكلاء',
    'nav.sell': 'بيع عقار',
    'nav.news': 'الأخبار',
    'nav.build': 'بناء منزل',
    'nav.agencies': 'الوكالات',
    'nav.buy': 'شراء',
    'nav.valuation': 'التقييم',
    'nav.more': 'المزيد',
    'nav.saved': 'المحفوظات',
    'nav.admin': 'الإدارة',
    'nav.agency': 'الوكالة',
    'nav.agency_dashboard': 'لوحة الوكالة',
    'nav.location_manager': 'مدير المواقع',
    'nav.map_configuration': 'إعدادات الخريطة',
    'nav.hello': 'مرحباً',
    'nav.menu': 'القائمة',
    'nav.manage_news': 'إدارة الأخبار',
    'nav.all_properties': 'جميع العقارات',
    'nav.new_production': 'المشاريع الجديدة',
    
    // Search Section
    'search.for_sale': 'للبيع',
    'search.for_rent': 'للإيجار',
    'search.sold_prices': 'أسعار البيع',
    'search.search_agents': 'البحث عن وكلاء',
    'search.area_label': 'المنطقة',
    'search.area_placeholder': 'اكتب المنطقة أو العنوان',
    'search.area_examples': 'مثال: بغداد، البصرة، أربيل، النجف، كربلاء',
    'search.expand_area': 'توسيع المنطقة بـ',
    'search.property_type': 'نوع العقار',
    'search.show_filters': 'إظهار مرشحات البحث',
    'search.find_properties': 'العثور على عقارات للبيع',
    'search.recent_searches': 'عمليات البحث الأخيرة',
    'search.hide_history': 'إخفاء السجل',
    'search.clear_history': 'حذف السجل',
    
    // Property Types
    'property.all_types': 'جميع الأنواع',
    'property.villas': 'فيلات',
    'property.townhouses': 'منازل متلاصقة',
    'property.apartments': 'شقق',
    'property.business': 'عقارات تجارية',
    'property.land': 'أراضي',
    'property.farms': 'مزارع/غابات',
    'property.other': 'أخرى',
    
    // Cities/Governorates
    'city.baghdad': 'بغداد',
    'city.basra': 'البصرة',
    'city.erbil': 'أربيل',
    'city.sulaymaniyah': 'السليمانية',
    'city.najaf': 'النجف',
    'city.karbala': 'كربلاء',
    'city.mosul': 'الموصل',
    'city.kirkuk': 'كركوك',
    'city.anbar': 'الأنبار',
    'city.dhi_qar': 'ذي قار',
    'city.maysan': 'ميسان',
    'city.wasit': 'واسط',
    'city.babil': 'بابل',
    'city.qadisiyyah': 'القادسية',
    'city.muthanna': 'المثنى',
    'city.diyala': 'ديالى',
    'city.saladin': 'صلاح الدين',
    'city.nineveh': 'نينوى',
    'city.dohuk': 'دهوك',
    
    // Auth extended
    'auth.first_name': 'الاسم الأول',
    'auth.last_name': 'اسم العائلة',
    'auth.first_name_placeholder': 'أدخل اسمك الأول',
    'auth.last_name_placeholder': 'أدخل اسم عائلتك',
    'auth.display_name': 'اسم العرض',
    'auth.display_name_placeholder': 'اختر اسم العرض',
    'auth.display_name_hint': 'هذا هو الاسم الوحيد الذي سيراه الآخرون',
    'auth.date_of_birth': 'تاريخ الميلاد',
    'auth.fill_all_fields': 'يرجى ملء جميع الحقول المطلوبة',
    'auth.must_be_18': 'يجب أن يكون عمرك 18 عامًا على الأقل للتسجيل',
    'auth.must_be_18_hint': 'يجب أن يكون عمرك 18 عامًا على الأقل',
    
    // Identity Verification
    'verification.verify_identity': 'التحقق من الهوية',
    'verification.verified': 'موثق',
    'verification.title': 'التحقق من الهوية',
    'verification.description': 'قم بتحميل وثيقة حكومية للتحقق من هويتك والحصول على شارة التحقق.',
    'verification.document_type': 'نوع الوثيقة',
    'verification.doc_id_card': 'بطاقة الهوية',
    'verification.doc_passport': 'جواز السفر',
    'verification.doc_drivers_license': 'رخصة القيادة',
    'verification.upload_document': 'تحميل الوثيقة',
    'verification.click_to_upload': 'انقر للتحميل أو اسحب وأفلت',
    'verification.file_requirements': 'JPG، PNG، WEBP أو PDF (حد أقصى 10 ميجابايت)',
    'verification.submit': 'إرسال للتحقق',
    'verification.uploading': 'جاري التحميل...',
    'verification.submitted': 'تم إرسال طلب التحقق',
    'verification.submitted_desc': 'تم إرسال طلب التحقق الخاص بك. سنراجعه قريبًا.',
    'verification.pending_message': 'طلب التحقق الخاص بك قيد المراجعة. يستغرق هذا عادة 1-2 أيام عمل.',
    'verification.rejected_message': 'تم رفض طلب التحقق السابق. يرجى تقديم وثيقة جديدة.',
    'verification.status_approved': 'موافق عليه',
    'verification.status_pending': 'قيد الانتظار',
    'verification.status_rejected': 'مرفوض',
    'verification.invalid_file_type': 'نوع ملف غير صالح',
    'verification.allowed_types': 'يرجى تحميل ملف JPG أو PNG أو WEBP أو PDF',
    'verification.file_too_large': 'الملف كبير جدًا',
    'verification.max_size': 'الحد الأقصى لحجم الملف هو 10 ميجابايت',
    'verification.error': 'خطأ في التحقق',
    'verification.admin_title': 'التحقق من الهويات',
    'verification.admin_description': 'مراجعة والموافقة على طلبات التحقق من الهوية',
    'verification.no_requests': 'لا توجد طلبات تحقق',
    'verification.view': 'مراجعة',
    'verification.review_title': 'مراجعة التحقق',
    'verification.review_notes': 'ملاحظات المراجعة',
    'verification.review_notes_placeholder': 'أضف ملاحظات حول قرارك (اختياري)',
    'verification.approve': 'موافقة',
    'verification.reject': 'رفض',
    'verification.approved': 'تمت الموافقة على التحقق',
    'verification.rejected': 'تم رفض التحقق',
    'verification.review_complete': 'تمت معالجة التحقق.',

    // Common
    'common.price': 'السعر',
    'common.rooms': 'الغرف',
    'common.size': 'الحجم',
    'common.governorate': 'المحافظة',
    'common.select_governorate': 'اختر المحافظة',
    'common.cancel': 'إلغاء',
    'common.save': 'حفظ',
    'common.error': 'خطأ',
    'common.delete': 'حذف',
    'common.free': 'مجاني',
    
    // Property card
    'property.sold': 'مباع',
    'property.photos': 'صور',
    'property.rooms_short': 'غرف',
    'property.plot': 'أرض',
    'property.per_month': '/شهر',
    'property.delete_title': 'حذف العقار؟',
    'property.delete_confirm': 'هل أنت متأكد أنك تريد حذف {address}؟ لا يمكن التراجع عن هذا الإجراء.',
    'property.deleted': 'تم حذف العقار',
    'property.deleted_desc': 'تمت إزالة العقار',
    'property.delete_error': 'تعذر حذف العقار',
    
    // Saved searches
    'saved_searches.created': 'تم الإنشاء',
    'saved_searches.alerts_on': 'الإشعارات مفعلة',
    'saved_searches.alerts_off': 'الإشعارات معطلة',
    'saved_searches.email_enabled': 'تم تفعيل إشعارات البريد الإلكتروني',
    'saved_searches.email_disabled': 'تم تعطيل إشعارات البريد الإلكتروني',
    'saved_searches.settings_updated': 'تم تحديث الإعداد',
    'saved_searches.removed': 'تمت إزالة البحث',
    'saved_searches.removed_desc': 'تمت إزالة البحث المحفوظ',
    'saved_searches.error': 'خطأ',
    'saved_searches.update_error': 'تعذر تحديث الإعداد',
    'saved_searches.delete_error': 'تعذر حذف البحث',
    'saved_searches.all_properties': 'جميع العقارات',
    'loading.saved_searches': 'جاري تحميل عمليات البحث المحفوظة...',
    
    // Rental properties
    'rental.for_sale': 'للبيع',
    'rental.for_rent': 'للإيجار',
    'rental.listing_type': 'نوع الإعلان',
    'rental.rental_details': 'تفاصيل الإيجار',
    'rental.rental_period': 'فترة الإيجار',
    'rental.rental_period_hint': 'كم مرة يتم دفع الإيجار',
    'rental.daily': 'يومياً',
    'rental.weekly': 'أسبوعياً',
    'rental.monthly': 'شهرياً',
    'rental.yearly': 'سنوياً',
    'rental.per_daily': '/يوم',
    'rental.per_weekly': '/أسبوع',
    'rental.per_monthly': '/شهر',
    'rental.per_yearly': '/سنة',
    'rental.deposit_amount': 'مبلغ التأمين (دولار)',
    'rental.minimum_lease': 'الحد الأدنى للإيجار (شهور)',
    'rental.months': 'شهور',
    'rental.available_from': 'متاح من',
    'rental.furnished': 'مفروش',
    'rental.utilities_included': 'المرافق مشمولة',
    'rental.pets_allowed': 'الحيوانات الأليفة مسموحة',
    // Hero/News Section
    'hero.find_dream_home': 'ابحث عن منزل أحلامك في العراق',
    'hero.explore_description': 'استكشف آلاف المنازل المعروضة للبيع والأسعار النهائية من جميع أنحاء العراق. احصل على تقييمات، اقرأ أخبار العقارات واتصل بالوكلاء.',
    'hero.badge': 'منصة العقارات الرائدة في العراق',
    
    // Home Features
    'home.feature.properties': 'آلاف العقارات',
    'home.feature.properties_desc': 'تصفح أكبر مجموعة من المنازل والشقق والأراضي في جميع محافظات العراق.',
    'home.feature.market': 'خبرة محلية',
    'home.feature.market_desc': 'معرفة عميقة بكل محافظة وحي في العراق.',
    'home.feature.trusted': 'وكلاء موثوقون',
    'home.feature.trusted_desc': 'تواصل مع محترفي العقارات الموثوقين والمعتمدين.',
    'home.why_choose': 'لماذا تختار عقار نت',
    'home.why_choose_desc': 'كل ما تحتاجه للعثور على عقارك المثالي',
    
    // CTA Section
    'cta.ready': 'مستعد للعثور على منزلك؟',
    'cta.desc': 'تصفح آلاف العقارات في جميع أنحاء العراق',
    'cta.explore': 'استكشف العقارات',
    // Property Listings
    'listings.latest_for_sale': 'أحدث المعروض للبيع',
    'listings.properties_found': 'عقار تم العثور عليه',
    'listings.all_news': 'جميع الأخبار',
    'listings.add_property': 'إضافة عقار',
    'listings.edit': 'تحرير',
    'listings.monthly_fee': 'الرسوم الشهرية',
    'listings.open_floor_plan': 'مخطط مفتوح',
    'listings.large_balcony': 'شرفة كبيرة',
    'listings.family_friendly': 'مناسب للعائلات',
    'listings.photos': 'صور',
    'listings.per_month': 'شهرياً',
    'listings.delete_confirm': 'هل أنت متأكد أنك تريد حذف هذا العقار؟',
    
    // Forms
    'form.price': 'السعر',
    'form.priceUSD': 'السعر (دولار)',
    'form.priceIQD': 'السعر (دينار عراقي)',
    'form.priceConversionNote': 'أدخل السعر بأي عملة - سيتم حساب الأخرى تلقائياً (السعر: 1 دولار ≈ 1,310 دينار)',
    'form.address': 'العنوان',
    'form.city': 'المدينة',
    'form.rooms': 'الغرف',
    'form.bathrooms': 'الحمامات',
    'form.size': 'المساحة (م²)',
    'form.type': 'النوع',
    'form.propertyType': 'نوع العقار',
    'form.monthly_fee': 'الرسوم الشهرية (دينار)',
    'form.description': 'الوصف',
    'form.payment_plan': 'خطة الدفع',
    'form.payment_plan_placeholder': 'مثال: 30% دفعة مقدمة، 70% عند التسليم، أو أقساط شهرية...',
    'form.payment_plan_hint': 'أضف أقساط الدفع لإظهار خيارات الدفع للمشترين',
    'payment_plan.total': 'المجموع',
    'payment_plan.amount': 'المبلغ',
    'payment_plan.description': 'الوصف',
    'payment_plan.date': 'التاريخ',
    'payment_plan.percentage': 'النسبة',
    'payment_plan.add_installment': 'إضافة قسط',
    'payment_plan.hint': 'اتركه فارغاً إذا لم تقدم خطة دفع',
    'payment_plan.down_payment': 'الدفعة المقدمة',
    'payment_plan.first_payment': 'الدفعة الأولى',
    'payment_plan.second_payment': 'الدفعة الثانية',
    'payment_plan.third_payment': 'الدفعة الثالثة',
    'payment_plan.fourth_payment': 'الدفعة الرابعة',
    'payment_plan.fifth_payment': 'الدفعة الخامسة',
    'payment_plan.sixth_payment': 'الدفعة السادسة',
    'payment_plan.seventh_payment': 'الدفعة السابعة',
    'payment_plan.final_payment': 'الدفعة الأخيرة',
    'payment_plan.payment_description': 'وصف الدفعة',
    'payment_plan.enable': 'تضمين خطة الدفع',
    'form.floor_plans': 'مخططات الطوابق',
    'form.upload_floor_plans': 'تحميل صور مخططات الطوابق',
    'form.features': 'المميزات (مفصولة بفاصلة)',
    'form.amenities': 'المرافق',
    'form.amenities_hint': 'اختر جميع المرافق التي تنطبق على هذا العقار',
    'amenities.balcony': 'شرفة',
    'amenities.electricity_generator': 'مولد كهربائي',
    'amenities.central_ac': 'تكييف مركزي',
    'amenities.double_glazed_windows': 'نوافذ زجاج مزدوج',
    'amenities.satellite_tv': 'قنوات فضائية',
    'amenities.cctv': 'كاميرات مراقبة',
    'amenities.security_staff': 'حراسة أمنية',
    'amenities.sewerage_system': 'شبكة صرف صحي',
    'amenities.furnished_kitchen': 'مطبخ مؤثث',
    'amenities.24hr_electricity': 'كهرباء 24 ساعة',
    'amenities.market': 'سوق قريب',
    'amenities.elevator': 'مصعد',
    'amenities.school': 'مدرسة قريبة',
    'amenities.mosque': 'مسجد قريب',
    'amenities.central_gas': 'غاز مركزي',
    'amenities.maintenance': 'خدمات صيانة',
    'amenities.cleaning': 'خدمات تنظيف',
    'amenities.fire_fighting': 'معدات إطفاء حريق',
    'amenities.car_washing': 'غسيل سيارات',
    'amenities.smart_home': 'منزل ذكي',
    'amenities.earthquake_resistant': 'مقاوم للزلازل',
    'amenities.central_fire_system': 'نظام إطفاء مركزي',
    'amenities.hot_water_system': 'نظام ماء ساخن مركزي',
    'amenities.central_vacuum': 'مكنسة مركزية',
    'amenities.fountain': 'نافورة',
    'amenities.24hr_water': 'ماء 24 ساعة',
    'amenities.garbage_chute': 'مزلق نفايات',
    'amenities.kitchen_counter': 'منضدة مطبخ',
    'amenities.street_lights': 'إنارة شوارع',
    'amenities.playground': 'ملعب أطفال',
    'amenities.gym': 'صالة رياضية',
    'amenities.garden': 'حديقة',
    'amenities.parking': 'موقف سيارات',
    'amenities.wifi': 'واي فاي',
    'amenities.security_door': 'باب أمان',
    'amenities.split_ac': 'تكييف سبليت',
    'amenities.bathtub': 'حوض استحمام',
    'amenities.refrigerator': 'ثلاجة',
    'amenities.washing_machine': 'غسالة',
    'amenities.swimming_pool': 'مسبح',
    'amenities.solar_panels': 'ألواح شمسية',
    'amenities.water_tank': 'خزان ماء',
    'amenities.water_well': 'بئر ماء',
    'amenities.septic_tank': 'بيارة',
    'amenities.central_heating': 'تدفئة مركزية',
    'amenities.desert_cooler': 'مبردة صحراوية',
    'amenities.intercom': 'انتركوم',
    'amenities.external_camera': 'كاميرا خارجية',
    'amenities.guard_room': 'غرفة حارس',
    'amenities.rooftop_terrace': 'تراس سطح',
    'amenities.basement': 'قبو',
    'amenities.storage_room': 'غرفة تخزين',
    'amenities.internal_stairs': 'درج داخلي',
    'amenities.external_stairs': 'درج خارجي',
    'amenities.corner_unit': 'وحدة زاوية',
    'amenities.two_streets': 'على شارعين',
    'amenities.double_entrance': 'مدخل مزدوج',
    'amenities.marble_flooring': 'أرضية رخام',
    'amenities.ceramic_tiles': 'بلاط سيراميك',
    'amenities.parquet_flooring': 'أرضية باركيه',
    'amenities.gypsum_decorations': 'ديكور جبس',
    'amenities.false_ceiling': 'سقف مستعار',
    'amenities.steel_doors': 'أبواب حديد',
    'amenities.aluminum_windows': 'نوافذ ألمنيوم',
    'amenities.pvc_windows': 'نوافذ PVC',
    'amenities.guest_room': 'ديوان (غرفة ضيوف)',
    'amenities.servant_quarters': 'غرفة خدم',
    'amenities.covered_parking': 'موقف مسقوف',
    'amenities.concrete_fence': 'سور خرساني',
    'amenities.iron_fence': 'سور حديد',
    'amenities.street_pavement': 'شارع مبلط',
    'amenities.fully_furnished': 'مفروش بالكامل',
    'amenities.hospital': 'مستشفى قريب',
    'amenities.category.utilities': 'الكهرباء والمياه',
    'amenities.category.climate': 'التكييف والتدفئة',
    'amenities.category.security': 'الأمن والحماية',
    'amenities.category.building': 'مميزات المبنى',
    'amenities.category.interior': 'الديكور والأرضيات',
    'amenities.category.rooms': 'الغرف',
    'amenities.category.outdoor': 'الخارجية والمواقف',
    'amenities.category.appliances': 'الأجهزة',
    'amenities.category.services': 'الخدمات',
    'amenities.category.nearby': 'قريب من',
    'common.selected': 'مختار',
    'form.property_images': 'صور العقار',
    'form.required': 'مطلوب',
    'form.optional': 'اختياري',
    'form.cancel': 'إلغاء',
    'form.save_changes': 'حفظ التغييرات',
    'form.uploading': 'جاري الرفع...',
    'form.upload_images': 'رفع الصور',
    'form.click_to_upload': 'انقر لرفع الصور',
    'form.file_formats': 'JPEG, PNG, WebP, GIF (أقصى حد 10MB لكل صورة)',
    'form.select_type': 'اختر النوع',
    'form.location_on_map': 'موقع العقار على الخريطة',
    'form.location_hint': 'انقر على الخريطة لتحديد الموقع الدقيق للعقار',
    'form.hide_preview': 'إخفاء المعاينة',
    'form.show_preview': 'عرض المعاينة',
    'form.selected_images': 'الصور المحددة',
    'form.click_set_primary': 'انقر على صورة لتعيينها كصورة رئيسية',
    'form.uploaded_images': 'الصور المرفوعة',
    'form.images': 'صور',
    'form.uploading_images': 'جاري رفع الصور...',
    'form.city_placeholder': 'ابحث عن مدينة...',
    'form.new_construction': 'بناء جديد (منزل مستقبلي)',
    'form.preview': 'معاينة',
    'form.preview_description': 'هكذا سيظهر إعلانك على الموقع',
    'form.fullscreen': 'ملء الشاشة',
    'form.exit_fullscreen': 'الخروج من ملء الشاشة',
    'form.click_preview_fullscreen': 'انقر على المعاينة لعرض ملء الشاشة',
    'form.preview_contact_disabled': 'خيارات الاتصال معطلة في وضع المعاينة',
    'form.invalid_format': 'صيغة ملف غير صالحة',
    'form.invalid_format_desc': '{filename} له صيغة غير صالحة. المسموح: JPEG، PNG، WebP، GIF',
    'form.file_too_large': 'الملف كبير جداً',
    'form.file_too_large_desc': '{filename} أكبر من 10 ميجابايت',
    'form.upload_failed': 'فشل الرفع',
    'form.upload_failed_desc': 'تعذر رفع الصور',
    'form.images_required': 'الصور مطلوبة',
    'form.images_required_desc': 'يرجى رفع صورة واحدة على الأقل للعقار',
    'form.sold_price_usd': 'سعر البيع (دولار أمريكي)',
    'form.sold_price_placeholder': 'أدخل سعر البيع الفعلي',
    'form.sold_price_description': 'سعر البيع الفعلي (قد يختلف عن سعر العرض)',
    'form.video_url': 'رابط جولة الفيديو',
    'form.video': 'فيديو',
    'form.add_video': 'إضافة فيديو آخر',
    'form.video_addon': '+3$ إضافة',
    'form.video_included': 'مجاناً',
    'form.video_url_hint': 'أضف جولة فيديو من YouTube أو Vimeo. يمكن شراء إضافة الفيديو بعد إنشاء الإعلان.',
    'form.video_url_hint_free': 'أضف جولة فيديو من YouTube أو Vimeo - مضمنة مع اشتراكك!',
    'form.video_url_hint_addon': 'أضف جولة فيديو من YouTube أو Vimeo. سيتم إضافة 3$ إلى تكلفة إعلانك.',
    'form.include_video_tour': 'تضمين جولة فيديو',
    'form.show_map': 'عرض الخريطة',
    'form.no_location': 'لم يتم تحديد موقع',
    
    // Footer
    'footer.contact': 'اتصل',
    'footer.customer_service': 'خدمة العملاء والاتصال',
    'footer.about': 'حول همنت',
    'footer.press': 'الصحافة',
    'footer.investors': 'للمستثمرين',
    'footer.advertising': 'الإعلان والتسويق',
    'footer.jobs': 'العمل معنا',
    'footer.statistics': 'إحصائيات العقارات',
    'footer.apps': 'التطبيقات',
    'footer.iphone_ipad': 'آيفون وآيباد',
    'footer.android': 'أندرويد',
    'footer.follow': 'تابع همنت',
    'footer.newsletter': 'نشرة همنت الإخبارية',
    'footer.whatsapp': 'واتساب',
    'footer.facebook': 'فيسبوك',
    'footer.instagram': 'إنستغرام',
    'footer.linkedin': 'لينكد إن',
    'footer.pinterest': 'بينتيريست',
    'footer.tiktok': 'تيك توك',
    'footer.threads': 'ثريدز',
    'footer.podcast': 'بودكاست همنت - طريق الهمت',
    'footer.all_properties': 'جميع العقارات',
    'footer.privacy': 'الخصوصية',
    'footer.cookies': 'ملفات تعريف الارتباط',
    'footer.terms': 'الشروط والمعلومات القانونية',
    'footer.cookie_settings': 'إعدادات ملفات تعريف الارتباط',
    
    // Agents
    'agents.title': 'العثور على وكيل',
    'agents.subtitle': 'تفكر في البيع؟ نحن نساعدك في العثور على وكلاء خبراء في منطقتك!',
    'agents.area_search': 'البحث بالمنطقة',
    'agents.name_search': 'البحث بالاسم',
    'agents.search_placeholder': 'أدخل المنطقة أو العنوان',
    'agents.show': 'إظهار',
    'agents.sort_by': 'ترتيب حسب',
    'agents.agents_count': 'الوكلاء',
    'agents.companies_label': 'شركات الوساطة',
    'agents.recent_sales': 'تاريخ آخر عملية بيع',
    'agents.most_sales': 'أكثر المبيعات',
    'agents.alphabetical': 'الترتيب الأبجدي',
    'agents.showing_results': 'عرض',
    'agents.agents_label': 'وكلاء',
    'agents.loading': 'جاري تحميل الوكلاء...',
    'agents.no_results': 'لم يتم العثور على وكلاء.',
    'agents.phone': 'الهاتف:',
    'agents.status': 'الحالة:',
    'agents.active_agent': 'وكيل نشط',
    'agents.anonymous_agent': 'وكيل مجهول',
    'agents.agent_label': 'وكيل',
    'agents.show_more': 'إظهار المزيد من الوكلاء',

    // Properties for Sale Page
    'propertiesForSale.title': 'عقارات للبيع - السويد',
    'propertiesForSale.statistics.municipalities': 'المحافظات',
    'propertiesForSale.statistics.forSale': 'للبيع',
    'propertiesForSale.statistics.sold': 'مُبيع هذا الأسبوع',
    'propertiesForSale.map.fullscreen': 'الخريطة / ملء الشاشة',
    'propertiesForSale.map.area': 'المنطقة',
    'propertiesForSale.filters.searchWithin': 'بحث ضمن',
    'propertiesForSale.filters.km': 'كم',
    'propertiesForSale.filters.propertyType': 'نوع العقار',
    'propertiesForSale.filters.allTypes': 'جميع الأنواع',
    'propertiesForSale.filters.villa': 'فيلا',
    'propertiesForSale.filters.apartment': 'شقة',
    'propertiesForSale.filters.townhouse': 'منزل متلاصق',
    'propertiesForSale.filters.plot': 'قطعة أرض',
    'propertiesForSale.filters.commercial': 'تجاري',
    'propertiesForSale.filters.other': 'أخرى',
    'propertiesForSale.filters.rooms': 'الغرف',
    'propertiesForSale.filters.bathrooms': 'الحمامات',
    'propertiesForSale.filters.min': 'الحد الأدنى',
    'propertiesForSale.filters.max': 'الحد الأقصى',
    'propertiesForSale.filters.size': 'المساحة',
    'propertiesForSale.filters.price': 'السعر',
    'propertiesForSale.filters.features': 'المميزات',
    'propertiesForSale.filters.balcony': 'شرفة/تراس/فناء',
    'propertiesForSale.filters.pets': 'الحيوانات الأليفة',
    'propertiesForSale.filters.newBuild': 'بناء جديد',
    'propertiesForSale.filters.showAll': 'إظهار الكل',
    'propertiesForSale.filters.buildYear': 'سنة البناء',
    'propertiesForSale.filters.searchResults': 'نتائج البحث',
    'propertiesForSale.filters.searchPlaceholder': 'ابحث في المناطق والمدن...',
    'propertiesForSale.filters.searchingFor': 'البحث عن:',
    'propertiesForSale.filters.any': 'أي',
    'propertiesForSale.filters.keywords': 'الكلمات المفتاحية',
    'propertiesForSale.filters.showResults': 'عرض النتائج',
    'propertiesForSale.filters.clearFilters': 'مسح',
    'propertiesForSale.filters.realEstateSituation': 'حالة العقار',
    'propertiesForSale.filters.situation.tapu': 'طابو',
    'propertiesForSale.filters.situation.landContract': 'عقد أرض',
    'propertiesForSale.filters.situation.noDocuments': 'لا يوجد لديك وثائق',
    'propertiesForSale.filters.situation.investment': 'استثمار',
    'propertiesForSale.filters.situation.musataha': 'مساطحة',
    'propertiesForSale.filters.situation.card': 'بطاقة',
    'propertiesForSale.filters.situation.agriculturalTapu': 'طابو زراعي',
    'propertiesForSale.sort.newest': 'الأحدث أولاً',
    'propertiesForSale.sort.price_low': 'السعر: من الأقل للأعلى',
    'propertiesForSale.sort.price_high': 'السعر: من الأعلى للأقل',
    'propertiesForSale.sort.size': 'المساحة',
    'propertiesForSale.noResults': 'لم يتم العثور على عقارات',
    'propertiesForSale.tryAdjustingFilters': 'حاول تعديل الفلاتر أو البحث في منطقة مختلفة',

    // Property Types
    'propertyTypes.allTypes': 'جميع الأنواع',
    'propertyTypes.villa': 'الفلل',
    'propertyTypes.apartment': 'الشقق',
    'propertyTypes.townhouse': 'البيوت المتصلة',
    'propertyTypes.business': 'عقارات تجارية',
    'propertyTypes.plot': 'الأراضي',
    'propertyTypes.farm': 'المزارع/الغابات',
    'propertyTypes.other': 'أخرى',

    // Cookie Policy
    'cookie.title': 'سياسة ملفات تعريف الارتباط',
    'cookie.last_updated': 'آخر تحديث:',
    'cookie.section1.title': 'القسم الأول - الأحكام العامة المتعلقة بتنفيذ تقنيات التتبع',
    'cookie.section1.p1': 'تقوم شركة Ploqs AB، المشار إليها فيما يلي باسم "مزود الخدمة"، إلى جانب الشركاء والمقاولين من الباطن المعينين من قبل مزود الخدمة، بتنفيذ واستخدام أشكال مختلفة من آليات التتبع الرقمي، بما في ذلك على سبيل المثال لا الحصر ملفات تعريف الارتباط وإشارات الويب وعلامات البكسل وغيرها من الحلول التقنية المماثلة (يُشار إليها مجتمعة باسم "تقنيات التتبع") على منصات مزود الخدمة الرقمية ومواقع الويب والتطبيقات المحمولة (يُشار إليها مجتمعة باسم "القنوات الرقمية لمزود الخدمة"). تحدد هذه السياسة لمعالجة تقنيات التتبع الأغراض التي يطبق مزود الخدمة من أجلها والطرق التي يطبق بها التقنيات المذكورة.',
    'cookie.section1.p2': 'تتكون تقنيات التتبع بشكل أساسي من ملفات بيانات نصية صغيرة يتم إيداعها على المعدات الطرفية للمستخدم أو برنامج متصفح الويب لغرض تجميع المعلومات المتعلقة بتفاعل المستخدم مع المنصات الرقمية والتطبيقات المحمولة والتنقل فيها.',
    'cookie.section1.h1': 'معالجة البيانات الشخصية واعتبارات حماية البيانات',
    'cookie.section1.p3': 'تشكل المعلومات المجمعة من خلال تطبيق تقنيات التتبع في معظم الحالات بيانات يمكن استخدامها بشكل مباشر أو غير مباشر، وبالاشتراك مع البيانات المتاحة الأخرى، لتحديد هوية الأفراد، بما في ذلك على سبيل المثال لا الحصر عناوين IP ومعرفات ملفات تعريف الارتباط الفريدة ومعرفات الإعلانات (IDFA) ومعرفات الأجهزة والبيانات الوصفية المختلفة التي تتيح التمييز بين الأجهزة الطرفية المحددة.',
    'cookie.section1.h2': 'خيارات المستخدم',
    'cookie.section1.p4': 'يحق للمستخدم مراجعة الموافقات المقدمة مسبقاً في أي وقت من خلال الانتقال إلى خيارات إدارة الموافقة المتوفرة عبر الواجهة الرقمية لمزود الخدمة.',
    'cookie.section1.h3': 'استمرارية وفترات تخزين تقنيات التتبع',
    'cookie.section1.p5': 'تُظهر تقنيات التتبع استمرارية متفاوتة اعتماداً على الغرض المقصود ووظيفة كل تقنية. يتم بدء تقنيات التتبع القائمة على الجلسة عند اتصال المستخدم بالمنصة الرقمية ويتم إنهاؤها في موعد أقصاه إغلاق مثيل متصفح المستخدم. يتم الاحتفاظ بتقنيات التتبع الدائمة في متصفح المستخدم لفترة زمنية محددة مسبقاً تتجاوز الجلسات الفردية.',
    'cookie.section2.title': 'القسم الثاني - تقنيات التتبع الإلزامية لتقديم الخدمة',
    'cookie.section2.p1': 'تشير تقنيات التتبع الإلزامية إلى تلك التطبيقات التقنية المطلوبة لمزود الخدمة لتوفير الوظائف الأساسية والضرورية للمستخدم، على سبيل المثال لغرض الحفاظ على المعلومات المتعلقة بموافقات المستخدم المقدمة مسبقاً فيما يتعلق بتقنيات التتبع، لتمكين التمييز بين جلسات المستخدم المختلفة من خلال المعرفات المجهولة الهوية، لتسهيل الوظائف الخرائطية، لتمكين تشغيل المواد السمعية والبصرية في إعلانات العقارات التي يرفعها وكلاء العقارات، للحفاظ على تفضيلات البحث المحفوظة للمستخدم، للحفاظ على أمان الخدمات، ومنع الاستخدام غير السليم للخدمات وضمان الأداء المستمر والأمثل للخدمة.',
    'cookie.section2.legal': 'الأساس القانوني للمعالجة:',
    'cookie.section2.legal_value': 'المصلحة المشروعة وفقاً للمادة 6.1(f) من اللائحة العامة لحماية البيانات',
    'cookie.section2.storage': 'فترة التخزين:',
    'cookie.section2.storage_value': 'حتى أربعة وعشرين (24) شهراً لتقنيات التتبع الدائمة؛ يتم إنهاء تقنيات التتبع القائمة على الجلسة فوراً عند إغلاق مثيل المتصفح.',
    'cookie.section3.title': 'القسم الثالث - تقنيات التتبع للأغراض التحليلية',
    'cookie.section3.p1': 'يولي مزود الخدمة أهمية كبيرة للقدرة على قياس تكرار الزيارات وتسجيل بيانات التفاعل على المنصة الرقمية وفي التطبيقات المحمولة، وتحليل كيفية استخدام المستخدمين للخدمات الرقمية لمزود الخدمة بشكل عام. شريطة أن يكون المستخدم قد أعطى الموافقة المطلوبة، يقوم مزود الخدمة بتنفيذ تقنيات التتبع، باستثناء البيانات الشخصية المباشرة، لغرض تحليل وتطوير الخدمات الرقمية لمزود الخدمة بشكل أكبر ولتقديم تجربة مستخدم محسنة باستمرار للمستخدم.',
    'cookie.section3.legal_value': 'الموافقة وفقاً للمادة 6.1(a) من اللائحة العامة لحماية البيانات',
    'cookie.section3.storage_value': 'حتى ستة وعشرين (26) شهراً',
    'cookie.section4.title': 'القسم الرابع - تقنيات التتبع لأغراض التخصيص',
    'cookie.section4.p1': 'تهدف تقنيات التتبع المنفذة لأغراض التخصيص إلى تكييف تجربة المستخدم على المنصات الرقمية لمزود الخدمة من خلال توفير محتوى بصلة شخصية معززة للمستخدم الفردي. يعتمد هذا التكيف، على سبيل المثال، على سلوك بحث المستخدم وأنماط التفاعل.',
    'cookie.section5.title': 'القسم الخامس - تقنيات التتبع للأغراض التسويقية',
    'cookie.section5.p1': 'يقوم مزود الخدمة بتنفيذ تقنيات التتبع للأغراض التسويقية، شريطة أن يكون المستخدم قد أعطى الموافقة المطلوبة، لغرض تمكين عرض محتوى إعلاني ذي صلة وإعلانات عقارية بصلة معززة للمستخدم بناءً على ملف تعريف إعلاني مُنشأ.',
    'cookie.section5.p2': 'من خلال تنفيذ تقنيات التتبع، يضمن مزود الخدمة أيضاً أن التعرضات الإعلانية تقتصر على عدد محدد مسبقاً من المرات لكل مستخدم، وأن فعالية الإعلان يتم قياسها وتحليلها.',
    'cookie.section5.p3': 'تجدر الإشارة إلى أنه في حالة اختيار المستخدم إلغاء تنشيط تقنيات التتبع للأغراض التسويقية، سيظل المحتوى الإعلاني معروضاً للمستخدم على المنصات الرقمية لمزود الخدمة، ولكن بدون تخصيص.',
    'cookie.section5.storage_value': 'حتى أربعة وعشرين (24) شهراً',
    'cookie.section6.title': 'القسم السادس - عمليات نقل البيانات الدولية إلى دول ثالثة',
    'cookie.section6.p1': 'في أداة إدارة الموافقة الخاصة بمزود الخدمة، يتم توفير معلومات بشأن تقنيات التتبع والشركاء الذين يعالجون البيانات الشخصية للمستخدم داخل، أو ينقلون البيانات الشخصية للمستخدم إلى، ولايات قضائية خارج الاتحاد الأوروبي والمنطقة الاقتصادية الأوروبية (ما يسمى بـ "الدول الثالثة").',
    'cookie.section7.title': 'القسم السابع - تعليمات إزالة تقنيات التتبع',
    'cookie.section7.p1': 'يمتلك المستخدم خيار إزالة تقنيات التتبع من معداته الطرفية أو جهازه المحمول وتكوين إعدادات تخزين البيانات المحلية عبر واجهة إعدادات المتصفح.',
    'cookie.section7.p2': 'للحصول على تعليمات مفصلة بشأن معالجة وإزالة تقنيات التتبع، يُشار إلى خيار المساعدة في متصفح المستخدم أو الروابط التشعبية أدناه لكل تطبيق متصفح:',
    
    // Auth
    'auth.loading': 'جاري التحميل...',
    'auth.login': 'تسجيل الدخول',
    'auth.create_account': 'إنشاء حساب',
    'auth.login_subtitle': 'قم بتسجيل الدخول عن طريق اختيار أحد الخيارات أدناه.',
    'auth.signup_subtitle': 'أنشئ حسابًا جديدًا للبدء.',
    'auth.continue_apple': 'المتابعة مع Apple',
    'auth.continue_google': 'المتابعة مع Google',
    'auth.or': 'أو',
    'auth.email': 'البريد الإلكتروني',
    'auth.email_placeholder': 'أدخل البريد الإلكتروني',
    'auth.password': 'كلمة المرور',
    'auth.password_placeholder': 'أدخل كلمة المرور',
    'auth.account_type': 'نوع الحساب',
    'auth.user_role': 'مستخدم - أريد شراء/استئجار عقار',
    'auth.agent_role': 'وكيل - أريد بيع/تأجير عقارات',
    'auth.continue': 'متابعة',
    'auth.no_account': 'ليس لديك حساب؟ إنشاء حساب',
    'auth.have_account': 'لديك حساب بالفعل؟ تسجيل الدخول',
    'auth.login_failed': 'فشل تسجيل الدخول',
    'auth.invalid_credentials': 'بريد إلكتروني أو كلمة مرور غير صالحة',
    'auth.welcome_back': 'مرحبًا بعودتك!',
    'auth.logged_in': 'تم تسجيل دخولك.',
    'auth.signup_failed': 'فشل التسجيل',
    'auth.account_created': 'تم إنشاء الحساب!',
    'auth.check_email': 'تحقق من بريدك الإلكتروني لتأكيد حسابك.',
    'auth.error': 'حدث خطأ',
    'auth.try_again': 'يرجى المحاولة مرة أخرى لاحقًا.',
    'auth.broker_interest_title': 'هل أنت وسيط عقاري؟',
    'auth.broker_interest_description': 'إذا كنت مهتمًا بإدراج عقارات على عقار نت، أرسل لنا إعلان اهتمام وسنتواصل معك.',
    'auth.broker_contact_us': 'تواصل معنا',
    'auth.broker_email_subject': 'اهتمام بتسجيل وسيط عقاري',
    'auth.broker_email_body': 'مرحبًا،\n\nأنا مهتم بالتسجيل كوسيط عقاري على عقار نت.\n\nالاسم:\nاسم الوكالة:\nالهاتف:\nالمدينة:\n\nشكرًا!',
    
    // Favorites Page
    'favorites.title': 'المفضلة',
    'favorites.saved_properties': 'عقارات محفوظة',
    'favorites.login_required': 'سجل دخول لرؤية المفضلة',
    'favorites.login_description': 'احفظ العقارات التي تعجبك لتجدها بسهولة لاحقاً.',
    'favorites.loading': 'جاري تحميل المفضلة...',
    'favorites.no_favorites': 'لا توجد مفضلات بعد',
    'favorites.start_saving': 'ابدأ بحفظ العقارات بالنقر على القلب في الإعلانات التي تعجبك.',
    'favorites.search_properties': 'البحث عن عقارات',
    
    // Profile Page
    'profile.title': 'ملفي الشخصي',
    'profile.subtitle': 'إدارة معلوماتك الشخصية والإعدادات',
    'profile.loading': 'جاري تحميل الملف الشخصي...',
    'profile.fetch_error': 'خطأ في جلب الملف الشخصي',
    'profile.fetch_error_description': 'تعذر جلب معلومات الملف الشخصي.',
    'profile.unnamed_user': 'مستخدم بدون اسم',
    'profile.personal_info': 'المعلومات الشخصية',
    'profile.update_info': 'تحديث معلوماتك الشخصية',
    'profile.email': 'البريد الإلكتروني',
    'profile.email_readonly': 'لا يمكن تغيير البريد الإلكتروني',
    'profile.display_name': 'الاسم المعروض',
    'profile.display_name_placeholder': 'اسمك',
    'profile.phone': 'رقم الهاتف',
    'profile.phone_placeholder': '070-123 45 67',
    'profile.save_changes': 'حفظ التغييرات',
    'profile.saving': 'جاري الحفظ...',
    'profile.reset': 'إعادة تعيين',
    'profile.updated': 'تم تحديث الملف الشخصي',
    'profile.updated_description': 'تم حفظ ملفك الشخصي.',
    'profile.update_error': 'خطأ في التحديث',
    'profile.update_error_description': 'تعذر حفظ التغييرات.',
    'profile.active_listings': 'الإعلانات النشطة',
    'profile.active_listings_description': 'إعلانات العقارات المنشورة للبيع',
    'profile.view_all': 'عرض الكل',
    'profile.loading_listings': 'جاري تحميل الإعلانات...',
    'profile.no_active_listings': 'لا توجد إعلانات نشطة بعد',
    'profile.more_active_listings': 'و {count} إعلانات نشطة أخرى',
    'profile.sold_properties': 'العقارات المباعة',
    'profile.sold_properties_description': 'عقاراتك المباعة',
    'profile.no_sold_properties': 'لا توجد عقارات مباعة بعد',
    'profile.more_sold_properties': 'و {count} عقارات مباعة أخرى',
    'profile.listing_fetch_error': 'خطأ في جلب الإعلانات',
    'profile.listing_fetch_error_description': 'تعذر جلب إعلاناتك.',
    'profile.account_settings': 'إعدادات الحساب',
    'profile.account_settings_description': 'إدارة حسابك والأمان',
    'profile.change_password': 'تغيير كلمة المرور',
    'profile.change_password_description': 'حدث كلمة المرور لتسجيل دخول أكثر أماناً',
    'profile.change': 'تغيير',
    'profile.two_factor': 'المصادقة الثنائية',
    'profile.two_factor_description': 'أضف أماناً إضافياً لحسابك',
    'profile.enable': 'تفعيل',
    'profile.delete_account': 'حذف الحساب',
    'profile.delete_account_description': 'حذف دائم لحسابك وجميع البيانات',
    'profile.delete': 'حذف',
    'profile.logout': 'تسجيل الخروج',
    
    // Profile Email Notifications
    'profile.email_notifications': 'إشعارات البريد الإلكتروني',
    'profile.email_notifications_description': 'اختر الإشعارات التي تريد تلقيها',
    'profile.notif_property_alerts': 'تنبيهات العقارات',
    'profile.notif_property_alerts_desc': 'احصل على إشعارات عن العقارات الجديدة التي تطابق عمليات البحث المحفوظة',
    'profile.notif_saved_search_alerts': 'تحديثات البحث المحفوظ',
    'profile.notif_saved_search_alerts_desc': 'تلقي تحديثات عندما تتطابق عقارات جديدة مع معاييرك',
    'profile.notif_newsletter': 'النشرة الإخبارية',
    'profile.notif_newsletter_desc': 'تلقي نشرتنا الأسبوعية مع رؤى السوق',
    'profile.notif_viewing_confirmations': 'تأكيدات المعاينة',
    'profile.notif_viewing_confirmations_desc': 'احصل على رسائل تأكيد للمعاينات المجدولة',
    'profile.save_preferences': 'حفظ التفضيلات',
    'profile.notifications_saved': 'تم حفظ تفضيلات الإشعارات',
    'profile.notifications_save_error': 'فشل في حفظ تفضيلات الإشعارات',
    
    // Not Found Page
    'notfound.title': '404',
    'notfound.message': 'عفواً! الصفحة غير موجودة',
    'notfound.back_home': 'العودة للصفحة الرئيسية',
    
    // Property Detail Page
    'property.back_to_listings': 'العودة للإعلانات',
    'property.not_found': 'العقار غير موجود',
    'property.link_copied': 'تم نسخ الرابط',
    'property.link_copied_description': 'تم نسخ رابط العقار إلى الحافظة',
    'property.description': 'الوصف',
    'property.features': 'المميزات',
    'property.details': 'تفاصيل العقار',
    'property.rooms_label': 'الغرف',
    'property.size_label': 'المساحة',
    'property.plot_size_label': 'مساحة الأرض',
    'property.monthly_fee_label': 'الرسوم الشهرية',
    'property.contact_agent': 'تواصل مع الوكيل',
    'property.send_message': 'إرسال رسالة',
    'property.schedule_viewing': 'حجز موعد معاينة',
    'property.section.details': 'التفاصيل',
    'property.section.amenities': 'المرافق',
    'property.section.overview': 'نظرة عامة',
    'property.section.payment_plan': 'خطة الدفع',
    'property.section.floor_plans': 'مخططات الطوابق',
    'property.section.view_map': 'عرض الخريطة',
    'property.copy_link': 'نسخ الرابط',
    'property.share': 'مشاركة',
    'property.share_text': 'شاهد هذا العقار',
    'property.posted_on': 'تاريخ النشر',
    'property.type': 'النوع',
    'property.status': 'الحالة',
    'property.available': 'متاح',
    'property.no_amenities': 'لا توجد مرافق مدرجة لهذا العقار',
    'property.no_description': 'لا يوجد وصف متاح',
    'property.location': 'الموقع',
    'property.payment_plan_info': 'تواصل مع الوكيل للحصول على تفاصيل خطة الدفع',
    'property.contact_for_payment_plan': 'تواصل لخطة الدفع',
    'property.no_floor_plans': 'لا توجد مخططات طوابق متاحة',
    'property.request_floor_plans': 'طلب مخططات الطوابق',
    'property.no_map_location': 'الموقع غير متاح على الخريطة',
    
    // News Page
    'news.subtitle': 'ابق على اطلاع بآخر أخبار العقارات ورؤى السوق والأدلة',
    'news.no_articles': 'لا توجد مقالات إخبارية بعد',
    'news.check_back': 'عد قريباً لآخر أخبار العقارات ورؤى السوق.',
    'news.search_placeholder': 'بحث في المقالات...',
    'news.showing_filtered': 'عرض {shown} من {total} مقالة في هذه الصفحة',
    'news.clear_filters': 'مسح الفلاتر',
    'news.no_matching': 'لا توجد مقالات مطابقة',
    'news.adjust_filters': 'جرب تعديل البحث أو الفلاتر للعثور على ما تبحث عنه.',
    'news.publisher': 'الناشر',
    'news.page_info': 'الصفحة {current} من {total} • {count} مقالة',
    'news.category.all': 'الكل',
    'news.category.market_news': 'أخبار السوق',
    'news.category.property_guides': 'أدلة العقارات',
    'news.category.investment_tips': 'نصائح الاستثمار',
    'news.category.industry_updates': 'تحديثات القطاع',
    'news.category.announcements': 'الإعلانات',
    'news.category.expert_advice': 'نصائح الخبراء',
    
    // Filters/Sorting
    'filters.any': 'الكل',
    'filters.newest_first': 'الأحدث أولاً',
    'filters.price_low_high': 'السعر: من الأقل للأعلى',
    'filters.price_high_low': 'السعر: من الأعلى للأقل',
    'filters.size': 'المساحة',
    'filters.searching_for': 'البحث عن',
    'filters.search_areas': 'ابحث عن مناطق، مدن...',
    
    // Toast Messages
    'toast.error': 'خطأ',
    'toast.success': 'نجاح',
    'toast.auth_required': 'المصادقة مطلوبة',
    'toast.sign_in_required': 'يرجى تسجيل الدخول للمتابعة',
    'toast.sign_in_favorites': 'يرجى تسجيل الدخول لإضافة المفضلة',
    'toast.sign_in_properties': 'يرجى تسجيل الدخول لإضافة العقارات',
    'toast.properties_load_error': 'فشل في تحميل العقارات',
    'toast.property_added': 'تمت إضافة العقار بنجاح',
    'toast.property_add_error': 'فشل في إضافة العقار',
    'toast.property_updated': 'تم تحديث العقار بنجاح',
    'toast.property_update_error': 'فشل في تحديث العقار',
    'toast.property_deleted': 'تم حذف العقار بنجاح',
    'toast.property_delete_error': 'فشل في حذف العقار',
    'toast.property_marked_sold': 'تم وضع علامة على العقار كمباع بنجاح',
    'toast.property_sold_error': 'فشل في وضع علامة على العقار كمباع',
    'toast.invalid_price': 'يرجى إدخال سعر بيع صالح',
    'toast.favorites_load_error': 'فشل في تحميل المفضلة',
    'toast.added_to_favorites': 'تمت الإضافة إلى المفضلة',
    'toast.add_favorites_error': 'فشل في الإضافة إلى المفضلة',
    'toast.removed_from_favorites': 'تمت الإزالة من المفضلة',
    'toast.remove_favorites_error': 'فشل في الإزالة من المفضلة',
    'toast.search_saved': 'تم حفظ البحث',
    'toast.search_saved_desc': 'تم حفظه مع تفعيل تنبيهات البريد الإلكتروني',
    'toast.search_save_error': 'لا يمكن حفظ البحث',
    'toast.valuation_complete': 'اكتمل التقييم',
    'toast.valuation_estimated': 'القيمة المقدرة',
    'toast.valuation_error': 'لا يمكن الحصول على التقييم',
    'toast.message_sent': 'تم إرسال الرسالة',
    'toast.agent_contact_desc': 'سيتواصل معك الوكيل قريباً',
    'toast.message_error': 'لا يمكن إرسال الرسالة',
    'toast.viewing_booked': 'تم حجز المعاينة',
    'toast.viewing_confirmed': 'تم حجز معاينتك وإرسال التأكيد عبر البريد الإلكتروني',
    'toast.viewing_error': 'لا يمكن حجز المعاينة',
    'toast.image_analysis_complete': 'اكتمل تحليل الصورة',
    'toast.image_analysis_desc': 'اكتمل تحليل الذكاء الاصطناعي للصورة',
    'toast.image_analysis_error': 'لا يمكن تحليل الصورة',
    'toast.logged_out': 'تم تسجيل الخروج',
    'toast.welcome_back': 'مرحباً بك مرة أخرى في أي وقت!',
    
    // Recent searches
    'search.properties_label': 'العقارات',
    'search.from': 'من',
    'search.up_to': 'حتى',
    
    // News Management
    'news_mgmt.title': 'إدارة الأخبار',
    'news_mgmt.subtitle': 'إنشاء وإدارة المقالات الإخبارية',
    'news_mgmt.create_article': 'إنشاء مقال',
    'news_mgmt.edit_article': 'تعديل المقال',
    'news_mgmt.create_new': 'إنشاء مقال جديد',
    'news_mgmt.all_articles': 'جميع المقالات',
    'news_mgmt.no_articles': 'لا توجد مقالات بعد. أنشئ أول مقال!',
    'news_mgmt.delete_article': 'حذف المقال',
    'news_mgmt.delete_confirm': 'هل أنت متأكد من حذف هذا المقال؟ لا يمكن التراجع عن هذا الإجراء.',
    'news_mgmt.title_label': 'العنوان',
    'news_mgmt.title_placeholder': 'أدخل عنوان المقال',
    'news_mgmt.category_label': 'الفئة',
    'news_mgmt.category_placeholder': 'مثل: أخبار السوق، أدلة، نصائح',
    'news_mgmt.image_url': 'رابط الصورة',
    'news_mgmt.content': 'المحتوى',
    'news_mgmt.content_placeholder': 'أدخل محتوى المقال',
    'news_mgmt.published': 'منشور',
    'news_mgmt.draft': 'مسودة',
    'news_mgmt.update_article': 'تحديث المقال',
    'news_mgmt.required_fields': 'العنوان والفئة مطلوبان',
    'news_mgmt.article_updated': 'تم تحديث المقال بنجاح',
    'news_mgmt.article_created': 'تم إنشاء المقال بنجاح',
    'news_mgmt.article_save_error': 'فشل في حفظ المقال',
    'news_mgmt.article_deleted': 'تم حذف المقال بنجاح',
    'news_mgmt.article_delete_error': 'فشل في حذف المقال',
    'news_mgmt.access_denied': 'تم رفض الوصول',
    'news_mgmt.access_denied_desc': 'ليس لديك إذن للوصول إلى هذه الصفحة. يمكن للناشرين والمسؤولين فقط إدارة الأخبار.',
    
    // Badges & Labels
    'badge.new': 'جديد',
    'badge.sold': 'مباع',
    'badge.featured': 'مميز',
    'badge.reduced': 'سعر مخفض',
    'badge.new_construction': 'بناء جديد',
    
    // Confirmation Messages
    'confirm.delete': 'حذف',
    'confirm.cancel': 'إلغاء',
    'confirm.yes': 'نعم',
    'confirm.no': 'لا',
    'confirm.are_you_sure': 'هل أنت متأكد؟',
    'confirm.action_irreversible': 'لا يمكن التراجع عن هذا الإجراء.',
    
    // Tooltips
    'tooltip.favorite': 'إضافة إلى المفضلة',
    'tooltip.share': 'مشاركة العقار',
    'tooltip.edit': 'تعديل العقار',
    'tooltip.delete': 'حذف العقار',
    'tooltip.contact': 'التواصل مع الوكيل',
    'tooltip.schedule': 'جدولة المعاينة',
    'tooltip.map_view': 'عرض على الخريطة',
    'tooltip.list_view': 'عرض القائمة',
    'tooltip.grid_view': 'عرض الشبكة',
    
    // Leaderboard
    'leaderboard': 'لوحة المتصدرين',
    'leaderboardDescription': 'اكتشف أفضل الوكالات والوسطاء مرتبين حسب المشاهدات والمبيعات',
    'topAgencies': 'أفضل الوكالات',
    'topBrokers': 'أفضل الوسطاء',
    'topAgenciesDescription': 'الوكالات مرتبة حسب مشاهدات العقارات وأداء المبيعات',
    'topBrokersDescription': 'الوسطاء مرتبين حسب مشاهدات العقارات وأداء المبيعات',
    'allRegions': 'جميع المناطق',
    'noAgenciesFound': 'لم يتم العثور على وكالات',
    'noBrokersFound': 'لم يتم العثور على وسطاء',
    'brokers': 'وسطاء',
    'sold': 'مباع',
    'active': 'نشط',
    'views': 'مشاهدات',
    
    // Dialogs - Contact Agent
    'dialog.contact_agent': 'تواصل مع الوكيل',
    'dialog.contact_agent_btn': 'تواصل مع الوكيل',
    'dialog.contact_message_placeholder': 'مرحباً! أنا مهتم بالعقار في {address} وأرغب في معرفة المزيد. يرجى التواصل معي عندما يكون لديك وقت.',
    'dialog.contact_sending': 'جاري الإرسال...',
    'dialog.contact_send': 'إرسال الرسالة',
    'dialog.contact_note': 'سيتم إرسال استفسارك مباشرة إلى الوكيل المسؤول الذي سيتواصل معك.',
    
    // Dialogs - Schedule Viewing
    'dialog.schedule_viewing': 'حجز موعد معاينة',
    'dialog.schedule_viewing_btn': 'حجز موعد معاينة',
    'dialog.preferred_time': 'وقت المعاينة المفضل',
    'dialog.preferred_time_note': 'اختر التاريخ والوقت للمعاينة. سيؤكد الوكيل التوفر.',
    'dialog.special_requests': 'طلبات خاصة',
    'dialog.special_requests_placeholder': 'أي طلبات أو أسئلة خاصة...',
    'dialog.schedule_booking': 'جاري الحجز...',
    'dialog.schedule_book': 'حجز المعاينة',
    'dialog.schedule_note': 'سيتم إرسال طلبك إلى الوكيل الذي سيؤكد الوقت عبر البريد الإلكتروني.',
    
    // Dialogs - Save Search
    'dialog.save_search': 'حفظ البحث',
    'dialog.save_search_btn': 'حفظ البحث',
    'dialog.search_name': 'اسم البحث',
    'dialog.search_name_placeholder': 'مثال: شقة في بغداد',
    'dialog.city_municipality': 'المدينة/البلدية',
    'dialog.min_rooms': 'الحد الأدنى للغرف',
    'dialog.min_size': 'الحد الأدنى للمساحة (م²)',
    'dialog.property_type': 'نوع العقار',
    'dialog.email_alerts': 'إشعارات البريد عند توفر عقارات مطابقة',
    'dialog.saving_search': 'جاري الحفظ...',
    
    // Dialogs - Mark as Sold
    'dialog.mark_sold': 'تحديد كمباع',
    'dialog.mark_sold_description': 'أدخل سعر البيع النهائي لهذا العقار. سينقل الإعلان إلى العقارات المباعة.',
    'dialog.final_sale_price': 'سعر البيع النهائي (دولار)',
    'dialog.listed_price': 'السعر المعلن',
    'dialog.property_details': 'تفاصيل العقار',
    'dialog.enter_valid_price': 'يرجى إدخال سعر بيع صالح',
    'dialog.enter_price_placeholder': 'أدخل سعر البيع',
    'dialog.marked_sold_success': 'تم تحديد العقار كمباع بنجاح',
    'dialog.mark_sold_error': 'فشل في تحديد العقار كمباع',
    
    // Dialogs - Add Property
    'dialog.add_property': 'إضافة عقار جديد',
    'dialog.hide_preview': 'إخفاء المعاينة',
    'dialog.show_preview': 'عرض المعاينة',
    'dialog.selected_images': 'الصور المحددة',
    'dialog.uploaded_images': 'الصور المرفوعة',
    'dialog.click_primary': 'انقر على صورة لتعيينها كرئيسية',
    'dialog.invalid_format': 'صيغة غير صالحة',
    'dialog.file_too_large': 'الملف كبير جداً',
    'dialog.upload_failed': 'فشل الرفع',
    'dialog.images_required': 'الصور مطلوبة',
    'dialog.please_upload': 'يرجى رفع صورة واحدة على الأقل للعقار',
    'dialog.form_validation_failed': 'فشل التحقق من النموذج',
    
    // Admin Pages
    'admin.loading': 'جار التحميل...',
    'admin.access_denied': 'تم رفض الوصول. مطلوب صلاحيات المسؤول.',
    
    // Admin Notification Settings
    'admin.notifications.title': 'إعدادات الإشعارات',
    'admin.notifications.description': 'إدارة إعدادات الإشعارات لجميع أنواع المستخدمين. تفعيل أو تعطيل أو إخفاء الإشعارات حسب الحاجة.',
    'admin.notifications.fetch_error': 'فشل في جلب إعدادات الإشعارات',
    'admin.notifications.update_success': 'تم تحديث إعداد الإشعار',
    'admin.notifications.update_error': 'فشل في تحديث إعداد الإشعار',
    'admin.notifications.role_description': 'إدارة الإشعارات لمستخدمي {role}',
    'admin.notifications.hidden': 'مخفي',
    'admin.notifications.disabled': 'معطل',
    'admin.notifications.active': 'نشط',
    'admin.notifications.hide': 'إخفاء',
    'admin.notifications.roles.user': 'المستخدمون',
    'admin.notifications.roles.maklare': 'الوسطاء',
    'admin.notifications.roles.agency': 'الوكالات',
    'admin.notifications.roles.admin': 'المسؤولون',
    'admin.notifications.roles.publisher': 'الناشرون',
    'admin.notifications.types.new_viewing_request': 'طلب معاينة جديد',
    'admin.notifications.types.new_client_message': 'رسالة عميل جديدة',
    'admin.notifications.types.property_alerts': 'تنبيهات العقارات',
    'admin.notifications.types.weekly_summary': 'الملخص الأسبوعي',
    'admin.notifications.types.broker_property_added': 'وسيط أضاف عقار',
    'admin.notifications.types.broker_viewing_request': 'طلب معاينة الوسيط',
    'admin.notifications.types.broker_client_message': 'رسالة عميل الوسيط',
    'admin.notifications.types.broker_joined': 'وسيط انضم',
    'admin.notifications.types.weekly_agency_summary': 'الملخص الأسبوعي للوكالة',
    'admin.notifications.types.saved_search_alerts': 'تنبيهات البحث المحفوظ',
    'admin.notifications.types.favorite_property_updates': 'تحديثات العقارات المفضلة',
    'admin.notifications.types.newsletter': 'النشرة الإخبارية',
    'admin.notifications.types.system_alerts': 'تنبيهات النظام',
    'admin.notifications.types.new_user_signup': 'تسجيل مستخدم جديد',
    'admin.notifications.types.article_published': 'تم نشر المقال',

    // Admin Settings
    'admin.settings': 'إعدادات المسؤول',
    'admin.messaging_settings': 'إعدادات المراسلة',
    'admin.messaging_settings_description': 'التحكم في ميزات المراسلة والمكالمات لجميع المستخدمين',
    'admin.messaging': 'المراسلة',
    'admin.messaging_description': 'السماح للمستخدمين بإرسال الرسائل',
    'admin.voice_calls': 'المكالمات الصوتية',
    'admin.voice_calls_description': 'السماح للمستخدمين بإجراء مكالمات صوتية',
    'admin.video_calls': 'مكالمات الفيديو',
    'admin.video_calls_description': 'السماح للمستخدمين بإجراء مكالمات فيديو',
    'admin.screen_sharing': 'مشاركة الشاشة',
    'admin.screen_sharing_description': 'السماح للمستخدمين بمشاركة شاشتهم أثناء المكالمات',

    'location_mgr.title': 'مدير المواقع',
    'location_mgr.current_locations': 'المواقع الحالية',
    'location_mgr.locations_in_db': '{count} موقع في قاعدة البيانات',
    'location_mgr.refresh': 'تحديث',
    'location_mgr.populate_title': 'ملء المواقع',
    'location_mgr.populate_desc': 'جلب جميع المدن والمناطق العراقية من Google Places API وحفظها في قاعدة البيانات. سيؤدي هذا إلى استبدال جميع المواقع الموجودة.',
    'location_mgr.populating': 'جار الملء...',
    'location_mgr.populate_btn': 'ملء من Google Places',
    'location_mgr.success': 'نجاح',
    'location_mgr.success_msg': 'تم ملء المواقع بنجاح',
    'location_mgr.error': 'خطأ',
    'location_mgr.error_msg': 'فشل ملء المواقع. تحقق من وحدة التحكم للحصول على التفاصيل.',
    
    // Map Configuration Manager
    'map_config.title': 'مدير تكوين الخريطة',
    'map_config.subtitle': 'إدارة تكوينات الخريطة الدائمة لتقليل استخدام Google Maps API',
    'map_config.loading': 'جار تحميل تكوينات الخريطة...',
    'map_config.create': 'إنشاء تكوين',
    'map_config.create_title': 'إنشاء تكوين الخريطة',
    'map_config.create_desc': 'إنشاء تكوين خريطة دائم جديد سيتم استخدامه بدلاً من استدعاء Google Maps API بشكل متكرر.',
    'map_config.edit_title': 'تعديل تكوين الخريطة',
    'map_config.edit_desc': 'تحديث تكوين الخريطة الدائم.',
    'map_config.name': 'اسم التكوين',
    'map_config.name_placeholder': 'مثال: خريطة عقارات العراق',
    'map_config.description': 'الوصف (اختياري)',
    'map_config.description_placeholder': 'وصف مختصر لهذا التكوين',
    'map_config.map_data': 'تكوين الخريطة (JSON)',
    'map_config.map_data_placeholder': 'تكوين الخريطة بصيغة JSON',
    'map_config.cancel': 'إلغاء',
    'map_config.update': 'تحديث التكوين',
    'map_config.no_configs': 'لا توجد تكوينات خريطة',
    'map_config.no_configs_desc': 'أنشئ أول تكوين خريطة لبدء إدارة خرائطك بكفاءة.',
    'map_config.create_first': 'إنشاء أول تكوين',
    'map_config.active': 'نشط',
    'map_config.activate': 'تفعيل',
    'map_config.edit': 'تعديل',
    'map_config.delete': 'حذف',
    'map_config.delete_confirm': 'هل أنت متأكد؟',
    'map_config.delete_desc': 'سيؤدي هذا إلى حذف تكوين الخريطة "{name}" نهائياً. لا يمكن التراجع عن هذا الإجراء.',
    'map_config.created': 'تم الإنشاء',
    'map_config.last_updated': 'آخر تحديث',
    'map_config.map_center': 'مركز الخريطة',
    'map_config.zoom_level': 'مستوى التكبير',
    'map_config.cities': 'المدن',
    'map_config.cities_configured': '{count} مدينة مُكوَّنة',
    'map_config.invalid_json': 'صيغة JSON غير صالحة في بيانات الخريطة',
    
    // About Page
    'about.title': 'عن عقارنت',
    'about.subtitle': 'شريكك الموثوق في سوق العقارات العراقي',
    'about.description': 'عقارنت هي المنصة العقارية الرائدة في العراق، تربط بين المشترين والبائعين والوكلاء في جميع أنحاء البلاد. نقدم قوائم عقارية شاملة ورؤى سوقية وأدوات احترافية لجعل رحلتك العقارية سلسة.',
    'about.mission_title': 'مهمتنا',
    'about.mission_text': 'إحداث ثورة في سوق العقارات العراقي من خلال توفير معلومات عقارية شفافة وسهلة الوصول وموثوقة للجميع.',
    'about.vision_title': 'رؤيتنا',
    'about.vision_text': 'أن نصبح المنصة العقارية الأكثر ثقة في الشرق الأوسط، وتمكين الجميع من العثور على منزل أحلامهم.',
    'about.services_title': 'خدماتنا',
    'about.services_text': 'قوائم العقارات، تحليل السوق، التواصل مع الوكلاء، تقييمات العقارات، وأخبار عقارية شاملة.',
    'about.team_title': 'فريقنا',
    'about.team_text': 'فريق متخصص من المحترفين العقاريين وخبراء التكنولوجيا ومتخصصي خدمة العملاء.',
    'about.company_info': 'معلومات الشركة',
    'about.based_in': 'مقرها في السويد، تخدم العراق',
    
    // Contact Page
    'contact.title': 'اتصل بنا',
    'contact.subtitle': 'نحن هنا للمساعدة. تواصل معنا لأي أسئلة أو استفسارات.',
    'contact.email_label': 'البريد الإلكتروني',
    'contact.phone_label': 'الهاتف',
    'contact.phone_info': 'قريباً',
    'contact.location_label': 'الموقع',
    'contact.location_info': 'السويد',
    'contact.form_title': 'أرسل لنا رسالة',
    'contact.form_description': 'املأ النموذج أدناه وسنعود إليك في أقرب وقت ممكن.',
    'contact.name_placeholder': 'اسمك',
    'contact.email_placeholder': 'بريدك@الإلكتروني.com',
    'contact.subject': 'الموضوع',
    'contact.subject_placeholder': 'ما هو هذا بخصوص؟',
    'contact.message_placeholder': 'رسالتك...',
    'contact.success': 'تم إرسال الرسالة! سنعود إليك قريباً.',
    
    // Press Page
    'press.title': 'الصحافة والإعلام',
    'press.subtitle': 'آخر الأخبار والبيانات الصحفية من عقارنت',
    'press.contact_title': 'جهة اتصال الصحافة',
    'press.contact_description': 'للاستفسارات الصحفية، يرجى التواصل معنا عبر البريد الإلكتروني.',
    'press.add_release': 'إضافة بيان صحفي',
    'press.edit_release': 'تعديل البيان الصحفي',
    'press.new_release': 'بيان صحفي جديد',
    'press.release_title': 'العنوان',
    'press.content': 'المحتوى',
    'press.publish': 'نشر',
    'press.no_releases': 'لا توجد بيانات صحفية بعد.',
    'press.delete_confirm_title': 'حذف البيان الصحفي؟',
    'press.delete_confirm_description': 'لا يمكن التراجع عن هذا الإجراء.',
    
    // Investors Page
    'investors.title': 'للمستثمرين',
    'investors.subtitle': 'انضم إلينا في تحويل سوق العقارات العراقي',
    'investors.growth_title': 'إمكانات النمو',
    'investors.growth_text': 'يشهد سوق العقارات العراقي نمواً سريعاً مع فرص استثمارية كبيرة.',
    'investors.market_title': 'رائد السوق',
    'investors.market_text': 'عقارنت في موقع ريادي كمنصة رقمية في قطاع العقارات العراقي.',
    'investors.vision_title': 'التوسع الإقليمي',
    'investors.vision_text': 'رؤيتنا تمتد إلى ما وراء العراق لنصبح المنصة الرائدة في منطقة الشرق الأوسط.',
    'investors.opportunity_title': 'فرصة استثمارية',
    'investors.opportunity_text': 'تقدم عقارنت فرصة فريدة للاستثمار في التحول الرقمي لواحد من أكبر أسواق العقارات الناشئة في العالم.',
    'investors.opportunity_text_2': 'مع قاعدة مستخدمين متنامية وخدمات متوسعة، نحن في وضع جيد للنمو المستدام والريادة في السوق.',
    'investors.contact_title': 'تواصل معنا',
    'investors.contact_description': 'مهتم بفرص الاستثمار؟ تواصل معنا لمعرفة المزيد.',
    'investors.company': 'الشركة / المؤسسة',
    'investors.company_placeholder': 'اسم شركتك',
    'investors.message_placeholder': 'أخبرنا عن اهتماماتك الاستثمارية...',
    'investors.success': 'شكراً لاهتمامك! سنتواصل معك قريباً.',
    
    // Advertising Page
    'advertising.title': 'الإعلان والتسويق',
    'advertising.subtitle': 'الوصول إلى آلاف المشترين والمستأجرين المحتملين في العراق',
    'advertising.targeted_title': 'وصول مستهدف',
    'advertising.targeted_text': 'الوصول إلى المستخدمين الذين يبحثون بنشاط عن عقارات في مناطق ونطاقات أسعار محددة.',
    'advertising.audience_title': 'جمهور ذو جودة',
    'advertising.audience_text': 'التواصل مع مشترين ومستأجرين جادين مستعدين لاتخاذ القرارات.',
    'advertising.analytics_title': 'تحليلات مفصلة',
    'advertising.analytics_text': 'تتبع أداء حملتك مع تحليلات ورؤى شاملة.',
    'advertising.formats_title': 'أشكال متعددة',
    'advertising.formats_text': 'إعلانات بانر، قوائم مميزة، محتوى مدعوم، والمزيد من خيارات الإعلان.',
    'advertising.contact_title': 'ابدأ الإعلان',
    'advertising.contact_description': 'تواصل معنا لمناقشة فرص الإعلان.',
    'advertising.company': 'الشركة / المؤسسة',
    'advertising.company_placeholder': 'اسم شركتك',
    'advertising.budget': 'نطاق الميزانية',
    'advertising.budget_placeholder': 'مثال: 500 - 1000 دولار/شهر',
    'advertising.message_placeholder': 'أخبرنا عن أهدافك الإعلانية...',
    'advertising.success': 'شكراً! سنتواصل معك بخصوص فرص الإعلان.',
    
    // Jobs Page
    'jobs.title': 'الوظائف في عقارنت',
    'jobs.subtitle': 'انضم إلى فريقنا وساعد في تشكيل مستقبل العقارات في العراق',
    'jobs.add_job': 'إضافة وظيفة',
    'jobs.edit_job': 'تعديل الوظيفة',
    'jobs.new_job': 'وظيفة جديدة',
    'jobs.job_title': 'المسمى الوظيفي',
    'jobs.department': 'القسم',
    'jobs.location': 'الموقع',
    'jobs.employment_type': 'نوع التوظيف',
    'jobs.full_time': 'دوام كامل',
    'jobs.part_time': 'دوام جزئي',
    'jobs.contract': 'عقد',
    'jobs.internship': 'تدريب',
    'jobs.description': 'الوصف',
    'jobs.requirements': 'المتطلبات',
    'jobs.benefits': 'المزايا',
    'jobs.publish': 'نشر',
    'jobs.no_jobs': 'لا توجد وظائف شاغرة حالياً.',
    'jobs.check_back': 'تحقق لاحقاً للفرص الجديدة.',
    'jobs.apply_now': 'تقدم الآن',
    'jobs.apply_for': 'تقدم لـ',
    'jobs.apply_description': 'أرسل طلبك عبر البريد الإلكتروني.',
    'jobs.application_sent': 'تم إرسال الطلب! حظاً موفقاً!',
    'jobs.delete_confirm_title': 'حذف الوظيفة؟',
    'jobs.delete_confirm_description': 'لا يمكن التراجع عن هذا الإجراء.',
    
    // Property Statistics Page
    'statistics.title': 'إحصائيات العقارات',
    'statistics.subtitle': 'رؤى السوق والاتجاهات بناءً على العقارات المباعة',
    'statistics.6_months': 'آخر 6 أشهر',
    'statistics.12_months': 'آخر 12 شهر',
    'statistics.24_months': 'آخر 24 شهر',
    'statistics.36_months': 'آخر 36 شهر',
    'statistics.all_cities': 'جميع المدن',
    'statistics.total_sold': 'إجمالي المبيعات',
    'statistics.avg_price': 'متوسط السعر',
    'statistics.avg_price_sqm': 'متوسط السعر/م²',
    'statistics.price_change': 'تغير السعر',
    'statistics.price_trends': 'اتجاهات الأسعار',
    'statistics.price_trends_desc': 'متوسط أسعار العقارات على مر الزمن',
    'statistics.sales_volume': 'حجم المبيعات',
    'statistics.sales_volume_desc': 'عدد العقارات المباعة شهرياً',
    'statistics.properties_sold': 'عقارات مباعة',
    'statistics.price_by_city': 'السعر لكل م² حسب المدينة',
    'statistics.price_by_city_desc': 'مقارنة متوسط السعر لكل متر مربع عبر المدن',
    'statistics.price_per_sqm': 'السعر لكل م²',
    'statistics.type_distribution': 'توزيع أنواع العقارات',
    'statistics.type_distribution_desc': 'توزيع العقارات المباعة حسب النوع',
    'statistics.avg_days_market': 'متوسط الأيام في السوق',
    'statistics.days': 'يوم',
    'statistics.price_ranges': 'توزيع نطاقات الأسعار',
    'statistics.price_ranges_desc': 'عدد العقارات المباعة في كل نطاق سعري',
    'statistics.properties': 'عقارات',
    'statistics.year_over_year': 'المقارنة السنوية',
    'statistics.year_over_year_desc': 'مقارنة متوسط الأسعار وحجم المبيعات عبر السنوات',
    'statistics.all_types': 'جميع أنواع العقارات',
    'statistics.all_rooms': 'جميع الغرف',
    'statistics.rooms': 'غرف',
    'statistics.export': 'تصدير',
    'statistics.export_csv': 'تحميل CSV',
    'statistics.export_json': 'تحميل JSON',
    'statistics.share': 'مشاركة',
    'statistics.link_copied': 'تم نسخ الرابط',
    'statistics.link_copied_desc': 'تم نسخ رابط تقرير الإحصائيات إلى الحافظة',
    'statistics.custom_date': 'تاريخ مخصص',
    'statistics.use_preset': 'استخدام المحدد مسبقاً',
    'statistics.start_date': 'تاريخ البدء',
    'statistics.end_date': 'تاريخ الانتهاء',
    'statistics.city_comparison': 'مقارنة المدن',
    'statistics.city_comparison_desc': 'قارن إحصائيات العقارات بين مدينتين جنبًا إلى جنب',
    'statistics.select_city_1': 'اختر المدينة الأولى',
    'statistics.select_city_2': 'اختر المدينة الثانية',
    'statistics.vs': 'مقابل',
    'statistics.select_cities_to_compare': 'اختر مدينتين أعلاه لمقارنة إحصائياتهما',
    'statistics.avg_size': 'متوسط المساحة',
    
    // Cookie Settings
    'cookie_settings.title': 'إعدادات ملفات تعريف الارتباط',
    'cookie_settings.description': 'نستخدم ملفات تعريف الارتباط لتحسين تجربة التصفح الخاصة بك. يمكنك تخصيص تفضيلاتك أدناه.',
    'cookie_settings.necessary': 'ملفات تعريف الارتباط الضرورية',
    'cookie_settings.necessary_desc': 'هذه الملفات ضرورية لعمل الموقع بشكل صحيح. لا يمكن تعطيلها.',
    'cookie_settings.functional': 'ملفات تعريف الارتباط الوظيفية',
    'cookie_settings.functional_desc': 'تمكّن هذه الملفات الميزات المخصصة مثل تفضيلات اللغة والإعدادات المحفوظة.',
    'cookie_settings.analytics': 'ملفات تعريف الارتباط التحليلية',
    'cookie_settings.analytics_desc': 'تساعدنا هذه الملفات في فهم كيفية تفاعل الزوار مع موقعنا لتحسين تجربة المستخدم.',
    'cookie_settings.marketing': 'ملفات تعريف الارتباط التسويقية',
    'cookie_settings.marketing_desc': 'تُستخدم هذه الملفات لعرض إعلانات ذات صلة وتتبع فعالية حملاتنا.',
    'cookie_settings.accept_all': 'قبول الكل',
    'cookie_settings.reject_all': 'رفض الكل',
    'cookie_settings.save_preferences': 'حفظ التفضيلات',
    'cookie_settings.saved': 'تم حفظ تفضيلات ملفات تعريف الارتباط',
    
    // Cookie Banner
    'cookie_banner.title': 'نحن نستخدم ملفات تعريف الارتباط',
    'cookie_banner.description': 'نستخدم ملفات تعريف الارتباط لتحسين تجربة التصفح وعرض محتوى مخصص وتحليل حركة المرور. بالنقر على "قبول الكل"، فإنك توافق على استخدامنا لملفات تعريف الارتباط.',
    'cookie_banner.accept_all': 'قبول الكل',
    'cookie_banner.reject_all': 'رفض الكل',
    'cookie_banner.customize': 'تخصيص',
    
    // Terms of Service
    'terms.title': 'شروط الخدمة',
    'terms.last_updated': 'آخر تحديث:',
    'terms.section1.title': '1. قبول الشروط',
    'terms.section1.p1': 'باستخدام عقارنت، فإنك توافق على الالتزام بشروط الخدمة هذه. إذا كنت لا توافق على هذه الشروط، يرجى عدم استخدام خدماتنا.',
    'terms.section1.p2': 'نحتفظ بالحق في تعديل هذه الشروط في أي وقت. الاستمرار في استخدام الخدمة بعد التغييرات يعني قبول الشروط المعدلة.',
    'terms.section2.title': '2. وصف الخدمة',
    'terms.section2.p1': 'توفر عقارنت منصة رقمية لإعلانات العقارات والبحث عن العقارات والتواصل بين المشترين والبائعين والوكلاء في العراق.',
    'terms.section2.p2': 'تشمل خدماتنا قوائم العقارات ووظائف البحث والبحث المحفوظ والمفضلات وأدوات التواصل بين المستخدمين والوكلاء.',
    'terms.section3.title': '3. مسؤوليات المستخدم',
    'terms.section3.p1': 'كمستخدم لعقارنت، توافق على:',
    'terms.section3.li1': 'تقديم معلومات دقيقة وصادقة في إعلاناتك وملفك الشخصي',
    'terms.section3.li2': 'عدم استخدام المنصة لأنشطة احتيالية أو غير قانونية',
    'terms.section3.li3': 'احترام حقوق الملكية الفكرية للآخرين',
    'terms.section3.li4': 'عدم التدخل في الأداء السليم للخدمة',
    'terms.section4.title': '4. إعلانات العقارات',
    'terms.section4.p1': 'المستخدمون الذين يدرجون العقارات مسؤولون عن دقة إعلاناتهم. لا تتحقق عقارنت من دقة معلومات العقارات.',
    'terms.section4.p2': 'نحتفظ بالحق في إزالة أي إعلان ينتهك شروطنا أو يتم الإبلاغ عنه باعتباره احتياليًا.',
    'terms.section5.title': '5. الملكية الفكرية',
    'terms.section5.p1': 'جميع المحتويات على عقارنت، بما في ذلك النصوص والرسومات والشعارات والبرامج، هي ملك لعقارنت أو موردي محتواها.',
    'terms.section5.p2': 'لا يجوز لك إعادة إنتاج أو توزيع أو إنشاء أعمال مشتقة من محتوانا دون إذن كتابي صريح.',
    'terms.section6.title': '6. تحديد المسؤولية',
    'terms.section6.p1': 'يتم تقديم عقارنت "كما هي" دون ضمانات من أي نوع. نحن غير مسؤولين عن أي أضرار ناشئة عن استخدامك لخدماتنا.',
    'terms.section7.title': '7. القانون الحاكم',
    'terms.section7.p1': 'تخضع هذه الشروط لقوانين السويد. يجب حل أي نزاعات في محاكم السويد.',
    'terms.section7.p2': 'إذا تبين أن أي حكم من هذه الشروط غير قابل للتنفيذ، تظل الأحكام المتبقية سارية المفعول.',
    'terms.section8.title': '8. معلومات الاتصال',
    'terms.section8.p1': 'للاستفسارات حول شروط الخدمة هذه، يرجى الاتصال بنا:',
    'terms.section8.email': 'البريد الإلكتروني',
    
    // Privacy Policy
    'privacy.title': 'سياسة الخصوصية',
    'privacy.last_updated': 'آخر تحديث:',
    'privacy.section1.title': '1. مقدمة',
    'privacy.section1.p1': 'في عقارنت، نحن ملتزمون بحماية خصوصيتك وبياناتك الشخصية. توضح هذه السياسة كيفية جمع معلوماتك واستخدامها وحمايتها.',
    'privacy.section1.p2': 'باستخدام خدماتنا، فإنك توافق على ممارسات البيانات الموضحة في هذه السياسة.',
    'privacy.section2.title': '2. المعلومات التي نجمعها',
    'privacy.section2.p1': 'نجمع الأنواع التالية من المعلومات:',
    'privacy.section2.li1': 'معلومات الحساب: الاسم، عنوان البريد الإلكتروني، رقم الهاتف',
    'privacy.section2.li2': 'بيانات العقارات: العناوين، الأوصاف، الصور، الأسعار',
    'privacy.section2.li3': 'بيانات الاستخدام: سجل البحث، العقارات المحفوظة، سلوك التصفح',
    'privacy.section2.li4': 'معلومات الجهاز: عنوان IP، نوع المتصفح، نظام التشغيل',
    'privacy.section3.title': '3. كيف نستخدم معلوماتك',
    'privacy.section3.p1': 'نستخدم معلوماتك من أجل:',
    'privacy.section3.li1': 'تقديم وتحسين خدماتنا',
    'privacy.section3.li2': 'تخصيص تجربتك وتوصياتك',
    'privacy.section3.li3': 'إرسال إشعارات حول عمليات البحث المحفوظة والقوائم الجديدة',
    'privacy.section3.li4': 'التواصل معك بشأن حسابك واستفساراتك',
    'privacy.section4.title': '4. مشاركة البيانات',
    'privacy.section4.p1': 'نحن لا نبيع بياناتك الشخصية. قد نشارك المعلومات مع وكلاء العقارات عند تقديم استفسارات، ومع مقدمي الخدمات الذين يساعدون في عملياتنا.',
    'privacy.section4.p2': 'قد نكشف عن المعلومات عندما يتطلب القانون ذلك أو لحماية حقوقنا وسلامتنا.',
    'privacy.section5.title': '5. حقوقك',
    'privacy.section5.p1': 'لديك الحقوق التالية فيما يتعلق ببياناتك:',
    'privacy.section5.li1': 'الوصول: طلب نسخة من بياناتك الشخصية',
    'privacy.section5.li2': 'التصحيح: تحديث أو تصحيح المعلومات غير الدقيقة',
    'privacy.section5.li3': 'الحذف: طلب حذف حسابك وبياناتك',
    'privacy.section5.li4': 'النقل: الحصول على بياناتك بتنسيق قابل للنقل',
    'privacy.section5.li5': 'الاعتراض: الانسحاب من أنشطة معالجة بيانات معينة',
    'privacy.section6.title': '6. أمان البيانات',
    'privacy.section6.p1': 'نطبق التدابير التقنية والتنظيمية المناسبة لحماية بياناتك من الوصول غير المصرح به أو التعديل أو التدمير.',
    'privacy.section6.p2': 'ومع ذلك، لا توجد طريقة نقل عبر الإنترنت آمنة بنسبة 100%، ولا يمكننا ضمان الأمان المطلق.',
    'privacy.section7.title': '7. التغييرات على هذه السياسة',
    'privacy.section7.p1': 'قد نقوم بتحديث سياسة الخصوصية هذه من وقت لآخر. سنخطرك بالتغييرات الهامة من خلال نشر إشعار على موقعنا أو إرسال بريد إلكتروني إليك.',
    'privacy.section8.title': '8. اتصل بنا',
    'privacy.section8.p1': 'إذا كانت لديك أسئلة حول سياسة الخصوصية هذه أو بياناتك الشخصية، يرجى الاتصال بنا:',
    'privacy.section8.email': 'البريد الإلكتروني',
    
    // FAQ Page
    'faq.title': 'الأسئلة الشائعة',
    'faq.subtitle': 'اعثر على إجابات للأسئلة الشائعة حول استخدام عقارنت',
    'faq.account.title': 'الحساب والتسجيل',
    'faq.account.q1': 'كيف أنشئ حساباً؟',
    'faq.account.q1_answer': 'انقر على "تسجيل الدخول" في الرأس، ثم اختر "إنشاء حساب". يمكنك التسجيل بالبريد الإلكتروني أو Google أو Apple.',
    'faq.account.q2': 'كيف أعيد تعيين كلمة المرور؟',
    'faq.account.q2_answer': 'في صفحة تسجيل الدخول، انقر على "نسيت كلمة المرور" وأدخل بريدك الإلكتروني.',
    'faq.account.q3': 'هل يمكنني تغيير بريدي الإلكتروني؟',
    'faq.account.q3_answer': 'حالياً لا يمكنك تغيير بريدك الإلكتروني مباشرة. يرجى الاتصال بفريق الدعم.',
    'faq.listings.title': 'قوائم العقارات',
    'faq.listings.q1': 'كيف أعرض عقاراً للبيع؟',
    'faq.listings.q1_answer': 'يجب أن تكون مسجلاً كوكيل لعرض العقارات. بعد تسجيل الدخول، انقر على "إضافة عقار".',
    'faq.listings.q2': 'كم مدة بقاء الإعلان نشطاً؟',
    'faq.listings.q2_answer': 'تبقى الإعلانات نشطة حتى يتم وضع علامة مباع أو إزالتها من قبل المالك.',
    'faq.listings.q3': 'هل يمكنني تعديل إعلاني بعد النشر؟',
    'faq.listings.q3_answer': 'نعم، يمكنك تعديل إعلانك في أي وقت من صفحة الملف الشخصي.',
    'faq.search.title': 'البحث عن العقارات',
    'faq.search.q1': 'كيف أحفظ بحثاً؟',
    'faq.search.q1_answer': 'بعد إجراء البحث، انقر على "حفظ البحث". يمكنك تفعيل إشعارات البريد الإلكتروني.',
    'faq.search.q2': 'كيف أضيف عقاراً إلى المفضلة؟',
    'faq.search.q2_answer': 'انقر على أيقونة القلب على أي بطاقة عقار. يجب أن تكون مسجل الدخول.',
    'faq.search.q3': 'هل يمكنني البحث على الخريطة؟',
    'faq.search.q3_answer': 'نعم، استخدم عرض الخريطة في صفحة العقارات للتصفح حسب الموقع.',
    'faq.contact.title': 'الاتصال بالوكلاء',
    'faq.contact.q1': 'كيف أتصل بوكيل عقاري؟',
    'faq.contact.q1_answer': 'انقر على "اتصل بالوكيل" على أي إعلان لإرسال رسالة مباشرة.',
    'faq.contact.q2': 'هل يمكنني جدولة معاينة؟',
    'faq.contact.q2_answer': 'نعم، انقر على "جدولة معاينة" على صفحة العقار لطلب موعد.',
    'faq.security.title': 'الأمان والخصوصية',
    'faq.security.q1': 'هل معلوماتي الشخصية آمنة؟',
    'faq.security.q1_answer': 'نعم، نستخدم تشفيراً قياسياً وإجراءات أمنية لحماية بياناتك.',
    'faq.security.q2': 'كيف يمكنني حذف حسابي؟',
    'faq.security.q2_answer': 'اذهب إلى إعدادات الملف الشخصي واختر "حذف الحساب". هذا الإجراء دائم.',
    'faq.still_questions': 'هل لا تزال لديك أسئلة؟',
    'faq.contact_us': 'اتصل بفريق الدعم لدينا وسنكون سعداء بمساعدتك.',
    'newsletter.title': 'اشتراك النشرة الإخبارية',
    'newsletter.description': 'اشترك للحصول على آخر أخبار العقارات.',
    'newsletter.placeholder': 'أدخل بريدك الإلكتروني',
    'newsletter.subscribe': 'اشترك',
    'newsletter.subscribing': 'جاري الاشتراك...',
    'newsletter.success': 'تم الاشتراك بنجاح!',
    'newsletter.subscribed': 'شكراً لاشتراكك!',
    'newsletter.invalid_email': 'يرجى إدخال بريد إلكتروني صالح',
    'newsletter.error': 'فشل الاشتراك',
    'newsletter.unsubscribe_note': 'يمكنك إلغاء الاشتراك في أي وقت.',
    'newsletter.unsubscribe_title': 'إلغاء الاشتراك من النشرة الإخبارية',
    'newsletter.unsubscribe_description': 'أدخل بريدك الإلكتروني لإلغاء الاشتراك.',
    'newsletter.unsubscribe_button': 'إلغاء الاشتراك',
    'newsletter.unsubscribe_success': 'تم إلغاء الاشتراك بنجاح',
    'newsletter.unsubscribe_error': 'فشل إلغاء الاشتراك',
    'newsletter.unsubscribe_message': 'لن تتلقى نشرتنا الإخبارية بعد الآن.',
    'newsletter.invalid_link': 'رابط إلغاء الاشتراك غير صالح',
    'newsletter.invalid_link_message': 'رابط إلغاء الاشتراك هذا غير صالح أو منتهي الصلاحية. يرجى استخدام الرابط من بريد النشرة الإخبارية.',
    'newsletter.unsubscribe_reason_label': 'سبب إلغاء الاشتراك (اختياري)',
    'newsletter.select_reason': 'اختر سبباً...',
    'newsletter.reason_too_many_emails': 'رسائل بريد إلكتروني كثيرة جداً',
    'newsletter.reason_not_relevant': 'المحتوى غير ذي صلة بي',
    'newsletter.reason_never_subscribed': 'لم أشترك أبداً',
    'newsletter.reason_found_another': 'وجدت خدمة أخرى',
    'newsletter.reason_other': 'أخرى',
    'common.try_again': 'حاول مرة أخرى',
    'newsletter.admin.title': 'المشتركون في النشرة الإخبارية',
    'newsletter.admin.description': 'إدارة المشتركين في النشرة الإخبارية',
    'newsletter.admin.total': 'إجمالي المشتركين',
    'newsletter.admin.active': 'نشط',
    'newsletter.admin.unsubscribed': 'ملغي الاشتراك',
    'newsletter.admin.export_csv': 'تصدير CSV',
    'newsletter.admin.refresh': 'تحديث',
    'newsletter.admin.subscribers_list': 'قائمة المشتركين',
    'newsletter.admin.subscribers_description': 'جميع المشتركين وحالتهم',
    'newsletter.admin.no_subscribers': 'لا يوجد مشتركون بعد',
    'newsletter.admin.subscribed_at': 'تاريخ الاشتراك',
    'newsletter.admin.status': 'الحالة',
    'newsletter.admin.unsubscribed_at': 'تاريخ إلغاء الاشتراك',
    'newsletter.admin.actions': 'الإجراءات',
    'newsletter.admin.deleted': 'تم حذف المشترك',
    'newsletter.admin.delete_error': 'فشل حذف المشترك',
    'newsletter.admin.no_data': 'لا توجد بيانات للتصدير',
    'newsletter.admin.exported': 'تم التصدير بنجاح',
    'newsletter.admin.delete_confirm_title': 'حذف المشترك؟',
    'newsletter.admin.delete_confirm_description': 'سيتم حذف هذا المشترك نهائياً.',
    'footer.faq': 'الأسئلة الشائعة',
    
    // Broker Dashboard
    'broker.dashboard_title': 'لوحة تحكم الوسيط',
    'broker.dashboard_subtitle': 'إدارة عقاراتك وتفاعلات العملاء',
    'broker.my_properties': 'عقاراتي',
    'broker.my_properties_desc': 'عرض وإدارة إعلانات عقاراتك',
    'broker.viewings': 'المعاينات',
    'broker.viewings_desc': 'إدارة مواعيد معاينة العقارات',
    'broker.contacts': 'رسائل العملاء',
    'broker.contacts_desc': 'عرض والرد على استفسارات العملاء',
    'broker.analytics': 'تحليلات العقارات',
    'broker.analytics_desc': 'تتبع المشاهدات والتفاعل على إعلاناتك',
    'broker.subscription': 'الاشتراك',
    'broker.subscription_desc': 'إدارة خطتك وحدود الإعلانات',
    'broker.access_denied': 'هذه الصفحة متاحة فقط للوسطاء المسجلين.',
    'broker.profile_title': 'ملف الوسيط الشخصي',
    'broker.profile_description': 'إدارة ملفك الشخصي العام ومعلومات الاتصال',
    'broker.back_to_dashboard': 'العودة إلى لوحة التحكم',
    'broker.display_name': 'الاسم المعروض',
    'broker.display_name_placeholder': 'اسمك كما يظهر للعملاء',
    'broker.contact_email': 'البريد الإلكتروني للتواصل',
    'broker.email_placeholder': 'your@email.com',
    'broker.email_notification_note': 'سيتم استخدام هذا البريد للإشعارات حول المعاينات ورسائل العملاء.',
    'broker.phone_number': 'رقم الهاتف',
    'broker.phone_placeholder': '+964 xxx xxx xxxx',
    'broker.change_photo': 'تغيير الصورة',
    'broker.save_profile': 'حفظ الملف الشخصي',
    'broker.profile_saved': 'تم حفظ الملف الشخصي بنجاح',
    'broker.profile_save_error': 'فشل حفظ الملف الشخصي',
    'broker.profile_load_error': 'فشل تحميل الملف الشخصي',
    'broker.avatar_uploaded': 'تم رفع الصورة بنجاح',
    'broker.avatar_upload_error': 'فشل رفع الصورة',
    'broker.invalid_image_type': 'يرجى اختيار ملف صورة',
    'broker.image_too_large': 'يجب أن تكون الصورة أقل من 2 ميغابايت',
    'broker.notification_preferences': 'إعدادات الإشعارات',
    'broker.notification_preferences_description': 'اختر إشعارات البريد الإلكتروني التي تريد تلقيها',
    'broker.notif_new_viewing_requests': 'طلبات المعاينة الجديدة',
    'broker.notif_new_viewing_requests_desc': 'الحصول على إشعار عند طلب العملاء للمعاينات',
    'broker.notif_new_client_messages': 'رسائل العملاء الجديدة',
    'broker.notif_new_client_messages_desc': 'الحصول على إشعار عند إرسال العملاء رسائل',
    'broker.notif_property_alerts': 'تنبيهات العقارات',
    'broker.notif_property_alerts_desc': 'تلقي تحديثات السوق وتنبيهات العقارات المطابقة',
    'broker.notif_weekly_summary': 'ملخص أسبوعي',
    'broker.notif_weekly_summary_desc': 'تلقي ملخص أسبوعي لنشاطك',
    'broker.listing_usage': 'استخدام الإعلانات',
    'broker.monthly_listing_limits': 'حدود الإعلانات الشهرية',
    'broker.listings_used': 'الإعلانات المستخدمة',
    'broker.listings_remaining': 'إعلانات متبقية',
    'broker.period_resets': 'تنتهي الفترة',
    'broker.export_csv': 'تصدير CSV',
    'broker.total_views': 'إجمالي المشاهدات',
    'broker.scheduled_viewings': 'المعاينات المجدولة',
    'broker.client_messages': 'رسائل العملاء',
    'broker.filters': 'فلاتر',
    'broker.all_time': 'كل الوقت',
    'broker.active_listings': 'الإعلانات النشطة',
    'broker.sold_listings': 'الإعلانات المباعة',
    'broker.active_listings_desc': 'عقاراتك المنشورة حالياً',
    'broker.no_active_listings': 'لا توجد إعلانات نشطة بعد. أضف أول عقار للبدء.',
    'broker.all_types': 'جميع الأنواع',
    'broker.all_cities': 'جميع المدن',
    'broker.property_type': 'نوع العقار',
    'broker.city': 'المدينة',
    'broker.date_range': 'نطاق التاريخ',
    'broker.last_week': 'الأسبوع الماضي',
    'broker.last_month': 'الشهر الماضي',
    'broker.last_quarter': 'آخر 3 أشهر',
    'broker.sold_listings_desc': 'العقارات التي بعتها',
    'broker.no_sold_listings': 'لا توجد عقارات مباعة بعد',
    'broker.clear_filters': 'مسح الفلاتر',
    'broker.selected': 'محدد',
    'broker.compare': 'مقارنة',
    'broker.mark_as_sold': 'تحديد كمباع',
    'broker.delete_selected': 'حذف المحدد',
    'broker.select_all': 'تحديد الكل',
    'broker.rejected_listings': 'الإعلانات المرفوضة',
    'broker.rejected_listings_desc': 'تم رفض هذه الإعلانات من قبل المشرفين. يمكنك تقديم استئناف.',
    'broker.no_rejected_listings': 'لا توجد إعلانات مرفوضة',
    'broker.confirm_delete': 'تأكيد الحذف',
    'broker_public.not_found': 'لم يتم العثور على الوسيط',
    'broker_public.not_found_description': 'ملف الوسيط الذي تبحث عنه غير موجود.',
    'broker_public.back_to_search': 'العودة للبحث عن وسيط',
    'broker_public.contact_broker': 'اتصل بالوسيط',
    'broker_public.agency': 'الوكالة',
    'broker_public.visit_website': 'زيارة الموقع',
    'broker_public.active_listings': 'الإعلانات النشطة',
    'broker_public.sold_properties': 'العقارات المباعة',
    'broker_public.total_sales': 'إجمالي المبيعات',
    'broker_public.avg_days_market': 'متوسط الأيام في السوق',
    'broker_public.no_active_listings': 'لا توجد إعلانات نشطة حالياً',
    'broker_public.no_sold_properties': 'لا توجد عقارات مباعة بعد',
    'broker_public.view_all_properties': 'عرض جميع العقارات',
    'broker_public.send_message': 'إرسال رسالة',
    'broker_public.view_agency': 'عرض الوكالة',
    'broker_public.works_at': 'يعمل في',
    'broker_public.listings': 'الإعلانات',
    'broker_public.sold': 'مباع',
    'broker_public.reviews': 'التقييمات',
    'broker_public.rating': 'التقييم',
    'broker_public.featured_broker': 'وسيط مميز',
    'broker_public.top_performer': 'أفضل أداء',
    'agents.verified_agent': 'وسيط موثق',
    
    // Agency Detail
    'agency_detail.brokers': 'الوسطاء',
    'agency_detail.listings': 'الإعلانات',
    'agency_detail.sold': 'مباع',
    'agency_detail.reviews': 'التقييمات',
    'agency_detail.rating': 'التقييم',
    'agency_detail.active_listings': 'الإعلانات النشطة',
    'agency_detail.no_active_listings': 'لا توجد إعلانات نشطة حالياً',
    'agency_detail.no_sold_properties': 'لا توجد عقارات مباعة بعد',
    'agency_detail.view_profile': 'عرض الملف الشخصي',
    'agency_detail.message': 'رسالة',
    'agency_detail.featured_agency': 'وكالة مميزة',
    'agency_detail.premium_agency': 'وكالة مميزة',
    'agency_detail.verified_agency': 'وكالة موثقة',
    
    // Reviews
    'reviews.write_review': 'اكتب تقييماً',
    'reviews.write_agency_review': 'قيّم هذه الوكالة',
    'reviews.write_broker_review': 'قيّم هذا الوسيط',
    'reviews.select_rating': 'يرجى اختيار تقييم',
    'reviews.submitted': 'تم إرسال التقييم بنجاح',
    'reviews.submit_error': 'فشل إرسال التقييم',
    'reviews.comment_placeholder': 'شارك تجربتك... (اختياري)',
    'reviews.submitting': 'جارٍ الإرسال...',
    'reviews.submit': 'إرسال التقييم',
    'reviews.all_reviews': 'جميع التقييمات',
    'reviews.no_reviews': 'لا توجد تقييمات بعد. كن أول من يترك تقييماً!',
    'reviews.login_to_review': 'سجل الدخول لترك تقييم',
    'reviews.cannot_review_self': 'لا يمكنك تقييم نفسك',
    'common.login_required': 'يرجى تسجيل الدخول للوصول إلى هذه الصفحة.',
    
    // Agency Dashboard
    'agency.my_agency': 'وكالتي',
    'agency.dashboard_title': 'لوحة الوكالة',
    'agency.dashboard_subtitle': 'إدارة الوسطاء وإعدادات الوكالة',
    'agency.brokers_tab': 'الوسطاء',
    'agency.invites_tab': 'الدعوات',
    'agency.settings_tab': 'الإعدادات',
    'agency.brokers_title': 'وسطاء الوكالة',
    'agency.brokers_description': 'إدارة الوسطاء في وكالتك',
    'agency.create_broker': 'إنشاء وسيط',
    'agency.create_broker_title': 'إنشاء حساب وسيط جديد',
    'agency.create_broker_description': 'إنشاء حساب وسيط جديد مرتبط بوكالتك',
    'agency.broker_name': 'الاسم الكامل',
    'agency.broker_name_placeholder': 'أدخل اسم الوسيط',
    'agency.broker_email': 'البريد الإلكتروني',
    'agency.broker_email_placeholder': 'wasit@example.com',
    'agency.broker_password': 'كلمة المرور',
    'agency.broker_password_placeholder': 'إنشاء كلمة مرور',
    'agency.create_broker_btn': 'إنشاء حساب وسيط',
    'agency.broker_created': 'تم إنشاء حساب الوسيط بنجاح',
    'agency.broker_create_error': 'فشل في إنشاء حساب الوسيط',
    'agency.no_brokers': 'لا يوجد وسطاء بعد',
    'agency.no_brokers_description': 'إنشاء حسابات وسطاء أو إرسال دعوات لإضافة وسطاء',
    'agency.broker_column': 'الوسيط',
    'agency.email_column': 'البريد الإلكتروني',
    'agency.phone_column': 'الهاتف',
    'agency.actions_column': 'الإجراءات',
    'agency.unnamed_broker': 'وسيط بدون اسم',
    'agency.broker_removed': 'تمت إزالة الوسيط من الوكالة',
    'agency.broker_remove_error': 'فشل في إزالة الوسيط',
    'agency.invites_title': 'الدعوات المعلقة',
    'agency.invites_description': 'إدارة الدعوات المرسلة للوسطاء المحتملين',
    'agency.send_invite': 'إرسال دعوة',
    'agency.send_invite_title': 'دعوة وسيط',
    'agency.send_invite_description': 'إرسال دعوة للانضمام إلى وكالتك',
    'agency.invite_email': 'البريد الإلكتروني',
    'agency.invite_email_placeholder': 'wasit@example.com',
    'agency.send_invite_btn': 'إرسال الدعوة',
    'agency.invite_sent': 'تم إرسال الدعوة بنجاح',
    'agency.invite_error': 'فشل في إرسال الدعوة',
    'agency.link_copied': 'تم نسخ رابط الدعوة',
    'agency.no_invites': 'لا توجد دعوات معلقة',
    'agency.status_column': 'الحالة',
    'agency.sent_column': 'أُرسلت',
    'agency.expires_column': 'تنتهي',
    'agency.status_pending': 'معلقة',
    'agency.status_accepted': 'مقبولة',
    'agency.status_expired': 'منتهية',
    'agency.settings_title': 'إعدادات الوكالة',
    'agency.settings_description': 'تحديث معلومات وكالتك',
    'agency.agency_name': 'اسم الوكالة',
    'agency.agency_name_placeholder': 'اسم وكالتك',
    'agency.agency_description': 'الوصف',
    'agency.agency_description_placeholder': 'وصف موجز لوكالتك',
    'agency.agency_phone': 'الهاتف',
    'agency.agency_phone_placeholder': '+964 xxx xxx xxxx',
    'agency.agency_email': 'البريد الإلكتروني',
    'agency.agency_email_placeholder': 'contact@agency.com',
    'agency.agency_website': 'الموقع الإلكتروني',
    'agency.settings_saved': 'تم حفظ إعدادات الوكالة',
    'agency.settings_save_error': 'فشل في حفظ الإعدادات',
    'agency.load_error': 'فشل في تحميل بيانات الوكالة',
    'agency.default_name': 'وكالتي',
    'agency.notifications_tab': 'الإشعارات',
    'agency.notifications_title': 'إشعارات البريد الإلكتروني',
    'agency.notifications_description': 'تحكم في الإشعارات التي تتلقاها حول نشاط الوسطاء',
    'agency.notifications_saved': 'تم حفظ إعدادات الإشعارات',
    'agency.notifications_save_error': 'فشل في حفظ الإعدادات',
    'agency.notif_new_broker_joins': 'انضمام وسيط جديد',
    'agency.notif_new_broker_joins_desc': 'تلقي إشعار عند قبول وسيط لدعوتك',
    'agency.notif_broker_property_added': 'إضافة عقار من وسيط',
    'agency.notif_broker_property_added_desc': 'تلقي إشعار عند إضافة الوسطاء لعقارات جديدة',
    'agency.notif_broker_viewing_requests': 'طلبات المعاينة',
    'agency.notif_broker_viewing_requests_desc': 'تلقي إشعار حول طلبات معاينة عقارات الوسطاء',
    'agency.notif_broker_client_messages': 'رسائل العملاء',
    'agency.notif_broker_client_messages_desc': 'تلقي إشعار عند تواصل العملاء مع وسطائك',
    'agency.notif_weekly_summary': 'ملخص أسبوعي',
    'agency.notif_weekly_summary_desc': 'تلقي ملخص أسبوعي لنشاط الوكالة',
    'agency.analytics_tab': 'التحليلات',
    'agency.no_brokers_analytics': 'أضف وسطاء لعرض تحليلات الأداء',
    'agency.total_properties': 'إجمالي العقارات',
    'agency.total_views': 'إجمالي المشاهدات',
    'agency.total_viewings': 'طلبات المعاينة',
    'agency.total_sold': 'العقارات المباعة',
    'agency.total_sales_value': 'إجمالي قيمة المبيعات',
    'agency.broker_performance_chart': 'أداء الوسطاء',
    'agency.broker_performance_chart_desc': 'مقارنة إحصائيات الوسطاء بنظرة واحدة',
    'agency.individual_broker_stats': 'إحصائيات الوسطاء الفردية',
    'agency.individual_broker_stats_desc': 'مقاييس تفصيلية لكل وسيط',
    'agency.properties': 'العقارات',
    'agency.views': 'المشاهدات',
    'agency.viewings': 'المعاينات',
    'agency.contacts': 'جهات الاتصال',
    'agency.active': 'نشط',
    'agency.sold': 'مباع',
    'agency.sales_value': 'قيمة المبيعات',
    'agency.upgrade_plan': 'ترقية الخطة',
    'agency.view_plans': 'عرض الخطط',
    'agency.billing_tab': 'الفواتير',
    'agency.name_label': 'اسم الوكالة',
    'agency.email_label': 'البريد الإلكتروني',
    'agency.phone_label': 'الهاتف',
    'agency.website_label': 'الموقع الإلكتروني',
    'agency.address_label': 'العنوان',
    'agency.city_label': 'المدينة',
    'agency.description_label': 'الوصف',
    'agency.description_placeholder': 'أخبر العملاء عن وكالتك...',
    'agency.save_settings': 'حفظ الإعدادات',
    'agency.broker_usage': 'استخدام الوسطاء',
    'agency.invite_broker': 'دعوة وسيط',
    'agency.brokers': 'وسطاء',
    'agency.broker_limit_reached': 'تم الوصول إلى حد الوسطاء',
    'agency.upgrade_for_more': 'قم بترقية خطتك لإضافة المزيد من الوسطاء',
    'agency.active_listings': 'القوائم النشطة',
    'agency.broker_slots_remaining': 'الأماكن المتبقية',
    'agency.manage_subscription': 'إدارة الاشتراك',
    'agency.upgrade_your_plan': 'ترقية خطتك',
    'agency.choose_plan': 'اختر الخطة التي تناسب وكالتك',
    'agency.pro_plan': 'برو',
    'agency.enterprise_plan': 'المؤسسات',
    'agency.for_growing_agencies': 'للوكالات المتنامية',
    'agency.for_large_agencies': 'للوكالات الكبيرة',
    'agency.per_month': '/شهر',
    'agency.up_to_brokers': 'حتى {count} وسطاء',
    'agency.unlimited_brokers': 'وسطاء غير محدودين',
    'agency.advanced_analytics': 'تحليلات متقدمة',
    'agency.full_analytics': 'مجموعة تحليلات كاملة',
    'agency.priority_support': 'دعم ذو أولوية',
    'agency.dedicated_support': 'دعم مخصص',
    'agency.upgrade_to_pro': 'الترقية إلى برو',
    'agency.upgrade_to_enterprise': 'الترقية إلى المؤسسات',
    'agency.popular': 'شائع',
    'agency.current_plan': 'الخطة الحالية',
    'agency.plan_features': 'ميزات الخطة',
    'agency.billing_history': 'سجل الفواتير',
    'agency.no_billing_history': 'لا يوجد سجل فواتير بعد',
    'agency.subscription_billing': 'الاشتراك والفواتير',
    'agency.manage_agency_subscription': 'إدارة اشتراك وكالتك',
    'agency.plan': 'الخطة',
    'agency.basic_plan': 'الأساسية',
    'agency.broker_limit': 'حد الوسطاء',
    'agency.current_usage': 'الاستخدام الحالي',
    'agency.status': 'الحالة',
    'agency.active_status': 'نشط',
    'agency.unlimited': 'غير محدود',
    'agency.feature': 'الميزة',
    'agency.price': 'السعر',
    'agency.free': 'مجاني',
    'agency.analytics': 'التحليلات',
    'agency.basic_analytics': 'أساسي',
    'agency.advanced': 'متقدم',
    'agency.full_suite': 'مجموعة كاملة',
    'agency.support': 'الدعم',
    'agency.email_support': 'البريد الإلكتروني',
    'agency.priority': 'أولوية',
    'agency.dedicated': 'مخصص',
    
    // Agency Invite Accept
    'agency_invite.title': 'انضم إلى الوكالة',
    'agency_invite.description': 'لقد تمت دعوتك للانضمام إلى {agencyName}',
    'agency_invite.joining': 'أنت تنضم إلى',
    'agency_invite.logged_in_as': 'مسجل الدخول كـ',
    'agency_invite.accept_btn': 'قبول الدعوة',
    'agency_invite.accepted_success': 'تم الانضمام إلى الوكالة بنجاح!',
    'agency_invite.accept_error': 'فشل في قبول الدعوة',
    'agency_invite.create_account': 'إنشاء حساب',
    'agency_invite.create_account_description': 'أنشئ حساباً لقبول هذه الدعوة',
    'agency_invite.your_name': 'اسمك',
    'agency_invite.name_placeholder': 'أدخل اسمك الكامل',
    'agency_invite.email_placeholder': 'your@email.com',
    'agency_invite.password': 'كلمة المرور',
    'agency_invite.password_placeholder': 'إنشاء كلمة مرور',
    'agency_invite.confirm_password': 'تأكيد كلمة المرور',
    'agency_invite.confirm_password_placeholder': 'تأكيد كلمة المرور',
    'agency_invite.register_accept_btn': 'إنشاء حساب والانضمام',
    'agency_invite.registered_success': 'تم إنشاء الحساب والانضمام إلى الوكالة!',
    'agency_invite.register_error': 'فشل في إنشاء الحساب',
    'agency_invite.passwords_mismatch': 'كلمات المرور غير متطابقة',
    'agency_invite.password_too_short': 'يجب أن تكون كلمة المرور 6 أحرف على الأقل',
    'agency_invite.already_have_account': 'لديك حساب بالفعل؟',
    'agency_invite.login_here': 'تسجيل الدخول هنا',
    'agency_invite.invalid_link': 'رابط دعوة غير صالح',
    'agency_invite.not_found': 'لم يتم العثور على الدعوة',
    'agency_invite.expired': 'انتهت صلاحية هذه الدعوة',
    'agency_invite.already_accepted': 'تم قبول هذه الدعوة بالفعل',
    'agency_invite.fetch_error': 'فشل في تحميل الدعوة',
    'agency_invite.error_title': 'دعوة غير صالحة',
    'agency_invite.back_home': 'العودة للصفحة الرئيسية',
    
    // User Listings
    'userListings.title': 'إعلاناتي',
    'userListings.subtitle': 'إدارة إعلانات العقارات الخاصة بك وإنشاء إعلانات جديدة',
    'userListings.pricingTitle': 'أسعار الإعلانات',
    'userListings.pricingDesc': 'أنشئ إعلان عقارك بأسعارنا البسيطة',
    'userListings.listingPrice': '10$ لكل إعلان',
    'userListings.listingDuration': 'صالح لمدة شهر واحد',
    'userListings.videoPrice': '+3$ للفيديو',
    'userListings.videoDesc': 'أضف جولة فيديو لإعلانك',
    'userListings.includeVideo': 'تضمين جولة فيديو',
    'userListings.createListing': 'إنشاء إعلان',
    'userListings.myListingsTitle': 'عقاراتك',
    'userListings.noListings': 'لا توجد إعلانات بعد',
    'userListings.noListingsDesc': 'أنشئ أول إعلان عقاري للبدء',
    'userListings.createFirst': 'أنشئ إعلانك الأول',
    'userListings.hasVideo': 'فيديو',
    'userListings.renew': 'تجديد',
    'userListings.renewSuccess': 'تم تجديد الإعلان!',
    'userListings.renewSuccessDesc': 'تم تجديد إعلانك لمدة شهر آخر',
    'userListings.paymentSuccess': 'تمت عملية الدفع بنجاح!',
    'userListings.paymentSuccessDesc': 'إعلانك الآن مباشر',
    'userListings.paymentCancelled': 'تم إلغاء الدفع',
    'userListings.paymentCancelledDesc': 'تم إلغاء عملية الدفع الخاصة بك',
    'userListings.checkoutError': 'فشل في إنشاء الدفع',
    'userListings.renewError': 'فشل في تجديد الإعلان',
    'userListings.createDraft': 'إنشاء إعلان',
    'userListings.previewListing': 'معاينة إعلانك',
    'userListings.draft': 'مسودة',
    'userListings.drafts': 'المسودات',
    'userListings.draftSaved': 'تم حفظ المسودة',
    'userListings.draftSavedDesc': 'تم حفظ إعلانك كمسودة',
    'userListings.draftError': 'فشل في حفظ المسودة',
    'userListings.draftDeleted': 'تم حذف المسودة',
    'userListings.deleteError': 'فشل في الحذف',
    'userListings.saveDraft': 'حفظ المسودة',
    'userListings.saveAndPreview': 'حفظ ومعاينة',
    'userListings.publishOptions': 'خيارات النشر',
    'userListings.addVideoTour': 'إضافة جولة فيديو',
    'userListings.videoIncluded': 'جولة الفيديو مضمنة',
    'userListings.totalPrice': 'المجموع',
    'userListings.backToEdit': 'العودة للتعديل',
    'userListings.publishNow': 'نشر الآن',
    'userListings.publish': 'نشر',
    'userListings.publishSuccess': 'تم نشر الإعلان!',
    'userListings.publishSuccessDesc': 'إعلانك الآن قيد المراجعة',
    'userListings.firstListingFree': 'أول إعلان مجاني!',
    'userListings.firstListingFreeDesc': 'إعلانك العقاري الأول مجاني تماماً. انشر الآن وتواصل مع المشترين في جميع أنحاء العراق!',
    'userListings.pending': 'قيد المراجعة',
    'userListings.pendingInfo': 'هذه الإعلانات قيد المراجعة من فريق الإشراف. سيتم إخطارك بمجرد الموافقة عليها.',
    'userListings.pendingReview': 'قيد المراجعة',
    'userListings.awaitingApproval': 'في انتظار موافقة المشرف',
    'userListings.rejected': 'الإعلانات المرفوضة',
    'userListings.rejectedInfo': 'لم تتم الموافقة على هذه الإعلانات. يرجى التعديل وإصلاح المشاكل قبل إعادة التقديم.',
    'userListings.rejectedBadge': 'مرفوض',
    'userListings.live': 'مباشر',
    'userListings.editDraft': 'تعديل',
    'userListings.editAndResubmit': 'تعديل وإعادة التقديم',
    'userListings.pendingCannotEdit': 'لا يمكن التعديل أثناء المراجعة',
    'userListings.resubmitForReview': 'إعادة التقديم للمراجعة',
    'userListings.resubmitInfo': 'تم حفظ تغييراتك. أرسل إعلانك مرة أخرى للمراجعة بدون تكلفة إضافية.',
    'userListings.submitForReview': 'إرسال للمراجعة',
    'userListings.validation.priceRequired': 'السعر مطلوب',
    'userListings.validation.cityRequired': 'المدينة مطلوبة',
    'userListings.validation.roomsRequired': 'عدد الغرف مطلوب',
    'userListings.validation.sizeRequired': 'المساحة مطلوبة',
    'userListings.validation.plotSizeRequired': 'مساحة الأرض مطلوبة',
    'userListings.validation.imagesRequired': 'مطلوب صورة واحدة على الأقل',
    'userListings.choosePlan': 'اختر خطة إعلانك',
    'userListings.choosePlanDesc': 'اختر عدد الصور التي ستظهر على بطاقة إعلانك',
    'userListings.upgradePlan': 'ترقية خطة إعلانك',
    'userListings.upgradePlanDesc': 'قم بالترقية لعرض المزيد من الصور. تدفع الفرق فقط.',
    'userListings.selectImages': 'اختر الصور للعرض',
    'userListings.selectImagesDesc': 'اختر الصور لعرضها على بطاقة إعلانك',
    'userListings.selectPrimaryImage': 'اختر الصورة الرئيسية',
    'userListings.selectPrimaryImageDesc': 'اختر الصورة الرئيسية التي ستظهر أولاً في إعلانك',
    'userListings.addVideo': 'إضافة جولة فيديو',
    'userListings.pleaseSelectImages': 'يرجى اختيار الصور للمتابعة',
    'userListings.pleaseSelectPrimaryImage': 'يرجى اختيار صورة رئيسية للمتابعة',
    'userListings.proceedToPayment': 'المتابعة للدفع',
    'userListings.tierBasic': 'أساسي',
    'userListings.tierStandard': 'قياسي',
    'userListings.tierPremium': 'مميز',
    'userListings.tierBasicDesc': 'صورة واحدة على البطاقة',
    'userListings.tierStandardDesc': 'عرض 3 صور',
    'userListings.tierPremiumDesc': 'جميع الصور + شارة',
    'userListings.tierDuration': 'نشط لمدة 30 يوماً',
    'userListings.tierBest': 'الأفضل',
    'userListings.tierBasicFull': 'الباقة الأساسية (صورة واحدة)',
    'userListings.tierStandardFull': 'الباقة القياسية (3 صور)',
    'userListings.tierPremiumFull': 'الباقة المميزة (جميع الصور)',
    'userListings.changeTier': 'تغيير الباقة',
    'userListings.selectImageTier': 'اختر باقة الصور',
    'userListings.selectTierFirst': 'اختر باقة قبل النشر',
    'userListings.upgrade': 'ترقية',
    'common.total': 'المجموع',
    
    // Settings Page
    'settings.title': 'الإعدادات',
    'settings.subtitle': 'إدارة إعدادات حسابك وتفضيلاتك',
    'settings.profile': 'الملف الشخصي',
    'settings.notifications': 'الإشعارات',
    'settings.account': 'الحساب',
    'settings.profile_info': 'معلومات الملف الشخصي',
    'settings.profile_desc': 'تحديث معلوماتك الشخصية',
    'settings.change_photo': 'تغيير الصورة',
    'settings.display_name': 'اسم العرض',
    'settings.display_name_placeholder': 'اسمك',
    'settings.email': 'البريد الإلكتروني',
    'settings.email_placeholder': 'your@email.com',
    'settings.phone': 'الهاتف',
    'settings.phone_placeholder': '+964 770 123 4567',
    'settings.save': 'حفظ التغييرات',
    'settings.saved': 'تم حفظ الإعدادات بنجاح',
    'settings.load_error': 'فشل تحميل الإعدادات',
    'settings.save_error': 'فشل حفظ الإعدادات',
    'settings.invalid_image': 'يرجى اختيار ملف صورة',
    'settings.image_too_large': 'يجب أن تكون الصورة أقل من 2 ميجابايت',
    'settings.avatar_uploaded': 'تم رفع الصورة',
    'settings.avatar_error': 'فشل رفع الصورة',
    'settings.notification_preferences': 'تفضيلات الإشعارات',
    'settings.notification_desc': 'اختر الإشعارات التي تريد تلقيها',
    'settings.notif_property_alerts': 'تنبيهات العقارات',
    'settings.notif_property_alerts_desc': 'احصل على إشعارات حول العقارات الجديدة',
    'settings.notif_saved_search': 'تنبيهات البحث المحفوظ',
    'settings.notif_saved_search_desc': 'احصل على إشعارات عندما تتطابق عقارات جديدة مع عمليات البحث المحفوظة',
    'settings.notif_newsletter': 'النشرة الإخبارية',
    'settings.notif_newsletter_desc': 'تلقي نشرتنا الإخبارية مع تحديثات السوق',
    'settings.notif_viewing': 'تأكيدات المعاينة',
    'settings.notif_viewing_desc': 'احصل على تأكيدات للمعاينات المجدولة',
    'settings.account_settings': 'إعدادات الحساب',
    'settings.account_desc': 'إدارة أمان حسابك وتفضيلاتك',
    'settings.change_password': 'تغيير كلمة المرور',
    'settings.change_password_desc': 'تحديث كلمة المرور الخاصة بك',
    'settings.change': 'تغيير',
    'settings.two_factor': 'المصادقة الثنائية',
    'settings.two_factor_desc': 'إضافة أمان إضافي لحسابك',
    'settings.enable': 'تفعيل',
    'settings.delete_account': 'حذف الحساب',
    'settings.delete_account_desc': 'حذف حسابك نهائياً',
    'settings.delete': 'حذف',
    'settings.logout': 'تسجيل الخروج',
    
    // Common
    'common.back': 'رجوع',
    
    // Contact Form Modal
    'contact_form.title': 'اتصل',
    'contact_form.description': 'أرسل رسالة للتواصل.',
    'contact_form.name': 'الاسم',
    'contact_form.name_placeholder': 'اسمك',
    'contact_form.email': 'البريد الإلكتروني',
    'contact_form.email_placeholder': 'your@email.com',
    'contact_form.phone': 'الهاتف',
    'contact_form.phone_placeholder': 'رقم هاتفك',
    'contact_form.message': 'الرسالة',
    'contact_form.message_placeholder': 'كيف يمكننا مساعدتك؟',
    'contact_form.send': 'إرسال الرسالة',
    'contact_form.sending': 'جاري الإرسال...',
    'contact_form.success_title': 'تم إرسال الرسالة!',
    'contact_form.success_message': 'تم إرسال رسالتك. ستتلقى تأكيداً عبر البريد الإلكتروني.',
    'contact_form.error_title': 'خطأ',
    'contact_form.error_message': 'فشل إرسال الرسالة. يرجى المحاولة مرة أخرى.',
    
    // Messaging
    'messaging.title': 'الرسائل',
    'messaging.description': 'محادثاتك الخاصة',
    'messaging.new_message': 'رسالة جديدة',
    'messaging.no_messages': 'لا توجد رسائل بعد',
    'messaging.no_conversations': 'لا توجد محادثات بعد',
    'messaging.send': 'إرسال',
    'messaging.send_message': 'إرسال رسالة',
    'messaging.message_to': 'إرسال رسالة إلى',
    'messaging.subject': 'الموضوع',
    'messaging.subject_placeholder': 'الموضوع (اختياري)',
    'messaging.message': 'الرسالة',
    'messaging.message_placeholder': 'اكتب رسالتك...',
    'messaging.login_required': 'يجب تسجيل الدخول لإرسال الرسائل.',
    'messaging.message_sent': 'تم إرسال الرسالة بنجاح',
    'messaging.contact_agent_description': 'تواصل مع الوكيل العقاري مباشرة',
    'messaging.type_message': 'اكتب رسالة...',
    'messaging.inbox': 'البريد الوارد',
    'messaging.sent': 'المرسلة',
    'messaging.search': 'البحث في المحادثات...',
    'messaging.select_conversation': 'اختر محادثة',
    'messaging.select_description': 'اختر محادثة من القائمة لبدء المراسلة',
    'messaging.encrypted': 'مشفرة من طرف إلى طرف',
    'messaging.decrypt': 'فك تشفير الرسالة',
    'messaging.reply': 'رد',
    'messaging.replying_to': 'الرد على',
    'messaging.replying_to_yourself': 'نفسك',
    'messaging.forward': 'إعادة توجيه',
    'messaging.delete': 'حذف',
    'messaging.delete_confirm': 'حذف الرسالة',
    'messaging.delete_description': 'سيتم حذف هذه الرسالة للجميع. لا يمكن التراجع عن هذا الإجراء.',
    'messaging.delete_for_everyone': 'حذف للجميع',
    'messaging.pin': 'تثبيت',
    'messaging.unpin': 'إلغاء التثبيت',
    'messaging.pinned_messages': 'الرسائل المثبتة',
    'messaging.no_pinned': 'لا توجد رسائل مثبتة',
    'messaging.delivered': 'تم التسليم',
    'messaging.read': 'مقروءة',
    'messaging.seen_at': 'شوهدت في',
    'messaging.voice_message': 'رسالة صوتية',
    'messaging.search_messages': 'البحث في الرسائل...',
    'messaging.contacts': 'جهات الاتصال',
    'messaging.results_found': 'نتائج موجودة',
    'messaging.recording': 'جاري التسجيل...',
    'messaging.typing': 'يكتب...',
    'messaging.online': 'متصل',
    'messaging.tap_for_info': 'اضغط هنا لمعلومات الاتصال',
    'messaging.photos': 'صور',
    'messaging.camera': 'كاميرا',
    'messaging.location': 'الموقع',
    'messaging.contact': 'جهة اتصال',
    'messaging.document': 'مستند',
    'messaging.poll': 'استطلاع',
    'messaging.event': 'حدث',
    'messaging.location_coming_soon': 'مشاركة الموقع قريباً',
    'messaging.contact_coming_soon': 'مشاركة جهات الاتصال قريباً',
    'messaging.poll_coming_soon': 'إنشاء الاستطلاعات قريباً',
    'messaging.event_coming_soon': 'إنشاء الأحداث قريباً',
    'messaging.schedule_event': 'جدولة حدث',
    'messaging.event_title': 'عنوان الحدث',
    'messaging.event_title_placeholder': 'مثال: معاينة العقار',
    'messaging.event_date': 'التاريخ',
    'messaging.event_time': 'الوقت',
    'messaging.event_location': 'الموقع',
    'messaging.event_location_placeholder': 'أدخل الموقع أو العنوان',
    'messaging.event_description': 'الوصف',
    'messaging.event_description_placeholder': 'أضف أي تفاصيل إضافية...',
    'messaging.send_event': 'إرسال الحدث',
    'messaging.event_created': 'تم إنشاء الحدث',
    'messaging.create_poll': 'إنشاء استطلاع',
    'messaging.poll_question': 'السؤال',
    'messaging.poll_question_placeholder': 'ماذا تريد أن تسأل؟',
    'messaging.poll_options': 'الخيارات',
    'messaging.option': 'خيار',
    'messaging.add_option': 'إضافة خيار',
    'messaging.allow_multiple': 'السماح بإجابات متعددة',
    'messaging.send_poll': 'إرسال الاستطلاع',
    'messaging.poll_created': 'تم إنشاء الاستطلاع',
    'messaging.edit': 'تعديل',
    'messaging.edit_message': 'تعديل الرسالة',
    'messaging.edit_description': 'قم بإجراء تغييرات على رسالتك',
    'messaging.edit_time_remaining': 'لديك',
    'messaging.minutes_to_edit': 'دقائق لتعديل هذه الرسالة',
    'messaging.message_edited': 'تم تعديل الرسالة',
    'messaging.edit_failed': 'فشل في تعديل الرسالة',
    'messaging.from_date': 'من',
    'messaging.to_date': 'إلى',
    'messaging.clear_filters': 'مسح الفلاتر',
    'messaging.export_chat': 'تصدير المحادثة',
    'messaging.export_description': 'تصدير سجل المحادثة',
    'messaging.export_text': 'ملف نصي (.txt)',
    'messaging.export_text_desc': 'تنسيق نصي بسيط، سهل القراءة والمشاركة',
    'messaging.export_pdf': 'مستند PDF',
    'messaging.export_pdf_desc': 'مستند منسق، مثالي للطباعة',
    'messaging.export_messages_count': 'رسالة سيتم تصديرها',
    'messaging.exporting': 'جاري التصدير...',
    'messaging.export': 'تصدير',
    'messaging.export_success': 'تم تصدير المحادثة بنجاح',
    'messaging.export_failed': 'فشل في تصدير المحادثة',
    'messaging.is_typing': 'يكتب...',
    
    // Quick Reply Templates
    'quick_reply.title': 'ردود سريعة',
    'quick_reply.greeting': 'مرحباً! شكراً على تواصلك.',
    'quick_reply.interested': 'أنا مهتم بهذا العقار. هل يمكنك تقديم المزيد من التفاصيل؟',
    'quick_reply.schedule_viewing': 'أرغب في حجز معاينة. ما هي الأوقات المتاحة؟',
    'quick_reply.ask_price': 'هل السعر قابل للتفاوض؟',
    'quick_reply.ask_availability': 'هل هذا العقار لا يزال متاحاً؟',
    'quick_reply.thank_you': 'شكراً على المعلومات. سأعود إليك قريباً.',
    'quick_reply.will_contact': 'سأتواصل معك قريباً مع المزيد من التفاصيل.',
    'quick_reply.not_interested': 'شكراً، لكنني لم أعد مهتماً بهذا العقار.',
    
    // Notifications
    'notifications.enable': 'تفعيل الإشعارات',
    'notifications.on': 'الإشعارات مفعلة',
    'notifications.preferences': 'إعدادات الإشعارات',
    'notifications.sound': 'صوت الإشعار',
    'notifications.sound_desc': 'تشغيل صوت عند استلام رسالة',
    'notifications.sound_default': 'افتراضي',
    'notifications.sound_chime': 'رنين',
    'notifications.sound_bell': 'جرس',
    'notifications.sound_pop': 'فرقعة',
    'notifications.test': 'اختبار',
    'notifications.vibration': 'الاهتزاز',
    'notifications.vibration_desc': 'اهتزاز عند استلام رسالة (للجوال فقط)',
    'messaging.you': 'أنت',
    'messaging.encrypted_message': 'رسالة مشفرة',
    'messaging.e2e_notice': 'الرسائل والمكالمات مشفرة من طرف إلى طرف. يمكن فقط للأشخاص في هذه الدردشة قراءتها أو الاستماع إليها أو مشاركتها.',
    'messaging.today': 'اليوم',
    'messaging.yesterday': 'أمس',
    'messaging.search_groups': 'البحث في المجموعات...',
    
    // Attachment viewer
    'attachment.all_media': 'كل الوسائط',
    'attachment.go_to_message': 'الذهاب إلى الرسالة',
    'attachment.download': 'تحميل',
    'attachment.view_fullscreen': 'عرض ملء الشاشة',
    'attachment.save_to_gallery': 'حفظ في المعرض',
    'attachment.share': 'مشاركة',
    'attachment.double_tap_zoom': 'اضغط مرتين للتكبير',
    'attachment.zoom_in': 'تكبير',
    'attachment.zoom_out': 'تصغير',
    'attachment.reset_zoom': 'إعادة تعيين التكبير',
    'attachment.ctrl_scroll_zoom': 'Ctrl + التمرير للتكبير',
    'attachment.fit_width': 'ملاءمة العرض',
    'attachment.fit_height': 'ملاءمة الارتفاع',
    'attachment.click_to_navigate': 'انقر للتنقل',
    
    // Status
    'status.online': 'متصل',
    'status.offline': 'غير متصل',
    'status.last_seen': 'آخر ظهور',
    
    // Blocking
    'blocking.block_user': 'حظر المستخدم',
    'blocking.block_confirm': 'هل أنت متأكد أنك تريد حظر {name}؟ لن يتمكن من إرسال رسائل إليك.',
    'blocking.reason_optional': 'السبب (اختياري)',
    'blocking.reason_placeholder': 'لماذا تحظر هذا المستخدم؟',
    'blocking.block': 'حظر',
    'blocking.user_blocked': 'تم حظر المستخدم بنجاح',
    'blocking.already_blocked': 'المستخدم محظور بالفعل',
    'blocking.error': 'فشل في حظر المستخدم',
    'blocking.blocked_users': 'المستخدمون المحظورون',
    'blocking.blocked_users_description': 'إدارة قائمة المستخدمين المحظورين',
    'blocking.no_blocked_users': 'لم تحظر أي شخص',
    'blocking.blocked_on': 'محظور في {date}',
    'blocking.unblock': 'إلغاء الحظر',
    'blocking.user_unblocked': 'تم إلغاء حظر المستخدم',
    'blocking.unblock_error': 'فشل في إلغاء حظر المستخدم',
    'blocking.fetch_error': 'فشل في تحميل المستخدمين المحظورين',
    'blocking.you_blocked_user': 'لقد حظرت هذا المستخدم. قم بإلغاء الحظر لإرسال الرسائل.',
    'blocking.user_blocked_you': 'هذا المستخدم حظرك. لا يمكنك إرسال رسائل.',
    
    // Reporting
    'reporting.report_user': 'الإبلاغ عن المستخدم',
    'reporting.report_description': 'الإبلاغ عن {name} لانتهاك إرشاداتنا',
    'reporting.select_reason': 'يرجى اختيار سبب',
    'reporting.reason': 'سبب الإبلاغ',
    'reporting.additional_details': 'تفاصيل إضافية',
    'reporting.details_placeholder': 'قدم أي سياق إضافي...',
    'reporting.submit_report': 'إرسال البلاغ',
    'reporting.report_submitted': 'تم إرسال البلاغ. سنراجعه قريباً.',
    'reporting.error': 'فشل في إرسال البلاغ',
    'reporting.reasons.spam': 'رسائل مزعجة أو غير مرغوب فيها',
    'reporting.reasons.harassment': 'تحرش أو تنمر',
    'reporting.reasons.inappropriate_content': 'محتوى غير لائق',
    'reporting.reasons.fraud': 'احتيال أو نصب',
    'reporting.reasons.impersonation': 'انتحال شخصية',
    'reporting.reasons.other': 'أخرى',
    
    // Admin Reports
    'admin.reports': 'البلاغات',
    'admin.reports_panel': 'بلاغات المستخدمين',
    'admin.reports_description': 'مراجعة وحل بلاغات المستخدمين',
    'admin.fetch_reports_error': 'فشل في جلب البلاغات',
    'admin.pending': 'قيد الانتظار',
    'admin.resolved': 'تم الحل',
    'admin.all': 'الكل',
    'admin.no_reports': 'لا توجد بلاغات',
    'admin.back_to_list': 'العودة للقائمة',
    'admin.reporter': 'المُبلّغ',
    'admin.reported_user': 'المستخدم المُبلّغ عنه',
    'admin.reason': 'السبب',
    'admin.reported_on': 'تاريخ البلاغ',
    'admin.reported_by': 'أبلغ عنه',
    'admin.resolution_notes': 'أضف ملاحظات الحل...',
    'admin.dismiss': 'رفض',
    'admin.warn': 'تحذير المستخدم',
    'admin.block': 'حظر المستخدم',
    'admin.report_resolved': 'تم حل البلاغ بنجاح',
    'admin.resolve_error': 'فشل في حل البلاغ',
    'admin.resolution': 'الحل',
    'messaging.unknown_user': 'مستخدم غير معروف',
    'messaging.no_groups': 'لا توجد مجموعات بعد',
    'messaging.create_group': 'إنشاء مجموعة',
    'messaging.group_chat': 'محادثة جماعية',
    'messaging.direct_messages': 'مباشر',
    'messaging.groups': 'المجموعات',
    'messaging.group_name': 'اسم المجموعة',
    'messaging.group_description': 'الوصف',
    
    'nav.favorites': 'المفضلة',
    'nav.profile': 'الملف الشخصي',
    'nav.login': 'تسجيل الدخول',
    'nav.logout': 'تسجيل الخروج',
    'nav.recent_searches': 'عمليات البحث الأخيرة',
    'nav.no_recent_searches': 'لا توجد عمليات بحث حديثة',
    
    // Agency Pricing
    'agency_pricing.badge': 'خطط الوكالة',
    'agency_pricing.title': 'نمِّ وكالتك مع الخطة المناسبة',
    'agency_pricing.subtitle': 'اختر الخطة المثالية لوكالتك العقارية. وسّع فريقك وأدر المزيد من العقارات وقدم خدمة استثنائية لعملائك.',
    'agency_pricing.most_popular': 'الأكثر شعبية',
    'agency_pricing.per_month': '/شهرياً',
    'agency_pricing.processing': 'جارٍ المعالجة...',
    'agency_pricing.comparison_title': 'مقارنة الميزات التفصيلية',
    'agency_pricing.feature': 'الميزة',
    'agency_pricing.faq_title': 'الأسئلة الشائعة',
    'agency_pricing.cta_title': 'مستعد لتنمية وكالتك؟',
    'agency_pricing.cta_subtitle': 'انضم إلى آلاف الوكالات العقارية الناجحة التي تستخدم منصتنا لإدارة فرقها وعقاراتها بكفاءة.',
    'agency_pricing.cta_button': 'ابدأ مع Pro',
    'agency_pricing.sign_in_required': 'الرجاء تسجيل الدخول لترقية خطتك',
    'agency_pricing.tier.starter': 'البداية',
    'agency_pricing.tier.starter_desc': 'مثالية للوكالات الصغيرة التي تبدأ للتو',
    'agency_pricing.tier.starter_cta': 'ابدأ الآن',
    'agency_pricing.tier.pro': 'Pro',
    'agency_pricing.tier.pro_desc': 'للوكالات النامية ذات الفرق الأكبر',
    'agency_pricing.tier.pro_cta': 'الترقية إلى Pro',
    'agency_pricing.tier.enterprise': 'Enterprise',
    'agency_pricing.tier.enterprise_desc': 'للوكالات الكبيرة ذات الاحتياجات غير المحدودة',
    'agency_pricing.tier.enterprise_cta': 'انتقل إلى Enterprise',
    'agency_pricing.feature.brokers_3': 'حتى 3 وسطاء',
    'agency_pricing.feature.brokers_10': 'حتى 10 وسطاء',
    'agency_pricing.feature.brokers_unlimited': 'وسطاء غير محدودين',
    'agency_pricing.feature.listings_basic': 'إعلانات عقارية أساسية',
    'agency_pricing.feature.listings_advanced': 'إعلانات عقارية متقدمة',
    'agency_pricing.feature.listings_premium': 'إعلانات عقارية ممتازة',
    'agency_pricing.feature.messaging': 'مراسلة العملاء',
    'agency_pricing.feature.scheduling': 'جدولة المعاينات',
    'agency_pricing.feature.analytics': 'لوحة التحليلات',
    'agency_pricing.feature.analytics_advanced': 'تحليلات متقدمة',
    'agency_pricing.feature.support': 'دعم ذو أولوية',
    'agency_pricing.feature.support_247': 'دعم على مدار الساعة',
    'agency_pricing.feature.branding': 'علامة تجارية مخصصة',
    'agency_pricing.feature.api': 'الوصول إلى API',
    'agency_pricing.comparison.brokers': 'عدد الوسطاء',
    'agency_pricing.comparison.listings': 'إعلانات العقارات',
    'agency_pricing.comparison.basic': 'أساسي',
    'agency_pricing.comparison.advanced': 'متقدم',
    'agency_pricing.comparison.premium': 'ممتاز',
    'agency_pricing.faq.change_plan_q': 'هل يمكنني تغيير خطتي لاحقاً؟',
    'agency_pricing.faq.change_plan_a': 'نعم! يمكنك الترقية أو التخفيض في أي وقت. تسري التغييرات فوراً وسنعدل فاتورتك وفقاً لذلك.',
    'agency_pricing.faq.exceed_limit_q': 'ماذا يحدث إذا تجاوزت حد الوسطاء؟',
    'agency_pricing.faq.exceed_limit_a': 'ستحتاج للترقية إلى خطة أعلى لإضافة المزيد من الوسطاء. سيستمر وسطاؤك الحاليون في العمل بشكل طبيعي.',
    'agency_pricing.faq.trial_q': 'هل هناك فترة تجريبية مجانية للخطط المدفوعة؟',
    'agency_pricing.faq.trial_a': 'نقدم ضمان استرداد الأموال لمدة 14 يوماً على جميع الخطط المدفوعة. إذا لم تكن راضياً، تواصل معنا لاسترداد كامل المبلغ.',
    'agency_pricing.faq.cancel_q': 'كيف ألغي اشتراكي؟',
    'agency_pricing.faq.cancel_a': 'يمكنك إدارة اشتراكك من لوحة تحكم الوكالة. انتقل إلى علامة تبويب الفوترة وانقر على "إدارة الاشتراك" للإلغاء أو التعديل.',
    'agency_pricing.view_plans': 'عرض الخطط والأسعار',
    
    // Broker Pricing
    'broker_pricing.badge': 'خطط الوسطاء',
    'broker_pricing.title': 'ترقية حساب الوسيط الخاص بك',
    'broker_pricing.subtitle': 'اختر الخطة المثالية لعملك العقاري. احصل على المزيد من القوائم وتحليلات أفضل ودعم ذي أولوية.',
    'broker_pricing.most_popular': 'الأكثر شعبية',
    'broker_pricing.current_plan': 'خطتك الحالية',
    'broker_pricing.per_month': '/شهر',
    'broker_pricing.processing': 'جاري المعالجة...',
    'broker_pricing.loading': 'جاري التحميل...',
    'broker_pricing.manage_subscription': 'إدارة الاشتراك',
    'broker_pricing.manage_note': 'خفض أو إلغاء في أي وقت • يتجدد تلقائياً شهرياً',
    'broker_pricing.downgrade': 'خفض',
    'broker_pricing.comparison_title': 'مقارنة تفصيلية للميزات',
    'broker_pricing.feature': 'الميزة',
    'broker_pricing.faq_title': 'الأسئلة الشائعة',
    'broker_pricing.cta_title': 'مستعد لتنمية عملك؟',
    'broker_pricing.cta_subtitle': 'انضم إلى آلاف الوسطاء الناجحين الذين يستخدمون منصتنا لإدراج المزيد من العقارات وإتمام المزيد من الصفقات.',
    'broker_pricing.cta_button': 'ابدأ مع Pro',
    'broker_pricing.sign_in_required': 'يرجى تسجيل الدخول لترقية خطتك',
    'broker_pricing.already_on_plan': 'أنت بالفعل على هذه الخطة',
    'broker_pricing.checkout_error': 'فشل في بدء الدفع',
    'broker_pricing.portal_error': 'فشل في فتح إدارة الاشتراك',
    'broker_pricing.tier.normal': 'عادي',
    'broker_pricing.tier.normal_desc': 'مثالي للوسطاء الأفراد المبتدئين',
    'broker_pricing.tier.normal_cta': 'الخطة الحالية',
    'broker_pricing.tier.pro': 'احترافي',
    'broker_pricing.tier.pro_desc': 'للوسطاء النامين مع المزيد من القوائم',
    'broker_pricing.tier.pro_cta': 'الترقية إلى Pro',
    'broker_pricing.tier.enterprise': 'المؤسسات',
    'broker_pricing.tier.enterprise_desc': 'للوسطاء ذوي الحجم الكبير مع احتياجات غير محدودة',
    'broker_pricing.tier.enterprise_cta': 'اذهب إلى Enterprise',
    'broker_pricing.feature.listings_5': '5 قوائم شهرياً',
    'broker_pricing.feature.listings_10': '10 قوائم شهرياً',
    'broker_pricing.feature.listings_15': '15 قائمة شهرياً',
    'broker_pricing.feature.basic_features': 'ميزات العقارات الأساسية',
    'broker_pricing.feature.advanced_features': 'ميزات العقارات المتقدمة',
    'broker_pricing.feature.premium_features': 'ميزات العقارات المميزة',
    'broker_pricing.feature.messaging': 'رسائل العملاء',
    'broker_pricing.feature.scheduling': 'جدولة المعاينات',
    'broker_pricing.feature.video_paid': 'جولات فيديو ($3 لكل قائمة)',
    'broker_pricing.feature.video_free': 'جولات فيديو مجانية مضمنة',
    'broker_pricing.feature.analytics': 'لوحة التحليلات',
    'broker_pricing.feature.analytics_advanced': 'تحليلات متقدمة',
    'broker_pricing.feature.support': 'دعم ذو أولوية',
    'broker_pricing.feature.support_247': 'دعم ذو أولوية 24/7',
    'broker_pricing.feature.featured': 'قوائم مميزة',
    'broker_pricing.feature.extra_listings': 'خيار القوائم الإضافية',
    'broker_pricing.feature.extra_price': '+$10 لكل قائمة إضافية',
    'broker_pricing.comparison.listings': 'القوائم شهرياً',
    'broker_pricing.comparison.video': 'جولات الفيديو',
    'broker_pricing.comparison.extra': 'قوائم إضافية',
    'broker_pricing.comparison.duration': 'مدة القائمة',
    'broker_pricing.comparison.featured': 'قوائم مميزة',
    'broker_pricing.comparison.30_days': '30 يوماً',
    'broker_pricing.comparison.per_listing': '$3 لكل قائمة',
    'broker_pricing.comparison.free': 'مجاني',
    'broker_pricing.comparison.each': '$10 لكل واحدة',
    'broker_pricing.comparison.15_plus': '15 + إضافية',
    'broker_pricing.faq.autorenew_q': 'هل يتجدد اشتراكي تلقائياً؟',
    'broker_pricing.faq.autorenew_a': 'نعم! يتجدد اشتراكك تلقائياً كل شهر حتى لا تفقد الوصول إلى قوائمك. ستتم محاسبتك في نفس اليوم كل شهر. يمكنك الإلغاء في أي وقت من بوابة إدارة الاشتراك.',
    'broker_pricing.faq.upgrade_q': 'هل يمكنني الترقية أو الخفض في أي وقت؟',
    'broker_pricing.faq.upgrade_a': 'بالتأكيد! تدخل الترقيات حيز التنفيذ فوراً مع الفوترة النسبية. تطبق التخفيضات في نهاية فترة الفوترة الحالية - تحتفظ بجميع الميزات حتى ذلك الحين. انقر على "إدارة الاشتراك" لإجراء التغييرات.',
    'broker_pricing.faq.expire_q': 'ماذا يحدث عندما تنتهي قوائمي؟',
    'broker_pricing.faq.expire_a': 'تنتهي القوائم بعد 30 يوماً. ستتلقى إشعاراً بالبريد الإلكتروني قبل 3 أيام من انتهاء الصلاحية. يمكنك تجديدها باستخدام الفتحات المجانية الشهرية أو دفع $10 لكل قائمة إضافية (Enterprise فقط).',
    'broker_pricing.faq.extra_q': 'ما هي "القوائم الإضافية"؟',
    'broker_pricing.faq.extra_a': 'يمكن لأعضاء خطة Enterprise شراء قوائم إضافية بسعر $10 لكل واحدة تتجاوز 15 قائمة مجانية شهرياً. هذا رائع للأشهر ذات الحجم الكبير.',
    'broker_pricing.faq.cancel_q': 'كيف ألغي اشتراكي؟',
    'broker_pricing.faq.cancel_a': 'انقر على "إدارة الاشتراك" للوصول إلى بوابة الفوترة حيث يمكنك الإلغاء أو الخفض أو تحديث طريقة الدفع. تدخل الإلغاءات حيز التنفيذ في نهاية فترة الفوترة.',
    'messaging.conversation_pinned': 'تم تثبيت المحادثة',
    'messaging.conversation_unpinned': 'تم إلغاء تثبيت المحادثة',
    'messaging.pin_chat': 'تثبيت المحادثة',
    'messaging.unpin_chat': 'إلغاء تثبيت المحادثة',
    'messaging.pin_failed': 'فشل تثبيت المحادثة',
    'messaging.unpin_failed': 'فشل إلغاء تثبيت المحادثة',
    'messaging.archive': 'أرشفة',
    'messaging.unarchive': 'استعادة',
    'messaging.show_archived': 'المؤرشفة',
    'messaging.show_active': 'عرض النشطة',
    'messaging.conversation_archived': 'تم أرشفة المحادثة',
    'messaging.conversation_unarchived': 'تم استعادة المحادثة',
    'messaging.archive_failed': 'فشل أرشفة المحادثة',
    'messaging.unarchive_failed': 'فشل استعادة المحادثة',
  },
  ku: {
    // Header (Kurmanji - Latin script)
    'nav.search': 'Lêgerîna Xanîyan',
    'nav.search_agents': 'Lêgerîna Brokerî',
    'nav.sell': 'Firotina Xanîyê',
    'nav.news': 'Nûçe',
    'nav.build': 'Avahiya Xanîyê',
    'nav.agencies': 'Ajans',
    'nav.buy': 'Kirîn',
    'nav.valuation': 'Nirxandin',
    'nav.more': 'Zêdetir',
    'nav.saved': 'Tomarkirî',
    'nav.admin': 'Rêvebir',
    'nav.agency': 'Ajans',
    'nav.agency_dashboard': 'Panelê Ajansê',
    'nav.location_manager': 'Rêveberiya Cîhan',
    'nav.map_configuration': 'Mîhengên Nexşeyê',
    'nav.hello': 'Silav',
    'nav.menu': 'Menû',
    'nav.manage_news': 'Rêveberiya Nûçeyan',
    'nav.all_properties': 'Hemû Xanî',
    'nav.new_production': 'Avakirinên Nû',
    
    // Search Section
    'search.for_sale': 'Ji bo Firotin',
    'search.for_rent': 'Ji bo Kirêkirinê',
    'search.sold_prices': 'Nirxên Firotin',
    'search.search_agents': 'Lêgerîna Brokerî',
    'search.area_label': 'Herêm',
    'search.area_placeholder': 'Herêm an navnîşan binivîse',
    'search.area_examples': 'Mînak: Bexda, Basra, Hewlêr, Necef, Kerbela',
    'search.expand_area': 'Berfirehkirina herêmê bi',
    'search.property_type': 'Cureya Xanîyê',
    'search.show_filters': 'Nîşandana fîltirên lêgerînê',
    'search.find_properties': 'Dîtina xanîyan ji bo firotin',
    'search.recent_searches': 'Lêgerînên dawî',
    'search.hide_history': 'Dîroka veşêre',
    'search.clear_history': 'Dîrokê jê bibe',
    
    // Property Types
    'property.all_types': 'Hemû Cure',
    'property.villas': 'Vîla',
    'property.townhouses': 'Xanîyên Girêdayî',
    'property.apartments': 'Apartman',
    'property.business': 'Xanîyên Bazirganiyê',
    'property.land': 'Ax',
    'property.farms': 'Çandinî/Daristanî',
    'property.other': 'Yên Din',
    
    // Cities/Governorates
    'city.baghdad': 'Bexda',
    'city.basra': 'Basra',
    'city.erbil': 'Hewlêr',
    'city.sulaymaniyah': 'Silêmanî',
    'city.najaf': 'Necef',
    'city.karbala': 'Kerbela',
    'city.mosul': 'Mûsil',
    'city.kirkuk': 'Kerkûk',
    'city.anbar': 'Ênbar',
    'city.dhi_qar': 'Zîqar',
    'city.maysan': 'Meysan',
    'city.wasit': 'Wasit',
    'city.babil': 'Babil',
    'city.qadisiyyah': 'Qadisîye',
    'city.muthanna': 'Misena',
    'city.diyala': 'Dîyale',
    'city.saladin': 'Selahedîn',
    'city.nineveh': 'Neynewa',
    'city.dohuk': 'Dihok',
    
    // Auth extended
    'auth.first_name': 'Navê Pêşî',
    'auth.last_name': 'Navê Paşî',
    'auth.first_name_placeholder': 'Navê xwe yê pêşî binivîse',
    'auth.last_name_placeholder': 'Navê xwe yê paşî binivîse',
    'auth.display_name': 'Navê Nîşanê',
    'auth.display_name_placeholder': 'Navê nîşanê hilbijêre',
    'auth.display_name_hint': 'Ev tenê navê ku kesên din wê bibînin e',
    'auth.date_of_birth': 'Dîroka Jidayikbûnê',
    'auth.fill_all_fields': 'Ji kerema xwe hemû qadên pêwîst dagirin',
    'auth.must_be_18': 'Divê temenê te herî kêm 18 sal be ji bo tomarkirinê',
    'auth.must_be_18_hint': 'Divê temenê te herî kêm 18 sal be',
    
    // Identity Verification
    'verification.verify_identity': 'Nasnameya Xwe Piştrastbike',
    'verification.verified': 'Piştrastkirî',
    'verification.title': 'Piştrastkirina Nasnameyê',
    'verification.description': 'Belgeyek hukûmetê bar bike da ku nasnameya xwe piştrastbikî û nîşanek piştrastkirî bistînî.',
    'verification.document_type': 'Cureya Belgeyê',
    'verification.doc_id_card': 'Karta Nasnameyê',
    'verification.doc_passport': 'Derbas',
    'verification.doc_drivers_license': 'Lîsansa Şoferiyê',
    'verification.upload_document': 'Belgeyê Bar Bike',
    'verification.click_to_upload': 'Bikirtîne ji bo barkirinê an bikişîne û berde',
    'verification.file_requirements': 'JPG, PNG, WEBP an PDF (herî zêde 10MB)',
    'verification.submit': 'Ji Bo Piştrastkirinê Bişîne',
    'verification.uploading': 'Tê barkirin...',
    'verification.submitted': 'Piştrastkirin Hat Şandin',
    'verification.submitted_desc': 'Daxwaza piştrastkirina te hat şandin. Em ê di demek kurt de binirxînin.',
    'verification.pending_message': 'Piştrastkirina te tê nirxandin. Ev bi gelemperî 1-2 rojên xebatê digire.',
    'verification.rejected_message': 'Piştrastkirina te ya berê hat redkirin. Ji kerema xwe belgeyek nû bişîne.',
    'verification.status_approved': 'Pejirandî',
    'verification.status_pending': 'Li Bendê',
    'verification.status_rejected': 'Redkirî',
    'verification.invalid_file_type': 'Cureya pelê nederbasdar',
    'verification.allowed_types': 'Ji kerema xwe pelê JPG, PNG, WEBP, an PDF bar bike',
    'verification.file_too_large': 'Pel pir mezin e',
    'verification.max_size': 'Mezinahiya herî zêde ya pelê 10MB ye',
    'verification.error': 'Çewtiya Piştrastkirinê',
    'verification.admin_title': 'Piştrastkirinên Nasnameyê',
    'verification.admin_description': 'Daxwazên piştrastkirina nasnameyê binirxîne û bipejirîne',
    'verification.no_requests': 'Daxwazên piştrastkirinê tune',
    'verification.view': 'Binirxîne',
    'verification.review_title': 'Piştrastkirinê Binirxîne',
    'verification.review_notes': 'Têbîniyên Nirxandinê',
    'verification.review_notes_placeholder': 'Têbîniyên li ser biryara xwe zêde bike (vebijarkî)',
    'verification.approve': 'Bipejirîne',
    'verification.reject': 'Redbike',
    'verification.approved': 'Piştrastkirin Hat Pejirandin',
    'verification.rejected': 'Piştrastkirin Hat Redkirin',
    'verification.review_complete': 'Piştrastkirin hat pêvajokirin.',

    // Common
    'common.price': 'Nirx',
    'common.rooms': 'Jûr',
    'common.size': 'Mezinahî',
    'common.governorate': 'Parêzgeh',
    'common.select_governorate': 'Parêzgehê hilbijêre',
    'common.cancel': 'Betal bike',
    'common.save': 'Tomar bike',
    'common.saving': 'Tê tomarkirin...',
    'common.send': 'Bişîne',
    'common.sending': 'Tê şandin...',
    'common.submit': 'Bişîne',
    'common.close': 'Bigire',
    'common.error': 'Çewtî',
    'common.success': 'Serketî',
    'common.loading': 'Tê barkirin...',
    'common.name': 'Nav',
    'common.email': 'E-peyam',
    'common.phone': 'Têlefon',
    'common.message': 'Peyam',
    'common.required': 'Pêwîst',
    'common.optional': 'Vebijarkî',
    'common.delete': 'Jêbirin',
    'common.free': 'Belaş',
    
    // Leaderboard
    'leaderboard': 'Lîsteya Pêşengan',
    'leaderboardDescription': 'Ajans û brokerên herî baş bibînin li gorî dîtin û firotanên',
    'topAgencies': 'Ajansên Top',
    'topBrokers': 'Brokerên Top',
    'topAgenciesDescription': 'Ajans li gorî dîtinên xanîyan û performansa firotanê hatine rêzkirin',
    'topBrokersDescription': 'Broker li gorî dîtinên xanîyan û performansa firotanê hatine rêzkirin',
    'allRegions': 'Hemû Herêm',
    'noAgenciesFound': 'Ajans nehat dîtin',
    'noBrokersFound': 'Broker nehat dîtin',
    'brokers': 'broker',
    'sold': 'firotî',
    'active': 'çalak',
    'views': 'dîtin',
    
    // Dialogs - Contact Agent
    'dialog.contact_agent': 'Bi Brokerê Re Têkilî Bike',
    'dialog.contact_agent_btn': 'Bi Brokerê Re Têkilî Bike',
    'dialog.contact_message_placeholder': 'Silav! Ez di xanîya li {address} de eleqedar im û dixwazim zêdetir zanibim. Ji kerema xwe dema ku wextê we hebe bi min re têkilî daynin.',
    'dialog.contact_sending': 'Tê şandin...',
    'dialog.contact_send': 'Peyamê Bişîne',
    'dialog.contact_note': 'Pirsyara te yekser dê ji brokerê berpirsiyar re were şandin ku dê bi te re têkilî bike.',
    
    // Dialogs - Schedule Viewing
    'dialog.schedule_viewing': 'Serdanê Rezerve Bike',
    'dialog.schedule_viewing_btn': 'Serdanê Rezerve Bike',
    'dialog.preferred_time': 'Dema serdanê ya bijartî',
    'dialog.preferred_time_note': 'Tarîx û demjimêr ji bo serdanê hilbijêre. Broker dê hebûnê piştrast bike.',
    'dialog.special_requests': 'Daxwazên taybet',
    'dialog.special_requests_placeholder': 'Daxwazên taybet an pirsên...',
    'dialog.schedule_booking': 'Tê rezervekirin...',
    'dialog.schedule_book': 'Serdanê Rezerve Bike',
    'dialog.schedule_note': 'Daxwaza te dê ji brokerê re were şandin ku dê demê bi e-peyamê piştrast bike.',
    
    // Dialogs - Save Search
    'dialog.save_search': 'Lêgerînê Tomar Bike',
    'dialog.save_search_btn': 'Lêgerînê Tomar Bike',
    'dialog.search_name': 'Navê lêgerînê',
    'dialog.search_name_placeholder': 'Mînak: Apartmanê li Hewlêr',
    'dialog.city_municipality': 'Bajar/Şaredarî',
    'dialog.min_rooms': 'Herî kêm jûr',
    'dialog.min_size': 'Herî kêm mezinahî (m²)',
    'dialog.property_type': 'Cureya xanîyê',
    'dialog.email_alerts': 'Agahdariyên e-peyamê dema ku xanîyên nû lihev dikişînin',
    'dialog.saving_search': 'Tê tomarkirin...',
    
    // Dialogs - Mark as Sold
    'dialog.mark_sold': 'Wekî Firotî Nîşan Bike',
    'dialog.mark_sold_description': 'Nirxê firotina dawî ya vê xanîyê binivîse. Ev ê reklama xanîyê bibe nav xanîyên hatine firotin.',
    'dialog.final_sale_price': 'Nirxê Firotina Dawî (Dolar)',
    'dialog.listed_price': 'Nirxê hatî danîn',
    'dialog.property_details': 'Hûrguliyên Xanîyê',
    'dialog.enter_valid_price': 'Ji kerema xwe nirxê firotinê yê derbasdar binivîse',
    'dialog.enter_price_placeholder': 'Nirxê firotinê binivîse',
    'dialog.marked_sold_success': 'Xanî bi serkeftî wekî firotî hat nîşankirin',
    'dialog.mark_sold_error': 'Nîşankirina xanîyê wekî firotî bi ser neket',
    
    // Dialogs - Add Property
    'dialog.add_property': 'Xanîyekê Nû Lê Zêde Bike',
    'dialog.hide_preview': 'Pêşdîtinê veşêre',
    'dialog.show_preview': 'Pêşdîtinê nîşan bide',
    'dialog.selected_images': 'Wêneyên hatine hilbijartin',
    'dialog.uploaded_images': 'Wêneyên hatine barkirin',
    'dialog.click_primary': 'Li wêneyekê bitikîne da ku wekî wêneya serekî were danîn',
    'dialog.invalid_format': 'Formata pelê ne derbasdar e',
    'dialog.file_too_large': 'Pel pir mezin e',
    'dialog.upload_failed': 'Barkirin bi ser neket',
    'dialog.images_required': 'Wêne pêwîst in',
    'dialog.please_upload': 'Ji kerema xwe ji bo xanîyê herî kêm wêneyek bar bike',
    'dialog.form_validation_failed': 'Piştrastkirina formê bi ser neket',
    
    // Hero/News Section
    'hero.find_dream_home': 'Xanîya xewna xwe li Iraqê bibîne',
    'hero.explore_description': 'Bi hezaran xanîyên ji bo firotin û bihayên dawî ji seranserê Iraqê bibîne.',
    'hero.badge': 'Platforma Xanîyê ya Iraqê',
    
    // Home Features
    'home.feature.properties': 'Bi hezaran xanî',
    'home.feature.properties_desc': 'Di hemû parêzgehên Iraqê de xanî, apartman û axa herî mezin bibînin.',
    'home.feature.market': 'Pisporiya herêmî',
    'home.feature.market_desc': 'Zanîna kûr a her parêzgeh û taxên Iraqê.',
    'home.feature.trusted': 'Brokerên piştrastkirî',
    'home.feature.trusted_desc': 'Bi pisporên sondxwarî û piştrastkirî re têkilî daynin.',
    'home.why_choose': 'Çima Aqarnet hilbijêrin',
    'home.why_choose_desc': 'Her tiştê ku hûn hewce ne ji bo dîtina xanîya xwe ya bêkêmasî',
    
    // CTA Section
    'cta.ready': 'Amade ne ku mala xwe bibînin?',
    'cta.desc': 'Bi hezaran xanîyên li seranserê Iraqê bibînin',
    'cta.explore': 'Xanîyan Bibînin',
    
    // Property Listings
    'listings.latest_for_sale': 'Herî Dawî ji bo Firotin',
    'listings.properties_found': 'Xanî hatin dîtin',
    'listings.all_news': 'Hemû Nûçe',
    'listings.add_property': 'Xanîyê Lê Zêde bike',
    'listings.edit': 'Guhertin',
    'listings.monthly_fee': 'Kirê ya Mehane',
    'listings.open_floor_plan': 'Plana Vekirî',
    'listings.large_balcony': 'Balkonê Mezin',
    'listings.family_friendly': 'Ji bo Malbatan',
    'listings.photos': 'Wêne',
    'listings.per_month': 'mehane',
    'listings.delete_confirm': 'Dixwazî vê xanîyê jêbibî?',

    // Property Types  
    'propertyTypes.allTypes': 'Hemû Cure',
    'propertyTypes.villa': 'Vîla', 
    'propertyTypes.apartment': 'Apartman',
    'propertyTypes.townhouse': 'Xanîyên Girêdayî',
    'propertyTypes.business': 'Xanîyên Bazirganiyê',
    'propertyTypes.plot': 'Ax',
    'propertyTypes.farm': 'Çandinî/Daristanî',
    'propertyTypes.other': 'Yên Din',
    
    // Forms
    'form.price': 'Nirx',
    'form.priceUSD': 'Nirx (USD)',
    'form.priceIQD': 'Nirx (IQD)',
    'form.priceConversionNote': 'Nirxê bi yek ji her du diravî binivîse - ya din bixweber tê hesabkirin (Rêje: 1 USD ≈ 1,310 IQD)',
    'form.address': 'Navnîşan',
    'form.city': 'Bajar',
    'form.rooms': 'Jûr',
    'form.bathrooms': 'Serşok',
    'form.size': 'Mezinahî (m²)',
    'form.type': 'Cure',
    'form.propertyType': 'Cureya xaniyê',
    'form.monthly_fee': 'Kirê ya Mehane (Dînar)',
    'form.description': 'Danasîn',
    'form.payment_plan': 'Plana Dayînê',
    'form.payment_plan_placeholder': 'Mînak: 30% pêşdanîn, 70% di radestkirinê de, an jî dravdanên mehane...',
    'form.payment_plan_hint': 'Dravdanên taksîtê zêde bike da ku vebijarkên dayînê nîşanî kiryaran bidî',
    'payment_plan.total': 'Tevahî',
    'payment_plan.amount': 'Miqdar',
    'payment_plan.description': 'Danasîn',
    'payment_plan.date': 'Dîrok',
    'payment_plan.percentage': 'Ji sed',
    'payment_plan.add_installment': 'Taksît zêde bike',
    'payment_plan.hint': 'Vala bihêle eger plana dayînê nayê pêşkêşkirin',
    'payment_plan.down_payment': 'Pêşdanîn',
    'payment_plan.first_payment': 'Dravdana yekem',
    'payment_plan.second_payment': 'Dravdana duyem',
    'payment_plan.third_payment': 'Dravdana sêyem',
    'payment_plan.fourth_payment': 'Dravdana çarem',
    'payment_plan.fifth_payment': 'Dravdana pêncem',
    'payment_plan.sixth_payment': 'Dravdana şeşem',
    'payment_plan.seventh_payment': 'Dravdana heftem',
    'payment_plan.final_payment': 'Dravdana dawî',
    'payment_plan.payment_description': 'Danasîna dravdanê',
    'payment_plan.enable': 'Plana dravdanê têxe',
    'form.floor_plans': 'Nexşeyên Qat',
    'form.upload_floor_plans': 'Wêneyên nexşeyên qat bar bikin',
    'form.features': 'Taybetmendî (bi koma veqetandî)',
    'form.amenities': 'Hêsanî',
    'form.amenities_hint': 'Hemû hêsaniyên ku ji bo vê xaniyê dikevin hilbijêre',
    'amenities.balcony': 'Balkon',
    'amenities.electricity_generator': 'Jeneratorê Elektrîkê',
    'amenities.central_ac': 'Sarîkirina Navendî',
    'amenities.double_glazed_windows': 'Pencereyên Du-Cam',
    'amenities.satellite_tv': 'TV Satelît/Kablo',
    'amenities.cctv': 'CCTV',
    'amenities.security_staff': 'Karmendên Ewlehiyê',
    'amenities.sewerage_system': 'Sîstema Kanalizasyonê',
    'amenities.furnished_kitchen': 'Metbexê Moble',
    'amenities.24hr_electricity': 'Elektrîk 24 saet',
    'amenities.market': 'Bazar',
    'amenities.elevator': 'Asansor',
    'amenities.school': 'Dibistan',
    'amenities.mosque': 'Mizgeft',
    'amenities.central_gas': 'Gaza Navendî',
    'amenities.maintenance': 'Tamîrkirin',
    'amenities.cleaning': 'Paqijkirin',
    'amenities.fire_fighting': 'Vemirandina Agir',
    'amenities.car_washing': 'Şuştina Otomobîlê',
    'amenities.smart_home': 'Mala Jîr',
    'amenities.earthquake_resistant': 'Li Hember Erdhejê Berxwedêr',
    'amenities.central_fire_system': 'Sîstema Vemirandina Agir ya Navendî',
    'amenities.hot_water_system': 'Sîstema Ava Germ',
    'amenities.central_vacuum': 'Sîstema Vakuumê ya Navendî',
    'amenities.fountain': 'Kanî',
    'amenities.24hr_water': 'Av 24 saet',
    'amenities.garbage_chute': 'Kanalê Zibilan',
    'amenities.kitchen_counter': 'Konterê Metbexê',
    'amenities.street_lights': 'Ronahiya Kolanan',
    'amenities.playground': 'Qada Lîstikê ya Hundir',
    'amenities.gym': 'Salona Werzîşê',
    'amenities.garden': 'Baxçe',
    'amenities.parking': 'Parkirin',
    'amenities.wifi': 'WiFi',
    'amenities.security_door': 'Deriyê Ewlehiyê',
    'amenities.split_ac': 'AC Split',
    'amenities.bathtub': 'Benek',
    'amenities.refrigerator': 'Sarincok',
    'amenities.washing_machine': 'Makîneya Cilşuştinê',
    'amenities.swimming_pool': 'Hewza Avjeniyê',
    'amenities.solar_panels': 'Panelên Rojê',
    'amenities.water_tank': 'Tanka Avê',
    'amenities.water_well': 'Bîra Avê',
    'amenities.septic_tank': 'Tanka Septîk',
    'amenities.central_heating': 'Germkirina Navendî',
    'amenities.desert_cooler': 'Sarînkera Çolê',
    'amenities.intercom': 'Înterkom',
    'amenities.external_camera': 'Kameraya Derve',
    'amenities.guard_room': 'Odeya Nobedar',
    'amenities.rooftop_terrace': 'Terasê Banê',
    'amenities.basement': 'Bodrum',
    'amenities.storage_room': 'Odeya Depoyê',
    'amenities.internal_stairs': 'Pêlikên Hundurîn',
    'amenities.external_stairs': 'Pêlikên Derve',
    'amenities.corner_unit': 'Yekîneya Quncikê',
    'amenities.two_streets': 'Du Kolan',
    'amenities.double_entrance': 'Derketina Dualî',
    'amenities.marble_flooring': 'Zemîna Mermez',
    'amenities.ceramic_tiles': 'Qerşeyên Seramîk',
    'amenities.parquet_flooring': 'Zemîna Parket',
    'amenities.gypsum_decorations': 'Xemlên Gîpsê',
    'amenities.false_ceiling': 'Banê Derewîn',
    'amenities.steel_doors': 'Deriyên Polayan',
    'amenities.aluminum_windows': 'Pencereyên Alumînyûmê',
    'amenities.pvc_windows': 'Pencereyên PVC',
    'amenities.guest_room': 'Odeya Mêvanan',
    'amenities.servant_quarters': 'Cihên Xizmetkar',
    'amenities.covered_parking': 'Parkkirina Sergirtî',
    'amenities.concrete_fence': 'Têla Betonê',
    'amenities.iron_fence': 'Têla Hesinê',
    'amenities.street_pavement': 'Rêya Peyarê',
    'amenities.fully_furnished': 'Bi Tevahî Moble',
    'amenities.hospital': 'Nexweşxaneya Nêzîk',
    'amenities.category.utilities': 'Elektrîk û Av',
    'amenities.category.climate': 'Kontrola Hewayê',
    'amenities.category.security': 'Ewlehî',
    'amenities.category.building': 'Taybetmendiyên Avahiyê',
    'amenities.category.interior': 'Hundur û Zemîn',
    'amenities.category.rooms': 'Ode',
    'amenities.category.outdoor': 'Derve û Parkirin',
    'amenities.category.appliances': 'Amûr',
    'amenities.category.services': 'Xizmet',
    'amenities.category.nearby': 'Nêzîk',
    'common.selected': 'bijartî',
    'form.property_images': 'Wêneyên Xanîyê',
    'form.required': 'Pêwîst',
    'form.optional': 'Vebijarkî',
    'form.cancel': 'Betal bike',
    'form.save_changes': 'Guhertinan tomar bike',
    'form.uploading': 'Tê barkirin...',
    'form.upload_images': 'Wêne Bar bike',
    'form.click_to_upload': 'Ji bo barkirina wêneyan klîk bike',
    'form.file_formats': 'JPEG, PNG, WebP, GIF (Herî zêde 10MB ji bo her wêneyekê)',
    'form.select_type': 'Cure hilbijêre',
    'form.location_on_map': 'Cîyê xanîyê li ser nexşeyê',
    'form.location_hint': 'Li ser nexşeyê klîk bike ji bo diyarkirina cîyê xanîyê',
    'form.hide_preview': 'Pêşdîtinê veşêre',
    'form.show_preview': 'Pêşdîtinê nîşan bide',
    'form.selected_images': 'Wêneyên bijartî',
    'form.click_set_primary': 'Ji bo wêneyek wekî ya sereke danîn li wêneyê klîk bike',
    'form.uploaded_images': 'Wêneyên barkirî',
    'form.images': 'wêne',
    'form.uploading_images': 'Wêne tên barkirin...',
    'form.city_placeholder': 'Li bajarê bigere...',
    'form.new_construction': 'Avakirina nû (Xaniyê pêşeroj)',
    'form.preview': 'Pêşdîtin',
    'form.preview_description': 'Reklamê te wê li ser malperê wiha xuya bibe',
    'form.fullscreen': 'Ekrana tevahî',
    'form.exit_fullscreen': 'Ji ekrana tevahî derkeve',
    'form.click_preview_fullscreen': 'Pêşdîtinê bikirtînin ji bo ekrana tevahî',
    'form.preview_contact_disabled': 'Vebijarkên têkiliyê di moda pêşdîtinê de neçalak in',
    'form.invalid_format': 'Formata dosyayê nederbasdar e',
    'form.invalid_format_desc': '{filename} formata nederbasdar heye. Destûr: JPEG, PNG, WebP, GIF',
    'form.file_too_large': 'Dosya pir mezin e',
    'form.file_too_large_desc': '{filename} ji 10MB mezintir e',
    'form.upload_failed': 'Barkirin têkçû',
    'form.upload_failed_desc': 'Nekarî wêneyan bar bike',
    'form.images_required': 'Wêne hewce ne',
    'form.images_required_desc': 'Ji kerema xwe herî kêm yek wêne ji bo milkê bar bike',
    'form.sold_price_usd': 'Bihayê Firotinê (USD)',
    'form.sold_price_placeholder': 'Bihayê firotinê yê rastîn binivîse',
    'form.sold_price_description': 'Bihayê firotinê yê rastîn (dibe ku ji bihayê lîsteyê cuda be)',
    'form.video_url': 'Lînka Gera Vîdyoyê',
    'form.video': 'Vîdyo',
    'form.add_video': 'Vîdyoyek din zêde bike',
    'form.video_addon': '+$3 pêvek',
    'form.video_included': 'Belaş tê de ye',
    'form.video_url_hint': 'Vîdyoyeke gera YouTube an Vimeo lê zêde bike. Pêveka vîdyoyê piştî afirandina lîsteyê dikare were kirîn.',
    'form.video_url_hint_free': 'Vîdyoyeke gera YouTube an Vimeo lê zêde bike - di abonetiya te de tê de ye!',
    'form.video_url_hint_addon': 'Vîdyoyeke gera YouTube an Vimeo lê zêde bike. $3 dê li lêçûna lîsteya te were zêdekirin.',
    'form.include_video_tour': 'Gera vîdyoyê tê de ye',
    'form.show_map': 'Nexşeyê nîşan bide',
    'form.no_location': 'Cî nehatiye hilbijartin',
    
    // Footer
    'footer.contact': 'Pêwendî',
    'footer.customer_service': 'Xizmeta xerîdar û pêwendî',
    'footer.about': 'Derbarê Aqarnet',
    'footer.press': 'Çapemenî',
    'footer.investors': 'Ji bo veberhêneran',
    'footer.advertising': 'Reklam û marketing',
    'footer.jobs': 'Kar bi me re',
    'footer.statistics': 'Statîstîkên milkan',
    'footer.apps': 'Sepanan',
    'footer.iphone_ipad': 'iPhone û iPad',
    'footer.android': 'Android',
    'footer.follow': 'Aqarnet bişopîne',
    'footer.newsletter': 'Nûçenameya Aqarnet',
    'footer.whatsapp': 'WhatsApp',
    'footer.facebook': 'Facebook',
    'footer.instagram': 'Instagram',
    'footer.linkedin': 'LinkedIn',
    'footer.pinterest': 'Pinterest',
    'footer.tiktok': 'TikTok',
    'footer.threads': 'Threads',
    'footer.podcast': 'Podcast Aqarnet',
    'footer.all_properties': 'Hemû milk',
    'footer.privacy': 'Nepenî',
    'footer.cookies': 'Cookies',
    'footer.terms': 'Şert û Agahdariya Qanûnî',
    'footer.cookie_settings': 'Mîhengên cookie',
    
    // Agents
    'agents.title': 'Brokerekî bibîne',
    'agents.subtitle': 'Li fikra firotinê yî? Em dikarin ji te re brokerekî bibînin!',
    'agents.area_search': 'Li gorî herêmê bigere',
    'agents.name_search': 'Li gorî navî bigere',
    'agents.search_placeholder': 'Herêm an navnîşan binivîse',
    'agents.show': 'Nîşan bide',
    'agents.sort_by': 'Rêz bike li gorî',
    'agents.agents_count': 'Broker',
    'agents.companies_label': 'Pargîdaniyên Brokerî',
    'agents.recent_sales': 'Firotinên Dawî',
    'agents.most_sales': 'Herî Zêde Firotin',
    'agents.alphabetical': 'Rêza Elfabeya',
    'agents.showing_results': 'Nîşandan',
    'agents.agents_label': 'Broker',
    'agents.loading': 'Brokerî tên barkirin...',
    'agents.no_results': 'Tu broker nehat dîtin.',
    'agents.phone': 'Têlefon:',
    'agents.status': 'Rewş:',
    'agents.active_agent': 'Brokerê Çalak',
    'agents.anonymous_agent': 'Brokerê Nediyar',
    'agents.agent_label': 'Broker',
    'agents.show_more': 'Bêtir nîşan bide',

    // Properties for Sale Page
    'propertiesForSale.title': 'Xanî ji bo Firotin - Iraq',
    'propertiesForSale.statistics.municipalities': 'Şaredarî',
    'propertiesForSale.statistics.forSale': 'Ji bo Firotin',
    'propertiesForSale.statistics.sold': 'Di vê hefteyê de hatine firotin',
    'propertiesForSale.map.fullscreen': 'Nexşe / Dîmena Tijî',
    'propertiesForSale.map.area': 'Herêm',
    'propertiesForSale.filters.searchWithin': 'Di nav de bigere',
    'propertiesForSale.filters.km': 'km',
    'propertiesForSale.filters.propertyType': 'Cureya Xanîyê',
    'propertiesForSale.filters.allTypes': 'Hemû Cure',
    'propertiesForSale.filters.villa': 'Vîla',
    'propertiesForSale.filters.apartment': 'Apartman',
    'propertiesForSale.filters.townhouse': 'Xanîyê Girêdayî',
    'propertiesForSale.filters.plot': 'Ax',
    'propertiesForSale.filters.commercial': 'Bazirganî',
    'propertiesForSale.filters.other': 'Yên Din',
    'propertiesForSale.filters.rooms': 'Jûr',
    'propertiesForSale.filters.bathrooms': 'Serşok',
    'propertiesForSale.filters.min': 'Kêmtirîn',
    'propertiesForSale.filters.max': 'Zêdetirîn',
    'propertiesForSale.filters.size': 'Mezinahî',
    'propertiesForSale.filters.price': 'Nirx',
    'propertiesForSale.filters.features': 'Taybetmendî',
    'propertiesForSale.filters.balcony': 'Balkon/Terras',
    'propertiesForSale.filters.pets': 'Heywanên Malê',
    'propertiesForSale.filters.newBuild': 'Avahiya Nû',
    'propertiesForSale.filters.showAll': 'Hemû nîşan bide',
    'propertiesForSale.filters.buildYear': 'Sala Avahiyê',
    'propertiesForSale.filters.searchResults': 'Encamên Lêgerînê',
    'propertiesForSale.filters.searchPlaceholder': 'Li herêm, bajar hwd. bigere...',
    'propertiesForSale.filters.searchingFor': 'Lê digerin:',
    'propertiesForSale.filters.any': 'Her',
    'propertiesForSale.filters.keywords': 'Peyvên sereke',
    'propertiesForSale.filters.showResults': 'Encaman nîşan bide',
    'propertiesForSale.filters.clearFilters': 'Paqij bike',
    'propertiesForSale.filters.realEstateSituation': 'Rewşa Milkê',
    'propertiesForSale.filters.situation.tapu': 'Tapu',
    'propertiesForSale.filters.situation.landContract': 'Peymana Axê',
    'propertiesForSale.filters.situation.noDocuments': 'Belge Tune',
    'propertiesForSale.filters.situation.investment': 'Veberhênan',
    'propertiesForSale.filters.situation.musataha': 'Musataha',
    'propertiesForSale.filters.situation.card': 'Kart',
    'propertiesForSale.filters.situation.agriculturalTapu': 'Tapuya Çandiniyê',
    'propertiesForSale.sort.newest': 'Nûtirîn pêşî',
    'propertiesForSale.sort.price_low': 'Biha: Ji kêm bo bilind',
    'propertiesForSale.sort.price_high': 'Biha: Ji bilind bo kêm',
    'propertiesForSale.sort.size': 'Mezinî',
    'propertiesForSale.noResults': 'Ti milk nehatin dîtin',
    'propertiesForSale.tryAdjustingFilters': 'Hewl bidin fîlterên xwe rast bikin an li deverek din bigerin',

    // Cookie Policy
    'cookie.title': 'Siyaseta Cookie',
    'cookie.last_updated': 'Dawî hatiye nûkirin:',
    'cookie.section1.title': 'Beşa 1 - Rêziknameyên Giştî yên Derbarê Bicihkirina Teknolojiyên Şopandinê',
    'cookie.section1.p1': 'Ploqs AB, ji vir û pê ve wekî "Pêşkêşkerê Xizmetê" tê binavkirin, ligel hevkar û peymankerên jêrîn ku ji hêla Pêşkêşkerê Xizmetê ve hatine wezîfedarkirin, formên cûda yên mekanîzmayên şopandina dîjîtal bicih dike û bikar tîne, di nav de lê ne bi sînor ji bo cookie, webbeacon, pixeltag û çareyên teknolojîk ên din ên mîna wan (bi hev re wekî "Teknolojiyên Şopandinê" têne binavkirin) li ser platformên dîjîtal, malperên û sepanên mobîl ên Pêşkêşkerê Xizmetê (bi hev re wekî "Kanalên Dîjîtal ên Pêşkêşkerê Xizmetê" têne binavkirin). Ev siyaseta ji bo birêvebirina Teknolojiyên Şopandinê armancên ku ji bo wan û rêbazên ku Pêşkêşkerê Xizmetê teknolojiyên navborî bi kar tîne diyar dike.',
    'cookie.section1.p2': 'Teknolojiyên Şopandinê bi piranî ji pelên daneya tekstî yên piçûk pêk tên ku li ser alavên termînalê yên bikarhêner an bernameya gerokê têne danîn da ku agahdariya derbarê danûstendina bikarhêner bi platformên dîjîtal û sepanên mobîl û navîgasyona li ser wan re kom bikin.',
    'cookie.section1.h1': 'Hilberîna Daneyên Kesane û Nerîtên Parastina Daneyan',
    'cookie.section1.p3': 'Agahdariya ku bi riya bicihkirina Teknolojiyên Şopandinê tê berhevkirin di piraniya rewşan de dane ye ku, rasterast an nerasterast û bi daneyên din ên berdest re, dikare were bikar anîn da ku kesan nas bike, di nav de lê ne bi sînor ji navnîşanên IP, nasnameyên cookie yên yekane, nasnameyên reklamê (IDFA), nasnameyên amûrê û metadataya cûda ku cûdakirina alavên termînalê yên taybetî gengaz dike.',
    'cookie.section1.h2': 'Vebijarkên Bikarhêner',
    'cookie.section1.p4': 'Bikarhêner mafê xwe heye ku di her demê de razîbûnên berê yên hatine dayîn bi navîgasyona bo vebijarkên birêvebirina razîbûnê yên ku bi navgîniya navbeynkariya dîjîtal a Pêşkêşkerê Xizmetê têne peyda kirin venêre.',
    'cookie.section1.h3': 'Domdarî û Demên Hilanînê yên Teknolojiyên Şopandinê',
    'cookie.section1.p5': 'Teknolojiyên Şopandinê li gorî armanc û fonksiyona her teknolojiyê domdariya cûda nîşan didin. Teknolojiyên Şopandinê yên li ser danişînê hatine damezrandin dema ku bikarhêner bi platforma dîjîtal ve girêdayî ye dest pê dikin û herî dereng dema girtina geroka bikarhêner têne qedandin.',
    'cookie.section2.title': 'Beşa 2 - Teknolojiyên Şopandinê yên Pêwist bo Pêşkêşkirina Xizmetê',
    'cookie.section2.p1': 'Teknolojiyên Şopandinê yên Pêwist wateya wan bicihkirinên teknolojîk in ku ji bo Pêşkêşkerê Xizmetê pêwist in da ku fonksiyonên bingehîn û girîng ji bikarhêner re peyda bike.',
    'cookie.section2.legal': 'Bingeha Qanûnî ji bo Hilberînê:',
    'cookie.section2.legal_value': 'Berjewendiya rewa li gorî madeya 6.1(f) GDPR',
    'cookie.section2.storage': 'Dema Hilanînê:',
    'cookie.section2.storage_value': 'Heta bîst û çar (24) meh ji bo Teknolojiyên Şopandinê yên domdar; Teknolojiyên Şopandinê yên li ser danişînê yekser dema girtina geroka têne qedandin.',
    'cookie.section3.title': 'Beşa 3 - Teknolojiyên Şopandinê ji bo Armancên Analîtîk',
    'cookie.section3.p1': 'Pêşkêşkerê Xizmetê girîngiya mezin dide şiyana jimartina pîvana serdanan, tomarkirina daneyên danûstendinê li ser platforma dîjîtal û di sepanên mobîl de, û bi awayek din analîzkirina ka xizmetên dîjîtal ên Pêşkêşkerê Xizmetê çawa ji hêla bikarhêneran ve têne bikar anîn.',
    'cookie.section3.legal_value': 'Razîbûn li gorî madeya 6.1(a) GDPR',
    'cookie.section3.storage_value': 'Heta bîst û şeş (26) meh',
    'cookie.section4.title': 'Beşa 4 - Teknolojiyên Şopandinê ji bo Armancên Kesanekirin',
    'cookie.section4.p1': 'Teknolojiyên Şopandinê yên ku ji bo armancên kesanekirinê hatine bicihkirin armanc dikin ku ezmûna bikarhêner li ser platformên dîjîtal ên Pêşkêşkerê Xizmetê bi peydakirina naverokê bi têkildariya kesane ya zêdekirî ji bo bikarhênerê takekesî adapte bikin.',
    'cookie.section5.title': 'Beşa 5 - Teknolojiyên Şopandinê ji bo Armancên Kirrûbirrê',
    'cookie.section5.p1': 'Pêşkêşkerê Xizmetê Teknolojiyên Şopandinê ji bo armancên kirrûbirrê bicih dike, bi şertê ku bikarhêner razîbûna pêwist daye, ji bo gengaz kirina pêşandana naveroka reklamê ya têkildar û reklamên xanîyan bi têkildariya zêdekirî ji bo bikarhêner li ser bingeha profîlek reklamê ya avakirî.',
    'cookie.section5.p2': 'Bi bicihkirina Teknolojiyên Şopandinê, Pêşkêşkerê Xizmetê di heman demê de piştrast dike ku pêşandanên reklamê bi hejmara demjimêrên berê hatine diyarkirin ji bo her bikarhêner têne sînorkirin, û bandorkeriya reklamê tê pîvandin û analîzkirin.',
    'cookie.section5.p3': 'Divê were zanîn ku eger bikarhêner hilbijêre ku Teknolojiyên Şopandinê ji bo armancên kirrûbirrê neçalak bike, naveroka reklamê dê hîn jî li ser platformên dîjîtal ên Pêşkêşkerê Xizmetê ji bikarhêner re were pêşandan, lê bêyî kesanekirin.',
    'cookie.section5.storage_value': 'Heta bîst û çar (24) meh',
    'cookie.section6.title': 'Beşa 6 - Veguheztina Daneyên Navneteweyî bo Welatên Sêyem',
    'cookie.section6.p1': 'Di amûra birêvebirina razîbûnê ya Pêşkêşkerê Xizmetê de, agahdarî di derbarê Teknolojiyên Şopandinê û hevkaran de ku daneyên kesane yên bikarhêner di hundur de hilberandin, an daneyên kesane yên bikarhêner veguhezînin nav dadweriyên derveyî Yekîtiya Ewropî û Herêma Aborî ya Ewropî (bi navê "welatên sêyem") tê peyda kirin.',
    'cookie.section7.title': 'Beşa 7 - Rêwerzên ji bo Rakirina Teknolojiyên Şopandinê',
    'cookie.section7.p1': 'Bikarhêner vebijarka xwe heye ku Teknolojiyên Şopandinê ji alavên termînalê yên xwe an amûra mobîl rake û mîhengên hilanîna daneyên herêmî bi navgîniya navbeynkariya mîhengên gerokê saz bike.',
    'cookie.section7.p2': 'Ji bo rêwerzên berfireh ên derbarê birêvebirina û rakirina Teknolojiyên Şopandinê, referans bi vebijarka alîkariyê ya di geroka bikarhêner de an girêdanên hîperlînk ên jêrîn ji bo her sepana gerokê tê dayîn:',
    
    // Auth
    'auth.loading': 'Tê barkirin...',
    'auth.login': 'Têketin',
    'auth.create_account': 'Hesab çêke',
    'auth.login_subtitle': 'Bi hilbijartina yek ji vebijarkên jêrîn têkeve.',
    'auth.signup_subtitle': 'Hesabek nû çêke da ku dest pê bikî.',
    'auth.continue_apple': 'Bi Apple berdewam bike',
    'auth.continue_google': 'Bi Google berdewam bike',
    'auth.or': 'an',
    'auth.email': 'E-name',
    'auth.email_placeholder': 'E-nameyê binivîse',
    'auth.password': 'Şîfre',
    'auth.password_placeholder': 'Şîfreyê binivîse',
    'auth.account_type': 'Cureya hesabê',
    'auth.user_role': 'Bikarhêner - Ez dixwazim xanî bikirrim/kirê bikim',
    'auth.agent_role': 'Broker - Ez dixwazim xanîyan bifroşim/kirê bikim',
    'auth.continue': 'Berdewam bike',
    'auth.no_account': 'Hesabek tune? Hesab çêke',
    'auth.have_account': 'Hesabek heye? Têkeve',
    'auth.login_failed': 'Têketin têk çû',
    'auth.invalid_credentials': 'E-name an şîfreya çewt',
    'auth.welcome_back': 'Bi xêr hatî!',
    'auth.logged_in': 'Tu têketî.',
    'auth.signup_failed': 'Tomarkirin têk çû',
    'auth.account_created': 'Hesab hat çêkirin!',
    'auth.check_email': 'E-nameya xwe kontrol bike da ku hesabê xwe piştrast bikî.',
    'auth.error': 'Xeletiyek çêbû',
    'auth.try_again': 'Ji kerema xwe paşê dîsa biceribîne.',
    'auth.broker_interest_title': 'Tu broker î?',
    'auth.broker_interest_description': 'Heke tu dixwazî li Aqarnet xanîyan tomar bikî, daxwaznameyekê ji me re bişîne û em ê bi te re têkilî daynin.',
    'auth.broker_contact_us': 'Bi me re têkilî dayne',
    'auth.broker_email_subject': 'Daxwazname bo Tomarkirina Broker',
    'auth.broker_email_body': 'Silav,\n\nEz dixwazim wekî broker li Aqarnet tomar bibim.\n\nNav:\nNavê Ajaneyê:\nTelefon:\nBajar:\n\nSpas!',
    
    // Favorites Page
    'favorites.title': 'Bijareyan',
    'favorites.saved_properties': 'xanî hatine tomarkirin',
    'favorites.login_required': 'Ji bo dîtina bijareyan têkeve',
    'favorites.login_description': 'Xanîyên ku tu dihez tomar bike da ku paşê bi hêsanî wan bibînî.',
    'favorites.loading': 'Bijareyên te tên barkirin...',
    'favorites.no_favorites': 'Hîn bijareyên tune',
    'favorites.start_saving': 'Dest bi tomarkirina xanîyan bike bi tikandina dil li ser reklamên ku tu dihez.',
    'favorites.search_properties': 'Li xanîyan bigere',
    
    // Profile Page
    'profile.title': 'Profîla min',
    'profile.subtitle': 'Agahdariya kesane û mîhengên xwe birêve bibe',
    'profile.loading': 'Profîl tê barkirin...',
    'profile.fetch_error': 'Xeletî di anîna profîlê de',
    'profile.fetch_error_description': 'Nehat agahdariya profîlê anîn.',
    'profile.unnamed_user': 'Bikarhênerê bênav',
    'profile.personal_info': 'Agahdariya kesane',
    'profile.update_info': 'Agahdariya kesane ya xwe nûve bike',
    'profile.email': 'E-name',
    'profile.email_readonly': 'E-name nayê guhertin',
    'profile.display_name': 'Navê nîşanê',
    'profile.display_name_placeholder': 'Navê te',
    'profile.phone': 'Hejmara têlefonê',
    'profile.phone_placeholder': '070-123 45 67',
    'profile.save_changes': 'Guhertinan tomar bike',
    'profile.saving': 'Tê tomarkirin...',
    'profile.reset': 'Vegerîne',
    'profile.updated': 'Profîl hate nûvekirin',
    'profile.updated_description': 'Profîla te hate tomarkirin.',
    'profile.update_error': 'Xeletî di nûvekirinê de',
    'profile.update_error_description': 'Nehat guhertin tomarkirin.',
    'profile.active_listings': 'Reklamên çalak',
    'profile.active_listings_description': 'Reklamên xanîyên te yên weşandî ji bo firotin',
    'profile.view_all': 'Hemû nîşan bide',
    'profile.loading_listings': 'Reklam tên barkirin...',
    'profile.no_active_listings': 'Hîn reklamên çalak tune',
    'profile.more_active_listings': 'Û {count} reklamên çalak ên din',
    'profile.sold_properties': 'Xanîyên firotî',
    'profile.sold_properties_description': 'Xanîyên te yên firotî',
    'profile.no_sold_properties': 'Hîn xanîyên firotî tune',
    'profile.more_sold_properties': 'Û {count} xanîyên firotî yên din',
    'profile.listing_fetch_error': 'Xeletî di anîna reklaman de',
    'profile.listing_fetch_error_description': 'Nehat reklamên te anîn.',
    'profile.account_settings': 'Mîhengên hesabê',
    'profile.account_settings_description': 'Hesabê xwe û ewlehiyê birêve bibe',
    'profile.change_password': 'Şîfreyê biguherîne',
    'profile.change_password_description': 'Şîfreya xwe nûve bike ji bo têketina ewletir',
    'profile.change': 'Biguherîne',
    'profile.two_factor': 'Rastkirina du-faktorî',
    'profile.two_factor_description': 'Ji bo hesabê xwe ewlehiya zêde zêde bike',
    'profile.enable': 'Çalak bike',
    'profile.delete_account': 'Hesabê jê bibe',
    'profile.delete_account_description': 'Jêbirina herheyî ya hesabê te û hemû dane',
    'profile.delete': 'Jê bibe',
    'profile.logout': 'Derkeve',
    
    // Profile Email Notifications
    'profile.email_notifications': 'Agahdariyên E-nameyê',
    'profile.email_notifications_description': 'Hilbijêre kîjan agahdariyên e-nameyê tu dixwazî werbigirî',
    'profile.notif_property_alerts': 'Hişyariyên Milkan',
    'profile.notif_property_alerts_desc': 'Agahdarî bistîne li ser milkên nû yên li gorî lêgerînên te yên tomarkirî',
    'profile.notif_saved_search_alerts': 'Nûvekirinên Lêgerîna Tomarkirî',
    'profile.notif_saved_search_alerts_desc': 'Nûvekirinan bistîne dema milkên nû li gorî pîvanên te ne',
    'profile.notif_newsletter': 'Nûçename',
    'profile.notif_newsletter_desc': 'Nûçenameya me ya heftane bi têgihiştinên bazarê bistîne',
    'profile.notif_viewing_confirmations': 'Pejirandinên Dîtinê',
    'profile.notif_viewing_confirmations_desc': 'E-nameyên pejirandinê ji bo dîtinên plansazkirî bistîne',
    'profile.save_preferences': 'Tercîhan tomar bike',
    'profile.notifications_saved': 'Tercîhên agahdariyê hatin tomarkirin',
    'profile.notifications_save_error': 'Nekarî tercîhên agahdariyê tomar bike',
    
    // Not Found Page
    'notfound.title': '404',
    'notfound.message': 'Xeletiyek! Rûpel nehate dîtin',
    'notfound.back_home': 'Vegere malê',
    
    // Property Detail Page
    'property.back_to_listings': 'Vegere reklaman',
    'property.not_found': 'Xanî nehate dîtin',
    'property.link_copied': 'Lînk hate kopîkirin',
    'property.link_copied_description': 'Lînka xanîyê hate kopîkirin',
    'property.description': 'Danasîn',
    'property.features': 'Taybetmendî',
    'property.details': 'Hûrgiliyên xanîyê',
    'property.rooms_label': 'Jûr',
    'property.size_label': 'Mezinahî',
    'property.plot_size_label': 'Mezinahiya axê',
    'property.monthly_fee_label': 'Kirê ya mehane',
    'property.contact_agent': 'Bi brokerê re têkilî bike',
    'property.send_message': 'Peyam bişîne',
    'property.schedule_viewing': 'Dîtinê rezerv bike',
    'property.section.details': 'Hûragahî',
    'property.section.amenities': 'Taybetmendî',
    'property.section.overview': 'Lênerîn',
    'property.section.payment_plan': 'Plana Dayînê',
    'property.section.floor_plans': 'Nexşeyên Qat',
    'property.section.view_map': 'Nexşeyê Bibîne',
    'property.copy_link': 'Lînkê kopî bike',
    'property.share': 'Parve bike',
    'property.share_text': 'Vê xanîyê bibîne',
    'property.posted_on': 'Dîroka Weşanê',
    'property.type': 'Cure',
    'property.status': 'Rewş',
    'property.available': 'Amade',
    'property.no_amenities': 'Ji bo vê xanîyê tu taybetmendî nehatine tomarkirin',
    'property.no_description': 'Daxuyanî ne amade ye',
    'property.location': 'Cih',
    'property.payment_plan_info': 'Ji bo plana dayînê bi brokerê re têkilî bike',
    'property.contact_for_payment_plan': 'Ji bo Plana Dayînê Têkilî',
    'property.no_floor_plans': 'Nexşeyên qat ne amade ne',
    'property.request_floor_plans': 'Nexşeyên Qat Bixwaze',
    'property.no_map_location': 'Cih li ser nexşeyê ne amade ye',
    
    // News Page
    'news.subtitle': 'Bi nûçeyên herî dawî yên xanîyan, nêrînên bazarê û rêberiyê re bimîne',
    'news.no_articles': 'Hîn gotarên nûçeyan tune',
    'news.check_back': 'Zûtirê vegere ji bo nûçeyên herî dawî yên xanîyan û nêrînên bazarê.',
    'news.search_placeholder': 'Li gotaran bigere...',
    'news.showing_filtered': '{shown} ji {total} gotaran li vê rûpelê tê xuyang kirin',
    'news.clear_filters': 'Fîltreyan paqij bike',
    'news.no_matching': 'Gotarên lihev nayên',
    'news.adjust_filters': 'Hewl bide lêgerîn an fîltreyan rast bikî da ku tiştê ku tu lê digerî bibînî.',
    'news.publisher': 'Weşanger',
    'news.page_info': 'Rûpel {current} ji {total} • {count} gotarên tevahî',
    'news.category.all': 'Hemû',
    'news.category.market_news': 'Nûçeyên Bazarê',
    'news.category.property_guides': 'Rêberên Xaniyan',
    'news.category.investment_tips': 'Şîretên Veberhênanê',
    'news.category.industry_updates': 'Nûvekirinên Pîşesaziyê',
    'news.category.announcements': 'Daxuyanî',
    'news.category.expert_advice': 'Şîretên Pisporan',
    
    // Filters/Sorting
    'filters.any': 'Hemû',
    'filters.newest_first': 'Herî nû yên pêşîn',
    'filters.price_low_high': 'Nirx: Kêm bo Zêde',
    'filters.price_high_low': 'Nirx: Zêde bo Kêm',
    'filters.size': 'Mezinahî',
    'filters.searching_for': 'Li digere',
    'filters.search_areas': 'Li herêm, bajar bigere...',
    
    // Toast Messages
    'toast.error': 'Çewtî',
    'toast.success': 'Serketî',
    'toast.auth_required': 'Piştrastkirin pêwîst e',
    'toast.sign_in_required': 'Ji kerema xwe têkeve da ku berdewam bikî',
    'toast.sign_in_favorites': 'Ji kerema xwe têkeve da ku bijarte lê zêde bikî',
    'toast.sign_in_properties': 'Ji kerema xwe têkeve da ku xanî lê zêde bikî',
    'toast.properties_load_error': 'Xanî nehat barkirin',
    'toast.property_added': 'Xanî bi serkeftî hat zêdekirin',
    'toast.property_add_error': 'Xanî nehat zêdekirin',
    'toast.property_updated': 'Xanî bi serkeftî hat nûkirin',
    'toast.property_update_error': 'Xanî nehat nûkirin',
    'toast.property_deleted': 'Xanî bi serkeftî hat jêbirin',
    'toast.property_delete_error': 'Xanî nehat jêbirin',
    'toast.property_marked_sold': 'Xanî bi serkeftî wekî firotî hat nîşankirin',
    'toast.property_sold_error': 'Xanî nehat nîşankirin wekî firotî',
    'toast.invalid_price': 'Ji kerema xwe nirxek firotinê yê derbasdar binivîse',
    'toast.favorites_load_error': 'Bijarte nehat barkirin',
    'toast.added_to_favorites': 'Li bijarte hat zêdekirin',
    'toast.add_favorites_error': 'Li bijarte nehat zêdekirin',
    'toast.removed_from_favorites': 'Ji bijarte hat jêbirin',
    'toast.remove_favorites_error': 'Ji bijarte nehat jêbirin',
    'toast.search_saved': 'Lêgerîn hat tomarkirin',
    'toast.search_saved_desc': 'bi agahdariyên e-peyamê hat tomarkirin',
    'toast.search_save_error': 'Lêgerîn nehat tomarkirin',
    'toast.valuation_complete': 'Nirxandin qediya',
    'toast.valuation_estimated': 'Nirxê texmînkirî',
    'toast.valuation_error': 'Nirxandin nehat girtin',
    'toast.message_sent': 'Peyam hat şandin',
    'toast.agent_contact_desc': 'Broker dê di demek nêz de bi te re têkilî bike',
    'toast.message_error': 'Peyam nehat şandin',
    'toast.viewing_booked': 'Serdan hat rezervekirin',
    'toast.viewing_confirmed': 'Serdana te hat rezervekirin û piştrast bi e-peyamê hat şandin',
    'toast.viewing_error': 'Serdan nehat rezervekirin',
    'toast.image_analysis_complete': 'Analîza wêneyê qediya',
    'toast.image_analysis_desc': 'Analîza AI ya wêneyê qediya',
    'toast.image_analysis_error': 'Wêne nehat analîzkirin',
    'toast.logged_out': 'Derket',
    'toast.welcome_back': 'Her dem bi xêr bên!',
    
    // Recent searches
    'search.properties_label': 'Xanî',
    'search.from': 'ji',
    'search.up_to': 'heta',
    
    // News Management
    'news_mgmt.title': 'Rêvebiriya Nûçeyan',
    'news_mgmt.subtitle': 'Gotarên nûçeyan biafirîne û bi rê ve bibe',
    'news_mgmt.create_article': 'Gotar Biafirîne',
    'news_mgmt.edit_article': 'Gotarê Biguherîne',
    'news_mgmt.create_new': 'Gotarek Nû Biafirîne',
    'news_mgmt.all_articles': 'Hemû Gotar',
    'news_mgmt.no_articles': 'Hîn gotar tune. Ya yekem biafirîne!',
    'news_mgmt.delete_article': 'Gotarê Jêbibe',
    'news_mgmt.delete_confirm': 'Ma tu bawer î ku dixwazî vê gotarê jê bibî? Ev kirin nayê paşvekirin.',
    'news_mgmt.title_label': 'Sernav',
    'news_mgmt.title_placeholder': 'Sernavê gotarê binivîse',
    'news_mgmt.category_label': 'Kategorî',
    'news_mgmt.category_placeholder': 'Mînak: Nûçeyên Bazarê, Rêber, Şîret',
    'news_mgmt.image_url': 'URL-ya Wêneyê',
    'news_mgmt.content': 'Naverok',
    'news_mgmt.content_placeholder': 'Naveroka gotarê binivîse',
    'news_mgmt.published': 'Hatî weşandin',
    'news_mgmt.draft': 'Reşnivîs',
    'news_mgmt.update_article': 'Gotarê Nûke',
    'news_mgmt.required_fields': 'Sernav û kategorî pêwîst in',
    'news_mgmt.article_updated': 'Gotara nûçeyan bi serkeftî hat nûkirin',
    'news_mgmt.article_created': 'Gotara nûçeyan bi serkeftî hat afirandin',
    'news_mgmt.article_save_error': 'Gotara nûçeyan nehat tomarkirin',
    'news_mgmt.article_deleted': 'Gotara nûçeyan bi serkeftî hat jêbirin',
    'news_mgmt.article_delete_error': 'Gotara nûçeyan nehat jêbirin',
    'news_mgmt.access_denied': 'Gihîştin Hat Redkirin',
    'news_mgmt.access_denied_desc': 'Tu destûr nayê ku bigihîjî vê rûpelê. Tenê weşanger û rêveber dikarin nûçeyan bi rê ve bibin.',
    
    // Badges & Labels
    'badge.new': 'Nû',
    'badge.sold': 'Firotî',
    'badge.featured': 'Taybet',
    'badge.reduced': 'Nirx Kêmkirî',
    'badge.new_construction': 'Avakirina Nû',
    
    // Confirmation Messages
    'confirm.delete': 'Jêbibe',
    'confirm.cancel': 'Betal bike',
    'confirm.yes': 'Erê',
    'confirm.no': 'Na',
    'confirm.are_you_sure': 'Ma tu bawer î?',
    'confirm.action_irreversible': 'Ev kirin nayê paşvekirin.',
    
    // Tooltips
    'tooltip.favorite': 'Li bijarte zêde bike',
    'tooltip.share': 'Xanî parve bike',
    'tooltip.edit': 'Xanî biguherîne',
    'tooltip.delete': 'Xanî jêbibe',
    'tooltip.contact': 'Bi broker re têkilî bike',
    'tooltip.schedule': 'Serdanê rezerve bike',
    'tooltip.map_view': 'Li ser nexşeyê bibîne',
    'tooltip.list_view': 'Dîmena lîsteyê',
    'tooltip.grid_view': 'Dîmena torê',
    
    // Property card
    'property.sold': 'FIROTÎ',
    'property.photos': 'wêne',
    'property.rooms_short': 'jûr',
    'property.plot': 'ax',
    'property.per_month': '/meh',
    'property.delete_title': 'Xanî jê bibî?',
    'property.delete_confirm': 'Tu piştrast î ku dixwazî {address} jê bibî? Ev çalakî nayê paşvegerandin.',
    'property.deleted': 'Xanî hate jêbirin',
    'property.deleted_desc': 'Xanî hate rakirin',
    'property.delete_error': 'Nehat xanî jêbirin',
    
    // Saved Searches
    'loading.saved_searches': 'Lêgerînên tomarkirî tên barkirin...',
    'saved_searches.title': 'Lêgerînên tomarkirî',
    'saved_searches.empty': 'Hîn lêgerînên tomarkirî tune',
    'saved_searches.description': 'Lêgerînekê tomar bike da ku dema ku xanîyên lihevkirî têne zêdekirin agahdarî bistîne',
    'saved_searches.created': 'Hat çêkirin',
    'saved_searches.alerts_on': 'Agahdarî vekirî',
    'saved_searches.alerts_off': 'Agahdarî girtî',
    'saved_searches.email_enabled': 'Agahdariyên e-peyamê vekirî',
    'saved_searches.email_disabled': 'Agahdariyên e-peyamê girtî',
    'saved_searches.settings_updated': 'Mîheng hate nûvekirin',
    'saved_searches.removed': 'Lêgerîn hate rakirin',
    'saved_searches.removed_desc': 'Lêgerîna tomarkirî hate rakirin',
    'saved_searches.error': 'Xeletî',
    'saved_searches.update_error': 'Nehat mîheng nûvekirin',
    'saved_searches.delete_error': 'Nehat lêgerîn jêbirin',
    'saved_searches.all_properties': 'Hemû xanî',
    
    // Rental properties
    'rental.for_sale': 'Ji bo Firotin',
    'rental.for_rent': 'Ji bo Kirê',
    'rental.listing_type': 'Cureya Reklamê',
    'rental.rental_details': 'Hûrgelên Kirê',
    'rental.rental_period': 'Dema Kirê',
    'rental.rental_period_hint': 'Çiqas caran kirê tê dayîn',
    'rental.daily': 'Rojane',
    'rental.weekly': 'Heftane',
    'rental.monthly': 'Mehane',
    'rental.yearly': 'Salane',
    'rental.per_daily': '/roj',
    'rental.per_weekly': '/hefte',
    'rental.per_monthly': '/meh',
    'rental.per_yearly': '/sal',
    'rental.deposit_amount': 'Mîqdara Depozît (USD)',
    'rental.minimum_lease': 'Kirêya Herî Kêm (meh)',
    'rental.months': 'meh',
    'rental.available_from': 'Ji',
    'rental.furnished': 'Bi Mobîlya',
    'rental.utilities_included': 'Xizmetên Tê de',
    'rental.pets_allowed': 'Heywanên Kedî Destûr e',
    
    // Admin Pages
    'admin.loading': 'Tê barkirin...',
    'admin.access_denied': 'Destûr hatî redkirin. Destûra rêveberiyê pêwîst e.',
    
    // Admin Notification Settings
    'admin.notifications.title': 'Mîhengên Agahdariyê',
    'admin.notifications.description': 'Mîhengên agahdariyê ji bo hemû celebên bikarhêneran birêve bibin. Li gorî hewcedariyê çalak bikin, neçalak bikin an veşêrin.',
    'admin.notifications.fetch_error': 'Nekarî mîhengên agahdariyê bistîne',
    'admin.notifications.update_success': 'Mîhenga agahdariyê hate nûve kirin',
    'admin.notifications.update_error': 'Nekarî mîhenga agahdariyê nûve bike',
    'admin.notifications.role_description': 'Agahdariyên ji bo bikarhênerên {role} birêve bibin',
    'admin.notifications.hidden': 'Veşartî',
    'admin.notifications.disabled': 'Neçalak',
    'admin.notifications.active': 'Çalak',
    'admin.notifications.hide': 'Veşêre',
    'admin.notifications.roles.user': 'Bikarhêner',
    'admin.notifications.roles.maklare': 'Brokerên',
    'admin.notifications.roles.agency': 'Ajans',
    'admin.notifications.roles.admin': 'Rêveber',
    'admin.notifications.roles.publisher': 'Weşanger',
    'admin.notifications.types.new_viewing_request': 'Daxwaza Dîtinê ya Nû',
    'admin.notifications.types.new_client_message': 'Peyama Xerîdarê Nû',
    'admin.notifications.types.property_alerts': 'Hişyariyên Xanî',
    'admin.notifications.types.weekly_summary': 'Kurteya Heftane',
    'admin.notifications.types.broker_property_added': 'Broker Xanî Zêde Kir',
    'admin.notifications.types.broker_viewing_request': 'Daxwaza Dîtinê ya Broker',
    'admin.notifications.types.broker_client_message': 'Peyama Xerîdarê Broker',
    'admin.notifications.types.broker_joined': 'Broker Tev Bû',
    'admin.notifications.types.weekly_agency_summary': 'Kurteya Heftane ya Ajansê',
    'admin.notifications.types.saved_search_alerts': 'Hişyariyên Lêgerîna Tomarkirî',
    'admin.notifications.types.favorite_property_updates': 'Nûvekirinên Xaniyên Bijarte',
    'admin.notifications.types.newsletter': 'Nûçename',
    'admin.notifications.types.system_alerts': 'Hişyariyên Pergalê',
    'admin.notifications.types.new_user_signup': 'Tomarkirina Bikarhênerê Nû',
    'admin.notifications.types.article_published': 'Gotar Hate Weşandin',

    // Admin Settings
    'admin.settings': 'Mîhengên Rêvebir',
    'admin.messaging_settings': 'Mîhengên Peyaman',
    'admin.messaging_settings_description': 'Kontrolkirina taybetmendiyên peyam û bangan ji bo hemû bikarhêneran',
    'admin.messaging': 'Peyamdan',
    'admin.messaging_description': 'Destûrê bide bikarhêneran ku peyaman bişînin',
    'admin.voice_calls': 'Bangên Dengî',
    'admin.voice_calls_description': 'Destûrê bide bikarhêneran ku bangên dengî bikin',
    'admin.video_calls': 'Bangên Vîdyoyê',
    'admin.video_calls_description': 'Destûrê bide bikarhêneran ku bangên vîdyoyê bikin',
    'admin.screen_sharing': 'Parvekirina Ekranê',
    'admin.screen_sharing_description': 'Destûrê bide bikarhêneran ku di dema bangan de ekrana xwe parve bikin',

    // Location Manager
    'location_mgr.title': 'Rêveberê Cihan',
    'location_mgr.current_locations': 'Cihên niha',
    'location_mgr.locations_in_db': '{count} cih di databasa de',
    'location_mgr.refresh': 'Nûve bike',
    'location_mgr.populate_title': 'Cihanê dagir bike',
    'location_mgr.populate_desc': 'Hemû bajar û deverên Iraqê ji Google Places API bîne û li databasa tomar bike. Ev ê hemû cihên heyî bike.',
    'location_mgr.populating': 'Tê dagirkirin...',
    'location_mgr.populate_btn': 'Ji Google Places dagir bike',
    'location_mgr.success': 'Serkeftî',
    'location_mgr.success_msg': 'Cih bi serkeftî hatin dagirkirin',
    'location_mgr.error': 'Çewtî',
    'location_mgr.error_msg': 'Dagirkirina cihanê biserneket. Ji bo hûragahiyan konsolê kontrol bike.',
    
    // Map Configuration Manager
    'map_config.title': 'Rêveberê Mîhengên Nexşeyê',
    'map_config.subtitle': 'Mîhengên nexşeya daîmî birêve bibe da ku bikaranîna Google Maps API kêm bibe',
    'map_config.loading': 'Mîhengên nexşeyê tên barkirin...',
    'map_config.create': 'Mîheng çêke',
    'map_config.create_title': 'Mîhenga Nexşeyê Çêke',
    'map_config.create_desc': 'Mîhengek nexşeya daîmî ya nû çêke ku li şûna bangkirina Google Maps API bi gelek caran were bikaranîn.',
    'map_config.edit_title': 'Mîhenga Nexşeyê Biguherîne',
    'map_config.edit_desc': 'Mîhenga nexşeya xwe ya daîmî nûve bike.',
    'map_config.name': 'Navê mîhengê',
    'map_config.name_placeholder': 'mînak: Nexşeya Xaniyên Iraqê',
    'map_config.description': 'Danasîn (ne mecbûrî)',
    'map_config.description_placeholder': 'Danasîna kurt ya vê mîhengê',
    'map_config.map_data': 'Mîhenga Nexşeyê (JSON)',
    'map_config.map_data_placeholder': 'Mîhenga nexşeyê bi forma JSON',
    'map_config.cancel': 'Betal',
    'map_config.update': 'Mîhengê nûve bike',
    'map_config.no_configs': 'Mîhengên nexşeyê tune ne',
    'map_config.no_configs_desc': 'Ji bo dest bi rêvebirina nexşeyên xwe bi karî bike, mîhenga xwe ya yekem çêke.',
    'map_config.create_first': 'Mîhenga Yekem Çêke',
    'map_config.active': 'Çalak',
    'map_config.activate': 'Çalak bike',
    'map_config.edit': 'Biguherîne',
    'map_config.delete': 'Jê bibe',
    'map_config.delete_confirm': 'Tu piştrast î?',
    'map_config.delete_desc': 'Ev ê mîhenga nexşeyê "{name}" bi awayekî daîmî jê bibe. Ev çalakî nayê vegerandin.',
    'map_config.created': 'Hatî afirandin',
    'map_config.last_updated': 'Cara dawî nûve bû',
    'map_config.map_center': 'Navenda nexşeyê',
    'map_config.zoom_level': 'Asta zoom',
    'map_config.cities': 'Bajar',
    'map_config.cities_configured': '{count} bajar hatine mîheng kirin',
    'map_config.invalid_json': 'Forma JSON ya nederbasdar di daneyên nexşeyê de',
    
    // About Page
    'about.title': 'Derbarê Aqarnet',
    'about.subtitle': 'Hevkarê te yê pêbawer di bazara xanîyan a Iraqê de',
    'about.description': 'Aqarnet platforma pêşeng a xanîyan a Iraqê ye, ku kirêr, firoşkar û brokaran li seranserê welêt girêdide. Em lîsteyên xanîyan ên berfireh, nêrînên bazarê û amûrên profesyonel pêşkêş dikin.',
    'about.mission_title': 'Mîsyona me',
    'about.mission_text': 'Şoreşkirina bazara xanîyan a Iraqê bi peydakirina agahdariya xanîyan a şefaf, gihîştî û pêbawer ji hemûyan re.',
    'about.vision_title': 'Dîtina me',
    'about.vision_text': 'Bûna platforma xanîyan a herî pêbawer li Rojhilata Navîn, hêzkirina hemûyan da ku mala xewnên xwe bibînin.',
    'about.services_title': 'Xizmetên me',
    'about.services_text': 'Lîsteyên xanîyan, analîza bazarê, girêdanên brokaran, nirxandinên xanîyan, û nûçeyên xanîyan ên berfireh.',
    'about.team_title': 'Tîma me',
    'about.team_text': 'Tîmek dilsoz ji pisporên xanîyan, pisporên teknolojiyê û pisporên xizmeta xerîdar.',
    'about.company_info': 'Agahdariya pargîdaniyê',
    'about.based_in': 'Li Swêdê, ji bo Iraqê kar dike',
    
    // Contact Page
    'contact.title': 'Bi me re têkilî daynin',
    'contact.subtitle': 'Em li vir in ji bo alîkariyê. Ji bo her pirsê an fikarê bi me re têkilî daynin.',
    'contact.email_label': 'E-name',
    'contact.phone_label': 'Têlefon',
    'contact.phone_info': 'Zûtir',
    'contact.location_label': 'Cî',
    'contact.location_info': 'Swêd',
    'contact.form_title': 'Peyamek ji me re bişîne',
    'contact.form_description': 'Forma jêrîn dagirin û em ê di zûtirîn dem de bi we re têkilî daynin.',
    'contact.name_placeholder': 'Navê te',
    'contact.email_placeholder': 'te@email.com',
    'contact.subject': 'Mijar',
    'contact.subject_placeholder': 'Ev çi ye?',
    'contact.message_placeholder': 'Peyama te...',
    'contact.success': 'Peyam hat şandin! Em ê zû bi we re têkilî daynin.',
    
    // Press Page
    'press.title': 'Çapemenî û Medya',
    'press.subtitle': 'Nûçe û daxuyaniyên çapemeniyê yên herî dawî yên Aqarnet',
    'press.contact_title': 'Têkiliya çapemeniyê',
    'press.contact_description': 'Ji bo pirsên çapemeniyê, bi e-nameyê bi me re têkilî daynin.',
    'press.add_release': 'Daxuyaniya çapemeniyê zêde bike',
    'press.edit_release': 'Daxuyaniya çapemeniyê biguherîne',
    'press.new_release': 'Daxuyaniya çapemeniyê ya nû',
    'press.release_title': 'Sernav',
    'press.content': 'Naverok',
    'press.publish': 'Weşandin',
    'press.no_releases': 'Hîn daxuyaniyên çapemeniyê tune.',
    'press.delete_confirm_title': 'Daxuyaniya çapemeniyê jê bibe?',
    'press.delete_confirm_description': 'Ev çalakî nayê paşvekişandin.',
    
    // Investors Page
    'investors.title': 'Ji bo veberhêneran',
    'investors.subtitle': 'Bi me re beşdarî veguherandina bazara xanîyan a Iraqê bibin',
    'investors.growth_title': 'Potansiyela mezinbûnê',
    'investors.growth_text': 'Bazara xanîyan a Iraqê mezinbûna bilez bi derfetên veberhênanê yên girîng diceribîne.',
    'investors.market_title': 'Pêşengê bazarê',
    'investors.market_text': 'Aqarnet wekî platforma dîjîtal a pêşeng di sektora xanîyan a Iraqê de ye.',
    'investors.vision_title': 'Berfirehkirina herêmî',
    'investors.vision_text': 'Dîtina me ji Iraqê derbas dibe da ku bibe platforma pêşeng li herêma Rojhilata Navîn.',
    'investors.opportunity_title': 'Derfeta veberhênanê',
    'investors.opportunity_text': 'Aqarnet derfetek yekta pêşkêş dike ji bo veberhênanê di veguherandina dîjîtal a yek ji mezintirîn bazarên xanîyan ên derketî yên cîhanê de.',
    'investors.opportunity_text_2': 'Bi bingeha bikarhêneran a mezinbûyî û xizmetên berfirehkirî, em ji bo mezinbûna domdar û pêşengiya bazarê baş in.',
    'investors.contact_title': 'Bi me re têkilî daynin',
    'investors.contact_description': 'Ji bo derfetên veberhênanê eleqedar in? Ji bo bêtir fêrbûnê bi me re têkilî daynin.',
    'investors.company': 'Pargîdanî / Rêxistin',
    'investors.company_placeholder': 'Navê pargîdaniya te',
    'investors.message_placeholder': 'Ji me re li ser berjewendiyên veberhênanê yên xwe bibêjin...',
    'investors.success': 'Spas ji bo eleqeya we! Em ê zû bi we re têkilî daynin.',
    
    // Advertising Page
    'advertising.title': 'Reklam û Kirrûbirr',
    'advertising.subtitle': 'Bigihîjin bi hezaran kirêr û kirêkarên potansiyel li Iraqê',
    'advertising.targeted_title': 'Gihîştina armanc',
    'advertising.targeted_text': 'Bigihîjin bikarhênerên ku bi çalakî li xanîyan li herêm û rêzikên bihayê taybetî digerin.',
    'advertising.audience_title': 'Temaşevanên kalîte',
    'advertising.audience_text': 'Bi kirêr û kirêkarên cidî yên amade biryaran bidin ve girêbidin.',
    'advertising.analytics_title': 'Analîtîkên berfireh',
    'advertising.analytics_text': 'Performansa kampanyaya xwe bi analîtîk û nêrînên berfireh bişopînin.',
    'advertising.formats_title': 'Formatên pirjimar',
    'advertising.formats_text': 'Reklamên banner, lîsteyên taybet, naveroka sponsorkirî, û vebijarkên reklamê yên din.',
    'advertising.contact_title': 'Dest bi reklamê bikin',
    'advertising.contact_description': 'Ji bo nîqaşkirina derfetên reklamê bi me re têkilî daynin.',
    'advertising.company': 'Pargîdanî / Rêxistin',
    'advertising.company_placeholder': 'Navê pargîdaniya te',
    'advertising.budget': 'Rêjeya budceyê',
    'advertising.budget_placeholder': 'mînak: $500 - $1000/meh',
    'advertising.message_placeholder': 'Ji me re li ser armancên reklamê yên xwe bibêjin...',
    'advertising.success': 'Spas! Em ê li ser derfetên reklamê bi we re têkilî daynin.',
    
    // Jobs Page
    'jobs.title': 'Kariyerên li Aqarnet',
    'jobs.subtitle': 'Beşdarî tîma me bibin û alîkariya şêwandina pêşerojê ya xanîyan li Iraqê bikin',
    'jobs.add_job': 'Kar zêde bike',
    'jobs.edit_job': 'Karê biguherîne',
    'jobs.new_job': 'Karê nû',
    'jobs.job_title': 'Sernavê karê',
    'jobs.department': 'Beş',
    'jobs.location': 'Cî',
    'jobs.employment_type': 'Cureya kar',
    'jobs.full_time': 'Demjimêra tijî',
    'jobs.part_time': 'Demjimêra nîv',
    'jobs.contract': 'Peymana',
    'jobs.internship': 'Stajyerî',
    'jobs.description': 'Danasîn',
    'jobs.requirements': 'Hewcedarî',
    'jobs.benefits': 'Feyde',
    'jobs.publish': 'Weşandin',
    'jobs.no_jobs': 'Niha cihên vekirî tune.',
    'jobs.check_back': 'Ji bo derfetên nû paşê kontrol bikin.',
    'jobs.apply_now': 'Niha serlêdin',
    'jobs.apply_for': 'Ji bo serlêdin',
    'jobs.apply_description': 'Serlêdana xwe bi e-nameyê bişînin.',
    'jobs.application_sent': 'Serlêdan hat şandin! Bextê we!',
    'jobs.delete_confirm_title': 'Karê jê bibe?',
    'jobs.delete_confirm_description': 'Ev çalakî nayê paşvekişandin.',
    
    // Property Statistics Page
    'statistics.title': 'Statîstîkên xanîyan',
    'statistics.subtitle': 'Nêrînên bazarê û meylên li ser bingeha xanîyên firotî',
    'statistics.6_months': '6 mehên dawî',
    'statistics.12_months': '12 mehên dawî',
    'statistics.24_months': '24 mehên dawî',
    'statistics.36_months': '36 mehên dawî',
    'statistics.all_cities': 'Hemû bajar',
    'statistics.total_sold': 'Tevahiya firotî',
    'statistics.avg_price': 'Bihayê navîn',
    'statistics.avg_price_sqm': 'Bihayê navîn/m²',
    'statistics.price_change': 'Guhertina bihayê',
    'statistics.price_trends': 'Meylên bihayê',
    'statistics.price_trends_desc': 'Bihayên navîn ên xanîyan li ser demê',
    'statistics.sales_volume': 'Qebareya firotanê',
    'statistics.sales_volume_desc': 'Hejmara xanîyên firotî her meh',
    'statistics.properties_sold': 'Xanîyên firotî',
    'statistics.price_by_city': 'Biha/m² li gorî bajar',
    'statistics.price_by_city_desc': 'Berhevkirina bihayê navîn/m² li bajarên cûda',
    'statistics.price_per_sqm': 'Biha/m²',
    'statistics.type_distribution': 'Dabeşkirina cureya xanîyan',
    'statistics.type_distribution_desc': 'Dabeşkirina xanîyên firotî li gorî cure',
    'statistics.avg_days_market': 'Rojên navîn li bazarê',
    'statistics.days': 'roj',
    'statistics.price_ranges': 'Dabeşkirina navberên bihayê',
    'statistics.price_ranges_desc': 'Hejmara xanîyên firotî di her navbera bihayê de',
    'statistics.properties': 'Xanî',
    'statistics.year_over_year': 'Berhevkirina salane',
    'statistics.year_over_year_desc': 'Berhevkirina bihayên navîn û qebareya firotanê li ser salan',
    'statistics.all_types': 'Hemû cureyên xaniyan',
    'statistics.all_rooms': 'Hemû ode',
    'statistics.rooms': 'ode',
    'statistics.export': 'Eksport',
    'statistics.export_csv': 'CSV dakêşin',
    'statistics.export_json': 'JSON dakêşin',
    'statistics.share': 'Parve bikin',
    'statistics.link_copied': 'Link hat kopîkirin',
    'statistics.link_copied_desc': 'Lînka rapora amarê hat kopîkirin',
    'statistics.custom_date': 'Dîroka taybet',
    'statistics.use_preset': 'Pêşwext bikar bîne',
    'statistics.start_date': 'Dîroka destpêkê',
    'statistics.end_date': 'Dîroka dawî',
    'statistics.city_comparison': 'Berhevdana Bajaran',
    'statistics.city_comparison_desc': 'Statistîkên emlakê di navbera du bajaran de hev li hev bikin',
    'statistics.select_city_1': 'Bajarê yekem hilbijêre',
    'statistics.select_city_2': 'Bajarê duyem hilbijêre',
    'statistics.vs': 'li hember',
    'statistics.select_cities_to_compare': 'Ji bo berhevkirina statîstîkên wan du bajaran li jor hilbijêre',
    'statistics.avg_size': 'Mezinahiya Navîn',
    
    // Cookie Settings
    'cookie_settings.title': 'Mîhengên Cookie',
    'cookie_settings.description': 'Em cookie bikar tînin da ku ezmûna gerîna we baştir bikin. Hûn dikarin tercîhên cookie yên xwe li jêr xweş bikin.',
    'cookie_settings.necessary': 'Cookieyên Pêwîst',
    'cookie_settings.necessary_desc': 'Ev cookie ji bo xebata rast a malperê girîng in. Ew nikarin bên astengkirin.',
    'cookie_settings.functional': 'Cookieyên Fonksiyonel',
    'cookie_settings.functional_desc': 'Ev cookie taybetmendiyên kesane yên wekî tercîhên ziman û mîhengên hilanîn çalak dikin.',
    'cookie_settings.analytics': 'Cookieyên Analîtîk',
    'cookie_settings.analytics_desc': 'Ev cookie ji me re dibin alîkar ku em fêm bikin ka mêvan çawa bi malpera me re têkildar dibin da ku ezmûna bikarhêner baştir bikin.',
    'cookie_settings.marketing': 'Cookieyên Marketing',
    'cookie_settings.marketing_desc': 'Ev cookie ji bo nîşandana reklamên têkildar û şopandina bandoriya kampanyayên me têne bikar anîn.',
    'cookie_settings.accept_all': 'Hemûyan Bipejirîne',
    'cookie_settings.reject_all': 'Hemûyan Red Bike',
    'cookie_settings.save_preferences': 'Tercîhan Hilîne',
    'cookie_settings.saved': 'Tercîhên cookie hatin hilanîn',
    
    // Cookie Banner
    'cookie_banner.title': 'Em cookie bikar tînin',
    'cookie_banner.description': 'Em cookie bikar tînin da ku ezmûna gerîna we baştir bikin, naveroka kesane nîşan bidin û seyrûsefera me analîz bikin. Bi tikandina "Hemûyan Bipejirîne", hûn razî dibin ku em cookie bikar bînin.',
    'cookie_banner.accept_all': 'Hemûyan Bipejirîne',
    'cookie_banner.reject_all': 'Hemûyan Red Bike',
    'cookie_banner.customize': 'Xweş Bike',
    
    // Terms of Service
    'terms.title': 'Şertên Xizmetê',
    'terms.last_updated': 'Dawî hate nûvekirin:',
    'terms.section1.title': '1. Qebûlkirina Şertan',
    'terms.section1.p1': 'Bi bikaranîna Aqarnet, hûn dipejirînin ku bi van şertên xizmetê ve girêdayî ne. Ger hûn bi van şertan razî nebin, ji kerema xwe xizmetên me bikar neynin.',
    'terms.section1.p2': 'Em mafê xwe diparêzin ku van şertan her dem biguherînin. Bikaranîna berdewam piştî guherandinan wekî pejirandina şertên guhertî tê hesibandin.',
    'terms.section2.title': '2. Danasîna Xizmetê',
    'terms.section2.p1': 'Aqarnet platformek dîjîtal peyda dike ji bo rêzikên xaniyan, lêgerîna milk û pêwendiyên di navbera kiriyar, firotkar û nûneran de li Iraqê.',
    'terms.section2.p2': 'Xizmetên me lîsteyên milkan, fonksiyonên lêgerînê, lêgerînên tomarkirî, favorîtan û amûrên ragihandinê di navbera bikarhêner û nûneran de hene.',
    'terms.section3.title': '3. Berpirsiyariyên Bikarhêner',
    'terms.section3.p1': 'Wekî bikarhênerek Aqarnet, hûn qebûl dikin ku:',
    'terms.section3.li1': 'Di rêzik û profîla xwe de agahiyên rast û rastdar peyda bikin',
    'terms.section3.li2': 'Platformê ji bo çalakiyên xapînok an neqanûnî bikar neynin',
    'terms.section3.li3': 'Mafên milkiyeta rewşenbîrî yên kesên din rêz bikin',
    'terms.section3.li4': 'Di xebata rast a xizmetê de mudaxele nekin',
    'terms.section4.title': '4. Rêzikên Milkan',
    'terms.section4.p1': 'Bikarhênerên ku milkan dikin lîste berpirsiyar in ji bo rastiya rêzikên xwe. Aqarnet rastiya agahiyên milkan verast nake.',
    'terms.section4.p2': 'Em mafê xwe diparêzin ku her rêzikek ku şertên me binpê dike an wekî xapînok tê ragihandin jê bikin.',
    'terms.section5.title': '5. Milkiyeta Rewşenbîrî',
    'terms.section5.p1': 'Hemî naverok li ser Aqarnet, di nav de nivîs, grafîk, logo û nermalav, milkê Aqarnet an dabînkerên naveroka wê ye.',
    'terms.section5.p2': 'Hûn nikarin naveroka me bêyî destûra nivîskî ya eşkere dubare bikin, belav bikin an karên jê derketî biafirînin.',
    'terms.section6.title': '6. Sînorkirina Berpirsiyariyê',
    'terms.section6.p1': 'Aqarnet "wekî ku heye" bêyî garantiyan ji her celebî tê pêşkêş kirin. Em ne berpirsiyar in ji bo zirarên ku ji bikaranîna xizmetên me derdikevin.',
    'terms.section7.title': '7. Zagona Serdest',
    'terms.section7.p1': 'Ev şert ji hêla qanûnên Swêdê ve têne rêve kirin. Divê her nakokî li dadgehên Swêdê were çareser kirin.',
    'terms.section7.p2': 'Ger her bendek ji van şertan bêdawî were dîtin, bendên mayî bi hêz dimînin.',
    'terms.section8.title': '8. Agahiyên Têkiliyê',
    'terms.section8.p1': 'Ji bo pirsên li ser van şertên xizmetê, ji kerema xwe bi me re têkilî daynin:',
    'terms.section8.email': 'E-name',
    
    // Privacy Policy
    'privacy.title': 'Polîtîkaya Nepenîtiyê',
    'privacy.last_updated': 'Dawî hate nûvekirin:',
    'privacy.section1.title': '1. Destpêk',
    'privacy.section1.p1': 'Li Aqarnet, em soz didin ku nepenîtiya we û daneyên kesane biparêzin. Ev polîtîka rave dike ka em çawa agahiyên we berhev dikin, bikar tînin û diparêzin.',
    'privacy.section1.p2': 'Bi bikaranîna xizmetên me, hûn bi pratîkên daneyên ku di vê polîtîkayê de hatine diyar kirin razî dibin.',
    'privacy.section2.title': '2. Agahiyên Ku Em Berhev Dikin',
    'privacy.section2.p1': 'Em cûreyên agahiyên jêrîn berhev dikin:',
    'privacy.section2.li1': 'Agahiyên hesabê: nav, navnîşana e-nameyê, jimara têlefonê',
    'privacy.section2.li2': 'Daneyên lîsteya milkan: navnîşan, danasîn, wêne, biha',
    'privacy.section2.li3': 'Daneyên bikaranînê: dîroka lêgerînê, milkên tomarkirî, tevgerên gera',
    'privacy.section2.li4': 'Agahiyên amûrê: navnîşana IP, celebê gerokê, pergala xebitandinê',
    'privacy.section3.title': '3. Em Çawa Agahiyên We Bikar Tînin',
    'privacy.section3.p1': 'Em agahiyên we ji bo bikar tînin:',
    'privacy.section3.li1': 'Xizmetên xwe pêşkêş bikin û baştir bikin',
    'privacy.section3.li2': 'Ezmûna we û pêşniyaran kesane bikin',
    'privacy.section3.li3': 'Nîşanên li ser lêgerînên tomarkirî û lîsteyên nû bişînin',
    'privacy.section3.li4': 'Bi we re li ser hesabê we û pirsên we ragihandinê bikin',
    'privacy.section4.title': '4. Parvekirina Daneyan',
    'privacy.section4.p1': 'Em daneyên weya kesane nafiroşin. Dibe ku em agahiyan bi nûnerên milkan re parve bikin dema ku hûn pirsan dikin, û bi pêşkêşkerên xizmetê yên ku di karûbarên me de arîkariyê dikin.',
    'privacy.section4.p2': 'Dibe ku em agahiyan eşkere bikin dema ku qanûn hewce dike an ji bo parastina maf û ewlehiya xwe.',
    'privacy.section5.title': '5. Mafên We',
    'privacy.section5.p1': 'Li ser daneyên xwe mafên jêrîn hene:',
    'privacy.section5.li1': 'Gihîştin: Kopiyeke daneyên xwe yên kesane bixwazin',
    'privacy.section5.li2': 'Serast kirin: Agahiyên nerast nûve bikin an serast bikin',
    'privacy.section5.li3': 'Jêbirin: Jêbirina hesab û daneyên xwe bixwazin',
    'privacy.section5.li4': 'Veguheztin: Daneyên xwe bi formatek veguhêzbar bistînin',
    'privacy.section5.li5': 'Nerazîbûn: Ji çalakiyên pêvajokirina daneyan derkevin',
    'privacy.section6.title': '6. Ewlehiya Daneyan',
    'privacy.section6.p1': 'Em tedbîrên teknîkî û rêxistinî yên guncan bicîh tînin da ku daneyên we li dijî gihîştina bêdestûr, guhertinê an hilweşînê biparêzin.',
    'privacy.section6.p2': 'Lê belê, tu rêbaza veguhastinê ya li ser înternetê %100 ewle nîne, û em nikarin ewlehiya mutleq garantî bikin.',
    'privacy.section7.title': '7. Guhertinên Di Vê Polîtîkayê de',
    'privacy.section7.p1': 'Dibe ku em carinan vê polîtîkaya nepenîtiyê nûve bikin. Em ê we li ser guhertinên girîng bi şandina agahiyek li ser malpera me an şandina e-nameyek agahdar bikin.',
    'privacy.section8.title': '8. Bi Me Re Têkilî Daynin',
    'privacy.section8.p1': 'Ger pirsên we li ser vê polîtîkaya nepenîtiyê an daneyên kesane hene, ji kerema xwe bi me re têkilî daynin:',
    'privacy.section8.email': 'E-name',
    
    // FAQ Page
    'faq.title': 'Pirsên Pirr Pirsîn',
    'faq.subtitle': 'Bersivên pirsên hevpar li ser bikaranîna Aqarnet bibînin',
    'faq.account.title': 'Hesab & Tomarkirin',
    'faq.account.q1': 'Ez çawa hesabek çêdikim?',
    'faq.account.q1_answer': 'Li "Têkeve" di serî de bikirtînin, paşê "Hesab çêke" hilbijêrin.',
    'faq.account.q2': 'Ez çawa şîfreya xwe vediguhezînim?',
    'faq.account.q2_answer': 'Di rûpela têketinê de, li "Şîfre ji bîr kir" bikirtînin û e-nameya xwe binivîsin.',
    'faq.account.q3': 'Gelo ez dikarim e-nameya xwe biguherînim?',
    'faq.account.q3_answer': 'Niha hûn nikarin e-nameya xwe rasterast biguhezînin. Ji kerema xwe bi tîma piştgiriyê re têkilî daynin.',
    'faq.listings.title': 'Lîsteyên Milkan',
    'faq.listings.q1': 'Ez çawa milkek ji bo firotanê dikim lîste?',
    'faq.listings.q1_answer': 'Divê hûn wekî ajantek qeydkirî bin. Piştî têketinê, li "Milkê Zêde Bike" bikirtînin.',
    'faq.listings.q2': 'Lîsteyek çiqas demê çalak dimîne?',
    'faq.listings.q2_answer': 'Lîste çalak dimînin heya ku wekî hatine firotin an ji hêla xwedî ve hatine rakirin.',
    'faq.listings.q3': 'Gelo ez dikarim lîsteya xwe piştî weşandinê biguherînim?',
    'faq.listings.q3_answer': 'Erê, hûn dikarin her dem lîsteya xwe biguherînin.',
    'faq.search.title': 'Lêgerîna Milkan',
    'faq.search.q1': 'Ez çawa lêgerînekê diparêzim?',
    'faq.search.q1_answer': 'Piştî lêgerînê, li "Lêgerînê Tomar Bike" bikirtînin.',
    'faq.search.q2': 'Ez çawa milkek li bijareyan zêde dikim?',
    'faq.search.q2_answer': 'Li îkona dilê li ser her karta milkê bikirtînin.',
    'faq.search.q3': 'Gelo ez dikarim li ser nexşeyê bigerin?',
    'faq.search.q3_answer': 'Erê, dîtina nexşeyê bikar bînin.',
    'faq.contact.title': 'Pêwendiya bi Ajantan',
    'faq.contact.q1': 'Ez çawa bi ajanek re têkilî daynin?',
    'faq.contact.q1_answer': 'Li "Pêwendî bi Ajanê" li ser her lîsteyê bikirtînin.',
    'faq.contact.q2': 'Gelo ez dikarim dîtinek plansaz bikim?',
    'faq.contact.q2_answer': 'Erê, li "Dîtinê Plansaz Bike" bikirtînin.',
    'faq.security.title': 'Ewlehî & Nepenîtî',
    'faq.security.q1': 'Gelo agahdariya min ewle ye?',
    'faq.security.q1_answer': 'Erê, em şîfrekirin û tedbîrên ewlehiyê bikar tînin.',
    'faq.security.q2': 'Ez çawa hesabê xwe jê dikim?',
    'faq.security.q2_answer': 'Biçin mîhengên profîlê û "Hesabê Jê Bike" hilbijêrin.',
    'faq.still_questions': 'Hîn jî pirsên we hene?',
    'faq.contact_us': 'Bi tîma piştgiriya me re têkilî daynin.',
    'newsletter.title': 'Abonebûna Rojnameyê',
    'newsletter.description': 'Ji bo nûçeyên milkê nûtirîn abone bibin.',
    'newsletter.placeholder': 'E-nameya xwe binivîsin',
    'newsletter.subscribe': 'Abone bibin',
    'newsletter.subscribing': 'Abone dibin...',
    'newsletter.success': 'Bi serkeftî abone bû!',
    'newsletter.subscribed': 'Spas ji bo abonebûna we!',
    'newsletter.invalid_email': 'Ji kerema xwe e-nameyek derbasdar binivîsin',
    'newsletter.error': 'Abonebûn biserneket',
    'newsletter.unsubscribe_note': 'Hûn dikarin her dem ji aboneyê derkevin.',
    'newsletter.unsubscribe_title': 'Ji Rojnameya Abonebûnê Derkeve',
    'newsletter.unsubscribe_description': 'E-nameya xwe binivîsin da ku ji aboneyê derkevin.',
    'newsletter.unsubscribe_button': 'Ji aboneyê derkeve',
    'newsletter.unsubscribe_success': 'Bi serkeftî ji aboneyê derket',
    'newsletter.unsubscribe_error': 'Derketina ji aboneyê biserneket',
    'newsletter.unsubscribe_message': 'Hûn ê êdî rojnameya me wernegirin.',
    'newsletter.invalid_link': 'Lînka Derketinê ya Nederbasdar',
    'newsletter.invalid_link_message': 'Ev lînka derketinê nederbasdar e an jî dema wê qediyaye. Ji kerema xwe lînka ji e-nameya rojnameyê bikar bînin.',
    'newsletter.unsubscribe_reason_label': 'Sedema derketinê (bi vebijarkî)',
    'newsletter.select_reason': 'Sedemek hilbijêre...',
    'newsletter.reason_too_many_emails': 'Pir gelek e-name',
    'newsletter.reason_not_relevant': 'Naverok ji min re ne têkildar e',
    'newsletter.reason_never_subscribed': 'Min qet abone nebû',
    'newsletter.reason_found_another': 'Xizmetek din dît',
    'newsletter.reason_other': 'Din',
    'common.try_again': 'Dîsa biceribîne',
    'newsletter.admin.title': 'Aboneyên Rojnameyê',
    'newsletter.admin.description': 'Aboneyên rojnameya xwe birêve bibin',
    'newsletter.admin.total': 'Giştî Abone',
    'newsletter.admin.active': 'Çalak',
    'newsletter.admin.unsubscribed': 'Ji aboneyê derketî',
    'newsletter.admin.export_csv': 'CSV Export bike',
    'newsletter.admin.refresh': 'Nûve bike',
    'newsletter.admin.subscribers_list': 'Lîsteya Aboneyan',
    'newsletter.admin.subscribers_description': 'Hemû abone û rewşa wan',
    'newsletter.admin.no_subscribers': 'Hîn abone tune',
    'newsletter.admin.subscribed_at': 'Dema Abonebûnê',
    'newsletter.admin.status': 'Rewş',
    'newsletter.admin.unsubscribed_at': 'Dema Derketinê',
    'newsletter.admin.actions': 'Çalakî',
    'newsletter.admin.deleted': 'Abone hate jêbirin',
    'newsletter.admin.delete_error': 'Jêbirina aboneyê biserneket',
    'newsletter.admin.no_data': 'Daneya ji bo exportê tune',
    'newsletter.admin.exported': 'Bi serkeftî export bû',
    'newsletter.admin.delete_confirm_title': 'Aboneyê jê bibe?',
    'newsletter.admin.delete_confirm_description': 'Ev ê aboneyê bi temamî jê bibe.',
    'footer.faq': 'Pirsên Pirr Pirsîn',
    
    // Broker Dashboard
    'broker.dashboard_title': 'Panela Brokerê',
    'broker.dashboard_subtitle': 'Milk û têkiliyên xerîdar birêvebirin',
    'broker.my_properties': 'Milkên Min',
    'broker.my_properties_desc': 'Lîsteyên milkên xwe bibînin û birêvebirin',
    'broker.viewings': 'Dîmenên Milkan',
    'broker.viewings_desc': 'Randevûyên dîmenên milkan birêvebirin',
    'broker.contacts': 'Peyamên Xerîdar',
    'broker.contacts_desc': 'Pirsên xerîdaran bibînin û bersiv bidin',
    'broker.analytics': 'Analîtîka Milkan',
    'broker.analytics_desc': 'Dîtin û alîkariyê li ser lîsteyên xwe bişopînin',
    'broker.subscription': 'Abonetî',
    'broker.subscription_desc': 'Plana xwe û sînorên lîsteyan birêve bibin',
    'broker.access_denied': 'Ev rûpel tenê ji bo brokerên tomarkirî peyda ye.',
    'broker.profile_title': 'Profîla Brokerê',
    'broker.profile_description': 'Profîla xwe ya gelemperî û agahdariya têkiliyê birêve bibin',
    'broker.back_to_dashboard': 'Vegere Panelê',
    'broker.display_name': 'Navê Nîşanê',
    'broker.display_name_placeholder': 'Navê we wekî ji xerîdaran re tê nîşandan',
    'broker.contact_email': 'E-maila Têkiliyê',
    'broker.email_placeholder': 'email@xwe.com',
    'broker.email_notification_note': 'Ev e-mail dê ji bo agahdariyên li ser dîmenan û peyamên xerîdaran were bikaranîn.',
    'broker.phone_number': 'Jimara Têlefonê',
    'broker.phone_placeholder': '+964 xxx xxx xxxx',
    'broker.change_photo': 'Wêneyê Biguherîne',
    'broker.save_profile': 'Profîlê Tomar Bike',
    'broker.profile_saved': 'Profîl bi serkeftî hate tomarkirin',
    'broker.profile_save_error': 'Tomarkirina profîlê biserneket',
    'broker.profile_load_error': 'Barkirina profîlê biserneket',
    'broker.avatar_uploaded': 'Wêne bi serkeftî hate barkirin',
    'broker.avatar_upload_error': 'Barkirina wêneyê biserneket',
    'broker.invalid_image_type': 'Ji kerema xwe pelê wêneyê hilbijêrin',
    'broker.image_too_large': 'Wêne divê kêmtir ji 2MB be',
    'common.login_required': 'Ji kerema xwe têkevin da ku bigihîjin vê rûpelê.',
    
    // Broker Public Profile
    'broker.listing_usage': 'Bikaranîna Lîsteyan',
    'broker.monthly_listing_limits': 'Sînorên lîsteyên mehane',
    'broker.listings_used': 'Lîsteyên bikaranîn',
    'broker.listings_remaining': 'lîsteyên mayî',
    'broker.period_resets': 'Dem nûve dibe',
    'broker.export_csv': 'CSV derxe',
    'broker.total_views': 'Hemû dîtin',
    'broker.scheduled_viewings': 'Dîmenên plankirî',
    'broker.client_messages': 'Peyamên xerîdar',
    'broker.filters': 'Parzûn',
    'broker.all_time': 'Hemû dem',
    'broker.active_listings': 'Lîsteyên çalak',
    'broker.sold_listings': 'Lîsteyên firotî',
    'broker.active_listings_desc': 'Milkên weşandî yên niha',
    'broker.no_active_listings': 'Hêj lîsteyek çalak tune. Milkê xwe yê yekem lê zêde bike.',
    'broker.all_types': 'Hemû cûre',
    'broker.all_cities': 'Hemû bajar',
    'broker.property_type': 'Cûreyê milkê',
    'broker.city': 'Bajar',
    'broker.date_range': 'Rêza dîrokê',
    'broker.last_week': 'Hefteya borî',
    'broker.last_month': 'Meha borî',
    'broker.last_quarter': '3 mehên dawîn',
    'broker.sold_listings_desc': 'Milkên ku we firotin',
    'broker.no_sold_listings': 'Hêj milkên firotî tune',
    'broker.clear_filters': 'Parzûnan paqij bike',
    'broker.selected': 'hilbijartî',
    'broker.compare': 'Berhevdan',
    'broker.mark_as_sold': 'Wek firotî nîşan bide',
    'broker.delete_selected': 'Yên hilbijartî jê bibe',
    'broker.select_all': 'Hemûyan hilbijêre',
    'broker.rejected_listings': 'Lîsteyên redkirî',
    'broker.rejected_listings_desc': 'Ev lîste ji aliyê moderatoran ve hatin redkirin. Tu dikarî îtîraz bikî.',
    'broker.no_rejected_listings': 'Lîsteyên redkirî tune',
    'broker.confirm_delete': 'Jêbirinê piştrast bike',
    'broker_public.not_found': 'Broker nehat dîtin',
    'broker_public.not_found_description': 'Profîla broker a ku hûn lê digerin tune ye.',
    'broker_public.back_to_search': 'Vegere lêgerîna brokaran',
    'broker_public.contact_broker': 'Bi broker re têkilî daynin',
    'broker_public.agency': 'Ajans',
    'broker_public.visit_website': 'Serdana malperê bikin',
    'broker_public.active_listings': 'Listeyên çalak',
    'broker_public.sold_properties': 'Milkên firotî',
    'broker_public.total_sales': 'Firotana tevahî',
    'broker_public.avg_days_market': 'Rojên navîn li bazarê',
    'broker_public.no_active_listings': 'Di vê demê de liste çalak tune',
    'broker_public.no_sold_properties': 'Hîn milkên firotî tune',
    'broker_public.view_all_properties': 'Hemû milkan bibînin',
    'broker_public.send_message': 'Peyamê bişînin',
    'broker_public.view_agency': 'Ajansê bibînin',
    'broker_public.works_at': 'Li kar dike',
    'broker_public.listings': 'Liste',
    'broker_public.sold': 'Firotî',
    'broker_public.reviews': 'Nirxandin',
    'broker_public.rating': 'Nirx',
    'broker_public.featured_broker': 'Broker bijartî',
    'broker_public.top_performer': 'Performansa herî baş',
    
    // Agency Dashboard
    'agency.my_agency': 'Ajansa Min',
    'agency.dashboard_title': 'Panela Ajansê',
    'agency.dashboard_subtitle': 'Brokerên xwe û mîhengên ajansê birêvebirin',
    'agency.brokers_tab': 'Broker',
    'agency.invites_tab': 'Vexwendin',
    'agency.settings_tab': 'Mîheng',
    'agency.brokers_title': 'Brokerên Ajansê',
    'agency.brokers_description': 'Brokerên di ajansa xwe de birêvebirin',
    'agency.create_broker': 'Broker Biafirîne',
    'agency.create_broker_title': 'Hesabê Brokerê Nû Biafirîne',
    'agency.create_broker_description': 'Hesabê brokerê nû ku rasterast bi ajansa we ve girêdayî ye biafirînin',
    'agency.broker_name': 'Navê Tam',
    'agency.broker_email': 'E-nameyê',
    'agency.broker_password': 'Şîfre',
    'agency.create_broker_btn': 'Hesabê Brokerê Biafirîne',
    'agency.broker_created': 'Hesabê broker bi serkeftî hate afirandin',
    'agency.broker_create_error': 'Afirandina hesabê broker biserneket',
    'agency.no_brokers': 'Hîn broker tune',
    'agency.no_brokers_description': 'Hesabên broker biafirînin an vexwendinan bişînin',
    'agency.broker_removed': 'Broker ji ajansê hate rakirin',
    'agency.broker_remove_error': 'Rakirina broker biserneket',
    'agency.invites_title': 'Vexwendinên Bendêwar',
    'agency.invites_description': 'Vexwendinên şandî ji brokerên pêşniyazkirî re birêvebirin',
    'agency.send_invite': 'Vexwendinê Bişîne',
    'agency.send_invite_title': 'Brokerek Vexwîne',
    'agency.send_invite_description': 'Vexwendinê bişînin ku tevlî ajansa we bibin',
    'agency.invite_email': 'E-nameyê',
    'agency.send_invite_btn': 'Vexwendinê Bişîne',
    'agency.invite_sent': 'Vexwendin bi serkeftî hat şandin',
    'agency.invite_error': 'Şandina vexwendinê biserneket',
    'agency.link_copied': 'Lînka vexwendinê hat kopîkirin',
    'agency.no_invites': 'Vexwendinên bendêwar tune',
    'agency.settings_title': 'Mîhengên Ajansê',
    'agency.settings_description': 'Agahdariya ajansa xwe nûve bikin',
    'agency.settings_saved': 'Mîhengên ajansê hatin tomarkirin',
    'agency.settings_save_error': 'Tomarkirina mîhengan biserneket',
    'agency.load_error': 'Barkirina daneyên ajansê biserneket',
    'agency_invite.title': 'Tevlî Ajansê Bibin',
    'agency_invite.description': 'Hûn hatine vexwendin ku tevlî {agencyName} bibin',
    'agency_invite.accept_btn': 'Vexwendinê Qebûl Bikin',
    'agency_invite.accepted_success': 'Bi serkeftî tevlî ajansê bûn!',
    'agency_invite.accept_error': 'Qebûlkirina vexwendinê biserneket',
    'agency_invite.create_account': 'Hesabek Biafirînin',
    'agency_invite.invalid_link': 'Lînka vexwendinê nederbasdar e',
    'agency_invite.expired': 'Ev vexwendin qediya',
    'agency_invite.already_accepted': 'Ev vexwendin berê hate qebûlkirin',
    'agency_invite.fetch_error': 'Barkirina vexwendinê biserneket',
    'agency_invite.error_title': 'Vexwendin Nederbasdar',
    'agency_invite.back_home': 'Vegere Destpêkê',
    'agency_invite.create_account_description': 'Hesabek biafirînin da ku vexwendinê qebûl bikin',
    'agency_invite.your_name': 'Navê Te',
    'agency_invite.name_placeholder': 'Navê xwe yê tevahî binivîse',
    'agency_invite.email_placeholder': 'email@xwe.com',
    'agency_invite.password': 'Şîfre',
    'agency_invite.password_placeholder': 'Şîfreyekê biafirîne',
    'agency_invite.confirm_password': 'Şîfreyê Piştrastbike',
    'agency_invite.confirm_password_placeholder': 'Şîfreya xwe piştrastbike',
    'agency_invite.register_accept_btn': 'Hesab Biafirîne û Tevlîbibe',
    'agency_invite.registered_success': 'Hesab hate afirandin û tevlî ajansê bû!',
    'agency_invite.register_error': 'Afirandina hesabê biserneket',
    'agency_invite.passwords_mismatch': 'Şîfre li hev nakin',
    'agency_invite.password_too_short': 'Şîfre divê herî kêm 6 tîp be',
    'agency_invite.already_have_account': 'Berê hesabek heye?',
    'agency_invite.login_here': 'Li vir têkeve',
    'agency_invite.joining': 'Tu tevlî dibî',
    'agency_invite.logged_in_as': 'Têketî wek',
    'agency.notifications_tab': 'Agahdarî',
    'agency.notifications_title': 'Agahdariyên E-nameyê',
    'agency.notifications_description': 'Bijart bike kîjan agahdariyên li ser çalakiyên brokerê werdigirî',
    'agency.notifications_saved': 'Tercîhên agahdariyê hatin tomarkirin',
    'agency.notifications_save_error': 'Tomarkirina tercîhan biserneket',
    'agency.notif_new_broker_joins': 'Brokerê Nû Tevlî Dibe',
    'agency.notif_new_broker_joins_desc': 'Agahdarbibe dema brokerek vexwendina te qebûl dike',
    'agency.notif_broker_property_added': 'Broker Xaniyek Zêde Dike',
    'agency.notif_broker_property_added_desc': 'Agahdarbibe dema broker xaniyên nû zêde dikin',
    'agency.notif_broker_viewing_requests': 'Daxwazên Dîtinê',
    'agency.notif_broker_viewing_requests_desc': 'Agahdarbibe li ser daxwazên dîtinê ji bo xaniyên brokerê',
    'agency.notif_broker_client_messages': 'Peyamên Xerîdar',
    'agency.notif_broker_client_messages_desc': 'Agahdarbibe dema xerîdar bi brokerên te re têkilî datînin',
    'agency.notif_weekly_summary': 'Kurteya Heftane',
    'agency.notif_weekly_summary_desc': 'Kurteya heftane ya çalakiya ajansê werdigire',
    'agency.analytics_tab': 'Analîz',
    'agency.no_brokers_analytics': 'Brokerên zêde bike ku analîza performansê bibînî',
    'agency.total_properties': 'Giştî Xanî',
    'agency.total_views': 'Giştî Dîtin',
    'agency.total_viewings': 'Daxwazên Nîşandanê',
    'agency.total_sold': 'Xanîyên Firotin',
    'agency.total_sales_value': 'Nirxê Giştî yê Firotin',
    'agency.broker_performance_chart': 'Performansa Brokerê',
    'agency.broker_performance_chart_desc': 'Berawirdkirina metrîkên brokerê bi yekcarê',
    'agency.individual_broker_stats': 'Statîstîkên Brokerên Takekesî',
    'agency.individual_broker_stats_desc': 'Metrîkên hûrgulî ji bo her brokerekê',
    'agency.properties': 'Xanî',
    'agency.views': 'Dîtin',
    'agency.viewings': 'Nîşandan',
    'agency.contacts': 'Têkilî',
    'agency.active': 'Çalak',
    'agency.sold': 'Firotin',
    'agency.sales_value': 'Nirxê Firotin',
    'agency.upgrade_plan': 'Plana Bilind Bike',
    'agency.view_plans': 'Planan Bibîne',
    'agency.billing_tab': 'Fatûre',
    'agency.name_label': 'Navê Ajansê',
    'agency.email_label': 'E-peyam',
    'agency.phone_label': 'Telefon',
    'agency.website_label': 'Malpera',
    'agency.address_label': 'Navnîşan',
    'agency.city_label': 'Bajar',
    'agency.description_label': 'Danasîn',
    'agency.description_placeholder': 'Ji xerîdaran re li ser ajansa xwe bêje...',
    'agency.save_settings': 'Mîhengan Tomar Bike',
    'agency.broker_usage': 'Bikaranîna Broker',
    'agency.invite_broker': 'Brokerê Vexwîne',
    'agency.brokers': 'broker',
    'agency.broker_limit_reached': 'Sînorê broker hate gihîştin',
    'agency.upgrade_for_more': 'Plana xwe bilind bike ji bo zêdekirina brokerên din',
    'agency.active_listings': 'Navnîşên çalak',
    'agency.broker_slots_remaining': 'Cihên mayîn',
    'agency.manage_subscription': 'Aboneyê Birêvebirin',
    'agency.upgrade_your_plan': 'Plana xwe bilind bike',
    'agency.choose_plan': 'Plana ku ji bo ajansa te çêtirîn e hilbijêre',
    'agency.pro_plan': 'Pro',
    'agency.enterprise_plan': 'Enterprise',
    'agency.for_growing_agencies': 'Ji bo ajansên mezinbûyî',
    'agency.for_large_agencies': 'Ji bo ajansên mezin',
    'agency.per_month': '/meh',
    'agency.up_to_brokers': 'Heta {count} broker',
    'agency.unlimited_brokers': 'Brokerên bêsînor',
    'agency.advanced_analytics': 'Analîtîkên pêşketî',
    'agency.full_analytics': 'Pakêta analîtîkê ya tam',
    'agency.priority_support': 'Piştgiriya pêşîn',
    'agency.dedicated_support': 'Piştgiriya taybet',
    'agency.upgrade_to_pro': 'Bilind bike bo Pro',
    'agency.upgrade_to_enterprise': 'Bilind bike bo Enterprise',
    'agency.popular': 'Populer',
    'agency.current_plan': 'Plana niha',
    'agency.plan_features': 'Taybetmendiyên planê',
    'agency.billing_history': 'Dîroka fatûreyê',
    'agency.no_billing_history': 'Hîn dîroka fatûreyê tune',
    'agency.subscription_billing': 'Abonetî & Fatûrekirin',
    'agency.manage_agency_subscription': 'Abonetiya ajansê xwe birêve bibin',
    'agency.plan': 'Plan',
    'agency.basic_plan': 'Bingehîn',
    'agency.broker_limit': 'Sînorê Broker',
    'agency.current_usage': 'Bikaranîna heyî',
    'agency.status': 'Rewş',
    'agency.active_status': 'Çalak',
    'agency.unlimited': 'Bêsînor',
    'agency.feature': 'Taybetmendî',
    'agency.price': 'Buhayê',
    'agency.free': 'Belaş',
    'agency.analytics': 'Analîz',
    'agency.basic_analytics': 'Bingehîn',
    'agency.advanced': 'Pêşketî',
    'agency.full_suite': 'Pakêta Tevahî',
    'agency.support': 'Piştgirî',
    'agency.email_support': 'E-name',
    'agency.priority': 'Pêşîn',
    'agency.dedicated': 'Taybet',
    'agency_directory.title': 'Ajansên Xaniyan',
    'agency_directory.subtitle': 'Ajansên xaniyan ên pêbawer bibînin û bibînin',
    'agency_directory.search_placeholder': 'Ajansên bigerin...',
    'agency_directory.location_placeholder': 'Li gorî cih filter bikin...',
    'agency_directory.agencies_found': 'ajans hatin dîtin',
    'agency_directory.no_agencies': 'Ajans nehat dîtin',
    'agency_directory.no_agencies_description': 'Pîvanên lêgerînê biguherînin',
    'agency_directory.brokers': 'broker',
    'agency_directory.view_agency': 'Ajansê Bibîne',
    'agency_directory.not_found': 'Ajans nehat dîtin',
    'agency_directory.back_to_directory': 'Vegere Rêbernameyê',
    'agency_directory.visit_website': 'Serdana Malperê',
    'agency_directory.our_brokers': 'Brokerên Me',
    'agency_directory.no_brokers_yet': 'Hîn broker di vê ajansê de tune',
    'agency_directory.agency_listings': 'Lîsteyên Xaniyan',
    
    // User Listings
    'userListings.title': 'Navnîşanên min',
    'userListings.subtitle': 'Navnîşanên xaniyên xwe bi rêve bibe û yên nû biafirîne',
    'userListings.pricingTitle': 'Bihayên navnîşanan',
    'userListings.pricingDesc': 'Navnîşana xaniyê xwe bi bihayên me yên hêsan biafirîne',
    'userListings.listingPrice': '$10 ji bo her navnîşanê',
    'userListings.listingDuration': 'Ji bo 1 meh derbasdar e',
    'userListings.videoPrice': '+$3 ji bo vîdyoyê',
    'userListings.videoDesc': 'Gerîdana vîdyoyê li navnîşana xwe zêde bike',
    'userListings.includeVideo': 'Gerîdana vîdyoyê tê de hebe',
    'userListings.createListing': 'Navnîşanê biafirîne',
    'userListings.myListingsTitle': 'Xaniyên te',
    'userListings.noListings': 'Hîn navnîşan tune',
    'userListings.noListingsDesc': 'Navnîşana xaniyê xwe ya yekem biafirîne da ku dest pê bikî',
    'userListings.createFirst': 'Navnîşana xwe ya yekem biafirîne',
    'userListings.hasVideo': 'Vîdyo',
    'userListings.renew': 'Nû bike',
    'userListings.renewSuccess': 'Navnîşan hat nûkirin!',
    'userListings.renewSuccessDesc': 'Navnîşana te ji bo mehekê din hat nûkirin',
    'userListings.paymentSuccess': 'Pere dan serkeftî!',
    'userListings.paymentSuccessDesc': 'Navnîşana te niha çalak e',
    'userListings.paymentCancelled': 'Pere dan betal kirin',
    'userListings.paymentCancelledDesc': 'Pere dana te betal kirin',
    'userListings.checkoutError': 'Afirandina peredanê bi ser neket',
    'userListings.renewError': 'Nûkirina navnîşanê bi ser neket',
    'userListings.createDraft': 'Navnîşan biafirîne',
    'userListings.previewListing': 'Pêşdîtina navnîşana xwe',
    'userListings.draft': 'Pêşnûvis',
    'userListings.drafts': 'Pêşnûvis',
    'userListings.draftSaved': 'Pêşnûvis hat tomarkirin',
    'userListings.draftSavedDesc': 'Navnîşana te wekî pêşnûvis hat tomarkirin',
    'userListings.draftError': 'Tomarkirina pêşnûvisê bi ser neket',
    'userListings.draftDeleted': 'Pêşnûvis hat jêbirin',
    'userListings.deleteError': 'Jêbirin bi ser neket',
    'userListings.saveDraft': 'Pêşnûvisê tomar bike',
    'userListings.saveAndPreview': 'Tomar bike & pêşdîtin',
    'userListings.publishOptions': 'Vebijarkên weşandinê',
    'userListings.addVideoTour': 'Gerrîdeya vîdyoyê zêde bike',
    'userListings.videoIncluded': 'Gerrîdeya vîdyoyê tê de ye',
    'userListings.totalPrice': 'Giştî',
    'userListings.backToEdit': 'Vegere guherandinê',
    'userListings.publishNow': 'Niha weşîne',
    'userListings.publish': 'Weşîne',
    'userListings.publishSuccess': 'Navnîşan hat weşandin!',
    'userListings.publishSuccessDesc': 'Navnîşana te niha li benda vekolînê ye',
    'userListings.firstListingFree': 'Navnîşana yekem belaş e!',
    'userListings.firstListingFreeDesc': 'Navnîşana xanîyê te ya yekem bi tevahî belaş e. Niha weşîne û bi kirînên li seranserê Iraqê re têkilî dayne!',
    'userListings.pending': 'Li benda vekolînê',
    'userListings.pendingInfo': 'Ev navnîşan ji aliyê tîmê moderatorê ve têne vekolîn. Dema ku ew werin pejirandin, dê were te agahdar kirin.',
    'userListings.pendingReview': 'Li benda vekolînê',
    'userListings.awaitingApproval': 'Li benda pejirandina moderatorê',
    'userListings.rejected': 'Navnîşanên redkirî',
    'userListings.rejectedInfo': 'Ev navnîşan nehatin pejirandin. Ji kerema xwe biguhere û pirsgirêkan berî şandina dîsa rast bike.',
    'userListings.rejectedBadge': 'Redkirî',
    'userListings.live': 'Zindî',
    'userListings.editDraft': 'Biguherîne',
    'userListings.editAndResubmit': 'Biguherîne & dîsa bişîne',
    'userListings.pendingCannotEdit': 'Di dema vekolînê de nabe biguherînin',
    'userListings.resubmitForReview': 'Ji bo vekolînê dîsa bişîne',
    'userListings.resubmitInfo': 'Guhertinên te hatin tomarkirin. Navnîşana xwe dîsa ji bo vekolînê bişîne bê lêçûnek zêde.',
    'userListings.submitForReview': 'Ji bo vekolînê bişîne',
    'userListings.validation.priceRequired': 'Biha pêwîst e',
    'userListings.validation.cityRequired': 'Bajar pêwîst e',
    'userListings.validation.roomsRequired': 'Ode pêwîst e',
    'userListings.validation.sizeRequired': 'Mezinahî pêwîst e',
    'userListings.validation.plotSizeRequired': 'Mezinahiya erdê pêwîst e',
    'userListings.validation.imagesRequired': 'Bi kêmî ve yek wêne pêwîst e',
    'userListings.choosePlan': 'Plana navnîşana xwe hilbijêre',
    'userListings.choosePlanDesc': 'Hilbijêre çend wêne li ser karta navnîşana xwe nîşan bide',
    'userListings.upgradePlan': 'Plana navnîşana xwe bilind bike',
    'userListings.upgradePlanDesc': 'Bilind bike da ku bêtir wêne nîşan bidî. Tu tenê cûdahiyê didî.',
    'userListings.selectImages': 'Wêneyên nîşandanê hilbijêre',
    'userListings.selectImagesDesc': 'Wêneyên ku li ser karta navnîşana xwe nîşan bidî hilbijêre',
    'userListings.selectPrimaryImage': 'Wêneya sereke hilbijêre',
    'userListings.selectPrimaryImageDesc': 'Wêneya sereke ya ku pêşî li navnîşana xwe nîşan bide hilbijêre',
    'userListings.addVideo': 'Gerîdana vîdyoyê zêde bike',
    'userListings.pleaseSelectImages': 'Ji kerema xwe wêneyan hilbijêre da ku berdewam bikî',
    'userListings.pleaseSelectPrimaryImage': 'Ji kerema xwe wêneya sereke hilbijêre da ku berdewam bikî',
    'userListings.proceedToPayment': 'Berdewam bike bo peredanê',
    'userListings.tierBasic': 'Bingehîn',
    'userListings.tierStandard': 'Standard',
    'userListings.tierPremium': 'Premium',
    'userListings.tierBasicDesc': '1 wêne li ser kartê',
    'userListings.tierStandardDesc': '3 wêne carousel',
    'userListings.tierPremiumDesc': 'Hemû wêne + nîşan',
    'userListings.tierDuration': '30 roj çalak',
    'userListings.tierBest': 'Çêtirîn',
    'userListings.tierBasicFull': 'Asta Bingehîn (1 wêne)',
    'userListings.tierStandardFull': 'Asta Standard (3 wêne)',
    'userListings.tierPremiumFull': 'Asta Premium (Hemû wêne)',
    'userListings.changeTier': 'Astê biguherîne',
    'userListings.selectImageTier': 'Asta wêneyan hilbijêre',
    'userListings.selectTierFirst': 'Berî weşandinê astekê hilbijêre',
    'userListings.upgrade': 'Bilindkirin',
    'common.total': 'Tevahî',
    
    // Settings Page
    'settings.title': 'Mîheng',
    'settings.subtitle': 'Mîhengên hesabê xwe û tercîhan bi rê ve bibe',
    'settings.profile': 'Profîl',
    'settings.notifications': 'Agahdarî',
    'settings.account': 'Hesab',
    'settings.profile_info': 'Agahdariya Profîlê',
    'settings.profile_desc': 'Agahdariya kesane ya xwe nûve bike',
    'settings.change_photo': 'Wêneyê Biguherîne',
    'settings.display_name': 'Navê Nîşanê',
    'settings.display_name_placeholder': 'Navê te',
    'settings.email': 'E-name',
    'settings.email_placeholder': 'your@email.com',
    'settings.phone': 'Têlefon',
    'settings.phone_placeholder': '+964 770 123 4567',
    'settings.save': 'Guhertinan Tomar Bike',
    'settings.saved': 'Mîheng bi serkeftî hatin tomarkirin',
    'settings.load_error': 'Mîheng nehat barkirin',
    'settings.save_error': 'Mîheng nehat tomarkirin',
    'settings.invalid_image': 'Ji kerema xwe wêneyek hilbijêre',
    'settings.image_too_large': 'Wêne divê ji 2MB kêmtir be',
    'settings.avatar_uploaded': 'Wêne hat barkirin',
    'settings.avatar_error': 'Wêne nehat barkirin',
    'settings.notification_preferences': 'Tercîhên Agahdariyê',
    'settings.notification_desc': 'Hilbijêre kîjan agahdariyên tu dixwazî bistînî',
    'settings.notif_property_alerts': 'Hişyariyên Xanîyê',
    'settings.notif_property_alerts_desc': 'Li ser xanîyên nû agahdar bibe',
    'settings.notif_saved_search': 'Hişyariyên Lêgerîna Tomarkirî',
    'settings.notif_saved_search_desc': 'Dema xanîyên nû bi lêgerînên te yên tomarkirî re li hev dikin agahdar bibe',
    'settings.notif_newsletter': 'Nûçename',
    'settings.notif_newsletter_desc': 'Nûçenameya me bi nûvekirinên bazarê bistîne',
    'settings.notif_viewing': 'Pejirandinên Dîtinê',
    'settings.notif_viewing_desc': 'Ji bo dîtinên plansazkirî pejirandan bistîne',
    'settings.account_settings': 'Mîhengên Hesabê',
    'settings.account_desc': 'Ewlehî û tercîhên hesabê xwe bi rê ve bibe',
    'settings.change_password': 'Şîfreyê Biguherîne',
    'settings.change_password_desc': 'Şîfreya xwe nûve bike',
    'settings.change': 'Biguherîne',
    'settings.two_factor': 'Rastkirina Du-Faktorî',
    'settings.two_factor_desc': 'Ewlehiya zêde ji bo hesabê xwe zêde bike',
    'settings.enable': 'Çalak Bike',
    'settings.delete_account': 'Hesabê Jêbibe',
    'settings.delete_account_desc': 'Hesabê xwe bi temamî jêbibe',
    'settings.delete': 'Jêbibe',
    'settings.logout': 'Derkeve',
    
    // Common
    'common.back': 'Paşve',
    
    // Contact Form Modal
    'contact_form.title': 'Têkilî',
    'contact_form.description': 'Peyamek bişîne da ku têkilî bi dest bixe.',
    'contact_form.name': 'Nav',
    'contact_form.name_placeholder': 'Navê te',
    'contact_form.email': 'E-name',
    'contact_form.email_placeholder': 'your@email.com',
    'contact_form.phone': 'Têlefon',
    'contact_form.phone_placeholder': 'Jimareya te ya têlefonê',
    'contact_form.message': 'Peyam',
    'contact_form.message_placeholder': 'Em çawa dikarin alîkariya te bikin?',
    'contact_form.send': 'Peyamê Bişîne',
    'contact_form.sending': 'Tê şandin...',
    'contact_form.success_title': 'Peyam hat şandin!',
    'contact_form.success_message': 'Peyama te hat şandin. Tu ê pejirandinek bi e-peyamê bistînî.',
    'contact_form.error_title': 'Xeletî',
    'contact_form.error_message': 'Nekarî peyamê bişîne. Ji kerema xwe dîsa biceribîne.',
    
    // Messaging
    'messaging.title': 'Peyam',
    'messaging.description': 'Axaftinên te yên taybet',
    'messaging.new_message': 'Peyama nû',
    'messaging.no_messages': 'Hîn peyam tune',
    'messaging.no_conversations': 'Hîn axaftin tune',
    'messaging.send': 'Bişîne',
    'messaging.send_message': 'Peyamê bişîne',
    'messaging.message_to': 'Peyamekê ji bo bişîne',
    'messaging.subject': 'Mijar',
    'messaging.subject_placeholder': 'Mijar (vebijarkî)',
    'messaging.message': 'Peyam',
    'messaging.message_placeholder': 'Peyama xwe binivîse...',
    'messaging.login_required': 'Divê tu têkevî da ku peyaman bişînî.',
    'messaging.message_sent': 'Peyam bi serkeftî hat şandin',
    'messaging.contact_agent_description': 'Bi rasterast bi ajansê re têkilî daynin',
    'messaging.type_message': 'Peyamekê binivîse...',
    'messaging.inbox': 'Posta hatî',
    'messaging.sent': 'Şandî',
    'messaging.search': 'Di axaftinan de bigere...',
    'messaging.select_conversation': 'Axaftinekê hilbijêre',
    'messaging.select_description': 'Ji lîsteyê axaftinekê hilbijêre da ku dest bi peyamê bikî',
    'messaging.encrypted': 'Şîfrekirî ji dawî heta dawiyê',
    'messaging.decrypt': 'Peyamê veşîfre bike',
    'messaging.reply': 'Bersiv bide',
    'messaging.replying_to': 'Bersiv dide',
    'messaging.replying_to_yourself': 'xwe',
    'messaging.forward': 'Bişîne pêş',
    'messaging.delete': 'Jê bibe',
    'messaging.delete_confirm': 'Peyamê jê bibe',
    'messaging.delete_description': 'Ev peyam ji bo her kesî wê bê jêbirin. Ev kiryar nayê paşve girtin.',
    'messaging.delete_for_everyone': 'Ji bo her kesî jê bibe',
    'messaging.pin': 'Bişêl bike',
    'messaging.unpin': 'Bişêlkirinê rake',
    'messaging.pinned_messages': 'Peyamên bişêlkirî',
    'messaging.no_pinned': 'Peyamên bişêlkirî tune',
    'messaging.delivered': 'Hat radest kirin',
    'messaging.read': 'Xwendî',
    'messaging.seen_at': 'Hate dîtin li',
    'messaging.voice_message': 'Peyama dengî',
    'messaging.search_messages': 'Di peyaman de bigere...',
    'messaging.contacts': 'Têkilî',
    'messaging.results_found': 'encam hatin dîtin',
    'messaging.recording': 'Tê tomar kirin...',
    'messaging.typing': 'dinivîse...',
    'messaging.online': 'serhêl',
    'messaging.tap_for_info': 'ji bo agahdariya pêwendiyê li vir bitikîne',
    'messaging.photos': 'Wêne',
    'messaging.camera': 'Kamera',
    'messaging.location': 'Cîh',
    'messaging.contact': 'Têkilî',
    'messaging.document': 'Belge',
    'messaging.poll': 'Rapirsî',
    'messaging.event': 'Bûyer',
    'messaging.location_coming_soon': 'Parvekirina cîh zû tê',
    'messaging.contact_coming_soon': 'Parvekirina têkiliyê zû tê',
    'messaging.poll_coming_soon': 'Çêkirina rapirsiyê zû tê',
    'messaging.event_coming_soon': 'Çêkirina bûyerê zû tê',
    'messaging.schedule_event': 'Bûyerê Plansaz Bike',
    'messaging.event_title': 'Sernavê Bûyerê',
    'messaging.event_title_placeholder': 'Mînak: Dîtina Milkê',
    'messaging.event_date': 'Tarîx',
    'messaging.event_time': 'Dem',
    'messaging.event_location': 'Cih',
    'messaging.event_location_placeholder': 'Cih an navnîşan binivîse',
    'messaging.event_description': 'Danasîn',
    'messaging.event_description_placeholder': 'Agahdariyên din zêde bike...',
    'messaging.send_event': 'Bûyerê Bişîne',
    'messaging.event_created': 'Bûyer hate afirandin',
    'messaging.create_poll': 'Rapirsiyê Biafirîne',
    'messaging.poll_question': 'Pirs',
    'messaging.poll_question_placeholder': 'Tu dixwazî çi biprsî?',
    'messaging.poll_options': 'Vebijêrk',
    'messaging.option': 'Vebijêrk',
    'messaging.add_option': 'Vebijêrkê Zêde Bike',
    'messaging.allow_multiple': 'Bersivên pirjimar destûr bide',
    'messaging.send_poll': 'Rapirsiyê Bişîne',
    'messaging.poll_created': 'Rapirsî hate afirandin',
    'messaging.edit': 'Biguherîne',
    'messaging.edit_message': 'Peyamê Biguherîne',
    'messaging.edit_description': 'Guhertinên li peyama xwe bike',
    'messaging.edit_time_remaining': 'Tu',
    'messaging.minutes_to_edit': 'deqeyan heye ku vê peyamê biguherînî',
    'messaging.message_edited': 'Peyam hat guhertin',
    'messaging.edit_failed': 'Guherandina peyamê têk çû',
    'messaging.from_date': 'Ji',
    'messaging.to_date': 'Heta',
    'messaging.clear_filters': 'Fîlteran paqij bike',
    'messaging.export_chat': 'Chat Export Bike',
    'messaging.export_description': 'Dîroka sohbeta xwe export bike',
    'messaging.export_text': 'Pelê Tekstî (.txt)',
    'messaging.export_text_desc': 'Formata tekstê ya hêsan, hêsan e ku were xwendin û parve kirin',
    'messaging.export_pdf': 'Belgeya PDF',
    'messaging.export_pdf_desc': 'Belgeya formatkirî, ji bo çapkirinê ideale',
    'messaging.export_messages_count': 'peyam wê werin export kirin',
    'messaging.exporting': 'Tê export kirin...',
    'messaging.export': 'Export Bike',
    'messaging.export_success': 'Chat bi serkeftî hat export kirin',
    'messaging.export_failed': 'Exportkirina chatê têk çû',
    'messaging.is_typing': 'dinivîse...',
    
    // Quick Reply Templates
    'quick_reply.title': 'Bersivên Bilez',
    'quick_reply.greeting': 'Silav! Spas ji bo peywendiyê.',
    'quick_reply.interested': 'Ez bi vê milkê re eleqedar im. Ma hûn dikarin bêtir agahdarî bidin?',
    'quick_reply.schedule_viewing': 'Ez dixwazim dîtinek bikin. Kîjan dem hene?',
    'quick_reply.ask_price': 'Ma bihayê dikare were danûstandin?',
    'quick_reply.ask_availability': 'Ma ev milk hîn jî heye?',
    'quick_reply.thank_you': 'Spas ji bo agahdariyê. Ez ê zû vegerim.',
    'quick_reply.will_contact': 'Ez ê zû bi we re têkilî daynin bi agahdariya zêde.',
    'quick_reply.not_interested': 'Spas, lê ez êdî bi vê milkê re eleqedar nînim.',
    
    // Notifications
    'notifications.enable': 'Agahdariyên veke',
    'notifications.on': 'Agahdarî vekirî ne',
    'notifications.preferences': 'Mîhengên Agahdariyê',
    'notifications.sound': 'Dengê Agahdariyê',
    'notifications.sound_desc': 'Dengek lêxe gava peyamek distîne',
    'notifications.sound_default': 'Bêtir',
    'notifications.sound_chime': 'Zengil',
    'notifications.sound_bell': 'Zeng',
    'notifications.sound_pop': 'Pop',
    'notifications.test': 'Test',
    'notifications.vibration': 'Lerîn',
    'notifications.vibration_desc': 'Lerîn gava peyamek distîne (tenê mobîl)',
    'messaging.you': 'Tu',
    'messaging.encrypted_message': 'Peyama şîfrekirî',
    'messaging.e2e_notice': 'Peyam û bang ji dawî heta dawiyê şîfrekirî ne. Tenê kesên di vê sohbetê de dikarin wan bixwînin, guhdarî bikin an parve bikin.',
    'messaging.today': 'Îro',
    'messaging.yesterday': 'Duh',
    'messaging.search_groups': 'Di koman de bigere...',
    
    // Attachment viewer
    'attachment.all_media': 'Hemû medya',
    'attachment.go_to_message': 'Biçe peyamê',
    'attachment.download': 'Daxistin',
    'attachment.view_fullscreen': 'Di ekrana tam de bibîne',
    'attachment.save_to_gallery': 'Di galeriyê de tomar bike',
    'attachment.share': 'Parve bike',
    'attachment.double_tap_zoom': 'Du caran lê bixin bo mezinkirin',
    'attachment.zoom_in': 'Mezin bike',
    'attachment.zoom_out': 'Biçûk bike',
    'attachment.reset_zoom': 'Vegerîne',
    'attachment.ctrl_scroll_zoom': 'Ctrl + Scroll bo mezinkirin',
    'attachment.fit_width': 'Li gorî berfirehiyê bike',
    'attachment.fit_height': 'Li gorî dirêjahiyê bike',
    'attachment.click_to_navigate': 'Bo navîgasyonê bikirtînin',
    
    // Status
    'status.online': 'Serhêl',
    'status.offline': 'Neveşarî',
    'status.last_seen': 'Carek dawî hat dîtin',
    
    // Blocking
    'blocking.block_user': 'Bikarhênerê asteng bike',
    'blocking.block_confirm': 'Tu bawer î ku tu dixwazî {name} asteng bikî? Ew nikare peyaman ji te re bişîne.',
    'blocking.reason_optional': 'Sedem (ne pêwîst)',
    'blocking.reason_placeholder': 'Çima tu vê bikarhênerê asteng dikî?',
    'blocking.block': 'Asteng bike',
    'blocking.user_blocked': 'Bikarhêner bi serkeftî hate asteng kirin',
    'blocking.already_blocked': 'Bikarhêner jixwe asteng kiriye',
    'blocking.error': 'Asteng kirin bi ser neket',
    'blocking.blocked_users': 'Bikarhênerên asteng kirî',
    'blocking.blocked_users_description': 'Lîsteya bikarhênerên asteng kirî birêve bibe',
    'blocking.no_blocked_users': 'Te kesek asteng nekiriye',
    'blocking.blocked_on': 'Di {date} de hate asteng kirin',
    'blocking.unblock': 'Astengê rake',
    'blocking.user_unblocked': 'Asteng hate rakirin',
    'blocking.unblock_error': 'Rakirana astengê bi ser neket',
    'blocking.fetch_error': 'Barkirina bikarhênerên asteng kirî bi ser neket',
    'blocking.you_blocked_user': 'Te ev bikarhêner asteng kiriye. Ji bo şandina peyaman astengê rake.',
    'blocking.user_blocked_you': 'Ev bikarhêner te asteng kiriye. Tu nikarî peyaman bişînî.',
    
    // Reporting
    'reporting.report_user': 'Bikarhêner rapor bike',
    'reporting.report_description': '{name} ji ber binpêkirina rêbernameyên me rapor bike',
    'reporting.select_reason': 'Ji kerema xwe sedemek hilbijêre',
    'reporting.reason': 'Sedema raporê',
    'reporting.additional_details': 'Agahiyên zêde',
    'reporting.details_placeholder': 'Kontekstek din peyda bike...',
    'reporting.submit_report': 'Raporê bişîne',
    'reporting.report_submitted': 'Rapor hate şandin. Em ê demekê kurt de lê binêrin.',
    'reporting.error': 'Şandina raporê bi ser neket',
    'reporting.reasons.spam': 'Spam an peyamên nexwestî',
    'reporting.reasons.harassment': 'Tacîz an qeşmerî',
    'reporting.reasons.inappropriate_content': 'Naveroka ne guncaw',
    'reporting.reasons.fraud': 'Xapandin an xapînok',
    'reporting.reasons.impersonation': 'Xwe wekî kesekî din nîşan dan',
    'reporting.reasons.other': 'Din',
    
    // Admin Reports
    'admin.reports': 'Rapor',
    'admin.reports_panel': 'Raporên Bikarhêneran',
    'admin.reports_description': 'Raporan binirxîne û çareser bike',
    'admin.fetch_reports_error': 'Raporên wergirtin bi ser neket',
    'admin.pending': 'Li bendê',
    'admin.resolved': 'Çareser bû',
    'admin.all': 'Hemû',
    'admin.no_reports': 'Tu rapor nehat dîtin',
    'admin.back_to_list': 'Vegere lîsteyê',
    'admin.reporter': 'Raporker',
    'admin.reported_user': 'Bikarhênerê rapor kirî',
    'admin.reason': 'Sedem',
    'admin.reported_on': 'Di dîrokê de hate rapor kirin',
    'admin.reported_by': 'Ji aliyê ve hate rapor kirin',
    'admin.resolution_notes': 'Notên çareseriyê zêde bike...',
    'admin.dismiss': 'Red bike',
    'admin.warn': 'Bikarhêner hişyar bike',
    'admin.block': 'Bikarhêner asteng bike',
    'admin.report_resolved': 'Rapor bi serkeftî hate çareser kirin',
    'admin.resolve_error': 'Çareserkirina raporê bi ser neket',
    'admin.resolution': 'Çareserî',
    'messaging.unknown_user': 'Bikarhênerê nenas',
    'messaging.no_groups': 'Hîna koma tune',
    'messaging.create_group': 'Kom çêke',
    'messaging.group_chat': 'Sohbeta komê',
    'messaging.direct_messages': 'Rasterast',
    'messaging.groups': 'Kom',
    'messaging.group_name': 'Navê komê',
    'messaging.group_description': 'Danasîn',
    
    'nav.favorites': 'Bijare',
    'nav.profile': 'Profîl',
    'nav.login': 'Têkeve',
    'nav.logout': 'Derkeve',
    'nav.recent_searches': 'Lêgerînên dawî',
    'nav.no_recent_searches': 'Tu lêgerînên dawî tune',
    
    // Agency Pricing
    'agency_pricing.badge': 'Planên Ajansê',
    'agency_pricing.title': 'Ajansa xwe bi plana rast mezin bike',
    'agency_pricing.subtitle': 'Plana bêkêmasî ji bo ajansa xwe ya xanûberan hilbijêre. Tîma xwe mezin bike, xanûberên zêdetir birêve bibe û xizmeta bêhempa ji xerîdarên xwe re pêşkêş bike.',
    'agency_pricing.most_popular': 'Herî populer',
    'agency_pricing.per_month': '/mehê',
    'agency_pricing.processing': 'Tê xebitandin...',
    'agency_pricing.comparison_title': 'Berhevdana hûrgulî ya taybetmendiyan',
    'agency_pricing.feature': 'Taybetmendî',
    'agency_pricing.faq_title': 'Pirsên Pir Pirsîn',
    'agency_pricing.cta_title': 'Tu amade yî ku ajansa xwe mezin bikî?',
    'agency_pricing.cta_subtitle': 'Bi hezaran ajansên xanûberan ên serketî yên ku platforma me bikar tînin da ku tîmên xwe û xanûberan bi bandor birêve bibin re tevlî bibin.',
    'agency_pricing.cta_button': 'Bi Pro dest pê bike',
    'agency_pricing.sign_in_required': 'Ji kerema xwe têkeve da ku plana xwe bilind bikî',
    'agency_pricing.tier.starter': 'Destpêk',
    'agency_pricing.tier.starter_desc': 'Bêkêmasî ji bo ajansên biçûk ên ku nû dest pê dikin',
    'agency_pricing.tier.starter_cta': 'Dest pê bike',
    'agency_pricing.tier.pro': 'Pro',
    'agency_pricing.tier.pro_desc': 'Ji bo ajansên mezinbûyî yên bi tîmên mezintir',
    'agency_pricing.tier.pro_cta': 'Bilind bike bo Pro',
    'agency_pricing.tier.enterprise': 'Enterprise',
    'agency_pricing.tier.enterprise_desc': 'Ji bo ajansên mezin ên bi hewcedariyên bêsînor',
    'agency_pricing.tier.enterprise_cta': 'Biçe Enterprise',
    'agency_pricing.feature.brokers_3': 'Heta 3 broker',
    'agency_pricing.feature.brokers_10': 'Heta 10 broker',
    'agency_pricing.feature.brokers_unlimited': 'Brokerên bêsînor',
    'agency_pricing.feature.listings_basic': 'Îlanên xanûberan ên bingehîn',
    'agency_pricing.feature.listings_advanced': 'Îlanên xanûberan ên pêşketî',
    'agency_pricing.feature.listings_premium': 'Îlanên xanûberan ên premium',
    'agency_pricing.feature.messaging': 'Peyamên xerîdar',
    'agency_pricing.feature.scheduling': 'Plansazkirina dîtinan',
    'agency_pricing.feature.analytics': 'Panela analîtîkê',
    'agency_pricing.feature.analytics_advanced': 'Analîtîka pêşketî',
    'agency_pricing.feature.support': 'Piştgiriya pêşîn',
    'agency_pricing.feature.support_247': 'Piştgiriya 24/7',
    'agency_pricing.feature.branding': 'Branda xwerû',
    'agency_pricing.feature.api': 'Gihîştina API',
    'agency_pricing.comparison.brokers': 'Hejmara brokeren',
    'agency_pricing.comparison.listings': 'Îlanên xanûberan',
    'agency_pricing.comparison.basic': 'Bingehîn',
    'agency_pricing.comparison.advanced': 'Pêşketî',
    'agency_pricing.comparison.premium': 'Premium',
    'agency_pricing.faq.change_plan_q': 'Ez dikarim plana xwe paşê biguherînim?',
    'agency_pricing.faq.change_plan_a': 'Erê! Tu dikarî her dem plana xwe bilind an dakêşî. Guhertin tavilê çêdibe û em ê fatûreya te li gor wê rast bikin.',
    'agency_pricing.faq.exceed_limit_q': 'Çi dibe ger ez ji sînorê brokeran derbas bibim?',
    'agency_pricing.faq.exceed_limit_a': 'Divê tu plana bilindtir bilind bikî da ku brokerên zêdetir zêde bikî. Brokerên te yên heyî wekî normal kar dikin.',
    'agency_pricing.faq.trial_q': 'Ji bo planên bi pere ceribandinek belaş heye?',
    'agency_pricing.faq.trial_a': 'Em garantiya vegerandina dravê 14-rojî li ser hemû planên bi pere pêşkêş dikin. Ger tu razî nebî, ji bo vegerandina tevahî bi me re têkilî bigire.',
    'agency_pricing.faq.cancel_q': 'Ez çawa abonetiya xwe betalkirin?',
    'agency_pricing.faq.cancel_a': 'Tu dikarî abonetiya xwe ji panela ajansa xwe birêve bibî. Biçe tabê Fatûre û li "Abonetiyê Birêve Bibe" bikirtîne da ku betalkirin an guhertinê bikî.',
    'agency_pricing.view_plans': 'Planan û bihan bibîne',
    
    // Broker Pricing
    'broker_pricing.badge': 'Planên Komisyonkar',
    'broker_pricing.title': 'Hesabê Komisyonkariya Xwe Bilindtir Bike',
    'broker_pricing.subtitle': 'Plana bêkêmasî ji bo karê xweya emlakê hilbijêre. Zêdetir lîste, analîzên çêtir û piştgiriya pêşîn bistîne.',
    'broker_pricing.most_popular': 'Herî populer',
    'broker_pricing.current_plan': 'Plana Te ya Niha',
    'broker_pricing.per_month': '/meh',
    'broker_pricing.processing': 'Tê pêvajo...',
    'broker_pricing.loading': 'Tê barkirin...',
    'broker_pricing.manage_subscription': 'Abonetiyê Birêve Bibe',
    'broker_pricing.manage_note': 'Her dem jêr bibe an betalkirin • Her meh bixweber nû dibe',
    'broker_pricing.downgrade': 'Jêr Bibe',
    'broker_pricing.comparison_title': 'Berawirdkirina Hûrgulî ya Taybetmendiyan',
    'broker_pricing.feature': 'Taybetmendî',
    'broker_pricing.faq_title': 'Pirsên Pir Pirsîn',
    'broker_pricing.cta_title': 'Ji bo Mezinkirina Karê Xwe Amade yî?',
    'broker_pricing.cta_subtitle': 'Bi hezaran komisyonkarên serkeftî re ku platforma me bikar tînin ji bo lîstekirina zêdetir mûlkan û girtina zêdetir danûstandinan.',
    'broker_pricing.cta_button': 'Bi Pro Dest Pê Bike',
    'broker_pricing.sign_in_required': 'Ji kerema xwe têkevin da ku plana xwe bilindtir bikin',
    'broker_pricing.already_on_plan': 'Tu jixwe li ser vê planê yî',
    'broker_pricing.checkout_error': 'Destpêkirina dravdanê bi ser neket',
    'broker_pricing.portal_error': 'Vekirina rêveberiya abonetiyê bi ser neket',
    'broker_pricing.tier.normal': 'Normal',
    'broker_pricing.tier.normal_desc': 'Ji bo komisyonkarên kesane yên destpêker bêkêmasî ye',
    'broker_pricing.tier.normal_cta': 'Plana Niha',
    'broker_pricing.tier.pro': 'Pro',
    'broker_pricing.tier.pro_desc': 'Ji bo komisyonkarên mezinbûyî bi zêdetir lîsteyan',
    'broker_pricing.tier.pro_cta': 'Bilindtir bike bo Pro',
    'broker_pricing.tier.enterprise': 'Enterprise',
    'broker_pricing.tier.enterprise_desc': 'Ji bo komisyonkarên bi hejmara bilind bi hewcedariyên bêsînor',
    'broker_pricing.tier.enterprise_cta': 'Biçe Enterprise',
    'broker_pricing.feature.listings_5': '5 lîste her meh',
    'broker_pricing.feature.listings_10': '10 lîste her meh',
    'broker_pricing.feature.listings_15': '15 lîste her meh',
    'broker_pricing.feature.basic_features': 'Taybetmendiyên bingehîn ên mûlkan',
    'broker_pricing.feature.advanced_features': 'Taybetmendiyên pêşkeftî yên mûlkan',
    'broker_pricing.feature.premium_features': 'Taybetmendiyên premium yên mûlkan',
    'broker_pricing.feature.messaging': 'Peyamên xerîdar',
    'broker_pricing.feature.scheduling': 'Plansaziya dîtinê',
    'broker_pricing.feature.video_paid': 'Gerên vîdyoyî ($3 ji bo her lîsteyekê)',
    'broker_pricing.feature.video_free': 'Gerên vîdyoyî yên belaş di nav de',
    'broker_pricing.feature.analytics': 'Panela analîzê',
    'broker_pricing.feature.analytics_advanced': 'Analîzên pêşkeftî',
    'broker_pricing.feature.support': 'Piştgiriya pêşîn',
    'broker_pricing.feature.support_247': 'Piştgiriya pêşîn 24/7',
    'broker_pricing.feature.featured': 'Lîsteyên taybet',
    'broker_pricing.feature.extra_listings': 'Vebijarka lîsteyên zêde',
    'broker_pricing.feature.extra_price': '+$10 ji bo her lîsteya zêde',
    'broker_pricing.comparison.listings': 'Lîste her meh',
    'broker_pricing.comparison.video': 'Gerên Vîdyoyî',
    'broker_pricing.comparison.extra': 'Lîsteyên Zêde',
    'broker_pricing.comparison.duration': 'Demjimêra Lîsteyê',
    'broker_pricing.comparison.featured': 'Lîsteyên Taybet',
    'broker_pricing.comparison.30_days': '30 roj',
    'broker_pricing.comparison.per_listing': '$3 ji bo her lîsteyekê',
    'broker_pricing.comparison.free': 'Belaş',
    'broker_pricing.comparison.each': '$10 her yek',
    'broker_pricing.comparison.15_plus': '15 + zêde',
    'broker_pricing.faq.autorenew_q': 'Abonetiya min bixweber nû dibe?',
    'broker_pricing.faq.autorenew_a': 'Erê! Abonetiya te her meh bixweber nû dibe da ku tu tu carî gihîştina lîsteyên xwe winda nekî. Tu wê her meh di heman rojê de werin daxistin. Tu dikarî her dem ji portala Rêveberiya Abonetiyê betalkirî.',
    'broker_pricing.faq.upgrade_q': 'Ma ez dikarim her dem bilindtir an jêr bibim?',
    'broker_pricing.faq.upgrade_a': 'Bi teqez! Bilindkirin tavilê bi fatûrkirina rêjeyî bandor dibe. Jêrbûn di dawiya heyama fatûrkirina we ya niha de tê sepandin - hûn hemû taybetmendiyan heya wê demê diparêzin. Li "Abonetiyê Birêve Bibe" bikirtîne da ku guhertinên bikin.',
    'broker_pricing.faq.expire_q': 'Çi dibe dema ku lîsteyên min diqedin?',
    'broker_pricing.faq.expire_a': 'Lîste piştî 30 rojan diqedin. Tu wê 3 roj berî qedandinê hişyariyek bi e-nameyê bistînî. Tu dikarî bi cihên xwe yên mehane yên belaş wan nû bikî an $10 ji bo her lîsteya zêde bidî (tenê Enterprise).',
    'broker_pricing.faq.extra_q': '"Lîsteyên zêde" çi ne?',
    'broker_pricing.faq.extra_a': 'Endamên plana Enterprise dikarin lîsteyên din bi $10 her yek li ser 15 lîsteyên belaş ên mehane bikirin. Ev ji bo mehên bi hejmara bilind pir baş e.',
    'broker_pricing.faq.cancel_q': 'Ez çawa abonetiya xwe betalkirim?',
    'broker_pricing.faq.cancel_a': 'Li "Abonetiyê Birêve Bibe" bikirtîne da ku bigihîjî portala fatûrkirinê ku tu dikarî betalkirî, jêr bibî an awayê dravdanê nû bikî. Betalkirin di dawiya heyama fatûrkirinê de bandor dibe.',
    'messaging.conversation_pinned': 'Axaftin hat darve kirin',
    'messaging.conversation_unpinned': 'Axaftin ji darve hat rakirin',
    'messaging.pin_chat': 'Axaftinê darve bike',
    'messaging.unpin_chat': 'Axaftinê ji darve rake',
    'messaging.pin_failed': 'Darve kirin nehat',
    'messaging.unpin_failed': 'Rakirina darve nehat',
    'messaging.archive': 'Arşîv bike',
    'messaging.unarchive': 'Vegerîne',
    'messaging.show_archived': 'Arşîvkirî',
    'messaging.show_active': 'Aktîvan nîşan bide',
    'messaging.conversation_archived': 'Axaftin hate arşîv kirin',
    'messaging.conversation_unarchived': 'Axaftin hate vegerandin',
    'messaging.archive_failed': 'Arşîv kirin nehat',
    'messaging.unarchive_failed': 'Vegerandin nehat',
  },
  ckb: {
    // Header (Sorani - Arabic script)
    'nav.search': 'گەڕان بۆ خانووبەرە',
    'nav.search_agents': 'گەڕان بۆ بروکەر',
    'nav.sell': 'فرۆشتنی خانووبەرە',
    'nav.news': 'هەواڵەکان',
    'nav.build': 'بنیادنانی خانوو',
    'nav.agencies': 'ئاژانسەکان',
    'nav.buy': 'کڕین',
    'nav.valuation': 'نرخاندن',
    'nav.more': 'زیاتر',
    'nav.saved': 'پاشەکەوتکراوەکان',
    'nav.admin': 'بەڕێوەبەر',
    'nav.agency': 'ئاژانس',
    'nav.agency_dashboard': 'داشبۆردی ئاژانس',
    'nav.location_manager': 'بەڕێوەبەری شوێن',
    'nav.map_configuration': 'ڕێکخستنی نەخشە',
    'nav.hello': 'سڵاو',
    'nav.menu': 'مێنیو',
    'nav.manage_news': 'بەڕێوەبردنی هەواڵ',
    'nav.all_properties': 'هەموو خانووبەرەکان',
    'nav.new_production': 'پڕۆژەی نوێ',
    
    // Search Section
    'search.for_sale': 'بۆ فرۆشتن',
    'search.for_rent': 'بۆ کرێ',
    'search.sold_prices': 'نرخی فرۆشراو',
    'search.search_agents': 'گەڕان بۆ بروکەر',
    'search.area_label': 'ناوچە',
    'search.area_placeholder': 'ناوچە یان ناونیشان بنووسە',
    'search.area_examples': 'نموونە: بەغداد، بەسرە، هەولێر، نەجەف، کەربەلا',
    'search.expand_area': 'فراوانکردنی ناوچە بە',
    'search.property_type': 'جۆری خانووبەرە',
    'search.show_filters': 'پیشاندانی فلتەرەکانی گەڕان',
    'search.find_properties': 'دۆزینەوەی خانووبەرە بۆ فرۆشتن',
    'search.recent_searches': 'گەڕانی دوایی',
    'search.hide_history': 'شاردنەوەی مێژوو',
    'search.clear_history': 'سڕینەوەی مێژوو',
    
    // Property Types
    'property.all_types': 'هەموو جۆرەکان',
    'property.villas': 'ڤیلاکان',
    'property.townhouses': 'خانووی پەیوەست',
    'property.apartments': 'شوقەکان',
    'property.business': 'خانووی بازرگانی',
    'property.land': 'زەوی',
    'property.farms': 'کێڵگە/دارستان',
    'property.other': 'هیتر',
    
    // Cities/Governorates
    'city.baghdad': 'بەغداد',
    'city.basra': 'بەسرە',
    'city.erbil': 'هەولێر',
    'city.sulaymaniyah': 'سلێمانی',
    'city.najaf': 'نەجەف',
    'city.karbala': 'کەربەلا',
    'city.mosul': 'موسڵ',
    'city.kirkuk': 'کەرکووک',
    'city.anbar': 'ئەنبار',
    'city.dhi_qar': 'زی قار',
    'city.maysan': 'مەیسان',
    'city.wasit': 'واسیت',
    'city.babil': 'بابل',
    'city.qadisiyyah': 'قادسیە',
    'city.muthanna': 'موسەنا',
    'city.diyala': 'دیاڵە',
    'city.saladin': 'سەلاحەدین',
    'city.nineveh': 'نەینەوا',
    'city.dohuk': 'دهۆک',
    
    // Auth extended
    'auth.first_name': 'ناوی یەکەم',
    'auth.last_name': 'ناوی خێزان',
    'auth.first_name_placeholder': 'ناوی یەکەمت بنووسە',
    'auth.last_name_placeholder': 'ناوی خێزانت بنووسە',
    'auth.display_name': 'ناوی نیشاندان',
    'auth.display_name_placeholder': 'ناوی نیشاندان هەڵبژێرە',
    'auth.display_name_hint': 'ئەمە یەکە ناوەیە کە کەسانی تر دەیبینن',
    'auth.date_of_birth': 'بەرواری لەدایکبوون',
    'auth.fill_all_fields': 'تکایە هەموو خانە پێویستەکان پڕ بکەرەوە',
    'auth.must_be_18': 'دەبێت تەمەنت لانیکەم ١٨ ساڵ بێت بۆ تۆمارکردن',
    'auth.must_be_18_hint': 'دەبێت تەمەنت لانیکەم ١٨ ساڵ بێت',
    
    // Identity Verification
    'verification.verify_identity': 'ناسنامە پشتڕاست بکەرەوە',
    'verification.verified': 'پشتڕاستکراوە',
    'verification.title': 'پشتڕاستکردنەوەی ناسنامە',
    'verification.description': 'بەڵگەنامەی حکومی بەربارە بکە بۆ پشتڕاستکردنەوەی ناسنامەکەت و بەدەستهێنانی نیشانەی پشتڕاستکراو.',
    'verification.document_type': 'جۆری بەڵگەنامە',
    'verification.doc_id_card': 'کارتی ناسنامە',
    'verification.doc_passport': 'پاسپۆرت',
    'verification.doc_drivers_license': 'مۆڵەتی شۆفێری',
    'verification.upload_document': 'بەڵگەنامە بەربار بکە',
    'verification.click_to_upload': 'کرتە بکە بۆ بەرباركردن یان ڕاکێشە و بەردە',
    'verification.file_requirements': 'JPG، PNG، WEBP یان PDF (زۆرینە ١٠ مێگابایت)',
    'verification.submit': 'بنێرە بۆ پشتڕاستکردنەوە',
    'verification.uploading': 'بەرباردەکرێت...',
    'verification.submitted': 'پشتڕاستکردنەوە نێردرا',
    'verification.submitted_desc': 'داواکاری پشتڕاستکردنەوەکەت نێردرا. بەم زووانە پێداچوونەوەی دەکەین.',
    'verification.pending_message': 'پشتڕاستکردنەوەکەت لە چاوپێکەوتندایە. ئەمە بەشێوەی ئاسایی ١-٢ ڕۆژی کاری دەخایەنێت.',
    'verification.rejected_message': 'پشتڕاستکردنەوەی پێشووت ڕەتکرایەوە. تکایە بەڵگەنامەیەکی نوێ بنێرە.',
    'verification.status_approved': 'پەسەندکراوە',
    'verification.status_pending': 'لە چاوەڕوانیدایە',
    'verification.status_rejected': 'ڕەتکراوەتەوە',
    'verification.invalid_file_type': 'جۆری فایل نادروستە',
    'verification.allowed_types': 'تکایە فایلی JPG، PNG، WEBP، یان PDF بەربار بکە',
    'verification.file_too_large': 'فایلەکە زۆر گەورەیە',
    'verification.max_size': 'زۆرترین قەبارەی فایل ١٠ مێگابایتە',
    'verification.error': 'هەڵەی پشتڕاستکردنەوە',
    'verification.admin_title': 'پشتڕاستکردنەوەکانی ناسنامە',
    'verification.admin_description': 'پێداچوونەوە و پەسەندکردنی داواکاری پشتڕاستکردنەوەی ناسنامە',
    'verification.no_requests': 'داواکاری پشتڕاستکردنەوە نییە',
    'verification.view': 'پێداچوونەوە',
    'verification.review_title': 'پێداچوونەوەی پشتڕاستکردنەوە',
    'verification.review_notes': 'تێبینییەکانی پێداچوونەوە',
    'verification.review_notes_placeholder': 'تێبینی لەسەر بڕیارەکەت زیاد بکە (بەدڵخوازی)',
    'verification.approve': 'پەسەند بکە',
    'verification.reject': 'ڕەت بکەرەوە',
    'verification.approved': 'پشتڕاستکردنەوە پەسەندکرا',
    'verification.rejected': 'پشتڕاستکردنەوە ڕەتکرایەوە',
    'verification.review_complete': 'پشتڕاستکردنەوە پرۆسەکرا.',

    // Common
    'common.price': 'نرخ',
    'common.rooms': 'ژوورەکان',
    'common.size': 'قەبارە',
    'common.governorate': 'پارێزگا',
    'common.select_governorate': 'پارێزگا هەڵبژێرە',
    'common.free': 'بەخۆڕایی',
    
    // Hero/News Section
    'hero.find_dream_home': 'ماڵی خەونەکانت لە عێراق بدۆزەرەوە',
    'hero.explore_description': 'هەزاران خانوو بۆ فرۆشتن و نرخی کۆتایی لە هەموو عێراقەوە بدۆزەرەوە.',
    'hero.badge': 'پلاتفۆرمی سەرەکی خانووبەرەی عێراق',
    
    // Home Features
    'home.feature.properties': 'هەزاران خانووبەرە',
    'home.feature.properties_desc': 'لە هەموو پارێزگاکانی عێراقدا خانوو، شوقە و زەوی ببینە.',
    'home.feature.market': 'شارەزایی ناوخۆیی',
    'home.feature.market_desc': 'زانینی قووڵ لە هەموو پارێزگا و گەڕەکەکانی عێراق.',
    'home.feature.trusted': 'بەڕێوەبەرانی پشتڕاستکراو',
    'home.feature.trusted_desc': 'پەیوەندی بە پسپۆڕانی خانووبەرەی متمانەپێکراو و پشتڕاستکراوەوە بگرە.',
    'home.why_choose': 'بۆچی ئەقارنێت هەڵبژێریت',
    'home.why_choose_desc': 'هەموو ئەوەی پێویستت پێیە بۆ دۆزینەوەی خانووبەرەی تەواوت',
    
    // CTA Section
    'cta.ready': 'ئامادەیت ماڵەکەت بدۆزیتەوە؟',
    'cta.desc': 'هەزاران خانووبەرە لە سەرانسەری عێراق ببینە',
    'cta.explore': 'خانووبەرەکان بگەڕێ',
    
    // Property Listings
    'listings.latest_for_sale': 'دواترین بۆ فرۆشتن',
    'listings.properties_found': 'خانووبەرە دۆزرایەوە',
    'listings.all_news': 'هه‌موو هه‌واڵه‌کان',
    'listings.add_property': 'خانووبەرە زیاد بکە',
    'listings.edit': 'دەستکاری',
    'listings.monthly_fee': 'کرێی مانگانە',
    'listings.open_floor_plan': 'پلانی کراوە',
    'listings.large_balcony': 'بالکۆنی گەورە',
    'listings.family_friendly': 'گونجاو بۆ خێزان',
    'listings.photos': 'وێنەکان',
    'listings.per_month': 'مانگ',
    'listings.delete_confirm': 'دڵنیایت دەتەوێت ئەم خانووبەرە بسڕیتەوە؟',

    // Property Types  
    'propertyTypes.allTypes': 'هەموو جۆرەکان',
    'propertyTypes.villa': 'ڤیلاکان', 
    'propertyTypes.apartment': 'شوقەکان',
    'propertyTypes.townhouse': 'خانووی پێکەوەبەستراو',
    'propertyTypes.business': 'خانووی بازرگانی',
    'propertyTypes.plot': 'زەوییەکان',
    'propertyTypes.farm': 'کێڵگە/دارستان',
    'propertyTypes.other': 'ئەوانی تر',
    
    // Forms
    'form.price': 'نرخ',
    'form.priceUSD': 'نرخ (دۆلار)',
    'form.priceIQD': 'نرخ (دینار عێراقی)',
    'form.priceConversionNote': 'نرخ بە هەر دراوێک بنووسە - ئەوی تر بە خۆکاری دەژمێردرێت (رێژە: 1 دۆلار ≈ 1,310 دینار)',
    'form.address': 'ناونیشان',
    'form.city': 'شار',
    'form.rooms': 'ژوورەکان',
    'form.bathrooms': 'حەمامەکان',
    'form.size': 'قەبارە (م²)',
    'form.type': 'جۆر',
    'form.propertyType': 'جۆری خانووبەرە',
    'form.monthly_fee': 'کرێی مانگانە (دینار)',
    'form.description': 'وەسف',
    'form.payment_plan': 'پلانی پارەدان',
    'form.payment_plan_placeholder': 'بۆ نموونە: 30% پارەی پێشوەخت، 70% لە کاتی ڕاگەیاندن، یان قستی مانگانە...',
    'form.payment_plan_hint': 'قستەکانی پارەدان زیاد بکە بۆ پیشاندانی هەڵبژاردەکانی پارەدان بە کڕیاران',
    'payment_plan.total': 'کۆی گشتی',
    'payment_plan.amount': 'بڕ',
    'payment_plan.description': 'وەسف',
    'payment_plan.date': 'بەروار',
    'payment_plan.percentage': 'لە سەد',
    'payment_plan.add_installment': 'قست زیادبکە',
    'payment_plan.hint': 'بەتاڵی بهێڵەوە ئەگەر پلانی پارەدان پێشکەش ناکەیت',
    'payment_plan.down_payment': 'پارەی پێشوەخت',
    'payment_plan.first_payment': 'پارەدانی یەکەم',
    'payment_plan.second_payment': 'پارەدانی دووەم',
    'payment_plan.third_payment': 'پارەدانی سێیەم',
    'payment_plan.fourth_payment': 'پارەدانی چوارەم',
    'payment_plan.fifth_payment': 'پارەدانی پێنجەم',
    'payment_plan.sixth_payment': 'پارەدانی شەشەم',
    'payment_plan.seventh_payment': 'پارەدانی حەوتەم',
    'payment_plan.final_payment': 'پارەدانی کۆتایی',
    'payment_plan.payment_description': 'وەسفی پارەدان',
    'payment_plan.enable': 'پلانی پارەدان تێبخە',
    'form.floor_plans': 'نەخشەی نهۆمەکان',
    'form.upload_floor_plans': 'وێنەی نەخشەی نهۆمەکان بار بکە',
    'form.features': 'تایبەتمەندییەکان (بە کۆما جیاکراوە)',
    'form.amenities': 'ئاسانکارییەکان',
    'form.amenities_hint': 'هەموو ئاسانکارییەکان هەڵبژێرە کە بۆ ئەم خانووبەرەیە دەگونجێن',
    'amenities.balcony': 'بەڵکۆنە',
    'amenities.electricity_generator': 'جەنەرەیتەر',
    'amenities.central_ac': 'کەناڵی ناوەندی',
    'amenities.double_glazed_windows': 'پەنجەرەی دوو شووشە',
    'amenities.satellite_tv': 'دیش',
    'amenities.cctv': 'کامێرای چاودێری',
    'amenities.security_staff': 'پاسەوان',
    'amenities.sewerage_system': 'شەبەکەی لافاو',
    'amenities.furnished_kitchen': 'چێشتخانەی تەواو',
    'amenities.24hr_electricity': 'کارەبا 24 کاتژمێر',
    'amenities.market': 'بازاڕی نزیک',
    'amenities.elevator': 'ئەلیڤەیتەر',
    'amenities.school': 'قوتابخانەی نزیک',
    'amenities.mosque': 'مزگەوتی نزیک',
    'amenities.central_gas': 'گازی ناوەندی',
    'amenities.maintenance': 'خزمەتگوزاری چاککردنەوە',
    'amenities.cleaning': 'خزمەتگوزاری پاککردنەوە',
    'amenities.fire_fighting': 'ئامێری کوژاندنەوەی ئاگر',
    'amenities.car_washing': 'شوشتنی ئۆتۆمبێل',
    'amenities.smart_home': 'ماڵی زیرەک',
    'amenities.earthquake_resistant': 'بەرگری لە بوومەلەرزە',
    'amenities.central_fire_system': 'سیستەمی ناوەندی کوژاندنەوەی ئاگر',
    'amenities.hot_water_system': 'سیستەمی ئاوی گەرم',
    'amenities.central_vacuum': 'ڤاکیوومی ناوەندی',
    'amenities.fountain': 'فەورە',
    'amenities.24hr_water': 'ئاو 24 کاتژمێر',
    'amenities.garbage_chute': 'بۆری زبڵ',
    'amenities.kitchen_counter': 'مێزی چێشتخانە',
    'amenities.street_lights': 'ڕووناکی شەقام',
    'amenities.playground': 'یاریگای منداڵان',
    'amenities.gym': 'یاریگای وەرزشی',
    'amenities.garden': 'باخچە',
    'amenities.parking': 'پارکینگ',
    'amenities.wifi': 'وایفای',
    'amenities.security_door': 'دەروازەی ئاسایش',
    'amenities.split_ac': 'سپلیت',
    'amenities.bathtub': 'باسینی سەرشۆی',
    'amenities.refrigerator': 'سەرماوە',
    'amenities.washing_machine': 'مەکینەی جلشۆر',
    'amenities.swimming_pool': 'مەلەوانگە',
    'amenities.solar_panels': 'پەنەڵی خۆر',
    'amenities.water_tank': 'تانکی ئاو',
    'amenities.water_well': 'بیری ئاو',
    'amenities.septic_tank': 'بیارە',
    'amenities.central_heating': 'گەرمکردنەوەی ناوەندی',
    'amenities.desert_cooler': 'سەرینکەری دەشت',
    'amenities.intercom': 'ئینتەرکۆم',
    'amenities.external_camera': 'کامێرای دەرەوە',
    'amenities.guard_room': 'ژووری پاسەوان',
    'amenities.rooftop_terrace': 'بەشی سەربان',
    'amenities.basement': 'ژێرزەوی',
    'amenities.storage_room': 'ژووری کۆگا',
    'amenities.internal_stairs': 'پلیکانی ناوەوە',
    'amenities.external_stairs': 'پلیکانی دەرەوە',
    'amenities.corner_unit': 'سوچکە',
    'amenities.two_streets': 'دوو شەقام',
    'amenities.double_entrance': 'دوو دەروازە',
    'amenities.marble_flooring': 'زەوی مەرمەر',
    'amenities.ceramic_tiles': 'کاشی',
    'amenities.parquet_flooring': 'پارکێت',
    'amenities.gypsum_decorations': 'جیپسوم',
    'amenities.false_ceiling': 'بەرباسک',
    'amenities.steel_doors': 'دەرگای ئاسن',
    'amenities.aluminum_windows': 'پەنجەرەی ئەلەمنیۆم',
    'amenities.pvc_windows': 'پەنجەرەی PVC',
    'amenities.guest_room': 'دیوانخانە',
    'amenities.servant_quarters': 'ژووری خزمەتکار',
    'amenities.covered_parking': 'پارکینگی سەرپۆشراو',
    'amenities.concrete_fence': 'سووری کۆنکریت',
    'amenities.iron_fence': 'سووری ئاسن',
    'amenities.street_pavement': 'شەقامی ئەسفەڵت',
    'amenities.fully_furnished': 'تەواو ئاماریکراو',
    'amenities.hospital': 'نەخۆشخانەی نزیک',
    'amenities.category.utilities': 'کارەبا و ئاو',
    'amenities.category.climate': 'سەرینکەر و گەرمکەر',
    'amenities.category.security': 'پاسەوانی',
    'amenities.category.building': 'تایبەتمەندیەکانی باڵەخانە',
    'amenities.category.interior': 'ناوەوە و زەوی',
    'amenities.category.rooms': 'ژوورەکان',
    'amenities.category.outdoor': 'دەرەوە و پارکینگ',
    'amenities.category.appliances': 'ئامێرەکان',
    'amenities.category.services': 'خزمەتگوزاریەکان',
    'amenities.category.nearby': 'نزیک لە',
    'common.selected': 'هەڵبژێردراو',
    'form.property_images': 'وێنەکانی خانووبەرە',
    'form.required': 'پێویست',
    'form.optional': 'ئیختیاری',
    'form.cancel': 'هەڵوەشاندنەوە',
    'form.save_changes': 'گۆڕانکارییەکان پاشەکەوت بکە',
    'form.uploading': 'بارکردن...',
    'form.upload_images': 'وێنە بارکردن',
    'form.click_to_upload': 'کلیک بکە بۆ بارکردنی وێنەکان',
    'form.file_formats': 'JPEG, PNG, WebP, GIF (زۆرترین 10MB بۆ هەر وێنەیەک)',
    'form.select_type': 'جۆر هەڵبژێرە',
    'form.location_on_map': 'شوێنی خانووبەرە لەسەر نەخشە',
    'form.location_hint': 'لەسەر نەخشە کلیک بکە بۆ دیاریکردنی شوێنی وردی خانووبەرەکە',
    'form.hide_preview': 'پێشبینین بشارەوە',
    'form.show_preview': 'پێشبینین نیشان بدە',
    'form.selected_images': 'وێنە هەڵبژێردراوەکان',
    'form.click_set_primary': 'لەسەر وێنەیەک کلیک بکە بۆ دانانی وەک سەرەکی',
    'form.uploaded_images': 'وێنە بارکراوەکان',
    'form.images': 'وێنە',
    'form.uploading_images': 'وێنەکان بارکراوە...',
    'form.city_placeholder': 'بۆ شارێک بگەڕێ...',
    'form.new_construction': 'بیناسازی نوێ (ماڵی داهاتوو)',
    'form.preview': 'پێشبینین',
    'form.preview_description': 'ڕیکلامەکەت بەم شێوەیە لەسەر ماڵپەڕەکە دەردەکەوێت',
    'form.fullscreen': 'پڕ شاشە',
    'form.exit_fullscreen': 'دەرچوون لە پڕ شاشە',
    'form.click_preview_fullscreen': 'کرتە بکە لەسەر پێشبینین بۆ بینینی پڕ شاشە',
    'form.preview_contact_disabled': 'هەڵبژاردنەکانی پەیوەندی لە دۆخی پێشبینین ناچالاکە',
    'form.invalid_format': 'فۆرماتی فایلی هەڵە',
    'form.invalid_format_desc': '{filename} فۆرماتی هەڵەی هەیە. ڕێگەپێدراو: JPEG، PNG، WebP، GIF',
    'form.file_too_large': 'فایلەکە زۆر گەورەیە',
    'form.file_too_large_desc': '{filename} گەورەترە لە 10MB',
    'form.upload_failed': 'بارکردن سەرکەوتوو نەبوو',
    'form.upload_failed_desc': 'نەتوانرا وێنەکان بارکرێن',
    'form.images_required': 'وێنە پێویستە',
    'form.images_required_desc': 'تکایە لانیکەم یەک وێنە بۆ خانووبەرەکە باربکە',
    'form.sold_price_usd': 'نرخی فرۆشتن (دۆلار)',
    'form.sold_price_placeholder': 'نرخی ڕاستەقینەی فرۆشتن بنووسە',
    'form.sold_price_description': 'نرخی ڕاستەقینەی فرۆشتن (لەوانەیە جیاواز بێت لە نرخی لیستەکە)',
    'form.video_url': 'لینکی ڤیدیۆی گەشت',
    'form.video': 'ڤیدیۆ',
    'form.add_video': 'ڤیدیۆیەکی تر زیاد بکە',
    'form.video_addon': '+$3 زیادە',
    'form.video_included': 'بەخۆڕایی لەخۆگرتووە',
    'form.video_url_hint': 'ڤیدیۆی گەشتی YouTube یان Vimeo زیاد بکە. دوای دروستکردنی لیستەکە دەتوانیت زیادەی ڤیدیۆ بکڕیت.',
    'form.video_url_hint_free': 'ڤیدیۆی گەشتی YouTube یان Vimeo زیاد بکە - لەگەڵ بەشداریەکەت بەخۆڕایی!',
    'form.video_url_hint_addon': 'ڤیدیۆی گەشتی YouTube یان Vimeo زیاد بکە. $3 زیاد دەکرێت بۆ تێچوونی ڕێکلامەکەت.',
    'form.include_video_tour': 'گەشتی ڤیدیۆ لەخۆ بگرە',
    'form.show_map': 'نەخشە پیشان بدە',
    'form.no_location': 'هیچ شوێنێک هەڵنەبژێردراوە',
    
    // Footer
    'footer.contact': 'پەیوەندی',
    'footer.customer_service': 'خزمەتگوزاری کڕیار و پەیوەندی',
    'footer.about': 'دەربارەی Aqarnet',
    'footer.press': 'ڕۆژنامەوانی',
    'footer.investors': 'بۆ وەبەرهێنەران',
    'footer.advertising': 'ڕیکلام و مارکێتینگ',
    'footer.jobs': 'کارکردن لەگەڵ ئێمە',
    'footer.statistics': 'ئامارەکانی خانووبەرە',
    'footer.apps': 'ئەپڵیکەیشنەکان',
    'footer.iphone_ipad': 'iPhone و iPad',
    'footer.android': 'Android',
    'footer.follow': 'Aqarnet بەدواداچوون',
    'footer.newsletter': 'نامەی هەواڵی Aqarnet',
    'footer.whatsapp': 'WhatsApp',
    'footer.facebook': 'Facebook',
    'footer.instagram': 'Instagram',
    'footer.linkedin': 'LinkedIn',
    'footer.pinterest': 'Pinterest',
    'footer.tiktok': 'TikTok',
    'footer.threads': 'Threads',
    'footer.podcast': 'پۆدکاستی Aqarnet',
    'footer.all_properties': 'هەموو خانووبەرەکان',
    'footer.privacy': 'پاراستنی زانیاری کەسی',
    'footer.cookies': 'کوکیز',
    'footer.terms': 'مەرجەکان و زانیاری یاسایی',
    'footer.cookie_settings': 'ڕێکخستنەکانی کوکی',
    
    // Agents
    'agents.title': 'ئەیجنتێک بدۆزەرەوە',
    'agents.subtitle': 'بیر لە فرۆشتن دەکەیتەوە؟ یارمەتیت دەدەین ئەیجنتێک بدۆزیتەوە کە پسپۆڕی ناوچەکەتە!',
    'agents.area_search': 'گەڕان بە ناوچە',
    'agents.name_search': 'گەڕان بە ناو',
    'agents.search_placeholder': 'ناوچە یان ناونیشان بنووسە',
    'agents.show': 'پیشاندان',
    'agents.sort_by': 'ڕیزکردن بە',
    'agents.agents_count': 'ئەیجنتەکان',
    'agents.companies_label': 'کۆمپانیاکانی ئەیجنت',
    'agents.recent_sales': 'دوایین بەرواری فرۆشتن',
    'agents.most_sales': 'زۆرترین فرۆشتن',
    'agents.alphabetical': 'ڕیزی ئەلفوبێی',
    'agents.showing_results': 'پیشاندانی',
    'agents.agents_label': 'ئەیجنت',
    'agents.loading': 'بارکردنی ئەیجنتەکان...',
    'agents.no_results': 'هیچ ئەیجنتێک نەدۆزرایەوە.',
    'agents.phone': 'تەلەفۆن:',
    'agents.status': 'دۆخ:',
    'agents.active_agent': 'ئەیجنتی چالاک',
    'agents.anonymous_agent': 'ئەیجنتی نەناسراو',
    'agents.agent_label': 'ئەیجنت',
    'agents.show_more': 'زیاتر پیشان بدە',

    // Properties for Sale Page
    'propertiesForSale.title': 'خانووبەرە بۆ فرۆشتن - سوید',
    'propertiesForSale.statistics.municipalities': 'شارەوانییەکان',
    'propertiesForSale.statistics.forSale': 'بۆ فرۆشتن',
    'propertiesForSale.statistics.sold': 'فرۆشراو ئەم هەفتەیە',
    'propertiesForSale.map.fullscreen': 'نەخشە / پڕ شاشە',
    'propertiesForSale.map.area': 'ناوچە',
    'propertiesForSale.filters.searchWithin': 'گەڕان لەناو',
    'propertiesForSale.filters.km': 'کم',
    'propertiesForSale.filters.propertyType': 'جۆری خانووبەرە',
    'propertiesForSale.filters.allTypes': 'هەموو جۆرەکان',
    'propertiesForSale.filters.villa': 'ڤیلا',
    'propertiesForSale.filters.apartment': 'شوقە',
    'propertiesForSale.filters.townhouse': 'خانووی پەیوەست',
    'propertiesForSale.filters.plot': 'زەوی',
    'propertiesForSale.filters.commercial': 'بازرگانی',
    'propertiesForSale.filters.other': 'هیتر',
    'propertiesForSale.filters.rooms': 'ژوورەکان',
    'propertiesForSale.filters.bathrooms': 'حەمامەکان',
    'propertiesForSale.filters.min': 'کەمترین',
    'propertiesForSale.filters.max': 'زۆرترین',
    'propertiesForSale.filters.size': 'قەبارە',
    'propertiesForSale.filters.price': 'نرخ',
    'propertiesForSale.filters.features': 'تایبەتمەندییەکان',
    'propertiesForSale.filters.balcony': 'بالکۆن/پاقژ/تەراس',
    'propertiesForSale.filters.pets': 'ئاژەڵی ماڵی',
    'propertiesForSale.filters.newBuild': 'بنیادنانی نوێ',
    'propertiesForSale.filters.showAll': 'هەموو پیشان بدە',
    'propertiesForSale.filters.buildYear': 'ساڵی بنیادنان',
    'propertiesForSale.filters.searchResults': 'ئەنجامەکانی گەڕان',
    'propertiesForSale.filters.searchPlaceholder': 'لە ناوچەکان، شارەکان هتد بگەڕێ...',
    'propertiesForSale.filters.searchingFor': 'گەڕان بۆ:',
    'propertiesForSale.filters.any': 'هەر',
    'propertiesForSale.filters.keywords': 'وشە سەرەکییەکان',
    'propertiesForSale.filters.showResults': 'ئەنجامەکان پیشان بدە',
    'propertiesForSale.filters.clearFilters': 'پاککردنەوە',
    'propertiesForSale.filters.realEstateSituation': 'دۆخی خانووبەرە',
    'propertiesForSale.filters.situation.tapu': 'تاپو',
    'propertiesForSale.filters.situation.landContract': 'گرێبەستی زەوی',
    'propertiesForSale.filters.situation.noDocuments': 'بەڵگەنامەت نییە',
    'propertiesForSale.filters.situation.investment': 'وەبەرهێنان',
    'propertiesForSale.filters.situation.musataha': 'موساتەحە',
    'propertiesForSale.filters.situation.card': 'کارت',
    'propertiesForSale.filters.situation.agriculturalTapu': 'تاپوی کشتوکاڵی',
    'propertiesForSale.sort.newest': 'نوێترین یەکەم',
    'propertiesForSale.sort.price_low': 'نرخ: کەمەوە بۆ زۆر',
    'propertiesForSale.sort.price_high': 'نرخ: زۆرەوە بۆ کەم',
    'propertiesForSale.sort.size': 'قەبارە',
    'propertiesForSale.noResults': 'هیچ موڵکێک نەدۆزرایەوە',
    'propertiesForSale.tryAdjustingFilters': 'هەوڵ بدە فلتەرەکانت ڕێکبخەیت یان لە ناوچەیەکی تر بگەڕێیت',

    // Property card
    'property.sold': 'فرۆشراو',
    'property.photos': 'وێنە',
    'property.rooms_short': 'ژوور',
    'property.plot': 'زەوی',
    'property.per_month': '/مانگ',
    'property.delete_title': 'خانووبەرە بسڕیتەوە؟',
    'property.delete_confirm': 'دڵنیایت دەتەوێت {address} بسڕیتەوە؟ ئەم کارە ناگەڕێتەوە.',
    'property.deleted': 'خانووبەرە سڕایەوە',
    'property.deleted_desc': 'خانووبەرەکە لابرا',
    'property.delete_error': 'نەتوانرا خانووبەرە بسڕدرێتەوە',
    
    // Saved Searches
    'loading.saved_searches': 'گەڕانە پاشەکەوتکراوەکان باردەکرێن...',
    'saved_searches.title': 'گەڕانە پاشەکەوتکراوەکان',
    'saved_searches.empty': 'هێشتا گەڕانی پاشەکەوتکراو نییە',
    'saved_searches.description': 'گەڕانێک پاشەکەوت بکە بۆ وەرگرتنی ئاگادارکردنەوە کاتێک خانووبەرەی لێکچووی نوێ زیاد دەکرێت',
    'saved_searches.created': 'دروستکرا',
    'saved_searches.alerts_on': 'ئاگادارکردنەوەکان کراوەن',
    'saved_searches.alerts_off': 'ئاگادارکردنەوەکان داخراون',
    'saved_searches.email_enabled': 'ئاگادارکردنەوەی ئیمەیڵ چالاکە',
    'saved_searches.email_disabled': 'ئاگادارکردنەوەی ئیمەیڵ ناچالاکە',
    'saved_searches.settings_updated': 'ڕێکخستن نوێکرایەوە',
    'saved_searches.removed': 'گەڕان لابرا',
    'saved_searches.removed_desc': 'گەڕانە پاشەکەوتکراوەکە لابرا',
    'saved_searches.error': 'هەڵە',
    'saved_searches.update_error': 'نەتوانرا ڕێکخستن نوێبکرێتەوە',
    'saved_searches.delete_error': 'نەتوانرا گەڕان بسڕدرێتەوە',
    'saved_searches.all_properties': 'هەموو خانووبەرەکان',

    // Rental properties
    'rental.for_sale': 'بۆ فرۆشتن',
    'rental.for_rent': 'بۆ کرێ',
    'rental.listing_type': 'جۆری ڕیکلام',
    'rental.rental_details': 'وردەکاری کرێ',
    'rental.rental_period': 'ماوەی کرێ',
    'rental.rental_period_hint': 'کرێ چەند جار دەدرێت',
    'rental.daily': 'ڕۆژانە',
    'rental.weekly': 'هەفتانە',
    'rental.monthly': 'مانگانە',
    'rental.yearly': 'ساڵانە',
    'rental.per_daily': '/ڕۆژ',
    'rental.per_weekly': '/هەفتە',
    'rental.per_monthly': '/مانگ',
    'rental.per_yearly': '/ساڵ',
    'rental.deposit_amount': 'بڕی ڕەهن (دۆلار)',
    'rental.minimum_lease': 'کەمترین ماوەی کرێ (مانگ)',
    'rental.months': 'مانگ',
    'rental.available_from': 'بەردەستە لە',
    'rental.furnished': 'کەلوپەلدار',
    'rental.utilities_included': 'خزمەتگوزارییەکان تێدایە',
    'rental.pets_allowed': 'ئاژەڵی ماڵی ڕێگەپێدراوە',

    // Cookie Policy
    'cookie.title': 'سیاسەتی کووکی',
    'cookie.last_updated': 'دوایین نوێکردنەوە:',
    'cookie.section1.title': 'بەشی ١ - دەستوورە گشتییەکان سەبارەت بە جێبەجێکردنی تەکنەلۆژیاکانی شوێنکەوتن',
    'cookie.section1.p1': 'کۆمپانیای Ploqs AB، لە ئێستاوە بە ناوی "دابینکەری خزمەتگوزاری" ناودەبرێت، لەگەڵ هاوبەشەکان و ڕێکەوتکارەکانی لاوەکی کە لەلایەن دابینکەری خزمەتگوزاریەوە هەڵبژێردراون، شێوازە جیاوازەکانی مێکانیزمەکانی شوێنکەوتنی دیجیتاڵ جێبەجێ دەکات و بەکاردەهێنێت، بەمەش سنووردار نییە بۆ کووکییەکان، وێب بیکۆن، پیکسڵ تاگ و چارەسەرە تەکنەلۆژیەکانی تری هاوشێوە (بە یەکەوە بە ناوی "تەکنەلۆژیاکانی شوێنکەوتن" ناودەبرێن) لەسەر پلاتفۆرمە دیجیتاڵییەکان، ماڵپەڕەکان و بەرنامە مۆبایلەکانی دابینکەری خزمەتگوزاری (بە یەکەوە بە ناوی "کەناڵە دیجیتاڵییەکانی دابینکەری خزمەتگوزاری" ناودەبرێن). ئەم سیاسەتە بۆ مامەڵەکردن لەگەڵ تەکنەلۆژیاکانی شوێنکەوتن ئەو ئامانجانە دیاری دەکات کە دابینکەری خزمەتگوزاری تەکنەلۆژیاکانی ناوبراو بۆیان و بە چ شێوازێک بەکاردەهێنێت.',
    'cookie.section1.p2': 'تەکنەلۆژیاکانی شوێنکەوتن سەرەتا پێکهاتوون لە فایلە داتایی دەقی بچووک کە لەسەر ئامێرەکانی تەرمیناڵی بەکارهێنەر یان بەرنامەی وێبگەڕ دادەنرێن بە مەبەستی کۆکردنەوەی زانیاری سەبارەت بە کارلێکی بەکارهێنەر لەگەڵ پلاتفۆرمە دیجیتاڵییەکان و بەرنامە مۆبایلەکان و گەشتکردن تیایاندا.',
    'cookie.section1.h1': 'پرۆسەکردنی داتای کەسی و لەبەرچاوگرتنەکانی پاراستنی داتا',
    'cookie.section1.p3': 'زانیارییەکانی کۆکراوە لە ڕێگەی جێبەجێکردنی تەکنەلۆژیاکانی شوێنکەوتنەوە لە زۆربەی حاڵەتەکاندا داتان کە، ڕاستەوخۆ یان ناڕاستەوخۆ و لەگەڵ داتای بەردەست ی تردا، دەتوانرێت بەکاربهێنرێت بۆ ناسینەوەی تاکەکان، بەمەش سنووردار نییە بۆ ناونیشانەکانی IP، ناسنامەی کووکی تاکەکەس، ناسنامەی ڕیکلام (IDFA)، ناسنامەی ئامێر و مێتاداتای جیاواز کە جیاکردنەوەی ئامێرە تەرمیناڵییە دیاریکراوەکان ڕەوا دەکات.',
    'cookie.section1.h2': 'هەڵبژاردنەکانی بەکارهێنەر',
    'cookie.section1.p4': 'بەکارهێنەر مافی هەیە لە هەر کاتێکدا ڕەزامەندییە پێشتر دراوەکان پێداچوونەوەی پێبکات بە ڕێگەی گەشتکردن بۆ هەڵبژاردنەکانی بەڕێوەبردنی ڕەزامەندی کە لە ڕێگەی ڕووکاری دیجیتاڵی دابینکەری خزمەتگوزاریەوە دابین کراون.',
    'cookie.section1.h3': 'بەردەوامی و ماوەکانی هەڵگرتنی تەکنەلۆژیاکانی شوێنکەوتن',
    'cookie.section1.p5': 'تەکنەلۆژیاکانی شوێنکەوتن بەردەوامی جیاواز پیشان دەدەن بەپێی ئامانج و کارکردنی هەر تەکنەلۆژییەک. تەکنەلۆژیاکانی شوێنکەوتنی سەر دانیشتن کاتێک بەکارهێنەر پەیوەندی بە پلاتفۆرمی دیجیتاڵەوە دەکات دەستپێدەکەن و لە دواییندا کاتی داخستنی وێبگەڕی بەکارهێنەر کۆتایی دێت.',
    'cookie.section2.title': 'بەشی ٢ - تەکنەلۆژیاکانی شوێنکەوتنی پێویست بۆ دابینکردنی خزمەتگوزاری',
    'cookie.section2.p1': 'تەکنەلۆژیاکانی شوێنکەوتنی پێویست ئاماژەیە بۆ ئەو جێبەجێکردنە تەکنەلۆژییانەی کە پێویستن بۆ دابینکەری خزمەتگوزاری بۆ دابینکردنی کارکردنی بنەڕەتی و پێویست بۆ بەکارهێنەر.',
    'cookie.section2.legal': 'بنەمای یاسایی بۆ پرۆسەکردن:',
    'cookie.section2.legal_value': 'بەرژەوەندی ڕەوا بەپێی بڕگەی 6.1(f) GDPR',
    'cookie.section2.storage': 'ماوەی هەڵگرتن:',
    'cookie.section2.storage_value': 'تا بیست و چوار (٢٤) مانگ بۆ تەکنەلۆژیاکانی شوێنکەوتنی بەردەوام؛ تەکنەلۆژیاکانی شوێنکەوتنی سەر دانیشتن یەکسەر کاتی داخستنی وێبگەڕ کۆتایی دێت.',
    'cookie.section3.title': 'بەشی ٣ - تەکنەلۆژیاکانی شوێنکەوتن بۆ ئامانجە شیکارییەکان',
    'cookie.section3.p1': 'دابینکەری خزمەتگوزاری گرنگی زۆر بە توانای ژمارەکردنی ڕێژەی سەردان، تۆمارکردنی داتای کارلێک لەسەر پلاتفۆرمی دیجیتاڵ و لە بەرنامە مۆبایلەکاندا، و بەشێوەیەکی گشتی شیکردنەوەی ئەوەی خزمەتگوزارییە دیجیتاڵییەکانی دابینکەری خزمەتگوزاری چۆن لەلایەن بەکارهێنەرانەوە بەکاردەهێنرێن دەدات.',
    'cookie.section3.legal_value': 'ڕەزامەندی بەپێی بڕگەی 6.1(a) GDPR',
    'cookie.section3.storage_value': 'تا بیست و شەش (٢٦) مانگ',
    'cookie.section4.title': 'بەشی ٤ - تەکنەلۆژیاکانی شوێنکەوتن بۆ ئامانجەکانی کەسایەتیکردن',
    'cookie.section4.p1': 'تەکنەلۆژیاکانی شوێنکەوتن کە بۆ ئامانجەکانی کەسایەتیکردن جێبەجێ کراون ئامانجیان گونجاندنی ئەزموونی بەکارهێنەرە لەسەر پلاتفۆرمە دیجیتاڵییەکانی دابینکەری خزمەتگوزاری بە دابینکردنی ناوەڕۆک بە پەیوەندیدارێتی کەسی بەرزکراوە بۆ بەکارهێنەری تاکەکەس.',
    'cookie.section5.title': 'بەشی ٥ - تەکنەلۆژیاکانی شوێنکەوتن بۆ ئامانجەکانی بازارگەری',
    'cookie.section5.p1': 'دابینکەری خزمەتگوزاری تەکنەلۆژیاکانی شوێنکەوتن بۆ ئامانجەکانی بازارگەری جێبەجێ دەکات، بە مەرجێک بەکارهێنەر ڕەزامەندی پێویست دابێت، بۆ ڕەواکردنی پیشاندانی ناوەڕۆکی ڕیکلامی پەیوەندیدار و ڕیکلامەکانی خانووبەرە بە پەیوەندیدارێتی بەرزکراوە بۆ بەکارهێنەر لەسەر بنەمای پرۆفایلێکی ڕیکلامی دروستکراو.',
    'cookie.section5.p2': 'لە ڕێگەی جێبەجێکردنی تەکنەلۆژیاکانی شوێنکەوتنەوە، دابینکەری خزمەتگوزاری هەروەها دڵنیایی دەدات کە پیشاندانەکانی ڕیکلام سنووردار دەکرێن بۆ ژمارەیەکی پێشوەختە دیاریکراو بۆ هەر بەکارهێنەرێک، و کاریگەری ڕیکلام پێوراو و شیکراوەتەوە.',
    'cookie.section5.p3': 'دەبێت ئاماژە بکرێت کە لە حاڵەتی هەڵبژاردنی بەکارهێنەر بۆ ناچالاککردنی تەکنەلۆژیاکانی شوێنکەوتن بۆ ئامانجەکانی بازارگەری، ناوەڕۆکی ڕیکلام هێشتا بۆ بەکارهێنەر لەسەر پلاتفۆرمە دیجیتاڵییەکانی دابینکەری خزمەتگوزاری پیشان دەدرێت، بەڵام بەبێ کەسایەتیکردن.',
    'cookie.section5.storage_value': 'تا بیست و چوار (٢٤) مانگ',
    'cookie.section6.title': 'بەشی ٦ - گواستنەوەی داتای نێودەوڵەتی بۆ وڵاتانی سێیەم',
    'cookie.section6.p1': 'لە ئامرازی بەڕێوەبردنی ڕەزامەندی دابینکەری خزمەتگوزاریدا، زانیاری دابین کراوە سەبارەت بە کام تەکنەلۆژیاکانی شوێنکەوتن و هاوبەشەکان داتای کەسی بەکارهێنەر لە ناوەوە پرۆسە دەکەن، یان داتای کەسی بەکارهێنەر دەگوازنەوە بۆ، دادگاییەکان لە دەرەوەی یەکیەتی ئەوروپا و ناوچەی ئابووری ئەوروپایی (بە ناوی "وڵاتانی سێیەم").',
    'cookie.section7.title': 'بەشی ٧ - ڕێنماییەکان بۆ لابردنی تەکنەلۆژیاکانی شوێنکەوتن',
    'cookie.section7.p1': 'بەکارهێنەر هەڵبژاردنی هەیە بۆ لابردنی تەکنەلۆژیاکانی شوێنکەوتن لە ئامێرە تەرمیناڵییەکانی یان ئامێری مۆبایل و ڕێکخستنی ڕێکخستنەکان بۆ هەڵگرتنی داتای ناوخۆیی لە ڕێگەی ڕووکاری ڕێکخستنەکانی وێبگەڕەوە.',
    'cookie.section7.p2': 'بۆ ڕێنماییە ورد سەبارەت بە مامەڵەکردن و لابردنی تەکنەلۆژیاکانی شوێنکەوتن، ئاماژە دەکرێت بۆ هەڵبژاردنی یارمەتی لە وێبگەڕی بەکارهێنەر یان هایپەرلینکەکانی خوارەوە بۆ هەر بەرنامەی وێبگەڕێک:',
    
    // Auth
    'auth.loading': 'بارکردن...',
    'auth.login': 'چوونەژوورەوە',
    'auth.create_account': 'دروستکردنی هەژمار',
    'auth.login_subtitle': 'بە هەڵبژاردنی یەکێک لە بژاردەکانی خوارەوە بچۆرەژوورەوە.',
    'auth.signup_subtitle': 'هەژمارێکی نوێ دروست بکە بۆ دەستپێکردن.',
    'auth.continue_apple': 'بەردەوام بە بە Apple',
    'auth.continue_google': 'بەردەوام بە بە Google',
    'auth.or': 'یان',
    'auth.email': 'ئیمەیل',
    'auth.email_placeholder': 'ئیمەیل بنووسە',
    'auth.password': 'وشەی نهێنی',
    'auth.password_placeholder': 'وشەی نهێنی بنووسە',
    'auth.account_type': 'جۆری هەژمار',
    'auth.user_role': 'بەکارهێنەر - دەمەوێت خانوو بکڕم/بە کرێ بگرم',
    'auth.agent_role': 'بریکار - دەمەوێت خانوو بفرۆشم/بە کرێ بدەم',
    'auth.continue': 'بەردەوامبوون',
    'auth.no_account': 'هەژمارت نییە؟ هەژمار دروست بکە',
    'auth.have_account': 'هەژمارت هەیە؟ بچۆرەژوورەوە',
    'auth.login_failed': 'چوونەژوورەوە سەرکەوتوو نەبوو',
    'auth.invalid_credentials': 'ئیمەیل یان وشەی نهێنی هەڵە',
    'auth.welcome_back': 'بەخێربێیتەوە!',
    'auth.logged_in': 'چوویتەژوورەوە.',
    'auth.signup_failed': 'تۆمارکردن سەرکەوتوو نەبوو',
    'auth.account_created': 'هەژمار دروست کرا!',
    'auth.check_email': 'ئیمەیلەکەت بپشکنە بۆ دڵنیاکردنەوەی هەژمارەکەت.',
    'auth.error': 'هەڵەیەک ڕوویدا',
    'auth.try_again': 'تکایە دواتر دووبارە هەوڵ بدەرەوە.',
    'auth.broker_interest_title': 'ئایا تۆ بریکاری؟',
    'auth.broker_interest_description': 'ئەگەر حەزت لە تۆمارکردنی خانووبەرە لە عەقارنەت، داخوازنامەیەک بۆ ناردن و پەیوەندی پێوەت دەکەین.',
    'auth.broker_contact_us': 'پەیوەندیمان پێوە بکە',
    'auth.broker_email_subject': 'داخوازی تۆمارکردنی بریکار',
    'auth.broker_email_body': 'سڵاو،\n\nحەزم لە تۆمارکردنە وەک بریکار لە عەقارنەت.\n\nناو:\nناوی ئاژانس:\nژمارەی مۆبایل:\nشار:\n\nسوپاس!',
    
    // Favorites Page
    'favorites.title': 'دڵخوازەکانم',
    'favorites.saved_properties': 'خانووبەرەی پاشەکەوتکراو',
    'favorites.login_required': 'بچۆرەژوورەوە بۆ بینینی دڵخوازەکانت',
    'favorites.login_description': 'ئەو خانووبەرانە پاشەکەوت بکە کە حەزت لێیانە بۆ دۆزینەوەی ئاسانتر.',
    'favorites.loading': 'دڵخوازەکانت بارکردن...',
    'favorites.no_favorites': 'هێشتا دڵخوازت نییە',
    'favorites.start_saving': 'دەست بکە بە پاشەکەوتکردنی خانووبەرەکان بە کلیککردن لەسەر دڵ لە ڕیکلامەکان.',
    'favorites.search_properties': 'گەڕان بۆ خانووبەرە',
    
    // Profile Page
    'profile.title': 'پرۆفایلەکەم',
    'profile.subtitle': 'بەڕێوەبردنی زانیارییە کەسییەکان و ڕێکخستنەکان',
    'profile.loading': 'بارکردنی پرۆفایل...',
    'profile.fetch_error': 'هەڵە لە هێنانی پرۆفایل',
    'profile.fetch_error_description': 'نەتوانرا زانیاری پرۆفایل بهێنرێت.',
    'profile.unnamed_user': 'بەکارهێنەری بێناو',
    'profile.personal_info': 'زانیارییە کەسییەکان',
    'profile.update_info': 'نوێکردنەوەی زانیارییە کەسییەکانت',
    'profile.email': 'ئیمەیل',
    'profile.email_readonly': 'ئیمەیل ناگۆڕدرێت',
    'profile.display_name': 'ناوی پیشاندان',
    'profile.display_name_placeholder': 'ناوت',
    'profile.phone': 'ژمارەی تەلەفۆن',
    'profile.phone_placeholder': '070-123 45 67',
    'profile.save_changes': 'پاشەکەوتکردنی گۆڕانکارییەکان',
    'profile.saving': 'پاشەکەوت دەکرێت...',
    'profile.reset': 'ڕیسێتکردن',
    'profile.updated': 'پرۆفایل نوێکرایەوە',
    'profile.updated_description': 'پرۆفایلەکەت پاشەکەوت کرا.',
    'profile.update_error': 'هەڵە لە نوێکردنەوە',
    'profile.update_error_description': 'نەتوانرا گۆڕانکارییەکان پاشەکەوت بکرێن.',
    'profile.active_listings': 'ڕیکلامە چالاکەکان',
    'profile.active_listings_description': 'ڕیکلامەکانی خانووبەرەی بڵاوکراوەت بۆ فرۆشتن',
    'profile.view_all': 'هەموو پیشان بدە',
    'profile.loading_listings': 'بارکردنی ڕیکلامەکان...',
    'profile.no_active_listings': 'هێشتا ڕیکلامی چالاکت نییە',
    'profile.more_active_listings': 'و {count} ڕیکلامی چالاکی تر',
    'profile.sold_properties': 'خانووبەرە فرۆشراوەکان',
    'profile.sold_properties_description': 'خانووبەرە فرۆشراوەکانت',
    'profile.no_sold_properties': 'هێشتا خانووبەرەی فرۆشراوت نییە',
    'profile.more_sold_properties': 'و {count} خانووبەرەی فرۆشراوی تر',
    'profile.listing_fetch_error': 'هەڵە لە هێنانی ڕیکلامەکان',
    'profile.listing_fetch_error_description': 'نەتوانرا ڕیکلامەکانت بهێنرێن.',
    'profile.account_settings': 'ڕێکخستنەکانی هەژمار',
    'profile.account_settings_description': 'بەڕێوەبردنی هەژمار و ئاسایش',
    'profile.change_password': 'گۆڕینی وشەی نهێنی',
    'profile.change_password_description': 'نوێکردنەوەی وشەی نهێنی بۆ چوونەژوورەوەی ئاسوودەتر',
    'profile.change': 'گۆڕین',
    'profile.two_factor': 'ڕاستکردنەوەی دوو-هەنگاوی',
    'profile.two_factor_description': 'زیادکردنی ئاسایشی زیاتر بۆ هەژمارەکەت',
    'profile.enable': 'چالاککردن',
    'profile.delete_account': 'سڕینەوەی هەژمار',
    'profile.delete_account_description': 'سڕینەوەی هەمیشەیی هەژمار و هەموو داتاکان',
    'profile.delete': 'سڕینەوە',
    'profile.logout': 'چوونەدەرەوە',
    
    // Profile Email Notifications
    'profile.email_notifications': 'ئاگادارکردنەوەکانی ئیمەیڵ',
    'profile.email_notifications_description': 'هەڵبژێرە کام ئاگادارکردنەوەی ئیمەیڵ دەتەوێت وەربگرێت',
    'profile.notif_property_alerts': 'ئاگادارکردنەوەی خانوو',
    'profile.notif_property_alerts_desc': 'ئاگادارکراوە بە سەبارەت بە خانووە نوێیەکان کە لەگەڵ گەڕانەکانت دەگونجێن',
    'profile.notif_saved_search_alerts': 'نوێکردنەوەی گەڕانی پاشەکەوتکراو',
    'profile.notif_saved_search_alerts_desc': 'نوێکردنەوە وەربگرە کاتێک خانووە نوێیەکان لەگەڵ پێوەرەکانت دەگونجێن',
    'profile.notif_newsletter': 'ڕۆژنامەی نوێ',
    'profile.notif_newsletter_desc': 'ڕۆژنامەی هەفتانەمان وەربگرە لەگەڵ بینینەکانی بازاڕ',
    'profile.notif_viewing_confirmations': 'دڵنیاکردنەوەی سەردان',
    'profile.notif_viewing_confirmations_desc': 'ئیمەیڵی دڵنیاکردنەوە وەربگرە بۆ سەردانە ڕێکخراوەکان',
    'profile.save_preferences': 'پاشەکەوتکردنی هەڵبژاردەکان',
    'profile.notifications_saved': 'هەڵبژاردەکانی ئاگادارکردنەوە پاشەکەوت کرا',
    'profile.notifications_save_error': 'نەتوانرا هەڵبژاردەکانی ئاگادارکردنەوە پاشەکەوت بکرێن',
    
    // Not Found Page
    'notfound.title': '404',
    'notfound.message': 'ببوورە! پەڕە نەدۆزرایەوە',
    'notfound.back_home': 'گەڕانەوە بۆ ماڵەوە',
    
    // Property Detail Page
    'property.back_to_listings': 'گەڕانەوە بۆ ڕیکلامەکان',
    'property.not_found': 'خانووبەرە نەدۆزرایەوە',
    'property.link_copied': 'لینک کۆپی کرا',
    'property.link_copied_description': 'لینکی خانووبەرە کۆپی کرا',
    'property.description': 'وەسف',
    'property.features': 'تایبەتمەندییەکان',
    'property.details': 'وردەکارییەکانی خانووبەرە',
    'property.rooms_label': 'ژوورەکان',
    'property.size_label': 'قەبارە',
    'property.plot_size_label': 'قەبارەی زەوی',
    'property.monthly_fee_label': 'کرێی مانگانە',
    'property.contact_agent': 'پەیوەندی بە ئەیجنت',
    'property.send_message': 'ناردنی پەیام',
    'property.schedule_viewing': 'ڕێکخستنی سەردان',
    'property.section.details': 'وردەکارییەکان',
    'property.section.amenities': 'ڕازانەکارییەکان',
    'property.section.overview': 'پوختە',
    'property.section.payment_plan': 'پلانی پارەدان',
    'property.section.floor_plans': 'نەخشەی نهۆمەکان',
    'property.section.view_map': 'نەخشە ببینە',
    'property.copy_link': 'لینک کۆپی بکە',
    'property.share': 'ھاوبەش بکە',
    'property.share_text': 'ئەم خانووبەرەیە ببینە',
    'property.posted_on': 'بڵاوکراوەتەوە لە',
    'property.type': 'جۆر',
    'property.status': 'بارودۆخ',
    'property.available': 'بەردەستە',
    'property.no_amenities': 'هیچ ڕازانەکاریەک بۆ ئەم خانووبەرەیە تۆمار نەکراوە',
    'property.no_description': 'وەسف بەردەست نییە',
    'property.location': 'شوێن',
    'property.payment_plan_info': 'بۆ وردەکاری پلانی پارەدان پەیوەندی بە ئەیجنت بکە',
    'property.contact_for_payment_plan': 'پەیوەندی بۆ پلانی پارەدان',
    'property.no_floor_plans': 'نەخشەی نهۆمەکان بەردەست نییە',
    'property.request_floor_plans': 'داواکردنی نەخشەی نهۆمەکان',
    'property.no_map_location': 'شوێن لەسەر نەخشە بەردەست نییە',
    
    // News Page
    'news.subtitle': 'لەگەڵ تازەترین هەواڵی خانووبەرە و بینینی بازاڕ و ڕێنمایی بمێنەوە',
    'news.no_articles': 'هێشتا وتارێکی هەواڵ نییە',
    'news.check_back': 'زوو بگەڕێوە بۆ تازەترین هەواڵی خانووبەرە و بینینی بازاڕ.',
    'news.search_placeholder': 'گەڕان لە وتارەکان...',
    'news.showing_filtered': 'پیشاندانی {shown} لە {total} وتار لەم پەڕەیەدا',
    'news.clear_filters': 'سڕینەوەی فلتەرەکان',
    'news.no_matching': 'هیچ وتارێکی هاوتا نییە',
    'news.adjust_filters': 'هەوڵ بدە گەڕان یان فلتەرەکان بگۆڕیت بۆ دۆزینەوەی ئەوەی بەدوایدا دەگەڕێیت.',
    'news.publisher': 'بڵاوکەرەوە',
    'news.page_info': 'پەڕە {current} لە {total} • {count} کۆی وتارەکان',
    'news.category.all': 'هەموو',
    'news.category.market_news': 'هەواڵی بازاڕ',
    'news.category.property_guides': 'ڕێنماییەکانی خانووبەرە',
    'news.category.investment_tips': 'ئامۆژگاری وەبەرهێنان',
    'news.category.industry_updates': 'نوێکردنەوەکانی پیشەسازی',
    'news.category.announcements': 'ڕاگەیاندنەکان',
    'news.category.expert_advice': 'ئامۆژگاری پسپۆڕان',
    
    // Filters/Sorting
    'filters.any': 'هەموو',
    'filters.newest_first': 'تازەترین یەکەم',
    'filters.price_low_high': 'نرخ: کەم بۆ زۆر',
    'filters.price_high_low': 'نرخ: زۆر بۆ کەم',
    'filters.size': 'قەبارە',
    'filters.searching_for': 'گەڕان بۆ',
    'filters.search_areas': 'گەڕان بۆ ناوچەکان، شارەکان...',
    
    // Toast Messages
    'toast.error': 'هەڵە',
    'toast.success': 'سەرکەوتوو',
    'toast.auth_required': 'ناسنامە پێویستە',
    'toast.sign_in_required': 'تکایە بچۆ ژوورەوە بۆ بەردەوامبوون',
    'toast.sign_in_favorites': 'تکایە بچۆ ژوورەوە بۆ زیادکردنی دڵخواز',
    'toast.sign_in_properties': 'تکایە بچۆ ژوورەوە بۆ زیادکردنی خانوو',
    'toast.properties_load_error': 'شکستی هێنا لە بارکردنی خانووەکان',
    'toast.property_added': 'خانوو بە سەرکەوتوویی زیادکرا',
    'toast.property_add_error': 'شکستی هێنا لە زیادکردنی خانوو',
    'toast.property_updated': 'خانوو بە سەرکەوتوویی نوێکرایەوە',
    'toast.property_update_error': 'شکستی هێنا لە نوێکردنەوەی خانوو',
    'toast.property_deleted': 'خانوو بە سەرکەوتوویی سڕایەوە',
    'toast.property_delete_error': 'شکستی هێنا لە سڕینەوەی خانوو',
    'toast.property_marked_sold': 'خانوو بە سەرکەوتوویی وەک فرۆشراو نیشانکرا',
    'toast.property_sold_error': 'شکستی هێنا لە نیشانکردنی خانوو وەک فرۆشراو',
    'toast.invalid_price': 'تکایە نرخێکی فرۆشتنی دروست بنووسە',
    'toast.favorites_load_error': 'شکستی هێنا لە بارکردنی دڵخوازەکان',
    'toast.added_to_favorites': 'زیادکرا بۆ دڵخوازەکان',
    'toast.add_favorites_error': 'شکستی هێنا لە زیادکردن بۆ دڵخوازەکان',
    'toast.removed_from_favorites': 'لابرا لە دڵخوازەکان',
    'toast.remove_favorites_error': 'شکستی هێنا لە لابردن لە دڵخوازەکان',
    'toast.search_saved': 'گەڕان پاشەکەوتکرا',
    'toast.search_saved_desc': 'پاشەکەوتکرا لەگەڵ ئاگاداریەکانی ئیمەیڵ',
    'toast.search_save_error': 'ناتوانرێت گەڕان پاشەکەوت بکرێت',
    'toast.valuation_complete': 'نرخاندن تەواوبوو',
    'toast.valuation_estimated': 'بەهای تەخمینکراو',
    'toast.valuation_error': 'ناتوانرێت نرخاندن بکرێت',
    'toast.message_sent': 'نامە نێردرا',
    'toast.agent_contact_desc': 'بریکار بەم زووانە پەیوەندیت پێوە دەکات',
    'toast.message_error': 'ناتوانرێت نامە بنێردرێت',
    'toast.viewing_booked': 'سەردان نوسراوە',
    'toast.viewing_confirmed': 'سەردانەکەت نوسراوە و پشتڕاستکردنەوە بە ئیمەیڵ نێردرا',
    'toast.viewing_error': 'ناتوانرێت سەردان بنوسرێت',
    'toast.image_analysis_complete': 'شیکاری وێنە تەواوبوو',
    'toast.image_analysis_desc': 'شیکاری AI ی وێنە تەواوبوو',
    'toast.image_analysis_error': 'ناتوانرێت وێنە شیکاری بکرێت',
    'toast.logged_out': 'دەرچوون',
    'toast.welcome_back': 'بەخێربێیتەوە کاتێک بتەوێ!',
    
    // Recent searches
    'search.properties_label': 'خانوبەرە',
    'search.from': 'لە',
    'search.up_to': 'تا',
    
    // News Management
    'news_mgmt.title': 'بەڕێوەبردنی هەواڵەکان',
    'news_mgmt.subtitle': 'دروستکردن و بەڕێوەبردنی بابەتەکانی هەواڵ',
    'news_mgmt.create_article': 'دروستکردنی بابەت',
    'news_mgmt.edit_article': 'دەستکاری بابەت',
    'news_mgmt.create_new': 'دروستکردنی بابەتێکی نوێ',
    'news_mgmt.all_articles': 'هەموو بابەتەکان',
    'news_mgmt.no_articles': 'هیچ بابەتێک نیە هێشتا. یەکەمیان دروست بکە!',
    'news_mgmt.delete_article': 'سڕینەوەی بابەت',
    'news_mgmt.delete_confirm': 'ئایا دڵنیایت دەتەوێت ئەم بابەتە بسڕیتەوە؟ ئەم کردارە ناگەڕێتەوە.',
    'news_mgmt.title_label': 'ناونیشان',
    'news_mgmt.title_placeholder': 'ناونیشانی بابەت بنووسە',
    'news_mgmt.category_label': 'پۆل',
    'news_mgmt.category_placeholder': 'بۆ نموونە: هەواڵی بازاڕ، ڕێنماییەکان، ئامۆژگاریەکان',
    'news_mgmt.image_url': 'لینکی وێنە',
    'news_mgmt.content': 'ناوەڕۆک',
    'news_mgmt.content_placeholder': 'ناوەڕۆکی بابەت بنووسە',
    'news_mgmt.published': 'بڵاوکراوەتەوە',
    'news_mgmt.draft': 'ڕەشنووس',
    'news_mgmt.update_article': 'نوێکردنەوەی بابەت',
    'news_mgmt.required_fields': 'ناونیشان و پۆل پێویستن',
    'news_mgmt.article_updated': 'بابەتی هەواڵ بە سەرکەوتوویی نوێکرایەوە',
    'news_mgmt.article_created': 'بابەتی هەواڵ بە سەرکەوتوویی دروستکرا',
    'news_mgmt.article_save_error': 'شکستی هێنا لە پاشەکەوتکردنی بابەتی هەواڵ',
    'news_mgmt.article_deleted': 'بابەتی هەواڵ بە سەرکەوتوویی سڕایەوە',
    'news_mgmt.article_delete_error': 'شکستی هێنا لە سڕینەوەی بابەتی هەواڵ',
    'news_mgmt.access_denied': 'دەستگەیشتن ڕەتکرایەوە',
    'news_mgmt.access_denied_desc': 'تۆ مۆڵەتت نیە بچیتە ئەم پەڕەیە. تەنها بڵاوکەرەوەکان و بەڕێوەبەرەکان دەتوانن هەواڵەکان بەڕێوە ببەن.',
    
    // Badges & Labels
    'badge.new': 'نوێ',
    'badge.sold': 'فرۆشراو',
    'badge.featured': 'تایبەت',
    'badge.reduced': 'نرخ داشکاندووە',
    'badge.new_construction': 'بیناسازی نوێ',
    
    // Confirmation Messages
    'confirm.delete': 'سڕینەوە',
    'confirm.cancel': 'هەڵوەشاندنەوە',
    'confirm.yes': 'بەڵێ',
    'confirm.no': 'نەخێر',
    'confirm.are_you_sure': 'ئایا دڵنیایت؟',
    'confirm.action_irreversible': 'ئەم کردارە ناگەڕێتەوە.',
    
    // Tooltips
    'tooltip.favorite': 'زیادکردن بۆ دڵخوازەکان',
    'tooltip.share': 'هاوبەشکردنی خانوو',
    'tooltip.edit': 'دەستکاری خانوو',
    'tooltip.delete': 'سڕینەوەی خانوو',
    'tooltip.contact': 'پەیوەندی بە بریکار',
    'tooltip.schedule': 'نوسینی سەردان',
    'tooltip.map_view': 'بینین لەسەر نەخشە',
    'tooltip.list_view': 'دیمەنی لیست',
    'tooltip.grid_view': 'دیمەنی تۆڕ',
    
    // Leaderboard
    'leaderboard': 'لیستی پێشەوە',
    'leaderboardDescription': 'ئاژانس و بروکەرە باشترینەکان ببینە بە پێی بینین و فرۆشتن',
    'topAgencies': 'ئاژانسە باشترینەکان',
    'topBrokers': 'بروکەرە باشترینەکان',
    'topAgenciesDescription': 'ئاژانسەکان بە پێی بینینی خانووبەرە و ئەدای فرۆشتن ڕیزبەندکراون',
    'topBrokersDescription': 'بروکەرەکان بە پێی بینینی خانووبەرە و ئەدای فرۆشتن ڕیزبەندکراون',
    'allRegions': 'هەموو ناوچەکان',
    'noAgenciesFound': 'ئاژانس نەدۆزرایەوە',
    'noBrokersFound': 'بروکەر نەدۆزرایەوە',
    'brokers': 'بروکەر',
    'sold': 'فرۆشراو',
    'active': 'چالاک',
    'views': 'بینین',
    
    // Dialogs - Contact Agent
    'dialog.contact_agent': 'پەیوەندی بە بریکار',
    'dialog.contact_agent_btn': 'پەیوەندی بە بریکار',
    'dialog.contact_message_placeholder': 'سڵاو! من حەزم لە خانووبەرەکەی لە {address} هەیە و دەمەوێت زیاتر بزانم. تکایە کاتێک وەختت هەیە پەیوەندیم پێوە بکە.',
    'dialog.contact_sending': 'ناردن...',
    'dialog.contact_send': 'ناردنی پەیام',
    'dialog.contact_note': 'داواکاریەکەت ڕاستەوخۆ دەنێردرێت بۆ بریکاری بەرپرس کە پەیوەندیت پێوە دەکات.',
    
    // Dialogs - Schedule Viewing
    'dialog.schedule_viewing': 'ڕێکخستنی سەردان',
    'dialog.schedule_viewing_btn': 'ڕێکخستنی سەردان',
    'dialog.preferred_time': 'کاتی سەردانی پەسندکراو',
    'dialog.preferred_time_note': 'بەروار و کات بۆ سەردان هەڵبژێرە. بریکار بەردەستبوون دڵنیا دەکاتەوە.',
    'dialog.special_requests': 'داواکارییە تایبەتەکان',
    'dialog.special_requests_placeholder': 'هەر داواکاری یان پرسیارێکی تایبەت...',
    'dialog.schedule_booking': 'ڕێکخستنی سەردان...',
    'dialog.schedule_book': 'نوێندی سەردان',
    'dialog.schedule_note': 'داواکاریەکەت دەنێردرێت بۆ بریکار کە کاتەکە لە ڕێگەی ئیمەیلەوە دڵنیا دەکاتەوە.',
    
    // Dialogs - Save Search
    'dialog.save_search': 'پاشەکەوتکردنی گەڕان',
    'dialog.save_search_btn': 'پاشەکەوتکردنی گەڕان',
    'dialog.search_name': 'ناوی گەڕان',
    'dialog.search_name_placeholder': 'بۆ نموونە: شوقە لە بەغداد',
    'dialog.city_municipality': 'شار/شارەوانی',
    'dialog.min_rooms': 'کەمترین ژوور',
    'dialog.min_size': 'کەمترین قەبارە (م²)',
    'dialog.property_type': 'جۆری خانووبەرە',
    'dialog.email_alerts': 'ئاگادارکردنەوەی ئیمەیل کاتێک خانووبەرەی لێکچوو زیاد دەکرێت',
    'dialog.saving_search': 'پاشەکەوت دەکرێت...',
    
    // Dialogs - Mark as Sold
    'dialog.mark_sold': 'وەک فرۆشراو دیاری بکە',
    'dialog.mark_sold_description': 'نرخی کۆتایی فرۆشتن بۆ ئەم خانووبەرە بنووسە. ئەمە ڕیکلامەکە دەگوازێتەوە بۆ خانووبەرە فرۆشراوەکان.',
    'dialog.final_sale_price': 'نرخی کۆتایی فرۆشتن (دۆلار)',
    'dialog.listed_price': 'نرخی لیستکراو',
    'dialog.property_details': 'وردەکارییەکانی خانووبەرە',
    'dialog.enter_valid_price': 'تکایە نرخێکی فرۆشتنی دروست بنووسە',
    'dialog.enter_price_placeholder': 'نرخی فرۆشتن بنووسە',
    'dialog.marked_sold_success': 'خانووبەرە بە سەرکەوتوویی وەک فرۆشراو دیاریکرا',
    'dialog.mark_sold_error': 'دیاریکردنی خانووبەرە وەک فرۆشراو سەرکەوتوو نەبوو',
    
    // Dialogs - Add Property
    'dialog.add_property': 'زیادکردنی خانووبەرەی نوێ',
    'dialog.hide_preview': 'شاردنەوەی پێشبینین',
    'dialog.show_preview': 'پیشاندانی پێشبینین',
    'dialog.selected_images': 'وێنە هەڵبژێردراوەکان',
    'dialog.uploaded_images': 'وێنە بارکراوەکان',
    'dialog.click_primary': 'کلیک لەسەر وێنەیەک بکە بۆ دیاریکردنی وەک سەرەکی',
    'dialog.invalid_format': 'فۆرماتی نادروست',
    'dialog.file_too_large': 'فایلەکە زۆر گەورەیە',
    'dialog.upload_failed': 'بارکردن سەرکەوتوو نەبوو',
    'dialog.images_required': 'وێنە پێویستە',
    'dialog.please_upload': 'تکایە لانیکەم یەک وێنە بۆ خانووبەرەکە بار بکە',
    'dialog.form_validation_failed': 'پشتڕاستکردنەوەی فۆرم سەرکەوتوو نەبوو',
    
    // Admin Pages
    'admin.loading': 'بارکردن...',
    'admin.access_denied': 'ڕێپێدان ڕەتکرایەوە. مۆڵەتی بەڕێوەبەر پێویستە.',
    
    // Admin Notification Settings
    'admin.notifications.title': 'ڕێکخستنەکانی ئاگادارکردنەوە',
    'admin.notifications.description': 'ڕێکخستنەکانی ئاگادارکردنەوە بەڕێوەببە بۆ هەموو جۆرەکانی بەکارهێنەران. چالاک بکە، ناچالاک بکە، یان بشارەوە بەپێی پێویست.',
    'admin.notifications.fetch_error': 'شکستی هێنا لە هێنانی ڕێکخستنەکانی ئاگادارکردنەوە',
    'admin.notifications.update_success': 'ڕێکخستنی ئاگادارکردنەوە نوێکرایەوە',
    'admin.notifications.update_error': 'شکستی هێنا لە نوێکردنەوەی ڕێکخستنی ئاگادارکردنەوە',
    'admin.notifications.role_description': 'ئاگادارکردنەوەکان بەڕێوەببە بۆ بەکارهێنەرانی {role}',
    'admin.notifications.hidden': 'شاراوە',
    'admin.notifications.disabled': 'ناچالاک',
    'admin.notifications.active': 'چالاک',
    'admin.notifications.hide': 'بشارەوە',
    'admin.notifications.roles.user': 'بەکارهێنەران',
    'admin.notifications.roles.maklare': 'دەلاڵەکان',
    'admin.notifications.roles.agency': 'ئاژانسەکان',
    'admin.notifications.roles.admin': 'بەڕێوەبەرەکان',
    'admin.notifications.roles.publisher': 'بڵاوکەرەوەکان',
    'admin.notifications.types.new_viewing_request': 'داواکاری بینینی نوێ',
    'admin.notifications.types.new_client_message': 'نامەی کڕیاری نوێ',
    'admin.notifications.types.property_alerts': 'ئاگادارکردنەوەکانی موڵک',
    'admin.notifications.types.weekly_summary': 'پوختەی هەفتانە',
    'admin.notifications.types.broker_property_added': 'دەلاڵ موڵکی زیادکرد',
    'admin.notifications.types.broker_viewing_request': 'داواکاری بینینی دەلاڵ',
    'admin.notifications.types.broker_client_message': 'نامەی کڕیاری دەلاڵ',
    'admin.notifications.types.broker_joined': 'دەلاڵ پەیوەست بوو',
    'admin.notifications.types.weekly_agency_summary': 'پوختەی هەفتانەی ئاژانس',
    'admin.notifications.types.saved_search_alerts': 'ئاگادارکردنەوەکانی گەڕانی پاشەکەوتکراو',
    'admin.notifications.types.favorite_property_updates': 'نوێکردنەوەکانی موڵکی دڵخواز',
    'admin.notifications.types.newsletter': 'نامەی هەواڵ',
    'admin.notifications.types.system_alerts': 'ئاگادارکردنەوەکانی سیستەم',
    'admin.notifications.types.new_user_signup': 'تۆمارکردنی بەکارهێنەری نوێ',
    'admin.notifications.types.article_published': 'وتار بڵاوکرایەوە',

    // Admin Settings
    'admin.settings': 'ڕێکخستنەکانی بەڕێوەبەر',
    'admin.messaging_settings': 'ڕێکخستنەکانی پەیامنێردن',
    'admin.messaging_settings_description': 'کۆنترۆڵکردنی تایبەتمەندییەکانی پەیام و پەیوەندی بۆ هەموو بەکارهێنەران',
    'admin.messaging': 'پەیامنێردن',
    'admin.messaging_description': 'ڕێگەدان بە بەکارهێنەران بۆ ناردنی پەیام',
    'admin.voice_calls': 'پەیوەندی دەنگی',
    'admin.voice_calls_description': 'ڕێگەدان بە بەکارهێنەران بۆ کردنی پەیوەندی دەنگی',
    'admin.video_calls': 'پەیوەندی ڤیدیۆیی',
    'admin.video_calls_description': 'ڕێگەدان بە بەکارهێنەران بۆ کردنی پەیوەندی ڤیدیۆیی',
    'admin.screen_sharing': 'هاوبەشکردنی شاشە',
    'admin.screen_sharing_description': 'ڕێگەدان بە بەکارهێنەران بۆ هاوبەشکردنی شاشەیان لە کاتی پەیوەندیدا',

    // Location Manager
    'location_mgr.title': 'بەڕێوەبەری شوێنەکان',
    'location_mgr.current_locations': 'شوێنە ئێستاییەکان',
    'location_mgr.locations_in_db': '{count} شوێن لە داتابەیسدا',
    'location_mgr.refresh': 'نوێکردنەوە',
    'location_mgr.populate_title': 'پڕکردنەوەی شوێنەکان',
    'location_mgr.populate_desc': 'هێنانی هەموو شارەکان و ناوچەکانی عێراق لە Google Places API و پاشەکەوتکردنیان لە داتابەیس. ئەمە هەموو شوێنە بەردەستەکان دەگۆڕێت.',
    'location_mgr.populating': 'پڕدەکرێتەوە...',
    'location_mgr.populate_btn': 'پڕکردنەوە لە Google Places',
    'location_mgr.success': 'سەرکەوتوو',
    'location_mgr.success_msg': 'شوێنەکان بە سەرکەوتوویی پڕکرانەوە',
    'location_mgr.error': 'هەڵە',
    'location_mgr.error_msg': 'پڕکردنەوەی شوێنەکان سەرکەوتوو نەبوو. کۆنسۆڵ بپشکنە بۆ وردەکاری.',
    
    // Map Configuration Manager
    'map_config.title': 'بەڕێوەبەری ڕێکخستنی نەخشە',
    'map_config.subtitle': 'بەڕێوەبردنی ڕێکخستنە هەمیشەییەکانی نەخشە بۆ کەمکردنەوەی بەکارهێنانی Google Maps API',
    'map_config.loading': 'بارکردنی ڕێکخستنەکانی نەخشە...',
    'map_config.create': 'دروستکردنی ڕێکخستن',
    'map_config.create_title': 'دروستکردنی ڕێکخستنی نەخشە',
    'map_config.create_desc': 'دروستکردنی ڕێکخستنی نەخشەیەکی هەمیشەیی نوێ کە لە جێگای بانگکردنی Google Maps API بەردەوام بەکار دەهێنرێت.',
    'map_config.edit_title': 'دەستکاریکردنی ڕێکخستنی نەخشە',
    'map_config.edit_desc': 'نوێکردنەوەی ڕێکخستنی نەخشەی هەمیشەییەکەت.',
    'map_config.name': 'ناوی ڕێکخستن',
    'map_config.name_placeholder': 'نم: نەخشەی خانووبەرەکانی عێراق',
    'map_config.description': 'وەسف (ئارەزوومەندانە)',
    'map_config.description_placeholder': 'وەسفی کورت بۆ ئەم ڕێکخستنە',
    'map_config.map_data': 'ڕێکخستنی نەخشە (JSON)',
    'map_config.map_data_placeholder': 'ڕێکخستنی نەخشە بە فۆرماتی JSON',
    'map_config.cancel': 'هەڵوەشاندنەوە',
    'map_config.update': 'نوێکردنەوەی ڕێکخستن',
    'map_config.no_configs': 'ڕێکخستنی نەخشە نییە',
    'map_config.no_configs_desc': 'یەکەم ڕێکخستنی نەخشەکەت دروست بکە بۆ دەستپێکردنی بەڕێوەبردنی نەخشەکانت بە کاریگەری.',
    'map_config.create_first': 'دروستکردنی یەکەم ڕێکخستن',
    'map_config.active': 'چالاک',
    'map_config.activate': 'چالاککردن',
    'map_config.edit': 'دەستکاری',
    'map_config.delete': 'سڕینەوە',
    'map_config.delete_confirm': 'دڵنیایت؟',
    'map_config.delete_desc': 'ئەمە ڕێکخستنی نەخشەی "{name}" بەردەوام دەسڕێتەوە. ئەم کردارە ناگەڕێتەوە.',
    'map_config.created': 'دروستکرا',
    'map_config.last_updated': 'دواین نوێکردنەوە',
    'map_config.map_center': 'ناوەندی نەخشە',
    'map_config.zoom_level': 'ئاستی زووم',
    'map_config.cities': 'شارەکان',
    'map_config.cities_configured': '{count} شار ڕێکخراون',
    'map_config.invalid_json': 'فۆرماتی JSON نادروست لە داتای نەخشەدا',
    
    // About Page
    'about.title': 'دەربارەی عەقارنێت',
    'about.subtitle': 'هاوکاری متمانەپێکراوت لە بازاڕی خانووبەرەی عێراق',
    'about.description': 'عەقارنێت پلاتفۆرمی پێشەنگی خانووبەرەی عێراقە، کڕیار و فرۆشیار و بریکەرەکان لە سەرانسەری وڵات بەیەکەوە دەبەستێتەوە. ئێمە لیستی خانووبەرەی تەواو، تێڕوانینی بازاڕ و ئامرازی پڕۆفیشناڵ دەخەینە بەردەست.',
    'about.mission_title': 'ئەرکی ئێمە',
    'about.mission_text': 'شۆڕشگێڕی لە بازاڕی خانووبەرەی عێراق بە دابینکردنی زانیاری خانووبەرەی ڕوون، دەستپێگەیشتوو و متمانەپێکراو بۆ هەموو کەس.',
    'about.vision_title': 'دیدگای ئێمە',
    'about.vision_text': 'بوون بە متمانەپێکراوترین پلاتفۆرمی خانووبەرە لە ڕۆژهەڵاتی ناوەڕاست، توانادانی هەموو کەس بۆ دۆزینەوەی ماڵی خەونەکانیان.',
    'about.services_title': 'خزمەتگوزارییەکانمان',
    'about.services_text': 'لیستی خانووبەرە، شیکاری بازاڕ، پەیوەندی بریکەرەکان، نرخاندنی خانووبەرە، و هەواڵی خانووبەرەی تەواو.',
    'about.team_title': 'تیمەکەمان',
    'about.team_text': 'تیمێکی دیلسۆز لە پسپۆڕانی خانووبەرە، پسپۆڕانی تەکنەلۆژیا و پسپۆڕانی خزمەتگوزاری.',
    'about.company_info': 'زانیاری کۆمپانیا',
    'about.based_in': 'لە سوید، بۆ عێراق خزمەت دەکات',
    
    // Contact Page
    'contact.title': 'پەیوەندیمان پێوە بکە',
    'contact.subtitle': 'ئێمە لێرەین بۆ یارمەتی. بۆ هەر پرسیارێک یان نیگەرانییەک پەیوەندیمان پێوە بکە.',
    'contact.email_label': 'ئیمەیڵ',
    'contact.phone_label': 'تەلەفۆن',
    'contact.phone_info': 'بەم زووانە',
    'contact.location_label': 'شوێن',
    'contact.location_info': 'سوید',
    'contact.form_title': 'نامەیەکمان بۆ بنێرە',
    'contact.form_description': 'فۆڕمی خوارەوە پڕبکەوە و ئێمە بەزووترین کات پەیوەندیت پێوە دەکەین.',
    'contact.name_placeholder': 'ناوەکەت',
    'contact.email_placeholder': 'ئیمەیڵەکەت@.com',
    'contact.subject': 'بابەت',
    'contact.subject_placeholder': 'ئەمە سەبارەت بە چییە؟',
    'contact.message_placeholder': 'نامەکەت...',
    'contact.success': 'نامە نێردرا! بەزووانە پەیوەندیت پێوە دەکەین.',
    
    // Press Page
    'press.title': 'ڕۆژنامەوانی و میدیا',
    'press.subtitle': 'دوایین هەواڵەکان و دەرهێنانەکانی ڕۆژنامەوانی لە عەقارنێت',
    'press.contact_title': 'پەیوەندی ڕۆژنامەوانی',
    'press.contact_description': 'بۆ پرسیارەکانی ڕۆژنامەوانی، تکایە بە ئیمەیڵ پەیوەندیمان پێوە بکە.',
    'press.add_release': 'دەرهێنانی ڕۆژنامەوانی زیاد بکە',
    'press.edit_release': 'دەرهێنانی ڕۆژنامەوانی بگۆڕە',
    'press.new_release': 'دەرهێنانی ڕۆژنامەوانی نوێ',
    'press.release_title': 'ناونیشان',
    'press.content': 'ناوەڕۆک',
    'press.publish': 'بڵاوکردنەوە',
    'press.no_releases': 'هیچ دەرهێنانی ڕۆژنامەوانی نییە تا ئێستا.',
    'press.delete_confirm_title': 'دەرهێنانی ڕۆژنامەوانی بسڕەوە؟',
    'press.delete_confirm_description': 'ناتوانی ئەم کارە بگەڕێنیتەوە.',
    
    // Investors Page
    'investors.title': 'بۆ وەبەرهێنەران',
    'investors.subtitle': 'پێکەوە لە گۆڕینی بازاڕی خانووبەرەی عێراق بەشداربن',
    'investors.growth_title': 'توانای گەشەکردن',
    'investors.growth_text': 'بازاڕی خانووبەرەی عێراق گەشەی خێرا بەدەست دێنێت لەگەڵ دەرفەتی وەبەرهێنانی گرنگ.',
    'investors.market_title': 'پێشەنگی بازاڕ',
    'investors.market_text': 'عەقارنێت وەک پلاتفۆرمی دیجیتاڵی پێشەنگ لە بواری خانووبەرەی عێراقە.',
    'investors.vision_title': 'فراوانکردنی ناوچەیی',
    'investors.vision_text': 'دیدگای ئێمە لە عێراقەوە تێدەپەڕێت بۆ بوون بە پلاتفۆرمی پێشەنگ لە ناوچەی ڕۆژهەڵاتی ناوەڕاست.',
    'investors.opportunity_title': 'دەرفەتی وەبەرهێنان',
    'investors.opportunity_text': 'عەقارنێت دەرفەتێکی تایبەت دەخاتە بەردەست بۆ وەبەرهێنان لە گۆڕینی دیجیتاڵی یەکێک لە گەورەترین بازاڕە خانووبەرە دەرکەوتووەکانی جیهان.',
    'investors.opportunity_text_2': 'بە بنەمای بەکارهێنەری گەورەبوو و خزمەتگوزاری فراوان، ئێمە بۆ گەشەی بەردەوام و پێشەنگی بازاڕ باش ئامادەین.',
    'investors.contact_title': 'پەیوەندی بگرە',
    'investors.contact_description': 'حەزت لە دەرفەتەکانی وەبەرهێنانە؟ پەیوەندیمان پێوە بکە بۆ زانیاری زیاتر.',
    'investors.company': 'کۆمپانیا / ڕێکخراو',
    'investors.company_placeholder': 'ناوی کۆمپانیاکەت',
    'investors.message_placeholder': 'لەبارەی ئارەزووەکانی وەبەرهێنانت بۆمان بڵێ...',
    'investors.success': 'سوپاس بۆ ئارەزووت! بەزووانە پەیوەندیت پێوە دەکەین.',
    
    // Advertising Page
    'advertising.title': 'ڕیکلام و مارکێتینگ',
    'advertising.subtitle': 'بگەیە بە هەزاران کڕیار و کرێچی ئەگەرمەند لە عێراق',
    'advertising.targeted_title': 'گەیشتنی ئامانج',
    'advertising.targeted_text': 'بگەیە بە بەکارهێنەرانی بەچالاکی بۆ خانووبەرە لە ناوچە و رێژەی نرخی تایبەت دەگەڕێن.',
    'advertising.audience_title': 'بینەری کوالیتی',
    'advertising.audience_text': 'پەیوەندی بگرە لەگەڵ کڕیار و کرێچیە جدییەکان کە ئامادەن بریار بدەن.',
    'advertising.analytics_title': 'شیکاری وردەکاری',
    'advertising.analytics_text': 'کاری کەمپینەکەت بەدواداچوون بکە لەگەڵ شیکاری تەواو و تێڕوانین.',
    'advertising.formats_title': 'فۆرماتی زۆر',
    'advertising.formats_text': 'ڕیکلامی بانەر، لیستی تایبەت، ناوەڕۆکی سپۆنسەرکراو، و هەڵبژاردنی ڕیکلامی زیاتر.',
    'advertising.contact_title': 'دەست بە ڕیکلام بکە',
    'advertising.contact_description': 'پەیوەندیمان پێوە بکە بۆ گفتوگۆ سەبارەت بە دەرفەتەکانی ڕیکلام.',
    'advertising.company': 'کۆمپانیا / ڕێکخراو',
    'advertising.company_placeholder': 'ناوی کۆمپانیاکەت',
    'advertising.budget': 'رێژەی بودجە',
    'advertising.budget_placeholder': 'نمـ: $500 - $1000/مانگ',
    'advertising.message_placeholder': 'لەبارەی ئامانجەکانی ڕیکلامت بۆمان بڵێ...',
    'advertising.success': 'سوپاس! دەربارەی دەرفەتەکانی ڕیکلام پەیوەندیت پێوە دەکەین.',
    
    // Jobs Page
    'jobs.title': 'کاریگەری لە عەقارنێت',
    'jobs.subtitle': 'بەشداربن لە تیمەکەمان و یارمەتی بدەن لە داهاتووی خانووبەرە لە عێراق',
    'jobs.add_job': 'کار زیاد بکە',
    'jobs.edit_job': 'کارەکە بگۆڕە',
    'jobs.new_job': 'کاری نوێ',
    'jobs.job_title': 'ناوی کار',
    'jobs.department': 'بەش',
    'jobs.location': 'شوێن',
    'jobs.employment_type': 'جۆری کار',
    'jobs.full_time': 'تەواوکات',
    'jobs.part_time': 'نیوکات',
    'jobs.contract': 'گرێبەست',
    'jobs.internship': 'ستاژ',
    'jobs.description': 'وەسف',
    'jobs.requirements': 'پێویستییەکان',
    'jobs.benefits': 'سوودەکان',
    'jobs.publish': 'بڵاوکردنەوە',
    'jobs.no_jobs': 'هیچ شوێنی کراوە نییە لە ئێستادا.',
    'jobs.check_back': 'دواتر بگەڕێوە بۆ دەرفەتەکانی نوێ.',
    'jobs.apply_now': 'ئێستا داوای بکە',
    'jobs.apply_for': 'داوای بکە بۆ',
    'jobs.apply_description': 'داواکارییەکەت بە ئیمەیڵ بنێرە.',
    'jobs.application_sent': 'داواکاری نێردرا! بەختت لەگەڵ بێت!',
    'jobs.delete_confirm_title': 'کارەکە بسڕەوە؟',
    'jobs.delete_confirm_description': 'ناتوانی ئەم کارە بگەڕێنیتەوە.',
    
    // Property Statistics Page
    'statistics.title': 'ستاتیستیکی خانووبەرە',
    'statistics.subtitle': 'تێڕوانینی بازاڕ و ترێندەکان لەسەر بنەمای خانووبەرەی فرۆشراو',
    'statistics.6_months': '6 مانگی ڕابردوو',
    'statistics.12_months': '12 مانگی ڕابردوو',
    'statistics.24_months': '24 مانگی ڕابردوو',
    'statistics.36_months': '36 مانگی ڕابردوو',
    'statistics.all_cities': 'هەموو شارەکان',
    'statistics.total_sold': 'کۆی فرۆشراو',
    'statistics.avg_price': 'نرخی ناوەندی',
    'statistics.avg_price_sqm': 'نرخ/م² ناوەندی',
    'statistics.price_change': 'گۆڕانی نرخ',
    'statistics.price_trends': 'ترێندی نرخ',
    'statistics.price_trends_desc': 'نرخی ناوەندی خانووبەرە لە ماوەی کات',
    'statistics.sales_volume': 'قەبارەی فرۆشتن',
    'statistics.sales_volume_desc': 'ژمارەی خانووبەرەی فرۆشراو بۆ هەر مانگ',
    'statistics.properties_sold': 'خانووبەرەی فرۆشراو',
    'statistics.price_by_city': 'نرخ/م² بۆ هەر شار',
    'statistics.price_by_city_desc': 'بەراوردی نرخی ناوەندی/م² لە شارەکاندا',
    'statistics.price_per_sqm': 'نرخ/م²',
    'statistics.type_distribution': 'دابەشکردنی جۆری خانووبەرە',
    'statistics.type_distribution_desc': 'دابەشکردنی خانووبەرەی فرۆشراو بە جۆر',
    'statistics.avg_days_market': 'ڕۆژی ناوەندی لە بازاڕ',
    'statistics.days': 'ڕۆژ',
    'statistics.price_ranges': 'دابەشکردنی مەودای نرخ',
    'statistics.price_ranges_desc': 'ژمارەی خانووبەرەی فرۆشراو لە هەر مەودایەک',
    'statistics.properties': 'خانووبەرە',
    'statistics.year_over_year': 'بەراوردی ساڵانە',
    'statistics.year_over_year_desc': 'بەراوردکردنی نرخی ناوەندی و قەبارەی فرۆشتن بە ساڵان',
    'statistics.all_types': 'هەموو جۆرەکانی خانووبەرە',
    'statistics.all_rooms': 'هەموو ژوورەکان',
    'statistics.rooms': 'ژوور',
    'statistics.export': 'هەناردەکردن',
    'statistics.export_csv': 'داگرتنی CSV',
    'statistics.export_json': 'داگرتنی JSON',
    'statistics.share': 'هاوبەشکردن',
    'statistics.link_copied': 'لینک کۆپی کرا',
    'statistics.link_copied_desc': 'لینکی راپۆرتی ئامار کۆپی کرا بۆ کلیپبۆرد',
    'statistics.custom_date': 'بەرواری تایبەت',
    'statistics.use_preset': 'بەکارهێنانی پێشوەخت',
    'statistics.start_date': 'بەرواری دەستپێک',
    'statistics.end_date': 'بەرواری کۆتایی',
    'statistics.city_comparison': 'بەراوردکردنی شارەکان',
    'statistics.city_comparison_desc': 'ئامارەکانی خانووبەرە لەنێوان دوو شاردا بەراورد بکە',
    'statistics.select_city_1': 'شاری یەکەم هەڵبژێرە',
    'statistics.select_city_2': 'شاری دووەم هەڵبژێرە',
    'statistics.vs': 'دژ',
    'statistics.select_cities_to_compare': 'دوو شار لە سەرەوە هەڵبژێرە بۆ بەراوردکردنی ئامارەکانیان',
    'statistics.avg_size': 'ناوەندی قەبارە',
    
    // Cookie Settings
    'cookie_settings.title': 'ڕێکخستنەکانی کووکی',
    'cookie_settings.description': 'ئێمە کووکی بەکاردەهێنین بۆ باشترکردنی ئەزموونی گەڕانت. دەتوانیت هەڵبژاردنەکانی کووکیت لە خوارەوە دیاری بکەیت.',
    'cookie_settings.necessary': 'کووکییە پێویستەکان',
    'cookie_settings.necessary_desc': 'ئەم کووکیانە گرنگن بۆ کارکردنی دروستی ماڵپەڕەکە. ناتوانرێت لەکارخرابن.',
    'cookie_settings.functional': 'کووکییە فەنکشناڵەکان',
    'cookie_settings.functional_desc': 'ئەم کووکیانە تایبەتمەندییە کەسیەکان وەک هەڵبژاردنی زمان و ڕێکخستنە پاشەکەوتکراوەکان چالاک دەکەن.',
    'cookie_settings.analytics': 'کووکییە شیکارییەکان',
    'cookie_settings.analytics_desc': 'ئەم کووکیانە یارمەتیمان دەدەن بزانین سەردانکەران چۆن کارلێک دەکەن لەگەڵ ماڵپەڕەکەمان بۆ باشترکردنی ئەزموونی بەکارهێنەر.',
    'cookie_settings.marketing': 'کووکییە بازاڕییەکان',
    'cookie_settings.marketing_desc': 'ئەم کووکیانە بەکاردێن بۆ پیشاندانی ڕیکلامی پەیوەندیدار و شوێنکەوتنی کاریگەری کەمپینەکانمان.',
    'cookie_settings.accept_all': 'هەموو قبوڵکە',
    'cookie_settings.reject_all': 'هەموو ڕەتبکەرەوە',
    'cookie_settings.save_preferences': 'هەڵبژاردنەکان پاشەکەوتبکە',
    'cookie_settings.saved': 'هەڵبژاردنەکانی کووکی پاشەکەوتکران',
    
    // Cookie Banner
    'cookie_banner.title': 'ئێمە کووکی بەکاردەهێنین',
    'cookie_banner.description': 'ئێمە کووکی بەکاردەهێنین بۆ باشترکردنی ئەزموونی گەڕانت، پیشاندانی ناوەڕۆکی کەسی، و شیکاری هاتووچۆی ماڵپەڕەکەمان. بە کرتەکردن لەسەر "هەموو قبوڵکە"، تۆ ڕەزامەندی دەدەیت بۆ بەکارهێنانی کووکی.',
    'cookie_banner.accept_all': 'هەموو قبوڵکە',
    'cookie_banner.reject_all': 'هەموو ڕەتبکەرەوە',
    'cookie_banner.customize': 'کەسیکردن',
    
    // Terms of Service
    'terms.title': 'مەرجەکانی خزمەتگوزاری',
    'terms.last_updated': 'دوایین نوێکردنەوە:',
    'terms.section1.title': '1. پەسەندکردنی مەرجەکان',
    'terms.section1.p1': 'بە بەکارهێنانی عقارنت، تۆ پەسەند دەکەیت کە پابەندی ئەم مەرجانە بیت. ئەگەر لەگەڵ ئەم مەرجانە ڕازی نیت، تکایە خزمەتگوزارییەکانمان بەکارمەهێنە.',
    'terms.section1.p2': 'ئێمە مافی خۆمان دەپارێزین کە ئەم مەرجانە لە هەر کاتێکدا بگۆڕین. بەردەوامبوون لە بەکارهێنان دوای گۆڕانکارییەکان وەک پەسەندکردنی مەرجە گۆڕدراوەکان دادەنرێت.',
    'terms.section2.title': '2. پێناسەکردنی خزمەتگوزاری',
    'terms.section2.p1': 'عقارنت پلاتفۆرمێکی دیجیتاڵ دابین دەکات بۆ ڕیکلامەکانی خانوو، گەڕانی مولک و پەیوەندی لەنێوان کڕیار، فرۆشیار و ئەیجنتەکاندا لە عێراق.',
    'terms.section2.p2': 'خزمەتگوزارییەکانمان لیستی مولکەکان، کارکردنی گەڕان، گەڕانە پاشەکەوتکراوەکان، دڵخوازەکان و ئامرازەکانی پەیوەندیکردن لەنێوان بەکارهێنەران و ئەیجنتەکاندا لەخۆدەگرێت.',
    'terms.section3.title': '3. بەرپرسیارێتییەکانی بەکارهێنەر',
    'terms.section3.p1': 'وەک بەکارهێنەری عقارنت، تۆ ڕەزامەندی دەدەیت کە:',
    'terms.section3.li1': 'زانیاری دروست و ڕاست لە ڕیکلام و پرۆفایلەکەتدا دابین بکەیت',
    'terms.section3.li2': 'پلاتفۆرمەکە بۆ چالاکییە ساختەکار یان نایاساییەکان بەکارمەهێنیت',
    'terms.section3.li3': 'مافەکانی موڵکیەتی ژیرانەی کەسانی تر ڕێزبگریت',
    'terms.section3.li4': 'لە کاری دروستی خزمەتگوزاریەکەدا تێکەڵ نەبیت',
    'terms.section4.title': '4. ڕیکلامەکانی مولک',
    'terms.section4.p1': 'بەکارهێنەرانی کە مولکەکان لیست دەکەن بەرپرسن لە ڕاستی ڕیکلامەکانیان. عقارنت ڕاستی زانیاری مولکەکان پشتڕاست ناکاتەوە.',
    'terms.section4.p2': 'ئێمە مافی خۆمان دەپارێزین کە هەر ڕیکلامێک کە مەرجەکانمان پێشێلدەکات یان وەک ساختەکاری ڕاپۆرت دەکرێت لاببەین.',
    'terms.section5.title': '5. موڵکیەتی ژیرانە',
    'terms.section5.p1': 'هەموو ناوەڕۆکێک لەسەر عقارنت، لەوانە دەق، گرافیک، لۆگۆ و نەرمەئامراز، موڵکی عقارنت یان دابینکەرانی ناوەڕۆکەکەیەتی.',
    'terms.section5.p2': 'ناتوانیت ناوەڕۆکەکەمان دووبارە بکەیتەوە، بڵاوی بکەیتەوە یان کاری دەرکراو لێی دروستبکەیت بەبێ مۆڵەتی نووسراوی ڕوون.',
    'terms.section6.title': '6. سنووردارکردنی بەرپرسیارێتی',
    'terms.section6.p1': 'عقارنت "وەک هەیە" بەبێ هیچ جۆرە گەرەنتییەک پێشکەش دەکرێت. ئێمە بەرپرس نین لە هەر زیانێک کە لە بەکارهێنانی خزمەتگوزارییەکانمان دەرچێت.',
    'terms.section7.title': '7. یاسای دەسەڵاتدار',
    'terms.section7.p1': 'ئەم مەرجانە بە یاسای سوید بەڕێوە دەچن. هەر ناکۆکییەک دەبێت لە دادگاکانی سویددا چارەسەر بکرێت.',
    'terms.section7.p2': 'ئەگەر هەر دیاردەیەکی ئەم مەرجانە نەبەجێکردنی بێت، دیاردەکانی تر بەهێز دەمێننەوە.',
    'terms.section8.title': '8. زانیاری پەیوەندی',
    'terms.section8.p1': 'بۆ پرسیارەکان دەربارەی ئەم مەرجانەی خزمەتگوزاری، تکایە پەیوەندیمان پێوەبکەن:',
    'terms.section8.email': 'ئیمەیڵ',
    
    // Privacy Policy
    'privacy.title': 'سیاسەتی تایبەتمەندی',
    'privacy.last_updated': 'دوایین نوێکردنەوە:',
    'privacy.section1.title': '1. پێشەکی',
    'privacy.section1.p1': 'لە عقارنتدا، ئێمە پابەندین بە پاراستنی تایبەتمەندی و داتا کەسییەکانت. ئەم سیاسەتە ڕوون دەکاتەوە کە چۆن زانیارییەکانت کۆدەکەینەوە، بەکاردەهێنین و دەیپارێزین.',
    'privacy.section1.p2': 'بە بەکارهێنانی خزمەتگوزارییەکانمان، ڕەزامەندی دەدەیت بۆ ئەو پراکتیکانەی داتا کە لەم سیاسەتەدا باسکراون.',
    'privacy.section2.title': '2. زانیارییەکان کە کۆدەکەینەوە',
    'privacy.section2.p1': 'ئەم جۆرە زانیاریانە کۆدەکەینەوە:',
    'privacy.section2.li1': 'زانیاری هەژمار: ناو، ناونیشانی ئیمەیڵ، ژمارەی تەلەفۆن',
    'privacy.section2.li2': 'داتای لیستی مولک: ناونیشانەکان، پەناسەکان، وێنەکان، نرخەکان',
    'privacy.section2.li3': 'داتای بەکارهێنان: مێژووی گەڕان، مولکە پاشەکەوتکراوەکان، ڕەفتاری گەڕان',
    'privacy.section2.li4': 'زانیاری ئامێر: ناونیشانی IP، جۆری وێبگەڕ، سیستەمی کارپێکردن',
    'privacy.section3.title': '3. چۆن زانیارییەکانت بەکاردەهێنین',
    'privacy.section3.p1': 'ئێمە زانیارییەکانت بەکاردەهێنین بۆ:',
    'privacy.section3.li1': 'دابینکردن و باشترکردنی خزمەتگوزارییەکانمان',
    'privacy.section3.li2': 'کەسیکردنی ئەزموون و پێشنیارەکانت',
    'privacy.section3.li3': 'ناردنی ئاگادارکردنەوە دەربارەی گەڕانە پاشەکەوتکراوەکان و لیستە نوێیەکان',
    'privacy.section3.li4': 'پەیوەندیکردن لەگەڵت دەربارەی هەژمارەکەت و پرسیارەکانت',
    'privacy.section4.title': '4. هاوبەشکردنی داتا',
    'privacy.section4.p1': 'ئێمە داتا کەسییەکانت نافرۆشین. لەوانەیە زانیاری لەگەڵ ئەیجنتەکانی مولک هاوبەش بکەین کاتێک پرسیار دەکەیت، و لەگەڵ دابینکەرانی خزمەتگوزاری کە یارمەتی کارەکانمان دەدەن.',
    'privacy.section4.p2': 'لەوانەیە زانیاری ئاشکرا بکەین کاتێک یاسا پێویستی دەکات یان بۆ پاراستنی مافەکان و پارێزگاریمان.',
    'privacy.section5.title': '5. مافەکانت',
    'privacy.section5.p1': 'ئەم مافانەی خوارەوەت هەیە سەبارەت بە داتاکانت:',
    'privacy.section5.li1': 'دەستپێگەیشتن: داواکردنی کۆپییەک لە داتا کەسییەکانت',
    'privacy.section5.li2': 'ڕاستکردنەوە: نوێکردنەوە یان ڕاستکردنەوەی زانیاری هەڵە',
    'privacy.section5.li3': 'سڕینەوە: داواکردنی سڕینەوەی هەژمار و داتاکانت',
    'privacy.section5.li4': 'گواستنەوە: وەرگرتنی داتاکانت بە فۆرماتێکی گواستنەوەپێکراو',
    'privacy.section5.li5': 'نەرازیبوونەوە: دەرچوون لە چالاکییەکانی پرۆسەکردنی داتا',
    'privacy.section6.title': '6. پاراستنی داتا',
    'privacy.section6.p1': 'ئێمە ڕێکارە تەکنیکی و ڕێکخراوییە گونجاوەکان جێبەجێدەکەین بۆ پاراستنی داتاکانت لە دەستپێگەیشتنی بێ مۆڵەت، گۆڕین یان تێکدان.',
    'privacy.section6.p2': 'بەڵام، هیچ ڕێگایەکی گواستنەوە لە ڕێگەی ئینتەرنێتەوە %100 پارێزراو نییە، و ناتوانین پاراستنی ڕەها گەرەنتی بکەین.',
    'privacy.section7.title': '7. گۆڕانکارییەکان لەم سیاسەتەدا',
    'privacy.section7.p1': 'لەوانەیە ئەم سیاسەتە تایبەتمەندییە کاتبەکات نوێبکەینەوە. ئاگادارت دەکەینەوە لە گۆڕانکارییە گرنگەکان بە بڵاوکردنەوەی ئاگادارییەک لەسەر ماڵپەڕەکەمان یان ناردنی ئیمەیڵ.',
    'privacy.section8.title': '8. پەیوەندیمان پێوەبکە',
    'privacy.section8.p1': 'ئەگەر پرسیارت هەیە دەربارەی ئەم سیاسەتە تایبەتمەندییە یان داتا کەسییەکانت، تکایە پەیوەندیمان پێوەبکە:',
    'privacy.section8.email': 'ئیمەیڵ',
    
    // FAQ Page
    'faq.title': 'پرسیارە باوەکان',
    'faq.subtitle': 'وەڵامی پرسیارە باوەکان دەربارەی بەکارهێنانی عقارنیت بدۆزەرەوە',
    'faq.account.title': 'هەژمار و تۆمارکردن',
    'faq.account.q1': 'چۆن هەژمارێک دروست بکەم؟',
    'faq.account.q1_answer': 'کرتە لەسەر "چوونەژوورەوە" بکە، پاشان "هەژمار دروستکردن" هەڵبژێرە.',
    'faq.account.q2': 'چۆن وشەی نهێنیەکەم ڕیسێت بکەم؟',
    'faq.account.q2_answer': 'لە پەڕەی چوونەژوورەوە، کرتە لەسەر "وشەی نهێنی لەبیرکراوە" بکە.',
    'faq.account.q3': 'ئایا دەتوانم ئیمەیڵەکەم بگۆڕم؟',
    'faq.account.q3_answer': 'ئێستا ناتوانیت ئیمەیڵەکەت ڕاستەوخۆ بگۆڕیت. تکایە پەیوەندی بە تیمی پشتگیری بکە.',
    'faq.listings.title': 'لیستی موڵکەکان',
    'faq.listings.q1': 'چۆن موڵکێک بۆ فرۆشتن زیاد بکەم؟',
    'faq.listings.q1_answer': 'دەبێت وەکو بریکارێک تۆمارکراو بیت.',
    'faq.listings.q2': 'لیستێک چەند دەمێنێتەوە؟',
    'faq.listings.q2_answer': 'لیستەکان چالاک دەمێننەوە تاوەکو وەکو فرۆشراو نیشانکرابن.',
    'faq.listings.q3': 'ئایا دەتوانم لیستەکەم دوای بڵاوکردنەوە دەستکاری بکەم؟',
    'faq.listings.q3_answer': 'بەڵێ، دەتوانیت لیستەکەت هەر کاتێک دەستکاری بکەیت.',
    'faq.search.title': 'گەڕان بۆ موڵکەکان',
    'faq.search.q1': 'چۆن گەڕانێک پاشەکەوت بکەم؟',
    'faq.search.q1_answer': 'دوای گەڕان، کرتە لەسەر "گەڕان پاشەکەوت بکە" بکە.',
    'faq.search.q2': 'چۆن موڵکێک زیاد بکەم بۆ دڵخوازەکان؟',
    'faq.search.q2_answer': 'کرتە لەسەر ئایکۆنی دڵ لەسەر هەر کارتی موڵکێک بکە.',
    'faq.search.q3': 'ئایا دەتوانم لەسەر نەخشە بگەڕێم؟',
    'faq.search.q3_answer': 'بەڵێ، بینینی نەخشە بەکاربهێنە.',
    'faq.contact.title': 'پەیوەندیکردن بە بریکارەکان',
    'faq.contact.q1': 'چۆن پەیوەندی بە بریکارێک بکەم؟',
    'faq.contact.q1_answer': 'کرتە لەسەر "پەیوەندی بە بریکار" لەسەر هەر لیستێک بکە.',
    'faq.contact.q2': 'ئایا دەتوانم بینینێک دابڕێژم؟',
    'faq.contact.q2_answer': 'بەڵێ، کرتە لەسەر "بینین دابڕێژە" بکە.',
    'faq.security.title': 'ئاسایش و تایبەتمەندی',
    'faq.security.q1': 'ئایا زانیاریە کەسییەکانم پارێزراون؟',
    'faq.security.q1_answer': 'بەڵێ، ئێمە شێوازی نهێنیکردن و ڕێکارەکانی ئاسایش بەکاردەهێنین.',
    'faq.security.q2': 'چۆن دەتوانم هەژمارەکەم بسڕمەوە؟',
    'faq.security.q2_answer': 'بڕۆ بۆ ڕێکخستنەکانی پرۆفایل و "سڕینەوەی هەژمار" هەڵبژێرە.',
    'faq.still_questions': 'هێشتا پرسیارت هەیە؟',
    'faq.contact_us': 'پەیوەندی بە تیمی پشتگیریمان بکە.',
    'newsletter.title': 'بەشداربوون لە هەواڵنامە',
    'newsletter.description': 'بەشداربە بۆ وەرگرتنی نوێترین هەواڵەکانی موڵک.',
    'newsletter.placeholder': 'ئیمەیڵەکەت بنووسە',
    'newsletter.subscribe': 'بەشداربە',
    'newsletter.subscribing': 'بەشداردەبیت...',
    'newsletter.success': 'بە سەرکەوتوویی بەشداربوو!',
    'newsletter.subscribed': 'سوپاس بۆ بەشداربوونت!',
    'newsletter.invalid_email': 'تکایە ئیمەیڵێکی دروست بنووسە',
    'newsletter.error': 'بەشداربوون سەرنەکەوت',
    'newsletter.unsubscribe_note': 'دەتوانیت هەر کاتێک لەبەشداربوون لابدەیت.',
    'newsletter.unsubscribe_title': 'لابردنی بەشداربوون لە هەواڵنامە',
    'newsletter.unsubscribe_description': 'ئیمەیڵەکەت بنووسە بۆ لابردنی بەشداربوون.',
    'newsletter.unsubscribe_button': 'لابردنی بەشداربوون',
    'newsletter.unsubscribe_success': 'بە سەرکەوتوویی لابرا',
    'newsletter.unsubscribe_error': 'لابردن سەرنەکەوت',
    'newsletter.unsubscribe_message': 'ئیتر هەواڵنامەکەمان وەرناگریت.',
    'newsletter.invalid_link': 'لینکی لابردنی نادروست',
    'newsletter.invalid_link_message': 'ئەم لینکی لابردنە نادروستە یان بەسەرچووە. تکایە لینکەکەی ئیمەیڵی هەواڵنامەکە بەکاربهێنە.',
    'newsletter.unsubscribe_reason_label': 'هۆی لابردن (دڵخوازانە)',
    'newsletter.select_reason': 'هۆیەک هەڵبژێرە...',
    'newsletter.reason_too_many_emails': 'ئیمەیڵی زۆر',
    'newsletter.reason_not_relevant': 'ناوەڕۆک پەیوەندیدار نییە بە من',
    'newsletter.reason_never_subscribed': 'هەرگیز بەشداری نەکردم',
    'newsletter.reason_found_another': 'خزمەتگوزاریەکی تر دۆزیمەوە',
    'newsletter.reason_other': 'هۆی تر',
    'common.try_again': 'دووبارە هەوڵ بدەوە',
    'newsletter.admin.title': 'بەشدارانی هەواڵنامە',
    'newsletter.admin.description': 'بەڕێوەبردنی بەشدارانی هەواڵنامەکەت',
    'newsletter.admin.total': 'کۆی بەشداران',
    'newsletter.admin.active': 'چالاک',
    'newsletter.admin.unsubscribed': 'لابراو',
    'newsletter.admin.export_csv': 'هەناردەکردنی CSV',
    'newsletter.admin.refresh': 'نوێکردنەوە',
    'newsletter.admin.subscribers_list': 'لیستی بەشداران',
    'newsletter.admin.subscribers_description': 'هەموو بەشداران و دۆخیان',
    'newsletter.admin.no_subscribers': 'هێشتا بەشدار نییە',
    'newsletter.admin.subscribed_at': 'کاتی بەشداربوون',
    'newsletter.admin.status': 'دۆخ',
    'newsletter.admin.unsubscribed_at': 'کاتی لابردن',
    'newsletter.admin.actions': 'کردارەکان',
    'newsletter.admin.deleted': 'بەشدار سڕایەوە',
    'newsletter.admin.delete_error': 'سڕینەوە سەرنەکەوت',
    'newsletter.admin.no_data': 'داتا نییە بۆ هەناردەکردن',
    'newsletter.admin.exported': 'بە سەرکەوتوویی هەناردەکرا',
    'newsletter.admin.delete_confirm_title': 'بەشدار بسڕیتەوە؟',
    'newsletter.admin.delete_confirm_description': 'ئەمە بەشداری بە تەواوی دەسڕێتەوە.',
    'footer.faq': 'پرسیارە باوەکان',
    
    // Broker Dashboard
    'broker.dashboard_title': 'داشبۆردی بروکەر',
    'broker.dashboard_subtitle': 'بەڕێوەبردنی خانووبەرەکانت و پەیوەندی کڕیارەکان',
    'broker.my_properties': 'خانووبەرەکانم',
    'broker.my_properties_desc': 'بینین و بەڕێوەبردنی لیستەکانی خانووبەرەکانت',
    'broker.viewings': 'بینینەکانی خانووبەرە',
    'broker.viewings_desc': 'بەڕێوەبردنی کاتی بینینی خانووبەرەکان',
    'broker.contacts': 'نامەکانی کڕیار',
    'broker.contacts_desc': 'بینین و وەڵامدانەوەی پرسیارەکانی کڕیاران',
    'broker.analytics': 'شیکاری خانووبەرە',
    'broker.analytics_desc': 'شوێنکەوتنی بینین و چالاکی لە لیستەکانت',
    'broker.subscription': 'بەشداربوون',
    'broker.subscription_desc': 'بەڕێوەبردنی پلان و سنووری لیستەکان',
    'broker.access_denied': 'ئەم پەڕەیە تەنها بۆ بروکەرە تۆمارکراوەکان دەستپێگەیشتنە.',
    'broker.profile_title': 'پرۆفایلی بروکەر',
    'broker.profile_description': 'بەڕێوەبردنی پرۆفایلی گشتی و زانیاری پەیوەندیکردن',
    'broker.back_to_dashboard': 'گەڕانەوە بۆ داشبۆرد',
    'broker.display_name': 'ناوی نیشاندراو',
    'broker.display_name_placeholder': 'ناوەکەت وەک لە کڕیاران دەدرێت',
    'broker.contact_email': 'ئیمەیڵی پەیوەندی',
    'broker.email_placeholder': 'email@xwe.com',
    'broker.email_notification_note': 'ئەم ئیمەیڵە بۆ ئاگادارکردنەوەکان دەربارەی بینینەکان و نامەکانی کڕیاران بەکاردێت.',
    'broker.phone_number': 'ژمارەی تەلەفۆن',
    'broker.phone_placeholder': '+964 xxx xxx xxxx',
    'broker.change_photo': 'گۆڕینی وێنە',
    'broker.save_profile': 'پاشەکەوتکردنی پرۆفایل',
    'broker.profile_saved': 'پرۆفایل بە سەرکەوتوویی پاشەکەوت کرا',
    'broker.profile_save_error': 'پاشەکەوتکردنی پرۆفایل سەرکەوتوو نەبوو',
    'broker.profile_load_error': 'بارکردنی پرۆفایل سەرکەوتوو نەبوو',
    'broker.avatar_uploaded': 'وێنە بە سەرکەوتوویی بارکرا',
    'broker.avatar_upload_error': 'بارکردنی وێنە سەرکەوتوو نەبوو',
    'broker.invalid_image_type': 'تکایە فایلێکی وێنە هەڵبژێرە',
    'broker.image_too_large': 'وێنە دەبێت کەمتر لە 2MB بێت',
    'common.login_required': 'تکایە بچۆ ژوورەوە بۆ دەستگەیشتن بە ئەم پەڕەیە.',
    
    // Broker Public Profile
    'broker.listing_usage': 'بەکارهێنانی لیست',
    'broker.monthly_listing_limits': 'سنووری لیستە مانگانەکان',
    'broker.listings_used': 'لیستە بەکارهێنراوەکان',
    'broker.listings_remaining': 'لیستی ماوە',
    'broker.period_resets': 'ماوە نوێ دەبێتەوە',
    'broker.export_csv': 'هەناردەکردنی CSV',
    'broker.total_views': 'کۆی بینینەکان',
    'broker.scheduled_viewings': 'بینینە ڕێکخراوەکان',
    'broker.client_messages': 'نامەکانی کڕیار',
    'broker.filters': 'پاڵاوتن',
    'broker.all_time': 'هەموو کات',
    'broker.active_listings': 'لیستە چالاکەکان',
    'broker.sold_listings': 'لیستە فرۆشراوەکان',
    'broker.active_listings_desc': 'خانووبەرە بڵاوکراوەکانت',
    'broker.no_active_listings': 'هێشتا لیستی چالاک نییە. یەکەم خانووبەرەکەت زیاد بکە.',
    'broker.all_types': 'هەموو جۆرەکان',
    'broker.all_cities': 'هەموو شارەکان',
    'broker.property_type': 'جۆری خانووبەرە',
    'broker.city': 'شار',
    'broker.date_range': 'مەودای بەروار',
    'broker.last_week': 'هەفتەی ڕابردوو',
    'broker.last_month': 'مانگی ڕابردوو',
    'broker.last_quarter': '٣ مانگی ڕابردوو',
    'broker.sold_listings_desc': 'خانووبەرەکانی کە فرۆشتوتە',
    'broker.no_sold_listings': 'هێشتا هیچ خانووبەرەیەک نەفرۆشراوە',
    'broker.clear_filters': 'پاککردنەوەی فلتەرەکان',
    'broker.selected': 'هەڵبژێردراو',
    'broker.compare': 'بەراوردکردن',
    'broker.mark_as_sold': 'وەک فرۆشراو دیاری بکە',
    'broker.delete_selected': 'سڕینەوەی هەڵبژێردراوەکان',
    'broker.select_all': 'هەموو هەڵبژێرە',
    'broker.rejected_listings': 'لیستە ڕەتکراوەکان',
    'broker.rejected_listings_desc': 'ئەم لیستانە لەلایەن مۆدەرەیتەرەوە ڕەتکراونەتەوە. دەتوانیت ڕازییەتی پێشکەش بکەیت.',
    'broker.no_rejected_listings': 'هیچ لیستی ڕەتکراوە نییە',
    'broker.confirm_delete': 'دڵنیاکردنەوەی سڕینەوە',
    'broker_public.not_found': 'بروکەر نەدۆزرایەوە',
    'broker_public.not_found_description': 'پرۆفایلی بروکەر کە بەدوایدا دەگەڕێیت بوونی نییە.',
    'broker_public.back_to_search': 'گەڕانەوە بۆ گەڕانی بروکەر',
    'broker_public.contact_broker': 'پەیوەندی بە بروکەرەوە',
    'broker_public.agency': 'ئاژانس',
    'broker_public.visit_website': 'سەردانی ماڵپەڕ',
    'broker_public.active_listings': 'ڕیکلامە چالاکەکان',
    'broker_public.sold_properties': 'خانووبەرەی فرۆشراو',
    'broker_public.total_sales': 'کۆی فرۆشتن',
    'broker_public.avg_days_market': 'ڕۆژی ناوەندی لە بازاڕ',
    'broker_public.no_active_listings': 'هیچ ڕیکلامی چالاک نییە لە ئێستادا',
    'broker_public.no_sold_properties': 'هێشتا هیچ خانووبەرەیەک نەفرۆشراوە',
    'broker_public.view_all_properties': 'بینینی هەموو خانووبەرەکان',
    'broker_public.send_message': 'ناردنی نامە',
    'broker_public.view_agency': 'بینینی ئاژانس',
    'broker_public.works_at': 'کار دەکات لە',
    'broker_public.listings': 'ڕیکلامەکان',
    'broker_public.sold': 'فرۆشراو',
    'broker_public.reviews': 'هەڵسەنگاندنەکان',
    'broker_public.rating': 'نمرە',
    'broker_public.featured_broker': 'بروکەری تایبەت',
    'broker_public.top_performer': 'باشترین ئەدا',
    
    // Agency Dashboard
    'agency.my_agency': 'ئاژانسەکەم',
    'agency.dashboard_title': 'داشبۆردی ئاژانس',
    'agency.dashboard_subtitle': 'بەڕێوەبردنی بروکەرەکانت و ڕێکخستنەکانی ئاژانس',
    'agency.brokers_tab': 'بروکەرەکان',
    'agency.invites_tab': 'بانگهێشتنەکان',
    'agency.settings_tab': 'ڕێکخستنەکان',
    'agency.brokers_title': 'بروکەرەکانی ئاژانس',
    'agency.brokers_description': 'بەڕێوەبردنی بروکەرەکان لە ئاژانسەکەت',
    'agency.create_broker': 'دروستکردنی بروکەر',
    'agency.create_broker_title': 'دروستکردنی هەژماری بروکەری نوێ',
    'agency.create_broker_description': 'دروستکردنی هەژماری بروکەری نوێ کە ڕاستەوخۆ بەستراوە بە ئاژانسەکەت',
    'agency.broker_name': 'ناوی تەواو',
    'agency.broker_email': 'ئیمەیڵ',
    'agency.broker_password': 'وشەی نهێنی',
    'agency.create_broker_btn': 'دروستکردنی هەژماری بروکەر',
    'agency.broker_created': 'هەژماری بروکەر بە سەرکەوتوویی دروستکرا',
    'agency.broker_create_error': 'دروستکردنی هەژماری بروکەر سەرکەوتوو نەبوو',
    'agency.no_brokers': 'هێشتا بروکەر نییە',
    'agency.no_brokers_description': 'هەژماری بروکەر دروستبکە یان بانگهێشتن بنێرە',
    'agency.broker_removed': 'بروکەر لە ئاژانسەکە لابرا',
    'agency.broker_remove_error': 'لابردنی بروکەر سەرکەوتوو نەبوو',
    'agency.invites_title': 'بانگهێشتنە چاوەڕوانکراوەکان',
    'agency.invites_description': 'بەڕێوەبردنی بانگهێشتنەکان بۆ بروکەرە داهاتووەکان',
    'agency.send_invite': 'ناردنی بانگهێشت',
    'agency.send_invite_title': 'بانگهێشتکردنی بروکەر',
    'agency.send_invite_description': 'بانگهێشتنامەیەک بنێرە بۆ پەیوەستبوون بە ئاژانسەکەت',
    'agency.invite_email': 'ئیمەیڵ',
    'agency.send_invite_btn': 'ناردنی بانگهێشت',
    'agency.invite_sent': 'بانگهێشت بە سەرکەوتوویی نێردرا',
    'agency.invite_error': 'ناردنی بانگهێشت سەرکەوتوو نەبوو',
    'agency.link_copied': 'لینکی بانگهێشت کۆپی کرا',
    'agency.no_invites': 'بانگهێشتی چاوەڕوانکراو نییە',
    'agency.settings_title': 'ڕێکخستنەکانی ئاژانس',
    'agency.settings_description': 'نوێکردنەوەی زانیاری ئاژانسەکەت',
    'agency.settings_saved': 'ڕێکخستنەکانی ئاژانس پاشەکەوت کرا',
    'agency.settings_save_error': 'پاشەکەوتکردنی ڕێکخستنەکان سەرکەوتوو نەبوو',
    'agency.load_error': 'بارکردنی داتای ئاژانس سەرکەوتوو نەبوو',
    'agency_invite.title': 'پەیوەستبوون بە ئاژانس',
    'agency_invite.description': 'بانگهێشتت کراوە بۆ پەیوەستبوون بە {agencyName}',
    'agency_invite.accept_btn': 'وەرگرتنی بانگهێشت',
    'agency_invite.accepted_success': 'بە سەرکەوتوویی پەیوەست بوویت بە ئاژانسەکە!',
    'agency_invite.accept_error': 'وەرگرتنی بانگهێشت سەرکەوتوو نەبوو',
    'agency_invite.create_account': 'دروستکردنی هەژمار',
    'agency_invite.invalid_link': 'لینکی بانگهێشت نادروستە',
    'agency_invite.expired': 'ئەم بانگهێشتە بەسەرچووە',
    'agency_invite.already_accepted': 'ئەم بانگهێشتە پێشتر وەرگیراوە',
    'agency_invite.fetch_error': 'بارکردنی بانگهێشت سەرکەوتوو نەبوو',
    'agency_invite.error_title': 'بانگهێشتی نادروست',
    'agency_invite.back_home': 'گەڕانەوە بۆ سەرەتا',
    'agency_invite.create_account_description': 'هەژمارێک دروستبکە بۆ وەرگرتنی بانگهێشت',
    'agency_invite.your_name': 'ناوی تۆ',
    'agency_invite.name_placeholder': 'ناوی تەواوت بنووسە',
    'agency_invite.email_placeholder': 'ئیمەیڵ@تۆ.com',
    'agency_invite.password': 'وشەی نهێنی',
    'agency_invite.password_placeholder': 'وشەی نهێنی دروستبکە',
    'agency_invite.confirm_password': 'دووپاتکردنەوەی وشەی نهێنی',
    'agency_invite.confirm_password_placeholder': 'وشەی نهێنیت دووپات بکەرەوە',
    'agency_invite.register_accept_btn': 'هەژمار دروستبکە و پەیوەست بە',
    'agency_invite.registered_success': 'هەژمار دروستکرا و پەیوەست بوو!',
    'agency_invite.register_error': 'دروستکردنی هەژمار سەرکەوتوو نەبوو',
    'agency_invite.passwords_mismatch': 'وشەی نهێنیەکان یەک ناگرنەوە',
    'agency_invite.password_too_short': 'وشەی نهێنی دەبێت لانیکەم ٦ پیت بێت',
    'agency_invite.already_have_account': 'پێشتر هەژمارت هەیە؟',
    'agency_invite.login_here': 'لێرە بچۆ ژوورەوە',
    'agency_invite.joining': 'پەیوەست دەبیت بە',
    'agency_invite.logged_in_as': 'چووتەتە ژوورەوە وەک',
    'agency.notifications_tab': 'ئاگادارکردنەوەکان',
    'agency.notifications_title': 'ئاگادارکردنەوەکانی ئیمەیڵ',
    'agency.notifications_description': 'دیاریبکە کام ئاگادارکردنەوە لەسەر چالاکی بروکەرەکان وەردەگریت',
    'agency.notifications_saved': 'هەڵبژاردنەکانی ئاگادارکردنەوە پاشەکەوت کران',
    'agency.notifications_save_error': 'پاشەکەوتکردنی هەڵبژاردنەکان سەرکەوتوو نەبوو',
    'agency.notif_new_broker_joins': 'بروکەری نوێ پەیوەست دەبێت',
    'agency.notif_new_broker_joins_desc': 'ئاگادار بکرێیتەوە کاتێک بروکەرێک بانگهێشتەکەت وەردەگرێت',
    'agency.notif_broker_property_added': 'بروکەر خانووبەرە زیاد دەکات',
    'agency.notif_broker_property_added_desc': 'ئاگادار بکرێیتەوە کاتێک بروکەرەکان خانووبەرەی نوێ زیاد دەکەن',
    'agency.notif_broker_viewing_requests': 'داواکاری سەردان',
    'agency.notif_broker_viewing_requests_desc': 'ئاگادار بکرێیتەوە لەسەر داواکاری سەردان بۆ خانووبەرەکانی بروکەر',
    'agency.notif_broker_client_messages': 'نامەکانی کڕیار',
    'agency.notif_broker_client_messages_desc': 'ئاگادار بکرێیتەوە کاتێک کڕیارەکان پەیوەندی بە بروکەرەکانت دەکەن',
    'agency.notif_weekly_summary': 'کورتەی هەفتانە',
    'agency.notif_weekly_summary_desc': 'کورتەی هەفتانەی چالاکی ئاژانس وەربگرە',
    'agency.analytics_tab': 'شیکاری',
    'agency.no_brokers_analytics': 'بروکەر زیادبکە بۆ بینینی شیکاری ئەدا',
    'agency.total_properties': 'کۆی خانووبەرە',
    'agency.total_views': 'کۆی بینین',
    'agency.total_viewings': 'داواکاری سەردان',
    'agency.total_sold': 'خانووبەرەی فرۆشراو',
    'agency.total_sales_value': 'کۆی بەهای فرۆشتن',
    'agency.broker_performance_chart': 'ئەدای بروکەر',
    'agency.broker_performance_chart_desc': 'بەراوردکردنی ئامارەکانی بروکەر بە یەک سەیر',
    'agency.individual_broker_stats': 'ئامارەکانی تاکە بروکەر',
    'agency.individual_broker_stats_desc': 'پێوەرە وردەکان بۆ هەر بروکەرێک',
    'agency.properties': 'خانووبەرە',
    'agency.views': 'بینین',
    'agency.viewings': 'سەردان',
    'agency.contacts': 'پەیوەندی',
    'agency.active': 'چالاک',
    'agency.sold': 'فرۆشراو',
    'agency.sales_value': 'بەهای فرۆشتن',
    'agency.upgrade_plan': 'پلانەکە بەرزبکەوە',
    'agency.view_plans': 'پلانەکان ببینە',
    'agency.billing_tab': 'پارەدان',
    'agency.name_label': 'ناوی ئاژانس',
    'agency.email_label': 'ئیمەیڵ',
    'agency.phone_label': 'تەلەفۆن',
    'agency.website_label': 'ماڵپەڕ',
    'agency.address_label': 'ناونیشان',
    'agency.city_label': 'شار',
    'agency.description_label': 'وەسف',
    'agency.description_placeholder': 'بە کڕیارەکان دەربارەی ئاژانسەکەت بڵێ...',
    'agency.save_settings': 'ڕێکخستنەکان هەڵبگرە',
    'agency.broker_usage': 'بەکارهێنانی بروکەر',
    'agency.invite_broker': 'بانگهێشتی بروکەر',
    'agency.brokers': 'بروکەر',
    'agency.broker_limit_reached': 'سنووری بروکەر پڕبووە',
    'agency.upgrade_for_more': 'پلانەکەت بەرزبکەوە بۆ زیادکردنی بروکەری زیاتر',
    'agency.active_listings': 'لیستە چالاکەکان',
    'agency.broker_slots_remaining': 'شوێنە ماوەکان',
    'agency.manage_subscription': 'بەڕێوەبردنی ئابوونە',
    'agency.upgrade_your_plan': 'پلانەکەت بەرزبکەوە',
    'agency.choose_plan': 'ئەو پلانەی باشترین ئاژانسەکەتە هەڵبژێرە',
    'agency.pro_plan': 'پرۆ',
    'agency.enterprise_plan': 'کۆمپانیا',
    'agency.for_growing_agencies': 'بۆ ئاژانسە گەشەکراوەکان',
    'agency.for_large_agencies': 'بۆ ئاژانسە گەورەکان',
    'agency.per_month': '/مانگ',
    'agency.up_to_brokers': 'هەتا {count} بروکەر',
    'agency.unlimited_brokers': 'بروکەری بێسنوور',
    'agency.advanced_analytics': 'شیکاری پێشکەوتوو',
    'agency.full_analytics': 'پاکێجی شیکاری تەواو',
    'agency.priority_support': 'پشتگیری پێشینە',
    'agency.dedicated_support': 'پشتگیری تایبەت',
    'agency.upgrade_to_pro': 'بەرزکردنەوە بۆ پرۆ',
    'agency.upgrade_to_enterprise': 'بەرزکردنەوە بۆ کۆمپانیا',
    'agency.popular': 'ناسراو',
    'agency.current_plan': 'پلانی ئێستا',
    'agency.plan_features': 'تایبەتمەندییەکانی پلان',
    'agency.billing_history': 'مێژووی پارەدان',
    'agency.no_billing_history': 'هێشتا مێژووی پارەدان نییە',
    'agency.subscription_billing': 'بەشداربوون و پارەدان',
    'agency.manage_agency_subscription': 'بەڕێوەبردنی بەشداربوونی ئاژانسەکەت',
    'agency.plan': 'پلان',
    'agency.basic_plan': 'بنەڕەتی',
    'agency.broker_limit': 'سنووری بروکەر',
    'agency.current_usage': 'بەکارهێنانی ئێستا',
    'agency.status': 'دۆخ',
    'agency.active_status': 'چالاک',
    'agency.unlimited': 'بێسنوور',
    'agency.feature': 'تایبەتمەندی',
    'agency.price': 'نرخ',
    'agency.free': 'بەخۆڕایی',
    'agency.analytics': 'شیکاری',
    'agency.basic_analytics': 'بنەڕەتی',
    'agency.advanced': 'پێشکەوتوو',
    'agency.full_suite': 'کۆی تەواو',
    'agency.support': 'پشتگیری',
    'agency.email_support': 'ئیمەیڵ',
    'agency.priority': 'لەپێشەوە',
    'agency.dedicated': 'تایبەت',
    'agency_directory.title': 'ئاژانسەکانی خانووبەرە',
    'agency_directory.subtitle': 'گەڕان و دۆزینەوەی ئاژانسە متمانەپێکراوەکان',
    'agency_directory.search_placeholder': 'گەڕان بۆ ئاژانس...',
    'agency_directory.location_placeholder': 'فلتەرکردن بەپێی شوێن...',
    'agency_directory.agencies_found': 'ئاژانس دۆزرایەوە',
    'agency_directory.no_agencies': 'هیچ ئاژانسێک نەدۆزرایەوە',
    'agency_directory.no_agencies_description': 'هەوڵبدە پێوەرەکانی گەڕان بگۆڕیت',
    'agency_directory.brokers': 'بروکەر',
    'agency_directory.view_agency': 'بینینی ئاژانس',
    'agency_directory.not_found': 'ئاژانس نەدۆزرایەوە',
    'agency_directory.back_to_directory': 'گەڕانەوە بۆ لیست',
    'agency_directory.visit_website': 'سەردانی ماڵپەڕ',
    'agency_directory.our_brokers': 'بروکەرەکانمان',
    'agency_directory.no_brokers_yet': 'هێشتا هیچ بروکەرێک لەم ئاژانسەدا نییە',
    'agency_directory.agency_listings': 'خانووبەرەکان',
    
    // User Listings
    'userListings.title': 'ڕێکلامەکانم',
    'userListings.subtitle': 'بەڕێوەبردنی ڕێکلامەکانی خانووبەرەکانت و دروستکردنی نوێیەکان',
    'userListings.pricingTitle': 'نرخی ڕێکلامەکان',
    'userListings.pricingDesc': 'ڕێکلامی خانووبەرەکەت دروست بکە بە نرخی ئاسانی ئێمە',
    'userListings.listingPrice': '$10 بۆ هەر ڕێکلامێک',
    'userListings.listingDuration': 'بۆ ماوەی 1 مانگ کارا دەبێت',
    'userListings.videoPrice': '+$3 بۆ ڤیدیۆ',
    'userListings.videoDesc': 'گەشتی ڤیدیۆ زیاد بکە بۆ ڕێکلامەکەت',
    'userListings.includeVideo': 'گەشتی ڤیدیۆ تێدا بێت',
    'userListings.createListing': 'دروستکردنی ڕێکلام',
    'userListings.myListingsTitle': 'خانووبەرەکانت',
    'userListings.noListings': 'هێشتا ڕێکلامت نییە',
    'userListings.noListingsDesc': 'یەکەم ڕێکلامی خانووبەرەکەت دروست بکە بۆ دەستپێکردن',
    'userListings.createFirst': 'یەکەم ڕێکلامەکەت دروست بکە',
    'userListings.hasVideo': 'ڤیدیۆ',
    'userListings.renew': 'نوێکردنەوە',
    'userListings.renewSuccess': 'ڕێکلام نوێکرایەوە!',
    'userListings.renewSuccessDesc': 'ڕێکلامەکەت بۆ مانگێکی تر نوێکرایەوە',
    'userListings.paymentSuccess': 'پارەدان سەرکەوتوو بوو!',
    'userListings.paymentSuccessDesc': 'ڕێکلامەکەت ئێستا چالاکە',
    'userListings.paymentCancelled': 'پارەدان هەڵوەشایەوە',
    'userListings.paymentCancelledDesc': 'پارەدانەکەت هەڵوەشایەوە',
    'userListings.checkoutError': 'دروستکردنی پارەدان سەرنەکەوت',
    'userListings.renewError': 'نوێکردنەوەی ڕێکلام سەرنەکەوت',
    'userListings.createDraft': 'دروستکردنی ڕێکلام',
    'userListings.previewListing': 'پێشبینینی ڕێکلامەکەت',
    'userListings.draft': 'ڕەشنووس',
    'userListings.drafts': 'ڕەشنووسەکان',
    'userListings.draftSaved': 'ڕەشنووس پاشەکەوت کرا',
    'userListings.draftSavedDesc': 'ڕێکلامەکەت وەک ڕەشنووس پاشەکەوت کرا',
    'userListings.draftError': 'پاشەکەوتکردنی ڕەشنووس سەرنەکەوت',
    'userListings.draftDeleted': 'ڕەشنووس سڕایەوە',
    'userListings.deleteError': 'سڕینەوە سەرنەکەوت',
    'userListings.saveDraft': 'پاشەکەوتکردنی ڕەشنووس',
    'userListings.saveAndPreview': 'پاشەکەوت و پێشبینین',
    'userListings.publishOptions': 'هەڵبژاردنەکانی بڵاوکردنەوە',
    'userListings.addVideoTour': 'زیادکردنی گەشتی ڤیدیۆ',
    'userListings.videoIncluded': 'گەشتی ڤیدیۆ تێدایە',
    'userListings.totalPrice': 'کۆی گشتی',
    'userListings.backToEdit': 'گەڕانەوە بۆ دەستکاری',
    'userListings.publishNow': 'ئێستا بڵاوی بکەرەوە',
    'userListings.publish': 'بڵاوکردنەوە',
    'userListings.publishSuccess': 'ڕێکلام بڵاوکرایەوە!',
    'userListings.publishSuccessDesc': 'ڕێکلامەکەت ئێستا چاوەڕوانی پشکنین دەکات',
    'userListings.firstListingFree': 'یەکەم ڕێکلام بەخۆڕایی!',
    'userListings.firstListingFreeDesc': 'یەکەم ڕێکلامی خانووبەرەکەت بەتەواوی بەخۆڕاییە. ئێستا بڵاوی بکەرەوە و کڕیاران لە سەرانسەری عێراق بگەن!',
    'userListings.pending': 'چاوەڕوانی پشکنین',
    'userListings.pendingInfo': 'ئەم ڕێکلامانە لەلایەن تیمی چاوەڕوانی پشکنین دەکرێن. کاتێک پەسەند کران ئاگاداری دەکرێیتەوە.',
    'userListings.pendingReview': 'چاوەڕوانی پشکنین',
    'userListings.awaitingApproval': 'چاوەڕوانی پەسەندکردن',
    'userListings.rejected': 'ڕێکلامە ڕەتکراوەکان',
    'userListings.rejectedInfo': 'ئەم ڕێکلامانە پەسەند نەکران. تکایە دەستکاری بکە و کێشەکان چارەسەر بکە پێش دووبارە ناردن.',
    'userListings.rejectedBadge': 'ڕەتکراوە',
    'userListings.live': 'چالاک',
    'userListings.editDraft': 'دەستکاری',
    'userListings.editAndResubmit': 'دەستکاری و دووبارە ناردن',
    'userListings.pendingCannotEdit': 'ناتوانرێت دەستکاری بکرێت لە کاتی پشکنین',
    'userListings.resubmitForReview': 'دووبارە بینێرە بۆ پشکنین',
    'userListings.resubmitInfo': 'گۆڕانکاریەکانت پاشەکەوت کران. ڕێکلامەکەت دووبارە بۆ پشکنین بنێرە بەبێ تێچوونی زیادە.',
    'userListings.submitForReview': 'ناردن بۆ پشکنین',
    'userListings.validation.priceRequired': 'نرخ پێویستە',
    'userListings.validation.cityRequired': 'شار پێویستە',
    'userListings.validation.roomsRequired': 'ژمارەی ژوور پێویستە',
    'userListings.validation.sizeRequired': 'قەبارە پێویستە',
    'userListings.validation.plotSizeRequired': 'قەبارەی زەوی پێویستە',
    'userListings.validation.imagesRequired': 'لانیکەم یەک وێنە پێویستە',
    'userListings.choosePlan': 'پلانی ڕێکلامەکەت هەڵبژێرە',
    'userListings.choosePlanDesc': 'هەڵبژێرە چەند وێنە لەسەر کارتی ڕێکلامەکەت پیشان بدرێت',
    'userListings.upgradePlan': 'پلانی ڕێکلامەکەت باشتر بکە',
    'userListings.upgradePlanDesc': 'باشتر بکە بۆ پیشاندانی وێنەی زیاتر. تەنها جیاوازییەکە دەدەیت.',
    'userListings.selectImages': 'وێنەکان بۆ پیشاندان هەڵبژێرە',
    'userListings.selectImagesDesc': 'وێنەکان هەڵبژێرە بۆ پیشاندان لەسەر کارتی ڕێکلامەکەت',
    'userListings.selectPrimaryImage': 'وێنەی سەرەکی هەڵبژێرە',
    'userListings.selectPrimaryImageDesc': 'وێنەی سەرەکی هەڵبژێرە کە یەکەم جار لەسەر ڕێکلامەکەت پیشان بدرێت',
    'userListings.addVideo': 'گەشتی ڤیدیۆ زیاد بکە',
    'userListings.pleaseSelectImages': 'تکایە وێنەکان هەڵبژێرە بۆ بەردەوامبوون',
    'userListings.pleaseSelectPrimaryImage': 'تکایە وێنەی سەرەکی هەڵبژێرە بۆ بەردەوامبوون',
    'userListings.proceedToPayment': 'بەردەوام بە بۆ پارەدان',
    'userListings.tierBasic': 'بنەڕەتی',
    'userListings.tierStandard': 'ستاندارد',
    'userListings.tierPremium': 'پریمیۆم',
    'userListings.tierBasicDesc': '١ وێنە لەسەر کارت',
    'userListings.tierStandardDesc': '٣ وێنە سلایدشۆ',
    'userListings.tierPremiumDesc': 'هەموو وێنەکان + باج',
    'userListings.tierDuration': '٣٠ ڕۆژ چالاک',
    'userListings.tierBest': 'باشترین',
    'userListings.tierBasicFull': 'ئاستی بنەڕەتی (١ وێنە)',
    'userListings.tierStandardFull': 'ئاستی ستاندارد (٣ وێنە)',
    'userListings.tierPremiumFull': 'ئاستی پریمیۆم (هەموو وێنەکان)',
    'userListings.changeTier': 'گۆڕینی ئاست',
    'userListings.selectImageTier': 'ئاستی وێنە هەڵبژێرە',
    'userListings.selectTierFirst': 'پێش بڵاوکردنەوە ئاستێک هەڵبژێرە',
    'userListings.upgrade': 'بەرزکردنەوە',
    'common.total': 'کۆی گشتی',
    
    // Settings Page
    'settings.title': 'ڕێکخستنەکان',
    'settings.subtitle': 'بەڕێوەبردنی ڕێکخستنەکان و هەڵبژاردنەکانی هەژمارەکەت',
    'settings.profile': 'پڕۆفایل',
    'settings.notifications': 'ئاگادارکردنەوەکان',
    'settings.account': 'هەژمار',
    'settings.profile_info': 'زانیاری پڕۆفایل',
    'settings.profile_desc': 'نوێکردنەوەی زانیاری کەسیت',
    'settings.change_photo': 'گۆڕینی وێنە',
    'settings.display_name': 'ناوی نیشاندان',
    'settings.display_name_placeholder': 'ناوەکەت',
    'settings.email': 'ئیمەیڵ',
    'settings.email_placeholder': 'your@email.com',
    'settings.phone': 'تەلەفۆن',
    'settings.phone_placeholder': '+964 770 123 4567',
    'settings.save': 'پاشەکەوتکردنی گۆڕانکاریەکان',
    'settings.saved': 'ڕێکخستنەکان بە سەرکەوتوویی پاشەکەوت کران',
    'settings.load_error': 'بارکردنی ڕێکخستنەکان سەرکەوتوو نەبوو',
    'settings.save_error': 'پاشەکەوتکردنی ڕێکخستنەکان سەرکەوتوو نەبوو',
    'settings.invalid_image': 'تکایە فایلێکی وێنە هەڵبژێرە',
    'settings.image_too_large': 'وێنەکە دەبێت کەمتر لە 2MB بێت',
    'settings.avatar_uploaded': 'وێنەکە بارکرا',
    'settings.avatar_error': 'بارکردنی وێنە سەرکەوتوو نەبوو',
    'settings.notification_preferences': 'هەڵبژاردنەکانی ئاگادارکردنەوە',
    'settings.notification_desc': 'هەڵبژێرە کام ئاگادارکردنەوە دەتەوێت وەربگریت',
    'settings.notif_property_alerts': 'ئاگادارکردنەوەی خانووبەرە',
    'settings.notif_property_alerts_desc': 'ئاگادار بکرێیتەوە لەسەر خانووبەرە نوێیەکان',
    'settings.notif_saved_search': 'ئاگادارکردنەوەی گەڕانی پاشەکەوتکراو',
    'settings.notif_saved_search_desc': 'ئاگادار بکرێیتەوە کاتێک خانووبەرەی نوێ لەگەڵ گەڕانە پاشەکەوتکراوەکانت دەگونجێت',
    'settings.notif_newsletter': 'نامەی هەفتانە',
    'settings.notif_newsletter_desc': 'نامەی هەفتانەمان بەڵگەنامەکانی بازاڕ وەربگرە',
    'settings.notif_viewing': 'پشتڕاستکردنەوەی سەردان',
    'settings.notif_viewing_desc': 'پشتڕاستکردنەوە وەربگرە بۆ سەردانە داڕێژراوەکان',
    'settings.account_settings': 'ڕێکخستنەکانی هەژمار',
    'settings.account_desc': 'بەڕێوەبردنی ئاسایشی هەژمارەکەت و هەڵبژاردنەکان',
    'settings.change_password': 'گۆڕینی وشەی نهێنی',
    'settings.change_password_desc': 'نوێکردنەوەی وشەی نهێنیت',
    'settings.change': 'گۆڕین',
    'settings.two_factor': 'ڕاستکردنەوەی دوو-هەنگاوی',
    'settings.two_factor_desc': 'زیادکردنی ئاسایشی زیادە بۆ هەژمارەکەت',
    'settings.enable': 'چالاککردن',
    'settings.delete_account': 'سڕینەوەی هەژمار',
    'settings.delete_account_desc': 'سڕینەوەی هەژمارەکەت بە تەواوی',
    'settings.delete': 'سڕینەوە',
    'settings.logout': 'دەرچوون',
    
    // Common
    'common.back': 'گەڕانەوە',
    
    // Contact Form Modal
    'contact_form.title': 'پەیوەندی',
    'contact_form.description': 'نامەیەک بنێرە بۆ پەیوەندیکردن.',
    'contact_form.name': 'ناو',
    'contact_form.name_placeholder': 'ناوەکەت',
    'contact_form.email': 'ئیمەیڵ',
    'contact_form.email_placeholder': 'your@email.com',
    'contact_form.phone': 'تەلەفۆن',
    'contact_form.phone_placeholder': 'ژمارەی تەلەفۆنەکەت',
    'contact_form.message': 'نامە',
    'contact_form.message_placeholder': 'چۆن دەتوانین یارمەتیت بدەین؟',
    'contact_form.send': 'ناردنی نامە',
    'contact_form.sending': 'دەنێردرێت...',
    'contact_form.success_title': 'نامەکە نێردرا!',
    'contact_form.success_message': 'نامەکەت نێردرا. پشتڕاستکردنەوەیەک بە ئیمەیڵ وەردەگریت.',
    'contact_form.error_title': 'هەڵە',
    'contact_form.error_message': 'ناردنی نامە سەرکەوتوو نەبوو. تکایە دووبارە هەوڵ بدەرەوە.',
    
    // Messaging
    'messaging.title': 'نامەکان',
    'messaging.description': 'گفتوگۆ تایبەتەکانت',
    'messaging.new_message': 'نامەی نوێ',
    'messaging.no_messages': 'هیچ نامەیەک نیە هێشتا',
    'messaging.no_conversations': 'هیچ گفتوگۆیەک نیە هێشتا',
    'messaging.send': 'ناردن',
    'messaging.send_message': 'ناردنی نامە',
    'messaging.message_to': 'ناردنی نامە بۆ',
    'messaging.subject': 'بابەت',
    'messaging.subject_placeholder': 'بابەت (ئارەزوومەندانە)',
    'messaging.message': 'نامە',
    'messaging.message_placeholder': 'نامەکەت بنووسە...',
    'messaging.login_required': 'دەبێت بچیتە ژوورەوە بۆ ناردنی نامە.',
    'messaging.message_sent': 'نامەکە بە سەرکەوتوویی نێردرا',
    'messaging.contact_agent_description': 'ڕاستەوخۆ پەیوەندی بە بریکارەکەوە بکە',
    'messaging.type_message': 'نامەیەک بنووسە...',
    'messaging.inbox': 'پۆستی هاتوو',
    'messaging.sent': 'نێردراو',
    'messaging.search': 'گەڕان لە گفتوگۆکان...',
    'messaging.select_conversation': 'گفتوگۆیەک هەڵبژێرە',
    'messaging.select_description': 'گفتوگۆیەک لە لیستەکە هەڵبژێرە بۆ دەستپێکردنی نامە',
    'messaging.encrypted': 'شیفرەکراو لە سەرەتا بۆ کۆتایی',
    'messaging.decrypt': 'شیفرەکردنی نامە لابردن',
    'messaging.reply': 'وەڵام',
    'messaging.replying_to': 'وەڵامدانەوە بە',
    'messaging.replying_to_yourself': 'خۆت',
    'messaging.forward': 'ناردنەوە',
    'messaging.delete': 'سڕینەوە',
    'messaging.delete_confirm': 'سڕینەوەی نامە',
    'messaging.delete_description': 'ئەم نامەیە بۆ هەموو کەس دەسڕدرێتەوە. ئەم کردارە ناگەڕێتەوە.',
    'messaging.delete_for_everyone': 'سڕینەوە بۆ هەموان',
    'messaging.pin': 'هەڵواسین',
    'messaging.unpin': 'لابردنی هەڵواسین',
    'messaging.pinned_messages': 'نامە هەڵواسراوەکان',
    'messaging.no_pinned': 'هیچ نامەیەکی هەڵواسراو نیە',
    'messaging.delivered': 'گەیشت',
    'messaging.read': 'خوێندراوە',
    'messaging.seen_at': 'بینراوە لە',
    'messaging.voice_message': 'نامەی دەنگی',
    'messaging.search_messages': 'گەڕان لە نامەکان...',
    'messaging.contacts': 'پەیوەندیەکان',
    'messaging.results_found': 'ئەنجام دۆزرایەوە',
    'messaging.recording': 'تۆمارکردن...',
    'messaging.typing': 'دەنووسێت...',
    'messaging.online': 'سەرهێڵ',
    'messaging.tap_for_info': 'لێرە بکلیک بکە بۆ زانیاری پەیوەندی',
    'messaging.photos': 'وێنە',
    'messaging.camera': 'کامێرا',
    'messaging.location': 'شوێن',
    'messaging.contact': 'پەیوەندی',
    'messaging.document': 'بەڵگەنامە',
    'messaging.poll': 'ڕاپرسی',
    'messaging.event': 'ڕووداو',
    'messaging.location_coming_soon': 'هاوبەشی شوێن بەم زووانە دێت',
    'messaging.contact_coming_soon': 'هاوبەشی پەیوەندی بەم زووانە دێت',
    'messaging.poll_coming_soon': 'دروستکردنی ڕاپرسی بەم زووانە دێت',
    'messaging.event_coming_soon': 'دروستکردنی ڕووداو بەم زووانە دێت',
    'messaging.schedule_event': 'خشتەبەندی ڕووداو',
    'messaging.event_title': 'ناونیشانی ڕووداو',
    'messaging.event_title_placeholder': 'بۆ نموونە: سەردانی خانوو',
    'messaging.event_date': 'بەروار',
    'messaging.event_time': 'کات',
    'messaging.event_location': 'شوێن',
    'messaging.event_location_placeholder': 'شوێن یان ناونیشان بنووسە',
    'messaging.event_description': 'وەسف',
    'messaging.event_description_placeholder': 'هەر وردەکاریەکی تر زیاد بکە...',
    'messaging.send_event': 'ناردنی ڕووداو',
    'messaging.event_created': 'ڕووداو دروستکرا',
    'messaging.create_poll': 'دروستکردنی ڕاپرسی',
    'messaging.poll_question': 'پرسیار',
    'messaging.poll_question_placeholder': 'دەتەوێت چی بپرسیت؟',
    'messaging.poll_options': 'هەڵبژاردنەکان',
    'messaging.option': 'هەڵبژاردن',
    'messaging.add_option': 'زیادکردنی هەڵبژاردن',
    'messaging.allow_multiple': 'ڕێگەدان بە وەڵامی زیاتر',
    'messaging.send_poll': 'ناردنی ڕاپرسی',
    'messaging.poll_created': 'ڕاپرسی دروستکرا',
    'messaging.edit': 'دەستکاری',
    'messaging.edit_message': 'دەستکاری نامە',
    'messaging.edit_description': 'گۆڕانکاری لە نامەکەتدا بکە',
    'messaging.edit_time_remaining': 'تۆ',
    'messaging.minutes_to_edit': 'خولەکت هەیە بۆ دەستکاری ئەم نامەیە',
    'messaging.message_edited': 'نامە دەستکاری کرا',
    'messaging.edit_failed': 'دەستکاری نامە سەرنەکەوت',
    'messaging.from_date': 'لە',
    'messaging.to_date': 'بۆ',
    'messaging.clear_filters': 'پاککردنەوەی فلتەرەکان',
    'messaging.export_chat': 'ناردنەوەی گفتوگۆ',
    'messaging.export_description': 'ناردنەوەی مێژووی گفتوگۆ',
    'messaging.export_text': 'فایلی دەق (.txt)',
    'messaging.export_text_desc': 'فۆرماتی دەقی ساکار، ئاسان بۆ خوێندنەوە و هاوبەشکردن',
    'messaging.export_pdf': 'بەڵگەنامەی PDF',
    'messaging.export_pdf_desc': 'بەڵگەنامەی شێوەپێدراو، گونجاو بۆ چاپکردن',
    'messaging.export_messages_count': 'نامە دەناردرێت',
    'messaging.exporting': 'دەناردرێت...',
    'messaging.export': 'ناردنەوە',
    'messaging.export_success': 'گفتوگۆ بە سەرکەوتوویی نێردرا',
    'messaging.export_failed': 'ناردنەوەی گفتوگۆ سەرکەوتوو نەبوو',
    'messaging.is_typing': 'دەنووسێت...',
    
    // Quick Reply Templates
    'quick_reply.title': 'وەڵامە خێراکان',
    'quick_reply.greeting': 'سڵاو! سوپاس بۆ پەیوەندی کردن.',
    'quick_reply.interested': 'من حەزم لەم خانووبەرەیە. دەتوانیت زیاتر باسی بکەیت؟',
    'quick_reply.schedule_viewing': 'دەمەوێت سەردانێک ڕێکبخەم. چ کاتێک بەردەستە؟',
    'quick_reply.ask_price': 'ئایا نرخەکە دەکرێ گفتوگۆ لەسەر بکرێت؟',
    'quick_reply.ask_availability': 'ئایا ئەم خانووبەرەیە هێشتا بەردەستە؟',
    'quick_reply.thank_you': 'سوپاس بۆ زانیارییەکان. زوو پەیوەندیت پێوە دەکەم.',
    'quick_reply.will_contact': 'زوو پەیوەندیت پێوە دەکەم بە زانیاری زیاتر.',
    'quick_reply.not_interested': 'سوپاس، بەڵام ئیتر حەزم لەم خانووبەرەیە نییە.',
    
    // Notifications
    'notifications.enable': 'چالاککردنی ئاگادارکردنەوەکان',
    'notifications.on': 'ئاگادارکردنەوەکان چالاکن',
    'notifications.preferences': 'ڕێکخستنەکانی ئاگادارکردنەوە',
    'notifications.sound': 'دەنگی ئاگادارکردنەوە',
    'notifications.sound_desc': 'دەنگێک لێبدە کاتێک پەیامێک وەردەگریت',
    'notifications.sound_default': 'بنەڕەت',
    'notifications.sound_chime': 'زەنگ',
    'notifications.sound_bell': 'زەنگوولە',
    'notifications.sound_pop': 'پۆپ',
    'notifications.test': 'تاقیکردنەوە',
    'notifications.vibration': 'لەرزین',
    'notifications.vibration_desc': 'لەرزین کاتێک پەیامێک وەردەگریت (تەنها مۆبایل)',
    'messaging.you': 'تۆ',
    'messaging.encrypted_message': 'پەیامی کۆدکراو',
    'messaging.e2e_notice': 'پەیام و پەیوەندییەکان لە سەرەتاوە بۆ کۆتایی کۆدکراون. تەنها کەسانی ئەم چاتە دەتوانن بیخوێننەوە، گوێی لێ بگرن، یان هاوبەشی بکەن.',
    'messaging.today': 'ئەمڕۆ',
    'messaging.yesterday': 'دوێنێ',
    'messaging.search_groups': 'گەڕان لە گروپەکان...',
    
    // Attachment viewer
    'attachment.all_media': 'هەموو میدیاکان',
    'attachment.go_to_message': 'بڕۆ بۆ پەیام',
    'attachment.download': 'داگرتن',
    'attachment.view_fullscreen': 'بینینی پڕ شاشە',
    'attachment.save_to_gallery': 'هەڵگرتن لە گالێری',
    'attachment.share': 'هاوبەشکردن',
    'attachment.double_tap_zoom': 'دوو جار لێدان بۆ زووم',
    'attachment.zoom_in': 'زووم کردن',
    'attachment.zoom_out': 'زووم کردنەوە',
    'attachment.reset_zoom': 'ڕێکخستنەوەی زووم',
    'attachment.ctrl_scroll_zoom': 'Ctrl + سکڕۆڵ بۆ زووم',
    'attachment.fit_width': 'گونجاندن بۆ پانی',
    'attachment.fit_height': 'گونجاندن بۆ بەرزی',
    'attachment.click_to_navigate': 'کلیک بکە بۆ ناڤبەری',
    
    // Status
    'status.online': 'سەرهێڵ',
    'status.offline': 'دەرهێڵ',
    'status.last_seen': 'دوایین جار بینرا',
    
    // Blocking
    'blocking.block_user': 'بلۆک کردنی بەکارهێنەر',
    'blocking.block_confirm': 'دڵنیایت کە دەتەوێت {name} بلۆک بکەیت؟ ناتوانن پەیامت بۆ بنێرن.',
    'blocking.reason_optional': 'هۆکار (دڵخوازانە)',
    'blocking.reason_placeholder': 'بۆچی ئەم بەکارهێنەرە بلۆک دەکەیت؟',
    'blocking.block': 'بلۆک',
    'blocking.user_blocked': 'بەکارهێنەر بە سەرکەوتوویی بلۆک کرا',
    'blocking.already_blocked': 'بەکارهێنەر پێشتر بلۆک کراوە',
    'blocking.error': 'بلۆک کردن سەرکەوتوو نەبوو',
    'blocking.blocked_users': 'بەکارهێنەرە بلۆک کراوەکان',
    'blocking.blocked_users_description': 'بەڕێوەبردنی لیستی بەکارهێنەرە بلۆک کراوەکان',
    'blocking.no_blocked_users': 'تۆ کەسێکت بلۆک نەکردووە',
    'blocking.blocked_on': 'لە {date} بلۆک کرا',
    'blocking.unblock': 'لابردنی بلۆک',
    'blocking.user_unblocked': 'بلۆک لابرا',
    'blocking.unblock_error': 'لابردنی بلۆک سەرکەوتوو نەبوو',
    'blocking.fetch_error': 'بارکردنی بەکارهێنەرە بلۆک کراوەکان سەرکەوتوو نەبوو',
    'blocking.you_blocked_user': 'تۆ ئەم بەکارهێنەرەت بلۆک کردووە. بۆ ناردنی پەیام بلۆکەکە لاببە.',
    'blocking.user_blocked_you': 'ئەم بەکارهێنەرە تۆی بلۆک کردووە. ناتوانیت پەیام بنێریت.',
    
    // Reporting
    'reporting.report_user': 'گوزارشت کردنی بەکارهێنەر',
    'reporting.report_description': 'گوزارشت کردنی {name} بۆ پێشێل کردنی ڕێنماییەکانمان',
    'reporting.select_reason': 'تکایە هۆکارێک هەڵبژێرە',
    'reporting.reason': 'هۆکاری گوزارشت',
    'reporting.additional_details': 'وردەکاری زیاتر',
    'reporting.details_placeholder': 'کۆنتێکستی زیاتر دابین بکە...',
    'reporting.submit_report': 'ناردنی گوزارشت',
    'reporting.report_submitted': 'گوزارشت نێردرا. بە زووی پێداچوونەوەی دەکەین.',
    'reporting.error': 'ناردنی گوزارشت سەرکەوتوو نەبوو',
    'reporting.reasons.spam': 'سپام یان پەیامی نەخوازراو',
    'reporting.reasons.harassment': 'ئازار یان ڕق و کینە',
    'reporting.reasons.inappropriate_content': 'ناوەڕۆکی نەگونجاو',
    'reporting.reasons.fraud': 'فریودەری یان تەڵەکە',
    'reporting.reasons.impersonation': 'خۆ دانانی بە کەسێکی تر',
    'reporting.reasons.other': 'هی تر',
    
    // Admin Reports
    'admin.reports': 'گوزارشتەکان',
    'admin.reports_panel': 'گوزارشتەکانی بەکارهێنەران',
    'admin.reports_description': 'پێداچوونەوە و چارەسەرکردنی گوزارشتەکانی بەکارهێنەران',
    'admin.fetch_reports_error': 'هێنانی گوزارشتەکان سەرکەوتوو نەبوو',
    'admin.pending': 'چاوەڕوان',
    'admin.resolved': 'چارەسەر کرا',
    'admin.all': 'هەموو',
    'admin.no_reports': 'هیچ گوزارشتێک نەدۆزرایەوە',
    'admin.back_to_list': 'گەڕانەوە بۆ لیست',
    'admin.reporter': 'گوزارشتکەر',
    'admin.reported_user': 'بەکارهێنەری گوزارشت کراو',
    'admin.reason': 'هۆکار',
    'admin.reported_on': 'لە ڕێکەوتی گوزارشت کرا',
    'admin.reported_by': 'گوزارشت کراوە لەلایەن',
    'admin.resolution_notes': 'تێبینییەکانی چارەسەری زیاد بکە...',
    'admin.dismiss': 'ڕەتکردنەوە',
    'admin.warn': 'ئاگاداری بەکارهێنەر',
    'admin.block': 'بلۆک کردنی بەکارهێنەر',
    'admin.report_resolved': 'گوزارشت بە سەرکەوتوویی چارەسەر کرا',
    'admin.resolve_error': 'چارەسەرکردنی گوزارشت سەرکەوتوو نەبوو',
    'admin.resolution': 'چارەسەر',
    'messaging.unknown_user': 'بەکارهێنەری نەناسراو',
    'messaging.no_groups': 'هیچ گروپێک نییە تا ئێستا',
    'messaging.create_group': 'دروستکردنی گروپ',
    'messaging.group_chat': 'چاتی گروپی',
    'messaging.direct_messages': 'ڕاستەوخۆ',
    'messaging.groups': 'گروپەکان',
    'messaging.group_name': 'ناوی گروپ',
    'messaging.group_description': 'وەسف',
    
    'nav.favorites': 'دڵخوازەکان',
    'nav.profile': 'پڕۆفایل',
    'nav.login': 'چوونەژوورەوە',
    'nav.logout': 'چوونەدەرەوە',
    'nav.recent_searches': 'گەڕانی دوایی',
    'nav.no_recent_searches': 'گەڕانی دوایی نییە',
    
    // Agency Pricing
    'agency_pricing.badge': 'پلانەکانی ئاژانس',
    'agency_pricing.title': 'ئاژانسەکەت گەورە بکە بە پلانی گونجاو',
    'agency_pricing.subtitle': 'پلانی تەواو بۆ ئاژانسی خانووبەرەکەت هەڵبژێرە. تیمەکەت گەورە بکە، خانووبەرەی زیاتر بەڕێوەببە و خزمەتگوزاری نایاب بە کڕیارەکانت پێشکەش بکە.',
    'agency_pricing.most_popular': 'باوترین',
    'agency_pricing.per_month': '/مانگانە',
    'agency_pricing.processing': 'لە کارکردندایە...',
    'agency_pricing.comparison_title': 'بەراوردکردنی وردی تایبەتمەندییەکان',
    'agency_pricing.feature': 'تایبەتمەندی',
    'agency_pricing.faq_title': 'پرسیارە باوەکان',
    'agency_pricing.cta_title': 'ئامادەیت ئاژانسەکەت گەورە بکەیت؟',
    'agency_pricing.cta_subtitle': 'پەیوەندی بکە بە هەزاران ئاژانسی خانووبەرەی سەرکەوتوو کە پلاتفۆرمەکەمان بەکاردەهێنن بۆ بەڕێوەبردنی تیمەکانیان و خانووبەرەکانیان بە شێوەیەکی کاریگەر.',
    'agency_pricing.cta_button': 'دەستپێکردن بە Pro',
    'agency_pricing.sign_in_required': 'تکایە بچۆرەژوورەوە بۆ بەرزکردنەوەی پلانەکەت',
    'agency_pricing.tier.starter': 'دەستپێکەر',
    'agency_pricing.tier.starter_desc': 'تەواوە بۆ ئاژانسە بچووکەکان کە تازە دەستیان پێکردووە',
    'agency_pricing.tier.starter_cta': 'دەستپێبکە',
    'agency_pricing.tier.pro': 'Pro',
    'agency_pricing.tier.pro_desc': 'بۆ ئاژانسە گەشەکردووەکان کە تیمی گەورەتریان هەیە',
    'agency_pricing.tier.pro_cta': 'بەرزکردنەوە بۆ Pro',
    'agency_pricing.tier.enterprise': 'Enterprise',
    'agency_pricing.tier.enterprise_desc': 'بۆ ئاژانسە گەورەکان کە پێویستییە بێسنووریان هەیە',
    'agency_pricing.tier.enterprise_cta': 'بڕۆ بۆ Enterprise',
    'agency_pricing.feature.brokers_3': 'تا 3 بروکەر',
    'agency_pricing.feature.brokers_10': 'تا 10 بروکەر',
    'agency_pricing.feature.brokers_unlimited': 'بروکەری بێسنوور',
    'agency_pricing.feature.listings_basic': 'ڕیکلامی خانووبەرەی بنەڕەتی',
    'agency_pricing.feature.listings_advanced': 'ڕیکلامی خانووبەرەی پێشکەوتوو',
    'agency_pricing.feature.listings_premium': 'ڕیکلامی خانووبەرەی پریمیۆم',
    'agency_pricing.feature.messaging': 'نامەنێرین بۆ کڕیار',
    'agency_pricing.feature.scheduling': 'خشتەبەندی سەردان',
    'agency_pricing.feature.analytics': 'داشبۆردی شیکاری',
    'agency_pricing.feature.analytics_advanced': 'شیکاری پێشکەوتوو',
    'agency_pricing.feature.support': 'پشتگیری پێشینەدار',
    'agency_pricing.feature.support_247': 'پشتگیری 24/7',
    'agency_pricing.feature.branding': 'براندی تایبەت',
    'agency_pricing.feature.api': 'دەستگەیشتن بە API',
    'agency_pricing.comparison.brokers': 'ژمارەی بروکەرەکان',
    'agency_pricing.comparison.listings': 'ڕیکلامی خانووبەرە',
    'agency_pricing.comparison.basic': 'بنەڕەتی',
    'agency_pricing.comparison.advanced': 'پێشکەوتوو',
    'agency_pricing.comparison.premium': 'پریمیۆم',
    'agency_pricing.faq.change_plan_q': 'دەتوانم پلانەکەم دواتر بگۆڕم؟',
    'agency_pricing.faq.change_plan_a': 'بەڵێ! دەتوانیت لە هەر کاتێکدا پلانەکەت بەرز بکەیتەوە یان دابەزێنیت. گۆڕانکارییەکان دەستبەجێ کاردەکەن و قەرزەکەت لە پێیدا ڕێک دەخەینەوە.',
    'agency_pricing.faq.exceed_limit_q': 'چی ڕوودەدات ئەگەر لە سنووری بروکەر تێپەڕم؟',
    'agency_pricing.faq.exceed_limit_a': 'پێویستە پلانی بەرزتر هەڵبژێریت بۆ زیادکردنی بروکەری زیاتر. بروکەرە ئەمڕۆییەکانت بە ئاسایی کاردەکەن.',
    'agency_pricing.faq.trial_q': 'تاقیکردنەوەی بەخۆڕایی هەیە بۆ پلانە پولدارەکان؟',
    'agency_pricing.faq.trial_a': 'دڵنیایی گەڕانەوەی پارە بۆ 14 ڕۆژ لەسەر هەموو پلانە پولدارەکان پێشکەش دەکەین. ئەگەر ڕازی نەبیت، پەیوەندیمان پێوە بکە بۆ گەڕانەوەی تەواو.',
    'agency_pricing.faq.cancel_q': 'چۆن بەشداریکردنەکەم هەڵدەوەشێنمەوە؟',
    'agency_pricing.faq.cancel_a': 'دەتوانیت بەشداریکردنەکەت لە داشبۆردی ئاژانسەکەت بەڕێوەببەیت. بڕۆ بۆ تابی قەرز و کرتە لەسەر "بەڕێوەبردنی بەشداری" بکە بۆ هەڵوەشاندنەوە یان گۆڕین.',
    'agency_pricing.view_plans': 'بینینی پلان و نرخەکان',
    
    // Broker Pricing
    'broker_pricing.badge': 'پلانەکانی دەڵاڵ',
    'broker_pricing.title': 'هەژمارەکەت وەک دەڵاڵ بەرزبکەرەوە',
    'broker_pricing.subtitle': 'پلانی تەواو بۆ کارەکەت لە خانووبەرە هەڵبژێرە. زیاتر لیست، تەحلیلی باشتر و پشتگیری پێشینە بەدەستبهێنە.',
    'broker_pricing.most_popular': 'باوترین',
    'broker_pricing.current_plan': 'پلانی ئێستات',
    'broker_pricing.per_month': '/مانگ',
    'broker_pricing.processing': 'چارەسەرکردن...',
    'broker_pricing.loading': 'بارکردن...',
    'broker_pricing.manage_subscription': 'بەڕێوەبردنی بەشداری',
    'broker_pricing.manage_note': 'هەر کات دابەزاندن یان هەڵوەشاندنەوە • خۆکارانە مانگانە نوێ دەبێتەوە',
    'broker_pricing.downgrade': 'دابەزاندن',
    'broker_pricing.comparison_title': 'بەراوردکردنی وردی تایبەتمەندییەکان',
    'broker_pricing.feature': 'تایبەتمەندی',
    'broker_pricing.faq_title': 'پرسیارە باوەکان',
    'broker_pricing.cta_title': 'ئامادەیت بۆ گەشەکردنی کارەکەت؟',
    'broker_pricing.cta_subtitle': 'بەندببە بە هەزاران دەڵاڵی سەرکەوتوو کە پلاتفۆرمەکەمان بەکاردەهێنن بۆ لیستکردنی زیاتر و گرێبەستنی زیاتر.',
    'broker_pricing.cta_button': 'دەست پێبکە لەگەڵ Pro',
    'broker_pricing.sign_in_required': 'تکایە چوونەژوورەوە بکە بۆ بەرزکردنەوەی پلانەکەت',
    'broker_pricing.already_on_plan': 'تۆ لە ئێستادا لەسەر ئەم پلانەیت',
    'broker_pricing.checkout_error': 'نەتوانرا پارەدان دەستپێبکرێت',
    'broker_pricing.portal_error': 'نەتوانرا بەڕێوەبردنی بەشداری بکرێتەوە',
    'broker_pricing.tier.normal': 'ئاسایی',
    'broker_pricing.tier.normal_desc': 'تەواوە بۆ دەڵاڵە تاکەکەسییەکان کە تازە دەستپێدەکەن',
    'broker_pricing.tier.normal_cta': 'پلانی ئێستا',
    'broker_pricing.tier.pro': 'پڕۆ',
    'broker_pricing.tier.pro_desc': 'بۆ دەڵاڵەکانی گەشەکردوو لەگەڵ لیستی زیاتر',
    'broker_pricing.tier.pro_cta': 'بەرزکردنەوە بۆ Pro',
    'broker_pricing.tier.enterprise': 'ئینتەرپڕایز',
    'broker_pricing.tier.enterprise_desc': 'بۆ دەڵاڵەکانی قەبارەی گەورە لەگەڵ پێداویستییە بێسنوورەکان',
    'broker_pricing.tier.enterprise_cta': 'بڕۆ بۆ Enterprise',
    'broker_pricing.feature.listings_5': '5 لیست لە مانگ',
    'broker_pricing.feature.listings_10': '10 لیست لە مانگ',
    'broker_pricing.feature.listings_15': '15 لیست لە مانگ',
    'broker_pricing.feature.basic_features': 'تایبەتمەندییە سەرەتاییەکانی خانووبەرە',
    'broker_pricing.feature.advanced_features': 'تایبەتمەندییە پێشکەوتووەکانی خانووبەرە',
    'broker_pricing.feature.premium_features': 'تایبەتمەندییە پرێمیۆمەکانی خانووبەرە',
    'broker_pricing.feature.messaging': 'پەیامەکانی کڕیار',
    'broker_pricing.feature.scheduling': 'خشتەی بینین',
    'broker_pricing.feature.video_paid': 'گەشتە ڤیدیۆییەکان ($3 بۆ هەر لیستێک)',
    'broker_pricing.feature.video_free': 'گەشتە ڤیدیۆییە بەخۆڕاییەکان لەخۆگرتووە',
    'broker_pricing.feature.analytics': 'داشبۆردی تەحلیل',
    'broker_pricing.feature.analytics_advanced': 'تەحلیلی پێشکەوتوو',
    'broker_pricing.feature.support': 'پشتگیری پێشینە',
    'broker_pricing.feature.support_247': 'پشتگیری پێشینە 24/7',
    'broker_pricing.feature.featured': 'لیستە تایبەتەکان',
    'broker_pricing.feature.extra_listings': 'هەڵبژاردەی لیستی زیادە',
    'broker_pricing.feature.extra_price': '+$10 بۆ هەر لیستێکی زیادە',
    'broker_pricing.comparison.listings': 'لیست لە مانگ',
    'broker_pricing.comparison.video': 'گەشتە ڤیدیۆییەکان',
    'broker_pricing.comparison.extra': 'لیستە زیادەکان',
    'broker_pricing.comparison.duration': 'ماوەی لیست',
    'broker_pricing.comparison.featured': 'لیستە تایبەتەکان',
    'broker_pricing.comparison.30_days': '30 ڕۆژ',
    'broker_pricing.comparison.per_listing': '$3 بۆ هەر لیستێک',
    'broker_pricing.comparison.free': 'بەخۆڕایی',
    'broker_pricing.comparison.each': '$10 هەر یەکێک',
    'broker_pricing.comparison.15_plus': '15 + زیادە',
    'broker_pricing.faq.autorenew_q': 'بەشداریکردنەکەم خۆکارانە نوێ دەبێتەوە؟',
    'broker_pricing.faq.autorenew_a': 'بەڵێ! بەشداریکردنەکەت خۆکارانە هەموو مانگێک نوێ دەبێتەوە بۆ ئەوەی هەرگیز دەستت لە لیستەکانت نەچێت. لە هەمان ڕۆژ هەموو مانگێک پارەت لێ دەگیرێت. دەتوانیت هەر کات لە پۆرتاڵی بەڕێوەبردنی بەشداری هەڵبوەشێنیتەوە.',
    'broker_pricing.faq.upgrade_q': 'دەتوانم هەر کات بەرزبکەمەوە یان دابەزێنم؟',
    'broker_pricing.faq.upgrade_a': 'بەدڵنیاییەوە! بەرزکردنەوەکان دەستبەجێ کاردەکەن لەگەڵ پسووڵەی ڕێژەیی. دابەزاندنەکان لە کۆتایی ماوەی پسووڵەکەت کاردەکەن - هەموو تایبەتمەندییەکان تا ئەو کاتە دەپارێزیت. کرتە لەسەر "بەڕێوەبردنی بەشداری" بکە بۆ گۆڕینکاری.',
    'broker_pricing.faq.expire_q': 'چی ڕوودەدات کاتێک لیستەکانم تەواو دەبن؟',
    'broker_pricing.faq.expire_a': 'لیستەکان دوای 30 ڕۆژ تەواو دەبن. 3 ڕۆژ پێش تەواوبوون ئاگاداریی ئیمەیڵ وەردەگریت. دەتوانیت بە بەکارهێنانی شوێنە بەخۆڕاییەکانی مانگانەت نوێیان بکەیتەوە یان $10 بۆ هەر لیستێکی زیادە بدەیت (تەنها Enterprise).',
    'broker_pricing.faq.extra_q': '"لیستە زیادەکان" چین؟',
    'broker_pricing.faq.extra_a': 'ئەندامانی پلانی Enterprise دەتوانن لیستی زیادە بە $10 هەر یەکێک بکڕن سەرەڕای 15 لیستە بەخۆڕاییەکانی مانگانە. ئەمە زۆر باشە بۆ مانگە قەبارە گەورەکان.',
    'broker_pricing.faq.cancel_q': 'چۆن بەشداریکردنەکەم هەڵدەوەشێنمەوە؟',
    'broker_pricing.faq.cancel_a': 'کرتە لەسەر "بەڕێوەبردنی بەشداری" بکە بۆ دەستڕاگەیشتن بە پۆرتاڵی پسووڵەکردن کە دەتوانیت هەڵبوەشێنیتەوە، دابەزێنیت یان شێوازی پارەدان نوێ بکەیت. هەڵوەشاندنەوەکان لە کۆتایی ماوەی پسووڵەکردن کاردەکەن.',
    'messaging.conversation_pinned': 'گفتوگۆ سنجاندرا',
    'messaging.conversation_unpinned': 'گفتوگۆ لە سنجان لابرا',
    'messaging.pin_chat': 'گفتوگۆ سنجاندن',
    'messaging.unpin_chat': 'گفتوگۆ لە سنجان لابردن',
    'messaging.pin_failed': 'سنجاندنی گفتوگۆ سەرکەوتوو نەبوو',
    'messaging.unpin_failed': 'لابردنی سنجان سەرکەوتوو نەبوو',
    'messaging.archive': 'ئەرشیف بکە',
    'messaging.unarchive': 'گەڕانەوە',
    'messaging.show_archived': 'ئەرشیفکراوەکان',
    'messaging.show_active': 'چالاکەکان نیشان بدە',
    'messaging.conversation_archived': 'گفتوگۆ ئەرشیفکرا',
    'messaging.conversation_unarchived': 'گفتوگۆ گەڕێندرایەوە',
    'messaging.archive_failed': 'ئەرشیفکردن سەرکەوتوو نەبوو',
    'messaging.unarchive_failed': 'گەڕاندنەوە سەرکەوتوو نەبوو',
  },
  fa: {
    // Header
    'nav.search': 'جستجوی املاک',
    'nav.search_agents': 'جستجوی مشاوران',
    'nav.sell': 'فروش ملک',
    'nav.news': 'اخبار',
    'nav.build': 'ساخت خانه',
    'nav.agencies': 'آژانس‌ها',
    'nav.buy': 'خرید',
    'nav.valuation': 'ارزیابی',
    'nav.more': 'بیشتر',
    'nav.saved': 'ذخیره شده',
    'nav.admin': 'مدیر',
    'nav.agency': 'آژانس',
    'nav.agency_dashboard': 'داشبورد آژانس',
    'nav.location_manager': 'مدیر موقعیت',
    'nav.map_configuration': 'تنظیمات نقشه',
    'nav.hello': 'سلام',
    'nav.menu': 'منو',
    'nav.manage_news': 'مدیریت اخبار',
    'nav.all_properties': 'همه املاک',
    'nav.new_production': 'پروژه‌های جدید',
    
    // Search Section
    'search.for_sale': 'برای فروش',
    'search.for_rent': 'برای اجاره',
    'search.sold_prices': 'قیمت‌های فروخته شده',
    'search.search_agents': 'جستجوی مشاوران',
    'search.area_label': 'منطقه',
    'search.area_placeholder': 'منطقه یا آدرس را تایپ کنید',
    'search.area_examples': 'مثال: تهران، اصفهان، شیراز، مشهد، تبریز',
    'search.expand_area': 'گسترش منطقه با',
    'search.property_type': 'نوع ملک',
    'search.show_filters': 'نمایش فیلترهای جستجو',
    'search.find_properties': 'یافتن املاک برای فروش',
    'search.min_rooms': 'حداقل اتاق',
    'search.min_area': 'حداقل متراژ',
    'search.max_price': 'حداکثر قیمت',
    'search.all': 'همه',
    'search.no_max': 'بدون حداکثر',
    'search.new_construction': 'ساخت جدید',
    'search.show_new_construction': 'نمایش ساخت جدید',
    'search.only_new_construction': 'فقط ساخت جدید',
    'search.exclude_new_construction': 'بدون ساخت جدید',
    'search.keywords': 'کلمات کلیدی',
    'search.keywords_placeholder': 'استخر، شومینه و غیره',
    'search.more_filters_note': 'فیلترهای بیشتر در نتایج جستجو موجود است.',
    'search.recent_searches': 'جستجوهای اخیر',
    'search.hide_history': 'مخفی کردن تاریخچه',
    'search.clear_history': 'پاک کردن تاریخچه',
    'search.recent_erbil': 'املاک، اربیل',
    
    // Property Types
    'property.all_types': 'همه انواع',
    'property.villas': 'ویلاها',
    'property.townhouses': 'خانه‌های شهری',
    'property.apartments': 'آپارتمان‌ها',
    'property.condominiums': 'کاندومینیوم‌ها',
    'property.business': 'ملک تجاری',
    'property.land': 'زمین',
    'property.farms': 'مزارع/جنگل‌ها',
    'property.other': 'سایر',
    
    // Cities/Governorates
    'city.baghdad': 'بغداد',
    'city.basra': 'بصره',
    'city.erbil': 'اربیل',
    'city.sulaymaniyah': 'سلیمانیه',
    'city.najaf': 'نجف',
    'city.karbala': 'کربلا',
    'city.mosul': 'موصل',
    'city.kirkuk': 'کرکوک',
    'city.anbar': 'انبار',
    'city.dhi_qar': 'ذی قار',
    'city.maysan': 'میسان',
    'city.wasit': 'واسط',
    'city.babil': 'بابل',
    'city.qadisiyyah': 'قادسیه',
    'city.muthanna': 'مثنی',
    'city.diyala': 'دیاله',
    'city.saladin': 'صلاح الدین',
    'city.nineveh': 'نینوا',
    'city.dohuk': 'دهوک',
    
    // Auth extended
    'auth.first_name': 'نام',
    'auth.last_name': 'نام خانوادگی',
    'auth.first_name_placeholder': 'نام خود را وارد کنید',
    'auth.last_name_placeholder': 'نام خانوادگی خود را وارد کنید',
    'auth.display_name': 'نام نمایشی',
    'auth.display_name_placeholder': 'یک نام نمایشی انتخاب کنید',
    'auth.display_name_hint': 'این تنها نامی است که دیگران می‌بینند',
    'auth.date_of_birth': 'تاریخ تولد',
    'auth.fill_all_fields': 'لطفاً تمام فیلدهای الزامی را پر کنید',
    'auth.must_be_18': 'برای ثبت‌نام باید حداقل ۱۸ سال داشته باشید',
    'auth.must_be_18_hint': 'باید حداقل ۱۸ سال داشته باشید',
    
    // Identity Verification
    'verification.verify_identity': 'تأیید هویت',
    'verification.verified': 'تأیید شده',
    'verification.title': 'تأیید هویت',
    'verification.description': 'یک مدرک دولتی آپلود کنید تا هویت شما تأیید شود و نشان تأیید شده دریافت کنید.',
    'verification.document_type': 'نوع مدرک',
    'verification.doc_id_card': 'کارت شناسایی',
    'verification.doc_passport': 'گذرنامه',
    'verification.doc_drivers_license': 'گواهینامه رانندگی',
    'verification.upload_document': 'آپلود مدرک',
    'verification.click_to_upload': 'برای آپلود کلیک کنید یا بکشید و رها کنید',
    'verification.file_requirements': 'JPG، PNG، WEBP یا PDF (حداکثر ۱۰ مگابایت)',
    'verification.submit': 'ارسال برای تأیید',
    'verification.uploading': 'در حال آپلود...',
    'verification.submitted': 'درخواست تأیید ارسال شد',
    'verification.submitted_desc': 'درخواست تأیید شما ارسال شده است. به زودی آن را بررسی خواهیم کرد.',
    'verification.pending_message': 'تأیید شما در حال بررسی است. معمولاً ۱-۲ روز کاری طول می‌کشد.',
    'verification.rejected_message': 'تأیید قبلی شما رد شد. لطفاً مدرک جدیدی ارسال کنید.',
    'verification.status_approved': 'تأیید شده',
    'verification.status_pending': 'در انتظار',
    'verification.status_rejected': 'رد شده',
    'verification.invalid_file_type': 'نوع فایل نامعتبر',
    'verification.allowed_types': 'لطفاً فایل JPG، PNG، WEBP یا PDF آپلود کنید',
    'verification.file_too_large': 'فایل خیلی بزرگ است',
    'verification.max_size': 'حداکثر حجم فایل ۱۰ مگابایت است',
    'verification.error': 'خطای تأیید',
    'verification.admin_title': 'تأییدهای هویت',
    'verification.admin_description': 'بررسی و تأیید درخواست‌های تأیید هویت',
    'verification.no_requests': 'درخواست تأییدی وجود ندارد',
    'verification.view': 'بررسی',
    'verification.review_title': 'بررسی تأیید',
    'verification.review_notes': 'یادداشت‌های بررسی',
    'verification.review_notes_placeholder': 'یادداشتی درباره تصمیم خود اضافه کنید (اختیاری)',
    'verification.approve': 'تأیید',
    'verification.reject': 'رد',
    'verification.approved': 'تأیید هویت پذیرفته شد',
    'verification.rejected': 'تأیید هویت رد شد',
    'verification.review_complete': 'تأیید پردازش شده است.',

    // Common
    'common.price': 'قیمت',
    'common.rooms': 'اتاق‌ها',
    'common.size': 'اندازه',
    'common.governorate': 'استان',
    'common.select_governorate': 'انتخاب استان',
    'common.cancel': 'لغو',
    'common.save': 'ذخیره',
    'common.saving': 'در حال ذخیره...',
    'common.send': 'ارسال',
    'common.sending': 'در حال ارسال...',
    'common.submit': 'ارسال',
    'common.close': 'بستن',
    'common.error': 'خطا',
    'common.success': 'موفقیت',
    'common.loading': 'در حال بارگذاری...',
    'common.name': 'نام',
    'common.email': 'ایمیل',
    'common.phone': 'تلفن',
    'common.message': 'پیام',
    'common.required': 'الزامی',
    'common.optional': 'اختیاری',
    'common.delete': 'حذف',
    'common.free': 'رایگان',
    
    // Leaderboard
    'leaderboard': 'جدول رده‌بندی',
    'leaderboardDescription': 'آژانس‌ها و مشاوران برتر را بر اساس بازدیدها و فروش کشف کنید',
    'topAgencies': 'آژانس‌های برتر',
    'topBrokers': 'مشاوران برتر',
    'topAgenciesDescription': 'آژانس‌ها بر اساس بازدید املاک و عملکرد فروش رتبه‌بندی شده‌اند',
    'topBrokersDescription': 'مشاوران بر اساس بازدید املاک و عملکرد فروش رتبه‌بندی شده‌اند',
    'allRegions': 'همه مناطق',
    'noAgenciesFound': 'آژانسی پیدا نشد',
    'noBrokersFound': 'مشاوری پیدا نشد',
    'brokers': 'مشاوران',
    'sold': 'فروخته شده',
    'active': 'فعال',
    'views': 'بازدیدها',
    
    // Dialogs - Contact Agent
    'dialog.contact_agent': 'تماس با مشاور',
    'dialog.contact_agent_btn': 'تماس با مشاور',
    'dialog.contact_message_placeholder': 'سلام! من به ملک واقع در {address} علاقه‌مندم و می‌خواهم بیشتر بدانم. لطفاً وقتی فرصت داشتید با من تماس بگیرید.',
    'dialog.contact_sending': 'در حال ارسال...',
    'dialog.contact_send': 'ارسال پیام',
    'dialog.contact_note': 'درخواست شما مستقیماً به مشاور مسئول ارسال می‌شود که با شما تماس خواهد گرفت.',
    
    // Dialogs - Schedule Viewing
    'dialog.schedule_viewing': 'رزرو بازدید',
    'dialog.schedule_viewing_btn': 'رزرو بازدید',
    'dialog.preferred_time': 'زمان بازدید ترجیحی',
    'dialog.preferred_time_note': 'تاریخ و ساعت بازدید را انتخاب کنید. مشاور در دسترس بودن را تأیید خواهد کرد.',
    'dialog.special_requests': 'درخواست‌های خاص',
    'dialog.special_requests_placeholder': 'هر درخواست خاص یا سؤالی...',
    'dialog.schedule_booking': 'در حال رزرو...',
    'dialog.schedule_book': 'رزرو بازدید',
    'dialog.schedule_note': 'درخواست شما به مشاور ارسال می‌شود که زمان را از طریق ایمیل تأیید خواهد کرد.',
    
    // Dialogs - Save Search
    'dialog.save_search': 'ذخیره جستجو',
    'dialog.save_search_btn': 'ذخیره جستجو',
    'dialog.search_name': 'نام جستجو',
    'dialog.search_name_placeholder': 'مثال: آپارتمان در تهران',
    'dialog.city_municipality': 'شهر/شهرداری',
    'dialog.min_rooms': 'حداقل اتاق',
    'dialog.min_size': 'حداقل متراژ (م²)',
    'dialog.property_type': 'نوع ملک',
    'dialog.email_alerts': 'اعلان‌های ایمیلی هنگام مطابقت املاک جدید',
    'dialog.saving_search': 'در حال ذخیره...',
    
    // Dialogs - Mark as Sold
    'dialog.mark_sold': 'علامت‌گذاری به عنوان فروخته شده',
    'dialog.mark_sold_description': 'قیمت فروش نهایی این ملک را وارد کنید. این کار آگهی را به املاک فروخته شده منتقل می‌کند.',
    'dialog.final_sale_price': 'قیمت فروش نهایی (دلار)',
    'dialog.listed_price': 'قیمت لیست شده',
    'dialog.property_details': 'جزئیات ملک',
    'dialog.enter_valid_price': 'لطفاً یک قیمت فروش معتبر وارد کنید',
    'dialog.enter_price_placeholder': 'قیمت فروش را وارد کنید',
    'dialog.marked_sold_success': 'ملک با موفقیت به عنوان فروخته شده علامت‌گذاری شد',
    'dialog.mark_sold_error': 'علامت‌گذاری ملک به عنوان فروخته شده ناموفق بود',
    
    // Dialogs - Add Property
    'dialog.add_property': 'افزودن ملک جدید',
    'dialog.hide_preview': 'مخفی کردن پیش‌نمایش',
    'dialog.show_preview': 'نمایش پیش‌نمایش',
    'dialog.selected_images': 'تصاویر انتخاب شده',
    'dialog.uploaded_images': 'تصاویر آپلود شده',
    'dialog.click_primary': 'برای تنظیم به عنوان تصویر اصلی روی تصویر کلیک کنید',
    'dialog.invalid_format': 'فرمت فایل نامعتبر',
    'dialog.file_too_large': 'فایل خیلی بزرگ است',
    'dialog.upload_failed': 'آپلود ناموفق',
    'dialog.images_required': 'تصاویر الزامی است',
    'dialog.please_upload': 'لطفاً حداقل یک تصویر برای ملک آپلود کنید',
    'dialog.form_validation_failed': 'اعتبارسنجی فرم ناموفق بود',
    
    // Rental properties
    'rental.for_sale': 'فروشی',
    'rental.for_rent': 'اجاره‌ای',
    'rental.listing_type': 'نوع آگهی',
    'rental.rental_details': 'جزئیات اجاره',
    'rental.rental_period': 'دوره اجاره',
    'rental.rental_period_hint': 'اجاره چند وقت یکبار پرداخت می‌شود',
    'rental.daily': 'روزانه',
    'rental.weekly': 'هفتگی',
    'rental.monthly': 'ماهانه',
    'rental.yearly': 'سالانه',
    'rental.per_daily': '/روز',
    'rental.per_weekly': '/هفته',
    'rental.per_monthly': '/ماه',
    'rental.per_yearly': '/سال',
    'rental.deposit_amount': 'مبلغ ودیعه (دلار)',
    'rental.minimum_lease': 'حداقل مدت اجاره (ماه)',
    'rental.months': 'ماه',
    'rental.available_from': 'موجود از',
    'rental.furnished': 'مبله',
    'rental.utilities_included': 'هزینه‌های جانبی شامل',
    'rental.pets_allowed': 'حیوان خانگی مجاز',
    
    // Hero/News Section
    'hero.find_dream_home': 'خانه رویایی خود را در عراق پیدا کنید',
    'hero.explore_description': 'هزاران خانه برای فروش و قیمت‌های نهایی از سراسر عراق را کشف کنید. ارزیابی دریافت کنید، اخبار املاک بخوانید و با مشاوران تماس بگیرید.',
    'hero.badge': 'پلتفرم برتر املاک عراق',
    
    // Home Features
    'home.feature.properties': 'هزاران ملک',
    'home.feature.properties_desc': 'بزرگترین مجموعه خانه‌ها، آپارتمان‌ها و زمین‌ها را در سراسر عراق مرور کنید.',
    'home.feature.market': 'تخصص محلی',
    'home.feature.market_desc': 'دانش عمیق از هر استان و محله در عراق.',
    'home.feature.trusted': 'مشاوران تأیید شده',
    'home.feature.trusted_desc': 'با متخصصان املاک مورد اعتماد و تأیید شده ارتباط برقرار کنید.',
    'home.why_choose': 'چرا عقارنت را انتخاب کنید',
    'home.why_choose_desc': 'همه چیز برای یافتن ملک ایده‌آل شما',
    
    // CTA Section
    'cta.ready': 'آماده‌اید خانه‌تان را پیدا کنید؟',
    'cta.desc': 'هزاران ملک در سراسر عراق مرور کنید',
    'cta.explore': 'کشف املاک',
    // Property Listings
    'listings.latest_for_sale': 'جدیدترین املاک برای فروش',
    'listings.properties_found': 'ملک یافت شد',
    'listings.all_news': 'همه اخبار',
    'listings.add_property': 'افزودن ملک',
    'listings.edit': 'ویرایش',
    'listings.edit_property': 'ویرایش ملک',
    'listings.monthly_fee': 'هزینه ماهانه',
    'listings.open_floor_plan': 'پلان باز',
    'listings.large_balcony': 'بالکن بزرگ',
    'listings.family_friendly': 'مناسب خانواده',
    'listings.photos': 'عکس',
    'listings.per_month': 'ماه',
    'listings.delete_confirm': 'آیا مطمئن هستید که می‌خواهید این ملک را حذف کنید؟',
    
    // Forms
    'form.price': 'قیمت',
    'form.priceUSD': 'قیمت (دلار)',
    'form.priceIQD': 'قیمت (دینار عراقی)',
    'form.priceConversionNote': 'قیمت را به هر ارزی وارد کنید - دیگری به‌صورت خودکار محاسبه می‌شود (نرخ: 1 دلار ≈ 1,310 دینار)',
    'form.price_usd': 'قیمت (دلار)',
    'form.price_placeholder': 'قیمت را به دلار آمریکا وارد کنید',
    'form.price_description': 'قیمت را به دلار آمریکا وارد کنید. هم به دلار و هم به دینار عراق نمایش داده می‌شود.',
    'form.address': 'آدرس',
    'form.city': 'شهر',
    'form.rooms': 'اتاق‌ها',
    'form.bathrooms': 'حمام‌ها',
    'form.size': 'متراژ (متر مربع)',
    'form.building_size': 'متراژ ساختمان (متر مربع)',
    'form.building_size_placeholder': 'متراژ ساختمان به متر مربع',
    'form.building_size_description': 'متراژ ساختمان (boarea)',
    'form.plot_size': 'متراژ زمین (متر مربع)',
    'form.plot_size_placeholder': 'متراژ زمین به متر مربع',
    'form.plot_size_description': 'کل متراژ زمین (tomtarea)',
    'form.type': 'نوع',
    'form.propertyType': 'نوع ملک',
    'form.monthly_fee': 'هزینه ماهانه (ریال)',
    'form.monthly_fee_usd': 'هزینه ماهانه (دلار)',
    'form.monthly_fee_placeholder': 'هزینه ماهانه به دلار وارد کنید (اختیاری)',
    'form.monthly_fee_description': 'هزینه ماهانه اختیاری به دلار آمریکا',
    'form.description': 'توضیحات',
    'form.description_placeholder': 'ملک را توصیف کنید...',
    'form.payment_plan': 'برنامه پرداخت',
    'form.payment_plan_placeholder': 'مثال: 30% پیش‌پرداخت، 70% در تحویل، یا اقساط ماهانه...',
    'form.payment_plan_hint': 'اقساط پرداخت را اضافه کنید تا گزینه‌های پرداخت را به خریداران نشان دهید',
    'payment_plan.total': 'مجموع',
    'payment_plan.amount': 'مبلغ',
    'payment_plan.description': 'توضیحات',
    'payment_plan.date': 'تاریخ',
    'payment_plan.percentage': 'درصد',
    'payment_plan.add_installment': 'افزودن قسط',
    'payment_plan.hint': 'اگر برنامه پرداختی ندارید خالی بگذارید',
    'payment_plan.down_payment': 'پیش پرداخت',
    'payment_plan.first_payment': 'پرداخت اول',
    'payment_plan.second_payment': 'پرداخت دوم',
    'payment_plan.third_payment': 'پرداخت سوم',
    'payment_plan.fourth_payment': 'پرداخت چهارم',
    'payment_plan.fifth_payment': 'پرداخت پنجم',
    'payment_plan.sixth_payment': 'پرداخت ششم',
    'payment_plan.seventh_payment': 'پرداخت هفتم',
    'payment_plan.final_payment': 'پرداخت نهایی',
    'payment_plan.payment_description': 'توضیحات پرداخت',
    'payment_plan.enable': 'شامل برنامه پرداخت',
    'form.floor_plans': 'نقشه طبقات',
    'form.upload_floor_plans': 'آپلود تصاویر نقشه طبقات',
    'form.features': 'ویژگی‌ها (با کاما جدا شده)',
    'form.features_placeholder': 'مثال: استخر، باغ، گاراژ، آشپزخانه مدرن',
    'form.features_description': 'چندین ویژگی را با کاما از هم جدا کنید',
    'form.amenities': 'امکانات',
    'form.amenities_hint': 'همه امکاناتی که برای این ملک قابل اعمال است را انتخاب کنید',
    'amenities.balcony': 'بالکن',
    'amenities.electricity_generator': 'ژنراتور برق',
    'amenities.central_ac': 'کولر گازی مرکزی',
    'amenities.double_glazed_windows': 'پنجره دوجداره',
    'amenities.satellite_tv': 'تلویزیون ماهواره',
    'amenities.cctv': 'دوربین مداربسته',
    'amenities.security_staff': 'نگهبان',
    'amenities.sewerage_system': 'سیستم فاضلاب',
    'amenities.furnished_kitchen': 'آشپزخانه مبله',
    'amenities.24hr_electricity': 'برق 24 ساعته',
    'amenities.market': 'بازار نزدیک',
    'amenities.elevator': 'آسانسور',
    'amenities.school': 'مدرسه نزدیک',
    'amenities.mosque': 'مسجد نزدیک',
    'amenities.central_gas': 'گاز مرکزی',
    'amenities.maintenance': 'خدمات تعمیر',
    'amenities.cleaning': 'خدمات نظافت',
    'amenities.fire_fighting': 'تجهیزات آتش‌نشانی',
    'amenities.car_washing': 'کارواش',
    'amenities.smart_home': 'خانه هوشمند',
    'amenities.earthquake_resistant': 'مقاوم در برابر زلزله',
    'amenities.central_fire_system': 'سیستم آتش‌نشانی مرکزی',
    'amenities.hot_water_system': 'سیستم آب گرم مرکزی',
    'amenities.central_vacuum': 'جاروبرقی مرکزی',
    'amenities.fountain': 'آبنما',
    'amenities.24hr_water': 'آب 24 ساعته',
    'amenities.garbage_chute': 'شوت زباله',
    'amenities.kitchen_counter': 'کانتر آشپزخانه',
    'amenities.street_lights': 'روشنایی خیابان',
    'amenities.playground': 'زمین بازی کودکان',
    'amenities.gym': 'باشگاه ورزشی',
    'amenities.garden': 'باغچه',
    'amenities.parking': 'پارکینگ',
    'amenities.wifi': 'وای‌فای',
    'amenities.security_door': 'درب ضد سرقت',
    'amenities.split_ac': 'کولر اسپلیت',
    'amenities.bathtub': 'وان حمام',
    'amenities.refrigerator': 'یخچال',
    'amenities.washing_machine': 'ماشین لباسشویی',
    'amenities.swimming_pool': 'استخر',
    'amenities.solar_panels': 'پنل خورشیدی',
    'amenities.water_tank': 'مخزن آب',
    'amenities.water_well': 'چاه آب',
    'amenities.septic_tank': 'چاه فاضلاب',
    'amenities.central_heating': 'گرمایش مرکزی',
    'amenities.desert_cooler': 'کولر آبی',
    'amenities.intercom': 'آیفون',
    'amenities.external_camera': 'دوربین خارجی',
    'amenities.guard_room': 'اتاق نگهبان',
    'amenities.rooftop_terrace': 'تراس پشت‌بام',
    'amenities.basement': 'زیرزمین',
    'amenities.storage_room': 'انباری',
    'amenities.internal_stairs': 'پله داخلی',
    'amenities.external_stairs': 'پله خارجی',
    'amenities.corner_unit': 'واحد نبش',
    'amenities.two_streets': 'دو نبش',
    'amenities.double_entrance': 'دو ورودی',
    'amenities.marble_flooring': 'کف سنگ مرمر',
    'amenities.ceramic_tiles': 'کاشی سرامیک',
    'amenities.parquet_flooring': 'پارکت',
    'amenities.gypsum_decorations': 'گچبری',
    'amenities.false_ceiling': 'سقف کاذب',
    'amenities.steel_doors': 'درب فولادی',
    'amenities.aluminum_windows': 'پنجره آلومینیوم',
    'amenities.pvc_windows': 'پنجره PVC',
    'amenities.guest_room': 'اتاق مهمان',
    'amenities.servant_quarters': 'اتاق خدمتکار',
    'amenities.covered_parking': 'پارکینگ مسقف',
    'amenities.concrete_fence': 'حصار بتنی',
    'amenities.iron_fence': 'حصار آهنی',
    'amenities.street_pavement': 'کف‌سازی خیابان',
    'amenities.fully_furnished': 'کاملاً مبله',
    'amenities.hospital': 'بیمارستان نزدیک',
    'amenities.category.utilities': 'برق و آب',
    'amenities.category.climate': 'سرمایش و گرمایش',
    'amenities.category.security': 'امنیت',
    'amenities.category.building': 'ویژگی‌های ساختمان',
    'amenities.category.interior': 'داخلی و کف',
    'amenities.category.rooms': 'اتاق‌ها',
    'amenities.category.outdoor': 'فضای بیرون و پارکینگ',
    'amenities.category.appliances': 'لوازم خانگی',
    'amenities.category.services': 'خدمات',
    'amenities.category.nearby': 'در نزدیکی',
    'common.selected': 'انتخاب شده',
    'form.property_images': 'تصاویر ملک',
    'form.current_images': 'تصاویر فعلی',
    'form.image': 'تصویر',
    'form.primary': 'اصلی',
    'form.click_image_primary': 'برای تنظیم به عنوان تصویر اصلی روی تصویر کلیک کنید. از فلش‌ها برای تغییر ترتیب استفاده کنید.',
    'form.click_to_upload_new': 'برای آپلود تصاویر جدید کلیک کنید',
    'form.new_images_upload': 'تصاویر جدید برای آپلود',
    'form.new_image': 'تصویر جدید',
    'form.upload': 'آپلود',
    'form.new_images': 'تصاویر جدید',
    'form.location': 'موقعیت',
    'form.required': 'الزامی',
    'form.optional': 'اختیاری',
    'form.cancel': 'لغو',
    'form.save_changes': 'ذخیره تغییرات',
    'form.uploading': 'در حال آپلود...',
    'form.upload_images': 'آپلود تصاویر',
    'form.click_to_upload': 'برای آپلود تصاویر کلیک کنید',
    'form.file_formats': 'JPEG, PNG, WebP, GIF (حداکثر 10MB برای هر تصویر)',
    'form.select_type': 'انتخاب نوع',
    'form.location_on_map': 'موقعیت ملک روی نقشه',
    'form.location_hint': 'برای تنظیم موقعیت دقیق ملک روی نقشه کلیک کنید',
    'form.hide_preview': 'پنهان کردن پیش‌نمایش',
    'form.show_preview': 'نمایش پیش‌نمایش',
    'form.selected_images': 'تصاویر انتخاب شده',
    'form.click_set_primary': 'روی تصویر کلیک کنید تا به عنوان اصلی تنظیم شود',
    'form.uploaded_images': 'تصاویر آپلود شده',
    'form.images': 'تصاویر',
    'form.uploading_images': 'در حال آپلود تصاویر...',
    'form.city_placeholder': 'جستجوی شهر...',
    'form.new_construction': 'ساخت جدید (خانه آینده)',
    'form.preview': 'پیش‌نمایش',
    'form.preview_description': 'آگهی شما در سایت به این شکل نمایش داده می‌شود',
    'form.fullscreen': 'تمام صفحه',
    'form.exit_fullscreen': 'خروج از تمام صفحه',
    'form.click_preview_fullscreen': 'برای مشاهده تمام صفحه روی پیش‌نمایش کلیک کنید',
    'form.preview_contact_disabled': 'گزینه‌های تماس در حالت پیش‌نمایش غیرفعال هستند',
    'form.invalid_format': 'فرمت فایل نامعتبر',
    'form.invalid_format_desc': '{filename} فرمت نامعتبر دارد. مجاز: JPEG، PNG، WebP، GIF',
    'form.file_too_large': 'فایل خیلی بزرگ است',
    'form.file_too_large_desc': '{filename} بزرگتر از 10 مگابایت است',
    'form.upload_failed': 'آپلود ناموفق',
    'form.upload_failed_desc': 'آپلود تصاویر ممکن نبود',
    'form.images_required': 'تصاویر الزامی است',
    'form.images_required_desc': 'لطفاً حداقل یک تصویر برای ملک آپلود کنید',
    'form.sold_price_usd': 'قیمت فروش (دلار آمریکا)',
    'form.sold_price_placeholder': 'قیمت فروش واقعی را وارد کنید',
    'form.sold_price_description': 'قیمت فروش واقعی (ممکن است با قیمت درج شده متفاوت باشد)',
    'form.video_url': 'لینک ویدیوی تور',
    'form.video': 'ویدیو',
    'form.add_video': 'افزودن ویدیوی دیگر',
    'form.video_addon': '+$3 افزودنی',
    'form.video_included': 'رایگان شامل شده',
    'form.video_url_hint': 'ویدیوی تور YouTube یا Vimeo اضافه کنید. افزودنی ویدیو پس از ایجاد آگهی قابل خرید است.',
    'form.video_url_hint_free': 'ویدیوی تور YouTube یا Vimeo اضافه کنید - در اشتراک شما رایگان است!',
    'form.video_url_hint_addon': 'ویدیوی تور YouTube یا Vimeo اضافه کنید. $3 به هزینه آگهی شما اضافه می‌شود.',
    'form.include_video_tour': 'شامل کردن تور ویدیویی',
    'form.show_map': 'نمایش نقشه',
    'form.no_location': 'موقعیتی انتخاب نشده',
    
    // News Section
    'news.property_news': 'اخبار املاک',
    'news.all_news': 'همه اخبار',
    'news.click_tops': 'پربازدیدترین‌ها',
    'news.guides': 'راهنماها',
    'news.news': 'اخبار',
    'news.article_1_title': '"خانه لوندل" هزاران نفر را به خود جذب می‌کند - دوباره پربازدیدترین هفته',
    'news.article_2_title': 'خانه رویایی را با هم پیدا کنید - مجموعه‌ها را در Aqarnet ایجاد و به اشتراک بگذارید',
    'news.article_3_title': 'قلعه کوچک برای فروش: "با خانواده‌های مشهور زیادی مرتبط است"',
    
    // Loading and empty states
    'loading.properties': 'در حال بارگذاری املاک...',
    'empty.no_properties': 'در حال حاضر هیچ ملکی موجود نیست',
    'empty.check_back': 'برای مشاهده فهرست‌های جدید بعداً بررسی کنید.',
    'saved_searches.title': 'جستجوهای ذخیره شده',
    'saved_searches.empty': 'هنوز جستجوی ذخیره شده‌ای نیست',
    'saved_searches.description': 'یک جستجو را ذخیره کنید تا هنگام اضافه شدن املاک مطابق، اعلان ایمیل دریافت کنید',
    
    // Navigation
    'nav.favorites': 'علاقه‌مندی‌ها',
    'nav.profile': 'پروفایل',
    'nav.login': 'ورود',
    'nav.logout': 'خروج',
    'nav.recent_searches': 'جستجوهای اخیر',
    'nav.no_recent_searches': 'جستجوی اخیری وجود ندارد',
    
    // Footer
    'footer.contact': 'تماس',
    'footer.customer_service': 'خدمات مشتری و تماس',
    'footer.about': 'درباره Aqarnet',
    'footer.press': 'مطبوعات',
    'footer.investors': 'برای سرمایه‌گذاران',
    'footer.advertising': 'تبلیغات و بازاریابی',
    'footer.jobs': 'مشاغل با ما',
    'footer.statistics': 'آمار املاک',
    'footer.apps': 'اپلیکیشن‌ها',
    'footer.iphone_ipad': 'iPhone و iPad',
    'footer.android': 'Android',
    'footer.follow': 'دنبال کردن Aqarnet',
    'footer.newsletter': 'خبرنامه Aqarnet',
    'footer.whatsapp': 'WhatsApp',
    'footer.facebook': 'Facebook',
    'footer.instagram': 'Instagram',
    'footer.linkedin': 'LinkedIn',
    'footer.pinterest': 'Pinterest',
    'footer.tiktok': 'TikTok',
    'footer.threads': 'Threads',
    'footer.podcast': 'پادکست Aqarnet - راه Aqarnet',
    'footer.all_properties': 'همه املاک',
    'footer.privacy': 'حریم خصوصی',
    'footer.cookies': 'کوکی‌ها',
    'footer.terms': 'شرایط و اطلاعات حقوقی',
    'footer.cookie_settings': 'تنظیمات کوکی',
    
    'agents.title': 'پیدا کردن مشاور',
    'agents.subtitle': 'فکر فروش می‌کنید؟ ما به شما کمک می‌کنیم مشاوران متخصص منطقه‌تان را پیدا کنید!',
    'agents.area_search': 'جستجوی منطقه',
    'agents.name_search': 'جستجوی نام',
    'agents.search_placeholder': 'منطقه یا آدرس را وارد کنید',
    'agents.show': 'نمایش',
    'agents.sort_by': 'مرتب‌سازی بر اساس',
    'agents.agents_count': 'مشاوران',
    'agents.companies_label': 'شرکت‌های مشاورین املاک',
    'agents.recent_sales': 'آخرین تاریخ فروش',
    'agents.most_sales': 'بیشترین فروش',
    'agents.alphabetical': 'ترتیب الفبایی',
    'agents.showing_results': 'نمایش',
    'agents.agents_label': 'مشاوران',
    'agents.loading': 'در حال بارگذاری مشاوران...',
    'agents.no_results': 'هیچ مشاوری یافت نشد.',
    'agents.phone': 'تلفن:',
    'agents.status': 'وضعیت:',
    'agents.active_agent': 'مشاور فعال',
    'agents.anonymous_agent': 'مشاور ناشناس',
    'agents.agent_label': 'مشاور',
    'agents.show_more': 'نمایش مشاوران بیشتر',

    // Properties for Sale Page
    'propertiesForSale.title': 'املاک برای فروش در ایران',
    'propertiesForSale.statistics.municipalities': 'شهرداری‌ها',
    'propertiesForSale.statistics.forSale': 'برای فروش',
    'propertiesForSale.statistics.sold': 'فروخته شده این هفته',
    'propertiesForSale.map.fullscreen': 'نقشه / تمام صفحه',
    'propertiesForSale.map.area': 'منطقه',
    'propertiesForSale.filters.searchWithin': 'جستجو در',
    'propertiesForSale.filters.km': 'کیلومتر',
    'propertiesForSale.filters.propertyType': 'نوع ملک',
    'propertiesForSale.filters.allTypes': 'همه انواع',
    'propertiesForSale.filters.villa': 'ویلا',
    'propertiesForSale.filters.apartment': 'آپارتمان',
    'propertiesForSale.filters.townhouse': 'خانه شهری',
    'propertiesForSale.filters.plot': 'زمین',
    'propertiesForSale.filters.commercial': 'تجاری',
    'propertiesForSale.filters.other': 'سایر',
    'propertiesForSale.filters.rooms': 'اتاق‌ها',
    'propertiesForSale.filters.bathrooms': 'حمام‌ها',
    'propertiesForSale.filters.min': 'حداقل',
    'propertiesForSale.filters.max': 'حداکثر',
    'propertiesForSale.filters.size': 'اندازه',
    'propertiesForSale.filters.price': 'قیمت',
    'propertiesForSale.filters.features': 'ویژگی‌ها',
    'propertiesForSale.filters.balcony': 'بالکن/تراس/پشت‌بام',
    'propertiesForSale.filters.pets': 'حیوانات خانگی',
    'propertiesForSale.filters.newBuild': 'ساخت جدید',
    'propertiesForSale.filters.showAll': 'نمایش همه',
    'propertiesForSale.filters.buildYear': 'سال ساخت',
    'propertiesForSale.filters.searchResults': 'نتایج جستجو',
    'propertiesForSale.filters.searchPlaceholder': 'جستجو در مناطق، شهرها و...',
    'propertiesForSale.filters.searchingFor': 'جستجو برای:',
    'propertiesForSale.filters.any': 'همه',
    'propertiesForSale.filters.keywords': 'کلمات کلیدی',
    'propertiesForSale.filters.showResults': 'نمایش نتایج',
    'propertiesForSale.filters.clearFilters': 'پاک کردن',
    'propertiesForSale.filters.realEstateSituation': 'وضعیت ملک',
    'propertiesForSale.filters.situation.tapu': 'تاپو',
    'propertiesForSale.filters.situation.landContract': 'قرارداد زمین',
    'propertiesForSale.filters.situation.noDocuments': 'بدون مدرک',
    'propertiesForSale.filters.situation.investment': 'سرمایه‌گذاری',
    'propertiesForSale.filters.situation.musataha': 'مساطحه',
    'propertiesForSale.filters.situation.card': 'کارت',
    'propertiesForSale.filters.situation.agriculturalTapu': 'تاپوی کشاورزی',
    'propertiesForSale.sort.newest': 'جدیدترین اول',
    'propertiesForSale.sort.price_low': 'قیمت: کم به زیاد',
    'propertiesForSale.sort.price_high': 'قیمت: زیاد به کم',
    'propertiesForSale.sort.size': 'اندازه',
    'propertiesForSale.noResults': 'هیچ ملکی پیدا نشد',
    'propertiesForSale.tryAdjustingFilters': 'فیلترها را تنظیم کنید یا در منطقه دیگری جستجو کنید',

    // Property Types
    'propertyTypes.allTypes': 'همه انواع',
    'propertyTypes.villa': 'ویلاها',
    'propertyTypes.apartment': 'آپارتمان‌ها',
    'propertyTypes.townhouse': 'خانه‌های شهری',
    'propertyTypes.business': 'ملک تجاری',
    'propertyTypes.plot': 'زمین‌ها',
    'propertyTypes.farm': 'مزارع/جنگل‌ها',
    'propertyTypes.other': 'سایر',

    // Cookie Policy
    'cookie.title': 'سیاست کوکی',
    'cookie.last_updated': 'آخرین به‌روزرسانی:',
    'cookie.section1.title': 'بخش ۱ - مقررات عمومی در مورد اجرای فناوری‌های ردیابی',
    'cookie.section1.p1': 'شرکت Ploqs AB که در اینجا به عنوان "ارائه‌دهنده خدمات" نامیده می‌شود، همراه با شرکا و پیمانکاران فرعی که توسط ارائه‌دهنده خدمات استخدام شده‌اند، اشکال مختلفی از مکانیزم‌های ردیابی دیجیتال را پیاده‌سازی و استفاده می‌کند.',
    'cookie.section1.p2': 'فناوری‌های ردیابی عمدتاً از فایل‌های داده متنی کوچک تشکیل شده‌اند که بر روی تجهیزات پایانی کاربر یا برنامه مرورگر وب قرار می‌گیرند.',
    'cookie.section1.h1': 'پردازش داده‌های شخصی و ملاحظات حفاظت از داده‌ها',
    'cookie.section1.p3': 'اطلاعات جمع‌آوری شده از طریق اعمال فناوری‌های ردیابی در بیشتر موارد داده‌هایی هستند که می‌توانند مستقیم یا غیرمستقیم برای شناسایی افراد استفاده شوند.',
    'cookie.section1.h2': 'انتخاب‌های کاربر',
    'cookie.section1.p4': 'کاربر حق دارد در هر زمان رضایت‌های قبلی داده شده را با مراجعه به گزینه‌های مدیریت رضایت تجدید نظر کند.',
    'cookie.section1.h3': 'ماندگاری و دوره‌های ذخیره‌سازی فناوری‌های ردیابی',
    'cookie.section1.p5': 'فناوری‌های ردیابی بسته به هدف و عملکرد هر فناوری ماندگاری متفاوتی نشان می‌دهند.',
    'cookie.section2.title': 'بخش ۲ - فناوری‌های ردیابی اجباری برای ارائه خدمات',
    'cookie.section2.p1': 'فناوری‌های ردیابی اجباری به پیاده‌سازی‌های فناوری اشاره دارند که برای ارائه‌دهنده خدمات برای ارائه عملکرد اساسی و ضروری به کاربر الزامی هستند.',
    'cookie.section2.legal': 'مبنای قانونی برای پردازش:',
    'cookie.section2.legal_value': 'منافع مشروع مطابق با ماده ۶.۱(f) GDPR',
    'cookie.section2.storage': 'دوره ذخیره‌سازی:',
    'cookie.section2.storage_value': 'تا بیست و چهار (۲۴) ماه برای فناوری‌های ردیابی پایدار؛ فناوری‌های ردیابی مبتنی بر جلسه بلافاصله پس از بستن مرورگر خاتمه می‌یابند.',
    'cookie.section3.title': 'بخش ۳ - فناوری‌های ردیابی برای اهداف تحلیلی',
    'cookie.section3.p1': 'ارائه‌دهنده خدمات اهمیت قابل توجهی به توانایی تعیین فراوانی بازدید و ثبت داده‌های تعامل در پلتفرم دیجیتال می‌دهد.',
    'cookie.section3.legal_value': 'رضایت مطابق با ماده ۶.۱(a) GDPR',
    'cookie.section3.storage_value': 'تا بیست و شش (۲۶) ماه',
    'cookie.section4.title': 'بخش ۴ - فناوری‌های ردیابی برای اهداف شخصی‌سازی',
    'cookie.section4.p1': 'فناوری‌های ردیابی که برای اهداف شخصی‌سازی اجرا می‌شوند، هدفشان تطبیق تجربه کاربر در پلتفرم‌های دیجیتال ارائه‌دهنده خدمات با ارائه محتوای با ارتباط شخصی افزایش یافته است.',
    'cookie.section5.title': 'بخش ۵ - فناوری‌های ردیابی برای اهداف بازاریابی',
    'cookie.section5.p1': 'ارائه‌دهنده خدمات فناوری‌های ردیابی را برای اهداف بازاریابی برای فعال کردن نمایش محتوای تبلیغاتی مرتبط اجرا می‌کند.',
    'cookie.section5.p2': 'با اجرای فناوری‌های ردیابی، ارائه‌دهنده خدمات همچنین اطمینان حاصل می‌کند که نمایش تبلیغات به تعداد از پیش تعیین شده برای هر کاربر محدود می‌شود.',
    'cookie.section5.p3': 'باید توجه داشت که در صورتی که کاربر انتخاب کند فناوری‌های ردیابی را برای اهداف بازاریابی غیرفعال کند، محتوای تبلیغاتی همچنان به کاربر نمایش داده می‌شود اما بدون شخصی‌سازی.',
    'cookie.section5.storage_value': 'تا بیست و چهار (۲۴) ماه',
    'cookie.section6.title': 'بخش ۶ - انتقال داده‌های بین‌المللی به کشورهای ثالث',
    'cookie.section6.p1': 'در ابزار مدیریت رضایت ارائه‌دهنده خدمات، اطلاعات در مورد اینکه کدام فناوری‌های ردیابی و شرکا داده‌های شخصی کاربر را پردازش می‌کنند ارائه می‌شود.',
    'cookie.section7.title': 'بخش ۷ - دستورالعمل‌ها برای حذف فناوری‌های ردیابی',
    'cookie.section7.p1': 'کاربر می‌تواند فناوری‌های ردیابی را از تجهیزات پایانی یا دستگاه موبایل خود حذف کند و تنظیمات ذخیره‌سازی داده‌های محلی را از طریق رابط تنظیمات مرورگر پیکربندی کند.',
    'cookie.section7.p2': 'برای دستورالعمل‌های دقیق در مورد مدیریت و حذف فناوری‌های ردیابی، به گزینه راهنما در مرورگر خود یا لینک‌های زیر مراجعه کنید:',
    
    // Auth
    'auth.loading': 'در حال بارگذاری...',
    'auth.login': 'ورود',
    'auth.create_account': 'ایجاد حساب',
    'auth.login_subtitle': 'با انتخاب یکی از گزینه‌های زیر وارد شوید.',
    'auth.signup_subtitle': 'یک حساب جدید ایجاد کنید تا شروع کنید.',
    'auth.continue_apple': 'ادامه با Apple',
    'auth.continue_google': 'ادامه با Google',
    'auth.or': 'یا',
    'auth.email': 'ایمیل',
    'auth.email_placeholder': 'ایمیل را وارد کنید',
    'auth.password': 'رمز عبور',
    'auth.password_placeholder': 'رمز عبور را وارد کنید',
    'auth.account_type': 'نوع حساب',
    'auth.user_role': 'کاربر - می‌خواهم ملک بخرم/اجاره کنم',
    'auth.agent_role': 'مشاور - می‌خواهم ملک بفروشم/اجاره دهم',
    'auth.continue': 'ادامه',
    'auth.no_account': 'حساب ندارید؟ ایجاد حساب',
    'auth.have_account': 'حساب دارید؟ ورود',
    'auth.login_failed': 'ورود ناموفق',
    'auth.invalid_credentials': 'ایمیل یا رمز عبور نامعتبر',
    'auth.welcome_back': 'خوش آمدید!',
    'auth.logged_in': 'وارد شدید.',
    'auth.signup_failed': 'ثبت‌نام ناموفق',
    'auth.account_created': 'حساب ایجاد شد!',
    'auth.check_email': 'ایمیل خود را برای تأیید حساب بررسی کنید.',
    'auth.error': 'خطایی رخ داد',
    'auth.try_again': 'لطفاً بعداً دوباره امتحان کنید.',
    'auth.broker_interest_title': 'آیا شما کارگزار هستید؟',
    'auth.broker_interest_description': 'اگر علاقه‌مند به ثبت املاک در عقارنت هستید، درخواست خود را برای ما ارسال کنید تا با شما تماس بگیریم.',
    'auth.broker_contact_us': 'تماس با ما',
    'auth.broker_email_subject': 'درخواست ثبت‌نام کارگزار',
    'auth.broker_email_body': 'سلام،\n\nمن علاقه‌مند به ثبت‌نام به عنوان کارگزار در عقارنت هستم.\n\nنام:\nنام آژانس:\nتلفن:\nشهر:\n\nمتشکرم!',
    
    // Favorites Page
    'favorites.title': 'علاقه‌مندی‌های من',
    'favorites.saved_properties': 'املاک ذخیره شده',
    'favorites.login_required': 'برای مشاهده علاقه‌مندی‌ها وارد شوید',
    'favorites.login_description': 'املاکی که دوست دارید را ذخیره کنید تا راحت‌تر پیدا کنید.',
    'favorites.loading': 'بارگذاری علاقه‌مندی‌ها...',
    'favorites.no_favorites': 'هنوز علاقه‌مندی ندارید',
    'favorites.start_saving': 'با کلیک روی دکمه قلب در آگهی‌ها، ذخیره کردن املاک را شروع کنید.',
    'favorites.search_properties': 'جستجوی املاک',
    
    // Profile Page
    'profile.title': 'پروفایل من',
    'profile.subtitle': 'مدیریت اطلاعات شخصی و تنظیمات',
    'profile.loading': 'بارگذاری پروفایل...',
    'profile.fetch_error': 'خطا در دریافت پروفایل',
    'profile.fetch_error_description': 'نتوانستیم اطلاعات پروفایل را دریافت کنیم.',
    'profile.unnamed_user': 'کاربر بی‌نام',
    'profile.personal_info': 'اطلاعات شخصی',
    'profile.update_info': 'به‌روزرسانی اطلاعات شخصی',
    'profile.email': 'ایمیل',
    'profile.email_readonly': 'ایمیل قابل تغییر نیست',
    'profile.display_name': 'نام نمایشی',
    'profile.display_name_placeholder': 'نام شما',
    'profile.phone': 'شماره تلفن',
    'profile.phone_placeholder': '۰۹۱۲-۱۲۳۴۵۶۷',
    'profile.save_changes': 'ذخیره تغییرات',
    'profile.saving': 'در حال ذخیره...',
    'profile.reset': 'بازنشانی',
    'profile.updated': 'پروفایل به‌روز شد',
    'profile.updated_description': 'پروفایل شما ذخیره شد.',
    'profile.update_error': 'خطا در به‌روزرسانی',
    'profile.update_error_description': 'نتوانستیم تغییرات را ذخیره کنیم.',
    'profile.active_listings': 'آگهی‌های فعال',
    'profile.active_listings_description': 'آگهی‌های منتشر شده شما برای فروش',
    'profile.view_all': 'مشاهده همه',
    'profile.loading_listings': 'بارگذاری آگهی‌ها...',
    'profile.no_active_listings': 'هنوز آگهی فعالی ندارید',
    'profile.more_active_listings': 'و {count} آگهی فعال دیگر',
    'profile.sold_properties': 'املاک فروخته شده',
    'profile.sold_properties_description': 'املاک فروخته شده شما',
    'profile.no_sold_properties': 'هنوز ملک فروخته شده‌ای ندارید',
    'profile.more_sold_properties': 'و {count} ملک فروخته شده دیگر',
    'profile.listing_fetch_error': 'خطا در دریافت آگهی‌ها',
    'profile.listing_fetch_error_description': 'نتوانستیم آگهی‌های شما را دریافت کنیم.',
    'profile.account_settings': 'تنظیمات حساب',
    'profile.account_settings_description': 'مدیریت حساب و امنیت',
    'profile.change_password': 'تغییر رمز عبور',
    'profile.change_password_description': 'به‌روزرسانی رمز عبور برای ورود امن‌تر',
    'profile.change': 'تغییر',
    'profile.two_factor': 'احراز هویت دو مرحله‌ای',
    'profile.two_factor_description': 'افزودن امنیت بیشتر به حساب',
    'profile.enable': 'فعال‌سازی',
    'profile.delete_account': 'حذف حساب',
    'profile.delete_account_description': 'حذف دائمی حساب و تمام داده‌ها',
    'profile.delete': 'حذف',
    'profile.logout': 'خروج',
    
    // Profile Email Notifications
    'profile.email_notifications': 'اعلان‌های ایمیل',
    'profile.email_notifications_description': 'انتخاب کنید کدام اعلان‌های ایمیل را می‌خواهید دریافت کنید',
    'profile.notif_property_alerts': 'هشدارهای ملک',
    'profile.notif_property_alerts_desc': 'اطلاع‌رسانی درباره ملک‌های جدید مطابق با جستجوهای ذخیره شده',
    'profile.notif_saved_search_alerts': 'به‌روزرسانی جستجوی ذخیره شده',
    'profile.notif_saved_search_alerts_desc': 'به‌روزرسانی دریافت کنید وقتی ملک‌های جدید با معیارهای شما مطابقت دارند',
    'profile.notif_newsletter': 'خبرنامه',
    'profile.notif_newsletter_desc': 'خبرنامه هفتگی ما را با بینش‌های بازار دریافت کنید',
    'profile.notif_viewing_confirmations': 'تأییدیه بازدید',
    'profile.notif_viewing_confirmations_desc': 'ایمیل‌های تأیید برای بازدیدهای برنامه‌ریزی شده دریافت کنید',
    'profile.save_preferences': 'ذخیره تنظیمات',
    'profile.notifications_saved': 'تنظیمات اعلان ذخیره شد',
    'profile.notifications_save_error': 'ذخیره تنظیمات اعلان ناموفق بود',
    
    // Not Found Page
    'notfound.title': '۴۰۴',
    'notfound.message': 'متأسفیم! صفحه پیدا نشد',
    'notfound.back_home': 'بازگشت به خانه',
    
    // Property Detail Page
    'property.back_to_listings': 'بازگشت به آگهی‌ها',
    'property.not_found': 'ملک پیدا نشد',
    'property.link_copied': 'لینک کپی شد',
    'property.link_copied_description': 'لینک ملک کپی شد',
    'property.description': 'توضیحات',
    'property.features': 'ویژگی‌ها',
    'property.details': 'جزئیات ملک',
    'property.rooms_label': 'اتاق‌ها',
    'property.size_label': 'متراژ',
    'property.plot_size_label': 'متراژ زمین',
    'property.monthly_fee_label': 'هزینه ماهانه',
    'property.contact_agent': 'تماس با مشاور',
    'property.send_message': 'ارسال پیام',
    'property.schedule_viewing': 'رزرو بازدید',
    'property.section.details': 'جزئیات',
    'property.section.amenities': 'امکانات',
    'property.section.overview': 'نمای کلی',
    'property.section.payment_plan': 'برنامه پرداخت',
    'property.section.floor_plans': 'نقشه طبقات',
    'property.section.view_map': 'مشاهده نقشه',
    'property.copy_link': 'کپی لینک',
    'property.share': 'اشتراک',
    'property.share_text': 'این ملک را ببینید',
    'property.posted_on': 'تاریخ انتشار',
    'property.type': 'نوع',
    'property.status': 'وضعیت',
    'property.available': 'موجود',
    'property.no_amenities': 'هیچ امکاناتی برای این ملک ثبت نشده',
    'property.no_description': 'توضیحات موجود نیست',
    'property.location': 'موقعیت',
    'property.payment_plan_info': 'برای جزئیات برنامه پرداخت با مشاور تماس بگیرید',
    'property.contact_for_payment_plan': 'تماس برای برنامه پرداخت',
    'property.no_floor_plans': 'نقشه طبقات موجود نیست',
    'property.request_floor_plans': 'درخواست نقشه طبقات',
    'property.no_map_location': 'موقعیت روی نقشه موجود نیست',
    
    // News Page
    'news.subtitle': 'با آخرین اخبار املاک، بینش‌های بازار و راهنماها به‌روز بمانید',
    'news.no_articles': 'هنوز مقاله خبری وجود ندارد',
    'news.check_back': 'به زودی برای آخرین اخبار و بینش‌های بازار مراجعه کنید.',
    'news.search_placeholder': 'جستجو در مقالات...',
    'news.showing_filtered': 'نمایش {shown} از {total} مقاله در این صفحه',
    'news.clear_filters': 'پاک کردن فیلترها',
    'news.no_matching': 'مقاله منطبقی یافت نشد',
    'news.adjust_filters': 'جستجو یا فیلترهای خود را تنظیم کنید تا آنچه می‌خواهید پیدا کنید.',
    'news.publisher': 'ناشر',
    'news.page_info': 'صفحه {current} از {total} • {count} مقاله',
    'news.category.all': 'همه',
    'news.category.market_news': 'اخبار بازار',
    'news.category.property_guides': 'راهنمای املاک',
    'news.category.investment_tips': 'نکات سرمایه‌گذاری',
    'news.category.industry_updates': 'به‌روزرسانی‌های صنعت',
    'news.category.announcements': 'اطلاعیه‌ها',
    'news.category.expert_advice': 'مشاوره تخصصی',
    
    // Filters/Sorting
    'filters.any': 'همه',
    'filters.newest_first': 'جدیدترین اول',
    'filters.price_low_high': 'قیمت: کم به زیاد',
    'filters.price_high_low': 'قیمت: زیاد به کم',
    'filters.size': 'متراژ',
    'filters.searching_for': 'جستجو برای',
    'filters.search_areas': 'جستجوی مناطق، شهرها...',
    
    // Toast Messages
    'toast.error': 'خطا',
    'toast.success': 'موفقیت',
    'toast.auth_required': 'احراز هویت لازم است',
    'toast.sign_in_required': 'لطفاً برای ادامه وارد شوید',
    'toast.sign_in_favorites': 'لطفاً برای افزودن به علاقه‌مندی‌ها وارد شوید',
    'toast.sign_in_properties': 'لطفاً برای افزودن ملک وارد شوید',
    'toast.properties_load_error': 'بارگیری ملک‌ها با خطا مواجه شد',
    'toast.property_added': 'ملک با موفقیت اضافه شد',
    'toast.property_add_error': 'افزودن ملک با خطا مواجه شد',
    'toast.property_updated': 'ملک با موفقیت به‌روزرسانی شد',
    'toast.property_update_error': 'به‌روزرسانی ملک با خطا مواجه شد',
    'toast.property_deleted': 'ملک با موفقیت حذف شد',
    'toast.property_delete_error': 'حذف ملک با خطا مواجه شد',
    'toast.property_marked_sold': 'ملک با موفقیت به عنوان فروخته‌شده علامت‌گذاری شد',
    'toast.property_sold_error': 'علامت‌گذاری ملک به عنوان فروخته‌شده با خطا مواجه شد',
    'toast.invalid_price': 'لطفاً قیمت فروش معتبر وارد کنید',
    'toast.favorites_load_error': 'بارگیری علاقه‌مندی‌ها با خطا مواجه شد',
    'toast.added_to_favorites': 'به علاقه‌مندی‌ها اضافه شد',
    'toast.add_favorites_error': 'افزودن به علاقه‌مندی‌ها با خطا مواجه شد',
    'toast.removed_from_favorites': 'از علاقه‌مندی‌ها حذف شد',
    'toast.remove_favorites_error': 'حذف از علاقه‌مندی‌ها با خطا مواجه شد',
    'toast.search_saved': 'جستجو ذخیره شد',
    'toast.search_saved_desc': 'با هشدارهای ایمیل ذخیره شده است',
    'toast.search_save_error': 'ذخیره جستجو امکان‌پذیر نیست',
    'toast.valuation_complete': 'ارزیابی کامل شد',
    'toast.valuation_estimated': 'ارزش تخمینی',
    'toast.valuation_error': 'دریافت ارزیابی امکان‌پذیر نیست',
    'toast.message_sent': 'پیام ارسال شد',
    'toast.agent_contact_desc': 'نماینده به زودی با شما تماس خواهد گرفت',
    'toast.message_error': 'ارسال پیام امکان‌پذیر نیست',
    'toast.viewing_booked': 'بازدید رزرو شد',
    'toast.viewing_confirmed': 'بازدید شما رزرو شده و تأییدیه از طریق ایمیل ارسال شد',
    'toast.viewing_error': 'رزرو بازدید امکان‌پذیر نیست',
    'toast.image_analysis_complete': 'تحلیل تصویر کامل شد',
    'toast.image_analysis_desc': 'تحلیل هوش مصنوعی تصویر کامل شد',
    'toast.image_analysis_error': 'تحلیل تصویر امکان‌پذیر نیست',
    'toast.logged_out': 'خارج شدید',
    'toast.welcome_back': 'هر زمان که خواستید برگردید!',
    
    // Recent searches
    'search.properties_label': 'ملک‌ها',
    'search.from': 'از',
    'search.up_to': 'تا',
    
    // News Management
    'news_mgmt.title': 'مدیریت اخبار',
    'news_mgmt.subtitle': 'ایجاد و مدیریت مقالات خبری',
    'news_mgmt.create_article': 'ایجاد مقاله',
    'news_mgmt.edit_article': 'ویرایش مقاله',
    'news_mgmt.create_new': 'ایجاد مقاله جدید',
    'news_mgmt.all_articles': 'همه مقالات',
    'news_mgmt.no_articles': 'هنوز مقاله‌ای وجود ندارد. اولین مقاله را ایجاد کنید!',
    'news_mgmt.delete_article': 'حذف مقاله',
    'news_mgmt.delete_confirm': 'آیا مطمئن هستید که می‌خواهید این مقاله را حذف کنید؟ این عمل قابل بازگشت نیست.',
    'news_mgmt.title_label': 'عنوان',
    'news_mgmt.title_placeholder': 'عنوان مقاله را وارد کنید',
    'news_mgmt.category_label': 'دسته‌بندی',
    'news_mgmt.category_placeholder': 'مثلاً: اخبار بازار، راهنماها، نکات',
    'news_mgmt.image_url': 'آدرس تصویر',
    'news_mgmt.content': 'محتوا',
    'news_mgmt.content_placeholder': 'محتوای مقاله را وارد کنید',
    'news_mgmt.published': 'منتشرشده',
    'news_mgmt.draft': 'پیش‌نویس',
    'news_mgmt.update_article': 'به‌روزرسانی مقاله',
    'news_mgmt.required_fields': 'عنوان و دسته‌بندی الزامی است',
    'news_mgmt.article_updated': 'مقاله خبری با موفقیت به‌روزرسانی شد',
    'news_mgmt.article_created': 'مقاله خبری با موفقیت ایجاد شد',
    'news_mgmt.article_save_error': 'ذخیره مقاله خبری با خطا مواجه شد',
    'news_mgmt.article_deleted': 'مقاله خبری با موفقیت حذف شد',
    'news_mgmt.article_delete_error': 'حذف مقاله خبری با خطا مواجه شد',
    'news_mgmt.access_denied': 'دسترسی رد شد',
    'news_mgmt.access_denied_desc': 'شما اجازه دسترسی به این صفحه را ندارید. فقط ناشران و مدیران می‌توانند اخبار را مدیریت کنند.',
    
    // Badges & Labels
    'badge.new': 'جدید',
    'badge.sold': 'فروخته‌شده',
    'badge.featured': 'ویژه',
    'badge.reduced': 'تخفیف‌خورده',
    'badge.new_construction': 'ساخت جدید',
    
    // Confirmation Messages
    'confirm.delete': 'حذف',
    'confirm.cancel': 'انصراف',
    'confirm.yes': 'بله',
    'confirm.no': 'خیر',
    'confirm.are_you_sure': 'آیا مطمئن هستید؟',
    'confirm.action_irreversible': 'این عمل قابل بازگشت نیست.',
    
    // Tooltips
    'tooltip.favorite': 'افزودن به علاقه‌مندی‌ها',
    'tooltip.share': 'اشتراک‌گذاری ملک',
    'tooltip.edit': 'ویرایش ملک',
    'tooltip.delete': 'حذف ملک',
    'tooltip.contact': 'تماس با نماینده',
    'tooltip.schedule': 'رزرو بازدید',
    'tooltip.map_view': 'مشاهده روی نقشه',
    'tooltip.list_view': 'نمای لیست',
    'tooltip.grid_view': 'نمای شبکه‌ای',
    
    // Admin Pages
    'admin.loading': 'در حال بارگذاری...',
    'admin.access_denied': 'دسترسی رد شد. مجوز مدیریت لازم است.',
    
    // Admin Notification Settings
    'admin.notifications.title': 'تنظیمات اعلان',
    'admin.notifications.description': 'تنظیمات اعلان را برای همه انواع کاربران مدیریت کنید. در صورت نیاز فعال، غیرفعال یا پنهان کنید.',
    'admin.notifications.fetch_error': 'دریافت تنظیمات اعلان ناموفق بود',
    'admin.notifications.update_success': 'تنظیم اعلان به‌روزرسانی شد',
    'admin.notifications.update_error': 'به‌روزرسانی تنظیم اعلان ناموفق بود',
    'admin.notifications.role_description': 'مدیریت اعلان‌ها برای کاربران {role}',
    'admin.notifications.hidden': 'پنهان',
    'admin.notifications.disabled': 'غیرفعال',
    'admin.notifications.active': 'فعال',
    'admin.notifications.hide': 'پنهان کردن',
    'admin.notifications.roles.user': 'کاربران',
    'admin.notifications.roles.maklare': 'مشاوران',
    'admin.notifications.roles.agency': 'آژانس‌ها',
    'admin.notifications.roles.admin': 'مدیران',
    'admin.notifications.roles.publisher': 'ناشران',
    'admin.notifications.types.new_viewing_request': 'درخواست بازدید جدید',
    'admin.notifications.types.new_client_message': 'پیام مشتری جدید',
    'admin.notifications.types.property_alerts': 'هشدارهای ملک',
    'admin.notifications.types.weekly_summary': 'خلاصه هفتگی',
    'admin.notifications.types.broker_property_added': 'مشاور ملک اضافه کرد',
    'admin.notifications.types.broker_viewing_request': 'درخواست بازدید مشاور',
    'admin.notifications.types.broker_client_message': 'پیام مشتری مشاور',
    'admin.notifications.types.broker_joined': 'مشاور پیوست',
    'admin.notifications.types.weekly_agency_summary': 'خلاصه هفتگی آژانس',
    'admin.notifications.types.saved_search_alerts': 'هشدارهای جستجوی ذخیره‌شده',
    'admin.notifications.types.favorite_property_updates': 'به‌روزرسانی ملک‌های مورد علاقه',
    'admin.notifications.types.newsletter': 'خبرنامه',
    'admin.notifications.types.system_alerts': 'هشدارهای سیستم',
    'admin.notifications.types.new_user_signup': 'ثبت‌نام کاربر جدید',
    'admin.notifications.types.article_published': 'مقاله منتشر شد',

    // Admin Settings
    'admin.settings': 'تنظیمات مدیر',
    'admin.messaging_settings': 'تنظیمات پیام‌رسانی',
    'admin.messaging_settings_description': 'کنترل ویژگی‌های پیام‌رسانی و تماس برای همه کاربران',
    'admin.messaging': 'پیام‌رسانی',
    'admin.messaging_description': 'اجازه به کاربران برای ارسال پیام',
    'admin.voice_calls': 'تماس‌های صوتی',
    'admin.voice_calls_description': 'اجازه به کاربران برای برقراری تماس صوتی',
    'admin.video_calls': 'تماس‌های ویدیویی',
    'admin.video_calls_description': 'اجازه به کاربران برای برقراری تماس ویدیویی',
    'admin.screen_sharing': 'اشتراک‌گذاری صفحه',
    'admin.screen_sharing_description': 'اجازه به کاربران برای اشتراک‌گذاری صفحه در طول تماس',

    // Location Manager
    'location_mgr.title': 'مدیر مکان‌ها',
    'location_mgr.current_locations': 'مکان‌های فعلی',
    'location_mgr.locations_in_db': '{count} مکان در پایگاه داده',
    'location_mgr.refresh': 'بازخوانی',
    'location_mgr.populate_title': 'پر کردن مکان‌ها',
    'location_mgr.populate_desc': 'دریافت همه شهرها و مناطق عراق از Google Places API و ذخیره در پایگاه داده. این کار جایگزین همه مکان‌های موجود می‌شود.',
    'location_mgr.populating': 'در حال پر کردن...',
    'location_mgr.populate_btn': 'پر کردن از Google Places',
    'location_mgr.success': 'موفق',
    'location_mgr.success_msg': 'مکان‌ها با موفقیت پر شدند',
    'location_mgr.error': 'خطا',
    'location_mgr.error_msg': 'پر کردن مکان‌ها ناموفق بود. کنسول را برای جزئیات بررسی کنید.',
    
    // Map Configuration Manager
    'map_config.title': 'مدیر پیکربندی نقشه',
    'map_config.subtitle': 'مدیریت پیکربندی‌های دائمی نقشه برای کاهش استفاده از Google Maps API',
    'map_config.loading': 'در حال بارگذاری پیکربندی‌های نقشه...',
    'map_config.create': 'ایجاد پیکربندی',
    'map_config.create_title': 'ایجاد پیکربندی نقشه',
    'map_config.create_desc': 'ایجاد پیکربندی نقشه دائمی جدید که به جای فراخوانی مکرر Google Maps API استفاده می‌شود.',
    'map_config.edit_title': 'ویرایش پیکربندی نقشه',
    'map_config.edit_desc': 'به‌روزرسانی پیکربندی نقشه دائمی خود.',
    'map_config.name': 'نام پیکربندی',
    'map_config.name_placeholder': 'مثال: نقشه املاک عراق',
    'map_config.description': 'توضیحات (اختیاری)',
    'map_config.description_placeholder': 'توضیح مختصر این پیکربندی',
    'map_config.map_data': 'پیکربندی نقشه (JSON)',
    'map_config.map_data_placeholder': 'پیکربندی نقشه با فرمت JSON',
    'map_config.cancel': 'لغو',
    'map_config.update': 'به‌روزرسانی پیکربندی',
    'map_config.no_configs': 'پیکربندی نقشه‌ای وجود ندارد',
    'map_config.no_configs_desc': 'اولین پیکربندی نقشه خود را بسازید تا مدیریت کارآمد نقشه‌ها را شروع کنید.',
    'map_config.create_first': 'ایجاد اولین پیکربندی',
    'map_config.active': 'فعال',
    'map_config.activate': 'فعال‌سازی',
    'map_config.edit': 'ویرایش',
    'map_config.delete': 'حذف',
    'map_config.delete_confirm': 'آیا مطمئن هستید؟',
    'map_config.delete_desc': 'این کار پیکربندی نقشه "{name}" را برای همیشه حذف می‌کند. این عمل قابل بازگشت نیست.',
    'map_config.created': 'ایجاد شده',
    'map_config.last_updated': 'آخرین به‌روزرسانی',
    'map_config.map_center': 'مرکز نقشه',
    'map_config.zoom_level': 'سطح بزرگنمایی',
    'map_config.cities': 'شهرها',
    'map_config.cities_configured': '{count} شهر پیکربندی شده',
    'map_config.invalid_json': 'فرمت JSON نامعتبر در داده‌های نقشه',
    
    // About Page
    'about.title': 'درباره عقارنت',
    'about.subtitle': 'شریک مورد اعتماد شما در بازار املاک عراق',
    'about.description': 'عقارنت پلتفرم پیشرو املاک عراق است که خریداران، فروشندگان و نمایندگان را در سراسر کشور به هم متصل می‌کند. ما فهرست‌های جامع املاک، بینش‌های بازار و ابزارهای حرفه‌ای را ارائه می‌دهیم.',
    'about.mission_title': 'مأموریت ما',
    'about.mission_text': 'تحول در بازار املاک عراق با ارائه اطلاعات شفاف، در دسترس و قابل اعتماد برای همه.',
    'about.vision_title': 'چشم‌انداز ما',
    'about.vision_text': 'تبدیل شدن به معتبرترین پلتفرم املاک در خاورمیانه و توانمندسازی همه برای یافتن خانه رویایی خود.',
    'about.services_title': 'خدمات ما',
    'about.services_text': 'فهرست املاک، تحلیل بازار، اتصال به نمایندگان، ارزیابی املاک و اخبار جامع املاک.',
    'about.team_title': 'تیم ما',
    'about.team_text': 'تیمی متعهد از متخصصان املاک، کارشناسان فناوری و متخصصان خدمات مشتریان.',
    'about.company_info': 'اطلاعات شرکت',
    'about.based_in': 'مستقر در سوئد، خدمات‌رسانی به عراق',
    
    // Contact Page
    'contact.title': 'تماس با ما',
    'contact.subtitle': 'ما اینجا هستیم تا کمک کنیم. با هر سؤال یا نگرانی با ما تماس بگیرید.',
    'contact.email_label': 'ایمیل',
    'contact.phone_label': 'تلفن',
    'contact.phone_info': 'به زودی',
    'contact.location_label': 'موقعیت',
    'contact.location_info': 'سوئد',
    'contact.form_title': 'پیامی برای ما بفرستید',
    'contact.form_description': 'فرم زیر را پر کنید و در اسرع وقت با شما تماس می‌گیریم.',
    'contact.name_placeholder': 'نام شما',
    'contact.email_placeholder': 'ایمیل@شما.com',
    'contact.subject': 'موضوع',
    'contact.subject_placeholder': 'این درباره چیست؟',
    'contact.message_placeholder': 'پیام شما...',
    'contact.success': 'پیام ارسال شد! به زودی با شما تماس می‌گیریم.',
    
    // Press Page
    'press.title': 'مطبوعات و رسانه',
    'press.subtitle': 'آخرین اخبار و بیانیه‌های مطبوعاتی از عقارنت',
    'press.contact_title': 'تماس مطبوعاتی',
    'press.contact_description': 'برای سؤالات مطبوعاتی، لطفاً از طریق ایمیل با ما تماس بگیرید.',
    'press.add_release': 'افزودن بیانیه مطبوعاتی',
    'press.edit_release': 'ویرایش بیانیه مطبوعاتی',
    'press.new_release': 'بیانیه مطبوعاتی جدید',
    'press.release_title': 'عنوان',
    'press.content': 'محتوا',
    'press.publish': 'انتشار',
    'press.no_releases': 'هنوز بیانیه مطبوعاتی وجود ندارد.',
    'press.delete_confirm_title': 'بیانیه مطبوعاتی حذف شود؟',
    'press.delete_confirm_description': 'این عمل قابل بازگشت نیست.',
    
    // Investors Page
    'investors.title': 'برای سرمایه‌گذاران',
    'investors.subtitle': 'به ما بپیوندید در تحول بازار املاک عراق',
    'investors.growth_title': 'پتانسیل رشد',
    'investors.growth_text': 'بازار املاک عراق با رشد سریع و فرصت‌های سرمایه‌گذاری قابل توجه مواجه است.',
    'investors.market_title': 'رهبر بازار',
    'investors.market_text': 'عقارنت به عنوان پلتفرم دیجیتال پیشرو در بخش املاک عراق قرار دارد.',
    'investors.vision_title': 'گسترش منطقه‌ای',
    'investors.vision_text': 'چشم‌انداز ما فراتر از عراق گسترش می‌یابد تا به پلتفرم پیشرو در منطقه خاورمیانه تبدیل شویم.',
    'investors.opportunity_title': 'فرصت سرمایه‌گذاری',
    'investors.opportunity_text': 'عقارنت فرصتی منحصر به فرد برای سرمایه‌گذاری در تحول دیجیتال یکی از بزرگترین بازارهای نوظهور املاک جهان ارائه می‌دهد.',
    'investors.opportunity_text_2': 'با پایگاه کاربری رو به رشد و خدمات در حال گسترش، ما برای رشد پایدار و رهبری بازار در موقعیت مناسبی هستیم.',
    'investors.contact_title': 'تماس بگیرید',
    'investors.contact_description': 'به فرصت‌های سرمایه‌گذاری علاقه‌مند هستید؟ برای اطلاعات بیشتر با ما تماس بگیرید.',
    'investors.company': 'شرکت / سازمان',
    'investors.company_placeholder': 'نام شرکت شما',
    'investors.message_placeholder': 'درباره علایق سرمایه‌گذاری خود به ما بگویید...',
    'investors.success': 'از علاقه شما متشکریم! به زودی با شما تماس می‌گیریم.',
    
    // Advertising Page
    'advertising.title': 'تبلیغات و بازاریابی',
    'advertising.subtitle': 'به هزاران خریدار و مستأجر بالقوه در عراق دسترسی پیدا کنید',
    'advertising.targeted_title': 'دسترسی هدفمند',
    'advertising.targeted_text': 'به کاربرانی دسترسی پیدا کنید که فعالانه به دنبال املاک در مناطق و محدوده قیمت خاص هستند.',
    'advertising.audience_title': 'مخاطبان با کیفیت',
    'advertising.audience_text': 'با خریداران و مستأجران جدی که آماده تصمیم‌گیری هستند ارتباط برقرار کنید.',
    'advertising.analytics_title': 'تحلیل دقیق',
    'advertising.analytics_text': 'عملکرد کمپین خود را با تحلیل‌ها و بینش‌های جامع پیگیری کنید.',
    'advertising.formats_title': 'فرمت‌های متعدد',
    'advertising.formats_text': 'تبلیغات بنری، فهرست‌های ویژه، محتوای حمایت شده و گزینه‌های تبلیغاتی بیشتر.',
    'advertising.contact_title': 'شروع تبلیغات',
    'advertising.contact_description': 'برای بحث درباره فرصت‌های تبلیغاتی با ما تماس بگیرید.',
    'advertising.company': 'شرکت / سازمان',
    'advertising.company_placeholder': 'نام شرکت شما',
    'advertising.budget': 'محدوده بودجه',
    'advertising.budget_placeholder': 'مثال: 500 - 1000 دلار/ماه',
    'advertising.message_placeholder': 'درباره اهداف تبلیغاتی خود به ما بگویید...',
    'advertising.success': 'متشکریم! درباره فرصت‌های تبلیغاتی با شما تماس می‌گیریم.',
    
    // Jobs Page
    'jobs.title': 'مشاغل در عقارنت',
    'jobs.subtitle': 'به تیم ما بپیوندید و به شکل‌دهی آینده املاک در عراق کمک کنید',
    'jobs.add_job': 'افزودن شغل',
    'jobs.edit_job': 'ویرایش شغل',
    'jobs.new_job': 'شغل جدید',
    'jobs.job_title': 'عنوان شغل',
    'jobs.department': 'بخش',
    'jobs.location': 'موقعیت',
    'jobs.employment_type': 'نوع استخدام',
    'jobs.full_time': 'تمام وقت',
    'jobs.part_time': 'پاره وقت',
    'jobs.contract': 'قراردادی',
    'jobs.internship': 'کارآموزی',
    'jobs.description': 'توضیحات',
    'jobs.requirements': 'الزامات',
    'jobs.benefits': 'مزایا',
    'jobs.publish': 'انتشار',
    'jobs.no_jobs': 'در حال حاضر موقعیت شغلی باز وجود ندارد.',
    'jobs.check_back': 'بعداً برای فرصت‌های جدید مراجعه کنید.',
    'jobs.apply_now': 'اکنون درخواست دهید',
    'jobs.apply_for': 'درخواست برای',
    'jobs.apply_description': 'درخواست خود را از طریق ایمیل ارسال کنید.',
    'jobs.application_sent': 'درخواست ارسال شد! موفق باشید!',
    'jobs.delete_confirm_title': 'شغل حذف شود؟',
    'jobs.delete_confirm_description': 'این عمل قابل بازگشت نیست.',
    
    // Property Statistics Page
    'statistics.title': 'آمار املاک',
    'statistics.subtitle': 'بینش‌های بازار و روندها بر اساس املاک فروخته شده',
    'statistics.6_months': '۶ ماه گذشته',
    'statistics.12_months': '۱۲ ماه گذشته',
    'statistics.24_months': '۲۴ ماه گذشته',
    'statistics.36_months': '۳۶ ماه گذشته',
    'statistics.all_cities': 'همه شهرها',
    'statistics.total_sold': 'کل فروخته شده',
    'statistics.avg_price': 'میانگین قیمت',
    'statistics.avg_price_sqm': 'میانگین قیمت/م²',
    'statistics.price_change': 'تغییر قیمت',
    'statistics.price_trends': 'روند قیمت',
    'statistics.price_trends_desc': 'میانگین قیمت املاک در طول زمان',
    'statistics.sales_volume': 'حجم فروش',
    'statistics.sales_volume_desc': 'تعداد املاک فروخته شده در هر ماه',
    'statistics.properties_sold': 'املاک فروخته شده',
    'statistics.price_by_city': 'قیمت/م² به تفکیک شهر',
    'statistics.price_by_city_desc': 'مقایسه میانگین قیمت/م² در شهرها',
    'statistics.price_per_sqm': 'قیمت/م²',
    'statistics.type_distribution': 'توزیع نوع ملک',
    'statistics.type_distribution_desc': 'توزیع املاک فروخته شده بر اساس نوع',
    'statistics.avg_days_market': 'میانگین روزها در بازار',
    'statistics.days': 'روز',
    'statistics.price_ranges': 'توزیع محدوده قیمت',
    'statistics.price_ranges_desc': 'تعداد املاک فروخته شده در هر محدوده قیمت',
    'statistics.properties': 'املاک',
    'statistics.year_over_year': 'مقایسه سال به سال',
    'statistics.year_over_year_desc': 'مقایسه میانگین قیمت و حجم فروش در سال‌ها',
    'statistics.all_types': 'همه انواع ملک',
    'statistics.all_rooms': 'همه اتاق‌ها',
    'statistics.rooms': 'اتاق',
    'statistics.export': 'صادرات',
    'statistics.export_csv': 'دانلود CSV',
    'statistics.export_json': 'دانلود JSON',
    'statistics.share': 'اشتراک گذاری',
    'statistics.link_copied': 'لینک کپی شد',
    'statistics.link_copied_desc': 'لینک گزارش آماری در کلیپ‌بورد کپی شد',
    'statistics.custom_date': 'تاریخ سفارشی',
    'statistics.use_preset': 'استفاده از پیش‌تنظیم',
    'statistics.start_date': 'تاریخ شروع',
    'statistics.end_date': 'تاریخ پایان',
    'statistics.city_comparison': 'مقایسه شهرها',
    'statistics.city_comparison_desc': 'آمار املاک بین دو شهر را در کنار هم مقایسه کنید',
    'statistics.select_city_1': 'شهر اول را انتخاب کنید',
    'statistics.select_city_2': 'شهر دوم را انتخاب کنید',
    'statistics.vs': 'در مقابل',
    'statistics.select_cities_to_compare': 'دو شهر در بالا انتخاب کنید تا آمارهایشان را مقایسه کنید',
    'statistics.avg_size': 'میانگین متراژ',
    
    // Cookie Settings
    'cookie_settings.title': 'تنظیمات کوکی',
    'cookie_settings.description': 'ما از کوکی‌ها برای بهبود تجربه مرور شما استفاده می‌کنیم. می‌توانید ترجیحات کوکی خود را در زیر سفارشی کنید.',
    'cookie_settings.necessary': 'کوکی‌های ضروری',
    'cookie_settings.necessary_desc': 'این کوکی‌ها برای عملکرد صحیح وب‌سایت ضروری هستند. نمی‌توان آن‌ها را غیرفعال کرد.',
    'cookie_settings.functional': 'کوکی‌های عملکردی',
    'cookie_settings.functional_desc': 'این کوکی‌ها ویژگی‌های شخصی‌سازی شده مانند ترجیحات زبان و تنظیمات ذخیره شده را فعال می‌کنند.',
    'cookie_settings.analytics': 'کوکی‌های تحلیلی',
    'cookie_settings.analytics_desc': 'این کوکی‌ها به ما کمک می‌کنند بفهمیم بازدیدکنندگان چگونه با وب‌سایت ما تعامل دارند تا تجربه کاربری را بهبود دهیم.',
    'cookie_settings.marketing': 'کوکی‌های بازاریابی',
    'cookie_settings.marketing_desc': 'این کوکی‌ها برای نمایش تبلیغات مرتبط و پیگیری اثربخشی کمپین‌های ما استفاده می‌شوند.',
    'cookie_settings.accept_all': 'پذیرش همه',
    'cookie_settings.reject_all': 'رد همه',
    'cookie_settings.save_preferences': 'ذخیره ترجیحات',
    'cookie_settings.saved': 'ترجیحات کوکی ذخیره شد',
    
    // Cookie Banner
    'cookie_banner.title': 'ما از کوکی‌ها استفاده می‌کنیم',
    'cookie_banner.description': 'ما از کوکی‌ها برای بهبود تجربه مرور، نمایش محتوای شخصی‌سازی شده و تحلیل ترافیک استفاده می‌کنیم. با کلیک بر روی "پذیرش همه"، شما به استفاده ما از کوکی‌ها رضایت می‌دهید.',
    'cookie_banner.accept_all': 'پذیرش همه',
    'cookie_banner.reject_all': 'رد همه',
    'cookie_banner.customize': 'سفارشی‌سازی',
    
    // Terms of Service
    'terms.title': 'شرایط خدمات',
    'terms.last_updated': 'آخرین به‌روزرسانی:',
    'terms.section1.title': '1. پذیرش شرایط',
    'terms.section1.p1': 'با استفاده از عقارنت، شما موافقت می‌کنید که به این شرایط خدمات متعهد باشید. اگر با این شرایط موافق نیستید، لطفاً از خدمات ما استفاده نکنید.',
    'terms.section1.p2': 'ما حق تغییر این شرایط را در هر زمان داریم. استفاده مداوم پس از تغییرات به معنای پذیرش شرایط اصلاح شده است.',
    'terms.section2.title': '2. توصیف خدمات',
    'terms.section2.p1': 'عقارنت یک پلتفرم دیجیتال برای آگهی‌های املاک، جستجوی ملک و ارتباط بین خریداران، فروشندگان و نمایندگان در عراق فراهم می‌کند.',
    'terms.section2.p2': 'خدمات ما شامل لیست املاک، قابلیت جستجو، جستجوهای ذخیره شده، علاقه‌مندی‌ها و ابزارهای ارتباطی بین کاربران و نمایندگان است.',
    'terms.section3.title': '3. مسئولیت‌های کاربر',
    'terms.section3.p1': 'به عنوان کاربر عقارنت، شما موافقت می‌کنید که:',
    'terms.section3.li1': 'اطلاعات دقیق و صادقانه در آگهی‌ها و پروفایل خود ارائه دهید',
    'terms.section3.li2': 'از پلتفرم برای فعالیت‌های کلاهبرداری یا غیرقانونی استفاده نکنید',
    'terms.section3.li3': 'حقوق مالکیت فکری دیگران را رعایت کنید',
    'terms.section3.li4': 'در عملکرد صحیح سرویس دخالت نکنید',
    'terms.section4.title': '4. آگهی‌های ملک',
    'terms.section4.p1': 'کاربرانی که املاک را لیست می‌کنند مسئول صحت آگهی‌های خود هستند. عقارنت صحت اطلاعات ملک را تأیید نمی‌کند.',
    'terms.section4.p2': 'ما حق حذف هر آگهی که شرایط ما را نقض کند یا به عنوان کلاهبرداری گزارش شود، داریم.',
    'terms.section5.title': '5. مالکیت فکری',
    'terms.section5.p1': 'تمام محتوای عقارنت، از جمله متن، گرافیک، لوگو و نرم‌افزار، متعلق به عقارنت یا تأمین‌کنندگان محتوای آن است.',
    'terms.section5.p2': 'شما نمی‌توانید بدون اجازه کتبی صریح، محتوای ما را بازتولید، توزیع یا آثار مشتق از آن ایجاد کنید.',
    'terms.section6.title': '6. محدودیت مسئولیت',
    'terms.section6.p1': 'عقارنت "همانطور که هست" بدون هیچ نوع ضمانتی ارائه می‌شود. ما مسئول هیچ خسارتی که از استفاده شما از خدمات ما ناشی شود، نیستیم.',
    'terms.section7.title': '7. قانون حاکم',
    'terms.section7.p1': 'این شرایط توسط قوانین سوئد اداره می‌شود. هر گونه اختلاف باید در دادگاه‌های سوئد حل شود.',
    'terms.section7.p2': 'اگر هر ماده‌ای از این شرایط غیرقابل اجرا تشخیص داده شود، مواد باقی‌مانده به قوت خود باقی می‌مانند.',
    'terms.section8.title': '8. اطلاعات تماس',
    'terms.section8.p1': 'برای سؤالات درباره این شرایط خدمات، لطفاً با ما تماس بگیرید:',
    'terms.section8.email': 'ایمیل',
    
    // Privacy Policy
    'privacy.title': 'سیاست حفظ حریم خصوصی',
    'privacy.last_updated': 'آخرین به‌روزرسانی:',
    'privacy.section1.title': '1. مقدمه',
    'privacy.section1.p1': 'در عقارنت، ما متعهد به حفاظت از حریم خصوصی و داده‌های شخصی شما هستیم. این سیاست توضیح می‌دهد که چگونه اطلاعات شما را جمع‌آوری، استفاده و محافظت می‌کنیم.',
    'privacy.section1.p2': 'با استفاده از خدمات ما، شما با شیوه‌های داده توصیف شده در این سیاست موافقت می‌کنید.',
    'privacy.section2.title': '2. اطلاعاتی که جمع‌آوری می‌کنیم',
    'privacy.section2.p1': 'ما انواع زیر از اطلاعات را جمع‌آوری می‌کنیم:',
    'privacy.section2.li1': 'اطلاعات حساب: نام، آدرس ایمیل، شماره تلفن',
    'privacy.section2.li2': 'داده‌های لیست ملک: آدرس‌ها، توضیحات، عکس‌ها، قیمت‌ها',
    'privacy.section2.li3': 'داده‌های استفاده: تاریخچه جستجو، املاک ذخیره شده، رفتار مرور',
    'privacy.section2.li4': 'اطلاعات دستگاه: آدرس IP، نوع مرورگر، سیستم عامل',
    'privacy.section3.title': '3. نحوه استفاده از اطلاعات شما',
    'privacy.section3.p1': 'ما از اطلاعات شما استفاده می‌کنیم برای:',
    'privacy.section3.li1': 'ارائه و بهبود خدمات ما',
    'privacy.section3.li2': 'شخصی‌سازی تجربه و توصیه‌های شما',
    'privacy.section3.li3': 'ارسال اعلان‌ها درباره جستجوهای ذخیره شده و لیست‌های جدید',
    'privacy.section3.li4': 'ارتباط با شما درباره حساب و پرسش‌های شما',
    'privacy.section4.title': '4. اشتراک‌گذاری داده',
    'privacy.section4.p1': 'ما داده‌های شخصی شما را نمی‌فروشیم. ممکن است اطلاعات را با نمایندگان ملک هنگام ارسال پرسش به اشتراک بگذاریم، و با ارائه‌دهندگان خدمات که در عملیات ما کمک می‌کنند.',
    'privacy.section4.p2': 'ممکن است اطلاعات را زمانی که قانون الزام می‌کند یا برای حفاظت از حقوق و امنیت خود افشا کنیم.',
    'privacy.section5.title': '5. حقوق شما',
    'privacy.section5.p1': 'شما حقوق زیر را در رابطه با داده‌های خود دارید:',
    'privacy.section5.li1': 'دسترسی: درخواست کپی از داده‌های شخصی خود',
    'privacy.section5.li2': 'اصلاح: به‌روزرسانی یا اصلاح اطلاعات نادرست',
    'privacy.section5.li3': 'حذف: درخواست حذف حساب و داده‌های خود',
    'privacy.section5.li4': 'قابلیت انتقال: دریافت داده‌های خود در قالب قابل انتقال',
    'privacy.section5.li5': 'اعتراض: انصراف از فعالیت‌های پردازش داده خاص',
    'privacy.section6.title': '6. امنیت داده',
    'privacy.section6.p1': 'ما اقدامات فنی و سازمانی مناسب را برای محافظت از داده‌های شما در برابر دسترسی غیرمجاز، تغییر یا تخریب اجرا می‌کنیم.',
    'privacy.section6.p2': 'با این حال، هیچ روش انتقالی از طریق اینترنت 100% امن نیست و ما نمی‌توانیم امنیت مطلق را تضمین کنیم.',
    'privacy.section7.title': '7. تغییرات در این سیاست',
    'privacy.section7.p1': 'ممکن است این سیاست حفظ حریم خصوصی را گاهی به‌روزرسانی کنیم. تغییرات مهم را با انتشار اطلاعیه در وب‌سایت یا ارسال ایمیل به شما اطلاع می‌دهیم.',
    'privacy.section8.title': '8. تماس با ما',
    'privacy.section8.p1': 'اگر سؤالی درباره این سیاست حفظ حریم خصوصی یا داده‌های شخصی خود دارید، لطفاً با ما تماس بگیرید:',
    'privacy.section8.email': 'ایمیل',
    
    // FAQ Page
    'faq.title': 'سؤالات متداول',
    'faq.subtitle': 'پاسخ سؤالات رایج درباره استفاده از عقارنت را بیابید',
    'faq.account.title': 'حساب کاربری و ثبت‌نام',
    'faq.account.q1': 'چگونه یک حساب ایجاد کنم؟',
    'faq.account.q1_answer': 'روی "ورود" در سربرگ کلیک کنید، سپس "ایجاد حساب" را انتخاب کنید.',
    'faq.account.q2': 'چگونه رمز عبورم را بازنشانی کنم؟',
    'faq.account.q2_answer': 'در صفحه ورود، روی "فراموشی رمز عبور" کلیک کنید و ایمیل خود را وارد کنید.',
    'faq.account.q3': 'آیا می‌توانم ایمیلم را تغییر دهم؟',
    'faq.account.q3_answer': 'در حال حاضر نمی‌توانید ایمیل خود را مستقیماً تغییر دهید. لطفاً با تیم پشتیبانی تماس بگیرید.',
    'faq.listings.title': 'آگهی‌های ملک',
    'faq.listings.q1': 'چگونه یک ملک برای فروش ثبت کنم؟',
    'faq.listings.q1_answer': 'باید به عنوان نماینده ثبت‌نام شده باشید. پس از ورود، روی "افزودن ملک" کلیک کنید.',
    'faq.listings.q2': 'یک آگهی چه مدت فعال می‌ماند؟',
    'faq.listings.q2_answer': 'آگهی‌ها تا زمانی که به عنوان فروخته شده علامت‌گذاری شوند فعال می‌مانند.',
    'faq.listings.q3': 'آیا می‌توانم آگهی‌ام را بعد از انتشار ویرایش کنم؟',
    'faq.listings.q3_answer': 'بله، می‌توانید هر زمان آگهی خود را ویرایش کنید.',
    'faq.search.title': 'جستجوی ملک‌ها',
    'faq.search.q1': 'چگونه یک جستجو را ذخیره کنم؟',
    'faq.search.q1_answer': 'پس از جستجو، روی "ذخیره جستجو" کلیک کنید.',
    'faq.search.q2': 'چگونه یک ملک را به علاقه‌مندی‌ها اضافه کنم؟',
    'faq.search.q2_answer': 'روی آیکون قلب روی هر کارت ملک کلیک کنید.',
    'faq.search.q3': 'آیا می‌توانم روی نقشه جستجو کنم؟',
    'faq.search.q3_answer': 'بله، از نمای نقشه استفاده کنید.',
    'faq.contact.title': 'تماس با نمایندگان',
    'faq.contact.q1': 'چگونه با یک نماینده تماس بگیرم؟',
    'faq.contact.q1_answer': 'روی "تماس با نماینده" در هر آگهی کلیک کنید.',
    'faq.contact.q2': 'آیا می‌توانم یک بازدید برنامه‌ریزی کنم؟',
    'faq.contact.q2_answer': 'بله، روی "برنامه‌ریزی بازدید" کلیک کنید.',
    'faq.security.title': 'امنیت و حریم خصوصی',
    'faq.security.q1': 'آیا اطلاعات شخصی من امن است؟',
    'faq.security.q1_answer': 'بله، ما از رمزنگاری و اقدامات امنیتی استفاده می‌کنیم.',
    'faq.security.q2': 'چگونه می‌توانم حسابم را حذف کنم؟',
    'faq.security.q2_answer': 'به تنظیمات پروفایل بروید و "حذف حساب" را انتخاب کنید.',
    'faq.still_questions': 'هنوز سؤال دارید؟',
    'faq.contact_us': 'با تیم پشتیبانی ما تماس بگیرید.',
    'newsletter.title': 'اشتراک خبرنامه',
    'newsletter.description': 'برای دریافت آخرین اخبار ملک اشتراک بگیرید.',
    'newsletter.placeholder': 'ایمیل خود را وارد کنید',
    'newsletter.subscribe': 'اشتراک',
    'newsletter.subscribing': 'در حال اشتراک...',
    'newsletter.success': 'اشتراک با موفقیت انجام شد!',
    'newsletter.subscribed': 'از اشتراک شما متشکریم!',
    'newsletter.invalid_email': 'لطفاً یک ایمیل معتبر وارد کنید',
    'newsletter.error': 'اشتراک ناموفق بود',
    'newsletter.unsubscribe_note': 'می‌توانید هر زمان لغو اشتراک کنید.',
    'newsletter.unsubscribe_title': 'لغو اشتراک از خبرنامه',
    'newsletter.unsubscribe_description': 'ایمیل خود را برای لغو اشتراک وارد کنید.',
    'newsletter.unsubscribe_button': 'لغو اشتراک',
    'newsletter.unsubscribe_success': 'لغو اشتراک با موفقیت انجام شد',
    'newsletter.unsubscribe_error': 'لغو اشتراک ناموفق بود',
    'newsletter.unsubscribe_message': 'شما دیگر خبرنامه ما را دریافت نخواهید کرد.',
    'newsletter.invalid_link': 'لینک لغو اشتراک نامعتبر',
    'newsletter.invalid_link_message': 'این لینک لغو اشتراک نامعتبر است یا منقضی شده است. لطفاً از لینک ایمیل خبرنامه استفاده کنید.',
    'newsletter.unsubscribe_reason_label': 'دلیل لغو اشتراک (اختیاری)',
    'newsletter.select_reason': 'یک دلیل انتخاب کنید...',
    'newsletter.reason_too_many_emails': 'ایمیل‌های زیاد',
    'newsletter.reason_not_relevant': 'محتوا مرتبط با من نیست',
    'newsletter.reason_never_subscribed': 'هرگز مشترک نشدم',
    'newsletter.reason_found_another': 'سرویس دیگری پیدا کردم',
    'newsletter.reason_other': 'سایر',
    'common.try_again': 'دوباره امتحان کنید',
    'newsletter.admin.title': 'مشترکین خبرنامه',
    'newsletter.admin.description': 'مدیریت مشترکین خبرنامه',
    'newsletter.admin.total': 'کل مشترکین',
    'newsletter.admin.active': 'فعال',
    'newsletter.admin.unsubscribed': 'لغو اشتراک شده',
    'newsletter.admin.export_csv': 'خروجی CSV',
    'newsletter.admin.refresh': 'بروزرسانی',
    'newsletter.admin.subscribers_list': 'لیست مشترکین',
    'newsletter.admin.subscribers_description': 'همه مشترکین و وضعیت آنها',
    'newsletter.admin.no_subscribers': 'هنوز مشترکی نیست',
    'newsletter.admin.subscribed_at': 'تاریخ اشتراک',
    'newsletter.admin.status': 'وضعیت',
    'newsletter.admin.unsubscribed_at': 'تاریخ لغو',
    'newsletter.admin.actions': 'اقدامات',
    'newsletter.admin.deleted': 'مشترک حذف شد',
    'newsletter.admin.delete_error': 'حذف ناموفق بود',
    'newsletter.admin.no_data': 'داده‌ای برای خروجی نیست',
    'newsletter.admin.exported': 'خروجی موفق',
    'newsletter.admin.delete_confirm_title': 'حذف مشترک؟',
    'newsletter.admin.delete_confirm_description': 'این مشترک برای همیشه حذف می‌شود.',
    'footer.faq': 'سؤالات متداول',
    
    // Broker Dashboard
    'broker.dashboard_title': 'داشبورد مشاور',
    'broker.dashboard_subtitle': 'مدیریت املاک و تعاملات مشتریان',
    'broker.my_properties': 'املاک من',
    'broker.my_properties_desc': 'مشاهده و مدیریت آگهی‌های املاک',
    'broker.viewings': 'بازدیدهای ملک',
    'broker.viewings_desc': 'مدیریت بازدیدهای برنامه‌ریزی شده',
    'broker.contacts': 'پیام‌های مشتریان',
    'broker.contacts_desc': 'مشاهده و پاسخ به سؤالات مشتریان',
    'broker.analytics': 'تحلیل املاک',
    'broker.analytics_desc': 'پیگیری بازدیدها و تعامل در آگهی‌هایتان',
    'broker.subscription': 'اشتراک',
    'broker.subscription_desc': 'مدیریت طرح و محدودیت آگهی‌ها',
    'broker.access_denied': 'این صفحه فقط برای مشاوران ثبت‌شده در دسترس است.',
    'broker.profile_title': 'پروفایل مشاور',
    'broker.profile_description': 'مدیریت پروفایل عمومی و اطلاعات تماس خود',
    'broker.back_to_dashboard': 'بازگشت به داشبورد',
    'broker.display_name': 'نام نمایشی',
    'broker.display_name_placeholder': 'نام شما همانطور که به مشتریان نشان داده می‌شود',
    'broker.contact_email': 'ایمیل تماس',
    'broker.email_placeholder': 'your@email.com',
    'broker.email_notification_note': 'این ایمیل برای اعلان‌ها درباره بازدیدها و پیام‌های مشتریان استفاده می‌شود.',
    'broker.phone_number': 'شماره تلفن',
    'broker.phone_placeholder': '+964 xxx xxx xxxx',
    'broker.change_photo': 'تغییر عکس',
    'broker.save_profile': 'ذخیره پروفایل',
    'broker.profile_saved': 'پروفایل با موفقیت ذخیره شد',
    'broker.profile_save_error': 'ذخیره پروفایل ناموفق بود',
    'broker.profile_load_error': 'بارگذاری پروفایل ناموفق بود',
    'broker.avatar_uploaded': 'عکس با موفقیت آپلود شد',
    'broker.avatar_upload_error': 'آپلود عکس ناموفق بود',
    'broker.invalid_image_type': 'لطفاً یک فایل تصویر انتخاب کنید',
    'broker.image_too_large': 'تصویر باید کمتر از 2 مگابایت باشد',
    'common.login_required': 'لطفاً برای دسترسی به این صفحه وارد شوید.',
    
    // Broker Public Profile
    'broker.listing_usage': 'استفاده از آگهی',
    'broker.monthly_listing_limits': 'محدودیت آگهی ماهانه',
    'broker.listings_used': 'آگهی‌های استفاده شده',
    'broker.listings_remaining': 'آگهی باقی مانده',
    'broker.period_resets': 'دوره تمدید می‌شود',
    'broker.export_csv': 'خروجی CSV',
    'broker.total_views': 'کل بازدیدها',
    'broker.scheduled_viewings': 'بازدیدهای برنامه‌ریزی شده',
    'broker.client_messages': 'پیام‌های مشتریان',
    'broker.filters': 'فیلترها',
    'broker.all_time': 'همه زمان‌ها',
    'broker.active_listings': 'آگهی‌های فعال',
    'broker.sold_listings': 'آگهی‌های فروخته شده',
    'broker.active_listings_desc': 'املاک منتشر شده شما',
    'broker.no_active_listings': 'هنوز آگهی فعالی ندارید. اولین ملک خود را اضافه کنید.',
    'broker.all_types': 'همه انواع',
    'broker.all_cities': 'همه شهرها',
    'broker.property_type': 'نوع ملک',
    'broker.city': 'شهر',
    'broker.date_range': 'بازه زمانی',
    'broker.last_week': 'هفته گذشته',
    'broker.last_month': 'ماه گذشته',
    'broker.last_quarter': '۳ ماه گذشته',
    'broker.sold_listings_desc': 'املاکی که فروخته‌اید',
    'broker.no_sold_listings': 'هنوز ملکی فروخته نشده',
    'broker.clear_filters': 'پاک کردن فیلترها',
    'broker.selected': 'انتخاب شده',
    'broker.compare': 'مقایسه',
    'broker.mark_as_sold': 'علامت‌گذاری به عنوان فروخته شده',
    'broker.delete_selected': 'حذف انتخاب شده‌ها',
    'broker.select_all': 'انتخاب همه',
    'broker.rejected_listings': 'آگهی‌های رد شده',
    'broker.rejected_listings_desc': 'این آگهی‌ها توسط مدیران رد شده‌اند. می‌توانید درخواست تجدید نظر ارسال کنید.',
    'broker.no_rejected_listings': 'هیچ آگهی رد شده‌ای وجود ندارد',
    'broker.confirm_delete': 'تایید حذف',
    'broker_public.not_found': 'مشاور پیدا نشد',
    'broker_public.not_found_description': 'پروفایل مشاوری که به دنبال آن هستید وجود ندارد.',
    'broker_public.back_to_search': 'بازگشت به جستجوی مشاور',
    'broker_public.contact_broker': 'تماس با مشاور',
    'broker_public.agency': 'آژانس',
    'broker_public.visit_website': 'بازدید از وبسایت',
    'broker_public.active_listings': 'آگهی‌های فعال',
    'broker_public.sold_properties': 'املاک فروخته شده',
    'broker_public.total_sales': 'کل فروش',
    'broker_public.avg_days_market': 'میانگین روز در بازار',
    'broker_public.no_active_listings': 'در حال حاضر آگهی فعالی وجود ندارد',
    'broker_public.no_sold_properties': 'هنوز ملکی فروخته نشده',
    'broker_public.view_all_properties': 'مشاهده همه املاک',
    'broker_public.send_message': 'ارسال پیام',
    'broker_public.view_agency': 'مشاهده آژانس',
    'broker_public.works_at': 'کار می‌کند در',
    'broker_public.listings': 'آگهی‌ها',
    'broker_public.sold': 'فروخته شده',
    'broker_public.reviews': 'نظرات',
    'broker_public.rating': 'امتیاز',
    'broker_public.featured_broker': 'مشاور ویژه',
    'broker_public.top_performer': 'بهترین عملکرد',
    
    // Agency Dashboard
    'agency.my_agency': 'آژانس من',
    'agency.dashboard_title': 'داشبورد آژانس',
    'agency.dashboard_subtitle': 'مدیریت مشاوران و تنظیمات آژانس',
    'agency.brokers_tab': 'مشاوران',
    'agency.invites_tab': 'دعوت‌ها',
    'agency.settings_tab': 'تنظیمات',
    'agency.brokers_title': 'مشاوران آژانس',
    'agency.brokers_description': 'مدیریت مشاوران در آژانس شما',
    'agency.create_broker': 'ایجاد مشاور',
    'agency.create_broker_title': 'ایجاد حساب مشاور جدید',
    'agency.create_broker_description': 'ایجاد حساب مشاور جدید که مستقیماً به آژانس شما متصل است',
    'agency.broker_name': 'نام کامل',
    'agency.broker_email': 'ایمیل',
    'agency.broker_password': 'رمز عبور',
    'agency.create_broker_btn': 'ایجاد حساب مشاور',
    'agency.broker_created': 'حساب مشاور با موفقیت ایجاد شد',
    'agency.broker_create_error': 'ایجاد حساب مشاور ناموفق بود',
    'agency.no_brokers': 'هنوز مشاوری نیست',
    'agency.no_brokers_description': 'حساب‌های مشاور ایجاد کنید یا دعوت‌نامه بفرستید',
    'agency.broker_removed': 'مشاور از آژانس حذف شد',
    'agency.broker_remove_error': 'حذف مشاور ناموفق بود',
    'agency.invites_title': 'دعوت‌های در انتظار',
    'agency.invites_description': 'مدیریت دعوت‌نامه‌ها برای مشاوران احتمالی',
    'agency.send_invite': 'ارسال دعوت',
    'agency.send_invite_title': 'دعوت از مشاور',
    'agency.send_invite_description': 'دعوت‌نامه برای پیوستن به آژانس بفرستید',
    'agency.invite_email': 'ایمیل',
    'agency.send_invite_btn': 'ارسال دعوت‌نامه',
    'agency.invite_sent': 'دعوت‌نامه با موفقیت ارسال شد',
    'agency.invite_error': 'ارسال دعوت‌نامه ناموفق بود',
    'agency.link_copied': 'لینک دعوت کپی شد',
    'agency.no_invites': 'دعوت در انتظاری نیست',
    'agency.settings_title': 'تنظیمات آژانس',
    'agency.settings_description': 'به‌روزرسانی اطلاعات آژانس',
    'agency.settings_saved': 'تنظیمات آژانس ذخیره شد',
    'agency.settings_save_error': 'ذخیره تنظیمات ناموفق بود',
    'agency.load_error': 'بارگذاری داده‌های آژانس ناموفق بود',
    'agency_invite.title': 'پیوستن به آژانس',
    'agency_invite.description': 'شما دعوت شده‌اید به {agencyName} بپیوندید',
    'agency_invite.accept_btn': 'قبول دعوت',
    'agency_invite.accepted_success': 'با موفقیت به آژانس پیوستید!',
    'agency_invite.accept_error': 'قبول دعوت ناموفق بود',
    'agency_invite.create_account': 'ایجاد حساب',
    'agency_invite.invalid_link': 'لینک دعوت نامعتبر است',
    'agency_invite.expired': 'این دعوت‌نامه منقضی شده است',
    'agency_invite.already_accepted': 'این دعوت‌نامه قبلاً پذیرفته شده است',
    'agency_invite.fetch_error': 'بارگذاری دعوت‌نامه ناموفق بود',
    'agency_invite.error_title': 'دعوت‌نامه نامعتبر',
    'agency_invite.back_home': 'بازگشت به خانه',
    'agency_invite.create_account_description': 'برای پذیرش دعوت‌نامه یک حساب ایجاد کنید',
    'agency_invite.your_name': 'نام شما',
    'agency_invite.name_placeholder': 'نام کامل خود را وارد کنید',
    'agency_invite.email_placeholder': 'email@شما.com',
    'agency_invite.password': 'رمز عبور',
    'agency_invite.password_placeholder': 'یک رمز عبور ایجاد کنید',
    'agency_invite.confirm_password': 'تأیید رمز عبور',
    'agency_invite.confirm_password_placeholder': 'رمز عبور خود را تأیید کنید',
    'agency_invite.register_accept_btn': 'ایجاد حساب و پیوستن',
    'agency_invite.registered_success': 'حساب ایجاد شد و به آژانس پیوستید!',
    'agency_invite.register_error': 'ایجاد حساب ناموفق بود',
    'agency_invite.passwords_mismatch': 'رمزهای عبور مطابقت ندارند',
    'agency_invite.password_too_short': 'رمز عبور باید حداقل ۶ کاراکتر باشد',
    'agency_invite.already_have_account': 'قبلاً حساب دارید؟',
    'agency_invite.login_here': 'اینجا وارد شوید',
    'agency_invite.joining': 'در حال پیوستن به',
    'agency_invite.logged_in_as': 'وارد شده به عنوان',
    'agency.notifications_tab': 'اعلان‌ها',
    'agency.notifications_title': 'اعلان‌های ایمیل',
    'agency.notifications_description': 'انتخاب کنید کدام اعلان‌ها درباره فعالیت مشاوران دریافت کنید',
    'agency.notifications_saved': 'تنظیمات اعلان ذخیره شد',
    'agency.notifications_save_error': 'ذخیره تنظیمات ناموفق بود',
    'agency.notif_new_broker_joins': 'مشاور جدید می‌پیوندد',
    'agency.notif_new_broker_joins_desc': 'وقتی مشاوری دعوت شما را می‌پذیرد مطلع شوید',
    'agency.notif_broker_property_added': 'مشاور ملک اضافه می‌کند',
    'agency.notif_broker_property_added_desc': 'وقتی مشاوران املاک جدید اضافه می‌کنند مطلع شوید',
    'agency.notif_broker_viewing_requests': 'درخواست‌های بازدید',
    'agency.notif_broker_viewing_requests_desc': 'درباره درخواست‌های بازدید برای املاک مشاور مطلع شوید',
    'agency.notif_broker_client_messages': 'پیام‌های مشتری',
    'agency.notif_broker_client_messages_desc': 'وقتی مشتریان با مشاوران شما تماس می‌گیرند مطلع شوید',
    'agency.notif_weekly_summary': 'خلاصه هفتگی',
    'agency.notif_weekly_summary_desc': 'خلاصه هفتگی فعالیت آژانس دریافت کنید',
    'agency.analytics_tab': 'تحلیل‌ها',
    'agency.no_brokers_analytics': 'مشاوران را اضافه کنید تا تحلیل عملکرد را ببینید',
    'agency.total_properties': 'کل املاک',
    'agency.total_views': 'کل بازدیدها',
    'agency.total_viewings': 'درخواست‌های بازدید',
    'agency.total_sold': 'املاک فروخته شده',
    'agency.total_sales_value': 'ارزش کل فروش',
    'agency.broker_performance_chart': 'عملکرد مشاوران',
    'agency.broker_performance_chart_desc': 'مقایسه آمار مشاوران در یک نگاه',
    'agency.individual_broker_stats': 'آمار مشاوران فردی',
    'agency.individual_broker_stats_desc': 'معیارهای دقیق برای هر مشاور',
    'agency.properties': 'املاک',
    'agency.views': 'بازدیدها',
    'agency.viewings': 'بازدیدها',
    'agency.contacts': 'تماس‌ها',
    'agency.active': 'فعال',
    'agency.sold': 'فروخته شده',
    'agency.sales_value': 'ارزش فروش',
    'agency.upgrade_plan': 'ارتقای طرح',
    'agency.view_plans': 'مشاهده طرح‌ها',
    'agency.billing_tab': 'صورتحساب',
    'agency.name_label': 'نام آژانس',
    'agency.email_label': 'ایمیل',
    'agency.phone_label': 'تلفن',
    'agency.website_label': 'وب‌سایت',
    'agency.address_label': 'آدرس',
    'agency.city_label': 'شهر',
    'agency.description_label': 'توضیحات',
    'agency.description_placeholder': 'به مشتریان درباره آژانس خود بگویید...',
    'agency.save_settings': 'ذخیره تنظیمات',
    'agency.broker_usage': 'استفاده کارگزار',
    'agency.invite_broker': 'دعوت کارگزار',
    'agency.brokers': 'کارگزار',
    'agency.broker_limit_reached': 'محدودیت کارگزار تمام شد',
    'agency.upgrade_for_more': 'طرح خود را ارتقا دهید تا کارگزاران بیشتری اضافه کنید',
    'agency.active_listings': 'لیست‌های فعال',
    'agency.broker_slots_remaining': 'جای باقیمانده',
    'agency.manage_subscription': 'مدیریت اشتراک',
    'agency.upgrade_your_plan': 'طرح خود را ارتقا دهید',
    'agency.choose_plan': 'طرحی را انتخاب کنید که بهترین برای آژانس شماست',
    'agency.pro_plan': 'حرفه‌ای',
    'agency.enterprise_plan': 'سازمانی',
    'agency.for_growing_agencies': 'برای آژانس‌های در حال رشد',
    'agency.for_large_agencies': 'برای آژانس‌های بزرگ',
    'agency.per_month': '/ماه',
    'agency.up_to_brokers': 'تا {count} کارگزار',
    'agency.unlimited_brokers': 'کارگزاران نامحدود',
    'agency.advanced_analytics': 'تحلیل پیشرفته',
    'agency.full_analytics': 'مجموعه تحلیل کامل',
    'agency.priority_support': 'پشتیبانی اولویت‌دار',
    'agency.dedicated_support': 'پشتیبانی اختصاصی',
    'agency.upgrade_to_pro': 'ارتقا به حرفه‌ای',
    'agency.upgrade_to_enterprise': 'ارتقا به سازمانی',
    'agency.popular': 'محبوب',
    'agency.current_plan': 'طرح فعلی',
    'agency.plan_features': 'ویژگی‌های طرح',
    'agency.billing_history': 'تاریخچه صورتحساب',
    'agency.no_billing_history': 'هنوز تاریخچه صورتحساب وجود ندارد',
    'agency.subscription_billing': 'اشتراک و صورتحساب',
    'agency.manage_agency_subscription': 'مدیریت اشتراک آژانس شما',
    'agency.plan': 'طرح',
    'agency.basic_plan': 'پایه',
    'agency.broker_limit': 'محدودیت کارگزار',
    'agency.current_usage': 'استفاده فعلی',
    'agency.status': 'وضعیت',
    'agency.active_status': 'فعال',
    'agency.unlimited': 'نامحدود',
    'agency.feature': 'ویژگی',
    'agency.price': 'قیمت',
    'agency.free': 'رایگان',
    'agency.analytics': 'تجزیه و تحلیل',
    'agency.basic_analytics': 'پایه',
    'agency.advanced': 'پیشرفته',
    'agency.full_suite': 'مجموعه کامل',
    'agency.support': 'پشتیبانی',
    'agency.email_support': 'ایمیل',
    'agency.priority': 'اولویت',
    'agency.dedicated': 'اختصاصی',
    'agency_directory.title': 'آژانس‌های املاک',
    'agency_directory.subtitle': 'جستجو و یافتن آژانس‌های املاک معتبر',
    'agency_directory.search_placeholder': 'جستجوی آژانس‌ها...',
    'agency_directory.location_placeholder': 'فیلتر بر اساس موقعیت...',
    'agency_directory.agencies_found': 'آژانس یافت شد',
    'agency_directory.no_agencies': 'آژانسی یافت نشد',
    'agency_directory.no_agencies_description': 'معیارهای جستجو را تنظیم کنید',
    'agency_directory.brokers': 'مشاور',
    'agency_directory.view_agency': 'مشاهده آژانس',
    'agency_directory.not_found': 'آژانس یافت نشد',
    'agency_directory.back_to_directory': 'بازگشت به فهرست',
    'agency_directory.visit_website': 'بازدید از وب‌سایت',
    'agency_directory.our_brokers': 'مشاوران ما',
    'agency_directory.no_brokers_yet': 'هنوز مشاوری در این آژانس نیست',
    'agency_directory.agency_listings': 'آگهی‌های ملک',
    
    // User Listings
    'userListings.title': 'آگهی‌های من',
    'userListings.subtitle': 'مدیریت آگهی‌های ملک خود و ایجاد آگهی‌های جدید',
    'userListings.pricingTitle': 'قیمت‌گذاری آگهی',
    'userListings.pricingDesc': 'آگهی ملک خود را با قیمت‌گذاری ساده ما ایجاد کنید',
    'userListings.listingPrice': '10$ برای هر آگهی',
    'userListings.listingDuration': 'معتبر برای 1 ماه',
    'userListings.videoPrice': '+3$ برای ویدیو',
    'userListings.videoDesc': 'تور ویدیویی به آگهی خود اضافه کنید',
    'userListings.includeVideo': 'شامل تور ویدیویی',
    'userListings.createListing': 'ایجاد آگهی',
    'userListings.myListingsTitle': 'املاک شما',
    'userListings.noListings': 'هنوز آگهی ندارید',
    'userListings.noListingsDesc': 'اولین آگهی ملک خود را برای شروع ایجاد کنید',
    'userListings.createFirst': 'اولین آگهی خود را ایجاد کنید',
    'userListings.hasVideo': 'ویدیو',
    'userListings.renew': 'تمدید',
    'userListings.renewSuccess': 'آگهی تمدید شد!',
    'userListings.renewSuccessDesc': 'آگهی شما برای یک ماه دیگر تمدید شد',
    'userListings.paymentSuccess': 'پرداخت موفق!',
    'userListings.paymentSuccessDesc': 'آگهی شما اکنون فعال است',
    'userListings.paymentCancelled': 'پرداخت لغو شد',
    'userListings.paymentCancelledDesc': 'پرداخت شما لغو شد',
    'userListings.checkoutError': 'ایجاد پرداخت ناموفق بود',
    'userListings.renewError': 'تمدید آگهی ناموفق بود',
    'userListings.createDraft': 'ایجاد آگهی',
    'userListings.previewListing': 'پیش‌نمایش آگهی شما',
    'userListings.draft': 'پیش‌نویس',
    'userListings.drafts': 'پیش‌نویس‌ها',
    'userListings.draftSaved': 'پیش‌نویس ذخیره شد',
    'userListings.draftSavedDesc': 'آگهی شما به عنوان پیش‌نویس ذخیره شد',
    'userListings.draftError': 'ذخیره پیش‌نویس ناموفق بود',
    'userListings.draftDeleted': 'پیش‌نویس حذف شد',
    'userListings.deleteError': 'حذف ناموفق بود',
    'userListings.saveDraft': 'ذخیره پیش‌نویس',
    'userListings.saveAndPreview': 'ذخیره و پیش‌نمایش',
    'userListings.publishOptions': 'گزینه‌های انتشار',
    'userListings.addVideoTour': 'افزودن تور ویدیویی',
    'userListings.videoIncluded': 'تور ویدیویی شامل شده',
    'userListings.totalPrice': 'مجموع',
    'userListings.backToEdit': 'بازگشت به ویرایش',
    'userListings.publishNow': 'انتشار الان',
    'userListings.publish': 'انتشار',
    'userListings.publishSuccess': 'آگهی منتشر شد!',
    'userListings.publishSuccessDesc': 'آگهی شما اکنون در انتظار بررسی است',
    'userListings.firstListingFree': 'اولین آگهی رایگان!',
    'userListings.firstListingFreeDesc': 'اولین آگهی ملک شما کاملاً رایگان است. همین الان منتشر کنید و با خریداران سراسر عراق ارتباط برقرار کنید!',
    'userListings.pending': 'در انتظار بررسی',
    'userListings.pendingInfo': 'این آگهی‌ها توسط تیم بررسی ما در حال بررسی هستند. پس از تأیید به شما اطلاع داده می‌شود.',
    'userListings.pendingReview': 'در انتظار بررسی',
    'userListings.awaitingApproval': 'در انتظار تأیید مدیر',
    'userListings.rejected': 'آگهی‌های رد شده',
    'userListings.rejectedInfo': 'این آگهی‌ها تأیید نشدند. لطفاً ویرایش کنید و مشکلات را قبل از ارسال مجدد رفع کنید.',
    'userListings.rejectedBadge': 'رد شده',
    'userListings.live': 'فعال',
    'userListings.editDraft': 'ویرایش',
    'userListings.editAndResubmit': 'ویرایش و ارسال مجدد',
    'userListings.pendingCannotEdit': 'در حین بررسی امکان ویرایش نیست',
    'userListings.resubmitForReview': 'ارسال مجدد برای بررسی',
    'userListings.resubmitInfo': 'تغییرات شما ذخیره شد. آگهی خود را دوباره برای بررسی بدون هزینه اضافی ارسال کنید.',
    'userListings.submitForReview': 'ارسال برای بررسی',
    'userListings.validation.priceRequired': 'قیمت الزامی است',
    'userListings.validation.cityRequired': 'شهر الزامی است',
    'userListings.validation.roomsRequired': 'تعداد اتاق الزامی است',
    'userListings.validation.sizeRequired': 'اندازه الزامی است',
    'userListings.validation.plotSizeRequired': 'اندازه زمین الزامی است',
    'userListings.validation.imagesRequired': 'حداقل یک تصویر الزامی است',
    'userListings.choosePlan': 'پلان آگهی خود را انتخاب کنید',
    'userListings.choosePlanDesc': 'انتخاب کنید چند تصویر روی کارت آگهی نمایش داده شود',
    'userListings.upgradePlan': 'ارتقای پلان آگهی',
    'userListings.upgradePlanDesc': 'ارتقا دهید تا تصاویر بیشتری نمایش دهید. فقط تفاوت را پرداخت می‌کنید.',
    'userListings.selectImages': 'تصاویر برای نمایش انتخاب کنید',
    'userListings.selectImagesDesc': 'تصاویری را برای نمایش روی کارت آگهی انتخاب کنید',
    'userListings.selectPrimaryImage': 'تصویر اصلی را انتخاب کنید',
    'userListings.selectPrimaryImageDesc': 'تصویر اصلی را انتخاب کنید که اول روی آگهی نمایش داده شود',
    'userListings.addVideo': 'افزودن تور ویدیویی',
    'userListings.pleaseSelectImages': 'لطفاً تصاویر را برای ادامه انتخاب کنید',
    'userListings.pleaseSelectPrimaryImage': 'لطفاً تصویر اصلی را برای ادامه انتخاب کنید',
    'userListings.proceedToPayment': 'ادامه به پرداخت',
    'userListings.tierBasic': 'پایه',
    'userListings.tierStandard': 'استاندارد',
    'userListings.tierPremium': 'ویژه',
    'userListings.tierBasicDesc': '۱ تصویر روی کارت',
    'userListings.tierStandardDesc': '۳ تصویر اسلایدی',
    'userListings.tierPremiumDesc': 'همه تصاویر + نشان',
    'userListings.tierDuration': '۳۰ روز فعال',
    'userListings.tierBest': 'بهترین',
    'userListings.tierBasicFull': 'پلن پایه (۱ تصویر)',
    'userListings.tierStandardFull': 'پلن استاندارد (۳ تصویر)',
    'userListings.tierPremiumFull': 'پلن ویژه (همه تصاویر)',
    'userListings.changeTier': 'تغییر پلن',
    'userListings.selectImageTier': 'انتخاب پلن تصویر',
    'userListings.selectTierFirst': 'قبل از انتشار یک پلن انتخاب کنید',
    'userListings.upgrade': 'ارتقا',
    'common.total': 'مجموع',
    
    // Settings Page
    'settings.title': 'تنظیمات',
    'settings.subtitle': 'مدیریت تنظیمات و ترجیحات حساب شما',
    'settings.profile': 'پروفایل',
    'settings.notifications': 'اعلان‌ها',
    'settings.account': 'حساب',
    'settings.profile_info': 'اطلاعات پروفایل',
    'settings.profile_desc': 'به‌روزرسانی اطلاعات شخصی شما',
    'settings.change_photo': 'تغییر عکس',
    'settings.display_name': 'نام نمایشی',
    'settings.display_name_placeholder': 'نام شما',
    'settings.email': 'ایمیل',
    'settings.email_placeholder': 'your@email.com',
    'settings.phone': 'تلفن',
    'settings.phone_placeholder': '+98 912 345 6789',
    'settings.save': 'ذخیره تغییرات',
    'settings.saved': 'تنظیمات با موفقیت ذخیره شد',
    'settings.load_error': 'بارگذاری تنظیمات ناموفق بود',
    'settings.save_error': 'ذخیره تنظیمات ناموفق بود',
    'settings.invalid_image': 'لطفاً یک فایل تصویر انتخاب کنید',
    'settings.image_too_large': 'تصویر باید کمتر از 2MB باشد',
    'settings.avatar_uploaded': 'عکس آپلود شد',
    'settings.avatar_error': 'آپلود عکس ناموفق بود',
    'settings.notification_preferences': 'ترجیحات اعلان',
    'settings.notification_desc': 'انتخاب کنید کدام اعلان‌ها را می‌خواهید دریافت کنید',
    'settings.notif_property_alerts': 'هشدارهای ملک',
    'settings.notif_property_alerts_desc': 'درباره املاک جدید مطلع شوید',
    'settings.notif_saved_search': 'هشدارهای جستجوی ذخیره‌شده',
    'settings.notif_saved_search_desc': 'وقتی املاک جدید با جستجوهای ذخیره‌شده شما مطابقت دارند مطلع شوید',
    'settings.notif_newsletter': 'خبرنامه',
    'settings.notif_newsletter_desc': 'خبرنامه ما با به‌روزرسانی‌های بازار را دریافت کنید',
    'settings.notif_viewing': 'تأییدیه‌های بازدید',
    'settings.notif_viewing_desc': 'تأییدیه برای بازدیدهای برنامه‌ریزی‌شده دریافت کنید',
    'settings.account_settings': 'تنظیمات حساب',
    'settings.account_desc': 'مدیریت امنیت و ترجیحات حساب شما',
    'settings.change_password': 'تغییر رمز عبور',
    'settings.change_password_desc': 'به‌روزرسانی رمز عبور شما',
    'settings.change': 'تغییر',
    'settings.two_factor': 'احراز هویت دو عاملی',
    'settings.two_factor_desc': 'افزودن امنیت اضافی به حساب شما',
    'settings.enable': 'فعال‌سازی',
    'settings.delete_account': 'حذف حساب',
    'settings.delete_account_desc': 'حذف دائمی حساب شما',
    'settings.delete': 'حذف',
    'settings.logout': 'خروج',
    
    // Common
    'common.back': 'بازگشت',
    
    // Contact Form Modal
    'contact_form.title': 'تماس',
    'contact_form.description': 'برای برقراری ارتباط پیامی ارسال کنید.',
    'contact_form.name': 'نام',
    'contact_form.name_placeholder': 'نام شما',
    'contact_form.email': 'ایمیل',
    'contact_form.email_placeholder': 'your@email.com',
    'contact_form.phone': 'تلفن',
    'contact_form.phone_placeholder': 'شماره تلفن شما',
    'contact_form.message': 'پیام',
    'contact_form.message_placeholder': 'چگونه می‌توانیم کمک کنیم؟',
    'contact_form.send': 'ارسال پیام',
    'contact_form.sending': 'در حال ارسال...',
    'contact_form.success_title': 'پیام ارسال شد!',
    'contact_form.success_message': 'پیام شما ارسال شد. تأییدیه‌ای از طریق ایمیل دریافت خواهید کرد.',
    'contact_form.error_title': 'خطا',
    'contact_form.error_message': 'ارسال پیام ناموفق بود. لطفاً دوباره تلاش کنید.',
    
    // Messaging
    'messaging.title': 'پیام‌ها',
    'messaging.description': 'مکالمات خصوصی شما',
    'messaging.new_message': 'پیام جدید',
    'messaging.no_messages': 'هنوز پیامی وجود ندارد',
    'messaging.no_conversations': 'هنوز مکالمه‌ای وجود ندارد',
    'messaging.send': 'ارسال',
    'messaging.send_message': 'ارسال پیام',
    'messaging.message_to': 'ارسال پیام به',
    'messaging.subject': 'موضوع',
    'messaging.subject_placeholder': 'موضوع (اختیاری)',
    'messaging.message': 'پیام',
    'messaging.message_placeholder': 'پیام خود را بنویسید...',
    'messaging.login_required': 'برای ارسال پیام باید وارد شوید.',
    'messaging.message_sent': 'پیام با موفقیت ارسال شد',
    'messaging.contact_agent_description': 'مستقیماً با نماینده ملک تماس بگیرید',
    'messaging.type_message': 'پیامی بنویسید...',
    'messaging.inbox': 'صندوق ورودی',
    'messaging.sent': 'ارسال شده',
    'messaging.search': 'جستجو در مکالمات...',
    'messaging.select_conversation': 'یک مکالمه انتخاب کنید',
    'messaging.select_description': 'یک مکالمه از لیست انتخاب کنید تا پیام‌رسانی را شروع کنید',
    'messaging.encrypted': 'رمزنگاری سرتاسری',
    'messaging.decrypt': 'رمزگشایی پیام',
    'messaging.reply': 'پاسخ',
    'messaging.replying_to': 'پاسخ به',
    'messaging.replying_to_yourself': 'خودتان',
    'messaging.forward': 'ارسال مجدد',
    'messaging.delete': 'حذف',
    'messaging.delete_confirm': 'حذف پیام',
    'messaging.delete_description': 'این پیام برای همه حذف خواهد شد. این عمل قابل بازگشت نیست.',
    'messaging.delete_for_everyone': 'حذف برای همه',
    'messaging.pin': 'سنجاق',
    'messaging.unpin': 'برداشتن سنجاق',
    'messaging.pinned_messages': 'پیام‌های سنجاق شده',
    'messaging.no_pinned': 'پیام سنجاق شده‌ای وجود ندارد',
    'messaging.delivered': 'تحویل داده شد',
    'messaging.read': 'خوانده شده',
    'messaging.seen_at': 'دیده شده در',
    'messaging.voice_message': 'پیام صوتی',
    'messaging.search_messages': 'جستجو در پیام‌ها...',
    'messaging.contacts': 'مخاطبین',
    'messaging.results_found': 'نتیجه یافت شد',
    'messaging.recording': 'در حال ضبط...',
    'messaging.typing': 'در حال تایپ...',
    'messaging.online': 'آنلاین',
    'messaging.tap_for_info': 'برای اطلاعات تماس اینجا بزنید',
    'messaging.photos': 'عکس‌ها',
    'messaging.camera': 'دوربین',
    'messaging.location': 'موقعیت',
    'messaging.contact': 'مخاطب',
    'messaging.document': 'سند',
    'messaging.poll': 'نظرسنجی',
    'messaging.event': 'رویداد',
    'messaging.location_coming_soon': 'اشتراک موقعیت به زودی',
    'messaging.contact_coming_soon': 'اشتراک مخاطب به زودی',
    'messaging.poll_coming_soon': 'ایجاد نظرسنجی به زودی',
    'messaging.event_coming_soon': 'ایجاد رویداد به زودی',
    'messaging.schedule_event': 'برنامه‌ریزی رویداد',
    'messaging.event_title': 'عنوان رویداد',
    'messaging.event_title_placeholder': 'مثال: بازدید از ملک',
    'messaging.event_date': 'تاریخ',
    'messaging.event_time': 'زمان',
    'messaging.event_location': 'مکان',
    'messaging.event_location_placeholder': 'مکان یا آدرس را وارد کنید',
    'messaging.event_description': 'توضیحات',
    'messaging.event_description_placeholder': 'جزئیات بیشتری اضافه کنید...',
    'messaging.send_event': 'ارسال رویداد',
    'messaging.event_created': 'رویداد ایجاد شد',
    'messaging.create_poll': 'ایجاد نظرسنجی',
    'messaging.poll_question': 'سؤال',
    'messaging.poll_question_placeholder': 'می‌خواهید چه بپرسید؟',
    'messaging.poll_options': 'گزینه‌ها',
    'messaging.option': 'گزینه',
    'messaging.add_option': 'افزودن گزینه',
    'messaging.allow_multiple': 'اجازه پاسخ‌های چندگانه',
    'messaging.send_poll': 'ارسال نظرسنجی',
    'messaging.poll_created': 'نظرسنجی ایجاد شد',
    'messaging.edit': 'ویرایش',
    'messaging.edit_message': 'ویرایش پیام',
    'messaging.edit_description': 'تغییرات خود را در پیام اعمال کنید',
    'messaging.edit_time_remaining': 'شما',
    'messaging.minutes_to_edit': 'دقیقه برای ویرایش این پیام دارید',
    'messaging.message_edited': 'پیام ویرایش شد',
    'messaging.edit_failed': 'ویرایش پیام ناموفق بود',
    'messaging.from_date': 'از',
    'messaging.to_date': 'تا',
    'messaging.clear_filters': 'پاک کردن فیلترها',
    'messaging.export_chat': 'خروجی گفتگو',
    'messaging.export_description': 'خروجی تاریخچه گفتگو',
    'messaging.export_text': 'فایل متنی (.txt)',
    'messaging.export_text_desc': 'فرمت متنی ساده، آسان برای خواندن و اشتراک‌گذاری',
    'messaging.export_pdf': 'سند PDF',
    'messaging.export_pdf_desc': 'سند فرمت‌شده، مناسب برای چاپ',
    'messaging.export_messages_count': 'پیام صادر خواهد شد',
    'messaging.exporting': 'در حال صادرات...',
    'messaging.export': 'صادرات',
    'messaging.export_success': 'گفتگو با موفقیت صادر شد',
    'messaging.export_failed': 'صادرات گفتگو ناموفق بود',
    'messaging.is_typing': 'در حال نوشتن...',
    
    // Quick Reply Templates
    'quick_reply.title': 'پاسخ‌های سریع',
    'quick_reply.greeting': 'سلام! ممنون که تماس گرفتید.',
    'quick_reply.interested': 'من به این ملک علاقه‌مندم. آیا می‌توانید اطلاعات بیشتری ارائه دهید؟',
    'quick_reply.schedule_viewing': 'می‌خواهم یک بازدید تنظیم کنم. چه زمان‌هایی در دسترس است؟',
    'quick_reply.ask_price': 'آیا قیمت قابل مذاکره است؟',
    'quick_reply.ask_availability': 'آیا این ملک هنوز موجود است؟',
    'quick_reply.thank_you': 'ممنون از اطلاعات. به زودی با شما تماس می‌گیرم.',
    'quick_reply.will_contact': 'به زودی با جزئیات بیشتر با شما تماس می‌گیرم.',
    'quick_reply.not_interested': 'ممنون، اما دیگر به این ملک علاقه‌مند نیستم.',
    
    // Notifications
    'notifications.enable': 'فعال کردن اعلان‌ها',
    'notifications.on': 'اعلان‌ها فعال',
    'notifications.preferences': 'تنظیمات اعلان',
    'notifications.sound': 'صدای اعلان',
    'notifications.sound_desc': 'پخش صدا هنگام دریافت پیام',
    'notifications.sound_default': 'پیش‌فرض',
    'notifications.sound_chime': 'زنگ',
    'notifications.sound_bell': 'زنگوله',
    'notifications.sound_pop': 'پاپ',
    'notifications.test': 'تست',
    'notifications.vibration': 'لرزش',
    'notifications.vibration_desc': 'لرزش هنگام دریافت پیام (فقط موبایل)',
    'messaging.you': 'شما',
    'messaging.encrypted_message': 'پیام رمزگذاری شده',
    'messaging.e2e_notice': 'پیام‌ها و تماس‌ها رمزنگاری سرتاسری دارند. فقط افرادی که در این گفتگو هستند می‌توانند آنها را بخوانند، گوش دهند یا به اشتراک بگذارند.',
    'messaging.today': 'امروز',
    'messaging.yesterday': 'دیروز',
    'messaging.search_groups': 'جستجو در گروه‌ها...',
    
    // Attachment viewer
    'attachment.all_media': 'همه رسانه‌ها',
    'attachment.go_to_message': 'رفتن به پیام',
    'attachment.download': 'دانلود',
    'attachment.view_fullscreen': 'مشاهده تمام صفحه',
    'attachment.save_to_gallery': 'ذخیره در گالری',
    'attachment.share': 'اشتراک‌گذاری',
    'attachment.double_tap_zoom': 'دوبار ضربه برای بزرگنمایی',
    'attachment.zoom_in': 'بزرگنمایی',
    'attachment.zoom_out': 'کوچکنمایی',
    'attachment.reset_zoom': 'بازنشانی بزرگنمایی',
    'attachment.ctrl_scroll_zoom': 'Ctrl + اسکرول برای بزرگنمایی',
    'attachment.fit_width': 'تناسب با عرض',
    'attachment.fit_height': 'تناسب با ارتفاع',
    'attachment.click_to_navigate': 'برای ناوبری کلیک کنید',
    
    // Status
    'status.online': 'آنلاین',
    'status.offline': 'آفلاین',
    'status.last_seen': 'آخرین بازدید',
    
    // Blocking
    'blocking.block_user': 'مسدود کردن کاربر',
    'blocking.block_confirm': 'آیا مطمئن هستید که می‌خواهید {name} را مسدود کنید؟ نمی‌توانند برای شما پیام ارسال کنند.',
    'blocking.reason_optional': 'دلیل (اختیاری)',
    'blocking.reason_placeholder': 'چرا این کاربر را مسدود می‌کنید؟',
    'blocking.block': 'مسدود کردن',
    'blocking.user_blocked': 'کاربر با موفقیت مسدود شد',
    'blocking.already_blocked': 'کاربر قبلاً مسدود شده است',
    'blocking.error': 'مسدود کردن کاربر ناموفق بود',
    'blocking.blocked_users': 'کاربران مسدود شده',
    'blocking.blocked_users_description': 'مدیریت لیست کاربران مسدود شده',
    'blocking.no_blocked_users': 'شما کسی را مسدود نکرده‌اید',
    'blocking.blocked_on': 'در {date} مسدود شده',
    'blocking.unblock': 'رفع مسدودیت',
    'blocking.user_unblocked': 'مسدودیت کاربر رفع شد',
    'blocking.unblock_error': 'رفع مسدودیت کاربر ناموفق بود',
    'blocking.fetch_error': 'بارگذاری کاربران مسدود شده ناموفق بود',
    'blocking.you_blocked_user': 'شما این کاربر را مسدود کرده‌اید. برای ارسال پیام مسدودیت را رفع کنید.',
    'blocking.user_blocked_you': 'این کاربر شما را مسدود کرده است. نمی‌توانید پیام ارسال کنید.',
    
    // Reporting
    'reporting.report_user': 'گزارش کاربر',
    'reporting.report_description': 'گزارش {name} برای نقض دستورالعمل‌های ما',
    'reporting.select_reason': 'لطفاً یک دلیل انتخاب کنید',
    'reporting.reason': 'دلیل گزارش',
    'reporting.additional_details': 'جزئیات بیشتر',
    'reporting.details_placeholder': 'توضیحات بیشتری ارائه دهید...',
    'reporting.submit_report': 'ارسال گزارش',
    'reporting.report_submitted': 'گزارش ارسال شد. به زودی بررسی خواهیم کرد.',
    'reporting.error': 'ارسال گزارش ناموفق بود',
    'reporting.reasons.spam': 'اسپم یا پیام‌های ناخواسته',
    'reporting.reasons.harassment': 'آزار و اذیت یا قلدری',
    'reporting.reasons.inappropriate_content': 'محتوای نامناسب',
    'reporting.reasons.fraud': 'کلاهبرداری یا فریب',
    'reporting.reasons.impersonation': 'جعل هویت',
    'reporting.reasons.other': 'سایر',
    
    // Admin Reports
    'admin.reports': 'گزارش‌ها',
    'admin.reports_panel': 'گزارش‌های کاربران',
    'admin.reports_description': 'بررسی و حل گزارش‌های کاربران',
    'admin.fetch_reports_error': 'دریافت گزارش‌ها ناموفق بود',
    'admin.pending': 'در انتظار',
    'admin.resolved': 'حل شده',
    'admin.all': 'همه',
    'admin.no_reports': 'گزارشی یافت نشد',
    'admin.back_to_list': 'بازگشت به لیست',
    'admin.reporter': 'گزارش‌دهنده',
    'admin.reported_user': 'کاربر گزارش شده',
    'admin.reason': 'دلیل',
    'admin.reported_on': 'تاریخ گزارش',
    'admin.reported_by': 'گزارش شده توسط',
    'admin.resolution_notes': 'یادداشت‌های حل را اضافه کنید...',
    'admin.dismiss': 'رد کردن',
    'admin.warn': 'هشدار به کاربر',
    'admin.block': 'مسدود کردن کاربر',
    'admin.report_resolved': 'گزارش با موفقیت حل شد',
    'admin.resolve_error': 'حل گزارش ناموفق بود',
    'admin.resolution': 'حل',
    'messaging.unknown_user': 'کاربر ناشناس',
    'messaging.no_groups': 'هنوز گروهی نیست',
    'messaging.create_group': 'ایجاد گروه',
    'messaging.group_chat': 'چت گروهی',
    'messaging.direct_messages': 'مستقیم',
    'messaging.groups': 'گروه‌ها',
    'messaging.group_name': 'نام گروه',
    'messaging.group_description': 'توضیحات',
    
    // Broker Pricing
    'broker_pricing.badge': 'طرح‌های مشاور',
    'broker_pricing.title': 'ارتقای حساب مشاور',
    'broker_pricing.subtitle': 'طرح مناسب برای کسب‌وکار املاک خود را انتخاب کنید. آگهی‌های بیشتر، تحلیل بهتر و پشتیبانی اولویت‌دار.',
    'broker_pricing.most_popular': 'محبوب‌ترین',
    'broker_pricing.current_plan': 'طرح فعلی',
    'broker_pricing.your_current_plan': 'طرح فعلی شما',
    'broker_pricing.free': 'رایگان',
    'broker_pricing.month': 'ماه',
    'broker_pricing.per_month': '/ماه',
    'broker_pricing.processing': 'در حال پردازش...',
    'broker_pricing.loading': 'در حال بارگذاری...',
    'broker_pricing.manage_subscription': 'مدیریت اشتراک',
    'broker_pricing.cancel_anytime': 'کاهش رتبه یا لغو در هر زمان • تمدید خودکار ماهانه',
    'broker_pricing.manage_note': 'کاهش رتبه یا لغو در هر زمان • تمدید خودکار ماهانه',
    'broker_pricing.downgrade': 'کاهش رتبه',
    'broker_pricing.upgrade_to_pro': 'ارتقا به حرفه‌ای',
    'broker_pricing.go_enterprise': 'انتخاب سازمانی',
    'broker_pricing.get_started_pro': 'شروع با حرفه‌ای',
    'broker_pricing.comparison_title': 'مقایسه دقیق ویژگی‌ها',
    'broker_pricing.feature': 'ویژگی',
    'broker_pricing.faq_title': 'سوالات متداول',
    'broker_pricing.cta_title': 'آماده رشد کسب‌وکار خود هستید؟',
    'broker_pricing.cta_subtitle': 'به هزاران مشاور موفق بپیوندید که از پلتفرم ما برای ثبت آگهی‌های بیشتر و بستن معاملات بیشتر استفاده می‌کنند.',
    'broker_pricing.cta_button': 'شروع با حرفه‌ای',
    'broker_pricing.sign_in_required': 'لطفاً برای ارتقای طرح وارد شوید',
    'broker_pricing.already_on_plan': 'شما در حال حاضر در این طرح هستید',
    'broker_pricing.checkout_error': 'شروع پرداخت ناموفق بود',
    'broker_pricing.portal_error': 'باز کردن مدیریت اشتراک ناموفق بود',
    'broker_pricing.tier.normal': 'عادی',
    'broker_pricing.tier.normal_desc': 'مناسب برای مشاوران فردی تازه‌کار',
    'broker_pricing.tier.normal_cta': 'طرح فعلی',
    'broker_pricing.tier.pro': 'حرفه‌ای',
    'broker_pricing.tier.pro_desc': 'برای مشاوران در حال رشد با آگهی‌های بیشتر',
    'broker_pricing.tier.pro_cta': 'ارتقا به حرفه‌ای',
    'broker_pricing.tier.enterprise': 'سازمانی',
    'broker_pricing.tier.enterprise_desc': 'برای مشاوران پرحجم با نیازهای نامحدود',
    'broker_pricing.tier.enterprise_cta': 'انتخاب سازمانی',
    'broker_pricing.feature.listings_5': '۵ آگهی در ماه',
    'broker_pricing.feature.listings_10': '۱۰ آگهی در ماه',
    'broker_pricing.feature.listings_15': '۱۵ آگهی در ماه',
    'broker_pricing.feature.basic_features': 'ویژگی‌های پایه ملک',
    'broker_pricing.feature.advanced_features': 'ویژگی‌های پیشرفته ملک',
    'broker_pricing.feature.premium_features': 'ویژگی‌های ویژه ملک',
    'broker_pricing.feature.client_messaging': 'پیام‌رسانی با مشتری',
    'broker_pricing.feature.viewing_scheduling': 'زمان‌بندی بازدید',
    'broker_pricing.feature.video_tours_paid': 'تورهای ویدیویی ($۳ به ازای هر آگهی)',
    'broker_pricing.feature.video_tours_free': 'تورهای ویدیویی رایگان',
    'broker_pricing.feature.analytics': 'داشبورد تحلیلی',
    'broker_pricing.feature.advanced_analytics': 'تحلیل پیشرفته',
    'broker_pricing.feature.priority_support': 'پشتیبانی اولویت‌دار',
    'broker_pricing.feature.support_24_7': 'پشتیبانی ۲۴/۷',
    'broker_pricing.feature.featured_listings': 'آگهی‌های ویژه',
    'broker_pricing.feature.extra_listings': 'گزینه آگهی اضافی',
    'broker_pricing.feature.extra_10': '+$۱۰ به ازای هر آگهی اضافی',
    'broker_pricing.feature.messaging': 'پیام‌رسانی با مشتری',
    'broker_pricing.feature.scheduling': 'زمان‌بندی بازدید',
    'broker_pricing.feature.video_paid': 'تورهای ویدیویی ($۳ به ازای هر آگهی)',
    'broker_pricing.feature.video_free': 'تورهای ویدیویی رایگان',
    'broker_pricing.feature.analytics_advanced': 'تحلیل پیشرفته',
    'broker_pricing.feature.support': 'پشتیبانی اولویت‌دار',
    'broker_pricing.feature.featured': 'آگهی‌های ویژه',
    'broker_pricing.feature.extra_price': '+$۱۰ به ازای هر آگهی اضافی',
    'broker_pricing.table.listings_per_month': 'آگهی در ماه',
    'broker_pricing.table.video_tours': 'تورهای ویدیویی',
    'broker_pricing.table.extra_listings': 'آگهی‌های اضافی',
    'broker_pricing.table.listing_duration': 'مدت آگهی',
    'broker_pricing.table.client_messaging': 'پیام‌رسانی با مشتری',
    'broker_pricing.table.analytics_dashboard': 'داشبورد تحلیلی',
    'broker_pricing.table.priority_support': 'پشتیبانی اولویت‌دار',
    'broker_pricing.table.featured_listings': 'آگهی‌های ویژه',
    'broker_pricing.table.15_extras': '۱۵ + اضافی',
    'broker_pricing.table.3_per_listing': '$۳ به ازای هر آگهی',
    'broker_pricing.table.10_each': '$۱۰ هرکدام',
    'broker_pricing.table.30_days': '۳۰ روز',
    'broker_pricing.comparison.listings': 'آگهی در ماه',
    'broker_pricing.comparison.video': 'تورهای ویدیویی',
    'broker_pricing.comparison.extra': 'آگهی‌های اضافی',
    'broker_pricing.comparison.duration': 'مدت آگهی',
    'broker_pricing.comparison.featured': 'آگهی‌های ویژه',
    'broker_pricing.comparison.30_days': '۳۰ روز',
    'broker_pricing.comparison.per_listing': '$۳ به ازای هر آگهی',
    'broker_pricing.comparison.free': 'رایگان',
    'broker_pricing.comparison.each': '$۱۰ هرکدام',
    'broker_pricing.comparison.15_plus': '۱۵ + اضافی',
    'broker_pricing.faq.autorenew_q': 'آیا اشتراک من به‌صورت خودکار تمدید می‌شود؟',
    'broker_pricing.faq.autorenew_a': 'بله! اشتراک شما هر ماه به‌صورت خودکار تمدید می‌شود تا هرگز دسترسی به آگهی‌های خود را از دست ندهید. همان روز هر ماه شارژ می‌شوید. هر زمان از پورتال مدیریت اشتراک می‌توانید لغو کنید.',
    'broker_pricing.faq.upgrade_q': 'آیا هر زمان می‌توانم ارتقا یا کاهش رتبه دهم؟',
    'broker_pricing.faq.upgrade_a': 'قطعاً! ارتقاها فوراً با صورتحساب نسبی اعمال می‌شوند. کاهش رتبه در پایان دوره صورتحساب فعلی اعمال می‌شود—تا آن زمان همه ویژگی‌ها را دارید. روی "مدیریت اشتراک" کلیک کنید.',
    'broker_pricing.faq.expire_q': 'وقتی آگهی‌هایم منقضی شوند چه می‌شود؟',
    'broker_pricing.faq.expire_a': 'آگهی‌ها پس از ۳۰ روز منقضی می‌شوند. ۳ روز قبل از انقضا ایمیل اعلان دریافت می‌کنید. می‌توانید با استفاده از سهمیه رایگان ماهانه یا پرداخت $۱۰ برای هر آگهی اضافی (فقط سازمانی) تمدید کنید.',
    'broker_pricing.faq.extra_q': 'آگهی‌های اضافی چیست؟',
    'broker_pricing.faq.extra_a': 'اعضای طرح سازمانی می‌توانند آگهی‌های اضافی به قیمت $۱۰ هرکدام فراتر از ۱۵ آگهی رایگان ماهانه خریداری کنند. این برای ماه‌های پرحجم عالی است.',
    'broker_pricing.faq.cancel_q': 'چگونه اشتراک خود را لغو کنم؟',
    'broker_pricing.faq.cancel_a': 'روی "مدیریت اشتراک" کلیک کنید تا به پورتال صورتحساب دسترسی پیدا کنید و بتوانید لغو، کاهش رتبه یا روش پرداخت را به‌روز کنید. لغوها در پایان دوره صورتحساب اعمال می‌شوند.',
    'messaging.conversation_pinned': 'مکالمه سنجاق شد',
    'messaging.conversation_unpinned': 'سنجاق مکالمه برداشته شد',
    'messaging.pin_chat': 'سنجاق کردن مکالمه',
    'messaging.unpin_chat': 'برداشتن سنجاق مکالمه',
    'messaging.pin_failed': 'سنجاق کردن مکالمه ناموفق بود',
    'messaging.unpin_failed': 'برداشتن سنجاق مکالمه ناموفق بود',
    'messaging.archive': 'بایگانی',
    'messaging.unarchive': 'بازیابی',
    'messaging.show_archived': 'بایگانی شده',
    'messaging.show_active': 'نمایش فعال‌ها',
    'messaging.conversation_archived': 'مکالمه بایگانی شد',
    'messaging.conversation_unarchived': 'مکالمه بازیابی شد',
    'messaging.archive_failed': 'بایگانی مکالمه ناموفق بود',
    'messaging.unarchive_failed': 'بازیابی مکالمه ناموفق بود',
  },
  de: {
    // Header
    'nav.search': 'Immobilien suchen',
    'nav.search_agents': 'Makler suchen',
    'nav.sell': 'Immobilie verkaufen',
    'nav.news': 'Nachrichten',
    'nav.build': 'Haus bauen',
    'nav.agencies': 'Agenturen',
    'nav.buy': 'Kaufen',
    'nav.valuation': 'Bewertung',
    'nav.more': 'Mehr',
    'nav.saved': 'Gespeichert',
    'nav.admin': 'Admin',
    'nav.agency': 'Agentur',
    'nav.agency_dashboard': 'Agentur-Dashboard',
    'nav.location_manager': 'Standortverwaltung',
    'nav.map_configuration': 'Kartenkonfiguration',
    'nav.hello': 'Hallo',
    'nav.menu': 'Menü',
    'nav.manage_news': 'Nachrichten verwalten',
    'nav.all_properties': 'Alle Immobilien',
    'nav.new_production': 'Neubau',
    
    // Cookie Policy
    'cookie.title': 'Cookie-Richtlinie',
    'cookie.last_updated': 'Zuletzt aktualisiert:',
    'cookie.section1.title': 'Abschnitt 1 - Allgemeine Bestimmungen zur Implementierung von Tracking-Technologien',
    'cookie.section1.p1': 'Ploqs AB, im Folgenden als "Dienstanbieter" bezeichnet, zusammen mit vom Dienstanbieter beauftragten Partnern und Subunternehmern, implementiert und nutzt verschiedene Formen digitaler Tracking-Mechanismen, einschließlich, aber nicht beschränkt auf Cookies, Web-Beacons, Pixel-Tags und andere ähnliche technologische Lösungen (zusammenfassend als "Tracking-Technologien" bezeichnet) auf den digitalen Plattformen, Websites und mobilen Anwendungen des Dienstanbieters (zusammenfassend als "Digitale Kanäle des Dienstanbieters" bezeichnet). Diese Richtlinie für den Umgang mit Tracking-Technologien legt die Zwecke fest, für die und die Methoden, mit denen der Dienstanbieter diese Technologien anwendet.',
    'cookie.section1.p2': 'Tracking-Technologien bestehen hauptsächlich aus kleineren textbasierten Datendateien, die auf dem Endgerät des Benutzers oder dem Webbrowser-Programm abgelegt werden, um Informationen über die Interaktion des Benutzers mit digitalen Plattformen und mobilen Anwendungen sowie die Navigation darauf zu sammeln.',
    'cookie.section1.h1': 'Verarbeitung personenbezogener Daten und Datenschutzerwägungen',
    'cookie.section1.p3': 'Die durch die Anwendung von Tracking-Technologien gesammelten Informationen stellen in den meisten Fällen Daten dar, die direkt oder indirekt und in Kombination mit anderen verfügbaren Daten potenziell zur Identifizierung von Personen verwendet werden können, einschließlich, aber nicht beschränkt auf IP-Adressen, eindeutige Cookie-Kennungen, Werbekennungen (IDFA), Gerätekennungen und verschiedene Metadaten, die die Unterscheidung bestimmter Endgeräte ermöglichen.',
    'cookie.section1.h2': 'Benutzerwahlmöglichkeiten',
    'cookie.section1.p4': 'Der Benutzer hat das Recht, zuvor erteilte Einwilligungen jederzeit zu widerrufen, indem er zu den über die digitale Schnittstelle des Dienstanbieters bereitgestellten Einwilligungsverwaltungsoptionen navigiert.',
    'cookie.section1.h3': 'Persistenz und Speicherzeiträume von Tracking-Technologien',
    'cookie.section1.p5': 'Tracking-Technologien weisen je nach beabsichtigtem Zweck und Funktionalität jeder Technologie unterschiedliche Persistenz auf. Sitzungsbasierte Tracking-Technologien werden bei der Verbindung des Benutzers mit der digitalen Plattform initiiert und spätestens beim Schließen der Browser-Instanz des Benutzers beendet. Persistente Tracking-Technologien werden für einen vordefinierten Zeitraum, der über einzelne Sitzungen hinausgeht, im Browser des Benutzers aufbewahrt.',
    'cookie.section2.title': 'Abschnitt 2 - Obligatorische Tracking-Technologien für die Diensterbringung',
    'cookie.section2.p1': 'Obligatorische Tracking-Technologien beziehen sich auf solche technologischen Implementierungen, die erforderlich sind, damit der Dienstanbieter dem Benutzer grundlegende und wesentliche Funktionalität bieten kann, beispielsweise zum Zweck der Aufrechterhaltung von Informationen über zuvor erteilte Einwilligungen des Benutzers bezüglich Tracking-Technologien, zur Unterscheidung zwischen verschiedenen Benutzersitzungen durch anonymisierte Kennungen, zur Ermöglichung kartografischer Funktionalität, zur Wiedergabe audiovisueller Materialien in von Immobilienmaklern hochgeladenen Immobilienanzeigen, zur Aufrechterhaltung der gespeicherten Suchpräferenzen des Benutzers, zur Aufrechterhaltung der Sicherheit der Dienste, zur Verhinderung einer unsachgemäßen Nutzung der Dienste und zur Gewährleistung einer kontinuierlichen und optimalen Dienstleistung.',
    'cookie.section2.legal': 'Rechtsgrundlage für die Verarbeitung:',
    'cookie.section2.legal_value': 'Berechtigtes Interesse gemäß Artikel 6.1(f) DSGVO',
    'cookie.section2.storage': 'Speicherzeitraum:',
    'cookie.section2.storage_value': 'Bis zu vierundzwanzig (24) Monate für persistente Tracking-Technologien; sitzungsbasierte Tracking-Technologien werden sofort beim Schließen der Browser-Instanz beendet.',
    'cookie.section3.title': 'Abschnitt 3 - Tracking-Technologien für analytische Zwecke',
    'cookie.section3.p1': 'Der Dienstanbieter misst der Fähigkeit, die Besuchshäufigkeit zu quantifizieren, Interaktionsdaten auf der digitalen Plattform und in mobilen Anwendungen zu registrieren und anderweitig zu analysieren, wie die digitalen Dienste des Dienstanbieters von Benutzern genutzt werden, erhebliche Bedeutung bei. Vorausgesetzt, dass der Benutzer die erforderliche Einwilligung erteilt hat, implementiert der Dienstanbieter Tracking-Technologien, ausgenommen direkte personenbezogene Daten, zum Zweck der Analyse und Weiterentwicklung der digitalen Dienste des Dienstanbieters und um dem Benutzer kontinuierlich ein optimiertes Benutzererlebnis zu bieten, die Dienstleistung aufrechtzuerhalten und Zielgruppenanalysen und statistische Zusammenstellungen bezüglich der Plattformnutzung zu erstellen.',
    'cookie.section3.legal_value': 'Einwilligung gemäß Artikel 6.1(a) DSGVO',
    'cookie.section3.storage_value': 'Bis zu sechsundzwanzig (26) Monate',
    'cookie.section4.title': 'Abschnitt 4 - Tracking-Technologien für Personalisierungszwecke',
    'cookie.section4.p1': 'Tracking-Technologien, die für Personalisierungszwecke implementiert werden, zielen darauf ab, das Erlebnis des Benutzers auf den digitalen Plattformen des Dienstanbieters anzupassen, indem Inhalte mit erhöhter persönlicher Relevanz für den einzelnen Benutzer bereitgestellt werden. Diese Anpassung basiert beispielsweise auf dem Suchverhalten und den Interaktionsmustern des Benutzers. Falls der Benutzer über ein registriertes Benutzerkonto beim Dienstanbieter verfügt, werden diese Verhaltensdaten mit Informationen kombiniert, die der Benutzer freiwillig im Rahmen des Dienstes bereitstellt, beispielsweise Informationen über die Wohnsituation des Benutzers, wodurch Inhaltsanpassungen auf der Grundlage des aggregierten Profils des Benutzers vorgenommen werden.',
    'cookie.section5.title': 'Abschnitt 5 - Tracking-Technologien für Marketingzwecke',
    'cookie.section5.p1': 'Der Dienstanbieter implementiert Tracking-Technologien für Marketingzwecke, vorausgesetzt, dass der Benutzer die erforderliche Einwilligung erteilt hat, um die Anzeige relevanter Werbeinhalte und Immobilienanzeigen mit erhöhter Relevanz für den Benutzer auf der Grundlage eines erstellten Werbeprofils zu ermöglichen.',
    'cookie.section5.p2': 'Durch die Implementierung von Tracking-Technologien stellt der Dienstanbieter auch sicher, dass Werbeeinblendungen auf eine vordefinierte Anzahl von Gelegenheiten pro Benutzer beschränkt werden und dass die Werbeeffektivität gemessen und analysiert wird. Die Werbeinhalte des Dienstanbieters können auch auf externen digitalen Plattformen angezeigt werden.',
    'cookie.section5.p3': 'Es ist zu beachten, dass im Falle, dass der Benutzer Tracking-Technologien für Marketingzwecke deaktiviert, Werbeinhalte dem Benutzer weiterhin auf den digitalen Plattformen des Dienstanbieters angezeigt werden, jedoch ohne Personalisierung.',
    'cookie.section5.storage_value': 'Bis zu vierundzwanzig (24) Monate',
    'cookie.section6.title': 'Abschnitt 6 - Internationale Datenübertragungen in Drittländer',
    'cookie.section6.p1': 'Im Einwilligungsverwaltungstool des Dienstanbieters werden Informationen darüber bereitgestellt, welche Tracking-Technologien und Partner die personenbezogenen Daten des Benutzers innerhalb von Gerichtsbarkeiten außerhalb der Europäischen Union und des Europäischen Wirtschaftsraums (sogenannte "Drittländer") verarbeiten oder dorthin übertragen.',
    'cookie.section7.title': 'Abschnitt 7 - Anweisungen zur Entfernung von Tracking-Technologien',
    'cookie.section7.p1': 'Der Benutzer hat die Möglichkeit, Tracking-Technologien von seinem Endgerät oder mobilen Gerät zu entfernen und Einstellungen für die lokale Datenspeicherung über die Einstellungsoberfläche des Browsers zu konfigurieren. Der Benutzer kann Tracking-Technologien deaktivieren oder jedes Mal Benachrichtigungen erhalten, wenn eine neue Tracking-Technologie auf dem Endgerät des Benutzers initiiert wird.',
    'cookie.section7.p2': 'Für detaillierte Anweisungen zum Umgang mit und zur Entfernung von Tracking-Technologien wird auf die Hilfeoption im Browser des Benutzers oder die nachstehenden Hyperlinks für jede Browser-Anwendung verwiesen:',
    
    // Auth
    'auth.loading': 'Wird geladen...',
    'auth.login': 'Anmelden',
    'auth.create_account': 'Konto erstellen',
    'auth.login_subtitle': 'Melden Sie sich an, indem Sie eine der folgenden Optionen wählen.',
    'auth.signup_subtitle': 'Erstellen Sie ein neues Konto, um zu beginnen.',
    'auth.continue_apple': 'Mit Apple fortfahren',
    'auth.continue_google': 'Mit Google fortfahren',
    'auth.or': 'oder',
    'auth.email': 'E-Mail',
    'auth.email_placeholder': 'E-Mail eingeben',
    'auth.password': 'Passwort',
    'auth.password_placeholder': 'Passwort eingeben',
    'auth.account_type': 'Kontotyp',
    'auth.user_role': 'Benutzer - Ich möchte eine Immobilie kaufen/mieten',
    'auth.agent_role': 'Makler - Ich möchte Immobilien verkaufen/vermieten',
    'auth.continue': 'Fortfahren',
    'auth.no_account': 'Kein Konto? Konto erstellen',
    'auth.have_account': 'Bereits ein Konto? Anmelden',
    'auth.login_failed': 'Anmeldung fehlgeschlagen',
    'auth.invalid_credentials': 'Ungültige E-Mail oder Passwort',
    'auth.welcome_back': 'Willkommen zurück!',
    'auth.logged_in': 'Sie wurden angemeldet.',
    'auth.signup_failed': 'Registrierung fehlgeschlagen',
    'auth.account_created': 'Konto erstellt!',
    'auth.check_email': 'Überprüfen Sie Ihre E-Mail, um Ihr Konto zu bestätigen.',
    'auth.error': 'Ein Fehler ist aufgetreten',
    'auth.try_again': 'Bitte versuchen Sie es später erneut.',
    'auth.broker_interest_title': 'Sind Sie Makler?',
    'auth.broker_interest_description': 'Wenn Sie Immobilien auf Aqarnet inserieren möchten, senden Sie uns eine Interessensbekundung und wir melden uns bei Ihnen.',
    'auth.broker_contact_us': 'Kontaktieren Sie uns',
    'auth.broker_email_subject': 'Interessensbekundung Maklerregistrierung',
    'auth.broker_email_body': 'Hallo,\n\nIch interessiere mich für die Registrierung als Makler auf Aqarnet.\n\nName:\nAgenturname:\nTelefon:\nStadt:\n\nVielen Dank!',
    
    // Auth extended
    'auth.first_name': 'Vorname',
    'auth.last_name': 'Nachname',
    'auth.first_name_placeholder': 'Geben Sie Ihren Vornamen ein',
    'auth.last_name_placeholder': 'Geben Sie Ihren Nachnamen ein',
    'auth.display_name': 'Anzeigename',
    'auth.display_name_placeholder': 'Wählen Sie einen Anzeigenamen',
    'auth.display_name_hint': 'Dies ist der einzige Name, den andere sehen werden',
    'auth.date_of_birth': 'Geburtsdatum',
    'auth.fill_all_fields': 'Bitte füllen Sie alle erforderlichen Felder aus',
    'auth.must_be_18': 'Sie müssen mindestens 18 Jahre alt sein, um sich zu registrieren',
    'auth.must_be_18_hint': 'Sie müssen mindestens 18 Jahre alt sein',
    
    // Identity Verification
    'verification.verify_identity': 'Identität verifizieren',
    'verification.verified': 'Verifiziert',
    'verification.title': 'Identitätsverifizierung',
    'verification.description': 'Laden Sie ein amtliches Dokument hoch, um Ihre Identität zu verifizieren und ein verifiziertes Abzeichen zu erhalten.',
    'verification.document_type': 'Dokumenttyp',
    'verification.doc_id_card': 'Personalausweis',
    'verification.doc_passport': 'Reisepass',
    'verification.doc_drivers_license': 'Führerschein',
    'verification.upload_document': 'Dokument hochladen',
    'verification.click_to_upload': 'Klicken Sie zum Hochladen oder ziehen Sie die Datei hierher',
    'verification.file_requirements': 'JPG, PNG, WEBP oder PDF (max. 10MB)',
    'verification.submit': 'Zur Verifizierung einreichen',
    'verification.uploading': 'Wird hochgeladen...',
    'verification.submitted': 'Verifizierung eingereicht',
    'verification.submitted_desc': 'Ihr Verifizierungsantrag wurde eingereicht. Wir werden ihn in Kürze prüfen.',
    'verification.pending_message': 'Ihre Verifizierung wird geprüft. Dies dauert normalerweise 1-2 Werktage.',
    'verification.rejected_message': 'Ihre vorherige Verifizierung wurde abgelehnt. Bitte reichen Sie ein neues Dokument ein.',
    'verification.status_approved': 'Genehmigt',
    'verification.status_pending': 'Ausstehend',
    'verification.status_rejected': 'Abgelehnt',
    'verification.invalid_file_type': 'Ungültiger Dateityp',
    'verification.allowed_types': 'Bitte laden Sie eine JPG-, PNG-, WEBP- oder PDF-Datei hoch',
    'verification.file_too_large': 'Datei zu groß',
    'verification.max_size': 'Maximale Dateigröße ist 10MB',
    'verification.error': 'Verifizierungsfehler',
    'verification.admin_title': 'Identitätsverifizierungen',
    'verification.admin_description': 'Identitätsverifizierungsanträge prüfen und genehmigen',
    'verification.no_requests': 'Keine Verifizierungsanträge',
    'verification.view': 'Prüfen',
    'verification.review_title': 'Verifizierung prüfen',
    'verification.review_notes': 'Prüfungsnotizen',
    'verification.review_notes_placeholder': 'Notizen zu Ihrer Entscheidung hinzufügen (optional)',
    'verification.approve': 'Genehmigen',
    'verification.reject': 'Ablehnen',
    'verification.approved': 'Verifizierung genehmigt',
    'verification.rejected': 'Verifizierung abgelehnt',
    'verification.review_complete': 'Die Verifizierung wurde bearbeitet.',

    // Favorites Page
    'favorites.title': 'Meine Favoriten',
    'favorites.saved_properties': 'Gespeicherte Immobilien',
    'favorites.login_required': 'Melden Sie sich an, um Ihre Favoriten zu sehen',
    'favorites.login_description': 'Speichern Sie Immobilien, die Sie mögen, um sie leichter zu finden.',
    'favorites.loading': 'Favoriten werden geladen...',
    'favorites.no_favorites': 'Noch keine Favoriten',
    'favorites.start_saving': 'Beginnen Sie mit dem Speichern von Immobilien, indem Sie auf das Herz in den Anzeigen klicken.',
    'favorites.search_properties': 'Immobilien suchen',
    
    // Profile Page
    'profile.title': 'Mein Profil',
    'profile.subtitle': 'Persönliche Informationen und Einstellungen verwalten',
    'profile.loading': 'Profil wird geladen...',
    'profile.fetch_error': 'Fehler beim Abrufen des Profils',
    'profile.fetch_error_description': 'Profilinformationen konnten nicht abgerufen werden.',
    'profile.unnamed_user': 'Namenloser Benutzer',
    'profile.personal_info': 'Persönliche Informationen',
    'profile.update_info': 'Aktualisieren Sie Ihre persönlichen Daten',
    'profile.email': 'E-Mail',
    'profile.email_readonly': 'E-Mail kann nicht geändert werden',
    'profile.display_name': 'Anzeigename',
    'profile.display_name_placeholder': 'Ihr Name',
    'profile.phone': 'Telefonnummer',
    'profile.phone_placeholder': '0170-1234567',
    'profile.save_changes': 'Änderungen speichern',
    'profile.saving': 'Wird gespeichert...',
    'profile.reset': 'Zurücksetzen',
    'profile.updated': 'Profil aktualisiert',
    'profile.updated_description': 'Ihr Profil wurde gespeichert.',
    'profile.update_error': 'Fehler beim Aktualisieren',
    'profile.update_error_description': 'Änderungen konnten nicht gespeichert werden.',
    'profile.active_listings': 'Aktive Anzeigen',
    'profile.active_listings_description': 'Ihre veröffentlichten Immobilienanzeigen zum Verkauf',
    'profile.view_all': 'Alle anzeigen',
    'profile.loading_listings': 'Anzeigen werden geladen...',
    'profile.no_active_listings': 'Noch keine aktiven Anzeigen',
    'profile.more_active_listings': 'und {count} weitere aktive Anzeigen',
    'profile.sold_properties': 'Verkaufte Immobilien',
    'profile.sold_properties_description': 'Ihre verkauften Immobilien',
    'profile.no_sold_properties': 'Noch keine verkauften Immobilien',
    'profile.more_sold_properties': 'und {count} weitere verkaufte Immobilien',
    'profile.listing_fetch_error': 'Fehler beim Abrufen der Anzeigen',
    'profile.listing_fetch_error_description': 'Ihre Anzeigen konnten nicht abgerufen werden.',
    'profile.account_settings': 'Kontoeinstellungen',
    'profile.account_settings_description': 'Konto und Sicherheit verwalten',
    'profile.change_password': 'Passwort ändern',
    'profile.change_password_description': 'Passwort für sicherere Anmeldung aktualisieren',
    'profile.change': 'Ändern',
    'profile.two_factor': 'Zwei-Faktor-Authentifizierung',
    'profile.two_factor_description': 'Zusätzliche Sicherheit für Ihr Konto hinzufügen',
    'profile.enable': 'Aktivieren',
    'profile.delete_account': 'Konto löschen',
    'profile.delete_account_description': 'Konto und alle Daten dauerhaft löschen',
    'profile.delete': 'Löschen',
    'profile.logout': 'Abmelden',
    
    // Profile Email Notifications
    'profile.email_notifications': 'E-Mail-Benachrichtigungen',
    'profile.email_notifications_description': 'Wählen Sie, welche E-Mail-Benachrichtigungen Sie erhalten möchten',
    'profile.notif_property_alerts': 'Immobilien-Benachrichtigungen',
    'profile.notif_property_alerts_desc': 'Benachrichtigung über neue Immobilien, die Ihren gespeicherten Suchen entsprechen',
    'profile.notif_saved_search_alerts': 'Gespeicherte Suche Updates',
    'profile.notif_saved_search_alerts_desc': 'Updates erhalten, wenn neue Immobilien Ihren Kriterien entsprechen',
    'profile.notif_newsletter': 'Newsletter',
    'profile.notif_newsletter_desc': 'Erhalten Sie unseren wöchentlichen Newsletter mit Markteinblicken',
    'profile.notif_viewing_confirmations': 'Besichtigungsbestätigungen',
    'profile.notif_viewing_confirmations_desc': 'Bestätigungs-E-Mails für geplante Besichtigungen erhalten',
    'profile.save_preferences': 'Einstellungen speichern',
    'profile.notifications_saved': 'Benachrichtigungseinstellungen gespeichert',
    'profile.notifications_save_error': 'Speichern der Benachrichtigungseinstellungen fehlgeschlagen',
    
    // Not Found Page
    'notfound.title': '404',
    'notfound.message': 'Entschuldigung! Seite nicht gefunden',
    'notfound.back_home': 'Zurück zur Startseite',
    
    // Property Detail Page
    'property.back_to_listings': 'Zurück zu den Anzeigen',
    'property.not_found': 'Immobilie nicht gefunden',
    'property.link_copied': 'Link kopiert',
    'property.link_copied_description': 'Immobilienlink kopiert',
    'property.description': 'Beschreibung',
    'property.features': 'Ausstattung',
    'property.details': 'Immobiliendetails',
    'property.rooms_label': 'Zimmer',
    'property.size_label': 'Größe',
    'property.plot_size_label': 'Grundstücksgröße',
    'property.monthly_fee_label': 'Monatliche Gebühr',
    'property.contact_agent': 'Makler kontaktieren',
    'property.send_message': 'Nachricht senden',
    'property.schedule_viewing': 'Besichtigung vereinbaren',
    'property.section.details': 'Details',
    'property.section.amenities': 'Ausstattung',
    'property.section.overview': 'Übersicht',
    'property.section.payment_plan': 'Zahlungsplan',
    'property.section.floor_plans': 'Grundrisse',
    'property.section.view_map': 'Karte anzeigen',
    'property.copy_link': 'Link kopieren',
    'property.share': 'Teilen',
    'property.share_text': 'Schauen Sie sich diese Immobilie an',
    'property.posted_on': 'Veröffentlicht am',
    'property.type': 'Typ',
    'property.status': 'Status',
    'property.available': 'Verfügbar',
    'property.no_amenities': 'Keine Ausstattung für diese Immobilie aufgeführt',
    'property.no_description': 'Keine Beschreibung verfügbar',
    'property.location': 'Standort',
    'property.payment_plan_info': 'Kontaktieren Sie den Makler für Zahlungsplan-Details',
    'property.contact_for_payment_plan': 'Kontakt für Zahlungsplan',
    'property.no_floor_plans': 'Keine Grundrisse verfügbar',
    'property.request_floor_plans': 'Grundrisse anfordern',
    'property.no_map_location': 'Standort auf Karte nicht verfügbar',
    
    // News Page
    'news.subtitle': 'Bleiben Sie auf dem Laufenden mit den neuesten Immobiliennachrichten, Markteinblicken und Ratgebern',
    'news.no_articles': 'Noch keine Nachrichtenartikel',
    'news.check_back': 'Schauen Sie bald wieder vorbei für die neuesten Immobiliennachrichten und Markteinblicke.',
    'news.search_placeholder': 'Artikel suchen...',
    'news.showing_filtered': 'Zeige {shown} von {total} Artikeln auf dieser Seite',
    'news.clear_filters': 'Filter löschen',
    'news.no_matching': 'Keine passenden Artikel',
    'news.adjust_filters': 'Versuchen Sie, Ihre Suche oder Filter anzupassen, um das Gewünschte zu finden.',
    'news.publisher': 'Herausgeber',
    'news.page_info': 'Seite {current} von {total} • {count} Artikel insgesamt',
    'news.category.all': 'Alle',
    'news.category.market_news': 'Marktnachrichten',
    'news.category.property_guides': 'Immobilienratgeber',
    'news.category.investment_tips': 'Investitionstipps',
    'news.category.industry_updates': 'Branchenaktualisierungen',
    'news.category.announcements': 'Ankündigungen',
    'news.category.expert_advice': 'Expertenrat',
    
    // Filters/Sorting
    'filters.any': 'Alle',
    'filters.newest_first': 'Neueste zuerst',
    'filters.price_low_high': 'Preis: Niedrig bis Hoch',
    'filters.price_high_low': 'Preis: Hoch bis Niedrig',
    'filters.size': 'Größe',
    'filters.searching_for': 'Suche nach',
    'filters.search_areas': 'Gebiete, Städte suchen...',
    
    // Common
    'common.cancel': 'Abbrechen',
    'common.save': 'Speichern',
    'common.saving': 'Wird gespeichert...',
    'common.send': 'Senden',
    'common.sending': 'Wird gesendet...',
    'common.submit': 'Absenden',
    'common.close': 'Schließen',
    'common.error': 'Fehler',
    'common.success': 'Erfolg',
    'common.loading': 'Wird geladen...',
    'common.name': 'Name',
    'common.email': 'E-Mail',
    'common.phone': 'Telefon',
    'common.message': 'Nachricht',
    'common.required': 'Erforderlich',
    'common.optional': 'Optional',
    'common.delete': 'Löschen',
    'common.free': 'Kostenlos',
    
    // Leaderboard
    'leaderboard': 'Rangliste',
    'leaderboardDescription': 'Entdecken Sie Top-Agenturen und Makler nach Aufrufen und Verkäufen',
    'topAgencies': 'Top-Agenturen',
    'topBrokers': 'Top-Makler',
    'topAgenciesDescription': 'Agenturen nach Immobilienaufrufen und Verkaufsleistung',
    'topBrokersDescription': 'Makler nach Immobilienaufrufen und Verkaufsleistung',
    'allRegions': 'Alle Regionen',
    'noAgenciesFound': 'Keine Agenturen gefunden',
    'noBrokersFound': 'Keine Makler gefunden',
    'brokers': 'Makler',
    'sold': 'verkauft',
    'active': 'aktiv',
    'views': 'Aufrufe',
    
    // Dialogs - Contact Agent
    'dialog.contact_agent': 'Makler kontaktieren',
    'dialog.contact_agent_btn': 'Makler kontaktieren',
    'dialog.contact_message_placeholder': 'Hallo! Ich bin an der Immobilie in {address} interessiert und würde gerne mehr erfahren. Bitte kontaktieren Sie mich, wenn Sie Zeit haben.',
    'dialog.contact_sending': 'Wird gesendet...',
    'dialog.contact_send': 'Nachricht senden',
    'dialog.contact_note': 'Ihre Anfrage wird direkt an den zuständigen Makler gesendet, der Sie kontaktieren wird.',
    
    // Dialogs - Schedule Viewing
    'dialog.schedule_viewing': 'Besichtigung vereinbaren',
    'dialog.schedule_viewing_btn': 'Besichtigung vereinbaren',
    'dialog.preferred_time': 'Gewünschter Besichtigungstermin',
    'dialog.preferred_time_note': 'Wählen Sie Datum und Uhrzeit für die Besichtigung. Der Makler wird die Verfügbarkeit bestätigen.',
    'dialog.special_requests': 'Besondere Wünsche',
    'dialog.special_requests_placeholder': 'Besondere Wünsche oder Fragen...',
    'dialog.schedule_booking': 'Besichtigung wird gebucht...',
    'dialog.schedule_book': 'Besichtigung buchen',
    'dialog.schedule_note': 'Ihre Anfrage wird an den Makler gesendet, der den Termin per E-Mail bestätigen wird.',
    
    // Dialogs - Save Search
    'dialog.save_search': 'Suche speichern',
    'dialog.save_search_btn': 'Suche speichern',
    'dialog.search_name': 'Name der Suche',
    'dialog.search_name_placeholder': 'z.B. Wohnung in Berlin',
    'dialog.city_municipality': 'Stadt/Gemeinde',
    'dialog.min_rooms': 'Min. Zimmer',
    'dialog.min_size': 'Min. Größe (m²)',
    'dialog.property_type': 'Immobilientyp',
    'dialog.email_alerts': 'E-Mail-Benachrichtigungen bei passenden neuen Immobilien',
    'dialog.saving_search': 'Wird gespeichert...',
    
    // Dialogs - Mark as Sold
    'dialog.mark_sold': 'Als verkauft markieren',
    'dialog.mark_sold_description': 'Geben Sie den endgültigen Verkaufspreis für diese Immobilie ein. Dies verschiebt die Anzeige zu den verkauften Immobilien.',
    'dialog.final_sale_price': 'Endgültiger Verkaufspreis (USD)',
    'dialog.listed_price': 'Angebotspreis',
    'dialog.property_details': 'Immobiliendetails',
    'dialog.enter_valid_price': 'Bitte geben Sie einen gültigen Verkaufspreis ein',
    'dialog.enter_price_placeholder': 'Verkaufspreis eingeben',
    'dialog.marked_sold_success': 'Immobilie erfolgreich als verkauft markiert',
    'dialog.mark_sold_error': 'Immobilie konnte nicht als verkauft markiert werden',
    
    // Dialogs - Add Property
    'dialog.add_property': 'Neue Immobilie hinzufügen',
    'dialog.hide_preview': 'Vorschau ausblenden',
    'dialog.show_preview': 'Vorschau anzeigen',
    'dialog.selected_images': 'Ausgewählte Bilder',
    'dialog.uploaded_images': 'Hochgeladene Bilder',
    'dialog.click_primary': 'Klicken Sie auf ein Bild, um es als Hauptbild festzulegen',
    'dialog.invalid_format': 'Ungültiges Dateiformat',
    'dialog.file_too_large': 'Datei zu groß',
    'dialog.upload_failed': 'Upload fehlgeschlagen',
    'dialog.images_required': 'Bilder erforderlich',
    'dialog.please_upload': 'Bitte laden Sie mindestens ein Bild für die Immobilie hoch',
    'dialog.form_validation_failed': 'Formularvalidierung fehlgeschlagen',
    
    // Properties for Sale Page
    'propertiesForSale.filters.searchWithin': 'Suchen in',
    'propertiesForSale.filters.km': 'km',
    'propertiesForSale.filters.propertyType': 'Immobilientyp',
    'propertiesForSale.filters.rooms': 'Zimmer',
    'propertiesForSale.filters.bathrooms': 'Badezimmer',
    'propertiesForSale.filters.min': 'Min',
    'propertiesForSale.filters.max': 'Max',
    'propertiesForSale.filters.size': 'Größe',
    'propertiesForSale.filters.price': 'Preis',
    'propertiesForSale.filters.searchResults': 'Suchergebnisse',
    'propertiesForSale.filters.searchPlaceholder': 'Gebiete, Städte suchen...',
    'propertiesForSale.filters.searchingFor': 'Suche nach:',
    'propertiesForSale.filters.any': 'Alle',
    'propertiesForSale.filters.keywords': 'Schlüsselwörter',
    'propertiesForSale.filters.showResults': 'Ergebnisse anzeigen',
    'propertiesForSale.filters.clearFilters': 'Löschen',
    'propertiesForSale.filters.realEstateSituation': 'Immobiliensituation',
    'propertiesForSale.filters.situation.tapu': 'Tapu',
    'propertiesForSale.filters.situation.landContract': 'Grundstücksvertrag',
    'propertiesForSale.filters.situation.noDocuments': 'Keine Dokumente',
    'propertiesForSale.filters.situation.investment': 'Investition',
    'propertiesForSale.filters.situation.musataha': 'Musataha',
    'propertiesForSale.filters.situation.card': 'Karte',
    'propertiesForSale.filters.situation.agriculturalTapu': 'Landwirtschaftliches Tapu',
    'propertiesForSale.sort.newest': 'Neueste zuerst',
    'propertiesForSale.sort.price_low': 'Preis: Niedrig bis Hoch',
    'propertiesForSale.sort.price_high': 'Preis: Hoch bis Niedrig',
    'propertiesForSale.sort.size': 'Größe',
    'propertiesForSale.noResults': 'Keine Immobilien gefunden',
    'propertiesForSale.tryAdjustingFilters': 'Versuchen Sie, Ihre Filter anzupassen oder in einem anderen Bereich zu suchen',
    
    // Search Section
    'search.for_sale': 'Zu verkaufen',
    'search.for_rent': 'Zu vermieten',
    'search.sold_prices': 'Verkaufspreise',
    'search.search_agents': 'Makler suchen',
    'search.area_label': 'Gebiet',
    'search.area_placeholder': 'Gebiet oder Adresse eingeben',
    'search.area_examples': 'Beispiel: Bagdad, Basra, Erbil, Najaf, Karbala',
    'search.expand_area': 'Gebiet erweitern um',
    'search.property_type': 'Immobilientyp',
    'search.show_filters': 'Suchfilter anzeigen',
    'search.find_properties': 'Immobilien zum Verkauf finden',
    'search.min_rooms': 'Min. Zimmer',
    'search.min_area': 'Min. Fläche',
    'search.max_price': 'Max. Preis',
    'search.all': 'Alle',
    'search.no_max': 'Kein Maximum',
    'search.new_construction': 'Neubau',
    'search.show_new_construction': 'Neubau anzeigen',
    'search.only_new_construction': 'Nur Neubau',
    'search.exclude_new_construction': 'Neubau ausschließen',
    'search.keywords': 'Stichwörter',
    'search.keywords_placeholder': 'Pool, Kamin, usw.',
    'search.more_filters_note': 'Weitere Suchfilter sind bei den Suchergebnissen verfügbar.',
    'search.recent_searches': 'Letzte Suchen',
    'search.hide_history': 'Verlauf ausblenden',
    'search.clear_history': 'Verlauf löschen',
    
    'property.all_types': 'Alle Typen',
    'property.villas': 'Villen',
    'property.townhouses': 'Reihenhäuser',
    'property.apartments': 'Wohnungen',
    'property.business': 'Gewerbeimmobilien',
    'property.land': 'Grundstücke',
    'property.farms': 'Bauernhöfe/Wälder',
    'property.other': 'Sonstige',

    // Property Types (Select)
    'propertyTypes.allTypes': 'Alle Typen',
    'propertyTypes.villa': 'Villen',
    'propertyTypes.apartment': 'Wohnungen',
    'propertyTypes.townhouse': 'Reihenhäuser',
    'propertyTypes.business': 'Gewerbeimmobilien',
    'propertyTypes.plot': 'Grundstücke',
    'propertyTypes.farm': 'Bauernhöfe/Wälder',
    'propertyTypes.other': 'Sonstige',
    
    // Listings
    'listings.latest_for_sale': 'Neueste zum Verkauf',
    'listings.properties_found': 'Immobilien gefunden',
    'listings.add_property': 'Immobilie hinzufügen',
    'listings.all_news': 'Alle Nachrichten',
    'listings.edit': 'Bearbeiten',
    'listings.edit_property': 'Immobilie bearbeiten',
    'listings.monthly_fee': 'Monatliche Gebühr',
    'listings.open_floor_plan': 'Offener Grundriss',
    'listings.large_balcony': 'Großer Balkon',
    'listings.family_friendly': 'Familienfreundlich',
    'listings.photos': 'Fotos',
    'listings.per_month': 'Monat',
    'loading.properties': 'Immobilien werden geladen...',
    'empty.no_properties': 'Derzeit keine Immobilien verfügbar',
    'empty.check_back': 'Schauen Sie später für neue Angebote.',
    
    // Property card
    'property.sold': 'VERKAUFT',
    'property.photos': 'Fotos',
    'property.rooms_short': 'Zimmer',
    'property.plot': 'Grundstück',
    'property.per_month': '/Monat',
    'property.delete_title': 'Immobilie löschen?',
    'property.delete_confirm': 'Sind Sie sicher, dass Sie {address} löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden.',
    'property.deleted': 'Immobilie gelöscht',
    'property.deleted_desc': 'Die Immobilie wurde entfernt',
    'property.delete_error': 'Immobilie konnte nicht gelöscht werden',
    
    // Saved Searches
    'loading.saved_searches': 'Gespeicherte Suchen werden geladen...',
    'saved_searches.title': 'Gespeicherte Suchen',
    'saved_searches.empty': 'Noch keine gespeicherten Suchen',
    'saved_searches.description': 'Speichern Sie eine Suche, um Benachrichtigungen zu erhalten, wenn passende Immobilien hinzugefügt werden',
    'saved_searches.created': 'Erstellt',
    'saved_searches.alerts_on': 'Benachrichtigungen an',
    'saved_searches.alerts_off': 'Benachrichtigungen aus',
    'saved_searches.email_enabled': 'E-Mail-Benachrichtigungen aktiviert',
    'saved_searches.email_disabled': 'E-Mail-Benachrichtigungen deaktiviert',
    'saved_searches.settings_updated': 'Einstellung aktualisiert',
    'saved_searches.removed': 'Suche entfernt',
    'saved_searches.removed_desc': 'Die gespeicherte Suche wurde entfernt',
    'saved_searches.error': 'Fehler',
    'saved_searches.update_error': 'Einstellung konnte nicht aktualisiert werden',
    'saved_searches.delete_error': 'Suche konnte nicht gelöscht werden',
    'saved_searches.all_properties': 'Alle Immobilien',
    
    // Rental properties
    'rental.for_sale': 'Zum Verkauf',
    'rental.for_rent': 'Zur Miete',
    'rental.listing_type': 'Angebotstyp',
    'rental.rental_details': 'Mietdetails',
    'rental.rental_period': 'Mietdauer',
    'rental.rental_period_hint': 'Wie oft die Miete gezahlt wird',
    'rental.daily': 'Täglich',
    'rental.weekly': 'Wöchentlich',
    'rental.monthly': 'Monatlich',
    'rental.yearly': 'Jährlich',
    'rental.per_daily': '/Tag',
    'rental.per_weekly': '/Woche',
    'rental.per_monthly': '/Monat',
    'rental.per_yearly': '/Jahr',
    'rental.deposit_amount': 'Kautionsbetrag (USD)',
    'rental.minimum_lease': 'Mindestmietdauer (Monate)',
    'rental.months': 'Monate',
    'rental.available_from': 'Verfügbar ab',
    'rental.furnished': 'Möbliert',
    'rental.utilities_included': 'Nebenkosten inklusive',
    'rental.pets_allowed': 'Haustiere erlaubt',
    
    // Admin Pages
    'admin.loading': 'Wird geladen...',
    'admin.access_denied': 'Zugriff verweigert. Administrator-Berechtigung erforderlich.',
    
    // Admin Notification Settings
    'admin.notifications.title': 'Benachrichtigungseinstellungen',
    'admin.notifications.description': 'Verwalten Sie Benachrichtigungseinstellungen für alle Benutzertypen. Aktivieren, deaktivieren oder ausblenden nach Bedarf.',
    'admin.notifications.fetch_error': 'Benachrichtigungseinstellungen konnten nicht abgerufen werden',
    'admin.notifications.update_success': 'Benachrichtigungseinstellung aktualisiert',
    'admin.notifications.update_error': 'Benachrichtigungseinstellung konnte nicht aktualisiert werden',
    'admin.notifications.role_description': 'Benachrichtigungen für {role} verwalten',
    'admin.notifications.hidden': 'Versteckt',
    'admin.notifications.disabled': 'Deaktiviert',
    'admin.notifications.active': 'Aktiv',
    'admin.notifications.hide': 'Ausblenden',
    'admin.notifications.roles.user': 'Benutzer',
    'admin.notifications.roles.maklare': 'Makler',
    'admin.notifications.roles.agency': 'Agenturen',
    'admin.notifications.roles.admin': 'Administratoren',
    'admin.notifications.roles.publisher': 'Herausgeber',
    'admin.notifications.types.new_viewing_request': 'Neue Besichtigungsanfrage',
    'admin.notifications.types.new_client_message': 'Neue Kundennachricht',
    'admin.notifications.types.property_alerts': 'Immobilienbenachrichtigungen',
    'admin.notifications.types.weekly_summary': 'Wöchentliche Zusammenfassung',
    'admin.notifications.types.broker_property_added': 'Makler hat Immobilie hinzugefügt',
    'admin.notifications.types.broker_viewing_request': 'Makler-Besichtigungsanfrage',
    'admin.notifications.types.broker_client_message': 'Makler-Kundennachricht',
    'admin.notifications.types.broker_joined': 'Makler beigetreten',
    'admin.notifications.types.weekly_agency_summary': 'Wöchentliche Agenturzusammenfassung',
    'admin.notifications.types.saved_search_alerts': 'Gespeicherte Suchbenachrichtigungen',
    'admin.notifications.types.favorite_property_updates': 'Favoriten-Immobilienaktualisierungen',
    'admin.notifications.types.newsletter': 'Newsletter',
    'admin.notifications.types.system_alerts': 'Systemwarnungen',
    'admin.notifications.types.new_user_signup': 'Neue Benutzerregistrierung',
    'admin.notifications.types.article_published': 'Artikel veröffentlicht',

    // Admin Settings
    'admin.settings': 'Admin-Einstellungen',
    'admin.messaging_settings': 'Nachrichteneinstellungen',
    'admin.messaging_settings_description': 'Nachrichten- und Anruffunktionen für alle Benutzer steuern',
    'admin.messaging': 'Nachrichten',
    'admin.messaging_description': 'Benutzern erlauben, Nachrichten zu senden',
    'admin.voice_calls': 'Sprachanrufe',
    'admin.voice_calls_description': 'Benutzern erlauben, Sprachanrufe zu tätigen',
    'admin.video_calls': 'Videoanrufe',
    'admin.video_calls_description': 'Benutzern erlauben, Videoanrufe zu tätigen',
    'admin.screen_sharing': 'Bildschirmfreigabe',
    'admin.screen_sharing_description': 'Benutzern erlauben, ihren Bildschirm während Anrufen zu teilen',

    // Location Manager
    'location_mgr.title': 'Standortmanager',
    'location_mgr.current_locations': 'Aktuelle Standorte',
    'location_mgr.locations_in_db': '{count} Standorte in der Datenbank',
    'location_mgr.refresh': 'Aktualisieren',
    'location_mgr.populate_title': 'Standorte füllen',
    'location_mgr.populate_desc': 'Alle irakischen Städte und Gebiete von Google Places API abrufen und in der Datenbank speichern. Dies ersetzt alle vorhandenen Standorte.',
    'location_mgr.populating': 'Wird gefüllt...',
    'location_mgr.populate_btn': 'Von Google Places füllen',
    'location_mgr.success': 'Erfolg',
    'location_mgr.success_msg': 'Standorte erfolgreich gefüllt',
    'location_mgr.error': 'Fehler',
    'location_mgr.error_msg': 'Standorte konnten nicht gefüllt werden. Überprüfen Sie die Konsole für Details.',
    
    // Map Configuration Manager
    'map_config.title': 'Kartenkonfigurationsmanager',
    'map_config.subtitle': 'Verwalten Sie Ihre permanenten Kartenkonfigurationen, um die Google Maps API-Nutzung zu minimieren',
    'map_config.loading': 'Kartenkonfigurationen werden geladen...',
    'map_config.create': 'Konfiguration erstellen',
    'map_config.create_title': 'Kartenkonfiguration erstellen',
    'map_config.create_desc': 'Erstellen Sie eine neue permanente Kartenkonfiguration, die anstelle wiederholter Google Maps API-Aufrufe verwendet wird.',
    'map_config.edit_title': 'Kartenkonfiguration bearbeiten',
    'map_config.edit_desc': 'Aktualisieren Sie Ihre permanente Kartenkonfiguration.',
    'map_config.name': 'Konfigurationsname',
    'map_config.name_placeholder': 'z.B. Irak Immobilienkarte',
    'map_config.description': 'Beschreibung (optional)',
    'map_config.description_placeholder': 'Kurze Beschreibung dieser Konfiguration',
    'map_config.map_data': 'Kartenkonfiguration (JSON)',
    'map_config.map_data_placeholder': 'Kartenkonfiguration im JSON-Format',
    'map_config.cancel': 'Abbrechen',
    'map_config.update': 'Konfiguration aktualisieren',
    'map_config.no_configs': 'Keine Kartenkonfigurationen',
    'map_config.no_configs_desc': 'Erstellen Sie Ihre erste Kartenkonfiguration, um mit der effizienten Verwaltung Ihrer Karten zu beginnen.',
    'map_config.create_first': 'Erste Konfiguration erstellen',
    'map_config.active': 'Aktiv',
    'map_config.activate': 'Aktivieren',
    'map_config.edit': 'Bearbeiten',
    'map_config.delete': 'Löschen',
    'map_config.delete_confirm': 'Sind Sie sicher?',
    'map_config.delete_desc': 'Dies löscht die Kartenkonfiguration "{name}" dauerhaft. Diese Aktion kann nicht rückgängig gemacht werden.',
    'map_config.created': 'Erstellt',
    'map_config.last_updated': 'Zuletzt aktualisiert',
    'map_config.map_center': 'Kartenzentrum',
    'map_config.zoom_level': 'Zoomstufe',
    'map_config.cities': 'Städte',
    'map_config.cities_configured': '{count} Städte konfiguriert',
    'map_config.invalid_json': 'Ungültiges JSON-Format in den Kartendaten',
    
    // Forms
    'form.price': 'Preis',
    'form.priceUSD': 'Preis (USD)',
    'form.priceIQD': 'Preis (IQD)',
    'form.priceConversionNote': 'Geben Sie den Preis in einer der Währungen ein - die andere wird automatisch berechnet (Kurs: 1 USD ≈ 1.310 IQD)',
    'form.price_usd': 'Preis (USD)',
    'form.price_placeholder': 'Preis in US-Dollar eingeben',
    'form.price_description': 'Geben Sie den Preis in US-Dollar ein. Er wird sowohl in USD als auch in irakischem Dinar angezeigt.',
    'form.address': 'Adresse',
    'form.city': 'Stadt',
    'form.rooms': 'Zimmer',
    'form.bathrooms': 'Badezimmer',
    'form.size': 'Größe (m²)',
    'form.building_size': 'Gebäudegröße (m²)',
    'form.building_size_placeholder': 'Gebäudefläche in m²',
    'form.building_size_description': 'Gebäudefläche (Wohnfläche)',
    'form.plot_size': 'Grundstücksgröße (m²)',
    'form.plot_size_placeholder': 'Grundstücksfläche in m²',
    'form.plot_size_description': 'Gesamte Grundstücksfläche',
    'form.type': 'Typ',
    'form.propertyType': 'Immobilientyp',
    'form.monthly_fee': 'Monatliche Gebühr (SEK)',
    'form.monthly_fee_usd': 'Monatliche Gebühr (USD)',
    'form.monthly_fee_placeholder': 'Monatliche Gebühr in USD eingeben (optional)',
    'form.monthly_fee_description': 'Optionale monatliche Gebühr in US-Dollar',
    'form.description': 'Beschreibung',
    'form.description_placeholder': 'Immobilie beschreiben...',
    'form.payment_plan': 'Zahlungsplan',
    'form.payment_plan_placeholder': 'z.B. 30% Anzahlung, 70% bei Übergabe, oder monatliche Raten...',
    'form.payment_plan_hint': 'Fügen Sie Raten hinzu, um Käufern Ihre Zahlungsoptionen zu zeigen',
    'payment_plan.total': 'Gesamt',
    'payment_plan.amount': 'Betrag',
    'payment_plan.description': 'Beschreibung',
    'payment_plan.date': 'Datum',
    'payment_plan.percentage': 'Prozent',
    'payment_plan.add_installment': 'Rate hinzufügen',
    'payment_plan.hint': 'Leer lassen, wenn kein Zahlungsplan angeboten wird',
    'payment_plan.down_payment': 'Anzahlung',
    'payment_plan.first_payment': 'Erste Rate',
    'payment_plan.second_payment': 'Zweite Rate',
    'payment_plan.third_payment': 'Dritte Rate',
    'payment_plan.fourth_payment': 'Vierte Rate',
    'payment_plan.fifth_payment': 'Fünfte Rate',
    'payment_plan.sixth_payment': 'Sechste Rate',
    'payment_plan.seventh_payment': 'Siebte Rate',
    'payment_plan.final_payment': 'Letzte Zahlung',
    'payment_plan.payment_description': 'Zahlungsbeschreibung',
    'payment_plan.enable': 'Zahlungsplan einschließen',
    'form.floor_plans': 'Grundrisse',
    'form.upload_floor_plans': 'Grundrissbilder hochladen',
    'form.features': 'Merkmale (kommagetrennt)',
    'form.features_placeholder': 'z.B. Pool, Garten, Garage, moderne Küche',
    'form.features_description': 'Mehrere Merkmale mit Komma trennen',
    'form.amenities': 'Ausstattung',
    'form.amenities_hint': 'Wählen Sie alle Ausstattungsmerkmale, die für diese Immobilie gelten',
    'amenities.balcony': 'Balkon',
    'amenities.electricity_generator': 'Stromgenerator',
    'amenities.central_ac': 'Zentralklimaanlage',
    'amenities.double_glazed_windows': 'Doppelverglasung',
    'amenities.satellite_tv': 'Satelliten-TV',
    'amenities.cctv': 'CCTV-Sicherheit',
    'amenities.security_staff': 'Sicherheitspersonal',
    'amenities.sewerage_system': 'Abwassersystem',
    'amenities.furnished_kitchen': 'Eingerichtete Küche',
    'amenities.24hr_electricity': '24-Stunden-Strom',
    'amenities.market': 'Markt in der Nähe',
    'amenities.elevator': 'Aufzug',
    'amenities.school': 'Schule in der Nähe',
    'amenities.mosque': 'Moschee in der Nähe',
    'amenities.central_gas': 'Zentralgas',
    'amenities.maintenance': 'Wartungsdienste',
    'amenities.cleaning': 'Reinigungsdienste',
    'amenities.fire_fighting': 'Brandbekämpfungsausrüstung',
    'amenities.car_washing': 'Autowaschanlage',
    'amenities.smart_home': 'Smart-Home-System',
    'amenities.earthquake_resistant': 'Erdbebensicher',
    'amenities.central_fire_system': 'Zentrales Feuerlöschsystem',
    'amenities.hot_water_system': 'Zentrales Warmwassersystem',
    'amenities.central_vacuum': 'Zentralstaubsauger',
    'amenities.fountain': 'Brunnen',
    'amenities.24hr_water': '24-Stunden-Wasserversorgung',
    'amenities.garbage_chute': 'Müllschlucker',
    'amenities.kitchen_counter': 'Küchentheke',
    'amenities.street_lights': 'Straßenbeleuchtung',
    'amenities.playground': 'Kinderspielplatz',
    'amenities.gym': 'Fitnessstudio',
    'amenities.garden': 'Garten',
    'amenities.parking': 'Parkplatz',
    'amenities.wifi': 'WLAN',
    'amenities.security_door': 'Sicherheitstür',
    'amenities.split_ac': 'Split-Klimaanlage',
    'amenities.bathtub': 'Badewanne',
    'amenities.refrigerator': 'Kühlschrank',
    'amenities.washing_machine': 'Waschmaschine',
    'amenities.swimming_pool': 'Swimmingpool',
    'amenities.solar_panels': 'Solarmodule',
    'amenities.water_tank': 'Wassertank',
    'amenities.water_well': 'Wasserbrunnen',
    'amenities.septic_tank': 'Klärgrube',
    'amenities.central_heating': 'Zentralheizung',
    'amenities.desert_cooler': 'Wüstenkühler',
    'amenities.intercom': 'Gegensprechanlage',
    'amenities.external_camera': 'Außenkamera',
    'amenities.guard_room': 'Wachraum',
    'amenities.rooftop_terrace': 'Dachterrasse',
    'amenities.basement': 'Keller',
    'amenities.storage_room': 'Abstellraum',
    'amenities.internal_stairs': 'Innentreppe',
    'amenities.external_stairs': 'Außentreppe',
    'amenities.corner_unit': 'Eckeinheit',
    'amenities.two_streets': 'Zwei Straßen',
    'amenities.double_entrance': 'Doppeleingang',
    'amenities.marble_flooring': 'Marmorboden',
    'amenities.ceramic_tiles': 'Keramikfliesen',
    'amenities.parquet_flooring': 'Parkettboden',
    'amenities.gypsum_decorations': 'Gipsdekorationen',
    'amenities.false_ceiling': 'Abgehängte Decke',
    'amenities.steel_doors': 'Stahltüren',
    'amenities.aluminum_windows': 'Aluminiumfenster',
    'amenities.pvc_windows': 'PVC-Fenster',
    'amenities.guest_room': 'Gästezimmer',
    'amenities.servant_quarters': 'Dienstbotenquartier',
    'amenities.covered_parking': 'Überdachter Parkplatz',
    'amenities.concrete_fence': 'Betonzaun',
    'amenities.iron_fence': 'Eisenzaun',
    'amenities.street_pavement': 'Straßenpflaster',
    'amenities.fully_furnished': 'Voll möbliert',
    'amenities.hospital': 'Krankenhaus in der Nähe',
    'amenities.category.utilities': 'Strom & Wasser',
    'amenities.category.climate': 'Klimatisierung',
    'amenities.category.security': 'Sicherheit',
    'amenities.category.building': 'Gebäudemerkmale',
    'amenities.category.interior': 'Innenausstattung & Böden',
    'amenities.category.rooms': 'Räume',
    'amenities.category.outdoor': 'Außenbereich & Parken',
    'amenities.category.appliances': 'Geräte',
    'amenities.category.services': 'Dienstleistungen',
    'amenities.category.nearby': 'In der Nähe',
    'common.selected': 'ausgewählt',
    'form.property_images': 'Immobilienbilder',
    'form.current_images': 'Aktuelle Bilder',
    'form.image': 'Bild',
    'form.primary': 'Primär',
    'form.click_image_primary': 'Auf ein Bild klicken, um es als primär festzulegen. Pfeile zum Ändern der Reihenfolge verwenden.',
    'form.click_to_upload_new': 'Klicken zum Hochladen neuer Bilder',
    'form.new_images_upload': 'Neue Bilder zum Hochladen',
    'form.new_image': 'Neues Bild',
    'form.upload': 'Hochladen',
    'form.new_images': 'neue Bilder',
    'form.location': 'Standort',
    'form.required': 'Erforderlich',
    'form.optional': 'Optional',
    'form.cancel': 'Abbrechen',
    'form.save_changes': 'Änderungen speichern',
    'form.uploading': 'Wird hochgeladen...',
    'form.upload_images': 'Bilder hochladen',
    'form.click_to_upload': 'Klicken zum Hochladen von Bildern',
    'form.file_formats': 'JPEG, PNG, WebP, GIF (max. 10MB pro Bild)',
    'form.select_type': 'Typ auswählen',
    'form.location_on_map': 'Immobilienstandort auf der Karte',
    'form.location_hint': 'Auf die Karte klicken, um den genauen Standort festzulegen',
    'form.hide_preview': 'Vorschau ausblenden',
    'form.show_preview': 'Vorschau anzeigen',
    'form.selected_images': 'Ausgewählte Bilder',
    'form.click_set_primary': 'Auf ein Bild klicken, um es als primär festzulegen',
    'form.uploaded_images': 'Hochgeladene Bilder',
    'form.images': 'Bilder',
    'form.uploading_images': 'Bilder werden hochgeladen...',
    'form.city_placeholder': 'Nach einer Stadt suchen...',
    'form.new_construction': 'Neubau (Zukünftiges Zuhause)',
    'form.preview': 'Vorschau',
    'form.preview_description': 'So wird Ihre Anzeige auf der Seite erscheinen',
    'form.fullscreen': 'Vollbild',
    'form.exit_fullscreen': 'Vollbild beenden',
    'form.click_preview_fullscreen': 'Klicken Sie auf die Vorschau für Vollbild',
    'form.preview_contact_disabled': 'Kontaktoptionen sind im Vorschaumodus deaktiviert',
    'form.invalid_format': 'Ungültiges Dateiformat',
    'form.invalid_format_desc': '{filename} hat ein ungültiges Format. Erlaubt: JPEG, PNG, WebP, GIF',
    'form.file_too_large': 'Datei zu groß',
    'form.file_too_large_desc': '{filename} ist größer als 10MB',
    'form.upload_failed': 'Hochladen fehlgeschlagen',
    'form.upload_failed_desc': 'Bilder konnten nicht hochgeladen werden',
    'form.images_required': 'Bilder erforderlich',
    'form.images_required_desc': 'Bitte laden Sie mindestens ein Bild für die Immobilie hoch',
    'form.sold_price_usd': 'Verkaufspreis (USD)',
    'form.sold_price_placeholder': 'Tatsächlichen Verkaufspreis eingeben',
    'form.sold_price_description': 'Der tatsächliche Verkaufspreis (kann vom Angebotspreis abweichen)',
    'form.video_url': 'Video-Tour URL',
    'form.video': 'Video',
    'form.add_video': 'Weiteres Video hinzufügen',
    'form.video_addon': '+$3 Zusatz',
    'form.video_included': 'Kostenlos enthalten',
    'form.video_url_hint': 'YouTube- oder Vimeo-Video-Tour hinzufügen. Video-Zusatz kann nach Erstellung der Anzeige erworben werden.',
    'form.video_url_hint_free': 'YouTube- oder Vimeo-Video-Tour hinzufügen - in Ihrem Abonnement enthalten!',
    'form.video_url_hint_addon': 'YouTube- oder Vimeo-Video-Tour hinzufügen. $3 werden zu Ihren Anzeigenkosten hinzugefügt.',
    'form.include_video_tour': 'Video-Tour einschließen',
    'form.show_map': 'Karte anzeigen',
    'form.no_location': 'Kein Standort ausgewählt',
    
    // Footer
    'footer.contact': 'Kontakt',
    'footer.customer_service': 'Kundenservice & Kontakt',
    'footer.about': 'Über Aqarnet',
    'footer.press': 'Presse',
    'footer.investors': 'Für Investoren',
    'footer.advertising': 'Werbung und Marketing',
    'footer.jobs': 'Jobs bei uns',
    'footer.statistics': 'Immobilienstatistiken',
    'footer.apps': 'Apps',
    'footer.iphone_ipad': 'iPhone und iPad',
    'footer.android': 'Android',
    'footer.follow': 'Aqarnet folgen',
    'footer.newsletter': 'Aqarnet Newsletter',
    'footer.whatsapp': 'WhatsApp',
    'footer.facebook': 'Facebook',
    'footer.instagram': 'Instagram',
    'footer.linkedin': 'LinkedIn',
    'footer.pinterest': 'Pinterest',
    'footer.tiktok': 'TikTok',
    'footer.threads': 'Threads',
    'footer.podcast': 'Aqarnet Podcast',
    'footer.all_properties': 'Alle Immobilien',
    'footer.privacy': 'Datenschutz',
    'footer.cookies': 'Cookies',
    'footer.terms': 'AGB & Rechtliche Informationen',
    'footer.cookie_settings': 'Cookie-Einstellungen',
    
    // About Page
    'about.title': 'Über Aqarnet',
    'about.subtitle': 'Ihr vertrauenswürdiger Partner auf dem irakischen Immobilienmarkt',
    'about.description': 'Aqarnet ist Iraks führende Immobilienplattform, die Käufer, Verkäufer und Makler im ganzen Land verbindet. Wir bieten umfassende Immobilienangebote, Marktanalysen und professionelle Tools.',
    'about.mission_title': 'Unsere Mission',
    'about.mission_text': 'Den irakischen Immobilienmarkt durch transparente, zugängliche und zuverlässige Immobilieninformationen zu revolutionieren.',
    'about.vision_title': 'Unsere Vision',
    'about.vision_text': 'Die vertrauenswürdigste Immobilienplattform im Nahen Osten zu werden.',
    'about.services_title': 'Unsere Dienstleistungen',
    'about.services_text': 'Immobilienangebote, Marktanalysen, Maklervermittlung, Immobilienbewertungen und umfassende Immobiliennachrichten.',
    'about.team_title': 'Unser Team',
    'about.team_text': 'Ein engagiertes Team aus Immobilienprofis, Technologieexperten und Kundendienstspezialisten.',
    'about.company_info': 'Unternehmensinformationen',
    'about.based_in': 'Sitz in Schweden, tätig im Irak',
    
    // Contact Page
    'contact.title': 'Kontakt',
    'contact.subtitle': 'Wir sind für Sie da. Kontaktieren Sie uns bei Fragen oder Anliegen.',
    'contact.email_label': 'E-Mail',
    'contact.phone_label': 'Telefon',
    'contact.phone_info': 'Demnächst verfügbar',
    'contact.location_label': 'Standort',
    'contact.location_info': 'Schweden',
    'contact.form_title': 'Nachricht senden',
    'contact.form_description': 'Füllen Sie das Formular aus und wir melden uns schnellstmöglich.',
    'contact.name_placeholder': 'Ihr Name',
    'contact.email_placeholder': 'ihre@email.com',
    'contact.subject': 'Betreff',
    'contact.subject_placeholder': 'Worum geht es?',
    'contact.message_placeholder': 'Ihre Nachricht...',
    'contact.success': 'Nachricht gesendet! Wir melden uns bald.',
    
    // Press Page
    'press.title': 'Presse & Medien',
    'press.subtitle': 'Aktuelle Nachrichten und Pressemitteilungen von Aqarnet',
    'press.contact_title': 'Pressekontakt',
    'press.contact_description': 'Für Presseanfragen kontaktieren Sie uns bitte per E-Mail.',
    'press.add_release': 'Pressemitteilung hinzufügen',
    'press.edit_release': 'Pressemitteilung bearbeiten',
    'press.new_release': 'Neue Pressemitteilung',
    'press.release_title': 'Titel',
    'press.content': 'Inhalt',
    'press.publish': 'Veröffentlichen',
    'press.no_releases': 'Noch keine Pressemitteilungen.',
    'press.delete_confirm_title': 'Pressemitteilung löschen?',
    'press.delete_confirm_description': 'Diese Aktion kann nicht rückgängig gemacht werden.',
    
    // Investors Page
    'investors.title': 'Für Investoren',
    'investors.subtitle': 'Begleiten Sie uns bei der Transformation des irakischen Immobilienmarktes',
    'investors.growth_title': 'Wachstumspotenzial',
    'investors.growth_text': 'Der irakische Immobilienmarkt erlebt ein rasantes Wachstum mit bedeutenden Investitionsmöglichkeiten.',
    'investors.market_title': 'Marktführer',
    'investors.market_text': 'Aqarnet ist als führende digitale Plattform im irakischen Immobiliensektor positioniert.',
    'investors.vision_title': 'Regionale Expansion',
    'investors.vision_text': 'Unsere Vision reicht über den Irak hinaus zur führenden Plattform im Nahen Osten.',
    'investors.opportunity_title': 'Investitionsmöglichkeit',
    'investors.opportunity_text': 'Aqarnet bietet eine einzigartige Gelegenheit, in die digitale Transformation eines der größten Schwellenländer-Immobilienmärkte zu investieren.',
    'investors.opportunity_text_2': 'Mit einer wachsenden Nutzerbasis und erweiterten Diensten sind wir gut für nachhaltiges Wachstum positioniert.',
    'investors.contact_title': 'Kontaktieren Sie uns',
    'investors.contact_description': 'Interesse an Investitionsmöglichkeiten? Kontaktieren Sie uns für mehr Informationen.',
    'investors.company': 'Unternehmen / Organisation',
    'investors.company_placeholder': 'Ihr Unternehmensname',
    'investors.message_placeholder': 'Erzählen Sie uns von Ihren Investitionsinteressen...',
    'investors.success': 'Vielen Dank für Ihr Interesse! Wir melden uns bald.',
    
    // Advertising Page
    'advertising.title': 'Werbung & Marketing',
    'advertising.subtitle': 'Erreichen Sie tausende potenzielle Käufer und Mieter im Irak',
    'advertising.targeted_title': 'Gezielte Reichweite',
    'advertising.targeted_text': 'Erreichen Sie Nutzer, die aktiv nach Immobilien in bestimmten Gebieten suchen.',
    'advertising.audience_title': 'Qualitätspublikum',
    'advertising.audience_text': 'Verbinden Sie sich mit ernsthaften Käufern und Mietern.',
    'advertising.analytics_title': 'Detaillierte Analysen',
    'advertising.analytics_text': 'Verfolgen Sie Ihre Kampagnenleistung mit umfassenden Analysen.',
    'advertising.formats_title': 'Mehrere Formate',
    'advertising.formats_text': 'Banneranzeigen, hervorgehobene Angebote, gesponserte Inhalte und mehr.',
    'advertising.contact_title': 'Werbung starten',
    'advertising.contact_description': 'Kontaktieren Sie uns für Werbemöglichkeiten.',
    'advertising.company': 'Unternehmen / Organisation',
    'advertising.company_placeholder': 'Ihr Unternehmensname',
    'advertising.budget': 'Budgetrahmen',
    'advertising.budget_placeholder': 'z.B. 500 - 1000 $/Monat',
    'advertising.message_placeholder': 'Erzählen Sie uns von Ihren Werbezielen...',
    'advertising.success': 'Vielen Dank! Wir kontaktieren Sie bezüglich Werbemöglichkeiten.',
    
    // Jobs Page
    'jobs.title': 'Karriere bei Aqarnet',
    'jobs.subtitle': 'Werden Sie Teil unseres Teams und gestalten Sie die Zukunft der Immobilien im Irak',
    'jobs.add_job': 'Stelle hinzufügen',
    'jobs.edit_job': 'Stelle bearbeiten',
    'jobs.new_job': 'Neue Stellenausschreibung',
    'jobs.job_title': 'Stellentitel',
    'jobs.department': 'Abteilung',
    'jobs.location': 'Standort',
    'jobs.employment_type': 'Beschäftigungsart',
    'jobs.full_time': 'Vollzeit',
    'jobs.part_time': 'Teilzeit',
    'jobs.contract': 'Vertrag',
    'jobs.internship': 'Praktikum',
    'jobs.description': 'Beschreibung',
    'jobs.requirements': 'Anforderungen',
    'jobs.benefits': 'Leistungen',
    'jobs.publish': 'Veröffentlichen',
    'jobs.no_jobs': 'Derzeit keine offenen Stellen.',
    'jobs.check_back': 'Schauen Sie später für neue Möglichkeiten vorbei.',
    'jobs.apply_now': 'Jetzt bewerben',
    'jobs.apply_for': 'Bewerben für',
    'jobs.apply_description': 'Senden Sie Ihre Bewerbung per E-Mail.',
    'jobs.application_sent': 'Bewerbung gesendet! Viel Erfolg!',
    'jobs.delete_confirm_title': 'Stellenausschreibung löschen?',
    'jobs.delete_confirm_description': 'Diese Aktion kann nicht rückgängig gemacht werden.',
    
    // Statistics
    'statistics.title': 'Immobilienstatistiken',
    'statistics.subtitle': 'Analysieren Sie Markttrends und Preisentwicklung basierend auf verkauften Immobilien',
    'statistics.6_months': 'Letzte 6 Monate',
    'statistics.12_months': 'Letzte 12 Monate',
    'statistics.24_months': 'Letzte 24 Monate',
    'statistics.36_months': 'Letzte 36 Monate',
    'statistics.all_cities': 'Alle Städte',
    'statistics.total_sold': 'Gesamt verkauft',
    'statistics.avg_price': 'Durchschnittspreis',
    'statistics.avg_price_sqm': 'Durchschn. Preis/m²',
    'statistics.price_change': 'Preisänderung',
    'statistics.price_trends': 'Preistrends',
    'statistics.price_trends_desc': 'Durchschnittliche Immobilienpreise im Zeitverlauf',
    'statistics.sales_volume': 'Verkaufsvolumen',
    'statistics.sales_volume_desc': 'Anzahl verkaufter Immobilien pro Monat',
    'statistics.properties_sold': 'Verkaufte Immobilien',
    'statistics.price_by_city': 'Preis pro m² nach Stadt',
    'statistics.price_by_city_desc': 'Vergleich des durchschnittlichen Preises pro Quadratmeter zwischen Städten',
    'statistics.price_per_sqm': 'Preis pro m²',
    'statistics.type_distribution': 'Immobilientyp-Verteilung',
    'statistics.type_distribution_desc': 'Verteilung verkaufter Immobilien nach Typ',
    'statistics.avg_days_market': 'Durchschn. Tage am Markt',
    'statistics.days': 'Tage',
    'statistics.price_ranges': 'Preisbereich-Verteilung',
    'statistics.price_ranges_desc': 'Anzahl verkaufter Immobilien in jedem Preisbereich',
    'statistics.properties': 'Immobilien',
    'statistics.year_over_year': 'Jahresvergleich',
    'statistics.year_over_year_desc': 'Vergleichen Sie Durchschnittspreise und Verkaufsvolumen über Jahre',
    'statistics.all_types': 'Alle Immobilientypen',
    'statistics.all_rooms': 'Alle Zimmer',
    'statistics.rooms': 'Zimmer',
    'statistics.export': 'Exportieren',
    'statistics.export_csv': 'CSV herunterladen',
    'statistics.export_json': 'JSON herunterladen',
    'statistics.share': 'Teilen',
    'statistics.link_copied': 'Link kopiert',
    'statistics.link_copied_desc': 'Der Link zum Statistikbericht wurde in die Zwischenablage kopiert',
    'statistics.custom_date': 'Benutzerdefiniertes Datum',
    'statistics.use_preset': 'Voreinstellung verwenden',
    'statistics.start_date': 'Startdatum',
    'statistics.end_date': 'Enddatum',
    'statistics.city_comparison': 'Städtevergleich',
    'statistics.city_comparison_desc': 'Vergleichen Sie Immobilienstatistiken zwischen zwei Städten nebeneinander',
    'statistics.select_city_1': 'Erste Stadt wählen',
    'statistics.select_city_2': 'Zweite Stadt wählen',
    'statistics.vs': 'vs',
    'statistics.select_cities_to_compare': 'Wählen Sie oben zwei Städte aus, um ihre Statistiken zu vergleichen',
    'statistics.avg_size': 'Durchschn. Größe',
    
    // Cookie Settings
    'cookie_settings.title': 'Cookie-Einstellungen',
    'cookie_settings.description': 'Wir verwenden Cookies, um Ihr Browser-Erlebnis zu verbessern. Sie können Ihre Cookie-Präferenzen unten anpassen.',
    'cookie_settings.necessary': 'Notwendige Cookies',
    'cookie_settings.necessary_desc': 'Diese Cookies sind für das ordnungsgemäße Funktionieren der Website unerlässlich. Sie können nicht deaktiviert werden.',
    'cookie_settings.functional': 'Funktionale Cookies',
    'cookie_settings.functional_desc': 'Diese Cookies ermöglichen personalisierte Funktionen wie Spracheinstellungen und gespeicherte Einstellungen.',
    'cookie_settings.analytics': 'Analytische Cookies',
    'cookie_settings.analytics_desc': 'Diese Cookies helfen uns zu verstehen, wie Besucher mit unserer Website interagieren, um die Benutzererfahrung zu verbessern.',
    'cookie_settings.marketing': 'Marketing-Cookies',
    'cookie_settings.marketing_desc': 'Diese Cookies werden verwendet, um Ihnen relevante Werbung zu zeigen und die Wirksamkeit unserer Kampagnen zu verfolgen.',
    'cookie_settings.accept_all': 'Alle akzeptieren',
    'cookie_settings.reject_all': 'Alle ablehnen',
    'cookie_settings.save_preferences': 'Einstellungen speichern',
    'cookie_settings.saved': 'Cookie-Einstellungen gespeichert',
    
    // Cookie Banner
    'cookie_banner.title': 'Wir verwenden Cookies',
    'cookie_banner.description': 'Wir verwenden Cookies, um Ihr Browser-Erlebnis zu verbessern, personalisierte Inhalte anzuzeigen und unseren Datenverkehr zu analysieren. Durch Klicken auf "Alle akzeptieren" stimmen Sie unserer Verwendung von Cookies zu.',
    'cookie_banner.accept_all': 'Alle akzeptieren',
    'cookie_banner.reject_all': 'Alle ablehnen',
    'cookie_banner.customize': 'Anpassen',
    
    // Terms of Service
    'terms.title': 'Nutzungsbedingungen',
    'terms.last_updated': 'Zuletzt aktualisiert:',
    'terms.section1.title': '1. Annahme der Bedingungen',
    'terms.section1.p1': 'Durch die Nutzung von Aqarnet stimmen Sie diesen Nutzungsbedingungen zu. Wenn Sie diesen Bedingungen nicht zustimmen, nutzen Sie bitte unsere Dienste nicht.',
    'terms.section1.p2': 'Wir behalten uns das Recht vor, diese Bedingungen jederzeit zu ändern. Die fortgesetzte Nutzung nach Änderungen gilt als Annahme der geänderten Bedingungen.',
    'terms.section2.title': '2. Beschreibung des Dienstes',
    'terms.section2.p1': 'Aqarnet bietet eine digitale Plattform für Immobilienanzeigen, Immobiliensuche und Verbindungen zwischen Käufern, Verkäufern und Maklern im Irak.',
    'terms.section2.p2': 'Unsere Dienste umfassen Immobilienangebote, Suchfunktionen, gespeicherte Suchen, Favoriten und Kommunikationstools zwischen Nutzern und Maklern.',
    'terms.section3.title': '3. Nutzerverantwortlichkeiten',
    'terms.section3.p1': 'Als Nutzer von Aqarnet stimmen Sie zu:',
    'terms.section3.li1': 'Genaue und wahrheitsgemäße Informationen in Ihren Anzeigen und Ihrem Profil bereitzustellen',
    'terms.section3.li2': 'Die Plattform nicht für betrügerische oder illegale Aktivitäten zu nutzen',
    'terms.section3.li3': 'Die geistigen Eigentumsrechte anderer zu respektieren',
    'terms.section3.li4': 'Den ordnungsgemäßen Betrieb des Dienstes nicht zu stören',
    'terms.section4.title': '4. Immobilienanzeigen',
    'terms.section4.p1': 'Nutzer, die Immobilien einstellen, sind für die Richtigkeit ihrer Anzeigen verantwortlich. Aqarnet überprüft nicht die Richtigkeit der Immobilieninformationen.',
    'terms.section4.p2': 'Wir behalten uns das Recht vor, Anzeigen zu entfernen, die gegen unsere Bedingungen verstoßen oder als betrügerisch gemeldet werden.',
    'terms.section5.title': '5. Geistiges Eigentum',
    'terms.section5.p1': 'Alle Inhalte auf Aqarnet, einschließlich Text, Grafiken, Logos und Software, sind Eigentum von Aqarnet oder seinen Inhaltsanbietern.',
    'terms.section5.p2': 'Sie dürfen unsere Inhalte ohne ausdrückliche schriftliche Genehmigung nicht reproduzieren, verteilen oder abgeleitete Werke erstellen.',
    'terms.section6.title': '6. Haftungsbeschränkung',
    'terms.section6.p1': 'Aqarnet wird "wie besehen" ohne jegliche Garantien bereitgestellt. Wir haften nicht für Schäden, die aus der Nutzung unserer Dienste entstehen.',
    'terms.section7.title': '7. Anwendbares Recht',
    'terms.section7.p1': 'Diese Bedingungen unterliegen schwedischem Recht. Streitigkeiten werden vor schwedischen Gerichten beigelegt.',
    'terms.section7.p2': 'Sollte eine Bestimmung dieser Bedingungen für nicht durchsetzbar befunden werden, bleiben die übrigen Bestimmungen in Kraft.',
    'terms.section8.title': '8. Kontaktinformationen',
    'terms.section8.p1': 'Bei Fragen zu diesen Nutzungsbedingungen kontaktieren Sie uns bitte:',
    'terms.section8.email': 'E-Mail',
    
    // Privacy Policy
    'privacy.title': 'Datenschutzrichtlinie',
    'privacy.last_updated': 'Zuletzt aktualisiert:',
    'privacy.section1.title': '1. Einleitung',
    'privacy.section1.p1': 'Bei Aqarnet verpflichten wir uns, Ihre Privatsphäre und persönlichen Daten zu schützen. Diese Richtlinie erklärt, wie wir Ihre Informationen sammeln, verwenden und schützen.',
    'privacy.section1.p2': 'Durch die Nutzung unserer Dienste stimmen Sie den in dieser Richtlinie beschriebenen Datenpraktiken zu.',
    'privacy.section2.title': '2. Informationen, die wir sammeln',
    'privacy.section2.p1': 'Wir sammeln folgende Arten von Informationen:',
    'privacy.section2.li1': 'Kontoinformationen: Name, E-Mail-Adresse, Telefonnummer',
    'privacy.section2.li2': 'Immobilienangebotsdaten: Adressen, Beschreibungen, Fotos, Preise',
    'privacy.section2.li3': 'Nutzungsdaten: Suchverlauf, gespeicherte Immobilien, Browsing-Verhalten',
    'privacy.section2.li4': 'Geräteinformationen: IP-Adresse, Browsertyp, Betriebssystem',
    'privacy.section3.title': '3. Wie wir Ihre Informationen verwenden',
    'privacy.section3.p1': 'Wir verwenden Ihre Informationen, um:',
    'privacy.section3.li1': 'Unsere Dienste bereitzustellen und zu verbessern',
    'privacy.section3.li2': 'Ihr Erlebnis und Empfehlungen zu personalisieren',
    'privacy.section3.li3': 'Benachrichtigungen über gespeicherte Suchen und neue Angebote zu senden',
    'privacy.section3.li4': 'Mit Ihnen über Ihr Konto und Anfragen zu kommunizieren',
    'privacy.section4.title': '4. Datenweitergabe',
    'privacy.section4.p1': 'Wir verkaufen Ihre persönlichen Daten nicht. Wir können Informationen mit Immobilienmaklern teilen, wenn Sie Anfragen stellen, und mit Dienstleistern, die unsere Geschäftstätigkeit unterstützen.',
    'privacy.section4.p2': 'Wir können Informationen offenlegen, wenn das Gesetz es erfordert oder um unsere Rechte und Sicherheit zu schützen.',
    'privacy.section5.title': '5. Ihre Rechte',
    'privacy.section5.p1': 'Sie haben folgende Rechte bezüglich Ihrer Daten:',
    'privacy.section5.li1': 'Zugang: Eine Kopie Ihrer persönlichen Daten anfordern',
    'privacy.section5.li2': 'Berichtigung: Ungenaue Informationen aktualisieren oder korrigieren',
    'privacy.section5.li3': 'Löschung: Löschung Ihres Kontos und Ihrer Daten beantragen',
    'privacy.section5.li4': 'Übertragbarkeit: Ihre Daten in einem übertragbaren Format erhalten',
    'privacy.section5.li5': 'Widerspruch: Bestimmte Datenverarbeitungsaktivitäten ablehnen',
    'privacy.section6.title': '6. Datensicherheit',
    'privacy.section6.p1': 'Wir implementieren geeignete technische und organisatorische Maßnahmen, um Ihre Daten vor unbefugtem Zugriff, Änderung oder Zerstörung zu schützen.',
    'privacy.section6.p2': 'Keine Übertragungsmethode über das Internet ist jedoch 100% sicher, und wir können keine absolute Sicherheit garantieren.',
    'privacy.section7.title': '7. Änderungen dieser Richtlinie',
    'privacy.section7.p1': 'Wir können diese Datenschutzrichtlinie von Zeit zu Zeit aktualisieren. Wir werden Sie über wesentliche Änderungen durch eine Mitteilung auf unserer Website oder per E-Mail informieren.',
    'privacy.section8.title': '8. Kontaktieren Sie uns',
    'privacy.section8.p1': 'Bei Fragen zu dieser Datenschutzrichtlinie oder Ihren persönlichen Daten kontaktieren Sie uns bitte:',
    'privacy.section8.email': 'E-Mail',
    
    // FAQ Page
    'faq.title': 'Häufig gestellte Fragen',
    'faq.subtitle': 'Finden Sie Antworten auf häufige Fragen zur Nutzung von Aqarnet',
    'faq.account.title': 'Konto & Registrierung',
    'faq.account.q1': 'Wie erstelle ich ein Konto?',
    'faq.account.q1_answer': 'Klicken Sie im Header auf "Anmelden", dann wählen Sie "Konto erstellen".',
    'faq.account.q2': 'Wie setze ich mein Passwort zurück?',
    'faq.account.q2_answer': 'Klicken Sie auf der Anmeldeseite auf "Passwort vergessen" und geben Sie Ihre E-Mail ein.',
    'faq.account.q3': 'Kann ich meine E-Mail-Adresse ändern?',
    'faq.account.q3_answer': 'Derzeit können Sie Ihre E-Mail nicht direkt ändern. Kontaktieren Sie bitte unser Support-Team.',
    'faq.listings.title': 'Immobilienanzeigen',
    'faq.listings.q1': 'Wie liste ich eine Immobilie zum Verkauf?',
    'faq.listings.q1_answer': 'Sie müssen als Makler registriert sein. Nach der Anmeldung klicken Sie auf "Immobilie hinzufügen".',
    'faq.listings.q2': 'Wie lange bleibt eine Anzeige aktiv?',
    'faq.listings.q2_answer': 'Anzeigen bleiben aktiv, bis sie als verkauft markiert oder entfernt werden.',
    'faq.listings.q3': 'Kann ich meine Anzeige nach der Veröffentlichung bearbeiten?',
    'faq.listings.q3_answer': 'Ja, Sie können Ihre Anzeige jederzeit bearbeiten.',
    'faq.search.title': 'Immobiliensuche',
    'faq.search.q1': 'Wie speichere ich eine Suche?',
    'faq.search.q1_answer': 'Nach der Suche klicken Sie auf "Suche speichern".',
    'faq.search.q2': 'Wie füge ich eine Immobilie zu Favoriten hinzu?',
    'faq.search.q2_answer': 'Klicken Sie auf das Herzsymbol auf jeder Immobilienkarte.',
    'faq.search.q3': 'Kann ich auf der Karte suchen?',
    'faq.search.q3_answer': 'Ja, verwenden Sie die Kartenansicht.',
    'faq.contact.title': 'Makler kontaktieren',
    'faq.contact.q1': 'Wie kontaktiere ich einen Makler?',
    'faq.contact.q1_answer': 'Klicken Sie auf "Makler kontaktieren" bei jeder Anzeige.',
    'faq.contact.q2': 'Kann ich eine Besichtigung planen?',
    'faq.contact.q2_answer': 'Ja, klicken Sie auf "Besichtigung planen".',
    'faq.security.title': 'Sicherheit & Datenschutz',
    'faq.security.q1': 'Sind meine persönlichen Daten sicher?',
    'faq.security.q1_answer': 'Ja, wir verwenden Verschlüsselung und Sicherheitsmaßnahmen.',
    'faq.security.q2': 'Wie kann ich mein Konto löschen?',
    'faq.security.q2_answer': 'Gehen Sie zu den Profileinstellungen und wählen Sie "Konto löschen".',
    'faq.still_questions': 'Noch Fragen?',
    'faq.contact_us': 'Kontaktieren Sie unser Support-Team.',
    'newsletter.title': 'Newsletter-Anmeldung',
    'newsletter.description': 'Abonnieren Sie für die neuesten Immobiliennachrichten.',
    'newsletter.placeholder': 'Ihre E-Mail eingeben',
    'newsletter.subscribe': 'Abonnieren',
    'newsletter.subscribing': 'Wird abonniert...',
    'newsletter.success': 'Erfolgreich abonniert!',
    'newsletter.subscribed': 'Danke für Ihr Abonnement!',
    'newsletter.invalid_email': 'Bitte geben Sie eine gültige E-Mail ein',
    'newsletter.error': 'Abonnement fehlgeschlagen',
    'newsletter.unsubscribe_note': 'Sie können sich jederzeit abmelden.',
    'newsletter.unsubscribe_title': 'Newsletter abbestellen',
    'newsletter.unsubscribe_description': 'E-Mail eingeben zum Abbestellen.',
    'newsletter.unsubscribe_button': 'Abbestellen',
    'newsletter.unsubscribe_success': 'Erfolgreich abgemeldet',
    'newsletter.unsubscribe_error': 'Abmeldung fehlgeschlagen',
    'newsletter.unsubscribe_message': 'Sie erhalten unseren Newsletter nicht mehr.',
    'newsletter.invalid_link': 'Ungültiger Abmeldelink',
    'newsletter.invalid_link_message': 'Dieser Abmeldelink ist ungültig oder abgelaufen. Bitte verwenden Sie den Link aus Ihrer Newsletter-E-Mail.',
    'newsletter.unsubscribe_reason_label': 'Grund für die Abmeldung (optional)',
    'newsletter.select_reason': 'Wählen Sie einen Grund...',
    'newsletter.reason_too_many_emails': 'Zu viele E-Mails',
    'newsletter.reason_not_relevant': 'Inhalt ist für mich nicht relevant',
    'newsletter.reason_never_subscribed': 'Ich habe nie abonniert',
    'newsletter.reason_found_another': 'Anderen Dienst gefunden',
    'newsletter.reason_other': 'Sonstiges',
    'common.try_again': 'Erneut versuchen',
    'newsletter.admin.title': 'Newsletter-Abonnenten',
    'newsletter.admin.description': 'Verwalten Sie Ihre Abonnenten',
    'newsletter.admin.total': 'Gesamt Abonnenten',
    'newsletter.admin.active': 'Aktiv',
    'newsletter.admin.unsubscribed': 'Abgemeldet',
    'newsletter.admin.export_csv': 'CSV Exportieren',
    'newsletter.admin.refresh': 'Aktualisieren',
    'newsletter.admin.subscribers_list': 'Abonnentenliste',
    'newsletter.admin.subscribers_description': 'Alle Abonnenten und ihr Status',
    'newsletter.admin.no_subscribers': 'Noch keine Abonnenten',
    'newsletter.admin.subscribed_at': 'Abonniert am',
    'newsletter.admin.status': 'Status',
    'newsletter.admin.unsubscribed_at': 'Abgemeldet am',
    'newsletter.admin.actions': 'Aktionen',
    'newsletter.admin.deleted': 'Abonnent gelöscht',
    'newsletter.admin.delete_error': 'Löschen fehlgeschlagen',
    'newsletter.admin.no_data': 'Keine Daten zum Exportieren',
    'newsletter.admin.exported': 'Erfolgreich exportiert',
    'newsletter.admin.delete_confirm_title': 'Abonnent löschen?',
    'newsletter.admin.delete_confirm_description': 'Dies löscht den Abonnenten dauerhaft.',
    'footer.faq': 'Häufige Fragen',
    
    // Broker Dashboard
    'broker.dashboard_title': 'Makler-Dashboard',
    'broker.dashboard_subtitle': 'Verwalten Sie Ihre Immobilien und Kundeninteraktionen',
    'broker.my_properties': 'Meine Immobilien',
    'broker.my_properties_desc': 'Anzeigen und Verwalten Ihrer Immobilienanzeigen',
    'broker.viewings': 'Besichtigungen',
    'broker.viewings_desc': 'Geplante Immobilienbesichtigungen verwalten',
    'broker.contacts': 'Kundennachrichten',
    'broker.contacts_desc': 'Kundenanfragen anzeigen und beantworten',
    'broker.analytics': 'Immobilienanalyse',
    'broker.analytics_desc': 'Aufrufe und Engagement Ihrer Anzeigen verfolgen',
    'broker.subscription': 'Abonnement',
    'broker.subscription_desc': 'Verwalten Sie Ihren Plan und Anzeigenlimits',
    'broker.access_denied': 'Diese Seite ist nur für registrierte Makler zugänglich.',
    'broker.profile_title': 'Maklerprofil',
    'broker.profile_description': 'Verwalten Sie Ihr öffentliches Profil und Kontaktdaten',
    'broker.back_to_dashboard': 'Zurück zum Dashboard',
    'broker.display_name': 'Anzeigename',
    'broker.display_name_placeholder': 'Ihr Name, wie er Kunden angezeigt wird',
    'broker.contact_email': 'Kontakt-E-Mail',
    'broker.email_placeholder': 'ihre@email.com',
    'broker.email_notification_note': 'Diese E-Mail wird für Benachrichtigungen über Besichtigungen und Kundennachrichten verwendet.',
    'broker.phone_number': 'Telefonnummer',
    'broker.phone_placeholder': '+49 xxx xxx xxxx',
    'broker.change_photo': 'Foto ändern',
    'broker.save_profile': 'Profil speichern',
    'broker.profile_saved': 'Profil erfolgreich gespeichert',
    'broker.profile_save_error': 'Profil konnte nicht gespeichert werden',
    'broker.profile_load_error': 'Profil konnte nicht geladen werden',
    'broker.avatar_uploaded': 'Foto erfolgreich hochgeladen',
    'broker.avatar_upload_error': 'Foto konnte nicht hochgeladen werden',
    'broker.invalid_image_type': 'Bitte wählen Sie eine Bilddatei',
    'broker.image_too_large': 'Bild muss kleiner als 2MB sein',
    'common.login_required': 'Bitte melden Sie sich an, um auf diese Seite zuzugreifen.',
    
    // Agency Dashboard
    'agency.my_agency': 'Meine Agentur',
    'agency.dashboard_title': 'Agentur-Dashboard',
    'agency.dashboard_subtitle': 'Verwalten Sie Ihre Makler und Agentureinstellungen',
    'agency.brokers_tab': 'Makler',
    'agency.invites_tab': 'Einladungen',
    'agency.settings_tab': 'Einstellungen',
    'agency.brokers_title': 'Agenturmakler',
    'agency.brokers_description': 'Verwalten Sie Makler in Ihrer Agentur',
    'agency.create_broker': 'Makler erstellen',
    'agency.create_broker_title': 'Neues Maklerkonto erstellen',
    'agency.create_broker_description': 'Erstellen Sie ein neues Maklerkonto, das direkt mit Ihrer Agentur verbunden ist',
    'agency.broker_name': 'Vollständiger Name',
    'agency.broker_email': 'E-Mail-Adresse',
    'agency.broker_password': 'Passwort',
    'agency.create_broker_btn': 'Maklerkonto erstellen',
    'agency.broker_created': 'Maklerkonto erfolgreich erstellt',
    'agency.broker_create_error': 'Maklerkonto konnte nicht erstellt werden',
    'agency.no_brokers': 'Noch keine Makler',
    'agency.no_brokers_description': 'Erstellen Sie Maklerkonten oder senden Sie Einladungen',
    'agency.broker_removed': 'Makler aus der Agentur entfernt',
    'agency.broker_remove_error': 'Makler konnte nicht entfernt werden',
    'agency.invites_title': 'Ausstehende Einladungen',
    'agency.invites_description': 'Verwalten Sie Einladungen an potenzielle Makler',
    'agency.send_invite': 'Einladung senden',
    'agency.send_invite_title': 'Makler einladen',
    'agency.send_invite_description': 'Senden Sie eine Einladung, um Ihrer Agentur beizutreten',
    'agency.invite_email': 'E-Mail-Adresse',
    'agency.send_invite_btn': 'Einladung senden',
    'agency.invite_sent': 'Einladung erfolgreich gesendet',
    'agency.invite_error': 'Einladung konnte nicht gesendet werden',
    'agency.link_copied': 'Einladungslink kopiert',
    'agency.no_invites': 'Keine ausstehenden Einladungen',
    'agency.settings_title': 'Agentureinstellungen',
    'agency.settings_description': 'Aktualisieren Sie Ihre Agenturinformationen',
    'agency.settings_saved': 'Agentureinstellungen gespeichert',
    'agency.settings_save_error': 'Einstellungen konnten nicht gespeichert werden',
    'agency.load_error': 'Agenturdaten konnten nicht geladen werden',
    'agency_invite.title': 'Agentur beitreten',
    'agency_invite.description': 'Sie wurden eingeladen, {agencyName} beizutreten',
    'agency_invite.accept_btn': 'Einladung annehmen',
    'agency_invite.accepted_success': 'Erfolgreich der Agentur beigetreten!',
    'agency_invite.accept_error': 'Einladung konnte nicht angenommen werden',
    'agency_invite.create_account': 'Konto erstellen',
    'agency_invite.invalid_link': 'Ungültiger Einladungslink',
    'agency_invite.expired': 'Diese Einladung ist abgelaufen',
    'agency_invite.already_accepted': 'Diese Einladung wurde bereits angenommen',
    'agency_invite.fetch_error': 'Einladung konnte nicht geladen werden',
    'agency_invite.error_title': 'Ungültige Einladung',
    'agency_invite.back_home': 'Zurück zur Startseite',
    'agency_invite.create_account_description': 'Erstellen Sie ein Konto, um die Einladung anzunehmen',
    'agency_invite.your_name': 'Ihr Name',
    'agency_invite.name_placeholder': 'Geben Sie Ihren vollständigen Namen ein',
    'agency_invite.email_placeholder': 'ihre@email.com',
    'agency_invite.password': 'Passwort',
    'agency_invite.password_placeholder': 'Erstellen Sie ein Passwort',
    'agency_invite.confirm_password': 'Passwort bestätigen',
    'agency_invite.confirm_password_placeholder': 'Bestätigen Sie Ihr Passwort',
    'agency_invite.register_accept_btn': 'Konto erstellen & beitreten',
    'agency_invite.registered_success': 'Konto erstellt und Agentur beigetreten!',
    'agency_invite.register_error': 'Konto konnte nicht erstellt werden',
    'agency_invite.passwords_mismatch': 'Passwörter stimmen nicht überein',
    'agency_invite.password_too_short': 'Passwort muss mindestens 6 Zeichen haben',
    'agency_invite.already_have_account': 'Haben Sie bereits ein Konto?',
    'agency_invite.login_here': 'Hier anmelden',
    'agency_invite.joining': 'Sie treten bei',
    'agency_invite.logged_in_as': 'Angemeldet als',
    'agency.notifications_tab': 'Benachrichtigungen',
    'agency.notifications_title': 'E-Mail-Benachrichtigungen',
    'agency.notifications_description': 'Wählen Sie, welche Benachrichtigungen über Makleraktivitäten Sie erhalten',
    'agency.notifications_saved': 'Benachrichtigungseinstellungen gespeichert',
    'agency.notifications_save_error': 'Einstellungen konnten nicht gespeichert werden',
    'agency.notif_new_broker_joins': 'Neuer Makler tritt bei',
    'agency.notif_new_broker_joins_desc': 'Benachrichtigt werden, wenn ein Makler Ihre Einladung annimmt',
    'agency.notif_broker_property_added': 'Makler fügt Immobilie hinzu',
    'agency.notif_broker_property_added_desc': 'Benachrichtigt werden, wenn Makler neue Immobilien hinzufügen',
    'agency.notif_broker_viewing_requests': 'Besichtigungsanfragen',
    'agency.notif_broker_viewing_requests_desc': 'Über Besichtigungsanfragen für Maklerimmobilien benachrichtigt werden',
    'agency.notif_broker_client_messages': 'Kundennachrichten',
    'agency.notif_broker_client_messages_desc': 'Benachrichtigt werden, wenn Kunden Ihre Makler kontaktieren',
    'agency.notif_weekly_summary': 'Wöchentliche Zusammenfassung',
    'agency.notif_weekly_summary_desc': 'Erhalten Sie eine wöchentliche Zusammenfassung der Agenturaktivitäten',
    'agency.analytics_tab': 'Analytik',
    'agency.no_brokers_analytics': 'Fügen Sie Makler hinzu, um Leistungsanalysen zu sehen',
    'agency.total_properties': 'Immobilien gesamt',
    'agency.total_views': 'Gesamtaufrufe',
    'agency.total_viewings': 'Besichtigungsanfragen',
    'agency.total_sold': 'Verkaufte Immobilien',
    'agency.total_sales_value': 'Gesamtverkaufswert',
    'agency.broker_performance_chart': 'Maklerleistung',
    'agency.broker_performance_chart_desc': 'Vergleichen Sie Maklerkennzahlen auf einen Blick',
    'agency.individual_broker_stats': 'Individuelle Maklerstatistiken',
    'agency.individual_broker_stats_desc': 'Detaillierte Kennzahlen für jeden Makler',
    'agency.properties': 'Immobilien',
    'agency.views': 'Aufrufe',
    'agency.viewings': 'Besichtigungen',
    'agency.contacts': 'Kontakte',
    'agency.active': 'Aktiv',
    'agency.sold': 'Verkauft',
    'agency.sales_value': 'Verkaufswert',
    'agency.upgrade_plan': 'Plan upgraden',
    'agency.view_plans': 'Pläne ansehen',
    'agency.billing_tab': 'Abrechnung',
    'agency.name_label': 'Agenturname',
    'agency.email_label': 'E-Mail',
    'agency.phone_label': 'Telefon',
    'agency.website_label': 'Webseite',
    'agency.address_label': 'Adresse',
    'agency.city_label': 'Stadt',
    'agency.description_label': 'Beschreibung',
    'agency.description_placeholder': 'Erzählen Sie Kunden von Ihrer Agentur...',
    'agency.save_settings': 'Einstellungen speichern',
    'agency.broker_usage': 'Maklernutzung',
    'agency.invite_broker': 'Makler einladen',
    'agency.brokers': 'Makler',
    'agency.broker_limit_reached': 'Maklerlimit erreicht',
    'agency.upgrade_for_more': 'Upgraden Sie Ihren Plan, um mehr Makler hinzuzufügen',
    'agency.active_listings': 'Aktive Anzeigen',
    'agency.broker_slots_remaining': 'Verbleibende Plätze',
    'agency.manage_subscription': 'Abonnement verwalten',
    'agency.upgrade_your_plan': 'Ihren Plan upgraden',
    'agency.choose_plan': 'Wählen Sie den Plan, der am besten zu Ihrer Agentur passt',
    'agency.pro_plan': 'Pro',
    'agency.enterprise_plan': 'Enterprise',
    'agency.for_growing_agencies': 'Für wachsende Agenturen',
    'agency.for_large_agencies': 'Für große Agenturen',
    'agency.per_month': '/Monat',
    'agency.up_to_brokers': 'Bis zu {count} Makler',
    'agency.unlimited_brokers': 'Unbegrenzte Makler',
    'agency.advanced_analytics': 'Erweiterte Analysen',
    'agency.full_analytics': 'Vollständiges Analysepaket',
    'agency.priority_support': 'Prioritäts-Support',
    'agency.dedicated_support': 'Dedizierter Support',
    'agency.upgrade_to_pro': 'Auf Pro upgraden',
    'agency.upgrade_to_enterprise': 'Auf Enterprise upgraden',
    'agency.popular': 'Beliebt',
    'agency.current_plan': 'Aktueller Plan',
    'agency.plan_features': 'Planfunktionen',
    'agency.billing_history': 'Rechnungsverlauf',
    'agency.no_billing_history': 'Noch kein Rechnungsverlauf',
    'agency.subscription_billing': 'Abonnement & Abrechnung',
    'agency.manage_agency_subscription': 'Verwalten Sie das Abonnement Ihrer Agentur',
    'agency.plan': 'Plan',
    'agency.basic_plan': 'Basis',
    'agency.broker_limit': 'Maklerlimit',
    'agency.current_usage': 'Aktuelle Nutzung',
    'agency.status': 'Status',
    'agency.active_status': 'Aktiv',
    'agency.unlimited': 'Unbegrenzt',
    'agency.feature': 'Funktion',
    'agency.price': 'Preis',
    'agency.free': 'Kostenlos',
    'agency.analytics': 'Analytik',
    'agency.basic_analytics': 'Basis',
    'agency.advanced': 'Erweitert',
    'agency.full_suite': 'Vollständige Suite',
    'agency.support': 'Support',
    'agency.email_support': 'E-Mail',
    'agency.priority': 'Priorität',
    'agency.dedicated': 'Dediziert',
    'agency_directory.title': 'Immobilienagenturen',
    'agency_directory.subtitle': 'Durchsuchen und finden Sie vertrauenswürdige Immobilienagenturen',
    'agency_directory.search_placeholder': 'Agenturen suchen...',
    'agency_directory.location_placeholder': 'Nach Standort filtern...',
    'agency_directory.agencies_found': 'Agenturen gefunden',
    'agency_directory.no_agencies': 'Keine Agenturen gefunden',
    'agency_directory.no_agencies_description': 'Versuchen Sie, Ihre Suchkriterien anzupassen',
    'agency_directory.brokers': 'Makler',
    'agency_directory.view_agency': 'Agentur ansehen',
    'agency_directory.not_found': 'Agentur nicht gefunden',
    'agency_directory.back_to_directory': 'Zurück zum Verzeichnis',
    'agency_directory.visit_website': 'Website besuchen',
    'agency_directory.our_brokers': 'Unsere Makler',
    'agency_directory.no_brokers_yet': 'Noch keine Makler in dieser Agentur',
    'agency_directory.agency_listings': 'Immobilienanzeigen',
    
    // User Listings
    'userListings.title': 'Meine Anzeigen',
    'userListings.subtitle': 'Verwalten Sie Ihre Immobilienanzeigen und erstellen Sie neue',
    'userListings.pricingTitle': 'Anzeigenpreise',
    'userListings.pricingDesc': 'Erstellen Sie Ihre Immobilienanzeige mit unserer einfachen Preisgestaltung',
    'userListings.listingPrice': '10$ pro Anzeige',
    'userListings.listingDuration': 'Gültig für 1 Monat',
    'userListings.videoPrice': '+3$ für Video',
    'userListings.videoDesc': 'Fügen Sie eine Videotour zu Ihrer Anzeige hinzu',
    'userListings.includeVideo': 'Videotour einschließen',
    'userListings.createListing': 'Anzeige erstellen',
    'userListings.myListingsTitle': 'Ihre Immobilien',
    'userListings.noListings': 'Noch keine Anzeigen',
    'userListings.noListingsDesc': 'Erstellen Sie Ihre erste Immobilienanzeige um zu beginnen',
    'userListings.createFirst': 'Erste Anzeige erstellen',
    'userListings.hasVideo': 'Video',
    'userListings.renew': 'Erneuern',
    'userListings.renewSuccess': 'Anzeige erneuert!',
    'userListings.renewSuccessDesc': 'Ihre Anzeige wurde um einen weiteren Monat verlängert',
    'userListings.paymentSuccess': 'Zahlung erfolgreich!',
    'userListings.paymentSuccessDesc': 'Ihre Anzeige ist jetzt live',
    'userListings.paymentCancelled': 'Zahlung abgebrochen',
    'userListings.paymentCancelledDesc': 'Ihre Zahlung wurde abgebrochen',
    'userListings.checkoutError': 'Checkout konnte nicht erstellt werden',
    'userListings.renewError': 'Anzeige konnte nicht erneuert werden',
    'userListings.createDraft': 'Anzeige erstellen',
    'userListings.previewListing': 'Vorschau Ihrer Anzeige',
    'userListings.draft': 'Entwurf',
    'userListings.drafts': 'Entwürfe',
    'userListings.draftSaved': 'Entwurf gespeichert',
    'userListings.draftSavedDesc': 'Ihre Anzeige wurde als Entwurf gespeichert',
    'userListings.draftError': 'Entwurf konnte nicht gespeichert werden',
    'userListings.draftDeleted': 'Entwurf gelöscht',
    'userListings.deleteError': 'Löschen fehlgeschlagen',
    'userListings.saveDraft': 'Entwurf speichern',
    'userListings.saveAndPreview': 'Speichern & Vorschau',
    'userListings.publishOptions': 'Veröffentlichungsoptionen',
    'userListings.addVideoTour': 'Videotour hinzufügen',
    'userListings.videoIncluded': 'Videotour enthalten',
    'userListings.totalPrice': 'Gesamt',
    'userListings.backToEdit': 'Zurück zur Bearbeitung',
    'userListings.publishNow': 'Jetzt veröffentlichen',
    'userListings.publish': 'Veröffentlichen',
    'userListings.publishSuccess': 'Anzeige veröffentlicht!',
    'userListings.publishSuccessDesc': 'Ihre Anzeige wartet nun auf Überprüfung',
    'userListings.firstListingFree': 'Erste Anzeige ist kostenlos!',
    'userListings.firstListingFreeDesc': 'Ihre erste Immobilienanzeige ist völlig kostenlos. Jetzt einreichen und Käufer im ganzen Irak erreichen!',
    'userListings.pending': 'Überprüfung ausstehend',
    'userListings.pendingInfo': 'Diese Anzeigen werden von unserem Moderationsteam überprüft. Sie werden benachrichtigt, sobald sie genehmigt werden.',
    'userListings.pendingReview': 'Überprüfung ausstehend',
    'userListings.awaitingApproval': 'Warten auf Moderatorgenehmigung',
    'userListings.rejected': 'Abgelehnte Anzeigen',
    'userListings.rejectedInfo': 'Diese Anzeigen wurden nicht genehmigt. Bitte bearbeiten und beheben Sie die Probleme vor dem erneuten Einreichen.',
    'userListings.rejectedBadge': 'Abgelehnt',
    'userListings.live': 'Aktiv',
    'userListings.editDraft': 'Bearbeiten',
    'userListings.editAndResubmit': 'Bearbeiten & erneut einreichen',
    'userListings.pendingCannotEdit': 'Bearbeitung während der Überprüfung nicht möglich',
    'userListings.resubmitForReview': 'Zur Überprüfung erneut einreichen',
    'userListings.resubmitInfo': 'Ihre Änderungen wurden gespeichert. Reichen Sie Ihre Anzeige erneut zur Überprüfung ohne zusätzliche Kosten ein.',
    'userListings.submitForReview': 'Zur Überprüfung einreichen',
    'userListings.validation.priceRequired': 'Preis ist erforderlich',
    'userListings.validation.cityRequired': 'Stadt ist erforderlich',
    'userListings.validation.roomsRequired': 'Zimmeranzahl ist erforderlich',
    'userListings.validation.sizeRequired': 'Größe ist erforderlich',
    'userListings.validation.plotSizeRequired': 'Grundstücksgröße ist erforderlich',
    'userListings.validation.imagesRequired': 'Mindestens ein Bild ist erforderlich',
    'userListings.choosePlan': 'Wählen Sie Ihren Anzeigenplan',
    'userListings.choosePlanDesc': 'Wählen Sie, wie viele Bilder auf Ihrer Anzeigenkarte angezeigt werden sollen',
    'userListings.upgradePlan': 'Upgraden Sie Ihren Anzeigenplan',
    'userListings.upgradePlanDesc': 'Upgraden Sie, um mehr Bilder anzuzeigen. Sie zahlen nur die Differenz.',
    'userListings.selectImages': 'Bilder zur Anzeige auswählen',
    'userListings.selectImagesDesc': 'Wählen Sie Bilder aus, die auf Ihrer Anzeigenkarte angezeigt werden sollen',
    'userListings.selectPrimaryImage': 'Hauptbild auswählen',
    'userListings.selectPrimaryImageDesc': 'Wählen Sie das Hauptbild, das zuerst auf Ihrer Anzeige angezeigt wird',
    'userListings.addVideo': 'Videotour hinzufügen',
    'userListings.pleaseSelectImages': 'Bitte wählen Sie Bilder aus, um fortzufahren',
    'userListings.pleaseSelectPrimaryImage': 'Bitte wählen Sie ein Hauptbild aus, um fortzufahren',
    'userListings.proceedToPayment': 'Weiter zur Zahlung',
    'userListings.tierBasic': 'Basis',
    'userListings.tierStandard': 'Standard',
    'userListings.tierPremium': 'Premium',
    'userListings.tierBasicDesc': '1 Bild auf der Karte',
    'userListings.tierStandardDesc': '3 Bilder Karussell',
    'userListings.tierPremiumDesc': 'Alle Bilder + Abzeichen',
    'userListings.tierDuration': '30 Tage aktiv',
    'userListings.tierBest': 'Beste',
    'userListings.tierBasicFull': 'Basis-Stufe (1 Bild)',
    'userListings.tierStandardFull': 'Standard-Stufe (3 Bilder)',
    'userListings.tierPremiumFull': 'Premium-Stufe (Alle Bilder)',
    'userListings.changeTier': 'Stufe ändern',
    'userListings.selectImageTier': 'Bildstufe auswählen',
    'userListings.selectTierFirst': 'Wählen Sie eine Stufe vor der Veröffentlichung',
    'userListings.upgrade': 'Upgrade',
    'common.total': 'Gesamt',
    
    // Settings Page
    'settings.title': 'Einstellungen',
    'settings.subtitle': 'Verwalten Sie Ihre Kontoeinstellungen und Präferenzen',
    'settings.profile': 'Profil',
    'settings.notifications': 'Benachrichtigungen',
    'settings.account': 'Konto',
    'settings.profile_info': 'Profilinformationen',
    'settings.profile_desc': 'Aktualisieren Sie Ihre persönlichen Informationen',
    'settings.change_photo': 'Foto ändern',
    'settings.display_name': 'Anzeigename',
    'settings.display_name_placeholder': 'Ihr Name',
    'settings.email': 'E-Mail',
    'settings.email_placeholder': 'ihre@email.de',
    'settings.phone': 'Telefon',
    'settings.phone_placeholder': '+49 170 123 4567',
    'settings.save': 'Änderungen speichern',
    'settings.saved': 'Einstellungen erfolgreich gespeichert',
    'settings.load_error': 'Einstellungen konnten nicht geladen werden',
    'settings.save_error': 'Einstellungen konnten nicht gespeichert werden',
    'settings.invalid_image': 'Bitte wählen Sie eine Bilddatei aus',
    'settings.image_too_large': 'Das Bild muss kleiner als 2MB sein',
    'settings.avatar_uploaded': 'Foto hochgeladen',
    'settings.avatar_error': 'Foto konnte nicht hochgeladen werden',
    'settings.notification_preferences': 'Benachrichtigungseinstellungen',
    'settings.notification_desc': 'Wählen Sie, welche Benachrichtigungen Sie erhalten möchten',
    'settings.notif_property_alerts': 'Immobilienbenachrichtigungen',
    'settings.notif_property_alerts_desc': 'Werden Sie über neue Immobilien benachrichtigt',
    'settings.notif_saved_search': 'Gespeicherte Suchbenachrichtigungen',
    'settings.notif_saved_search_desc': 'Werden Sie benachrichtigt, wenn neue Immobilien Ihren gespeicherten Suchen entsprechen',
    'settings.notif_newsletter': 'Newsletter',
    'settings.notif_newsletter_desc': 'Erhalten Sie unseren Newsletter mit Marktaktualisierungen',
    'settings.notif_viewing': 'Besichtigungsbestätigungen',
    'settings.notif_viewing_desc': 'Erhalten Sie Bestätigungen für geplante Besichtigungen',
    'settings.account_settings': 'Kontoeinstellungen',
    'settings.account_desc': 'Verwalten Sie Ihre Kontosicherheit und Präferenzen',
    'settings.change_password': 'Passwort ändern',
    'settings.change_password_desc': 'Aktualisieren Sie Ihr Passwort',
    'settings.change': 'Ändern',
    'settings.two_factor': 'Zwei-Faktor-Authentifizierung',
    'settings.two_factor_desc': 'Fügen Sie Ihrem Konto zusätzliche Sicherheit hinzu',
    'settings.enable': 'Aktivieren',
    'settings.delete_account': 'Konto löschen',
    'settings.delete_account_desc': 'Löschen Sie Ihr Konto dauerhaft',
    'settings.delete': 'Löschen',
    'settings.logout': 'Abmelden',
    
    // Common
    'common.back': 'Zurück',
    
    // Contact Form Modal
    'contact_form.title': 'Kontakt',
    'contact_form.description': 'Senden Sie eine Nachricht, um in Kontakt zu treten.',
    'contact_form.name': 'Name',
    'contact_form.name_placeholder': 'Ihr Name',
    'contact_form.email': 'E-Mail',
    'contact_form.email_placeholder': 'ihre@email.de',
    'contact_form.phone': 'Telefon',
    'contact_form.phone_placeholder': 'Ihre Telefonnummer',
    'contact_form.message': 'Nachricht',
    'contact_form.message_placeholder': 'Wie können wir Ihnen helfen?',
    'contact_form.send': 'Nachricht senden',
    'contact_form.sending': 'Wird gesendet...',
    'contact_form.success_title': 'Nachricht gesendet!',
    'contact_form.success_message': 'Ihre Nachricht wurde gesendet. Sie erhalten eine Bestätigung per E-Mail.',
    'contact_form.error_title': 'Fehler',
    'contact_form.error_message': 'Nachricht konnte nicht gesendet werden. Bitte versuchen Sie es erneut.',
    
    // Messaging
    'messaging.title': 'Nachrichten',
    'messaging.description': 'Ihre privaten Unterhaltungen',
    'messaging.new_message': 'Neue Nachricht',
    'messaging.no_messages': 'Noch keine Nachrichten',
    'messaging.no_conversations': 'Noch keine Unterhaltungen',
    'messaging.send': 'Senden',
    'messaging.send_message': 'Nachricht senden',
    'messaging.message_to': 'Nachricht senden an',
    'messaging.subject': 'Betreff',
    'messaging.subject_placeholder': 'Betreff (optional)',
    'messaging.message': 'Nachricht',
    'messaging.message_placeholder': 'Schreiben Sie Ihre Nachricht...',
    'messaging.login_required': 'Sie müssen angemeldet sein, um Nachrichten zu senden.',
    'messaging.message_sent': 'Nachricht erfolgreich gesendet',
    'messaging.contact_agent_description': 'Kontaktieren Sie den Immobilienmakler direkt',
    'messaging.type_message': 'Nachricht eingeben...',
    'messaging.inbox': 'Posteingang',
    'messaging.sent': 'Gesendet',
    'messaging.search': 'Unterhaltungen suchen...',
    'messaging.select_conversation': 'Unterhaltung auswählen',
    'messaging.select_description': 'Wählen Sie eine Unterhaltung aus der Liste, um zu chatten',
    'messaging.encrypted': 'Ende-zu-Ende-verschlüsselt',
    'messaging.decrypt': 'Nachricht entschlüsseln',
    'messaging.reply': 'Antworten',
    'messaging.replying_to': 'Antwort an',
    'messaging.replying_to_yourself': 'sich selbst',
    'messaging.forward': 'Weiterleiten',
    'messaging.delete': 'Löschen',
    'messaging.delete_confirm': 'Nachricht löschen',
    'messaging.delete_description': 'Diese Nachricht wird für alle gelöscht. Diese Aktion kann nicht rückgängig gemacht werden.',
    'messaging.delete_for_everyone': 'Für alle löschen',
    'messaging.pin': 'Anheften',
    'messaging.unpin': 'Lösen',
    'messaging.pinned_messages': 'Angeheftete Nachrichten',
    'messaging.no_pinned': 'Keine angehefteten Nachrichten',
    'messaging.delivered': 'Zugestellt',
    'messaging.read': 'Gelesen',
    'messaging.seen_at': 'Gesehen um',
    'messaging.voice_message': 'Sprachnachricht',
    'messaging.search_messages': 'In Nachrichten suchen...',
    'messaging.contacts': 'Kontakte',
    'messaging.results_found': 'Ergebnisse gefunden',
    'messaging.recording': 'Aufnahme...',
    'messaging.typing': 'schreibt...',
    'messaging.online': 'online',
    'messaging.tap_for_info': 'hier tippen für Kontaktinfo',
    'messaging.photos': 'Fotos',
    'messaging.camera': 'Kamera',
    'messaging.location': 'Standort',
    'messaging.contact': 'Kontakt',
    'messaging.document': 'Dokument',
    'messaging.poll': 'Umfrage',
    'messaging.event': 'Ereignis',
    'messaging.location_coming_soon': 'Standortfreigabe kommt bald',
    'messaging.contact_coming_soon': 'Kontaktfreigabe kommt bald',
    'messaging.poll_coming_soon': 'Umfrageerstellung kommt bald',
    'messaging.event_coming_soon': 'Ereigniserstellung kommt bald',
    'messaging.schedule_event': 'Ereignis planen',
    'messaging.event_title': 'Ereignistitel',
    'messaging.event_title_placeholder': 'z.B. Immobilienbesichtigung',
    'messaging.event_date': 'Datum',
    'messaging.event_time': 'Zeit',
    'messaging.event_location': 'Ort',
    'messaging.event_location_placeholder': 'Ort oder Adresse eingeben',
    'messaging.event_description': 'Beschreibung',
    'messaging.event_description_placeholder': 'Weitere Details hinzufügen...',
    'messaging.send_event': 'Ereignis senden',
    'messaging.event_created': 'Ereignis erstellt',
    'messaging.create_poll': 'Umfrage erstellen',
    'messaging.poll_question': 'Frage',
    'messaging.poll_question_placeholder': 'Was möchten Sie fragen?',
    'messaging.poll_options': 'Optionen',
    'messaging.option': 'Option',
    'messaging.add_option': 'Option hinzufügen',
    'messaging.allow_multiple': 'Mehrfachantworten erlauben',
    'messaging.send_poll': 'Umfrage senden',
    'messaging.poll_created': 'Umfrage erstellt',
    'messaging.edit': 'Bearbeiten',
    'messaging.edit_message': 'Nachricht bearbeiten',
    'messaging.edit_description': 'Änderungen an Ihrer Nachricht vornehmen',
    'messaging.edit_time_remaining': 'Sie haben',
    'messaging.minutes_to_edit': 'Minuten, um diese Nachricht zu bearbeiten',
    'messaging.message_edited': 'Nachricht bearbeitet',
    'messaging.edit_failed': 'Nachricht konnte nicht bearbeitet werden',
    'messaging.from_date': 'Von',
    'messaging.to_date': 'Bis',
    'messaging.clear_filters': 'Filter löschen',
    'messaging.export_chat': 'Chat exportieren',
    'messaging.export_description': 'Exportieren Sie Ihren Gesprächsverlauf',
    'messaging.export_text': 'Textdatei (.txt)',
    'messaging.export_text_desc': 'Einfaches Textformat, leicht zu lesen und zu teilen',
    'messaging.export_pdf': 'PDF-Dokument',
    'messaging.export_pdf_desc': 'Formatiertes Dokument, ideal zum Drucken',
    'messaging.export_messages_count': 'Nachrichten werden exportiert',
    'messaging.exporting': 'Exportieren...',
    'messaging.export': 'Exportieren',
    'messaging.export_success': 'Chat erfolgreich exportiert',
    'messaging.export_failed': 'Chat-Export fehlgeschlagen',
    'messaging.is_typing': 'tippt...',
    
    // Quick Reply Templates
    'quick_reply.title': 'Schnellantworten',
    'quick_reply.greeting': 'Hallo! Danke für Ihre Kontaktaufnahme.',
    'quick_reply.interested': 'Ich interessiere mich für diese Immobilie. Können Sie mir mehr Details geben?',
    'quick_reply.schedule_viewing': 'Ich möchte einen Besichtigungstermin vereinbaren. Welche Zeiten sind verfügbar?',
    'quick_reply.ask_price': 'Ist der Preis verhandelbar?',
    'quick_reply.ask_availability': 'Ist diese Immobilie noch verfügbar?',
    'quick_reply.thank_you': 'Danke für die Informationen. Ich melde mich bald.',
    'quick_reply.will_contact': 'Ich werde Sie in Kürze mit weiteren Details kontaktieren.',
    'quick_reply.not_interested': 'Danke, aber ich bin an dieser Immobilie nicht mehr interessiert.',
    
    // Notifications
    'notifications.enable': 'Benachrichtigungen aktivieren',
    'notifications.on': 'Benachrichtigungen an',
    'notifications.preferences': 'Benachrichtigungseinstellungen',
    'notifications.sound': 'Benachrichtigungston',
    'notifications.sound_desc': 'Ton abspielen, wenn Sie eine Nachricht erhalten',
    'notifications.sound_default': 'Standard',
    'notifications.sound_chime': 'Glockenspiel',
    'notifications.sound_bell': 'Glocke',
    'notifications.sound_pop': 'Pop',
    'notifications.test': 'Testen',
    'notifications.vibration': 'Vibration',
    'notifications.vibration_desc': 'Vibrieren, wenn Sie eine Nachricht erhalten (nur mobil)',
    'messaging.you': 'Du',
    'messaging.encrypted_message': 'Verschlüsselte Nachricht',
    'messaging.e2e_notice': 'Nachrichten und Anrufe sind Ende-zu-Ende-verschlüsselt. Nur Personen in diesem Chat können sie lesen, anhören oder teilen.',
    'messaging.today': 'Heute',
    'messaging.yesterday': 'Gestern',
    'messaging.search_groups': 'Gruppen suchen...',
    
    // Attachment viewer
    'attachment.all_media': 'Alle Medien',
    'attachment.go_to_message': 'Zur Nachricht gehen',
    'attachment.download': 'Herunterladen',
    'attachment.view_fullscreen': 'Vollbild anzeigen',
    'attachment.save_to_gallery': 'In Galerie speichern',
    'attachment.share': 'Teilen',
    'attachment.double_tap_zoom': 'Doppeltippen zum Zoomen',
    'attachment.zoom_in': 'Vergrößern',
    'attachment.zoom_out': 'Verkleinern',
    'attachment.reset_zoom': 'Zoom zurücksetzen',
    'attachment.ctrl_scroll_zoom': 'Strg + Scrollen zum Zoomen',
    'attachment.fit_width': 'An Breite anpassen',
    'attachment.fit_height': 'An Höhe anpassen',
    'attachment.click_to_navigate': 'Klicken zum Navigieren',
    
    // Status
    'status.online': 'Online',
    'status.offline': 'Offline',
    'status.last_seen': 'Zuletzt gesehen',
    
    // Blocking
    'blocking.block_user': 'Benutzer blockieren',
    'blocking.block_confirm': 'Sind Sie sicher, dass Sie {name} blockieren möchten? Sie können Ihnen keine Nachrichten mehr senden.',
    'blocking.reason_optional': 'Grund (optional)',
    'blocking.reason_placeholder': 'Warum blockieren Sie diesen Benutzer?',
    'blocking.block': 'Blockieren',
    'blocking.user_blocked': 'Benutzer erfolgreich blockiert',
    'blocking.already_blocked': 'Benutzer ist bereits blockiert',
    'blocking.error': 'Benutzer konnte nicht blockiert werden',
    'blocking.blocked_users': 'Blockierte Benutzer',
    'blocking.blocked_users_description': 'Verwalten Sie Ihre Liste blockierter Benutzer',
    'blocking.no_blocked_users': 'Sie haben niemanden blockiert',
    'blocking.blocked_on': 'Blockiert am {date}',
    'blocking.unblock': 'Entsperren',
    'blocking.user_unblocked': 'Benutzer entsperrt',
    'blocking.unblock_error': 'Benutzer konnte nicht entsperrt werden',
    'blocking.fetch_error': 'Blockierte Benutzer konnten nicht geladen werden',
    'blocking.you_blocked_user': 'Sie haben diesen Benutzer blockiert. Entsperren Sie, um Nachrichten zu senden.',
    'blocking.user_blocked_you': 'Dieser Benutzer hat Sie blockiert. Sie können keine Nachrichten senden.',
    
    // Reporting
    'reporting.report_user': 'Benutzer melden',
    'reporting.report_description': '{name} wegen Verstoß gegen unsere Richtlinien melden',
    'reporting.select_reason': 'Bitte wählen Sie einen Grund',
    'reporting.reason': 'Grund der Meldung',
    'reporting.additional_details': 'Zusätzliche Details',
    'reporting.details_placeholder': 'Geben Sie zusätzlichen Kontext an...',
    'reporting.submit_report': 'Meldung absenden',
    'reporting.report_submitted': 'Meldung eingereicht. Wir werden sie in Kürze prüfen.',
    'reporting.error': 'Meldung konnte nicht eingereicht werden',
    'reporting.reasons.spam': 'Spam oder unerwünschte Nachrichten',
    'reporting.reasons.harassment': 'Belästigung oder Mobbing',
    'reporting.reasons.inappropriate_content': 'Unangemessener Inhalt',
    'reporting.reasons.fraud': 'Betrug oder Abzocke',
    'reporting.reasons.impersonation': 'Identitätsdiebstahl',
    'reporting.reasons.other': 'Sonstiges',
    
    // Admin Reports
    'admin.reports': 'Meldungen',
    'admin.reports_panel': 'Benutzermeldungen',
    'admin.reports_description': 'Benutzermeldungen prüfen und bearbeiten',
    'admin.fetch_reports_error': 'Meldungen konnten nicht abgerufen werden',
    'admin.pending': 'Ausstehend',
    'admin.resolved': 'Gelöst',
    'admin.all': 'Alle',
    'admin.no_reports': 'Keine Meldungen gefunden',
    'admin.back_to_list': 'Zurück zur Liste',
    'admin.reporter': 'Melder',
    'admin.reported_user': 'Gemeldeter Benutzer',
    'admin.reason': 'Grund',
    'admin.reported_on': 'Gemeldet am',
    'admin.reported_by': 'Gemeldet von',
    'admin.resolution_notes': 'Lösungsnotizen hinzufügen...',
    'admin.dismiss': 'Ablehnen',
    'admin.warn': 'Benutzer warnen',
    'admin.block': 'Benutzer blockieren',
    'admin.report_resolved': 'Meldung erfolgreich bearbeitet',
    'admin.resolve_error': 'Meldung konnte nicht bearbeitet werden',
    'admin.resolution': 'Lösung',
    'messaging.unknown_user': 'Unbekannter Benutzer',
    'messaging.no_groups': 'Noch keine Gruppen',
    'messaging.create_group': 'Gruppe erstellen',
    'messaging.group_chat': 'Gruppenchat',
    'messaging.direct_messages': 'Direkt',
    'messaging.groups': 'Gruppen',
    'messaging.group_name': 'Gruppenname',
    'messaging.group_description': 'Beschreibung',
    
    'nav.favorites': 'Favoriten',
    'nav.profile': 'Profil',
    'nav.login': 'Anmelden',
    'nav.logout': 'Abmelden',
    'nav.recent_searches': 'Letzte Suchen',
    'nav.no_recent_searches': 'Keine letzten Suchen',
    
    'agents.title': 'Makler finden',
    'agents.subtitle': 'Denken Sie an einen Verkauf? Wir helfen Ihnen, Makler zu finden, die Experten in Ihrer Region sind!',
    'agents.area_search': 'Gebietssuche',
    'agents.name_search': 'Namenssuche',
    'agents.search_placeholder': 'Gebiet oder Adresse eingeben',
    'agents.show': 'Anzeigen',
    'agents.sort_by': 'Sortieren nach',
    'agents.agents_count': 'Makler',
    'agents.companies_label': 'Maklerfirmen',
    'agents.recent_sales': 'Letztes Verkaufsdatum',
    'agents.most_sales': 'Meiste Verkäufe',
    'agents.alphabetical': 'Alphabetische Reihenfolge',
    'agents.showing_results': 'Zeigt',
    'agents.agents_label': 'Makler',
    'agents.loading': 'Makler werden geladen...',
    'agents.no_results': 'Keine Makler gefunden.',
    'agents.phone': 'Telefon:',
    'agents.status': 'Status:',
    'agents.active_agent': 'Aktiver Makler',
    'agents.anonymous_agent': 'Anonymer Makler',
    'agents.agent_label': 'Makler',
    'agents.show_more': 'Mehr Makler anzeigen',
    'agents.verified_agent': 'Verifizierter Makler',
    
    // Broker Public Profile
    'broker.listing_usage': 'Anzeigennutzung',
    'broker.monthly_listing_limits': 'Monatliche Anzeigenlimits',
    'broker.listings_used': 'Verwendete Anzeigen',
    'broker.listings_remaining': 'Anzeigen übrig',
    'broker.period_resets': 'Zeitraum wird zurückgesetzt',
    'broker.export_csv': 'CSV exportieren',
    'broker.total_views': 'Gesamtaufrufe',
    'broker.scheduled_viewings': 'Geplante Besichtigungen',
    'broker.client_messages': 'Kundennachrichten',
    'broker.filters': 'Filter',
    'broker.all_time': 'Gesamtzeit',
    'broker.active_listings': 'Aktive Anzeigen',
    'broker.sold_listings': 'Verkaufte Anzeigen',
    'broker.active_listings_desc': 'Ihre aktuell veröffentlichten Immobilien',
    'broker.no_active_listings': 'Noch keine aktiven Anzeigen. Fügen Sie Ihre erste Immobilie hinzu.',
    'broker.all_types': 'Alle Typen',
    'broker.all_cities': 'Alle Städte',
    'broker.property_type': 'Immobilientyp',
    'broker.city': 'Stadt',
    'broker.date_range': 'Datumsbereich',
    'broker.last_week': 'Letzte Woche',
    'broker.last_month': 'Letzter Monat',
    'broker.last_quarter': 'Letzte 3 Monate',
    'broker.sold_listings_desc': 'Von Ihnen verkaufte Immobilien',
    'broker.no_sold_listings': 'Noch keine verkauften Immobilien',
    'broker.clear_filters': 'Filter löschen',
    'broker.selected': 'ausgewählt',
    'broker.compare': 'Vergleichen',
    'broker.mark_as_sold': 'Als verkauft markieren',
    'broker.delete_selected': 'Ausgewählte löschen',
    'broker.select_all': 'Alle auswählen',
    'broker.rejected_listings': 'Abgelehnte Anzeigen',
    'broker.rejected_listings_desc': 'Diese Anzeigen wurden von Moderatoren abgelehnt. Sie können Einspruch einlegen.',
    'broker.no_rejected_listings': 'Keine abgelehnten Anzeigen',
    'broker.confirm_delete': 'Löschen bestätigen',
    'broker_public.not_found': 'Makler nicht gefunden',
    'broker_public.not_found_description': 'Das gesuchte Maklerprofil existiert nicht.',
    'broker_public.back_to_search': 'Zurück zur Maklersuche',
    'broker_public.contact_broker': 'Makler kontaktieren',
    'broker_public.agency': 'Agentur',
    'broker_public.visit_website': 'Website besuchen',
    'broker_public.active_listings': 'Aktive Anzeigen',
    'broker_public.sold_properties': 'Verkaufte Immobilien',
    'broker_public.total_sales': 'Gesamtumsatz',
    'broker_public.avg_days_market': 'Ø Tage auf dem Markt',
    'broker_public.no_active_listings': 'Derzeit keine aktiven Anzeigen',
    'broker_public.no_sold_properties': 'Noch keine verkauften Immobilien',
    'broker_public.view_all_properties': 'Alle Immobilien anzeigen',
    'broker_public.send_message': 'Nachricht senden',
    'broker_public.view_agency': 'Agentur anzeigen',
    'broker_public.works_at': 'Arbeitet bei',
    'broker_public.listings': 'Anzeigen',
    'broker_public.sold': 'Verkauft',
    'broker_public.reviews': 'Bewertungen',
    'broker_public.rating': 'Bewertung',
    'broker_public.featured_broker': 'Empfohlener Makler',
    'broker_public.top_performer': 'Top-Performer',
    
    // Toast messages
    'toast.logged_out': 'Abgemeldet',
    'toast.welcome_back': 'Willkommen zurück jederzeit!',
    
    // Recent searches
    'search.properties_label': 'Immobilien',
    'search.from': 'ab',
    'search.up_to': 'bis',
    
    // Agency Pricing
    'agency_pricing.badge': 'Agenturpläne',
    'agency_pricing.title': 'Wachsen Sie Ihre Agentur mit dem richtigen Plan',
    'agency_pricing.subtitle': 'Wählen Sie den perfekten Plan für Ihre Immobilienagentur.',
    'agency_pricing.most_popular': 'Am beliebtesten',
    'agency_pricing.per_month': '/Monat',
    'agency_pricing.processing': 'Verarbeitung...',
    'agency_pricing.comparison_title': 'Detaillierter Funktionsvergleich',
    'agency_pricing.feature': 'Funktion',
    'agency_pricing.faq_title': 'Häufig gestellte Fragen',
    'agency_pricing.cta_title': 'Bereit, Ihre Agentur zu vergrößern?',
    'agency_pricing.cta_subtitle': 'Schließen Sie sich Tausenden erfolgreicher Immobilienagenturen an.',
    'agency_pricing.cta_button': 'Mit Pro starten',
    'agency_pricing.sign_in_required': 'Bitte melden Sie sich an',
    'agency_pricing.tier.starter': 'Starter',
    'agency_pricing.tier.starter_desc': 'Perfekt für kleine Agenturen',
    'agency_pricing.tier.starter_cta': 'Loslegen',
    'agency_pricing.tier.pro': 'Pro',
    'agency_pricing.tier.pro_desc': 'Für wachsende Agenturen',
    'agency_pricing.tier.pro_cta': 'Auf Pro upgraden',
    'agency_pricing.tier.enterprise': 'Enterprise',
    'agency_pricing.tier.enterprise_desc': 'Für große Agenturen',
    'agency_pricing.tier.enterprise_cta': 'Enterprise wählen',
    'agency_pricing.feature.brokers_3': 'Bis zu 3 Makler',
    'agency_pricing.feature.brokers_10': 'Bis zu 10 Makler',
    'agency_pricing.feature.brokers_unlimited': 'Unbegrenzte Makler',
    'agency_pricing.feature.listings_basic': 'Basis-Immobilienanzeigen',
    'agency_pricing.feature.listings_advanced': 'Erweiterte Anzeigen',
    'agency_pricing.feature.listings_premium': 'Premium-Anzeigen',
    'agency_pricing.feature.messaging': 'Kundennachrichten',
    'agency_pricing.feature.scheduling': 'Besichtigungsplanung',
    'agency_pricing.feature.analytics': 'Analytics-Dashboard',
    'agency_pricing.feature.analytics_advanced': 'Erweiterte Analytics',
    'agency_pricing.feature.support': 'Prioritäts-Support',
    'agency_pricing.feature.support_247': '24/7 Support',
    'agency_pricing.feature.branding': 'Custom Branding',
    'agency_pricing.feature.api': 'API-Zugang',
    'agency_pricing.comparison.brokers': 'Anzahl der Makler',
    'agency_pricing.comparison.listings': 'Immobilienanzeigen',
    'agency_pricing.comparison.basic': 'Basis',
    'agency_pricing.comparison.advanced': 'Erweitert',
    'agency_pricing.comparison.premium': 'Premium',
    'agency_pricing.faq.change_plan_q': 'Kann ich meinen Plan später ändern?',
    'agency_pricing.faq.change_plan_a': 'Ja! Sie können jederzeit upgraden oder downgraden.',
    'agency_pricing.faq.exceed_limit_q': 'Was passiert bei Überschreitung des Makler-Limits?',
    'agency_pricing.faq.exceed_limit_a': 'Sie müssen upgraden, um mehr Makler hinzuzufügen.',
    'agency_pricing.faq.trial_q': 'Gibt es eine kostenlose Testversion?',
    'agency_pricing.faq.trial_a': 'Wir bieten eine 14-tägige Geld-zurück-Garantie.',
    'agency_pricing.faq.cancel_q': 'Wie kündige ich mein Abonnement?',
    'agency_pricing.faq.cancel_a': 'Verwalten Sie Ihr Abo im Agentur-Dashboard.',
    'agency_pricing.view_plans': 'Pläne & Preise ansehen',
    
    // Broker Pricing
    'broker_pricing.badge': 'Makler-Pläne',
    'broker_pricing.title': 'Ihr Maklerkonto upgraden',
    'broker_pricing.subtitle': 'Wählen Sie den perfekten Plan für Ihr Immobiliengeschäft. Mehr Anzeigen, bessere Analysen und Prioritäts-Support.',
    'broker_pricing.most_popular': 'Am beliebtesten',
    'broker_pricing.current_plan': 'Ihr aktueller Plan',
    'broker_pricing.your_current_plan': 'Ihr aktueller Plan',
    'broker_pricing.free': 'Kostenlos',
    'broker_pricing.month': 'Monat',
    'broker_pricing.per_month': '/Monat',
    'broker_pricing.processing': 'Verarbeitung...',
    'broker_pricing.loading': 'Laden...',
    'broker_pricing.manage_subscription': 'Abonnement verwalten',
    'broker_pricing.cancel_anytime': 'Jederzeit herabstufen oder kündigen • Verlängert sich monatlich',
    'broker_pricing.manage_note': 'Jederzeit herabstufen oder kündigen • Verlängert sich monatlich',
    'broker_pricing.downgrade': 'Herabstufen',
    'broker_pricing.upgrade_to_pro': 'Auf Pro upgraden',
    'broker_pricing.go_enterprise': 'Enterprise wählen',
    'broker_pricing.get_started_pro': 'Mit Pro starten',
    'broker_pricing.comparison_title': 'Detaillierter Funktionsvergleich',
    'broker_pricing.feature': 'Funktion',
    'broker_pricing.faq_title': 'Häufig gestellte Fragen',
    'broker_pricing.cta_title': 'Bereit, Ihr Geschäft auszubauen?',
    'broker_pricing.cta_subtitle': 'Schließen Sie sich Tausenden erfolgreicher Makler an, die unsere Plattform nutzen.',
    'broker_pricing.cta_button': 'Mit Pro starten',
    'broker_pricing.sign_in_required': 'Bitte anmelden, um Ihren Plan zu upgraden',
    'broker_pricing.already_on_plan': 'Sie sind bereits auf diesem Plan',
    'broker_pricing.checkout_error': 'Checkout konnte nicht gestartet werden',
    'broker_pricing.portal_error': 'Abonnementverwaltung konnte nicht geöffnet werden',
    'broker_pricing.tier.normal': 'Normal',
    'broker_pricing.tier.normal_desc': 'Perfekt für einzelne Makler, die anfangen',
    'broker_pricing.tier.normal_cta': 'Aktueller Plan',
    'broker_pricing.tier.pro': 'Pro',
    'broker_pricing.tier.pro_desc': 'Für wachsende Makler mit mehr Anzeigen',
    'broker_pricing.tier.pro_cta': 'Auf Pro upgraden',
    'broker_pricing.tier.enterprise': 'Enterprise',
    'broker_pricing.tier.enterprise_desc': 'Für Makler mit hohem Volumen und unbegrenzten Bedürfnissen',
    'broker_pricing.tier.enterprise_cta': 'Enterprise wählen',
    'broker_pricing.feature.listings_5': '5 Anzeigen pro Monat',
    'broker_pricing.feature.listings_10': '10 Anzeigen pro Monat',
    'broker_pricing.feature.listings_15': '15 Anzeigen pro Monat',
    'broker_pricing.feature.basic_features': 'Basis-Immobilienfunktionen',
    'broker_pricing.feature.advanced_features': 'Erweiterte Immobilienfunktionen',
    'broker_pricing.feature.premium_features': 'Premium-Immobilienfunktionen',
    'broker_pricing.feature.client_messaging': 'Kundennachrichten',
    'broker_pricing.feature.viewing_scheduling': 'Besichtigungsplanung',
    'broker_pricing.feature.video_tours_paid': 'Video-Touren ($3 pro Anzeige)',
    'broker_pricing.feature.video_tours_free': 'Kostenlose Video-Touren inklusive',
    'broker_pricing.feature.analytics': 'Analyse-Dashboard',
    'broker_pricing.feature.advanced_analytics': 'Erweiterte Analysen',
    'broker_pricing.feature.priority_support': 'Prioritäts-Support',
    'broker_pricing.feature.support_24_7': '24/7 Prioritäts-Support',
    'broker_pricing.feature.featured_listings': 'Hervorgehobene Anzeigen',
    'broker_pricing.feature.extra_listings': 'Option für zusätzliche Anzeigen',
    'broker_pricing.feature.extra_10': '+$10 pro zusätzliche Anzeige',
    'broker_pricing.feature.messaging': 'Kundennachrichten',
    'broker_pricing.feature.scheduling': 'Besichtigungsplanung',
    'broker_pricing.feature.video_paid': 'Video-Touren ($3 pro Anzeige)',
    'broker_pricing.feature.video_free': 'Kostenlose Video-Touren inklusive',
    'broker_pricing.feature.analytics_advanced': 'Erweiterte Analysen',
    'broker_pricing.feature.support': 'Prioritäts-Support',
    'broker_pricing.feature.support_247': '24/7 Prioritäts-Support',
    'broker_pricing.feature.featured': 'Hervorgehobene Anzeigen',
    'broker_pricing.feature.extra_price': '+$10 pro zusätzliche Anzeige',
    'broker_pricing.table.listings_per_month': 'Anzeigen pro Monat',
    'broker_pricing.table.video_tours': 'Video-Touren',
    'broker_pricing.table.extra_listings': 'Zusätzliche Anzeigen',
    'broker_pricing.table.listing_duration': 'Anzeigendauer',
    'broker_pricing.table.client_messaging': 'Kundennachrichten',
    'broker_pricing.table.analytics_dashboard': 'Analyse-Dashboard',
    'broker_pricing.table.priority_support': 'Prioritäts-Support',
    'broker_pricing.table.featured_listings': 'Hervorgehobene Anzeigen',
    'broker_pricing.table.15_extras': '15 + extra',
    'broker_pricing.table.3_per_listing': '$3 pro Anzeige',
    'broker_pricing.table.10_each': '$10 pro Stück',
    'broker_pricing.table.30_days': '30 Tage',
    'broker_pricing.comparison.listings': 'Anzeigen pro Monat',
    'broker_pricing.comparison.video': 'Video-Touren',
    'broker_pricing.comparison.extra': 'Zusätzliche Anzeigen',
    'broker_pricing.comparison.duration': 'Anzeigendauer',
    'broker_pricing.comparison.featured': 'Hervorgehobene Anzeigen',
    'broker_pricing.comparison.30_days': '30 Tage',
    'broker_pricing.comparison.per_listing': '$3 pro Anzeige',
    'broker_pricing.comparison.free': 'Kostenlos',
    'broker_pricing.comparison.each': '$10 pro Stück',
    'broker_pricing.comparison.15_plus': '15 + extra',
    'broker_pricing.faq.autorenew_q': 'Verlängert sich mein Abo automatisch?',
    'broker_pricing.faq.autorenew_a': 'Ja! Ihr Abonnement verlängert sich jeden Monat automatisch, damit Sie nie den Zugang zu Ihren Anzeigen verlieren. Sie werden am gleichen Tag jeden Monat belastet. Sie können jederzeit über das Abonnementportal kündigen.',
    'broker_pricing.faq.upgrade_q': 'Kann ich jederzeit upgraden oder herabstufen?',
    'broker_pricing.faq.upgrade_a': 'Absolut! Upgrades werden sofort mit anteiliger Abrechnung wirksam. Herabstufungen gelten am Ende Ihrer aktuellen Abrechnungsperiode—Sie behalten alle Funktionen bis dahin. Klicken Sie auf "Abo verwalten" für Änderungen.',
    'broker_pricing.faq.expire_q': 'Was passiert, wenn meine Anzeigen ablaufen?',
    'broker_pricing.faq.expire_a': 'Anzeigen laufen nach 30 Tagen ab. Sie erhalten 3 Tage vorher eine E-Mail-Benachrichtigung. Sie können sie mit Ihren monatlichen kostenlosen Plätzen erneuern oder $10 pro zusätzliche Anzeige zahlen (nur Enterprise).',
    'broker_pricing.faq.extra_q': 'Was sind "zusätzliche Anzeigen"?',
    'broker_pricing.faq.extra_a': 'Enterprise-Mitglieder können zusätzliche Anzeigen für $10 pro Stück über ihre 15 monatlichen kostenlosen Anzeigen hinaus kaufen. Ideal für Monate mit hohem Volumen.',
    'broker_pricing.faq.cancel_q': 'Wie kündige ich mein Abonnement?',
    'broker_pricing.faq.cancel_a': 'Klicken Sie auf "Abo verwalten", um auf Ihr Abrechnungsportal zuzugreifen, wo Sie kündigen, herabstufen oder Ihre Zahlungsmethode aktualisieren können. Kündigungen werden am Ende Ihrer Abrechnungsperiode wirksam.',
    'messaging.conversation_pinned': 'Konversation angeheftet',
    'messaging.conversation_unpinned': 'Konversation abgeheftet',
    'messaging.pin_chat': 'Chat anheften',
    'messaging.unpin_chat': 'Chat abheften',
    'messaging.pin_failed': 'Anheften fehlgeschlagen',
    'messaging.unpin_failed': 'Abheften fehlgeschlagen',
    'messaging.archive': 'Archivieren',
    'messaging.unarchive': 'Wiederherstellen',
    'messaging.show_archived': 'Archiviert',
    'messaging.show_active': 'Aktive anzeigen',
    'messaging.conversation_archived': 'Konversation archiviert',
    'messaging.conversation_unarchived': 'Konversation wiederhergestellt',
    'messaging.archive_failed': 'Archivieren fehlgeschlagen',
    'messaging.unarchive_failed': 'Wiederherstellen fehlgeschlagen',
    
    // Hero/News Section
    'hero.find_dream_home': 'Finden Sie Ihr Traumhaus im Irak',
    'hero.explore_description': 'Entdecken Sie Tausende von Häusern zum Verkauf und Endpreise aus dem ganzen Irak. Lassen Sie sich bewerten, lesen Sie Immobiliennews und kontaktieren Sie Makler.',
    'hero.badge': 'Iraks führende Immobilienplattform',
  },
  fr: {
    // Header
    'nav.search': 'Rechercher des propriétés',
    'nav.search_agents': 'Rechercher des agents',
    'nav.sell': 'Vendre une propriété',
    'nav.news': 'Actualités',
    'nav.build': 'Construire une maison',
    'nav.agencies': 'Agences',
    'nav.buy': 'Acheter',
    'nav.valuation': 'Évaluation',
    'nav.more': 'Plus',
    'nav.saved': 'Sauvegardé',
    'nav.admin': 'Admin',
    'nav.agency': 'Agence',
    'nav.agency_dashboard': 'Tableau de bord agence',
    'nav.location_manager': 'Gestionnaire de lieux',
    'nav.map_configuration': 'Configuration de carte',
    'nav.hello': 'Bonjour',
    'nav.menu': 'Menu',
    'nav.manage_news': 'Gérer les actualités',
    'nav.all_properties': 'Toutes les propriétés',
    'nav.new_production': 'Nouvelles constructions',
    
    // Cookie Policy
    'cookie.title': 'Politique des cookies',
    'cookie.last_updated': 'Dernière mise à jour:',
    'cookie.section1.title': 'Section 1 - Dispositions générales concernant la mise en œuvre des technologies de suivi',
    'cookie.section1.p1': 'Ploqs AB, ci-après dénommé le "Fournisseur de services", ainsi que les partenaires et sous-traitants engagés par le Fournisseur de services, met en œuvre et utilise diverses formes de mécanismes de suivi numérique, y compris, mais sans s\'y limiter, les cookies, les balises web, les pixels de suivi et autres solutions technologiques similaires (collectivement dénommés "Technologies de suivi") sur les plateformes numériques, sites web et applications mobiles du Fournisseur de services (collectivement dénommés "Canaux numériques du Fournisseur de services"). Cette politique de gestion des Technologies de suivi spécifie les finalités pour lesquelles et les méthodes par lesquelles le Fournisseur de services applique lesdites technologies.',
    'cookie.section1.p2': 'Les Technologies de suivi consistent principalement en des fichiers de données textuelles plus petits qui sont déposés sur l\'équipement terminal de l\'utilisateur ou le programme de navigateur web dans le but d\'agréger des informations concernant l\'interaction de l\'utilisateur avec les plateformes numériques et les applications mobiles et la navigation sur celles-ci.',
    'cookie.section1.h1': 'Traitement des données personnelles et considérations relatives à la protection des données',
    'cookie.section1.p3': 'Les informations collectées par l\'application des Technologies de suivi constituent dans la plupart des cas des données qui, directement ou indirectement et en combinaison avec d\'autres données disponibles, peuvent potentiellement être utilisées pour identifier des individus, y compris, mais sans s\'y limiter, les adresses IP, les identifiants de cookies uniques, les identifiants publicitaires (IDFA), les identifiants d\'appareils et diverses métadonnées permettant de différencier des équipements terminaux spécifiques.',
    'cookie.section1.h2': 'Choix de l\'utilisateur',
    'cookie.section1.p4': 'L\'utilisateur a le droit de réviser à tout moment les consentements précédemment donnés en naviguant vers les options de gestion du consentement fournies via l\'interface numérique du Fournisseur de services.',
    'cookie.section1.h3': 'Persistance et périodes de stockage des Technologies de suivi',
    'cookie.section1.p5': 'Les Technologies de suivi présentent une persistance variable selon l\'objectif prévu et la fonctionnalité de chaque technologie. Les Technologies de suivi basées sur la session sont initiées lors de la connexion de l\'utilisateur à la plateforme numérique et sont terminées au plus tard lors de la fermeture de l\'instance du navigateur de l\'utilisateur. Les Technologies de suivi persistantes sont conservées dans le navigateur de l\'utilisateur pendant une période prédéterminée dépassant les sessions individuelles.',
    'cookie.section2.title': 'Section 2 - Technologies de suivi obligatoires pour la fourniture de services',
    'cookie.section2.p1': 'Les Technologies de suivi obligatoires désignent les implémentations technologiques requises pour que le Fournisseur de services puisse fournir des fonctionnalités de base et essentielles à l\'utilisateur, par exemple dans le but de maintenir des informations concernant les consentements précédemment donnés par l\'utilisateur concernant les Technologies de suivi, de permettre la différenciation entre des sessions utilisateur distinctes par des identifiants anonymisés, de faciliter les fonctionnalités cartographiques, de permettre la lecture de matériel audiovisuel dans les annonces immobilières téléchargées par les agents immobiliers, de préserver les préférences de recherche enregistrées de l\'utilisateur, de maintenir la sécurité des services, d\'empêcher l\'utilisation abusive des services et d\'assurer une performance de service continue et optimale.',
    'cookie.section2.legal': 'Base juridique du traitement:',
    'cookie.section2.legal_value': 'Intérêt légitime conformément à l\'article 6.1(f) du RGPD',
    'cookie.section2.storage': 'Période de stockage:',
    'cookie.section2.storage_value': 'Jusqu\'à vingt-quatre (24) mois pour les Technologies de suivi persistantes; les Technologies de suivi basées sur la session sont terminées immédiatement à la fermeture de l\'instance du navigateur.',
    'cookie.section3.title': 'Section 3 - Technologies de suivi à des fins analytiques',
    'cookie.section3.p1': 'Le Fournisseur de services accorde une importance significative à la capacité de quantifier la fréquence des visites, d\'enregistrer les données d\'interaction sur la plateforme numérique et dans les applications mobiles, et d\'analyser autrement comment les services numériques du Fournisseur de services sont utilisés par les utilisateurs. À condition que l\'utilisateur ait donné le consentement requis, le Fournisseur de services met en œuvre des Technologies de suivi, à l\'exclusion des données personnelles directes, dans le but d\'analyser et de développer davantage les services numériques du Fournisseur de services et d\'offrir continuellement à l\'utilisateur une expérience utilisateur optimisée, de maintenir la performance du service et de produire des analyses de groupes cibles et des compilations statistiques concernant l\'utilisation de la plateforme.',
    'cookie.section3.legal_value': 'Consentement conformément à l\'article 6.1(a) du RGPD',
    'cookie.section3.storage_value': 'Jusqu\'à vingt-six (26) mois',
    'cookie.section4.title': 'Section 4 - Technologies de suivi à des fins de personnalisation',
    'cookie.section4.p1': 'Les Technologies de suivi mises en œuvre à des fins de personnalisation visent à adapter l\'expérience de l\'utilisateur sur les plateformes numériques du Fournisseur de services en fournissant un contenu avec une pertinence personnelle accrue pour l\'utilisateur individuel. Cette adaptation est basée, par exemple, sur le comportement de recherche et les modèles d\'interaction de l\'utilisateur. Dans le cas où l\'utilisateur dispose d\'un compte utilisateur enregistré auprès du Fournisseur de services, ces données comportementales sont combinées avec les informations que l\'utilisateur fournit volontairement dans le cadre du service, par exemple des informations concernant les conditions de logement de l\'utilisateur, par lesquelles des adaptations de contenu sont effectuées sur la base du profil agrégé de l\'utilisateur.',
    'cookie.section5.title': 'Section 5 - Technologies de suivi à des fins marketing',
    'cookie.section5.p1': 'Le Fournisseur de services met en œuvre des Technologies de suivi à des fins marketing, à condition que l\'utilisateur ait donné le consentement requis, dans le but de permettre l\'exposition de contenu publicitaire pertinent et d\'annonces immobilières avec une pertinence accrue pour l\'utilisateur sur la base d\'un profil publicitaire construit.',
    'cookie.section5.p2': 'Par la mise en œuvre de Technologies de suivi, le Fournisseur de services s\'assure également que les expositions publicitaires sont limitées à un nombre prédéfini d\'occasions par utilisateur, et que l\'efficacité publicitaire est mesurée et analysée. Le contenu publicitaire du Fournisseur de services peut également être exposé sur des plateformes numériques externes.',
    'cookie.section5.p3': 'Il convient de noter que dans le cas où l\'utilisateur choisit de désactiver les Technologies de suivi à des fins marketing, le contenu publicitaire sera toujours exposé à l\'utilisateur sur les plateformes numériques du Fournisseur de services, cependant sans personnalisation.',
    'cookie.section5.storage_value': 'Jusqu\'à vingt-quatre (24) mois',
    'cookie.section6.title': 'Section 6 - Transferts internationaux de données vers des pays tiers',
    'cookie.section6.p1': 'Dans l\'outil de gestion du consentement du Fournisseur de services, des informations sont fournies concernant quelles Technologies de suivi et quels partenaires traitent les données personnelles de l\'utilisateur dans, ou transfèrent les données personnelles de l\'utilisateur vers, des juridictions en dehors de l\'Union européenne et de l\'Espace économique européen (appelés "pays tiers").',
    'cookie.section7.title': 'Section 7 - Instructions pour la suppression des Technologies de suivi',
    'cookie.section7.p1': 'L\'utilisateur a la possibilité de supprimer les Technologies de suivi de son équipement terminal ou appareil mobile et de configurer les paramètres de stockage de données locales via l\'interface de paramètres du navigateur. L\'utilisateur peut choisir de désactiver les Technologies de suivi ou de recevoir des notifications chaque fois qu\'une nouvelle Technologie de suivi est initiée sur l\'équipement terminal de l\'utilisateur.',
    'cookie.section7.p2': 'Pour des instructions détaillées concernant la gestion et la suppression des Technologies de suivi, référence est faite à l\'option d\'aide dans le navigateur de l\'utilisateur ou aux hyperliens ci-dessous pour chaque application de navigateur:',
    
    // Auth
    'auth.loading': 'Chargement...',
    'auth.login': 'Connexion',
    'auth.create_account': 'Créer un compte',
    'auth.login_subtitle': 'Connectez-vous en choisissant l\'une des options ci-dessous.',
    'auth.signup_subtitle': 'Créez un nouveau compte pour commencer.',
    'auth.continue_apple': 'Continuer avec Apple',
    'auth.continue_google': 'Continuer avec Google',
    'auth.or': 'ou',
    'auth.email': 'E-mail',
    'auth.email_placeholder': 'Entrez votre e-mail',
    'auth.password': 'Mot de passe',
    'auth.password_placeholder': 'Entrez votre mot de passe',
    'auth.account_type': 'Type de compte',
    'auth.user_role': 'Utilisateur - Je veux acheter/louer une propriété',
    'auth.agent_role': 'Agent - Je veux vendre/louer des propriétés',
    'auth.continue': 'Continuer',
    'auth.no_account': 'Pas de compte ? Créer un compte',
    'auth.have_account': 'Déjà un compte ? Connexion',
    'auth.login_failed': 'Échec de la connexion',
    'auth.invalid_credentials': 'E-mail ou mot de passe invalide',
    'auth.welcome_back': 'Bon retour !',
    'auth.logged_in': 'Vous êtes connecté.',
    'auth.signup_failed': 'Échec de l\'inscription',
    'auth.account_created': 'Compte créé !',
    'auth.check_email': 'Vérifiez votre e-mail pour confirmer votre compte.',
    'auth.error': 'Une erreur s\'est produite',
    'auth.try_again': 'Veuillez réessayer plus tard.',
    'auth.broker_interest_title': 'Êtes-vous courtier?',
    'auth.broker_interest_description': 'Si vous souhaitez publier des propriétés sur Aqarnet, envoyez-nous une déclaration d\'intérêt et nous vous recontacterons.',
    'auth.broker_contact_us': 'Contactez-nous',
    'auth.broker_email_subject': 'Déclaration d\'intérêt courtier',
    'auth.broker_email_body': 'Bonjour,\n\nJe suis intéressé par l\'inscription en tant que courtier sur Aqarnet.\n\nNom:\nNom de l\'agence:\nTéléphone:\nVille:\n\nMerci!',
    'auth.first_name': 'Prénom',
    'auth.last_name': 'Nom de famille',
    'auth.first_name_placeholder': 'Entrez votre prénom',
    'auth.last_name_placeholder': 'Entrez votre nom de famille',
    'auth.display_name': 'Nom d\'affichage',
    'auth.display_name_placeholder': 'Choisissez un nom d\'affichage',
    'auth.display_name_hint': 'C\'est le seul nom que les autres verront',
    'auth.date_of_birth': 'Date de naissance',
    'auth.fill_all_fields': 'Veuillez remplir tous les champs obligatoires',
    'auth.must_be_18': 'Vous devez avoir au moins 18 ans pour vous inscrire',
    'auth.must_be_18_hint': 'Vous devez avoir au moins 18 ans',
    'verification.verify_identity': 'Vérifier l\'identité',
    'verification.verified': 'Vérifié',
    'verification.title': 'Vérification d\'identité',
    'verification.description': 'Téléchargez un document officiel pour vérifier votre identité et obtenir un badge vérifié.',
    'verification.document_type': 'Type de document',
    'verification.doc_id_card': 'Carte d\'identité',
    'verification.doc_passport': 'Passeport',
    'verification.doc_drivers_license': 'Permis de conduire',
    'verification.upload_document': 'Télécharger le document',
    'verification.click_to_upload': 'Cliquez pour télécharger ou glissez-déposez',
    'verification.file_requirements': 'JPG, PNG, WEBP ou PDF (max 10Mo)',
    'verification.submit': 'Soumettre pour vérification',
    'verification.uploading': 'Téléchargement...',
    'verification.submitted': 'Vérification soumise',
    'verification.submitted_desc': 'Votre demande de vérification a été soumise. Nous l\'examinerons sous peu.',
    'verification.pending_message': 'Votre vérification est en cours d\'examen. Cela prend généralement 1-2 jours ouvrables.',
    'verification.rejected_message': 'Votre vérification précédente a été rejetée. Veuillez soumettre un nouveau document.',
    'verification.status_approved': 'Approuvé',
    'verification.status_pending': 'En attente',
    'verification.status_rejected': 'Rejeté',
    'verification.invalid_file_type': 'Type de fichier invalide',
    'verification.allowed_types': 'Veuillez télécharger un fichier JPG, PNG, WEBP ou PDF',
    'verification.file_too_large': 'Fichier trop volumineux',
    'verification.max_size': 'La taille maximale du fichier est de 10Mo',
    'verification.error': 'Erreur de vérification',
    'verification.admin_title': 'Vérifications d\'identité',
    'verification.admin_description': 'Examiner et approuver les demandes de vérification d\'identité',
    'verification.no_requests': 'Aucune demande de vérification',
    'verification.view': 'Examiner',
    'verification.review_title': 'Examiner la vérification',
    'verification.review_notes': 'Notes d\'examen',
    'verification.review_notes_placeholder': 'Ajouter des notes sur votre décision (optionnel)',
    'verification.approve': 'Approuver',
    'verification.reject': 'Rejeter',
    'verification.approved': 'Vérification approuvée',
    'verification.rejected': 'Vérification rejetée',
    'verification.review_complete': 'La vérification a été traitée.',
    
    // Favorites Page
    'favorites.title': 'Mes favoris',
    'favorites.saved_properties': 'Propriétés sauvegardées',
    'favorites.login_required': 'Connectez-vous pour voir vos favoris',
    'favorites.login_description': 'Sauvegardez les propriétés qui vous plaisent pour les retrouver plus facilement.',
    'favorites.loading': 'Chargement des favoris...',
    'favorites.no_favorites': 'Pas encore de favoris',
    'favorites.start_saving': 'Commencez à sauvegarder des propriétés en cliquant sur le cœur dans les annonces.',
    'favorites.search_properties': 'Rechercher des propriétés',
    
    // Profile Page
    'profile.title': 'Mon profil',
    'profile.subtitle': 'Gérer les informations personnelles et les paramètres',
    'profile.loading': 'Chargement du profil...',
    'profile.fetch_error': 'Erreur lors de la récupération du profil',
    'profile.fetch_error_description': 'Impossible de récupérer les informations du profil.',
    'profile.unnamed_user': 'Utilisateur sans nom',
    'profile.personal_info': 'Informations personnelles',
    'profile.update_info': 'Mettre à jour vos informations personnelles',
    'profile.email': 'E-mail',
    'profile.email_readonly': 'L\'e-mail ne peut pas être modifié',
    'profile.display_name': 'Nom d\'affichage',
    'profile.display_name_placeholder': 'Votre nom',
    'profile.phone': 'Numéro de téléphone',
    'profile.phone_placeholder': '06 12 34 56 78',
    'profile.save_changes': 'Enregistrer les modifications',
    'profile.saving': 'Enregistrement...',
    'profile.reset': 'Réinitialiser',
    'profile.updated': 'Profil mis à jour',
    'profile.updated_description': 'Votre profil a été enregistré.',
    'profile.update_error': 'Erreur de mise à jour',
    'profile.update_error_description': 'Impossible d\'enregistrer les modifications.',
    'profile.active_listings': 'Annonces actives',
    'profile.active_listings_description': 'Vos annonces immobilières publiées à vendre',
    'profile.view_all': 'Voir tout',
    'profile.loading_listings': 'Chargement des annonces...',
    'profile.no_active_listings': 'Pas encore d\'annonces actives',
    'profile.more_active_listings': 'et {count} autres annonces actives',
    'profile.sold_properties': 'Propriétés vendues',
    'profile.sold_properties_description': 'Vos propriétés vendues',
    'profile.no_sold_properties': 'Pas encore de propriétés vendues',
    'profile.more_sold_properties': 'et {count} autres propriétés vendues',
    'profile.listing_fetch_error': 'Erreur lors de la récupération des annonces',
    'profile.listing_fetch_error_description': 'Impossible de récupérer vos annonces.',
    'profile.account_settings': 'Paramètres du compte',
    'profile.account_settings_description': 'Gérer le compte et la sécurité',
    'profile.change_password': 'Changer le mot de passe',
    'profile.change_password_description': 'Mettre à jour le mot de passe pour une connexion plus sécurisée',
    'profile.change': 'Modifier',
    'profile.two_factor': 'Authentification à deux facteurs',
    'profile.two_factor_description': 'Ajouter une sécurité supplémentaire à votre compte',
    'profile.enable': 'Activer',
    'profile.delete_account': 'Supprimer le compte',
    'profile.delete_account_description': 'Supprimer définitivement le compte et toutes les données',
    'profile.delete': 'Supprimer',
    'profile.logout': 'Se déconnecter',
    
    // Not Found Page
    'notfound.title': '404',
    'notfound.message': 'Désolé ! Page non trouvée',
    'notfound.back_home': 'Retour à l\'accueil',
    
    // Property Detail Page
    'property.back_to_listings': 'Retour aux annonces',
    'property.not_found': 'Propriété non trouvée',
    'property.link_copied': 'Lien copié',
    'property.link_copied_description': 'Lien de la propriété copié',
    'property.description': 'Description',
    'property.features': 'Caractéristiques',
    'property.details': 'Détails de la propriété',
    'property.rooms_label': 'Pièces',
    'property.size_label': 'Surface',
    'property.plot_size_label': 'Surface du terrain',
    'property.monthly_fee_label': 'Frais mensuels',
    'property.contact_agent': 'Contacter l\'agent',
    'property.send_message': 'Envoyer un message',
    'property.schedule_viewing': 'Planifier une visite',
    'property.section.details': 'Détails',
    'property.section.amenities': 'Équipements',
    'property.section.overview': 'Aperçu',
    'property.section.payment_plan': 'Plan de paiement',
    'property.section.floor_plans': 'Plans d\'étage',
    'property.section.view_map': 'Voir la carte',
    'property.copy_link': 'Copier le lien',
    'property.share': 'Partager',
    'property.share_text': 'Découvrez cette propriété',
    'property.posted_on': 'Publié le',
    'property.type': 'Type',
    'property.status': 'Statut',
    'property.available': 'Disponible',
    'property.no_amenities': 'Aucun équipement répertorié pour cette propriété',
    'property.no_description': 'Aucune description disponible',
    'property.location': 'Emplacement',
    'property.payment_plan_info': 'Contactez l\'agent pour les détails du plan de paiement',
    'property.contact_for_payment_plan': 'Contact pour le plan de paiement',
    'property.no_floor_plans': 'Aucun plan d\'étage disponible',
    'property.request_floor_plans': 'Demander les plans d\'étage',
    'property.no_map_location': 'Emplacement non disponible sur la carte',
    
    // News Page
    'news.subtitle': 'Restez informé des dernières actualités immobilières, analyses du marché et guides',
    'news.no_articles': 'Pas encore d\'articles',
    'news.check_back': 'Revenez bientôt pour les dernières actualités immobilières et analyses du marché.',
    'news.search_placeholder': 'Rechercher des articles...',
    'news.showing_filtered': 'Affichage de {shown} sur {total} articles sur cette page',
    'news.clear_filters': 'Effacer les filtres',
    'news.no_matching': 'Aucun article correspondant',
    'news.adjust_filters': 'Essayez d\'ajuster votre recherche ou vos filtres pour trouver ce que vous cherchez.',
    'news.publisher': 'Éditeur',
    'news.page_info': 'Page {current} sur {total} • {count} articles au total',
    'news.category.all': 'Tout',
    'news.category.market_news': 'Actualités du marché',
    'news.category.property_guides': 'Guides immobiliers',
    'news.category.investment_tips': 'Conseils d\'investissement',
    'news.category.industry_updates': 'Mises à jour du secteur',
    'news.category.announcements': 'Annonces',
    'news.category.expert_advice': 'Conseils d\'experts',
    
    // Filters/Sorting
    'filters.any': 'Tous',
    'filters.newest_first': 'Plus récents d\'abord',
    'filters.price_low_high': 'Prix : croissant',
    'filters.price_high_low': 'Prix : décroissant',
    'filters.size': 'Surface',
    'filters.searching_for': 'Recherche de',
    'filters.search_areas': 'Rechercher des zones, villes...',
    
    // Properties for Sale Page
    'propertiesForSale.filters.searchWithin': 'Rechercher dans',
    'propertiesForSale.filters.km': 'km',
    'propertiesForSale.filters.propertyType': 'Type de propriété',
    'propertiesForSale.filters.rooms': 'Pièces',
    'propertiesForSale.filters.bathrooms': 'Salles de bain',
    'propertiesForSale.filters.min': 'Min',
    'propertiesForSale.filters.max': 'Max',
    'propertiesForSale.filters.size': 'Surface',
    'propertiesForSale.filters.price': 'Prix',
    'propertiesForSale.filters.searchResults': 'Résultats de recherche',
    'propertiesForSale.filters.searchPlaceholder': 'Rechercher zones, villes...',
    'propertiesForSale.filters.searchingFor': 'Recherche de:',
    'propertiesForSale.filters.any': 'Tous',
    'propertiesForSale.filters.keywords': 'Mots-clés',
    'propertiesForSale.filters.showResults': 'Afficher les résultats',
    'propertiesForSale.filters.clearFilters': 'Effacer',
    'propertiesForSale.filters.realEstateSituation': 'Situation immobilière',
    'propertiesForSale.filters.situation.tapu': 'Tapu',
    'propertiesForSale.filters.situation.landContract': 'Contrat foncier',
    'propertiesForSale.filters.situation.noDocuments': 'Pas de documents',
    'propertiesForSale.filters.situation.investment': 'Investissement',
    'propertiesForSale.filters.situation.musataha': 'Musataha',
    'propertiesForSale.filters.situation.card': 'Carte',
    'propertiesForSale.filters.situation.agriculturalTapu': 'Tapu agricole',
    'propertiesForSale.sort.newest': 'Plus récents',
    'propertiesForSale.sort.price_low': 'Prix: croissant',
    'propertiesForSale.sort.price_high': 'Prix: décroissant',
    'propertiesForSale.sort.size': 'Surface',
    'propertiesForSale.noResults': 'Aucune propriété trouvée',
    'propertiesForSale.tryAdjustingFilters': 'Essayez de modifier vos filtres ou de rechercher dans une autre zone',
    
    // Search Section
    'search.for_sale': 'À vendre',
    'search.for_rent': 'À louer',
    'search.sold_prices': 'Prix de vente',
    'search.search_agents': 'Rechercher agents',
    'search.area_label': 'Zone',
    'search.area_placeholder': 'Saisir zone ou adresse',
    'search.area_examples': 'Exemple: Bagdad, Basra, Erbil, Najaf, Karbala',
    'search.expand_area': 'Élargir la zone de',
    'search.property_type': 'Type de propriété',
    'search.show_filters': 'Afficher les filtres',
    'search.find_properties': 'Trouver des propriétés à vendre',
    'search.min_rooms': 'Min. pièces',
    'search.min_area': 'Min. surface',
    'search.max_price': 'Prix max.',
    'search.all': 'Tous',
    'search.no_max': 'Pas de max.',
    'search.new_construction': 'Construction neuve',
    'search.show_new_construction': 'Afficher construction neuve',
    'search.only_new_construction': 'Uniquement construction neuve',
    'search.exclude_new_construction': 'Exclure construction neuve',
    'search.keywords': 'Mots-clés',
    'search.keywords_placeholder': 'Piscine, cheminée, etc.',
    'search.more_filters_note': 'Plus de filtres disponibles dans les résultats de recherche.',
    'search.recent_searches': 'Recherches récentes',
    'search.hide_history': 'Masquer l\'historique',
    'search.clear_history': 'Supprimer l\'historique',
    
    'property.all_types': 'Tous types',
    'property.villas': 'Villas',
    'property.townhouses': 'Maisons mitoyennes',
    'property.apartments': 'Appartements',
    'property.business': 'Immobilier commercial',
    'property.land': 'Terrains',
    'property.farms': 'Fermes/Forêts',
    'property.other': 'Autres',

    // Property Types (Select)
    'propertyTypes.allTypes': 'Tous types',
    'propertyTypes.villa': 'Villas',
    'propertyTypes.apartment': 'Appartements',
    'propertyTypes.townhouse': 'Maisons mitoyennes',
    'propertyTypes.business': 'Immobilier commercial',
    'propertyTypes.plot': 'Terrains',
    'propertyTypes.farm': 'Fermes/Forêts',
    'propertyTypes.other': 'Autres',
    
    // Listings
    'listings.latest_for_sale': 'Dernières annonces',
    'listings.properties_found': 'propriétés trouvées',
    'listings.add_property': 'Ajouter un bien',
    'listings.all_news': 'Toutes les actualités',
    'listings.edit': 'Modifier',
    'listings.edit_property': 'Modifier le bien',
    'listings.monthly_fee': 'Frais mensuels',
    'listings.open_floor_plan': 'Plan ouvert',
    'listings.large_balcony': 'Grand balcon',
    'listings.family_friendly': 'Adapté aux familles',
    'listings.photos': 'photos',
    'listings.per_month': 'mois',
    'loading.properties': 'Chargement des propriétés...',
    'empty.no_properties': 'Aucune propriété disponible',
    'empty.check_back': 'Revenez plus tard pour de nouvelles annonces.',
    
    // Property card
    'property.sold': 'VENDU',
    'property.photos': 'photos',
    'property.rooms_short': 'pièces',
    'property.plot': 'terrain',
    'property.per_month': '/mois',
    'property.delete_title': 'Supprimer la propriété ?',
    'property.delete_confirm': 'Êtes-vous sûr de vouloir supprimer {address} ? Cette action est irréversible.',
    'property.deleted': 'Propriété supprimée',
    'property.deleted_desc': 'La propriété a été supprimée',
    'property.delete_error': 'Impossible de supprimer la propriété',
    
    // Leaderboard
    'leaderboard': 'Classement',
    'leaderboardDescription': 'Découvrez les meilleures agences et courtiers classés par vues et ventes',
    'topAgencies': 'Meilleures Agences',
    'topBrokers': 'Meilleurs Courtiers',
    'topAgenciesDescription': 'Agences classées par vues immobilières et performance de vente',
    'topBrokersDescription': 'Courtiers classés par vues immobilières et performance de vente',
    'allRegions': 'Toutes les régions',
    'noAgenciesFound': 'Aucune agence trouvée',
    'noBrokersFound': 'Aucun courtier trouvé',
    'brokers': 'courtiers',
    'sold': 'vendu',
    'active': 'actif',
    'views': 'vues',
    
    // Dialogs - Contact Agent
    'dialog.contact_agent': 'Contacter l\'agent',
    'dialog.contact_agent_btn': 'Contacter l\'agent',
    'dialog.contact_message_placeholder': 'Bonjour ! Je suis intéressé par la propriété à {address} et j\'aimerais en savoir plus. Veuillez me contacter quand vous en avez le temps.',
    'dialog.contact_sending': 'Envoi...',
    'dialog.contact_send': 'Envoyer le message',
    'dialog.contact_note': 'Votre demande sera envoyée directement à l\'agent responsable qui vous contactera.',
    
    // Dialogs - Schedule Viewing
    'dialog.schedule_viewing': 'Planifier une visite',
    'dialog.schedule_viewing_btn': 'Planifier une visite',
    'dialog.preferred_time': 'Heure de visite préférée',
    'dialog.preferred_time_note': 'Sélectionnez la date et l\'heure de la visite. L\'agent confirmera la disponibilité.',
    'dialog.special_requests': 'Demandes spéciales',
    'dialog.special_requests_placeholder': 'Demandes spéciales ou questions...',
    'dialog.schedule_booking': 'Réservation en cours...',
    'dialog.schedule_book': 'Réserver une visite',
    'dialog.schedule_note': 'Votre demande sera envoyée à l\'agent qui confirmera l\'heure par e-mail.',
    
    // Dialogs - Save Search
    'dialog.save_search': 'Sauvegarder la recherche',
    'dialog.save_search_btn': 'Sauvegarder la recherche',
    'dialog.search_name': 'Nom de la recherche',
    'dialog.search_name_placeholder': 'ex. Appartement à Paris',
    'dialog.city_municipality': 'Ville/Commune',
    'dialog.min_rooms': 'Min. pièces',
    'dialog.min_size': 'Min. surface (m²)',
    'dialog.property_type': 'Type de propriété',
    'dialog.email_alerts': 'Notifications par e-mail pour les nouvelles propriétés correspondantes',
    'dialog.saving_search': 'Sauvegarde...',
    
    // Dialogs - Mark as Sold
    'dialog.mark_sold': 'Marquer comme vendu',
    'dialog.mark_sold_description': 'Entrez le prix de vente final de cette propriété. Cela déplacera l\'annonce vers les propriétés vendues.',
    'dialog.final_sale_price': 'Prix de vente final (USD)',
    'dialog.listed_price': 'Prix affiché',
    'dialog.property_details': 'Détails de la propriété',
    'dialog.enter_valid_price': 'Veuillez entrer un prix de vente valide',
    'dialog.enter_price_placeholder': 'Entrez le prix de vente',
    'dialog.marked_sold_success': 'Propriété marquée comme vendue avec succès',
    'dialog.mark_sold_error': 'Impossible de marquer la propriété comme vendue',
    
    // Dialogs - Add Property
    'dialog.add_property': 'Ajouter une nouvelle propriété',
    'dialog.hide_preview': 'Masquer l\'aperçu',
    'dialog.show_preview': 'Afficher l\'aperçu',
    'dialog.selected_images': 'Images sélectionnées',
    'dialog.uploaded_images': 'Images téléchargées',
    'dialog.click_primary': 'Cliquez sur une image pour la définir comme principale',
    'dialog.invalid_format': 'Format de fichier invalide',
    'dialog.file_too_large': 'Fichier trop volumineux',
    'dialog.upload_failed': 'Échec du téléchargement',
    'dialog.images_required': 'Images requises',
    'dialog.please_upload': 'Veuillez télécharger au moins une image de la propriété',
    'dialog.form_validation_failed': 'Échec de la validation du formulaire',
    
    // Common
    'common.cancel': 'Annuler',
    'common.save': 'Enregistrer',
    'common.saving': 'Enregistrement...',
    'common.send': 'Envoyer',
    'common.sending': 'Envoi...',
    'common.submit': 'Soumettre',
    'common.close': 'Fermer',
    'common.error': 'Erreur',
    'common.success': 'Succès',
    'common.loading': 'Chargement...',
    'common.name': 'Nom',
    'common.email': 'E-mail',
    'common.phone': 'Téléphone',
    'common.message': 'Message',
    'common.required': 'Obligatoire',
    'common.optional': 'Optionnel',
    'common.delete': 'Supprimer',
    'common.free': 'Gratuit',
    'common.edit': 'Modifier',

    // Saved Searches
    'loading.saved_searches': 'Chargement des recherches sauvegardées...',
    'saved_searches.title': 'Recherches sauvegardées',
    'saved_searches.empty': 'Aucune recherche sauvegardée',
    'saved_searches.description': 'Sauvegardez une recherche pour recevoir des notifications lorsque des propriétés correspondantes sont ajoutées',
    'saved_searches.created': 'Créée',
    'saved_searches.alerts_on': 'Notifications activées',
    'saved_searches.alerts_off': 'Notifications désactivées',
    'saved_searches.email_enabled': 'Notifications email activées',
    'saved_searches.email_disabled': 'Notifications email désactivées',
    'saved_searches.settings_updated': 'Paramètre mis à jour',
    'saved_searches.removed': 'Recherche supprimée',
    'saved_searches.removed_desc': 'La recherche sauvegardée a été supprimée',
    'saved_searches.error': 'Erreur',
    'saved_searches.update_error': 'Impossible de mettre à jour le paramètre',
    'saved_searches.delete_error': 'Impossible de supprimer la recherche',
    'saved_searches.all_properties': 'Toutes les propriétés',
    
    // Rental properties
    'rental.for_sale': 'À vendre',
    'rental.for_rent': 'À louer',
    'rental.listing_type': 'Type d\'annonce',
    'rental.rental_details': 'Détails de location',
    'rental.rental_period': 'Période de location',
    'rental.rental_period_hint': 'Fréquence de paiement du loyer',
    'rental.daily': 'Quotidien',
    'rental.weekly': 'Hebdomadaire',
    'rental.monthly': 'Mensuel',
    'rental.yearly': 'Annuel',
    'rental.per_daily': '/jour',
    'rental.per_weekly': '/semaine',
    'rental.per_monthly': '/mois',
    'rental.per_yearly': '/an',
    'rental.deposit_amount': 'Montant du dépôt (USD)',
    'rental.minimum_lease': 'Bail minimum (mois)',
    'rental.months': 'mois',
    'rental.available_from': 'Disponible à partir de',
    'rental.furnished': 'Meublé',
    'rental.utilities_included': 'Charges incluses',
    'rental.pets_allowed': 'Animaux acceptés',
    
    // Admin Pages
    'admin.loading': 'Chargement...',
    'admin.access_denied': 'Accès refusé. Privilèges administrateur requis.',
    
    // Admin Notification Settings
    'admin.notifications.title': 'Paramètres de notification',
    'admin.notifications.description': 'Gérez les paramètres de notification pour tous les types d\'utilisateurs. Activez, désactivez ou masquez selon vos besoins.',
    'admin.notifications.fetch_error': 'Échec de la récupération des paramètres de notification',
    'admin.notifications.update_success': 'Paramètre de notification mis à jour',
    'admin.notifications.update_error': 'Échec de la mise à jour du paramètre de notification',
    'admin.notifications.role_description': 'Gérer les notifications pour les utilisateurs {role}',
    'admin.notifications.hidden': 'Masqué',
    'admin.notifications.disabled': 'Désactivé',
    'admin.notifications.active': 'Actif',
    'admin.notifications.hide': 'Masquer',
    'admin.notifications.roles.user': 'Utilisateurs',
    'admin.notifications.roles.maklare': 'Courtiers',
    'admin.notifications.roles.agency': 'Agences',
    'admin.notifications.roles.admin': 'Administrateurs',
    'admin.notifications.roles.publisher': 'Éditeurs',
    'admin.notifications.types.new_viewing_request': 'Nouvelle demande de visite',
    'admin.notifications.types.new_client_message': 'Nouveau message client',
    'admin.notifications.types.property_alerts': 'Alertes immobilières',
    'admin.notifications.types.weekly_summary': 'Résumé hebdomadaire',
    'admin.notifications.types.broker_property_added': 'Courtier a ajouté une propriété',
    'admin.notifications.types.broker_viewing_request': 'Demande de visite du courtier',
    'admin.notifications.types.broker_client_message': 'Message client du courtier',
    'admin.notifications.types.broker_joined': 'Courtier a rejoint',
    'admin.notifications.types.weekly_agency_summary': 'Résumé hebdomadaire de l\'agence',
    'admin.notifications.types.saved_search_alerts': 'Alertes de recherche sauvegardée',
    'admin.notifications.types.favorite_property_updates': 'Mises à jour des propriétés favorites',
    'admin.notifications.types.newsletter': 'Newsletter',
    'admin.notifications.types.system_alerts': 'Alertes système',
    'admin.notifications.types.new_user_signup': 'Inscription nouvel utilisateur',
    'admin.notifications.types.article_published': 'Article publié',

    // Admin Settings
    'admin.settings': 'Paramètres administrateur',
    'admin.messaging_settings': 'Paramètres de messagerie',
    'admin.messaging_settings_description': 'Contrôler les fonctionnalités de messagerie et d\'appel pour tous les utilisateurs',
    'admin.messaging': 'Messagerie',
    'admin.messaging_description': 'Autoriser les utilisateurs à envoyer des messages',
    'admin.voice_calls': 'Appels vocaux',
    'admin.voice_calls_description': 'Autoriser les utilisateurs à passer des appels vocaux',
    'admin.video_calls': 'Appels vidéo',
    'admin.video_calls_description': 'Autoriser les utilisateurs à passer des appels vidéo',
    'admin.screen_sharing': 'Partage d\'écran',
    'admin.screen_sharing_description': 'Autoriser les utilisateurs à partager leur écran pendant les appels',

    'location_mgr.title': 'Gestionnaire de lieux',
    'location_mgr.current_locations': 'Lieux actuels',
    'location_mgr.locations_in_db': '{count} lieux dans la base de données',
    'location_mgr.refresh': 'Actualiser',
    'location_mgr.populate_title': 'Remplir les lieux',
    'location_mgr.populate_desc': 'Récupérer toutes les villes et zones irakiennes depuis l\'API Google Places.',
    'location_mgr.populating': 'Remplissage...',
    'location_mgr.populate_btn': 'Remplir depuis Google Places',
    'location_mgr.success': 'Succès',
    'location_mgr.success_msg': 'Lieux remplis avec succès',
    'location_mgr.error': 'Erreur',
    'location_mgr.error_msg': 'Échec du remplissage des lieux.',
    'map_config.title': 'Gestionnaire de configuration de carte',
    'map_config.subtitle': 'Gérez vos configurations de carte permanentes',
    'map_config.loading': 'Chargement des configurations...',
    'map_config.create': 'Créer une configuration',
    'map_config.create_title': 'Créer une configuration de carte',
    'map_config.create_desc': 'Créer une nouvelle configuration de carte permanente.',
    'map_config.edit_title': 'Modifier la configuration',
    'map_config.edit_desc': 'Mettre à jour votre configuration.',
    'map_config.name': 'Nom de la configuration',
    'map_config.name_placeholder': 'ex: Carte immobilière Irak',
    'map_config.description': 'Description (optionnelle)',
    'map_config.description_placeholder': 'Brève description',
    'map_config.map_data': 'Configuration de carte (JSON)',
    'map_config.map_data_placeholder': 'Configuration au format JSON',
    'map_config.cancel': 'Annuler',
    'map_config.update': 'Mettre à jour',
    'map_config.no_configs': 'Aucune configuration',
    'map_config.no_configs_desc': 'Créez votre première configuration.',
    'map_config.create_first': 'Créer la première configuration',
    'map_config.active': 'Actif',
    'map_config.activate': 'Activer',
    'map_config.edit': 'Modifier',
    'map_config.delete': 'Supprimer',
    'map_config.delete_confirm': 'Êtes-vous sûr?',
    'map_config.delete_desc': 'Cela supprimera définitivement la configuration "{name}".',
    'map_config.created': 'Créé',
    'map_config.last_updated': 'Dernière mise à jour',
    'map_config.map_center': 'Centre de la carte',
    'map_config.zoom_level': 'Niveau de zoom',
    'map_config.cities': 'Villes',
    'map_config.cities_configured': '{count} villes configurées',
    'map_config.invalid_json': 'Format JSON invalide',
    
    // Forms
    'form.price': 'Prix',
    'form.priceUSD': 'Prix (USD)',
    'form.priceIQD': 'Prix (IQD)',
    'form.priceConversionNote': 'Entrez le prix dans l\'une des devises - l\'autre sera calculée automatiquement (Taux: 1 USD ≈ 1 310 IQD)',
    'form.price_usd': 'Prix (USD)',
    'form.price_placeholder': 'Entrez le prix en dollars américains',
    'form.price_description': 'Entrez le prix en dollars américains. Il sera affiché en USD et en dinar irakien.',
    'form.address': 'Adresse',
    'form.city': 'Ville',
    'form.rooms': 'Pièces',
    'form.bathrooms': 'Salles de bain',
    'form.size': 'Surface (m²)',
    'form.building_size': 'Surface du bâtiment (m²)',
    'form.building_size_placeholder': 'Surface du bâtiment en m²',
    'form.building_size_description': 'Surface habitable',
    'form.plot_size': 'Surface du terrain (m²)',
    'form.plot_size_placeholder': 'Surface du terrain en m²',
    'form.plot_size_description': 'Surface totale du terrain',
    'form.type': 'Type',
    'form.propertyType': 'Type de propriété',
    'form.monthly_fee': 'Frais mensuels (SEK)',
    'form.monthly_fee_usd': 'Frais mensuels (USD)',
    'form.monthly_fee_placeholder': 'Entrez les frais mensuels en USD (optionnel)',
    'form.monthly_fee_description': 'Frais mensuels optionnels en dollars américains',
    'form.description': 'Description',
    'form.description_placeholder': 'Décrivez la propriété...',
    'form.payment_plan': 'Plan de paiement',
    'form.payment_plan_placeholder': 'ex: 30% acompte, 70% à la livraison, ou versements mensuels...',
    'form.payment_plan_hint': 'Ajoutez des versements pour montrer vos options de paiement aux acheteurs',
    'payment_plan.total': 'Total',
    'payment_plan.amount': 'Montant',
    'payment_plan.description': 'Description',
    'payment_plan.date': 'Date',
    'payment_plan.percentage': 'Pourcentage',
    'payment_plan.add_installment': 'Ajouter un versement',
    'payment_plan.hint': 'Laissez vide si vous ne proposez pas de plan de paiement',
    'payment_plan.down_payment': 'Acompte',
    'payment_plan.first_payment': 'Premier versement',
    'payment_plan.second_payment': 'Deuxième versement',
    'payment_plan.third_payment': 'Troisième versement',
    'payment_plan.fourth_payment': 'Quatrième versement',
    'payment_plan.fifth_payment': 'Cinquième versement',
    'payment_plan.sixth_payment': 'Sixième versement',
    'payment_plan.seventh_payment': 'Septième versement',
    'payment_plan.final_payment': 'Paiement final',
    'payment_plan.payment_description': 'Description du paiement',
    'payment_plan.enable': 'Inclure le plan de paiement',
    'form.floor_plans': 'Plans d\'étage',
    'form.upload_floor_plans': 'Télécharger les images des plans d\'étage',
    'form.features': 'Caractéristiques (séparées par virgule)',
    'form.features_placeholder': 'ex. Piscine, Jardin, Garage, Cuisine moderne',
    'form.features_description': 'Séparez les caractéristiques par des virgules',
    'form.amenities': 'Équipements',
    'form.amenities_hint': 'Sélectionnez tous les équipements qui s\'appliquent à cette propriété',
    'amenities.balcony': 'Balcon',
    'amenities.electricity_generator': 'Générateur électrique',
    'amenities.central_ac': 'Climatisation centrale',
    'amenities.double_glazed_windows': 'Double vitrage',
    'amenities.satellite_tv': 'TV satellite',
    'amenities.cctv': 'Caméras de surveillance',
    'amenities.security_staff': 'Personnel de sécurité',
    'amenities.sewerage_system': 'Système d\'égouts',
    'amenities.furnished_kitchen': 'Cuisine équipée',
    'amenities.24hr_electricity': 'Électricité 24h',
    'amenities.market': 'Marché à proximité',
    'amenities.elevator': 'Ascenseur',
    'amenities.school': 'École à proximité',
    'amenities.mosque': 'Mosquée à proximité',
    'amenities.central_gas': 'Gaz central',
    'amenities.maintenance': 'Services de maintenance',
    'amenities.cleaning': 'Services de nettoyage',
    'amenities.fire_fighting': 'Équipement anti-incendie',
    'amenities.car_washing': 'Station de lavage',
    'amenities.smart_home': 'Maison intelligente',
    'amenities.earthquake_resistant': 'Résistant aux séismes',
    'amenities.central_fire_system': 'Système anti-incendie central',
    'amenities.hot_water_system': 'Système d\'eau chaude central',
    'amenities.central_vacuum': 'Aspirateur central',
    'amenities.fountain': 'Fontaine',
    'amenities.24hr_water': 'Eau 24h',
    'amenities.garbage_chute': 'Vide-ordures',
    'amenities.kitchen_counter': 'Comptoir de cuisine',
    'amenities.street_lights': 'Éclairage public',
    'amenities.playground': 'Aire de jeux pour enfants',
    'amenities.gym': 'Salle de sport',
    'amenities.garden': 'Jardin',
    'amenities.parking': 'Parking',
    'amenities.wifi': 'WiFi',
    'amenities.security_door': 'Porte de sécurité',
    'amenities.split_ac': 'Climatisation split',
    'amenities.bathtub': 'Baignoire',
    'amenities.refrigerator': 'Réfrigérateur',
    'amenities.washing_machine': 'Lave-linge',
    'amenities.swimming_pool': 'Piscine',
    'amenities.solar_panels': 'Panneaux solaires',
    'amenities.water_tank': 'Réservoir d\'eau',
    'amenities.water_well': 'Puits',
    'amenities.septic_tank': 'Fosse septique',
    'amenities.central_heating': 'Chauffage central',
    'amenities.desert_cooler': 'Refroidisseur désertique',
    'amenities.intercom': 'Interphone',
    'amenities.external_camera': 'Caméra extérieure',
    'amenities.guard_room': 'Loge du gardien',
    'amenities.rooftop_terrace': 'Terrasse sur le toit',
    'amenities.basement': 'Sous-sol',
    'amenities.storage_room': 'Débarras',
    'amenities.internal_stairs': 'Escalier intérieur',
    'amenities.external_stairs': 'Escalier extérieur',
    'amenities.corner_unit': 'Unité d\'angle',
    'amenities.two_streets': 'Deux rues',
    'amenities.double_entrance': 'Double entrée',
    'amenities.marble_flooring': 'Sol en marbre',
    'amenities.ceramic_tiles': 'Carreaux de céramique',
    'amenities.parquet_flooring': 'Parquet',
    'amenities.gypsum_decorations': 'Décorations en plâtre',
    'amenities.false_ceiling': 'Faux plafond',
    'amenities.steel_doors': 'Portes en acier',
    'amenities.aluminum_windows': 'Fenêtres en aluminium',
    'amenities.pvc_windows': 'Fenêtres PVC',
    'amenities.guest_room': 'Chambre d\'amis',
    'amenities.servant_quarters': 'Quartiers des domestiques',
    'amenities.covered_parking': 'Parking couvert',
    'amenities.concrete_fence': 'Clôture en béton',
    'amenities.iron_fence': 'Clôture en fer',
    'amenities.street_pavement': 'Revêtement de rue',
    'amenities.fully_furnished': 'Entièrement meublé',
    'amenities.hospital': 'Hôpital à proximité',
    'amenities.category.utilities': 'Électricité & Eau',
    'amenities.category.climate': 'Climatisation',
    'amenities.category.security': 'Sécurité',
    'amenities.category.building': 'Caractéristiques du bâtiment',
    'amenities.category.interior': 'Intérieur & Sols',
    'amenities.category.rooms': 'Pièces',
    'amenities.category.outdoor': 'Extérieur & Stationnement',
    'amenities.category.appliances': 'Électroménager',
    'amenities.category.services': 'Services',
    'amenities.category.nearby': 'À proximité',
    'common.selected': 'sélectionné(s)',
    'form.property_images': 'Images de la propriété',
    'form.current_images': 'Images actuelles',
    'form.image': 'Image',
    'form.primary': 'Principale',
    'form.click_image_primary': 'Cliquez sur une image pour la définir comme principale. Utilisez les flèches pour modifier l\'ordre.',
    'form.click_to_upload_new': 'Cliquez pour télécharger de nouvelles images',
    'form.new_images_upload': 'Nouvelles images à télécharger',
    'form.new_image': 'Nouvelle image',
    'form.upload': 'Télécharger',
    'form.new_images': 'nouvelles images',
    'form.location': 'Emplacement',
    'form.required': 'Obligatoire',
    'form.optional': 'Optionnel',
    'form.cancel': 'Annuler',
    'form.save_changes': 'Enregistrer les modifications',
    'form.uploading': 'Téléchargement...',
    'form.upload_images': 'Télécharger des images',
    'form.click_to_upload': 'Cliquez pour télécharger des images',
    'form.file_formats': 'JPEG, PNG, WebP, GIF (max 10Mo par image)',
    'form.select_type': 'Sélectionner le type',
    'form.location_on_map': 'Emplacement de la propriété sur la carte',
    'form.location_hint': 'Cliquez sur la carte pour définir l\'emplacement exact',
    'form.hide_preview': 'Masquer l\'aperçu',
    'form.show_preview': 'Afficher l\'aperçu',
    'form.selected_images': 'Images sélectionnées',
    'form.click_set_primary': 'Cliquez sur une image pour la définir comme principale',
    'form.uploaded_images': 'Images téléchargées',
    'form.images': 'images',
    'form.uploading_images': 'Téléchargement des images...',
    'form.city_placeholder': 'Rechercher une ville...',
    'form.new_construction': 'Construction neuve (Maison future)',
    'form.preview': 'Aperçu',
    'form.preview_description': 'Voici comment votre annonce apparaîtra sur le site',
    'form.fullscreen': 'Plein écran',
    'form.exit_fullscreen': 'Quitter le plein écran',
    'form.click_preview_fullscreen': 'Cliquez sur l\'aperçu pour afficher en plein écran',
    'form.preview_contact_disabled': 'Les options de contact sont désactivées en mode aperçu',
    'form.invalid_format': 'Format de fichier invalide',
    'form.invalid_format_desc': '{filename} a un format invalide. Autorisés: JPEG, PNG, WebP, GIF',
    'form.file_too_large': 'Fichier trop volumineux',
    'form.file_too_large_desc': '{filename} est supérieur à 10Mo',
    'form.upload_failed': 'Échec du téléchargement',
    'form.upload_failed_desc': 'Impossible de télécharger les images',
    'form.images_required': 'Images requises',
    'form.images_required_desc': 'Veuillez télécharger au moins une image de la propriété',
    'form.sold_price_usd': 'Prix de vente (USD)',
    'form.sold_price_placeholder': 'Entrez le prix de vente réel',
    'form.sold_price_description': 'Le prix de vente réel (peut différer du prix affiché)',
    'form.video_url': 'URL de la visite vidéo',
    'form.video': 'Vidéo',
    'form.add_video': 'Ajouter une autre vidéo',
    'form.video_addon': '+3$ supplément',
    'form.video_included': 'Inclus gratuitement',
    'form.video_url_hint': 'Ajoutez une visite vidéo YouTube ou Vimeo. Le supplément vidéo peut être acheté après la création de l\'annonce.',
    'form.video_url_hint_free': 'Ajoutez une visite vidéo YouTube ou Vimeo - inclus dans votre abonnement!',
    'form.video_url_hint_addon': 'Ajoutez une visite vidéo YouTube ou Vimeo. 3$ seront ajoutés au coût de votre annonce.',
    'form.include_video_tour': 'Inclure la visite vidéo',
    'form.show_map': 'Afficher la carte',
    'form.no_location': 'Aucun emplacement sélectionné',
    
    // Footer
    'footer.contact': 'Contact',
    'footer.customer_service': 'Service client & contact',
    'footer.about': 'À propos de Aqarnet',
    'footer.press': 'Presse',
    'footer.investors': 'Pour les investisseurs',
    'footer.advertising': 'Publicité et marketing',
    'footer.jobs': 'Emplois chez nous',
    'footer.statistics': 'Statistiques immobilières',
    'footer.apps': 'Applications',
    'footer.iphone_ipad': 'iPhone et iPad',
    'footer.android': 'Android',
    'footer.follow': 'Suivre Aqarnet',
    'footer.newsletter': 'Newsletter Aqarnet',
    'footer.whatsapp': 'WhatsApp',
    'footer.facebook': 'Facebook',
    'footer.instagram': 'Instagram',
    'footer.linkedin': 'LinkedIn',
    'footer.pinterest': 'Pinterest',
    'footer.tiktok': 'TikTok',
    'footer.threads': 'Threads',
    'footer.podcast': 'Podcast Aqarnet',
    'footer.all_properties': 'Toutes les propriétés',
    'footer.privacy': 'Confidentialité',
    'footer.cookies': 'Cookies',
    'footer.terms': 'Conditions générales & Informations légales',
    'footer.cookie_settings': 'Paramètres des cookies',
    
    // About Page
    'about.title': 'À propos d\'Aqarnet',
    'about.subtitle': 'Votre partenaire de confiance sur le marché immobilier irakien',
    'about.description': 'Aqarnet est la principale plateforme immobilière d\'Irak, connectant acheteurs, vendeurs et agents à travers le pays. Nous fournissons des annonces complètes, des analyses de marché et des outils professionnels.',
    'about.mission_title': 'Notre Mission',
    'about.mission_text': 'Révolutionner le marché immobilier irakien en fournissant des informations immobilières transparentes, accessibles et fiables.',
    'about.vision_title': 'Notre Vision',
    'about.vision_text': 'Devenir la plateforme immobilière la plus fiable du Moyen-Orient.',
    'about.services_title': 'Nos Services',
    'about.services_text': 'Annonces immobilières, analyses de marché, mise en relation avec des agents, évaluations et actualités immobilières.',
    'about.team_title': 'Notre Équipe',
    'about.team_text': 'Une équipe dédiée de professionnels de l\'immobilier, d\'experts en technologie et de spécialistes du service client.',
    'about.company_info': 'Informations sur l\'entreprise',
    'about.based_in': 'Basée en Suède, au service de l\'Irak',
    
    // Contact Page
    'contact.title': 'Contactez-nous',
    'contact.subtitle': 'Nous sommes là pour vous aider. Contactez-nous pour toute question.',
    'contact.email_label': 'E-mail',
    'contact.phone_label': 'Téléphone',
    'contact.phone_info': 'Bientôt disponible',
    'contact.location_label': 'Emplacement',
    'contact.location_info': 'Suède',
    'contact.form_title': 'Envoyez-nous un message',
    'contact.form_description': 'Remplissez le formulaire et nous vous répondrons dès que possible.',
    'contact.name_placeholder': 'Votre nom',
    'contact.email_placeholder': 'votre@email.com',
    'contact.subject': 'Sujet',
    'contact.subject_placeholder': 'De quoi s\'agit-il ?',
    'contact.message_placeholder': 'Votre message...',
    'contact.success': 'Message envoyé ! Nous vous répondrons bientôt.',
    
    // Press Page
    'press.title': 'Presse & Médias',
    'press.subtitle': 'Dernières actualités et communiqués de presse d\'Aqarnet',
    'press.contact_title': 'Contact Presse',
    'press.contact_description': 'Pour les demandes de presse, contactez-nous par e-mail.',
    'press.add_release': 'Ajouter un communiqué',
    'press.edit_release': 'Modifier le communiqué',
    'press.new_release': 'Nouveau communiqué de presse',
    'press.release_title': 'Titre',
    'press.content': 'Contenu',
    'press.publish': 'Publier',
    'press.no_releases': 'Pas encore de communiqués de presse.',
    'press.delete_confirm_title': 'Supprimer le communiqué ?',
    'press.delete_confirm_description': 'Cette action ne peut pas être annulée.',
    
    // Investors Page
    'investors.title': 'Pour les Investisseurs',
    'investors.subtitle': 'Rejoignez-nous pour transformer le marché immobilier irakien',
    'investors.growth_title': 'Potentiel de Croissance',
    'investors.growth_text': 'Le marché immobilier irakien connaît une croissance rapide avec des opportunités d\'investissement significatives.',
    'investors.market_title': 'Leader du Marché',
    'investors.market_text': 'Aqarnet est positionnée comme la principale plateforme numérique du secteur immobilier irakien.',
    'investors.vision_title': 'Expansion Régionale',
    'investors.vision_text': 'Notre vision s\'étend au-delà de l\'Irak pour devenir la plateforme leader au Moyen-Orient.',
    'investors.opportunity_title': 'Opportunité d\'Investissement',
    'investors.opportunity_text': 'Aqarnet offre une opportunité unique d\'investir dans la transformation numérique d\'un des plus grands marchés immobiliers émergents.',
    'investors.opportunity_text_2': 'Avec une base d\'utilisateurs croissante et des services en expansion, nous sommes bien positionnés pour une croissance durable.',
    'investors.contact_title': 'Contactez-nous',
    'investors.contact_description': 'Intéressé par les opportunités d\'investissement ? Contactez-nous pour en savoir plus.',
    'investors.company': 'Entreprise / Organisation',
    'investors.company_placeholder': 'Nom de votre entreprise',
    'investors.message_placeholder': 'Parlez-nous de vos intérêts d\'investissement...',
    'investors.success': 'Merci pour votre intérêt ! Nous vous contacterons bientôt.',
    
    // Advertising Page
    'advertising.title': 'Publicité & Marketing',
    'advertising.subtitle': 'Atteignez des milliers d\'acheteurs et locataires potentiels en Irak',
    'advertising.targeted_title': 'Portée Ciblée',
    'advertising.targeted_text': 'Atteignez les utilisateurs recherchant activement des propriétés dans des zones spécifiques.',
    'advertising.audience_title': 'Audience de Qualité',
    'advertising.audience_text': 'Connectez-vous avec des acheteurs et locataires sérieux.',
    'advertising.analytics_title': 'Analyses Détaillées',
    'advertising.analytics_text': 'Suivez les performances de vos campagnes avec des analyses complètes.',
    'advertising.formats_title': 'Formats Multiples',
    'advertising.formats_text': 'Bannières publicitaires, annonces en vedette, contenu sponsorisé et plus.',
    'advertising.contact_title': 'Commencer à Faire de la Publicité',
    'advertising.contact_description': 'Contactez-nous pour discuter des opportunités publicitaires.',
    'advertising.company': 'Entreprise / Organisation',
    'advertising.company_placeholder': 'Nom de votre entreprise',
    'advertising.budget': 'Budget',
    'advertising.budget_placeholder': 'ex., 500 - 1000 $/mois',
    'advertising.message_placeholder': 'Parlez-nous de vos objectifs publicitaires...',
    'advertising.success': 'Merci ! Nous vous contacterons concernant les opportunités publicitaires.',
    
    // Jobs Page
    'jobs.title': 'Carrières chez Aqarnet',
    'jobs.subtitle': 'Rejoignez notre équipe et façonnez l\'avenir de l\'immobilier en Irak',
    'jobs.add_job': 'Ajouter une offre',
    'jobs.edit_job': 'Modifier l\'offre',
    'jobs.new_job': 'Nouvelle offre d\'emploi',
    'jobs.job_title': 'Titre du poste',
    'jobs.department': 'Département',
    'jobs.location': 'Lieu',
    'jobs.employment_type': 'Type d\'emploi',
    'jobs.full_time': 'Temps plein',
    'jobs.part_time': 'Temps partiel',
    'jobs.contract': 'Contrat',
    'jobs.internship': 'Stage',
    'jobs.description': 'Description',
    'jobs.requirements': 'Exigences',
    'jobs.benefits': 'Avantages',
    'jobs.publish': 'Publier',
    'jobs.no_jobs': 'Aucun poste ouvert pour le moment.',
    'jobs.check_back': 'Revenez plus tard pour de nouvelles opportunités.',
    'jobs.apply_now': 'Postuler maintenant',
    'jobs.apply_for': 'Postuler pour',
    'jobs.apply_description': 'Envoyez votre candidature par e-mail.',
    'jobs.application_sent': 'Candidature envoyée ! Bonne chance !',
    'jobs.delete_confirm_title': 'Supprimer l\'offre d\'emploi ?',
    'jobs.delete_confirm_description': 'Cette action ne peut pas être annulée.',
    
    // Statistics
    'statistics.title': 'Statistiques immobilières',
    'statistics.subtitle': 'Analysez les tendances du marché et l\'évolution des prix basées sur les propriétés vendues',
    'statistics.6_months': '6 derniers mois',
    'statistics.12_months': '12 derniers mois',
    'statistics.24_months': '24 derniers mois',
    'statistics.36_months': '36 derniers mois',
    'statistics.all_cities': 'Toutes les villes',
    'statistics.total_sold': 'Total vendu',
    'statistics.avg_price': 'Prix moyen',
    'statistics.avg_price_sqm': 'Prix moyen/m²',
    'statistics.price_change': 'Variation de prix',
    'statistics.price_trends': 'Tendances des prix',
    'statistics.price_trends_desc': 'Prix moyens des propriétés au fil du temps',
    'statistics.sales_volume': 'Volume des ventes',
    'statistics.sales_volume_desc': 'Nombre de propriétés vendues par mois',
    'statistics.properties_sold': 'Propriétés vendues',
    'statistics.price_by_city': 'Prix par m² par ville',
    'statistics.price_by_city_desc': 'Comparaison du prix moyen par mètre carré entre les villes',
    'statistics.price_per_sqm': 'Prix par m²',
    'statistics.type_distribution': 'Distribution des types de propriétés',
    'statistics.type_distribution_desc': 'Distribution des propriétés vendues par type',
    'statistics.avg_days_market': 'Jours moyens sur le marché',
    'statistics.days': 'jours',
    'statistics.price_ranges': 'Distribution des gammes de prix',
    'statistics.price_ranges_desc': 'Nombre de propriétés vendues dans chaque gamme de prix',
    'statistics.properties': 'Propriétés',
    'statistics.year_over_year': 'Comparaison annuelle',
    'statistics.year_over_year_desc': 'Comparez les prix moyens et le volume des ventes à travers les années',
    'statistics.all_types': 'Tous les types de biens',
    'statistics.all_rooms': 'Toutes les pièces',
    'statistics.rooms': 'pièces',
    'statistics.export': 'Exporter',
    'statistics.export_csv': 'Télécharger CSV',
    'statistics.export_json': 'Télécharger JSON',
    'statistics.share': 'Partager',
    'statistics.link_copied': 'Lien copié',
    'statistics.link_copied_desc': 'Le lien du rapport statistique a été copié dans le presse-papiers',
    'statistics.custom_date': 'Date personnalisée',
    'statistics.use_preset': 'Utiliser préréglage',
    'statistics.start_date': 'Date de début',
    'statistics.end_date': 'Date de fin',
    'statistics.city_comparison': 'Comparaison de villes',
    'statistics.city_comparison_desc': 'Comparez les statistiques immobilières entre deux villes côte à côte',
    'statistics.select_city_1': 'Sélectionnez la première ville',
    'statistics.select_city_2': 'Sélectionnez la deuxième ville',
    'statistics.vs': 'vs',
    'statistics.select_cities_to_compare': 'Sélectionnez deux villes ci-dessus pour comparer leurs statistiques',
    'statistics.avg_size': 'Surface moy.',
    
    // Cookie Settings
    'cookie_settings.title': 'Paramètres des cookies',
    'cookie_settings.description': 'Nous utilisons des cookies pour améliorer votre expérience de navigation. Vous pouvez personnaliser vos préférences ci-dessous.',
    'cookie_settings.necessary': 'Cookies nécessaires',
    'cookie_settings.necessary_desc': 'Ces cookies sont essentiels au bon fonctionnement du site web. Ils ne peuvent pas être désactivés.',
    'cookie_settings.functional': 'Cookies fonctionnels',
    'cookie_settings.functional_desc': 'Ces cookies permettent des fonctionnalités personnalisées comme les préférences de langue et les paramètres enregistrés.',
    'cookie_settings.analytics': 'Cookies analytiques',
    'cookie_settings.analytics_desc': 'Ces cookies nous aident à comprendre comment les visiteurs interagissent avec notre site pour améliorer l\'expérience utilisateur.',
    'cookie_settings.marketing': 'Cookies marketing',
    'cookie_settings.marketing_desc': 'Ces cookies sont utilisés pour vous montrer des publicités pertinentes et suivre l\'efficacité de nos campagnes.',
    'cookie_settings.accept_all': 'Tout accepter',
    'cookie_settings.reject_all': 'Tout refuser',
    'cookie_settings.save_preferences': 'Enregistrer les préférences',
    'cookie_settings.saved': 'Préférences de cookies enregistrées',
    
    // Cookie Banner
    'cookie_banner.title': 'Nous utilisons des cookies',
    'cookie_banner.description': 'Nous utilisons des cookies pour améliorer votre expérience de navigation, afficher du contenu personnalisé et analyser notre trafic. En cliquant sur "Tout accepter", vous consentez à notre utilisation des cookies.',
    'cookie_banner.accept_all': 'Tout accepter',
    'cookie_banner.reject_all': 'Tout refuser',
    'cookie_banner.customize': 'Personnaliser',
    'terms.title': 'Conditions de Service', 'terms.last_updated': 'Dernière mise à jour:', 'terms.section1.title': '1. Acceptation des Conditions', 'terms.section1.p1': 'En utilisant Aqarnet, vous acceptez ces conditions. Si vous n\'acceptez pas, veuillez ne pas utiliser nos services.', 'terms.section1.p2': 'Nous nous réservons le droit de modifier ces conditions à tout moment.', 'terms.section2.title': '2. Description du Service', 'terms.section2.p1': 'Aqarnet fournit une plateforme numérique pour les annonces immobilières en Irak.', 'terms.section2.p2': 'Nos services incluent les listes de propriétés, la recherche, les favoris et les outils de communication.', 'terms.section3.title': '3. Responsabilités de l\'Utilisateur', 'terms.section3.p1': 'En tant qu\'utilisateur, vous acceptez de:', 'terms.section3.li1': 'Fournir des informations exactes', 'terms.section3.li2': 'Ne pas utiliser la plateforme pour des activités illégales', 'terms.section3.li3': 'Respecter les droits de propriété intellectuelle', 'terms.section3.li4': 'Ne pas interférer avec le service', 'terms.section4.title': '4. Annonces Immobilières', 'terms.section4.p1': 'Les utilisateurs sont responsables de l\'exactitude de leurs annonces.', 'terms.section4.p2': 'Nous pouvons supprimer les annonces qui violent nos conditions.', 'terms.section5.title': '5. Propriété Intellectuelle', 'terms.section5.p1': 'Tout le contenu appartient à Aqarnet.', 'terms.section5.p2': 'Reproduction interdite sans autorisation.', 'terms.section6.title': '6. Limitation de Responsabilité', 'terms.section6.p1': 'Aqarnet est fourni "tel quel" sans garanties.', 'terms.section7.title': '7. Droit Applicable', 'terms.section7.p1': 'Ces conditions sont régies par le droit suédois.', 'terms.section7.p2': 'Les litiges seront résolus devant les tribunaux suédois.', 'terms.section8.title': '8. Contact', 'terms.section8.p1': 'Pour toute question, contactez-nous:', 'terms.section8.email': 'Email',
    'privacy.title': 'Politique de Confidentialité', 'privacy.last_updated': 'Dernière mise à jour:', 'privacy.section1.title': '1. Introduction', 'privacy.section1.p1': 'Nous nous engageons à protéger votre vie privée.', 'privacy.section1.p2': 'En utilisant nos services, vous acceptez nos pratiques de données.', 'privacy.section2.title': '2. Informations Collectées', 'privacy.section2.p1': 'Nous collectons:', 'privacy.section2.li1': 'Informations de compte', 'privacy.section2.li2': 'Données immobilières', 'privacy.section2.li3': 'Données d\'utilisation', 'privacy.section2.li4': 'Informations sur l\'appareil', 'privacy.section3.title': '3. Utilisation des Informations', 'privacy.section3.p1': 'Nous utilisons vos informations pour:', 'privacy.section3.li1': 'Fournir nos services', 'privacy.section3.li2': 'Personnaliser votre expérience', 'privacy.section3.li3': 'Envoyer des notifications', 'privacy.section3.li4': 'Communiquer avec vous', 'privacy.section4.title': '4. Partage des Données', 'privacy.section4.p1': 'Nous ne vendons pas vos données.', 'privacy.section4.p2': 'Nous pouvons partager des informations si requis par la loi.', 'privacy.section5.title': '5. Vos Droits', 'privacy.section5.p1': 'Vous avez les droits suivants:', 'privacy.section5.li1': 'Accès', 'privacy.section5.li2': 'Rectification', 'privacy.section5.li3': 'Suppression', 'privacy.section5.li4': 'Portabilité', 'privacy.section5.li5': 'Opposition', 'privacy.section6.title': '6. Sécurité des Données', 'privacy.section6.p1': 'Nous protégeons vos données.', 'privacy.section6.p2': 'Aucune méthode n\'est 100% sécurisée.', 'privacy.section7.title': '7. Modifications', 'privacy.section7.p1': 'Nous pouvons mettre à jour cette politique.', 'privacy.section8.title': '8. Contact', 'privacy.section8.p1': 'Contactez-nous pour toute question:', 'privacy.section8.email': 'Email',
    
    // Newsletter
    'newsletter.title': 'Inscription à la newsletter',
    'newsletter.description': 'Abonnez-vous pour recevoir les dernières nouvelles immobilières.',
    'newsletter.placeholder': 'Entrez votre email',
    'newsletter.subscribe': 'S\'abonner',
    'newsletter.subscribing': 'Abonnement en cours...',
    'newsletter.success': 'Abonnement réussi!',
    'newsletter.subscribed': 'Merci de votre abonnement!',
    'newsletter.invalid_email': 'Veuillez entrer un email valide',
    'newsletter.error': 'Échec de l\'abonnement',
    'newsletter.unsubscribe_note': 'Vous pouvez vous désabonner à tout moment.',
    'newsletter.unsubscribe_title': 'Se désabonner de la newsletter',
    'newsletter.unsubscribe_description': 'Entrez votre email pour vous désabonner.',
    'newsletter.unsubscribe_button': 'Se désabonner',
    'newsletter.unsubscribe_success': 'Désabonnement réussi',
    'newsletter.unsubscribe_error': 'Échec du désabonnement',
    'newsletter.unsubscribe_message': 'Vous ne recevrez plus notre newsletter.',
    'newsletter.invalid_link': 'Lien de désabonnement invalide',
    'newsletter.invalid_link_message': 'Ce lien de désabonnement est invalide ou a expiré. Veuillez utiliser le lien de votre email de newsletter.',
    'newsletter.unsubscribe_reason_label': 'Raison du désabonnement (optionnel)',
    'newsletter.select_reason': 'Sélectionnez une raison...',
    'newsletter.reason_too_many_emails': 'Trop d\'emails',
    'newsletter.reason_not_relevant': 'Le contenu ne me concerne pas',
    'newsletter.reason_never_subscribed': 'Je ne me suis jamais abonné',
    'newsletter.reason_found_another': 'J\'ai trouvé un autre service',
    'newsletter.reason_other': 'Autre',
    'common.try_again': 'Réessayer',
    'newsletter.admin.title': 'Abonnés à la newsletter',
    'newsletter.admin.description': 'Gérez vos abonnés',
    'newsletter.admin.total': 'Total abonnés',
    'newsletter.admin.active': 'Actif',
    'newsletter.admin.unsubscribed': 'Désabonné',
    'newsletter.admin.export_csv': 'Exporter CSV',
    'newsletter.admin.refresh': 'Actualiser',
    'newsletter.admin.subscribers_list': 'Liste des abonnés',
    'newsletter.admin.subscribers_description': 'Tous les abonnés et leur statut',
    'newsletter.admin.no_subscribers': 'Pas encore d\'abonnés',
    'newsletter.admin.subscribed_at': 'Abonné le',
    'newsletter.admin.status': 'Statut',
    'newsletter.admin.unsubscribed_at': 'Désabonné le',
    'newsletter.admin.actions': 'Actions',
    'newsletter.admin.deleted': 'Abonné supprimé',
    'newsletter.admin.delete_error': 'Échec de la suppression',
    'newsletter.admin.no_data': 'Pas de données à exporter',
    'newsletter.admin.exported': 'Exporté avec succès',
    'newsletter.admin.delete_confirm_title': 'Supprimer l\'abonné?',
    'newsletter.admin.delete_confirm_description': 'Cela supprimera définitivement cet abonné.',
    'footer.faq': 'FAQ',
    
    // FAQ Page
    'faq.title': 'Questions fréquemment posées',
    'faq.subtitle': 'Trouvez des réponses aux questions courantes sur Aqarnet',
    'faq.account.title': 'Compte et inscription',
    'faq.account.q1': 'Comment créer un compte?',
    'faq.account.q1_answer': 'Cliquez sur "Connexion" dans l\'en-tête, puis sélectionnez "Créer un compte". Vous pouvez vous inscrire avec votre email, Google ou Apple.',
    'faq.account.q2': 'Comment réinitialiser mon mot de passe?',
    'faq.account.q2_answer': 'Sur la page de connexion, cliquez sur "Mot de passe oublié" et entrez votre adresse email. Vous recevrez un lien pour réinitialiser votre mot de passe.',
    'faq.account.q3': 'Puis-je changer mon adresse email?',
    'faq.account.q3_answer': 'Actuellement, vous ne pouvez pas changer votre adresse email directement. Veuillez contacter notre équipe de support.',
    'faq.listings.title': 'Annonces immobilières',
    'faq.listings.q1': 'Comment publier une propriété à vendre?',
    'faq.listings.q1_answer': 'Vous devez être inscrit comme agent pour publier des propriétés. Après connexion, cliquez sur "Ajouter une propriété" et remplissez les détails.',
    'faq.listings.q2': 'Combien de temps une annonce reste-t-elle active?',
    'faq.listings.q2_answer': 'Les annonces restent actives jusqu\'à ce qu\'elles soient marquées comme vendues ou supprimées par le propriétaire.',
    'faq.listings.q3': 'Puis-je modifier mon annonce après publication?',
    'faq.listings.q3_answer': 'Oui, vous pouvez modifier votre annonce à tout moment. Allez dans votre profil, trouvez l\'annonce et cliquez sur "Modifier".',
    'faq.search.title': 'Recherche de propriétés',
    'faq.search.q1': 'Comment sauvegarder une recherche?',
    'faq.search.q1_answer': 'Après une recherche, cliquez sur "Sauvegarder la recherche". Vous pouvez activer les notifications par email pour les nouvelles propriétés.',
    'faq.search.q2': 'Comment ajouter une propriété aux favoris?',
    'faq.search.q2_answer': 'Cliquez sur l\'icône coeur sur n\'importe quelle carte de propriété. Vous devez être connecté pour sauvegarder des favoris.',
    'faq.search.q3': 'Puis-je rechercher sur la carte?',
    'faq.search.q3_answer': 'Oui, utilisez la vue carte sur la page des propriétés pour parcourir les annonces par emplacement.',
    'faq.contact.title': 'Contacter les agents',
    'faq.contact.q1': 'Comment contacter un agent immobilier?',
    'faq.contact.q1_answer': 'Cliquez sur "Contacter l\'agent" sur n\'importe quelle annonce pour envoyer un message directement à l\'agent responsable.',
    'faq.contact.q2': 'Puis-je planifier une visite?',
    'faq.contact.q2_answer': 'Oui, cliquez sur "Planifier une visite" sur la page de la propriété pour demander un créneau horaire.',
    'faq.security.title': 'Sécurité et confidentialité',
    'faq.security.q1': 'Mes informations personnelles sont-elles sécurisées?',
    'faq.security.q1_answer': 'Oui, nous utilisons un cryptage et des mesures de sécurité standard pour protéger vos données.',
    'faq.security.q2': 'Comment supprimer mon compte?',
    'faq.security.q2_answer': 'Allez dans les paramètres de votre profil et sélectionnez "Supprimer le compte". Cette action est permanente.',
    'faq.still_questions': 'Vous avez encore des questions?',
    'faq.contact_us': 'Contactez notre équipe de support et nous serons heureux de vous aider.',

    // Broker Dashboard
    'broker.dashboard_title': 'Tableau de bord du courtier',
    'broker.dashboard_subtitle': 'Gérez vos propriétés et interactions clients',
    'broker.my_properties': 'Mes propriétés',
    'broker.my_properties_desc': 'Consultez et gérez vos annonces immobilières',
    'broker.viewings': 'Visites de propriétés',
    'broker.viewings_desc': 'Gérez les visites programmées',
    'broker.contacts': 'Messages clients',
    'broker.contacts_desc': 'Consultez et répondez aux demandes des clients',
    'broker.analytics': 'Analyse des propriétés',
    'broker.analytics_desc': "Suivez les vues et l'engagement sur vos annonces",
    'broker.subscription': 'Abonnement',
    'broker.subscription_desc': 'Gérez votre plan et limites d\'annonces',
    'broker.access_denied': 'Cette page est uniquement accessible aux courtiers enregistrés.',
    'broker.profile_title': 'Profil du courtier',
    'broker.profile_description': 'Gérez votre profil public et vos coordonnées',
    'broker.back_to_dashboard': 'Retour au tableau de bord',
    'broker.display_name': 'Nom d\'affichage',
    'broker.display_name_placeholder': 'Votre nom tel qu\'il apparaît aux clients',
    'broker.contact_email': 'Email de contact',
    'broker.email_placeholder': 'votre@email.com',
    'broker.email_notification_note': 'Cet email sera utilisé pour les notifications concernant les visites et les messages des clients.',
    'broker.phone_number': 'Numéro de téléphone',
    'broker.phone_placeholder': '+33 xxx xxx xxx',
    'broker.change_photo': 'Changer la photo',
    'broker.save_profile': 'Enregistrer le profil',
    'broker.profile_saved': 'Profil enregistré avec succès',
    'broker.profile_save_error': 'Échec de l\'enregistrement du profil',
    'broker.profile_load_error': 'Échec du chargement du profil',
    'broker.avatar_uploaded': 'Photo téléchargée avec succès',
    'broker.avatar_upload_error': 'Échec du téléchargement de la photo',
    'broker.invalid_image_type': 'Veuillez sélectionner un fichier image',
    'broker.image_too_large': 'L\'image doit faire moins de 2Mo',
    'common.login_required': 'Veuillez vous connecter pour accéder à cette page.',
    
    // Profile Email Notifications
    'profile.email_notifications': 'Notifications par e-mail',
    'profile.email_notifications_description': 'Choisissez les notifications par e-mail que vous souhaitez recevoir',
    'profile.notif_property_alerts': 'Alertes propriétés',
    'profile.notif_property_alerts_desc': 'Recevez des notifications sur les nouvelles propriétés correspondant à vos recherches enregistrées',
    'profile.notif_saved_search_alerts': 'Alertes recherches enregistrées',
    'profile.notif_saved_search_alerts_desc': 'Recevez des alertes lorsque de nouvelles propriétés correspondent à vos critères de recherche',
    'profile.notif_newsletter': 'Newsletter',
    'profile.notif_newsletter_desc': 'Recevez notre newsletter avec des actualités immobilières et des analyses de marché',
    'profile.notif_viewing_confirmations': 'Confirmations de visites',
    'profile.notif_viewing_confirmations_desc': 'Recevez des confirmations par e-mail pour vos visites programmées',
    'profile.email_prefs_saved': 'Préférences e-mail enregistrées',
    'profile.email_prefs_error': 'Échec de l\'enregistrement des préférences',
    
    // Agency Dashboard
    'agency.my_agency': 'Mon agence',
    'agency.dashboard_title': 'Tableau de bord de l\'agence',
    'agency.dashboard_subtitle': 'Gérez vos courtiers et les paramètres de l\'agence',
    'agency.brokers_tab': 'Courtiers',
    'agency.invites_tab': 'Invitations',
    'agency.settings_tab': 'Paramètres',
    'agency.brokers_title': 'Courtiers de l\'agence',
    'agency.brokers_description': 'Gérez les courtiers de votre agence',
    'agency.create_broker': 'Créer un courtier',
    'agency.create_broker_title': 'Créer un nouveau compte courtier',
    'agency.create_broker_description': 'Créez un nouveau compte courtier directement lié à votre agence',
    'agency.broker_name': 'Nom complet',
    'agency.broker_email': 'Adresse e-mail',
    'agency.broker_password': 'Mot de passe',
    'agency.create_broker_btn': 'Créer le compte courtier',
    'agency.broker_created': 'Compte courtier créé avec succès',
    'agency.broker_create_error': 'Échec de la création du compte courtier',
    'agency.no_brokers': 'Pas encore de courtiers',
    'agency.no_brokers_description': 'Créez des comptes courtiers ou envoyez des invitations',
    'agency.broker_removed': 'Courtier retiré de l\'agence',
    'agency.broker_remove_error': 'Échec du retrait du courtier',
    'agency.invites_title': 'Invitations en attente',
    'agency.invites_description': 'Gérez les invitations envoyées aux courtiers potentiels',
    'agency.send_invite': 'Envoyer une invitation',
    'agency.send_invite_title': 'Inviter un courtier',
    'agency.send_invite_description': 'Envoyez une invitation à rejoindre votre agence',
    'agency.invite_email': 'Adresse e-mail',
    'agency.send_invite_btn': 'Envoyer l\'invitation',
    'agency.invite_sent': 'Invitation envoyée avec succès',
    'agency.invite_error': 'Échec de l\'envoi de l\'invitation',
    'agency.link_copied': 'Lien d\'invitation copié',
    'agency.no_invites': 'Aucune invitation en attente',
    'agency.settings_title': 'Paramètres de l\'agence',
    'agency.settings_description': 'Mettez à jour les informations de votre agence',
    'agency.settings_saved': 'Paramètres de l\'agence enregistrés',
    'agency.settings_save_error': 'Échec de l\'enregistrement des paramètres',
    'agency.load_error': 'Échec du chargement des données de l\'agence',
    'agency_invite.title': 'Rejoindre l\'agence',
    'agency_invite.description': 'Vous avez été invité à rejoindre {agencyName}',
    'agency_invite.accept_btn': 'Accepter l\'invitation',
    'agency_invite.accepted_success': 'Agence rejointe avec succès !',
    'agency_invite.accept_error': 'Échec de l\'acceptation de l\'invitation',
    'agency_invite.create_account': 'Créer un compte',
    'agency_invite.invalid_link': 'Lien d\'invitation invalide',
    'agency_invite.expired': 'Cette invitation a expiré',
    'agency_invite.already_accepted': 'Cette invitation a déjà été acceptée',
    'agency_invite.fetch_error': 'Échec du chargement de l\'invitation',
    'agency_invite.error_title': 'Invitation invalide',
    'agency_invite.back_home': 'Retour à l\'accueil',
    'agency_invite.create_account_description': 'Créez un compte pour accepter cette invitation',
    'agency_invite.your_name': 'Votre nom',
    'agency_invite.name_placeholder': 'Entrez votre nom complet',
    'agency_invite.email_placeholder': 'votre@email.com',
    'agency_invite.password': 'Mot de passe',
    'agency_invite.password_placeholder': 'Créez un mot de passe',
    'agency_invite.confirm_password': 'Confirmer le mot de passe',
    'agency_invite.confirm_password_placeholder': 'Confirmez votre mot de passe',
    'agency_invite.register_accept_btn': 'Créer un compte et rejoindre',
    'agency_invite.registered_success': 'Compte créé et agence rejointe !',
    'agency_invite.register_error': 'Échec de la création du compte',
    'agency_invite.passwords_mismatch': 'Les mots de passe ne correspondent pas',
    'agency_invite.password_too_short': 'Le mot de passe doit avoir au moins 6 caractères',
    'agency_invite.already_have_account': 'Vous avez déjà un compte ?',
    'agency_invite.login_here': 'Connectez-vous ici',
    'agency_invite.joining': 'Vous rejoignez',
    'agency_invite.logged_in_as': 'Connecté en tant que',
    'agency.notifications_tab': 'Notifications',
    'agency.notifications_title': 'Notifications par e-mail',
    'agency.notifications_description': 'Choisissez les notifications que vous recevez sur l\'activité des courtiers',
    'agency.notifications_saved': 'Préférences de notification enregistrées',
    'agency.notifications_save_error': 'Échec de l\'enregistrement des préférences',
    'agency.notif_new_broker_joins': 'Nouveau courtier rejoint',
    'agency.notif_new_broker_joins_desc': 'Soyez notifié quand un courtier accepte votre invitation',
    'agency.notif_broker_property_added': 'Courtier ajoute une propriété',
    'agency.notif_broker_property_added_desc': 'Soyez notifié quand les courtiers ajoutent de nouvelles propriétés',
    'agency.notif_broker_viewing_requests': 'Demandes de visite',
    'agency.notif_broker_viewing_requests_desc': 'Soyez notifié des demandes de visite pour les propriétés des courtiers',
    'agency.notif_broker_client_messages': 'Messages des clients',
    'agency.notif_broker_client_messages_desc': 'Soyez notifié quand les clients contactent vos courtiers',
    'agency.notif_weekly_summary': 'Résumé hebdomadaire',
    'agency.notif_weekly_summary_desc': 'Recevez un résumé hebdomadaire de l\'activité de l\'agence',
    'agency.analytics_tab': 'Analytique',
    'agency.no_brokers_analytics': 'Ajoutez des courtiers pour voir les analyses de performance',
    'agency.total_properties': 'Total des propriétés',
    'agency.total_views': 'Vues totales',
    'agency.total_viewings': 'Demandes de visite',
    'agency.total_sold': 'Propriétés vendues',
    'agency.total_sales_value': 'Valeur totale des ventes',
    'agency.broker_performance_chart': 'Performance des courtiers',
    'agency.broker_performance_chart_desc': 'Comparez les métriques des courtiers en un coup d\'œil',
    'agency.individual_broker_stats': 'Statistiques individuelles',
    'agency.individual_broker_stats_desc': 'Métriques détaillées pour chaque courtier',
    'agency.properties': 'Propriétés',
    'agency.views': 'Vues',
    'agency.viewings': 'Visites',
    'agency.contacts': 'Contacts',
    'agency.active': 'Actif',
    'agency.sold': 'Vendu',
    'agency.sales_value': 'Valeur des ventes',
    'agency.upgrade_plan': 'Améliorer le plan',
    'agency.view_plans': 'Voir les plans',
    'agency.billing_tab': 'Facturation',
    'agency.name_label': "Nom de l'agence",
    'agency.email_label': 'E-mail',
    'agency.phone_label': 'Téléphone',
    'agency.website_label': 'Site web',
    'agency.address_label': 'Adresse',
    'agency.city_label': 'Ville',
    'agency.description_label': 'Description',
    'agency.description_placeholder': 'Parlez de votre agence aux clients...',
    'agency.save_settings': 'Enregistrer les paramètres',
    'agency.broker_usage': 'Utilisation des courtiers',
    'agency.invite_broker': 'Inviter un courtier',
    'agency.brokers': 'courtiers',
    'agency.broker_limit_reached': 'Limite de courtiers atteinte',
    'agency.upgrade_for_more': 'Mettez à niveau votre plan pour ajouter plus de courtiers',
    'agency.active_listings': 'Annonces actives',
    'agency.broker_slots_remaining': 'Places restantes',
    'agency.manage_subscription': 'Gérer l\'abonnement',
    'agency.upgrade_your_plan': 'Mettre à niveau votre plan',
    'agency.choose_plan': 'Choisissez le plan qui convient le mieux à votre agence',
    'agency.pro_plan': 'Pro',
    'agency.enterprise_plan': 'Entreprise',
    'agency.for_growing_agencies': 'Pour les agences en croissance',
    'agency.for_large_agencies': 'Pour les grandes agences',
    'agency.per_month': '/mois',
    'agency.up_to_brokers': 'Jusqu\'à {count} courtiers',
    'agency.unlimited_brokers': 'Courtiers illimités',
    'agency.advanced_analytics': 'Analyses avancées',
    'agency.full_analytics': 'Suite d\'analyses complète',
    'agency.priority_support': 'Support prioritaire',
    'agency.dedicated_support': 'Support dédié',
    'agency.upgrade_to_pro': 'Passer à Pro',
    'agency.upgrade_to_enterprise': 'Passer à Entreprise',
    'agency.popular': 'Populaire',
    'agency.current_plan': 'Plan actuel',
    'agency.plan_features': 'Fonctionnalités du plan',
    'agency.billing_history': 'Historique de facturation',
    'agency.no_billing_history': 'Pas encore d\'historique de facturation',
    'agency.subscription_billing': 'Abonnement & Facturation',
    'agency.manage_agency_subscription': 'Gérer l\'abonnement de votre agence',
    'agency.plan': 'Plan',
    'agency.basic_plan': 'Basique',
    'agency.broker_limit': 'Limite de courtiers',
    'agency.current_usage': 'Utilisation actuelle',
    'agency.status': 'Statut',
    'agency.active_status': 'Actif',
    'agency.unlimited': 'Illimité',
    'agency.feature': 'Fonctionnalité',
    'agency.price': 'Prix',
    'agency.free': 'Gratuit',
    'agency.analytics': 'Analytique',
    'agency.basic_analytics': 'Basique',
    'agency.advanced': 'Avancé',
    'agency.full_suite': 'Suite complète',
    'agency.support': 'Support',
    'agency.email_support': 'E-mail',
    'agency.priority': 'Prioritaire',
    'agency.dedicated': 'Dédié',
    'agency_directory.title': 'Agences immobilières',
    'agency_directory.subtitle': 'Parcourez et trouvez des agences immobilières de confiance',
    'agency_directory.search_placeholder': 'Rechercher des agences...',
    'agency_directory.location_placeholder': 'Filtrer par lieu...',
    'agency_directory.agencies_found': 'agences trouvées',
    'agency_directory.no_agencies': 'Aucune agence trouvée',
    'agency_directory.no_agencies_description': 'Essayez d\'ajuster vos critères de recherche',
    'agency_directory.brokers': 'courtiers',
    'agency_directory.view_agency': 'Voir l\'agence',
    'agency_directory.not_found': 'Agence non trouvée',
    'agency_directory.back_to_directory': 'Retour au répertoire',
    'agency_directory.visit_website': 'Visiter le site web',
    'agency_directory.our_brokers': 'Nos courtiers',
    'agency_directory.no_brokers_yet': 'Pas encore de courtiers dans cette agence',
    'agency_directory.agency_listings': 'Annonces immobilières',
    
    // User Listings
    'userListings.title': 'Mes annonces',
    'userListings.subtitle': 'Gérez vos annonces immobilières et créez-en de nouvelles',
    'userListings.pricingTitle': 'Tarification des annonces',
    'userListings.pricingDesc': 'Créez votre annonce immobilière avec notre tarification simple',
    'userListings.listingPrice': '10$ par annonce',
    'userListings.listingDuration': 'Valable 1 mois',
    'userListings.videoPrice': '+3$ pour la vidéo',
    'userListings.videoDesc': 'Ajoutez une visite vidéo à votre annonce',
    'userListings.includeVideo': 'Inclure la visite vidéo',
    'userListings.createListing': 'Créer une annonce',
    'userListings.myListingsTitle': 'Vos propriétés',
    'userListings.noListings': 'Pas encore d\'annonces',
    'userListings.noListingsDesc': 'Créez votre première annonce immobilière pour commencer',
    'userListings.createFirst': 'Créer votre première annonce',
    'userListings.hasVideo': 'Vidéo',
    'userListings.renew': 'Renouveler',
    'userListings.renewSuccess': 'Annonce renouvelée!',
    'userListings.renewSuccessDesc': 'Votre annonce a été renouvelée pour un mois supplémentaire',
    'userListings.paymentSuccess': 'Paiement réussi!',
    'userListings.paymentSuccessDesc': 'Votre annonce est maintenant en ligne',
    'userListings.paymentCancelled': 'Paiement annulé',
    'userListings.paymentCancelledDesc': 'Votre paiement a été annulé',
    'userListings.checkoutError': 'Échec de la création du paiement',
    'userListings.renewError': 'Échec du renouvellement de l\'annonce',
    'userListings.createDraft': 'Créer une annonce',
    'userListings.previewListing': 'Aperçu de votre annonce',
    'userListings.draft': 'Brouillon',
    'userListings.drafts': 'Brouillons',
    'userListings.draftSaved': 'Brouillon enregistré',
    'userListings.draftSavedDesc': 'Votre annonce a été enregistrée comme brouillon',
    'userListings.draftError': 'Échec de l\'enregistrement du brouillon',
    'userListings.draftDeleted': 'Brouillon supprimé',
    'userListings.deleteError': 'Échec de la suppression',
    'userListings.saveDraft': 'Enregistrer le brouillon',
    'userListings.saveAndPreview': 'Enregistrer et prévisualiser',
    'userListings.publishOptions': 'Options de publication',
    'userListings.addVideoTour': 'Ajouter une visite vidéo',
    'userListings.videoIncluded': 'Visite vidéo incluse',
    'userListings.totalPrice': 'Total',
    'userListings.backToEdit': 'Retour à l\'édition',
    'userListings.publishNow': 'Publier maintenant',
    'userListings.publish': 'Publier',
    'userListings.publishSuccess': 'Annonce publiée !',
    'userListings.publishSuccessDesc': 'Votre annonce est maintenant en attente de vérification',
    'userListings.firstListingFree': 'Première annonce gratuite !',
    'userListings.firstListingFreeDesc': 'Votre première annonce immobilière est entièrement gratuite. Soumettez maintenant et atteignez des acheteurs dans tout l\'Irak !',
    'userListings.pending': 'En attente de vérification',
    'userListings.pendingInfo': 'Ces annonces sont en cours de vérification par notre équipe de modération. Vous serez notifié une fois approuvées.',
    'userListings.pendingReview': 'En attente de vérification',
    'userListings.awaitingApproval': 'En attente d\'approbation du modérateur',
    'userListings.rejected': 'Annonces refusées',
    'userListings.rejectedInfo': 'Ces annonces n\'ont pas été approuvées. Veuillez modifier et corriger les problèmes avant de soumettre à nouveau.',
    'userListings.rejectedBadge': 'Refusée',
    'userListings.live': 'En ligne',
    'userListings.editDraft': 'Modifier',
    'userListings.editAndResubmit': 'Modifier et resoumettre',
    'userListings.pendingCannotEdit': 'Modification impossible pendant la vérification',
    'userListings.resubmitForReview': 'Resoumettre pour vérification',
    'userListings.resubmitInfo': 'Vos modifications ont été enregistrées. Soumettez à nouveau votre annonce pour vérification sans frais supplémentaires.',
    'userListings.submitForReview': 'Soumettre pour vérification',
    'userListings.validation.priceRequired': 'Le prix est requis',
    'userListings.validation.cityRequired': 'La ville est requise',
    'userListings.validation.roomsRequired': 'Le nombre de pièces est requis',
    'userListings.validation.sizeRequired': 'La taille est requise',
    'userListings.validation.plotSizeRequired': 'La taille du terrain est requise',
    'userListings.validation.imagesRequired': 'Au moins une image est requise',
    'userListings.choosePlan': 'Choisissez votre plan d\'annonce',
    'userListings.choosePlanDesc': 'Sélectionnez combien d\'images afficher sur votre carte d\'annonce',
    'userListings.upgradePlan': 'Améliorez votre plan d\'annonce',
    'userListings.upgradePlanDesc': 'Améliorez pour afficher plus d\'images. Vous ne payez que la différence.',
    'userListings.selectImages': 'Sélectionnez les images à afficher',
    'userListings.selectImagesDesc': 'Choisissez les images à afficher sur votre carte d\'annonce',
    'userListings.selectPrimaryImage': 'Sélectionnez l\'image principale',
    'userListings.selectPrimaryImageDesc': 'Choisissez l\'image principale qui s\'affiche en premier sur votre annonce',
    'userListings.addVideo': 'Ajouter une visite vidéo',
    'userListings.pleaseSelectImages': 'Veuillez sélectionner des images pour continuer',
    'userListings.pleaseSelectPrimaryImage': 'Veuillez sélectionner une image principale pour continuer',
    'userListings.proceedToPayment': 'Procéder au paiement',
    'userListings.tierBasic': 'Basique',
    'userListings.tierStandard': 'Standard',
    'userListings.tierPremium': 'Premium',
    'userListings.tierBasicDesc': '1 image sur la carte',
    'userListings.tierStandardDesc': '3 images carrousel',
    'userListings.tierPremiumDesc': 'Toutes les images + badge',
    'userListings.tierDuration': '30 jours actif',
    'userListings.tierBest': 'Meilleur',
    'userListings.tierBasicFull': 'Niveau Basique (1 image)',
    'userListings.tierStandardFull': 'Niveau Standard (3 images)',
    'userListings.tierPremiumFull': 'Niveau Premium (Toutes les images)',
    'userListings.changeTier': 'Changer de niveau',
    'userListings.selectImageTier': "Sélectionner le niveau d'image",
    'userListings.selectTierFirst': 'Sélectionnez un niveau avant de publier',
    'userListings.upgrade': 'Améliorer',
    'common.total': 'Total',
    
    // Settings Page
    'settings.title': 'Paramètres',
    'settings.subtitle': 'Gérer les paramètres et préférences de votre compte',
    'settings.profile': 'Profil',
    'settings.notifications': 'Notifications',
    'settings.account': 'Compte',
    'settings.profile_info': 'Informations du profil',
    'settings.profile_desc': 'Mettre à jour vos informations personnelles',
    'settings.change_photo': 'Changer la photo',
    'settings.display_name': 'Nom d\'affichage',
    'settings.display_name_placeholder': 'Votre nom',
    'settings.email': 'E-mail',
    'settings.email_placeholder': 'votre@email.fr',
    'settings.phone': 'Téléphone',
    'settings.phone_placeholder': '+33 6 12 34 56 78',
    'settings.save': 'Enregistrer les modifications',
    'settings.saved': 'Paramètres enregistrés avec succès',
    'settings.load_error': 'Échec du chargement des paramètres',
    'settings.save_error': 'Échec de l\'enregistrement des paramètres',
    'settings.invalid_image': 'Veuillez sélectionner un fichier image',
    'settings.image_too_large': 'L\'image doit être inférieure à 2 Mo',
    'settings.avatar_uploaded': 'Photo téléchargée',
    'settings.avatar_error': 'Échec du téléchargement de la photo',
    'settings.notification_preferences': 'Préférences de notification',
    'settings.notification_desc': 'Choisissez les notifications que vous souhaitez recevoir',
    'settings.notif_property_alerts': 'Alertes immobilières',
    'settings.notif_property_alerts_desc': 'Soyez notifié des nouvelles propriétés',
    'settings.notif_saved_search': 'Alertes de recherche sauvegardée',
    'settings.notif_saved_search_desc': 'Soyez notifié quand de nouvelles propriétés correspondent à vos recherches sauvegardées',
    'settings.notif_newsletter': 'Newsletter',
    'settings.notif_newsletter_desc': 'Recevez notre newsletter avec les actualités du marché',
    'settings.notif_viewing': 'Confirmations de visite',
    'settings.notif_viewing_desc': 'Recevez des confirmations pour les visites programmées',
    'settings.account_settings': 'Paramètres du compte',
    'settings.account_desc': 'Gérer la sécurité et les préférences de votre compte',
    'settings.change_password': 'Changer le mot de passe',
    'settings.change_password_desc': 'Mettre à jour votre mot de passe',
    'settings.change': 'Changer',
    'settings.two_factor': 'Authentification à deux facteurs',
    'settings.two_factor_desc': 'Ajouter une sécurité supplémentaire à votre compte',
    'settings.enable': 'Activer',
    'settings.delete_account': 'Supprimer le compte',
    'settings.delete_account_desc': 'Supprimer définitivement votre compte',
    'settings.delete': 'Supprimer',
    'settings.logout': 'Déconnexion',
    
    // Common
    'common.back': 'Retour',
    
    // Contact Form Modal
    'contact_form.title': 'Contact',
    'contact_form.description': 'Envoyez un message pour nous contacter.',
    'contact_form.name': 'Nom',
    'contact_form.name_placeholder': 'Votre nom',
    'contact_form.email': 'E-mail',
    'contact_form.email_placeholder': 'votre@email.fr',
    'contact_form.phone': 'Téléphone',
    'contact_form.phone_placeholder': 'Votre numéro de téléphone',
    'contact_form.message': 'Message',
    'contact_form.message_placeholder': 'Comment pouvons-nous vous aider ?',
    'contact_form.send': 'Envoyer le message',
    'contact_form.sending': 'Envoi en cours...',
    'contact_form.success_title': 'Message envoyé !',
    'contact_form.success_message': 'Votre message a été envoyé. Vous recevrez une confirmation par e-mail.',
    'contact_form.error_title': 'Erreur',
    'contact_form.error_message': 'Échec de l\'envoi du message. Veuillez réessayer.',
    
    // Messaging
    'messaging.title': 'Messages',
    'messaging.description': 'Vos conversations privées',
    'messaging.new_message': 'Nouveau message',
    'messaging.no_messages': 'Pas encore de messages',
    'messaging.no_conversations': 'Pas encore de conversations',
    'messaging.send': 'Envoyer',
    'messaging.send_message': 'Envoyer un message',
    'messaging.message_to': 'Envoyer un message à',
    'messaging.subject': 'Sujet',
    'messaging.subject_placeholder': 'Sujet (facultatif)',
    'messaging.message': 'Message',
    'messaging.message_placeholder': 'Écrivez votre message...',
    'messaging.login_required': 'Vous devez être connecté pour envoyer des messages.',
    'messaging.message_sent': 'Message envoyé avec succès',
    'messaging.contact_agent_description': 'Contactez directement l\'agent immobilier',
    'messaging.type_message': 'Tapez un message...',
    'messaging.inbox': 'Boîte de réception',
    'messaging.sent': 'Envoyés',
    'messaging.search': 'Rechercher des conversations...',
    'messaging.select_conversation': 'Sélectionnez une conversation',
    'messaging.select_description': 'Choisissez une conversation dans la liste pour commencer à discuter',
    'messaging.encrypted': 'Chiffrement de bout en bout',
    'messaging.decrypt': 'Déchiffrer le message',
    'messaging.reply': 'Répondre',
    'messaging.replying_to': 'Réponse à',
    'messaging.replying_to_yourself': 'vous-même',
    'messaging.forward': 'Transférer',
    'messaging.delete': 'Supprimer',
    'messaging.delete_confirm': 'Supprimer le message',
    'messaging.delete_description': 'Ce message sera supprimé pour tout le monde. Cette action est irréversible.',
    'messaging.delete_for_everyone': 'Supprimer pour tous',
    'messaging.pin': 'Épingler',
    'messaging.unpin': 'Désépingler',
    'messaging.pinned_messages': 'Messages épinglés',
    'messaging.no_pinned': 'Aucun message épinglé',
    'messaging.delivered': 'Livré',
    'messaging.read': 'Lu',
    'messaging.seen_at': 'Vu à',
    'messaging.voice_message': 'Message vocal',
    'messaging.search_messages': 'Rechercher dans les messages...',
    'messaging.contacts': 'Contacts',
    'messaging.results_found': 'résultats trouvés',
    'messaging.recording': 'Enregistrement...',
    'messaging.typing': 'écrit...',
    'messaging.online': 'en ligne',
    'messaging.tap_for_info': 'appuyez ici pour les infos de contact',
    'messaging.photos': 'Photos',
    'messaging.camera': 'Caméra',
    'messaging.location': 'Localisation',
    'messaging.contact': 'Contact',
    'messaging.document': 'Document',
    'messaging.poll': 'Sondage',
    'messaging.event': 'Événement',
    'messaging.location_coming_soon': 'Partage de localisation bientôt disponible',
    'messaging.contact_coming_soon': 'Partage de contact bientôt disponible',
    'messaging.poll_coming_soon': 'Création de sondage bientôt disponible',
    'messaging.event_coming_soon': 'Création d\'événement bientôt disponible',
    'messaging.schedule_event': 'Planifier un événement',
    'messaging.event_title': 'Titre de l\'événement',
    'messaging.event_title_placeholder': 'ex: Visite de propriété',
    'messaging.event_date': 'Date',
    'messaging.event_time': 'Heure',
    'messaging.event_location': 'Lieu',
    'messaging.event_location_placeholder': 'Entrez le lieu ou l\'adresse',
    'messaging.event_description': 'Description',
    'messaging.event_description_placeholder': 'Ajoutez des détails supplémentaires...',
    'messaging.send_event': 'Envoyer l\'événement',
    'messaging.event_created': 'Événement créé',
    'messaging.create_poll': 'Créer un sondage',
    'messaging.poll_question': 'Question',
    'messaging.poll_question_placeholder': 'Que voulez-vous demander?',
    'messaging.poll_options': 'Options',
    'messaging.option': 'Option',
    'messaging.add_option': 'Ajouter une option',
    'messaging.allow_multiple': 'Autoriser plusieurs réponses',
    'messaging.send_poll': 'Envoyer le sondage',
    'messaging.poll_created': 'Sondage créé',
    'messaging.edit': 'Modifier',
    'messaging.edit_message': 'Modifier le message',
    'messaging.edit_description': 'Apportez des modifications à votre message',
    'messaging.edit_time_remaining': 'Vous avez',
    'messaging.minutes_to_edit': 'minutes pour modifier ce message',
    'messaging.message_edited': 'Message modifié',
    'messaging.edit_failed': 'Échec de la modification',
    'messaging.from_date': 'De',
    'messaging.to_date': 'À',
    'messaging.clear_filters': 'Effacer les filtres',
    'messaging.export_chat': 'Exporter la conversation',
    'messaging.export_description': 'Exporter l\'historique de conversation',
    'messaging.export_text': 'Fichier texte (.txt)',
    'messaging.export_text_desc': 'Format texte simple, facile à lire et partager',
    'messaging.export_pdf': 'Document PDF',
    'messaging.export_pdf_desc': 'Document formaté, idéal pour l\'impression',
    'messaging.export_messages_count': 'messages seront exportés',
    'messaging.exporting': 'Exportation...',
    'messaging.export': 'Exporter',
    'messaging.export_success': 'Conversation exportée avec succès',
    'messaging.export_failed': 'Échec de l\'exportation',
    'messaging.is_typing': 'écrit...',
    
    // Quick Reply Templates
    'quick_reply.title': 'Réponses Rapides',
    'quick_reply.greeting': 'Bonjour ! Merci de nous avoir contactés.',
    'quick_reply.interested': 'Je suis intéressé par ce bien. Pouvez-vous me donner plus de détails ?',
    'quick_reply.schedule_viewing': 'Je souhaite planifier une visite. Quels créneaux sont disponibles ?',
    'quick_reply.ask_price': 'Le prix est-il négociable ?',
    'quick_reply.ask_availability': 'Ce bien est-il toujours disponible ?',
    'quick_reply.thank_you': 'Merci pour les informations. Je vous recontacte bientôt.',
    'quick_reply.will_contact': 'Je vous contacterai bientôt avec plus de détails.',
    'quick_reply.not_interested': 'Merci, mais je ne suis plus intéressé par ce bien.',
    
    // Notifications
    'notifications.enable': 'Activer les notifications',
    'notifications.on': 'Notifications activées',
    'notifications.preferences': 'Préférences de notification',
    'notifications.sound': 'Son de notification',
    'notifications.sound_desc': 'Jouer un son lorsque vous recevez un message',
    'notifications.sound_default': 'Défaut',
    'notifications.sound_chime': 'Carillon',
    'notifications.sound_bell': 'Cloche',
    'notifications.sound_pop': 'Pop',
    'notifications.test': 'Tester',
    'notifications.vibration': 'Vibration',
    'notifications.vibration_desc': 'Vibrer lorsque vous recevez un message (mobile uniquement)',
    'messaging.you': 'Vous',
    'messaging.encrypted_message': 'Message chiffré',
    'messaging.e2e_notice': 'Les messages et appels sont chiffrés de bout en bout. Seules les personnes dans cette conversation peuvent les lire, les écouter ou les partager.',
    'messaging.today': "Aujourd'hui",
    'messaging.yesterday': 'Hier',
    'messaging.search_groups': 'Rechercher des groupes...',
    
    // Attachment viewer
    'attachment.all_media': 'Tous les médias',
    'attachment.go_to_message': 'Aller au message',
    'attachment.download': 'Télécharger',
    'attachment.view_fullscreen': 'Afficher en plein écran',
    'attachment.save_to_gallery': 'Enregistrer dans la galerie',
    'attachment.share': 'Partager',
    'attachment.double_tap_zoom': 'Appuyez deux fois pour zoomer',
    'attachment.zoom_in': 'Zoom avant',
    'attachment.zoom_out': 'Zoom arrière',
    'attachment.reset_zoom': 'Réinitialiser le zoom',
    'attachment.ctrl_scroll_zoom': 'Ctrl + Défilement pour zoomer',
    'attachment.fit_width': 'Ajuster à la largeur',
    'attachment.fit_height': 'Ajuster à la hauteur',
    'attachment.click_to_navigate': 'Cliquer pour naviguer',
    
    // Status
    'status.online': 'En ligne',
    'status.offline': 'Hors ligne',
    'status.last_seen': 'Vu pour la dernière fois',
    
    // Blocking
    'blocking.block_user': 'Bloquer l\'utilisateur',
    'blocking.block_confirm': 'Êtes-vous sûr de vouloir bloquer {name} ? Ils ne pourront plus vous envoyer de messages.',
    'blocking.reason_optional': 'Raison (facultatif)',
    'blocking.reason_placeholder': 'Pourquoi bloquez-vous cet utilisateur ?',
    'blocking.block': 'Bloquer',
    'blocking.user_blocked': 'Utilisateur bloqué avec succès',
    'blocking.already_blocked': 'L\'utilisateur est déjà bloqué',
    'blocking.error': 'Échec du blocage de l\'utilisateur',
    'blocking.blocked_users': 'Utilisateurs bloqués',
    'blocking.blocked_users_description': 'Gérez votre liste d\'utilisateurs bloqués',
    'blocking.no_blocked_users': 'Vous n\'avez bloqué personne',
    'blocking.blocked_on': 'Bloqué le {date}',
    'blocking.unblock': 'Débloquer',
    'blocking.user_unblocked': 'Utilisateur débloqué',
    'blocking.unblock_error': 'Échec du déblocage de l\'utilisateur',
    'blocking.fetch_error': 'Échec du chargement des utilisateurs bloqués',
    'blocking.you_blocked_user': 'Vous avez bloqué cet utilisateur. Débloquez pour envoyer des messages.',
    'blocking.user_blocked_you': 'Cet utilisateur vous a bloqué. Vous ne pouvez pas envoyer de messages.',
    
    // Reporting
    'reporting.report_user': 'Signaler l\'utilisateur',
    'reporting.report_description': 'Signaler {name} pour violation de nos règles',
    'reporting.select_reason': 'Veuillez sélectionner une raison',
    'reporting.reason': 'Raison du signalement',
    'reporting.additional_details': 'Détails supplémentaires',
    'reporting.details_placeholder': 'Fournissez un contexte supplémentaire...',
    'reporting.submit_report': 'Envoyer le signalement',
    'reporting.report_submitted': 'Signalement envoyé. Nous l\'examinerons sous peu.',
    'reporting.error': 'Échec de l\'envoi du signalement',
    'reporting.reasons.spam': 'Spam ou messages indésirables',
    'reporting.reasons.harassment': 'Harcèlement ou intimidation',
    'reporting.reasons.inappropriate_content': 'Contenu inapproprié',
    'reporting.reasons.fraud': 'Fraude ou arnaque',
    'reporting.reasons.impersonation': 'Usurpation d\'identité',
    'reporting.reasons.other': 'Autre',
    
    // Admin Reports
    'admin.reports': 'Signalements',
    'admin.reports_panel': 'Signalements des utilisateurs',
    'admin.reports_description': 'Examiner et résoudre les signalements',
    'admin.fetch_reports_error': 'Échec de la récupération des signalements',
    'admin.pending': 'En attente',
    'admin.resolved': 'Résolu',
    'admin.all': 'Tous',
    'admin.no_reports': 'Aucun signalement trouvé',
    'admin.back_to_list': 'Retour à la liste',
    'admin.reporter': 'Signaleur',
    'admin.reported_user': 'Utilisateur signalé',
    'admin.reason': 'Raison',
    'admin.reported_on': 'Signalé le',
    'admin.reported_by': 'Signalé par',
    'admin.resolution_notes': 'Ajouter des notes de résolution...',
    'admin.dismiss': 'Rejeter',
    'admin.warn': 'Avertir l\'utilisateur',
    'admin.block': 'Bloquer l\'utilisateur',
    'admin.report_resolved': 'Signalement résolu avec succès',
    'admin.resolve_error': 'Échec de la résolution du signalement',
    'admin.resolution': 'Résolution',
    'messaging.unknown_user': 'Utilisateur inconnu',
    'messaging.no_groups': 'Pas encore de groupes',
    'messaging.create_group': 'Créer un groupe',
    'messaging.group_chat': 'Chat de groupe',
    'messaging.direct_messages': 'Direct',
    'messaging.groups': 'Groupes',
    'messaging.group_name': 'Nom du groupe',
    'messaging.group_description': 'Description',
    
    'nav.favorites': 'Favoris',
    'nav.profile': 'Profil',
    'nav.login': 'Connexion',
    'nav.logout': 'Déconnexion',
    'nav.recent_searches': 'Recherches récentes',
    'nav.no_recent_searches': 'Aucune recherche récente',
    
    'agents.title': 'Trouver un agent',
    'agents.subtitle': 'Vous pensez à vendre ? Nous vous aidons à trouver des agents experts dans votre région !',
    'agents.area_search': 'Recherche par zone',
    'agents.name_search': 'Recherche par nom',
    'agents.search_placeholder': 'Entrez une zone ou une adresse',
    'agents.show': 'Afficher',
    'agents.sort_by': 'Trier par',
    'agents.agents_count': 'Agents',
    'agents.companies_label': 'Agences immobilières',
    'agents.recent_sales': 'Dernière date de vente',
    'agents.most_sales': 'Plus de ventes',
    'agents.alphabetical': 'Ordre alphabétique',
    'agents.showing_results': 'Affichage',
    'agents.agents_label': 'agents',
    'agents.loading': 'Chargement des agents...',
    'agents.no_results': 'Aucun agent trouvé.',
    'agents.phone': 'Téléphone :',
    'agents.status': 'Statut :',
    'agents.active_agent': 'Agent actif',
    'agents.anonymous_agent': 'Agent anonyme',
    'agents.agent_label': 'Agent',
    'agents.show_more': 'Afficher plus d\'agents',
    'agents.verified_agent': 'Agent vérifié',
    
    // Broker Public Profile
    'broker.listing_usage': 'Utilisation des annonces',
    'broker.monthly_listing_limits': 'Limites mensuelles d\'annonces',
    'broker.listings_used': 'Annonces utilisées',
    'broker.listings_remaining': 'annonces restantes',
    'broker.period_resets': 'La période se réinitialise',
    'broker.export_csv': 'Exporter CSV',
    'broker.total_views': 'Vues totales',
    'broker.scheduled_viewings': 'Visites programmées',
    'broker.client_messages': 'Messages clients',
    'broker.filters': 'Filtres',
    'broker.all_time': 'Tout le temps',
    'broker.active_listings': 'Annonces actives',
    'broker.sold_listings': 'Annonces vendues',
    'broker.active_listings_desc': 'Vos propriétés actuellement publiées',
    'broker.no_active_listings': 'Aucune annonce active. Ajoutez votre première propriété.',
    'broker.all_types': 'Tous les types',
    'broker.all_cities': 'Toutes les villes',
    'broker.property_type': 'Type de propriété',
    'broker.city': 'Ville',
    'broker.date_range': 'Plage de dates',
    'broker.last_week': 'Semaine dernière',
    'broker.last_month': 'Mois dernier',
    'broker.last_quarter': '3 derniers mois',
    'broker.sold_listings_desc': 'Propriétés que vous avez vendues',
    'broker.no_sold_listings': 'Aucune propriété vendue pour le moment',
    'broker.clear_filters': 'Effacer les filtres',
    'broker.selected': 'sélectionné(s)',
    'broker.compare': 'Comparer',
    'broker.mark_as_sold': 'Marquer comme vendu',
    'broker.delete_selected': 'Supprimer la sélection',
    'broker.select_all': 'Tout sélectionner',
    'broker.rejected_listings': 'Annonces rejetées',
    'broker.rejected_listings_desc': 'Ces annonces ont été rejetées par les modérateurs. Vous pouvez faire appel.',
    'broker.no_rejected_listings': 'Aucune annonce rejetée',
    'broker.confirm_delete': 'Confirmer la suppression',
    'broker_public.not_found': 'Agent non trouvé',
    'broker_public.not_found_description': 'Le profil d\'agent recherché n\'existe pas.',
    'broker_public.back_to_search': 'Retour à la recherche d\'agents',
    'broker_public.contact_broker': 'Contacter l\'agent',
    'broker_public.agency': 'Agence',
    'broker_public.visit_website': 'Visiter le site web',
    'broker_public.active_listings': 'Annonces actives',
    'broker_public.sold_properties': 'Propriétés vendues',
    'broker_public.total_sales': 'Total des ventes',
    'broker_public.avg_days_market': 'Jours moyens sur le marché',
    'broker_public.no_active_listings': 'Aucune annonce active pour le moment',
    'broker_public.no_sold_properties': 'Aucune propriété vendue pour l\'instant',
    'broker_public.view_all_properties': 'Voir toutes les propriétés',
    'broker_public.send_message': 'Envoyer un message',
    'broker_public.view_agency': 'Voir l\'agence',
    'broker_public.works_at': 'Travaille chez',
    'broker_public.listings': 'Annonces',
    'broker_public.sold': 'Vendu',
    'broker_public.reviews': 'Avis',
    'broker_public.rating': 'Note',
    'broker_public.featured_broker': 'Agent en vedette',
    'broker_public.top_performer': 'Meilleur performeur',
    
    // Toast messages
    'toast.logged_out': 'Déconnecté',
    'toast.welcome_back': 'Revenez quand vous voulez!',
    
    // Recent searches
    'search.properties_label': 'Biens',
    'search.from': 'à partir de',
    'search.up_to': 'jusqu\'à',
    
    // Agency Pricing
    'agency_pricing.badge': 'Plans Agence',
    'agency_pricing.title': 'Développez votre agence avec le bon plan',
    'agency_pricing.subtitle': 'Choisissez le plan parfait pour votre agence immobilière.',
    'agency_pricing.most_popular': 'Le plus populaire',
    'agency_pricing.per_month': '/mois',
    'agency_pricing.processing': 'Traitement...',
    'agency_pricing.comparison_title': 'Comparaison détaillée',
    'agency_pricing.feature': 'Fonctionnalité',
    'agency_pricing.faq_title': 'Questions fréquentes',
    'agency_pricing.cta_title': 'Prêt à développer votre agence?',
    'agency_pricing.cta_subtitle': 'Rejoignez des milliers d\'agences immobilières.',
    'agency_pricing.cta_button': 'Commencer avec Pro',
    'agency_pricing.sign_in_required': 'Connectez-vous pour mettre à niveau',
    'agency_pricing.tier.starter': 'Débutant',
    'agency_pricing.tier.starter_desc': 'Parfait pour les petites agences',
    'agency_pricing.tier.starter_cta': 'Commencer',
    'agency_pricing.tier.pro': 'Pro',
    'agency_pricing.tier.pro_desc': 'Pour les agences en croissance',
    'agency_pricing.tier.pro_cta': 'Passer à Pro',
    'agency_pricing.tier.enterprise': 'Enterprise',
    'agency_pricing.tier.enterprise_desc': 'Pour les grandes agences',
    'agency_pricing.tier.enterprise_cta': 'Choisir Enterprise',
    'agency_pricing.feature.brokers_3': 'Jusqu\'à 3 courtiers',
    'agency_pricing.feature.brokers_10': 'Jusqu\'à 10 courtiers',
    'agency_pricing.feature.brokers_unlimited': 'Courtiers illimités',
    'agency_pricing.feature.listings_basic': 'Annonces de base',
    'agency_pricing.feature.listings_advanced': 'Annonces avancées',
    'agency_pricing.feature.listings_premium': 'Annonces premium',
    'agency_pricing.feature.messaging': 'Messagerie client',
    'agency_pricing.feature.scheduling': 'Planification visites',
    'agency_pricing.feature.analytics': 'Tableau de bord analytique',
    'agency_pricing.feature.analytics_advanced': 'Analytique avancée',
    'agency_pricing.feature.support': 'Support prioritaire',
    'agency_pricing.feature.support_247': 'Support 24/7',
    'agency_pricing.feature.branding': 'Marque personnalisée',
    'agency_pricing.feature.api': 'Accès API',
    'agency_pricing.comparison.brokers': 'Nombre de courtiers',
    'agency_pricing.comparison.listings': 'Annonces immobilières',
    'agency_pricing.comparison.basic': 'Base',
    'agency_pricing.comparison.advanced': 'Avancé',
    'agency_pricing.comparison.premium': 'Premium',
    'agency_pricing.faq.change_plan_q': 'Puis-je changer de plan plus tard?',
    'agency_pricing.faq.change_plan_a': 'Oui! Vous pouvez changer à tout moment.',
    'agency_pricing.faq.exceed_limit_q': 'Que se passe-t-il si je dépasse la limite?',
    'agency_pricing.faq.exceed_limit_a': 'Vous devrez passer à un plan supérieur.',
    'agency_pricing.faq.trial_q': 'Y a-t-il un essai gratuit?',
    'agency_pricing.faq.trial_a': 'Garantie de remboursement de 14 jours.',
    'agency_pricing.faq.cancel_q': 'Comment annuler mon abonnement?',
    'agency_pricing.faq.cancel_a': 'Gérez votre abonnement depuis le tableau de bord.',
    'agency_pricing.view_plans': 'Voir les plans et tarifs',
    
    // Broker Pricing
    'broker_pricing.badge': 'Plans Courtier',
    'broker_pricing.title': 'Améliorez votre compte courtier',
    'broker_pricing.subtitle': 'Choisissez le plan parfait pour votre activité immobilière. Plus d\'annonces, de meilleures analyses et un support prioritaire.',
    'broker_pricing.most_popular': 'Le plus populaire',
    'broker_pricing.current_plan': 'Votre plan actuel',
    'broker_pricing.your_current_plan': 'Votre plan actuel',
    'broker_pricing.free': 'Gratuit',
    'broker_pricing.month': 'mois',
    'broker_pricing.per_month': '/mois',
    'broker_pricing.processing': 'Traitement...',
    'broker_pricing.loading': 'Chargement...',
    'broker_pricing.manage_subscription': 'Gérer l\'abonnement',
    'broker_pricing.cancel_anytime': 'Rétrograder ou annuler à tout moment • Renouvellement mensuel automatique',
    'broker_pricing.manage_note': 'Rétrograder ou annuler à tout moment • Renouvellement mensuel automatique',
    'broker_pricing.downgrade': 'Rétrograder',
    'broker_pricing.upgrade_to_pro': 'Passer à Pro',
    'broker_pricing.go_enterprise': 'Choisir Enterprise',
    'broker_pricing.get_started_pro': 'Commencer avec Pro',
    'broker_pricing.comparison_title': 'Comparaison détaillée des fonctionnalités',
    'broker_pricing.feature': 'Fonctionnalité',
    'broker_pricing.faq_title': 'Questions fréquentes',
    'broker_pricing.cta_title': 'Prêt à développer votre activité ?',
    'broker_pricing.cta_subtitle': 'Rejoignez des milliers de courtiers qui utilisent notre plateforme pour publier plus d\'annonces et conclure plus d\'affaires.',
    'broker_pricing.cta_button': 'Commencer avec Pro',
    'broker_pricing.sign_in_required': 'Veuillez vous connecter pour mettre à niveau votre plan',
    'broker_pricing.already_on_plan': 'Vous êtes déjà sur ce plan',
    'broker_pricing.checkout_error': 'Échec du démarrage du paiement',
    'broker_pricing.portal_error': 'Échec de l\'ouverture de la gestion d\'abonnement',
    'broker_pricing.tier.normal': 'Normal',
    'broker_pricing.tier.normal_desc': 'Parfait pour les courtiers individuels débutants',
    'broker_pricing.tier.normal_cta': 'Plan actuel',
    'broker_pricing.tier.pro': 'Pro',
    'broker_pricing.tier.pro_desc': 'Pour les courtiers en croissance avec plus d\'annonces',
    'broker_pricing.tier.pro_cta': 'Passer à Pro',
    'broker_pricing.tier.enterprise': 'Enterprise',
    'broker_pricing.tier.enterprise_desc': 'Pour les courtiers à haut volume avec des besoins illimités',
    'broker_pricing.tier.enterprise_cta': 'Choisir Enterprise',
    'broker_pricing.feature.listings_5': '5 annonces par mois',
    'broker_pricing.feature.listings_10': '10 annonces par mois',
    'broker_pricing.feature.listings_15': '15 annonces par mois',
    'broker_pricing.feature.basic_features': 'Fonctionnalités immobilières de base',
    'broker_pricing.feature.advanced_features': 'Fonctionnalités immobilières avancées',
    'broker_pricing.feature.premium_features': 'Fonctionnalités immobilières premium',
    'broker_pricing.feature.client_messaging': 'Messagerie client',
    'broker_pricing.feature.viewing_scheduling': 'Planification des visites',
    'broker_pricing.feature.video_tours_paid': 'Visites vidéo (3$ par annonce)',
    'broker_pricing.feature.video_tours_free': 'Visites vidéo gratuites incluses',
    'broker_pricing.feature.analytics': 'Tableau de bord analytique',
    'broker_pricing.feature.advanced_analytics': 'Analyses avancées',
    'broker_pricing.feature.priority_support': 'Support prioritaire',
    'broker_pricing.feature.support_24_7': 'Support prioritaire 24/7',
    'broker_pricing.feature.featured_listings': 'Annonces en vedette',
    'broker_pricing.feature.extra_listings': 'Option annonces supplémentaires',
    'broker_pricing.feature.extra_10': '+10$ par annonce supplémentaire',
    'broker_pricing.feature.messaging': 'Messagerie client',
    'broker_pricing.feature.scheduling': 'Planification des visites',
    'broker_pricing.feature.video_paid': 'Visites vidéo (3$ par annonce)',
    'broker_pricing.feature.video_free': 'Visites vidéo gratuites incluses',
    'broker_pricing.feature.analytics_advanced': 'Analyses avancées',
    'broker_pricing.feature.support': 'Support prioritaire',
    'broker_pricing.feature.support_247': 'Support prioritaire 24/7',
    'broker_pricing.feature.featured': 'Annonces en vedette',
    'broker_pricing.feature.extra_price': '+10$ par annonce supplémentaire',
    'broker_pricing.table.listings_per_month': 'Annonces par mois',
    'broker_pricing.table.video_tours': 'Visites vidéo',
    'broker_pricing.table.extra_listings': 'Annonces supplémentaires',
    'broker_pricing.table.listing_duration': 'Durée de l\'annonce',
    'broker_pricing.table.client_messaging': 'Messagerie client',
    'broker_pricing.table.analytics_dashboard': 'Tableau de bord analytique',
    'broker_pricing.table.priority_support': 'Support prioritaire',
    'broker_pricing.table.featured_listings': 'Annonces en vedette',
    'broker_pricing.table.15_extras': '15 + supplémentaires',
    'broker_pricing.table.3_per_listing': '3$ par annonce',
    'broker_pricing.table.10_each': '10$ chacune',
    'broker_pricing.table.30_days': '30 jours',
    'broker_pricing.comparison.listings': 'Annonces par mois',
    'broker_pricing.comparison.video': 'Visites vidéo',
    'broker_pricing.comparison.extra': 'Annonces supplémentaires',
    'broker_pricing.comparison.duration': 'Durée de l\'annonce',
    'broker_pricing.comparison.featured': 'Annonces en vedette',
    'broker_pricing.comparison.30_days': '30 jours',
    'broker_pricing.comparison.per_listing': '3$ par annonce',
    'broker_pricing.comparison.free': 'Gratuit',
    'broker_pricing.comparison.each': '10$ chacune',
    'broker_pricing.comparison.15_plus': '15 + supplémentaires',
    'broker_pricing.faq.autorenew_q': 'Mon abonnement se renouvelle-t-il automatiquement ?',
    'broker_pricing.faq.autorenew_a': 'Oui ! Votre abonnement se renouvelle automatiquement chaque mois pour que vous ne perdiez jamais l\'accès à vos annonces. Vous serez facturé le même jour chaque mois. Vous pouvez annuler à tout moment depuis le portail de gestion.',
    'broker_pricing.faq.upgrade_q': 'Puis-je passer à un plan supérieur ou inférieur à tout moment ?',
    'broker_pricing.faq.upgrade_a': 'Absolument ! Les mises à niveau prennent effet immédiatement avec une facturation au prorata. Les rétrogradations s\'appliquent à la fin de votre période de facturation actuelle. Cliquez sur "Gérer l\'abonnement" pour effectuer des modifications.',
    'broker_pricing.faq.expire_q': 'Que se passe-t-il lorsque mes annonces expirent ?',
    'broker_pricing.faq.expire_a': 'Les annonces expirent après 30 jours. Vous recevrez une notification par e-mail 3 jours avant l\'expiration. Vous pouvez les renouveler en utilisant vos places mensuelles gratuites ou payer 10$ par annonce supplémentaire (Enterprise uniquement).',
    'broker_pricing.faq.extra_q': 'Que sont les "annonces supplémentaires" ?',
    'broker_pricing.faq.extra_a': 'Les membres Enterprise peuvent acheter des annonces supplémentaires à 10$ chacune au-delà de leurs 15 annonces mensuelles gratuites. Idéal pour les mois à fort volume.',
    'broker_pricing.faq.cancel_q': 'Comment annuler mon abonnement ?',
    'broker_pricing.faq.cancel_a': 'Cliquez sur "Gérer l\'abonnement" pour accéder à votre portail de facturation où vous pouvez annuler, rétrograder ou mettre à jour votre méthode de paiement. Les annulations prennent effet à la fin de votre période de facturation.',
    'messaging.conversation_pinned': 'Conversation épinglée',
    'messaging.conversation_unpinned': 'Conversation désépinglée',
    'messaging.pin_chat': 'Épingler la conversation',
    'messaging.unpin_chat': 'Désépingler la conversation',
    'messaging.pin_failed': 'Échec de l\'épinglage',
    'messaging.unpin_failed': 'Échec du désépinglage',
    'messaging.archive': 'Archiver',
    'messaging.unarchive': 'Restaurer',
    'messaging.show_archived': 'Archivées',
    'messaging.show_active': 'Afficher les actives',
    'messaging.conversation_archived': 'Conversation archivée',
    'messaging.conversation_unarchived': 'Conversation restaurée',
    'messaging.archive_failed': 'Échec de l\'archivage',
    'messaging.unarchive_failed': 'Échec de la restauration',
    
    // Hero/News Section
    'hero.find_dream_home': 'Trouvez votre maison de rêve en Irak',
    'hero.explore_description': 'Explorez des milliers de maisons à vendre et les prix finaux de tout l\'Irak. Obtenez des évaluations, lisez les actualités immobilières et contactez des agents.',
    'hero.badge': 'La première plateforme immobilière d\'Irak',
  },
  es: {
    // Header
    'nav.search': 'Buscar propiedades',
    'nav.search_agents': 'Buscar agentes',
    'nav.sell': 'Vender propiedad',
    'nav.news': 'Noticias',
    'nav.build': 'Construir casa',
    'nav.agencies': 'Agencias',
    'nav.buy': 'Comprar',
    'nav.valuation': 'Valoración',
    'nav.more': 'Más',
    'nav.saved': 'Guardado',
    'nav.admin': 'Admin',
    'nav.agency': 'Agencia',
    'nav.agency_dashboard': 'Panel de agencia',
    'nav.location_manager': 'Gestor de ubicaciones',
    'nav.map_configuration': 'Configuración de mapa',
    'nav.hello': 'Hola',
    'nav.menu': 'Menú',
    'nav.manage_news': 'Gestionar noticias',
    'nav.all_properties': 'Todas las propiedades',
    'nav.new_production': 'Nuevas construcciones',
    
    // Cookie Policy
    'cookie.title': 'Política de cookies',
    'cookie.last_updated': 'Última actualización:',
    'cookie.section1.title': 'Sección 1 - Disposiciones generales sobre la implementación de tecnologías de seguimiento',
    'cookie.section1.p1': 'Ploqs AB, en adelante denominado el "Proveedor de servicios", junto con los socios y subcontratistas contratados por el Proveedor de servicios, implementa y utiliza diversas formas de mecanismos de seguimiento digital, incluyendo, entre otros, cookies, web beacons, etiquetas de píxeles y otras soluciones tecnológicas similares (denominadas colectivamente "Tecnologías de seguimiento") en las plataformas digitales, sitios web y aplicaciones móviles del Proveedor de servicios (denominados colectivamente "Canales digitales del Proveedor de servicios"). Esta política para el manejo de Tecnologías de seguimiento especifica los propósitos para los cuales y los métodos por los cuales el Proveedor de servicios aplica dichas tecnologías.',
    'cookie.section1.p2': 'Las Tecnologías de seguimiento consisten principalmente en archivos de datos textuales más pequeños que se depositan en el equipo terminal del usuario o en el programa del navegador web con el propósito de agregar información sobre la interacción del usuario con las plataformas digitales y aplicaciones móviles y la navegación en ellas.',
    'cookie.section1.h1': 'Procesamiento de datos personales y consideraciones de protección de datos',
    'cookie.section1.p3': 'La información recopilada mediante la aplicación de Tecnologías de seguimiento constituye en la mayoría de los casos datos que, directa o indirectamente y en combinación con otros datos disponibles, pueden potencialmente utilizarse para identificar individuos, incluyendo, entre otros, direcciones IP, identificadores únicos de cookies, identificadores de publicidad (IDFA), identificadores de dispositivos y diversos metadatos que permiten la diferenciación de equipos terminales específicos.',
    'cookie.section1.h2': 'Opciones del usuario',
    'cookie.section1.p4': 'El usuario tiene derecho a revisar los consentimientos previamente otorgados en cualquier momento navegando a las opciones de gestión de consentimiento proporcionadas a través de la interfaz digital del Proveedor de servicios.',
    'cookie.section1.h3': 'Persistencia y períodos de almacenamiento de las Tecnologías de seguimiento',
    'cookie.section1.p5': 'Las Tecnologías de seguimiento exhiben persistencia variable dependiendo del propósito previsto y la funcionalidad de cada tecnología. Las Tecnologías de seguimiento basadas en sesión se inician cuando el usuario se conecta a la plataforma digital y se terminan a más tardar al cerrar la instancia del navegador del usuario. Las Tecnologías de seguimiento persistentes se retienen en el navegador del usuario durante un período de tiempo predeterminado que excede las sesiones individuales.',
    'cookie.section2.title': 'Sección 2 - Tecnologías de seguimiento obligatorias para la prestación de servicios',
    'cookie.section2.p1': 'Las Tecnologías de seguimiento obligatorias se refieren a aquellas implementaciones tecnológicas que son requeridas para que el Proveedor de servicios proporcione funcionalidad básica y esencial al usuario, por ejemplo, con el propósito de mantener información sobre los consentimientos previamente otorgados por el usuario con respecto a las Tecnologías de seguimiento, para permitir la diferenciación entre sesiones de usuario distintas mediante identificadores anonimizados, para facilitar la funcionalidad cartográfica, para permitir la reproducción de material audiovisual en anuncios de propiedades cargados por agentes inmobiliarios, para preservar las preferencias de búsqueda guardadas del usuario, para mantener la seguridad de los servicios, prevenir el uso indebido de los servicios y para asegurar un rendimiento de servicio continuo y óptimo.',
    'cookie.section2.legal': 'Base legal para el procesamiento:',
    'cookie.section2.legal_value': 'Interés legítimo de acuerdo con el Artículo 6.1(f) del RGPD',
    'cookie.section2.storage': 'Período de almacenamiento:',
    'cookie.section2.storage_value': 'Hasta veinticuatro (24) meses para Tecnologías de seguimiento persistentes; las Tecnologías de seguimiento basadas en sesión se terminan inmediatamente al cerrar la instancia del navegador.',
    'cookie.section3.title': 'Sección 3 - Tecnologías de seguimiento para propósitos analíticos',
    'cookie.section3.p1': 'El Proveedor de servicios atribuye una importancia significativa a la capacidad de cuantificar la frecuencia de visitas, registrar datos de interacción en la plataforma digital y en aplicaciones móviles, y analizar de otro modo cómo los usuarios utilizan los servicios digitales del Proveedor de servicios. Siempre que el usuario haya dado el consentimiento requerido, el Proveedor de servicios implementa Tecnologías de seguimiento, excluyendo datos personales directos, con el propósito de analizar y desarrollar aún más los servicios digitales del Proveedor de servicios y ofrecer continuamente al usuario una experiencia de usuario optimizada, mantener el rendimiento del servicio y producir análisis de grupos objetivo y compilaciones estadísticas sobre el uso de la plataforma.',
    'cookie.section3.legal_value': 'Consentimiento de acuerdo con el Artículo 6.1(a) del RGPD',
    'cookie.section3.storage_value': 'Hasta veintiséis (26) meses',
    'cookie.section4.title': 'Sección 4 - Tecnologías de seguimiento para propósitos de personalización',
    'cookie.section4.p1': 'Las Tecnologías de seguimiento implementadas para propósitos de personalización tienen como objetivo adaptar la experiencia del usuario en las plataformas digitales del Proveedor de servicios proporcionando contenido con mayor relevancia personal para el usuario individual. Esta adaptación se basa, por ejemplo, en el comportamiento de búsqueda y los patrones de interacción del usuario. En caso de que el usuario tenga una cuenta de usuario registrada con el Proveedor de servicios, estos datos de comportamiento se combinan con información que el usuario proporciona voluntariamente en el marco del servicio, por ejemplo, información sobre las condiciones de vivienda del usuario, mediante lo cual se realizan adaptaciones de contenido basadas en el perfil agregado del usuario.',
    'cookie.section5.title': 'Sección 5 - Tecnologías de seguimiento para propósitos de marketing',
    'cookie.section5.p1': 'El Proveedor de servicios implementa Tecnologías de seguimiento para propósitos de marketing, siempre que el usuario haya dado el consentimiento requerido, con el propósito de permitir la exposición de contenido publicitario relevante y anuncios de propiedades con mayor relevancia para el usuario basándose en un perfil publicitario construido.',
    'cookie.section5.p2': 'Mediante la implementación de Tecnologías de seguimiento, el Proveedor de servicios también asegura que las exposiciones publicitarias se limiten a un número predefinido de ocasiones por usuario, y que la efectividad publicitaria sea medida y analizada. El contenido publicitario del Proveedor de servicios también puede exponerse en plataformas digitales externas.',
    'cookie.section5.p3': 'Debe notarse que en caso de que el usuario elija desactivar las Tecnologías de seguimiento para propósitos de marketing, el contenido publicitario seguirá exponiéndose al usuario en las plataformas digitales del Proveedor de servicios, sin embargo, sin personalización.',
    'cookie.section5.storage_value': 'Hasta veinticuatro (24) meses',
    'cookie.section6.title': 'Sección 6 - Transferencias internacionales de datos a terceros países',
    'cookie.section6.p1': 'En la herramienta de gestión de consentimiento del Proveedor de servicios, se proporciona información sobre qué Tecnologías de seguimiento y socios procesan los datos personales del usuario dentro de, o transfieren los datos personales del usuario a, jurisdicciones fuera de la Unión Europea y el Espacio Económico Europeo (los llamados "terceros países").',
    'cookie.section7.title': 'Sección 7 - Instrucciones para la eliminación de Tecnologías de seguimiento',
    'cookie.section7.p1': 'El usuario tiene la opción de eliminar Tecnologías de seguimiento de su equipo terminal o dispositivo móvil y configurar ajustes para el almacenamiento de datos locales a través de la interfaz de configuración del navegador. El usuario puede elegir desactivar las Tecnologías de seguimiento o recibir notificaciones cada vez que se inicie una nueva Tecnología de seguimiento en el equipo terminal del usuario.',
    'cookie.section7.p2': 'Para instrucciones detalladas sobre el manejo y la eliminación de Tecnologías de seguimiento, se hace referencia a la opción de ayuda en el navegador del usuario o a los hipervínculos a continuación para cada aplicación de navegador:',
    
    // Auth
    'auth.loading': 'Cargando...',
    'auth.login': 'Iniciar sesión',
    'auth.create_account': 'Crear cuenta',
    'auth.login_subtitle': 'Inicie sesión eligiendo una de las opciones a continuación.',
    'auth.signup_subtitle': 'Cree una nueva cuenta para comenzar.',
    'auth.continue_apple': 'Continuar con Apple',
    'auth.continue_google': 'Continuar con Google',
    'auth.or': 'o',
    'auth.email': 'Correo electrónico',
    'auth.email_placeholder': 'Ingrese correo electrónico',
    'auth.password': 'Contraseña',
    'auth.password_placeholder': 'Ingrese contraseña',
    'auth.account_type': 'Tipo de cuenta',
    'auth.user_role': 'Usuario - Quiero comprar/alquilar una propiedad',
    'auth.agent_role': 'Agente - Quiero vender/alquilar propiedades',
    'auth.continue': 'Continuar',
    'auth.no_account': '¿No tiene cuenta? Crear cuenta',
    'auth.have_account': '¿Ya tiene cuenta? Iniciar sesión',
    'auth.login_failed': 'Error de inicio de sesión',
    'auth.invalid_credentials': 'Correo electrónico o contraseña inválidos',
    'auth.welcome_back': '¡Bienvenido de nuevo!',
    'auth.logged_in': 'Ha iniciado sesión.',
    'auth.signup_failed': 'Error de registro',
    'auth.account_created': '¡Cuenta creada!',
    'auth.check_email': 'Revise su correo electrónico para confirmar su cuenta.',
    'auth.error': 'Ocurrió un error',
    'auth.try_again': 'Por favor, inténtelo de nuevo más tarde.',
    'auth.broker_interest_title': '¿Eres corredor?',
    'auth.broker_interest_description': 'Si está interesado en publicar propiedades en Aqarnet, envíenos una declaración de interés y nos pondremos en contacto.',
    'auth.broker_contact_us': 'Contáctenos',
    'auth.broker_email_subject': 'Declaración de interés de corredor',
    'auth.broker_email_body': 'Hola,\n\nEstoy interesado en registrarme como corredor en Aqarnet.\n\nNombre:\nNombre de la agencia:\nTeléfono:\nCiudad:\n\n¡Gracias!',
    'auth.first_name': 'Nombre',
    'auth.last_name': 'Apellido',
    'auth.first_name_placeholder': 'Ingrese su nombre',
    'auth.last_name_placeholder': 'Ingrese su apellido',
    'auth.display_name': 'Nombre para mostrar',
    'auth.display_name_placeholder': 'Elija un nombre para mostrar',
    'auth.display_name_hint': 'Este es el único nombre que otros verán',
    'auth.date_of_birth': 'Fecha de nacimiento',
    'auth.fill_all_fields': 'Por favor complete todos los campos requeridos',
    'auth.must_be_18': 'Debe tener al menos 18 años para registrarse',
    'auth.must_be_18_hint': 'Debe tener al menos 18 años',
    'verification.verify_identity': 'Verificar identidad',
    'verification.verified': 'Verificado',
    'verification.title': 'Verificación de identidad',
    'verification.description': 'Suba un documento oficial para verificar su identidad y obtener una insignia verificada.',
    'verification.document_type': 'Tipo de documento',
    'verification.doc_id_card': 'Documento de identidad',
    'verification.doc_passport': 'Pasaporte',
    'verification.doc_drivers_license': 'Licencia de conducir',
    'verification.upload_document': 'Subir documento',
    'verification.click_to_upload': 'Haga clic para subir o arrastre y suelte',
    'verification.file_requirements': 'JPG, PNG, WEBP o PDF (máx. 10MB)',
    'verification.submit': 'Enviar para verificación',
    'verification.uploading': 'Subiendo...',
    'verification.submitted': 'Verificación enviada',
    'verification.submitted_desc': 'Su solicitud de verificación ha sido enviada. La revisaremos pronto.',
    'verification.pending_message': 'Su verificación está siendo revisada. Esto suele tardar 1-2 días hábiles.',
    'verification.rejected_message': 'Su verificación anterior fue rechazada. Por favor envíe un nuevo documento.',
    'verification.status_approved': 'Aprobado',
    'verification.status_pending': 'Pendiente',
    'verification.status_rejected': 'Rechazado',
    'verification.invalid_file_type': 'Tipo de archivo inválido',
    'verification.allowed_types': 'Por favor suba un archivo JPG, PNG, WEBP o PDF',
    'verification.file_too_large': 'Archivo demasiado grande',
    'verification.max_size': 'El tamaño máximo del archivo es 10MB',
    'verification.error': 'Error de verificación',
    'verification.admin_title': 'Verificaciones de identidad',
    'verification.admin_description': 'Revisar y aprobar solicitudes de verificación de identidad',
    'verification.no_requests': 'No hay solicitudes de verificación',
    'verification.view': 'Revisar',
    'verification.review_title': 'Revisar verificación',
    'verification.review_notes': 'Notas de revisión',
    'verification.review_notes_placeholder': 'Agregue notas sobre su decisión (opcional)',
    'verification.approve': 'Aprobar',
    'verification.reject': 'Rechazar',
    'verification.approved': 'Verificación aprobada',
    'verification.rejected': 'Verificación rechazada',
    'verification.review_complete': 'La verificación ha sido procesada.',
    
    // Favorites Page
    'favorites.title': 'Mis favoritos',
    'favorites.saved_properties': 'Propiedades guardadas',
    'favorites.login_required': 'Inicie sesión para ver sus favoritos',
    'favorites.login_description': 'Guarde las propiedades que le gustan para encontrarlas más fácilmente.',
    'favorites.loading': 'Cargando favoritos...',
    'favorites.no_favorites': 'Aún no tiene favoritos',
    'favorites.start_saving': 'Comience a guardar propiedades haciendo clic en el corazón en los anuncios.',
    'favorites.search_properties': 'Buscar propiedades',
    
    // Profile Page
    'profile.title': 'Mi perfil',
    'profile.subtitle': 'Gestionar información personal y configuración',
    'profile.loading': 'Cargando perfil...',
    'profile.fetch_error': 'Error al obtener el perfil',
    'profile.fetch_error_description': 'No se pudo obtener la información del perfil.',
    'profile.unnamed_user': 'Usuario sin nombre',
    'profile.personal_info': 'Información personal',
    'profile.update_info': 'Actualizar su información personal',
    'profile.email': 'Correo electrónico',
    'profile.email_readonly': 'El correo electrónico no se puede cambiar',
    'profile.display_name': 'Nombre para mostrar',
    'profile.display_name_placeholder': 'Su nombre',
    'profile.phone': 'Número de teléfono',
    'profile.phone_placeholder': '612 345 678',
    'profile.save_changes': 'Guardar cambios',
    'profile.saving': 'Guardando...',
    'profile.reset': 'Restablecer',
    'profile.updated': 'Perfil actualizado',
    'profile.updated_description': 'Su perfil ha sido guardado.',
    'profile.update_error': 'Error de actualización',
    'profile.update_error_description': 'No se pudieron guardar los cambios.',
    'profile.active_listings': 'Anuncios activos',
    'profile.active_listings_description': 'Sus anuncios de propiedades publicados para vender',
    'profile.view_all': 'Ver todo',
    'profile.loading_listings': 'Cargando anuncios...',
    'profile.no_active_listings': 'Aún no tiene anuncios activos',
    'profile.more_active_listings': 'y {count} anuncios activos más',
    'profile.sold_properties': 'Propiedades vendidas',
    'profile.sold_properties_description': 'Sus propiedades vendidas',
    'profile.no_sold_properties': 'Aún no tiene propiedades vendidas',
    'profile.more_sold_properties': 'y {count} propiedades vendidas más',
    'profile.listing_fetch_error': 'Error al obtener los anuncios',
    'profile.listing_fetch_error_description': 'No se pudieron obtener sus anuncios.',
    'profile.account_settings': 'Configuración de la cuenta',
    'profile.account_settings_description': 'Gestionar cuenta y seguridad',
    'profile.change_password': 'Cambiar contraseña',
    'profile.change_password_description': 'Actualizar contraseña para un inicio de sesión más seguro',
    'profile.change': 'Cambiar',
    'profile.two_factor': 'Autenticación de dos factores',
    'profile.two_factor_description': 'Añadir seguridad adicional a su cuenta',
    'profile.enable': 'Activar',
    'profile.delete_account': 'Eliminar cuenta',
    'profile.delete_account_description': 'Eliminar permanentemente la cuenta y todos los datos',
    'profile.delete': 'Eliminar',
    'profile.logout': 'Cerrar sesión',
    
    // Not Found Page
    'notfound.title': '404',
    'notfound.message': '¡Lo sentimos! Página no encontrada',
    'notfound.back_home': 'Volver al inicio',
    
    // Property Detail Page
    'property.back_to_listings': 'Volver a los anuncios',
    'property.not_found': 'Propiedad no encontrada',
    'property.link_copied': 'Enlace copiado',
    'property.link_copied_description': 'Enlace de la propiedad copiado',
    'property.description': 'Descripción',
    'property.features': 'Características',
    'property.details': 'Detalles de la propiedad',
    'property.rooms_label': 'Habitaciones',
    'property.size_label': 'Tamaño',
    'property.plot_size_label': 'Tamaño del terreno',
    'property.monthly_fee_label': 'Cuota mensual',
    'property.contact_agent': 'Contactar agente',
    'property.send_message': 'Enviar mensaje',
    'property.schedule_viewing': 'Programar visita',
    'property.section.details': 'Detalles',
    'property.section.amenities': 'Comodidades',
    'property.section.overview': 'Descripción general',
    'property.section.payment_plan': 'Plan de pago',
    'property.section.floor_plans': 'Planos de planta',
    'property.section.view_map': 'Ver mapa',
    'property.copy_link': 'Copiar enlace',
    'property.share': 'Compartir',
    'property.share_text': 'Mira esta propiedad',
    'property.posted_on': 'Publicado el',
    'property.type': 'Tipo',
    'property.status': 'Estado',
    'property.available': 'Disponible',
    'property.no_amenities': 'No hay comodidades listadas para esta propiedad',
    'property.no_description': 'Sin descripción disponible',
    'property.location': 'Ubicación',
    'property.payment_plan_info': 'Contacte al agente para detalles del plan de pago',
    'property.contact_for_payment_plan': 'Contacto para plan de pago',
    'property.no_floor_plans': 'No hay planos de planta disponibles',
    'property.request_floor_plans': 'Solicitar planos de planta',
    'property.no_map_location': 'Ubicación no disponible en el mapa',
    
    // News Page
    'news.subtitle': 'Manténgase al día con las últimas noticias inmobiliarias, análisis del mercado y guías',
    'news.no_articles': 'Aún no hay artículos de noticias',
    'news.check_back': 'Vuelva pronto para las últimas noticias inmobiliarias y análisis del mercado.',
    'news.search_placeholder': 'Buscar artículos...',
    'news.showing_filtered': 'Mostrando {shown} de {total} artículos en esta página',
    'news.clear_filters': 'Limpiar filtros',
    'news.no_matching': 'No hay artículos coincidentes',
    'news.adjust_filters': 'Intente ajustar su búsqueda o filtros para encontrar lo que busca.',
    'news.publisher': 'Editor',
    'news.page_info': 'Página {current} de {total} • {count} artículos en total',
    'news.category.all': 'Todo',
    'news.category.market_news': 'Noticias del mercado',
    'news.category.property_guides': 'Guías inmobiliarias',
    'news.category.investment_tips': 'Consejos de inversión',
    'news.category.industry_updates': 'Actualizaciones del sector',
    'news.category.announcements': 'Anuncios',
    'news.category.expert_advice': 'Consejos de expertos',
    
    // Filters/Sorting
    'filters.any': 'Todos',
    'filters.newest_first': 'Más recientes primero',
    'filters.price_low_high': 'Precio: de menor a mayor',
    'filters.price_high_low': 'Precio: de mayor a menor',
    'filters.size': 'Tamaño',
    'filters.searching_for': 'Buscando',
    'filters.search_areas': 'Buscar áreas, ciudades...',
    
    // Common
    'common.cancel': 'Cancelar',
    'common.save': 'Guardar',
    'common.saving': 'Guardando...',
    'common.send': 'Enviar',
    'common.sending': 'Enviando...',
    'common.submit': 'Enviar',
    'common.close': 'Cerrar',
    'common.error': 'Error',
    'common.success': 'Éxito',
    'common.loading': 'Cargando...',
    'common.name': 'Nombre',
    'common.email': 'Correo electrónico',
    'common.phone': 'Teléfono',
    'common.message': 'Mensaje',
    'common.required': 'Obligatorio',
    'common.optional': 'Opcional',
    'common.delete': 'Eliminar',
    'common.free': 'Gratis',
    
    // Leaderboard
    'leaderboard': 'Clasificación',
    'leaderboardDescription': 'Descubre las mejores agencias y agentes clasificados por vistas y ventas',
    'topAgencies': 'Mejores Agencias',
    'topBrokers': 'Mejores Agentes',
    'topAgenciesDescription': 'Agencias clasificadas por vistas de propiedades y rendimiento de ventas',
    'topBrokersDescription': 'Agentes clasificados por vistas de propiedades y rendimiento de ventas',
    'allRegions': 'Todas las regiones',
    'noAgenciesFound': 'No se encontraron agencias',
    'noBrokersFound': 'No se encontraron agentes',
    'brokers': 'agentes',
    'sold': 'vendido',
    'active': 'activo',
    'views': 'vistas',
    
    // Dialogs - Contact Agent
    'dialog.contact_agent': 'Contactar agente',
    'dialog.contact_agent_btn': 'Contactar agente',
    'dialog.contact_message_placeholder': '¡Hola! Estoy interesado en la propiedad en {address} y me gustaría saber más. Por favor, contácteme cuando tenga tiempo.',
    'dialog.contact_sending': 'Enviando...',
    'dialog.contact_send': 'Enviar mensaje',
    'dialog.contact_note': 'Su consulta se enviará directamente al agente responsable que se pondrá en contacto con usted.',
    
    // Dialogs - Schedule Viewing
    'dialog.schedule_viewing': 'Programar visita',
    'dialog.schedule_viewing_btn': 'Programar visita',
    'dialog.preferred_time': 'Hora de visita preferida',
    'dialog.preferred_time_note': 'Seleccione fecha y hora para la visita. El agente confirmará la disponibilidad.',
    'dialog.special_requests': 'Solicitudes especiales',
    'dialog.special_requests_placeholder': 'Cualquier solicitud especial o preguntas...',
    'dialog.schedule_booking': 'Reservando visita...',
    'dialog.schedule_book': 'Reservar visita',
    'dialog.schedule_note': 'Su solicitud se enviará al agente que confirmará la hora por correo electrónico.',
    
    // Dialogs - Save Search
    'dialog.save_search': 'Guardar búsqueda',
    'dialog.save_search_btn': 'Guardar búsqueda',
    'dialog.search_name': 'Nombre de la búsqueda',
    'dialog.search_name_placeholder': 'p.ej., Apartamento en Madrid',
    'dialog.city_municipality': 'Ciudad/Municipio',
    'dialog.min_rooms': 'Mín. habitaciones',
    'dialog.min_size': 'Mín. tamaño (m²)',
    'dialog.property_type': 'Tipo de propiedad',
    'dialog.email_alerts': 'Notificaciones por correo cuando coincidan nuevas propiedades',
    'dialog.saving_search': 'Guardando...',
    
    // Dialogs - Mark as Sold
    'dialog.mark_sold': 'Marcar como vendida',
    'dialog.mark_sold_description': 'Introduzca el precio de venta final de esta propiedad. Esto moverá el anuncio a propiedades vendidas.',
    'dialog.final_sale_price': 'Precio de venta final (USD)',
    'dialog.listed_price': 'Precio de lista',
    'dialog.property_details': 'Detalles de la propiedad',
    'dialog.enter_valid_price': 'Por favor, introduzca un precio de venta válido',
    'dialog.enter_price_placeholder': 'Introduzca el precio de venta',
    'dialog.marked_sold_success': 'Propiedad marcada como vendida correctamente',
    'dialog.mark_sold_error': 'Error al marcar la propiedad como vendida',
    
    // Dialogs - Add Property
    'dialog.add_property': 'Añadir nueva propiedad',
    'dialog.hide_preview': 'Ocultar vista previa',
    'dialog.show_preview': 'Mostrar vista previa',
    'dialog.selected_images': 'Imágenes seleccionadas',
    'dialog.uploaded_images': 'Imágenes subidas',
    'dialog.click_primary': 'Haga clic en una imagen para establecerla como principal',
    'dialog.invalid_format': 'Formato de archivo no válido',
    'dialog.file_too_large': 'Archivo demasiado grande',
    'dialog.upload_failed': 'Error de carga',
    'dialog.images_required': 'Imágenes requeridas',
    'dialog.please_upload': 'Por favor, suba al menos una imagen de la propiedad',
    'dialog.form_validation_failed': 'Error de validación del formulario',
    
    // Properties for Sale Page
    'propertiesForSale.filters.searchWithin': 'Buscar en',
    'propertiesForSale.filters.km': 'km',
    'propertiesForSale.filters.propertyType': 'Tipo de propiedad',
    'propertiesForSale.filters.rooms': 'Habitaciones',
    'propertiesForSale.filters.bathrooms': 'Baños',
    'propertiesForSale.filters.min': 'Mín',
    'propertiesForSale.filters.max': 'Máx',
    'propertiesForSale.filters.size': 'Tamaño',
    'propertiesForSale.filters.price': 'Precio',
    'propertiesForSale.filters.searchResults': 'Resultados de búsqueda',
    'propertiesForSale.filters.searchPlaceholder': 'Buscar zonas, ciudades...',
    'propertiesForSale.filters.searchingFor': 'Buscando:',
    'propertiesForSale.filters.any': 'Cualquiera',
    'propertiesForSale.filters.keywords': 'Palabras clave',
    'propertiesForSale.filters.showResults': 'Mostrar resultados',
    'propertiesForSale.filters.clearFilters': 'Borrar',
    'propertiesForSale.filters.realEstateSituation': 'Situación inmobiliaria',
    'propertiesForSale.filters.situation.tapu': 'Tapu',
    'propertiesForSale.filters.situation.landContract': 'Contrato de terreno',
    'propertiesForSale.filters.situation.noDocuments': 'Sin documentos',
    'propertiesForSale.filters.situation.investment': 'Inversión',
    'propertiesForSale.filters.situation.musataha': 'Musataha',
    'propertiesForSale.filters.situation.card': 'Tarjeta',
    'propertiesForSale.filters.situation.agriculturalTapu': 'Tapu agrícola',
    'propertiesForSale.sort.newest': 'Más recientes',
    'propertiesForSale.sort.price_low': 'Precio: menor a mayor',
    'propertiesForSale.sort.price_high': 'Precio: mayor a menor',
    'propertiesForSale.sort.size': 'Tamaño',
    'propertiesForSale.noResults': 'No se encontraron propiedades',
    'propertiesForSale.tryAdjustingFilters': 'Intenta ajustar tus filtros o buscar en otra área',
    
    // Search Section
    'search.for_sale': 'En venta',
    'search.for_rent': 'En alquiler',
    'search.sold_prices': 'Precios vendidos',
    'search.search_agents': 'Buscar agentes',
    'search.area_label': 'Área',
    'search.area_placeholder': 'Escribir área o dirección',
    'search.area_examples': 'Ejemplo: Bagdad, Basora, Erbil, Najaf, Karbala',
    'search.expand_area': 'Ampliar área con',
    'search.property_type': 'Tipo de propiedad',
    'search.show_filters': 'Mostrar filtros',
    'search.find_properties': 'Encontrar propiedades en venta',
    'search.min_rooms': 'Min. habitaciones',
    'search.min_area': 'Min. superficie',
    'search.max_price': 'Precio máx.',
    'search.all': 'Todos',
    'search.no_max': 'Sin máximo',
    'search.new_construction': 'Obra nueva',
    'search.show_new_construction': 'Mostrar obra nueva',
    'search.only_new_construction': 'Solo obra nueva',
    'search.exclude_new_construction': 'Excluir obra nueva',
    'search.keywords': 'Palabras clave',
    'search.keywords_placeholder': 'Piscina, chimenea, etc.',
    'search.more_filters_note': 'Más filtros disponibles en los resultados de búsqueda.',
    'search.recent_searches': 'Búsquedas recientes',
    'search.hide_history': 'Ocultar historial',
    'search.clear_history': 'Borrar historial',
    
    'property.all_types': 'Todos los tipos',
    'property.villas': 'Villas',
    'property.townhouses': 'Casas adosadas',
    'property.apartments': 'Pisos',
    'property.business': 'Propiedades comerciales',
    'property.land': 'Terrenos',
    'property.farms': 'Fincas/Bosques',
    'property.other': 'Otros',

    // Property Types (Select)
    'propertyTypes.allTypes': 'Todos los tipos',
    'propertyTypes.villa': 'Villas',
    'propertyTypes.apartment': 'Pisos',
    'propertyTypes.townhouse': 'Casas adosadas',
    'propertyTypes.business': 'Propiedades comerciales',
    'propertyTypes.plot': 'Terrenos',
    'propertyTypes.farm': 'Fincas/Bosques',
    'propertyTypes.other': 'Otros',
    
    // Listings
    'listings.latest_for_sale': 'Últimas en venta',
    'listings.properties_found': 'propiedades encontradas',
    'listings.add_property': 'Agregar propiedad',
    'listings.all_news': 'Todas las noticias',
    'listings.edit': 'Editar',
    'listings.edit_property': 'Editar propiedad',
    'listings.monthly_fee': 'Cuota mensual',
    'listings.open_floor_plan': 'Planta abierta',
    'listings.large_balcony': 'Balcón grande',
    'listings.family_friendly': 'Apto para familias',
    'listings.photos': 'fotos',
    'listings.per_month': 'mes',
    'loading.properties': 'Cargando propiedades...',
    'empty.no_properties': 'No hay propiedades disponibles',
    'empty.check_back': 'Vuelve más tarde para nuevos listados.',
    
    // Property card
    'property.sold': 'VENDIDO',
    'property.photos': 'fotos',
    'property.rooms_short': 'hab.',
    'property.plot': 'parcela',
    'property.per_month': '/mes',
    'property.delete_title': '¿Eliminar propiedad?',
    'property.delete_confirm': '¿Está seguro de que desea eliminar {address}? Esta acción no se puede deshacer.',
    'property.deleted': 'Propiedad eliminada',
    'property.deleted_desc': 'La propiedad ha sido eliminada',
    'property.delete_error': 'No se pudo eliminar la propiedad',
    
    // Saved Searches
    'loading.saved_searches': 'Cargando búsquedas guardadas...',
    'saved_searches.title': 'Búsquedas guardadas',
    'saved_searches.empty': 'Aún no hay búsquedas guardadas',
    'saved_searches.description': 'Guarde una búsqueda para recibir notificaciones cuando se añadan propiedades coincidentes',
    'saved_searches.created': 'Creada',
    'saved_searches.alerts_on': 'Notificaciones activadas',
    'saved_searches.alerts_off': 'Notificaciones desactivadas',
    'saved_searches.email_enabled': 'Notificaciones por email activadas',
    'saved_searches.email_disabled': 'Notificaciones por email desactivadas',
    'saved_searches.settings_updated': 'Configuración actualizada',
    'saved_searches.removed': 'Búsqueda eliminada',
    'saved_searches.removed_desc': 'La búsqueda guardada ha sido eliminada',
    'saved_searches.error': 'Error',
    'saved_searches.update_error': 'No se pudo actualizar la configuración',
    'saved_searches.delete_error': 'No se pudo eliminar la búsqueda',
    'saved_searches.all_properties': 'Todas las propiedades',
    
    // Rental properties
    'rental.for_sale': 'En venta',
    'rental.for_rent': 'En alquiler',
    'rental.listing_type': 'Tipo de anuncio',
    'rental.rental_details': 'Detalles del alquiler',
    'rental.rental_period': 'Período de alquiler',
    'rental.rental_period_hint': 'Con qué frecuencia se paga el alquiler',
    'rental.daily': 'Diario',
    'rental.weekly': 'Semanal',
    'rental.monthly': 'Mensual',
    'rental.yearly': 'Anual',
    'rental.per_daily': '/día',
    'rental.per_weekly': '/semana',
    'rental.per_monthly': '/mes',
    'rental.per_yearly': '/año',
    'rental.deposit_amount': 'Monto del depósito (USD)',
    'rental.minimum_lease': 'Arrendamiento mínimo (meses)',
    'rental.months': 'meses',
    'rental.available_from': 'Disponible desde',
    'rental.furnished': 'Amueblado',
    'rental.utilities_included': 'Servicios incluidos',
    'rental.pets_allowed': 'Mascotas permitidas',
    
    // Admin Pages
    'admin.loading': 'Cargando...',
    'admin.access_denied': 'Acceso denegado. Se requieren privilegios de administrador.',
    
    // Admin Notification Settings
    'admin.notifications.title': 'Configuración de notificaciones',
    'admin.notifications.description': 'Administre la configuración de notificaciones para todos los tipos de usuarios. Active, desactive u oculte según sea necesario.',
    'admin.notifications.fetch_error': 'Error al obtener configuración de notificaciones',
    'admin.notifications.update_success': 'Configuración de notificación actualizada',
    'admin.notifications.update_error': 'Error al actualizar configuración de notificación',
    'admin.notifications.role_description': 'Administrar notificaciones para usuarios {role}',
    'admin.notifications.hidden': 'Oculto',
    'admin.notifications.disabled': 'Desactivado',
    'admin.notifications.active': 'Activo',
    'admin.notifications.hide': 'Ocultar',
    'admin.notifications.roles.user': 'Usuarios',
    'admin.notifications.roles.maklare': 'Corredores',
    'admin.notifications.roles.agency': 'Agencias',
    'admin.notifications.roles.admin': 'Administradores',
    'admin.notifications.roles.publisher': 'Editores',
    'admin.notifications.types.new_viewing_request': 'Nueva solicitud de visita',
    'admin.notifications.types.new_client_message': 'Nuevo mensaje de cliente',
    'admin.notifications.types.property_alerts': 'Alertas de propiedades',
    'admin.notifications.types.weekly_summary': 'Resumen semanal',
    'admin.notifications.types.broker_property_added': 'Corredor agregó propiedad',
    'admin.notifications.types.broker_viewing_request': 'Solicitud de visita del corredor',
    'admin.notifications.types.broker_client_message': 'Mensaje de cliente del corredor',
    'admin.notifications.types.broker_joined': 'Corredor se unió',
    'admin.notifications.types.weekly_agency_summary': 'Resumen semanal de agencia',
    'admin.notifications.types.saved_search_alerts': 'Alertas de búsqueda guardada',
    'admin.notifications.types.favorite_property_updates': 'Actualizaciones de propiedades favoritas',
    'admin.notifications.types.newsletter': 'Boletín',
    'admin.notifications.types.system_alerts': 'Alertas del sistema',
    'admin.notifications.types.new_user_signup': 'Registro de nuevo usuario',
    'admin.notifications.types.article_published': 'Artículo publicado',

    // Admin Settings
    'admin.settings': 'Configuración de administrador',
    'admin.messaging_settings': 'Configuración de mensajería',
    'admin.messaging_settings_description': 'Controlar funciones de mensajería y llamadas para todos los usuarios',
    'admin.messaging': 'Mensajería',
    'admin.messaging_description': 'Permitir a los usuarios enviar mensajes',
    'admin.voice_calls': 'Llamadas de voz',
    'admin.voice_calls_description': 'Permitir a los usuarios hacer llamadas de voz',
    'admin.video_calls': 'Videollamadas',
    'admin.video_calls_description': 'Permitir a los usuarios hacer videollamadas',
    'admin.screen_sharing': 'Compartir pantalla',
    'admin.screen_sharing_description': 'Permitir a los usuarios compartir su pantalla durante las llamadas',

    'location_mgr.title': 'Gestor de ubicaciones',
    'location_mgr.current_locations': 'Ubicaciones actuales',
    'location_mgr.locations_in_db': '{count} ubicaciones en la base de datos',
    'location_mgr.refresh': 'Actualizar',
    'location_mgr.populate_title': 'Rellenar ubicaciones',
    'location_mgr.populate_desc': 'Obtener todas las ciudades y áreas iraquíes desde Google Places API.',
    'location_mgr.populating': 'Rellenando...',
    'location_mgr.populate_btn': 'Rellenar desde Google Places',
    'location_mgr.success': 'Éxito',
    'location_mgr.success_msg': 'Ubicaciones rellenadas correctamente',
    'location_mgr.error': 'Error',
    'location_mgr.error_msg': 'Error al rellenar ubicaciones.',
    'map_config.title': 'Gestor de configuración de mapa',
    'map_config.subtitle': 'Gestione sus configuraciones de mapa permanentes',
    'map_config.loading': 'Cargando configuraciones...',
    'map_config.create': 'Crear configuración',
    'map_config.create_title': 'Crear configuración de mapa',
    'map_config.create_desc': 'Crear una nueva configuración de mapa permanente.',
    'map_config.edit_title': 'Editar configuración',
    'map_config.edit_desc': 'Actualizar su configuración.',
    'map_config.name': 'Nombre de configuración',
    'map_config.name_placeholder': 'ej: Mapa inmobiliario de Irak',
    'map_config.description': 'Descripción (opcional)',
    'map_config.description_placeholder': 'Breve descripción',
    'map_config.map_data': 'Configuración de mapa (JSON)',
    'map_config.map_data_placeholder': 'Configuración en formato JSON',
    'map_config.cancel': 'Cancelar',
    'map_config.update': 'Actualizar',
    'map_config.no_configs': 'Sin configuraciones',
    'map_config.no_configs_desc': 'Cree su primera configuración.',
    'map_config.create_first': 'Crear primera configuración',
    'map_config.active': 'Activo',
    'map_config.activate': 'Activar',
    'map_config.edit': 'Editar',
    'map_config.delete': 'Eliminar',
    'map_config.delete_confirm': '¿Está seguro?',
    'map_config.delete_desc': 'Esto eliminará permanentemente la configuración "{name}".',
    'map_config.created': 'Creado',
    'map_config.last_updated': 'Última actualización',
    'map_config.map_center': 'Centro del mapa',
    'map_config.zoom_level': 'Nivel de zoom',
    'map_config.cities': 'Ciudades',
    'map_config.cities_configured': '{count} ciudades configuradas',
    'map_config.invalid_json': 'Formato JSON inválido',
    
    // Forms
    'form.price': 'Precio',
    'form.priceUSD': 'Precio (USD)',
    'form.priceIQD': 'Precio (IQD)',
    'form.priceConversionNote': 'Ingrese el precio en cualquiera de las monedas - la otra se calculará automáticamente (Tasa: 1 USD ≈ 1.310 IQD)',
    'form.price_usd': 'Precio (USD)',
    'form.price_placeholder': 'Ingrese el precio en dólares estadounidenses',
    'form.price_description': 'Ingrese el precio en dólares estadounidenses. Se mostrará tanto en USD como en dinar iraquí.',
    'form.address': 'Dirección',
    'form.city': 'Ciudad',
    'form.rooms': 'Habitaciones',
    'form.bathrooms': 'Baños',
    'form.size': 'Tamaño (m²)',
    'form.building_size': 'Tamaño del edificio (m²)',
    'form.building_size_placeholder': 'Área del edificio en m²',
    'form.building_size_description': 'Área habitable',
    'form.plot_size': 'Tamaño del terreno (m²)',
    'form.plot_size_placeholder': 'Área del terreno en m²',
    'form.plot_size_description': 'Área total del terreno',
    'form.type': 'Tipo',
    'form.propertyType': 'Tipo de propiedad',
    'form.monthly_fee': 'Cuota mensual (SEK)',
    'form.monthly_fee_usd': 'Cuota mensual (USD)',
    'form.monthly_fee_placeholder': 'Ingrese la cuota mensual en USD (opcional)',
    'form.monthly_fee_description': 'Cuota mensual opcional en dólares estadounidenses',
    'form.description': 'Descripción',
    'form.description_placeholder': 'Describa la propiedad...',
    'form.payment_plan': 'Plan de pago',
    'form.payment_plan_placeholder': 'ej: 30% enganche, 70% en entrega, o mensualidades...',
    'form.payment_plan_hint': 'Agregue cuotas de pago para mostrar sus opciones de pago a los compradores',
    'payment_plan.total': 'Total',
    'payment_plan.amount': 'Monto',
    'payment_plan.description': 'Descripción',
    'payment_plan.date': 'Fecha',
    'payment_plan.percentage': 'Porcentaje',
    'payment_plan.add_installment': 'Agregar cuota',
    'payment_plan.hint': 'Dejar vacío si no ofrece un plan de pago',
    'payment_plan.down_payment': 'Enganche',
    'payment_plan.first_payment': 'Primer pago',
    'payment_plan.second_payment': 'Segundo pago',
    'payment_plan.third_payment': 'Tercer pago',
    'payment_plan.fourth_payment': 'Cuarto pago',
    'payment_plan.fifth_payment': 'Quinto pago',
    'payment_plan.sixth_payment': 'Sexto pago',
    'payment_plan.seventh_payment': 'Séptimo pago',
    'payment_plan.final_payment': 'Pago final',
    'payment_plan.payment_description': 'Descripción del pago',
    'payment_plan.enable': 'Incluir plan de pago',
    'form.floor_plans': 'Planos de planta',
    'form.upload_floor_plans': 'Subir imágenes de planos de planta',
    'form.features': 'Características (separadas por coma)',
    'form.features_placeholder': 'ej. Piscina, Jardín, Garaje, Cocina moderna',
    'form.features_description': 'Separe las características con comas',
    'form.amenities': 'Comodidades',
    'form.amenities_hint': 'Seleccione todas las comodidades que aplican a esta propiedad',
    'amenities.balcony': 'Balcón',
    'amenities.electricity_generator': 'Generador eléctrico',
    'amenities.central_ac': 'Aire acondicionado central',
    'amenities.double_glazed_windows': 'Ventanas de doble cristal',
    'amenities.satellite_tv': 'TV por satélite',
    'amenities.cctv': 'Cámaras de seguridad',
    'amenities.security_staff': 'Personal de seguridad',
    'amenities.sewerage_system': 'Sistema de alcantarillado',
    'amenities.furnished_kitchen': 'Cocina amueblada',
    'amenities.24hr_electricity': 'Electricidad 24h',
    'amenities.market': 'Mercado cercano',
    'amenities.elevator': 'Ascensor',
    'amenities.school': 'Escuela cercana',
    'amenities.mosque': 'Mezquita cercana',
    'amenities.central_gas': 'Gas central',
    'amenities.maintenance': 'Servicios de mantenimiento',
    'amenities.cleaning': 'Servicios de limpieza',
    'amenities.fire_fighting': 'Equipo contra incendios',
    'amenities.car_washing': 'Lavado de coches',
    'amenities.smart_home': 'Casa inteligente',
    'amenities.earthquake_resistant': 'Resistente a terremotos',
    'amenities.central_fire_system': 'Sistema central contra incendios',
    'amenities.hot_water_system': 'Sistema de agua caliente central',
    'amenities.central_vacuum': 'Aspiradora central',
    'amenities.fountain': 'Fuente',
    'amenities.24hr_water': 'Agua 24h',
    'amenities.garbage_chute': 'Conducto de basura',
    'amenities.kitchen_counter': 'Encimera de cocina',
    'amenities.street_lights': 'Alumbrado público',
    'amenities.playground': 'Zona de juegos infantil',
    'amenities.gym': 'Gimnasio',
    'amenities.garden': 'Jardín',
    'amenities.parking': 'Aparcamiento',
    'amenities.wifi': 'WiFi',
    'amenities.security_door': 'Puerta de seguridad',
    'amenities.split_ac': 'Aire acondicionado split',
    'amenities.bathtub': 'Bañera',
    'amenities.refrigerator': 'Refrigerador',
    'amenities.washing_machine': 'Lavadora',
    'amenities.swimming_pool': 'Piscina',
    'amenities.solar_panels': 'Paneles solares',
    'amenities.water_tank': 'Tanque de agua',
    'amenities.water_well': 'Pozo de agua',
    'amenities.septic_tank': 'Fosa séptica',
    'amenities.central_heating': 'Calefacción central',
    'amenities.desert_cooler': 'Enfriador evaporativo',
    'amenities.intercom': 'Intercomunicador',
    'amenities.external_camera': 'Cámara exterior',
    'amenities.guard_room': 'Habitación del guardia',
    'amenities.rooftop_terrace': 'Terraza en la azotea',
    'amenities.basement': 'Sótano',
    'amenities.storage_room': 'Trastero',
    'amenities.internal_stairs': 'Escalera interior',
    'amenities.external_stairs': 'Escalera exterior',
    'amenities.corner_unit': 'Unidad esquinera',
    'amenities.two_streets': 'Dos calles',
    'amenities.double_entrance': 'Doble entrada',
    'amenities.marble_flooring': 'Suelo de mármol',
    'amenities.ceramic_tiles': 'Baldosas cerámicas',
    'amenities.parquet_flooring': 'Suelo de parquet',
    'amenities.gypsum_decorations': 'Decoraciones de yeso',
    'amenities.false_ceiling': 'Falso techo',
    'amenities.steel_doors': 'Puertas de acero',
    'amenities.aluminum_windows': 'Ventanas de aluminio',
    'amenities.pvc_windows': 'Ventanas de PVC',
    'amenities.guest_room': 'Habitación de invitados',
    'amenities.servant_quarters': 'Habitación de servicio',
    'amenities.covered_parking': 'Aparcamiento cubierto',
    'amenities.concrete_fence': 'Cerca de hormigón',
    'amenities.iron_fence': 'Cerca de hierro',
    'amenities.street_pavement': 'Pavimento de calle',
    'amenities.fully_furnished': 'Totalmente amueblado',
    'amenities.hospital': 'Hospital cercano',
    'amenities.category.utilities': 'Electricidad y Agua',
    'amenities.category.climate': 'Climatización',
    'amenities.category.security': 'Seguridad',
    'amenities.category.building': 'Características del edificio',
    'amenities.category.interior': 'Interior y Suelos',
    'amenities.category.rooms': 'Habitaciones',
    'amenities.category.outdoor': 'Exterior y Aparcamiento',
    'amenities.category.appliances': 'Electrodomésticos',
    'amenities.category.services': 'Servicios',
    'amenities.category.nearby': 'Cercano',
    'common.selected': 'seleccionado(s)',
    'form.property_images': 'Imágenes de la propiedad',
    'form.current_images': 'Imágenes actuales',
    'form.image': 'Imagen',
    'form.primary': 'Principal',
    'form.click_image_primary': 'Haga clic en una imagen para establecerla como principal. Use las flechas para cambiar el orden.',
    'form.click_to_upload_new': 'Haga clic para subir nuevas imágenes',
    'form.new_images_upload': 'Nuevas imágenes para subir',
    'form.new_image': 'Nueva imagen',
    'form.upload': 'Subir',
    'form.new_images': 'nuevas imágenes',
    'form.location': 'Ubicación',
    'form.required': 'Obligatorio',
    'form.optional': 'Opcional',
    'form.cancel': 'Cancelar',
    'form.save_changes': 'Guardar cambios',
    'form.uploading': 'Subiendo...',
    'form.upload_images': 'Subir imágenes',
    'form.click_to_upload': 'Haga clic para subir imágenes',
    'form.file_formats': 'JPEG, PNG, WebP, GIF (máx. 10MB por imagen)',
    'form.select_type': 'Seleccionar tipo',
    'form.location_on_map': 'Ubicación de la propiedad en el mapa',
    'form.location_hint': 'Haga clic en el mapa para establecer la ubicación exacta',
    'form.hide_preview': 'Ocultar vista previa',
    'form.show_preview': 'Mostrar vista previa',
    'form.selected_images': 'Imágenes seleccionadas',
    'form.click_set_primary': 'Haga clic en una imagen para establecerla como principal',
    'form.uploaded_images': 'Imágenes subidas',
    'form.images': 'imágenes',
    'form.uploading_images': 'Subiendo imágenes...',
    'form.city_placeholder': 'Buscar una ciudad...',
    'form.new_construction': 'Nueva construcción (Casa futura)',
    'form.preview': 'Vista previa',
    'form.preview_description': 'Así aparecerá su anuncio en el sitio',
    'form.fullscreen': 'Pantalla completa',
    'form.exit_fullscreen': 'Salir de pantalla completa',
    'form.click_preview_fullscreen': 'Haga clic en la vista previa para pantalla completa',
    'form.preview_contact_disabled': 'Las opciones de contacto están deshabilitadas en modo vista previa',
    'form.invalid_format': 'Formato de archivo inválido',
    'form.invalid_format_desc': '{filename} tiene un formato inválido. Permitidos: JPEG, PNG, WebP, GIF',
    'form.file_too_large': 'Archivo demasiado grande',
    'form.file_too_large_desc': '{filename} es mayor de 10MB',
    'form.upload_failed': 'Error de subida',
    'form.upload_failed_desc': 'No se pudieron subir las imágenes',
    'form.images_required': 'Imágenes requeridas',
    'form.images_required_desc': 'Por favor, suba al menos una imagen de la propiedad',
    'form.sold_price_usd': 'Precio de venta (USD)',
    'form.sold_price_placeholder': 'Ingrese el precio de venta real',
    'form.sold_price_description': 'El precio de venta real (puede diferir del precio de lista)',
    'form.video_url': 'URL del video tour',
    'form.video': 'Video',
    'form.add_video': 'Agregar otro video',
    'form.video_addon': '+$3 adicional',
    'form.video_included': 'Incluido gratis',
    'form.video_url_hint': 'Añade un video tour de YouTube o Vimeo. El adicional de video se puede comprar después de crear el anuncio.',
    'form.video_url_hint_free': '¡Añade un video tour de YouTube o Vimeo - incluido con tu suscripción!',
    'form.video_url_hint_addon': 'Añade un video tour de YouTube o Vimeo. Se añadirán $3 al costo de tu anuncio.',
    'form.include_video_tour': 'Incluir video tour',
    'form.show_map': 'Mostrar mapa',
    'form.no_location': 'Ninguna ubicación seleccionada',
    
    // Footer
    'footer.contact': 'Contacto',
    'footer.customer_service': 'Servicio al cliente y contacto',
    'footer.about': 'Sobre Aqarnet',
    'footer.press': 'Prensa',
    'footer.investors': 'Para inversores',
    'footer.advertising': 'Publicidad y marketing',
    'footer.jobs': 'Trabaja con nosotros',
    'footer.statistics': 'Estadísticas inmobiliarias',
    'footer.apps': 'Aplicaciones',
    'footer.iphone_ipad': 'iPhone y iPad',
    'footer.android': 'Android',
    'footer.follow': 'Seguir a Aqarnet',
    'footer.newsletter': 'Boletín de Aqarnet',
    'footer.whatsapp': 'WhatsApp',
    'footer.facebook': 'Facebook',
    'footer.instagram': 'Instagram',
    'footer.linkedin': 'LinkedIn',
    'footer.pinterest': 'Pinterest',
    'footer.tiktok': 'TikTok',
    'footer.threads': 'Threads',
    'footer.podcast': 'Podcast de Aqarnet',
    'footer.all_properties': 'Todas las propiedades',
    'footer.privacy': 'Privacidad',
    'footer.cookies': 'Cookies',
    'footer.terms': 'Términos e Información Legal',
    'footer.cookie_settings': 'Configuración de cookies',
    
    // About Page
    'about.title': 'Sobre Aqarnet',
    'about.subtitle': 'Tu socio de confianza en el mercado inmobiliario iraquí',
    'about.description': 'Aqarnet es la plataforma inmobiliaria líder de Irak, conectando compradores, vendedores y agentes en todo el país. Proporcionamos listados completos, análisis de mercado y herramientas profesionales.',
    'about.mission_title': 'Nuestra Misión',
    'about.mission_text': 'Revolucionar el mercado inmobiliario iraquí proporcionando información inmobiliaria transparente, accesible y confiable.',
    'about.vision_title': 'Nuestra Visión',
    'about.vision_text': 'Convertirnos en la plataforma inmobiliaria más confiable del Medio Oriente.',
    'about.services_title': 'Nuestros Servicios',
    'about.services_text': 'Listados de propiedades, análisis de mercado, conexiones con agentes, valoraciones y noticias inmobiliarias.',
    'about.team_title': 'Nuestro Equipo',
    'about.team_text': 'Un equipo dedicado de profesionales inmobiliarios, expertos en tecnología y especialistas en servicio al cliente.',
    'about.company_info': 'Información de la Empresa',
    'about.based_in': 'Con sede en Suecia, sirviendo a Irak',
    
    // Contact Page
    'contact.title': 'Contáctenos',
    'contact.subtitle': 'Estamos aquí para ayudar. Contáctenos con cualquier pregunta.',
    'contact.email_label': 'Correo electrónico',
    'contact.phone_label': 'Teléfono',
    'contact.phone_info': 'Próximamente',
    'contact.location_label': 'Ubicación',
    'contact.location_info': 'Suecia',
    'contact.form_title': 'Envíanos un mensaje',
    'contact.form_description': 'Complete el formulario y le responderemos lo antes posible.',
    'contact.name_placeholder': 'Su nombre',
    'contact.email_placeholder': 'su@email.com',
    'contact.subject': 'Asunto',
    'contact.subject_placeholder': '¿De qué se trata?',
    'contact.message_placeholder': 'Su mensaje...',
    'contact.success': '¡Mensaje enviado! Nos pondremos en contacto pronto.',
    
    // Press Page
    'press.title': 'Prensa y Medios',
    'press.subtitle': 'Últimas noticias y comunicados de prensa de Aqarnet',
    'press.contact_title': 'Contacto de Prensa',
    'press.contact_description': 'Para consultas de prensa, contáctenos por correo electrónico.',
    'press.add_release': 'Agregar comunicado',
    'press.edit_release': 'Editar comunicado',
    'press.new_release': 'Nuevo comunicado de prensa',
    'press.release_title': 'Título',
    'press.content': 'Contenido',
    'press.publish': 'Publicar',
    'press.no_releases': 'Aún no hay comunicados de prensa.',
    'press.delete_confirm_title': '¿Eliminar comunicado?',
    'press.delete_confirm_description': 'Esta acción no se puede deshacer.',
    
    // Investors Page
    'investors.title': 'Para Inversores',
    'investors.subtitle': 'Únase a nosotros para transformar el mercado inmobiliario iraquí',
    'investors.growth_title': 'Potencial de Crecimiento',
    'investors.growth_text': 'El mercado inmobiliario iraquí está experimentando un rápido crecimiento con oportunidades de inversión significativas.',
    'investors.market_title': 'Líder del Mercado',
    'investors.market_text': 'Aqarnet está posicionada como la principal plataforma digital en el sector inmobiliario iraquí.',
    'investors.vision_title': 'Expansión Regional',
    'investors.vision_text': 'Nuestra visión se extiende más allá de Irak para convertirnos en la plataforma líder en Medio Oriente.',
    'investors.opportunity_title': 'Oportunidad de Inversión',
    'investors.opportunity_text': 'Aqarnet ofrece una oportunidad única de invertir en la transformación digital de uno de los mercados inmobiliarios emergentes más grandes.',
    'investors.opportunity_text_2': 'Con una base de usuarios creciente y servicios en expansión, estamos bien posicionados para un crecimiento sostenible.',
    'investors.contact_title': 'Contáctenos',
    'investors.contact_description': '¿Interesado en oportunidades de inversión? Contáctenos para saber más.',
    'investors.company': 'Empresa / Organización',
    'investors.company_placeholder': 'Nombre de su empresa',
    'investors.message_placeholder': 'Cuéntenos sobre sus intereses de inversión...',
    'investors.success': '¡Gracias por su interés! Nos pondremos en contacto pronto.',
    
    // Advertising Page
    'advertising.title': 'Publicidad y Marketing',
    'advertising.subtitle': 'Llegue a miles de compradores e inquilinos potenciales en Irak',
    'advertising.targeted_title': 'Alcance Dirigido',
    'advertising.targeted_text': 'Llegue a usuarios que buscan activamente propiedades en áreas específicas.',
    'advertising.audience_title': 'Audiencia de Calidad',
    'advertising.audience_text': 'Conecte con compradores e inquilinos serios.',
    'advertising.analytics_title': 'Análisis Detallados',
    'advertising.analytics_text': 'Siga el rendimiento de su campaña con análisis completos.',
    'advertising.formats_title': 'Múltiples Formatos',
    'advertising.formats_text': 'Anuncios banner, listados destacados, contenido patrocinado y más.',
    'advertising.contact_title': 'Comenzar a Anunciar',
    'advertising.contact_description': 'Contáctenos para discutir oportunidades publicitarias.',
    'advertising.company': 'Empresa / Organización',
    'advertising.company_placeholder': 'Nombre de su empresa',
    'advertising.budget': 'Rango de Presupuesto',
    'advertising.budget_placeholder': 'ej., $500 - $1000/mes',
    'advertising.message_placeholder': 'Cuéntenos sobre sus objetivos publicitarios...',
    'advertising.success': '¡Gracias! Le contactaremos sobre oportunidades publicitarias.',
    
    // Jobs Page
    'jobs.title': 'Carreras en Aqarnet',
    'jobs.subtitle': 'Únase a nuestro equipo y ayude a dar forma al futuro inmobiliario en Irak',
    'jobs.add_job': 'Agregar empleo',
    'jobs.edit_job': 'Editar empleo',
    'jobs.new_job': 'Nueva oferta de empleo',
    'jobs.job_title': 'Título del puesto',
    'jobs.department': 'Departamento',
    'jobs.location': 'Ubicación',
    'jobs.employment_type': 'Tipo de empleo',
    'jobs.full_time': 'Tiempo completo',
    'jobs.part_time': 'Tiempo parcial',
    'jobs.contract': 'Contrato',
    'jobs.internship': 'Pasantía',
    'jobs.description': 'Descripción',
    'jobs.requirements': 'Requisitos',
    'jobs.benefits': 'Beneficios',
    'jobs.publish': 'Publicar',
    'jobs.no_jobs': 'No hay puestos abiertos en este momento.',
    'jobs.check_back': 'Vuelva más tarde para nuevas oportunidades.',
    'jobs.apply_now': 'Aplicar ahora',
    'jobs.apply_for': 'Aplicar para',
    'jobs.apply_description': 'Envíe su solicitud por correo electrónico.',
    'jobs.application_sent': '¡Solicitud enviada! ¡Buena suerte!',
    'jobs.delete_confirm_title': '¿Eliminar oferta de empleo?',
    'jobs.delete_confirm_description': 'Esta acción no se puede deshacer.',
    
    // Statistics
    'statistics.title': 'Estadísticas inmobiliarias',
    'statistics.subtitle': 'Analice las tendencias del mercado y la evolución de precios basadas en propiedades vendidas',
    'statistics.6_months': 'Últimos 6 meses',
    'statistics.12_months': 'Últimos 12 meses',
    'statistics.24_months': 'Últimos 24 meses',
    'statistics.36_months': 'Últimos 36 meses',
    'statistics.all_cities': 'Todas las ciudades',
    'statistics.total_sold': 'Total vendido',
    'statistics.avg_price': 'Precio promedio',
    'statistics.avg_price_sqm': 'Precio promedio/m²',
    'statistics.price_change': 'Cambio de precio',
    'statistics.price_trends': 'Tendencias de precios',
    'statistics.price_trends_desc': 'Precios promedio de propiedades a lo largo del tiempo',
    'statistics.sales_volume': 'Volumen de ventas',
    'statistics.sales_volume_desc': 'Número de propiedades vendidas por mes',
    'statistics.properties_sold': 'Propiedades vendidas',
    'statistics.price_by_city': 'Precio por m² por ciudad',
    'statistics.price_by_city_desc': 'Comparación del precio promedio por metro cuadrado entre ciudades',
    'statistics.price_per_sqm': 'Precio por m²',
    'statistics.type_distribution': 'Distribución de tipos de propiedad',
    'statistics.type_distribution_desc': 'Distribución de propiedades vendidas por tipo',
    'statistics.avg_days_market': 'Días promedio en mercado',
    'statistics.days': 'días',
    'statistics.price_ranges': 'Distribución de rangos de precio',
    'statistics.price_ranges_desc': 'Número de propiedades vendidas en cada rango de precio',
    'statistics.properties': 'Propiedades',
    'statistics.year_over_year': 'Comparación anual',
    'statistics.year_over_year_desc': 'Compare precios promedio y volumen de ventas a través de los años',
    'statistics.all_types': 'Todos los tipos de propiedad',
    'statistics.all_rooms': 'Todas las habitaciones',
    'statistics.rooms': 'habitaciones',
    'statistics.export': 'Exportar',
    'statistics.export_csv': 'Descargar CSV',
    'statistics.export_json': 'Descargar JSON',
    'statistics.share': 'Compartir',
    'statistics.link_copied': 'Enlace copiado',
    'statistics.link_copied_desc': 'El enlace del informe estadístico se ha copiado al portapapeles',
    'statistics.custom_date': 'Fecha personalizada',
    'statistics.use_preset': 'Usar preajuste',
    'statistics.start_date': 'Fecha de inicio',
    'statistics.end_date': 'Fecha de fin',
    'statistics.city_comparison': 'Comparación de ciudades',
    'statistics.city_comparison_desc': 'Compare estadísticas inmobiliarias entre dos ciudades lado a lado',
    'statistics.select_city_1': 'Seleccione primera ciudad',
    'statistics.select_city_2': 'Seleccione segunda ciudad',
    'statistics.vs': 'vs',
    'statistics.select_cities_to_compare': 'Seleccione dos ciudades arriba para comparar sus estadísticas',
    'statistics.avg_size': 'Tamaño prom.',
    
    // Cookie Settings
    'cookie_settings.title': 'Configuración de cookies',
    'cookie_settings.description': 'Utilizamos cookies para mejorar tu experiencia de navegación. Puedes personalizar tus preferencias a continuación.',
    'cookie_settings.necessary': 'Cookies necesarias',
    'cookie_settings.necessary_desc': 'Estas cookies son esenciales para el funcionamiento correcto del sitio web. No pueden ser desactivadas.',
    'cookie_settings.functional': 'Cookies funcionales',
    'cookie_settings.functional_desc': 'Estas cookies permiten funciones personalizadas como preferencias de idioma y configuraciones guardadas.',
    'cookie_settings.analytics': 'Cookies analíticas',
    'cookie_settings.analytics_desc': 'Estas cookies nos ayudan a entender cómo los visitantes interactúan con nuestro sitio para mejorar la experiencia del usuario.',
    'cookie_settings.marketing': 'Cookies de marketing',
    'cookie_settings.marketing_desc': 'Estas cookies se utilizan para mostrarte anuncios relevantes y rastrear la efectividad de nuestras campañas.',
    'cookie_settings.accept_all': 'Aceptar todo',
    'cookie_settings.reject_all': 'Rechazar todo',
    'cookie_settings.save_preferences': 'Guardar preferencias',
    'cookie_settings.saved': 'Preferencias de cookies guardadas',
    
    // Cookie Banner
    'cookie_banner.title': 'Usamos cookies',
    'cookie_banner.description': 'Usamos cookies para mejorar tu experiencia de navegación, mostrar contenido personalizado y analizar nuestro tráfico. Al hacer clic en "Aceptar todo", aceptas nuestro uso de cookies.',
    'cookie_banner.accept_all': 'Aceptar todo',
    'cookie_banner.reject_all': 'Rechazar todo',
    'cookie_banner.customize': 'Personalizar',
    'terms.title': 'Términos de Servicio', 'terms.last_updated': 'Última actualización:', 'terms.section1.title': '1. Aceptación de Términos', 'terms.section1.p1': 'Al usar Aqarnet, aceptas estos términos.', 'terms.section1.p2': 'Nos reservamos el derecho de modificar estos términos.', 'terms.section2.title': '2. Descripción del Servicio', 'terms.section2.p1': 'Aqarnet proporciona una plataforma digital para anuncios inmobiliarios en Irak.', 'terms.section2.p2': 'Nuestros servicios incluyen listados, búsqueda y herramientas de comunicación.', 'terms.section3.title': '3. Responsabilidades del Usuario', 'terms.section3.p1': 'Como usuario, aceptas:', 'terms.section3.li1': 'Proporcionar información precisa', 'terms.section3.li2': 'No usar la plataforma para actividades ilegales', 'terms.section3.li3': 'Respetar los derechos de propiedad intelectual', 'terms.section3.li4': 'No interferir con el servicio', 'terms.section4.title': '4. Anuncios de Propiedades', 'terms.section4.p1': 'Los usuarios son responsables de la exactitud de sus anuncios.', 'terms.section4.p2': 'Podemos eliminar anuncios que violen nuestros términos.', 'terms.section5.title': '5. Propiedad Intelectual', 'terms.section5.p1': 'Todo el contenido pertenece a Aqarnet.', 'terms.section5.p2': 'Reproducción prohibida sin permiso.', 'terms.section6.title': '6. Limitación de Responsabilidad', 'terms.section6.p1': 'Aqarnet se proporciona "tal cual" sin garantías.', 'terms.section7.title': '7. Ley Aplicable', 'terms.section7.p1': 'Estos términos se rigen por la ley sueca.', 'terms.section7.p2': 'Las disputas se resolverán en tribunales suecos.', 'terms.section8.title': '8. Contacto', 'terms.section8.p1': 'Para preguntas, contáctanos:', 'terms.section8.email': 'Email',
    'privacy.title': 'Política de Privacidad', 'privacy.last_updated': 'Última actualización:', 'privacy.section1.title': '1. Introducción', 'privacy.section1.p1': 'Estamos comprometidos con proteger tu privacidad.', 'privacy.section1.p2': 'Al usar nuestros servicios, aceptas nuestras prácticas de datos.', 'privacy.section2.title': '2. Información Recopilada', 'privacy.section2.p1': 'Recopilamos:', 'privacy.section2.li1': 'Información de cuenta', 'privacy.section2.li2': 'Datos de propiedades', 'privacy.section2.li3': 'Datos de uso', 'privacy.section2.li4': 'Información del dispositivo', 'privacy.section3.title': '3. Uso de la Información', 'privacy.section3.p1': 'Usamos tu información para:', 'privacy.section3.li1': 'Proporcionar servicios', 'privacy.section3.li2': 'Personalizar tu experiencia', 'privacy.section3.li3': 'Enviar notificaciones', 'privacy.section3.li4': 'Comunicarnos contigo', 'privacy.section4.title': '4. Compartir Datos', 'privacy.section4.p1': 'No vendemos tus datos.', 'privacy.section4.p2': 'Podemos compartir información si lo requiere la ley.', 'privacy.section5.title': '5. Tus Derechos', 'privacy.section5.p1': 'Tienes los siguientes derechos:', 'privacy.section5.li1': 'Acceso', 'privacy.section5.li2': 'Rectificación', 'privacy.section5.li3': 'Eliminación', 'privacy.section5.li4': 'Portabilidad', 'privacy.section5.li5': 'Oposición', 'privacy.section6.title': '6. Seguridad de Datos', 'privacy.section6.p1': 'Protegemos tus datos.', 'privacy.section6.p2': 'Ningún método es 100% seguro.', 'privacy.section7.title': '7. Cambios', 'privacy.section7.p1': 'Podemos actualizar esta política.', 'privacy.section8.title': '8. Contacto', 'privacy.section8.p1': 'Contáctanos para preguntas:', 'privacy.section8.email': 'Email',
    
    // Newsletter
    'newsletter.title': 'Suscripción al boletín',
    'newsletter.description': 'Suscríbase para recibir las últimas noticias inmobiliarias.',
    'newsletter.placeholder': 'Ingrese su email',
    'newsletter.subscribe': 'Suscribirse',
    'newsletter.subscribing': 'Suscribiendo...',
    'newsletter.success': '¡Suscripción exitosa!',
    'newsletter.subscribed': '¡Gracias por suscribirse!',
    'newsletter.invalid_email': 'Ingrese un email válido',
    'newsletter.error': 'Error al suscribirse',
    'newsletter.unsubscribe_note': 'Puede darse de baja en cualquier momento.',
    'newsletter.unsubscribe_title': 'Cancelar suscripción',
    'newsletter.unsubscribe_description': 'Ingrese su email para cancelar la suscripción.',
    'newsletter.unsubscribe_button': 'Cancelar suscripción',
    'newsletter.unsubscribe_success': 'Suscripción cancelada',
    'newsletter.unsubscribe_error': 'Error al cancelar',
    'newsletter.unsubscribe_message': 'Ya no recibirá nuestro boletín.',
    'newsletter.invalid_link': 'Enlace de cancelación inválido',
    'newsletter.invalid_link_message': 'Este enlace de cancelación es inválido o ha expirado. Por favor utilice el enlace de su email del boletín.',
    'newsletter.unsubscribe_reason_label': 'Motivo de la cancelación (opcional)',
    'newsletter.select_reason': 'Seleccione un motivo...',
    'newsletter.reason_too_many_emails': 'Demasiados emails',
    'newsletter.reason_not_relevant': 'El contenido no es relevante para mí',
    'newsletter.reason_never_subscribed': 'Nunca me suscribí',
    'newsletter.reason_found_another': 'Encontré otro servicio',
    'newsletter.reason_other': 'Otro',
    'common.try_again': 'Intentar de nuevo',
    'newsletter.admin.title': 'Suscriptores del boletín',
    'newsletter.admin.description': 'Gestione sus suscriptores',
    'newsletter.admin.total': 'Total suscriptores',
    'newsletter.admin.active': 'Activo',
    'newsletter.admin.unsubscribed': 'Dado de baja',
    'newsletter.admin.export_csv': 'Exportar CSV',
    'newsletter.admin.refresh': 'Actualizar',
    'newsletter.admin.subscribers_list': 'Lista de suscriptores',
    'newsletter.admin.subscribers_description': 'Todos los suscriptores y su estado',
    'newsletter.admin.no_subscribers': 'Sin suscriptores aún',
    'newsletter.admin.subscribed_at': 'Suscrito el',
    'newsletter.admin.status': 'Estado',
    'newsletter.admin.unsubscribed_at': 'Baja el',
    'newsletter.admin.actions': 'Acciones',
    'newsletter.admin.deleted': 'Suscriptor eliminado',
    'newsletter.admin.delete_error': 'Error al eliminar',
    'newsletter.admin.no_data': 'Sin datos para exportar',
    'newsletter.admin.exported': 'Exportado correctamente',
    'newsletter.admin.delete_confirm_title': '¿Eliminar suscriptor?',
    'newsletter.admin.delete_confirm_description': 'Esto eliminará permanentemente este suscriptor.',
    'footer.faq': 'Preguntas frecuentes',
    
    // FAQ Page
    'faq.title': 'Preguntas frecuentes',
    'faq.subtitle': 'Encuentra respuestas a preguntas comunes sobre Aqarnet',
    'faq.account.title': 'Cuenta y registro',
    'faq.account.q1': '¿Cómo creo una cuenta?',
    'faq.account.q1_answer': 'Haz clic en "Iniciar sesión" en el encabezado y selecciona "Crear cuenta". Puedes registrarte con tu email, Google o Apple.',
    'faq.account.q2': '¿Cómo restablezco mi contraseña?',
    'faq.account.q2_answer': 'En la página de inicio de sesión, haz clic en "Olvidé mi contraseña" e ingresa tu dirección de email. Recibirás un enlace para restablecer tu contraseña.',
    'faq.account.q3': '¿Puedo cambiar mi dirección de email?',
    'faq.account.q3_answer': 'Actualmente no puedes cambiar tu dirección de email directamente. Por favor contacta a nuestro equipo de soporte.',
    'faq.listings.title': 'Anuncios de propiedades',
    'faq.listings.q1': '¿Cómo publico una propiedad en venta?',
    'faq.listings.q1_answer': 'Debes estar registrado como agente para publicar propiedades. Después de iniciar sesión, haz clic en "Agregar propiedad" y completa los detalles.',
    'faq.listings.q2': '¿Cuánto tiempo permanece activo un anuncio?',
    'faq.listings.q2_answer': 'Los anuncios permanecen activos hasta que se marquen como vendidos o sean eliminados por el propietario.',
    'faq.listings.q3': '¿Puedo editar mi anuncio después de publicarlo?',
    'faq.listings.q3_answer': 'Sí, puedes editar tu anuncio en cualquier momento. Ve a tu perfil, encuentra el anuncio y haz clic en "Editar".',
    'faq.search.title': 'Búsqueda de propiedades',
    'faq.search.q1': '¿Cómo guardo una búsqueda?',
    'faq.search.q1_answer': 'Después de realizar una búsqueda, haz clic en "Guardar búsqueda". Puedes activar notificaciones por email para nuevas propiedades.',
    'faq.search.q2': '¿Cómo agrego una propiedad a favoritos?',
    'faq.search.q2_answer': 'Haz clic en el icono de corazón en cualquier tarjeta de propiedad. Debes estar conectado para guardar favoritos.',
    'faq.search.q3': '¿Puedo buscar en el mapa?',
    'faq.search.q3_answer': 'Sí, usa la vista de mapa en la página de propiedades para explorar anuncios por ubicación.',
    'faq.contact.title': 'Contactar agentes',
    'faq.contact.q1': '¿Cómo contacto a un agente inmobiliario?',
    'faq.contact.q1_answer': 'Haz clic en "Contactar agente" en cualquier anuncio para enviar un mensaje directamente al agente responsable.',
    'faq.contact.q2': '¿Puedo programar una visita?',
    'faq.contact.q2_answer': 'Sí, haz clic en "Programar visita" en la página de la propiedad para solicitar un horario de visita.',
    'faq.security.title': 'Seguridad y privacidad',
    'faq.security.q1': '¿Está segura mi información personal?',
    'faq.security.q1_answer': 'Sí, utilizamos encriptación y medidas de seguridad estándar de la industria para proteger tus datos.',
    'faq.security.q2': '¿Cómo elimino mi cuenta?',
    'faq.security.q2_answer': 'Ve a la configuración de tu perfil y selecciona "Eliminar cuenta". Esta acción es permanente.',
    'faq.still_questions': '¿Todavía tienes preguntas?',
    'faq.contact_us': 'Contacta a nuestro equipo de soporte y estaremos encantados de ayudarte.',

    // Broker Dashboard
    'broker.dashboard_title': 'Panel del corredor',
    'broker.dashboard_subtitle': 'Gestione sus propiedades e interacciones con clientes',
    'broker.my_properties': 'Mis propiedades',
    'broker.my_properties_desc': 'Ver y gestionar sus anuncios de propiedades',
    'broker.viewings': 'Visitas de propiedades',
    'broker.viewings_desc': 'Gestionar visitas programadas',
    'broker.contacts': 'Mensajes de clientes',
    'broker.contacts_desc': 'Ver y responder consultas de clientes',
    'broker.analytics': 'Análisis de propiedades',
    'broker.analytics_desc': 'Seguir vistas y engagement en sus anuncios',
    'broker.subscription': 'Suscripción',
    'broker.subscription_desc': 'Gestiona tu plan y límites de anuncios',
    'broker.access_denied': 'Esta página solo es accesible para corredores registrados.',
    'broker.profile_title': 'Perfil del corredor',
    'broker.profile_description': 'Gestiona tu perfil público e información de contacto',
    'broker.back_to_dashboard': 'Volver al panel',
    'broker.display_name': 'Nombre para mostrar',
    'broker.display_name_placeholder': 'Tu nombre como aparece a los clientes',
    'broker.contact_email': 'Email de contacto',
    'broker.email_placeholder': 'tu@email.com',
    'broker.email_notification_note': 'Este email se usará para notificaciones sobre visitas y mensajes de clientes.',
    'broker.phone_number': 'Número de teléfono',
    'broker.phone_placeholder': '+34 xxx xxx xxx',
    'broker.change_photo': 'Cambiar foto',
    'broker.save_profile': 'Guardar perfil',
    'broker.profile_saved': 'Perfil guardado correctamente',
    'broker.profile_save_error': 'Error al guardar el perfil',
    'broker.profile_load_error': 'Error al cargar el perfil',
    'broker.avatar_uploaded': 'Foto subida correctamente',
    'broker.avatar_upload_error': 'Error al subir la foto',
    'broker.invalid_image_type': 'Por favor selecciona un archivo de imagen',
    'broker.image_too_large': 'La imagen debe ser menor de 2MB',
    'common.login_required': 'Por favor inicie sesión para acceder a esta página.',
    
    // Profile Email Notifications
    'profile.email_notifications': 'Notificaciones por email',
    'profile.email_notifications_description': 'Elige qué notificaciones por email deseas recibir',
    'profile.notif_property_alerts': 'Alertas de propiedades',
    'profile.notif_property_alerts_desc': 'Recibe notificaciones sobre nuevas propiedades que coincidan con tus búsquedas guardadas',
    'profile.notif_saved_search_alerts': 'Alertas de búsquedas guardadas',
    'profile.notif_saved_search_alerts_desc': 'Recibe alertas cuando nuevas propiedades coincidan con tus criterios de búsqueda',
    'profile.notif_newsletter': 'Boletín informativo',
    'profile.notif_newsletter_desc': 'Recibe nuestro boletín con noticias inmobiliarias y análisis de mercado',
    'profile.notif_viewing_confirmations': 'Confirmaciones de visitas',
    'profile.notif_viewing_confirmations_desc': 'Recibe confirmaciones por email para tus visitas programadas',
    'profile.email_prefs_saved': 'Preferencias de email guardadas',
    'profile.email_prefs_error': 'Error al guardar las preferencias',
    
    // Agency Dashboard
    'agency.my_agency': 'Mi agencia',
    'agency.dashboard_title': 'Panel de la agencia',
    'agency.dashboard_subtitle': 'Gestione sus corredores y configuración de la agencia',
    'agency.brokers_tab': 'Corredores',
    'agency.invites_tab': 'Invitaciones',
    'agency.settings_tab': 'Configuración',
    'agency.brokers_title': 'Corredores de la agencia',
    'agency.brokers_description': 'Gestione los corredores de su agencia',
    'agency.create_broker': 'Crear corredor',
    'agency.create_broker_title': 'Crear nueva cuenta de corredor',
    'agency.create_broker_description': 'Cree una nueva cuenta de corredor vinculada directamente a su agencia',
    'agency.broker_name': 'Nombre completo',
    'agency.broker_email': 'Correo electrónico',
    'agency.broker_password': 'Contraseña',
    'agency.create_broker_btn': 'Crear cuenta de corredor',
    'agency.broker_created': 'Cuenta de corredor creada correctamente',
    'agency.broker_create_error': 'Error al crear la cuenta de corredor',
    'agency.no_brokers': 'Aún no hay corredores',
    'agency.no_brokers_description': 'Cree cuentas de corredor o envíe invitaciones',
    'agency.broker_removed': 'Corredor eliminado de la agencia',
    'agency.broker_remove_error': 'Error al eliminar el corredor',
    'agency.invites_title': 'Invitaciones pendientes',
    'agency.invites_description': 'Gestione las invitaciones a posibles corredores',
    'agency.send_invite': 'Enviar invitación',
    'agency.send_invite_title': 'Invitar a un corredor',
    'agency.send_invite_description': 'Envíe una invitación para unirse a su agencia',
    'agency.invite_email': 'Correo electrónico',
    'agency.send_invite_btn': 'Enviar invitación',
    'agency.invite_sent': 'Invitación enviada correctamente',
    'agency.invite_error': 'Error al enviar la invitación',
    'agency.link_copied': 'Enlace de invitación copiado',
    'agency.no_invites': 'No hay invitaciones pendientes',
    'agency.settings_title': 'Configuración de la agencia',
    'agency.settings_description': 'Actualice la información de su agencia',
    'agency.settings_saved': 'Configuración de la agencia guardada',
    'agency.settings_save_error': 'Error al guardar la configuración',
    'agency.load_error': 'Error al cargar los datos de la agencia',
    'agency_invite.title': 'Unirse a la agencia',
    'agency_invite.description': 'Ha sido invitado a unirse a {agencyName}',
    'agency_invite.accept_btn': 'Aceptar invitación',
    'agency_invite.accepted_success': '¡Se unió correctamente a la agencia!',
    'agency_invite.accept_error': 'Error al aceptar la invitación',
    'agency_invite.create_account': 'Crear una cuenta',
    'agency_invite.invalid_link': 'Enlace de invitación no válido',
    'agency_invite.expired': 'Esta invitación ha caducado',
    'agency_invite.already_accepted': 'Esta invitación ya ha sido aceptada',
    'agency_invite.fetch_error': 'Error al cargar la invitación',
    'agency_invite.error_title': 'Invitación no válida',
    'agency_invite.back_home': 'Volver al inicio',
    'agency_invite.create_account_description': 'Crea una cuenta para aceptar esta invitación',
    'agency_invite.your_name': 'Tu nombre',
    'agency_invite.name_placeholder': 'Introduce tu nombre completo',
    'agency_invite.email_placeholder': 'tu@email.com',
    'agency_invite.password': 'Contraseña',
    'agency_invite.password_placeholder': 'Crea una contraseña',
    'agency_invite.confirm_password': 'Confirmar contraseña',
    'agency_invite.confirm_password_placeholder': 'Confirma tu contraseña',
    'agency_invite.register_accept_btn': 'Crear cuenta y unirse',
    'agency_invite.registered_success': '¡Cuenta creada y unido a la agencia!',
    'agency_invite.register_error': 'Error al crear la cuenta',
    'agency_invite.passwords_mismatch': 'Las contraseñas no coinciden',
    'agency_invite.password_too_short': 'La contraseña debe tener al menos 6 caracteres',
    'agency_invite.already_have_account': '¿Ya tienes una cuenta?',
    'agency_invite.login_here': 'Inicia sesión aquí',
    'agency_invite.joining': 'Te unes a',
    'agency_invite.logged_in_as': 'Conectado como',
    'agency.notifications_tab': 'Notificaciones',
    'agency.notifications_title': 'Notificaciones por correo',
    'agency.notifications_description': 'Elige qué notificaciones recibes sobre la actividad de los corredores',
    'agency.notifications_saved': 'Preferencias de notificación guardadas',
    'agency.notifications_save_error': 'Error al guardar las preferencias',
    'agency.notif_new_broker_joins': 'Nuevo corredor se une',
    'agency.notif_new_broker_joins_desc': 'Recibe notificación cuando un corredor acepta tu invitación',
    'agency.notif_broker_property_added': 'Corredor añade propiedad',
    'agency.notif_broker_property_added_desc': 'Recibe notificación cuando los corredores añaden nuevas propiedades',
    'agency.notif_broker_viewing_requests': 'Solicitudes de visita',
    'agency.notif_broker_viewing_requests_desc': 'Recibe notificación sobre solicitudes de visita para propiedades de corredores',
    'agency.notif_broker_client_messages': 'Mensajes de clientes',
    'agency.notif_broker_client_messages_desc': 'Recibe notificación cuando los clientes contactan a tus corredores',
    'agency.notif_weekly_summary': 'Resumen semanal',
    'agency.notif_weekly_summary_desc': 'Recibe un resumen semanal de la actividad de la agencia',
    'agency.analytics_tab': 'Analíticas',
    'agency.no_brokers_analytics': 'Añade corredores para ver análisis de rendimiento',
    'agency.total_properties': 'Propiedades totales',
    'agency.total_views': 'Vistas totales',
    'agency.total_viewings': 'Solicitudes de visita',
    'agency.total_sold': 'Propiedades vendidas',
    'agency.total_sales_value': 'Valor total de ventas',
    'agency.broker_performance_chart': 'Rendimiento de corredores',
    'agency.broker_performance_chart_desc': 'Compara métricas de corredores de un vistazo',
    'agency.individual_broker_stats': 'Estadísticas individuales',
    'agency.individual_broker_stats_desc': 'Métricas detalladas para cada corredor',
    'agency.properties': 'Propiedades',
    'agency.views': 'Vistas',
    'agency.viewings': 'Visitas',
    'agency.contacts': 'Contactos',
    'agency.active': 'Activo',
    'agency.sold': 'Vendido',
    'agency.sales_value': 'Valor de ventas',
    'agency.upgrade_plan': 'Mejorar plan',
    'agency.view_plans': 'Ver planes',
    'agency.billing_tab': 'Facturación',
    'agency.name_label': 'Nombre de la agencia',
    'agency.email_label': 'Correo electrónico',
    'agency.phone_label': 'Teléfono',
    'agency.website_label': 'Sitio web',
    'agency.address_label': 'Dirección',
    'agency.city_label': 'Ciudad',
    'agency.description_label': 'Descripción',
    'agency.description_placeholder': 'Cuéntales a los clientes sobre tu agencia...',
    'agency.save_settings': 'Guardar configuración',
    'agency.broker_usage': 'Uso de agentes',
    'agency.invite_broker': 'Invitar agente',
    'agency.brokers': 'agentes',
    'agency.broker_limit_reached': 'Límite de agentes alcanzado',
    'agency.upgrade_for_more': 'Mejora tu plan para agregar más agentes',
    'agency.active_listings': 'Listados activos',
    'agency.broker_slots_remaining': 'Espacios restantes',
    'agency.manage_subscription': 'Gestionar suscripción',
    'agency.upgrade_your_plan': 'Mejora tu plan',
    'agency.choose_plan': 'Elige el plan que mejor se adapte a tu agencia',
    'agency.pro_plan': 'Pro',
    'agency.enterprise_plan': 'Empresa',
    'agency.for_growing_agencies': 'Para agencias en crecimiento',
    'agency.for_large_agencies': 'Para grandes agencias',
    'agency.per_month': '/mes',
    'agency.up_to_brokers': 'Hasta {count} agentes',
    'agency.unlimited_brokers': 'Agentes ilimitados',
    'agency.advanced_analytics': 'Análisis avanzados',
    'agency.full_analytics': 'Suite de análisis completa',
    'agency.priority_support': 'Soporte prioritario',
    'agency.dedicated_support': 'Soporte dedicado',
    'agency.upgrade_to_pro': 'Mejorar a Pro',
    'agency.upgrade_to_enterprise': 'Mejorar a Empresa',
    'agency.popular': 'Popular',
    'agency.current_plan': 'Plan actual',
    'agency.plan_features': 'Características del plan',
    'agency.billing_history': 'Historial de facturación',
    'agency.no_billing_history': 'Aún no hay historial de facturación',
    'agency.subscription_billing': 'Suscripción y Facturación',
    'agency.manage_agency_subscription': 'Administra la suscripción de tu agencia',
    'agency.plan': 'Plan',
    'agency.basic_plan': 'Básico',
    'agency.broker_limit': 'Límite de agentes',
    'agency.current_usage': 'Uso actual',
    'agency.status': 'Estado',
    'agency.active_status': 'Activo',
    'agency.unlimited': 'Ilimitado',
    'agency.feature': 'Característica',
    'agency.price': 'Precio',
    'agency.free': 'Gratis',
    'agency.analytics': 'Analíticas',
    'agency.basic_analytics': 'Básico',
    'agency.advanced': 'Avanzado',
    'agency.full_suite': 'Suite completa',
    'agency.support': 'Soporte',
    'agency.email_support': 'Correo',
    'agency.priority': 'Prioritario',
    'agency.dedicated': 'Dedicado',
    'agency_directory.title': 'Agencias inmobiliarias',
    'agency_directory.subtitle': 'Explora y encuentra agencias inmobiliarias de confianza',
    'agency_directory.search_placeholder': 'Buscar agencias...',
    'agency_directory.location_placeholder': 'Filtrar por ubicación...',
    'agency_directory.agencies_found': 'agencias encontradas',
    'agency_directory.no_agencies': 'No se encontraron agencias',
    'agency_directory.no_agencies_description': 'Intenta ajustar tus criterios de búsqueda',
    'agency_directory.brokers': 'corredores',
    'agency_directory.view_agency': 'Ver agencia',
    'agency_directory.not_found': 'Agencia no encontrada',
    'agency_directory.back_to_directory': 'Volver al directorio',
    'agency_directory.visit_website': 'Visitar sitio web',
    'agency_directory.our_brokers': 'Nuestros corredores',
    'agency_directory.no_brokers_yet': 'Aún no hay corredores en esta agencia',
    'agency_directory.agency_listings': 'Anuncios de propiedades',
    
    // User Listings
    'userListings.title': 'Mis anuncios',
    'userListings.subtitle': 'Gestiona tus anuncios de propiedades y crea nuevos',
    'userListings.pricingTitle': 'Precios de anuncios',
    'userListings.pricingDesc': 'Crea tu anuncio de propiedad con nuestros precios simples',
    'userListings.listingPrice': '$10 por anuncio',
    'userListings.listingDuration': 'Válido por 1 mes',
    'userListings.videoPrice': '+$3 por video',
    'userListings.videoDesc': 'Añade un tour en video a tu anuncio',
    'userListings.includeVideo': 'Incluir tour en video',
    'userListings.createListing': 'Crear anuncio',
    'userListings.myListingsTitle': 'Tus propiedades',
    'userListings.noListings': 'Aún no hay anuncios',
    'userListings.noListingsDesc': 'Crea tu primer anuncio de propiedad para comenzar',
    'userListings.createFirst': 'Crea tu primer anuncio',
    'userListings.hasVideo': 'Video',
    'userListings.renew': 'Renovar',
    'userListings.renewSuccess': '¡Anuncio renovado!',
    'userListings.renewSuccessDesc': 'Tu anuncio ha sido renovado por un mes más',
    'userListings.paymentSuccess': '¡Pago exitoso!',
    'userListings.paymentSuccessDesc': 'Tu anuncio ahora está activo',
    'userListings.paymentCancelled': 'Pago cancelado',
    'userListings.paymentCancelledDesc': 'Tu pago fue cancelado',
    'userListings.checkoutError': 'Error al crear el pago',
    'userListings.renewError': 'Error al renovar el anuncio',
    'userListings.createDraft': 'Crear anuncio',
    'userListings.previewListing': 'Vista previa de tu anuncio',
    'userListings.draft': 'Borrador',
    'userListings.drafts': 'Borradores',
    'userListings.draftSaved': 'Borrador guardado',
    'userListings.draftSavedDesc': 'Tu anuncio ha sido guardado como borrador',
    'userListings.draftError': 'Error al guardar el borrador',
    'userListings.draftDeleted': 'Borrador eliminado',
    'userListings.deleteError': 'Error al eliminar',
    'userListings.saveDraft': 'Guardar borrador',
    'userListings.saveAndPreview': 'Guardar y previsualizar',
    'userListings.publishOptions': 'Opciones de publicación',
    'userListings.addVideoTour': 'Añadir tour en video',
    'userListings.videoIncluded': 'Tour en video incluido',
    'userListings.totalPrice': 'Total',
    'userListings.backToEdit': 'Volver a editar',
    'userListings.publishNow': 'Publicar ahora',
    'userListings.publish': 'Publicar',
    'userListings.publishSuccess': '¡Anuncio publicado!',
    'userListings.publishSuccessDesc': 'Tu anuncio está ahora pendiente de revisión',
    'userListings.firstListingFree': '¡Primera publicación gratis!',
    'userListings.firstListingFreeDesc': 'Tu primera publicación de propiedad es completamente gratis. ¡Envíala ahora y llega a compradores en todo Irak!',
    'userListings.pending': 'Pendiente de revisión',
    'userListings.pendingInfo': 'Estos anuncios están siendo revisados por nuestro equipo de moderación. Serás notificado cuando sean aprobados.',
    'userListings.pendingReview': 'Pendiente de revisión',
    'userListings.awaitingApproval': 'Esperando aprobación del moderador',
    'userListings.rejected': 'Anuncios rechazados',
    'userListings.rejectedInfo': 'Estos anuncios no fueron aprobados. Por favor edita y corrige los problemas antes de volver a enviar.',
    'userListings.rejectedBadge': 'Rechazado',
    'userListings.live': 'Activo',
    'userListings.editDraft': 'Editar',
    'userListings.editAndResubmit': 'Editar y reenviar',
    'userListings.pendingCannotEdit': 'No se puede editar mientras está en revisión',
    'userListings.resubmitForReview': 'Reenviar para revisión',
    'userListings.resubmitInfo': 'Tus cambios han sido guardados. Envía tu anuncio nuevamente para revisión sin costo adicional.',
    'userListings.submitForReview': 'Enviar para revisión',
    'userListings.validation.priceRequired': 'El precio es requerido',
    'userListings.validation.cityRequired': 'La ciudad es requerida',
    'userListings.validation.roomsRequired': 'El número de habitaciones es requerido',
    'userListings.validation.sizeRequired': 'El tamaño es requerido',
    'userListings.validation.plotSizeRequired': 'El tamaño del terreno es requerido',
    'userListings.validation.imagesRequired': 'Se requiere al menos una imagen',
    'userListings.choosePlan': 'Elige tu plan de anuncio',
    'userListings.choosePlanDesc': 'Selecciona cuántas imágenes mostrar en tu tarjeta de anuncio',
    'userListings.upgradePlan': 'Mejora tu plan de anuncio',
    'userListings.upgradePlanDesc': 'Mejora para mostrar más imágenes. Solo pagas la diferencia.',
    'userListings.selectImages': 'Selecciona imágenes para mostrar',
    'userListings.selectImagesDesc': 'Elige imágenes para mostrar en tu tarjeta de anuncio',
    'userListings.selectPrimaryImage': 'Selecciona imagen principal',
    'userListings.selectPrimaryImageDesc': 'Elige la imagen principal que se mostrará primero en tu anuncio',
    'userListings.addVideo': 'Agregar tour en video',
    'userListings.pleaseSelectImages': 'Por favor selecciona imágenes para continuar',
    'userListings.pleaseSelectPrimaryImage': 'Por favor selecciona una imagen principal para continuar',
    'userListings.proceedToPayment': 'Proceder al pago',
    'userListings.tierBasic': 'Básico',
    'userListings.tierStandard': 'Estándar',
    'userListings.tierPremium': 'Premium',
    'userListings.tierBasicDesc': '1 imagen en la tarjeta',
    'userListings.tierStandardDesc': 'Carrusel de 3 imágenes',
    'userListings.tierPremiumDesc': 'Todas las imágenes + insignia',
    'userListings.tierDuration': '30 días activo',
    'userListings.tierBest': 'Mejor',
    'userListings.tierBasicFull': 'Nivel Básico (1 imagen)',
    'userListings.tierStandardFull': 'Nivel Estándar (3 imágenes)',
    'userListings.tierPremiumFull': 'Nivel Premium (Todas las imágenes)',
    'userListings.changeTier': 'Cambiar nivel',
    'userListings.selectImageTier': 'Seleccionar nivel de imagen',
    'userListings.selectTierFirst': 'Seleccione un nivel antes de publicar',
    'userListings.upgrade': 'Mejorar',
    'common.total': 'Total',
    
    // Settings Page
    'settings.title': 'Configuración',
    'settings.subtitle': 'Gestiona la configuración y preferencias de tu cuenta',
    'settings.profile': 'Perfil',
    'settings.notifications': 'Notificaciones',
    'settings.account': 'Cuenta',
    'settings.profile_info': 'Información del perfil',
    'settings.profile_desc': 'Actualiza tu información personal',
    'settings.change_photo': 'Cambiar foto',
    'settings.display_name': 'Nombre para mostrar',
    'settings.display_name_placeholder': 'Tu nombre',
    'settings.email': 'Correo electrónico',
    'settings.email_placeholder': 'tu@email.es',
    'settings.phone': 'Teléfono',
    'settings.phone_placeholder': '+34 612 345 678',
    'settings.save': 'Guardar cambios',
    'settings.saved': 'Configuración guardada con éxito',
    'settings.load_error': 'Error al cargar la configuración',
    'settings.save_error': 'Error al guardar la configuración',
    'settings.invalid_image': 'Por favor selecciona un archivo de imagen',
    'settings.image_too_large': 'La imagen debe ser menor de 2MB',
    'settings.avatar_uploaded': 'Foto subida',
    'settings.avatar_error': 'Error al subir la foto',
    'settings.notification_preferences': 'Preferencias de notificación',
    'settings.notification_desc': 'Elige qué notificaciones quieres recibir',
    'settings.notif_property_alerts': 'Alertas de propiedades',
    'settings.notif_property_alerts_desc': 'Recibe notificaciones sobre nuevas propiedades',
    'settings.notif_saved_search': 'Alertas de búsqueda guardada',
    'settings.notif_saved_search_desc': 'Recibe notificaciones cuando nuevas propiedades coincidan con tus búsquedas guardadas',
    'settings.notif_newsletter': 'Boletín',
    'settings.notif_newsletter_desc': 'Recibe nuestro boletín con actualizaciones del mercado',
    'settings.notif_viewing': 'Confirmaciones de visita',
    'settings.notif_viewing_desc': 'Recibe confirmaciones para visitas programadas',
    'settings.account_settings': 'Configuración de la cuenta',
    'settings.account_desc': 'Gestiona la seguridad y preferencias de tu cuenta',
    'settings.change_password': 'Cambiar contraseña',
    'settings.change_password_desc': 'Actualiza tu contraseña',
    'settings.change': 'Cambiar',
    'settings.two_factor': 'Autenticación de dos factores',
    'settings.two_factor_desc': 'Añade seguridad adicional a tu cuenta',
    'settings.enable': 'Activar',
    'settings.delete_account': 'Eliminar cuenta',
    'settings.delete_account_desc': 'Elimina tu cuenta permanentemente',
    'settings.delete': 'Eliminar',
    'settings.logout': 'Cerrar sesión',
    'common.back': 'Volver',
    'contact_form.title': 'Contacto',
    'contact_form.description': 'Envía un mensaje para ponerte en contacto.',
    'contact_form.name': 'Nombre',
    'contact_form.name_placeholder': 'Tu nombre',
    'contact_form.email': 'Correo electrónico',
    'contact_form.email_placeholder': 'tu@email.es',
    'contact_form.phone': 'Teléfono',
    'contact_form.phone_placeholder': 'Tu número de teléfono',
    'contact_form.message': 'Mensaje',
    'contact_form.message_placeholder': '¿Cómo podemos ayudarte?',
    'contact_form.send': 'Enviar mensaje',
    'contact_form.sending': 'Enviando...',
    'contact_form.success_title': '¡Mensaje enviado!',
    'contact_form.success_message': 'Tu mensaje ha sido enviado. Recibirás una confirmación por correo electrónico.',
    'contact_form.error_title': 'Error',
    'contact_form.error_message': 'Error al enviar el mensaje. Por favor, inténtalo de nuevo.',
    
    // Messaging
    'messaging.title': 'Mensajes',
    'messaging.description': 'Tus conversaciones privadas',
    'messaging.new_message': 'Nuevo mensaje',
    'messaging.no_messages': 'Aún no hay mensajes',
    'messaging.no_conversations': 'Aún no hay conversaciones',
    'messaging.send': 'Enviar',
    'messaging.send_message': 'Enviar mensaje',
    'messaging.message_to': 'Enviar mensaje a',
    'messaging.subject': 'Asunto',
    'messaging.subject_placeholder': 'Asunto (opcional)',
    'messaging.message': 'Mensaje',
    'messaging.message_placeholder': 'Escribe tu mensaje...',
    'messaging.login_required': 'Debes iniciar sesión para enviar mensajes.',
    'messaging.message_sent': 'Mensaje enviado con éxito',
    'messaging.contact_agent_description': 'Contacta directamente con el agente inmobiliario',
    'messaging.type_message': 'Escribe un mensaje...',
    'messaging.inbox': 'Bandeja de entrada',
    'messaging.sent': 'Enviados',
    'messaging.search': 'Buscar conversaciones...',
    'messaging.select_conversation': 'Selecciona una conversación',
    'messaging.select_description': 'Elige una conversación de la lista para empezar a chatear',
    'messaging.encrypted': 'Cifrado de extremo a extremo',
    'messaging.decrypt': 'Descifrar mensaje',
    'messaging.reply': 'Responder',
    'messaging.replying_to': 'Respondiendo a',
    'messaging.replying_to_yourself': 'ti mismo',
    'messaging.forward': 'Reenviar',
    'messaging.delete': 'Eliminar',
    'messaging.delete_confirm': 'Eliminar mensaje',
    'messaging.delete_description': 'Este mensaje se eliminará para todos. Esta acción no se puede deshacer.',
    'messaging.delete_for_everyone': 'Eliminar para todos',
    'messaging.pin': 'Fijar',
    'messaging.unpin': 'Desfijar',
    'messaging.pinned_messages': 'Mensajes fijados',
    'messaging.no_pinned': 'No hay mensajes fijados',
    'messaging.delivered': 'Entregado',
    'messaging.read': 'Leído',
    'messaging.seen_at': 'Visto a las',
    'messaging.voice_message': 'Mensaje de voz',
    'messaging.search_messages': 'Buscar en mensajes...',
    'messaging.contacts': 'Contactos',
    'messaging.results_found': 'resultados encontrados',
    'messaging.recording': 'Grabando...',
    'messaging.typing': 'escribiendo...',
    'messaging.online': 'en línea',
    'messaging.tap_for_info': 'toca aquí para info de contacto',
    'messaging.photos': 'Fotos',
    'messaging.camera': 'Cámara',
    'messaging.location': 'Ubicación',
    'messaging.contact': 'Contacto',
    'messaging.document': 'Documento',
    'messaging.poll': 'Encuesta',
    'messaging.event': 'Evento',
    'messaging.location_coming_soon': 'Compartir ubicación próximamente',
    'messaging.contact_coming_soon': 'Compartir contacto próximamente',
    'messaging.poll_coming_soon': 'Crear encuesta próximamente',
    'messaging.event_coming_soon': 'Crear evento próximamente',
    'messaging.schedule_event': 'Programar evento',
    'messaging.event_title': 'Título del evento',
    'messaging.event_title_placeholder': 'ej: Visita de propiedad',
    'messaging.event_date': 'Fecha',
    'messaging.event_time': 'Hora',
    'messaging.event_location': 'Ubicación',
    'messaging.event_location_placeholder': 'Ingrese ubicación o dirección',
    'messaging.event_description': 'Descripción',
    'messaging.event_description_placeholder': 'Agregue detalles adicionales...',
    'messaging.send_event': 'Enviar evento',
    'messaging.event_created': 'Evento creado',
    'messaging.create_poll': 'Crear encuesta',
    'messaging.poll_question': 'Pregunta',
    'messaging.poll_question_placeholder': '¿Qué desea preguntar?',
    'messaging.poll_options': 'Opciones',
    'messaging.option': 'Opción',
    'messaging.add_option': 'Agregar opción',
    'messaging.allow_multiple': 'Permitir respuestas múltiples',
    'messaging.send_poll': 'Enviar encuesta',
    'messaging.poll_created': 'Encuesta creada',
    'messaging.edit': 'Editar',
    'messaging.edit_message': 'Editar mensaje',
    'messaging.edit_description': 'Realiza cambios en tu mensaje',
    'messaging.edit_time_remaining': 'Tienes',
    'messaging.minutes_to_edit': 'minutos para editar este mensaje',
    'messaging.message_edited': 'Mensaje editado',
    'messaging.edit_failed': 'Error al editar mensaje',
    'messaging.from_date': 'Desde',
    'messaging.to_date': 'Hasta',
    'messaging.clear_filters': 'Limpiar filtros',
    'messaging.export_chat': 'Exportar conversación',
    'messaging.export_description': 'Exportar historial de conversación',
    'messaging.export_text': 'Archivo de texto (.txt)',
    'messaging.export_text_desc': 'Formato de texto simple, fácil de leer y compartir',
    'messaging.export_pdf': 'Documento PDF',
    'messaging.export_pdf_desc': 'Documento formateado, ideal para imprimir',
    'messaging.export_messages_count': 'mensajes serán exportados',
    'messaging.exporting': 'Exportando...',
    'messaging.export': 'Exportar',
    'messaging.export_success': 'Conversación exportada con éxito',
    'messaging.export_failed': 'Error al exportar',
    'messaging.is_typing': 'escribiendo...',
    
    // Quick Reply Templates
    'quick_reply.title': 'Respuestas Rápidas',
    'quick_reply.greeting': '¡Hola! Gracias por contactarnos.',
    'quick_reply.interested': 'Estoy interesado en esta propiedad. ¿Puede darme más detalles?',
    'quick_reply.schedule_viewing': 'Me gustaría programar una visita. ¿Qué horarios están disponibles?',
    'quick_reply.ask_price': '¿El precio es negociable?',
    'quick_reply.ask_availability': '¿Esta propiedad sigue disponible?',
    'quick_reply.thank_you': 'Gracias por la información. Me pondré en contacto pronto.',
    'quick_reply.will_contact': 'Me pondré en contacto pronto con más detalles.',
    'quick_reply.not_interested': 'Gracias, pero ya no estoy interesado en esta propiedad.',
    
    // Notifications
    'notifications.enable': 'Activar notificaciones',
    'notifications.on': 'Notificaciones activadas',
    'notifications.preferences': 'Preferencias de notificación',
    'notifications.sound': 'Sonido de notificación',
    'notifications.sound_desc': 'Reproducir un sonido al recibir un mensaje',
    'notifications.sound_default': 'Predeterminado',
    'notifications.sound_chime': 'Campanilla',
    'notifications.sound_bell': 'Campana',
    'notifications.sound_pop': 'Pop',
    'notifications.test': 'Probar',
    'notifications.vibration': 'Vibración',
    'notifications.vibration_desc': 'Vibrar al recibir un mensaje (solo móvil)',
    'messaging.you': 'Tú',
    'messaging.encrypted_message': 'Mensaje cifrado',
    'messaging.e2e_notice': 'Los mensajes y llamadas están cifrados de extremo a extremo. Solo las personas en este chat pueden leerlos, escucharlos o compartirlos.',
    'messaging.today': 'Hoy',
    'messaging.yesterday': 'Ayer',
    'messaging.search_groups': 'Buscar grupos...',
    
    // Attachment viewer
    'attachment.all_media': 'Todos los medios',
    'attachment.go_to_message': 'Ir al mensaje',
    'attachment.download': 'Descargar',
    'attachment.view_fullscreen': 'Ver pantalla completa',
    'attachment.save_to_gallery': 'Guardar en galería',
    'attachment.share': 'Compartir',
    'attachment.double_tap_zoom': 'Toca dos veces para hacer zoom',
    'attachment.zoom_in': 'Acercar',
    'attachment.zoom_out': 'Alejar',
    'attachment.reset_zoom': 'Restablecer zoom',
    'attachment.ctrl_scroll_zoom': 'Ctrl + Desplazar para zoom',
    'attachment.fit_width': 'Ajustar al ancho',
    'attachment.fit_height': 'Ajustar al alto',
    'attachment.click_to_navigate': 'Clic para navegar',
    
    // Status
    'status.online': 'En línea',
    'status.offline': 'Desconectado',
    'status.last_seen': 'Visto por última vez',
    
    // Blocking
    'blocking.block_user': 'Bloquear usuario',
    'blocking.block_confirm': '¿Estás seguro de que quieres bloquear a {name}? No podrán enviarte mensajes.',
    'blocking.reason_optional': 'Razón (opcional)',
    'blocking.reason_placeholder': '¿Por qué bloqueas a este usuario?',
    'blocking.block': 'Bloquear',
    'blocking.user_blocked': 'Usuario bloqueado correctamente',
    'blocking.blocked_users': 'Usuarios bloqueados',
    'blocking.unblock': 'Desbloquear',
    'blocking.you_blocked_user': 'Has bloqueado a este usuario. Desbloquea para enviar mensajes.',
    'blocking.user_blocked_you': 'Este usuario te ha bloqueado. No puedes enviar mensajes.',
    
    // Reporting
    'reporting.report_user': 'Reportar usuario',
    'reporting.reasons.spam': 'Spam o mensajes no deseados',
    'reporting.reasons.harassment': 'Acoso o intimidación',
    'reporting.reasons.inappropriate_content': 'Contenido inapropiado',
    'reporting.reasons.fraud': 'Fraude o estafa',
    'reporting.reasons.other': 'Otro',
    
    // Admin Reports
    'admin.reports': 'Reportes',
    'admin.reports_panel': 'Reportes de usuarios',
    'admin.pending': 'Pendiente',
    'admin.resolved': 'Resuelto',
    'admin.all': 'Todos',
    'messaging.unknown_user': 'Usuario desconocido',
    'messaging.no_groups': 'Sin grupos todavía',
    'messaging.create_group': 'Crear grupo',
    'messaging.group_chat': 'Chat grupal',
    'messaging.direct_messages': 'Directo',
    'messaging.groups': 'Grupos',
    'messaging.group_name': 'Nombre del grupo',
    'messaging.group_description': 'Descripción',
    
    'nav.favorites': 'Favoritos',
    'nav.profile': 'Perfil',
    'nav.login': 'Iniciar sesión',
    'nav.logout': 'Cerrar sesión',
    'nav.recent_searches': 'Búsquedas recientes',
    'nav.no_recent_searches': 'Sin búsquedas recientes',
    
    'agents.title': 'Encontrar un agente',
    'agents.subtitle': '¿Piensas vender? ¡Te ayudamos a encontrar agentes expertos en tu zona!',
    'agents.area_search': 'Búsqueda por zona',
    'agents.name_search': 'Búsqueda por nombre',
    'agents.search_placeholder': 'Ingresa zona o dirección',
    'agents.show': 'Mostrar',
    'agents.sort_by': 'Ordenar por',
    'agents.agents_count': 'Agentes',
    'agents.companies_label': 'Inmobiliarias',
    'agents.recent_sales': 'Última fecha de venta',
    'agents.most_sales': 'Más ventas',
    'agents.alphabetical': 'Orden alfabético',
    'agents.showing_results': 'Mostrando',
    'agents.agents_label': 'agentes',
    'agents.loading': 'Cargando agentes...',
    'agents.no_results': 'No se encontraron agentes.',
    'agents.phone': 'Teléfono:',
    'agents.status': 'Estado:',
    'agents.active_agent': 'Agente activo',
    'agents.anonymous_agent': 'Agente anónimo',
    'agents.agent_label': 'Agente',
    'agents.show_more': 'Mostrar más agentes',
    'agents.verified_agent': 'Agente verificado',
    
    // Broker Public Profile
    'broker.listing_usage': 'Uso de anuncios',
    'broker.monthly_listing_limits': 'Límites mensuales de anuncios',
    'broker.listings_used': 'Anuncios usados',
    'broker.listings_remaining': 'anuncios restantes',
    'broker.period_resets': 'El período se reinicia',
    'broker.export_csv': 'Exportar CSV',
    'broker.total_views': 'Vistas totales',
    'broker.scheduled_viewings': 'Visitas programadas',
    'broker.client_messages': 'Mensajes de clientes',
    'broker.filters': 'Filtros',
    'broker.all_time': 'Todo el tiempo',
    'broker.active_listings': 'Anuncios activos',
    'broker.sold_listings': 'Anuncios vendidos',
    'broker.active_listings_desc': 'Sus propiedades publicadas actualmente',
    'broker.no_active_listings': 'Aún no hay anuncios activos. Añada su primera propiedad.',
    'broker.all_types': 'Todos los tipos',
    'broker.all_cities': 'Todas las ciudades',
    'broker.property_type': 'Tipo de propiedad',
    'broker.city': 'Ciudad',
    'broker.date_range': 'Rango de fechas',
    'broker.last_week': 'Última semana',
    'broker.last_month': 'Último mes',
    'broker.last_quarter': 'Últimos 3 meses',
    'broker.sold_listings_desc': 'Propiedades que ha vendido',
    'broker.no_sold_listings': 'Aún no hay propiedades vendidas',
    'broker.clear_filters': 'Limpiar filtros',
    'broker.selected': 'seleccionado(s)',
    'broker.compare': 'Comparar',
    'broker.mark_as_sold': 'Marcar como vendido',
    'broker.delete_selected': 'Eliminar seleccionados',
    'broker.select_all': 'Seleccionar todo',
    'broker.rejected_listings': 'Anuncios rechazados',
    'broker.rejected_listings_desc': 'Estos anuncios fueron rechazados por moderadores. Puede presentar una apelación.',
    'broker.no_rejected_listings': 'No hay anuncios rechazados',
    'broker.confirm_delete': 'Confirmar eliminación',
    'broker_public.not_found': 'Agente no encontrado',
    'broker_public.not_found_description': 'El perfil del agente que buscas no existe.',
    'broker_public.back_to_search': 'Volver a búsqueda de agentes',
    'broker_public.contact_broker': 'Contactar agente',
    'broker_public.agency': 'Agencia',
    'broker_public.visit_website': 'Visitar sitio web',
    'broker_public.active_listings': 'Anuncios activos',
    'broker_public.sold_properties': 'Propiedades vendidas',
    'broker_public.total_sales': 'Ventas totales',
    'broker_public.avg_days_market': 'Días promedio en mercado',
    'broker_public.no_active_listings': 'No hay anuncios activos en este momento',
    'broker_public.no_sold_properties': 'Aún no hay propiedades vendidas',
    'broker_public.view_all_properties': 'Ver todas las propiedades',
    'broker_public.send_message': 'Enviar mensaje',
    'broker_public.view_agency': 'Ver agencia',
    'broker_public.works_at': 'Trabaja en',
    'broker_public.listings': 'Anuncios',
    'broker_public.sold': 'Vendido',
    'broker_public.reviews': 'Reseñas',
    'broker_public.rating': 'Calificación',
    'broker_public.featured_broker': 'Agente destacado',
    'broker_public.top_performer': 'Mejor rendimiento',
    
    // Toast messages
    'toast.logged_out': 'Sesión cerrada',
    'toast.welcome_back': '¡Vuelve cuando quieras!',
    
    // Recent searches
    'search.properties_label': 'Propiedades',
    'search.from': 'desde',
    'search.up_to': 'hasta',
    
    // Agency Pricing
    'agency_pricing.badge': 'Planes de Agencia',
    'agency_pricing.title': 'Haz crecer tu agencia con el plan correcto',
    'agency_pricing.subtitle': 'Elige el plan perfecto para tu agencia inmobiliaria.',
    'agency_pricing.most_popular': 'Más popular',
    'agency_pricing.per_month': '/mes',
    'agency_pricing.processing': 'Procesando...',
    'agency_pricing.comparison_title': 'Comparación detallada',
    'agency_pricing.feature': 'Característica',
    'agency_pricing.faq_title': 'Preguntas frecuentes',
    'agency_pricing.cta_title': '¿Listo para hacer crecer tu agencia?',
    'agency_pricing.cta_subtitle': 'Únete a miles de agencias inmobiliarias exitosas.',
    'agency_pricing.cta_button': 'Comenzar con Pro',
    'agency_pricing.sign_in_required': 'Inicia sesión para actualizar',
    'agency_pricing.tier.starter': 'Inicial',
    'agency_pricing.tier.starter_desc': 'Perfecto para agencias pequeñas',
    'agency_pricing.tier.starter_cta': 'Comenzar',
    'agency_pricing.tier.pro': 'Pro',
    'agency_pricing.tier.pro_desc': 'Para agencias en crecimiento',
    'agency_pricing.tier.pro_cta': 'Actualizar a Pro',
    'agency_pricing.tier.enterprise': 'Enterprise',
    'agency_pricing.tier.enterprise_desc': 'Para grandes agencias',
    'agency_pricing.tier.enterprise_cta': 'Elegir Enterprise',
    'agency_pricing.feature.brokers_3': 'Hasta 3 corredores',
    'agency_pricing.feature.brokers_10': 'Hasta 10 corredores',
    'agency_pricing.feature.brokers_unlimited': 'Corredores ilimitados',
    'agency_pricing.feature.listings_basic': 'Listados básicos',
    'agency_pricing.feature.listings_advanced': 'Listados avanzados',
    'agency_pricing.feature.listings_premium': 'Listados premium',
    'agency_pricing.feature.messaging': 'Mensajería con clientes',
    'agency_pricing.feature.scheduling': 'Programación de visitas',
    'agency_pricing.feature.analytics': 'Panel de análisis',
    'agency_pricing.feature.analytics_advanced': 'Análisis avanzado',
    'agency_pricing.feature.support': 'Soporte prioritario',
    'agency_pricing.feature.support_247': 'Soporte 24/7',
    'agency_pricing.feature.branding': 'Marca personalizada',
    'agency_pricing.feature.api': 'Acceso API',
    'agency_pricing.comparison.brokers': 'Número de corredores',
    'agency_pricing.comparison.listings': 'Listados de propiedades',
    'agency_pricing.comparison.basic': 'Básico',
    'agency_pricing.comparison.advanced': 'Avanzado',
    'agency_pricing.comparison.premium': 'Premium',
    'agency_pricing.faq.change_plan_q': '¿Puedo cambiar mi plan después?',
    'agency_pricing.faq.change_plan_a': '¡Sí! Puedes cambiar en cualquier momento.',
    'agency_pricing.faq.exceed_limit_q': '¿Qué pasa si excedo el límite?',
    'agency_pricing.faq.exceed_limit_a': 'Deberás actualizar a un plan superior.',
    'agency_pricing.faq.trial_q': '¿Hay prueba gratuita?',
    'agency_pricing.faq.trial_a': 'Garantía de devolución de 14 días.',
    'agency_pricing.faq.cancel_q': '¿Cómo cancelo mi suscripción?',
    'agency_pricing.faq.cancel_a': 'Administra tu suscripción desde el panel.',
    'agency_pricing.view_plans': 'Ver planes y precios',
    
    // Broker Pricing
    'broker_pricing.badge': 'Planes de Corredor',
    'broker_pricing.title': 'Mejora tu cuenta de corredor',
    'broker_pricing.subtitle': 'Elige el plan perfecto para tu negocio inmobiliario. Más anuncios, mejores análisis y soporte prioritario.',
    'broker_pricing.most_popular': 'Más popular',
    'broker_pricing.current_plan': 'Tu plan actual',
    'broker_pricing.your_current_plan': 'Tu plan actual',
    'broker_pricing.free': 'Gratis',
    'broker_pricing.month': 'mes',
    'broker_pricing.per_month': '/mes',
    'broker_pricing.processing': 'Procesando...',
    'broker_pricing.loading': 'Cargando...',
    'broker_pricing.manage_subscription': 'Gestionar suscripción',
    'broker_pricing.cancel_anytime': 'Degrada o cancela en cualquier momento • Se renueva automáticamente mensualmente',
    'broker_pricing.manage_note': 'Degrada o cancela en cualquier momento • Se renueva automáticamente mensualmente',
    'broker_pricing.downgrade': 'Degradar',
    'broker_pricing.upgrade_to_pro': 'Actualizar a Pro',
    'broker_pricing.go_enterprise': 'Ir a Enterprise',
    'broker_pricing.get_started_pro': 'Empezar con Pro',
    'broker_pricing.comparison_title': 'Comparación detallada de características',
    'broker_pricing.feature': 'Característica',
    'broker_pricing.faq_title': 'Preguntas frecuentes',
    'broker_pricing.cta_title': '¿Listo para hacer crecer tu negocio?',
    'broker_pricing.cta_subtitle': 'Únete a miles de corredores exitosos que usan nuestra plataforma para publicar más propiedades y cerrar más tratos.',
    'broker_pricing.cta_button': 'Empezar con Pro',
    'broker_pricing.sign_in_required': 'Por favor inicia sesión para mejorar tu plan',
    'broker_pricing.already_on_plan': 'Ya estás en este plan',
    'broker_pricing.checkout_error': 'Error al iniciar el pago',
    'broker_pricing.portal_error': 'Error al abrir la gestión de suscripción',
    'broker_pricing.tier.normal': 'Normal',
    'broker_pricing.tier.normal_desc': 'Perfecto para corredores individuales que empiezan',
    'broker_pricing.tier.normal_cta': 'Plan actual',
    'broker_pricing.tier.pro': 'Pro',
    'broker_pricing.tier.pro_desc': 'Para corredores en crecimiento con más anuncios',
    'broker_pricing.tier.pro_cta': 'Actualizar a Pro',
    'broker_pricing.tier.enterprise': 'Enterprise',
    'broker_pricing.tier.enterprise_desc': 'Para corredores de alto volumen con necesidades ilimitadas',
    'broker_pricing.tier.enterprise_cta': 'Ir a Enterprise',
    'broker_pricing.feature.listings_5': '5 anuncios por mes',
    'broker_pricing.feature.listings_10': '10 anuncios por mes',
    'broker_pricing.feature.listings_15': '15 anuncios por mes',
    'broker_pricing.feature.basic_features': 'Características básicas de propiedades',
    'broker_pricing.feature.advanced_features': 'Características avanzadas de propiedades',
    'broker_pricing.feature.premium_features': 'Características premium de propiedades',
    'broker_pricing.feature.client_messaging': 'Mensajería con clientes',
    'broker_pricing.feature.viewing_scheduling': 'Programación de visitas',
    'broker_pricing.feature.video_tours_paid': 'Tours de video ($3 por anuncio)',
    'broker_pricing.feature.video_tours_free': 'Tours de video gratis incluidos',
    'broker_pricing.feature.analytics': 'Panel de análisis',
    'broker_pricing.feature.advanced_analytics': 'Análisis avanzado',
    'broker_pricing.feature.priority_support': 'Soporte prioritario',
    'broker_pricing.feature.support_24_7': 'Soporte prioritario 24/7',
    'broker_pricing.feature.featured_listings': 'Anuncios destacados',
    'broker_pricing.feature.extra_listings': 'Opción de anuncios extra',
    'broker_pricing.feature.extra_10': '+$10 por anuncio extra',
    'broker_pricing.feature.messaging': 'Mensajería con clientes',
    'broker_pricing.feature.scheduling': 'Programación de visitas',
    'broker_pricing.feature.video_paid': 'Tours de video ($3 por anuncio)',
    'broker_pricing.feature.video_free': 'Tours de video gratis incluidos',
    'broker_pricing.feature.analytics_advanced': 'Análisis avanzado',
    'broker_pricing.feature.support': 'Soporte prioritario',
    'broker_pricing.feature.support_247': 'Soporte prioritario 24/7',
    'broker_pricing.feature.featured': 'Anuncios destacados',
    'broker_pricing.feature.extra_price': '+$10 por anuncio extra',
    'broker_pricing.table.listings_per_month': 'Anuncios por mes',
    'broker_pricing.table.video_tours': 'Tours de video',
    'broker_pricing.table.extra_listings': 'Anuncios extra',
    'broker_pricing.table.listing_duration': 'Duración del anuncio',
    'broker_pricing.table.client_messaging': 'Mensajería con clientes',
    'broker_pricing.table.analytics_dashboard': 'Panel de análisis',
    'broker_pricing.table.priority_support': 'Soporte prioritario',
    'broker_pricing.table.featured_listings': 'Anuncios destacados',
    'broker_pricing.table.15_extras': '15 + extras',
    'broker_pricing.table.3_per_listing': '$3 por anuncio',
    'broker_pricing.table.10_each': '$10 cada uno',
    'broker_pricing.table.30_days': '30 días',
    'broker_pricing.comparison.listings': 'Anuncios por mes',
    'broker_pricing.comparison.video': 'Tours de video',
    'broker_pricing.comparison.extra': 'Anuncios extra',
    'broker_pricing.comparison.duration': 'Duración del anuncio',
    'broker_pricing.comparison.featured': 'Anuncios destacados',
    'broker_pricing.comparison.30_days': '30 días',
    'broker_pricing.comparison.per_listing': '$3 por anuncio',
    'broker_pricing.comparison.free': 'Gratis',
    'broker_pricing.comparison.each': '$10 cada uno',
    'broker_pricing.comparison.15_plus': '15 + extras',
    'broker_pricing.faq.autorenew_q': '¿Mi suscripción se renueva automáticamente?',
    'broker_pricing.faq.autorenew_a': '¡Sí! Tu suscripción se renueva automáticamente cada mes para que nunca pierdas acceso a tus anuncios. Se te cobrará el mismo día cada mes. Puedes cancelar en cualquier momento desde el portal de gestión de suscripción.',
    'broker_pricing.faq.upgrade_q': '¿Puedo mejorar o degradar en cualquier momento?',
    'broker_pricing.faq.upgrade_a': '¡Absolutamente! Las mejoras se aplican inmediatamente con facturación prorrateada. Las degradaciones se aplican al final de tu período de facturación actual. Haz clic en "Gestionar suscripción" para hacer cambios.',
    'broker_pricing.faq.expire_q': '¿Qué pasa cuando mis anuncios expiran?',
    'broker_pricing.faq.expire_a': 'Los anuncios expiran después de 30 días. Recibirás una notificación por email 3 días antes de la expiración. Puedes renovarlos usando tus espacios mensuales gratis o pagar $10 por anuncio extra (solo Enterprise).',
    'broker_pricing.faq.extra_q': '¿Qué son los "anuncios extra"?',
    'broker_pricing.faq.extra_a': 'Los miembros Enterprise pueden comprar anuncios adicionales a $10 cada uno más allá de sus 15 anuncios mensuales gratis. Ideal para meses de alto volumen.',
    'broker_pricing.faq.cancel_q': '¿Cómo cancelo mi suscripción?',
    'broker_pricing.faq.cancel_a': 'Haz clic en "Gestionar suscripción" para acceder a tu portal de facturación donde puedes cancelar, degradar o actualizar tu método de pago. Las cancelaciones se aplican al final de tu período de facturación.',
    'messaging.conversation_pinned': 'Conversación fijada',
    'messaging.conversation_unpinned': 'Conversación desanclada',
    'messaging.pin_chat': 'Fijar conversación',
    'messaging.unpin_chat': 'Desanclar conversación',
    'messaging.pin_failed': 'Error al fijar conversación',
    'messaging.unpin_failed': 'Error al desanclar conversación',
    'messaging.archive': 'Archivar',
    'messaging.unarchive': 'Restaurar',
    'messaging.show_archived': 'Archivadas',
    'messaging.show_active': 'Mostrar activas',
    'messaging.conversation_archived': 'Conversación archivada',
    'messaging.conversation_unarchived': 'Conversación restaurada',
    'messaging.archive_failed': 'Error al archivar conversación',
    'messaging.unarchive_failed': 'Error al restaurar conversación',
    
    // Hero/News Section
    'hero.find_dream_home': 'Encuentra tu casa soñada en Irak',
    'hero.explore_description': 'Explora miles de casas en venta y precios finales de todo Irak. Obtén valoraciones, lee noticias inmobiliarias y contacta con agentes.',
    'hero.badge': 'La plataforma inmobiliaria líder de Irak',
  },
  it: {
    // Header
    'nav.search': 'Cerca proprietà',
    'nav.search_agents': 'Cerca agenti',
    'nav.sell': 'Vendi proprietà',
    'nav.news': 'Notizie',
    'nav.build': 'Costruisci casa',
    'nav.agencies': 'Agenzie',
    'nav.buy': 'Acquista',
    'nav.valuation': 'Valutazione',
    'nav.more': 'Altro',
    'nav.saved': 'Salvato',
    'nav.admin': 'Admin',
    'nav.agency': 'Agenzia',
    'nav.agency_dashboard': 'Pannello agenzia',
    'nav.location_manager': 'Gestione posizioni',
    'nav.map_configuration': 'Configurazione mappa',
    'nav.hello': 'Ciao',
    'nav.menu': 'Menu',
    'nav.manage_news': 'Gestisci notizie',
    'nav.all_properties': 'Tutte le proprietà',
    'nav.new_production': 'Nuove costruzioni',
    
    // Cookie Policy
    'cookie.title': 'Politica sui cookie',
    'cookie.last_updated': 'Ultimo aggiornamento:',
    'cookie.section1.title': 'Sezione 1 - Disposizioni generali riguardanti l\'implementazione delle tecnologie di tracciamento',
    'cookie.section1.p1': 'Ploqs AB, di seguito denominato il "Fornitore di servizi", insieme ai partner e subappaltatori incaricati dal Fornitore di servizi, implementa e utilizza varie forme di meccanismi di tracciamento digitale, inclusi ma non limitati a cookie, web beacon, pixel tag e altre soluzioni tecnologiche simili (collettivamente denominati "Tecnologie di tracciamento") sulle piattaforme digitali, siti web e applicazioni mobili del Fornitore di servizi (collettivamente denominati "Canali digitali del Fornitore di servizi"). Questa politica per la gestione delle Tecnologie di tracciamento specifica gli scopi per i quali e i metodi con i quali il Fornitore di servizi applica dette tecnologie.',
    'cookie.section1.p2': 'Le Tecnologie di tracciamento consistono principalmente in file di dati testuali più piccoli che vengono depositati sull\'apparecchiatura terminale dell\'utente o sul programma del browser web allo scopo di aggregare informazioni riguardanti l\'interazione dell\'utente con le piattaforme digitali e le applicazioni mobili e la navigazione su di esse.',
    'cookie.section1.h1': 'Trattamento dei dati personali e considerazioni sulla protezione dei dati',
    'cookie.section1.p3': 'Le informazioni raccolte attraverso l\'applicazione delle Tecnologie di tracciamento costituiscono nella maggior parte dei casi dati che, direttamente o indirettamente e in combinazione con altri dati disponibili, possono potenzialmente essere utilizzati per identificare individui, inclusi ma non limitati a indirizzi IP, identificatori univoci di cookie, identificatori pubblicitari (IDFA), identificatori di dispositivi e vari metadati che consentono la differenziazione di apparecchiature terminali specifiche.',
    'cookie.section1.h2': 'Scelte dell\'utente',
    'cookie.section1.p4': 'L\'utente ha il diritto di rivedere i consensi precedentemente dati in qualsiasi momento navigando verso le opzioni di gestione del consenso fornite tramite l\'interfaccia digitale del Fornitore di servizi.',
    'cookie.section1.h3': 'Persistenza e periodi di archiviazione delle Tecnologie di tracciamento',
    'cookie.section1.p5': 'Le Tecnologie di tracciamento mostrano persistenza variabile a seconda dello scopo previsto e della funzionalità di ciascuna tecnologia. Le Tecnologie di tracciamento basate sulla sessione vengono avviate al momento della connessione dell\'utente alla piattaforma digitale e vengono terminate al più tardi alla chiusura dell\'istanza del browser dell\'utente. Le Tecnologie di tracciamento persistenti vengono conservate nel browser dell\'utente per un periodo di tempo predeterminato che supera le singole sessioni.',
    'cookie.section2.title': 'Sezione 2 - Tecnologie di tracciamento obbligatorie per la fornitura del servizio',
    'cookie.section2.p1': 'Le Tecnologie di tracciamento obbligatorie si riferiscono a quelle implementazioni tecnologiche che sono necessarie affinché il Fornitore di servizi possa fornire funzionalità di base ed essenziali all\'utente, ad esempio allo scopo di mantenere informazioni riguardanti i consensi precedentemente dati dall\'utente riguardo alle Tecnologie di tracciamento, per consentire la differenziazione tra sessioni utente distinte attraverso identificatori anonimizzati, per facilitare la funzionalità cartografica, per consentire la riproduzione di materiale audiovisivo negli annunci immobiliari caricati dagli agenti immobiliari, per preservare le preferenze di ricerca salvate dell\'utente, per mantenere la sicurezza dei servizi, prevenire l\'uso improprio dei servizi e per garantire prestazioni di servizio continue e ottimali.',
    'cookie.section2.legal': 'Base giuridica per il trattamento:',
    'cookie.section2.legal_value': 'Interesse legittimo ai sensi dell\'Articolo 6.1(f) del GDPR',
    'cookie.section2.storage': 'Periodo di archiviazione:',
    'cookie.section2.storage_value': 'Fino a ventiquattro (24) mesi per le Tecnologie di tracciamento persistenti; le Tecnologie di tracciamento basate sulla sessione vengono terminate immediatamente alla chiusura dell\'istanza del browser.',
    'cookie.section3.title': 'Sezione 3 - Tecnologie di tracciamento per scopi analitici',
    'cookie.section3.p1': 'Il Fornitore di servizi attribuisce notevole importanza alla capacità di quantificare la frequenza delle visite, registrare i dati di interazione sulla piattaforma digitale e nelle applicazioni mobili, e analizzare in altro modo come i servizi digitali del Fornitore di servizi vengono utilizzati dagli utenti. A condizione che l\'utente abbia dato il consenso richiesto, il Fornitore di servizi implementa Tecnologie di tracciamento, escludendo i dati personali diretti, allo scopo di analizzare e sviluppare ulteriormente i servizi digitali del Fornitore di servizi e di offrire continuamente all\'utente un\'esperienza utente ottimizzata, mantenere le prestazioni del servizio e produrre analisi di gruppi target e compilazioni statistiche riguardanti l\'utilizzo della piattaforma.',
    'cookie.section3.legal_value': 'Consenso ai sensi dell\'Articolo 6.1(a) del GDPR',
    'cookie.section3.storage_value': 'Fino a ventisei (26) mesi',
    'cookie.section4.title': 'Sezione 4 - Tecnologie di tracciamento per scopi di personalizzazione',
    'cookie.section4.p1': 'Le Tecnologie di tracciamento implementate per scopi di personalizzazione mirano ad adattare l\'esperienza dell\'utente sulle piattaforme digitali del Fornitore di servizi fornendo contenuti con maggiore rilevanza personale per l\'utente individuale. Questo adattamento si basa, ad esempio, sul comportamento di ricerca e sui modelli di interazione dell\'utente. Nel caso in cui l\'utente disponga di un account utente registrato presso il Fornitore di servizi, questi dati comportamentali vengono combinati con le informazioni che l\'utente fornisce volontariamente nell\'ambito del servizio, ad esempio informazioni riguardanti le condizioni abitative dell\'utente, mediante le quali vengono effettuati adattamenti dei contenuti sulla base del profilo aggregato dell\'utente.',
    'cookie.section5.title': 'Sezione 5 - Tecnologie di tracciamento per scopi di marketing',
    'cookie.section5.p1': 'Il Fornitore di servizi implementa Tecnologie di tracciamento per scopi di marketing, a condizione che l\'utente abbia dato il consenso richiesto, allo scopo di consentire l\'esposizione di contenuti pubblicitari pertinenti e annunci immobiliari con maggiore rilevanza per l\'utente sulla base di un profilo pubblicitario costruito.',
    'cookie.section5.p2': 'Attraverso l\'implementazione delle Tecnologie di tracciamento, il Fornitore di servizi garantisce inoltre che le esposizioni pubblicitarie siano limitate a un numero predefinito di occasioni per utente e che l\'efficacia pubblicitaria sia misurata e analizzata. I contenuti pubblicitari del Fornitore di servizi possono anche essere esposti su piattaforme digitali esterne.',
    'cookie.section5.p3': 'Si noti che nel caso in cui l\'utente scelga di disattivare le Tecnologie di tracciamento per scopi di marketing, i contenuti pubblicitari continueranno ad essere esposti all\'utente sulle piattaforme digitali del Fornitore di servizi, tuttavia senza personalizzazione.',
    'cookie.section5.storage_value': 'Fino a ventiquattro (24) mesi',
    'cookie.section6.title': 'Sezione 6 - Trasferimenti internazionali di dati verso paesi terzi',
    'cookie.section6.p1': 'Nello strumento di gestione del consenso del Fornitore di servizi, vengono fornite informazioni riguardanti quali Tecnologie di tracciamento e partner trattano i dati personali dell\'utente all\'interno di, o trasferiscono i dati personali dell\'utente verso, giurisdizioni al di fuori dell\'Unione Europea e dello Spazio Economico Europeo (i cosiddetti "paesi terzi").',
    'cookie.section7.title': 'Sezione 7 - Istruzioni per la rimozione delle Tecnologie di tracciamento',
    'cookie.section7.p1': 'L\'utente ha la possibilità di rimuovere le Tecnologie di tracciamento dalla propria apparecchiatura terminale o dispositivo mobile e configurare le impostazioni per l\'archiviazione locale dei dati tramite l\'interfaccia delle impostazioni del browser. L\'utente può scegliere di disattivare le Tecnologie di tracciamento o ricevere notifiche ogni volta che viene avviata una nuova Tecnologia di tracciamento sull\'apparecchiatura terminale dell\'utente.',
    'cookie.section7.p2': 'Per istruzioni dettagliate riguardanti la gestione e la rimozione delle Tecnologie di tracciamento, si fa riferimento all\'opzione di aiuto nel browser dell\'utente o ai collegamenti ipertestuali di seguito per ciascuna applicazione del browser:',
    
    // Auth
    'auth.loading': 'Caricamento...',
    'auth.login': 'Accedi',
    'auth.create_account': 'Crea account',
    'auth.login_subtitle': 'Accedi scegliendo una delle opzioni qui sotto.',
    'auth.signup_subtitle': 'Crea un nuovo account per iniziare.',
    'auth.continue_apple': 'Continua con Apple',
    'auth.continue_google': 'Continua con Google',
    'auth.or': 'o',
    'auth.email': 'Email',
    'auth.email_placeholder': 'Inserisci email',
    'auth.password': 'Password',
    'auth.password_placeholder': 'Inserisci password',
    'auth.account_type': 'Tipo di account',
    'auth.user_role': 'Utente - Voglio comprare/affittare una proprietà',
    'auth.agent_role': 'Agente - Voglio vendere/affittare proprietà',
    'auth.continue': 'Continua',
    'auth.no_account': 'Non hai un account? Crea account',
    'auth.have_account': 'Hai già un account? Accedi',
    'auth.login_failed': 'Accesso fallito',
    'auth.invalid_credentials': 'Email o password non validi',
    'auth.welcome_back': 'Bentornato!',
    'auth.logged_in': 'Hai effettuato l\'accesso.',
    'auth.signup_failed': 'Registrazione fallita',
    'auth.account_created': 'Account creato!',
    'auth.check_email': 'Controlla la tua email per confermare il tuo account.',
    'auth.error': 'Si è verificato un errore',
    'auth.try_again': 'Per favore riprova più tardi.',
    'auth.broker_interest_title': 'Sei un agente immobiliare?',
    'auth.broker_interest_description': 'Se sei interessato a pubblicare proprietà su Aqarnet, inviaci una dichiarazione di interesse e ti ricontatteremo.',
    'auth.broker_contact_us': 'Contattaci',
    'auth.broker_email_subject': 'Dichiarazione di interesse agente',
    'auth.broker_email_body': 'Ciao,\n\nSono interessato a registrarmi come agente su Aqarnet.\n\nNome:\nNome agenzia:\nTelefono:\nCittà:\n\nGrazie!',
    'auth.first_name': 'Nome',
    'auth.last_name': 'Cognome',
    'auth.first_name_placeholder': 'Inserisci il tuo nome',
    'auth.last_name_placeholder': 'Inserisci il tuo cognome',
    'auth.display_name': 'Nome visualizzato',
    'auth.display_name_placeholder': 'Scegli un nome visualizzato',
    'auth.display_name_hint': 'Questo è l\'unico nome che gli altri vedranno',
    'auth.date_of_birth': 'Data di nascita',
    'auth.fill_all_fields': 'Compila tutti i campi obbligatori',
    'auth.must_be_18': 'Devi avere almeno 18 anni per registrarti',
    'auth.must_be_18_hint': 'Devi avere almeno 18 anni',
    'verification.verify_identity': 'Verifica identità',
    'verification.verified': 'Verificato',
    'verification.title': 'Verifica dell\'identità',
    'verification.description': 'Carica un documento ufficiale per verificare la tua identità e ottenere un badge verificato.',
    'verification.document_type': 'Tipo di documento',
    'verification.doc_id_card': 'Carta d\'identità',
    'verification.doc_passport': 'Passaporto',
    'verification.doc_drivers_license': 'Patente di guida',
    'verification.upload_document': 'Carica documento',
    'verification.click_to_upload': 'Clicca per caricare o trascina e rilascia',
    'verification.file_requirements': 'JPG, PNG, WEBP o PDF (max 10MB)',
    'verification.submit': 'Invia per verifica',
    'verification.uploading': 'Caricamento...',
    'verification.submitted': 'Verifica inviata',
    'verification.submitted_desc': 'La tua richiesta di verifica è stata inviata. La esamineremo a breve.',
    'verification.pending_message': 'La tua verifica è in corso di revisione. Di solito richiede 1-2 giorni lavorativi.',
    'verification.rejected_message': 'La tua verifica precedente è stata rifiutata. Invia un nuovo documento.',
    'verification.status_approved': 'Approvato',
    'verification.status_pending': 'In attesa',
    'verification.status_rejected': 'Rifiutato',
    'verification.invalid_file_type': 'Tipo di file non valido',
    'verification.allowed_types': 'Carica un file JPG, PNG, WEBP o PDF',
    'verification.file_too_large': 'File troppo grande',
    'verification.max_size': 'La dimensione massima del file è 10MB',
    'verification.error': 'Errore di verifica',
    'verification.admin_title': 'Verifiche dell\'identità',
    'verification.admin_description': 'Esamina e approva le richieste di verifica dell\'identità',
    'verification.no_requests': 'Nessuna richiesta di verifica',
    'verification.view': 'Esamina',
    'verification.review_title': 'Esamina verifica',
    'verification.review_notes': 'Note di revisione',
    'verification.review_notes_placeholder': 'Aggiungi note sulla tua decisione (opzionale)',
    'verification.approve': 'Approva',
    'verification.reject': 'Rifiuta',
    'verification.approved': 'Verifica approvata',
    'verification.rejected': 'Verifica rifiutata',
    'verification.review_complete': 'La verifica è stata elaborata.',
    
    // Favorites Page
    'favorites.title': 'I miei preferiti',
    'favorites.saved_properties': 'Proprietà salvate',
    'favorites.login_required': 'Accedi per vedere i tuoi preferiti',
    'favorites.login_description': 'Salva le proprietà che ti piacciono per trovarle più facilmente.',
    'favorites.loading': 'Caricamento preferiti...',
    'favorites.no_favorites': 'Ancora nessun preferito',
    'favorites.start_saving': 'Inizia a salvare proprietà cliccando sul cuore negli annunci.',
    'favorites.search_properties': 'Cerca proprietà',
    
    // Profile Page
    'profile.title': 'Il mio profilo',
    'profile.subtitle': 'Gestisci informazioni personali e impostazioni',
    'profile.loading': 'Caricamento profilo...',
    'profile.fetch_error': 'Errore nel recupero del profilo',
    'profile.fetch_error_description': 'Non è stato possibile recuperare le informazioni del profilo.',
    'profile.unnamed_user': 'Utente senza nome',
    'profile.personal_info': 'Informazioni personali',
    'profile.update_info': 'Aggiorna le tue informazioni personali',
    'profile.email': 'Email',
    'profile.email_readonly': 'L\'email non può essere modificata',
    'profile.display_name': 'Nome visualizzato',
    'profile.display_name_placeholder': 'Il tuo nome',
    'profile.phone': 'Numero di telefono',
    'profile.phone_placeholder': '333 1234567',
    'profile.save_changes': 'Salva modifiche',
    'profile.saving': 'Salvataggio...',
    'profile.reset': 'Reimposta',
    'profile.updated': 'Profilo aggiornato',
    'profile.updated_description': 'Il tuo profilo è stato salvato.',
    'profile.update_error': 'Errore di aggiornamento',
    'profile.update_error_description': 'Non è stato possibile salvare le modifiche.',
    'profile.active_listings': 'Annunci attivi',
    'profile.active_listings_description': 'I tuoi annunci immobiliari pubblicati in vendita',
    'profile.view_all': 'Vedi tutto',
    'profile.loading_listings': 'Caricamento annunci...',
    'profile.no_active_listings': 'Ancora nessun annuncio attivo',
    'profile.more_active_listings': 'e altri {count} annunci attivi',
    'profile.sold_properties': 'Proprietà vendute',
    'profile.sold_properties_description': 'Le tue proprietà vendute',
    'profile.no_sold_properties': 'Ancora nessuna proprietà venduta',
    'profile.more_sold_properties': 'e altre {count} proprietà vendute',
    'profile.listing_fetch_error': 'Errore nel recupero degli annunci',
    'profile.listing_fetch_error_description': 'Non è stato possibile recuperare i tuoi annunci.',
    'profile.account_settings': 'Impostazioni account',
    'profile.account_settings_description': 'Gestisci account e sicurezza',
    'profile.change_password': 'Cambia password',
    'profile.change_password_description': 'Aggiorna la password per un accesso più sicuro',
    'profile.change': 'Cambia',
    'profile.two_factor': 'Autenticazione a due fattori',
    'profile.two_factor_description': 'Aggiungi sicurezza extra al tuo account',
    'profile.enable': 'Attiva',
    'profile.delete_account': 'Elimina account',
    'profile.delete_account_description': 'Elimina permanentemente account e tutti i dati',
    'profile.delete': 'Elimina',
    'profile.logout': 'Esci',
    
    // Not Found Page
    'notfound.title': '404',
    'notfound.message': 'Spiacenti! Pagina non trovata',
    'notfound.back_home': 'Torna alla home',
    
    // Property Detail Page
    'property.back_to_listings': 'Torna agli annunci',
    'property.not_found': 'Proprietà non trovata',
    'property.link_copied': 'Link copiato',
    'property.link_copied_description': 'Link della proprietà copiato',
    'property.description': 'Descrizione',
    'property.features': 'Caratteristiche',
    'property.details': 'Dettagli proprietà',
    'property.rooms_label': 'Stanze',
    'property.size_label': 'Dimensione',
    'property.plot_size_label': 'Dimensione terreno',
    'property.monthly_fee_label': 'Canone mensile',
    'property.contact_agent': 'Contatta agente',
    'property.send_message': 'Invia messaggio',
    'property.schedule_viewing': 'Prenota visita',
    'property.section.details': 'Dettagli',
    'property.section.amenities': 'Servizi',
    'property.section.overview': 'Panoramica',
    'property.section.payment_plan': 'Piano di pagamento',
    'property.section.floor_plans': 'Planimetrie',
    'property.section.view_map': 'Visualizza mappa',
    'property.copy_link': 'Copia link',
    'property.share': 'Condividi',
    'property.share_text': 'Dai un\'occhiata a questa proprietà',
    'property.posted_on': 'Pubblicato il',
    'property.type': 'Tipo',
    'property.status': 'Stato',
    'property.available': 'Disponibile',
    'property.no_amenities': 'Nessun servizio elencato per questa proprietà',
    'property.no_description': 'Nessuna descrizione disponibile',
    'property.location': 'Posizione',
    'property.payment_plan_info': 'Contatta l\'agente per i dettagli del piano di pagamento',
    'property.contact_for_payment_plan': 'Contatta per piano di pagamento',
    'property.no_floor_plans': 'Nessuna planimetria disponibile',
    'property.request_floor_plans': 'Richiedi planimetrie',
    'property.no_map_location': 'Posizione non disponibile sulla mappa',
    
    // News Page
    'news.subtitle': 'Rimani aggiornato con le ultime notizie immobiliari, analisi di mercato e guide',
    'news.no_articles': 'Ancora nessun articolo di notizie',
    'news.check_back': 'Torna presto per le ultime notizie immobiliari e analisi di mercato.',
    'news.search_placeholder': 'Cerca articoli...',
    'news.showing_filtered': 'Visualizzazione di {shown} su {total} articoli in questa pagina',
    'news.clear_filters': 'Cancella filtri',
    'news.no_matching': 'Nessun articolo corrispondente',
    'news.adjust_filters': 'Prova a modificare la ricerca o i filtri per trovare ciò che cerchi.',
    'news.publisher': 'Editore',
    'news.page_info': 'Pagina {current} di {total} • {count} articoli in totale',
    'news.category.all': 'Tutto',
    'news.category.market_news': 'Notizie di mercato',
    'news.category.property_guides': 'Guide immobiliari',
    'news.category.investment_tips': 'Consigli di investimento',
    'news.category.industry_updates': 'Aggiornamenti del settore',
    'news.category.announcements': 'Annunci',
    'news.category.expert_advice': 'Consigli degli esperti',
    
    // Filters/Sorting
    'filters.any': 'Tutti',
    'filters.newest_first': 'Più recenti prima',
    'filters.price_low_high': 'Prezzo: dal più basso',
    'filters.price_high_low': 'Prezzo: dal più alto',
    'filters.size': 'Dimensione',
    'filters.searching_for': 'Cercando',
    'filters.search_areas': 'Cerca aree, città...',
    
    // Common
    'common.cancel': 'Annulla',
    'common.save': 'Salva',
    'common.saving': 'Salvataggio...',
    'common.send': 'Invia',
    'common.sending': 'Invio...',
    'common.submit': 'Invia',
    'common.close': 'Chiudi',
    'common.error': 'Errore',
    'common.success': 'Successo',
    'common.loading': 'Caricamento...',
    'common.name': 'Nome',
    'common.email': 'Email',
    'common.phone': 'Telefono',
    'common.message': 'Messaggio',
    'common.required': 'Obbligatorio',
    'common.optional': 'Opzionale',
    'common.delete': 'Elimina',
    'common.free': 'Gratuito',
    
    // Leaderboard
    'leaderboard': 'Classifica',
    'leaderboardDescription': 'Scopri le migliori agenzie e agenti classificati per visualizzazioni e vendite',
    'topAgencies': 'Migliori Agenzie',
    'topBrokers': 'Migliori Agenti',
    'topAgenciesDescription': 'Agenzie classificate per visualizzazioni immobili e performance di vendita',
    'topBrokersDescription': 'Agenti classificati per visualizzazioni immobili e performance di vendita',
    'allRegions': 'Tutte le regioni',
    'noAgenciesFound': 'Nessuna agenzia trovata',
    'noBrokersFound': 'Nessun agente trovato',
    'brokers': 'agenti',
    'sold': 'venduto',
    'active': 'attivo',
    'views': 'visualizzazioni',
    
    // Dialogs - Contact Agent
    'dialog.contact_agent': 'Contatta agente',
    'dialog.contact_agent_btn': 'Contatta agente',
    'dialog.contact_message_placeholder': 'Ciao! Sono interessato alla proprietà in {address} e vorrei saperne di più. Per favore, contattatemi quando avete tempo.',
    'dialog.contact_sending': 'Invio...',
    'dialog.contact_send': 'Invia messaggio',
    'dialog.contact_note': 'La tua richiesta verrà inviata direttamente all\'agente responsabile che ti contatterà.',
    
    // Dialogs - Schedule Viewing
    'dialog.schedule_viewing': 'Prenota visita',
    'dialog.schedule_viewing_btn': 'Prenota visita',
    'dialog.preferred_time': 'Orario di visita preferito',
    'dialog.preferred_time_note': 'Seleziona data e ora per la visita. L\'agente confermerà la disponibilità.',
    'dialog.special_requests': 'Richieste speciali',
    'dialog.special_requests_placeholder': 'Eventuali richieste speciali o domande...',
    'dialog.schedule_booking': 'Prenotazione in corso...',
    'dialog.schedule_book': 'Prenota visita',
    'dialog.schedule_note': 'La tua richiesta verrà inviata all\'agente che confermerà l\'orario via email.',
    
    // Dialogs - Save Search
    'dialog.save_search': 'Salva ricerca',
    'dialog.save_search_btn': 'Salva ricerca',
    'dialog.search_name': 'Nome della ricerca',
    'dialog.search_name_placeholder': 'es. Appartamento a Roma',
    'dialog.city_municipality': 'Città/Comune',
    'dialog.min_rooms': 'Min. stanze',
    'dialog.min_size': 'Min. dimensione (m²)',
    'dialog.property_type': 'Tipo di proprietà',
    'dialog.email_alerts': 'Notifiche email quando nuove proprietà corrispondono',
    'dialog.saving_search': 'Salvataggio...',
    
    // Dialogs - Mark as Sold
    'dialog.mark_sold': 'Segna come venduto',
    'dialog.mark_sold_description': 'Inserisci il prezzo di vendita finale per questa proprietà. Questo sposterà l\'annuncio alle proprietà vendute.',
    'dialog.final_sale_price': 'Prezzo di vendita finale (USD)',
    'dialog.listed_price': 'Prezzo di listino',
    'dialog.property_details': 'Dettagli proprietà',
    'dialog.enter_valid_price': 'Per favore, inserisci un prezzo di vendita valido',
    'dialog.enter_price_placeholder': 'Inserisci il prezzo di vendita',
    'dialog.marked_sold_success': 'Proprietà contrassegnata come venduta con successo',
    'dialog.mark_sold_error': 'Impossibile contrassegnare la proprietà come venduta',
    
    // Dialogs - Add Property
    'dialog.add_property': 'Aggiungi nuova proprietà',
    'dialog.hide_preview': 'Nascondi anteprima',
    'dialog.show_preview': 'Mostra anteprima',
    'dialog.selected_images': 'Immagini selezionate',
    'dialog.uploaded_images': 'Immagini caricate',
    'dialog.click_primary': 'Clicca su un\'immagine per impostarla come principale',
    'dialog.invalid_format': 'Formato file non valido',
    'dialog.file_too_large': 'File troppo grande',
    'dialog.upload_failed': 'Caricamento fallito',
    'dialog.images_required': 'Immagini richieste',
    'dialog.please_upload': 'Per favore, carica almeno un\'immagine della proprietà',
    'dialog.form_validation_failed': 'Validazione del modulo fallita',
    
    // Properties for Sale Page
    'propertiesForSale.filters.searchWithin': 'Cerca in',
    'propertiesForSale.filters.km': 'km',
    'propertiesForSale.filters.propertyType': 'Tipo di proprietà',
    'propertiesForSale.filters.rooms': 'Stanze',
    'propertiesForSale.filters.bathrooms': 'Bagni',
    'propertiesForSale.filters.min': 'Min',
    'propertiesForSale.filters.max': 'Max',
    'propertiesForSale.filters.size': 'Dimensione',
    'propertiesForSale.filters.price': 'Prezzo',
    'propertiesForSale.filters.searchResults': 'Risultati della ricerca',
    'propertiesForSale.filters.searchPlaceholder': 'Cerca zone, città...',
    'propertiesForSale.filters.searchingFor': 'Cercando:',
    'propertiesForSale.filters.any': 'Qualsiasi',
    'propertiesForSale.filters.keywords': 'Parole chiave',
    'propertiesForSale.filters.showResults': 'Mostra risultati',
    'propertiesForSale.filters.clearFilters': 'Cancella',
    'propertiesForSale.filters.realEstateSituation': 'Situazione immobiliare',
    'propertiesForSale.filters.situation.tapu': 'Tapu',
    'propertiesForSale.filters.situation.landContract': 'Contratto fondiario',
    'propertiesForSale.filters.situation.noDocuments': 'Nessun documento',
    'propertiesForSale.filters.situation.investment': 'Investimento',
    'propertiesForSale.filters.situation.musataha': 'Musataha',
    'propertiesForSale.filters.situation.card': 'Carta',
    'propertiesForSale.filters.situation.agriculturalTapu': 'Tapu agricolo',
    'propertiesForSale.sort.newest': 'Più recenti',
    'propertiesForSale.sort.price_low': 'Prezzo: crescente',
    'propertiesForSale.sort.price_high': 'Prezzo: decrescente',
    'propertiesForSale.sort.size': 'Dimensione',
    'propertiesForSale.noResults': 'Nessuna proprietà trovata',
    'propertiesForSale.tryAdjustingFilters': 'Prova a modificare i filtri o cerca in un altra zona',
    
    // Search Section
    'search.for_sale': 'In vendita',
    'search.for_rent': 'In affitto',
    'search.sold_prices': 'Prezzi venduti',
    'search.search_agents': 'Cerca agenti',
    'search.area_label': 'Zona',
    'search.area_placeholder': 'Inserisci zona o indirizzo',
    'search.area_examples': 'Esempio: Baghdad, Bassora, Erbil, Najaf, Karbala',
    'search.expand_area': 'Espandi zona di',
    'search.property_type': 'Tipo di proprietà',
    'search.show_filters': 'Mostra filtri',
    'search.find_properties': 'Trova proprietà in vendita',
    'search.min_rooms': 'Min. stanze',
    'search.min_area': 'Min. superficie',
    'search.max_price': 'Prezzo max.',
    'search.all': 'Tutti',
    'search.no_max': 'Nessun max.',
    'search.new_construction': 'Nuova costruzione',
    'search.show_new_construction': 'Mostra nuova costruzione',
    'search.only_new_construction': 'Solo nuova costruzione',
    'search.exclude_new_construction': 'Escludi nuova costruzione',
    'search.keywords': 'Parole chiave',
    'search.keywords_placeholder': 'Piscina, camino, ecc.',
    'search.more_filters_note': 'Altri filtri disponibili nei risultati di ricerca.',
    'search.recent_searches': 'Ricerche recenti',
    'search.hide_history': 'Nascondi cronologia',
    'search.clear_history': 'Cancella cronologia',
    
    'property.all_types': 'Tutti i tipi',
    'property.villas': 'Ville',
    'property.townhouses': 'Case a schiera',
    'property.apartments': 'Appartamenti',
    'property.business': 'Immobili commerciali',
    'property.land': 'Terreni',
    'property.farms': 'Fattorie/Boschi',
    'property.other': 'Altri',

    // Property Types (Select)
    'propertyTypes.allTypes': 'Tutti i tipi',
    'propertyTypes.villa': 'Ville',
    'propertyTypes.apartment': 'Appartamenti',
    'propertyTypes.townhouse': 'Case a schiera',
    'propertyTypes.business': 'Immobili commerciali',
    'propertyTypes.plot': 'Terreni',
    'propertyTypes.farm': 'Fattorie/Boschi',
    'propertyTypes.other': 'Altri',
    
    // Listings
    'listings.latest_for_sale': 'Ultimi in vendita',
    'listings.properties_found': 'proprietà trovate',
    'listings.add_property': 'Aggiungi proprietà',
    'listings.all_news': 'Tutte le notizie',
    'listings.edit': 'Modifica',
    'listings.edit_property': 'Modifica proprietà',
    'listings.monthly_fee': 'Canone mensile',
    'listings.open_floor_plan': 'Pianta aperta',
    'listings.large_balcony': 'Balcone grande',
    'listings.family_friendly': 'Adatto alle famiglie',
    'listings.photos': 'foto',
    'listings.per_month': 'mese',
    'loading.properties': 'Caricamento proprietà...',
    'empty.no_properties': 'Nessuna proprietà disponibile',
    'empty.check_back': 'Torna più tardi per nuovi annunci.',
    
    // Property card
    'property.sold': 'VENDUTO',
    'property.photos': 'foto',
    'property.rooms_short': 'stanze',
    'property.plot': 'terreno',
    'property.per_month': '/mese',
    'property.delete_title': 'Eliminare proprietà?',
    'property.delete_confirm': 'Sei sicuro di voler eliminare {address}? Questa azione non può essere annullata.',
    'property.deleted': 'Proprietà eliminata',
    'property.deleted_desc': 'La proprietà è stata eliminata',
    'property.delete_error': 'Impossibile eliminare la proprietà',
    
    // Saved Searches
    'loading.saved_searches': 'Caricamento ricerche salvate...',
    'saved_searches.title': 'Ricerche salvate',
    'saved_searches.empty': 'Ancora nessuna ricerca salvata',
    'saved_searches.description': 'Salva una ricerca per ricevere notifiche quando vengono aggiunte proprietà corrispondenti',
    'saved_searches.created': 'Creata',
    'saved_searches.alerts_on': 'Notifiche attive',
    'saved_searches.alerts_off': 'Notifiche disattivate',
    'saved_searches.email_enabled': 'Notifiche email attive',
    'saved_searches.email_disabled': 'Notifiche email disattivate',
    'saved_searches.settings_updated': 'Impostazione aggiornata',
    'saved_searches.removed': 'Ricerca eliminata',
    'saved_searches.removed_desc': 'La ricerca salvata è stata eliminata',
    'saved_searches.error': 'Errore',
    'saved_searches.update_error': 'Impossibile aggiornare l\'impostazione',
    'saved_searches.delete_error': 'Impossibile eliminare la ricerca',
    'saved_searches.all_properties': 'Tutte le proprietà',
    
    // Rental properties
    'rental.for_sale': 'In vendita',
    'rental.for_rent': 'In affitto',
    'rental.listing_type': 'Tipo di annuncio',
    'rental.rental_details': 'Dettagli affitto',
    'rental.rental_period': 'Periodo di affitto',
    'rental.rental_period_hint': 'Frequenza di pagamento dell\'affitto',
    'rental.daily': 'Giornaliero',
    'rental.weekly': 'Settimanale',
    'rental.monthly': 'Mensile',
    'rental.yearly': 'Annuale',
    'rental.per_daily': '/giorno',
    'rental.per_weekly': '/settimana',
    'rental.per_monthly': '/mese',
    'rental.per_yearly': '/anno',
    'rental.deposit_amount': 'Importo deposito (USD)',
    'rental.minimum_lease': 'Affitto minimo (mesi)',
    'rental.months': 'mesi',
    'rental.available_from': 'Disponibile da',
    'rental.furnished': 'Arredato',
    'rental.utilities_included': 'Utenze incluse',
    'rental.pets_allowed': 'Animali ammessi',
    
    // Admin Pages
    'admin.loading': 'Caricamento...',
    'admin.access_denied': 'Accesso negato. Privilegi di amministratore richiesti.',
    
    // Admin Notification Settings
    'admin.notifications.title': 'Impostazioni notifiche',
    'admin.notifications.description': 'Gestisci le impostazioni delle notifiche per tutti i tipi di utenti. Attiva, disattiva o nascondi secondo necessità.',
    'admin.notifications.fetch_error': 'Impossibile recuperare le impostazioni delle notifiche',
    'admin.notifications.update_success': 'Impostazione notifica aggiornata',
    'admin.notifications.update_error': 'Impossibile aggiornare l\'impostazione della notifica',
    'admin.notifications.role_description': 'Gestisci notifiche per utenti {role}',
    'admin.notifications.hidden': 'Nascosto',
    'admin.notifications.disabled': 'Disabilitato',
    'admin.notifications.active': 'Attivo',
    'admin.notifications.hide': 'Nascondi',
    'admin.notifications.roles.user': 'Utenti',
    'admin.notifications.roles.maklare': 'Agenti',
    'admin.notifications.roles.agency': 'Agenzie',
    'admin.notifications.roles.admin': 'Amministratori',
    'admin.notifications.roles.publisher': 'Editori',
    'admin.notifications.types.new_viewing_request': 'Nuova richiesta di visita',
    'admin.notifications.types.new_client_message': 'Nuovo messaggio cliente',
    'admin.notifications.types.property_alerts': 'Avvisi proprietà',
    'admin.notifications.types.weekly_summary': 'Riepilogo settimanale',
    'admin.notifications.types.broker_property_added': 'Agente ha aggiunto proprietà',
    'admin.notifications.types.broker_viewing_request': 'Richiesta di visita dell\'agente',
    'admin.notifications.types.broker_client_message': 'Messaggio cliente dell\'agente',
    'admin.notifications.types.broker_joined': 'Agente si è unito',
    'admin.notifications.types.weekly_agency_summary': 'Riepilogo settimanale agenzia',
    'admin.notifications.types.saved_search_alerts': 'Avvisi ricerca salvata',
    'admin.notifications.types.favorite_property_updates': 'Aggiornamenti proprietà preferite',
    'admin.notifications.types.newsletter': 'Newsletter',
    'admin.notifications.types.system_alerts': 'Avvisi di sistema',
    'admin.notifications.types.new_user_signup': 'Registrazione nuovo utente',
    'admin.notifications.types.article_published': 'Articolo pubblicato',

    // Admin Settings
    'admin.settings': 'Impostazioni amministratore',
    'admin.messaging_settings': 'Impostazioni messaggistica',
    'admin.messaging_settings_description': 'Controlla le funzionalità di messaggistica e chiamata per tutti gli utenti',
    'admin.messaging': 'Messaggistica',
    'admin.messaging_description': 'Consenti agli utenti di inviare messaggi',
    'admin.voice_calls': 'Chiamate vocali',
    'admin.voice_calls_description': 'Consenti agli utenti di effettuare chiamate vocali',
    'admin.video_calls': 'Videochiamate',
    'admin.video_calls_description': 'Consenti agli utenti di effettuare videochiamate',
    'admin.screen_sharing': 'Condivisione schermo',
    'admin.screen_sharing_description': 'Consenti agli utenti di condividere il loro schermo durante le chiamate',

    'location_mgr.title': 'Gestione posizioni',
    'location_mgr.current_locations': 'Posizioni attuali',
    'location_mgr.locations_in_db': '{count} posizioni nel database',
    'location_mgr.refresh': 'Aggiorna',
    'location_mgr.populate_title': 'Popola posizioni',
    'location_mgr.populate_desc': 'Recupera tutte le città e aree irachene da Google Places API.',
    'location_mgr.populating': 'Popolamento...',
    'location_mgr.populate_btn': 'Popola da Google Places',
    'location_mgr.success': 'Successo',
    'location_mgr.success_msg': 'Posizioni popolate con successo',
    'location_mgr.error': 'Errore',
    'location_mgr.error_msg': 'Impossibile popolare le posizioni.',
    'map_config.title': 'Gestione configurazione mappa',
    'map_config.subtitle': 'Gestisci le configurazioni permanenti della mappa',
    'map_config.loading': 'Caricamento configurazioni...',
    'map_config.create': 'Crea configurazione',
    'map_config.create_title': 'Crea configurazione mappa',
    'map_config.create_desc': 'Crea una nuova configurazione mappa permanente.',
    'map_config.edit_title': 'Modifica configurazione',
    'map_config.edit_desc': 'Aggiorna la tua configurazione.',
    'map_config.name': 'Nome configurazione',
    'map_config.name_placeholder': 'es: Mappa immobiliare Iraq',
    'map_config.description': 'Descrizione (opzionale)',
    'map_config.description_placeholder': 'Breve descrizione',
    'map_config.map_data': 'Configurazione mappa (JSON)',
    'map_config.map_data_placeholder': 'Configurazione in formato JSON',
    'map_config.cancel': 'Annulla',
    'map_config.update': 'Aggiorna',
    'map_config.no_configs': 'Nessuna configurazione',
    'map_config.no_configs_desc': 'Crea la tua prima configurazione.',
    'map_config.create_first': 'Crea prima configurazione',
    'map_config.active': 'Attivo',
    'map_config.activate': 'Attiva',
    'map_config.edit': 'Modifica',
    'map_config.delete': 'Elimina',
    'map_config.delete_confirm': 'Sei sicuro?',
    'map_config.delete_desc': 'Questo eliminerà permanentemente la configurazione "{name}".',
    'map_config.created': 'Creato',
    'map_config.last_updated': 'Ultimo aggiornamento',
    'map_config.map_center': 'Centro mappa',
    'map_config.zoom_level': 'Livello zoom',
    'map_config.cities': 'Città',
    'map_config.cities_configured': '{count} città configurate',
    'map_config.invalid_json': 'Formato JSON non valido',
    
    // Forms
    'form.price': 'Prezzo',
    'form.priceUSD': 'Prezzo (USD)',
    'form.priceIQD': 'Prezzo (IQD)',
    'form.priceConversionNote': 'Inserisci il prezzo in una delle valute - l\'altra verrà calcolata automaticamente (Tasso: 1 USD ≈ 1.310 IQD)',
    'form.price_usd': 'Prezzo (USD)',
    'form.price_placeholder': 'Inserisci il prezzo in dollari americani',
    'form.price_description': 'Inserisci il prezzo in dollari americani. Verrà visualizzato sia in USD che in dinaro iracheno.',
    'form.address': 'Indirizzo',
    'form.city': 'Città',
    'form.rooms': 'Stanze',
    'form.bathrooms': 'Bagni',
    'form.size': 'Dimensione (m²)',
    'form.building_size': 'Dimensione edificio (m²)',
    'form.building_size_placeholder': 'Area edificio in m²',
    'form.building_size_description': 'Area abitabile',
    'form.plot_size': 'Dimensione terreno (m²)',
    'form.plot_size_placeholder': 'Area terreno in m²',
    'form.plot_size_description': 'Area totale del terreno',
    'form.type': 'Tipo',
    'form.propertyType': 'Tipo di proprietà',
    'form.monthly_fee': 'Canone mensile (SEK)',
    'form.monthly_fee_usd': 'Canone mensile (USD)',
    'form.monthly_fee_placeholder': 'Inserisci il canone mensile in USD (opzionale)',
    'form.monthly_fee_description': 'Canone mensile opzionale in dollari americani',
    'form.description': 'Descrizione',
    'form.description_placeholder': 'Descrivi la proprietà...',
    'form.payment_plan': 'Piano di pagamento',
    'form.payment_plan_placeholder': 'es: 30% acconto, 70% alla consegna, o rate mensili...',
    'form.payment_plan_hint': 'Aggiungi rate di pagamento per mostrare le opzioni di pagamento agli acquirenti',
    'payment_plan.total': 'Totale',
    'payment_plan.amount': 'Importo',
    'payment_plan.description': 'Descrizione',
    'payment_plan.date': 'Data',
    'payment_plan.percentage': 'Percentuale',
    'payment_plan.add_installment': 'Aggiungi rata',
    'payment_plan.hint': 'Lascia vuoto se non offri un piano di pagamento',
    'payment_plan.down_payment': 'Acconto',
    'payment_plan.first_payment': 'Prima rata',
    'payment_plan.second_payment': 'Seconda rata',
    'payment_plan.third_payment': 'Terza rata',
    'payment_plan.fourth_payment': 'Quarta rata',
    'payment_plan.fifth_payment': 'Quinta rata',
    'payment_plan.sixth_payment': 'Sesta rata',
    'payment_plan.seventh_payment': 'Settima rata',
    'payment_plan.final_payment': 'Pagamento finale',
    'payment_plan.payment_description': 'Descrizione pagamento',
    'payment_plan.enable': 'Includi piano di pagamento',
    'form.floor_plans': 'Planimetrie',
    'form.upload_floor_plans': 'Carica immagini planimetrie',
    'form.features': 'Caratteristiche (separate da virgola)',
    'form.features_placeholder': 'es. Piscina, Giardino, Garage, Cucina moderna',
    'form.features_description': 'Separa le caratteristiche con virgole',
    'form.amenities': 'Servizi',
    'form.amenities_hint': 'Seleziona tutti i servizi applicabili a questa proprietà',
    'amenities.balcony': 'Balcone',
    'amenities.electricity_generator': 'Generatore elettrico',
    'amenities.central_ac': 'Aria condizionata centrale',
    'amenities.double_glazed_windows': 'Finestre con doppi vetri',
    'amenities.satellite_tv': 'TV satellitare',
    'amenities.cctv': 'Telecamere di sicurezza',
    'amenities.security_staff': 'Personale di sicurezza',
    'amenities.sewerage_system': 'Sistema fognario',
    'amenities.furnished_kitchen': 'Cucina arredata',
    'amenities.24hr_electricity': 'Elettricità 24h',
    'amenities.market': 'Mercato vicino',
    'amenities.elevator': 'Ascensore',
    'amenities.school': 'Scuola vicina',
    'amenities.mosque': 'Moschea vicina',
    'amenities.central_gas': 'Gas centrale',
    'amenities.maintenance': 'Servizi di manutenzione',
    'amenities.cleaning': 'Servizi di pulizia',
    'amenities.fire_fighting': 'Attrezzature antincendio',
    'amenities.car_washing': 'Autolavaggio',
    'amenities.smart_home': 'Casa intelligente',
    'amenities.earthquake_resistant': 'Resistente ai terremoti',
    'amenities.central_fire_system': 'Sistema antincendio centrale',
    'amenities.hot_water_system': 'Sistema di acqua calda centrale',
    'amenities.central_vacuum': 'Aspirapolvere centrale',
    'amenities.fountain': 'Fontana',
    'amenities.24hr_water': 'Acqua 24h',
    'amenities.garbage_chute': 'Scivolo per rifiuti',
    'amenities.kitchen_counter': 'Piano cucina',
    'amenities.street_lights': 'Illuminazione stradale',
    'amenities.playground': 'Parco giochi per bambini',
    'amenities.gym': 'Palestra',
    'amenities.garden': 'Giardino',
    'amenities.parking': 'Parcheggio',
    'amenities.wifi': 'WiFi',
    'amenities.security_door': 'Porta di sicurezza',
    'amenities.split_ac': 'Aria condizionata split',
    'amenities.bathtub': 'Vasca da bagno',
    'amenities.refrigerator': 'Frigorifero',
    'amenities.washing_machine': 'Lavatrice',
    'amenities.swimming_pool': 'Piscina',
    'amenities.solar_panels': 'Pannelli solari',
    'amenities.water_tank': 'Serbatoio d\'acqua',
    'amenities.water_well': 'Pozzo d\'acqua',
    'amenities.septic_tank': 'Fossa settica',
    'amenities.central_heating': 'Riscaldamento centrale',
    'amenities.desert_cooler': 'Raffrescatore evaporativo',
    'amenities.intercom': 'Citofono',
    'amenities.external_camera': 'Telecamera esterna',
    'amenities.guard_room': 'Stanza del guardiano',
    'amenities.rooftop_terrace': 'Terrazza sul tetto',
    'amenities.basement': 'Seminterrato',
    'amenities.storage_room': 'Ripostiglio',
    'amenities.internal_stairs': 'Scale interne',
    'amenities.external_stairs': 'Scale esterne',
    'amenities.corner_unit': 'Unità d\'angolo',
    'amenities.two_streets': 'Due strade',
    'amenities.double_entrance': 'Doppio ingresso',
    'amenities.marble_flooring': 'Pavimento in marmo',
    'amenities.ceramic_tiles': 'Piastrelle in ceramica',
    'amenities.parquet_flooring': 'Pavimento in parquet',
    'amenities.gypsum_decorations': 'Decorazioni in gesso',
    'amenities.false_ceiling': 'Controsoffitto',
    'amenities.steel_doors': 'Porte in acciaio',
    'amenities.aluminum_windows': 'Finestre in alluminio',
    'amenities.pvc_windows': 'Finestre in PVC',
    'amenities.guest_room': 'Camera degli ospiti',
    'amenities.servant_quarters': 'Alloggio per il personale',
    'amenities.covered_parking': 'Parcheggio coperto',
    'amenities.concrete_fence': 'Recinzione in cemento',
    'amenities.iron_fence': 'Recinzione in ferro',
    'amenities.street_pavement': 'Pavimentazione stradale',
    'amenities.fully_furnished': 'Completamente arredato',
    'amenities.hospital': 'Ospedale vicino',
    'amenities.category.utilities': 'Elettricità e Acqua',
    'amenities.category.climate': 'Climatizzazione',
    'amenities.category.security': 'Sicurezza',
    'amenities.category.building': 'Caratteristiche edificio',
    'amenities.category.interior': 'Interni e Pavimenti',
    'amenities.category.rooms': 'Stanze',
    'amenities.category.outdoor': 'Esterno e Parcheggio',
    'amenities.category.appliances': 'Elettrodomestici',
    'amenities.category.services': 'Servizi',
    'amenities.category.nearby': 'Nelle vicinanze',
    'common.selected': 'selezionato/i',
    'form.property_images': 'Immagini della proprietà',
    'form.current_images': 'Immagini attuali',
    'form.image': 'Immagine',
    'form.primary': 'Principale',
    'form.click_image_primary': 'Clicca su un\'immagine per impostarla come principale. Usa le frecce per cambiare l\'ordine.',
    'form.click_to_upload_new': 'Clicca per caricare nuove immagini',
    'form.new_images_upload': 'Nuove immagini da caricare',
    'form.new_image': 'Nuova immagine',
    'form.upload': 'Carica',
    'form.new_images': 'nuove immagini',
    'form.location': 'Posizione',
    'form.required': 'Obbligatorio',
    'form.optional': 'Opzionale',
    'form.cancel': 'Annulla',
    'form.save_changes': 'Salva modifiche',
    'form.uploading': 'Caricamento...',
    'form.upload_images': 'Carica immagini',
    'form.click_to_upload': 'Clicca per caricare immagini',
    'form.file_formats': 'JPEG, PNG, WebP, GIF (max 10MB per immagine)',
    'form.select_type': 'Seleziona tipo',
    'form.location_on_map': 'Posizione della proprietà sulla mappa',
    'form.location_hint': 'Clicca sulla mappa per impostare la posizione esatta',
    'form.hide_preview': 'Nascondi anteprima',
    'form.show_preview': 'Mostra anteprima',
    'form.selected_images': 'Immagini selezionate',
    'form.click_set_primary': 'Clicca su un\'immagine per impostarla come principale',
    'form.uploaded_images': 'Immagini caricate',
    'form.images': 'immagini',
    'form.uploading_images': 'Caricamento immagini...',
    'form.city_placeholder': 'Cerca una città...',
    'form.new_construction': 'Nuova costruzione (Casa futura)',
    'form.preview': 'Anteprima',
    'form.preview_description': 'Ecco come apparirà il tuo annuncio sul sito',
    'form.fullscreen': 'Schermo intero',
    'form.exit_fullscreen': 'Esci da schermo intero',
    'form.click_preview_fullscreen': 'Clicca sull\'anteprima per schermo intero',
    'form.preview_contact_disabled': 'Le opzioni di contatto sono disabilitate in modalità anteprima',
    'form.invalid_format': 'Formato file non valido',
    'form.invalid_format_desc': '{filename} ha un formato non valido. Consentiti: JPEG, PNG, WebP, GIF',
    'form.file_too_large': 'File troppo grande',
    'form.file_too_large_desc': '{filename} è più grande di 10MB',
    'form.upload_failed': 'Caricamento fallito',
    'form.upload_failed_desc': 'Impossibile caricare le immagini',
    'form.images_required': 'Immagini richieste',
    'form.images_required_desc': 'Per favore, carica almeno un\'immagine della proprietà',
    'form.sold_price_usd': 'Prezzo di vendita (USD)',
    'form.sold_price_placeholder': 'Inserisci il prezzo di vendita effettivo',
    'form.sold_price_description': 'Il prezzo di vendita effettivo (potrebbe differire dal prezzo di listino)',
    'form.video_url': 'URL del video tour',
    'form.video': 'Video',
    'form.add_video': 'Aggiungi un altro video',
    'form.video_addon': '+$3 aggiunta',
    'form.video_included': 'Incluso gratis',
    'form.video_url_hint': 'Aggiungi un video tour di YouTube o Vimeo. L\'aggiunta video può essere acquistata dopo aver creato l\'annuncio.',
    'form.video_url_hint_free': 'Aggiungi un video tour di YouTube o Vimeo - incluso nel tuo abbonamento!',
    'form.video_url_hint_addon': 'Aggiungi un video tour di YouTube o Vimeo. $3 verranno aggiunti al costo del tuo annuncio.',
    'form.include_video_tour': 'Includi video tour',
    'form.show_map': 'Mostra mappa',
    'form.no_location': 'Nessuna posizione selezionata',
    
    // Footer
    'footer.contact': 'Contatto',
    'footer.customer_service': 'Servizio clienti e contatto',
    'footer.about': 'Informazioni su Aqarnet',
    'footer.press': 'Stampa',
    'footer.investors': 'Per gli investitori',
    'footer.advertising': 'Pubblicità e marketing',
    'footer.jobs': 'Lavora con noi',
    'footer.statistics': 'Statistiche immobiliari',
    'footer.apps': 'App',
    'footer.iphone_ipad': 'iPhone e iPad',
    'footer.android': 'Android',
    'footer.follow': 'Segui Aqarnet',
    'footer.newsletter': 'Newsletter Aqarnet',
    'footer.whatsapp': 'WhatsApp',
    'footer.facebook': 'Facebook',
    'footer.instagram': 'Instagram',
    'footer.linkedin': 'LinkedIn',
    'footer.pinterest': 'Pinterest',
    'footer.tiktok': 'TikTok',
    'footer.threads': 'Threads',
    'footer.podcast': 'Podcast Aqarnet',
    'footer.all_properties': 'Tutte le proprietà',
    'footer.privacy': 'Privacy',
    'footer.cookies': 'Cookies',
    'footer.terms': 'Termini e Informazioni Legali',
    'footer.cookie_settings': 'Impostazioni cookie',
    
    // About Page
    'about.title': 'Chi siamo',
    'about.subtitle': 'Il tuo partner di fiducia nel mercato immobiliare iracheno',
    'about.description': 'Aqarnet è la principale piattaforma immobiliare dell\'Iraq, che collega acquirenti, venditori e agenti in tutto il paese. Offriamo annunci completi, analisi di mercato e strumenti professionali.',
    'about.mission_title': 'La Nostra Missione',
    'about.mission_text': 'Rivoluzionare il mercato immobiliare iracheno fornendo informazioni immobiliari trasparenti, accessibili e affidabili.',
    'about.vision_title': 'La Nostra Visione',
    'about.vision_text': 'Diventare la piattaforma immobiliare più affidabile del Medio Oriente.',
    'about.services_title': 'I Nostri Servizi',
    'about.services_text': 'Annunci immobiliari, analisi di mercato, connessioni con agenti, valutazioni e notizie immobiliari.',
    'about.team_title': 'Il Nostro Team',
    'about.team_text': 'Un team dedicato di professionisti immobiliari, esperti di tecnologia e specialisti del servizio clienti.',
    'about.company_info': 'Informazioni Aziendali',
    'about.based_in': 'Con sede in Svezia, al servizio dell\'Iraq',
    
    // Contact Page
    'contact.title': 'Contattaci',
    'contact.subtitle': 'Siamo qui per aiutarti. Contattaci per qualsiasi domanda.',
    'contact.email_label': 'E-mail',
    'contact.phone_label': 'Telefono',
    'contact.phone_info': 'Prossimamente',
    'contact.location_label': 'Sede',
    'contact.location_info': 'Svezia',
    'contact.form_title': 'Inviaci un messaggio',
    'contact.form_description': 'Compila il modulo e ti risponderemo il prima possibile.',
    'contact.name_placeholder': 'Il tuo nome',
    'contact.email_placeholder': 'tua@email.com',
    'contact.subject': 'Oggetto',
    'contact.subject_placeholder': 'Di cosa si tratta?',
    'contact.message_placeholder': 'Il tuo messaggio...',
    'contact.success': 'Messaggio inviato! Ti risponderemo presto.',
    
    // Press Page
    'press.title': 'Stampa e Media',
    'press.subtitle': 'Ultime notizie e comunicati stampa da Aqarnet',
    'press.contact_title': 'Contatto Stampa',
    'press.contact_description': 'Per richieste stampa, contattaci via e-mail.',
    'press.add_release': 'Aggiungi comunicato',
    'press.edit_release': 'Modifica comunicato',
    'press.new_release': 'Nuovo comunicato stampa',
    'press.release_title': 'Titolo',
    'press.content': 'Contenuto',
    'press.publish': 'Pubblica',
    'press.no_releases': 'Ancora nessun comunicato stampa.',
    'press.delete_confirm_title': 'Eliminare il comunicato?',
    'press.delete_confirm_description': 'Questa azione non può essere annullata.',
    
    // Investors Page
    'investors.title': 'Per Investitori',
    'investors.subtitle': 'Unisciti a noi per trasformare il mercato immobiliare iracheno',
    'investors.growth_title': 'Potenziale di Crescita',
    'investors.growth_text': 'Il mercato immobiliare iracheno sta vivendo una rapida crescita con significative opportunità di investimento.',
    'investors.market_title': 'Leader di Mercato',
    'investors.market_text': 'Aqarnet è posizionata come la principale piattaforma digitale nel settore immobiliare iracheno.',
    'investors.vision_title': 'Espansione Regionale',
    'investors.vision_text': 'La nostra visione si estende oltre l\'Iraq per diventare la piattaforma leader in Medio Oriente.',
    'investors.opportunity_title': 'Opportunità di Investimento',
    'investors.opportunity_text': 'Aqarnet offre un\'opportunità unica di investire nella trasformazione digitale di uno dei più grandi mercati immobiliari emergenti.',
    'investors.opportunity_text_2': 'Con una base utenti in crescita e servizi in espansione, siamo ben posizionati per una crescita sostenibile.',
    'investors.contact_title': 'Contattaci',
    'investors.contact_description': 'Interessato alle opportunità di investimento? Contattaci per saperne di più.',
    'investors.company': 'Azienda / Organizzazione',
    'investors.company_placeholder': 'Nome della tua azienda',
    'investors.message_placeholder': 'Parlaci dei tuoi interessi di investimento...',
    'investors.success': 'Grazie per il tuo interesse! Ti contatteremo presto.',
    
    // Advertising Page
    'advertising.title': 'Pubblicità e Marketing',
    'advertising.subtitle': 'Raggiungi migliaia di potenziali acquirenti e affittuari in Iraq',
    'advertising.targeted_title': 'Portata Mirata',
    'advertising.targeted_text': 'Raggiungi utenti che cercano attivamente immobili in aree specifiche.',
    'advertising.audience_title': 'Pubblico di Qualità',
    'advertising.audience_text': 'Connettiti con acquirenti e affittuari seri.',
    'advertising.analytics_title': 'Analisi Dettagliate',
    'advertising.analytics_text': 'Monitora le prestazioni delle tue campagne con analisi complete.',
    'advertising.formats_title': 'Formati Multipli',
    'advertising.formats_text': 'Banner pubblicitari, annunci in evidenza, contenuti sponsorizzati e altro.',
    'advertising.contact_title': 'Inizia a Fare Pubblicità',
    'advertising.contact_description': 'Contattaci per discutere le opportunità pubblicitarie.',
    'advertising.company': 'Azienda / Organizzazione',
    'advertising.company_placeholder': 'Nome della tua azienda',
    'advertising.budget': 'Range di Budget',
    'advertising.budget_placeholder': 'es., €500 - €1000/mese',
    'advertising.message_placeholder': 'Parlaci dei tuoi obiettivi pubblicitari...',
    'advertising.success': 'Grazie! Ti contatteremo riguardo le opportunità pubblicitarie.',
    
    // Jobs Page
    'jobs.title': 'Lavora con Aqarnet',
    'jobs.subtitle': 'Unisciti al nostro team e contribuisci a plasmare il futuro immobiliare in Iraq',
    'jobs.add_job': 'Aggiungi lavoro',
    'jobs.edit_job': 'Modifica lavoro',
    'jobs.new_job': 'Nuova offerta di lavoro',
    'jobs.job_title': 'Titolo della posizione',
    'jobs.department': 'Dipartimento',
    'jobs.location': 'Sede',
    'jobs.employment_type': 'Tipo di impiego',
    'jobs.full_time': 'Tempo pieno',
    'jobs.part_time': 'Part-time',
    'jobs.contract': 'Contratto',
    'jobs.internship': 'Stage',
    'jobs.description': 'Descrizione',
    'jobs.requirements': 'Requisiti',
    'jobs.benefits': 'Benefici',
    'jobs.publish': 'Pubblica',
    'jobs.no_jobs': 'Nessuna posizione aperta al momento.',
    'jobs.check_back': 'Torna più tardi per nuove opportunità.',
    'jobs.apply_now': 'Candidati ora',
    'jobs.apply_for': 'Candidati per',
    'jobs.apply_description': 'Invia la tua candidatura via e-mail.',
    'jobs.application_sent': 'Candidatura inviata! Buona fortuna!',
    'jobs.delete_confirm_title': 'Eliminare l\'offerta di lavoro?',
    'jobs.delete_confirm_description': 'Questa azione non può essere annullata.',
    
    // Statistics
    'statistics.title': 'Statistiche immobiliari',
    'statistics.subtitle': 'Analizza le tendenze di mercato e l\'evoluzione dei prezzi basate sugli immobili venduti',
    'statistics.6_months': 'Ultimi 6 mesi',
    'statistics.12_months': 'Ultimi 12 mesi',
    'statistics.24_months': 'Ultimi 24 mesi',
    'statistics.36_months': 'Ultimi 36 mesi',
    'statistics.all_cities': 'Tutte le città',
    'statistics.total_sold': 'Totale venduto',
    'statistics.avg_price': 'Prezzo medio',
    'statistics.avg_price_sqm': 'Prezzo medio/m²',
    'statistics.price_change': 'Variazione prezzo',
    'statistics.price_trends': 'Tendenze dei prezzi',
    'statistics.price_trends_desc': 'Prezzi medi degli immobili nel tempo',
    'statistics.sales_volume': 'Volume di vendite',
    'statistics.sales_volume_desc': 'Numero di immobili venduti al mese',
    'statistics.properties_sold': 'Immobili venduti',
    'statistics.price_by_city': 'Prezzo per m² per città',
    'statistics.price_by_city_desc': 'Confronto del prezzo medio per metro quadrato tra le città',
    'statistics.price_per_sqm': 'Prezzo per m²',
    'statistics.type_distribution': 'Distribuzione tipi di immobili',
    'statistics.type_distribution_desc': 'Distribuzione degli immobili venduti per tipo',
    'statistics.avg_days_market': 'Giorni medi sul mercato',
    'statistics.days': 'giorni',
    'statistics.price_ranges': 'Distribuzione fasce di prezzo',
    'statistics.price_ranges_desc': 'Numero di immobili venduti in ogni fascia di prezzo',
    'statistics.properties': 'Immobili',
    'statistics.year_over_year': 'Confronto annuale',
    'statistics.year_over_year_desc': 'Confronta prezzi medi e volume di vendite negli anni',
    'statistics.all_types': 'Tutti i tipi di immobili',
    'statistics.all_rooms': 'Tutte le stanze',
    'statistics.rooms': 'stanze',
    'statistics.export': 'Esporta',
    'statistics.export_csv': 'Scarica CSV',
    'statistics.export_json': 'Scarica JSON',
    'statistics.share': 'Condividi',
    'statistics.link_copied': 'Link copiato',
    'statistics.link_copied_desc': 'Il link del report statistico è stato copiato negli appunti',
    'statistics.custom_date': 'Data personalizzata',
    'statistics.use_preset': 'Usa predefinito',
    'statistics.start_date': 'Data inizio',
    'statistics.end_date': 'Data fine',
    'statistics.city_comparison': 'Confronto città',
    'statistics.city_comparison_desc': 'Confronta le statistiche immobiliari tra due città fianco a fianco',
    'statistics.select_city_1': 'Seleziona prima città',
    'statistics.select_city_2': 'Seleziona seconda città',
    'statistics.vs': 'vs',
    'statistics.select_cities_to_compare': 'Seleziona due città sopra per confrontare le loro statistiche',
    'statistics.avg_size': 'Dimensione media',
    
    // Cookie Settings
    'cookie_settings.title': 'Impostazioni dei cookie',
    'cookie_settings.description': 'Utilizziamo i cookie per migliorare la tua esperienza di navigazione. Puoi personalizzare le tue preferenze qui sotto.',
    'cookie_settings.necessary': 'Cookie necessari',
    'cookie_settings.necessary_desc': 'Questi cookie sono essenziali per il corretto funzionamento del sito web. Non possono essere disabilitati.',
    'cookie_settings.functional': 'Cookie funzionali',
    'cookie_settings.functional_desc': 'Questi cookie abilitano funzionalità personalizzate come le preferenze linguistiche e le impostazioni salvate.',
    'cookie_settings.analytics': 'Cookie analitici',
    'cookie_settings.analytics_desc': 'Questi cookie ci aiutano a capire come i visitatori interagiscono con il nostro sito per migliorare l\'esperienza utente.',
    'cookie_settings.marketing': 'Cookie di marketing',
    'cookie_settings.marketing_desc': 'Questi cookie vengono utilizzati per mostrarti annunci pertinenti e monitorare l\'efficacia delle nostre campagne.',
    'cookie_settings.accept_all': 'Accetta tutti',
    'cookie_settings.reject_all': 'Rifiuta tutti',
    'cookie_settings.save_preferences': 'Salva preferenze',
    'cookie_settings.saved': 'Preferenze cookie salvate',
    
    // Cookie Banner
    'cookie_banner.title': 'Utilizziamo i cookie',
    'cookie_banner.description': 'Utilizziamo i cookie per migliorare la tua esperienza di navigazione, mostrare contenuti personalizzati e analizzare il nostro traffico. Cliccando su "Accetta tutti", acconsenti al nostro utilizzo dei cookie.',
    'cookie_banner.accept_all': 'Accetta tutti',
    'cookie_banner.reject_all': 'Rifiuta tutti',
    'cookie_banner.customize': 'Personalizza',
    'terms.title': 'Termini di Servizio', 'terms.last_updated': 'Ultimo aggiornamento:', 'terms.section1.title': '1. Accettazione dei Termini', 'terms.section1.p1': 'Utilizzando Aqarnet, accetti questi termini.', 'terms.section1.p2': 'Ci riserviamo il diritto di modificare questi termini.', 'terms.section2.title': '2. Descrizione del Servizio', 'terms.section2.p1': 'Aqarnet fornisce una piattaforma digitale per annunci immobiliari in Iraq.', 'terms.section2.p2': 'I nostri servizi includono annunci, ricerca e strumenti di comunicazione.', 'terms.section3.title': '3. Responsabilità dell\'Utente', 'terms.section3.p1': 'Come utente, accetti di:', 'terms.section3.li1': 'Fornire informazioni accurate', 'terms.section3.li2': 'Non usare la piattaforma per attività illegali', 'terms.section3.li3': 'Rispettare i diritti di proprietà intellettuale', 'terms.section3.li4': 'Non interferire con il servizio', 'terms.section4.title': '4. Annunci Immobiliari', 'terms.section4.p1': 'Gli utenti sono responsabili dell\'accuratezza dei loro annunci.', 'terms.section4.p2': 'Possiamo rimuovere annunci che violano i nostri termini.', 'terms.section5.title': '5. Proprietà Intellettuale', 'terms.section5.p1': 'Tutti i contenuti appartengono ad Aqarnet.', 'terms.section5.p2': 'Riproduzione vietata senza permesso.', 'terms.section6.title': '6. Limitazione di Responsabilità', 'terms.section6.p1': 'Aqarnet è fornito "così com\'è" senza garanzie.', 'terms.section7.title': '7. Legge Applicabile', 'terms.section7.p1': 'Questi termini sono regolati dalla legge svedese.', 'terms.section7.p2': 'Le controversie saranno risolte nei tribunali svedesi.', 'terms.section8.title': '8. Contatto', 'terms.section8.p1': 'Per domande, contattaci:', 'terms.section8.email': 'Email',
    'privacy.title': 'Informativa sulla Privacy', 'privacy.last_updated': 'Ultimo aggiornamento:', 'privacy.section1.title': '1. Introduzione', 'privacy.section1.p1': 'Ci impegniamo a proteggere la tua privacy.', 'privacy.section1.p2': 'Utilizzando i nostri servizi, accetti le nostre pratiche sui dati.', 'privacy.section2.title': '2. Informazioni Raccolte', 'privacy.section2.p1': 'Raccogliamo:', 'privacy.section2.li1': 'Informazioni account', 'privacy.section2.li2': 'Dati immobiliari', 'privacy.section2.li3': 'Dati di utilizzo', 'privacy.section2.li4': 'Informazioni dispositivo', 'privacy.section3.title': '3. Uso delle Informazioni', 'privacy.section3.p1': 'Usiamo le tue informazioni per:', 'privacy.section3.li1': 'Fornire servizi', 'privacy.section3.li2': 'Personalizzare l\'esperienza', 'privacy.section3.li3': 'Inviare notifiche', 'privacy.section3.li4': 'Comunicare con te', 'privacy.section4.title': '4. Condivisione Dati', 'privacy.section4.p1': 'Non vendiamo i tuoi dati.', 'privacy.section4.p2': 'Possiamo condividere informazioni se richiesto dalla legge.', 'privacy.section5.title': '5. I Tuoi Diritti', 'privacy.section5.p1': 'Hai i seguenti diritti:', 'privacy.section5.li1': 'Accesso', 'privacy.section5.li2': 'Rettifica', 'privacy.section5.li3': 'Cancellazione', 'privacy.section5.li4': 'Portabilità', 'privacy.section5.li5': 'Opposizione', 'privacy.section6.title': '6. Sicurezza Dati', 'privacy.section6.p1': 'Proteggiamo i tuoi dati.', 'privacy.section6.p2': 'Nessun metodo è sicuro al 100%.', 'privacy.section7.title': '7. Modifiche', 'privacy.section7.p1': 'Possiamo aggiornare questa politica.', 'privacy.section8.title': '8. Contatto', 'privacy.section8.p1': 'Contattaci per domande:', 'privacy.section8.email': 'Email',
    
    // Newsletter
    'newsletter.title': 'Iscrizione alla newsletter',
    'newsletter.description': 'Iscriviti per ricevere le ultime notizie immobiliari.',
    'newsletter.placeholder': 'Inserisci la tua email',
    'newsletter.subscribe': 'Iscriviti',
    'newsletter.subscribing': 'Iscrizione in corso...',
    'newsletter.success': 'Iscrizione riuscita!',
    'newsletter.subscribed': 'Grazie per l\'iscrizione!',
    'newsletter.invalid_email': 'Inserisci un\'email valida',
    'newsletter.error': 'Iscrizione fallita',
    'newsletter.unsubscribe_note': 'Puoi annullare l\'iscrizione in qualsiasi momento.',
    'newsletter.unsubscribe_title': 'Annulla iscrizione alla newsletter',
    'newsletter.unsubscribe_description': 'Inserisci la tua email per annullare l\'iscrizione.',
    'newsletter.unsubscribe_button': 'Annulla iscrizione',
    'newsletter.unsubscribe_success': 'Iscrizione annullata',
    'newsletter.unsubscribe_error': 'Annullamento fallito',
    'newsletter.unsubscribe_message': 'Non riceverai più la nostra newsletter.',
    'newsletter.invalid_link': 'Link di cancellazione non valido',
    'newsletter.invalid_link_message': 'Questo link di cancellazione non è valido o è scaduto. Utilizza il link dalla tua email della newsletter.',
    'newsletter.unsubscribe_reason_label': 'Motivo della cancellazione (opzionale)',
    'newsletter.select_reason': 'Seleziona un motivo...',
    'newsletter.reason_too_many_emails': 'Troppe email',
    'newsletter.reason_not_relevant': 'Il contenuto non è rilevante per me',
    'newsletter.reason_never_subscribed': 'Non mi sono mai iscritto',
    'newsletter.reason_found_another': 'Ho trovato un altro servizio',
    'newsletter.reason_other': 'Altro',
    'common.try_again': 'Riprova',
    'newsletter.admin.title': 'Iscritti alla newsletter',
    'newsletter.admin.description': 'Gestisci i tuoi iscritti',
    'newsletter.admin.total': 'Totale iscritti',
    'newsletter.admin.active': 'Attivo',
    'newsletter.admin.unsubscribed': 'Disiscritto',
    'newsletter.admin.export_csv': 'Esporta CSV',
    'newsletter.admin.refresh': 'Aggiorna',
    'newsletter.admin.subscribers_list': 'Lista iscritti',
    'newsletter.admin.subscribers_description': 'Tutti gli iscritti e il loro stato',
    'newsletter.admin.no_subscribers': 'Nessun iscritto ancora',
    'newsletter.admin.subscribed_at': 'Iscritto il',
    'newsletter.admin.status': 'Stato',
    'newsletter.admin.unsubscribed_at': 'Disiscritto il',
    'newsletter.admin.actions': 'Azioni',
    'newsletter.admin.deleted': 'Iscritto eliminato',
    'newsletter.admin.delete_error': 'Eliminazione fallita',
    'newsletter.admin.no_data': 'Nessun dato da esportare',
    'newsletter.admin.exported': 'Esportato con successo',
    'newsletter.admin.delete_confirm_title': 'Eliminare iscritto?',
    'newsletter.admin.delete_confirm_description': 'Questo eliminerà definitivamente l\'iscritto.',
    'footer.faq': 'Domande frequenti',
    
    // FAQ Page
    'faq.title': 'Domande frequenti',
    'faq.subtitle': 'Trova risposte alle domande comuni su Aqarnet',
    'faq.account.title': 'Account e registrazione',
    'faq.account.q1': 'Come creo un account?',
    'faq.account.q1_answer': 'Clicca su "Accedi" nell\'intestazione, poi seleziona "Crea account". Puoi registrarti con email, Google o Apple.',
    'faq.account.q2': 'Come reimpostare la mia password?',
    'faq.account.q2_answer': 'Nella pagina di accesso, clicca su "Password dimenticata" e inserisci il tuo indirizzo email. Riceverai un link per reimpostare la password.',
    'faq.account.q3': 'Posso cambiare il mio indirizzo email?',
    'faq.account.q3_answer': 'Attualmente non puoi cambiare direttamente il tuo indirizzo email. Contatta il nostro team di supporto per assistenza.',
    'faq.listings.title': 'Annunci immobiliari',
    'faq.listings.q1': 'Come pubblico una proprietà in vendita?',
    'faq.listings.q1_answer': 'Devi essere registrato come agente per pubblicare proprietà. Dopo l\'accesso, clicca su "Aggiungi proprietà" e compila i dettagli.',
    'faq.listings.q2': 'Quanto tempo rimane attivo un annuncio?',
    'faq.listings.q2_answer': 'Gli annunci rimangono attivi fino a quando non vengono contrassegnati come venduti o rimossi dal proprietario.',
    'faq.listings.q3': 'Posso modificare il mio annuncio dopo la pubblicazione?',
    'faq.listings.q3_answer': 'Sì, puoi modificare il tuo annuncio in qualsiasi momento. Vai al tuo profilo, trova l\'annuncio e clicca su "Modifica".',
    'faq.search.title': 'Ricerca proprietà',
    'faq.search.q1': 'Come salvo una ricerca?',
    'faq.search.q1_answer': 'Dopo aver effettuato una ricerca, clicca su "Salva ricerca". Puoi attivare le notifiche email per nuove proprietà.',
    'faq.search.q2': 'Come aggiungo una proprietà ai preferiti?',
    'faq.search.q2_answer': 'Clicca sull\'icona del cuore su qualsiasi scheda proprietà. Devi essere connesso per salvare i preferiti.',
    'faq.search.q3': 'Posso cercare sulla mappa?',
    'faq.search.q3_answer': 'Sì, usa la vista mappa nella pagina delle proprietà per esplorare gli annunci per posizione.',
    'faq.contact.title': 'Contattare gli agenti',
    'faq.contact.q1': 'Come contatto un agente immobiliare?',
    'faq.contact.q1_answer': 'Clicca su "Contatta agente" su qualsiasi annuncio per inviare un messaggio direttamente all\'agente responsabile.',
    'faq.contact.q2': 'Posso programmare una visita?',
    'faq.contact.q2_answer': 'Sì, clicca su "Programma visita" nella pagina della proprietà per richiedere un orario di visita.',
    'faq.security.title': 'Sicurezza e privacy',
    'faq.security.q1': 'Le mie informazioni personali sono sicure?',
    'faq.security.q1_answer': 'Sì, utilizziamo crittografia e misure di sicurezza standard del settore per proteggere i tuoi dati.',
    'faq.security.q2': 'Come elimino il mio account?',
    'faq.security.q2_answer': 'Vai alle impostazioni del tuo profilo e seleziona "Elimina account". Questa azione è permanente.',
    'faq.still_questions': 'Hai ancora domande?',
    'faq.contact_us': 'Contatta il nostro team di supporto e saremo felici di aiutarti.',

    // Broker Dashboard
    'broker.dashboard_title': 'Dashboard del broker',
    'broker.dashboard_subtitle': 'Gestisci le tue proprietà e interazioni con i clienti',
    'broker.my_properties': 'Le mie proprietà',
    'broker.my_properties_desc': 'Visualizza e gestisci i tuoi annunci immobiliari',
    'broker.viewings': 'Visite alle proprietà',
    'broker.viewings_desc': 'Gestisci le visite programmate',
    'broker.contacts': 'Messaggi dei clienti',
    'broker.contacts_desc': 'Visualizza e rispondi alle richieste dei clienti',
    'broker.analytics': 'Analisi delle proprietà',
    'broker.analytics_desc': 'Monitora visualizzazioni e engagement sui tuoi annunci',
    'broker.subscription': 'Abbonamento',
    'broker.subscription_desc': 'Gestisci il tuo piano e limiti annunci',
    'broker.access_denied': 'Questa pagina è accessibile solo ai broker registrati.',
    'broker.profile_title': 'Profilo del broker',
    'broker.profile_description': 'Gestisci il tuo profilo pubblico e le informazioni di contatto',
    'broker.back_to_dashboard': 'Torna alla dashboard',
    'broker.display_name': 'Nome visualizzato',
    'broker.display_name_placeholder': 'Il tuo nome come appare ai clienti',
    'broker.contact_email': 'Email di contatto',
    'broker.email_placeholder': 'tua@email.com',
    'broker.email_notification_note': 'Questa email verrà utilizzata per le notifiche su visite e messaggi dei clienti.',
    'broker.phone_number': 'Numero di telefono',
    'broker.phone_placeholder': '+39 xxx xxx xxxx',
    'broker.change_photo': 'Cambia foto',
    'broker.save_profile': 'Salva profilo',
    'broker.profile_saved': 'Profilo salvato con successo',
    'broker.profile_save_error': 'Impossibile salvare il profilo',
    'broker.profile_load_error': 'Impossibile caricare il profilo',
    'broker.avatar_uploaded': 'Foto caricata con successo',
    'broker.avatar_upload_error': 'Impossibile caricare la foto',
    'broker.invalid_image_type': 'Seleziona un file immagine',
    'broker.image_too_large': 'L\'immagine deve essere inferiore a 2MB',
    'common.login_required': 'Effettua il login per accedere a questa pagina.',
    
    // Profile Email Notifications
    'profile.email_notifications': 'Notifiche email',
    'profile.email_notifications_description': 'Scegli quali notifiche email vuoi ricevere',
    'profile.notif_property_alerts': 'Avvisi proprietà',
    'profile.notif_property_alerts_desc': 'Ricevi notifiche sulle nuove proprietà che corrispondono alle tue ricerche salvate',
    'profile.notif_saved_search_alerts': 'Avvisi ricerche salvate',
    'profile.notif_saved_search_alerts_desc': 'Ricevi avvisi quando nuove proprietà corrispondono ai tuoi criteri di ricerca',
    'profile.notif_newsletter': 'Newsletter',
    'profile.notif_newsletter_desc': 'Ricevi la nostra newsletter con notizie immobiliari e analisi di mercato',
    'profile.notif_viewing_confirmations': 'Conferme visite',
    'profile.notif_viewing_confirmations_desc': 'Ricevi conferme email per le tue visite programmate',
    'profile.email_prefs_saved': 'Preferenze email salvate',
    'profile.email_prefs_error': 'Impossibile salvare le preferenze',
    
    // Agency Dashboard
    'agency.my_agency': 'La mia agenzia',
    'agency.dashboard_title': 'Dashboard dell\'agenzia',
    'agency.dashboard_subtitle': 'Gestisci i tuoi broker e le impostazioni dell\'agenzia',
    'agency.brokers_tab': 'Broker',
    'agency.invites_tab': 'Inviti',
    'agency.settings_tab': 'Impostazioni',
    'agency.brokers_title': 'Broker dell\'agenzia',
    'agency.brokers_description': 'Gestisci i broker nella tua agenzia',
    'agency.create_broker': 'Crea broker',
    'agency.create_broker_title': 'Crea nuovo account broker',
    'agency.create_broker_description': 'Crea un nuovo account broker collegato direttamente alla tua agenzia',
    'agency.broker_name': 'Nome completo',
    'agency.broker_email': 'Indirizzo email',
    'agency.broker_password': 'Password',
    'agency.create_broker_btn': 'Crea account broker',
    'agency.broker_created': 'Account broker creato con successo',
    'agency.broker_create_error': 'Impossibile creare l\'account broker',
    'agency.no_brokers': 'Nessun broker ancora',
    'agency.no_brokers_description': 'Crea account broker o invia inviti',
    'agency.broker_removed': 'Broker rimosso dall\'agenzia',
    'agency.broker_remove_error': 'Impossibile rimuovere il broker',
    'agency.invites_title': 'Inviti in attesa',
    'agency.invites_description': 'Gestisci gli inviti ai potenziali broker',
    'agency.send_invite': 'Invia invito',
    'agency.send_invite_title': 'Invita un broker',
    'agency.send_invite_description': 'Invia un invito per unirti alla tua agenzia',
    'agency.invite_email': 'Indirizzo email',
    'agency.send_invite_btn': 'Invia invito',
    'agency.invite_sent': 'Invito inviato con successo',
    'agency.invite_error': 'Impossibile inviare l\'invito',
    'agency.link_copied': 'Link dell\'invito copiato',
    'agency.no_invites': 'Nessun invito in attesa',
    'agency.settings_title': 'Impostazioni dell\'agenzia',
    'agency.settings_description': 'Aggiorna le informazioni della tua agenzia',
    'agency.settings_saved': 'Impostazioni dell\'agenzia salvate',
    'agency.settings_save_error': 'Impossibile salvare le impostazioni',
    'agency.load_error': 'Impossibile caricare i dati dell\'agenzia',
    'agency_invite.title': 'Unisciti all\'agenzia',
    'agency_invite.description': 'Sei stato invitato a unirti a {agencyName}',
    'agency_invite.accept_btn': 'Accetta invito',
    'agency_invite.accepted_success': 'Hai aderito con successo all\'agenzia!',
    'agency_invite.accept_error': 'Impossibile accettare l\'invito',
    'agency_invite.create_account': 'Crea un account',
    'agency_invite.invalid_link': 'Link dell\'invito non valido',
    'agency_invite.expired': 'Questo invito è scaduto',
    'agency_invite.already_accepted': 'Questo invito è già stato accettato',
    'agency_invite.fetch_error': 'Impossibile caricare l\'invito',
    'agency_invite.error_title': 'Invito non valido',
    'agency_invite.back_home': 'Torna alla home',
    'agency_invite.create_account_description': 'Crea un account per accettare questo invito',
    'agency_invite.your_name': 'Il tuo nome',
    'agency_invite.name_placeholder': 'Inserisci il tuo nome completo',
    'agency_invite.email_placeholder': 'tua@email.com',
    'agency_invite.password': 'Password',
    'agency_invite.password_placeholder': 'Crea una password',
    'agency_invite.confirm_password': 'Conferma password',
    'agency_invite.confirm_password_placeholder': 'Conferma la tua password',
    'agency_invite.register_accept_btn': 'Crea account e unisciti',
    'agency_invite.registered_success': 'Account creato e unito all\'agenzia!',
    'agency_invite.register_error': 'Impossibile creare l\'account',
    'agency_invite.passwords_mismatch': 'Le password non corrispondono',
    'agency_invite.password_too_short': 'La password deve avere almeno 6 caratteri',
    'agency_invite.already_have_account': 'Hai già un account?',
    'agency_invite.login_here': 'Accedi qui',
    'agency_invite.joining': 'Ti stai unendo a',
    'agency_invite.logged_in_as': 'Connesso come',
    'agency.notifications_tab': 'Notifiche',
    'agency.notifications_title': 'Notifiche email',
    'agency.notifications_description': 'Scegli quali notifiche ricevere sull\'attività dei broker',
    'agency.notifications_saved': 'Preferenze di notifica salvate',
    'agency.notifications_save_error': 'Impossibile salvare le preferenze',
    'agency.notif_new_broker_joins': 'Nuovo broker si unisce',
    'agency.notif_new_broker_joins_desc': 'Ricevi notifica quando un broker accetta il tuo invito',
    'agency.notif_broker_property_added': 'Broker aggiunge proprietà',
    'agency.notif_broker_property_added_desc': 'Ricevi notifica quando i broker aggiungono nuove proprietà',
    'agency.notif_broker_viewing_requests': 'Richieste di visita',
    'agency.notif_broker_viewing_requests_desc': 'Ricevi notifica sulle richieste di visita per le proprietà dei broker',
    'agency.notif_broker_client_messages': 'Messaggi dei clienti',
    'agency.notif_broker_client_messages_desc': 'Ricevi notifica quando i clienti contattano i tuoi broker',
    'agency.notif_weekly_summary': 'Riepilogo settimanale',
    'agency.notif_weekly_summary_desc': 'Ricevi un riepilogo settimanale dell\'attività dell\'agenzia',
    'agency.analytics_tab': 'Analisi',
    'agency.no_brokers_analytics': 'Aggiungi broker per vedere le analisi delle prestazioni',
    'agency.total_properties': 'Proprietà totali',
    'agency.total_views': 'Visualizzazioni totali',
    'agency.total_viewings': 'Richieste di visita',
    'agency.total_sold': 'Proprietà vendute',
    'agency.total_sales_value': 'Valore totale vendite',
    'agency.broker_performance_chart': 'Prestazioni broker',
    'agency.broker_performance_chart_desc': 'Confronta le metriche dei broker a colpo d\'occhio',
    'agency.individual_broker_stats': 'Statistiche individuali',
    'agency.individual_broker_stats_desc': 'Metriche dettagliate per ogni broker',
    'agency.properties': 'Proprietà',
    'agency.views': 'Visualizzazioni',
    'agency.viewings': 'Visite',
    'agency.contacts': 'Contatti',
    'agency.active': 'Attivo',
    'agency.sold': 'Venduto',
    'agency.sales_value': 'Valore vendite',
    'agency.upgrade_plan': 'Aggiorna piano',
    'agency.view_plans': 'Vedi piani',
    'agency.billing_tab': 'Fatturazione',
    'agency.name_label': "Nome dell'agenzia",
    'agency.email_label': 'E-mail',
    'agency.phone_label': 'Telefono',
    'agency.website_label': 'Sito web',
    'agency.address_label': 'Indirizzo',
    'agency.city_label': 'Città',
    'agency.description_label': 'Descrizione',
    'agency.description_placeholder': 'Racconta ai clienti della tua agenzia...',
    'agency.save_settings': 'Salva impostazioni',
    'agency.broker_usage': 'Utilizzo agenti',
    'agency.invite_broker': 'Invita agente',
    'agency.brokers': 'agenti',
    'agency.broker_limit_reached': 'Limite agenti raggiunto',
    'agency.upgrade_for_more': 'Aggiorna il tuo piano per aggiungere più agenti',
    'agency.active_listings': 'Annunci attivi',
    'agency.broker_slots_remaining': 'Posti rimanenti',
    'agency.manage_subscription': 'Gestisci abbonamento',
    'agency.upgrade_your_plan': 'Aggiorna il tuo piano',
    'agency.choose_plan': 'Scegli il piano più adatto alla tua agenzia',
    'agency.pro_plan': 'Pro',
    'agency.enterprise_plan': 'Enterprise',
    'agency.for_growing_agencies': 'Per agenzie in crescita',
    'agency.for_large_agencies': 'Per grandi agenzie',
    'agency.per_month': '/mese',
    'agency.up_to_brokers': 'Fino a {count} agenti',
    'agency.unlimited_brokers': 'Agenti illimitati',
    'agency.advanced_analytics': 'Analisi avanzate',
    'agency.full_analytics': 'Suite di analisi completa',
    'agency.priority_support': 'Supporto prioritario',
    'agency.dedicated_support': 'Supporto dedicato',
    'agency.upgrade_to_pro': 'Passa a Pro',
    'agency.upgrade_to_enterprise': 'Passa a Enterprise',
    'agency.popular': 'Popolare',
    'agency.current_plan': 'Piano attuale',
    'agency.plan_features': 'Funzionalità del piano',
    'agency.billing_history': 'Cronologia fatturazione',
    'agency.no_billing_history': 'Nessuna cronologia di fatturazione ancora',
    'agency.subscription_billing': 'Abbonamento e Fatturazione',
    'agency.manage_agency_subscription': 'Gestisci l\'abbonamento della tua agenzia',
    'agency.plan': 'Piano',
    'agency.basic_plan': 'Base',
    'agency.broker_limit': 'Limite agenti',
    'agency.current_usage': 'Utilizzo attuale',
    'agency.status': 'Stato',
    'agency.active_status': 'Attivo',
    'agency.unlimited': 'Illimitato',
    'agency.feature': 'Funzionalità',
    'agency.price': 'Prezzo',
    'agency.free': 'Gratuito',
    'agency.analytics': 'Analisi',
    'agency.basic_analytics': 'Base',
    'agency.advanced': 'Avanzato',
    'agency.full_suite': 'Suite completa',
    'agency.support': 'Supporto',
    'agency.email_support': 'Email',
    'agency.priority': 'Prioritario',
    'agency.dedicated': 'Dedicato',
    'agency_directory.title': 'Agenzie immobiliari',
    'agency_directory.subtitle': 'Sfoglia e trova agenzie immobiliari affidabili',
    'agency_directory.search_placeholder': 'Cerca agenzie...',
    'agency_directory.location_placeholder': 'Filtra per località...',
    'agency_directory.agencies_found': 'agenzie trovate',
    'agency_directory.no_agencies': 'Nessuna agenzia trovata',
    'agency_directory.no_agencies_description': 'Prova a modificare i criteri di ricerca',
    'agency_directory.brokers': 'broker',
    'agency_directory.view_agency': 'Visualizza agenzia',
    'agency_directory.not_found': 'Agenzia non trovata',
    'agency_directory.back_to_directory': 'Torna all\'elenco',
    'agency_directory.visit_website': 'Visita il sito web',
    'agency_directory.our_brokers': 'I nostri broker',
    'agency_directory.no_brokers_yet': 'Ancora nessun broker in questa agenzia',
    'agency_directory.agency_listings': 'Annunci immobiliari',
    
    // User Listings
    'userListings.title': 'I miei annunci',
    'userListings.subtitle': 'Gestisci i tuoi annunci immobiliari e creane di nuovi',
    'userListings.pricingTitle': 'Prezzi degli annunci',
    'userListings.pricingDesc': 'Crea il tuo annuncio immobiliare con i nostri prezzi semplici',
    'userListings.listingPrice': '10$ per annuncio',
    'userListings.listingDuration': 'Valido per 1 mese',
    'userListings.videoPrice': '+3$ per video',
    'userListings.videoDesc': 'Aggiungi un tour video al tuo annuncio',
    'userListings.includeVideo': 'Includi tour video',
    'userListings.createListing': 'Crea annuncio',
    'userListings.myListingsTitle': 'Le tue proprietà',
    'userListings.noListings': 'Nessun annuncio ancora',
    'userListings.noListingsDesc': 'Crea il tuo primo annuncio immobiliare per iniziare',
    'userListings.createFirst': 'Crea il tuo primo annuncio',
    'userListings.hasVideo': 'Video',
    'userListings.renew': 'Rinnova',
    'userListings.renewSuccess': 'Annuncio rinnovato!',
    'userListings.renewSuccessDesc': 'Il tuo annuncio è stato rinnovato per un altro mese',
    'userListings.paymentSuccess': 'Pagamento riuscito!',
    'userListings.paymentSuccessDesc': 'Il tuo annuncio è ora attivo',
    'userListings.paymentCancelled': 'Pagamento annullato',
    'userListings.paymentCancelledDesc': 'Il tuo pagamento è stato annullato',
    'userListings.checkoutError': 'Impossibile creare il pagamento',
    'userListings.renewError': 'Impossibile rinnovare l\'annuncio',
    'userListings.createDraft': 'Crea annuncio',
    'userListings.previewListing': 'Anteprima del tuo annuncio',
    'userListings.draft': 'Bozza',
    'userListings.drafts': 'Bozze',
    'userListings.draftSaved': 'Bozza salvata',
    'userListings.draftSavedDesc': 'Il tuo annuncio è stato salvato come bozza',
    'userListings.draftError': 'Salvataggio bozza non riuscito',
    'userListings.draftDeleted': 'Bozza eliminata',
    'userListings.deleteError': 'Eliminazione non riuscita',
    'userListings.saveDraft': 'Salva bozza',
    'userListings.saveAndPreview': 'Salva e visualizza anteprima',
    'userListings.publishOptions': 'Opzioni di pubblicazione',
    'userListings.addVideoTour': 'Aggiungi tour video',
    'userListings.videoIncluded': 'Tour video incluso',
    'userListings.totalPrice': 'Totale',
    'userListings.backToEdit': 'Torna alla modifica',
    'userListings.publishNow': 'Pubblica ora',
    'userListings.publish': 'Pubblica',
    'userListings.publishSuccess': 'Annuncio pubblicato!',
    'userListings.publishSuccessDesc': 'Il tuo annuncio è ora in attesa di revisione',
    'userListings.firstListingFree': 'Primo annuncio gratuito!',
    'userListings.firstListingFreeDesc': 'Il tuo primo annuncio immobiliare è completamente gratuito. Invialo ora e raggiungi acquirenti in tutto l\'Iraq!',
    'userListings.pending': 'In attesa di revisione',
    'userListings.pendingInfo': 'Questi annunci sono in fase di revisione dal nostro team di moderazione. Sarai notificato quando saranno approvati.',
    'userListings.pendingReview': 'In attesa di revisione',
    'userListings.awaitingApproval': 'In attesa di approvazione del moderatore',
    'userListings.rejected': 'Annunci rifiutati',
    'userListings.rejectedInfo': 'Questi annunci non sono stati approvati. Modifica e risolvi i problemi prima di inviare nuovamente.',
    'userListings.rejectedBadge': 'Rifiutato',
    'userListings.live': 'Attivo',
    'userListings.editDraft': 'Modifica',
    'userListings.editAndResubmit': 'Modifica e reinvia',
    'userListings.pendingCannotEdit': 'Impossibile modificare durante la revisione',
    'userListings.resubmitForReview': 'Reinvia per revisione',
    'userListings.resubmitInfo': 'Le tue modifiche sono state salvate. Invia nuovamente il tuo annuncio per la revisione senza costi aggiuntivi.',
    'userListings.submitForReview': 'Invia per revisione',
    'userListings.validation.priceRequired': 'Il prezzo è obbligatorio',
    'userListings.validation.cityRequired': 'La città è obbligatoria',
    'userListings.validation.roomsRequired': 'Il numero di stanze è obbligatorio',
    'userListings.validation.sizeRequired': 'La dimensione è obbligatoria',
    'userListings.validation.plotSizeRequired': 'La dimensione del terreno è obbligatoria',
    'userListings.validation.imagesRequired': 'È richiesta almeno un\'immagine',
    'userListings.choosePlan': 'Scegli il tuo piano annuncio',
    'userListings.choosePlanDesc': 'Seleziona quante immagini mostrare sulla tua scheda annuncio',
    'userListings.upgradePlan': 'Migliora il tuo piano annuncio',
    'userListings.upgradePlanDesc': 'Migliora per mostrare più immagini. Paghi solo la differenza.',
    'userListings.selectImages': 'Seleziona immagini da mostrare',
    'userListings.selectImagesDesc': 'Scegli le immagini da mostrare sulla tua scheda annuncio',
    'userListings.selectPrimaryImage': 'Seleziona immagine principale',
    'userListings.selectPrimaryImageDesc': 'Scegli l\'immagine principale che verrà mostrata per prima nel tuo annuncio',
    'userListings.addVideo': 'Aggiungi tour video',
    'userListings.pleaseSelectImages': 'Seleziona le immagini per continuare',
    'userListings.pleaseSelectPrimaryImage': 'Seleziona un\'immagine principale per continuare',
    'userListings.proceedToPayment': 'Procedi al pagamento',
    'userListings.tierBasic': 'Base',
    'userListings.tierStandard': 'Standard',
    'userListings.tierPremium': 'Premium',
    'userListings.tierBasicDesc': '1 immagine sulla scheda',
    'userListings.tierStandardDesc': 'Carosello di 3 immagini',
    'userListings.tierPremiumDesc': 'Tutte le immagini + badge',
    'userListings.tierDuration': '30 giorni attivo',
    'userListings.tierBest': 'Migliore',
    'userListings.tierBasicFull': 'Livello Base (1 immagine)',
    'userListings.tierStandardFull': 'Livello Standard (3 immagini)',
    'userListings.tierPremiumFull': 'Livello Premium (Tutte le immagini)',
    'userListings.changeTier': 'Cambia livello',
    'userListings.selectImageTier': 'Seleziona livello immagine',
    'userListings.selectTierFirst': 'Seleziona un livello prima di pubblicare',
    'userListings.upgrade': 'Aggiorna',
    'common.total': 'Totale',
    
    // Settings Page
    'settings.title': 'Impostazioni',
    'settings.subtitle': 'Gestisci le impostazioni e le preferenze del tuo account',
    'settings.profile': 'Profilo',
    'settings.notifications': 'Notifiche',
    'settings.account': 'Account',
    'settings.profile_info': 'Informazioni del profilo',
    'settings.profile_desc': 'Aggiorna le tue informazioni personali',
    'settings.change_photo': 'Cambia foto',
    'settings.display_name': 'Nome visualizzato',
    'settings.display_name_placeholder': 'Il tuo nome',
    'settings.email': 'Email',
    'settings.email_placeholder': 'tua@email.com',
    'settings.phone': 'Telefono',
    'settings.phone_placeholder': '+39 xxx xxx xxxx',
    'settings.save': 'Salva modifiche',
    'settings.saved': 'Impostazioni salvate con successo',
    'settings.save_error': 'Impossibile salvare le impostazioni',
    'settings.load_error': 'Impossibile caricare le impostazioni',
    'settings.notification_preferences': 'Preferenze di notifica',
    'settings.notification_desc': 'Scegli quali notifiche ricevere',
    'settings.notif_property_alerts': 'Avvisi sulle proprietà',
    'settings.notif_property_alerts_desc': 'Ricevi notifiche sulle nuove proprietà',
    'settings.notif_saved_search': 'Avvisi ricerche salvate',
    'settings.notif_saved_search_desc': 'Ricevi notifiche quando nuove proprietà corrispondono alle tue ricerche salvate',
    'settings.notif_newsletter': 'Newsletter',
    'settings.notif_newsletter_desc': 'Ricevi la nostra newsletter con aggiornamenti di mercato',
    'settings.notif_viewing': 'Conferme visite',
    'settings.notif_viewing_desc': 'Ricevi conferme per le visite programmate',
    'settings.account_settings': 'Impostazioni account',
    'settings.account_desc': 'Gestisci la sicurezza e le preferenze del tuo account',
    'settings.change_password': 'Cambia password',
    'settings.change_password_desc': 'Aggiorna la tua password',
    'settings.change': 'Cambia',
    'settings.two_factor': 'Autenticazione a due fattori',
    'settings.two_factor_desc': 'Aggiungi sicurezza extra al tuo account',
    'settings.enable': 'Abilita',
    'settings.disable': 'Disabilita',
    'settings.delete_account': 'Elimina account',
    'settings.delete_account_desc': 'Elimina permanentemente il tuo account',
    'settings.delete': 'Elimina',
    'settings.logout': 'Esci',
    'settings.invalid_image': 'Seleziona un file immagine',
    'settings.image_too_large': 'L\'immagine deve essere inferiore a 2MB',
    'settings.avatar_uploaded': 'Foto caricata',
    'settings.avatar_error': 'Impossibile caricare la foto',
    'settings.current_password': 'Password attuale',
    'settings.new_password': 'Nuova password',
    'settings.confirm_password': 'Conferma nuova password',
    'settings.password_changed': 'Password cambiata con successo',
    'settings.password_error': 'Impossibile cambiare la password',
    'settings.passwords_mismatch': 'Le password non corrispondono',
    'settings.password_too_short': 'La password deve avere almeno 6 caratteri',
    'settings.2fa_enabled': 'Autenticazione a due fattori abilitata',
    'settings.2fa_disabled': 'Autenticazione a due fattori disabilitata',
    'settings.2fa_error': 'Impossibile configurare 2FA',
    'settings.scan_qr': 'Scansiona questo codice QR con la tua app di autenticazione',
    'settings.enter_code': 'Inserisci il codice di verifica',
    'settings.verify': 'Verifica',
    
    // Contact Form
    'contact_form.title': 'Contatta l\'agente',
    'contact_form.name_placeholder': 'Il tuo nome',
    'contact_form.email_placeholder': 'La tua email',
    'contact_form.phone_placeholder': 'Il tuo telefono (opzionale)',
    'contact_form.message_placeholder': 'Ciao! Sono interessato alla proprietà in {address} e vorrei saperne di più.',
    'contact_form.send': 'Invia messaggio',
    'contact_form.sending': 'Invio...',
    'contact_form.success': 'Messaggio inviato con successo!',
    'contact_form.error': 'Impossibile inviare il messaggio',
    'contact_form.note': 'La tua richiesta sarà inviata direttamente all\'agente responsabile.',
    
    // Messaging
    'messaging.title': 'Messaggi',
    'messaging.description': 'Le tue conversazioni private',
    'messaging.new_message': 'Nuovo messaggio',
    'messaging.no_messages': 'Nessun messaggio ancora',
    'messaging.no_conversations': 'Nessuna conversazione ancora',
    'messaging.send': 'Invia',
    'messaging.send_message': 'Invia messaggio',
    'messaging.message_to': 'Invia messaggio a',
    'messaging.subject': 'Oggetto',
    'messaging.subject_placeholder': 'Oggetto (facoltativo)',
    'messaging.message': 'Messaggio',
    'messaging.message_placeholder': 'Scrivi il tuo messaggio...',
    'messaging.login_required': 'Devi effettuare il login per inviare messaggi.',
    'messaging.message_sent': 'Messaggio inviato con successo',
    'messaging.contact_agent_description': "Contatta direttamente l'agente immobiliare",
    'messaging.type_message': 'Scrivi un messaggio...',
    'messaging.inbox': 'Posta in arrivo',
    'messaging.sent': 'Inviati',
    'messaging.search': 'Cerca conversazioni...',
    'messaging.select_conversation': 'Seleziona una conversazione',
    'messaging.select_description': 'Scegli una conversazione dalla lista per iniziare a chattare',
    'messaging.encrypted': 'Crittografia end-to-end',
    'messaging.decrypt': 'Decifra messaggio',
    'messaging.reply': 'Rispondi',
    'messaging.replying_to': 'In risposta a',
    'messaging.replying_to_yourself': 'te stesso',
    'messaging.forward': 'Inoltra',
    'messaging.delete': 'Elimina',
    'messaging.delete_confirm': 'Elimina messaggio',
    'messaging.delete_description': 'Questo messaggio verrà eliminato per tutti. Questa azione non può essere annullata.',
    'messaging.delete_for_everyone': 'Elimina per tutti',
    'messaging.pin': 'Fissa',
    'messaging.unpin': 'Rimuovi',
    'messaging.pinned_messages': 'Messaggi fissati',
    'messaging.no_pinned': 'Nessun messaggio fissato',
    'messaging.delivered': 'Consegnato',
    'messaging.read': 'Letto',
    'messaging.seen_at': 'Visto alle',
    'messaging.voice_message': 'Messaggio vocale',
    'messaging.search_messages': 'Cerca nei messaggi...',
    'messaging.contacts': 'Contatti',
    'messaging.results_found': 'risultati trovati',
    'messaging.recording': 'Registrazione...',
    'messaging.typing': 'sta scrivendo...',
    'messaging.online': 'online',
    'messaging.tap_for_info': 'tocca qui per info contatto',
    'messaging.photos': 'Foto',
    'messaging.camera': 'Fotocamera',
    'messaging.location': 'Posizione',
    'messaging.contact': 'Contatto',
    'messaging.document': 'Documento',
    'messaging.poll': 'Sondaggio',
    'messaging.event': 'Evento',
    'messaging.location_coming_soon': 'Condivisione posizione in arrivo',
    'messaging.contact_coming_soon': 'Condivisione contatto in arrivo',
    'messaging.poll_coming_soon': 'Creazione sondaggio in arrivo',
    'messaging.event_coming_soon': 'Creazione evento in arrivo',
    'messaging.schedule_event': 'Pianifica evento',
    'messaging.event_title': 'Titolo evento',
    'messaging.event_title_placeholder': 'es: Visita immobiliare',
    'messaging.event_date': 'Data',
    'messaging.event_time': 'Ora',
    'messaging.event_location': 'Luogo',
    'messaging.event_location_placeholder': 'Inserisci luogo o indirizzo',
    'messaging.event_description': 'Descrizione',
    'messaging.event_description_placeholder': 'Aggiungi ulteriori dettagli...',
    'messaging.send_event': 'Invia evento',
    'messaging.event_created': 'Evento creato',
    'messaging.create_poll': 'Crea sondaggio',
    'messaging.poll_question': 'Domanda',
    'messaging.poll_question_placeholder': 'Cosa vorresti chiedere?',
    'messaging.poll_options': 'Opzioni',
    'messaging.option': 'Opzione',
    'messaging.add_option': 'Aggiungi opzione',
    'messaging.allow_multiple': 'Consenti risposte multiple',
    'messaging.send_poll': 'Invia sondaggio',
    'messaging.poll_created': 'Sondaggio creato',
    'messaging.edit': 'Modifica',
    'messaging.edit_message': 'Modifica messaggio',
    'messaging.edit_description': 'Apporta modifiche al tuo messaggio',
    'messaging.edit_time_remaining': 'Hai',
    'messaging.minutes_to_edit': 'minuti per modificare questo messaggio',
    'messaging.message_edited': 'Messaggio modificato',
    'messaging.edit_failed': 'Modifica messaggio fallita',
    'messaging.from_date': 'Da',
    'messaging.to_date': 'A',
    'messaging.clear_filters': 'Cancella filtri',
    'messaging.export_chat': 'Esporta conversazione',
    'messaging.export_description': 'Esporta la cronologia della conversazione',
    'messaging.export_text': 'File di testo (.txt)',
    'messaging.export_text_desc': 'Formato testo semplice, facile da leggere e condividere',
    'messaging.export_pdf': 'Documento PDF',
    'messaging.export_pdf_desc': 'Documento formattato, ideale per la stampa',
    'messaging.export_messages_count': 'messaggi saranno esportati',
    'messaging.exporting': 'Esportazione...',
    'messaging.export': 'Esporta',
    'messaging.export_success': 'Conversazione esportata con successo',
    'messaging.export_failed': 'Esportazione fallita',
    'messaging.is_typing': 'sta scrivendo...',
    
    // Quick Reply Templates
    'quick_reply.title': 'Risposte Rapide',
    'quick_reply.greeting': 'Ciao! Grazie per averci contattato.',
    'quick_reply.interested': 'Sono interessato a questa proprietà. Può fornirmi maggiori dettagli?',
    'quick_reply.schedule_viewing': 'Vorrei programmare una visita. Quali orari sono disponibili?',
    'quick_reply.ask_price': 'Il prezzo è trattabile?',
    'quick_reply.ask_availability': 'Questa proprietà è ancora disponibile?',
    'quick_reply.thank_you': 'Grazie per le informazioni. La ricontatterò presto.',
    'quick_reply.will_contact': 'La contatterò a breve con maggiori dettagli.',
    'quick_reply.not_interested': 'Grazie, ma non sono più interessato a questa proprietà.',
    
    // Notifications
    'notifications.enable': 'Attiva notifiche',
    'notifications.on': 'Notifiche attive',
    'notifications.preferences': 'Preferenze notifiche',
    'notifications.sound': 'Suono notifica',
    'notifications.sound_desc': 'Riproduci un suono quando ricevi un messaggio',
    'notifications.sound_default': 'Predefinito',
    'notifications.sound_chime': 'Campanello',
    'notifications.sound_bell': 'Campana',
    'notifications.sound_pop': 'Pop',
    'notifications.test': 'Prova',
    'notifications.vibration': 'Vibrazione',
    'notifications.vibration_desc': 'Vibra quando ricevi un messaggio (solo mobile)',
    'messaging.you': 'Tu',
    'messaging.encrypted_message': 'Messaggio crittografato',
    
    // Status
    'status.online': 'Online',
    'status.offline': 'Offline',
    'status.last_seen': 'Visto l\'ultima volta',
    
    // Blocking
    'blocking.block_user': 'Blocca utente',
    'blocking.blocked_users': 'Utenti bloccati',
    'blocking.unblock': 'Sblocca',
    'blocking.you_blocked_user': 'Hai bloccato questo utente. Sblocca per inviare messaggi.',
    'blocking.user_blocked_you': 'Questo utente ti ha bloccato. Non puoi inviare messaggi.',
    'reporting.report_user': 'Segnala utente',
    'admin.reports': 'Segnalazioni',
    'admin.reports_panel': 'Segnalazioni utenti',
    'admin.pending': 'In attesa',
    'admin.resolved': 'Risolto',
    'admin.all': 'Tutti',
    'messaging.unknown_user': 'Utente sconosciuto',
    'messaging.no_groups': 'Nessun gruppo ancora',
    'messaging.create_group': 'Crea gruppo',
    'messaging.group_chat': 'Chat di gruppo',
    'messaging.direct_messages': 'Diretto',
    'messaging.groups': 'Gruppi',
    'messaging.group_name': 'Nome gruppo',
    'messaging.group_description': 'Descrizione',
    'messaging.e2e_notice': 'I messaggi e le chiamate sono crittografati end-to-end. Solo le persone in questa chat possono leggerli, ascoltarli o condividerli.',
    'messaging.today': 'Oggi',
    'messaging.yesterday': 'Ieri',
    'messaging.search_groups': 'Cerca gruppi...',
    
    // Attachment viewer
    'attachment.all_media': 'Tutti i media',
    'attachment.go_to_message': 'Vai al messaggio',
    'attachment.download': 'Scarica',
    'attachment.view_fullscreen': 'Visualizza a schermo intero',
    'attachment.save_to_gallery': 'Salva nella galleria',
    'attachment.share': 'Condividi',
    'attachment.double_tap_zoom': 'Tocca due volte per ingrandire',
    'attachment.zoom_in': 'Ingrandisci',
    'attachment.zoom_out': 'Riduci',
    'attachment.reset_zoom': 'Reimposta zoom',
    'attachment.ctrl_scroll_zoom': 'Ctrl + Scorri per zoom',
    'attachment.fit_width': 'Adatta alla larghezza',
    'attachment.fit_height': 'Adatta all\'altezza',
    'attachment.click_to_navigate': 'Clicca per navigare',
    
    'nav.favorites': 'Preferiti',
    'nav.profile': 'Profilo',
    'nav.login': 'Accedi',
    'nav.logout': 'Esci',
    'nav.recent_searches': 'Ricerche recenti',
    'nav.no_recent_searches': 'Nessuna ricerca recente',
    
    'common.back': 'Indietro',
    
    // Agents
    'agents.title': 'Trova un agente',
    'agents.subtitle': 'Stai pensando di vendere? Ti aiutiamo a trovare agenti esperti nella tua zona!',
    'agents.area_search': 'Ricerca per zona',
    'agents.name_search': 'Ricerca per nome',
    'agents.search_placeholder': 'Inserisci zona o indirizzo',
    'agents.show': 'Mostra',
    'agents.sort_by': 'Ordina per',
    'agents.agents_count': 'Agenti',
    'agents.companies_label': 'Agenzie immobiliari',
    'agents.recent_sales': 'Ultima data di vendita',
    'agents.most_sales': 'Più vendite',
    'agents.alphabetical': 'Ordine alfabetico',
    'agents.showing_results': 'Mostrando',
    'agents.agents_label': 'agenti',
    'agents.loading': 'Caricamento agenti...',
    'agents.no_results': 'Nessun agente trovato.',
    'agents.phone': 'Telefono:',
    'agents.status': 'Stato:',
    'agents.active_agent': 'Agente attivo',
    'agents.anonymous_agent': 'Agente anonimo',
    'agents.agent_label': 'Agente',
    'agents.show_more': 'Mostra più agenti',
    'agents.verified_agent': 'Agente verificato',
    
    // Broker Public Profile
    'broker.listing_usage': 'Utilizzo annunci',
    'broker.monthly_listing_limits': 'Limiti mensili annunci',
    'broker.listings_used': 'Annunci usati',
    'broker.listings_remaining': 'annunci rimanenti',
    'broker.period_resets': 'Il periodo si reimposta',
    'broker.export_csv': 'Esporta CSV',
    'broker.total_views': 'Visualizzazioni totali',
    'broker.scheduled_viewings': 'Visite programmate',
    'broker.client_messages': 'Messaggi clienti',
    'broker.filters': 'Filtri',
    'broker.all_time': 'Tutto il tempo',
    'broker.active_listings': 'Annunci attivi',
    'broker.sold_listings': 'Annunci venduti',
    'broker.active_listings_desc': 'Le tue proprietà attualmente pubblicate',
    'broker.no_active_listings': 'Nessun annuncio attivo. Aggiungi la tua prima proprietà.',
    'broker.all_types': 'Tutti i tipi',
    'broker.all_cities': 'Tutte le città',
    'broker.property_type': 'Tipo di proprietà',
    'broker.city': 'Città',
    'broker.date_range': 'Intervallo date',
    'broker.last_week': 'Ultima settimana',
    'broker.last_month': 'Ultimo mese',
    'broker.last_quarter': 'Ultimi 3 mesi',
    'broker.sold_listings_desc': 'Proprietà che hai venduto',
    'broker.no_sold_listings': 'Nessuna proprietà venduta ancora',
    'broker.clear_filters': 'Cancella filtri',
    'broker.selected': 'selezionato/i',
    'broker.compare': 'Confronta',
    'broker.mark_as_sold': 'Segna come venduto',
    'broker.delete_selected': 'Elimina selezionati',
    'broker.select_all': 'Seleziona tutto',
    'broker.rejected_listings': 'Annunci rifiutati',
    'broker.rejected_listings_desc': 'Questi annunci sono stati rifiutati dai moderatori. Puoi presentare un ricorso.',
    'broker.no_rejected_listings': 'Nessun annuncio rifiutato',
    'broker.confirm_delete': 'Conferma eliminazione',
    'broker_public.not_found': 'Agente non trovato',
    'broker_public.not_found_description': 'Il profilo dell\'agente che stai cercando non esiste.',
    'broker_public.back_to_search': 'Torna alla ricerca agenti',
    'broker_public.contact_broker': 'Contatta agente',
    'broker_public.agency': 'Agenzia',
    'broker_public.visit_website': 'Visita sito web',
    'broker_public.active_listings': 'Annunci attivi',
    'broker_public.sold_properties': 'Proprietà vendute',
    'broker_public.total_sales': 'Vendite totali',
    'broker_public.avg_days_market': 'Giorni medi sul mercato',
    'broker_public.no_active_listings': 'Nessun annuncio attivo al momento',
    'broker_public.no_sold_properties': 'Nessuna proprietà venduta finora',
    'broker_public.view_all_properties': 'Vedi tutte le proprietà',
    'broker_public.send_message': 'Invia messaggio',
    'broker_public.view_agency': 'Vedi agenzia',
    'broker_public.works_at': 'Lavora presso',
    'broker_public.listings': 'Annunci',
    'broker_public.sold': 'Venduto',
    'broker_public.reviews': 'Recensioni',
    'broker_public.rating': 'Valutazione',
    'broker_public.featured_broker': 'Agente in evidenza',
    'broker_public.top_performer': 'Top performer',
    
    // Toast messages
    'toast.logged_out': 'Disconnesso',
    'toast.welcome_back': 'Torna quando vuoi!',
    
    // Recent searches
    'search.properties_label': 'Proprietà',
    'search.from': 'da',
    'search.up_to': 'fino a',
    
    // Agency Pricing
    'agency_pricing.badge': 'Piani Agenzia',
    'agency_pricing.title': 'Fai crescere la tua agenzia con il piano giusto',
    'agency_pricing.subtitle': 'Scegli il piano perfetto per la tua agenzia immobiliare.',
    'agency_pricing.most_popular': 'Più popolare',
    'agency_pricing.per_month': '/mese',
    'agency_pricing.processing': 'Elaborazione...',
    'agency_pricing.comparison_title': 'Confronto dettagliato',
    'agency_pricing.feature': 'Funzionalità',
    'agency_pricing.faq_title': 'Domande frequenti',
    'agency_pricing.cta_title': 'Pronto a far crescere la tua agenzia?',
    'agency_pricing.cta_subtitle': 'Unisciti a migliaia di agenzie immobiliari di successo.',
    'agency_pricing.cta_button': 'Inizia con Pro',
    'agency_pricing.sign_in_required': 'Accedi per aggiornare',
    'agency_pricing.tier.starter': 'Starter',
    'agency_pricing.tier.starter_desc': 'Perfetto per piccole agenzie',
    'agency_pricing.tier.starter_cta': 'Inizia',
    'agency_pricing.tier.pro': 'Pro',
    'agency_pricing.tier.pro_desc': 'Per agenzie in crescita',
    'agency_pricing.tier.pro_cta': 'Passa a Pro',
    'agency_pricing.tier.enterprise': 'Enterprise',
    'agency_pricing.tier.enterprise_desc': 'Per grandi agenzie',
    'agency_pricing.tier.enterprise_cta': 'Scegli Enterprise',
    'agency_pricing.feature.brokers_3': 'Fino a 3 broker',
    'agency_pricing.feature.brokers_10': 'Fino a 10 broker',
    'agency_pricing.feature.brokers_unlimited': 'Broker illimitati',
    'agency_pricing.feature.listings_basic': 'Annunci base',
    'agency_pricing.feature.listings_advanced': 'Annunci avanzati',
    'agency_pricing.feature.listings_premium': 'Annunci premium',
    'agency_pricing.feature.messaging': 'Messaggistica clienti',
    'agency_pricing.feature.scheduling': 'Pianificazione visite',
    'agency_pricing.feature.analytics': 'Dashboard analisi',
    'agency_pricing.feature.analytics_advanced': 'Analisi avanzata',
    'agency_pricing.feature.support': 'Supporto prioritario',
    'agency_pricing.feature.support_247': 'Supporto 24/7',
    'agency_pricing.feature.branding': 'Branding personalizzato',
    'agency_pricing.feature.api': 'Accesso API',
    'agency_pricing.comparison.brokers': 'Numero di broker',
    'agency_pricing.comparison.listings': 'Annunci immobiliari',
    'agency_pricing.comparison.basic': 'Base',
    'agency_pricing.comparison.advanced': 'Avanzato',
    'agency_pricing.comparison.premium': 'Premium',
    'agency_pricing.faq.change_plan_q': 'Posso cambiare piano dopo?',
    'agency_pricing.faq.change_plan_a': 'Sì! Puoi cambiare in qualsiasi momento.',
    'agency_pricing.faq.exceed_limit_q': 'Cosa succede se supero il limite?',
    'agency_pricing.faq.exceed_limit_a': 'Dovrai passare a un piano superiore.',
    'agency_pricing.faq.trial_q': 'C\'è una prova gratuita?',
    'agency_pricing.faq.trial_a': 'Garanzia di rimborso di 14 giorni.',
    'agency_pricing.faq.cancel_q': 'Come annullo l\'abbonamento?',
    'agency_pricing.faq.cancel_a': 'Gestisci l\'abbonamento dal pannello.',
    'agency_pricing.view_plans': 'Vedi piani e prezzi',
    
    // Broker Pricing
    'broker_pricing.badge': 'Piani Broker',
    'broker_pricing.title': 'Aggiorna il tuo account broker',
    'broker_pricing.subtitle': 'Scegli il piano perfetto per la tua attività immobiliare. Più annunci, analisi migliori e supporto prioritario.',
    'broker_pricing.most_popular': 'Più popolare',
    'broker_pricing.current_plan': 'Il tuo piano attuale',
    'broker_pricing.your_current_plan': 'Il tuo piano attuale',
    'broker_pricing.free': 'Gratuito',
    'broker_pricing.month': 'mese',
    'broker_pricing.per_month': '/mese',
    'broker_pricing.processing': 'Elaborazione...',
    'broker_pricing.loading': 'Caricamento...',
    'broker_pricing.manage_subscription': 'Gestisci abbonamento',
    'broker_pricing.cancel_anytime': 'Declassa o annulla in qualsiasi momento • Si rinnova automaticamente ogni mese',
    'broker_pricing.manage_note': 'Declassa o annulla in qualsiasi momento • Si rinnova automaticamente ogni mese',
    'broker_pricing.downgrade': 'Declassa',
    'broker_pricing.upgrade_to_pro': 'Passa a Pro',
    'broker_pricing.go_enterprise': 'Vai a Enterprise',
    'broker_pricing.get_started_pro': 'Inizia con Pro',
    'broker_pricing.comparison_title': 'Confronto dettagliato delle funzionalità',
    'broker_pricing.feature': 'Funzionalità',
    'broker_pricing.faq_title': 'Domande frequenti',
    'broker_pricing.cta_title': 'Pronto a far crescere il tuo business?',
    'broker_pricing.cta_subtitle': 'Unisciti a migliaia di broker di successo che usano la nostra piattaforma per pubblicare più proprietà e concludere più affari.',
    'broker_pricing.cta_button': 'Inizia con Pro',
    'broker_pricing.sign_in_required': 'Accedi per aggiornare il tuo piano',
    'broker_pricing.already_on_plan': 'Sei già su questo piano',
    'broker_pricing.checkout_error': 'Impossibile avviare il pagamento',
    'broker_pricing.portal_error': 'Impossibile aprire la gestione abbonamento',
    'broker_pricing.tier.normal': 'Normale',
    'broker_pricing.tier.normal_desc': 'Perfetto per broker individuali che iniziano',
    'broker_pricing.tier.normal_cta': 'Piano attuale',
    'broker_pricing.tier.pro': 'Pro',
    'broker_pricing.tier.pro_desc': 'Per broker in crescita con più annunci',
    'broker_pricing.tier.pro_cta': 'Passa a Pro',
    'broker_pricing.tier.enterprise': 'Enterprise',
    'broker_pricing.tier.enterprise_desc': 'Per broker ad alto volume con esigenze illimitate',
    'broker_pricing.tier.enterprise_cta': 'Vai a Enterprise',
    'broker_pricing.feature.listings_5': '5 annunci al mese',
    'broker_pricing.feature.listings_10': '10 annunci al mese',
    'broker_pricing.feature.listings_15': '15 annunci al mese',
    'broker_pricing.feature.basic_features': 'Funzionalità base per proprietà',
    'broker_pricing.feature.advanced_features': 'Funzionalità avanzate per proprietà',
    'broker_pricing.feature.premium_features': 'Funzionalità premium per proprietà',
    'broker_pricing.feature.client_messaging': 'Messaggistica clienti',
    'broker_pricing.feature.viewing_scheduling': 'Pianificazione visite',
    'broker_pricing.feature.video_tours_paid': 'Tour video ($3 per annuncio)',
    'broker_pricing.feature.video_tours_free': 'Tour video gratuiti inclusi',
    'broker_pricing.feature.analytics': 'Dashboard analisi',
    'broker_pricing.feature.advanced_analytics': 'Analisi avanzata',
    'broker_pricing.feature.priority_support': 'Supporto prioritario',
    'broker_pricing.feature.support_24_7': 'Supporto prioritario 24/7',
    'broker_pricing.feature.featured_listings': 'Annunci in evidenza',
    'broker_pricing.feature.extra_listings': 'Opzione annunci extra',
    'broker_pricing.feature.extra_10': '+$10 per annuncio extra',
    'broker_pricing.feature.messaging': 'Messaggistica clienti',
    'broker_pricing.feature.scheduling': 'Pianificazione visite',
    'broker_pricing.feature.video_paid': 'Tour video ($3 per annuncio)',
    'broker_pricing.feature.video_free': 'Tour video gratuiti inclusi',
    'broker_pricing.feature.analytics_advanced': 'Analisi avanzata',
    'broker_pricing.feature.support': 'Supporto prioritario',
    'broker_pricing.feature.support_247': 'Supporto prioritario 24/7',
    'broker_pricing.feature.featured': 'Annunci in evidenza',
    'broker_pricing.feature.extra_price': '+$10 per annuncio extra',
    'broker_pricing.table.listings_per_month': 'Annunci al mese',
    'broker_pricing.table.video_tours': 'Tour video',
    'broker_pricing.table.extra_listings': 'Annunci extra',
    'broker_pricing.table.listing_duration': 'Durata annuncio',
    'broker_pricing.table.client_messaging': 'Messaggistica clienti',
    'broker_pricing.table.analytics_dashboard': 'Dashboard analisi',
    'broker_pricing.table.priority_support': 'Supporto prioritario',
    'broker_pricing.table.featured_listings': 'Annunci in evidenza',
    'broker_pricing.table.15_extras': '15 + extra',
    'broker_pricing.table.3_per_listing': '$3 per annuncio',
    'broker_pricing.table.10_each': '$10 ciascuno',
    'broker_pricing.table.30_days': '30 giorni',
    'broker_pricing.comparison.listings': 'Annunci al mese',
    'broker_pricing.comparison.video': 'Tour video',
    'broker_pricing.comparison.extra': 'Annunci extra',
    'broker_pricing.comparison.duration': 'Durata annuncio',
    'broker_pricing.comparison.featured': 'Annunci in evidenza',
    'broker_pricing.comparison.30_days': '30 giorni',
    'broker_pricing.comparison.per_listing': '$3 per annuncio',
    'broker_pricing.comparison.free': 'Gratuito',
    'broker_pricing.comparison.each': '$10 ciascuno',
    'broker_pricing.comparison.15_plus': '15 + extra',
    'broker_pricing.faq.autorenew_q': 'Il mio abbonamento si rinnova automaticamente?',
    'broker_pricing.faq.autorenew_a': 'Sì! Il tuo abbonamento si rinnova automaticamente ogni mese così non perdi mai l\'accesso ai tuoi annunci. Sarai addebitato lo stesso giorno ogni mese. Puoi annullare in qualsiasi momento dal portale di gestione abbonamento.',
    'broker_pricing.faq.upgrade_q': 'Posso passare a un piano superiore o inferiore in qualsiasi momento?',
    'broker_pricing.faq.upgrade_a': 'Assolutamente! Gli upgrade hanno effetto immediato con fatturazione proporzionale. I downgrade si applicano alla fine del periodo di fatturazione corrente. Clicca su "Gestisci abbonamento" per apportare modifiche.',
    'broker_pricing.faq.expire_q': 'Cosa succede quando i miei annunci scadono?',
    'broker_pricing.faq.expire_a': 'Gli annunci scadono dopo 30 giorni. Riceverai una notifica email 3 giorni prima della scadenza. Puoi rinnovarli usando i tuoi slot mensili gratuiti o pagare $10 per annuncio extra (solo Enterprise).',
    'broker_pricing.faq.extra_q': 'Cosa sono gli "annunci extra"?',
    'broker_pricing.faq.extra_a': 'I membri Enterprise possono acquistare annunci aggiuntivi a $10 ciascuno oltre ai 15 annunci mensili gratuiti. Ideale per mesi ad alto volume.',
    'broker_pricing.faq.cancel_q': 'Come annullo il mio abbonamento?',
    'broker_pricing.faq.cancel_a': 'Clicca su "Gestisci abbonamento" per accedere al tuo portale di fatturazione dove puoi annullare, declassare o aggiornare il metodo di pagamento. Le cancellazioni hanno effetto alla fine del periodo di fatturazione.',
    'messaging.conversation_pinned': 'Conversazione fissata',
    'messaging.conversation_unpinned': 'Conversazione rimossa',
    'messaging.pin_chat': 'Fissa conversazione',
    'messaging.unpin_chat': 'Rimuovi conversazione',
    'messaging.pin_failed': 'Impossibile fissare la conversazione',
    'messaging.unpin_failed': 'Impossibile rimuovere la conversazione',
    'messaging.archive': 'Archivia',
    'messaging.unarchive': 'Ripristina',
    'messaging.show_archived': 'Archiviate',
    'messaging.show_active': 'Mostra attive',
    'messaging.conversation_archived': 'Conversazione archiviata',
    'messaging.conversation_unarchived': 'Conversazione ripristinata',
    'messaging.archive_failed': 'Archiviazione fallita',
    'messaging.unarchive_failed': 'Ripristino fallito',
    
    // Hero/News Section
    'hero.find_dream_home': 'Trova la tua casa dei sogni in Iraq',
    'hero.explore_description': 'Esplora migliaia di case in vendita e prezzi finali da tutto l\'Iraq. Ottieni valutazioni, leggi notizie immobiliari e contatta agenti.',
    'hero.badge': 'La principale piattaforma immobiliare dell\'Iraq',
  },
  pt: {
    // Header
    'nav.search': 'Pesquisar propriedades',
    'nav.search_agents': 'Pesquisar agentes',
    'nav.sell': 'Vender propriedade',
    'nav.news': 'Notícias',
    'nav.build': 'Construir casa',
    'nav.agencies': 'Agências',
    'nav.buy': 'Comprar',
    'nav.valuation': 'Avaliação',
    'nav.more': 'Mais',
    'nav.saved': 'Salvo',
    'nav.admin': 'Admin',
    'nav.agency': 'Agência',
    'nav.agency_dashboard': 'Painel da agência',
    'nav.location_manager': 'Gerenciador de locais',
    'nav.map_configuration': 'Configuração de mapa',
    'nav.hello': 'Olá',
    'nav.menu': 'Menu',
    'nav.manage_news': 'Gerenciar notícias',
    'nav.all_properties': 'Todas as propriedades',
    'nav.new_production': 'Novas construções',
    
    // Cookie Policy
    'cookie.title': 'Política de Cookies',
    'cookie.last_updated': 'Última atualização:',
    'cookie.section1.title': 'Secção 1 - Disposições gerais relativas à implementação de tecnologias de rastreamento',
    'cookie.section1.p1': 'A Ploqs AB, doravante designada como "Prestador de Serviços", juntamente com os parceiros e subcontratados contratados pelo Prestador de Serviços, implementa e utiliza diversas formas de mecanismos de rastreamento digital, incluindo, mas não se limitando a, cookies, web beacons, pixel tags e outras soluções tecnológicas semelhantes (coletivamente designadas como "Tecnologias de Rastreamento") nas plataformas digitais, websites e aplicações móveis do Prestador de Serviços (coletivamente designadas como "Canais Digitais do Prestador de Serviços"). Esta política para o tratamento de Tecnologias de Rastreamento especifica os propósitos para os quais e os métodos pelos quais o Prestador de Serviços aplica as referidas tecnologias.',
    'cookie.section1.p2': 'As Tecnologias de Rastreamento consistem principalmente em ficheiros de dados textuais mais pequenos que são depositados no equipamento terminal do utilizador ou no programa do navegador web com o propósito de agregar informações relativas à interação do utilizador com as plataformas digitais e aplicações móveis e a navegação nas mesmas.',
    'cookie.section1.h1': 'Processamento de dados pessoais e considerações de proteção de dados',
    'cookie.section1.p3': 'A informação recolhida através da aplicação de Tecnologias de Rastreamento constitui na maioria dos casos dados que, direta ou indiretamente e em combinação com outros dados disponíveis, podem potencialmente ser usados para identificar indivíduos, incluindo, mas não se limitando a, endereços IP, identificadores únicos de cookies, identificadores de publicidade (IDFA), identificadores de dispositivos e vários metadados que permitem a diferenciação de equipamentos terminais específicos.',
    'cookie.section1.h2': 'Escolhas do utilizador',
    'cookie.section1.p4': 'O utilizador tem o direito de rever os consentimentos anteriormente dados em qualquer momento navegando para as opções de gestão de consentimento fornecidas através da interface digital do Prestador de Serviços.',
    'cookie.section1.h3': 'Persistência e períodos de armazenamento das Tecnologias de Rastreamento',
    'cookie.section1.p5': 'As Tecnologias de Rastreamento exibem persistência variável dependendo do propósito pretendido e da funcionalidade de cada tecnologia. As Tecnologias de Rastreamento baseadas em sessão são iniciadas quando o utilizador se conecta à plataforma digital e são terminadas o mais tardar ao fechar a instância do navegador do utilizador. As Tecnologias de Rastreamento persistentes são retidas no navegador do utilizador por um período de tempo predeterminado que excede as sessões individuais.',
    'cookie.section2.title': 'Secção 2 - Tecnologias de Rastreamento obrigatórias para a prestação de serviços',
    'cookie.section2.p1': 'As Tecnologias de Rastreamento obrigatórias referem-se às implementações tecnológicas que são necessárias para que o Prestador de Serviços forneça funcionalidade básica e essencial ao utilizador, por exemplo, com o propósito de manter informações relativas aos consentimentos anteriormente dados pelo utilizador relativamente às Tecnologias de Rastreamento, para permitir a diferenciação entre sessões de utilizador distintas através de identificadores anonimizados, para facilitar a funcionalidade cartográfica, para permitir a reprodução de material audiovisual em anúncios de propriedades carregados por agentes imobiliários, para preservar as preferências de pesquisa guardadas do utilizador, para manter a segurança dos serviços, prevenir o uso indevido dos serviços e para garantir um desempenho de serviço contínuo e ótimo.',
    'cookie.section2.legal': 'Base legal para o processamento:',
    'cookie.section2.legal_value': 'Interesse legítimo de acordo com o Artigo 6.1(f) do RGPD',
    'cookie.section2.storage': 'Período de armazenamento:',
    'cookie.section2.storage_value': 'Até vinte e quatro (24) meses para Tecnologias de Rastreamento persistentes; as Tecnologias de Rastreamento baseadas em sessão são terminadas imediatamente ao fechar a instância do navegador.',
    'cookie.section3.title': 'Secção 3 - Tecnologias de Rastreamento para propósitos analíticos',
    'cookie.section3.p1': 'O Prestador de Serviços atribui importância significativa à capacidade de quantificar a frequência de visitas, registar dados de interação na plataforma digital e em aplicações móveis, e de outra forma analisar como os serviços digitais do Prestador de Serviços são usados pelos utilizadores. Desde que o utilizador tenha dado o consentimento necessário, o Prestador de Serviços implementa Tecnologias de Rastreamento, excluindo dados pessoais diretos, com o propósito de analisar e desenvolver ainda mais os serviços digitais do Prestador de Serviços e de oferecer continuamente ao utilizador uma experiência de utilizador otimizada, manter o desempenho do serviço e produzir análises de grupos-alvo e compilações estatísticas relativas ao uso da plataforma.',
    'cookie.section3.legal_value': 'Consentimento de acordo com o Artigo 6.1(a) do RGPD',
    'cookie.section3.storage_value': 'Até vinte e seis (26) meses',
    'cookie.section4.title': 'Secção 4 - Tecnologias de Rastreamento para propósitos de personalização',
    'cookie.section4.p1': 'As Tecnologias de Rastreamento implementadas para propósitos de personalização visam adaptar a experiência do utilizador nas plataformas digitais do Prestador de Serviços fornecendo conteúdo com relevância pessoal aumentada para o utilizador individual. Esta adaptação baseia-se, por exemplo, no comportamento de pesquisa e padrões de interação do utilizador. No caso de o utilizador possuir uma conta de utilizador registada junto do Prestador de Serviços, estes dados comportamentais são combinados com informações que o utilizador fornece voluntariamente no âmbito do serviço, por exemplo, informações relativas às condições de habitação do utilizador, através das quais são feitas adaptações de conteúdo com base no perfil agregado do utilizador.',
    'cookie.section5.title': 'Secção 5 - Tecnologias de Rastreamento para propósitos de marketing',
    'cookie.section5.p1': 'O Prestador de Serviços implementa Tecnologias de Rastreamento para propósitos de marketing, desde que o utilizador tenha dado o consentimento necessário, com o propósito de permitir a exposição de conteúdo publicitário relevante e anúncios de propriedades com relevância aumentada para o utilizador com base num perfil publicitário construído.',
    'cookie.section5.p2': 'Através da implementação de Tecnologias de Rastreamento, o Prestador de Serviços também garante que as exposições publicitárias são limitadas a um número predefinido de ocasiões por utilizador, e que a eficácia publicitária é medida e analisada. O conteúdo publicitário do Prestador de Serviços também pode ser exposto em plataformas digitais externas.',
    'cookie.section5.p3': 'Deve notar-se que no caso de o utilizador optar por desativar as Tecnologias de Rastreamento para propósitos de marketing, o conteúdo publicitário continuará a ser exposto ao utilizador nas plataformas digitais do Prestador de Serviços, contudo sem personalização.',
    'cookie.section5.storage_value': 'Até vinte e quatro (24) meses',
    'cookie.section6.title': 'Secção 6 - Transferências internacionais de dados para países terceiros',
    'cookie.section6.p1': 'Na ferramenta de gestão de consentimento do Prestador de Serviços, são fornecidas informações relativas às Tecnologias de Rastreamento e parceiros que processam os dados pessoais do utilizador dentro de, ou transferem os dados pessoais do utilizador para, jurisdições fora da União Europeia e do Espaço Económico Europeu (os chamados "países terceiros").',
    'cookie.section7.title': 'Secção 7 - Instruções para a remoção de Tecnologias de Rastreamento',
    'cookie.section7.p1': 'O utilizador tem a opção de remover Tecnologias de Rastreamento do seu equipamento terminal ou dispositivo móvel e configurar as definições para armazenamento de dados locais através da interface de definições do navegador. O utilizador pode optar por desativar as Tecnologias de Rastreamento ou receber notificações cada vez que uma nova Tecnologia de Rastreamento é iniciada no equipamento terminal do utilizador.',
    'cookie.section7.p2': 'Para instruções detalhadas relativas ao tratamento e remoção de Tecnologias de Rastreamento, faz-se referência à opção de ajuda no navegador do utilizador ou às hiperligações abaixo para cada aplicação de navegador:',
    
    // Auth
    'auth.loading': 'Carregando...',
    'auth.login': 'Entrar',
    'auth.create_account': 'Criar conta',
    'auth.login_subtitle': 'Entre escolhendo uma das opções abaixo.',
    'auth.signup_subtitle': 'Crie uma nova conta para começar.',
    'auth.continue_apple': 'Continuar com Apple',
    'auth.continue_google': 'Continuar com Google',
    'auth.or': 'ou',
    'auth.email': 'E-mail',
    'auth.email_placeholder': 'Digite o e-mail',
    'auth.password': 'Senha',
    'auth.password_placeholder': 'Digite a senha',
    'auth.account_type': 'Tipo de conta',
    'auth.user_role': 'Usuário - Quero comprar/alugar uma propriedade',
    'auth.agent_role': 'Corretor - Quero vender/alugar propriedades',
    'auth.continue': 'Continuar',
    'auth.no_account': 'Não tem conta? Criar conta',
    'auth.have_account': 'Já tem conta? Entrar',
    'auth.login_failed': 'Falha no login',
    'auth.invalid_credentials': 'E-mail ou senha inválidos',
    'auth.welcome_back': 'Bem-vindo de volta!',
    'auth.logged_in': 'Você entrou.',
    'auth.signup_failed': 'Falha no cadastro',
    'auth.account_created': 'Conta criada!',
    'auth.check_email': 'Verifique seu e-mail para confirmar sua conta.',
    'auth.error': 'Ocorreu um erro',
    'auth.try_again': 'Por favor, tente novamente mais tarde.',
    'auth.broker_interest_title': 'Você é corretor?',
    'auth.broker_interest_description': 'Se você tem interesse em anunciar propriedades no Aqarnet, envie-nos uma declaração de interesse e entraremos em contato.',
    'auth.broker_contact_us': 'Entre em contato',
    'auth.broker_email_subject': 'Declaração de interesse corretor',
    'auth.broker_email_body': 'Olá,\n\nEstou interessado em me registrar como corretor no Aqarnet.\n\nNome:\nNome da agência:\nTelefone:\nCidade:\n\nObrigado!',
    'auth.first_name': 'Nome',
    'auth.last_name': 'Sobrenome',
    'auth.first_name_placeholder': 'Digite seu nome',
    'auth.last_name_placeholder': 'Digite seu sobrenome',
    'auth.display_name': 'Nome de exibição',
    'auth.display_name_placeholder': 'Escolha um nome de exibição',
    'auth.display_name_hint': 'Este é o único nome que outros verão',
    'auth.date_of_birth': 'Data de nascimento',
    'auth.fill_all_fields': 'Por favor, preencha todos os campos obrigatórios',
    'auth.must_be_18': 'Você deve ter pelo menos 18 anos para se registrar',
    'auth.must_be_18_hint': 'Você deve ter pelo menos 18 anos',
    'verification.verify_identity': 'Verificar identidade',
    'verification.verified': 'Verificado',
    'verification.title': 'Verificação de identidade',
    'verification.description': 'Envie um documento oficial para verificar sua identidade e obter um selo verificado.',
    'verification.document_type': 'Tipo de documento',
    'verification.doc_id_card': 'Carteira de identidade',
    'verification.doc_passport': 'Passaporte',
    'verification.doc_drivers_license': 'Carteira de motorista',
    'verification.upload_document': 'Enviar documento',
    'verification.click_to_upload': 'Clique para enviar ou arraste e solte',
    'verification.file_requirements': 'JPG, PNG, WEBP ou PDF (máx. 10MB)',
    'verification.submit': 'Enviar para verificação',
    'verification.uploading': 'Enviando...',
    'verification.submitted': 'Verificação enviada',
    'verification.submitted_desc': 'Sua solicitação de verificação foi enviada. Analisaremos em breve.',
    'verification.pending_message': 'Sua verificação está sendo analisada. Isso geralmente leva 1-2 dias úteis.',
    'verification.rejected_message': 'Sua verificação anterior foi rejeitada. Por favor, envie um novo documento.',
    'verification.status_approved': 'Aprovado',
    'verification.status_pending': 'Pendente',
    'verification.status_rejected': 'Rejeitado',
    'verification.invalid_file_type': 'Tipo de arquivo inválido',
    'verification.allowed_types': 'Por favor, envie um arquivo JPG, PNG, WEBP ou PDF',
    'verification.file_too_large': 'Arquivo muito grande',
    'verification.max_size': 'O tamanho máximo do arquivo é 10MB',
    'verification.error': 'Erro de verificação',
    'verification.admin_title': 'Verificações de identidade',
    'verification.admin_description': 'Analisar e aprovar solicitações de verificação de identidade',
    'verification.no_requests': 'Nenhuma solicitação de verificação',
    'verification.view': 'Analisar',
    'verification.review_title': 'Analisar verificação',
    'verification.review_notes': 'Notas de análise',
    'verification.review_notes_placeholder': 'Adicione notas sobre sua decisão (opcional)',
    'verification.approve': 'Aprovar',
    'verification.reject': 'Rejeitar',
    'verification.approved': 'Verificação aprovada',
    'verification.rejected': 'Verificação rejeitada',
    'verification.review_complete': 'A verificação foi processada.',
    
    // Favorites Page
    'favorites.title': 'Meus favoritos',
    'favorites.saved_properties': 'Propriedades salvas',
    'favorites.login_required': 'Entre para ver seus favoritos',
    'favorites.login_description': 'Salve as propriedades que você gosta para encontrá-las mais facilmente.',
    'favorites.loading': 'Carregando favoritos...',
    'favorites.no_favorites': 'Ainda não há favoritos',
    'favorites.start_saving': 'Comece a salvar propriedades clicando no coração nos anúncios.',
    'favorites.search_properties': 'Pesquisar propriedades',
    
    // Profile Page
    'profile.title': 'Meu perfil',
    'profile.subtitle': 'Gerenciar informações pessoais e configurações',
    'profile.loading': 'Carregando perfil...',
    'profile.fetch_error': 'Erro ao buscar perfil',
    'profile.fetch_error_description': 'Não foi possível buscar as informações do perfil.',
    'profile.unnamed_user': 'Usuário sem nome',
    'profile.personal_info': 'Informações pessoais',
    'profile.update_info': 'Atualize suas informações pessoais',
    'profile.email': 'E-mail',
    'profile.email_readonly': 'O e-mail não pode ser alterado',
    'profile.display_name': 'Nome de exibição',
    'profile.display_name_placeholder': 'Seu nome',
    'profile.phone': 'Número de telefone',
    'profile.phone_placeholder': '11 91234-5678',
    'profile.save_changes': 'Salvar alterações',
    'profile.saving': 'Salvando...',
    'profile.reset': 'Redefinir',
    'profile.updated': 'Perfil atualizado',
    'profile.updated_description': 'Seu perfil foi salvo.',
    'profile.update_error': 'Erro de atualização',
    'profile.update_error_description': 'Não foi possível salvar as alterações.',
    'profile.active_listings': 'Anúncios ativos',
    'profile.active_listings_description': 'Seus anúncios de propriedades publicados para venda',
    'profile.view_all': 'Ver tudo',
    'profile.loading_listings': 'Carregando anúncios...',
    'profile.no_active_listings': 'Ainda não há anúncios ativos',
    'profile.more_active_listings': 'e mais {count} anúncios ativos',
    'profile.sold_properties': 'Propriedades vendidas',
    'profile.sold_properties_description': 'Suas propriedades vendidas',
    'profile.no_sold_properties': 'Ainda não há propriedades vendidas',
    'profile.more_sold_properties': 'e mais {count} propriedades vendidas',
    'profile.listing_fetch_error': 'Erro ao buscar anúncios',
    'profile.listing_fetch_error_description': 'Não foi possível buscar seus anúncios.',
    'profile.account_settings': 'Configurações da conta',
    'profile.account_settings_description': 'Gerenciar conta e segurança',
    'profile.change_password': 'Alterar senha',
    'profile.change_password_description': 'Atualizar senha para um login mais seguro',
    'profile.change': 'Alterar',
    'profile.two_factor': 'Autenticação de dois fatores',
    'profile.two_factor_description': 'Adicionar segurança extra à sua conta',
    'profile.enable': 'Ativar',
    'profile.delete_account': 'Excluir conta',
    'profile.delete_account_description': 'Excluir permanentemente conta e todos os dados',
    'profile.delete': 'Excluir',
    'profile.logout': 'Sair',
    
    // Not Found Page
    'notfound.title': '404',
    'notfound.message': 'Desculpe! Página não encontrada',
    'notfound.back_home': 'Voltar para o início',
    
    // Property Detail Page
    'property.back_to_listings': 'Voltar aos anúncios',
    'property.not_found': 'Propriedade não encontrada',
    'property.link_copied': 'Link copiado',
    'property.link_copied_description': 'Link da propriedade copiado',
    'property.description': 'Descrição',
    'property.features': 'Características',
    'property.details': 'Detalhes da propriedade',
    'property.rooms_label': 'Quartos',
    'property.size_label': 'Tamanho',
    'property.plot_size_label': 'Tamanho do terreno',
    'property.monthly_fee_label': 'Taxa mensal',
    'property.contact_agent': 'Contatar corretor',
    'property.send_message': 'Enviar mensagem',
    'property.schedule_viewing': 'Agendar visita',
    'property.section.details': 'Detalhes',
    'property.section.amenities': 'Comodidades',
    'property.section.overview': 'Visão geral',
    'property.section.payment_plan': 'Plano de pagamento',
    'property.section.floor_plans': 'Plantas baixas',
    'property.section.view_map': 'Ver mapa',
    'property.copy_link': 'Copiar link',
    'property.share': 'Compartilhar',
    'property.share_text': 'Confira esta propriedade',
    'property.posted_on': 'Publicado em',
    'property.type': 'Tipo',
    'property.status': 'Status',
    'property.available': 'Disponível',
    'property.no_amenities': 'Nenhuma comodidade listada para esta propriedade',
    'property.no_description': 'Sem descrição disponível',
    'property.location': 'Localização',
    'property.payment_plan_info': 'Entre em contato com o corretor para detalhes do plano de pagamento',
    'property.contact_for_payment_plan': 'Contato para plano de pagamento',
    'property.no_floor_plans': 'Nenhuma planta baixa disponível',
    'property.request_floor_plans': 'Solicitar plantas baixas',
    'property.no_map_location': 'Localização não disponível no mapa',
    
    // News Page
    'news.subtitle': 'Fique atualizado com as últimas notícias imobiliárias, análises de mercado e guias',
    'news.no_articles': 'Ainda não há artigos de notícias',
    'news.check_back': 'Volte em breve para as últimas notícias imobiliárias e análises de mercado.',
    'news.search_placeholder': 'Pesquisar artigos...',
    'news.showing_filtered': 'Mostrando {shown} de {total} artigos nesta página',
    'news.clear_filters': 'Limpar filtros',
    'news.no_matching': 'Nenhum artigo correspondente',
    'news.adjust_filters': 'Tente ajustar sua pesquisa ou filtros para encontrar o que procura.',
    'news.publisher': 'Editor',
    'news.page_info': 'Página {current} de {total} • {count} artigos no total',
    'news.category.all': 'Tudo',
    'news.category.market_news': 'Notícias do mercado',
    'news.category.property_guides': 'Guias imobiliários',
    'news.category.investment_tips': 'Dicas de investimento',
    'news.category.industry_updates': 'Atualizações do setor',
    'news.category.announcements': 'Anúncios',
    'news.category.expert_advice': 'Conselhos de especialistas',
    
    // Filters/Sorting
    'filters.any': 'Todos',
    'filters.newest_first': 'Mais recentes primeiro',
    'filters.price_low_high': 'Preço: menor para maior',
    'filters.price_high_low': 'Preço: maior para menor',
    'filters.size': 'Tamanho',
    'filters.searching_for': 'Pesquisando',
    'filters.search_areas': 'Pesquisar áreas, cidades...',
    
    // Common
    'common.cancel': 'Cancelar',
    'common.save': 'Salvar',
    'common.saving': 'Salvando...',
    'common.send': 'Enviar',
    'common.sending': 'Enviando...',
    'common.submit': 'Enviar',
    'common.close': 'Fechar',
    'common.error': 'Erro',
    'common.success': 'Sucesso',
    'common.loading': 'Carregando...',
    'common.name': 'Nome',
    'common.email': 'E-mail',
    'common.phone': 'Telefone',
    'common.message': 'Mensagem',
    'common.required': 'Obrigatório',
    'common.optional': 'Opcional',
    'common.delete': 'Eliminar',
    'common.free': 'Gratuito',
    
    // Leaderboard
    'leaderboard': 'Classificação',
    'leaderboardDescription': 'Descubra as melhores agências e agentes classificados por visualizações e vendas',
    'topAgencies': 'Melhores Agências',
    'topBrokers': 'Melhores Agentes',
    'topAgenciesDescription': 'Agências classificadas por visualizações de imóveis e desempenho de vendas',
    'topBrokersDescription': 'Agentes classificados por visualizações de imóveis e desempenho de vendas',
    'allRegions': 'Todas as regiões',
    'noAgenciesFound': 'Nenhuma agência encontrada',
    'noBrokersFound': 'Nenhum agente encontrado',
    'brokers': 'agentes',
    'sold': 'vendido',
    'active': 'ativo',
    'views': 'visualizações',
    
    // Dialogs - Contact Agent
    'dialog.contact_agent': 'Contactar agente',
    'dialog.contact_agent_btn': 'Contactar agente',
    'dialog.contact_message_placeholder': 'Olá! Estou interessado na propriedade em {address} e gostaria de saber mais. Por favor, entre em contacto quando tiver tempo.',
    'dialog.contact_sending': 'Enviando...',
    'dialog.contact_send': 'Enviar mensagem',
    'dialog.contact_note': 'A sua consulta será enviada diretamente ao agente responsável que entrará em contacto consigo.',
    
    // Dialogs - Schedule Viewing
    'dialog.schedule_viewing': 'Agendar visita',
    'dialog.schedule_viewing_btn': 'Agendar visita',
    'dialog.preferred_time': 'Hora de visita preferida',
    'dialog.preferred_time_note': 'Selecione data e hora para a visita. O agente confirmará a disponibilidade.',
    'dialog.special_requests': 'Pedidos especiais',
    'dialog.special_requests_placeholder': 'Quaisquer pedidos especiais ou perguntas...',
    'dialog.schedule_booking': 'Agendando visita...',
    'dialog.schedule_book': 'Agendar visita',
    'dialog.schedule_note': 'O seu pedido será enviado ao agente que confirmará a hora por e-mail.',
    
    // Dialogs - Save Search
    'dialog.save_search': 'Guardar pesquisa',
    'dialog.save_search_btn': 'Guardar pesquisa',
    'dialog.search_name': 'Nome da pesquisa',
    'dialog.search_name_placeholder': 'ex., Apartamento em Lisboa',
    'dialog.city_municipality': 'Cidade/Município',
    'dialog.min_rooms': 'Mín. quartos',
    'dialog.min_size': 'Mín. tamanho (m²)',
    'dialog.property_type': 'Tipo de propriedade',
    'dialog.email_alerts': 'Notificações por e-mail quando novas propriedades corresponderem',
    'dialog.saving_search': 'Guardando...',
    
    // Dialogs - Mark as Sold
    'dialog.mark_sold': 'Marcar como vendido',
    'dialog.mark_sold_description': 'Introduza o preço de venda final desta propriedade. Isto moverá o anúncio para propriedades vendidas.',
    'dialog.final_sale_price': 'Preço de venda final (USD)',
    'dialog.listed_price': 'Preço de lista',
    'dialog.property_details': 'Detalhes da propriedade',
    'dialog.enter_valid_price': 'Por favor, introduza um preço de venda válido',
    'dialog.enter_price_placeholder': 'Introduza o preço de venda',
    'dialog.marked_sold_success': 'Propriedade marcada como vendida com sucesso',
    'dialog.mark_sold_error': 'Falha ao marcar a propriedade como vendida',
    
    // Dialogs - Add Property
    'dialog.add_property': 'Adicionar nova propriedade',
    'dialog.hide_preview': 'Ocultar pré-visualização',
    'dialog.show_preview': 'Mostrar pré-visualização',
    'dialog.selected_images': 'Imagens selecionadas',
    'dialog.uploaded_images': 'Imagens carregadas',
    'dialog.click_primary': 'Clique numa imagem para defini-la como principal',
    'dialog.invalid_format': 'Formato de ficheiro inválido',
    'dialog.file_too_large': 'Ficheiro demasiado grande',
    'dialog.upload_failed': 'Falha no carregamento',
    'dialog.images_required': 'Imagens obrigatórias',
    'dialog.please_upload': 'Por favor, carregue pelo menos uma imagem da propriedade',
    'dialog.form_validation_failed': 'Falha na validação do formulário',
    
    // Properties for Sale Page
    'propertiesForSale.filters.searchWithin': 'Pesquisar em',
    'propertiesForSale.filters.km': 'km',
    'propertiesForSale.filters.propertyType': 'Tipo de propriedade',
    'propertiesForSale.filters.rooms': 'Quartos',
    'propertiesForSale.filters.bathrooms': 'Banheiros',
    'propertiesForSale.filters.min': 'Mín',
    'propertiesForSale.filters.max': 'Máx',
    'propertiesForSale.filters.size': 'Tamanho',
    'propertiesForSale.filters.price': 'Preço',
    'propertiesForSale.filters.searchResults': 'Resultados da pesquisa',
    'propertiesForSale.filters.searchPlaceholder': 'Pesquisar zonas, cidades...',
    'propertiesForSale.filters.searchingFor': 'Pesquisando:',
    'propertiesForSale.filters.any': 'Qualquer',
    'propertiesForSale.filters.keywords': 'Palavras-chave',
    'propertiesForSale.filters.showResults': 'Mostrar resultados',
    'propertiesForSale.filters.clearFilters': 'Limpar',
    'propertiesForSale.filters.realEstateSituation': 'Situação imobiliária',
    'propertiesForSale.filters.situation.tapu': 'Tapu',
    'propertiesForSale.filters.situation.landContract': 'Contrato de terreno',
    'propertiesForSale.filters.situation.noDocuments': 'Sem documentos',
    'propertiesForSale.filters.situation.investment': 'Investimento',
    'propertiesForSale.filters.situation.musataha': 'Musataha',
    'propertiesForSale.filters.situation.card': 'Cartão',
    'propertiesForSale.filters.situation.agriculturalTapu': 'Tapu agrícola',
    'propertiesForSale.sort.newest': 'Mais recentes',
    'propertiesForSale.sort.price_low': 'Preço: menor a maior',
    'propertiesForSale.sort.price_high': 'Preço: maior a menor',
    'propertiesForSale.sort.size': 'Tamanho',
    'propertiesForSale.noResults': 'Nenhum imóvel encontrado',
    'propertiesForSale.tryAdjustingFilters': 'Tente ajustar seus filtros ou pesquisar em outra área',
    
    // Search Section
    'search.for_sale': 'À venda',
    'search.for_rent': 'Para alugar',
    'search.sold_prices': 'Preços vendidos',
    'search.search_agents': 'Procurar agentes',
    'search.area_label': 'Área',
    'search.area_placeholder': 'Escrever área ou endereço',
    'search.area_examples': 'Exemplo: Bagdade, Bassorá, Erbil, Najaf, Karbala',
    'search.expand_area': 'Expandir área com',
    'search.property_type': 'Tipo de propriedade',
    'search.show_filters': 'Mostrar filtros',
    'search.find_properties': 'Encontrar propriedades à venda',
    'search.min_rooms': 'Mín. quartos',
    'search.min_area': 'Mín. área',
    'search.max_price': 'Preço máx.',
    'search.all': 'Todos',
    'search.no_max': 'Sem máximo',
    'search.new_construction': 'Construção nova',
    'search.show_new_construction': 'Mostrar construção nova',
    'search.only_new_construction': 'Apenas construção nova',
    'search.exclude_new_construction': 'Excluir construção nova',
    'search.keywords': 'Palavras-chave',
    'search.keywords_placeholder': 'Piscina, lareira, etc.',
    'search.more_filters_note': 'Mais filtros disponíveis nos resultados de pesquisa.',
    'search.recent_searches': 'Pesquisas recentes',
    'search.hide_history': 'Ocultar histórico',
    'search.clear_history': 'Limpar histórico',
    
    'property.all_types': 'Todos os tipos',
    'property.villas': 'Moradias',
    'property.townhouses': 'Casas geminadas',
    'property.apartments': 'Apartamentos',
    'property.business': 'Imóveis comerciais',
    'property.land': 'Terrenos',
    'property.farms': 'Quintas/Florestas',
    'property.other': 'Outros',

    // Property Types (Select)
    'propertyTypes.allTypes': 'Todos os tipos',
    'propertyTypes.villa': 'Moradias',
    'propertyTypes.apartment': 'Apartamentos',
    'propertyTypes.townhouse': 'Casas geminadas',
    'propertyTypes.business': 'Imóveis comerciais',
    'propertyTypes.plot': 'Terrenos',
    'propertyTypes.farm': 'Quintas/Florestas',
    'propertyTypes.other': 'Outros',
    
    // Listings
    'listings.latest_for_sale': 'Últimos à venda',
    'listings.properties_found': 'propriedades encontradas',
    'listings.add_property': 'Adicionar propriedade',
    'listings.all_news': 'Todas as notícias',
    'listings.edit': 'Editar',
    'listings.edit_property': 'Editar propriedade',
    'listings.monthly_fee': 'Taxa mensal',
    'listings.open_floor_plan': 'Planta aberta',
    'listings.large_balcony': 'Varanda grande',
    'listings.family_friendly': 'Adequado para famílias',
    'listings.photos': 'fotos',
    'listings.per_month': 'mês',
    'loading.properties': 'A carregar propriedades...',
    'empty.no_properties': 'Nenhuma propriedade disponível',
    'empty.check_back': 'Volte mais tarde para novos anúncios.',
    
    // Property card
    'property.sold': 'VENDIDO',
    'property.photos': 'fotos',
    'property.rooms_short': 'quartos',
    'property.plot': 'terreno',
    'property.per_month': '/mês',
    'property.delete_title': 'Eliminar propriedade?',
    'property.delete_confirm': 'Tem a certeza de que deseja eliminar {address}? Esta ação não pode ser desfeita.',
    'property.deleted': 'Propriedade eliminada',
    'property.deleted_desc': 'A propriedade foi eliminada',
    'property.delete_error': 'Não foi possível eliminar a propriedade',
    
    // Saved Searches
    'loading.saved_searches': 'A carregar pesquisas guardadas...',
    'saved_searches.title': 'Pesquisas guardadas',
    'saved_searches.empty': 'Ainda não tem pesquisas guardadas',
    'saved_searches.description': 'Guarde uma pesquisa para receber notificações quando forem adicionadas propriedades correspondentes',
    'saved_searches.created': 'Criada',
    'saved_searches.alerts_on': 'Notificações ativadas',
    'saved_searches.alerts_off': 'Notificações desativadas',
    'saved_searches.email_enabled': 'Notificações por email ativadas',
    'saved_searches.email_disabled': 'Notificações por email desativadas',
    'saved_searches.settings_updated': 'Definição atualizada',
    'saved_searches.removed': 'Pesquisa eliminada',
    'saved_searches.removed_desc': 'A pesquisa guardada foi eliminada',
    'saved_searches.error': 'Erro',
    'saved_searches.update_error': 'Não foi possível atualizar a definição',
    'saved_searches.delete_error': 'Não foi possível eliminar a pesquisa',
    'saved_searches.all_properties': 'Todas as propriedades',
    
    // Rental properties
    'rental.for_sale': 'À venda',
    'rental.for_rent': 'Para alugar',
    'rental.listing_type': 'Tipo de anúncio',
    'rental.rental_details': 'Detalhes do arrendamento',
    'rental.rental_period': 'Período de arrendamento',
    'rental.rental_period_hint': 'Com que frequência a renda é paga',
    'rental.daily': 'Diário',
    'rental.weekly': 'Semanal',
    'rental.monthly': 'Mensal',
    'rental.yearly': 'Anual',
    'rental.per_daily': '/dia',
    'rental.per_weekly': '/semana',
    'rental.per_monthly': '/mês',
    'rental.per_yearly': '/ano',
    'rental.deposit_amount': 'Valor do depósito (USD)',
    'rental.minimum_lease': 'Arrendamento mínimo (meses)',
    'rental.months': 'meses',
    'rental.available_from': 'Disponível a partir de',
    'rental.furnished': 'Mobilado',
    'rental.utilities_included': 'Despesas incluídas',
    'rental.pets_allowed': 'Animais permitidos',
    
    // Admin Pages
    'admin.loading': 'A carregar...',
    'admin.access_denied': 'Acesso negado. Privilégios de administrador necessários.',
    
    // Admin Notification Settings
    'admin.notifications.title': 'Configurações de notificação',
    'admin.notifications.description': 'Gerencie as configurações de notificação para todos os tipos de usuários. Ative, desative ou oculte conforme necessário.',
    'admin.notifications.fetch_error': 'Falha ao obter configurações de notificação',
    'admin.notifications.update_success': 'Configuração de notificação atualizada',
    'admin.notifications.update_error': 'Falha ao atualizar configuração de notificação',
    'admin.notifications.role_description': 'Gerenciar notificações para usuários {role}',
    'admin.notifications.hidden': 'Oculto',
    'admin.notifications.disabled': 'Desativado',
    'admin.notifications.active': 'Ativo',
    'admin.notifications.hide': 'Ocultar',
    'admin.notifications.roles.user': 'Usuários',
    'admin.notifications.roles.maklare': 'Corretores',
    'admin.notifications.roles.agency': 'Agências',
    'admin.notifications.roles.admin': 'Administradores',
    'admin.notifications.roles.publisher': 'Editores',
    'admin.notifications.types.new_viewing_request': 'Nova solicitação de visita',
    'admin.notifications.types.new_client_message': 'Nova mensagem de cliente',
    'admin.notifications.types.property_alerts': 'Alertas de propriedades',
    'admin.notifications.types.weekly_summary': 'Resumo semanal',
    'admin.notifications.types.broker_property_added': 'Corretor adicionou propriedade',
    'admin.notifications.types.broker_viewing_request': 'Solicitação de visita do corretor',
    'admin.notifications.types.broker_client_message': 'Mensagem de cliente do corretor',
    'admin.notifications.types.broker_joined': 'Corretor entrou',
    'admin.notifications.types.weekly_agency_summary': 'Resumo semanal da agência',
    'admin.notifications.types.saved_search_alerts': 'Alertas de pesquisa salva',
    'admin.notifications.types.favorite_property_updates': 'Atualizações de propriedades favoritas',
    'admin.notifications.types.newsletter': 'Newsletter',
    'admin.notifications.types.system_alerts': 'Alertas do sistema',
    'admin.notifications.types.new_user_signup': 'Registro de novo usuário',
    'admin.notifications.types.article_published': 'Artigo publicado',

    // Admin Settings
    'admin.settings': 'Configurações de administrador',
    'admin.messaging_settings': 'Configurações de mensagens',
    'admin.messaging_settings_description': 'Controlar recursos de mensagens e chamadas para todos os usuários',
    'admin.messaging': 'Mensagens',
    'admin.messaging_description': 'Permitir que os usuários enviem mensagens',
    'admin.voice_calls': 'Chamadas de voz',
    'admin.voice_calls_description': 'Permitir que os usuários façam chamadas de voz',
    'admin.video_calls': 'Chamadas de vídeo',
    'admin.video_calls_description': 'Permitir que os usuários façam chamadas de vídeo',
    'admin.screen_sharing': 'Compartilhamento de tela',
    'admin.screen_sharing_description': 'Permitir que os usuários compartilhem sua tela durante as chamadas',

    'location_mgr.title': 'Gestor de localizações',
    'location_mgr.current_locations': 'Localizações atuais',
    'location_mgr.locations_in_db': '{count} localizações na base de dados',
    'location_mgr.refresh': 'Atualizar',
    'location_mgr.populate_title': 'Preencher localizações',
    'location_mgr.populate_desc': 'Obter todas as cidades e áreas iraquianas da Google Places API.',
    'location_mgr.populating': 'A preencher...',
    'location_mgr.populate_btn': 'Preencher a partir do Google Places',
    'location_mgr.success': 'Sucesso',
    'location_mgr.success_msg': 'Localizações preenchidas com sucesso',
    'location_mgr.error': 'Erro',
    'location_mgr.error_msg': 'Falha ao preencher localizações.',
    'map_config.title': 'Gestor de configuração de mapa',
    'map_config.subtitle': 'Gerir as suas configurações de mapa permanentes',
    'map_config.loading': 'A carregar configurações...',
    'map_config.create': 'Criar configuração',
    'map_config.create_title': 'Criar configuração de mapa',
    'map_config.create_desc': 'Criar uma nova configuração de mapa permanente.',
    'map_config.edit_title': 'Editar configuração',
    'map_config.edit_desc': 'Atualizar a sua configuração.',
    'map_config.name': 'Nome da configuração',
    'map_config.name_placeholder': 'ex: Mapa imobiliário Iraque',
    'map_config.description': 'Descrição (opcional)',
    'map_config.description_placeholder': 'Breve descrição',
    'map_config.map_data': 'Configuração de mapa (JSON)',
    'map_config.map_data_placeholder': 'Configuração em formato JSON',
    'map_config.cancel': 'Cancelar',
    'map_config.update': 'Atualizar',
    'map_config.no_configs': 'Sem configurações',
    'map_config.no_configs_desc': 'Crie a sua primeira configuração.',
    'map_config.create_first': 'Criar primeira configuração',
    'map_config.active': 'Ativo',
    'map_config.activate': 'Ativar',
    'map_config.edit': 'Editar',
    'map_config.delete': 'Eliminar',
    'map_config.delete_confirm': 'Tem a certeza?',
    'map_config.delete_desc': 'Isto eliminará permanentemente a configuração "{name}".',
    'map_config.created': 'Criado',
    'map_config.last_updated': 'Última atualização',
    'map_config.map_center': 'Centro do mapa',
    'map_config.zoom_level': 'Nível de zoom',
    'map_config.cities': 'Cidades',
    'map_config.cities_configured': '{count} cidades configuradas',
    'map_config.invalid_json': 'Formato JSON inválido',
    
    // Forms
    'form.price': 'Preço',
    'form.priceUSD': 'Preço (USD)',
    'form.priceIQD': 'Preço (IQD)',
    'form.priceConversionNote': 'Insira o preço em qualquer moeda - a outra será calculada automaticamente (Taxa: 1 USD ≈ 1.310 IQD)',
    'form.price_usd': 'Preço (USD)',
    'form.price_placeholder': 'Insira o preço em dólares americanos',
    'form.price_description': 'Insira o preço em dólares americanos. Será exibido em USD e dinar iraquiano.',
    'form.address': 'Endereço',
    'form.city': 'Cidade',
    'form.rooms': 'Quartos',
    'form.bathrooms': 'Banheiros',
    'form.size': 'Tamanho (m²)',
    'form.building_size': 'Tamanho do edifício (m²)',
    'form.building_size_placeholder': 'Área do edifício em m²',
    'form.building_size_description': 'Área habitável',
    'form.plot_size': 'Tamanho do terreno (m²)',
    'form.plot_size_placeholder': 'Área do terreno em m²',
    'form.plot_size_description': 'Área total do terreno',
    'form.type': 'Tipo',
    'form.propertyType': 'Tipo de imóvel',
    'form.monthly_fee': 'Taxa mensal (SEK)',
    'form.monthly_fee_usd': 'Taxa mensal (USD)',
    'form.monthly_fee_placeholder': 'Insira a taxa mensal em USD (opcional)',
    'form.monthly_fee_description': 'Taxa mensal opcional em dólares americanos',
    'form.description': 'Descrição',
    'form.description_placeholder': 'Descreva a propriedade...',
    'form.payment_plan': 'Plano de pagamento',
    'form.payment_plan_placeholder': 'ex: 30% entrada, 70% na entrega, ou parcelas mensais...',
    'form.payment_plan_hint': 'Adicione parcelas para mostrar suas opções de pagamento aos compradores',
    'payment_plan.total': 'Total',
    'payment_plan.amount': 'Valor',
    'payment_plan.description': 'Descrição',
    'payment_plan.date': 'Data',
    'payment_plan.percentage': 'Porcentagem',
    'payment_plan.add_installment': 'Adicionar parcela',
    'payment_plan.hint': 'Deixe vazio se não oferecer plano de pagamento',
    'payment_plan.down_payment': 'Entrada',
    'payment_plan.first_payment': 'Primeira parcela',
    'payment_plan.second_payment': 'Segunda parcela',
    'payment_plan.third_payment': 'Terceira parcela',
    'payment_plan.fourth_payment': 'Quarta parcela',
    'payment_plan.fifth_payment': 'Quinta parcela',
    'payment_plan.sixth_payment': 'Sexta parcela',
    'payment_plan.seventh_payment': 'Sétima parcela',
    'payment_plan.final_payment': 'Pagamento final',
    'payment_plan.payment_description': 'Descrição do pagamento',
    'payment_plan.enable': 'Incluir plano de pagamento',
    'form.floor_plans': 'Plantas baixas',
    'form.upload_floor_plans': 'Fazer upload de imagens de plantas',
    'form.features': 'Características (separadas por vírgula)',
    'form.features_placeholder': 'ex. Piscina, Jardim, Garagem, Cozinha moderna',
    'form.features_description': 'Separe as características com vírgulas',
    'form.amenities': 'Comodidades',
    'form.amenities_hint': 'Selecione todas as comodidades aplicáveis a este imóvel',
    'amenities.balcony': 'Varanda',
    'amenities.electricity_generator': 'Gerador elétrico',
    'amenities.central_ac': 'Ar condicionado central',
    'amenities.double_glazed_windows': 'Janelas de vidro duplo',
    'amenities.satellite_tv': 'TV por satélite',
    'amenities.cctv': 'Câmeras de segurança',
    'amenities.security_staff': 'Pessoal de segurança',
    'amenities.sewerage_system': 'Sistema de esgoto',
    'amenities.furnished_kitchen': 'Cozinha mobiliada',
    'amenities.24hr_electricity': 'Eletricidade 24h',
    'amenities.market': 'Mercado próximo',
    'amenities.elevator': 'Elevador',
    'amenities.school': 'Escola próxima',
    'amenities.mosque': 'Mesquita próxima',
    'amenities.central_gas': 'Gás central',
    'amenities.maintenance': 'Serviços de manutenção',
    'amenities.cleaning': 'Serviços de limpeza',
    'amenities.fire_fighting': 'Equipamento contra incêndio',
    'amenities.car_washing': 'Lavagem de carros',
    'amenities.smart_home': 'Casa inteligente',
    'amenities.earthquake_resistant': 'Resistente a terremotos',
    'amenities.central_fire_system': 'Sistema central contra incêndio',
    'amenities.hot_water_system': 'Sistema de água quente central',
    'amenities.central_vacuum': 'Aspirador central',
    'amenities.fountain': 'Fonte',
    'amenities.24hr_water': 'Água 24h',
    'amenities.garbage_chute': 'Duto de lixo',
    'amenities.kitchen_counter': 'Bancada da cozinha',
    'amenities.street_lights': 'Iluminação pública',
    'amenities.playground': 'Parque infantil',
    'amenities.gym': 'Academia',
    'amenities.garden': 'Jardim',
    'amenities.parking': 'Estacionamento',
    'amenities.wifi': 'WiFi',
    'amenities.security_door': 'Porta de segurança',
    'amenities.split_ac': 'Ar condicionado split',
    'amenities.bathtub': 'Banheira',
    'amenities.refrigerator': 'Frigorífico',
    'amenities.washing_machine': 'Máquina de lavar',
    'amenities.swimming_pool': 'Piscina',
    'amenities.solar_panels': 'Painéis solares',
    'amenities.water_tank': 'Tanque de água',
    'amenities.water_well': 'Poço de água',
    'amenities.septic_tank': 'Fossa séptica',
    'amenities.central_heating': 'Aquecimento central',
    'amenities.desert_cooler': 'Refrigerador evaporativo',
    'amenities.intercom': 'Interfone',
    'amenities.external_camera': 'Câmera externa',
    'amenities.guard_room': 'Sala do guarda',
    'amenities.rooftop_terrace': 'Terraço na cobertura',
    'amenities.basement': 'Porão',
    'amenities.storage_room': 'Depósito',
    'amenities.internal_stairs': 'Escada interna',
    'amenities.external_stairs': 'Escada externa',
    'amenities.corner_unit': 'Unidade de esquina',
    'amenities.two_streets': 'Duas ruas',
    'amenities.double_entrance': 'Entrada dupla',
    'amenities.marble_flooring': 'Piso de mármore',
    'amenities.ceramic_tiles': 'Azulejos cerâmicos',
    'amenities.parquet_flooring': 'Piso de parquet',
    'amenities.gypsum_decorations': 'Decorações de gesso',
    'amenities.false_ceiling': 'Teto falso',
    'amenities.steel_doors': 'Portas de aço',
    'amenities.aluminum_windows': 'Janelas de alumínio',
    'amenities.pvc_windows': 'Janelas de PVC',
    'amenities.guest_room': 'Quarto de hóspedes',
    'amenities.servant_quarters': 'Quarto de serviço',
    'amenities.covered_parking': 'Estacionamento coberto',
    'amenities.concrete_fence': 'Cerca de concreto',
    'amenities.iron_fence': 'Cerca de ferro',
    'amenities.street_pavement': 'Pavimentação da rua',
    'amenities.fully_furnished': 'Totalmente mobiliado',
    'amenities.hospital': 'Hospital próximo',
    'amenities.category.utilities': 'Eletricidade e Água',
    'amenities.category.climate': 'Climatização',
    'amenities.category.security': 'Segurança',
    'amenities.category.building': 'Características do edifício',
    'amenities.category.interior': 'Interior e Pisos',
    'amenities.category.rooms': 'Quartos',
    'amenities.category.outdoor': 'Exterior e Estacionamento',
    'amenities.category.appliances': 'Eletrodomésticos',
    'amenities.category.services': 'Serviços',
    'amenities.category.nearby': 'Nas proximidades',
    'common.selected': 'selecionado(s)',
    'form.property_images': 'Imagens da propriedade',
    'form.current_images': 'Imagens atuais',
    'form.image': 'Imagem',
    'form.primary': 'Principal',
    'form.click_image_primary': 'Clique numa imagem para defini-la como principal. Use as setas para alterar a ordem.',
    'form.click_to_upload_new': 'Clique para carregar novas imagens',
    'form.new_images_upload': 'Novas imagens para carregar',
    'form.new_image': 'Nova imagem',
    'form.upload': 'Carregar',
    'form.new_images': 'novas imagens',
    'form.location': 'Localização',
    'form.required': 'Obrigatório',
    'form.optional': 'Opcional',
    'form.cancel': 'Cancelar',
    'form.save_changes': 'Guardar alterações',
    'form.uploading': 'A carregar...',
    'form.upload_images': 'Carregar imagens',
    'form.click_to_upload': 'Clique para carregar imagens',
    'form.file_formats': 'JPEG, PNG, WebP, GIF (máx. 10MB por imagem)',
    'form.select_type': 'Selecionar tipo',
    'form.location_on_map': 'Localização da propriedade no mapa',
    'form.location_hint': 'Clique no mapa para definir a localização exata',
    'form.hide_preview': 'Ocultar pré-visualização',
    'form.show_preview': 'Mostrar pré-visualização',
    'form.selected_images': 'Imagens selecionadas',
    'form.click_set_primary': 'Clique numa imagem para defini-la como principal',
    'form.uploaded_images': 'Imagens carregadas',
    'form.images': 'imagens',
    'form.uploading_images': 'A carregar imagens...',
    'form.city_placeholder': 'Procurar uma cidade...',
    'form.new_construction': 'Nova construção (Casa futura)',
    'form.preview': 'Pré-visualização',
    'form.preview_description': 'É assim que o seu anúncio aparecerá no site',
    'form.fullscreen': 'Ecrã inteiro',
    'form.exit_fullscreen': 'Sair do ecrã inteiro',
    'form.click_preview_fullscreen': 'Clique na pré-visualização para ecrã inteiro',
    'form.preview_contact_disabled': 'Opções de contacto estão desativadas no modo de pré-visualização',
    'form.invalid_format': 'Formato de ficheiro inválido',
    'form.invalid_format_desc': '{filename} tem um formato inválido. Permitidos: JPEG, PNG, WebP, GIF',
    'form.file_too_large': 'Ficheiro demasiado grande',
    'form.file_too_large_desc': '{filename} é maior que 10MB',
    'form.upload_failed': 'Falha no carregamento',
    'form.upload_failed_desc': 'Não foi possível carregar as imagens',
    'form.images_required': 'Imagens obrigatórias',
    'form.images_required_desc': 'Por favor, carregue pelo menos uma imagem da propriedade',
    'form.sold_price_usd': 'Preço de venda (USD)',
    'form.sold_price_placeholder': 'Insira o preço de venda real',
    'form.sold_price_description': 'O preço de venda real (pode diferir do preço de lista)',
    'form.video_url': 'URL do vídeo tour',
    'form.video': 'Vídeo',
    'form.add_video': 'Adicionar outro vídeo',
    'form.video_addon': '+$3 adicional',
    'form.video_included': 'Incluído grátis',
    'form.video_url_hint': 'Adicione um vídeo tour do YouTube ou Vimeo. O adicional de vídeo pode ser comprado após criar o anúncio.',
    'form.video_url_hint_free': 'Adicione um vídeo tour do YouTube ou Vimeo - incluído na sua assinatura!',
    'form.video_url_hint_addon': 'Adicione um vídeo tour do YouTube ou Vimeo. $3 serão adicionados ao custo do seu anúncio.',
    'form.include_video_tour': 'Incluir vídeo tour',
    'form.show_map': 'Mostrar mapa',
    'form.no_location': 'Nenhuma localização selecionada',
    
    // Footer
    'footer.contact': 'Contacto',
    'footer.customer_service': 'Serviço ao cliente e contacto',
    'footer.about': 'Sobre a Aqarnet',
    'footer.press': 'Imprensa',
    'footer.investors': 'Para investidores',
    'footer.advertising': 'Publicidade e marketing',
    'footer.jobs': 'Trabalhe connosco',
    'footer.statistics': 'Estatísticas imobiliárias',
    'footer.apps': 'Aplicações',
    'footer.iphone_ipad': 'iPhone e iPad',
    'footer.android': 'Android',
    'footer.follow': 'Seguir Aqarnet',
    'footer.newsletter': 'Newsletter Aqarnet',
    'footer.whatsapp': 'WhatsApp',
    'footer.facebook': 'Facebook',
    'footer.instagram': 'Instagram',
    'footer.linkedin': 'LinkedIn',
    'footer.pinterest': 'Pinterest',
    'footer.tiktok': 'TikTok',
    'footer.threads': 'Threads',
    'footer.podcast': 'Podcast Aqarnet',
    'footer.all_properties': 'Todas as propriedades',
    'footer.privacy': 'Privacidade',
    'footer.cookies': 'Cookies',
    'footer.terms': 'Termos e Informações Legais',
    'footer.cookie_settings': 'Configurações de cookies',
    
    // About Page
    'about.title': 'Sobre a Aqarnet',
    'about.subtitle': 'Seu parceiro de confiança no mercado imobiliário iraquiano',
    'about.description': 'A Aqarnet é a principal plataforma imobiliária do Iraque, conectando compradores, vendedores e agentes em todo o país. Oferecemos anúncios completos, análises de mercado e ferramentas profissionais.',
    'about.mission_title': 'Nossa Missão',
    'about.mission_text': 'Revolucionar o mercado imobiliário iraquiano fornecendo informações imobiliárias transparentes, acessíveis e confiáveis.',
    'about.vision_title': 'Nossa Visão',
    'about.vision_text': 'Tornar-se a plataforma imobiliária mais confiável do Oriente Médio.',
    'about.services_title': 'Nossos Serviços',
    'about.services_text': 'Anúncios de imóveis, análises de mercado, conexões com agentes, avaliações e notícias imobiliárias.',
    'about.team_title': 'Nossa Equipe',
    'about.team_text': 'Uma equipe dedicada de profissionais imobiliários, especialistas em tecnologia e especialistas em atendimento ao cliente.',
    'about.company_info': 'Informações da Empresa',
    'about.based_in': 'Com sede na Suécia, servindo o Iraque',
    
    // Contact Page
    'contact.title': 'Contate-nos',
    'contact.subtitle': 'Estamos aqui para ajudar. Entre em contato com qualquer dúvida.',
    'contact.email_label': 'E-mail',
    'contact.phone_label': 'Telefone',
    'contact.phone_info': 'Em breve',
    'contact.location_label': 'Localização',
    'contact.location_info': 'Suécia',
    'contact.form_title': 'Envie-nos uma mensagem',
    'contact.form_description': 'Preencha o formulário e responderemos o mais rápido possível.',
    'contact.name_placeholder': 'Seu nome',
    'contact.email_placeholder': 'seu@email.com',
    'contact.subject': 'Assunto',
    'contact.subject_placeholder': 'Sobre o que é?',
    'contact.message_placeholder': 'Sua mensagem...',
    'contact.success': 'Mensagem enviada! Entraremos em contato em breve.',
    
    // Press Page
    'press.title': 'Imprensa e Mídia',
    'press.subtitle': 'Últimas notícias e comunicados de imprensa da Aqarnet',
    'press.contact_title': 'Contato de Imprensa',
    'press.contact_description': 'Para consultas de imprensa, entre em contato por e-mail.',
    'press.add_release': 'Adicionar comunicado',
    'press.edit_release': 'Editar comunicado',
    'press.new_release': 'Novo comunicado de imprensa',
    'press.release_title': 'Título',
    'press.content': 'Conteúdo',
    'press.publish': 'Publicar',
    'press.no_releases': 'Ainda não há comunicados de imprensa.',
    'press.delete_confirm_title': 'Excluir comunicado?',
    'press.delete_confirm_description': 'Esta ação não pode ser desfeita.',
    
    // Investors Page
    'investors.title': 'Para Investidores',
    'investors.subtitle': 'Junte-se a nós para transformar o mercado imobiliário iraquiano',
    'investors.growth_title': 'Potencial de Crescimento',
    'investors.growth_text': 'O mercado imobiliário iraquiano está experimentando um rápido crescimento com oportunidades significativas de investimento.',
    'investors.market_title': 'Líder de Mercado',
    'investors.market_text': 'A Aqarnet está posicionada como a principal plataforma digital no setor imobiliário iraquiano.',
    'investors.vision_title': 'Expansão Regional',
    'investors.vision_text': 'Nossa visão se estende além do Iraque para nos tornarmos a plataforma líder no Oriente Médio.',
    'investors.opportunity_title': 'Oportunidade de Investimento',
    'investors.opportunity_text': 'A Aqarnet oferece uma oportunidade única de investir na transformação digital de um dos maiores mercados imobiliários emergentes.',
    'investors.opportunity_text_2': 'Com uma base de usuários crescente e serviços em expansão, estamos bem posicionados para crescimento sustentável.',
    'investors.contact_title': 'Entre em Contato',
    'investors.contact_description': 'Interessado em oportunidades de investimento? Contate-nos para saber mais.',
    'investors.company': 'Empresa / Organização',
    'investors.company_placeholder': 'Nome da sua empresa',
    'investors.message_placeholder': 'Conte-nos sobre seus interesses de investimento...',
    'investors.success': 'Obrigado pelo seu interesse! Entraremos em contato em breve.',
    
    // Advertising Page
    'advertising.title': 'Publicidade e Marketing',
    'advertising.subtitle': 'Alcance milhares de potenciais compradores e locatários no Iraque',
    'advertising.targeted_title': 'Alcance Direcionado',
    'advertising.targeted_text': 'Alcance usuários que buscam ativamente imóveis em áreas específicas.',
    'advertising.audience_title': 'Público de Qualidade',
    'advertising.audience_text': 'Conecte-se com compradores e locatários sérios.',
    'advertising.analytics_title': 'Análises Detalhadas',
    'advertising.analytics_text': 'Acompanhe o desempenho das suas campanhas com análises completas.',
    'advertising.formats_title': 'Múltiplos Formatos',
    'advertising.formats_text': 'Banners, anúncios em destaque, conteúdo patrocinado e mais.',
    'advertising.contact_title': 'Começar a Anunciar',
    'advertising.contact_description': 'Contate-nos para discutir oportunidades de publicidade.',
    'advertising.company': 'Empresa / Organização',
    'advertising.company_placeholder': 'Nome da sua empresa',
    'advertising.budget': 'Faixa de Orçamento',
    'advertising.budget_placeholder': 'ex., $500 - $1000/mês',
    'advertising.message_placeholder': 'Conte-nos sobre seus objetivos de publicidade...',
    'advertising.success': 'Obrigado! Entraremos em contato sobre oportunidades de publicidade.',
    
    // Jobs Page
    'jobs.title': 'Carreiras na Aqarnet',
    'jobs.subtitle': 'Junte-se à nossa equipe e ajude a moldar o futuro imobiliário no Iraque',
    'jobs.add_job': 'Adicionar vaga',
    'jobs.edit_job': 'Editar vaga',
    'jobs.new_job': 'Nova oferta de emprego',
    'jobs.job_title': 'Título da vaga',
    'jobs.department': 'Departamento',
    'jobs.location': 'Localização',
    'jobs.employment_type': 'Tipo de emprego',
    'jobs.full_time': 'Tempo integral',
    'jobs.part_time': 'Meio período',
    'jobs.contract': 'Contrato',
    'jobs.internship': 'Estágio',
    'jobs.description': 'Descrição',
    'jobs.requirements': 'Requisitos',
    'jobs.benefits': 'Benefícios',
    'jobs.publish': 'Publicar',
    'jobs.no_jobs': 'Nenhuma posição aberta no momento.',
    'jobs.check_back': 'Volte mais tarde para novas oportunidades.',
    'jobs.apply_now': 'Candidatar-se agora',
    'jobs.apply_for': 'Candidatar-se para',
    'jobs.apply_description': 'Envie sua candidatura por e-mail.',
    'jobs.application_sent': 'Candidatura enviada! Boa sorte!',
    'jobs.delete_confirm_title': 'Excluir oferta de emprego?',
    'jobs.delete_confirm_description': 'Esta ação não pode ser desfeita.',
    
    // Statistics
    'statistics.title': 'Estatísticas imobiliárias',
    'statistics.subtitle': 'Analise tendências de mercado e evolução de preços baseadas em imóveis vendidos',
    'statistics.6_months': 'Últimos 6 meses',
    'statistics.12_months': 'Últimos 12 meses',
    'statistics.24_months': 'Últimos 24 meses',
    'statistics.36_months': 'Últimos 36 meses',
    'statistics.all_cities': 'Todas as cidades',
    'statistics.total_sold': 'Total vendido',
    'statistics.avg_price': 'Preço médio',
    'statistics.avg_price_sqm': 'Preço médio/m²',
    'statistics.price_change': 'Variação de preço',
    'statistics.price_trends': 'Tendências de preços',
    'statistics.price_trends_desc': 'Preços médios de imóveis ao longo do tempo',
    'statistics.sales_volume': 'Volume de vendas',
    'statistics.sales_volume_desc': 'Número de imóveis vendidos por mês',
    'statistics.properties_sold': 'Imóveis vendidos',
    'statistics.price_by_city': 'Preço por m² por cidade',
    'statistics.price_by_city_desc': 'Comparação do preço médio por metro quadrado entre cidades',
    'statistics.price_per_sqm': 'Preço por m²',
    'statistics.type_distribution': 'Distribuição de tipos de imóveis',
    'statistics.type_distribution_desc': 'Distribuição de imóveis vendidos por tipo',
    'statistics.avg_days_market': 'Dias médios no mercado',
    'statistics.days': 'dias',
    'statistics.price_ranges': 'Distribuição de faixas de preço',
    'statistics.price_ranges_desc': 'Número de imóveis vendidos em cada faixa de preço',
    'statistics.properties': 'Imóveis',
    'statistics.year_over_year': 'Comparação anual',
    'statistics.year_over_year_desc': 'Compare preços médios e volume de vendas ao longo dos anos',
    'statistics.all_types': 'Todos os tipos de imóveis',
    'statistics.all_rooms': 'Todos os quartos',
    'statistics.rooms': 'quartos',
    'statistics.export': 'Exportar',
    'statistics.export_csv': 'Baixar CSV',
    'statistics.export_json': 'Baixar JSON',
    'statistics.share': 'Compartilhar',
    'statistics.link_copied': 'Link copiado',
    'statistics.link_copied_desc': 'O link do relatório estatístico foi copiado para a área de transferência',
    'statistics.custom_date': 'Data personalizada',
    'statistics.use_preset': 'Usar predefinido',
    'statistics.start_date': 'Data inicial',
    'statistics.end_date': 'Data final',
    'statistics.city_comparison': 'Comparação de cidades',
    'statistics.city_comparison_desc': 'Compare estatísticas imobiliárias entre duas cidades lado a lado',
    'statistics.select_city_1': 'Selecione primeira cidade',
    'statistics.select_city_2': 'Selecione segunda cidade',
    'statistics.vs': 'vs',
    'statistics.select_cities_to_compare': 'Selecione duas cidades acima para comparar suas estatísticas',
    'statistics.avg_size': 'Tamanho médio',
    
    // Cookie Settings
    'cookie_settings.title': 'Configurações de cookies',
    'cookie_settings.description': 'Usamos cookies para melhorar sua experiência de navegação. Você pode personalizar suas preferências abaixo.',
    'cookie_settings.necessary': 'Cookies necessários',
    'cookie_settings.necessary_desc': 'Esses cookies são essenciais para o funcionamento correto do site. Eles não podem ser desativados.',
    'cookie_settings.functional': 'Cookies funcionais',
    'cookie_settings.functional_desc': 'Esses cookies permitem recursos personalizados como preferências de idioma e configurações salvas.',
    'cookie_settings.analytics': 'Cookies analíticos',
    'cookie_settings.analytics_desc': 'Esses cookies nos ajudam a entender como os visitantes interagem com nosso site para melhorar a experiência do usuário.',
    'cookie_settings.marketing': 'Cookies de marketing',
    'cookie_settings.marketing_desc': 'Esses cookies são usados para mostrar anúncios relevantes e rastrear a eficácia de nossas campanhas.',
    'cookie_settings.accept_all': 'Aceitar tudo',
    'cookie_settings.reject_all': 'Rejeitar tudo',
    'cookie_settings.save_preferences': 'Salvar preferências',
    'cookie_settings.saved': 'Preferências de cookies salvas',
    
    // Cookie Banner
    'cookie_banner.title': 'Usamos cookies',
    'cookie_banner.description': 'Usamos cookies para melhorar sua experiência de navegação, exibir conteúdo personalizado e analisar nosso tráfego. Ao clicar em "Aceitar tudo", você concorda com nosso uso de cookies.',
    'cookie_banner.accept_all': 'Aceitar tudo',
    'cookie_banner.reject_all': 'Rejeitar tudo',
    'cookie_banner.customize': 'Personalizar',
    
    // Newsletter
    'newsletter.title': 'Newsletter Aqarnet',
    'newsletter.description': 'Receba as últimas notícias e atualizações sobre imóveis diretamente no seu e-mail.',
    'newsletter.subscribe': 'Subscrever newsletter',
    'newsletter.signup': 'Registar',
    'newsletter.email_placeholder': 'O seu endereço de e-mail',
    'newsletter.success': 'Obrigado por subscrever!',
    'newsletter.success_description': 'Receberá em breve as nossas atualizações.',
    'newsletter.invalid_email': 'Por favor, introduza um e-mail válido',
    'newsletter.error': 'Falha na subscrição',
    'newsletter.unsubscribe_note': 'Pode cancelar a subscrição a qualquer momento.',
    'newsletter.unsubscribe_title': 'Cancelar subscrição da Newsletter',
    'newsletter.unsubscribe_description': 'Introduza o seu e-mail para cancelar a subscrição.',
    'newsletter.unsubscribe_button': 'Cancelar subscrição',
    'newsletter.unsubscribe_success': 'Subscrição cancelada com sucesso',
    'newsletter.unsubscribe_error': 'Falha ao cancelar subscrição',
    'newsletter.unsubscribe_message': 'Deixará de receber a nossa newsletter.',
    'newsletter.invalid_link': 'Link de cancelamento inválido',
    'newsletter.invalid_link_message': 'Este link de cancelamento é inválido ou expirou. Por favor utilize o link do seu email da newsletter.',
    'newsletter.unsubscribe_reason_label': 'Motivo do cancelamento (opcional)',
    'newsletter.select_reason': 'Selecione um motivo...',
    'newsletter.reason_too_many_emails': 'Demasiados emails',
    'newsletter.reason_not_relevant': 'O conteúdo não é relevante para mim',
    'newsletter.reason_never_subscribed': 'Nunca me subscrevi',
    'newsletter.reason_found_another': 'Encontrei outro serviço',
    'newsletter.reason_other': 'Outro',
    'common.try_again': 'Tentar novamente',
    'newsletter.admin.title': 'Subscritores da Newsletter',
    'newsletter.admin.description': 'Gerir os seus subscritores da newsletter',
    'newsletter.admin.total': 'Total de subscritores',
    'newsletter.admin.active': 'Ativo',
    'newsletter.admin.unsubscribed': 'Cancelado',
    'newsletter.admin.export_csv': 'Exportar CSV',
    'newsletter.admin.refresh': 'Atualizar',
    'newsletter.admin.subscribers_list': 'Lista de subscritores',
    'newsletter.admin.subscribers_description': 'Todos os subscritores e o seu estado',
    'newsletter.admin.no_subscribers': 'Ainda não há subscritores',
    'newsletter.admin.subscribed_at': 'Subscrito em',
    'newsletter.admin.status': 'Estado',
    'newsletter.admin.unsubscribed_at': 'Cancelado em',
    'newsletter.admin.actions': 'Ações',
    'newsletter.admin.deleted': 'Subscritor eliminado',
    'newsletter.admin.delete_error': 'Falha ao eliminar',
    'newsletter.admin.no_data': 'Sem dados para exportar',
    'newsletter.admin.exported': 'Exportado com sucesso',
    'newsletter.admin.delete_confirm_title': 'Eliminar subscritor?',
    'newsletter.admin.delete_confirm_description': 'Isto irá eliminar permanentemente o subscritor.',
    'footer.faq': 'Perguntas frequentes',
    
    // FAQ Page
    'faq.title': 'Perguntas frequentes',
    'faq.subtitle': 'Encontre respostas para perguntas comuns sobre o Aqarnet',
    'faq.account.title': 'Conta e registo',
    'faq.account.q1': 'Como crio uma conta?',
    'faq.account.q1_answer': 'Clique em "Entrar" no cabeçalho e selecione "Criar conta". Pode registar-se com email, Google ou Apple.',
    'faq.account.q2': 'Como redefinir a minha palavra-passe?',
    'faq.account.q2_answer': 'Na página de login, clique em "Esqueci a palavra-passe" e insira o seu endereço de email. Receberá um link para redefinir a palavra-passe.',
    'faq.account.q3': 'Posso alterar o meu endereço de email?',
    'faq.account.q3_answer': 'Atualmente não pode alterar o seu endereço de email diretamente. Por favor contacte a nossa equipa de suporte.',
    'faq.listings.title': 'Anúncios de propriedades',
    'faq.listings.q1': 'Como publico uma propriedade à venda?',
    'faq.listings.q1_answer': 'Deve estar registado como agente para publicar propriedades. Após entrar, clique em "Adicionar propriedade" e preencha os detalhes.',
    'faq.listings.q2': 'Quanto tempo um anúncio permanece ativo?',
    'faq.listings.q2_answer': 'Os anúncios permanecem ativos até serem marcados como vendidos ou removidos pelo proprietário.',
    'faq.listings.q3': 'Posso editar o meu anúncio após a publicação?',
    'faq.listings.q3_answer': 'Sim, pode editar o seu anúncio a qualquer momento. Vá ao seu perfil, encontre o anúncio e clique em "Editar".',
    'faq.search.title': 'Pesquisar propriedades',
    'faq.search.q1': 'Como guardo uma pesquisa?',
    'faq.search.q1_answer': 'Após realizar uma pesquisa, clique em "Guardar pesquisa". Pode ativar notificações por email para novas propriedades.',
    'faq.search.q2': 'Como adiciono uma propriedade aos favoritos?',
    'faq.search.q2_answer': 'Clique no ícone de coração em qualquer cartão de propriedade. Deve estar conectado para guardar favoritos.',
    'faq.search.q3': 'Posso pesquisar no mapa?',
    'faq.search.q3_answer': 'Sim, use a vista de mapa na página de propriedades para explorar anúncios por localização.',
    'faq.contact.title': 'Contactar agentes',
    'faq.contact.q1': 'Como contacto um agente imobiliário?',
    'faq.contact.q1_answer': 'Clique em "Contactar agente" em qualquer anúncio para enviar uma mensagem diretamente ao agente responsável.',
    'faq.contact.q2': 'Posso agendar uma visita?',
    'faq.contact.q2_answer': 'Sim, clique em "Agendar visita" na página da propriedade para solicitar um horário de visita.',
    'faq.security.title': 'Segurança e privacidade',
    'faq.security.q1': 'As minhas informações pessoais estão seguras?',
    'faq.security.q1_answer': 'Sim, utilizamos encriptação e medidas de segurança padrão da indústria para proteger os seus dados.',
    'faq.security.q2': 'Como elimino a minha conta?',
    'faq.security.q2_answer': 'Vá às definições do seu perfil e selecione "Eliminar conta". Esta ação é permanente.',
    'faq.still_questions': 'Ainda tem perguntas?',
    'faq.contact_us': 'Contacte a nossa equipa de suporte e teremos todo o gosto em ajudar.',

    // Broker Dashboard
    'broker.dashboard_title': 'Painel do corretor',
    'broker.dashboard_subtitle': 'Gerencie suas propriedades e interações com clientes',
    'broker.my_properties': 'Minhas propriedades',
    'broker.my_properties_desc': 'Visualize e gerencie seus anúncios de imóveis',
    'broker.viewings': 'Visitas de propriedades',
    'broker.viewings_desc': 'Gerencie visitas agendadas',
    'broker.contacts': 'Mensagens de clientes',
    'broker.contacts_desc': 'Visualize e responda às consultas dos clientes',
    'broker.analytics': 'Análise de propriedades',
    'broker.analytics_desc': 'Acompanhe visualizações e engajamento em seus anúncios',
    'broker.subscription': 'Assinatura',
    'broker.subscription_desc': 'Gerencie seu plano e limites de anúncios',
    'broker.access_denied': 'Esta página é acessível apenas para corretores registrados.',
    'broker.profile_title': 'Perfil do corretor',
    'broker.profile_description': 'Gerencie seu perfil público e informações de contato',
    'broker.back_to_dashboard': 'Voltar ao painel',
    'broker.display_name': 'Nome de exibição',
    'broker.display_name_placeholder': 'Seu nome como aparece para os clientes',
    'broker.contact_email': 'Email de contato',
    'broker.email_placeholder': 'seu@email.com',
    'broker.email_notification_note': 'Este email será usado para notificações sobre visitas e mensagens de clientes.',
    'broker.phone_number': 'Número de telefone',
    'broker.phone_placeholder': '+55 xx xxxxx xxxx',
    'broker.change_photo': 'Alterar foto',
    'broker.save_profile': 'Salvar perfil',
    'broker.profile_saved': 'Perfil salvo com sucesso',
    'broker.profile_save_error': 'Falha ao salvar perfil',
    'broker.profile_load_error': 'Falha ao carregar perfil',
    'broker.avatar_uploaded': 'Foto enviada com sucesso',
    'broker.avatar_upload_error': 'Falha ao enviar foto',
    'broker.invalid_image_type': 'Por favor selecione um arquivo de imagem',
    'broker.image_too_large': 'A imagem deve ter menos de 2MB',
    'common.login_required': 'Por favor, faça login para acessar esta página.',
    
    // Profile Email Notifications
    'profile.email_notifications': 'Notificações por email',
    'profile.email_notifications_description': 'Escolha quais notificações por email deseja receber',
    'profile.notif_property_alerts': 'Alertas de propriedades',
    'profile.notif_property_alerts_desc': 'Receba notificações sobre novas propriedades que correspondam às suas pesquisas guardadas',
    'profile.notif_saved_search_alerts': 'Alertas de pesquisas guardadas',
    'profile.notif_saved_search_alerts_desc': 'Receba alertas quando novas propriedades correspondam aos seus critérios de pesquisa',
    'profile.notif_newsletter': 'Newsletter',
    'profile.notif_newsletter_desc': 'Receba a nossa newsletter com notícias imobiliárias e análises de mercado',
    'profile.notif_viewing_confirmations': 'Confirmações de visitas',
    'profile.notif_viewing_confirmations_desc': 'Receba confirmações por email para as suas visitas agendadas',
    'profile.email_prefs_saved': 'Preferências de email guardadas',
    'profile.email_prefs_error': 'Falha ao guardar preferências',
    
    // Agency Dashboard
    'agency.my_agency': 'Minha agência',
    'agency.dashboard_title': 'Painel da agência',
    'agency.dashboard_subtitle': 'Gerencie seus corretores e configurações da agência',
    'agency.brokers_tab': 'Corretores',
    'agency.invites_tab': 'Convites',
    'agency.settings_tab': 'Configurações',
    'agency.brokers_title': 'Corretores da agência',
    'agency.brokers_description': 'Gerencie os corretores da sua agência',
    'agency.create_broker': 'Criar corretor',
    'agency.create_broker_title': 'Criar nova conta de corretor',
    'agency.create_broker_description': 'Crie uma nova conta de corretor vinculada diretamente à sua agência',
    'agency.broker_name': 'Nome completo',
    'agency.broker_email': 'Endereço de email',
    'agency.broker_password': 'Senha',
    'agency.create_broker_btn': 'Criar conta de corretor',
    'agency.broker_created': 'Conta de corretor criada com sucesso',
    'agency.broker_create_error': 'Falha ao criar conta de corretor',
    'agency.no_brokers': 'Nenhum corretor ainda',
    'agency.no_brokers_description': 'Crie contas de corretor ou envie convites',
    'agency.broker_removed': 'Corretor removido da agência',
    'agency.broker_remove_error': 'Falha ao remover corretor',
    'agency.invites_title': 'Convites pendentes',
    'agency.invites_description': 'Gerencie convites para potenciais corretores',
    'agency.send_invite': 'Enviar convite',
    'agency.send_invite_title': 'Convidar um corretor',
    'agency.send_invite_description': 'Envie um convite para se juntar à sua agência',
    'agency.invite_email': 'Endereço de email',
    'agency.send_invite_btn': 'Enviar convite',
    'agency.invite_sent': 'Convite enviado com sucesso',
    'agency.invite_error': 'Falha ao enviar convite',
    'agency.link_copied': 'Link do convite copiado',
    'agency.no_invites': 'Nenhum convite pendente',
    'agency.settings_title': 'Configurações da agência',
    'agency.settings_description': 'Atualize as informações da sua agência',
    'agency.settings_saved': 'Configurações da agência salvas',
    'agency.settings_save_error': 'Falha ao salvar configurações',
    'agency.load_error': 'Falha ao carregar dados da agência',
    'agency_invite.title': 'Juntar-se à agência',
    'agency_invite.description': 'Você foi convidado para se juntar a {agencyName}',
    'agency_invite.accept_btn': 'Aceitar convite',
    'agency_invite.accepted_success': 'Você se juntou à agência com sucesso!',
    'agency_invite.accept_error': 'Falha ao aceitar convite',
    'agency_invite.create_account': 'Criar uma conta',
    'agency_invite.invalid_link': 'Link do convite inválido',
    'agency_invite.expired': 'Este convite expirou',
    'agency_invite.already_accepted': 'Este convite já foi aceito',
    'agency_invite.fetch_error': 'Falha ao carregar convite',
    'agency_invite.error_title': 'Convite inválido',
    'agency_invite.back_home': 'Voltar para o início',
    'agency_invite.create_account_description': 'Crie uma conta para aceitar este convite',
    'agency_invite.your_name': 'Seu nome',
    'agency_invite.name_placeholder': 'Digite seu nome completo',
    'agency_invite.email_placeholder': 'seu@email.com',
    'agency_invite.password': 'Senha',
    'agency_invite.password_placeholder': 'Crie uma senha',
    'agency_invite.confirm_password': 'Confirmar senha',
    'agency_invite.confirm_password_placeholder': 'Confirme sua senha',
    'agency_invite.register_accept_btn': 'Criar conta e entrar',
    'agency_invite.registered_success': 'Conta criada e entrou na agência!',
    'agency_invite.register_error': 'Falha ao criar conta',
    'agency_invite.passwords_mismatch': 'As senhas não coincidem',
    'agency_invite.password_too_short': 'A senha deve ter pelo menos 6 caracteres',
    'agency_invite.already_have_account': 'Já tem uma conta?',
    'agency_invite.login_here': 'Entre aqui',
    'agency_invite.joining': 'Você está entrando em',
    'agency_invite.logged_in_as': 'Conectado como',
    'agency.notifications_tab': 'Notificações',
    'agency.notifications_title': 'Notificações por email',
    'agency.notifications_description': 'Escolha quais notificações receber sobre a atividade dos corretores',
    'agency.notifications_saved': 'Preferências de notificação salvas',
    'agency.notifications_save_error': 'Falha ao salvar preferências',
    'agency.notif_new_broker_joins': 'Novo corretor entra',
    'agency.notif_new_broker_joins_desc': 'Seja notificado quando um corretor aceitar seu convite',
    'agency.notif_broker_property_added': 'Corretor adiciona imóvel',
    'agency.notif_broker_property_added_desc': 'Seja notificado quando corretores adicionarem novos imóveis',
    'agency.notif_broker_viewing_requests': 'Solicitações de visita',
    'agency.notif_broker_viewing_requests_desc': 'Seja notificado sobre solicitações de visita para imóveis dos corretores',
    'agency.notif_broker_client_messages': 'Mensagens de clientes',
    'agency.notif_broker_client_messages_desc': 'Seja notificado quando clientes entrarem em contato com seus corretores',
    'agency.notif_weekly_summary': 'Resumo semanal',
    'agency.notif_weekly_summary_desc': 'Receba um resumo semanal da atividade da agência',
    'agency.analytics_tab': 'Análises',
    'agency.no_brokers_analytics': 'Adicione corretores para ver análises de desempenho',
    'agency.total_properties': 'Total de propriedades',
    'agency.total_views': 'Visualizações totais',
    'agency.total_viewings': 'Solicitações de visita',
    'agency.total_sold': 'Propriedades vendidas',
    'agency.total_sales_value': 'Valor total de vendas',
    'agency.broker_performance_chart': 'Desempenho dos corretores',
    'agency.broker_performance_chart_desc': 'Compare métricas dos corretores de relance',
    'agency.individual_broker_stats': 'Estatísticas individuais',
    'agency.individual_broker_stats_desc': 'Métricas detalhadas para cada corretor',
    'agency.properties': 'Propriedades',
    'agency.views': 'Visualizações',
    'agency.viewings': 'Visitas',
    'agency.contacts': 'Contatos',
    'agency.active': 'Ativo',
    'agency.sold': 'Vendido',
    'agency.sales_value': 'Valor de vendas',
    'agency.upgrade_plan': 'Atualizar plano',
    'agency.view_plans': 'Ver planos',
    'agency.billing_tab': 'Faturamento',
    'agency.name_label': 'Nome da agência',
    'agency.email_label': 'E-mail',
    'agency.phone_label': 'Telefone',
    'agency.website_label': 'Site',
    'agency.address_label': 'Endereço',
    'agency.city_label': 'Cidade',
    'agency.description_label': 'Descrição',
    'agency.description_placeholder': 'Conte aos clientes sobre sua agência...',
    'agency.save_settings': 'Salvar configurações',
    'agency.broker_usage': 'Uso de corretores',
    'agency.invite_broker': 'Convidar corretor',
    'agency.brokers': 'corretores',
    'agency.broker_limit_reached': 'Limite de corretores atingido',
    'agency.upgrade_for_more': 'Atualize seu plano para adicionar mais corretores',
    'agency.active_listings': 'Anúncios ativos',
    'agency.broker_slots_remaining': 'Vagas restantes',
    'agency.manage_subscription': 'Gerenciar assinatura',
    'agency.upgrade_your_plan': 'Atualize seu plano',
    'agency.choose_plan': 'Escolha o plano que melhor se adapta à sua agência',
    'agency.pro_plan': 'Pro',
    'agency.enterprise_plan': 'Empresarial',
    'agency.for_growing_agencies': 'Para agências em crescimento',
    'agency.for_large_agencies': 'Para grandes agências',
    'agency.per_month': '/mês',
    'agency.up_to_brokers': 'Até {count} corretores',
    'agency.unlimited_brokers': 'Corretores ilimitados',
    'agency.advanced_analytics': 'Análises avançadas',
    'agency.full_analytics': 'Suíte completa de análises',
    'agency.priority_support': 'Suporte prioritário',
    'agency.dedicated_support': 'Suporte dedicado',
    'agency.upgrade_to_pro': 'Atualizar para Pro',
    'agency.upgrade_to_enterprise': 'Atualizar para Empresarial',
    'agency.popular': 'Popular',
    'agency.current_plan': 'Plano atual',
    'agency.plan_features': 'Recursos do plano',
    'agency.billing_history': 'Histórico de faturamento',
    'agency.no_billing_history': 'Nenhum histórico de faturamento ainda',
    'agency.subscription_billing': 'Assinatura e Faturamento',
    'agency.manage_agency_subscription': 'Gerencie a assinatura da sua agência',
    'agency.plan': 'Plano',
    'agency.basic_plan': 'Básico',
    'agency.broker_limit': 'Limite de corretores',
    'agency.current_usage': 'Uso atual',
    'agency.status': 'Status',
    'agency.active_status': 'Ativo',
    'agency.unlimited': 'Ilimitado',
    'agency.feature': 'Recurso',
    'agency.price': 'Preço',
    'agency.free': 'Grátis',
    'agency.analytics': 'Análise',
    'agency.basic_analytics': 'Básico',
    'agency.advanced': 'Avançado',
    'agency.full_suite': 'Suíte completa',
    'agency.support': 'Suporte',
    'agency.email_support': 'E-mail',
    'agency.priority': 'Prioritário',
    'agency.dedicated': 'Dedicado',
    'agency_directory.title': 'Agências imobiliárias',
    'agency_directory.subtitle': 'Navegue e encontre agências imobiliárias confiáveis',
    'agency_directory.search_placeholder': 'Pesquisar agências...',
    'agency_directory.location_placeholder': 'Filtrar por localização...',
    'agency_directory.agencies_found': 'agências encontradas',
    'agency_directory.no_agencies': 'Nenhuma agência encontrada',
    'agency_directory.no_agencies_description': 'Tente ajustar seus critérios de pesquisa',
    'agency_directory.brokers': 'corretores',
    'agency_directory.view_agency': 'Ver agência',
    'agency_directory.not_found': 'Agência não encontrada',
    'agency_directory.back_to_directory': 'Voltar ao diretório',
    'agency_directory.visit_website': 'Visitar site',
    'agency_directory.our_brokers': 'Nossos corretores',
    'agency_directory.no_brokers_yet': 'Ainda não há corretores nesta agência',
    'agency_directory.agency_listings': 'Anúncios de imóveis',
    
    // User Listings
    'userListings.title': 'Meus anúncios',
    'userListings.subtitle': 'Gerencie seus anúncios de imóveis e crie novos',
    'userListings.pricingTitle': 'Preços de anúncios',
    'userListings.pricingDesc': 'Crie seu anúncio de imóvel com nossa precificação simples',
    'userListings.listingPrice': '$10 por anúncio',
    'userListings.listingDuration': 'Válido por 1 mês',
    'userListings.videoPrice': '+$3 por vídeo',
    'userListings.videoDesc': 'Adicione um tour em vídeo ao seu anúncio',
    'userListings.includeVideo': 'Incluir tour em vídeo',
    'userListings.createListing': 'Criar anúncio',
    'userListings.myListingsTitle': 'Seus imóveis',
    'userListings.noListings': 'Nenhum anúncio ainda',
    'userListings.noListingsDesc': 'Crie seu primeiro anúncio de imóvel para começar',
    'userListings.createFirst': 'Crie seu primeiro anúncio',
    'userListings.hasVideo': 'Vídeo',
    'userListings.renew': 'Renovar',
    'userListings.renewSuccess': 'Anúncio renovado!',
    'userListings.renewSuccessDesc': 'Seu anúncio foi renovado por mais um mês',
    'userListings.paymentSuccess': 'Pagamento bem-sucedido!',
    'userListings.paymentSuccessDesc': 'Seu anúncio está ativo agora',
    'userListings.paymentCancelled': 'Pagamento cancelado',
    'userListings.paymentCancelledDesc': 'Seu pagamento foi cancelado',
    'userListings.checkoutError': 'Falha ao criar pagamento',
    'userListings.renewError': 'Falha ao renovar anúncio',
    'userListings.createDraft': 'Criar anúncio',
    'userListings.previewListing': 'Pré-visualizar seu anúncio',
    'userListings.draft': 'Rascunho',
    'userListings.drafts': 'Rascunhos',
    'userListings.draftSaved': 'Rascunho salvo',
    'userListings.draftSavedDesc': 'Seu anúncio foi salvo como rascunho',
    'userListings.draftError': 'Falha ao salvar rascunho',
    'userListings.draftDeleted': 'Rascunho excluído',
    'userListings.deleteError': 'Falha ao excluir',
    'userListings.saveDraft': 'Salvar rascunho',
    'userListings.saveAndPreview': 'Salvar e pré-visualizar',
    'userListings.publishOptions': 'Opções de publicação',
    'userListings.addVideoTour': 'Adicionar tour em vídeo',
    'userListings.videoIncluded': 'Tour em vídeo incluído',
    'userListings.totalPrice': 'Total',
    'userListings.backToEdit': 'Voltar para edição',
    'userListings.publishNow': 'Publicar agora',
    'userListings.publish': 'Publicar',
    'userListings.publishSuccess': 'Anúncio publicado!',
    'userListings.publishSuccessDesc': 'Seu anúncio está agora aguardando revisão',
    'userListings.firstListingFree': 'Primeiro anúncio é grátis!',
    'userListings.firstListingFreeDesc': 'Seu primeiro anúncio de imóvel é totalmente gratuito. Envie agora e alcance compradores em todo o Iraque!',
    'userListings.pending': 'Aguardando revisão',
    'userListings.pendingInfo': 'Estes anúncios estão sendo revisados pela nossa equipe de moderação. Você será notificado quando forem aprovados.',
    'userListings.pendingReview': 'Aguardando revisão',
    'userListings.awaitingApproval': 'Aguardando aprovação do moderador',
    'userListings.rejected': 'Anúncios rejeitados',
    'userListings.rejectedInfo': 'Estes anúncios não foram aprovados. Por favor, edite e corrija os problemas antes de reenviar.',
    'userListings.rejectedBadge': 'Rejeitado',
    'userListings.live': 'Ativo',
    'userListings.editDraft': 'Editar',
    'userListings.editAndResubmit': 'Editar e reenviar',
    'userListings.pendingCannotEdit': 'Não é possível editar durante a revisão',
    'userListings.resubmitForReview': 'Reenviar para revisão',
    'userListings.resubmitInfo': 'Suas alterações foram salvas. Envie seu anúncio novamente para revisão sem custo adicional.',
    'userListings.submitForReview': 'Enviar para revisão',
    'userListings.validation.priceRequired': 'Preço é obrigatório',
    'userListings.validation.cityRequired': 'Cidade é obrigatória',
    'userListings.validation.roomsRequired': 'Número de quartos é obrigatório',
    'userListings.validation.sizeRequired': 'Tamanho é obrigatório',
    'userListings.validation.plotSizeRequired': 'Tamanho do terreno é obrigatório',
    'userListings.validation.imagesRequired': 'Pelo menos uma imagem é obrigatória',
    'userListings.choosePlan': 'Escolha seu plano de anúncio',
    'userListings.choosePlanDesc': 'Selecione quantas imagens exibir no seu cartão de anúncio',
    'userListings.upgradePlan': 'Atualize seu plano de anúncio',
    'userListings.upgradePlanDesc': 'Atualize para mostrar mais imagens. Você paga apenas a diferença.',
    'userListings.selectImages': 'Selecione imagens para exibir',
    'userListings.selectImagesDesc': 'Escolha imagens para exibir no seu cartão de anúncio',
    'userListings.selectPrimaryImage': 'Selecione imagem principal',
    'userListings.selectPrimaryImageDesc': 'Escolha a imagem principal que será exibida primeiro no seu anúncio',
    'userListings.addVideo': 'Adicionar tour em vídeo',
    'userListings.pleaseSelectImages': 'Por favor, selecione imagens para continuar',
    'userListings.pleaseSelectPrimaryImage': 'Por favor, selecione uma imagem principal para continuar',
    'userListings.proceedToPayment': 'Prosseguir para pagamento',
    'userListings.tierBasic': 'Básico',
    'userListings.tierStandard': 'Padrão',
    'userListings.tierPremium': 'Premium',
    'userListings.tierBasicDesc': '1 imagem no cartão',
    'userListings.tierStandardDesc': 'Carrossel de 3 imagens',
    'userListings.tierPremiumDesc': 'Todas as imagens + selo',
    'userListings.tierDuration': '30 dias ativo',
    'userListings.tierBest': 'Melhor',
    'userListings.tierBasicFull': 'Nível Básico (1 imagem)',
    'userListings.tierStandardFull': 'Nível Padrão (3 imagens)',
    'userListings.tierPremiumFull': 'Nível Premium (Todas as imagens)',
    'userListings.changeTier': 'Alterar nível',
    'userListings.selectImageTier': 'Selecionar nível de imagem',
    'userListings.selectTierFirst': 'Selecione um nível antes de publicar',
    'userListings.upgrade': 'Atualizar',
    'common.total': 'Total',
    
    // Settings Page
    'settings.title': 'Configurações',
    'settings.subtitle': 'Gerencie as configurações e preferências da sua conta',
    'settings.profile': 'Perfil',
    'settings.notifications': 'Notificações',
    'settings.account': 'Conta',
    'settings.profile_info': 'Informações do perfil',
    'settings.profile_desc': 'Atualize suas informações pessoais',
    'settings.change_photo': 'Alterar foto',
    'settings.display_name': 'Nome de exibição',
    'settings.display_name_placeholder': 'Seu nome',
    'settings.email': 'Email',
    'settings.email_placeholder': 'seu@email.com',
    'settings.phone': 'Telefone',
    'settings.phone_placeholder': '+55 xx xxxxx-xxxx',
    'settings.save': 'Salvar alterações',
    'settings.saved': 'Configurações salvas com sucesso',
    'settings.save_error': 'Falha ao salvar configurações',
    'settings.load_error': 'Falha ao carregar configurações',
    'settings.notification_preferences': 'Preferências de notificação',
    'settings.notification_desc': 'Escolha quais notificações deseja receber',
    'settings.account_settings': 'Configurações da conta',
    'settings.account_desc': 'Gerencie a segurança e preferências da sua conta',
    'settings.change_password': 'Alterar senha',
    'settings.change_password_desc': 'Atualize sua senha',
    'settings.change': 'Alterar',
    'settings.two_factor': 'Autenticação de dois fatores',
    'settings.two_factor_desc': 'Adicione segurança extra à sua conta',
    'settings.enable': 'Ativar',
    'settings.disable': 'Desativar',
    'settings.delete_account': 'Excluir conta',
    'settings.delete_account_desc': 'Excluir permanentemente sua conta',
    'settings.delete': 'Excluir',
    'settings.logout': 'Sair',
    'settings.password_changed': 'Senha alterada com sucesso',
    'settings.passwords_mismatch': 'As senhas não coincidem',
    'settings.2fa_enabled': 'Autenticação de dois fatores ativada',
    'settings.2fa_disabled': 'Autenticação de dois fatores desativada',
    
    // Contact Form
    'contact_form.title': 'Contatar agente',
    'contact_form.send': 'Enviar mensagem',
    'contact_form.sending': 'Enviando...',
    'contact_form.success': 'Mensagem enviada com sucesso!',
    'contact_form.error': 'Falha ao enviar mensagem',
    
    'common.back': 'Voltar',
    
    // Messaging
    'messaging.title': 'Mensagens',
    'messaging.description': 'Suas conversas privadas',
    'messaging.new_message': 'Nova mensagem',
    'messaging.no_messages': 'Nenhuma mensagem ainda',
    'messaging.no_conversations': 'Nenhuma conversa ainda',
    'messaging.send': 'Enviar',
    'messaging.send_message': 'Enviar mensagem',
    'messaging.message_to': 'Enviar mensagem para',
    'messaging.subject': 'Assunto',
    'messaging.subject_placeholder': 'Assunto (opcional)',
    'messaging.message': 'Mensagem',
    'messaging.message_placeholder': 'Escreva sua mensagem...',
    'messaging.login_required': 'Você precisa estar logado para enviar mensagens.',
    'messaging.message_sent': 'Mensagem enviada com sucesso',
    'messaging.contact_agent_description': 'Entre em contato diretamente com o corretor',
    'messaging.type_message': 'Digite uma mensagem...',
    'messaging.inbox': 'Caixa de entrada',
    'messaging.sent': 'Enviadas',
    'messaging.search': 'Pesquisar conversas...',
    'messaging.select_conversation': 'Selecione uma conversa',
    'messaging.select_description': 'Escolha uma conversa da lista para começar a conversar',
    'messaging.encrypted': 'Criptografia de ponta a ponta',
    'messaging.decrypt': 'Descriptografar mensagem',
    'messaging.reply': 'Responder',
    'messaging.replying_to': 'Respondendo a',
    'messaging.replying_to_yourself': 'você mesmo',
    'messaging.forward': 'Encaminhar',
    'messaging.delete': 'Excluir',
    'messaging.delete_confirm': 'Excluir mensagem',
    'messaging.delete_description': 'Esta mensagem será excluída para todos. Esta ação não pode ser desfeita.',
    'messaging.delete_for_everyone': 'Excluir para todos',
    'messaging.pin': 'Fixar',
    'messaging.unpin': 'Desafixar',
    'messaging.pinned_messages': 'Mensagens fixadas',
    'messaging.no_pinned': 'Nenhuma mensagem fixada',
    'messaging.delivered': 'Entregue',
    'messaging.read': 'Lida',
    'messaging.seen_at': 'Visto às',
    'messaging.voice_message': 'Mensagem de voz',
    'messaging.search_messages': 'Pesquisar nas mensagens...',
    'messaging.contacts': 'Contatos',
    'messaging.results_found': 'resultados encontrados',
    'messaging.recording': 'Gravando...',
    'messaging.typing': 'digitando...',
    'messaging.online': 'online',
    'messaging.tap_for_info': 'toque aqui para info de contato',
    'messaging.photos': 'Fotos',
    'messaging.camera': 'Câmera',
    'messaging.location': 'Localização',
    'messaging.contact': 'Contato',
    'messaging.document': 'Documento',
    'messaging.poll': 'Enquete',
    'messaging.event': 'Evento',
    'messaging.location_coming_soon': 'Compartilhamento de localização em breve',
    'messaging.contact_coming_soon': 'Compartilhamento de contato em breve',
    'messaging.poll_coming_soon': 'Criação de enquete em breve',
    'messaging.event_coming_soon': 'Criação de evento em breve',
    'messaging.schedule_event': 'Agendar evento',
    'messaging.event_title': 'Título do evento',
    'messaging.event_title_placeholder': 'ex: Visita de imóvel',
    'messaging.event_date': 'Data',
    'messaging.event_time': 'Hora',
    'messaging.event_location': 'Local',
    'messaging.event_location_placeholder': 'Digite o local ou endereço',
    'messaging.event_description': 'Descrição',
    'messaging.event_description_placeholder': 'Adicione detalhes adicionais...',
    'messaging.send_event': 'Enviar evento',
    'messaging.event_created': 'Evento criado',
    'messaging.create_poll': 'Criar enquete',
    'messaging.poll_question': 'Pergunta',
    'messaging.poll_question_placeholder': 'O que você gostaria de perguntar?',
    'messaging.poll_options': 'Opções',
    'messaging.option': 'Opção',
    'messaging.add_option': 'Adicionar opção',
    'messaging.allow_multiple': 'Permitir múltiplas respostas',
    'messaging.send_poll': 'Enviar enquete',
    'messaging.poll_created': 'Enquete criada',
    'messaging.edit': 'Editar',
    'messaging.edit_message': 'Editar mensagem',
    'messaging.edit_description': 'Faça alterações na sua mensagem',
    'messaging.edit_time_remaining': 'Você tem',
    'messaging.minutes_to_edit': 'minutos para editar esta mensagem',
    'messaging.message_edited': 'Mensagem editada',
    'messaging.edit_failed': 'Falha ao editar mensagem',
    'messaging.from_date': 'De',
    'messaging.to_date': 'Até',
    'messaging.clear_filters': 'Limpar filtros',
    'messaging.export_chat': 'Exportar conversa',
    'messaging.export_description': 'Exportar histórico da conversa',
    'messaging.export_text': 'Arquivo de texto (.txt)',
    'messaging.export_text_desc': 'Formato de texto simples, fácil de ler e compartilhar',
    'messaging.export_pdf': 'Documento PDF',
    'messaging.export_pdf_desc': 'Documento formatado, ideal para impressão',
    'messaging.export_messages_count': 'mensagens serão exportadas',
    'messaging.exporting': 'Exportando...',
    'messaging.export': 'Exportar',
    'messaging.export_success': 'Conversa exportada com sucesso',
    'messaging.export_failed': 'Falha ao exportar',
    'messaging.is_typing': 'digitando...',
    
    // Quick Reply Templates
    'quick_reply.title': 'Respostas Rápidas',
    'quick_reply.greeting': 'Olá! Obrigado por entrar em contato.',
    'quick_reply.interested': 'Estou interessado neste imóvel. Pode fornecer mais detalhes?',
    'quick_reply.schedule_viewing': 'Gostaria de agendar uma visita. Quais horários estão disponíveis?',
    'quick_reply.ask_price': 'O preço é negociável?',
    'quick_reply.ask_availability': 'Este imóvel ainda está disponível?',
    'quick_reply.thank_you': 'Obrigado pelas informações. Entrarei em contato em breve.',
    'quick_reply.will_contact': 'Entrarei em contato em breve com mais detalhes.',
    'quick_reply.not_interested': 'Obrigado, mas não estou mais interessado neste imóvel.',
    
    // Notifications
    'notifications.enable': 'Ativar notificações',
    'notifications.on': 'Notificações ativadas',
    'notifications.preferences': 'Preferências de notificação',
    'notifications.sound': 'Som de notificação',
    'notifications.sound_desc': 'Reproduzir um som ao receber uma mensagem',
    'notifications.sound_default': 'Padrão',
    'notifications.sound_chime': 'Sino',
    'notifications.sound_bell': 'Campainha',
    'notifications.sound_pop': 'Pop',
    'notifications.test': 'Testar',
    'notifications.vibration': 'Vibração',
    'notifications.vibration_desc': 'Vibrar ao receber uma mensagem (apenas móvel)',
    'messaging.you': 'Você',
    'messaging.encrypted_message': 'Mensagem criptografada',
    'messaging.e2e_notice': 'Mensagens e chamadas são criptografadas de ponta a ponta. Apenas as pessoas neste chat podem lê-las, ouvi-las ou compartilhá-las.',
    'messaging.today': 'Hoje',
    'messaging.yesterday': 'Ontem',
    'messaging.search_groups': 'Pesquisar grupos...',
    
    // Attachment viewer
    'attachment.all_media': 'Todas as mídias',
    'attachment.go_to_message': 'Ir para mensagem',
    'attachment.download': 'Baixar',
    'attachment.view_fullscreen': 'Ver em tela cheia',
    'attachment.save_to_gallery': 'Salvar na galeria',
    'attachment.share': 'Compartilhar',
    'attachment.double_tap_zoom': 'Toque duas vezes para ampliar',
    'attachment.zoom_in': 'Ampliar',
    'attachment.zoom_out': 'Reduzir',
    'attachment.reset_zoom': 'Redefinir zoom',
    'attachment.ctrl_scroll_zoom': 'Ctrl + Rolar para zoom',
    'attachment.fit_width': 'Ajustar à largura',
    'attachment.fit_height': 'Ajustar à altura',
    'attachment.click_to_navigate': 'Clique para navegar',
    
    // Status
    'status.online': 'Online',
    'status.offline': 'Offline',
    'status.last_seen': 'Visto por último',
    
    // Blocking
    'blocking.block_user': 'Bloquear utilizador',
    'blocking.blocked_users': 'Utilizadores bloqueados',
    'blocking.unblock': 'Desbloquear',
    'blocking.you_blocked_user': 'Bloqueou este utilizador. Desbloqueie para enviar mensagens.',
    'blocking.user_blocked_you': 'Este utilizador bloqueou-o. Não pode enviar mensagens.',
    'reporting.report_user': 'Denunciar utilizador',
    'admin.reports': 'Denúncias',
    'admin.reports_panel': 'Denúncias de utilizadores',
    'admin.pending': 'Pendente',
    'admin.resolved': 'Resolvido',
    'admin.all': 'Todos',
    'messaging.unknown_user': 'Utilizador desconhecido',
    'messaging.no_groups': 'Sem grupos ainda',
    'messaging.create_group': 'Criar grupo',
    'messaging.group_chat': 'Chat em grupo',
    'messaging.direct_messages': 'Direto',
    'messaging.groups': 'Grupos',
    'messaging.group_name': 'Nome do grupo',
    'messaging.group_description': 'Descrição',
    
    'nav.favorites': 'Favoritos',
    'nav.profile': 'Perfil',
    'nav.login': 'Entrar',
    'nav.logout': 'Sair',
    'nav.recent_searches': 'Pesquisas recentes',
    'nav.no_recent_searches': 'Sem pesquisas recentes',
    
    'agents.title': 'Encontrar um agente',
    'agents.subtitle': 'Está pensando em vender? Ajudamos você a encontrar agentes especialistas na sua região!',
    'agents.area_search': 'Pesquisa por zona',
    'agents.name_search': 'Pesquisa por nome',
    'agents.search_placeholder': 'Insira zona ou endereço',
    'agents.show': 'Mostrar',
    'agents.sort_by': 'Ordenar por',
    'agents.agents_count': 'Agentes',
    'agents.companies_label': 'Imobiliárias',
    'agents.recent_sales': 'Última data de venda',
    'agents.most_sales': 'Mais vendas',
    'agents.alphabetical': 'Ordem alfabética',
    'agents.showing_results': 'Mostrando',
    'agents.agents_label': 'agentes',
    'agents.loading': 'Carregando agentes...',
    'agents.no_results': 'Nenhum agente encontrado.',
    'agents.phone': 'Telefone:',
    'agents.status': 'Estado:',
    'agents.active_agent': 'Agente ativo',
    'agents.anonymous_agent': 'Agente anónimo',
    'agents.agent_label': 'Agente',
    'agents.show_more': 'Mostrar mais agentes',
    'agents.verified_agent': 'Agente verificado',
    
    // Broker Public Profile
    'broker.listing_usage': 'Uso de anúncios',
    'broker.monthly_listing_limits': 'Limites mensais de anúncios',
    'broker.listings_used': 'Anúncios usados',
    'broker.listings_remaining': 'anúncios restantes',
    'broker.period_resets': 'O período reinicia',
    'broker.export_csv': 'Exportar CSV',
    'broker.total_views': 'Total de visualizações',
    'broker.scheduled_viewings': 'Visitas agendadas',
    'broker.client_messages': 'Mensagens de clientes',
    'broker.filters': 'Filtros',
    'broker.all_time': 'Todo o tempo',
    'broker.active_listings': 'Anúncios ativos',
    'broker.sold_listings': 'Anúncios vendidos',
    'broker.active_listings_desc': 'Suas propriedades publicadas atualmente',
    'broker.no_active_listings': 'Ainda sem anúncios ativos. Adicione sua primeira propriedade.',
    'broker.all_types': 'Todos os tipos',
    'broker.all_cities': 'Todas as cidades',
    'broker.property_type': 'Tipo de imóvel',
    'broker.city': 'Cidade',
    'broker.date_range': 'Intervalo de datas',
    'broker.last_week': 'Última semana',
    'broker.last_month': 'Último mês',
    'broker.last_quarter': 'Últimos 3 meses',
    'broker.sold_listings_desc': 'Imóveis que você vendeu',
    'broker.no_sold_listings': 'Ainda sem imóveis vendidos',
    'broker.clear_filters': 'Limpar filtros',
    'broker.selected': 'selecionado(s)',
    'broker.compare': 'Comparar',
    'broker.mark_as_sold': 'Marcar como vendido',
    'broker.delete_selected': 'Excluir selecionados',
    'broker.select_all': 'Selecionar tudo',
    'broker.rejected_listings': 'Anúncios rejeitados',
    'broker.rejected_listings_desc': 'Estes anúncios foram rejeitados pelos moderadores. Você pode apresentar um recurso.',
    'broker.no_rejected_listings': 'Nenhum anúncio rejeitado',
    'broker.confirm_delete': 'Confirmar exclusão',
    'broker_public.not_found': 'Agente não encontrado',
    'broker_public.not_found_description': 'O perfil do agente que você procura não existe.',
    'broker_public.back_to_search': 'Voltar à pesquisa de agentes',
    'broker_public.contact_broker': 'Contactar agente',
    'broker_public.agency': 'Agência',
    'broker_public.visit_website': 'Visitar site',
    'broker_public.active_listings': 'Anúncios ativos',
    'broker_public.sold_properties': 'Imóveis vendidos',
    'broker_public.total_sales': 'Vendas totais',
    'broker_public.avg_days_market': 'Dias médios no mercado',
    'broker_public.no_active_listings': 'Sem anúncios ativos no momento',
    'broker_public.no_sold_properties': 'Ainda sem imóveis vendidos',
    'broker_public.view_all_properties': 'Ver todos os imóveis',
    'broker_public.send_message': 'Enviar mensagem',
    'broker_public.view_agency': 'Ver agência',
    'broker_public.works_at': 'Trabalha em',
    'broker_public.listings': 'Anúncios',
    'broker_public.sold': 'Vendido',
    'broker_public.reviews': 'Avaliações',
    'broker_public.rating': 'Classificação',
    'broker_public.featured_broker': 'Agente em destaque',
    'broker_public.top_performer': 'Melhor desempenho',
    
    // Toast messages
    'toast.logged_out': 'Sessão encerrada',
    'toast.welcome_back': 'Volte quando quiser!',
    
    // Recent searches
    'search.properties_label': 'Imóveis',
    'search.from': 'de',
    'search.up_to': 'até',
    
    'terms.title': 'Termos de Serviço', 'terms.last_updated': 'Última atualização:', 'terms.section1.title': '1. Aceitação dos Termos', 'terms.section1.p1': 'Ao usar Aqarnet, você aceita estes termos.', 'terms.section1.p2': 'Reservamo-nos o direito de modificar estes termos.', 'terms.section2.title': '2. Descrição do Serviço', 'terms.section2.p1': 'Aqarnet fornece uma plataforma digital para anúncios imobiliários no Iraque.', 'terms.section2.p2': 'Nossos serviços incluem listagens, busca e ferramentas de comunicação.', 'terms.section3.title': '3. Responsabilidades do Usuário', 'terms.section3.p1': 'Como usuário, você concorda em:', 'terms.section3.li1': 'Fornecer informações precisas', 'terms.section3.li2': 'Não usar a plataforma para atividades ilegais', 'terms.section3.li3': 'Respeitar direitos de propriedade intelectual', 'terms.section3.li4': 'Não interferir no serviço', 'terms.section4.title': '4. Anúncios de Imóveis', 'terms.section4.p1': 'Os usuários são responsáveis pela precisão de seus anúncios.', 'terms.section4.p2': 'Podemos remover anúncios que violem nossos termos.', 'terms.section5.title': '5. Propriedade Intelectual', 'terms.section5.p1': 'Todo o conteúdo pertence à Aqarnet.', 'terms.section5.p2': 'Reprodução proibida sem permissão.', 'terms.section6.title': '6. Limitação de Responsabilidade', 'terms.section6.p1': 'Aqarnet é fornecido "como está" sem garantias.', 'terms.section7.title': '7. Lei Aplicável', 'terms.section7.p1': 'Estes termos são regidos pela lei sueca.', 'terms.section7.p2': 'Disputas serão resolvidas em tribunais suecos.', 'terms.section8.title': '8. Contato', 'terms.section8.p1': 'Para perguntas, entre em contato:', 'terms.section8.email': 'Email',
    'privacy.title': 'Política de Privacidade', 'privacy.last_updated': 'Última atualização:', 'privacy.section1.title': '1. Introdução', 'privacy.section1.p1': 'Estamos comprometidos em proteger sua privacidade.', 'privacy.section1.p2': 'Ao usar nossos serviços, você aceita nossas práticas de dados.', 'privacy.section2.title': '2. Informações Coletadas', 'privacy.section2.p1': 'Coletamos:', 'privacy.section2.li1': 'Informações de conta', 'privacy.section2.li2': 'Dados imobiliários', 'privacy.section2.li3': 'Dados de uso', 'privacy.section2.li4': 'Informações do dispositivo', 'privacy.section3.title': '3. Uso das Informações', 'privacy.section3.p1': 'Usamos suas informações para:', 'privacy.section3.li1': 'Fornecer serviços', 'privacy.section3.li2': 'Personalizar experiência', 'privacy.section3.li3': 'Enviar notificações', 'privacy.section3.li4': 'Comunicar com você', 'privacy.section4.title': '4. Compartilhamento de Dados', 'privacy.section4.p1': 'Não vendemos seus dados.', 'privacy.section4.p2': 'Podemos compartilhar informações se exigido por lei.', 'privacy.section5.title': '5. Seus Direitos', 'privacy.section5.p1': 'Você tem os seguintes direitos:', 'privacy.section5.li1': 'Acesso', 'privacy.section5.li2': 'Retificação', 'privacy.section5.li3': 'Exclusão', 'privacy.section5.li4': 'Portabilidade', 'privacy.section5.li5': 'Oposição', 'privacy.section6.title': '6. Segurança de Dados', 'privacy.section6.p1': 'Protegemos seus dados.', 'privacy.section6.p2': 'Nenhum método é 100% seguro.', 'privacy.section7.title': '7. Alterações', 'privacy.section7.p1': 'Podemos atualizar esta política.', 'privacy.section8.title': '8. Contato', 'privacy.section8.p1': 'Entre em contato para perguntas:', 'privacy.section8.email': 'Email',
    
    // Agency Pricing
    'agency_pricing.badge': 'Planos de Agência',
    'agency_pricing.title': 'Cresça sua agência com o plano certo',
    'agency_pricing.subtitle': 'Escolha o plano perfeito para sua imobiliária.',
    'agency_pricing.most_popular': 'Mais popular',
    'agency_pricing.per_month': '/mês',
    'agency_pricing.processing': 'Processando...',
    'agency_pricing.comparison_title': 'Comparação detalhada',
    'agency_pricing.feature': 'Recurso',
    'agency_pricing.faq_title': 'Perguntas frequentes',
    'agency_pricing.cta_title': 'Pronto para crescer sua agência?',
    'agency_pricing.cta_subtitle': 'Junte-se a milhares de imobiliárias de sucesso.',
    'agency_pricing.cta_button': 'Começar com Pro',
    'agency_pricing.sign_in_required': 'Faça login para atualizar',
    'agency_pricing.tier.starter': 'Inicial',
    'agency_pricing.tier.starter_desc': 'Perfeito para pequenas agências',
    'agency_pricing.tier.starter_cta': 'Começar',
    'agency_pricing.tier.pro': 'Pro',
    'agency_pricing.tier.pro_desc': 'Para agências em crescimento',
    'agency_pricing.tier.pro_cta': 'Atualizar para Pro',
    'agency_pricing.tier.enterprise': 'Enterprise',
    'agency_pricing.tier.enterprise_desc': 'Para grandes agências',
    'agency_pricing.tier.enterprise_cta': 'Escolher Enterprise',
    'agency_pricing.feature.brokers_3': 'Até 3 corretores',
    'agency_pricing.feature.brokers_10': 'Até 10 corretores',
    'agency_pricing.feature.brokers_unlimited': 'Corretores ilimitados',
    'agency_pricing.feature.listings_basic': 'Anúncios básicos',
    'agency_pricing.feature.listings_advanced': 'Anúncios avançados',
    'agency_pricing.feature.listings_premium': 'Anúncios premium',
    'agency_pricing.feature.messaging': 'Mensagens com clientes',
    'agency_pricing.feature.scheduling': 'Agendamento de visitas',
    'agency_pricing.feature.analytics': 'Painel de análise',
    'agency_pricing.feature.analytics_advanced': 'Análise avançada',
    'agency_pricing.feature.support': 'Suporte prioritário',
    'agency_pricing.feature.support_247': 'Suporte 24/7',
    'agency_pricing.feature.branding': 'Marca personalizada',
    'agency_pricing.feature.api': 'Acesso API',
    'agency_pricing.comparison.brokers': 'Número de corretores',
    'agency_pricing.comparison.listings': 'Anúncios de imóveis',
    'agency_pricing.comparison.basic': 'Básico',
    'agency_pricing.comparison.advanced': 'Avançado',
    'agency_pricing.comparison.premium': 'Premium',
    'agency_pricing.faq.change_plan_q': 'Posso mudar de plano depois?',
    'agency_pricing.faq.change_plan_a': 'Sim! Você pode mudar a qualquer momento.',
    'agency_pricing.faq.exceed_limit_q': 'O que acontece se eu exceder o limite?',
    'agency_pricing.faq.exceed_limit_a': 'Você precisará atualizar para um plano superior.',
    'agency_pricing.faq.trial_q': 'Há teste gratuito?',
    'agency_pricing.faq.trial_a': 'Garantia de reembolso de 14 dias.',
    'agency_pricing.faq.cancel_q': 'Como cancelo minha assinatura?',
    'agency_pricing.faq.cancel_a': 'Gerencie sua assinatura pelo painel.',
    'agency_pricing.view_plans': 'Ver planos e preços',
    
    // Broker Pricing
    'broker_pricing.badge': 'Planos de Corretor',
    'broker_pricing.title': 'Atualize sua conta de corretor',
    'broker_pricing.subtitle': 'Escolha o plano perfeito para seu negócio imobiliário. Mais anúncios, melhores análises e suporte prioritário.',
    'broker_pricing.most_popular': 'Mais popular',
    'broker_pricing.current_plan': 'Seu plano atual',
    'broker_pricing.your_current_plan': 'Seu plano atual',
    'broker_pricing.free': 'Gratuito',
    'broker_pricing.month': 'mês',
    'broker_pricing.per_month': '/mês',
    'broker_pricing.processing': 'Processando...',
    'broker_pricing.loading': 'Carregando...',
    'broker_pricing.manage_subscription': 'Gerenciar assinatura',
    'broker_pricing.cancel_anytime': 'Rebaixar ou cancelar a qualquer momento • Renova automaticamente mensalmente',
    'broker_pricing.manage_note': 'Rebaixar ou cancelar a qualquer momento • Renova automaticamente mensalmente',
    'broker_pricing.downgrade': 'Rebaixar',
    'broker_pricing.upgrade_to_pro': 'Atualizar para Pro',
    'broker_pricing.go_enterprise': 'Ir para Enterprise',
    'broker_pricing.get_started_pro': 'Começar com Pro',
    'broker_pricing.comparison_title': 'Comparação detalhada de recursos',
    'broker_pricing.feature': 'Recurso',
    'broker_pricing.faq_title': 'Perguntas frequentes',
    'broker_pricing.cta_title': 'Pronto para crescer seu negócio?',
    'broker_pricing.cta_subtitle': 'Junte-se a milhares de corretores de sucesso que usam nossa plataforma para listar mais propriedades e fechar mais negócios.',
    'broker_pricing.cta_button': 'Começar com Pro',
    'broker_pricing.sign_in_required': 'Por favor faça login para atualizar seu plano',
    'broker_pricing.already_on_plan': 'Você já está neste plano',
    'broker_pricing.checkout_error': 'Falha ao iniciar o pagamento',
    'broker_pricing.portal_error': 'Falha ao abrir gerenciamento de assinatura',
    'broker_pricing.tier.normal': 'Normal',
    'broker_pricing.tier.normal_desc': 'Perfeito para corretores individuais começando',
    'broker_pricing.tier.normal_cta': 'Plano atual',
    'broker_pricing.tier.pro': 'Pro',
    'broker_pricing.tier.pro_desc': 'Para corretores em crescimento com mais anúncios',
    'broker_pricing.tier.pro_cta': 'Atualizar para Pro',
    'broker_pricing.tier.enterprise': 'Enterprise',
    'broker_pricing.tier.enterprise_desc': 'Para corretores de alto volume com necessidades ilimitadas',
    'broker_pricing.tier.enterprise_cta': 'Ir para Enterprise',
    'broker_pricing.feature.listings_5': '5 anúncios por mês',
    'broker_pricing.feature.listings_10': '10 anúncios por mês',
    'broker_pricing.feature.listings_15': '15 anúncios por mês',
    'broker_pricing.feature.basic_features': 'Recursos básicos de propriedades',
    'broker_pricing.feature.advanced_features': 'Recursos avançados de propriedades',
    'broker_pricing.feature.premium_features': 'Recursos premium de propriedades',
    'broker_pricing.feature.client_messaging': 'Mensagens com clientes',
    'broker_pricing.feature.viewing_scheduling': 'Agendamento de visitas',
    'broker_pricing.feature.video_tours_paid': 'Tours de vídeo ($3 por anúncio)',
    'broker_pricing.feature.video_tours_free': 'Tours de vídeo grátis incluídos',
    'broker_pricing.feature.analytics': 'Painel de análise',
    'broker_pricing.feature.advanced_analytics': 'Análise avançada',
    'broker_pricing.feature.priority_support': 'Suporte prioritário',
    'broker_pricing.feature.support_24_7': 'Suporte prioritário 24/7',
    'broker_pricing.feature.featured_listings': 'Anúncios em destaque',
    'broker_pricing.feature.extra_listings': 'Opção de anúncios extras',
    'broker_pricing.feature.extra_10': '+$10 por anúncio extra',
    'broker_pricing.feature.messaging': 'Mensagens com clientes',
    'broker_pricing.feature.scheduling': 'Agendamento de visitas',
    'broker_pricing.feature.video_paid': 'Tours de vídeo ($3 por anúncio)',
    'broker_pricing.feature.video_free': 'Tours de vídeo grátis incluídos',
    'broker_pricing.feature.analytics_advanced': 'Análise avançada',
    'broker_pricing.feature.support': 'Suporte prioritário',
    'broker_pricing.feature.support_247': 'Suporte prioritário 24/7',
    'broker_pricing.feature.featured': 'Anúncios em destaque',
    'broker_pricing.feature.extra_price': '+$10 por anúncio extra',
    'broker_pricing.table.listings_per_month': 'Anúncios por mês',
    'broker_pricing.table.video_tours': 'Tours de vídeo',
    'broker_pricing.table.extra_listings': 'Anúncios extras',
    'broker_pricing.table.listing_duration': 'Duração do anúncio',
    'broker_pricing.table.client_messaging': 'Mensagens com clientes',
    'broker_pricing.table.analytics_dashboard': 'Painel de análise',
    'broker_pricing.table.priority_support': 'Suporte prioritário',
    'broker_pricing.table.featured_listings': 'Anúncios em destaque',
    'broker_pricing.table.15_extras': '15 + extras',
    'broker_pricing.table.3_per_listing': '$3 por anúncio',
    'broker_pricing.table.10_each': '$10 cada',
    'broker_pricing.table.30_days': '30 dias',
    'broker_pricing.comparison.listings': 'Anúncios por mês',
    'broker_pricing.comparison.video': 'Tours de vídeo',
    'broker_pricing.comparison.extra': 'Anúncios extras',
    'broker_pricing.comparison.duration': 'Duração do anúncio',
    'broker_pricing.comparison.featured': 'Anúncios em destaque',
    'broker_pricing.comparison.30_days': '30 dias',
    'broker_pricing.comparison.per_listing': '$3 por anúncio',
    'broker_pricing.comparison.free': 'Gratuito',
    'broker_pricing.comparison.each': '$10 cada',
    'broker_pricing.comparison.15_plus': '15 + extras',
    'broker_pricing.faq.autorenew_q': 'Minha assinatura renova automaticamente?',
    'broker_pricing.faq.autorenew_a': 'Sim! Sua assinatura renova automaticamente todo mês para que você nunca perca acesso aos seus anúncios. Você será cobrado no mesmo dia todo mês. Você pode cancelar a qualquer momento pelo portal de gerenciamento de assinatura.',
    'broker_pricing.faq.upgrade_q': 'Posso atualizar ou rebaixar a qualquer momento?',
    'broker_pricing.faq.upgrade_a': 'Com certeza! Atualizações têm efeito imediato com cobrança proporcional. Rebaixamentos se aplicam no final do período de cobrança atual. Clique em "Gerenciar assinatura" para fazer alterações.',
    'broker_pricing.faq.expire_q': 'O que acontece quando meus anúncios expiram?',
    'broker_pricing.faq.expire_a': 'Anúncios expiram após 30 dias. Você receberá uma notificação por email 3 dias antes da expiração. Você pode renová-los usando seus slots mensais grátis ou pagar $10 por anúncio extra (apenas Enterprise).',
    'broker_pricing.faq.extra_q': 'O que são "anúncios extras"?',
    'broker_pricing.faq.extra_a': 'Membros Enterprise podem comprar anúncios adicionais por $10 cada além dos 15 anúncios mensais grátis. Ótimo para meses de alto volume.',
    'broker_pricing.faq.cancel_q': 'Como cancelo minha assinatura?',
    'broker_pricing.faq.cancel_a': 'Clique em "Gerenciar assinatura" para acessar seu portal de cobrança onde você pode cancelar, rebaixar ou atualizar seu método de pagamento. Cancelamentos têm efeito no final do período de cobrança.',
    'messaging.conversation_pinned': 'Conversa fixada',
    'messaging.conversation_unpinned': 'Conversa desafixada',
    'messaging.pin_chat': 'Fixar conversa',
    'messaging.unpin_chat': 'Desafixar conversa',
    'messaging.pin_failed': 'Falha ao fixar conversa',
    'messaging.unpin_failed': 'Falha ao desafixar conversa',
    'messaging.archive': 'Arquivar',
    'messaging.unarchive': 'Restaurar',
    'messaging.show_archived': 'Arquivadas',
    'messaging.show_active': 'Mostrar ativas',
    'messaging.conversation_archived': 'Conversa arquivada',
    'messaging.conversation_unarchived': 'Conversa restaurada',
    'messaging.archive_failed': 'Falha ao arquivar conversa',
    'messaging.unarchive_failed': 'Falha ao restaurar conversa',
    
    // Hero/News Section
    'hero.find_dream_home': 'Encontre a casa dos seus sonhos no Iraque',
    'hero.explore_description': 'Explore milhares de casas à venda e preços finais de todo o Iraque. Obtenha avaliações, leia notícias imobiliárias e contacte agentes.',
    'hero.badge': 'A principal plataforma imobiliária do Iraque',
  },
  tr: {
    // Header
    'nav.search': 'Mülk ara',
    'nav.search_agents': 'Temsilci ara',
    'nav.sell': 'Mülk sat',
    'nav.news': 'Haberler',
    'nav.build': 'Ev yap',
    'nav.agencies': 'Acenteler',
    'nav.buy': 'Satın Al',
    'nav.valuation': 'Değerleme',
    'nav.more': 'Daha fazla',
    'nav.saved': 'Kaydedildi',
    'nav.admin': 'Yönetici',
    'nav.agency': 'Acente',
    'nav.agency_dashboard': 'Acente paneli',
    'nav.location_manager': 'Konum yöneticisi',
    'nav.map_configuration': 'Harita yapılandırması',
    'nav.hello': 'Merhaba',
    'nav.menu': 'Menü',
    'nav.manage_news': 'Haberleri yönet',
    'nav.all_properties': 'Tüm mülkler',
    'nav.new_production': 'Yeni yapılar',
    
    // Cookie Policy
    'cookie.title': 'Çerez Politikası',
    'cookie.last_updated': 'Son güncelleme:',
    'cookie.section1.title': 'Bölüm 1 - İzleme Teknolojilerinin Uygulanmasına İlişkin Genel Hükümler',
    'cookie.section1.p1': 'Bundan böyle "Hizmet Sağlayıcı" olarak anılacak olan Ploqs AB, Hizmet Sağlayıcı tarafından görevlendirilen ortaklar ve alt yüklenicilerle birlikte, Hizmet Sağlayıcının dijital platformları, web siteleri ve mobil uygulamaları (toplu olarak "Hizmet Sağlayıcının Dijital Kanalları" olarak anılacaktır) üzerinde çerezler, web işaretçileri, piksel etiketleri ve diğer benzer teknolojik çözümler dahil ancak bunlarla sınırlı olmamak üzere çeşitli dijital izleme mekanizmalarını (toplu olarak "İzleme Teknolojileri" olarak anılacaktır) uygular ve kullanır. İzleme Teknolojilerinin işlenmesine ilişkin bu politika, Hizmet Sağlayıcının söz konusu teknolojileri hangi amaçlarla ve hangi yöntemlerle uyguladığını belirtir.',
    'cookie.section1.p2': 'İzleme Teknolojileri, kullanıcının dijital platformlar ve mobil uygulamalarla etkileşimi ve bunlar üzerindeki gezinmesi hakkında bilgi toplamak amacıyla kullanıcının terminal ekipmanına veya web tarayıcı programına yerleştirilen daha küçük metin tabanlı veri dosyalarından oluşur.',
    'cookie.section1.h1': 'Kişisel Verilerin İşlenmesi ve Veri Koruma Hususları',
    'cookie.section1.p3': 'İzleme Teknolojilerinin uygulanmasıyla toplanan bilgiler, çoğu durumda doğrudan veya dolaylı olarak ve diğer mevcut verilerle birlikte bireyleri tanımlamak için potansiyel olarak kullanılabilecek verileri oluşturur; bunlara IP adresleri, benzersiz çerez tanımlayıcıları, reklam tanımlayıcıları (IDFA), cihaz tanımlayıcıları ve belirli terminal ekipmanlarının ayırt edilmesini sağlayan çeşitli meta veriler dahildir.',
    'cookie.section1.h2': 'Kullanıcı Seçenekleri',
    'cookie.section1.p4': 'Kullanıcı, Hizmet Sağlayıcının dijital arayüzü aracılığıyla sağlanan onay yönetimi seçeneklerine giderek daha önce verilmiş onayları herhangi bir zamanda gözden geçirme hakkına sahiptir.',
    'cookie.section1.h3': 'İzleme Teknolojilerinin Kalıcılığı ve Depolama Süreleri',
    'cookie.section1.p5': 'İzleme Teknolojileri, her teknolojinin amaçlanan amacına ve işlevselliğine bağlı olarak değişen kalıcılık gösterir. Oturum tabanlı İzleme Teknolojileri, kullanıcının dijital platforma bağlanmasıyla başlatılır ve en geç kullanıcının tarayıcı örneğinin kapatılmasıyla sonlandırılır. Kalıcı İzleme Teknolojileri, bireysel oturumları aşan önceden belirlenmiş bir süre boyunca kullanıcının tarayıcısında tutulur.',
    'cookie.section2.title': 'Bölüm 2 - Hizmet Sunumu için Zorunlu İzleme Teknolojileri',
    'cookie.section2.p1': 'Zorunlu İzleme Teknolojileri, Hizmet Sağlayıcının kullanıcıya temel ve vazgeçilmez işlevsellik sağlaması için gerekli olan teknolojik uygulamaları ifade eder; örneğin, kullanıcının İzleme Teknolojilerine ilişkin daha önce verilmiş onayları hakkında bilgi tutmak, anonimleştirilmiş tanımlayıcılar aracılığıyla farklı kullanıcı oturumları arasında ayrım yapmayı sağlamak, harita işlevselliğini kolaylaştırmak, emlak danışmanları tarafından yüklenen mülk ilanlarında görsel-işitsel materyalin oynatılmasını sağlamak, kullanıcının kayıtlı arama tercihlerini korumak, hizmetlerin güvenliğini sağlamak, hizmetlerin uygunsuz kullanımını önlemek ve sürekli ve optimal hizmet performansını sağlamak amacıyla.',
    'cookie.section2.legal': 'İşleme için yasal dayanak:',
    'cookie.section2.legal_value': 'GDPR Madde 6.1(f) uyarınca meşru menfaat',
    'cookie.section2.storage': 'Saklama süresi:',
    'cookie.section2.storage_value': 'Kalıcı İzleme Teknolojileri için yirmi dört (24) aya kadar; oturum tabanlı İzleme Teknolojileri tarayıcı örneğinin kapatılmasıyla derhal sonlandırılır.',
    'cookie.section3.title': 'Bölüm 3 - Analitik Amaçlar için İzleme Teknolojileri',
    'cookie.section3.p1': 'Hizmet Sağlayıcı, ziyaret sıklığını ölçme, dijital platformda ve mobil uygulamalarda etkileşim verilerini kaydetme ve Hizmet Sağlayıcının dijital hizmetlerinin kullanıcılar tarafından nasıl kullanıldığını başka şekillerde analiz etme yeteneğine önemli ölçüde değer vermektedir. Kullanıcının gerekli onayı vermesi koşuluyla, Hizmet Sağlayıcı, doğrudan kişisel veriler hariç olmak üzere, Hizmet Sağlayıcının dijital hizmetlerini analiz etmek ve daha da geliştirmek, kullanıcıya sürekli olarak optimize edilmiş bir kullanıcı deneyimi sunmak, hizmet performansını sürdürmek ve platform kullanımına ilişkin hedef grup analizleri ve istatistiksel derlemeler üretmek amacıyla İzleme Teknolojilerini uygular.',
    'cookie.section3.legal_value': 'GDPR Madde 6.1(a) uyarınca onay',
    'cookie.section3.storage_value': 'Yirmi altı (26) aya kadar',
    'cookie.section4.title': 'Bölüm 4 - Kişiselleştirme Amaçları için İzleme Teknolojileri',
    'cookie.section4.p1': 'Kişiselleştirme amaçlarıyla uygulanan İzleme Teknolojileri, bireysel kullanıcı için artırılmış kişisel alaka düzeyine sahip içerik sağlayarak kullanıcının Hizmet Sağlayıcının dijital platformlarındaki deneyimini uyarlamayı amaçlar. Bu uyarlama, örneğin kullanıcının arama davranışına ve etkileşim kalıplarına dayanmaktadır. Kullanıcının Hizmet Sağlayıcı nezdinde kayıtlı bir kullanıcı hesabına sahip olması durumunda, bu davranış verileri, kullanıcının hizmet kapsamında gönüllü olarak sağladığı bilgilerle birleştirilir; örneğin kullanıcının konut koşullarına ilişkin bilgiler, böylece kullanıcının toplu profiline dayalı içerik uyarlamaları yapılır.',
    'cookie.section5.title': 'Bölüm 5 - Pazarlama Amaçları için İzleme Teknolojileri',
    'cookie.section5.p1': 'Hizmet Sağlayıcı, kullanıcının gerekli onayı vermesi koşuluyla, oluşturulmuş bir reklam profiline dayalı olarak kullanıcı için artırılmış alaka düzeyine sahip ilgili reklam içeriği ve mülk ilanlarının gösterilmesini sağlamak amacıyla pazarlama amaçlarına yönelik İzleme Teknolojilerini uygular.',
    'cookie.section5.p2': 'İzleme Teknolojilerinin uygulanması yoluyla, Hizmet Sağlayıcı ayrıca reklam gösterimlerinin kullanıcı başına önceden belirlenmiş sayıda ile sınırlandırılmasını ve reklam etkinliğinin ölçülmesini ve analiz edilmesini sağlar. Hizmet Sağlayıcının reklam içeriği harici dijital platformlarda da gösterilebilir.',
    'cookie.section5.p3': 'Kullanıcının pazarlama amaçlı İzleme Teknolojilerini devre dışı bırakmayı seçmesi durumunda, reklam içeriğinin Hizmet Sağlayıcının dijital platformlarında kullanıcıya gösterilmeye devam edeceği, ancak kişiselleştirme olmadan olacağı belirtilmelidir.',
    'cookie.section5.storage_value': 'Yirmi dört (24) aya kadar',
    'cookie.section6.title': 'Bölüm 6 - Üçüncü Ülkelere Uluslararası Veri Aktarımları',
    'cookie.section6.p1': 'Hizmet Sağlayıcının onay yönetimi aracında, hangi İzleme Teknolojilerinin ve ortakların kullanıcının kişisel verilerini Avrupa Birliği ve Avrupa Ekonomik Alanı dışındaki yargı alanlarında ("üçüncü ülkeler" olarak adlandırılan) işlediği veya kullanıcının kişisel verilerini bu yargı alanlarına aktardığı hakkında bilgi sağlanmaktadır.',
    'cookie.section7.title': 'Bölüm 7 - İzleme Teknolojilerinin Kaldırılması için Talimatlar',
    'cookie.section7.p1': 'Kullanıcı, terminal ekipmanından veya mobil cihazından İzleme Teknolojilerini kaldırma ve tarayıcının ayarlar arayüzü aracılığıyla yerel veri depolama ayarlarını yapılandırma seçeneğine sahiptir. Kullanıcı, İzleme Teknolojilerini devre dışı bırakmayı veya kullanıcının terminal ekipmanında her yeni İzleme Teknolojisi başlatıldığında bildirim almayı seçebilir.',
    'cookie.section7.p2': 'İzleme Teknolojilerinin işlenmesi ve kaldırılmasına ilişkin ayrıntılı talimatlar için, kullanıcının tarayıcısındaki yardım seçeneğine veya her tarayıcı uygulaması için aşağıdaki köprülere başvurulmaktadır:',
    
    // Auth
    'auth.loading': 'Yükleniyor...',
    'auth.login': 'Giriş Yap',
    'auth.create_account': 'Hesap Oluştur',
    'auth.login_subtitle': 'Aşağıdaki seçeneklerden birini seçerek giriş yapın.',
    'auth.signup_subtitle': 'Başlamak için yeni bir hesap oluşturun.',
    'auth.continue_apple': 'Apple ile devam et',
    'auth.continue_google': 'Google ile devam et',
    'auth.or': 'veya',
    'auth.email': 'E-posta',
    'auth.email_placeholder': 'E-posta girin',
    'auth.password': 'Şifre',
    'auth.password_placeholder': 'Şifre girin',
    'auth.account_type': 'Hesap türü',
    'auth.user_role': 'Kullanıcı - Mülk satın almak/kiralamak istiyorum',
    'auth.agent_role': 'Temsilci - Mülk satmak/kiralamak istiyorum',
    'auth.continue': 'Devam Et',
    'auth.no_account': 'Hesabınız yok mu? Hesap oluştur',
    'auth.have_account': 'Zaten hesabınız var mı? Giriş yap',
    'auth.login_failed': 'Giriş başarısız',
    'auth.invalid_credentials': 'Geçersiz e-posta veya şifre',
    'auth.welcome_back': 'Tekrar hoş geldiniz!',
    'auth.logged_in': 'Giriş yaptınız.',
    'auth.signup_failed': 'Kayıt başarısız',
    'auth.account_created': 'Hesap oluşturuldu!',
    'auth.check_email': 'Hesabınızı onaylamak için e-postanızı kontrol edin.',
    'auth.error': 'Bir hata oluştu',
    'auth.try_again': 'Lütfen daha sonra tekrar deneyin.',
    'auth.broker_interest_title': 'Emlakçı mısınız?',
    'auth.broker_interest_description': 'Aqarnet\'te mülk listelemek istiyorsanız, bize bir ilgi beyanı gönderin ve sizinle iletişime geçelim.',
    'auth.broker_contact_us': 'Bize ulaşın',
    'auth.broker_email_subject': 'Emlakçı kayıt ilgi beyanı',
    'auth.broker_email_body': 'Merhaba,\n\nAqarnet\'te emlakçı olarak kayıt olmak istiyorum.\n\nİsim:\nAcente adı:\nTelefon:\nŞehir:\n\nTeşekkürler!',
    'auth.first_name': 'Ad',
    'auth.last_name': 'Soyad',
    'auth.first_name_placeholder': 'Adınızı girin',
    'auth.last_name_placeholder': 'Soyadınızı girin',
    'auth.display_name': 'Görünen Ad',
    'auth.display_name_placeholder': 'Bir görünen ad seçin',
    'auth.display_name_hint': 'Bu, başkalarının göreceği tek isimdir',
    'auth.date_of_birth': 'Doğum Tarihi',
    'auth.fill_all_fields': 'Lütfen tüm gerekli alanları doldurun',
    'auth.must_be_18': 'Kayıt olmak için en az 18 yaşında olmalısınız',
    'auth.must_be_18_hint': 'En az 18 yaşında olmalısınız',
    'verification.verify_identity': 'Kimliği Doğrula',
    'verification.verified': 'Doğrulanmış',
    'verification.title': 'Kimlik Doğrulama',
    'verification.description': 'Kimliğinizi doğrulamak ve doğrulanmış rozet almak için resmi bir belge yükleyin.',
    'verification.document_type': 'Belge Türü',
    'verification.doc_id_card': 'Kimlik Kartı',
    'verification.doc_passport': 'Pasaport',
    'verification.doc_drivers_license': 'Ehliyet',
    'verification.upload_document': 'Belge Yükle',
    'verification.click_to_upload': 'Yüklemek için tıklayın veya sürükleyip bırakın',
    'verification.file_requirements': 'JPG, PNG, WEBP veya PDF (maks. 10MB)',
    'verification.submit': 'Doğrulama için Gönder',
    'verification.uploading': 'Yükleniyor...',
    'verification.submitted': 'Doğrulama Gönderildi',
    'verification.submitted_desc': 'Doğrulama talebiniz gönderildi. Kısa süre içinde inceleyeceğiz.',
    'verification.pending_message': 'Doğrulamanız inceleniyor. Bu genellikle 1-2 iş günü sürer.',
    'verification.rejected_message': 'Önceki doğrulamanız reddedildi. Lütfen yeni bir belge gönderin.',
    'verification.status_approved': 'Onaylandı',
    'verification.status_pending': 'Beklemede',
    'verification.status_rejected': 'Reddedildi',
    'verification.invalid_file_type': 'Geçersiz dosya türü',
    'verification.allowed_types': 'Lütfen JPG, PNG, WEBP veya PDF dosyası yükleyin',
    'verification.file_too_large': 'Dosya çok büyük',
    'verification.max_size': 'Maksimum dosya boyutu 10MB\'dir',
    'verification.error': 'Doğrulama Hatası',
    'verification.admin_title': 'Kimlik Doğrulamaları',
    'verification.admin_description': 'Kimlik doğrulama taleplerini inceleyin ve onaylayın',
    'verification.no_requests': 'Doğrulama talebi yok',
    'verification.view': 'İncele',
    'verification.review_title': 'Doğrulamayı İncele',
    'verification.review_notes': 'İnceleme Notları',
    'verification.review_notes_placeholder': 'Kararınız hakkında notlar ekleyin (isteğe bağlı)',
    'verification.approve': 'Onayla',
    'verification.reject': 'Reddet',
    'verification.approved': 'Doğrulama Onaylandı',
    'verification.rejected': 'Doğrulama Reddedildi',
    'verification.review_complete': 'Doğrulama işlendi.',
    
    // Favorites Page
    'favorites.title': 'Favorilerim',
    'favorites.saved_properties': 'Kaydedilen mülkler',
    'favorites.login_required': 'Favorilerinizi görmek için giriş yapın',
    'favorites.login_description': 'Beğendiğiniz mülkleri daha kolay bulmak için kaydedin.',
    'favorites.loading': 'Favoriler yükleniyor...',
    'favorites.no_favorites': 'Henüz favori yok',
    'favorites.start_saving': 'İlanlardaki kalbe tıklayarak mülkleri kaydetmeye başlayın.',
    'favorites.search_properties': 'Mülk ara',
    
    // Profile Page
    'profile.title': 'Profilim',
    'profile.subtitle': 'Kişisel bilgileri ve ayarları yönetin',
    'profile.loading': 'Profil yükleniyor...',
    'profile.fetch_error': 'Profil alınırken hata',
    'profile.fetch_error_description': 'Profil bilgileri alınamadı.',
    'profile.unnamed_user': 'İsimsiz kullanıcı',
    'profile.personal_info': 'Kişisel bilgiler',
    'profile.update_info': 'Kişisel bilgilerinizi güncelleyin',
    'profile.email': 'E-posta',
    'profile.email_readonly': 'E-posta değiştirilemez',
    'profile.display_name': 'Görünen ad',
    'profile.display_name_placeholder': 'Adınız',
    'profile.phone': 'Telefon numarası',
    'profile.phone_placeholder': '0532 123 45 67',
    'profile.save_changes': 'Değişiklikleri kaydet',
    'profile.saving': 'Kaydediliyor...',
    'profile.reset': 'Sıfırla',
    'profile.updated': 'Profil güncellendi',
    'profile.updated_description': 'Profiliniz kaydedildi.',
    'profile.update_error': 'Güncelleme hatası',
    'profile.update_error_description': 'Değişiklikler kaydedilemedi.',
    'profile.active_listings': 'Aktif ilanlar',
    'profile.active_listings_description': 'Satışa sunulan yayınlanmış mülk ilanlarınız',
    'profile.view_all': 'Tümünü gör',
    'profile.loading_listings': 'İlanlar yükleniyor...',
    'profile.no_active_listings': 'Henüz aktif ilan yok',
    'profile.more_active_listings': 've {count} aktif ilan daha',
    'profile.sold_properties': 'Satılan mülkler',
    'profile.sold_properties_description': 'Satılan mülkleriniz',
    'profile.no_sold_properties': 'Henüz satılan mülk yok',
    'profile.more_sold_properties': 've {count} satılan mülk daha',
    'profile.listing_fetch_error': 'İlanlar alınırken hata',
    'profile.listing_fetch_error_description': 'İlanlarınız alınamadı.',
    'profile.account_settings': 'Hesap ayarları',
    'profile.account_settings_description': 'Hesap ve güvenliği yönetin',
    'profile.change_password': 'Şifre değiştir',
    'profile.change_password_description': 'Daha güvenli giriş için şifreyi güncelleyin',
    'profile.change': 'Değiştir',
    'profile.two_factor': 'İki faktörlü kimlik doğrulama',
    'profile.two_factor_description': 'Hesabınıza ekstra güvenlik ekleyin',
    'profile.enable': 'Etkinleştir',
    'profile.delete_account': 'Hesabı sil',
    'profile.delete_account_description': 'Hesabı ve tüm verileri kalıcı olarak silin',
    'profile.delete': 'Sil',
    'profile.logout': 'Çıkış yap',
    
    // Not Found Page
    'notfound.title': '404',
    'notfound.message': 'Üzgünüz! Sayfa bulunamadı',
    'notfound.back_home': 'Ana sayfaya dön',
    
    // Property Detail Page
    'property.back_to_listings': 'İlanlara geri dön',
    'property.not_found': 'Mülk bulunamadı',
    'property.link_copied': 'Bağlantı kopyalandı',
    'property.link_copied_description': 'Mülk bağlantısı kopyalandı',
    'property.description': 'Açıklama',
    'property.features': 'Özellikler',
    'property.details': 'Mülk detayları',
    'property.rooms_label': 'Odalar',
    'property.size_label': 'Boyut',
    'property.plot_size_label': 'Arsa boyutu',
    'property.monthly_fee_label': 'Aylık ücret',
    'property.contact_agent': 'Temsilci ile iletişime geç',
    'property.send_message': 'Mesaj gönder',
    'property.schedule_viewing': 'Görüntüleme planla',
    'property.section.details': 'Detaylar',
    'property.section.amenities': 'Olanaklar',
    'property.section.overview': 'Genel Bakış',
    'property.section.payment_plan': 'Ödeme Planı',
    'property.section.floor_plans': 'Kat Planları',
    'property.section.view_map': 'Haritayı Görüntüle',
    'property.copy_link': 'Linki Kopyala',
    'property.share': 'Paylaş',
    'property.share_text': 'Bu mülke göz atın',
    'property.posted_on': 'Yayınlanma Tarihi',
    'property.type': 'Tür',
    'property.status': 'Durum',
    'property.available': 'Mevcut',
    'property.no_amenities': 'Bu mülk için olanak listelenmemiş',
    'property.no_description': 'Açıklama mevcut değil',
    'property.location': 'Konum',
    'property.payment_plan_info': 'Ödeme planı detayları için temsilci ile iletişime geçin',
    'property.contact_for_payment_plan': 'Ödeme Planı için İletişim',
    'property.no_floor_plans': 'Kat planları mevcut değil',
    'property.request_floor_plans': 'Kat Planlarını Talep Et',
    'property.no_map_location': 'Konum haritada mevcut değil',
    
    // News Page
    'news.subtitle': 'En son emlak haberleri, pazar analizleri ve rehberlerle güncel kalın',
    'news.no_articles': 'Henüz haber makalesi yok',
    'news.check_back': 'En son emlak haberleri ve pazar analizleri için yakında tekrar gelin.',
    'news.search_placeholder': 'Makale ara...',
    'news.showing_filtered': 'Bu sayfada {total} makaleden {shown} tanesi gösteriliyor',
    'news.clear_filters': 'Filtreleri temizle',
    'news.no_matching': 'Eşleşen makale yok',
    'news.adjust_filters': 'Aradığınızı bulmak için aramanızı veya filtrelerinizi ayarlamayı deneyin.',
    'news.publisher': 'Yayıncı',
    'news.page_info': 'Sayfa {current} / {total} • Toplam {count} makale',
    'news.category.all': 'Tümü',
    'news.category.market_news': 'Pazar Haberleri',
    'news.category.property_guides': 'Emlak Rehberleri',
    'news.category.investment_tips': 'Yatırım İpuçları',
    'news.category.industry_updates': 'Sektör Güncellemeleri',
    'news.category.announcements': 'Duyurular',
    'news.category.expert_advice': 'Uzman Tavsiyeleri',
    
    // Filters/Sorting
    'filters.any': 'Tümü',
    'filters.newest_first': 'En yeniler önce',
    'filters.price_low_high': 'Fiyat: düşükten yükseğe',
    'filters.price_high_low': 'Fiyat: yüksekten düşüğe',
    'filters.size': 'Boyut',
    'filters.searching_for': 'Aranıyor',
    'filters.search_areas': 'Alan, şehir ara...',
    
    // Properties for Sale Page
    'propertiesForSale.filters.searchWithin': 'İçinde ara',
    'propertiesForSale.filters.km': 'km',
    'propertiesForSale.filters.propertyType': 'Mülk türü',
    'propertiesForSale.filters.rooms': 'Oda',
    'propertiesForSale.filters.bathrooms': 'Banyo',
    'propertiesForSale.filters.min': 'Min',
    'propertiesForSale.filters.max': 'Maks',
    'propertiesForSale.filters.size': 'Boyut',
    'propertiesForSale.filters.price': 'Fiyat',
    'propertiesForSale.filters.searchResults': 'Arama sonuçları',
    'propertiesForSale.filters.searchPlaceholder': 'Bölge, şehir ara...',
    'propertiesForSale.filters.searchingFor': 'Aranıyor:',
    'propertiesForSale.filters.any': 'Tümü',
    'propertiesForSale.filters.keywords': 'Anahtar kelimeler',
    'propertiesForSale.filters.showResults': 'Sonuçları göster',
    'propertiesForSale.filters.clearFilters': 'Temizle',
    'propertiesForSale.filters.realEstateSituation': 'Gayrimenkul Durumu',
    'propertiesForSale.filters.situation.tapu': 'Tapu',
    'propertiesForSale.filters.situation.landContract': 'Arazi Sözleşmesi',
    'propertiesForSale.filters.situation.noDocuments': 'Belgeniz Yok',
    'propertiesForSale.filters.situation.investment': 'Yatırım',
    'propertiesForSale.filters.situation.musataha': 'Müsataha',
    'propertiesForSale.filters.situation.card': 'Kart',
    'propertiesForSale.filters.situation.agriculturalTapu': 'Tarım Tapusu',
    'propertiesForSale.sort.newest': 'En yeniler',
    'propertiesForSale.sort.price_low': 'Fiyat: düşükten yükseğe',
    'propertiesForSale.sort.price_high': 'Fiyat: yüksekten düşüğe',
    'propertiesForSale.sort.size': 'Boyut',
    'propertiesForSale.noResults': 'Mülk bulunamadı',
    'propertiesForSale.tryAdjustingFilters': 'Filtrelerinizi ayarlamayı veya farklı bir bölgede aramayı deneyin',
    
    // Search Section
    'search.for_sale': 'Satılık',
    'search.for_rent': 'Kiralık',
    'search.sold_prices': 'Satış fiyatları',
    'search.search_agents': 'Temsilci ara',
    'search.area_label': 'Bölge',
    'search.area_placeholder': 'Bölge veya adres yazın',
    'search.area_examples': 'Örnek: Bağdat, Basra, Erbil, Necef, Kerbela',
    'search.expand_area': 'Bölgeyi genişlet',
    'search.property_type': 'Mülk tipi',
    'search.show_filters': 'Filtreleri göster',
    'search.find_properties': 'Satılık mülk bul',
    'search.min_rooms': 'Min. oda',
    'search.min_area': 'Min. alan',
    'search.max_price': 'Maks. fiyat',
    'search.all': 'Tümü',
    'search.no_max': 'Maksimum yok',
    'search.new_construction': 'Yeni inşaat',
    'search.show_new_construction': 'Yeni inşaatı göster',
    'search.only_new_construction': 'Sadece yeni inşaat',
    'search.exclude_new_construction': 'Yeni inşaatı hariç tut',
    'search.keywords': 'Anahtar kelimeler',
    'search.keywords_placeholder': 'Havuz, şömine, vb.',
    'search.more_filters_note': 'Daha fazla filtre arama sonuçlarında mevcut.',
    'search.recent_searches': 'Son aramalar',
    'search.hide_history': 'Geçmişi gizle',
    'search.clear_history': 'Geçmişi temizle',
    
    'property.all_types': 'Tüm tipler',
    'property.villas': 'Villalar',
    'property.townhouses': 'Sıra evler',
    'property.apartments': 'Daireler',
    'property.business': 'Ticari mülkler',
    'property.land': 'Arsalar',
    'property.farms': 'Çiftlikler/Ormanlar',
    'property.other': 'Diğer',

    // Property Types (Select)
    'propertyTypes.allTypes': 'Tüm tipler',
    'propertyTypes.villa': 'Villalar',
    'propertyTypes.apartment': 'Daireler',
    'propertyTypes.townhouse': 'Sıra evler',
    'propertyTypes.business': 'Ticari mülkler',
    'propertyTypes.plot': 'Arsalar',
    'propertyTypes.farm': 'Çiftlikler/Ormanlar',
    'propertyTypes.other': 'Diğer',
    
    // Listings
    'listings.latest_for_sale': 'Son satılık ilanlar',
    'listings.properties_found': 'mülk bulundu',
    'listings.add_property': 'Mülk ekle',
    'listings.all_news': 'Tüm haberler',
    'listings.edit': 'Düzenle',
    'listings.edit_property': 'Mülkü düzenle',
    'listings.monthly_fee': 'Aylık ücret',
    'listings.open_floor_plan': 'Açık plan',
    'listings.large_balcony': 'Geniş balkon',
    'listings.family_friendly': 'Aile dostu',
    'listings.photos': 'fotoğraf',
    'listings.per_month': 'ay',
    'loading.properties': 'Mülkler yükleniyor...',
    'empty.no_properties': 'Şu anda mülk yok',
    'empty.check_back': 'Yeni ilanlar için daha sonra tekrar gelin.',
    
    // Property card
    'property.sold': 'SATILDI',
    'property.photos': 'fotoğraf',
    'property.rooms_short': 'oda',
    'property.plot': 'arsa',
    'property.per_month': '/ay',
    'property.delete_title': 'Mülk silinsin mi?',
    'property.delete_confirm': '{address} adresini silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.',
    'property.deleted': 'Mülk silindi',
    'property.deleted_desc': 'Mülk kaldırıldı',
    'property.delete_error': 'Mülk silinemedi',
    
    // Saved Searches
    'loading.saved_searches': 'Kayıtlı aramalar yükleniyor...',
    'saved_searches.title': 'Kayıtlı aramalar',
    'saved_searches.empty': 'Henüz kayıtlı arama yok',
    'saved_searches.description': 'Eşleşen mülkler eklendiğinde bildirim almak için bir arama kaydedin',
    'saved_searches.created': 'Oluşturuldu',
    'saved_searches.alerts_on': 'Bildirimler açık',
    'saved_searches.alerts_off': 'Bildirimler kapalı',
    'saved_searches.email_enabled': 'E-posta bildirimleri açık',
    'saved_searches.email_disabled': 'E-posta bildirimleri kapalı',
    'saved_searches.settings_updated': 'Ayar güncellendi',
    'saved_searches.removed': 'Arama kaldırıldı',
    'saved_searches.removed_desc': 'Kayıtlı arama kaldırıldı',
    'saved_searches.error': 'Hata',
    'saved_searches.update_error': 'Ayar güncellenemedi',
    'saved_searches.delete_error': 'Arama silinemedi',
    'saved_searches.all_properties': 'Tüm mülkler',
    
    // Rental properties
    'rental.for_sale': 'Satılık',
    'rental.for_rent': 'Kiralık',
    'rental.listing_type': 'İlan türü',
    'rental.rental_details': 'Kira detayları',
    'rental.rental_period': 'Kira dönemi',
    'rental.rental_period_hint': 'Kira ne sıklıkla ödenir',
    'rental.daily': 'Günlük',
    'rental.weekly': 'Haftalık',
    'rental.monthly': 'Aylık',
    'rental.yearly': 'Yıllık',
    'rental.per_daily': '/gün',
    'rental.per_weekly': '/hafta',
    'rental.per_monthly': '/ay',
    'rental.per_yearly': '/yıl',
    'rental.deposit_amount': 'Depozito tutarı (USD)',
    'rental.minimum_lease': 'Minimum kira süresi (ay)',
    'rental.months': 'ay',
    'rental.available_from': 'Müsait olduğu tarih',
    'rental.furnished': 'Eşyalı',
    'rental.utilities_included': 'Faturalar dahil',
    'rental.pets_allowed': 'Evcil hayvan kabul edilir',
    
    // Admin Pages
    'admin.loading': 'Yükleniyor...',
    'admin.access_denied': 'Erişim reddedildi. Yönetici ayrıcalıkları gerekli.',
    
    // Admin Notification Settings
    'admin.notifications.title': 'Bildirim Ayarları',
    'admin.notifications.description': 'Tüm kullanıcı türleri için bildirim ayarlarını yönetin. Gerektiğinde etkinleştirin, devre dışı bırakın veya gizleyin.',
    'admin.notifications.fetch_error': 'Bildirim ayarları alınamadı',
    'admin.notifications.update_success': 'Bildirim ayarı güncellendi',
    'admin.notifications.update_error': 'Bildirim ayarı güncellenemedi',
    'admin.notifications.role_description': '{role} kullanıcıları için bildirimleri yönetin',
    'admin.notifications.hidden': 'Gizli',
    'admin.notifications.disabled': 'Devre dışı',
    'admin.notifications.active': 'Aktif',
    'admin.notifications.hide': 'Gizle',
    'admin.notifications.roles.user': 'Kullanıcılar',
    'admin.notifications.roles.maklare': 'Emlakçılar',
    'admin.notifications.roles.agency': 'Ajanslar',
    'admin.notifications.roles.admin': 'Yöneticiler',
    'admin.notifications.roles.publisher': 'Yayıncılar',
    'admin.notifications.types.new_viewing_request': 'Yeni Görüntüleme Talebi',
    'admin.notifications.types.new_client_message': 'Yeni Müşteri Mesajı',
    'admin.notifications.types.property_alerts': 'Mülk Uyarıları',
    'admin.notifications.types.weekly_summary': 'Haftalık Özet',
    'admin.notifications.types.broker_property_added': 'Emlakçı Mülk Ekledi',
    'admin.notifications.types.broker_viewing_request': 'Emlakçı Görüntüleme Talebi',
    'admin.notifications.types.broker_client_message': 'Emlakçı Müşteri Mesajı',
    'admin.notifications.types.broker_joined': 'Emlakçı Katıldı',
    'admin.notifications.types.weekly_agency_summary': 'Haftalık Ajans Özeti',
    'admin.notifications.types.saved_search_alerts': 'Kayıtlı Arama Uyarıları',
    'admin.notifications.types.favorite_property_updates': 'Favori Mülk Güncellemeleri',
    'admin.notifications.types.newsletter': 'Bülten',
    'admin.notifications.types.system_alerts': 'Sistem Uyarıları',
    'admin.notifications.types.new_user_signup': 'Yeni Kullanıcı Kaydı',
    'admin.notifications.types.article_published': 'Makale Yayınlandı',

    // Admin Settings
    'admin.settings': 'Yönetici Ayarları',
    'admin.messaging_settings': 'Mesajlaşma Ayarları',
    'admin.messaging_settings_description': 'Tüm kullanıcılar için mesajlaşma ve arama özelliklerini kontrol edin',
    'admin.messaging': 'Mesajlaşma',
    'admin.messaging_description': 'Kullanıcıların mesaj göndermesine izin ver',
    'admin.voice_calls': 'Sesli Aramalar',
    'admin.voice_calls_description': 'Kullanıcıların sesli arama yapmasına izin ver',
    'admin.video_calls': 'Görüntülü Aramalar',
    'admin.video_calls_description': 'Kullanıcıların görüntülü arama yapmasına izin ver',
    'admin.screen_sharing': 'Ekran Paylaşımı',
    'admin.screen_sharing_description': 'Kullanıcıların aramalar sırasında ekranlarını paylaşmasına izin ver',

    'location_mgr.title': 'Konum Yöneticisi',
    'location_mgr.current_locations': 'Mevcut konumlar',
    'location_mgr.locations_in_db': 'Veritabanında {count} konum',
    'location_mgr.refresh': 'Yenile',
    'location_mgr.populate_title': 'Konumları doldur',
    'location_mgr.populate_desc': 'Tüm Irak şehirlerini ve bölgelerini Google Places API\'den al.',
    'location_mgr.populating': 'Dolduruluyor...',
    'location_mgr.populate_btn': 'Google Places\'ten doldur',
    'location_mgr.success': 'Başarılı',
    'location_mgr.success_msg': 'Konumlar başarıyla dolduruldu',
    'location_mgr.error': 'Hata',
    'location_mgr.error_msg': 'Konumlar doldurulamadı.',
    'map_config.title': 'Harita Yapılandırma Yöneticisi',
    'map_config.subtitle': 'Kalıcı harita yapılandırmalarınızı yönetin',
    'map_config.loading': 'Yapılandırmalar yükleniyor...',
    'map_config.create': 'Yapılandırma oluştur',
    'map_config.create_title': 'Harita yapılandırması oluştur',
    'map_config.create_desc': 'Yeni kalıcı harita yapılandırması oluştur.',
    'map_config.edit_title': 'Yapılandırmayı düzenle',
    'map_config.edit_desc': 'Yapılandırmanızı güncelleyin.',
    'map_config.name': 'Yapılandırma adı',
    'map_config.name_placeholder': 'örn: Irak Emlak Haritası',
    'map_config.description': 'Açıklama (isteğe bağlı)',
    'map_config.description_placeholder': 'Kısa açıklama',
    'map_config.map_data': 'Harita yapılandırması (JSON)',
    'map_config.map_data_placeholder': 'JSON formatında yapılandırma',
    'map_config.cancel': 'İptal',
    'map_config.update': 'Güncelle',
    'map_config.no_configs': 'Yapılandırma yok',
    'map_config.no_configs_desc': 'İlk yapılandırmanızı oluşturun.',
    'map_config.create_first': 'İlk yapılandırmayı oluştur',
    'map_config.active': 'Aktif',
    'map_config.activate': 'Etkinleştir',
    'map_config.edit': 'Düzenle',
    'map_config.delete': 'Sil',
    'map_config.delete_confirm': 'Emin misiniz?',
    'map_config.delete_desc': 'Bu, "{name}" yapılandırmasını kalıcı olarak silecektir.',
    'map_config.created': 'Oluşturuldu',
    'map_config.last_updated': 'Son güncelleme',
    'map_config.map_center': 'Harita merkezi',
    'map_config.zoom_level': 'Yakınlaştırma seviyesi',
    'map_config.cities': 'Şehirler',
    'map_config.cities_configured': '{count} şehir yapılandırıldı',
    'map_config.invalid_json': 'Geçersiz JSON formatı',
    
    // Forms
    'form.price': 'Fiyat',
    'form.priceUSD': 'Fiyat (USD)',
    'form.priceIQD': 'Fiyat (IQD)',
    'form.priceConversionNote': 'Fiyatı herhangi bir para biriminde girin - diğeri otomatik hesaplanacaktır (Kur: 1 USD ≈ 1.310 IQD)',
    'form.price_usd': 'Fiyat (USD)',
    'form.price_placeholder': 'Fiyatı ABD doları olarak girin',
    'form.price_description': 'Fiyatı ABD doları olarak girin. Hem USD hem de Irak dinarı olarak görüntülenecektir.',
    'form.address': 'Adres',
    'form.city': 'Şehir',
    'form.rooms': 'Oda',
    'form.bathrooms': 'Banyo',
    'form.size': 'Boyut (m²)',
    'form.building_size': 'Bina boyutu (m²)',
    'form.building_size_placeholder': 'm² olarak bina alanı',
    'form.building_size_description': 'Yaşam alanı',
    'form.plot_size': 'Arsa boyutu (m²)',
    'form.plot_size_placeholder': 'm² olarak arsa alanı',
    'form.plot_size_description': 'Toplam arsa alanı',
    'form.type': 'Tip',
    'form.propertyType': 'Mülk Tipi',
    'form.monthly_fee': 'Aylık ücret (SEK)',
    'form.monthly_fee_usd': 'Aylık ücret (USD)',
    'form.monthly_fee_placeholder': 'Aylık ücreti USD olarak girin (isteğe bağlı)',
    'form.monthly_fee_description': 'İsteğe bağlı aylık ücret (ABD doları)',
    'form.description': 'Açıklama',
    'form.description_placeholder': 'Mülkü tanımlayın...',
    'form.payment_plan': 'Ödeme Planı',
    'form.payment_plan_placeholder': 'örn: %30 peşinat, %70 teslimde, veya aylık taksitler...',
    'form.payment_plan_hint': 'Alıcılara ödeme seçeneklerinizi göstermek için taksitler ekleyin',
    'payment_plan.total': 'Toplam',
    'payment_plan.amount': 'Tutar',
    'payment_plan.description': 'Açıklama',
    'payment_plan.date': 'Tarih',
    'payment_plan.percentage': 'Yüzde',
    'payment_plan.add_installment': 'Taksit ekle',
    'payment_plan.hint': 'Ödeme planı sunmuyorsanız boş bırakın',
    'payment_plan.down_payment': 'Peşinat',
    'payment_plan.first_payment': 'İlk ödeme',
    'payment_plan.second_payment': 'İkinci ödeme',
    'payment_plan.third_payment': 'Üçüncü ödeme',
    'payment_plan.fourth_payment': 'Dördüncü ödeme',
    'payment_plan.fifth_payment': 'Beşinci ödeme',
    'payment_plan.sixth_payment': 'Altıncı ödeme',
    'payment_plan.seventh_payment': 'Yedinci ödeme',
    'payment_plan.final_payment': 'Son ödeme',
    'payment_plan.payment_description': 'Ödeme açıklaması',
    'payment_plan.enable': 'Ödeme planı dahil et',
    'form.floor_plans': 'Kat Planları',
    'form.upload_floor_plans': 'Kat planı görsellerini yükle',
    'form.features': 'Özellikler (virgülle ayrılmış)',
    'form.features_placeholder': 'örn. Havuz, Bahçe, Garaj, Modern mutfak',
    'form.features_description': 'Özellikleri virgülle ayırın',
    'form.amenities': 'Olanaklar',
    'form.amenities_hint': 'Bu mülk için geçerli tüm olanakları seçin',
    'amenities.balcony': 'Balkon',
    'amenities.electricity_generator': 'Elektrik jeneratörü',
    'amenities.central_ac': 'Merkezi klima',
    'amenities.double_glazed_windows': 'Çift camlı pencereler',
    'amenities.satellite_tv': 'Uydu TV',
    'amenities.cctv': 'Güvenlik kameraları',
    'amenities.security_staff': 'Güvenlik personeli',
    'amenities.sewerage_system': 'Kanalizasyon sistemi',
    'amenities.furnished_kitchen': 'Mobilyalı mutfak',
    'amenities.24hr_electricity': '24 saat elektrik',
    'amenities.market': 'Yakın market',
    'amenities.elevator': 'Asansör',
    'amenities.school': 'Yakın okul',
    'amenities.mosque': 'Yakın cami',
    'amenities.central_gas': 'Merkezi gaz',
    'amenities.maintenance': 'Bakım hizmetleri',
    'amenities.cleaning': 'Temizlik hizmetleri',
    'amenities.fire_fighting': 'Yangın söndürme ekipmanı',
    'amenities.car_washing': 'Araba yıkama',
    'amenities.smart_home': 'Akıllı ev',
    'amenities.earthquake_resistant': 'Depreme dayanıklı',
    'amenities.central_fire_system': 'Merkezi yangın söndürme sistemi',
    'amenities.hot_water_system': 'Merkezi sıcak su sistemi',
    'amenities.central_vacuum': 'Merkezi süpürge',
    'amenities.fountain': 'Çeşme',
    'amenities.24hr_water': '24 saat su',
    'amenities.garbage_chute': 'Çöp bacası',
    'amenities.kitchen_counter': 'Mutfak tezgahı',
    'amenities.street_lights': 'Sokak aydınlatması',
    'amenities.playground': 'Çocuk oyun alanı',
    'amenities.gym': 'Spor salonu',
    'amenities.garden': 'Bahçe',
    'amenities.parking': 'Otopark',
    'amenities.wifi': 'WiFi',
    'amenities.security_door': 'Güvenlik kapısı',
    'amenities.split_ac': 'Split klima',
    'amenities.bathtub': 'Küvet',
    'amenities.refrigerator': 'Buzdolabı',
    'amenities.washing_machine': 'Çamaşır makinesi',
    'amenities.swimming_pool': 'Yüzme havuzu',
    'amenities.solar_panels': 'Güneş panelleri',
    'amenities.water_tank': 'Su deposu',
    'amenities.water_well': 'Su kuyusu',
    'amenities.septic_tank': 'Septik tank',
    'amenities.central_heating': 'Merkezi ısıtma',
    'amenities.desert_cooler': 'Çöl soğutucusu',
    'amenities.intercom': 'İnterkom',
    'amenities.external_camera': 'Dış kamera',
    'amenities.guard_room': 'Bekçi odası',
    'amenities.rooftop_terrace': 'Çatı terası',
    'amenities.basement': 'Bodrum',
    'amenities.storage_room': 'Depo',
    'amenities.internal_stairs': 'İç merdiven',
    'amenities.external_stairs': 'Dış merdiven',
    'amenities.corner_unit': 'Köşe daire',
    'amenities.two_streets': 'İki cadde',
    'amenities.double_entrance': 'Çift giriş',
    'amenities.marble_flooring': 'Mermer zemin',
    'amenities.ceramic_tiles': 'Seramik karolar',
    'amenities.parquet_flooring': 'Parke zemin',
    'amenities.gypsum_decorations': 'Alçı dekorasyonları',
    'amenities.false_ceiling': 'Asma tavan',
    'amenities.steel_doors': 'Çelik kapılar',
    'amenities.aluminum_windows': 'Alüminyum pencereler',
    'amenities.pvc_windows': 'PVC pencereler',
    'amenities.guest_room': 'Misafir odası',
    'amenities.servant_quarters': 'Hizmetli odası',
    'amenities.covered_parking': 'Kapalı otopark',
    'amenities.concrete_fence': 'Beton çit',
    'amenities.iron_fence': 'Demir çit',
    'amenities.street_pavement': 'Cadde kaplaması',
    'amenities.fully_furnished': 'Tam mobilyalı',
    'amenities.hospital': 'Yakın hastane',
    'amenities.category.utilities': 'Elektrik ve Su',
    'amenities.category.climate': 'İklim Kontrolü',
    'amenities.category.security': 'Güvenlik',
    'amenities.category.building': 'Bina Özellikleri',
    'amenities.category.interior': 'İç Mekan ve Zemin',
    'amenities.category.rooms': 'Odalar',
    'amenities.category.outdoor': 'Dış Mekan ve Otopark',
    'amenities.category.appliances': 'Beyaz Eşya',
    'amenities.category.services': 'Hizmetler',
    'amenities.category.nearby': 'Yakın Çevre',
    'common.selected': 'seçili',
    'form.property_images': 'Mülk görselleri',
    'form.current_images': 'Mevcut görseller',
    'form.image': 'Görsel',
    'form.primary': 'Birincil',
    'form.click_image_primary': 'Birincil olarak ayarlamak için bir görsele tıklayın. Sırayı değiştirmek için okları kullanın.',
    'form.click_to_upload_new': 'Yeni görseller yüklemek için tıklayın',
    'form.new_images_upload': 'Yüklenecek yeni görseller',
    'form.new_image': 'Yeni görsel',
    'form.upload': 'Yükle',
    'form.new_images': 'yeni görseller',
    'form.location': 'Konum',
    'form.required': 'Zorunlu',
    'form.optional': 'İsteğe bağlı',
    'form.cancel': 'İptal',
    'form.save_changes': 'Değişiklikleri kaydet',
    'form.uploading': 'Yükleniyor...',
    'form.upload_images': 'Görsel yükle',
    'form.click_to_upload': 'Görsel yüklemek için tıklayın',
    'form.file_formats': 'JPEG, PNG, WebP, GIF (görsel başına maks. 10MB)',
    'form.select_type': 'Tip seçin',
    'form.location_on_map': 'Mülk konumu haritada',
    'form.location_hint': 'Tam konumu ayarlamak için haritaya tıklayın',
    'form.hide_preview': 'Önizlemeyi gizle',
    'form.show_preview': 'Önizlemeyi göster',
    'form.selected_images': 'Seçilen görseller',
    'form.click_set_primary': 'Birincil olarak ayarlamak için bir görsele tıklayın',
    'form.uploaded_images': 'Yüklenen görseller',
    'form.images': 'görseller',
    'form.uploading_images': 'Görseller yükleniyor...',
    'form.city_placeholder': 'Şehir ara...',
    'form.new_construction': 'Yeni inşaat (Gelecek ev)',
    'form.preview': 'Önizleme',
    'form.preview_description': 'İlanınız sitede böyle görünecek',
    'form.fullscreen': 'Tam ekran',
    'form.exit_fullscreen': 'Tam ekrandan çık',
    'form.click_preview_fullscreen': 'Tam ekran görüntülemek için önizlemeye tıklayın',
    'form.preview_contact_disabled': 'Önizleme modunda iletişim seçenekleri devre dışıdır',
    'form.invalid_format': 'Geçersiz dosya formatı',
    'form.invalid_format_desc': '{filename} geçersiz formata sahip. İzin verilenler: JPEG, PNG, WebP, GIF',
    'form.file_too_large': 'Dosya çok büyük',
    'form.file_too_large_desc': '{filename} 10MB\'dan büyük',
    'form.upload_failed': 'Yükleme başarısız',
    'form.upload_failed_desc': 'Görseller yüklenemedi',
    'form.images_required': 'Görseller gerekli',
    'form.images_required_desc': 'Lütfen mülk için en az bir görsel yükleyin',
    'form.sold_price_usd': 'Satış fiyatı (USD)',
    'form.sold_price_placeholder': 'Gerçek satış fiyatını girin',
    'form.sold_price_description': 'Gerçek satış fiyatı (liste fiyatından farklı olabilir)',
    'form.video_url': 'Video Tur URL\'si',
    'form.video': 'Video',
    'form.add_video': 'Başka video ekle',
    'form.video_addon': '+$3 eklenti',
    'form.video_included': 'Ücretsiz dahil',
    'form.video_url_hint': 'YouTube veya Vimeo video turu ekleyin. Video eklentisi ilan oluşturulduktan sonra satın alınabilir.',
    'form.video_url_hint_free': 'YouTube veya Vimeo video turu ekleyin - aboneliğinize dahil!',
    'form.video_url_hint_addon': 'YouTube veya Vimeo video turu ekleyin. İlan maliyetinize $3 eklenecektir.',
    'form.include_video_tour': 'Video turu dahil et',
    'form.show_map': 'Haritayı göster',
    'form.no_location': 'Konum seçilmedi',
    
    // Footer
    'footer.contact': 'İletişim',
    'footer.customer_service': 'Müşteri hizmetleri ve iletişim',
    'footer.about': 'Aqarnet Hakkında',
    'footer.press': 'Basın',
    'footer.investors': 'Yatırımcılar için',
    'footer.advertising': 'Reklam ve pazarlama',
    'footer.jobs': 'Bizimle çalışın',
    'footer.statistics': 'Emlak istatistikleri',
    'footer.apps': 'Uygulamalar',
    'footer.iphone_ipad': 'iPhone ve iPad',
    'footer.android': 'Android',
    'footer.follow': "Aqarnet'i takip edin",
    'footer.newsletter': 'Aqarnet Bülteni',
    'footer.whatsapp': 'WhatsApp',
    'footer.facebook': 'Facebook',
    'footer.instagram': 'Instagram',
    'footer.linkedin': 'LinkedIn',
    'footer.pinterest': 'Pinterest',
    'footer.tiktok': 'TikTok',
    'footer.threads': 'Threads',
    'footer.podcast': 'Aqarnet Podcast',
    'footer.all_properties': 'Tüm mülkler',
    'footer.privacy': 'Gizlilik',
    'footer.cookies': 'Çerezler',
    'footer.terms': 'Şartlar ve Yasal Bilgiler',
    'footer.cookie_settings': 'Çerez ayarları',
    
    // About Page
    'about.title': 'Aqarnet Hakkında',
    'about.subtitle': 'Irak emlak pazarında güvenilir ortağınız',
    'about.description': 'Aqarnet, Irak\'ın önde gelen emlak platformudur ve ülke genelinde alıcıları, satıcıları ve temsilcileri birbirine bağlar. Kapsamlı ilanlar, piyasa analizleri ve profesyonel araçlar sunuyoruz.',
    'about.mission_title': 'Misyonumuz',
    'about.mission_text': 'Şeffaf, erişilebilir ve güvenilir emlak bilgileri sağlayarak Irak emlak pazarını devrimleştirmek.',
    'about.vision_title': 'Vizyonumuz',
    'about.vision_text': 'Orta Doğu\'nun en güvenilir emlak platformu olmak.',
    'about.services_title': 'Hizmetlerimiz',
    'about.services_text': 'Emlak ilanları, piyasa analizleri, temsilci bağlantıları, değerlemeler ve emlak haberleri.',
    'about.team_title': 'Ekibimiz',
    'about.team_text': 'Emlak profesyonelleri, teknoloji uzmanları ve müşteri hizmetleri uzmanlarından oluşan özel bir ekip.',
    'about.company_info': 'Şirket Bilgileri',
    'about.based_in': 'İsveç merkezli, Irak\'a hizmet veriyor',
    
    // Contact Page
    'contact.title': 'Bize Ulaşın',
    'contact.subtitle': 'Size yardımcı olmak için buradayız. Sorularınız için bizimle iletişime geçin.',
    'contact.email_label': 'E-posta',
    'contact.phone_label': 'Telefon',
    'contact.phone_info': 'Yakında',
    'contact.location_label': 'Konum',
    'contact.location_info': 'İsveç',
    'contact.form_title': 'Bize mesaj gönderin',
    'contact.form_description': 'Formu doldurun, en kısa sürede size geri döneceğiz.',
    'contact.name_placeholder': 'Adınız',
    'contact.email_placeholder': 'email@adresiniz.com',
    'contact.subject': 'Konu',
    'contact.subject_placeholder': 'Bu ne hakkında?',
    'contact.message_placeholder': 'Mesajınız...',
    'contact.success': 'Mesaj gönderildi! Yakında size döneceğiz.',
    
    // Press Page
    'press.title': 'Basın ve Medya',
    'press.subtitle': 'Aqarnet\'ten son haberler ve basın bültenleri',
    'press.contact_title': 'Basın İletişimi',
    'press.contact_description': 'Basın sorguları için lütfen e-posta ile iletişime geçin.',
    'press.add_release': 'Basın bülteni ekle',
    'press.edit_release': 'Basın bültenini düzenle',
    'press.new_release': 'Yeni basın bülteni',
    'press.release_title': 'Başlık',
    'press.content': 'İçerik',
    'press.publish': 'Yayınla',
    'press.no_releases': 'Henüz basın bülteni yok.',
    'press.delete_confirm_title': 'Basın bültenini sil?',
    'press.delete_confirm_description': 'Bu işlem geri alınamaz.',
    
    // Investors Page
    'investors.title': 'Yatırımcılar İçin',
    'investors.subtitle': 'Irak emlak pazarını dönüştürmek için bize katılın',
    'investors.growth_title': 'Büyüme Potansiyeli',
    'investors.growth_text': 'Irak emlak pazarı, önemli yatırım fırsatlarıyla hızlı bir büyüme yaşıyor.',
    'investors.market_title': 'Pazar Lideri',
    'investors.market_text': 'Aqarnet, Irak emlak sektöründe lider dijital platform olarak konumlandırılmıştır.',
    'investors.vision_title': 'Bölgesel Genişleme',
    'investors.vision_text': 'Vizyonumuz Irak\'ın ötesine, Orta Doğu\'nun lider platformu olmaya uzanıyor.',
    'investors.opportunity_title': 'Yatırım Fırsatı',
    'investors.opportunity_text': 'Aqarnet, dünyanın en büyük gelişmekte olan emlak pazarlarından birinin dijital dönüşümüne yatırım yapma fırsatı sunuyor.',
    'investors.opportunity_text_2': 'Büyüyen kullanıcı tabanı ve genişleyen hizmetlerle sürdürülebilir büyüme için iyi konumdayız.',
    'investors.contact_title': 'İletişime Geçin',
    'investors.contact_description': 'Yatırım fırsatlarıyla ilgileniyor musunuz? Daha fazla bilgi için bizimle iletişime geçin.',
    'investors.company': 'Şirket / Kuruluş',
    'investors.company_placeholder': 'Şirket adınız',
    'investors.message_placeholder': 'Yatırım ilgi alanlarınızdan bahsedin...',
    'investors.success': 'İlginiz için teşekkürler! Yakında sizinle iletişime geçeceğiz.',
    
    // Advertising Page
    'advertising.title': 'Reklam ve Pazarlama',
    'advertising.subtitle': 'Irak\'ta binlerce potansiyel alıcı ve kiracıya ulaşın',
    'advertising.targeted_title': 'Hedefli Erişim',
    'advertising.targeted_text': 'Belirli bölgelerde aktif olarak mülk arayan kullanıcılara ulaşın.',
    'advertising.audience_title': 'Kaliteli Kitle',
    'advertising.audience_text': 'Ciddi alıcılar ve kiracılarla bağlantı kurun.',
    'advertising.analytics_title': 'Detaylı Analizler',
    'advertising.analytics_text': 'Kapsamlı analizlerle kampanya performansınızı takip edin.',
    'advertising.formats_title': 'Çoklu Formatlar',
    'advertising.formats_text': 'Banner reklamları, öne çıkan ilanlar, sponsorlu içerik ve daha fazlası.',
    'advertising.contact_title': 'Reklam Vermeye Başlayın',
    'advertising.contact_description': 'Reklam fırsatlarını görüşmek için bizimle iletişime geçin.',
    'advertising.company': 'Şirket / Kuruluş',
    'advertising.company_placeholder': 'Şirket adınız',
    'advertising.budget': 'Bütçe Aralığı',
    'advertising.budget_placeholder': 'örn., $500 - $1000/ay',
    'advertising.message_placeholder': 'Reklam hedeflerinizden bahsedin...',
    'advertising.success': 'Teşekkürler! Reklam fırsatları hakkında sizinle iletişime geçeceğiz.',
    
    // Jobs Page
    'jobs.title': 'Aqarnet\'te Kariyer',
    'jobs.subtitle': 'Ekibimize katılın ve Irak\'ta emlak geleceğini şekillendirmeye yardımcı olun',
    'jobs.add_job': 'İş ekle',
    'jobs.edit_job': 'İşi düzenle',
    'jobs.new_job': 'Yeni iş ilanı',
    'jobs.job_title': 'Pozisyon başlığı',
    'jobs.department': 'Departman',
    'jobs.location': 'Konum',
    'jobs.employment_type': 'İstihdam türü',
    'jobs.full_time': 'Tam zamanlı',
    'jobs.part_time': 'Yarı zamanlı',
    'jobs.contract': 'Sözleşmeli',
    'jobs.internship': 'Staj',
    'jobs.description': 'Açıklama',
    'jobs.requirements': 'Gereksinimler',
    'jobs.benefits': 'Avantajlar',
    'jobs.publish': 'Yayınla',
    'jobs.no_jobs': 'Şu anda açık pozisyon yok.',
    'jobs.check_back': 'Yeni fırsatlar için daha sonra tekrar kontrol edin.',
    'jobs.apply_now': 'Şimdi başvur',
    'jobs.apply_for': 'Başvur:',
    'jobs.apply_description': 'Başvurunuzu e-posta ile gönderin.',
    'jobs.application_sent': 'Başvuru gönderildi! İyi şanslar!',
    'jobs.delete_confirm_title': 'İş ilanını sil?',
    'jobs.delete_confirm_description': 'Bu işlem geri alınamaz.',
    
    // Statistics
    'statistics.title': 'Emlak istatistikleri',
    'statistics.subtitle': 'Satılan mülklere dayalı piyasa trendlerini ve fiyat gelişimini analiz edin',
    'statistics.6_months': 'Son 6 ay',
    'statistics.12_months': 'Son 12 ay',
    'statistics.24_months': 'Son 24 ay',
    'statistics.36_months': 'Son 36 ay',
    'statistics.all_cities': 'Tüm şehirler',
    'statistics.total_sold': 'Toplam satılan',
    'statistics.avg_price': 'Ortalama fiyat',
    'statistics.avg_price_sqm': 'Ort. Fiyat/m²',
    'statistics.price_change': 'Fiyat değişimi',
    'statistics.price_trends': 'Fiyat trendleri',
    'statistics.price_trends_desc': 'Zaman içinde ortalama mülk fiyatları',
    'statistics.sales_volume': 'Satış hacmi',
    'statistics.sales_volume_desc': 'Aylık satılan mülk sayısı',
    'statistics.properties_sold': 'Satılan mülkler',
    'statistics.price_by_city': 'Şehre göre m² fiyatı',
    'statistics.price_by_city_desc': 'Şehirler arası ortalama metrekare fiyatı karşılaştırması',
    'statistics.price_per_sqm': 'm² başına fiyat',
    'statistics.type_distribution': 'Mülk tipi dağılımı',
    'statistics.type_distribution_desc': 'Tipe göre satılan mülklerin dağılımı',
    'statistics.avg_days_market': 'Ort. pazarda gün',
    'statistics.days': 'gün',
    'statistics.price_ranges': 'Fiyat aralığı dağılımı',
    'statistics.price_ranges_desc': 'Her fiyat aralığında satılan mülk sayısı',
    'statistics.properties': 'Mülkler',
    'statistics.year_over_year': 'Yıllık karşılaştırma',
    'statistics.year_over_year_desc': 'Yıllar boyunca ortalama fiyatları ve satış hacmini karşılaştırın',
    'statistics.all_types': 'Tüm mülk türleri',
    'statistics.all_rooms': 'Tüm odalar',
    'statistics.rooms': 'oda',
    'statistics.export': 'Dışa Aktar',
    'statistics.export_csv': 'CSV İndir',
    'statistics.export_json': 'JSON İndir',
    'statistics.share': 'Paylaş',
    'statistics.link_copied': 'Link kopyalandı',
    'statistics.link_copied_desc': 'İstatistik raporu linki panoya kopyalandı',
    'statistics.custom_date': 'Özel tarih',
    'statistics.use_preset': 'Ön ayar kullan',
    'statistics.start_date': 'Başlangıç tarihi',
    'statistics.end_date': 'Bitiş tarihi',
    'statistics.city_comparison': 'Şehir Karşılaştırması',
    'statistics.city_comparison_desc': 'İki şehir arasındaki emlak istatistiklerini yan yana karşılaştırın',
    'statistics.select_city_1': 'Birinci şehri seçin',
    'statistics.select_city_2': 'İkinci şehri seçin',
    'statistics.vs': 'vs',
    'statistics.select_cities_to_compare': 'İstatistiklerini karşılaştırmak için yukarıdan iki şehir seçin',
    'statistics.avg_size': 'Ort. Boyut',
    // Common
    'common.cancel': 'İptal',
    'common.save': 'Kaydet',
    'common.saving': 'Kaydediliyor...',
    'common.send': 'Gönder',
    'common.sending': 'Gönderiliyor...',
    'common.submit': 'Gönder',
    'common.close': 'Kapat',
    'common.error': 'Hata',
    'common.success': 'Başarılı',
    'common.loading': 'Yükleniyor...',
    'common.name': 'Ad',
    'common.email': 'E-posta',
    'common.phone': 'Telefon',
    'common.message': 'Mesaj',
    'common.required': 'Zorunlu',
    'common.optional': 'İsteğe bağlı',
    'common.delete': 'Sil',
    'common.free': 'Ücretsiz',
    
    // Leaderboard
    'leaderboard': 'Sıralama',
    'leaderboardDescription': 'Görüntüleme ve satışlara göre sıralanan en iyi ajansları ve temsilcileri keşfedin',
    'topAgencies': 'En İyi Ajanslar',
    'topBrokers': 'En İyi Temsilciler',
    'topAgenciesDescription': 'Mülk görüntülemelerine ve satış performansına göre sıralanan ajanslar',
    'topBrokersDescription': 'Mülk görüntülemelerine ve satış performansına göre sıralanan temsilciler',
    'allRegions': 'Tüm Bölgeler',
    'noAgenciesFound': 'Ajans bulunamadı',
    'noBrokersFound': 'Temsilci bulunamadı',
    'brokers': 'temsilciler',
    'sold': 'satıldı',
    'active': 'aktif',
    'views': 'görüntüleme',
    
    // Dialogs - Contact Agent
    'dialog.contact_agent': 'Temsilci ile iletişim',
    'dialog.contact_agent_btn': 'Temsilci ile iletişim',
    'dialog.contact_message_placeholder': 'Merhaba! {address} adresindeki mülkle ilgileniyorum ve daha fazla bilgi edinmek istiyorum. Müsait olduğunuzda lütfen benimle iletişime geçin.',
    'dialog.contact_sending': 'Gönderiliyor...',
    'dialog.contact_send': 'Mesaj gönder',
    'dialog.contact_note': 'Sorgunuz doğrudan sizinle iletişime geçecek olan sorumlu temsilciye gönderilecektir.',
    
    // Dialogs - Schedule Viewing
    'dialog.schedule_viewing': 'Görüntüleme planla',
    'dialog.schedule_viewing_btn': 'Görüntüleme planla',
    'dialog.preferred_time': 'Tercih edilen görüntüleme zamanı',
    'dialog.preferred_time_note': 'Görüntüleme için tarih ve saat seçin. Temsilci uygunluğu onaylayacaktır.',
    'dialog.special_requests': 'Özel istekler',
    'dialog.special_requests_placeholder': 'Herhangi bir özel istek veya soru...',
    'dialog.schedule_booking': 'Rezervasyon yapılıyor...',
    'dialog.schedule_book': 'Görüntüleme rezerve et',
    'dialog.schedule_note': 'Talebiniz, zamanı e-posta ile onaylayacak olan temsilciye gönderilecektir.',
    
    // Dialogs - Save Search
    'dialog.save_search': 'Aramayı kaydet',
    'dialog.save_search_btn': 'Aramayı kaydet',
    'dialog.search_name': 'Arama adı',
    'dialog.search_name_placeholder': 'örn., İstanbul\'da daire',
    'dialog.city_municipality': 'Şehir/Belediye',
    'dialog.min_rooms': 'Min. oda',
    'dialog.min_size': 'Min. boyut (m²)',
    'dialog.property_type': 'Mülk türü',
    'dialog.email_alerts': 'Yeni mülkler eşleştiğinde e-posta bildirimleri',
    'dialog.saving_search': 'Kaydediliyor...',
    
    // Dialogs - Mark as Sold
    'dialog.mark_sold': 'Satıldı olarak işaretle',
    'dialog.mark_sold_description': 'Bu mülk için son satış fiyatını girin. Bu, ilanı satılan mülklere taşıyacaktır.',
    'dialog.final_sale_price': 'Son satış fiyatı (USD)',
    'dialog.listed_price': 'Liste fiyatı',
    'dialog.property_details': 'Mülk detayları',
    'dialog.enter_valid_price': 'Lütfen geçerli bir satış fiyatı girin',
    'dialog.enter_price_placeholder': 'Satış fiyatını girin',
    'dialog.marked_sold_success': 'Mülk başarıyla satıldı olarak işaretlendi',
    'dialog.mark_sold_error': 'Mülk satıldı olarak işaretlenemedi',
    
    // Dialogs - Add Property
    'dialog.add_property': 'Yeni mülk ekle',
    'dialog.hide_preview': 'Önizlemeyi gizle',
    'dialog.show_preview': 'Önizlemeyi göster',
    'dialog.selected_images': 'Seçilen resimler',
    'dialog.uploaded_images': 'Yüklenen resimler',
    'dialog.click_primary': 'Birincil olarak ayarlamak için bir resme tıklayın',
    'dialog.invalid_format': 'Geçersiz dosya formatı',
    'dialog.file_too_large': 'Dosya çok büyük',
    'dialog.upload_failed': 'Yükleme başarısız',
    'dialog.images_required': 'Resimler gerekli',
    'dialog.please_upload': 'Lütfen mülk için en az bir resim yükleyin',
    'dialog.form_validation_failed': 'Form doğrulama başarısız',
    
    // Cookie Settings
    'cookie_settings.title': 'Çerez Ayarları',
    'cookie_settings.description': 'Tarama deneyiminizi geliştirmek için çerezler kullanıyoruz. Çerez tercihlerinizi aşağıdan özelleştirebilirsiniz.',
    'cookie_settings.necessary': 'Gerekli Çerezler',
    'cookie_settings.necessary_desc': 'Bu çerezler web sitesinin düzgün çalışması için gereklidir. Devre dışı bırakılamazlar.',
    'cookie_settings.functional': 'İşlevsel Çerezler',
    'cookie_settings.functional_desc': 'Bu çerezler dil tercihleri ve kayıtlı ayarlar gibi kişiselleştirilmiş özellikleri etkinleştirir.',
    'cookie_settings.analytics': 'Analitik Çerezler',
    'cookie_settings.analytics_desc': 'Bu çerezler, kullanıcı deneyimini iyileştirmek için ziyaretçilerin web sitemizle nasıl etkileşime girdiğini anlamamıza yardımcı olur.',
    'cookie_settings.marketing': 'Pazarlama Çerezleri',
    'cookie_settings.marketing_desc': 'Bu çerezler size ilgili reklamlar göstermek ve kampanyalarımızın etkinliğini izlemek için kullanılır.',
    'cookie_settings.accept_all': 'Tümünü Kabul Et',
    'cookie_settings.reject_all': 'Tümünü Reddet',
    'cookie_settings.save_preferences': 'Tercihleri Kaydet',
    'cookie_settings.saved': 'Çerez tercihleri kaydedildi',
    
    // Cookie Banner
    'cookie_banner.title': 'Çerez kullanıyoruz',
    'cookie_banner.description': 'Tarama deneyiminizi geliştirmek, kişiselleştirilmiş içerik sunmak ve trafiğimizi analiz etmek için çerezler kullanıyoruz. "Tümünü Kabul Et"e tıklayarak çerez kullanımımızı kabul etmiş olursunuz.',
    'cookie_banner.accept_all': 'Tümünü Kabul Et',
    'cookie_banner.reject_all': 'Tümünü Reddet',
    'cookie_banner.customize': 'Özelleştir',
    
    // Newsletter
    'newsletter.title': 'Aqarnet Bülteni',
    'newsletter.description': 'En son emlak haberlerini ve güncellemelerini doğrudan gelen kutunuza alın.',
    'newsletter.subscribe': 'Bültene abone ol',
    'newsletter.signup': 'Kaydol',
    'newsletter.email_placeholder': 'E-posta adresiniz',
    'newsletter.success': 'Abone olduğunuz için teşekkürler!',
    'newsletter.success_description': 'Yakında güncellemelerimizi alacaksınız.',
    'newsletter.invalid_email': 'Lütfen geçerli bir e-posta girin',
    'newsletter.error': 'Abonelik başarısız',
    'newsletter.unsubscribe_note': 'İstediğiniz zaman abonelikten çıkabilirsiniz.',
    'newsletter.unsubscribe_title': 'Bülten Aboneliğini İptal Et',
    'newsletter.unsubscribe_description': 'Aboneliği iptal etmek için e-postanızı girin.',
    'newsletter.unsubscribe_button': 'Aboneliği İptal Et',
    'newsletter.unsubscribe_success': 'Abonelik başarıyla iptal edildi',
    'newsletter.unsubscribe_error': 'Abonelik iptali başarısız',
    'newsletter.unsubscribe_message': 'Artık bültenimizi almayacaksınız.',
    'newsletter.invalid_link': 'Geçersiz iptal linki',
    'newsletter.invalid_link_message': 'Bu iptal linki geçersiz veya süresi dolmuş. Lütfen bülten e-postanızdaki linki kullanın.',
    'newsletter.unsubscribe_reason_label': 'İptal sebebi (isteğe bağlı)',
    'newsletter.select_reason': 'Bir sebep seçin...',
    'newsletter.reason_too_many_emails': 'Çok fazla e-posta',
    'newsletter.reason_not_relevant': 'İçerik benimle alakalı değil',
    'newsletter.reason_never_subscribed': 'Hiç abone olmadım',
    'newsletter.reason_found_another': 'Başka bir hizmet buldum',
    'newsletter.reason_other': 'Diğer',
    'common.try_again': 'Tekrar Dene',
    'newsletter.admin.title': 'Bülten Aboneleri',
    'newsletter.admin.description': 'Bülten abonelerinizi yönetin',
    'newsletter.admin.total': 'Toplam abone',
    'newsletter.admin.active': 'Aktif',
    'newsletter.admin.unsubscribed': 'İptal edilmiş',
    'newsletter.admin.export_csv': 'CSV Dışa Aktar',
    'newsletter.admin.refresh': 'Yenile',
    'newsletter.admin.subscribers_list': 'Abone listesi',
    'newsletter.admin.subscribers_description': 'Tüm aboneler ve durumları',
    'newsletter.admin.no_subscribers': 'Henüz abone yok',
    'newsletter.admin.subscribed_at': 'Abone olma tarihi',
    'newsletter.admin.status': 'Durum',
    'newsletter.admin.unsubscribed_at': 'İptal tarihi',
    'newsletter.admin.actions': 'İşlemler',
    'newsletter.admin.deleted': 'Abone silindi',
    'newsletter.admin.delete_error': 'Silme başarısız',
    'newsletter.admin.no_data': 'Dışa aktarılacak veri yok',
    'newsletter.admin.exported': 'Başarıyla dışa aktarıldı',
    'newsletter.admin.delete_confirm_title': 'Abone silinsin mi?',
    'newsletter.admin.delete_confirm_description': 'Bu, aboneyi kalıcı olarak silecektir.',
    'footer.faq': 'Sıkça sorulan sorular',
    
    // FAQ Page
    'faq.title': 'Sıkça Sorulan Sorular',
    'faq.subtitle': 'Aqarnet kullanımı hakkında sık sorulan soruların yanıtlarını bulun',
    'faq.account.title': 'Hesap ve Kayıt',
    'faq.account.q1': 'Nasıl hesap oluştururum?',
    'faq.account.q1_answer': 'Başlıktaki "Giriş Yap" butonuna tıklayın, ardından "Hesap Oluştur" seçeneğini seçin. Email, Google veya Apple ile kaydolabilirsiniz.',
    'faq.account.q2': 'Şifremi nasıl sıfırlarım?',
    'faq.account.q2_answer': 'Giriş sayfasında "Şifremi Unuttum" seçeneğine tıklayın ve email adresinizi girin. Şifrenizi sıfırlamak için bir bağlantı alacaksınız.',
    'faq.account.q3': 'Email adresimi değiştirebilir miyim?',
    'faq.account.q3_answer': 'Şu anda email adresinizi doğrudan değiştiremezsiniz. Lütfen destek ekibimizle iletişime geçin.',
    'faq.listings.title': 'Mülk İlanları',
    'faq.listings.q1': 'Satılık mülk nasıl ilan ederim?',
    'faq.listings.q1_answer': 'Mülk ilan etmek için acente olarak kayıtlı olmanız gerekir. Giriş yaptıktan sonra "Mülk Ekle" butonuna tıklayın ve detayları doldurun.',
    'faq.listings.q2': 'Bir ilan ne kadar süre aktif kalır?',
    'faq.listings.q2_answer': 'İlanlar satıldı olarak işaretlenene veya sahibi tarafından kaldırılana kadar aktif kalır.',
    'faq.listings.q3': 'Yayınladıktan sonra ilanımı düzenleyebilir miyim?',
    'faq.listings.q3_answer': 'Evet, ilanınızı istediğiniz zaman düzenleyebilirsiniz. Profilinize gidin, ilanı bulun ve "Düzenle" butonuna tıklayın.',
    'faq.search.title': 'Mülk Arama',
    'faq.search.q1': 'Aramayı nasıl kaydederim?',
    'faq.search.q1_answer': 'Arama yaptıktan sonra "Aramayı Kaydet" butonuna tıklayın. Yeni mülkler için email bildirimleri etkinleştirebilirsiniz.',
    'faq.search.q2': 'Bir mülkü favorilere nasıl eklerim?',
    'faq.search.q2_answer': 'Herhangi bir mülk kartındaki kalp simgesine tıklayın. Favorileri kaydetmek için giriş yapmış olmanız gerekir.',
    'faq.search.q3': 'Haritada arama yapabilir miyim?',
    'faq.search.q3_answer': 'Evet, mülkler sayfasında harita görünümünü kullanarak konuma göre ilanları inceleyebilirsiniz.',
    'faq.contact.title': 'Acentelerle İletişim',
    'faq.contact.q1': 'Bir emlak acentesiyle nasıl iletişime geçerim?',
    'faq.contact.q1_answer': 'Herhangi bir ilanda "Acenteyle İletişim" butonuna tıklayarak sorumlu acenteye doğrudan mesaj gönderebilirsiniz.',
    'faq.contact.q2': 'Görüntüleme randevusu alabilir miyim?',
    'faq.contact.q2_answer': 'Evet, mülk sayfasında "Görüntüleme Planla" butonuna tıklayarak görüntüleme zamanı talep edebilirsiniz.',
    'faq.security.title': 'Güvenlik ve Gizlilik',
    'faq.security.q1': 'Kişisel bilgilerim güvende mi?',
    'faq.security.q1_answer': 'Evet, verilerinizi korumak için endüstri standardı şifreleme ve güvenlik önlemleri kullanıyoruz.',
    'faq.security.q2': 'Hesabımı nasıl silerim?',
    'faq.security.q2_answer': 'Profil ayarlarınıza gidin ve "Hesabı Sil" seçeneğini seçin. Bu işlem kalıcıdır.',
    'faq.still_questions': 'Hala sorularınız mı var?',
    'faq.contact_us': 'Destek ekibimizle iletişime geçin, size yardımcı olmaktan mutluluk duyarız.',

    'terms.title': 'Hizmet Şartları', 'terms.last_updated': 'Son güncelleme:', 'terms.section1.title': '1. Şartların Kabulü', 'terms.section1.p1': 'Aqarnet kullanarak bu şartları kabul edersiniz.', 'terms.section1.p2': 'Bu şartları değiştirme hakkımızı saklı tutarız.', 'terms.section2.title': '2. Hizmet Açıklaması', 'terms.section2.p1': 'Aqarnet, Irak\'ta emlak ilanları için dijital bir platform sağlar.', 'terms.section2.p2': 'Hizmetlerimiz listeler, arama ve iletişim araçlarını içerir.', 'terms.section3.title': '3. Kullanıcı Sorumlulukları', 'terms.section3.p1': 'Kullanıcı olarak şunları kabul edersiniz:', 'terms.section3.li1': 'Doğru bilgi sağlamak', 'terms.section3.li2': 'Platformu yasadışı faaliyetler için kullanmamak', 'terms.section3.li3': 'Fikri mülkiyet haklarına saygı göstermek', 'terms.section3.li4': 'Hizmete müdahale etmemek', 'terms.section4.title': '4. Mülk İlanları', 'terms.section4.p1': 'Kullanıcılar ilanlarının doğruluğundan sorumludur.', 'terms.section4.p2': 'Şartlarımızı ihlal eden ilanları kaldırabiliriz.', 'terms.section5.title': '5. Fikri Mülkiyet', 'terms.section5.p1': 'Tüm içerik Aqarnet\'e aittir.', 'terms.section5.p2': 'İzinsiz çoğaltma yasaktır.', 'terms.section6.title': '6. Sorumluluk Sınırlaması', 'terms.section6.p1': 'Aqarnet garanti olmaksızın "olduğu gibi" sağlanır.', 'terms.section7.title': '7. Uygulanacak Hukuk', 'terms.section7.p1': 'Bu şartlar İsveç hukukuna tabidir.', 'terms.section7.p2': 'Anlaşmazlıklar İsveç mahkemelerinde çözülecektir.', 'terms.section8.title': '8. İletişim', 'terms.section8.p1': 'Sorular için bizimle iletişime geçin:', 'terms.section8.email': 'E-posta',
    'privacy.title': 'Gizlilik Politikası', 'privacy.last_updated': 'Son güncelleme:', 'privacy.section1.title': '1. Giriş', 'privacy.section1.p1': 'Gizliliğinizi korumaya kararlıyız.', 'privacy.section1.p2': 'Hizmetlerimizi kullanarak veri uygulamalarımızı kabul edersiniz.', 'privacy.section2.title': '2. Toplanan Bilgiler', 'privacy.section2.p1': 'Topladığımız bilgiler:', 'privacy.section2.li1': 'Hesap bilgileri', 'privacy.section2.li2': 'Mülk verileri', 'privacy.section2.li3': 'Kullanım verileri', 'privacy.section2.li4': 'Cihaz bilgileri', 'privacy.section3.title': '3. Bilgi Kullanımı', 'privacy.section3.p1': 'Bilgilerinizi şunlar için kullanırız:', 'privacy.section3.li1': 'Hizmet sağlamak', 'privacy.section3.li2': 'Deneyimi kişiselleştirmek', 'privacy.section3.li3': 'Bildirim göndermek', 'privacy.section3.li4': 'Sizinle iletişim kurmak', 'privacy.section4.title': '4. Veri Paylaşımı', 'privacy.section4.p1': 'Verilerinizi satmıyoruz.', 'privacy.section4.p2': 'Yasal zorunluluk halinde bilgi paylaşabiliriz.', 'privacy.section5.title': '5. Haklarınız', 'privacy.section5.p1': 'Şu haklara sahipsiniz:', 'privacy.section5.li1': 'Erişim', 'privacy.section5.li2': 'Düzeltme', 'privacy.section5.li3': 'Silme', 'privacy.section5.li4': 'Taşınabilirlik', 'privacy.section5.li5': 'İtiraz', 'privacy.section6.title': '6. Veri Güvenliği', 'privacy.section6.p1': 'Verilerinizi koruyoruz.', 'privacy.section6.p2': 'Hiçbir yöntem %100 güvenli değildir.', 'privacy.section7.title': '7. Değişiklikler', 'privacy.section7.p1': 'Bu politikayı güncelleyebiliriz.', 'privacy.section8.title': '8. İletişim', 'privacy.section8.p1': 'Sorular için bizimle iletişime geçin:', 'privacy.section8.email': 'E-posta',
    
    // Broker Dashboard
    'broker.dashboard_title': 'Broker Paneli',
    'broker.dashboard_subtitle': 'Mülklerinizi ve müşteri etkileşimlerinizi yönetin',
    'broker.my_properties': 'Mülklerim',
    'broker.my_properties_desc': 'Mülk ilanlarınızı görüntüleyin ve yönetin',
    'broker.viewings': 'Mülk Görüntülemeleri',
    'broker.viewings_desc': 'Planlanan mülk görüntülemelerini yönetin',
    'broker.contacts': 'Müşteri Mesajları',
    'broker.contacts_desc': 'Müşteri sorularını görüntüleyin ve yanıtlayın',
    'broker.analytics': 'Mülk Analitiği',
    'broker.analytics_desc': 'İlanlarınızdaki görüntülemeleri ve etkileşimi takip edin',
    'broker.subscription': 'Abonelik',
    'broker.subscription_desc': 'Planınızı ve ilan limitlerini yönetin',
    'broker.access_denied': 'Bu sayfaya yalnızca kayıtlı brokerlar erişebilir.',
    'broker.profile_title': 'Broker Profili',
    'broker.profile_description': 'Genel profilinizi ve iletişim bilgilerinizi yönetin',
    'broker.back_to_dashboard': 'Panele Dön',
    'broker.display_name': 'Görünen Ad',
    'broker.display_name_placeholder': 'Müşterilere gösterildiği şekliyle adınız',
    'broker.contact_email': 'İletişim E-postası',
    'broker.email_placeholder': 'email@adresiniz.com',
    'broker.email_notification_note': 'Bu e-posta, görüntülemeler ve müşteri mesajları hakkındaki bildirimler için kullanılacaktır.',
    'broker.phone_number': 'Telefon Numarası',
    'broker.phone_placeholder': '+90 xxx xxx xx xx',
    'broker.change_photo': 'Fotoğrafı Değiştir',
    'broker.save_profile': 'Profili Kaydet',
    'broker.profile_saved': 'Profil başarıyla kaydedildi',
    'broker.profile_save_error': 'Profil kaydedilemedi',
    'broker.profile_load_error': 'Profil yüklenemedi',
    'broker.avatar_uploaded': 'Fotoğraf başarıyla yüklendi',
    'broker.avatar_upload_error': 'Fotoğraf yüklenemedi',
    'broker.invalid_image_type': 'Lütfen bir resim dosyası seçin',
    'broker.image_too_large': 'Resim 2MB\'den küçük olmalıdır',
    'common.login_required': 'Bu sayfaya erişmek için lütfen giriş yapın.',
    
    // Profile Email Notifications
    'profile.email_notifications': 'E-posta Bildirimleri',
    'profile.email_notifications_description': 'Hangi e-posta bildirimlerini almak istediğinizi seçin',
    'profile.notif_property_alerts': 'Mülk Uyarıları',
    'profile.notif_property_alerts_desc': 'Kayıtlı aramalarınızla eşleşen yeni mülkler hakkında bildirim alın',
    'profile.notif_saved_search_alerts': 'Kayıtlı Arama Uyarıları',
    'profile.notif_saved_search_alerts_desc': 'Yeni mülkler arama kriterlerinizle eşleştiğinde uyarı alın',
    'profile.notif_newsletter': 'Bülten',
    'profile.notif_newsletter_desc': 'Emlak haberleri ve piyasa analizleri içeren bültenimizi alın',
    'profile.notif_viewing_confirmations': 'Görüntüleme Onayları',
    'profile.notif_viewing_confirmations_desc': 'Planlanan görüntülemeleriniz için e-posta onayları alın',
    'profile.email_prefs_saved': 'E-posta tercihleri kaydedildi',
    'profile.email_prefs_error': 'Tercihler kaydedilemedi',
    
    // Agency Dashboard
    'agency.my_agency': 'Acentem',
    'agency.dashboard_title': 'Acente Paneli',
    'agency.dashboard_subtitle': 'Brokerlerinizi ve acente ayarlarını yönetin',
    'agency.brokers_tab': 'Brokerler',
    'agency.invites_tab': 'Davetler',
    'agency.settings_tab': 'Ayarlar',
    'agency.brokers_title': 'Acente Brokerleri',
    'agency.brokers_description': 'Acentenizdeki brokerleri yönetin',
    'agency.create_broker': 'Broker Oluştur',
    'agency.create_broker_title': 'Yeni Broker Hesabı Oluştur',
    'agency.create_broker_description': 'Doğrudan acentenize bağlı yeni bir broker hesabı oluşturun',
    'agency.broker_name': 'Tam Ad',
    'agency.broker_email': 'E-posta Adresi',
    'agency.broker_password': 'Şifre',
    'agency.create_broker_btn': 'Broker Hesabı Oluştur',
    'agency.broker_created': 'Broker hesabı başarıyla oluşturuldu',
    'agency.broker_create_error': 'Broker hesabı oluşturulamadı',
    'agency.no_brokers': 'Henüz broker yok',
    'agency.no_brokers_description': 'Broker hesapları oluşturun veya davet gönderin',
    'agency.broker_removed': 'Broker acenteden çıkarıldı',
    'agency.broker_remove_error': 'Broker çıkarılamadı',
    'agency.invites_title': 'Bekleyen Davetler',
    'agency.invites_description': 'Potansiyel brokerlere gönderilen davetleri yönetin',
    'agency.send_invite': 'Davet Gönder',
    'agency.send_invite_title': 'Bir Broker Davet Et',
    'agency.send_invite_description': 'Acentenize katılmak için davet gönderin',
    'agency.invite_email': 'E-posta Adresi',
    'agency.send_invite_btn': 'Daveti Gönder',
    'agency.invite_sent': 'Davet başarıyla gönderildi',
    'agency.invite_error': 'Davet gönderilemedi',
    'agency.link_copied': 'Davet linki kopyalandı',
    'agency.no_invites': 'Bekleyen davet yok',
    'agency.settings_title': 'Acente Ayarları',
    'agency.settings_description': 'Acente bilgilerinizi güncelleyin',
    'agency.settings_saved': 'Acente ayarları kaydedildi',
    'agency.settings_save_error': 'Ayarlar kaydedilemedi',
    'agency.load_error': 'Acente verileri yüklenemedi',
    'agency_invite.title': 'Acenteye Katıl',
    'agency_invite.description': '{agencyName} acentesine katılmaya davet edildiniz',
    'agency_invite.accept_btn': 'Daveti Kabul Et',
    'agency_invite.accepted_success': 'Acenteye başarıyla katıldınız!',
    'agency_invite.accept_error': 'Davet kabul edilemedi',
    'agency_invite.create_account': 'Hesap Oluştur',
    'agency_invite.invalid_link': 'Geçersiz davet linki',
    'agency_invite.expired': 'Bu davet süresi dolmuş',
    'agency_invite.already_accepted': 'Bu davet zaten kabul edilmiş',
    'agency_invite.fetch_error': 'Davet yüklenemedi',
    'agency_invite.error_title': 'Geçersiz Davet',
    'agency_invite.back_home': 'Ana sayfaya dön',
    'agency_invite.create_account_description': 'Bu daveti kabul etmek için bir hesap oluşturun',
    'agency_invite.your_name': 'Adınız',
    'agency_invite.name_placeholder': 'Tam adınızı girin',
    'agency_invite.email_placeholder': 'sizin@email.com',
    'agency_invite.password': 'Şifre',
    'agency_invite.password_placeholder': 'Bir şifre oluşturun',
    'agency_invite.confirm_password': 'Şifreyi onayla',
    'agency_invite.confirm_password_placeholder': 'Şifrenizi onaylayın',
    'agency_invite.register_accept_btn': 'Hesap oluştur ve katıl',
    'agency_invite.registered_success': 'Hesap oluşturuldu ve acenteye katıldınız!',
    'agency_invite.register_error': 'Hesap oluşturulamadı',
    'agency_invite.passwords_mismatch': 'Şifreler eşleşmiyor',
    'agency_invite.password_too_short': 'Şifre en az 6 karakter olmalıdır',
    'agency_invite.already_have_account': 'Zaten bir hesabınız var mı?',
    'agency_invite.login_here': 'Buradan giriş yapın',
    'agency_invite.joining': 'Katılıyorsunuz',
    'agency_invite.logged_in_as': 'Giriş yapıldı',
    'agency.notifications_tab': 'Bildirimler',
    'agency.notifications_title': 'E-posta Bildirimleri',
    'agency.notifications_description': 'Broker faaliyetleri hakkında hangi bildirimleri alacağınızı seçin',
    'agency.notifications_saved': 'Bildirim tercihleri kaydedildi',
    'agency.notifications_save_error': 'Tercihler kaydedilemedi',
    'agency.notif_new_broker_joins': 'Yeni Broker Katılıyor',
    'agency.notif_new_broker_joins_desc': 'Bir broker davetinizi kabul ettiğinde bildirim alın',
    'agency.notif_broker_property_added': 'Broker Mülk Ekliyor',
    'agency.notif_broker_property_added_desc': 'Brokerler yeni mülk eklediğinde bildirim alın',
    'agency.notif_broker_viewing_requests': 'Görüntüleme İstekleri',
    'agency.notif_broker_viewing_requests_desc': 'Broker mülkleri için görüntüleme istekleri hakkında bildirim alın',
    'agency.notif_broker_client_messages': 'Müşteri Mesajları',
    'agency.notif_broker_client_messages_desc': 'Müşteriler brokerlerinizle iletişime geçtiğinde bildirim alın',
    'agency.notif_weekly_summary': 'Haftalık Özet',
    'agency.notif_weekly_summary_desc': 'Acente faaliyetlerinin haftalık özetini alın',
    'agency.analytics_tab': 'Analitik',
    'agency.no_brokers_analytics': 'Performans analizlerini görmek için broker ekleyin',
    'agency.total_properties': 'Toplam Mülkler',
    'agency.total_views': 'Toplam Görüntüleme',
    'agency.total_viewings': 'Görüntüleme Talepleri',
    'agency.total_sold': 'Satılan Mülkler',
    'agency.total_sales_value': 'Toplam Satış Değeri',
    'agency.broker_performance_chart': 'Broker Performansı',
    'agency.broker_performance_chart_desc': 'Broker metriklerini bir bakışta karşılaştırın',
    'agency.individual_broker_stats': 'Bireysel İstatistikler',
    'agency.individual_broker_stats_desc': 'Her broker için detaylı metrikler',
    'agency.properties': 'Mülkler',
    'agency.views': 'Görüntüleme',
    'agency.viewings': 'Ziyaretler',
    'agency.contacts': 'İletişim',
    'agency.active': 'Aktif',
    'agency.sold': 'Satıldı',
    'agency.sales_value': 'Satış Değeri',
    'agency.upgrade_plan': 'Planı Yükselt',
    'agency.view_plans': 'Planları Gör',
    'agency.billing_tab': 'Faturalama',
    'agency.name_label': 'Ajans Adı',
    'agency.email_label': 'E-posta',
    'agency.phone_label': 'Telefon',
    'agency.website_label': 'Web sitesi',
    'agency.address_label': 'Adres',
    'agency.city_label': 'Şehir',
    'agency.description_label': 'Açıklama',
    'agency.description_placeholder': 'Müşterilere ajansınız hakkında bilgi verin...',
    'agency.save_settings': 'Ayarları Kaydet',
    'agency.broker_usage': 'Broker Kullanımı',
    'agency.invite_broker': 'Broker Davet Et',
    'agency.brokers': 'broker',
    'agency.broker_limit_reached': 'Broker limiti doldu',
    'agency.upgrade_for_more': 'Daha fazla broker eklemek için planınızı yükseltin',
    'agency.active_listings': 'Aktif İlanlar',
    'agency.broker_slots_remaining': 'Kalan yerler',
    'agency.manage_subscription': 'Aboneliği Yönet',
    'agency.upgrade_your_plan': 'Planınızı Yükseltin',
    'agency.choose_plan': 'Ajansınıza en uygun planı seçin',
    'agency.pro_plan': 'Pro',
    'agency.enterprise_plan': 'Kurumsal',
    'agency.for_growing_agencies': 'Büyüyen ajanslar için',
    'agency.for_large_agencies': 'Büyük ajanslar için',
    'agency.per_month': '/ay',
    'agency.up_to_brokers': '{count} brokere kadar',
    'agency.unlimited_brokers': 'Sınırsız broker',
    'agency.advanced_analytics': 'Gelişmiş analizler',
    'agency.full_analytics': 'Tam analiz paketi',
    'agency.priority_support': 'Öncelikli destek',
    'agency.dedicated_support': 'Özel destek',
    'agency.upgrade_to_pro': 'Pro\'ya Yükselt',
    'agency.upgrade_to_enterprise': 'Kurumsal\'a Yükselt',
    'agency.popular': 'Popüler',
    'agency.current_plan': 'Mevcut Plan',
    'agency.plan_features': 'Plan Özellikleri',
    'agency.billing_history': 'Faturalama Geçmişi',
    'agency.no_billing_history': 'Henüz faturalama geçmişi yok',
    'agency.subscription_billing': 'Abonelik ve Faturalama',
    'agency.manage_agency_subscription': 'Ajansınızın aboneliğini yönetin',
    'agency.plan': 'Plan',
    'agency.basic_plan': 'Temel',
    'agency.broker_limit': 'Broker Limiti',
    'agency.current_usage': 'Mevcut Kullanım',
    'agency.status': 'Durum',
    'agency.active_status': 'Aktif',
    'agency.unlimited': 'Sınırsız',
    'agency.feature': 'Özellik',
    'agency.price': 'Fiyat',
    'agency.free': 'Ücretsiz',
    'agency.analytics': 'Analiz',
    'agency.basic_analytics': 'Temel',
    'agency.advanced': 'Gelişmiş',
    'agency.full_suite': 'Tam Paket',
    'agency.support': 'Destek',
    'agency.email_support': 'E-posta',
    'agency.priority': 'Öncelikli',
    'agency.dedicated': 'Özel',
    'agency_directory.title': 'Emlak Acenteleri',
    'agency_directory.subtitle': 'Güvenilir emlak acentelerini arayın ve bulun',
    'agency_directory.search_placeholder': 'Acente ara...',
    'agency_directory.location_placeholder': 'Konuma göre filtrele...',
    'agency_directory.agencies_found': 'acente bulundu',
    'agency_directory.no_agencies': 'Acente bulunamadı',
    'agency_directory.no_agencies_description': 'Arama kriterlerinizi ayarlamayı deneyin',
    'agency_directory.brokers': 'broker',
    'agency_directory.view_agency': 'Acenteyi Görüntüle',
    'agency_directory.not_found': 'Acente bulunamadı',
    'agency_directory.back_to_directory': 'Dizine Dön',
    'agency_directory.visit_website': 'Web Sitesini Ziyaret Et',
    'agency_directory.our_brokers': 'Brokerlerimiz',
    'agency_directory.no_brokers_yet': 'Bu acentede henüz broker yok',
    'agency_directory.agency_listings': 'Mülk İlanları',
    
    // Messaging
    'messaging.title': 'Mesajlar',
    'messaging.description': 'Özel sohbetleriniz',
    'messaging.new_message': 'Yeni mesaj',
    'messaging.no_messages': 'Henüz mesaj yok',
    'messaging.no_conversations': 'Henüz sohbet yok',
    'messaging.send': 'Gönder',
    'messaging.send_message': 'Mesaj gönder',
    'messaging.message_to': 'Mesaj gönder',
    'messaging.subject': 'Konu',
    'messaging.subject_placeholder': 'Konu (isteğe bağlı)',
    'messaging.message': 'Mesaj',
    'messaging.message_placeholder': 'Mesajınızı yazın...',
    'messaging.login_required': 'Mesaj göndermek için giriş yapmalısınız.',
    'messaging.message_sent': 'Mesaj başarıyla gönderildi',
    'messaging.contact_agent_description': 'Emlak danışmanıyla doğrudan iletişime geçin',
    'messaging.type_message': 'Bir mesaj yazın...',
    'messaging.inbox': 'Gelen kutusu',
    'messaging.sent': 'Gönderilen',
    'messaging.search': 'Sohbetlerde ara...',
    'messaging.select_conversation': 'Bir sohbet seçin',
    'messaging.select_description': 'Mesajlaşmaya başlamak için listeden bir sohbet seçin',
    'messaging.encrypted': 'Uçtan uca şifreli',
    'messaging.decrypt': 'Mesajı çöz',
    'messaging.reply': 'Yanıtla',
    'messaging.replying_to': 'Yanıt veriyor',
    'messaging.replying_to_yourself': 'kendinize',
    'messaging.forward': 'İlet',
    'messaging.delete': 'Sil',
    'messaging.delete_confirm': 'Mesajı sil',
    'messaging.delete_description': 'Bu mesaj herkes için silinecek. Bu işlem geri alınamaz.',
    'messaging.delete_for_everyone': 'Herkes için sil',
    'messaging.pin': 'Sabitle',
    'messaging.unpin': 'Sabitlemeyi kaldır',
    'messaging.pinned_messages': 'Sabitlenmiş mesajlar',
    'messaging.no_pinned': 'Sabitlenmiş mesaj yok',
    'messaging.delivered': 'Teslim edildi',
    'messaging.read': 'Okundu',
    'messaging.seen_at': 'Görüldü',
    'messaging.voice_message': 'Sesli mesaj',
    'messaging.search_messages': 'Mesajlarda ara...',
    'messaging.contacts': 'Kişiler',
    'messaging.results_found': 'sonuç bulundu',
    'messaging.recording': 'Kaydediliyor...',
    'messaging.typing': 'yazıyor...',
    'messaging.online': 'çevrimiçi',
    'messaging.tap_for_info': 'iletişim bilgisi için buraya dokun',
    'messaging.photos': 'Fotoğraflar',
    'messaging.camera': 'Kamera',
    'messaging.location': 'Konum',
    'messaging.contact': 'Kişi',
    'messaging.document': 'Belge',
    'messaging.poll': 'Anket',
    'messaging.event': 'Etkinlik',
    'messaging.location_coming_soon': 'Konum paylaşımı yakında',
    'messaging.contact_coming_soon': 'Kişi paylaşımı yakında',
    'messaging.poll_coming_soon': 'Anket oluşturma yakında',
    'messaging.event_coming_soon': 'Etkinlik oluşturma yakında',
    'messaging.schedule_event': 'Etkinlik planla',
    'messaging.event_title': 'Etkinlik başlığı',
    'messaging.event_title_placeholder': 'örn: Mülk gezisi',
    'messaging.event_date': 'Tarih',
    'messaging.event_time': 'Saat',
    'messaging.event_location': 'Konum',
    'messaging.event_location_placeholder': 'Konum veya adres girin',
    'messaging.event_description': 'Açıklama',
    'messaging.event_description_placeholder': 'Ek ayrıntılar ekleyin...',
    'messaging.send_event': 'Etkinlik gönder',
    'messaging.event_created': 'Etkinlik oluşturuldu',
    'messaging.create_poll': 'Anket oluştur',
    'messaging.poll_question': 'Soru',
    'messaging.poll_question_placeholder': 'Ne sormak istersiniz?',
    'messaging.poll_options': 'Seçenekler',
    'messaging.option': 'Seçenek',
    'messaging.add_option': 'Seçenek ekle',
    'messaging.allow_multiple': 'Birden fazla cevaba izin ver',
    'messaging.send_poll': 'Anketi gönder',
    'messaging.poll_created': 'Anket oluşturuldu',
    'messaging.edit': 'Düzenle',
    'messaging.edit_message': 'Mesajı Düzenle',
    'messaging.edit_description': 'Mesajınızda değişiklikler yapın',
    'messaging.edit_time_remaining': 'Bu mesajı düzenlemek için',
    'messaging.minutes_to_edit': 'dakikanız var',
    'messaging.message_edited': 'Mesaj düzenlendi',
    'messaging.edit_failed': 'Mesaj düzenlenemedi',
    'messaging.from_date': 'Başlangıç',
    'messaging.to_date': 'Bitiş',
    'messaging.clear_filters': 'Filtreleri temizle',
    'messaging.export_chat': 'Sohbeti Dışa Aktar',
    'messaging.export_description': 'Sohbet geçmişinizi dışa aktarın',
    'messaging.export_text': 'Metin Dosyası (.txt)',
    'messaging.export_text_desc': 'Basit metin formatı, okunması ve paylaşması kolay',
    'messaging.export_pdf': 'PDF Belgesi',
    'messaging.export_pdf_desc': 'Biçimlendirilmiş belge, yazdırma için ideal',
    'messaging.export_messages_count': 'mesaj dışa aktarılacak',
    'messaging.exporting': 'Dışa aktarılıyor...',
    'messaging.export': 'Dışa Aktar',
    'messaging.export_success': 'Sohbet başarıyla dışa aktarıldı',
    'messaging.export_failed': 'Sohbet dışa aktarılamadı',
    'messaging.is_typing': 'yazıyor...',
    
    // Quick Reply Templates
    'quick_reply.title': 'Hızlı Yanıtlar',
    'quick_reply.greeting': 'Merhaba! Bizimle iletişime geçtiğiniz için teşekkürler.',
    'quick_reply.interested': 'Bu mülkle ilgileniyorum. Daha fazla bilgi verebilir misiniz?',
    'quick_reply.schedule_viewing': 'Bir görüntüleme ayarlamak istiyorum. Hangi zamanlar müsait?',
    'quick_reply.ask_price': 'Fiyat pazarlığa açık mı?',
    'quick_reply.ask_availability': 'Bu mülk hala mevcut mu?',
    'quick_reply.thank_you': 'Bilgiler için teşekkürler. Yakında size dönüş yapacağım.',
    'quick_reply.will_contact': 'Daha fazla detayla yakında sizinle iletişime geçeceğim.',
    'quick_reply.not_interested': 'Teşekkürler, ancak artık bu mülkle ilgilenmiyorum.',
    
    // Notifications
    'notifications.enable': 'Bildirimleri etkinleştir',
    'notifications.on': 'Bildirimler açık',
    'notifications.preferences': 'Bildirim Tercihleri',
    'notifications.sound': 'Bildirim Sesi',
    'notifications.sound_desc': 'Mesaj aldığınızda ses çal',
    'notifications.sound_default': 'Varsayılan',
    'notifications.sound_chime': 'Çan',
    'notifications.sound_bell': 'Zil',
    'notifications.sound_pop': 'Pop',
    'notifications.test': 'Test',
    'notifications.vibration': 'Titreşim',
    'notifications.vibration_desc': 'Mesaj aldığınızda titreşim (sadece mobil)',
    'messaging.you': 'Sen',
    'messaging.encrypted_message': 'Şifreli mesaj',
    'messaging.e2e_notice': 'Mesajlar ve aramalar uçtan uca şifrelenmiştir. Bu sohbetteki kişiler yalnızca bunları okuyabilir, dinleyebilir veya paylaşabilir.',
    'messaging.today': 'Bugün',
    'messaging.yesterday': 'Dün',
    'messaging.search_groups': 'Grupları ara...',
    
    // Attachment viewer
    'attachment.all_media': 'Tüm medya',
    'attachment.go_to_message': 'Mesaja git',
    'attachment.download': 'İndir',
    'attachment.view_fullscreen': 'Tam ekran görüntüle',
    'attachment.save_to_gallery': 'Galeriye kaydet',
    'attachment.share': 'Paylaş',
    'attachment.double_tap_zoom': 'Yakınlaştırmak için çift dokunun',
    'attachment.zoom_in': 'Yakınlaştır',
    'attachment.zoom_out': 'Uzaklaştır',
    'attachment.reset_zoom': 'Yakınlaştırmayı sıfırla',
    'attachment.ctrl_scroll_zoom': 'Ctrl + Kaydır ile yakınlaştır',
    'attachment.fit_width': 'Genişliğe sığdır',
    'attachment.fit_height': 'Yüksekliğe sığdır',
    'attachment.click_to_navigate': 'Gezinmek için tıkla',
    
    // Status
    'status.online': 'Çevrimiçi',
    'status.offline': 'Çevrimdışı',
    'status.last_seen': 'Son görülme',
    
    // Blocking
    'blocking.block_user': 'Kullanıcıyı engelle',
    'blocking.blocked_users': 'Engellenen kullanıcılar',
    'blocking.unblock': 'Engeli kaldır',
    'blocking.you_blocked_user': 'Bu kullanıcıyı engellediniz. Mesaj göndermek için engeli kaldırın.',
    'blocking.user_blocked_you': 'Bu kullanıcı sizi engelledi. Mesaj gönderemezsiniz.',
    'reporting.report_user': 'Kullanıcıyı bildir',
    'admin.reports': 'Raporlar',
    'admin.reports_panel': 'Kullanıcı raporları',
    'admin.pending': 'Beklemede',
    'admin.resolved': 'Çözüldü',
    'admin.all': 'Tümü',
    'messaging.unknown_user': 'Bilinmeyen kullanıcı',
    'messaging.no_groups': 'Henüz grup yok',
    'messaging.create_group': 'Grup oluştur',
    'messaging.group_chat': 'Grup sohbeti',
    'messaging.direct_messages': 'Direkt',
    'messaging.groups': 'Gruplar',
    'messaging.group_name': 'Grup adı',
    'messaging.group_description': 'Açıklama',
    
    // User Listings
    'userListings.title': 'İlanlarım',
    'userListings.subtitle': 'Mülk ilanlarınızı yönetin ve yenilerini oluşturun',
    'userListings.pricingTitle': 'İlan Fiyatlandırması',
    'userListings.pricingDesc': 'Basit fiyatlandırmamızla mülk ilanınızı oluşturun',
    'userListings.listingPrice': 'İlan başına $10',
    'userListings.listingDuration': '1 ay geçerli',
    'userListings.videoPrice': 'Video için +$3',
    'userListings.videoDesc': 'İlanınıza video tur ekleyin',
    'userListings.includeVideo': 'Video tur dahil et',
    'userListings.createListing': 'İlan Oluştur',
    'userListings.myListingsTitle': 'Mülkleriniz',
    'userListings.noListings': 'Henüz ilan yok',
    'userListings.noListingsDesc': 'Başlamak için ilk mülk ilanınızı oluşturun',
    'userListings.createFirst': 'İlk İlanınızı Oluşturun',
    'userListings.hasVideo': 'Video',
    'userListings.renew': 'Yenile',
    'userListings.renewSuccess': 'İlan yenilendi!',
    'userListings.renewSuccessDesc': 'İlanınız bir ay daha yenilendi',
    'userListings.paymentSuccess': 'Ödeme başarılı!',
    'userListings.paymentSuccessDesc': 'İlanınız şimdi yayında',
    'userListings.paymentCancelled': 'Ödeme iptal edildi',
    'userListings.paymentCancelledDesc': 'Ödemeniz iptal edildi',
    'userListings.checkoutError': 'Ödeme oluşturulamadı',
    'userListings.renewError': 'İlan yenilenemedi',
    'userListings.createDraft': 'İlan oluştur',
    'userListings.previewListing': 'İlanınızın önizlemesi',
    'userListings.draft': 'Taslak',
    'userListings.drafts': 'Taslaklar',
    'userListings.draftSaved': 'Taslak kaydedildi',
    'userListings.draftSavedDesc': 'İlanınız taslak olarak kaydedildi',
    'userListings.draftError': 'Taslak kaydedilemedi',
    'userListings.draftDeleted': 'Taslak silindi',
    'userListings.deleteError': 'Silme başarısız',
    'userListings.saveDraft': 'Taslağı kaydet',
    'userListings.saveAndPreview': 'Kaydet ve önizle',
    'userListings.publishOptions': 'Yayınlama seçenekleri',
    'userListings.addVideoTour': 'Video turu ekle',
    'userListings.videoIncluded': 'Video turu dahil',
    'userListings.totalPrice': 'Toplam',
    'userListings.backToEdit': 'Düzenlemeye geri dön',
    'userListings.publishNow': 'Şimdi yayınla',
    'userListings.publish': 'Yayınla',
    'userListings.publishSuccess': 'İlan yayınlandı!',
    'userListings.publishSuccessDesc': 'İlanınız şimdi inceleme bekliyor',
    'userListings.firstListingFree': 'İlk ilan ücretsiz!',
    'userListings.firstListingFreeDesc': 'İlk mülk ilanınız tamamen ücretsiz. Şimdi gönderin ve Irak genelinde alıcılara ulaşın!',
    'userListings.pending': 'İnceleme bekleniyor',
    'userListings.pendingInfo': 'Bu ilanlar moderasyon ekibimiz tarafından inceleniyor. Onaylandıklarında bilgilendirileceksiniz.',
    'userListings.pendingReview': 'İnceleme bekleniyor',
    'userListings.awaitingApproval': 'Moderatör onayı bekleniyor',
    'userListings.rejected': 'Reddedilen ilanlar',
    'userListings.rejectedInfo': 'Bu ilanlar onaylanmadı. Lütfen tekrar göndermeden önce düzenleyin ve sorunları düzeltin.',
    'userListings.rejectedBadge': 'Reddedildi',
    'userListings.live': 'Aktif',
    'userListings.editDraft': 'Düzenle',
    'userListings.editAndResubmit': 'Düzenle ve tekrar gönder',
    'userListings.pendingCannotEdit': 'İnceleme sırasında düzenleme yapılamaz',
    'userListings.resubmitForReview': 'İnceleme için tekrar gönder',
    'userListings.resubmitInfo': 'Değişiklikleriniz kaydedildi. İlanınızı ek ücret ödemeden tekrar inceleme için gönderin.',
    'userListings.submitForReview': 'İnceleme için gönder',
    'userListings.validation.priceRequired': 'Fiyat gereklidir',
    'userListings.validation.cityRequired': 'Şehir gereklidir',
    'userListings.validation.roomsRequired': 'Oda sayısı gereklidir',
    'userListings.validation.sizeRequired': 'Boyut gereklidir',
    'userListings.validation.plotSizeRequired': 'Arsa boyutu gereklidir',
    'userListings.validation.imagesRequired': 'En az bir resim gereklidir',
    'userListings.choosePlan': 'İlan planınızı seçin',
    'userListings.choosePlanDesc': 'İlan kartınızda kaç resim gösterileceğini seçin',
    'userListings.upgradePlan': 'İlan planınızı yükseltin',
    'userListings.upgradePlanDesc': 'Daha fazla resim göstermek için yükseltin. Sadece farkı ödersiniz.',
    'userListings.selectImages': 'Gösterilecek resimleri seçin',
    'userListings.selectImagesDesc': 'İlan kartınızda gösterilecek resimleri seçin',
    'userListings.selectPrimaryImage': 'Ana resmi seçin',
    'userListings.selectPrimaryImageDesc': 'İlanınızda ilk gösterilecek ana resmi seçin',
    'userListings.addVideo': 'Video tur ekle',
    'userListings.pleaseSelectImages': 'Devam etmek için lütfen resim seçin',
    'userListings.pleaseSelectPrimaryImage': 'Devam etmek için lütfen ana resmi seçin',
    'userListings.proceedToPayment': 'Ödemeye devam et',
    'userListings.tierBasic': 'Temel',
    'userListings.tierStandard': 'Standart',
    'userListings.tierPremium': 'Premium',
    'userListings.tierBasicDesc': 'Kartta 1 resim',
    'userListings.tierStandardDesc': '3 resimli karusel',
    'userListings.tierPremiumDesc': 'Tüm resimler + rozet',
    'userListings.tierDuration': '30 gün aktif',
    'userListings.tierBest': 'En İyi',
    'userListings.tierBasicFull': 'Temel Seviye (1 resim)',
    'userListings.tierStandardFull': 'Standart Seviye (3 resim)',
    'userListings.tierPremiumFull': 'Premium Seviye (Tüm resimler)',
    'userListings.changeTier': 'Seviye Değiştir',
    'userListings.selectImageTier': 'Resim Seviyesi Seç',
    'userListings.selectTierFirst': 'Yayınlamadan önce bir seviye seçin',
    'userListings.upgrade': 'Yükselt',
    'common.total': 'Toplam',
    
    'nav.favorites': 'Favoriler',
    'nav.profile': 'Profil',
    'nav.login': 'Giriş Yap',
    'nav.logout': 'Çıkış Yap',
    'nav.recent_searches': 'Son aramalar',
    'nav.no_recent_searches': 'Son arama yok',
    
    'agents.title': 'Temsilci bul',
    'agents.subtitle': 'Satış mı düşünüyorsunuz? Bölgenizdeki uzman temsilcileri bulmanıza yardımcı oluyoruz!',
    'agents.area_search': 'Bölge Araması',
    'agents.name_search': 'İsim Araması',
    'agents.search_placeholder': 'Bölge veya adres girin',
    'agents.show': 'Göster',
    'agents.sort_by': 'Sırala',
    'agents.agents_count': 'Temsilciler',
    'agents.companies_label': 'Emlak şirketleri',
    'agents.recent_sales': 'Son satış tarihi',
    'agents.most_sales': 'En çok satış',
    'agents.alphabetical': 'Alfabetik sıra',
    'agents.showing_results': 'Gösterilen',
    'agents.agents_label': 'temsilci',
    'agents.loading': 'Temsilciler yükleniyor...',
    'agents.no_results': 'Temsilci bulunamadı.',
    'agents.phone': 'Telefon:',
    'agents.status': 'Durum:',
    'agents.active_agent': 'Aktif temsilci',
    'agents.anonymous_agent': 'Anonim temsilci',
    'agents.agent_label': 'Temsilci',
    'agents.show_more': 'Daha fazla temsilci göster',
    'agents.verified_agent': 'Doğrulanmış temsilci',
    
    // Broker Public Profile
    'broker.listing_usage': 'İlan Kullanımı',
    'broker.monthly_listing_limits': 'Aylık ilan limitleri',
    'broker.listings_used': 'Kullanılan ilanlar',
    'broker.listings_remaining': 'ilan kaldı',
    'broker.period_resets': 'Dönem sıfırlanıyor',
    'broker.export_csv': 'CSV dışa aktar',
    'broker.total_views': 'Toplam görüntüleme',
    'broker.scheduled_viewings': 'Planlı gösterimler',
    'broker.client_messages': 'Müşteri mesajları',
    'broker.filters': 'Filtreler',
    'broker.all_time': 'Tüm zamanlar',
    'broker.active_listings': 'Aktif ilanlar',
    'broker.sold_listings': 'Satılan ilanlar',
    'broker.active_listings_desc': 'Şu anda yayınlanan mülkleriniz',
    'broker.no_active_listings': 'Henüz aktif ilan yok. İlk mülkünüzü ekleyin.',
    'broker.all_types': 'Tüm tipler',
    'broker.all_cities': 'Tüm şehirler',
    'broker.property_type': 'Mülk tipi',
    'broker.city': 'Şehir',
    'broker.date_range': 'Tarih aralığı',
    'broker.last_week': 'Geçen hafta',
    'broker.last_month': 'Geçen ay',
    'broker.last_quarter': 'Son 3 ay',
    'broker.sold_listings_desc': 'Sattığınız mülkler',
    'broker.no_sold_listings': 'Henüz satılan mülk yok',
    'broker.clear_filters': 'Filtreleri temizle',
    'broker.selected': 'seçili',
    'broker.compare': 'Karşılaştır',
    'broker.mark_as_sold': 'Satıldı olarak işaretle',
    'broker.delete_selected': 'Seçilenleri sil',
    'broker.select_all': 'Tümünü seç',
    'broker.rejected_listings': 'Reddedilen ilanlar',
    'broker.rejected_listings_desc': 'Bu ilanlar moderatörler tarafından reddedildi. İtiraz edebilirsiniz.',
    'broker.no_rejected_listings': 'Reddedilen ilan yok',
    'broker.confirm_delete': 'Silmeyi onayla',
    'broker_public.not_found': 'Temsilci bulunamadı',
    'broker_public.not_found_description': 'Aradığınız temsilci profili mevcut değil.',
    'broker_public.back_to_search': 'Temsilci aramasına dön',
    'broker_public.contact_broker': 'Temsilciyle iletişime geç',
    'broker_public.agency': 'Acente',
    'broker_public.visit_website': 'Websiteyi ziyaret et',
    'broker_public.active_listings': 'Aktif ilanlar',
    'broker_public.sold_properties': 'Satılan mülkler',
    'broker_public.total_sales': 'Toplam satış',
    'broker_public.avg_days_market': 'Ortalama piyasada gün',
    'broker_public.no_active_listings': 'Şu anda aktif ilan yok',
    'broker_public.no_sold_properties': 'Henüz satılan mülk yok',
    'broker_public.view_all_properties': 'Tüm mülkleri gör',
    'broker_public.send_message': 'Mesaj gönder',
    'broker_public.view_agency': 'Acenteyi gör',
    'broker_public.works_at': 'Çalıştığı yer',
    'broker_public.listings': 'İlanlar',
    'broker_public.sold': 'Satıldı',
    'broker_public.reviews': 'Yorumlar',
    'broker_public.rating': 'Puan',
    'broker_public.featured_broker': 'Öne çıkan temsilci',
    'broker_public.top_performer': 'En iyi performans',
    
    // Toast messages
    'toast.logged_out': 'Çıkış yapıldı',
    'toast.welcome_back': 'İstediğiniz zaman tekrar gelin!',
    
    // Recent searches
    'search.properties_label': 'Mülkler',
    'search.from': 'den',
    'search.up_to': 'kadar',
    
    // Agency Pricing
    'agency_pricing.badge': 'Acente Planları',
    'agency_pricing.title': 'Doğru planla acentenizi büyütün',
    'agency_pricing.subtitle': 'Emlak acenteniz için mükemmel planı seçin.',
    'agency_pricing.most_popular': 'En popüler',
    'agency_pricing.per_month': '/ay',
    'agency_pricing.processing': 'İşleniyor...',
    'agency_pricing.comparison_title': 'Detaylı karşılaştırma',
    'agency_pricing.feature': 'Özellik',
    'agency_pricing.faq_title': 'Sık sorulan sorular',
    'agency_pricing.cta_title': 'Acentenizi büyütmeye hazır mısınız?',
    'agency_pricing.cta_subtitle': 'Binlerce başarılı emlak acentesine katılın.',
    'agency_pricing.cta_button': 'Pro ile başla',
    'agency_pricing.sign_in_required': 'Yükseltmek için giriş yapın',
    'agency_pricing.tier.starter': 'Başlangıç',
    'agency_pricing.tier.starter_desc': 'Küçük acenteler için ideal',
    'agency_pricing.tier.starter_cta': 'Başla',
    'agency_pricing.tier.pro': 'Pro',
    'agency_pricing.tier.pro_desc': 'Büyüyen acenteler için',
    'agency_pricing.tier.pro_cta': 'Pro\'ya yükselt',
    'agency_pricing.tier.enterprise': 'Enterprise',
    'agency_pricing.tier.enterprise_desc': 'Büyük acenteler için',
    'agency_pricing.tier.enterprise_cta': 'Enterprise seç',
    'agency_pricing.feature.brokers_3': '3 temsilciye kadar',
    'agency_pricing.feature.brokers_10': '10 temsilciye kadar',
    'agency_pricing.feature.brokers_unlimited': 'Sınırsız temsilci',
    'agency_pricing.feature.listings_basic': 'Temel ilanlar',
    'agency_pricing.feature.listings_advanced': 'Gelişmiş ilanlar',
    'agency_pricing.feature.listings_premium': 'Premium ilanlar',
    'agency_pricing.feature.messaging': 'Müşteri mesajlaşması',
    'agency_pricing.feature.scheduling': 'Ziyaret planlaması',
    'agency_pricing.feature.analytics': 'Analiz paneli',
    'agency_pricing.feature.analytics_advanced': 'Gelişmiş analiz',
    'agency_pricing.feature.support': 'Öncelikli destek',
    'agency_pricing.feature.support_247': '7/24 destek',
    'agency_pricing.feature.branding': 'Özel marka',
    'agency_pricing.feature.api': 'API erişimi',
    'agency_pricing.comparison.brokers': 'Temsilci sayısı',
    'agency_pricing.comparison.listings': 'Emlak ilanları',
    'agency_pricing.comparison.basic': 'Temel',
    'agency_pricing.comparison.advanced': 'Gelişmiş',
    'agency_pricing.comparison.premium': 'Premium',
    'agency_pricing.faq.change_plan_q': 'Planımı sonra değiştirebilir miyim?',
    'agency_pricing.faq.change_plan_a': 'Evet! İstediğiniz zaman değiştirebilirsiniz.',
    'agency_pricing.faq.exceed_limit_q': 'Limiti aşarsam ne olur?',
    'agency_pricing.faq.exceed_limit_a': 'Daha yüksek bir plana geçmeniz gerekir.',
    'agency_pricing.faq.trial_q': 'Ücretsiz deneme var mı?',
    'agency_pricing.faq.trial_a': '14 günlük para iade garantisi.',
    'agency_pricing.faq.cancel_q': 'Aboneliğimi nasıl iptal ederim?',
    'agency_pricing.faq.cancel_a': 'Aboneliğinizi panelden yönetin.',
    'agency_pricing.view_plans': 'Planları ve fiyatları gör',
    
    // Broker Pricing
    'broker_pricing.badge': 'Komisyoncu Planları',
    'broker_pricing.title': 'Komisyoncu hesabınızı yükseltin',
    'broker_pricing.subtitle': 'Emlak işiniz için mükemmel planı seçin. Daha fazla ilan, daha iyi analiz ve öncelikli destek.',
    'broker_pricing.most_popular': 'En popüler',
    'broker_pricing.current_plan': 'Mevcut planınız',
    'broker_pricing.your_current_plan': 'Mevcut planınız',
    'broker_pricing.free': 'Ücretsiz',
    'broker_pricing.month': 'ay',
    'broker_pricing.per_month': '/ay',
    'broker_pricing.processing': 'İşleniyor...',
    'broker_pricing.loading': 'Yükleniyor...',
    'broker_pricing.manage_subscription': 'Aboneliği yönet',
    'broker_pricing.cancel_anytime': 'İstediğiniz zaman düşürün veya iptal edin • Aylık otomatik yenilenir',
    'broker_pricing.manage_note': 'İstediğiniz zaman düşürün veya iptal edin • Aylık otomatik yenilenir',
    'broker_pricing.downgrade': 'Düşür',
    'broker_pricing.upgrade_to_pro': 'Pro\'ya yükselt',
    'broker_pricing.go_enterprise': 'Enterprise\'a geç',
    'broker_pricing.get_started_pro': 'Pro ile başla',
    'broker_pricing.comparison_title': 'Detaylı özellik karşılaştırması',
    'broker_pricing.feature': 'Özellik',
    'broker_pricing.faq_title': 'Sık sorulan sorular',
    'broker_pricing.cta_title': 'İşinizi büyütmeye hazır mısınız?',
    'broker_pricing.cta_subtitle': 'Daha fazla mülk listelemek ve daha fazla anlaşma kapatmak için platformumuzu kullanan binlerce başarılı komisyoncuya katılın.',
    'broker_pricing.cta_button': 'Pro ile başla',
    'broker_pricing.sign_in_required': 'Planınızı yükseltmek için lütfen giriş yapın',
    'broker_pricing.already_on_plan': 'Zaten bu plandasınız',
    'broker_pricing.checkout_error': 'Ödeme başlatılamadı',
    'broker_pricing.portal_error': 'Abonelik yönetimi açılamadı',
    'broker_pricing.tier.normal': 'Normal',
    'broker_pricing.tier.normal_desc': 'Başlayan bireysel komisyoncular için mükemmel',
    'broker_pricing.tier.normal_cta': 'Mevcut plan',
    'broker_pricing.tier.pro': 'Pro',
    'broker_pricing.tier.pro_desc': 'Daha fazla ilanla büyüyen komisyoncular için',
    'broker_pricing.tier.pro_cta': 'Pro\'ya yükselt',
    'broker_pricing.tier.enterprise': 'Enterprise',
    'broker_pricing.tier.enterprise_desc': 'Sınırsız ihtiyaçları olan yüksek hacimli komisyoncular için',
    'broker_pricing.tier.enterprise_cta': 'Enterprise\'a geç',
    'broker_pricing.feature.listings_5': 'Ayda 5 ilan',
    'broker_pricing.feature.listings_10': 'Ayda 10 ilan',
    'broker_pricing.feature.listings_15': 'Ayda 15 ilan',
    'broker_pricing.feature.basic_features': 'Temel mülk özellikleri',
    'broker_pricing.feature.advanced_features': 'Gelişmiş mülk özellikleri',
    'broker_pricing.feature.premium_features': 'Premium mülk özellikleri',
    'broker_pricing.feature.client_messaging': 'Müşteri mesajlaşması',
    'broker_pricing.feature.viewing_scheduling': 'Ziyaret planlaması',
    'broker_pricing.feature.video_tours_paid': 'Video turları (ilan başına $3)',
    'broker_pricing.feature.video_tours_free': 'Ücretsiz video turları dahil',
    'broker_pricing.feature.analytics': 'Analiz paneli',
    'broker_pricing.feature.advanced_analytics': 'Gelişmiş analiz',
    'broker_pricing.feature.priority_support': 'Öncelikli destek',
    'broker_pricing.feature.support_24_7': '7/24 öncelikli destek',
    'broker_pricing.feature.featured_listings': 'Öne çıkan ilanlar',
    'broker_pricing.feature.extra_listings': 'Ekstra ilan seçeneği',
    'broker_pricing.feature.extra_10': 'Ekstra ilan başına +$10',
    'broker_pricing.feature.messaging': 'Müşteri mesajlaşması',
    'broker_pricing.feature.scheduling': 'Ziyaret planlaması',
    'broker_pricing.feature.video_paid': 'Video turları (ilan başına $3)',
    'broker_pricing.feature.video_free': 'Ücretsiz video turları dahil',
    'broker_pricing.feature.analytics_advanced': 'Gelişmiş analiz',
    'broker_pricing.feature.support': 'Öncelikli destek',
    'broker_pricing.feature.support_247': '7/24 öncelikli destek',
    'broker_pricing.feature.featured': 'Öne çıkan ilanlar',
    'broker_pricing.feature.extra_price': 'Ekstra ilan başına +$10',
    'broker_pricing.table.listings_per_month': 'Aylık ilan',
    'broker_pricing.table.video_tours': 'Video turları',
    'broker_pricing.table.extra_listings': 'Ekstra ilanlar',
    'broker_pricing.table.listing_duration': 'İlan süresi',
    'broker_pricing.table.client_messaging': 'Müşteri mesajlaşması',
    'broker_pricing.table.analytics_dashboard': 'Analiz paneli',
    'broker_pricing.table.priority_support': 'Öncelikli destek',
    'broker_pricing.table.featured_listings': 'Öne çıkan ilanlar',
    'broker_pricing.table.15_extras': '15 + ekstra',
    'broker_pricing.table.3_per_listing': 'İlan başına $3',
    'broker_pricing.table.10_each': 'Her biri $10',
    'broker_pricing.table.30_days': '30 gün',
    'broker_pricing.comparison.listings': 'Aylık ilan',
    'broker_pricing.comparison.video': 'Video turları',
    'broker_pricing.comparison.extra': 'Ekstra ilanlar',
    'broker_pricing.comparison.duration': 'İlan süresi',
    'broker_pricing.comparison.featured': 'Öne çıkan ilanlar',
    'broker_pricing.comparison.30_days': '30 gün',
    'broker_pricing.comparison.per_listing': 'İlan başına $3',
    'broker_pricing.comparison.free': 'Ücretsiz',
    'broker_pricing.comparison.each': 'Her biri $10',
    'broker_pricing.comparison.15_plus': '15 + ekstra',
    'broker_pricing.faq.autorenew_q': 'Aboneliğim otomatik yenileniyor mu?',
    'broker_pricing.faq.autorenew_a': 'Evet! Aboneliğiniz her ay otomatik olarak yenilenir, böylece ilanlarınıza erişiminizi asla kaybetmezsiniz. Her ay aynı gün ücretlendirilirsiniz. Abonelik yönetimi portalından istediğiniz zaman iptal edebilirsiniz.',
    'broker_pricing.faq.upgrade_q': 'İstediğim zaman yükseltebilir veya düşürebilir miyim?',
    'broker_pricing.faq.upgrade_a': 'Kesinlikle! Yükseltmeler orantılı faturalandırma ile hemen geçerli olur. Düşürmeler mevcut fatura döneminin sonunda uygulanır. Değişiklik yapmak için "Aboneliği yönet"e tıklayın.',
    'broker_pricing.faq.expire_q': 'İlanlarım sona erdiğinde ne olur?',
    'broker_pricing.faq.expire_a': 'İlanlar 30 gün sonra sona erer. Sona ermeden 3 gün önce e-posta bildirimi alacaksınız. Aylık ücretsiz slotlarınızı kullanarak veya ekstra ilan başına $10 ödeyerek (yalnızca Enterprise) yenileyebilirsiniz.',
    'broker_pricing.faq.extra_q': '"Ekstra ilanlar" nedir?',
    'broker_pricing.faq.extra_a': 'Enterprise üyeleri 15 aylık ücretsiz ilanın ötesinde her biri $10\'a ek ilan satın alabilir. Yüksek hacimli aylar için harika.',
    'broker_pricing.faq.cancel_q': 'Aboneliğimi nasıl iptal ederim?',
    'broker_pricing.faq.cancel_a': 'İptal etmek, düşürmek veya ödeme yönteminizi güncellemek için "Aboneliği yönet"e tıklayarak fatura portalınıza erişin. İptaller fatura döneminin sonunda geçerli olur.',
    'messaging.conversation_pinned': 'Sohbet sabitlendi',
    'messaging.conversation_unpinned': 'Sohbet sabitlemesi kaldırıldı',
    'messaging.pin_chat': 'Sohbeti sabitle',
    'messaging.unpin_chat': 'Sohbet sabitlemesini kaldır',
    'messaging.pin_failed': 'Sohbet sabitlenemedi',
    'messaging.unpin_failed': 'Sohbet sabitlemesi kaldırılamadı',
    'messaging.archive': 'Arşivle',
    'messaging.unarchive': 'Geri yükle',
    'messaging.show_archived': 'Arşivlenenler',
    'messaging.show_active': 'Aktif olanları göster',
    'messaging.conversation_archived': 'Sohbet arşivlendi',
    'messaging.conversation_unarchived': 'Sohbet geri yüklendi',
    'messaging.archive_failed': 'Sohbet arşivlenemedi',
    'messaging.unarchive_failed': 'Sohbet geri yüklenemedi',
    
    // Hero/News Section
    'hero.find_dream_home': 'Irak\'ta hayalinizdeki evi bulun',
    'hero.explore_description': 'Irak\'ın her yerinden binlerce satılık ev ve nihai fiyatları keşfedin. Değerlendirmeler alın, emlak haberlerini okuyun ve temsilcilerle iletişime geçin.',
    'hero.badge': 'Irak\'ın önde gelen emlak platformu',
  }
};

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>(getInitialLanguage);

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    try {
      localStorage.setItem(STORAGE_KEY, lang);
    } catch {
      // localStorage not available
    }
  };

  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  const t = (key: string): string => {
    const langTranslations = translations[language] as Record<string, string> | undefined;
    return langTranslations?.[key] || key;
  };

  const isRTL = language === 'ar' || language === 'fa' || language === 'ckb';

  const value: LanguageContextType = {
    language,
    setLanguage,
    t,
    isRTL,
  };

  return (
    <LanguageContext.Provider value={value}>
      <div dir={isRTL ? 'rtl' : 'ltr'} className={isRTL ? 'font-arabic' : ''}>
        {children}
      </div>
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};